// Product copy. Ids are `<area>.<surface>.<role>` and never change when the English does.
// Placeholders use ICU syntax: `{flagKey}`. Call sites must supply every placeholder.
// oxlint-disable no-template-curly-in-string -- prices put a literal `$` before a placeholder
import type { MessageId } from '../src/ids';

export const enUS: Record<MessageId, string> = {
  'access.reason.cannotChangeSetting': 'Your permissions don’t allow you to change this setting',
  'access.reason.cannotModifyField': 'Your permissions don’t allow you to modify this field',
  'access.reason.cannotPerformAction': "Your permissions don't allow you to perform this action",
  'access.reason.notForSelf': 'You may not modify this setting for yourself',
  'access.reason.roleCannotChangeSetting': 'The "{roleName}" role does not allow you to change this setting',
  'access.reason.roleCannotModifyField': 'The "{roleName}" role does not allow you to modify this field',
  'access.reason.roleCannotPerformAction': 'The "{roleName}" role does not allow you to perform this action',
  'access.reason.serviceTokenRoles': 'You may not change the roles of a service token',
  'account.announcementBanner.body':
    'Use an announcement banner to communicate important information in-app to all members across your organization.',
  'account.announcementBanner.heading': 'Announcement banner',
  'account.announcementBanner.loadingLabel': 'Loading announcement banner management',
  'account.announcementBanner.regionLabel': 'Announcement banner management',
  'account.announcementBannerConfirm.cancel': 'Cancel',
  'account.announcementBannerConfirm.closeLabel': 'close modal',
  'account.announcementBannerConfirm.confirm': 'Confirm',
  'account.announcementBannerForm.activateHeading': 'Activate announcement banner',
  'account.announcementBannerForm.activePeriodLabel': 'Active period',
  'account.announcementBannerForm.createError': 'Failed to create announcement banner',
  'account.announcementBannerForm.createForbidden': 'You do not have permission to create announcement banners',
  'account.announcementBannerForm.createdToast': 'Announcement banner created',
  'account.announcementBannerForm.datesRequired': 'Select both start and end dates',
  'account.announcementBannerForm.descriptionLabel': 'Description',
  'account.announcementBannerForm.descriptionPlaceholder':
    'This field accepts a limited subset of markdown. Refer to the documentation for more information.',
  'account.announcementBannerForm.descriptionRequired': 'Description is required',
  'account.announcementBannerForm.descriptionTooLong': 'Description must be less than 650 characters',
  'account.announcementBannerForm.endBeforeStart': 'End date must be after start date',
  'account.announcementBannerForm.endDateInvalid': 'End date must be a valid date',
  'account.announcementBannerForm.endDateTooEarly': 'End date must be tomorrow or later',
  'account.announcementBannerForm.headerLabel': 'Header',
  'account.announcementBannerForm.headerRequired': 'Header is required',
  'account.announcementBannerForm.headerTooLong': 'Header must be less than 100 characters',
  'account.announcementBannerForm.immediateConfirm':
    'Announcement banner will appear immediately and display until <dates>{endDate}</dates>. You can update the banner text after it is live.',
  'account.announcementBannerForm.invalidDate': 'Invalid date',
  'account.announcementBannerForm.nextMonthLabel': 'next',
  'account.announcementBannerForm.openCalendarLabel': 'calendar',
  'account.announcementBannerForm.previewHint': 'Edit the fields below to preview the banner:',
  'account.announcementBannerForm.previousMonthLabel': 'previous',
  'account.announcementBannerForm.saveChanges': 'Save changes',
  'account.announcementBannerForm.scheduleHeading': 'Schedule announcement banner',
  'account.announcementBannerForm.scheduledConfirm':
    'Announcement banner is scheduled to appear from <dates>{startDate} to {endDate}.</dates> You can update the banner text after it is live.',
  'account.announcementBannerForm.startDateInvalid': 'Start date must be a valid date',
  'account.announcementBannerForm.startDateTooEarly': 'Start date must be today or later',
  'account.announcementBannerForm.styleCritical': 'Critical',
  'account.announcementBannerForm.styleInfo': 'Info',
  'account.announcementBannerForm.styleLabel': 'Style',
  'account.announcementBannerForm.styleWarning': 'Warning',
  'account.announcementBannerForm.turnOffBody':
    'Announcement banner will be removed immediately. Do you want to proceed?',
  'account.announcementBannerForm.turnOffButton': 'Turn off banner',
  'account.announcementBannerForm.turnOffError': 'Failed to turn off announcement banner',
  'account.announcementBannerForm.turnOffForbidden': 'You do not have permission to turn off this announcement banner',
  'account.announcementBannerForm.turnOffHeading': 'Turn off banner',
  'account.announcementBannerForm.turnedOffToast': 'Announcement banner turned off',
  'account.announcementBannerForm.updateError': 'Failed to update announcement banner',
  'account.announcementBannerForm.updateForbidden': 'You do not have permission to update this announcement banner',
  'account.announcementBannerForm.updatedToast': 'Announcement banner updated',
  'account.apiVersionPicker.endOfLifeWarning': 'This API version will reach end of life soon. Learn more.',
  'account.apiVersionPicker.listLabel': 'API versions',
  'account.apiVersionPicker.triggerLabel': 'Select API version',
  'account.deleteAccount.body': 'This will permanently delete this LaunchDarkly account, including all of its users.',
  'account.deleteAccount.confirmLabel': 'Delete',
  'account.deleteAccount.confirmTitle': 'Permanently delete account?',
  'account.deleteAccount.confirmWarning':
    'This LaunchDarkly account, including all its members, flags, and other data, will be permanently deleted',
  'account.deleteAccount.deleteButton': 'Delete account',
  'account.deleteAccount.heading': 'Delete account',
  'account.emailVerification.expiryNote': 'Your verification link expires in 2 hours.',
  'account.emailVerification.pendingBody': "You're almost there. Click on the link you received at <b>{email}</b>",
  'account.emailVerification.pendingBodySuffix': 'to finish creating your account.',
  'account.emailVerification.pendingHeading': 'Verify your email',
  'account.emailVerification.resendEmail': 'Resend email',
  'account.emailVerification.verifiedBody': 'To start using LaunchDarkly, go to <account>your account</account>.',
  'account.emailVerification.verifiedHeading': 'Your email has been verified',
  'account.genAiOptIn.checkboxLabel': 'Enable generative AI use cases',
  'account.genAiOptIn.noPermissionTooltip':
    "You don't have sufficient permissions to modify this setting. Reach out to your admin to make this change.",
  'account.genAiOptIn.update': 'Update',
  'account.impersonation.disableEnhancedSupport': 'Disable enhanced support',
  'account.impersonation.enable': 'Enable',
  'account.impersonation.enableEnhancedSupport': 'Enable enhanced support',
  'account.impersonation.enabledUntilPrefix': 'Enhanced support is enabled until',
  'account.impersonation.supportTimePeriod': 'Support time period',
  'account.limitedAccess.adminBody':
    'Your account has limited access. To continue, <link>select a plan</link> or <contactUs></contactUs>.',
  'account.limitedAccess.contactSupport': 'contact Support',
  'account.limitedAccess.memberBody': 'Your account has limited access. Contact your administrator to continue.',
  'account.notificationSettings.accessTokenExpiryChatLabel':
    'Enable chat integration notifications for access token expiry',
  'account.notificationSettings.accessTokenExpiryDescription':
    'Get notified as your access tokens approach their expiration date.',
  'account.notificationSettings.accessTokenExpiryEmailLabel': 'Enable email notifications for access token expiry',
  'account.notificationSettings.accessTokenExpiryInboxLabel': 'Enable inbox notifications for access token expiry',
  'account.notificationSettings.accessTokenExpiryReminders':
    'You will receive reminders {daysList} {unit} before expiration.',
  'account.notificationSettings.accessTokenExpiryTitle': 'Access token expiry',
  'account.notificationSettings.approvalsChatLabel': 'Enable chat integration notifications for approvals',
  'account.notificationSettings.approvalsDescription':
    'Get notified when you receive approval requests or when someone has approved, declined, or commented on an approval.',
  'account.notificationSettings.approvalsEmailLabel': 'Enable email notifications for approvals',
  'account.notificationSettings.approvalsInboxLabel': 'Enable inbox notifications for approvals',
  'account.notificationSettings.approvalsTitle': 'Approvals',
  'account.notificationSettings.followExperimentsChatLabel':
    'Enable chat integration notifications for followed experiments',
  'account.notificationSettings.followExperimentsDescription':
    "Get notified when someone makes updates to an experiment you're following.",
  'account.notificationSettings.followExperimentsEmailLabel': 'Enable email notifications for followed experiments',
  'account.notificationSettings.followExperimentsInboxLabel': 'Enable inbox notifications for followed experiments',
  'account.notificationSettings.followExperimentsTitle': 'Follow experiments',
  'account.notificationSettings.followFlagsChatLabel': 'Enable chat integration notifications for followed flags',
  'account.notificationSettings.followFlagsDescription':
    "Get notified when someone makes targeting updates to a feature flag you're following.",
  'account.notificationSettings.followFlagsEmailLabel': 'Enable email notifications for followed flags',
  'account.notificationSettings.followFlagsInboxLabel': 'Enable inbox notifications for followed flags',
  'account.notificationSettings.followFlagsTitle': 'Follow flags',
  'account.notificationSettings.inboxHint': 'Access the Inbox from the bell icon in the sidebar.',
  'account.notificationSettings.organizationAccessChatLabel': 'Enable slack notifications for organization access',
  'account.notificationSettings.organizationAccessChatUnavailable': 'Unavailable for organization access',
  'account.notificationSettings.organizationAccessDescription':
    'Get notified when new members join your account through domain matching or matched seat provisioning.',
  'account.notificationSettings.organizationAccessEmailLabel': 'Enable email notifications for organization access',
  'account.notificationSettings.organizationAccessInboxLabel': 'Enable inbox notifications for organization access',
  'account.notificationSettings.organizationAccessTitle': 'Organization access',
  'account.notificationSettings.suggestedInvitesChatLabel': 'Disabled chat notifications for suggested invite requests',
  'account.notificationSettings.suggestedInvitesChatUnavailable': 'Unavailable for suggested invite requests',
  'account.notificationSettings.suggestedInvitesDescription': 'Get notified of new member invite requests.',
  'account.notificationSettings.suggestedInvitesEmailLabel': 'Enable email notifications for suggested invite requests',
  'account.notificationSettings.suggestedInvitesInboxLabel': 'Enable inbox notifications for suggested invite requests',
  'account.notificationSettings.suggestedInvitesTitle': 'Suggested invites',
  'account.notificationSettings.typeColumn': 'Type',
  'account.notifications.enhancedSupportEnabled': 'Enhanced support enabled',
  'account.notifications.enhancedSupportRemoved': 'Enhanced support removed',
  'account.notifications.ownerUpdated': 'Account owner updated',
  'account.notifications.seatsRequestFailed': 'Error sending request',
  'account.notifications.seatsRequestSent': 'Your request to sales has been sent',
  'account.notifications.updated': 'Account updated',
  'account.organization.heading': 'Your organization',
  'account.organizationForm.formLabel': 'Update organization',
  'account.organizationForm.loading': 'Loading…',
  'account.organizationForm.name': 'Name',
  'account.organizationForm.updateMyOrganization': 'Update my organization',
  'account.organizationForm.updating': 'Updating your organization',
  'account.organizationOwner.heading': 'Change owner',
  'account.ownerForm.cancel': 'Cancel',
  'account.ownerForm.change': 'Change',
  'account.ownerForm.confirmHeading': 'Are you ready to change the account owner to {name}?',
  'account.ownerForm.formLabel': 'Update owner',
  'account.ownerForm.loading': 'Loading…',
  'account.ownerForm.owner': 'Owner',
  'account.ownerForm.ownerPlaceholder': 'Select owner',
  'account.ownerForm.selectOwnerLabel': 'Select owner',
  'account.ownerForm.updateOwner': 'Update owner',
  'account.requestSeatsDialog.descriptionLabel': 'Description',
  'account.requestSeatsDialog.emailLabel': 'Email',
  'account.requestSeatsDialog.firstNameLabel': 'First name',
  'account.requestSeatsDialog.formBody': "Fill out the form, and we'll be in touch.",
  'account.requestSeatsDialog.imageAlt': 'Upgrade seats dark mode',
  'account.requestSeatsDialog.lastNameLabel': 'Last name',
  'account.requestSeatsDialog.seatsExceededBody':
    "You've reached the maximum number of members. Add seats today to make sure no one misses out.",
  'account.requestSeatsDialog.seatsLabel': 'Number of seats',
  'account.requestSeatsDialog.submit': 'Submit',
  'account.requestSeatsDialog.submitLoading': 'Submitting request',
  'account.requestSeatsDialog.submittedBody':
    'Thank you for submitting your request. An expert will be in touch with you shortly.',
  'account.requestSeatsDialog.submittedTitle': 'Request submitted',
  'account.requestSeatsDialog.title': "It's time for an upgrade",
  'account.resendEmailVerification.rateLimitedBody':
    'Resend email verification temporarily disabled, too many attempts.',
  'account.resendEmailVerification.sentBody': 'It might take a few seconds for the email to arrive in your inbox.',
  'account.resendEmailVerification.sentToast': 'Email sent to {email}',
  'account.resendEmailVerification.supportHint': 'If you are still having trouble, <support></support>.',
  'account.resendEmailVerification.tooSoonBody':
    'You can request another verification email after 1 minute has passed.',
  'account.status.loading': 'Loading…',
  'account.supportGenAiOptIn.checkboxLabel': 'Allow LaunchDarkly Support to use generative AI',
  'account.supportGenAiOptIn.noPermissionTooltip':
    "You don't have sufficient permissions to modify this setting. Reach out to your admin to make this change.",
  'account.supportGenAiOptIn.update': 'Update',
  'account.tokenLimits.averageUsageLabel':
    'Avg. usage today · {count, plural, one {# active member} other {# active members}}',
  'account.tokenLimits.dailyLimitBody':
    'Set the limit based on your highest-cost model. Token costs can vary by 10x or more between models.',
  'account.tokenLimits.dailyLimitHeading': 'Daily token limit per member',
  'account.tokenLimits.memberMeterLabel': '{name} token usage',
  'account.tokenLimits.memberUsageEmpty': 'No members have used tokens today.',
  'account.tokenLimits.memberUsageLoadError': "We couldn't load member usage. Refresh the page to try again.",
  'account.tokenLimits.noLimitOption': '0 (No limit)',
  'account.tokenLimits.noLimitPlaceholder': 'No limit',
  'account.tokenLimits.pageBody':
    'Track the tokens your organization uses to run offline evaluations and test prompts in the playground, and set a daily limit per member to control costs.',
  'account.tokenLimits.saveErrorToast': 'Failed to save daily token limit. Try again or contact support.',
  'account.tokenLimits.saveSuccessToast': 'Daily token limit saved',
  'account.tokenLimits.serviceAccountName': 'Service account',
  'account.tokenLimits.todaysUsageBody':
    'Total input and output tokens used by your members today. Usage resets daily at 00:00 UTC.',
  'account.tokenLimits.todaysUsageHeading': "Today's usage",
  'account.tokenLimits.todaysUsageLoadError': "We couldn't load token usage. Refresh the page to try again.",
  'account.tokenLimits.tokensUsedSoFar': 'tokens used so far',
  'account.tokenLimits.unknownMemberName': 'Unknown member',
  'account.tokenLimits.usageByMemberHeading': 'Usage by member',
  'account.tokenLimits.valueInputLabel': 'Daily token limit per member value',
  'account.unlockEditsDialog.submit': 'Unlock',
  'account.unlockEditsDialog.title': 'Enter your password to unlock edits',
  'account.verificationRequiredBanner.body':
    'Verification email sent to <bold>{email}</bold>. Re-send or change your email address in your <profile>profile settings</profile>.',
  'agentcontrol.migrationBanner.body':
    "This flag is configured with AI instructions and model settings. Convert it to an AI config to use the latest <docs>AgentControl features</docs>. Your original flag won't be changed.",
  'agentcontrol.migrationBanner.createConfig': 'Create config',
  'agentcontrol.migrationTeaser.convertBody':
    'Convert this flag to an AgentControl config to take advantage of AI evaluations, metrics, and more.',
  'agentcontrol.migrationTeaser.convertCta': 'Create config',
  'agentcontrol.migrationTeaser.exploreBody': 'Built flags for AI? We built AgentControl for AI.',
  'agentcontrol.migrationTeaser.exploreCta': 'Explore AgentControl',
  'agentcontrol.page.title': 'AgentControl',
  'agentcontrolWelcome.listPage.title': 'AgentControl Welcome',
  'agentcontrolWelcome.page.breadcrumb': 'AgentControl Welcome',
  'agentcontrolWelcome.setupSdkPage.title': 'Set up SDK',
  'ai.codingAgents.header.agents': 'Cursor, Claude Code, Copilot, Codex',
  'ai.codingAgents.header.developers': '{count, plural, one {# developer} other {# developers}}',
  'ai.codingAgents.header.environment': 'Environment',
  'ai.codingAgents.header.export': 'Export · OTel / CSV',
  'ai.codingAgents.header.useDarkTheme': 'Use dark theme',
  'ai.codingAgents.header.useLightTheme': 'Use light theme',
  'ai.codingAgents.nav.access': 'Access',
  'ai.codingAgents.nav.businessImpact': 'Business impact',
  'ai.codingAgents.nav.developerView': 'Developer view',
  'ai.codingAgents.nav.overview': 'Overview',
  'ai.codingAgents.nav.peopleAndSegments': 'People & segments',
  'ai.codingAgents.nav.skillsAndFactory': 'Skills & factory',
  'ai.codingAgents.page.title': 'Coding agents',
  'ai.codingAgents.placeholder.body': 'Control how coding agents run in this project. This page is not ready yet.',
  'ai.evaluationsPage.anUnexpectedError': 'An unexpected error has occurred.',
  'ai.evaluationsPage.newEvaluation': 'New evaluation',
  'ai.evaluationsPage.runEvaluations': 'Run evaluations',
  'ai.evaluationsPage.title': 'Evaluations',
  'ai.evaluationsRunsPage.anUnexpectedError': 'An unexpected error has occurred.',
  'ai.evaluationsRunsPage.backToEvaluations': 'Back to evaluations',
  'ai.evaluationsRunsPage.evaluations': 'Evaluations',
  'ai.evaluationsRunsPage.results': 'Results',
  'ai.evaluationsRunsPage.title': 'Evaluation run not found',
  'ai.evaluationsRunsPage.title2': 'Evaluation run summary',
  'ai.generatedCard.poweredByAi': 'Powered by AI, content quality may vary.',
  'ai.generatedItem.createNew':
    'Based on your hypothesis, you should create a new {itemKind, select, metric {metric} other {flag}} called',
  'ai.generatedItem.fallbackBody':
    "If you'd like my help, try editing your hypothesis. You may also pick from the list of {itemKind, select, metric {metrics} other {flags}} below.",
  'ai.generatedItem.orCreateNew': 'or create a new {itemKind, select, metric {metric} other {flag}} called',
  'ai.generatedItem.suggestionsHeading': 'Based on your hypothesis, you could experiment on {items}:',
  'ai.generatedItem.suggestionsPlural': 'one of these {itemKind, select, metric {metrics} other {flags}}',
  'ai.generatedItem.suggestionsSingular': 'this {itemKind, select, metric {metric} other {flag}}',
  'ai.generatedItem.thinking': 'Thinking…',
  'ai.graphsListPage.createNewGraph': 'Create new graph',
  'ai.graphsListPage.somethingWentWrong2': 'Something went wrong',
  'ai.graphsListPage.title': 'Agent graphs',
  'ai.graphsPage.title': 'Agent graphs',
  'ai.landingPage.aiLanding': 'AI Landing',
  'ai.landingPage.title': 'AI',
  'ai.optimizeAgentConfigurationPage.breadcrumb': 'Configuration',
  'ai.optimizeAgentListPage.somethingWentWrong2': 'Something went wrong',
  'ai.optimizeAgentListPage.title': 'Agent optimization',
  'ai.optimizeAgentNewPage.breadcrumb': 'New optimization',
  'ai.optimizeAgentNewPage.title': 'New optimization | Agent optimization',
  'ai.optimizeAgentPage.breadcrumb': 'Agent optimization',
  'ai.optimizeAgentPage.somethingWentWrong2': 'Something went wrong',
  'ai.optimizeAgentRunsPage.breadcrumb': 'Results',
  'ai.playgroundsActivePage.title': 'Playgrounds | Active',
  'ai.playgroundsArchivedPage.title': 'Playgrounds | Archived',
  'ai.playgroundsPage.anUnexpectedError': 'An unexpected error has occurred.',
  'ai.playgroundsPage.breadcrumb': 'Playgrounds',
  'ai.playgroundsPage.failedToLoad4': 'Failed to load playgrounds. Try again',
  'ai.playgroundsPage.loadingEvaluation': 'Loading evaluation…',
  'ai.playgroundsPage.manageApiKeys': 'Manage API keys',
  'ai.playgroundsPage.tryAgain': 'Try again',
  'aiConfigs.acceptanceCriteria.accuracyThresholdTooltip':
    'Minimum passing threshold for the accuracy score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.acceptanceCriteria.addCriteria': 'Add criteria',
  'aiConfigs.acceptanceCriteria.addCriteriaLabel': 'Add acceptance criteria',
  'aiConfigs.acceptanceCriteria.answerRelevancyThresholdTooltip':
    'Minimum passing threshold for the answer relevancy score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.acceptanceCriteria.biasThresholdTooltip':
    'Maximum allowed bias score (0.0 to 1.0). Responses with bias scores above this threshold will be flagged. Default is 0.5.',
  'aiConfigs.acceptanceCriteria.contextualPrecisionThresholdTooltip':
    'Minimum passing threshold for the contextual precision score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.acceptanceCriteria.contextualRecallThresholdTooltip':
    'Minimum passing threshold for the contextual recall score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.acceptanceCriteria.contextualRelevancyThresholdTooltip':
    'Minimum passing threshold for the contextual relevancy score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.acceptanceCriteria.controlResponseHelp':
    'The ideal or reference response to compare against the actual output.',
  'aiConfigs.acceptanceCriteria.controlResponseLabel': 'Control response',
  'aiConfigs.acceptanceCriteria.controlResponsePlaceholder': 'Enter the control response',
  'aiConfigs.acceptanceCriteria.datasetExpectedOutputNotice': 'Expected responses are defined by the dataset.',
  'aiConfigs.acceptanceCriteria.evaluationCriteriaLabel': 'Evaluation criteria',
  'aiConfigs.acceptanceCriteria.expectedOutputLabel': 'Expected output',
  'aiConfigs.acceptanceCriteria.expectedOutputPlaceholder': 'Enter the expected or ideal response',
  'aiConfigs.acceptanceCriteria.expectedOutputPrecisionHelp':
    'The ideal or correct response that will be used to evaluate the retrieval context alignment.',
  'aiConfigs.acceptanceCriteria.expectedOutputRecallHelp':
    'The ideal or correct response that will be used to evaluate how well the retrieval context captures all relevant information.',
  'aiConfigs.acceptanceCriteria.expectedResponseLabel': 'Expected response',
  'aiConfigs.acceptanceCriteria.expectedResponsePlaceholder': 'Enter the expected response',
  'aiConfigs.acceptanceCriteria.factualityCriteriaHelp':
    'Define the specific criteria for evaluating the factuality of the response. This will be used by G-EVAL to assess the output.',
  'aiConfigs.acceptanceCriteria.factualityCriteriaPlaceholder':
    "Enter the criteria for evaluating factuality (e.g., 'Evaluate if the response contains factual inaccuracies or contradictions')",
  'aiConfigs.acceptanceCriteria.factualityThresholdTooltip':
    'Minimum passing threshold for the factuality score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.acceptanceCriteria.faithfulnessThresholdTooltip':
    'Minimum passing threshold for the faithfulness score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.acceptanceCriteria.groundTruthHelp':
    'The verified or ground truth information that will be used to detect misinformation in the response.',
  'aiConfigs.acceptanceCriteria.groundTruthLabel': 'Ground truth context',
  'aiConfigs.acceptanceCriteria.groundTruthPlaceholder':
    'Enter the ground truth or verified information to compare against',
  'aiConfigs.acceptanceCriteria.likenessCriteriaHelp':
    'Define the specific criteria for evaluating the likeness between the response and control response. This will be used by G-EVAL to assess the output.',
  'aiConfigs.acceptanceCriteria.likenessCriteriaPlaceholder':
    "Enter the criteria for evaluating likeness (e.g., 'Evaluate the semantic similarity and structural alignment between the response and control response')",
  'aiConfigs.acceptanceCriteria.likenessThresholdTooltip':
    'Minimum passing threshold for the likeness score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.acceptanceCriteria.minimumPassRate': 'Minimum pass rate',
  'aiConfigs.acceptanceCriteria.misinformationThresholdTooltip':
    'Maximum allowed misinformation score (0.0 to 1.0). Responses with misinformation scores above this threshold will be flagged. Default is 0.5.',
  'aiConfigs.acceptanceCriteria.passRateInfoLabel': 'About minimum pass rate',
  'aiConfigs.acceptanceCriteria.passRateTooltip':
    'The minimum percentage of dataset rows that must pass this criteria for the overall evaluation to be considered successful.',
  'aiConfigs.acceptanceCriteria.passWhenAtLeast': 'Pass when score ≥',
  'aiConfigs.acceptanceCriteria.passWhenAtMost': 'Pass when score ≤',
  'aiConfigs.acceptanceCriteria.penalizeAmbiguousClaims': 'Penalize ambiguous claims',
  'aiConfigs.acceptanceCriteria.penalizeAmbiguousHelp':
    'When enabled, ambiguous claims will not be counted as faithful, resulting in a stricter evaluation.',
  'aiConfigs.acceptanceCriteria.protectedAttributesHelp':
    'The protected attributes or demographic groups to evaluate for bias in the response.',
  'aiConfigs.acceptanceCriteria.protectedAttributesLabel': 'Protected attributes',
  'aiConfigs.acceptanceCriteria.protectedAttributesPlaceholder':
    'Enter protected attributes to check for bias (e.g., race, gender, age, religion)',
  'aiConfigs.acceptanceCriteria.removeCriterionLabel': 'Remove {name}',
  'aiConfigs.acceptanceCriteria.retrievalContextAlignmentHelp':
    'The context retrieved from your RAG pipeline that will be used to evaluate factual alignment with the response.',
  'aiConfigs.acceptanceCriteria.retrievalContextComparePlaceholder': 'Enter the retrieval context to compare against',
  'aiConfigs.acceptanceCriteria.retrievalContextLabel': 'Retrieval context',
  'aiConfigs.acceptanceCriteria.retrievalContextPlaceholder': 'Enter the retrieval context from your RAG pipeline',
  'aiConfigs.acceptanceCriteria.retrievalContextPrecisionHelp':
    'The context retrieved from your RAG pipeline that will be evaluated for ranking quality.',
  'aiConfigs.acceptanceCriteria.retrievalContextRecallHelp':
    'The context retrieved from your RAG pipeline that will be evaluated for completeness and relevance.',
  'aiConfigs.acceptanceCriteria.retrievalContextRelevancyHelp':
    'The context retrieved from your RAG pipeline that will be evaluated for overall relevance to the input query.',
  'aiConfigs.acceptanceCriteria.scoreThresholdLabel': 'Score threshold',
  'aiConfigs.acceptanceCriteria.strictMode': 'Strict mode',
  'aiConfigs.acceptanceCriteria.strictModeHelp':
    'When enabled, enforces a binary metric score: 1 for perfection, 0 otherwise. This also sets the threshold to 1.',
  'aiConfigs.acceptanceCriteria.thresholdInfoLabel': 'Threshold details',
  'aiConfigs.acceptanceCriteria.thresholdLabel': 'Threshold',
  'aiConfigs.acceptanceCriteria.thresholdLenient': 'Lenient',
  'aiConfigs.acceptanceCriteria.thresholdStrict': 'Strict',
  'aiConfigs.acceptanceCriteria.toxicityThresholdTooltip':
    'Maximum allowed toxicity score (0.0 to 1.0). Responses with toxicity scores above this threshold will be flagged. Default is 0.5.',
  'aiConfigs.acceptanceCriteria.truthsLimitHelp':
    'Maximum number of factual truths to extract from the retrieval context. The truths will be ordered by importance. Leave empty for no limit.',
  'aiConfigs.acceptanceCriteria.truthsLimitLabel': 'Truths extraction limit',
  'aiConfigs.acceptanceCriteria.truthsLimitPlaceholder': 'No limit',
  'aiConfigs.accuracyCriteria.expectedResponseLabel': 'Expected response',
  'aiConfigs.accuracyCriteria.expectedResponsePlaceholder': 'Enter the expected response',
  'aiConfigs.accuracyCriteria.thresholdDescription':
    'Minimum passing threshold for the accuracy score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.addVariation.addAnotherVariation': 'Add another variation',
  'aiConfigs.addVariation.addLabel': 'Add new variation',
  'aiConfigs.agentControlOptIn.bodyStart':
    'Agent behavior shifts in production without warning. Evaluate and improve your agents in real time, automatically, without redeploys or guesswork.',
  'aiConfigs.agentControlOptIn.contactSales': 'Contact sales',
  'aiConfigs.agentControlOptIn.docsLink': 'Read about AgentControl',
  'aiConfigs.agentControlOptIn.enable': 'Enable AgentControl',
  'aiConfigs.agentControlOptIn.enableErrorToast': 'Something went wrong. Try again or contact support.',
  'aiConfigs.agentControlOptIn.enabledToast': 'AgentControl enabled',
  'aiConfigs.agentControlOptIn.enterpriseContactSentence':
    '<contactSales></contactSales> to add more AI runs to your plan.',
  'aiConfigs.agentControlOptIn.enterprisePricingNote': 'Get 5,000 AI runs per month for free.',
  'aiConfigs.agentControlOptIn.heading': 'Control your agents. In production.',
  'aiConfigs.agentControlOptIn.heroAlt': 'AgentControl',
  'aiConfigs.agentControlOptIn.noPermissionTooltip':
    "You don't have permission to enable AgentControl. Contact your admin to request access.",
  'aiConfigs.agentControlOptIn.pricingNote':
    'Your first 5,000 AI runs/month are free; extra usage is $5 per 1,000 runs.',
  'aiConfigs.agentGraphActionsMenu.copyKey': 'Copy key',
  'aiConfigs.agentGraphActionsMenu.deleteGraph': 'Delete graph',
  'aiConfigs.agentGraphActionsMenu.keyCopied': 'Copied to clipboard',
  'aiConfigs.agentGraphActionsMenu.moreActionsLabel': 'More actions',
  'aiConfigs.agentGraphAgentPicker.addNewAgent': 'Add new agent',
  'aiConfigs.agentGraphAgentPicker.searchLabel': 'Search agents…',
  'aiConfigs.agentGraphAgentPicker.searchPlaceholder': 'Search agents…',
  'aiConfigs.agentGraphAgentStep.agentCreateFailed': 'Failed to create agent',
  'aiConfigs.agentGraphAgentStep.agentCreatedSuccessfully': 'Agent created successfully',
  'aiConfigs.agentGraphAgentStep.basedOnYour':
    'Based on your selected provider, we recommend using the following model:',
  'aiConfigs.agentGraphAgentStep.chooseAView': 'Choose a view to continue',
  'aiConfigs.agentGraphAgentStep.closeSuggestionLabel': 'Close suggestion',
  'aiConfigs.agentGraphAgentStep.createAgent': 'Create agent',
  'aiConfigs.agentGraphAgentStep.instructionsPlaceholder': 'Enter the instructions for your agent',
  'aiConfigs.agentGraphAgentStep.lastStepGive': 'Last step. Give your agent a name and select Create agent.',
  'aiConfigs.agentGraphAgentStep.namePlaceholder': 'Enter the name for your agent',
  'aiConfigs.agentGraphAgentStep.nextProvideThe':
    'Next provide the instructions for your agent. This will be the goal or task that the agent will complete.',
  'aiConfigs.agentGraphAgentStep.nextWeLl':
    'Next, select the model for your agent. Use a model with strong reasoning capabilities for complex tasks.',
  'aiConfigs.agentGraphAgentStep.step1': 'Step 1.',
  'aiConfigs.agentGraphAgentStep.step2': 'Step 2.',
  'aiConfigs.agentGraphAgentStep.step3': 'Step 3.',
  'aiConfigs.agentGraphAgentStep.step4': 'Step 4.',
  'aiConfigs.agentGraphAgentStep.toCreateAn':
    'To create an agent graph, we first need to create an agent to serve as the root of the graph. First, select the provider for the model you want to use:',
  'aiConfigs.agentGraphAgentStep.useThisModel': 'Use this model',
  'aiConfigs.agentGraphAgentStep.viewAgent': 'View agent',
  'aiConfigs.agentGraphAgentStep.viewRequired': 'View required',
  'aiConfigs.agentGraphCanvas.addYourFirst': 'Add your first agent',
  'aiConfigs.agentGraphCanvas.closeLabel': 'Close',
  'aiConfigs.agentGraphCanvas.discard': 'Discard',
  'aiConfigs.agentGraphCanvas.docsLinkText': 'Read about agent graphs',
  'aiConfigs.agentGraphCanvas.editDescriptionLabel': 'Edit description',
  'aiConfigs.agentGraphCanvas.editNameLabel': 'Edit name',
  'aiConfigs.agentGraphCanvas.enterDescription': 'Enter description',
  'aiConfigs.agentGraphCanvas.everyNodeIs':
    'Every node is an agent config. Your first node is the root, where the workflow starts. Connect a node to another agent to hand off work.',
  'aiConfigs.agentGraphCanvas.graph': 'Graph',
  'aiConfigs.agentGraphCanvas.graphJson': 'Graph JSON',
  'aiConfigs.agentGraphCanvas.graphUpdateFailed': 'Failed to update agent graph. Try again.',
  'aiConfigs.agentGraphCanvas.graphUpdated': 'Agent graph updated',
  'aiConfigs.agentGraphCanvas.handoffInvalidJson': 'Handoff data is not valid JSON',
  'aiConfigs.agentGraphCanvas.invalidGraph': 'Invalid agent graph',
  'aiConfigs.agentGraphCanvas.invalidGraphDescription': 'Check the agent graph for errors',
  'aiConfigs.agentGraphCanvas.jsonEditorLabel': 'Graph JSON representation',
  'aiConfigs.agentGraphCanvas.keyCopied': 'Copied to clipboard',
  'aiConfigs.agentGraphCanvas.monitoring': 'Monitoring',
  'aiConfigs.agentGraphCanvas.nodeAgentRequired': 'You must select an agent to continue',
  'aiConfigs.agentGraphCanvas.nodeNotConnected': 'This node is not connected',
  'aiConfigs.agentGraphCanvas.rootNodeNotFound': 'Root node not found',
  'aiConfigs.agentGraphCanvas.save': 'Save',
  'aiConfigs.agentGraphCreateModal.aGraphConnects':
    'A graph connects agent configs into a workflow. Each node is an agent config, and each connection passes work from one agent to the next.',
  'aiConfigs.agentGraphCreateModal.cancel': 'Cancel',
  'aiConfigs.agentGraphCreateModal.closeLabel': 'Close',
  'aiConfigs.agentGraphCreateModal.create': 'Create',
  'aiConfigs.agentGraphCreateModal.createNewGraph': 'Create new graph',
  'aiConfigs.agentGraphCreateModal.description': 'Description',
  'aiConfigs.agentGraphCreateModal.docsLinkText': 'Read about agent graphs',
  'aiConfigs.agentGraphCreateModal.editKey': 'Edit key',
  'aiConfigs.agentGraphCreateModal.key': 'Key',
  'aiConfigs.agentGraphCreateModal.keyLabel': 'Graph key',
  'aiConfigs.agentGraphCreateModal.keyPlaceholder': 'Enter Graph key',
  'aiConfigs.agentGraphCreateModal.name': 'Name',
  'aiConfigs.agentGraphDetail.connectAppPrompt': 'Connect your app to get started with AgentControl.',
  'aiConfigs.agentGraphDetail.copyKeyTooltip': 'Copy agent graph key',
  'aiConfigs.agentGraphDetail.copyLinkTooltip': 'Copy agent graph link',
  'aiConfigs.agentGraphDetail.deleteAgentGraph': 'Delete agent graph',
  'aiConfigs.agentGraphDetail.entityActionLabel': 'entity action',
  'aiConfigs.agentGraphDetail.graphDeleteFailed': 'Failed to delete graph',
  'aiConfigs.agentGraphDetail.graphDeleted': 'Graph deleted',
  'aiConfigs.agentGraphDetail.viewJsonRepresentation': 'View JSON representation',
  'aiConfigs.agentGraphDetailsSidebar.addDescription': '(Add description)',
  'aiConfigs.agentGraphDetailsSidebar.descriptionHeading': 'Description',
  'aiConfigs.agentGraphDetailsSidebar.descriptionInputLabel': 'Config description',
  'aiConfigs.agentGraphDetailsSidebar.descriptionPlaceholder': 'Enter a description',
  'aiConfigs.agentGraphDetailsSidebar.documentation': 'Documentation',
  'aiConfigs.agentGraphDetailsSidebar.editDescriptionLabel': 'Edit description',
  'aiConfigs.agentGraphDetailsSidebar.editNameLabel': 'Edit name',
  'aiConfigs.agentGraphDetailsSidebar.key': 'Key',
  'aiConfigs.agentGraphDetailsSidebar.name': 'Name',
  'aiConfigs.agentGraphDetailsSidebar.nameInputLabel': 'Config name',
  'aiConfigs.agentGraphDetailsSidebar.quickstartLinkText': 'Quickstart guide',
  'aiConfigs.agentGraphDetailsSidebar.saveDescriptionLabel': 'Save description',
  'aiConfigs.agentGraphDetailsSidebar.saveNameLabel': 'Save name',
  'aiConfigs.agentGraphGetStarted.buildAVisual':
    'Build a visual workflow that defines how you orchestrate your agents.',
  'aiConfigs.agentGraphGetStarted.createYourFirst': 'Create your first agent graph',
  'aiConfigs.agentGraphGetStarted.docsLinkText': 'Read more about agent graphs',
  'aiConfigs.agentGraphGetStarted.openStepLabel': 'Open step',
  'aiConfigs.agentGraphGraphStep.1ChooseThe': '1. Choose the agent for the root of the graph',
  'aiConfigs.agentGraphGraphStep.2GiveYour': '2. Give your graph a name and optional description.',
  'aiConfigs.agentGraphGraphStep.agent': 'Agent',
  'aiConfigs.agentGraphGraphStep.createGraph': 'Create Graph',
  'aiConfigs.agentGraphGraphStep.description': 'Description',
  'aiConfigs.agentGraphGraphStep.descriptionPlaceholder': 'Enter the description of your graph',
  'aiConfigs.agentGraphGraphStep.graphCreateFailed': 'Failed to create agent graph',
  'aiConfigs.agentGraphGraphStep.graphName': 'Graph name',
  'aiConfigs.agentGraphGraphStep.namePlaceholder': 'Enter the name of your graph',
  'aiConfigs.agentGraphGraphStep.searchAgentsLabel': 'Search agents…',
  'aiConfigs.agentGraphGraphStep.selectAnAgent': 'Select an agent',
  'aiConfigs.agentGraphHandoff.deleteEdgeLabel': 'Delete edge',
  'aiConfigs.agentGraphHandoff.edit': 'Edit',
  'aiConfigs.agentGraphHandoff.handoffData': 'Handoff data',
  'aiConfigs.agentGraphHandoff.invalidJson': 'Invalid JSON',
  'aiConfigs.agentGraphHandoff.jsonPlaceholder': 'Enter JSON',
  'aiConfigs.agentGraphHandoff.readMoreAbout': 'Read more about handoffs',
  'aiConfigs.agentGraphHandoff.save': 'Save',
  'aiConfigs.agentGraphHandoff.specifyAnyHandoff': 'Specify any handoff data from the parent node to the child node.',
  'aiConfigs.agentGraphHandoff.theDataFor': 'The data for handoff should be valid JSON.',
  'aiConfigs.agentGraphMonitoring.aggregateMetricsFor':
    'Aggregate metrics for the entire agent graph across all nodes.',
  'aiConfigs.agentGraphMonitoring.avgDuration': 'Avg. duration',
  'aiConfigs.agentGraphMonitoring.avgTokensChartTitle': 'Avg. tokens',
  'aiConfigs.agentGraphMonitoring.avgToolCalls': 'Avg. tool calls',
  'aiConfigs.agentGraphMonitoring.durationLabel': 'Duration',
  'aiConfigs.agentGraphMonitoring.errorRateChartTitle': 'Error rate',
  'aiConfigs.agentGraphMonitoring.globalMetrics': 'Global metrics',
  'aiConfigs.agentGraphMonitoring.globalValues': 'Global values',
  'aiConfigs.agentGraphMonitoring.invocationsByNodeChartTitle': 'Invocations by node',
  'aiConfigs.agentGraphMonitoring.invocationsChartTitle': 'Invocations',
  'aiConfigs.agentGraphMonitoring.latencyByNodeChartTitle': 'Latency by node',
  'aiConfigs.agentGraphMonitoring.metricsBrokenDown':
    'Metrics broken down by agent node (config). Each line represents a node in the graph.',
  'aiConfigs.agentGraphMonitoring.metricsLoadFailed': 'Failed to fetch agent graph metrics',
  'aiConfigs.agentGraphMonitoring.metricsLoadFailedDescription': 'Try again',
  'aiConfigs.agentGraphMonitoring.nodeLevelMetrics': 'Node-level metrics',
  'aiConfigs.agentGraphMonitoring.requestDurationChartTitle': 'Request duration',
  'aiConfigs.agentGraphMonitoring.toolCalls': 'Tool calls',
  'aiConfigs.agentGraphMonitoring.toolCallsByNodeChartTitle': 'Tool calls by node',
  'aiConfigs.agentGraphNode.allVariations': 'All variations',
  'aiConfigs.agentGraphNode.deleteAgentLabel': 'Delete agent',
  'aiConfigs.agentGraphNode.deleteNodeLabel': 'Delete node',
  'aiConfigs.agentGraphNode.hideTools': 'Hide tools',
  'aiConfigs.agentGraphNode.infoLabel': 'Info',
  'aiConfigs.agentGraphNode.selectAgentPlaceholder': 'Select an agent…',
  'aiConfigs.agentGraphNode.selectVariationLabel': 'Select variation',
  'aiConfigs.agentGraphNode.thisAgentIs':
    'This agent is disabled in this environment. Including it disables this graph in this environment.',
  'aiConfigs.agentGraphNode.toolsLabel': 'Tools',
  'aiConfigs.agentGraphNode.viewAiConfig': 'View AI config',
  'aiConfigs.agentGraphNodeMetrics.avgDuration': 'Avg. duration',
  'aiConfigs.agentGraphNodeMetrics.avgToolCalls': 'Avg. tool calls',
  'aiConfigs.agentGraphNodeMetrics.avgTotalTokens': 'Avg. total tokens',
  'aiConfigs.agentGraphNodeMetrics.errorRate': 'Error rate',
  'aiConfigs.agentGraphNodeMetrics.input': 'Input',
  'aiConfigs.agentGraphNodeMetrics.invocations': 'Invocations',
  'aiConfigs.agentGraphNodeMetrics.output': 'Output',
  'aiConfigs.agentGraphNodeMetrics.toolCallsMade': 'Tool calls made',
  'aiConfigs.agentGraphsList.connectAppPrompt': 'Connect your app to get started with AgentControl.',
  'aiConfigs.agentGraphsList.graphDeleteFailed': 'Failed to delete graph',
  'aiConfigs.agentGraphsList.graphDeleted': 'Graph deleted',
  'aiConfigs.agentGraphsList.keyCopied': 'Copied to clipboard',
  'aiConfigs.agentGraphsList.listLabel': 'Config list',
  'aiConfigs.agentGraphsList.noGraphsMatch': 'No graphs match your search.',
  'aiConfigs.agentGraphsList.searchLabel': 'Search agent graphs by name, description or key',
  'aiConfigs.agentGraphsList.searchPlaceholder': 'Search fields by name, description or key',
  'aiConfigs.agentVariationCard.addCustomModelAction': 'Add custom model',
  'aiConfigs.agentVariationCard.addToolsAction': 'Add tools',
  'aiConfigs.agentVariationCard.atLeastOneVariationError': 'At least one variation is required',
  'aiConfigs.agentVariationCard.collapseLabel': 'Collapse',
  'aiConfigs.agentVariationCard.createVariationsError': 'Error creating variations',
  'aiConfigs.agentVariationCard.deleteVariationError': 'Error deleting variation',
  'aiConfigs.agentVariationCard.discardChanges': 'Discard changes',
  'aiConfigs.agentVariationCard.draft': 'Draft',
  'aiConfigs.agentVariationCard.duplicateNameError': 'All variation names must be unique',
  'aiConfigs.agentVariationCard.goalOrTask': 'Goal or task',
  'aiConfigs.agentVariationCard.instructionsPlaceholder': 'Enter a goal or task',
  'aiConfigs.agentVariationCard.instructionsRequiredError': 'Instructions are required',
  'aiConfigs.agentVariationCard.judgesTitle': 'Judges',
  'aiConfigs.agentVariationCard.modelConfigurationTitle': 'Model configuration',
  'aiConfigs.agentVariationCard.modelRequiredError': 'Model selection is required',
  'aiConfigs.agentVariationCard.nameLabel': 'Variation name',
  'aiConfigs.agentVariationCard.namePlaceholder': 'Untitled variation*',
  'aiConfigs.agentVariationCard.nameRequiredError': 'Name is required',
  'aiConfigs.agentVariationCard.nameTooLongError': 'Name length must not exceed 256 characters',
  'aiConfigs.agentVariationCard.toolsTitle': 'Tools',
  'aiConfigs.agentVariationCard.updateAgentError': 'Error updating agent',
  'aiConfigs.agentVariationCard.updateVariationError': 'Error updating variation',
  'aiConfigs.agentVariationCard.variationDeleted': 'Variation deleted successfully',
  'aiConfigs.agentVariationCard.variationNameRequiredError': 'Agent variation name is required',
  'aiConfigs.agentVariationCard.variationUpdated': 'Variation updated successfully',
  'aiConfigs.agentVariationCard.variationsCreated':
    '{count, plural, one {Variation} other {Variations}} created successfully',
  'aiConfigs.agentVariationCard.variationsUpdated':
    '{count, plural, one {Variation} other {Variations}} updated successfully',
  'aiConfigs.agentVariationCard.versionHistoryLabel': 'View version history',
  'aiConfigs.aiLibrary.maintainerLoading': 'Loading member',
  'aiConfigs.aiLibraryBreadcrumb.datasetName': '{name} (v{version})',
  'aiConfigs.aiLibraryBreadcrumb.newTool': 'New tool',
  'aiConfigs.aiLibraryNav.datasets': 'Datasets',
  'aiConfigs.aiLibraryNav.judges': 'Judges',
  'aiConfigs.aiLibraryNav.models': 'Models',
  'aiConfigs.aiLibraryNav.skills': 'Skills',
  'aiConfigs.aiLibraryNav.snippets': 'Snippets',
  'aiConfigs.aiLibraryNav.tools': 'Tools',
  'aiConfigs.alignmentCriteria.penalizeAmbiguousDescription':
    'When enabled, ambiguous claims will not be counted as faithful, resulting in a stricter evaluation.',
  'aiConfigs.alignmentCriteria.penalizeAmbiguousLabel': 'Penalize ambiguous claims',
  'aiConfigs.alignmentCriteria.retrievalContextDescription':
    'The context retrieved from your RAG pipeline that will be used to evaluate factual alignment with the response.',
  'aiConfigs.alignmentCriteria.retrievalContextLabel': 'Retrieval context',
  'aiConfigs.alignmentCriteria.retrievalContextPlaceholder': 'Enter the retrieval context to compare against',
  'aiConfigs.alignmentCriteria.strictModeDescription':
    'When enabled, enforces a binary metric score: 1 for perfection, 0 otherwise. This also sets the threshold to 1.',
  'aiConfigs.alignmentCriteria.strictModeLabel': 'Strict mode',
  'aiConfigs.alignmentCriteria.thresholdDescription':
    'Minimum passing threshold for the faithfulness score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.alignmentCriteria.truthsLimitDescription':
    'Maximum number of factual truths to extract from the retrieval context. The truths will be ordered by importance. Leave empty for no limit.',
  'aiConfigs.alignmentCriteria.truthsLimitLabel': 'Truths extraction limit',
  'aiConfigs.alignmentCriteria.truthsLimitPlaceholder': 'No limit',
  'aiConfigs.answerRelevancyCriteria.thresholdDescription':
    'Minimum passing threshold for the answer relevancy score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.apiKeySetup.aiProvider': 'AI provider',
  'aiConfigs.apiKeySetup.anthropicDocsLinkText': 'Get your Anthropic API key',
  'aiConfigs.apiKeySetup.apiKey': 'API key',
  'aiConfigs.apiKeySetup.apiKeyBody': 'Add your API key to start running prompts.',
  'aiConfigs.apiKeySetup.apiKeyPlaceholder': 'Enter your API key',
  'aiConfigs.apiKeySetup.awsRegion': 'AWS region',
  'aiConfigs.apiKeySetup.awsRoleArn': 'AWS role ARN',
  'aiConfigs.apiKeySetup.bedrockBody': 'Add your AWS role details to start running prompts.',
  'aiConfigs.apiKeySetup.bedrockDocsLinkText': 'Read about connecting Amazon Bedrock',
  'aiConfigs.apiKeySetup.connect': 'Connect',
  'aiConfigs.apiKeySetup.docsLinkText': 'Read about supported providers',
  'aiConfigs.apiKeySetup.dontSeeProvider': 'Provider not listed?',
  'aiConfigs.apiKeySetup.externalId': 'External ID',
  'aiConfigs.apiKeySetup.externalIdDescription': 'Use this external ID when you create the IAM role in AWS.',
  'aiConfigs.apiKeySetup.heading': 'Connect your AI provider',
  'aiConfigs.apiKeySetup.openAiDocsLinkText': 'Get your OpenAI API key',
  'aiConfigs.apiKeySetup.providerDocsLinkText': 'Get your {provider} API key',
  'aiConfigs.apiKeySetup.providerListLabel': 'Select AI provider',
  'aiConfigs.apiKeySetup.regionPlaceholder': 'Select an AWS region',
  'aiConfigs.apiKeySetup.retryBody': 'Try again.',
  'aiConfigs.apiKeySetup.saveErrorTitle':
    '{kind, select, bedrock {Failed to save configuration} other {Failed to save API key}}',
  'aiConfigs.apiKeySetup.savedToastBody':
    'A different {provider} {kind, select, bedrock {configuration} other {API key}} is currently enabled.',
  'aiConfigs.apiKeySetup.savedToastTitle': '{kind, select, bedrock {Configuration saved} other {API key saved}}',
  'aiConfigs.apiKeySetupBanner.body': 'Connect your model provider to start running prompts.',
  'aiConfigs.apiKeySetupBanner.heading': 'Set up your API key',
  'aiConfigs.apiKeySetupBanner.setUpApiKey': 'Set up API key',
  'aiConfigs.apiTokenDialog.cancel': 'Cancel',
  'aiConfigs.apiTokenDialog.closeLabel': 'Close dialog',
  'aiConfigs.apiTokenDialog.createErrorTitle': 'Failed to create API token',
  'aiConfigs.apiTokenDialog.createToken': 'Create token',
  'aiConfigs.apiTokenDialog.createdHeading': 'Evaluations token created',
  'aiConfigs.apiTokenDialog.doneLabel': 'Done',
  'aiConfigs.apiTokenDialog.editLaterBody': 'After you create it, you can edit it in Settings → Authorization.',
  'aiConfigs.apiTokenDialog.heading': 'Create API token',
  'aiConfigs.apiTokenDialog.projectScopedBody':
    'Create a project-scoped API token for running evaluations from code. This token will have the permissions needed for Evaluations within this project.',
  'aiConfigs.apiTokenDialog.readMoreText': 'Read more.',
  'aiConfigs.apiTokenDialog.serviceTokenBody': 'Recommended for CI and automation. Permissions are locked at creation.',
  'aiConfigs.apiTokenDialog.serviceTokenHeading': 'This is a service token',
  'aiConfigs.apiTokenDialog.trigger': 'Create API token',
  'aiConfigs.apiTokenDialog.writerRoleBody':
    'Create an API token for running evaluations from code. Project-scoped token permissions are unavailable on your plan, so this token will use the Writer role. It can write to everything in your account.',
  'aiConfigs.appliedInstruction.appliedChange': 'Applied change',
  'aiConfigs.appliedInstruction.instructionsTitle': 'Instructions',
  'aiConfigs.appliedInstruction.judgeConfigTitle': 'Judge configuration',
  'aiConfigs.appliedInstruction.messagesTitle': 'Messages',
  'aiConfigs.appliedInstruction.modelConfigTitle': 'Model config',
  'aiConfigs.appliedInstruction.toolsTitle': 'Tools',
  'aiConfigs.approvalInstruction.addVariation': 'Add variation',
  'aiConfigs.approvalInstruction.conflictBody':
    'These instructions will fail and prevent the change request from being applied',
  'aiConfigs.approvalInstruction.instructionsTitle': 'Instructions',
  'aiConfigs.approvalInstruction.judgeConfigTitle': 'Judge configuration',
  'aiConfigs.approvalInstruction.loadError': 'Unable to load AI Config',
  'aiConfigs.approvalInstruction.messagesTitle': 'Messages',
  'aiConfigs.approvalInstruction.modelConfigTitle': 'Model config',
  'aiConfigs.approvalInstruction.toolsTitle': 'Tools',
  'aiConfigs.approvalInstruction.updateVariation': 'Update variation',
  'aiConfigs.approvalRequestToast.createFailed': 'Failed to create approval request',
  'aiConfigs.approvalRequestToast.createFailedBody': 'Try again later',
  'aiConfigs.approvalRequestToast.requested': 'Approval requested successfully and reviewers have been notified',
  'aiConfigs.approvalRequestToast.viewApprovalRequest': 'View approval request',
  'aiConfigs.approvalToast.requested': 'Approval requested successfully and reviewers have been notified',
  'aiConfigs.approvalToast.viewRequest': 'View approval request',
  'aiConfigs.archivedVariations.heading': 'There are no archived variations',
  'aiConfigs.archivedVariations.viewPublished': 'View published variations',
  'aiConfigs.attachTools.attachTools': 'Attach tools',
  'aiConfigs.attachTools.createNewTool': 'Create new tool',
  'aiConfigs.attachTools.exploreTools': 'Explore tools',
  'aiConfigs.attachTools.newerVersionAvailable': 'This tool has a newer version available',
  'aiConfigs.attachTools.quickFind': 'Quick find',
  'aiConfigs.attachTools.removeToolLabel': 'Remove tool',
  'aiConfigs.attachTools.searchPlaceholder': 'Search',
  'aiConfigs.attachTools.viewToolDetailsLabel': 'View tool details for {key}',
  'aiConfigs.attachedJudges.eventKey': 'Event key',
  'aiConfigs.attachedJudges.judgeNotFound': 'Judge not found',
  'aiConfigs.attachedJudges.loadingJudgeLabel': 'Loading judge details…',
  'aiConfigs.attachedJudges.name': 'Name',
  'aiConfigs.attachedJudges.noEventKey': 'No event key',
  'aiConfigs.attachedJudges.provider': 'Provider',
  'aiConfigs.attachedJudges.removeJudgeLabel': 'Remove judge',
  'aiConfigs.attachedJudges.samplingPercentage': 'Sampling percentage',
  'aiConfigs.attachedJudges.samplingRateLabel': 'Sampling rate for {judgeName}. {tooltip}',
  'aiConfigs.attachedJudges.tableLabel': 'Attached judges',
  'aiConfigs.biasCriteria.protectedAttributesDescription':
    'The protected attributes or demographic groups to evaluate for bias in the response.',
  'aiConfigs.biasCriteria.protectedAttributesLabel': 'Protected attributes',
  'aiConfigs.biasCriteria.protectedAttributesPlaceholder':
    'Enter protected attributes to check for bias (e.g., race, gender, age, religion)',
  'aiConfigs.biasCriteria.thresholdDescription':
    'Maximum allowed bias score (0.0 to 1.0). Responses with bias scores above this threshold will be flagged. Default is 0.5.',
  'aiConfigs.bulkVersionApply.allPending':
    '{count, plural, one {# usage has} other {# usages have}} a pending approval request already; {usageFor} otherwise unchanged.',
  'aiConfigs.bulkVersionApply.appliedAndPendingApproval':
    '{usageFor}: applied on {updated, plural, one {# config} other {# configs}}; {approval, plural, one {# config will} other {# configs will}} update once approved.',
  'aiConfigs.bulkVersionApply.appliedTo': 'Applied to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.bulkVersionApply.applyChanges': 'Apply changes',
  'aiConfigs.bulkVersionApply.applyConjunction': 'Apply &',
  'aiConfigs.bulkVersionApply.applyVerb': 'Apply',
  'aiConfigs.bulkVersionApply.applyingAndRequesting':
    'Applying changes to {direct, plural, one {# config} other {# configs}} and requesting {approval, plural, one {# approval} other {# approvals}}.',
  'aiConfigs.bulkVersionApply.applyingChanges': 'Applying changes…',
  'aiConfigs.bulkVersionApply.applyingToConfigs':
    'Applying changes to {count, plural, one {# config} other {# configs}}.',
  'aiConfigs.bulkVersionApply.done': 'Done',
  'aiConfigs.bulkVersionApply.networkErrorBody': 'Check your connection and try again.',
  'aiConfigs.bulkVersionApply.networkErrorPartialBody':
    'LaunchDarkly updated some configs before the error. Check your connection and try again for the rest.',
  'aiConfigs.bulkVersionApply.networkErrorPartialTitle': 'Could not finish applying {resourceLabel} version changes',
  'aiConfigs.bulkVersionApply.networkErrorTitle': 'Could not apply {resourceLabel} version changes',
  'aiConfigs.bulkVersionApply.partialFailureBody': 'Review the notification list or try again.',
  'aiConfigs.bulkVersionApply.partialFailureBodyAfterSuccess':
    'Earlier version updates succeeded; remaining updates failed or were skipped. Review or try again.',
  'aiConfigs.bulkVersionApply.partialFailureTitle': 'Some usages were not updated',
  'aiConfigs.bulkVersionApply.processed': '{usageFor}: processed.',
  'aiConfigs.bulkVersionApply.requestApprovalFor':
    'Request approval for {count, plural, one {# config} other {# configs}}',
  'aiConfigs.bulkVersionApply.requestedApprovals': 'Requested {count, plural, one {# approval} other {# approvals}}',
  'aiConfigs.bulkVersionApply.requestingApprovalCount':
    'Requesting {count, plural, one {# approval} other {# approvals}}.',
  'aiConfigs.bulkVersionApply.requestingApprovals': 'Requesting {count, plural, one {approval} other {approvals}}…',
  'aiConfigs.bulkVersionApply.save': 'Save',
  'aiConfigs.bulkVersionApply.saveConjunction': 'Save &',
  'aiConfigs.bulkVersionApply.saveVerb': 'Save',
  'aiConfigs.bulkVersionApply.savingSnippetVersion': 'Saving snippet version…',
  'aiConfigs.bulkVersionApply.skippedPending':
    'Skipped {count, plural, one {# config} other {# configs}} (pending approval)',
  'aiConfigs.bulkVersionApply.snippetResourceLabel': 'Snippet',
  'aiConfigs.bulkVersionApply.snippetResourceLabelLower': 'snippet',
  'aiConfigs.bulkVersionApply.updated': '{usageFor}: updated on {count, plural, one {# config} other {# configs}}.',
  'aiConfigs.bulkVersionApply.updatedAndSkipped':
    '{usageFor}: updated on {updated, plural, one {# config} other {# configs}}. Skipped {pending, plural, one {# config} other {# configs}} with a pending approval.',
  'aiConfigs.bulkVersionApply.usageFor': '{resourceLabel} usage for "{name}"',
  'aiConfigs.bulkVersionApply.usageUpdated': 'Usage updated',
  'aiConfigs.bulkVersionApply.verbAndRequestApprovals':
    '{conjunction} request {count, plural, one {# approval} other {# approvals}}',
  'aiConfigs.bulkVersionApply.verbToConfigs': '{verb} to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.bulkVersionApply.willUpdateOnceApproved':
    '{usageFor} will update on {count, plural, one {# config} other {# configs}} once approved.',
  'aiConfigs.classifierRules.addChoiceOption': 'Add option',
  'aiConfigs.classifierRules.addRule': 'Add rule',
  'aiConfigs.classifierRules.addScoreLevel': 'Add level',
  'aiConfigs.classifierRules.atLeastOneRule': 'Add at least one classifier rule',
  'aiConfigs.classifierRules.choiceOptionDescription': 'Description',
  'aiConfigs.classifierRules.choiceOptionKey': 'Option key',
  'aiConfigs.classifierRules.choiceOptionKeyRequired': 'Option key is required',
  'aiConfigs.classifierRules.choiceOptionsLabel': 'Options',
  'aiConfigs.classifierRules.classifiers': 'Classifier rules',
  'aiConfigs.classifierRules.eventKey': 'Event key: {eventKey}',
  'aiConfigs.classifierRules.eventKeyMissing': 'Set an event key on this judge to record metrics for this rule',
  'aiConfigs.classifierRules.instructionsLabel': 'Instructions',
  'aiConfigs.classifierRules.instructionsPlaceholder': 'What should this rule evaluate?',
  'aiConfigs.classifierRules.instructionsRequired': 'Instructions are required',
  'aiConfigs.classifierRules.keyInvalid': 'Use only letters, numbers, and hyphens',
  'aiConfigs.classifierRules.keyLabel': 'Key',
  'aiConfigs.classifierRules.keyNotUnique': 'Key must be unique',
  'aiConfigs.classifierRules.keyPlaceholder': 'accuracy',
  'aiConfigs.classifierRules.keyRequired': 'Key is required',
  'aiConfigs.classifierRules.messages': 'Messages',
  'aiConfigs.classifierRules.noDescription': 'No',
  'aiConfigs.classifierRules.noDescriptionPlaceholder': 'Describe a no answer',
  'aiConfigs.classifierRules.removeChoiceOption': 'Remove option',
  'aiConfigs.classifierRules.removeRule': 'Remove rule',
  'aiConfigs.classifierRules.removeScoreLevel': 'Remove level',
  'aiConfigs.classifierRules.ruleHeading': 'Rule {number}',
  'aiConfigs.classifierRules.scoreLevelCount': 'Use 2 to 10 score levels',
  'aiConfigs.classifierRules.scoreLevelPlaceholder': 'Level name',
  'aiConfigs.classifierRules.scoreLevelRequired': 'Level name is required',
  'aiConfigs.classifierRules.scoreLevelsLabel': 'Score levels',
  'aiConfigs.classifierRules.setupLabel': 'Variation setup',
  'aiConfigs.classifierRules.typeChoice': 'Choice',
  'aiConfigs.classifierRules.typeLabel': 'Type',
  'aiConfigs.classifierRules.typeSafeModelWarning':
    'Classifier rules require a classifier model. Select a classifier model.',
  'aiConfigs.classifierRules.typeScore': 'Score',
  'aiConfigs.classifierRules.typeYesNo': 'Yes or no',
  'aiConfigs.classifierRules.unavailable':
    "This variation's classifier rules stay saved while classifier rules are unavailable.",
  'aiConfigs.classifierRules.yesDescription': 'Yes',
  'aiConfigs.classifierRules.yesDescriptionPlaceholder': 'Describe a yes answer',
  'aiConfigs.cloneConfigDialog.body':
    'Clone <strong>{name}</strong> into a new config. The name, variations, and settings are copied as-is. Targeting is not copied.',
  'aiConfigs.cloneConfigDialog.cancel': 'Cancel',
  'aiConfigs.cloneConfigDialog.clone': 'Clone',
  'aiConfigs.cloneConfigDialog.cloneErrorBody': 'The config could not be cloned. Try again.',
  'aiConfigs.cloneConfigDialog.cloneErrorTitle': 'Failed to clone config',
  'aiConfigs.cloneConfigDialog.clonedToast': 'Config cloned',
  'aiConfigs.cloneConfigDialog.duplicateKeyError': 'Choose a key different from the original config',
  'aiConfigs.cloneConfigDialog.heading': 'Clone config',
  'aiConfigs.cloneConfigDialog.keyLabel': 'New config key',
  'aiConfigs.cloneConfigDialog.keyPlaceholder': 'Enter new config key',
  'aiConfigs.cloneConfigDialog.keyRequiredError': 'Key is required',
  'aiConfigs.cloneConfigDialog.keyTooLongError': 'Must be 256 characters or fewer',
  'aiConfigs.configActionsMenu.changeHistory': 'Change history',
  'aiConfigs.configActionsMenu.cloneConfig': 'Clone config',
  'aiConfigs.configActionsMenu.copiedKeyToast': 'Config key copied to clipboard',
  'aiConfigs.configActionsMenu.copyKey': 'Copy key',
  'aiConfigs.configActionsMenu.deleteConfig': 'Delete config',
  'aiConfigs.configActionsMenu.triggerLabel': 'Config actions',
  'aiConfigs.configCreator.addViewsLabel': 'Add views',
  'aiConfigs.configCreator.cancel': 'Cancel',
  'aiConfigs.configCreator.configCreatedToast': 'Config created',
  'aiConfigs.configCreator.createError': 'The config could not be created. Try again.',
  'aiConfigs.configCreator.duplicateKeyError': 'A config already exists with this key. Choose a different key.',
  'aiConfigs.configCreator.duplicateKeyJudgeError':
    'A config already exists with this key. Try again with a different description.',
  'aiConfigs.configCreator.editKey': 'Edit key',
  'aiConfigs.configCreator.formActionsLabel': 'Form actions',
  'aiConfigs.configCreator.generateJudge': 'Generate judge',
  'aiConfigs.configCreator.generating': 'Generating',
  'aiConfigs.configCreator.judgeCreatedToast': 'Judge created',
  'aiConfigs.configCreator.judgeDescriptionBody':
    "Describe the behavior or quality you want scored. We'll generate a rubric you can edit.",
  'aiConfigs.configCreator.judgeDescriptionHeading': 'What should this judge evaluate?',
  'aiConfigs.configCreator.judgeDescriptionLabel': 'Judge evaluation description',
  'aiConfigs.configCreator.judgeDescriptionPlaceholder': 'Detect hallucinated pricing claims and score severity',
  'aiConfigs.configCreator.judgeModelHelperText': 'Choose the model used to run this judge.',
  'aiConfigs.configCreator.keyErrorLabel': 'Config key error',
  'aiConfigs.configCreator.keyFieldLabel': 'Config key',
  'aiConfigs.configCreator.keyLabel': 'Key',
  'aiConfigs.configCreator.keyPlaceholder': 'Enter config key',
  'aiConfigs.configCreator.keyRequiredError': 'Key is required',
  'aiConfigs.configCreator.keyTooLongError': 'Key must be less than 256 characters',
  'aiConfigs.configCreator.loading': 'Loading…',
  'aiConfigs.configCreator.maintainerLabel': 'Maintainer',
  'aiConfigs.configCreator.maintainerPlaceholder': 'Select maintainer',
  'aiConfigs.configCreator.modeAgent': 'Agent',
  'aiConfigs.configCreator.modeAgentDescription':
    'Use an autonomous agent to perform multi-step tasks. Agents use a combination of available tools and strategies to complete goals like analyzing data, generating reports, or triggering actions.',
  'aiConfigs.configCreator.modeCompletion': 'Completion',
  'aiConfigs.configCreator.modeCompletionDescription': 'Send prompts to an LLM to generate a single response.',
  'aiConfigs.configCreator.modeJudge': 'Judge',
  'aiConfigs.configCreator.modeJudgeDescription':
    "Create a judge that evaluates your agent's output for quality metrics such as accuracy, relevance, and toxicity.",
  'aiConfigs.configCreator.modeSelectorLabel': 'Config mode',
  'aiConfigs.configCreator.nameDescription':
    'Describe how your app uses generative AI, for example, "Email summarizer"',
  'aiConfigs.configCreator.nameFieldLabel': 'Config name',
  'aiConfigs.configCreator.nameLabel': 'Name',
  'aiConfigs.configCreator.namePlaceholder': 'Enter config name',
  'aiConfigs.configCreator.nameRequiredError': 'Name is required',
  'aiConfigs.configCreator.nameTooLongError': 'Name must be less than 256 characters',
  'aiConfigs.configCreator.permissionError': 'You do not have permission to create a config',
  'aiConfigs.configCreator.restrictedKeyError': '"{key}" is a restricted key',
  'aiConfigs.configCreator.submitLabel': 'Create',
  'aiConfigs.configCreator.title': 'Create config',
  'aiConfigs.configDetail.addDescription': '(Add description)',
  'aiConfigs.configDetail.addExperimentLabel': 'Add experiment',
  'aiConfigs.configDetail.addTags': '(Add tags)',
  'aiConfigs.configDetail.approvalsLoadError': 'Failed to load pending approvals',
  'aiConfigs.configDetail.code': 'Code',
  'aiConfigs.configDetail.configDescriptionLabel': 'Config description',
  'aiConfigs.configDetail.configLoadError': 'Failed to load config. Try again',
  'aiConfigs.configDetail.configNameLabel': 'Config name',
  'aiConfigs.configDetail.connectAppPrompt': 'Connect your app to get started with AgentControl.',
  'aiConfigs.configDetail.copyKeyTooltip': 'Copy key: {configKey}',
  'aiConfigs.configDetail.copyLinkTooltip': 'Copy config link',
  'aiConfigs.configDetail.createExperimentTooltip': 'You must have at least two variations to create an experiment',
  'aiConfigs.configDetail.deleteBlockedTooltip': 'Cannot delete config used in an experiment',
  'aiConfigs.configDetail.deleteConfigLabel': 'Delete config',
  'aiConfigs.configDetail.deleteTooltip': 'Delete config',
  'aiConfigs.configDetail.descriptionPlaceholder': 'Enter a description',
  'aiConfigs.configDetail.descriptionTitle': 'Description',
  'aiConfigs.configDetail.documentation': 'Documentation',
  'aiConfigs.configDetail.editDescriptionLabel': 'Edit description',
  'aiConfigs.configDetail.editNameLabel': 'Edit name',
  'aiConfigs.configDetail.editTagsLabel': 'Edit tags',
  'aiConfigs.configDetail.entityActionLabel': 'entity action',
  'aiConfigs.configDetail.evaluationMetrics': 'Evaluation Metrics',
  'aiConfigs.configDetail.experimentVariationsTooltip': 'You need at least two variations to run an experiment',
  'aiConfigs.configDetail.experiments': 'Experiments',
  'aiConfigs.configDetail.experimentsLoadError': 'Failed to load experiments',
  'aiConfigs.configDetail.key': 'Key',
  'aiConfigs.configDetail.loadingApprovalsLabel': 'Loading pending approvals',
  'aiConfigs.configDetail.loadingLabel': 'Loading…',
  'aiConfigs.configDetail.mode': 'Mode',
  'aiConfigs.configDetail.monitoring': 'Monitoring',
  'aiConfigs.configDetail.nameTitle': 'Name',
  'aiConfigs.configDetail.noDescription': 'None',
  'aiConfigs.configDetail.noTags': 'No tags',
  'aiConfigs.configDetail.pendingApprovals': 'Pending approvals',
  'aiConfigs.configDetail.quickstartGuide': 'Quickstart guide',
  'aiConfigs.configDetail.saveDescriptionLabel': 'Save description',
  'aiConfigs.configDetail.saveNameLabel': 'Save name',
  'aiConfigs.configDetail.saveTagsLabel': 'Save tags',
  'aiConfigs.configDetail.tagsPlaceholder': 'Type to add tags',
  'aiConfigs.configDetail.tagsTitle': 'Tags',
  'aiConfigs.configDetail.targeting': 'Targeting',
  'aiConfigs.configDetail.tryAgain': 'Try again',
  'aiConfigs.configDetail.updateError': 'Failed to update config. Try again later.',
  'aiConfigs.configDetail.updatedToast': 'Config updated',
  'aiConfigs.configDetail.variations': 'Variations',
  'aiConfigs.configDetail.viewSampleCode': 'View sample code',
  'aiConfigs.configModes.agentDescription':
    'Runs multi-step tasks, like analyzing data, generating reports, or triggering actions.',
  'aiConfigs.configModes.agentLabel': 'Agent',
  'aiConfigs.configModes.allLabel': 'All',
  'aiConfigs.configModes.completionDescription': 'Sends prompts to an LLM to generate a single response.',
  'aiConfigs.configModes.completionLabel': 'Completion',
  'aiConfigs.configModes.judgeDescription':
    'Evaluates output for quality metrics like accuracy, relevance, and toxicity.',
  'aiConfigs.configModes.judgeLabel': 'Judge',
  'aiConfigs.configWizard.closeLabel': 'Close',
  'aiConfigs.configWizard.step1Label': 'Agent',
  'aiConfigs.configWizard.step1Title': 'Create your agent',
  'aiConfigs.configWizard.step2Label': 'Judge',
  'aiConfigs.configWizard.step2Title': 'Set up your judge',
  'aiConfigs.configWizard.step3Label': 'Experiment',
  'aiConfigs.configWizard.step3Title': 'Start an experiment',
  'aiConfigs.configWizard.step4Label': 'Code',
  'aiConfigs.configWizard.step4Title': 'Generate starter code',
  'aiConfigs.configWizard.stepperLabel': 'Step {current} of {total}',
  'aiConfigs.configsList.columnOptionsLabel': '{column} column options',
  'aiConfigs.configsList.copiedKeyToast': 'Copied {value}',
  'aiConfigs.configsList.copyKeyTooltip': 'Copy {value} to clipboard',
  'aiConfigs.configsList.createdOnTooltip': 'Created on {date}',
  'aiConfigs.configsList.deleteErrorBody': 'Failed to delete config. Try again later.',
  'aiConfigs.configsList.deleteErrorTitle': 'Failed to delete config.',
  'aiConfigs.configsList.deletedToast': 'Config deleted',
  'aiConfigs.configsList.deletingToast': 'Deleting config…',
  'aiConfigs.configsList.editTagsLabel': 'Edit tags',
  'aiConfigs.configsList.entitlementAlertTextValue': 'AI Config entitlement alert',
  'aiConfigs.configsList.hideProperty': 'Hide property',
  'aiConfigs.configsList.listLabel': 'Config list',
  'aiConfigs.configsList.modeTagLabel': 'Config mode',
  'aiConfigs.configsList.noVariationsCreated': 'No variations created',
  'aiConfigs.configsList.showMoreTagsLabel': '{count, plural, one {Show # more tag} other {Show # more tags}}',
  'aiConfigs.configsList.sortCreatedAscending': 'Sort oldest to newest',
  'aiConfigs.configsList.sortCreatedDescending': 'Sort newest to oldest',
  'aiConfigs.configsList.sortNameAscending': 'Sort A to Z',
  'aiConfigs.configsList.sortNameDescending': 'Sort Z to A',
  'aiConfigs.configsList.tagsHeading': 'Tags',
  'aiConfigs.configsList.tagsLabel': 'Config tags',
  'aiConfigs.configsList.tagsUpdateErrorToast': 'Failed to update tags',
  'aiConfigs.configsList.tagsUpdatedToast': 'Tags updated',
  'aiConfigs.contextBreakdown.groupedByTooltip': 'Grouped by {dimensionLabel}',
  'aiConfigs.contextBreakdown.tableLabel': 'AI config context breakdown table',
  'aiConfigs.contextualPrecisionCriteria.expectedOutputDescription':
    'The ideal or correct response that will be used to evaluate the retrieval context alignment.',
  'aiConfigs.contextualPrecisionCriteria.expectedOutputLabel': 'Expected output',
  'aiConfigs.contextualPrecisionCriteria.expectedOutputPlaceholder': 'Enter the expected or ideal response',
  'aiConfigs.contextualPrecisionCriteria.retrievalContextDescription':
    'The context retrieved from your RAG pipeline that will be evaluated for ranking quality.',
  'aiConfigs.contextualPrecisionCriteria.retrievalContextLabel': 'Retrieval context',
  'aiConfigs.contextualPrecisionCriteria.retrievalContextPlaceholder':
    'Enter the retrieval context from your RAG pipeline',
  'aiConfigs.contextualPrecisionCriteria.thresholdDescription':
    'Minimum passing threshold for the contextual precision score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.contextualRecallCriteria.expectedOutputDescription':
    'The ideal or correct response that will be used to evaluate how well the retrieval context captures all relevant information.',
  'aiConfigs.contextualRecallCriteria.expectedOutputLabel': 'Expected output',
  'aiConfigs.contextualRecallCriteria.expectedOutputPlaceholder': 'Enter the expected or ideal response',
  'aiConfigs.contextualRecallCriteria.retrievalContextDescription':
    'The context retrieved from your RAG pipeline that will be evaluated for completeness and relevance.',
  'aiConfigs.contextualRecallCriteria.retrievalContextLabel': 'Retrieval context',
  'aiConfigs.contextualRecallCriteria.retrievalContextPlaceholder':
    'Enter the retrieval context from your RAG pipeline',
  'aiConfigs.contextualRecallCriteria.thresholdDescription':
    'Minimum passing threshold for the contextual recall score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.contextualRelevancyCriteria.retrievalContextDescription':
    'The context retrieved from your RAG pipeline that will be evaluated for overall relevance to the input query.',
  'aiConfigs.contextualRelevancyCriteria.retrievalContextLabel': 'Retrieval context',
  'aiConfigs.contextualRelevancyCriteria.retrievalContextPlaceholder':
    'Enter the retrieval context from your RAG pipeline',
  'aiConfigs.contextualRelevancyCriteria.thresholdDescription':
    'Minimum passing threshold for the contextual relevancy score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.createDatasetDialog.cancel': 'Cancel',
  'aiConfigs.createDatasetDialog.createDataset': 'Create dataset',
  'aiConfigs.createDatasetDialog.createFailedBody': 'Try again, or choose a different name and key.',
  'aiConfigs.createDatasetDialog.createFailedTitle': 'Failed to create dataset',
  'aiConfigs.createDatasetDialog.keyLabel': 'Key',
  'aiConfigs.createDatasetDialog.keyPlaceholder': 'Enter dataset key',
  'aiConfigs.createDatasetDialog.keyRequired': 'Key is required',
  'aiConfigs.createDatasetDialog.nameLabel': 'Name',
  'aiConfigs.createDatasetDialog.namePlaceholder': 'Enter dataset name',
  'aiConfigs.createDatasetDialog.nameRequired': 'Name is required',
  'aiConfigs.createJudgeDialog.addMaintainer': 'Add maintainer',
  'aiConfigs.createJudgeDialog.cancel': 'Cancel',
  'aiConfigs.createJudgeDialog.chooseView': 'Choose a view to continue',
  'aiConfigs.createJudgeDialog.closeLabel': 'Close dialog',
  'aiConfigs.createJudgeDialog.createFailedBody': 'Failed to create judge. Try again later.',
  'aiConfigs.createJudgeDialog.createFailedTitle': 'Failed to create judge',
  'aiConfigs.createJudgeDialog.createJudge': 'Create judge',
  'aiConfigs.createJudgeDialog.created': 'Judge created',
  'aiConfigs.createJudgeDialog.creating': 'Creating judge…',
  'aiConfigs.createJudgeDialog.description': 'A judge uses a model to score the responses your configs return.',
  'aiConfigs.createJudgeDialog.docsLink': 'Read about judges',
  'aiConfigs.createJudgeDialog.editConfigKey': 'Edit config key',
  'aiConfigs.createJudgeDialog.editEventKey': 'Edit event key',
  'aiConfigs.createJudgeDialog.evaluationMetricKeyBlank': 'Evaluation metric key cannot be blank',
  'aiConfigs.createJudgeDialog.evaluationMetricKeyCharacters': 'Only letters, numbers, and hyphens allowed',
  'aiConfigs.createJudgeDialog.evaluationMetricKeyPrefix': 'Evaluation metric key must start with "{prefix}"',
  'aiConfigs.createJudgeDialog.evaluationMetricKeyRequired': 'Evaluation metric key is required',
  'aiConfigs.createJudgeDialog.evaluationMetricKeyTooLong': 'Evaluation metric key must be less than 256 characters',
  'aiConfigs.createJudgeDialog.eventKey': 'Event key',
  'aiConfigs.createJudgeDialog.eventKeyInfoLabel': 'Event key information',
  'aiConfigs.createJudgeDialog.eventKeyRequired': 'Event key is required',
  'aiConfigs.createJudgeDialog.eventKeyTooltip': '{base}. Use the same key across judges to combine results.',
  'aiConfigs.createJudgeDialog.eventKeyValidationLabel': 'Event key validation',
  'aiConfigs.createJudgeDialog.generatedKey': 'Config key generated from the judge name',
  'aiConfigs.createJudgeDialog.key': 'Key',
  'aiConfigs.createJudgeDialog.keyHelperText': 'Leave blank to generate the key from the judge name.',
  'aiConfigs.createJudgeDialog.keyLabel': 'Config key',
  'aiConfigs.createJudgeDialog.keyPlaceholder': 'Enter config key',
  'aiConfigs.createJudgeDialog.keyRequired': 'Key is required',
  'aiConfigs.createJudgeDialog.keyRestricted': '"{key}" is a restricted key',
  'aiConfigs.createJudgeDialog.keyTakenError': 'A config already exists with this key. Choose a different key.',
  'aiConfigs.createJudgeDialog.keyTooLong': 'Key must be less than 256 characters',
  'aiConfigs.createJudgeDialog.keyValidationLabel': 'Key validation',
  'aiConfigs.createJudgeDialog.loadingFormDataLabel': 'Loading form data…',
  'aiConfigs.createJudgeDialog.maintainerLabel': 'Maintainer',
  'aiConfigs.createJudgeDialog.modelRequired': 'Model is required',
  'aiConfigs.createJudgeDialog.name': 'Name',
  'aiConfigs.createJudgeDialog.nameLabel': 'Config name',
  'aiConfigs.createJudgeDialog.namePlaceholder': 'Enter judge name',
  'aiConfigs.createJudgeDialog.nameRequired': 'Name is required',
  'aiConfigs.createJudgeDialog.nameTooLong': 'Name must be less than 256 characters',
  'aiConfigs.createJudgeDialog.providerRequired': 'Provider is required',
  'aiConfigs.createJudgeDialog.viewRequiredTitle': 'View required',
  'aiConfigs.createJudgeForm.body':
    'Describe the behavior you want scored, such as accuracy, relevance, or toxicity. LaunchDarkly picks a judge template and customizes the rubric. You can change or remove any rules and values after creation.',
  'aiConfigs.createJudgeForm.cancel': 'Cancel',
  'aiConfigs.createJudgeForm.createErrorBody': 'Failed to create judge. Try again later.',
  'aiConfigs.createJudgeForm.createErrorTitle': 'Failed to create judge',
  'aiConfigs.createJudgeForm.createdToast': 'Judge created',
  'aiConfigs.createJudgeForm.creating': 'Creating judge…',
  'aiConfigs.createJudgeForm.criteriaPlaceholder': 'Score responses for hallucination about pricing',
  'aiConfigs.createJudgeForm.duplicateKeyBody':
    'A config already exists with this key. Try again with a different description.',
  'aiConfigs.createJudgeForm.duplicateKeyTitle': 'Duplicate config key',
  'aiConfigs.createJudgeForm.generating': 'Generating judge…',
  'aiConfigs.createJudgeForm.heading': 'What should this judge evaluate?',
  'aiConfigs.createJudgeForm.submit': 'Create judge',
  'aiConfigs.createJudgeForm.viewRequiredTitle': 'View required',
  'aiConfigs.createSkill.addTagLabel': 'Add tag',
  'aiConfigs.createSkill.addTagsLabel': 'Add tags',
  'aiConfigs.createSkill.create': 'Create',
  'aiConfigs.createSkill.createSkillLabel': 'Create skill',
  'aiConfigs.createSkill.creating': 'Creating…',
  'aiConfigs.createSkill.descriptionLabel': 'Description <optional>(optional)</optional>',
  'aiConfigs.createSkill.descriptionPlaceholder': 'Brief description of this skill',
  'aiConfigs.createSkill.descriptionTooLong': 'Description must be less than 1024 characters',
  'aiConfigs.createSkill.editKey': 'Edit key',
  'aiConfigs.createSkill.key': 'Key',
  'aiConfigs.createSkill.keyInvalid':
    "Keys must start with a lowercase letter or number and only contain lowercase letters, numbers, or '-'",
  'aiConfigs.createSkill.keyLabel': 'Skill key',
  'aiConfigs.createSkill.keyPlaceholder': 'Enter skill key',
  'aiConfigs.createSkill.keyReserved': "Keys can't be a reserved Windows file name, such as con, nul, com1, or lpt1",
  'aiConfigs.createSkill.keyRequired': 'Key is required',
  'aiConfigs.createSkill.keyTooLong': 'Key must be 64 characters or fewer',
  'aiConfigs.createSkill.manageTags': 'Manage tags',
  'aiConfigs.createSkill.markdownPlaceholder': 'Paste the verbatim SKILL.md content…',
  'aiConfigs.createSkill.markdownRequired': 'Markdown is required',
  'aiConfigs.createSkill.markdownTooLarge': 'Markdown must not exceed {limit}KB',
  'aiConfigs.createSkill.name': 'Name',
  'aiConfigs.createSkill.namePlaceholder': 'Enter skill name',
  'aiConfigs.createSkill.nameRequired': 'Name is required',
  'aiConfigs.createSkill.nameTooLong': 'Name must be less than 256 characters',
  'aiConfigs.createSkill.skillMd': 'SKILL.md',
  'aiConfigs.createSkill.tagCount': '{count, plural, one {# tag} other {# tags}}',
  'aiConfigs.createSkill.tagTooLong': 'Each tag must be at most {max} characters',
  'aiConfigs.createSkill.tooManyTags': 'You can add at most {max} tags',
  'aiConfigs.createSkillModal.cancel': 'Cancel',
  'aiConfigs.createSkillModal.createSkillLabel': 'Create skill',
  'aiConfigs.createSkillModal.save': 'Save',
  'aiConfigs.createSkillModal.saveSkill': 'Save skill',
  'aiConfigs.createSnippetModal.addTagLabel': 'Add tag',
  'aiConfigs.createSnippetModal.addTagsLabel': 'Add tags',
  'aiConfigs.createSnippetModal.body': 'Body',
  'aiConfigs.createSnippetModal.bodyPlaceholder': 'Enter the prompt…',
  'aiConfigs.createSnippetModal.cancel': 'Cancel',
  'aiConfigs.createSnippetModal.createFailedBody': 'Failed to create prompt snippet. Try again later.',
  'aiConfigs.createSnippetModal.createFailedTitle': 'Failed to create prompt snippet',
  'aiConfigs.createSnippetModal.created': 'Snippet added to <link>library</link>',
  'aiConfigs.createSnippetModal.descriptionLabel': 'Description <optional>(optional)</optional>',
  'aiConfigs.createSnippetModal.descriptionPlaceholder': 'Brief description of this prompt',
  'aiConfigs.createSnippetModal.dialogLabel': 'Create prompt snippet',
  'aiConfigs.createSnippetModal.editKey': 'Edit key',
  'aiConfigs.createSnippetModal.formLabel': 'Create snippet',
  'aiConfigs.createSnippetModal.key': 'Key',
  'aiConfigs.createSnippetModal.keyLabel': 'Snippet key',
  'aiConfigs.createSnippetModal.keyPlaceholder': 'Enter snippet key',
  'aiConfigs.createSnippetModal.name': 'Name',
  'aiConfigs.createSnippetModal.namePlaceholder': 'Enter prompt name',
  'aiConfigs.createSnippetModal.save': 'Save',
  'aiConfigs.createSnippetModal.saveSnippet': 'Save snippet',
  'aiConfigs.criteriaBreakdown.ambiguousCriteria': 'Ambiguous criteria:',
  'aiConfigs.criteriaBreakdown.failedCriteria': 'Failed criteria:',
  'aiConfigs.datasetEditDialog.cancel': 'Cancel',
  'aiConfigs.datasetEditDialog.editDataset': 'Edit dataset',
  'aiConfigs.datasetEditDialog.nameLabel': 'Name',
  'aiConfigs.datasetEditDialog.nameRequired': 'Name is required',
  'aiConfigs.datasetEditDialog.save': 'Save',
  'aiConfigs.datasetEditDialog.updateFailedTitle': 'Failed to update dataset',
  'aiConfigs.datasetEditDialog.updatedToastTitle': 'Dataset updated',
  'aiConfigs.datasetEvaluationRunActions.viewResults': 'View results',
  'aiConfigs.datasetEvaluationRunHeader.evaluatedOn': 'Evaluated on',
  'aiConfigs.datasetEvaluationRunHeader.model': 'Model',
  'aiConfigs.datasetEvaluationRunHeader.prompt': 'Prompt',
  'aiConfigs.datasetEvaluationRunHeader.rowsPassed': 'Rows passed',
  'aiConfigs.datasetEvaluationRunHeader.runBy': 'Run by',
  'aiConfigs.datasetEvaluationRunHeader.status': 'Status',
  'aiConfigs.datasetEvaluationSaveAsMenu.closeLabel': 'Close',
  'aiConfigs.datasetEvaluationSaveAsMenu.existingConfigDialogLabel': 'Save to existing config',
  'aiConfigs.datasetEvaluationSaveAsMenu.loadError': 'Failed to load save options.',
  'aiConfigs.datasetEvaluationSaveAsMenu.loading': 'Loading…',
  'aiConfigs.datasetEvaluationSaveAsMenu.newConfig': 'New config',
  'aiConfigs.datasetEvaluationSaveAsMenu.newConfigDialogLabel': 'Save as new config',
  'aiConfigs.datasetEvaluationSaveAsMenu.newVariation': 'New variation',
  'aiConfigs.datasetEvaluationSaveAsMenu.saveAs': 'Save as',
  'aiConfigs.datasetEvaluationSaveAsMenu.saveAsEllipsis': 'Save as…',
  'aiConfigs.datasetEvaluationSaveAsMenu.triggerLabel': 'Save as options for run {runId}',
  'aiConfigs.datasetEvaluationsEmptyState.body':
    'Run an evaluation to measure how your AI configuration performs on this dataset.',
  'aiConfigs.datasetEvaluationsEmptyState.heading': 'No evaluations yet',
  'aiConfigs.datasetEvaluationsProcessingState.body':
    "Your dataset is being processed. This may take a few minutes. You'll be able to run evaluations once it's ready.",
  'aiConfigs.datasetEvaluationsProcessingState.heading': 'Dataset is processing',
  'aiConfigs.datasetEvaluationsTab.datasetLoadError': 'Failed to load this dataset.',
  'aiConfigs.datasetEvaluationsTab.datasetProcessingFailed':
    'This dataset failed to process. Upload a new dataset or contact support if the problem persists.',
  'aiConfigs.datasetEvaluationsTab.docsLink': 'Read more about offline evaluations',
  'aiConfigs.datasetEvaluationsTab.evaluateInPlaygroundLabel': 'Evaluate in playground',
  'aiConfigs.datasetEvaluationsTab.newEvaluation': 'New evaluation',
  'aiConfigs.datasetEvaluationsTab.runsLoadError': 'Failed to load evaluation runs.',
  'aiConfigs.datasetEvaluationsTab.searchLabel': 'Search evaluations',
  'aiConfigs.datasetEvaluationsTab.searchPlaceholder': 'Search evaluations…',
  'aiConfigs.datasetEvaluationsTab.tryAgain': 'Try again',
  'aiConfigs.datasetEvaluationsTab.zeroStateBody':
    'See how your configuration performs before you ship. Evaluate against this dataset to measure, compare changes, and iterate confidently in the Playground.',
  'aiConfigs.datasetEvaluationsTable.passRateColumn': 'Pass rate',
  'aiConfigs.datasetEvaluationsTable.viewPromptLabel': 'View prompt for column {columnLetter}',
  'aiConfigs.datasetPicker.clearSelectionLabel': 'Clear dataset selection',
  'aiConfigs.datasetPicker.emptyState': 'No datasets yet',
  'aiConfigs.datasetPicker.errorLabel': 'Error loading datasets',
  'aiConfigs.datasetPicker.failedToLoad': 'Failed to load datasets',
  'aiConfigs.datasetPicker.goToDatasets': 'Go to datasets',
  'aiConfigs.datasetPicker.loadingDatasets': 'Loading datasets…',
  'aiConfigs.datasetPicker.loadingLabel': 'Loading datasets',
  'aiConfigs.datasetPicker.noMatchesLabel': 'No datasets match',
  'aiConfigs.datasetPicker.noSearchResults': 'No datasets match your search',
  'aiConfigs.datasetPicker.optionsLabel': 'Dataset options',
  'aiConfigs.datasetPicker.searchPlaceholder': 'Search datasets…',
  'aiConfigs.datasetPicker.triggerPlaceholder': 'Select a dataset (optional)',
  'aiConfigs.datasetPicker.uploadNewDataset': 'Upload new dataset',
  'aiConfigs.datasetRowActionsMenu.delete': 'Delete',
  'aiConfigs.datasetRowActionsMenu.moveDown': 'Move down',
  'aiConfigs.datasetRowActionsMenu.moveToBottom': 'Move to bottom',
  'aiConfigs.datasetRowActionsMenu.moveToTop': 'Move to top',
  'aiConfigs.datasetRowActionsMenu.moveUp': 'Move up',
  'aiConfigs.datasetRowActionsMenu.triggerLabel': 'Actions for row {rowNumber}',
  'aiConfigs.datasetRowDrawer.addEntry': 'Add',
  'aiConfigs.datasetRowDrawer.addEntryLabel': 'Add {section} entry',
  'aiConfigs.datasetRowDrawer.addFailedTitle': 'Failed to add row',
  'aiConfigs.datasetRowDrawer.addRowHeading': 'Add row',
  'aiConfigs.datasetRowDrawer.addRowLabel': 'Add row',
  'aiConfigs.datasetRowDrawer.addRowSubmit': 'Add row',
  'aiConfigs.datasetRowDrawer.cancel': 'Cancel',
  'aiConfigs.datasetRowDrawer.close': 'Close',
  'aiConfigs.datasetRowDrawer.deleteEntryLabel': 'Delete {section} entry {position}',
  'aiConfigs.datasetRowDrawer.doneSubmit': 'Save row',
  'aiConfigs.datasetRowDrawer.dotNotationHint': 'Use dot notation for nested values, e.g. company.team.department',
  'aiConfigs.datasetRowDrawer.editRowHeading': 'Edit row',
  'aiConfigs.datasetRowDrawer.editRowLabel': 'Edit row {rowNumber}',
  'aiConfigs.datasetRowDrawer.entryKeyLabel': '{section} key {position}',
  'aiConfigs.datasetRowDrawer.entryValueLabel': '{section} value {position}',
  'aiConfigs.datasetRowDrawer.expectedOutputHint': 'The ideal response used to score this input.',
  'aiConfigs.datasetRowDrawer.expectedOutputLabel': 'Expected output',
  'aiConfigs.datasetRowDrawer.inputLabel': 'Input',
  'aiConfigs.datasetRowDrawer.inputRequired': 'Input is required',
  'aiConfigs.datasetRowDrawer.keyDotSegments': 'Use a name between each dot',
  'aiConfigs.datasetRowDrawer.keyPlaceholder': 'Key',
  'aiConfigs.datasetRowDrawer.keyRequired': 'Key is required',
  'aiConfigs.datasetRowDrawer.keyReserved': 'Keys cannot use __proto__, constructor, or prototype',
  'aiConfigs.datasetRowDrawer.keyUnique': 'Keys must be unique and cannot overlap nested paths',
  'aiConfigs.datasetRowDrawer.metadataHeading': 'Metadata',
  'aiConfigs.datasetRowDrawer.noEntries': 'None',
  'aiConfigs.datasetRowDrawer.rowAddedTitle': 'Row added',
  'aiConfigs.datasetRowDrawer.rowUpdatedTitle': 'Row updated',
  'aiConfigs.datasetRowDrawer.saveFailedBody': 'Try again.',
  'aiConfigs.datasetRowDrawer.updateFailedTitle': 'Failed to update row',
  'aiConfigs.datasetRowDrawer.valuePlaceholder': 'Value',
  'aiConfigs.datasetRowDrawer.valueRequired': 'Value is required',
  'aiConfigs.datasetRowDrawer.variablesHeading': 'Variables',
  'aiConfigs.datasetRowDrawer.viewRowHeading': 'View row',
  'aiConfigs.datasetRowDrawer.viewRowLabel': 'View row {rowNumber}',
  'aiConfigs.datasetRowsTable.columnExpectedOutput': 'Expected output',
  'aiConfigs.datasetRowsTable.columnInput': 'Input',
  'aiConfigs.datasetRowsTable.columnMetadata': 'Metadata',
  'aiConfigs.datasetRowsTable.columnRow': 'Row',
  'aiConfigs.datasetRowsTable.columnVariables': 'Variables',
  'aiConfigs.datasetRowsTable.dragHandleLabel': 'Drag to reorder row {rowNumber}',
  'aiConfigs.datasetRowsTable.rowTextValue': 'Row {rowNumber}',
  'aiConfigs.datasetRowsTable.selectAllLabel': 'Select all rows on this page',
  'aiConfigs.datasetRowsTable.selectRowLabel': 'Select row {rowNumber}',
  'aiConfigs.datasetUploadHints.downloadSampleCsv': 'Download sample CSV',
  'aiConfigs.datasetUploadHints.downloadSampleJsonl': 'Download sample JSONL',
  'aiConfigs.datasetUploadHints.notSureHow': 'Not sure how to structure your dataset?',
  'aiConfigs.datasetUploadHints.readDocumentation': 'Read documentation',
  'aiConfigs.datasetUploadProgress.finalizingDataset': 'Finalizing dataset…',
  'aiConfigs.datasetUploadProgress.loadingErrorDetails': 'Loading error details…',
  'aiConfigs.datasetUploadProgress.removeFileLabel': 'Remove {fileName}',
  'aiConfigs.datasetUploadProgress.unexpectedError': 'An unexpected error has occurred',
  'aiConfigs.datasetUploadProgress.unknownError': 'Unknown error',
  'aiConfigs.datasetUploadProgress.uploadFailed': 'Upload failed',
  'aiConfigs.datasetUploadProgress.uploadingFile': 'Uploading file…',
  'aiConfigs.datasetUploadToast.cleanupFailedTitle': 'Dataset cleanup failed',
  'aiConfigs.datasetUploadToast.columnCountMismatch': 'Row {row} has {actual} columns, expected {expected}',
  'aiConfigs.datasetUploadToast.createDatasetFailed': 'Failed to create dataset',
  'aiConfigs.datasetUploadToast.createForbidden':
    'You do not have permission to create datasets. Contact your administrator for more information.',
  'aiConfigs.datasetUploadToast.fetchDatasetFailed': 'Failed to fetch dataset',
  'aiConfigs.datasetUploadToast.fileEmpty': 'File is empty',
  'aiConfigs.datasetUploadToast.fileTooLarge': 'This file exceeds the {limit} upload limit. Try a smaller file.',
  'aiConfigs.datasetUploadToast.invalidFileTitle': 'Invalid file',
  'aiConfigs.datasetUploadToast.invalidJsonLine': 'Line {line} is not valid JSON',
  'aiConfigs.datasetUploadToast.noDataRows': 'No data rows',
  'aiConfigs.datasetUploadToast.noDatasetId': 'No dataset ID provided',
  'aiConfigs.datasetUploadToast.noUploadInstructions': 'No upload instructions provided',
  'aiConfigs.datasetUploadToast.readError': 'Unexpected error reading file',
  'aiConfigs.datasetUploadToast.reuploadFailed': 'Failed to re-upload file',
  'aiConfigs.datasetUploadToast.rowsAddedBody':
    '{count, plural, one {# row added to dataset} other {# rows added to dataset}}',
  'aiConfigs.datasetUploadToast.rowsAddedTitle': 'Rows added',
  'aiConfigs.datasetUploadToast.unexpectedErrorTitle': 'Unexpected error',
  'aiConfigs.datasetUploadToast.unknownError': 'Unknown error',
  'aiConfigs.datasetUploadToast.unsupportedFormat': 'Unsupported file format',
  'aiConfigs.datasetUploadToast.updateForbidden':
    'You do not have permission to update this dataset. Contact your administrator for more information.',
  'aiConfigs.datasetUploadToast.uploadFailedTitle': 'Upload failed',
  'aiConfigs.datasetUploadToast.uploadFileFailed': 'Failed to upload file',
  'aiConfigs.datasetUploadToast.uploadRowsFailed': 'Failed to upload rows',
  'aiConfigs.datasetUploadToast.uploadedTitle': 'Dataset {filename} uploaded successfully',
  'aiConfigs.datasetVersionHistoryDrawer.close': 'Close',
  'aiConfigs.datasetVersionHistoryDrawer.couldNotLoad': 'Could not load dataset version history.',
  'aiConfigs.datasetVersionHistoryDrawer.currentBadge': 'Current',
  'aiConfigs.datasetVersionHistoryDrawer.loadingVersions': 'Loading versions',
  'aiConfigs.datasetVersionHistoryDrawer.noVersionsAre':
    'No versions yet. Each change to the dataset saves a new version.',
  'aiConfigs.datasetVersionHistoryDrawer.restore': 'Restore',
  'aiConfigs.datasetVersionHistoryDrawer.restoreVersionLabel': 'Restore v{version}',
  'aiConfigs.datasetVersionHistoryDrawer.restoredFrom': 'restored from v{version}',
  'aiConfigs.datasetVersionHistoryDrawer.rowCount': '{count, plural, one {# row} other {# rows}}',
  'aiConfigs.datasetVersionHistoryDrawer.versionHistory': 'Version history',
  'aiConfigs.datasetVersionHistoryDrawer.versionStatusLabel': 'Version status',
  'aiConfigs.datasetVersionHistoryDrawer.viewVersionLabel': 'View version {version}',
  'aiConfigs.datasetViewerHeader.backToDatasets': 'Back to datasets',
  'aiConfigs.datasetViewerHeader.versionHistory': 'Version history',
  'aiConfigs.datasetViewerHeader.versionSelectorLabel': 'Dataset version',
  'aiConfigs.datasetViewerPage.loadingRows': 'Loading rows…',
  'aiConfigs.datasetViewerPage.noRowsYet': 'No rows yet',
  'aiConfigs.datasetViewerPage.noRowsYetBody': 'Add a row to get started.',
  'aiConfigs.datasetViewerPage.noSearchResults': 'No rows match your search',
  'aiConfigs.datasetViewerPage.noSearchResultsBody': 'Try a different search term.',
  'aiConfigs.datasetViewerPage.paginationRange':
    '<b>{from}–{to}</b> of <b>{total}</b> {total, plural, one {row} other {rows}}',
  'aiConfigs.datasetViewerPage.rowOrderFailedBody': 'Try again.',
  'aiConfigs.datasetViewerPage.rowOrderFailedTitle': 'Failed to save row order',
  'aiConfigs.datasetViewerPage.rowOrderSavedTitle': 'Row order saved',
  'aiConfigs.datasetViewerPage.rowsGridLabel': 'Dataset rows',
  'aiConfigs.datasetViewerPage.rowsLoadError': 'An unexpected error occurred while loading dataset rows',
  'aiConfigs.datasetViewerTabs.evaluations': 'Evaluations',
  'aiConfigs.datasetViewerTabs.label': 'Dataset viewer sections',
  'aiConfigs.datasetViewerTabs.rows': 'Rows',
  'aiConfigs.datasetViewerToolbar.addRow': 'Add row',
  'aiConfigs.datasetViewerToolbar.delete': 'Delete',
  'aiConfigs.datasetViewerToolbar.deleteWithCount': 'Delete ({count})',
  'aiConfigs.datasetViewerToolbar.loadingRows': 'Loading rows…',
  'aiConfigs.datasetViewerToolbar.rowRange': 'Showing {from}–{to} of {total, plural, one {# row} other {# rows}}',
  'aiConfigs.datasetViewerToolbar.save': 'Save',
  'aiConfigs.datasetViewerToolbar.searchLabel': 'Search rows',
  'aiConfigs.datasetViewerToolbar.searchPlaceholder': 'Search rows…',
  'aiConfigs.datasetViewerToolbar.uploadRows': 'Upload rows',
  'aiConfigs.datasetViewerToolbar.viewingVersion': 'Viewing <v>v{version}</v>',
  'aiConfigs.datasetsEmptyState.body': 'Upload datasets to use in offline evaluations.',
  'aiConfigs.datasetsEmptyState.bodyWithCreate':
    'Create a dataset and add rows by hand, or upload a file to use in offline evaluations.',
  'aiConfigs.datasetsEmptyState.heading': 'No datasets yet',
  'aiConfigs.datasetsPage.columnAddedBy': 'Added by',
  'aiConfigs.datasetsPage.columnAddedOn': 'Added on',
  'aiConfigs.datasetsPage.columnEvaluations': '# of Evaluations',
  'aiConfigs.datasetsPage.columnName': 'Name',
  'aiConfigs.datasetsPage.columnRows': 'Rows',
  'aiConfigs.datasetsPage.columnUploadStatus': 'Upload status',
  'aiConfigs.datasetsPage.copyKey': 'Copy key',
  'aiConfigs.datasetsPage.delete': 'Delete',
  'aiConfigs.datasetsPage.description': 'Use datasets to run offline evaluations.',
  'aiConfigs.datasetsPage.docsLink': 'Read more about offline evaluations',
  'aiConfigs.datasetsPage.download': 'Download',
  'aiConfigs.datasetsPage.downloadFailedTitle': 'Failed to download dataset',
  'aiConfigs.datasetsPage.downloadNoFileBody': 'This dataset does not have a file to download.',
  'aiConfigs.datasetsPage.downloadNotReadyBody': 'Wait until processing finishes before downloading.',
  'aiConfigs.datasetsPage.downloadNotReadyTitle': 'Dataset is not ready',
  'aiConfigs.datasetsPage.downloadUnavailableTitle': 'Download unavailable',
  'aiConfigs.datasetsPage.downloadUnsupportedBody':
    'This dataset is not ready, or its format does not support downloads.',
  'aiConfigs.datasetsPage.evaluate': 'Eval',
  'aiConfigs.datasetsPage.evaluateInPlaygroundLabel': 'Evaluate in playground',
  'aiConfigs.datasetsPage.keyCopiedToast': 'Dataset key copied to clipboard',
  'aiConfigs.datasetsPage.loadError': 'An unexpected error occurred while loading datasets',
  'aiConfigs.datasetsPage.loading': 'Loading…',
  'aiConfigs.datasetsPage.rename': 'Rename',
  'aiConfigs.datasetsPage.rowActionsLabel': 'Actions for {name}',
  'aiConfigs.datasetsPage.searchLabel': 'Search datasets',
  'aiConfigs.datasetsPage.searchPlaceholder': 'Search datasets…',
  'aiConfigs.datasetsPage.tableLabel': 'Datasets list',
  'aiConfigs.datasetsPage.viewEvaluations': 'View evaluations',
  'aiConfigs.datasetsSearchEmptyState.body': 'Try adjusting your search terms',
  'aiConfigs.datasetsSearchEmptyState.clearSearch': 'Clear search',
  'aiConfigs.datasetsSearchEmptyState.heading': 'No datasets match your search',
  'aiConfigs.deleteConfigDialog.body':
    'This permanently deletes <name>{aiConfigName}</name> and all its variations from every environment. Remove any references to this config from your application code before you delete it.',
  'aiConfigs.deleteConfigDialog.cancel': 'Cancel',
  'aiConfigs.deleteConfigDialog.confirmDeleteLabel': 'Confirm delete config',
  'aiConfigs.deleteConfigDialog.delete': 'Delete',
  'aiConfigs.deleteConfigDialog.deleteConfig': 'Delete config',
  'aiConfigs.deleteConfigDialog.deletedToast': 'Config deleted',
  'aiConfigs.deleteConfigDialog.description': 'Are you sure you want to delete this config?',
  'aiConfigs.deleteConfigDialog.generalError': 'Failed to delete config. Try again later.',
  'aiConfigs.deleteConfigDialog.heading': 'Delete config',
  'aiConfigs.deleteConfigDialog.publishedVersionError': 'Cannot delete a config with published versions.',
  'aiConfigs.deleteConfigDialog.title': 'Delete config',
  'aiConfigs.deleteDatasetDialog.cancel': 'Cancel',
  'aiConfigs.deleteDatasetDialog.deleteDataset': 'Delete dataset',
  'aiConfigs.deleteDatasetDialog.deleteFailedTitle': 'Failed to delete dataset',
  'aiConfigs.deleteDatasetDialog.deletedToastTitle': 'Dataset "{name}" deleted',
  'aiConfigs.deleteDatasetDialog.heading': 'Delete "{name}"',
  'aiConfigs.deleteDatasetDialog.thisDatasetWill': "This permanently deletes the dataset. This action can't be undone.",
  'aiConfigs.deleteDatasetRowsDialog.bodyMultiple':
    'This deletes {count, plural, one {# row} other {# rows}} from “{datasetName}”. You can restore {count, plural, one {it} other {them}} later from the version history.',
  'aiConfigs.deleteDatasetRowsDialog.bodySingle':
    'This deletes row {rowNumber} from “{datasetName}”. You can restore it later from the version history.',
  'aiConfigs.deleteDatasetRowsDialog.cancel': 'Cancel',
  'aiConfigs.deleteDatasetRowsDialog.delete': 'Delete',
  'aiConfigs.deleteDatasetRowsDialog.deleteFailedBody': 'Try again.',
  'aiConfigs.deleteDatasetRowsDialog.deleteRowFailedTitle': 'Failed to delete row',
  'aiConfigs.deleteDatasetRowsDialog.deleteRowHeading': 'Delete row {rowNumber}',
  'aiConfigs.deleteDatasetRowsDialog.deleteRowsFailedTitle': 'Failed to delete rows',
  'aiConfigs.deleteDatasetRowsDialog.deleteRowsHeading': 'Delete rows',
  'aiConfigs.deleteDatasetRowsDialog.successTitleMultiple':
    '{count, plural, one {# row deleted} other {# rows deleted}}',
  'aiConfigs.deleteDatasetRowsDialog.successTitleSingle': 'Row {rowNumber} deleted',
  'aiConfigs.deleteModelDialog.body':
    'This permanently deletes the selected model config. This action cannot be undone.',
  'aiConfigs.deleteModelDialog.cancel': 'Cancel',
  'aiConfigs.deleteModelDialog.delete': 'Delete',
  'aiConfigs.deleteModelDialog.heading': 'Delete model config',
  'aiConfigs.deleteSkill.cancel': 'Cancel',
  'aiConfigs.deleteSkill.deleteSkill': 'Delete skill',
  'aiConfigs.deleteSkill.heading': 'Delete “{name}”',
  'aiConfigs.deleteSkill.thisSkillWill': "This permanently deletes the skill. This action can't be undone.",
  'aiConfigs.deleteSnippetDialog.body': "This permanently deletes the snippet. This action can't be undone.",
  'aiConfigs.deleteSnippetDialog.cancel': 'Cancel',
  'aiConfigs.deleteSnippetDialog.deleteFailed': 'Failed to delete snippet',
  'aiConfigs.deleteSnippetDialog.deleteSnippet': 'Delete snippet',
  'aiConfigs.deleteSnippetDialog.deleted': 'Snippet deleted',
  'aiConfigs.deleteSnippetDialog.heading': 'Delete “{name}”',
  'aiConfigs.desiredDirectionField.cancelEditLabel': 'Cancel editing desired direction',
  'aiConfigs.desiredDirectionField.desiredDirection': 'Desired direction',
  'aiConfigs.desiredDirectionField.editLabel': 'Edit desired direction',
  'aiConfigs.desiredDirectionField.higherIsBetter': 'Higher is better',
  'aiConfigs.desiredDirectionField.lowerIsBetter': 'Lower is better',
  'aiConfigs.desiredDirectionField.optionsLabel': 'Desired direction options',
  'aiConfigs.desiredDirectionField.saveLabel': 'Save desired direction',
  'aiConfigs.desiredDirectionField.selectLabel': 'Select desired direction',
  'aiConfigs.desiredDirectionField.updateError': 'Failed to update AI Config. Try again later.',
  'aiConfigs.desiredDirectionField.updatedToast': 'Event direction updated',
  'aiConfigs.detailPage.backToAi': 'Back to AI configs',
  'aiConfigs.detailPage.title': 'AI config not found',
  'aiConfigs.detailsHeader.archived': 'Archived',
  'aiConfigs.detailsHeader.expandAllLabel': 'Expand all variations',
  'aiConfigs.detailsHeader.formHasErrors': 'Fix the errors in the form',
  'aiConfigs.detailsHeader.modelFilterLabel': 'Select a model',
  'aiConfigs.detailsHeader.newVariationIncomplete': 'Instructions, name, and model are required for new variations',
  'aiConfigs.detailsHeader.noChangesToSave': 'No changes to save. Edit the config to enable saving.',
  'aiConfigs.detailsHeader.payloadTooBig':
    'Configs have a payload limit of {limit}kb. Shorten your messages or remove variations.',
  'aiConfigs.detailsHeader.published': 'Published',
  'aiConfigs.detailsHeader.saveChangesLabel': 'Save changes',
  'aiConfigs.detailsHeader.variationNamesNotUnique': 'All variations must have unique names within this config',
  'aiConfigs.detailsHeader.variationsIncomplete': 'All variations must have a name and model',
  'aiConfigs.detailsHeader.viewFilterLabel': 'Select a view',
  'aiConfigs.detailsModelParameters.addParameterLabel': 'Add model parameters',
  'aiConfigs.detailsModelParameters.addParameters': 'Add parameters',
  'aiConfigs.detailsModelParameters.adjustLabel': 'Adjust parameters',
  'aiConfigs.detailsModelParameters.baseValue': 'Base value',
  'aiConfigs.detailsModelParameters.customParametersDescription':
    'Custom parameters are not used directly by the model call, but are passed to the SDK as a JSON object',
  'aiConfigs.detailsModelParameters.customParametersTitle': 'Custom parameters',
  'aiConfigs.detailsModelParameters.editParameters': 'Edit parameters',
  'aiConfigs.detailsModelParameters.invalidJson': 'Invalid JSON',
  'aiConfigs.detailsModelParameters.key': 'Key',
  'aiConfigs.detailsModelParameters.modelParametersDescription':
    'Model parameters are used directly by the model call to modify its behavior',
  'aiConfigs.detailsModelParameters.modelParametersTitle': 'Model parameters',
  'aiConfigs.detailsModelParameters.overflowLabel': '{count} more parameters',
  'aiConfigs.detailsModelParameters.overrideValue': 'Override value',
  'aiConfigs.detailsModelParameters.parametersLabel': 'Parameters',
  'aiConfigs.detailsModelParameters.togglePanelLabel': 'Toggle {title}',
  'aiConfigs.detailsModelParameters.value': 'Value',
  'aiConfigs.detailsPage.createVariationFailed': 'Failed to create new config variation. Try again.',
  'aiConfigs.detailsPage.deleteVariationFailed': 'Failed to delete config variation. Try again later.',
  'aiConfigs.detailsPage.failedToLoad': 'Failed to load config variation',
  'aiConfigs.detailsPage.importVariationFailed': 'Failed to import config variation. Try again.',
  'aiConfigs.detailsPage.liveBehaviorWarningBody':
    'This variation is used in targeting rules. To prevent unintended behavior, we recommend saving your edits in a new variation.',
  'aiConfigs.detailsPage.liveBehaviorWarningHeading': 'Editing this variation may affect live behavior',
  'aiConfigs.detailsPage.restoringVersion': 'Restoring version…',
  'aiConfigs.detailsPage.tryAgain': 'Try again',
  'aiConfigs.detailsPage.updateVariationsFailed': 'Failed to update all config variations. Try again.',
  'aiConfigs.detailsPage.variationCreated': 'Config variation created',
  'aiConfigs.detailsPage.variationDeleted': 'Config variation deleted',
  'aiConfigs.detailsPage.variationStateChanged':
    'Config variation {state, select, archived {archived} other {published}}',
  'aiConfigs.detailsPage.variationsUpdated': 'Config variations updated',
  'aiConfigs.detailsPage.variationsUpdatedAndCreated': 'Config variations updated, 1 created',
  'aiConfigs.diffView.split': 'Split',
  'aiConfigs.diffView.splitLabel': 'Show split diff',
  'aiConfigs.diffView.unified': 'Unified',
  'aiConfigs.diffView.unifiedLabel': 'Show unified diff',
  'aiConfigs.displayPreferences.createdDate': 'Created date',
  'aiConfigs.displayPreferences.description': 'Description',
  'aiConfigs.displayPreferences.display': 'Display',
  'aiConfigs.displayPreferences.key': 'Key',
  'aiConfigs.displayPreferences.maintainer': 'Maintainer',
  'aiConfigs.displayPreferences.mode': 'Mode',
  'aiConfigs.displayPreferences.preferKeyOverName': 'Use key instead of name',
  'aiConfigs.displayPreferences.showTheseProperties': 'Show these properties',
  'aiConfigs.displayPreferences.tags': 'Tags',
  'aiConfigs.droppedParameters.body':
    "Some parameters couldn't be carried over due to key or type mismatches. Review your settings before saving.",
  'aiConfigs.droppedParameters.footer':
    'Review your settings before saving. To use parameters without a built-in setting, add them to the custom field.',
  'aiConfigs.droppedParameters.listIntro': 'The following parameters were unset due to changing the model family:',
  'aiConfigs.droppedParameters.showLess': 'Show less',
  'aiConfigs.droppedParameters.showMore': 'Show more',
  'aiConfigs.emptyState.body':
    'AgentControl lets you manage models, prompts, and behavior from one place. Make changes in production without redeploys, track results, and roll back at any time.',
  'aiConfigs.emptyState.createConfig': 'Create config',
  'aiConfigs.emptyState.heading': 'Control AI in production, without redeploys',
  'aiConfigs.emptyState.readAbout': 'Read about AgentControl',
  'aiConfigs.emptyState.seeExample': 'See example',
  'aiConfigs.entitlementAlert.body': "You've reached the limit of configs for your account.",
  'aiConfigs.entitlementAlert.cta': 'Upgrade now',
  'aiConfigs.entitlementAlert.dismissLabel': 'Dismiss alert',
  'aiConfigs.entitlementAlert.heading': 'Build your next AI feature',
  'aiConfigs.entitlementAlert.moreDetails': 'Tell me more about AgentControl',
  'aiConfigs.evaluateInPlaygroundMenu.choosePlaygroundLabel': 'Choose playground',
  'aiConfigs.evaluateInPlaygroundMenu.closeLabel': 'Close dialog',
  'aiConfigs.evaluateInPlaygroundMenu.fromCode': 'From code',
  'aiConfigs.evaluateInPlaygroundMenu.fromPlayground': 'From playground',
  'aiConfigs.evaluateInPlaygroundMenu.trigger': 'New evaluation',
  'aiConfigs.evaluationRunActions.stopRun': 'Stop run',
  'aiConfigs.evaluationRunActions.stopRunError': 'Error canceling run',
  'aiConfigs.evaluationRunActions.stopRunSuccess': 'Run canceled',
  'aiConfigs.evaluationRunActions.unknownError': 'Unknown error',
  'aiConfigs.evaluationRunConversation.loadingTrace': 'Loading trace',
  'aiConfigs.evaluationRunConversation.rowTitle': 'Trace for row {rowNumber}',
  'aiConfigs.evaluationRunDetails.anUnexpectedError': 'An unexpected error has occurred',
  'aiConfigs.evaluationRunDetails.backToPlayground': 'Back to playground',
  'aiConfigs.evaluationRunDetails.couldNotLoad': 'Could not load evaluation run results.',
  'aiConfigs.evaluationRunDetails.results': 'Results',
  'aiConfigs.evaluationRunDetails.resultsTableLabel': 'Evaluation run row results',
  'aiConfigs.evaluationRunMenu.saveAsNew': 'Save as new config',
  'aiConfigs.evaluationRunMenu.triggerLabel': 'Actions for run {runId}',
  'aiConfigs.evaluationRunMenu.viewFullResults': 'View full results',
  'aiConfigs.evaluationRunResults.failedOnly': 'Failed only',
  'aiConfigs.evaluationRunResults.results': 'Results',
  'aiConfigs.evaluationRunResults.resultsTableLabel': 'Evaluation run row results',
  'aiConfigs.evaluationRunResults.thisRunWas': 'This run was canceled before any results were recorded.',
  'aiConfigs.evaluationRunResults.trace': 'Trace',
  'aiConfigs.evaluationRunResults.traceRowLabel': 'Trace, row {row}',
  'aiConfigs.evaluationRunResults.unexpectedErrorLoading': 'Unexpected error loading evaluation run results.',
  'aiConfigs.evaluationRunSummary.avgTokensLabel': 'Avg tokens/row',
  'aiConfigs.evaluationRunSummary.inlineRows': 'Inline rows',
  'aiConfigs.evaluationRunSummary.legendErrors': '{percent}% ({count}) errors',
  'aiConfigs.evaluationRunSummary.legendFailed': '{percent}% ({count}) failed',
  'aiConfigs.evaluationRunSummary.legendPassed': '{percent}% ({count}) passed',
  'aiConfigs.evaluationRunSummary.legendRemaining': '{percent}% ({count}) remaining',
  'aiConfigs.evaluationRunSummary.metricColumnLabel': 'Metric',
  'aiConfigs.evaluationRunSummary.p50': 'P50',
  'aiConfigs.evaluationRunSummary.p50LatencyLabel': 'P50 latency',
  'aiConfigs.evaluationRunSummary.p95': 'P95',
  'aiConfigs.evaluationRunSummary.p95LatencyLabel': 'P95 latency',
  'aiConfigs.evaluationRunSummary.passRate': 'Pass rate',
  'aiConfigs.evaluationRunSummary.passRateThreshold': 'Pass rate / threshold',
  'aiConfigs.evaluationRunSummary.processed': 'Processed',
  'aiConfigs.evaluationRunSummary.rowsFailed': 'Rows failed',
  'aiConfigs.evaluationRunSummary.rowsProcessed': 'Rows processed: {completed} / {total} ({percent}%)',
  'aiConfigs.evaluationRunSummary.rowsProcessedLabel': 'Rows processed',
  'aiConfigs.evaluationRunSummary.rowsWithErrors': 'Rows with errors',
  'aiConfigs.evaluationRunSummary.runSummary': 'Run summary',
  'aiConfigs.evaluationRunSummary.sliceErrors': 'Errors',
  'aiConfigs.evaluationRunSummary.sliceFailed': 'Failed',
  'aiConfigs.evaluationRunSummary.slicePassed': 'Passed',
  'aiConfigs.evaluationRunSummary.slicePending': 'Pending',
  'aiConfigs.evaluationRunSummary.summaryLabel': 'Run results summary',
  'aiConfigs.evaluationRunSummary.totalRunCostLabel': 'Total run cost',
  'aiConfigs.evaluationRunSummary.worstScore': 'Worst score',
  'aiConfigs.evaluationRunsEmptyState.body': 'Try adjusting your search terms',
  'aiConfigs.evaluationRunsEmptyState.clearSearch': 'Clear search',
  'aiConfigs.evaluationRunsEmptyState.heading': 'No evaluation runs match your search',
  'aiConfigs.evaluationRunsPage.adHocEmptyStateBody': 'Evaluations you run without a dataset show up here.',
  'aiConfigs.evaluationRunsPage.adHocEmptyStateHeading': 'No ad hoc evaluations',
  'aiConfigs.evaluationRunsPage.adHocGroupHeading': 'Ad hoc evals (no dataset)',
  'aiConfigs.evaluationRunsPage.adHocGroupLabel': 'Ad hoc evaluation runs',
  'aiConfigs.evaluationRunsPage.adHocTab': 'Ad hoc evals',
  'aiConfigs.evaluationRunsPage.allTab': 'All',
  'aiConfigs.evaluationRunsPage.body': 'Track and compare all evaluation runs across your playgrounds.',
  'aiConfigs.evaluationRunsPage.codeEmptyStateBody': 'Evaluations you run from your code show up here.',
  'aiConfigs.evaluationRunsPage.codeEmptyStateHeading': 'No evaluations from code',
  'aiConfigs.evaluationRunsPage.codeTab': 'Evals from code',
  'aiConfigs.evaluationRunsPage.datasetEmptyStateBody': 'Evaluations you run against a dataset show up here.',
  'aiConfigs.evaluationRunsPage.datasetEmptyStateHeading': 'No dataset evaluations',
  'aiConfigs.evaluationRunsPage.datasetGroupLabel': 'Evaluation runs for {datasetName}',
  'aiConfigs.evaluationRunsPage.datasetTab': 'Dataset evals',
  'aiConfigs.evaluationRunsPage.docsLinkText': 'Read about offline evaluations',
  'aiConfigs.evaluationRunsPage.emptyStateBody': 'Score a model or prompt on your examples before users see it.',
  'aiConfigs.evaluationRunsPage.emptyStateHeading': 'Run your first evaluation',
  'aiConfigs.evaluationRunsPage.groupByDataset': 'Group by dataset',
  'aiConfigs.evaluationRunsPage.loadError': 'Failed to load evaluation runs.',
  'aiConfigs.evaluationRunsPage.loadingLabel': 'Loading evaluation runs',
  'aiConfigs.evaluationRunsPage.runsListLabel': 'Evaluation runs',
  'aiConfigs.evaluationRunsPage.searchLabel': 'Search evaluation runs',
  'aiConfigs.evaluationRunsPage.searchPlaceholder': 'Search evaluations…',
  'aiConfigs.evaluationRunsPage.showAllEvaluations': 'Show all evaluations',
  'aiConfigs.evaluationRunsPage.tryAgain': 'Try again',
  'aiConfigs.evaluationRunsPage.typeFilterLabel': 'Evaluation run type filter',
  'aiConfigs.evaluationRunsTable.aboutColumnLabel': 'About {text}',
  'aiConfigs.evaluationRunsTable.costPerRunColumn': 'Cost/run',
  'aiConfigs.evaluationRunsTable.costTooltip': 'Costs are estimated based on <link>model configuration</link>',
  'aiConfigs.evaluationRunsTable.datasetColumn': 'Dataset',
  'aiConfigs.evaluationRunsTable.inlineDataset': 'Inline',
  'aiConfigs.evaluationRunsTable.evaluatedOnColumn': 'Evaluated on',
  'aiConfigs.evaluationRunsTable.modelColumn': 'Model',
  'aiConfigs.evaluationRunsTable.promptCellLink': 'Prompt',
  'aiConfigs.evaluationRunsTable.promptColumn': 'Prompt',
  'aiConfigs.evaluationRunsTable.runByColumn': 'Run by',
  'aiConfigs.evaluationRunsTable.statusColumn': 'Status',
  'aiConfigs.evaluationSummary.closeLabel': 'Close',
  'aiConfigs.evaluationSummary.dataset': 'Dataset',
  'aiConfigs.evaluationSummary.datasetLinkLabel': 'View dataset {label} in a new tab',
  'aiConfigs.evaluationSummary.defaultMessageRole': 'Message',
  'aiConfigs.evaluationSummary.details': 'Details',
  'aiConfigs.evaluationSummary.inlineDatasetRowCount': 'Inline ({count, plural, one {# row} other {# rows}})',
  'aiConfigs.evaluationSummary.inlineToolTagLabel': '{toolKey} defined inline',
  'aiConfigs.evaluationSummary.model': 'Model',
  'aiConfigs.evaluationSummary.promptMessageLabel': '{role} message',
  'aiConfigs.evaluationSummary.promptMessages': 'Prompt messages',
  'aiConfigs.evaluationSummary.promptRoleHeading': '{role} prompt',
  'aiConfigs.evaluationSummary.summaryLabel': 'Evaluation summary',
  'aiConfigs.evaluationSummary.toolInlineBadge': 'Inline',
  'aiConfigs.evaluationSummary.toolTagLabel': '{toolKey} v{version}',
  'aiConfigs.evaluationSummary.tools': 'Tools',
  'aiConfigs.evaluationSummary.toolsLabel': 'Evaluation tools',
  'aiConfigs.evaluationSummary.viewPromptMessagesLabel': 'View full prompt messages',
  'aiConfigs.evaluationsListEmptyState.archivedBody':
    'Archived playgrounds appear here. You can archive a playground from the overflow menu on the Active tab.',
  'aiConfigs.evaluationsListEmptyState.archivedHeading': 'No archived playgrounds',
  'aiConfigs.evaluationsListEmptyState.body': 'Create a new playground to get started.',
  'aiConfigs.evaluationsListEmptyState.clearFilters': 'Clear filters',
  'aiConfigs.evaluationsListEmptyState.clearSearch': 'Clear search',
  'aiConfigs.evaluationsListEmptyState.filtersBody': 'Try adjusting your filters',
  'aiConfigs.evaluationsListEmptyState.filtersHeading': 'No runs match your filter criteria',
  'aiConfigs.evaluationsListEmptyState.heading': 'No playgrounds yet',
  'aiConfigs.evaluationsListEmptyState.searchBody': 'Try adjusting your search terms',
  'aiConfigs.evaluationsListEmptyState.searchHeading': 'No playgrounds match your search',
  'aiConfigs.evaluatorMetrics.exportChart': 'Export chart',
  'aiConfigs.evaluatorMetrics.exportCsvLabel': 'Export chart as CSV',
  'aiConfigs.evaluatorMetrics.exportSuccessToast': 'Chart exported successfully',
  'aiConfigs.evaluatorMetrics.judgeLoadErrorDescription': 'Unable to load judges for the following keys: {keys}',
  'aiConfigs.evaluatorMetrics.judgeLoadErrorTitle': 'No judges loaded',
  'aiConfigs.evaluatorMetrics.noEvalKeysDescription':
    'Add evaluation metric keys to the attached judges to view evaluator metrics',
  'aiConfigs.evaluatorMetrics.noEvalKeysTitle': 'No evaluation metric keys',
  'aiConfigs.evaluatorMetrics.noJudgesDescription': 'Attach judges to this AI config to view evaluator metrics',
  'aiConfigs.evaluatorMetrics.noJudgesTitle': 'No judges attached to this AI config',
  'aiConfigs.evaluatorMetrics.unableToLoad': 'Unable to load chart data',
  'aiConfigs.eventKeyField.blankError': 'Event key cannot be blank',
  'aiConfigs.eventKeyField.editLabel': 'Edit event key',
  'aiConfigs.eventKeyField.eventKey': 'Event key',
  'aiConfigs.eventKeyField.formatError': 'Event key can only contain letters, numbers, and hyphens',
  'aiConfigs.eventKeyField.noEventKey': 'No event key',
  'aiConfigs.eventKeyField.resetLabel': 'Reset event key',
  'aiConfigs.eventKeyField.saveLabel': 'Save event key',
  'aiConfigs.eventKeyField.updateError': 'Failed to update AI Config. Try again later.',
  'aiConfigs.eventKeyField.updatedToast': 'Event key updated',
  'aiConfigs.experimentAlert.body':
    'An experiment is running on this AI config, so you cannot delete variations or change their values.',
  'aiConfigs.experimentAlert.heading': 'Variation values are locked during an experiment',
  'aiConfigs.experimentAlert.stopExperiment': 'To edit them, <link>stop the experiment</link>.',
  'aiConfigs.factualityCriteria.evaluationCriteriaDescription':
    'Define the specific criteria for evaluating the factuality of the response. This will be used by G-EVAL to assess the output.',
  'aiConfigs.factualityCriteria.evaluationCriteriaLabel': 'Evaluation criteria',
  'aiConfigs.factualityCriteria.evaluationCriteriaPlaceholder':
    "Enter the criteria for evaluating factuality (e.g., 'Evaluate if the response contains factual inaccuracies or contradictions')",
  'aiConfigs.factualityCriteria.thresholdDescription':
    'Minimum passing threshold for the factuality score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.fileUploadDropzone.acceptedFormats': 'CSV or JSONL (up to {maxFileSize})',
  'aiConfigs.fileUploadDropzone.browseLabel': 'Browse',
  'aiConfigs.fileUploadDropzone.clickToBrowse': 'click to browse',
  'aiConfigs.fileUploadDropzone.dropYourFile': 'Drop your file here or',
  'aiConfigs.fileUploadDropzone.dropzoneLabel': 'Drag to upload file',
  'aiConfigs.filtersDialog.chooseMaintainer': 'Choose maintainer',
  'aiConfigs.filtersDialog.clear': 'Clear',
  'aiConfigs.filtersDialog.createdAnytime': 'At any time',
  'aiConfigs.filtersDialog.createdLabel': 'Created',
  'aiConfigs.filtersDialog.createdOverOneMonth': 'More than 1 month ago',
  'aiConfigs.filtersDialog.createdOverOneMonthTextValue': '1 month ago',
  'aiConfigs.filtersDialog.createdOverOneWeek': 'More than 1 week ago',
  'aiConfigs.filtersDialog.createdOverOneWeekTextValue': '1 week ago',
  'aiConfigs.filtersDialog.createdOverOneYear': 'More than 1 year ago',
  'aiConfigs.filtersDialog.createdOverOneYearTextValue': '1 year ago',
  'aiConfigs.filtersDialog.createdOverSixMonths': 'More than 6 months ago',
  'aiConfigs.filtersDialog.createdOverSixMonthsTextValue': '6 months ago',
  'aiConfigs.filtersDialog.createdOverThreeMonths': 'More than 3 months ago',
  'aiConfigs.filtersDialog.createdOverThreeMonthsTextValue': '3 months ago',
  'aiConfigs.filtersDialog.createdOverTwoMonths': 'More than 2 months ago',
  'aiConfigs.filtersDialog.createdOverTwoMonthsTextValue': '2 months ago',
  'aiConfigs.filtersDialog.heading': 'Filter configs by…',
  'aiConfigs.filtersDialog.loadError': 'Something went wrong while loading filters.',
  'aiConfigs.filtersDialog.loadErrorNotified': "We've been notified.",
  'aiConfigs.filtersDialog.maintainerLabel': 'Maintainer',
  'aiConfigs.filtersDialog.maintainersLoadingLabel': 'Loading…',
  'aiConfigs.filtersDialog.modeLabel': 'Mode',
  'aiConfigs.filtersDialog.tryAgain': 'Try again',
  'aiConfigs.folders.add': 'Add',
  'aiConfigs.folders.addConfigs': 'Add configs',
  'aiConfigs.folders.addSelected': '{count, plural, =0 {Add} one {Add # item} other {Add # items}}',
  'aiConfigs.folders.addSkills': 'Add skills',
  'aiConfigs.folders.addSubfolder': 'New subfolder',
  'aiConfigs.folders.cancel': 'Cancel',
  'aiConfigs.folders.clearFolderSearch': 'Clear search',
  'aiConfigs.folders.collapseAll': 'Collapse all',
  'aiConfigs.folders.collapseFolder': 'Collapse {name}',
  'aiConfigs.folders.create': 'Create',
  'aiConfigs.folders.createFolder': 'Create a new folder',
  'aiConfigs.folders.deleteFolder': 'Delete folder',
  'aiConfigs.folders.deleteFolderHasItems': 'Move all configs and skills out of this folder before deleting it',
  'aiConfigs.folders.folderActions': 'Actions for {name}',
  'aiConfigs.folders.createFolderTitle': 'Create folder',
  'aiConfigs.folders.emptyFolder': 'This folder is empty',
  'aiConfigs.folders.emptyFolderDetail': 'Add configs, skills, or subfolders to keep related work together.',
  'aiConfigs.folders.expandAll': 'Expand all',
  'aiConfigs.folders.expandFolder': 'Expand {name}',
  'aiConfigs.folders.folderNameLabel': 'Folder name',
  'aiConfigs.folders.folderNameRequired': 'Enter a folder name',
  'aiConfigs.folders.foldersHeading': 'Folders',
  'aiConfigs.folders.inThisFolderHeading': 'In {name}',
  'aiConfigs.folders.itemCount': '{count, plural, one {# item} other {# items}}',
  'aiConfigs.folders.loadItemsFailed': 'Couldn’t load items. Close this dialog and try again.',
  'aiConfigs.folders.loadingFolder': 'Loading folder',
  'aiConfigs.folders.loadingItems': 'Loading items',
  'aiConfigs.folders.missingFolder': 'Folder not found',
  'aiConfigs.folders.moveSkillToFolder': 'Move {name} to a folder',
  'aiConfigs.folders.folder': 'Folder',
  'aiConfigs.folders.moveTo': 'Move to',
  'aiConfigs.folders.newFolder': 'New folder',
  'aiConfigs.folders.newHeader': 'New',
  'aiConfigs.folders.nestedPagination': 'Folder pagination',
  'aiConfigs.folders.noItemsToAdd': 'Nothing left to add.',
  'aiConfigs.folders.noMatchingFolders': 'No matching folders',
  'aiConfigs.folders.noSearchResultsInFolder': 'Nothing in this folder matches your search.',
  'aiConfigs.folders.otherResultsHeading': 'Other results',
  'aiConfigs.folders.searchFolders': 'Search folders',
  'aiConfigs.folders.showParentFolders': 'Show parent folders',
  'aiConfigs.folders.searchItems': 'Search by name or key',
  'aiConfigs.folders.removeFromFolder': 'Remove from folder',
  'aiConfigs.folders.rootBreadcrumb': 'Configs',
  'aiConfigs.folders.sectionLabel': 'Config folders',
  'aiConfigs.folders.selectFolder': 'Select a folder',
  'aiConfigs.folders.skillsHeading': 'Skills',
  'aiConfigs.folders.subfoldersHeading': 'Folders',
  'aiConfigs.generatedJudgeCriteria.baseTemplate': 'Base template',
  'aiConfigs.generatedJudgeCriteria.rubricDescription': 'Customize the rubric before running the evaluation.',
  'aiConfigs.generatedJudgeCriteria.rubricLabel': 'Rubric',
  'aiConfigs.generatedJudgeCriteria.rubricPlaceholder': 'Judge system prompt and scoring rubric',
  'aiConfigs.generatedJudgeCriteria.thresholdDescription':
    'Minimum passing threshold for the judge score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.importFromPlayground.anthropicInstructions': 'Paste Anthropic CLI output below',
  'aiConfigs.importFromPlayground.awsInstructions': 'Paste AWS CLI output below',
  'aiConfigs.importFromPlayground.body': 'Bring configurations from external playgrounds to an AI Config version',
  'aiConfigs.importFromPlayground.cancel': 'Cancel',
  'aiConfigs.importFromPlayground.cliInputRequired': 'Model selection is required',
  'aiConfigs.importFromPlayground.closeLabel': 'Close',
  'aiConfigs.importFromPlayground.heading': 'Import from playground',
  'aiConfigs.importFromPlayground.providerPlaceholder': 'Select a provider',
  'aiConfigs.importFromPlayground.providerQuestion': 'Which playground are you using?',
  'aiConfigs.importFromPlayground.providerRequired': 'Model selection is required',
  'aiConfigs.inlineFlowModal.back': 'Back',
  'aiConfigs.inlineFlowModal.closeLabel': 'Close',
  'aiConfigs.inlineFlowModal.continue': 'Continue',
  'aiConfigs.inputDataPanel.addOption': 'Add option',
  'aiConfigs.inputDataPanel.addRow': 'Add row',
  'aiConfigs.inputDataPanel.addVariableChoice': 'Add variable choice',
  'aiConfigs.inputDataPanel.arrayItemLabel': '{label} {number}',
  'aiConfigs.inputDataPanel.downloadSample': 'Download sample',
  'aiConfigs.inputDataPanel.expectedResponse': 'Expected response',
  'aiConfigs.inputDataPanel.expectedResponsePlaceholder': 'Enter expected response',
  'aiConfigs.inputDataPanel.expectedResponseRequired': 'Expected response is required',
  'aiConfigs.inputDataPanel.expectedResponseRowLabel': 'Expected response {number}',
  'aiConfigs.inputDataPanel.groundTruthRows': 'Ground truth rows',
  'aiConfigs.inputDataPanel.inputDataTruncated': 'Input data truncated to 25 rows',
  'aiConfigs.inputDataPanel.jsonObjectError': 'Must be a JSON object (e.g. {example})',
  'aiConfigs.inputDataPanel.parseFileError': 'Failed to parse file',
  'aiConfigs.inputDataPanel.piiRow': 'row {row}',
  'aiConfigs.inputDataPanel.piiRowWarning': 'Possible PII: {types}',
  'aiConfigs.inputDataPanel.piiRows': 'rows {rows}',
  'aiConfigs.inputDataPanel.piiSummary':
    'Possible PII detected in your uploaded data: {details}. Review before saving.',
  'aiConfigs.inputDataPanel.piiTypeApiToken': 'API / SDK key',
  'aiConfigs.inputDataPanel.piiTypeCreditCard': 'Credit card number',
  'aiConfigs.inputDataPanel.piiTypeEmail': 'Email address',
  'aiConfigs.inputDataPanel.piiTypePhone': 'Phone number',
  'aiConfigs.inputDataPanel.piiTypeRows': '{type} ({rows})',
  'aiConfigs.inputDataPanel.piiTypeSsn': 'SSN',
  'aiConfigs.inputDataPanel.readFileError': 'Failed to read file',
  'aiConfigs.inputDataPanel.removeOptionLabel': 'Remove option {number}',
  'aiConfigs.inputDataPanel.removeRowLabel': 'Remove row {number}',
  'aiConfigs.inputDataPanel.removeVariableChoiceLabel': 'Remove variable choice {number}',
  'aiConfigs.inputDataPanel.uploadData': 'Upload data',
  'aiConfigs.inputDataPanel.userInput': 'User input',
  'aiConfigs.inputDataPanel.userInputOptionsLabel': 'User input options',
  'aiConfigs.inputDataPanel.userInputPlaceholder': 'Enter user input',
  'aiConfigs.inputDataPanel.userInputRequired': 'User input is required',
  'aiConfigs.inputDataPanel.userInputRowLabel': 'User input {number}',
  'aiConfigs.inputDataPanel.variableChoices': 'Variable choices',
  'aiConfigs.inputDataPanel.variables': 'Variables',
  'aiConfigs.inputDataPanel.variablesFoundIn': 'Variables found in config:',
  'aiConfigs.instructionsField.agentTask': 'Agent task',
  'aiConfigs.instructionsField.copyLabel': 'Copy',
  'aiConfigs.instructionsField.editAgentTask': 'Edit agent task',
  'aiConfigs.instructionsField.expandLabel': 'Expand',
  'aiConfigs.instructionsField.loadSnippet': 'Load snippet',
  'aiConfigs.instructionsField.newSnippet': 'New snippet',
  'aiConfigs.instructionsField.newVersionOf': 'New version of existing snippet',
  'aiConfigs.instructionsField.required': 'Instructions cannot be empty',
  'aiConfigs.instructionsField.saveSnippetAs': 'Save snippet as…',
  'aiConfigs.instructionsField.saveSnippetLabel': 'Save snippet as',
  'aiConfigs.instructionsField.saveSnippetOptionsLabel': 'Save snippet options',
  'aiConfigs.instructionsField.taskPlaceholder': 'Describe what you want the agent to do.',
  'aiConfigs.iterationDetailDrawer.agentVariationLabel': 'Agent variation',
  'aiConfigs.iterationDetailDrawer.closeLabel': 'Close',
  'aiConfigs.iterationDetailDrawer.cost': 'Cost',
  'aiConfigs.iterationDetailDrawer.inputTabLabel': 'Input {number}',
  'aiConfigs.iterationDetailDrawer.inputsLabel': 'Inputs',
  'aiConfigs.iterationDetailDrawer.instructionsLabel': 'Instructions',
  'aiConfigs.iterationDetailDrawer.latency': 'Latency',
  'aiConfigs.iterationDetailDrawer.output': 'Output',
  'aiConfigs.iterationDetailDrawer.parametersLabel': 'Parameters',
  'aiConfigs.iterationDetailDrawer.showAll': 'Show all',
  'aiConfigs.iterationDetailDrawer.showLess': 'Show less',
  'aiConfigs.iterationDetailDrawer.tokensBreakdown': 'Tokens ({input} in / {output} out)',
  'aiConfigs.iterationDetailDrawer.userInputLabel': 'User input',
  'aiConfigs.judgeCopilotPanel.body':
    'Describe what you want to evaluate. AI picks a judge template and customizes the rubric.',
  'aiConfigs.judgeCopilotPanel.criteriaLabel': 'Evaluation criteria',
  'aiConfigs.judgeCopilotPanel.criteriaPlaceholder': 'Score responses for hallucination about pricing',
  'aiConfigs.judgeCopilotPanel.generateErrorToastBody':
    'Try again with a more specific description of what you want to evaluate.',
  'aiConfigs.judgeCopilotPanel.generateErrorToastTitle': 'Could not generate judge',
  'aiConfigs.judgeCopilotPanel.generateJudge': 'Generate judge',
  'aiConfigs.judgeCopilotPanel.heading': 'Judge copilot',
  'aiConfigs.judgeCopilotPanel.missingCriteriaToastBody':
    'Enter what you want the judge to evaluate before generating.',
  'aiConfigs.judgeCopilotPanel.missingCriteriaToastTitle': 'Describe your criteria',
  'aiConfigs.judgeDetails.body': 'JudgeTemplateDetails - {name}',
  'aiConfigs.judgeDetails.invalidJudgeType': 'Invalid judge type',
  'aiConfigs.judgeDetails.judgeTypeNot': 'Judge type not found',
  'aiConfigs.judgeInUseAlert.body': 'This config can’t be deleted because other configs use it.',
  'aiConfigs.judgeInUseAlert.removeFirst': 'Remove it from these configs first:',
  'aiConfigs.judgeInUseDeleteDialog.close': 'Close',
  'aiConfigs.judgeInUseDeleteDialog.heading': 'Delete config',
  'aiConfigs.judgeModelPicker.allModelsSection': 'All',
  'aiConfigs.judgeModelPicker.model': 'Model',
  'aiConfigs.judgeModelPicker.noModelsFound': 'No {provider} models found',
  'aiConfigs.judgeModelPicker.searchLabel': 'Search models…',
  'aiConfigs.judgeModelPicker.searchPlaceholder': 'Search…',
  'aiConfigs.judgeModelPicker.selectModel': 'Select model',
  'aiConfigs.judgeModelPicker.validationLabel': 'Model validation',
  'aiConfigs.judgeProviderPicker.chooseYourProvider': 'Choose your provider',
  'aiConfigs.judgeProviderPicker.noProvidersFound': 'No providers found',
  'aiConfigs.judgeProviderPicker.optionsLabel': 'Provider options',
  'aiConfigs.judgeProviderPicker.provider': 'Provider',
  'aiConfigs.judgeProviderPicker.searchLabel': 'Search providers…',
  'aiConfigs.judgeProviderPicker.searchPlaceholder': 'Find a provider',
  'aiConfigs.judgeProviderPicker.triggerLabel': 'Select a provider',
  'aiConfigs.judgeProviderPicker.validationLabel': 'Provider validation',
  'aiConfigs.judgeSelection.alreadyAttached': 'Judge already attached',
  'aiConfigs.judgeSelection.attachJudges': 'Attach judges',
  'aiConfigs.judgeSelection.createJudge': 'Create judge',
  'aiConfigs.judgeSelection.createJudgesFailed': 'Failed to create {count} {count, plural, one {judge} other {judges}}',
  'aiConfigs.judgeSelection.detachFailedJudges':
    'Detach the failed {count, plural, one {judge} other {judges}} below and try again.',
  'aiConfigs.judgeSelection.eventKeyValue': 'Event key: <code>{eventKey}</code>',
  'aiConfigs.judgeSelection.listLabel': 'Available judges',
  'aiConfigs.judgeSelection.quickFind': 'Quick find',
  'aiConfigs.judgeSelection.searchLabel': 'Search judges',
  'aiConfigs.judgeSelection.searchPlaceholder': 'Search',
  'aiConfigs.judgeSelection.triggerLabel': 'Select a judge',
  'aiConfigs.judgeSelection.viewRequiredTitle': 'View required',
  'aiConfigs.judgeTypePicker.custom': 'Custom',
  'aiConfigs.judgeTypePicker.customLabel': 'Custom judge type',
  'aiConfigs.judgeTypePicker.fieldLabel': 'Judge type',
  'aiConfigs.judgeTypePicker.helpText':
    'The judge type sets the evaluation criteria you start from, such as accuracy, relevance, or toxicity. Select Custom to describe your own criteria. Judges are fully customizable. You can change or remove any rules and values after creation.',
  'aiConfigs.judgeTypePicker.listLabel': 'Judge types',
  'aiConfigs.judgeTypePicker.validationLabel': 'Type validation',
  'aiConfigs.judgeUsedBy.body':
    'This judge is used by <strong>{count, plural, one {# config} other {# configs}}</strong>.',
  'aiConfigs.judgeUsedBy.heading': 'Changes to this judge may impact these configs',
  'aiConfigs.judgeUsedBy.showLess': 'Show less',
  'aiConfigs.judgeUsedBy.showMore': 'Show more',
  'aiConfigs.judgesField.addJudges': 'Add judges',
  'aiConfigs.judgesField.alreadyAttachedToast': 'Judge already attached',
  'aiConfigs.judgesField.createJudgeLabel': 'Create judge',
  'aiConfigs.judgesField.pickerDialogLabel': 'Select a judge',
  'aiConfigs.judgesField.viewJudgeScores': 'View judge scores in Monitoring',
  'aiConfigs.judgesList.createJudge': 'Create judge',
  'aiConfigs.judgesList.createJudgeTooltip': 'Create {article} {type} judge',
  'aiConfigs.judgesList.description': 'Evaluate your configs with judges.',
  'aiConfigs.judgesList.docsLink': 'Read more about custom judges',
  'aiConfigs.judgesList.evaluationMetricKeyLabel': 'Evaluation metric key',
  'aiConfigs.judgesList.key': 'Key',
  'aiConfigs.judgesList.name': 'Name',
  'aiConfigs.judgesList.searchEmptyStateBody': 'Try adjusting your search',
  'aiConfigs.judgesList.searchEmptyStateHeading': 'No judges found',
  'aiConfigs.judgesList.searchLabel': 'Search judges',
  'aiConfigs.judgesList.searchPlaceholder': 'Search judges…',
  'aiConfigs.judgesMenu.addJudges': 'Add judges',
  'aiConfigs.judgesMenu.addSelected': 'Add {count, plural, one {# judge} other {# judges}}',
  'aiConfigs.judgesMenu.allJudges': 'All judges',
  'aiConfigs.judgesMenu.applySelectionLabel': 'Apply judge selection',
  'aiConfigs.judgesMenu.createJudge': 'Create judge',
  'aiConfigs.judgesMenu.key': 'Key',
  'aiConfigs.judgesMenu.model': 'Model',
  'aiConfigs.judgesMenu.noJudgesFound': 'No judges found',
  'aiConfigs.judgesMenu.optionsLabel': 'Judge options',
  'aiConfigs.judgesMenu.prompt': 'Prompt',
  'aiConfigs.judgesMenu.searchLabel': 'Search judges',
  'aiConfigs.judgesMenu.searchPlaceholder': 'Search judges…',
  'aiConfigs.judgesTable.eventKey': 'Event key',
  'aiConfigs.judgesTable.judgeNotFound': 'Judge not found',
  'aiConfigs.judgesTable.judges': 'Judges',
  'aiConfigs.judgesTable.loadingDetailsLabel': 'Loading judge details…',
  'aiConfigs.judgesTable.noEventKey': 'No event key',
  'aiConfigs.judgesTable.provider': 'Provider',
  'aiConfigs.judgesTable.removeAll': 'Remove all',
  'aiConfigs.judgesTable.removeJudgeLabel': 'Remove judge',
  'aiConfigs.judgesTable.samplingPercentage': 'Sampling percentage',
  'aiConfigs.judgesTable.samplingRateLabel': 'Sampling rate for {judgeName}. {tooltip}',
  'aiConfigs.judgesTable.tableLabel': 'Attached judges',
  'aiConfigs.legacyToolsWarning.legacyTools': 'This variation uses legacy tools.',
  'aiConfigs.legacyToolsWarning.overwriteWarning': 'Adding tools will overwrite the model parameters.',
  'aiConfigs.likenessCriteria.controlResponseDescription':
    'The ideal or reference response to compare against the actual output.',
  'aiConfigs.likenessCriteria.controlResponseLabel': 'Control response',
  'aiConfigs.likenessCriteria.controlResponsePlaceholder': 'Enter the control response',
  'aiConfigs.likenessCriteria.evaluationCriteriaDescription':
    'Define the specific criteria for evaluating the likeness between the response and control response. This will be used by G-EVAL to assess the output.',
  'aiConfigs.likenessCriteria.evaluationCriteriaLabel': 'Evaluation criteria',
  'aiConfigs.likenessCriteria.evaluationCriteriaPlaceholder':
    "Enter the criteria for evaluating likeness (e.g., 'Evaluate the semantic similarity and structural alignment between the response and control response')",
  'aiConfigs.likenessCriteria.thresholdDescription':
    'Minimum passing threshold for the likeness score (0.0 to 1.0). Default is 0.5.',
  'aiConfigs.listEmptyState.addToView': 'Add {modeLabel}',
  'aiConfigs.listEmptyState.agentConfigsLabel': 'agent configs',
  'aiConfigs.listEmptyState.body': 'Use configs to manage your AI-powered features',
  'aiConfigs.listEmptyState.clearFilters': 'Clear filters',
  'aiConfigs.listEmptyState.clearView': 'Clear view',
  'aiConfigs.listEmptyState.completionConfigsLabel': 'completion configs',
  'aiConfigs.listEmptyState.configsLabel': 'configs',
  'aiConfigs.listEmptyState.createAgent': 'Create agent',
  'aiConfigs.listEmptyState.createCompletionConfig': 'Create completion config',
  'aiConfigs.listEmptyState.createConfig': 'Create config',
  'aiConfigs.listEmptyState.createJudge': 'Create judge',
  'aiConfigs.listEmptyState.filteredBody': 'Try adjusting your filters',
  'aiConfigs.listEmptyState.filteredHeading': 'No {modeLabel} match your filter criteria',
  'aiConfigs.listEmptyState.heading': "This project doesn't have any {modeLabel}",
  'aiConfigs.listEmptyState.judgeConfigsLabel': 'judge configs',
  'aiConfigs.listEmptyState.readAboutAgents': 'Read about agent configs',
  'aiConfigs.listEmptyState.readAboutAiConfigs': 'Read about AI Configs',
  'aiConfigs.listEmptyState.readAboutJudges': 'Read about custom judges',
  'aiConfigs.listEmptyState.trialBody':
    'Create and iterate on prompts and model settings safely using configs. Your first one’s free with no time limit.',
  'aiConfigs.listEmptyState.trialHeading': 'Build with AI models and prompts',
  'aiConfigs.listEmptyState.viewBody':
    'Add {modeLabel} to this view, or clear the view to see all {modeLabel} in the project.',
  'aiConfigs.listEmptyState.viewFilteredBody': 'Try adjusting your filters, or add more {modeLabel} to this view.',
  'aiConfigs.listEmptyState.viewFilteredHeading': 'No {modeLabel} match your filters in this view',
  'aiConfigs.listEmptyState.viewHeading': 'No {modeLabel} in this view',
  'aiConfigs.listFilters.activeCountLabel': '{count, plural, one {# active filter} other {# active filters}}',
  'aiConfigs.listFilters.trigger': 'Filters',
  'aiConfigs.listOrdering.createdNewest': 'Created newest',
  'aiConfigs.listOrdering.createdOldest': 'Created oldest',
  'aiConfigs.listOrdering.heading': 'Sort',
  'aiConfigs.listOrdering.lastModifiedNewest': 'Last modified newest',
  'aiConfigs.listOrdering.lastModifiedOldest': 'Last modified oldest',
  'aiConfigs.listOrdering.nameAZ': 'Name A-Z',
  'aiConfigs.listOrdering.nameZA': 'Name Z-A',
  'aiConfigs.listPage.connectActionText': 'Connect your app to get started with AgentControl.',
  'aiConfigs.listPage.create': 'Create',
  'aiConfigs.listPage.createConfig': 'Create config',
  'aiConfigs.listPage.failedToLoad': 'Failed to load AI configs',
  'aiConfigs.listPage.initializeAgent': 'Initialize agent',
  'aiConfigs.listPage.label': 'Loading…',
  'aiConfigs.listPage.loading': 'Loading…',
  'aiConfigs.listPage.modeFilterLabel': 'Config mode filter',
  'aiConfigs.listPage.searchLabel': 'search ai configs',
  'aiConfigs.listPage.searchPlaceholder': 'Search fields by name, description or key',
  'aiConfigs.listPage.somethingWentWrong': 'Something went wrong',
  'aiConfigs.listPage.tryAgain': 'Try again',
  'aiConfigs.liveEvaluations.all': 'All',
  'aiConfigs.liveEvaluations.allAccuracyRelevance': 'All (Accuracy, Relevance, Toxicity)',
  'aiConfigs.liveEvaluations.body': 'Evaluate your AI config in real-time on production traffic.',
  'aiConfigs.liveEvaluations.liveEvaluation': 'Live evaluation',
  'aiConfigs.liveEvaluations.removeEvaluationLabel': 'Remove evaluation',
  'aiConfigs.liveEvaluations.selectEvaluators': 'Select evaluators ({count})',
  'aiConfigs.maintainerField.chooseMaintainer': 'Choose maintainer',
  'aiConfigs.maintainerField.editLabel': 'Edit maintainer',
  'aiConfigs.maintainerField.emptyState': 'Anyone',
  'aiConfigs.maintainerField.failedToLoad': 'Failed to load maintainers',
  'aiConfigs.maintainerField.loadingLabel': 'Loading…',
  'aiConfigs.maintainerField.pickerLabel': 'Maintainer',
  'aiConfigs.maintainerField.saving': 'Saving…',
  'aiConfigs.maintainerField.title': 'Maintainer',
  'aiConfigs.maintainerField.tryAgain': 'Try again',
  'aiConfigs.maintainerField.updatedToast': 'Maintainer updated',
  'aiConfigs.manageApiKeys.addApiKey': 'Add API key',
  'aiConfigs.manageApiKeys.body': 'View and manage your connected model providers.',
  'aiConfigs.manageApiKeys.cancel': 'Cancel',
  'aiConfigs.manageApiKeys.delete': 'Delete',
  'aiConfigs.manageApiKeys.deleteConfirmBody':
    'This action cannot be undone. This will permanently delete the API key "{name}".',
  'aiConfigs.manageApiKeys.deleteConfirmHeading': 'Delete API key',
  'aiConfigs.manageApiKeys.deleteErrorTitle': 'Failed to delete API key',
  'aiConfigs.manageApiKeys.deletedToast': '{name} deleted',
  'aiConfigs.manageApiKeys.disableErrorTitle': 'Failed to disable current API key',
  'aiConfigs.manageApiKeys.done': 'Close',
  'aiConfigs.manageApiKeys.emptyState': 'No API keys yet. Add an API key to connect a provider.',
  'aiConfigs.manageApiKeys.enable': 'Enable',
  'aiConfigs.manageApiKeys.enableConfirmBody':
    'Only one API key per provider can be enabled. This disables <strong>{currentName}</strong> and enables <strong>{newName}</strong>.',
  'aiConfigs.manageApiKeys.enableConfirmHeading': 'Enable API key',
  'aiConfigs.manageApiKeys.enableErrorTitle': 'Failed to enable API key',
  'aiConfigs.manageApiKeys.enabled': 'Enabled',
  'aiConfigs.manageApiKeys.enabledToast': '{name} enabled',
  'aiConfigs.manageApiKeys.heading': 'Manage API keys',
  'aiConfigs.manageApiKeys.retryBody': 'Try again.',
  'aiConfigs.manageApiKeys.rowActionsLabel': 'Actions for {name}',
  'aiConfigs.messagesField.addMessage': 'Add message',
  'aiConfigs.messagesField.atLeastOneMessage': 'At least one message is required',
  'aiConfigs.messagesField.closeLabel': 'Close',
  'aiConfigs.messagesField.contentPlaceholder': 'You are a helpful assistant.',
  'aiConfigs.messagesField.contentRequired': 'Message cannot be empty',
  'aiConfigs.messagesField.copyLabel': 'Copy',
  'aiConfigs.messagesField.deleteMessageLabel': 'Delete message',
  'aiConfigs.messagesField.editMessage': 'Edit message',
  'aiConfigs.messagesField.editorLabel': 'Message editor',
  'aiConfigs.messagesField.expandLabel': 'Expand',
  'aiConfigs.messagesField.loadSnippet': 'Load snippet',
  'aiConfigs.messagesField.messageContentLabel': 'Message content',
  'aiConfigs.messagesField.messageLabel': 'Message',
  'aiConfigs.messagesField.newSnippet': 'New snippet',
  'aiConfigs.messagesField.newVersionOf': 'New version of existing snippet',
  'aiConfigs.messagesField.saveSnippetAs': 'Save snippet as…',
  'aiConfigs.messagesField.saveSnippetLabel': 'Save snippet as',
  'aiConfigs.messagesField.saveSnippetOptionsLabel': 'Save snippet options',
  'aiConfigs.messagesField.snippetAppliedToast':
    'Snippet updated and applied to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.messagesField.snippetSaveFailedBody': 'Failed to save snippet. Try again later.',
  'aiConfigs.messagesField.snippetSaveFailedToast': 'Failed to save snippet',
  'aiConfigs.messagesField.snippetUpdatedToast': 'Snippet successfully updated',
  'aiConfigs.metricSelector.allMetrics': 'All metrics',
  'aiConfigs.metricSelector.apply': 'Apply',
  'aiConfigs.metricSelector.cancel': 'Cancel',
  'aiConfigs.metricSelector.customMetrics': 'Custom metrics',
  'aiConfigs.metricSelector.maxSelected': 'You can select up to {max} metrics',
  'aiConfigs.metricSelector.noCustomMetrics': 'No custom metrics',
  'aiConfigs.metricSelector.noMatchingMetrics': 'No matching metrics',
  'aiConfigs.metricSelector.searchMetrics': 'Search metrics',
  'aiConfigs.misinformationCriteria.groundTruthDescription':
    'The verified or ground truth information that will be used to detect misinformation in the response.',
  'aiConfigs.misinformationCriteria.groundTruthLabel': 'Ground truth context',
  'aiConfigs.misinformationCriteria.groundTruthPlaceholder':
    'Enter the ground truth or verified information to compare against',
  'aiConfigs.misinformationCriteria.thresholdDescription':
    'Maximum allowed misinformation score (0.0 to 1.0). Responses with misinformation scores above this threshold will be flagged. Default is 0.5.',
  'aiConfigs.modelConfigDetail.deleteErrorBody': 'Failed to delete AI model config',
  'aiConfigs.modelConfigDetail.deleteInUseError':
    'AI model config is still in use by an AI Config and cannot be deleted',
  'aiConfigs.modelConfigDetail.deleteModel': 'Delete AI model config',
  'aiConfigs.modelConfigDetail.deletedToast': 'Model config deleted',
  'aiConfigs.modelConfigDetail.notFound': 'Model config {configKey} not found',
  'aiConfigs.modelConfigForm.addBody':
    'Add a model that is not in the list LaunchDarkly provides, then use it in any variation in this project.',
  'aiConfigs.modelConfigForm.addCustomParameters': 'Add custom parameters',
  'aiConfigs.modelConfigForm.addErrorToast': 'Failed to add custom model {name}',
  'aiConfigs.modelConfigForm.addHeading': 'Add custom model',
  'aiConfigs.modelConfigForm.addParameterLabel': 'Add',
  'aiConfigs.modelConfigForm.addSuggestedParameters': 'Add suggested parameters',
  'aiConfigs.modelConfigForm.addTags': 'Add tags',
  'aiConfigs.modelConfigForm.cachedInputTokenCostLabel': 'Cached input token cost',
  'aiConfigs.modelConfigForm.changeMaintainer': 'Change maintainer',
  'aiConfigs.modelConfigForm.closeLabel': 'Close',
  'aiConfigs.modelConfigForm.customParametersBody':
    'JSON object for your own app-specific data. Available in the SDK as <code>model.custom</code> and not sent to the model.',
  'aiConfigs.modelConfigForm.customParametersHeading': 'Custom parameters',
  'aiConfigs.modelConfigForm.customProviderOption': 'Custom',
  'aiConfigs.modelConfigForm.docsLinkText': 'Read about custom models',
  'aiConfigs.modelConfigForm.duplicateModelError': 'A model with the name {name} and model ID {modelId} already exists',
  'aiConfigs.modelConfigForm.editBody': 'Update this custom model. Any variation in this project can use it.',
  'aiConfigs.modelConfigForm.editHeading': 'Edit custom model',
  'aiConfigs.modelConfigForm.filterParametersPlaceholder': 'Filter parameters',
  'aiConfigs.modelConfigForm.inputTokenCostLabel': 'Input token cost',
  'aiConfigs.modelConfigForm.invalidJsonError': 'Invalid JSON',
  'aiConfigs.modelConfigForm.loadingMaintainersLabel': 'Loading maintainers…',
  'aiConfigs.modelConfigForm.maintainerHeading': 'Maintainer',
  'aiConfigs.modelConfigForm.manageTagsLabel': 'Manage tags',
  'aiConfigs.modelConfigForm.modelAddedToast': 'Custom model {name} added',
  'aiConfigs.modelConfigForm.modelFamilyHeading': 'Model family',
  'aiConfigs.modelConfigForm.modelIdExample': 'Example: "gpt-4o-2024-08-06"',
  'aiConfigs.modelConfigForm.modelIdHeading': 'Model ID',
  'aiConfigs.modelConfigForm.modelIdHelp':
    'The model ID is the identifier for the model in the LLM provider. This should map to the model you wish to use.',
  'aiConfigs.modelConfigForm.modelIdImmutableHelp':
    'The model ID must match the identifier the provider uses. You cannot change it after you save this model.',
  'aiConfigs.modelConfigForm.modelIdLockedHelp': 'Model ID cannot be changed for an existing model.',
  'aiConfigs.modelConfigForm.modelIdRequiredError': 'Model ID is required',
  'aiConfigs.modelConfigForm.modelUpdatedToast': 'Custom model updated',
  'aiConfigs.modelConfigForm.nameExample': 'Example: "GPT4 Tuned"',
  'aiConfigs.modelConfigForm.nameHeading': 'Name',
  'aiConfigs.modelConfigForm.nameHelp':
    'The name is how you identify this model within LaunchDarkly. It does not map to any model properties.',
  'aiConfigs.modelConfigForm.nameRequiredError': 'Name is required',
  'aiConfigs.modelConfigForm.noMaintainer': 'No maintainer',
  'aiConfigs.modelConfigForm.noTags': 'No tags',
  'aiConfigs.modelConfigForm.notAnObjectError': 'Must be a JSON object',
  'aiConfigs.modelConfigForm.outputTokenCostLabel': 'Output token cost',
  'aiConfigs.modelConfigForm.parameterDocsLink': "View in model provider's API docs",
  'aiConfigs.modelConfigForm.parameterNameTooltip': 'Parameter name: <strong>{param}</strong>',
  'aiConfigs.modelConfigForm.parameterTypeLabel': 'Type',
  'aiConfigs.modelConfigForm.parametersBody':
    'Generation settings JSON (temperature, top_p, max_tokens, etc.) passed directly to the model.',
  'aiConfigs.modelConfigForm.parametersHeading': 'Model parameters',
  'aiConfigs.modelConfigForm.parametersJsonLabel': 'Model parameters JSON',
  'aiConfigs.modelConfigForm.perMillionTokens': '/ million tokens',
  'aiConfigs.modelConfigForm.providerHeading': 'Provider',
  'aiConfigs.modelConfigForm.providerImmutableHelp': 'You cannot change the provider after you save this model.',
  'aiConfigs.modelConfigForm.providerLockedHelp': 'Provider cannot be changed for an existing model.',
  'aiConfigs.modelConfigForm.removeParameterLabel': 'Remove parameter',
  'aiConfigs.modelConfigForm.save': 'Save',
  'aiConfigs.modelConfigForm.saveChanges': 'Save changes',
  'aiConfigs.modelConfigForm.saving': 'Saving changes…',
  'aiConfigs.modelConfigForm.selectModelFamilyLabel': 'Select a model family',
  'aiConfigs.modelConfigForm.selectProviderLabel': 'Select a provider',
  'aiConfigs.modelConfigForm.suggestedParametersLabel': 'Suggested parameters',
  'aiConfigs.modelConfigForm.tagCount': '{count, plural, one {# tag} other {# tags}}',
  'aiConfigs.modelConfigForm.tagTooLongError': 'Each tag must be at most 64 characters',
  'aiConfigs.modelConfigForm.tagsHeading': 'Tags',
  'aiConfigs.modelConfigForm.tokenCostNotNumberError': 'Token costs must be numbers',
  'aiConfigs.modelConfigForm.tooManyTagsError': 'You can add at most 32 tags',
  'aiConfigs.modelConfigForm.updateErrorToast':
    'The custom model could not be updated. Check your changes and try again.',
  'aiConfigs.modelConfigsFilters.activeFilterCount': '{count, plural, one {# active filter} other {# active filters}}',
  'aiConfigs.modelConfigsFilters.any': 'Any',
  'aiConfigs.modelConfigsFilters.availability': 'Availability',
  'aiConfigs.modelConfigsFilters.availableOnly': 'Available only',
  'aiConfigs.modelConfigsFilters.chooseMaintainer': 'Choose maintainer',
  'aiConfigs.modelConfigsFilters.customModelsOnly': 'Custom models only',
  'aiConfigs.modelConfigsFilters.dialogLabel': 'model type and availability filters',
  'aiConfigs.modelConfigsFilters.globalModelsOnly': 'Global models only',
  'aiConfigs.modelConfigsFilters.loadingLabel': 'Loading…',
  'aiConfigs.modelConfigsFilters.maintainerFieldLabel': 'Maintainer',
  'aiConfigs.modelConfigsFilters.modelType': 'Model Type',
  'aiConfigs.modelConfigsFilters.restrictedOnly': 'Restricted only',
  'aiConfigs.modelConfigsFilters.triggerLabel': 'Filters',
  'aiConfigs.modelConfigsList.availability': 'Availability',
  'aiConfigs.modelConfigsList.availableTag': 'Available',
  'aiConfigs.modelConfigsList.bulkAddRestriction': 'Add Restriction',
  'aiConfigs.modelConfigsList.bulkMakeAvailable': 'Make Available',
  'aiConfigs.modelConfigsList.createCustomModel': 'Create custom model',
  'aiConfigs.modelConfigsList.customTag': 'Custom',
  'aiConfigs.modelConfigsList.delete': 'Delete',
  'aiConfigs.modelConfigsList.docsLinkText': 'Read more about model configs',
  'aiConfigs.modelConfigsList.maintainer': 'Maintainer',
  'aiConfigs.modelConfigsList.model': 'Model',
  'aiConfigs.modelConfigsList.modelId': 'Model: {modelId}',
  'aiConfigs.modelConfigsList.modelUsageLabel': 'Model usage',
  'aiConfigs.modelConfigsList.noAiModel': 'No AI model configs found',
  'aiConfigs.modelConfigsList.notAuthorizedTooltip': 'You are not authorized to make changes on this page',
  'aiConfigs.modelConfigsList.otherProvider': 'Other',
  'aiConfigs.modelConfigsList.restrict': 'Restrict',
  'aiConfigs.modelConfigsList.restrictedTag': 'Restricted',
  'aiConfigs.modelConfigsList.rowActionsLabel': 'env actions',
  'aiConfigs.modelConfigsList.rowMakeAvailable': 'Make available',
  'aiConfigs.modelConfigsList.searchLabel': 'model config search field',
  'aiConfigs.modelConfigsList.searchPlaceholder': 'Search models…',
  'aiConfigs.modelConfigsList.settingsDescription':
    'Create custom model configs and restrict which models your team can use with AI Configs.',
  'aiConfigs.modelConfigsList.tableLabel': 'model config list',
  'aiConfigs.modelConfigsList.tagsColumnLabel': 'Tags',
  'aiConfigs.modelConfigsList.usageLoadErrorTitle': 'Could not load usage',
  'aiConfigs.modelConfigsList.usedInColumnLabel': 'Used in',
  'aiConfigs.modelConfigsList.versionColumnLabel': 'Version',
  'aiConfigs.modelConfigsList.versionHistoryLabel': 'Open version history for {name}, currently v{version}',
  'aiConfigs.modelConfigsOrdering.alphabetical': 'Alphabetical',
  'aiConfigs.modelConfigsOrdering.menuLabel': 'group sort order',
  'aiConfigs.modelConfigsOrdering.modelCount': 'Model count',
  'aiConfigs.modelConfigsOrdering.orderBy': 'Order By',
  'aiConfigs.modelConfigsOrdering.triggerLabel': 'Order providers by: {label}',
  'aiConfigs.modelParameters.add': 'Add',
  'aiConfigs.modelParameters.addParameterLabel': 'Add model parameter',
  'aiConfigs.modelParameters.adjustParametersLabel': 'Adjust parameters',
  'aiConfigs.modelParameters.adjustTooltip': 'Adjust parameters for the selected model',
  'aiConfigs.modelParameters.closeDrawerLabel': 'Close',
  'aiConfigs.modelParameters.customHeading': 'Custom parameters',
  'aiConfigs.modelParameters.customJsonLabel': 'Custom parameters JSON',
  'aiConfigs.modelParameters.example': 'Example: {example}',
  'aiConfigs.modelParameters.filterPlaceholder': 'Filter parameters',
  'aiConfigs.modelParameters.generationSettingsJson':
    'Generation settings JSON (temperature, top_p, max_tokens, etc.) passed directly to the model.',
  'aiConfigs.modelParameters.invalidJson': 'Invalid JSON',
  'aiConfigs.modelParameters.jsonEditorLabel': 'Model parameters JSON',
  'aiConfigs.modelParameters.jsonObjectFor': 'JSON object for your own app-specific data (not sent to the model).',
  'aiConfigs.modelParameters.jsonSchemaDefining':
    'JSON Schema defining the structured output format sent to the model.',
  'aiConfigs.modelParameters.key': 'Key',
  'aiConfigs.modelParameters.modelHeading': 'Model parameters',
  'aiConfigs.modelParameters.outputFormat': 'Output format',
  'aiConfigs.modelParameters.overflowLabel': '{count} more parameters',
  'aiConfigs.modelParameters.overflowTag': '+ {count} more',
  'aiConfigs.modelParameters.parameterTagsLabel': 'Parameters',
  'aiConfigs.modelParameters.parametersLabel': 'Model parameters',
  'aiConfigs.modelParameters.removeParamLabel': 'Remove {key}',
  'aiConfigs.modelParameters.sdkAccess': 'Accessible in the SDK as <code>model.custom</code>.',
  'aiConfigs.modelParameters.unrecognizedWarning':
    "LaunchDarkly does not recognize these parameters: {keys}. They'll be saved in your configuration but your provider may reject them.",
  'aiConfigs.modelParameters.unsupportedNote': "{model} doesn't support {parameters}. These parameters are omitted.",
  'aiConfigs.modelParameters.unsupportedNoteFallbackModel': 'This model',
  'aiConfigs.modelParameters.value': 'Value',
  'aiConfigs.modelPicker.addCustomModel': 'Add custom model',
  'aiConfigs.modelPicker.builtInModels': 'Built-in models',
  'aiConfigs.modelPicker.customModels': 'Custom models',
  'aiConfigs.modelPicker.editModelLabel': 'Edit {name}',
  'aiConfigs.modelPicker.modelOptionsLabel': 'Model options',
  'aiConfigs.modelPicker.modelSearchLabel': 'Search models…',
  'aiConfigs.modelPicker.modelSearchPlaceholder': 'Find a model',
  'aiConfigs.modelPicker.modelTriggerLabel': 'Select a model',
  'aiConfigs.modelPicker.providerOptionsLabel': 'Provider options',
  'aiConfigs.modelPicker.providerSearchLabel': 'Search providers…',
  'aiConfigs.modelPicker.providerSearchPlaceholder': 'Find a provider',
  'aiConfigs.modelPicker.providerTriggerLabel': 'Select a provider',
  'aiConfigs.modelPicker.recentlyUsed': 'Recently used',
  'aiConfigs.modelPicker.recentlyUsedLabel': 'Recently used models',
  'aiConfigs.modelPicker.selectAModel': 'Select a model*',
  'aiConfigs.modelPicker.selectAProvider': 'Select a provider*',
  'aiConfigs.modelPicker.viewModelConfigLabel': 'View model config',
  'aiConfigs.modelPicker.viewModelConfigTooltip': 'View model config for {name}',
  'aiConfigs.modelSelector.addCustomModel': 'Add custom model',
  'aiConfigs.modelSelector.costEstimatePreviousModel': 'the previous model',
  'aiConfigs.modelSelector.costEstimateTooltip':
    'Switching to {model} will cost ~{projected} based on your usage over the past {days} days, compared to {observed} with {previousModel}.',
  'aiConfigs.modelSelector.customModelsSection': 'Custom models',
  'aiConfigs.modelSelector.editModelLabel': 'Edit {model}',
  'aiConfigs.modelSelector.modelRequired': 'Model is required',
  'aiConfigs.modelSelector.modelsLabel': 'Models',
  'aiConfigs.modelSelector.noCustomProviderModels':
    'No custom {provider} models yet. Add a custom model to use it here.',
  'aiConfigs.modelSelector.noModelsFound': 'No models found',
  'aiConfigs.modelSelector.noProviderModels': 'No {provider} models found. Select a different provider.',
  'aiConfigs.modelSelector.priceDecrease': '−{percent} price decrease',
  'aiConfigs.modelSelector.priceIncrease': '+{percent} price increase',
  'aiConfigs.modelSelector.providersLabel': 'Model providers',
  'aiConfigs.modelSelector.searchLabel': 'Search models…',
  'aiConfigs.modelSelector.searchPlaceholder': 'Search…',
  'aiConfigs.modelSelector.selectModel': 'Select model',
  'aiConfigs.modelSelector.selectionFailed': "Couldn't select {model}. Try again or choose a different model.",
  'aiConfigs.modelUsageDrawer.applyChanges': 'Apply changes',
  'aiConfigs.modelUsageDrawer.applyChangesDisabledLabel': 'Apply changes. Select a different model first.',
  'aiConfigs.modelUsageDrawer.cancel': 'Cancel',
  'aiConfigs.modelUsageDrawer.close': 'Close',
  'aiConfigs.modelUsageDrawer.couldNotLoad': 'Could not load model usage.',
  'aiConfigs.modelUsageDrawer.couldNotLoadModels': 'Could not load the list of models to update to.',
  'aiConfigs.modelUsageDrawer.descApprovalOnly':
    'Model usage for "{modelName}" will update on {count, plural, one {# config} other {# configs}} once approved.',
  'aiConfigs.modelUsageDrawer.descMixed':
    'Model usage for "{modelName}": applied on {applied, plural, one {# config} other {# configs}}; {pending, plural, one {# config will} other {# configs will}} update once approved.',
  'aiConfigs.modelUsageDrawer.descPending':
    '{count, plural, one {# usage has} other {# usages have}} a pending approval request already; model usage for "{modelName}" otherwise unchanged.',
  'aiConfigs.modelUsageDrawer.descProcessed': 'Processed model usage for "{modelName}".',
  'aiConfigs.modelUsageDrawer.descUpdated':
    'Updated model usage for "{modelName}" on {count, plural, one {# config} other {# configs}}.',
  'aiConfigs.modelUsageDrawer.descUpdatedAndPending':
    'Updated model usage for "{modelName}" on {updated, plural, one {# config} other {# configs}}. Skipped {pending, plural, one {# config} other {# configs}} with a pending approval.',
  'aiConfigs.modelUsageDrawer.doneTitle': 'Done',
  'aiConfigs.modelUsageDrawer.loadingReferencesLabel': 'Loading references',
  'aiConfigs.modelUsageDrawer.modelWithVersion': '{modelName} v{version}',
  'aiConfigs.modelUsageDrawer.networkErrorDescription': 'Check your connection and try again.',
  'aiConfigs.modelUsageDrawer.networkErrorDescriptionSomeApplied':
    'LaunchDarkly updated some configs before the error. Check your connection and try again for the rest.',
  'aiConfigs.modelUsageDrawer.networkErrorTitle': 'Could not apply model changes',
  'aiConfigs.modelUsageDrawer.networkErrorTitleSomeApplied': 'Could not finish applying model changes',
  'aiConfigs.modelUsageDrawer.partialFailureDescription': 'Review the notification list or try again.',
  'aiConfigs.modelUsageDrawer.partialFailureDescriptionSomeApplied':
    'Earlier updates succeeded; remaining updates failed or were skipped. Review or try again.',
  'aiConfigs.modelUsageDrawer.partialFailureTitle': 'Some usages were not updated',
  'aiConfigs.modelUsageDrawer.rowModelSelectChangingLabel':
    'Update the model for {aiConfigName}, variation {variationName}. Changing from {fromModel} to {toModel}.',
  'aiConfigs.modelUsageDrawer.rowModelSelectLabel':
    'Update the model for {aiConfigName}, variation {variationName}. Currently {currentModel}.',
  'aiConfigs.modelUsageDrawer.selectModelLabel': 'Select a model',
  'aiConfigs.modelUsageDrawer.statusAborted': 'Not attempted because processing was stopped early.',
  'aiConfigs.modelUsageDrawer.statusApprovalRequested': 'Approval request created; update is pending review.',
  'aiConfigs.modelUsageDrawer.statusError': 'An unexpected error occurred while processing this target.',
  'aiConfigs.modelUsageDrawer.statusModelNotReferenced': 'Skipped because this variation does not reference the model.',
  'aiConfigs.modelUsageDrawer.statusPendingApprovalExists':
    'Skipped because a pending approval request already exists for this AI config.',
  'aiConfigs.modelUsageDrawer.statusUnknown': 'Unknown outcome ({status}).',
  'aiConfigs.modelUsageDrawer.statusUpdated': 'Updated to the new model.',
  'aiConfigs.modelUsageDrawer.successTitle': 'Usage updated',
  'aiConfigs.modelUsageDrawer.titleApplied': 'Applied to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.modelUsageDrawer.titleRequested': 'Requested {count, plural, one {# approval} other {# approvals}}',
  'aiConfigs.modelUsageDrawer.titleSkipped':
    'Skipped {count, plural, one {# config} other {# configs}} (pending approval)',
  'aiConfigs.modelUsageDrawer.updateAllLabel': 'Update all models',
  'aiConfigs.modelUsageDrawer.updateAllTo': 'Update all to',
  'aiConfigs.modelUsageDrawer.updateTo': 'Update to',
  'aiConfigs.modelUsageDrawer.usedInCount': 'Used in {count, plural, one {# config} other {# configs}}',
  'aiConfigs.modelVersionAdoptDialog.apply': 'Update variations',
  'aiConfigs.modelVersionAdoptDialog.applyChangesHeading': 'Apply changes to:',
  'aiConfigs.modelVersionAdoptDialog.applyCount':
    '{count, plural, one {Update # variation} other {Update # variations}}',
  'aiConfigs.modelVersionAdoptDialog.body':
    '<strong>{name}</strong> is used by <strong>{count, plural, one {# config} other {# configs}}</strong>. Select which configs should be updated to the new version.',
  'aiConfigs.modelVersionAdoptDialog.dialogLabel': 'Update variations to the new model version',
  'aiConfigs.modelVersionAdoptDialog.heading': 'Update variations to v{version}',
  'aiConfigs.modelVersionAdoptDialog.notNow': 'Not now',
  'aiConfigs.modelVersionAdoptDialog.referenceMeta': '{variationName} · v{version}',
  'aiConfigs.modelVersionAdoptDialog.usagesLabel': 'Model usages',
  'aiConfigs.modelVersionHistory.copiedToClipboard': 'Copied to clipboard',
  'aiConfigs.modelVersionHistory.copyVersionLabel': 'Copy version {version} content',
  'aiConfigs.modelVersionHistory.loadErrorMessage': 'Could not load model version history.',
  'aiConfigs.modelVersionHistory.menuLabel': 'Model versions',
  'aiConfigs.modelVersionHistory.selectVersionLabel': 'Select model version, current version v{version}',
  'aiConfigs.monitoringCharts.exportChart': 'Export chart',
  'aiConfigs.monitoringCharts.exportCsvLabel': 'Export chart as CSV',
  'aiConfigs.monitoringCharts.exportSuccessDescription': 'File {fileName}.csv has been downloaded',
  'aiConfigs.monitoringCharts.exportSuccessToast': 'Chart exported successfully',
  'aiConfigs.monitoringCharts.failedToLoad': 'Failed to load chart data. Try again.',
  'aiConfigs.monitoringCharts.nameErrorRate': 'Error rate',
  'aiConfigs.monitoringCharts.nameInputCost': 'Total input cost',
  'aiConfigs.monitoringCharts.nameJudgeAccuracy': '[Judge] Accuracy score:',
  'aiConfigs.monitoringCharts.nameJudgeRelevance': '[Judge] Relevance score:',
  'aiConfigs.monitoringCharts.nameJudgeToxicity': '[Judge] Toxicity score:',
  'aiConfigs.monitoringCharts.nameLatency': 'Request duration',
  'aiConfigs.monitoringCharts.nameOutputCost': 'Total output cost',
  'aiConfigs.monitoringCharts.nameRequests': 'Requests',
  'aiConfigs.monitoringCharts.nameSatisfaction': 'Satisfaction rating',
  'aiConfigs.monitoringCharts.nameThumbsDown': 'Thumbs down on average',
  'aiConfigs.monitoringCharts.nameThumbsUp': 'Thumbs up on average',
  'aiConfigs.monitoringCharts.nameTimeToFirstToken': 'Time to first token (TTFT)',
  'aiConfigs.monitoringCharts.nameTokens': 'Tokens',
  'aiConfigs.monitoringCharts.nameTokensIn': 'Input tokens',
  'aiConfigs.monitoringCharts.nameTokensOut': 'Output tokens',
  'aiConfigs.monitoringCharts.thisChartType': 'This chart type is not yet supported by the new API',
  'aiConfigs.monitoringCharts.tooltipErrorRate': 'Percentage of requests with errors for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipInputCost': 'Total input cost for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipJudgeAccuracy':
    'Average judge accuracy score per request for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipJudgeRelevance':
    'Average judge relevance score per request for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipJudgeToxicity':
    'Average judge toxicity score per request for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipLatency':
    'Average time to generate a response per request for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipOutputCost': 'Total output cost for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipRequests': 'Average number of requests per day for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipSatisfaction': 'Percentage of positive feedback for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipThumbsDown': 'Number of negative feedback events for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipThumbsUp': 'Number of positive feedback events for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipTimeToFirstToken':
    'Average time to first token per request for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipTokensIn': 'Average input tokens per request for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipTokensOut': 'Average output tokens per request for the specified time period.',
  'aiConfigs.monitoringCharts.tooltipTokensTotal': 'Average total tokens per request for the specified time period.',
  'aiConfigs.monitoringCharts.variationReleases': 'Variation releases',
  'aiConfigs.monitoringCharts.variationReleasesTooltip': 'Variation releases',
  'aiConfigs.monitoringEmptyState.docsLink': 'View AI metrics documentation',
  'aiConfigs.monitoringEmptyState.noDataDescription':
    'The selected variations have no metrics data. Select other variations or change the time range.',
  'aiConfigs.monitoringEmptyState.noDataTitle': 'No data for selected variations',
  'aiConfigs.monitoringEmptyState.noMetricsDescription':
    'Configure your SDK to start collecting metrics for your AI variations',
  'aiConfigs.monitoringEmptyState.noMetricsTitle': 'No metrics data available',
  'aiConfigs.monitoringEmptyState.noVariationsDescription': 'Select variations below to view metrics',
  'aiConfigs.monitoringInsights.aiInsights': 'AI insights',
  'aiConfigs.monitoringInsights.generating': 'Generating…',
  'aiConfigs.monitoringInsights.generatingLabel': 'Generating insights',
  'aiConfigs.monitoringJudgeMetrics.emptyDescription': 'Adjust the filters or review the judge configuration.',
  'aiConfigs.monitoringJudgeMetrics.emptyTitle': 'No judge metrics available for this selection.',
  'aiConfigs.monitoringJudgeMetrics.loadError': 'Recorded judge metrics could not be loaded.',
  'aiConfigs.monitoringJudgeMetrics.loadErrorDescription': 'Refresh the page to try again.',
  'aiConfigs.monitoringJudgeMetrics.selectVersions': 'Select a variation version to view recorded judge metrics.',
  'aiConfigs.monitoringPage.somethingWentWrong':
    'Something went wrong while loading monitoring data. Try again, and if the problem continues,',
  'aiConfigs.monitoringPage.tryAgain': 'Try again',
  'aiConfigs.monitoringPage.unableToLoad': 'Unable to load monitoring data',
  'aiConfigs.monitoringTable.archivedTag': 'Archived',
  'aiConfigs.monitoringTable.headerAvgCost': 'Avg cost',
  'aiConfigs.monitoringTable.headerErrorRate': 'Error rate',
  'aiConfigs.monitoringTable.headerErrors': 'Errors',
  'aiConfigs.monitoringTable.headerGenerations': 'Requests',
  'aiConfigs.monitoringTable.headerInputCost': 'Input cost',
  'aiConfigs.monitoringTable.headerInputCostAvg': 'Input cost (avg)',
  'aiConfigs.monitoringTable.headerLastEvaluatedDate': 'Last evaluated',
  'aiConfigs.monitoringTable.headerLatency': 'Request duration',
  'aiConfigs.monitoringTable.headerModelConfigKey': 'Model config key',
  'aiConfigs.monitoringTable.headerOutputCost': 'Output cost',
  'aiConfigs.monitoringTable.headerOutputCostAvg': 'Output cost (avg)',
  'aiConfigs.monitoringTable.headerSatisfaction': 'Satisfaction',
  'aiConfigs.monitoringTable.headerTimeToFirstToken': 'Time to first token (TTFT)',
  'aiConfigs.monitoringTable.headerTokensIn': 'Input tokens (avg)',
  'aiConfigs.monitoringTable.headerTokensInTotal': 'Input tokens (total)',
  'aiConfigs.monitoringTable.headerTokensOut': 'Output tokens (avg)',
  'aiConfigs.monitoringTable.headerTokensOutTotal': 'Output tokens (total)',
  'aiConfigs.monitoringTable.headerTotalCost': 'Total cost',
  'aiConfigs.monitoringTable.headerVariation': 'Variation',
  'aiConfigs.monitoringTable.hideArchived': 'Hide archived variations',
  'aiConfigs.monitoringTable.judgeAccuracy': 'Accuracy',
  'aiConfigs.monitoringTable.judgeRelevance': 'Relevance',
  'aiConfigs.monitoringTable.judgeToxicity': 'Toxicity',
  'aiConfigs.monitoringTable.showArchived': 'Show archived variations',
  'aiConfigs.monitoringTable.tableLabel': 'Ai configs monitoring dashboard table',
  'aiConfigs.monitoringToolbar.attributePlaceholder': 'Attribute',
  'aiConfigs.monitoringToolbar.bucketCosts': 'Cost',
  'aiConfigs.monitoringToolbar.bucketJudgeMetrics': 'Judge metrics',
  'aiConfigs.monitoringToolbar.bucketLatency': 'Request duration',
  'aiConfigs.monitoringToolbar.bucketRequests': 'Requests',
  'aiConfigs.monitoringToolbar.chartTypesLabel': 'Chart types',
  'aiConfigs.monitoringToolbar.charts': 'Charts',
  'aiConfigs.monitoringToolbar.clearValueLabel': 'Clear value',
  'aiConfigs.monitoringToolbar.context': 'Context',
  'aiConfigs.monitoringToolbar.contextAttributeLabel': 'Context attribute',
  'aiConfigs.monitoringToolbar.contextKindLabel': 'Context kind',
  'aiConfigs.monitoringToolbar.contextValueLabel': 'Context value',
  'aiConfigs.monitoringToolbar.defaultContextKindName': 'User',
  'aiConfigs.monitoringToolbar.environment': 'Environment',
  'aiConfigs.monitoringToolbar.environmentsLabel': 'Environments',
  'aiConfigs.monitoringToolbar.environmentsLoadingLabel': 'Loading…',
  'aiConfigs.monitoringToolbar.exportDataAs': 'Export data as CSV',
  'aiConfigs.monitoringToolbar.exportSuccessDescription': 'File {fileName}.csv has been downloaded',
  'aiConfigs.monitoringToolbar.exportSuccessToast': 'Table data exported successfully',
  'aiConfigs.monitoringToolbar.findEnvironmentPlaceholder': 'Find an environment',
  'aiConfigs.monitoringToolbar.groupByContext': 'Group by context',
  'aiConfigs.monitoringToolbar.judgeMetricsHeader': 'Judge metrics',
  'aiConfigs.monitoringToolbar.judgesLoading': 'Loading…',
  'aiConfigs.monitoringToolbar.noJudgesAttached': 'No judges attached to this AI config.',
  'aiConfigs.monitoringToolbar.performanceMetricsHeader': 'Performance metrics',
  'aiConfigs.monitoringToolbar.searchEnvironmentsLabel': 'Search environments…',
  'aiConfigs.monitoringToolbar.showContextKindsLabel': 'Show context kinds',
  'aiConfigs.monitoringToolbar.valueOptionalPlaceholder': 'Value (optional)',
  'aiConfigs.monitoringToolbar.valuePlaceholder': 'Value',
  'aiConfigs.monitoringTraces.columnConversationId': 'Conversation ID',
  'aiConfigs.monitoringTraces.columnService': 'Service',
  'aiConfigs.monitoringTraces.columnSpan': 'Span',
  'aiConfigs.monitoringTraces.columnSpanId': 'Span ID',
  'aiConfigs.monitoringTraces.columnTimestamp': 'Timestamp',
  'aiConfigs.monitoringTraces.columnTraceId': 'Trace ID',
  'aiConfigs.monitoringTraces.contextUnavailable':
    'These traces do not carry {kind} context keys, so they cannot be filtered by {kind}. The list below shows every trace for this AI Config.',
  'aiConfigs.monitoringTraces.defaultContextKind': 'context',
  'aiConfigs.monitoringTraces.failedToLoad': 'Failed to load trace details',
  'aiConfigs.monitoringTraces.filterUnsupported':
    'Traces cannot be filtered by {fields}. The list below ignores that part of your filter.',
  'aiConfigs.monitoringTraces.loadingMoreLabel': 'Loading more traces',
  'aiConfigs.monitoringTraces.noMatchDescription':
    'Try widening the filter, or clear the grouping to see every trace for this AI Config.',
  'aiConfigs.monitoringTraces.noMatchTitle': 'No traces match this filter',
  'aiConfigs.monitoringTraces.noTracesDescription':
    'Install the LaunchDarkly observability plugin with your AI SDK to start collecting traces for this config.',
  'aiConfigs.monitoringTraces.noTracesTitle': 'No LLM traces detected',
  'aiConfigs.monitoringTraces.onlyShowTop': 'Only show top level spans',
  'aiConfigs.monitoringTraces.readDocs': 'Read docs',
  'aiConfigs.monitoringTraces.refreshTraces': 'Refresh traces',
  'aiConfigs.monitoringTraces.sandboxNoTracesDescription':
    "Traces here come from synthetic users in realistic conversations mode, a few minutes after each conversation. Numbers-only mode doesn't create traces.",
  'aiConfigs.monitoringTraces.sandboxNoTracesTitle': 'No synthetic conversations yet',
  'aiConfigs.monitoringTraces.setUpAi': 'Set up AI SDK traces',
  'aiConfigs.monitoringTraces.tableLabel': 'AI config traces table',
  'aiConfigs.newDatasetMenu.createDataset': 'Create dataset',
  'aiConfigs.newDatasetMenu.newDataset': 'New dataset',
  'aiConfigs.newDatasetMenu.uploadDataset': 'Upload dataset',
  'aiConfigs.newPage.title': 'Create config',
  'aiConfigs.notifications.sampleConfigError': 'Could not create the sample config. Try again.',
  'aiConfigs.notifications.saved': 'Config saved',
  'aiConfigs.notifications.trialRequested': 'Successfully requested a free trial',
  'aiConfigs.onboardingCarousel.configureAlt': 'Configure AI agents',
  'aiConfigs.onboardingCarousel.configureBody':
    'Define your agent’s model, prompt, and parameters in one place. Then update them instantly without redeploying your app.',
  'aiConfigs.onboardingCarousel.dotsLabel': 'Carousel navigation',
  'aiConfigs.onboardingCarousel.evaluateAlt': 'Evaluate AI performance',
  'aiConfigs.onboardingCarousel.evaluateBody':
    'Compare performance and cost across all your agent use cases to make confident, data-backed promotion decisions.',
  'aiConfigs.onboardingCarousel.iterateAlt': 'Iterate with playground comparisons',
  'aiConfigs.onboardingCarousel.iterateBody':
    'Test model and prompt changes head-to-head in the playground before releasing them to a single real user.',
  'aiConfigs.onboardingCarousel.monitorAlt': 'Monitor quality insights',
  'aiConfigs.onboardingCarousel.monitorBody':
    'Catch quality regressions the moment they happen with always-on monitoring that alerts you before users feel the impact.',
  'aiConfigs.onboardingCarousel.nextImage': 'Next image',
  'aiConfigs.onboardingCarousel.releaseAlt': 'Release with rollout controls',
  'aiConfigs.onboardingCarousel.releaseBody':
    'New agent versions can be rolled out progressively, with automatic rollback triggered by quality, cost, or latency signals.',
  'aiConfigs.onboardingCarousel.slideLabel': 'Show image {index}: {alt}',
  'aiConfigs.onboardingGoalChoice.back': 'Back',
  'aiConfigs.onboardingGoalChoice.bookTime': 'Book a call',
  'aiConfigs.onboardingGoalChoice.continue': 'Continue',
  'aiConfigs.onboardingGoalChoice.continueControlApp': 'Set up the SDK',
  'aiConfigs.onboardingGoalChoice.continueControlSample': 'Create a config',
  'aiConfigs.onboardingGoalChoice.continueEvalsCode': 'Set up evals',
  'aiConfigs.onboardingGoalChoice.continueEvalsPlayground': 'Create a playground',
  'aiConfigs.onboardingGoalChoice.controlApp': 'In my own app',
  'aiConfigs.onboardingGoalChoice.controlAppDescription':
    'Create a config and connect your app with the SDK. Your app then reads its prompt and model from AgentControl.',
  'aiConfigs.onboardingGoalChoice.controlBody':
    'Either way, you end up with a config you can change live. You can try the other option later.',
  'aiConfigs.onboardingGoalChoice.controlHeading': 'How do you want to start?',
  'aiConfigs.onboardingGoalChoice.controlLabel': 'How to start',
  'aiConfigs.onboardingGoalChoice.controlSample': 'With a sample config',
  'aiConfigs.onboardingGoalChoice.controlSampleDescription':
    'Start with a sample config and change it live before you touch your code.',
  'aiConfigs.onboardingGoalChoice.controlUnavailable':
    "You don't have permission to create configs in this project, so the sample isn't available. Ask a project admin for access, or start in your own app.",
  'aiConfigs.onboardingGoalChoice.engineerBody':
    'Spend 30 minutes on your use case with someone who builds AgentControl.',
  'aiConfigs.onboardingGoalChoice.engineerHeading': 'Talk to an engineer',
  'aiConfigs.onboardingGoalChoice.engineerPrompt':
    'Rather talk it through? <link>Book 30 minutes with an engineer</link>',
  'aiConfigs.onboardingGoalChoice.evalsBody': 'Both share judges, datasets, and results. You can switch at any time.',
  'aiConfigs.onboardingGoalChoice.evalsCode': 'In my code',
  'aiConfigs.onboardingGoalChoice.evalsCodeDescription':
    "Automate evals in your test suite or CI with examples you already have. You'll need an SDK key and an eval API key.",
  'aiConfigs.onboardingGoalChoice.evalsHeading': 'Where do you want to run your evals?',
  'aiConfigs.onboardingGoalChoice.evalsLabel': 'Where to run evals',
  'aiConfigs.onboardingGoalChoice.evalsPlayground': 'In the playground',
  'aiConfigs.onboardingGoalChoice.evalsPlaygroundDescription':
    'Compare models and prompts side by side for a quick read. No code needed.',
  'aiConfigs.onboardingGoalChoice.exploreOnMyOwn': 'Explore on my own',
  'aiConfigs.onboardingGoalChoice.goToAgentControl': 'Explore AgentControl',
  'aiConfigs.onboardingGoalChoice.goalBody': 'You can explore the rest at any time.',
  'aiConfigs.onboardingGoalChoice.goalControl': 'Control agents in production',
  'aiConfigs.onboardingGoalChoice.goalControlDescription':
    'Change prompts and models without redeploying, and roll back in one click.',
  'aiConfigs.onboardingGoalChoice.goalEvals': 'Evaluate agents offline',
  'aiConfigs.onboardingGoalChoice.goalEvalsDescription':
    'Score a model or prompt on your examples before users see it.',
  'aiConfigs.onboardingGoalChoice.goalHeading': 'What do you want to do first?',
  'aiConfigs.onboardingGoalChoice.goalLabel': 'What you want to do first',
  'aiConfigs.onboardingGoalChoice.goalOther': 'Something else',
  'aiConfigs.onboardingGoalChoice.goalOtherDescription': 'Tell us what you need.',
  'aiConfigs.onboardingGoalChoice.or': 'or',
  'aiConfigs.onboardingGoalChoice.otherBody': 'Your note goes straight to the AgentControl product team.',
  'aiConfigs.onboardingGoalChoice.otherDetailsLabel': 'What are you building?',
  'aiConfigs.onboardingGoalChoice.otherDetailsPlaceholder':
    'We run agents behind a gateway and want to switch models per customer',
  'aiConfigs.onboardingGoalChoice.otherHeading': 'Tell us what you need',
  'aiConfigs.onboardingGoalChoice.otherSent': "It's with the AgentControl team now.",
  'aiConfigs.onboardingGoalChoice.otherSentHeading': 'Thanks for the note',
  'aiConfigs.onboardingGoalChoice.otherTopicsLabel': 'Topics',
  'aiConfigs.onboardingGoalChoice.send': 'Send',
  'aiConfigs.onboardingGoalChoice.sendError': 'LaunchDarkly could not send your note. Try again.',
  'aiConfigs.onboardingGoalChoice.topicCodingAgents': 'Control costs of coding agents',
  'aiConfigs.onboardingGoalChoice.topicGateway': 'Route through an AI gateway',
  'aiConfigs.onboardingGoalChoice.topicGuardrails': 'Add guardrails and safety checks',
  'aiConfigs.onboardingGoalChoice.topicMigrate': 'Switch from another tool',
  'aiConfigs.onboardingSplash.body':
    'AgentControl lets you manage models, prompts, and behavior from one place. Make changes in production without redeploys, track results, and roll back at any time.',
  'aiConfigs.onboardingSplash.bodyVariant':
    'Agent behavior shifts in production without warning. AgentControl helps keep agents on track, blocking bad behavior and steering responses in real time.',
  'aiConfigs.onboardingSplash.createConfig': 'Create a config',
  'aiConfigs.onboardingSplash.docsLink': 'Read about AgentControl',
  'aiConfigs.onboardingSplash.docsLinkVariant': 'Read more about AgentControl',
  'aiConfigs.onboardingSplash.heading': 'Control AI in production, without redeploys',
  'aiConfigs.onboardingSplash.headingVariant': 'Control your agents in production',
  'aiConfigs.onboardingSplash.sampleConfigCta': 'Get started with a sample config',
  'aiConfigs.onboardingSplash.setUpSdk': 'Set up SDK',
  'aiConfigs.onboardingSplash.setUpSdkVariant': 'Connect your app',
  'aiConfigs.onboardingSplash.tryPlayground': 'Try the playground',
  'aiConfigs.onboardingSplash.tryPlaygroundVariant': 'Experiment with prompts',
  'aiConfigs.optimizationCodeSample.closeLabel': 'Close',
  'aiConfigs.optimizationCodeSample.codeSample': 'Code sample',
  'aiConfigs.optimizationCodeSample.frameworkLabel': 'Framework',
  'aiConfigs.optimizationCodeSample.viewDocsLink': 'View full documentation',
  'aiConfigs.optimizationConfigForm.acceptanceStatement': 'Acceptance statement',
  'aiConfigs.optimizationConfigForm.acceptanceStatementPlaceholder':
    "Describe the desired output. e.g. 'The response should be four condensed bullet points. Failure to reference content from the original input is an automatic failure'",
  'aiConfigs.optimizationConfigForm.acceptanceStatementRequired': 'Enter an acceptance statement.',
  'aiConfigs.optimizationConfigForm.acceptanceThresholdLabel': 'Acceptance statement threshold',
  'aiConfigs.optimizationConfigForm.acceptanceThresholdPrefix': 'Pass when score ≥',
  'aiConfigs.optimizationConfigForm.agentOptionsLabel': 'Agent options',
  'aiConfigs.optimizationConfigForm.agentSectionBody': 'Select the agent you want to improve.',
  'aiConfigs.optimizationConfigForm.agentSectionHeading': 'Agent',
  'aiConfigs.optimizationConfigForm.aiConfigRequired': 'AI config is required',
  'aiConfigs.optimizationConfigForm.autoCommitBody':
    'Automatically save the generated variation back to LaunchDarkly when the run succeeds. If disabled, the variation must be added manually from the results page.',
  'aiConfigs.optimizationConfigForm.configSelectorLabel': 'Config selector',
  'aiConfigs.optimizationConfigForm.copiedKey': 'Copied {value}',
  'aiConfigs.optimizationConfigForm.copyOptimizationKey': 'Copy optimization key to clipboard',
  'aiConfigs.optimizationConfigForm.copyOptimizationKeyLabel': 'Copy optimization key {key} to clipboard',
  'aiConfigs.optimizationConfigForm.costEstimateLabel': 'Cost estimate info',
  'aiConfigs.optimizationConfigForm.costPerToken': '≈ {cost} per token',
  'aiConfigs.optimizationConfigForm.createAgent': 'Create agent',
  'aiConfigs.optimizationConfigForm.createFailedToast': 'Failed to create optimization',
  'aiConfigs.optimizationConfigForm.evaluationMethodRequired': 'Enable at least one evaluation method.',
  'aiConfigs.optimizationConfigForm.evaluationModel': 'Evaluation model',
  'aiConfigs.optimizationConfigForm.evaluationSectionBody':
    'Define how results are evaluated and what counts as success.',
  'aiConfigs.optimizationConfigForm.evaluationSectionHeading': 'Evaluation',
  'aiConfigs.optimizationConfigForm.expectedOutput': 'Expected output',
  'aiConfigs.optimizationConfigForm.expectedOutputModeBody':
    'Check outputs against predefined correct results. Best for established agents where inputs reliably map to specific outputs and you want to confirm nothing regressed.',
  'aiConfigs.optimizationConfigForm.exploratory': 'Exploratory',
  'aiConfigs.optimizationConfigForm.exploratoryModeBody':
    'Explore resilience across unpredictable inputs. Best for agents that handle open-ended, unstructured, or non-deterministic inputs.',
  'aiConfigs.optimizationConfigForm.intro':
    "Run iterative optimizations to improve your agent's performance. Configure which agent to improve, how results are evaluated, and which models to test.",
  'aiConfigs.optimizationConfigForm.judge': 'Judge',
  'aiConfigs.optimizationConfigForm.judgeModelRequired': 'Judge model is required',
  'aiConfigs.optimizationConfigForm.judgeRequired': 'Select a judge.',
  'aiConfigs.optimizationConfigForm.judgeThresholdLabel': 'Judge threshold',
  'aiConfigs.optimizationConfigForm.judgeThresholdPrefix': 'Pass when score ≥',
  'aiConfigs.optimizationConfigForm.judgeThresholdPrefixInverted': 'Pass when score ≤',
  'aiConfigs.optimizationConfigForm.labelField': 'Label',
  'aiConfigs.optimizationConfigForm.labelFieldBody': 'Optional label to identify runs from this configuration.',
  'aiConfigs.optimizationConfigForm.labelPlaceholder': 'e.g. production-baseline',
  'aiConfigs.optimizationConfigForm.latencyOptimizationBody':
    'Run a post-process after a successful iteration to attempt to reduce response latency.',
  'aiConfigs.optimizationConfigForm.learnMoreLink': 'Read more about agent optimization',
  'aiConfigs.optimizationConfigForm.maxIterationsBody': 'Maximum number of optimization iterations to attempt per run.',
  'aiConfigs.optimizationConfigForm.maxIterationsPer': 'Max iterations per run',
  'aiConfigs.optimizationConfigForm.maxTokenSpend': 'Max token spend per run',
  'aiConfigs.optimizationConfigForm.modeSectionBody': 'Choose how the system improves your agent',
  'aiConfigs.optimizationConfigForm.modeSectionHeading': 'Optimization mode',
  'aiConfigs.optimizationConfigForm.modelChoices': 'Model choices',
  'aiConfigs.optimizationConfigForm.modelRequired': 'At least one model is required',
  'aiConfigs.optimizationConfigForm.modelsSectionBody':
    'Select one or more models to use during optimization runs. The optimization process can change the model to any of the model choices below.',
  'aiConfigs.optimizationConfigForm.modelsSectionHeading': 'Models',
  'aiConfigs.optimizationConfigForm.noLimit': 'No limit',
  'aiConfigs.optimizationConfigForm.noModelsFound': 'No models found',
  'aiConfigs.optimizationConfigForm.optimizationKey': 'Optimization key',
  'aiConfigs.optimizationConfigForm.optimizeForCost': 'Optimize for cost',
  'aiConfigs.optimizationConfigForm.optimizeForSpeed': 'Optimize for speed',
  'aiConfigs.optimizationConfigForm.optionsSectionBody': 'Configure additional run behavior.',
  'aiConfigs.optimizationConfigForm.optionsSectionHeading': 'Options',
  'aiConfigs.optimizationConfigForm.save': 'Save',
  'aiConfigs.optimizationConfigForm.saveAutomatically': 'Save automatically',
  'aiConfigs.optimizationConfigForm.saveFailedToast': 'Failed to save configuration',
  'aiConfigs.optimizationConfigForm.savedToast': 'Configuration saved',
  'aiConfigs.optimizationConfigForm.searchConfigsLabel': 'Search configs',
  'aiConfigs.optimizationConfigForm.searchConfigsPlaceholder': 'Search configs…',
  'aiConfigs.optimizationConfigForm.searchModelsLabel': 'Search models',
  'aiConfigs.optimizationConfigForm.searchModelsPlaceholder': 'Search…',
  'aiConfigs.optimizationConfigForm.selectAgentLabel': 'Select agent',
  'aiConfigs.optimizationConfigForm.selectAgentTrigger': 'Select an agent',
  'aiConfigs.optimizationConfigForm.selectAiConfigLabel': 'Select AI config',
  'aiConfigs.optimizationConfigForm.selectEvaluationModelLabel': 'Select evaluation model',
  'aiConfigs.optimizationConfigForm.selectJudgeLabel': 'Select judge',
  'aiConfigs.optimizationConfigForm.selectJudgeTrigger': 'Select judge',
  'aiConfigs.optimizationConfigForm.selectModelTrigger': 'Select model',
  'aiConfigs.optimizationConfigForm.selectModelsLabel': 'Select models',
  'aiConfigs.optimizationConfigForm.selectModelsTrigger': 'Select models',
  'aiConfigs.optimizationConfigForm.selectVariationLabel': 'Select variation',
  'aiConfigs.optimizationConfigForm.selectVariationTrigger': 'Select a variation',
  'aiConfigs.optimizationConfigForm.selectedModelsLabel': 'Selected models',
  'aiConfigs.optimizationConfigForm.thresholdLenient': 'Lenient',
  'aiConfigs.optimizationConfigForm.thresholdStrict': 'Strict',
  'aiConfigs.optimizationConfigForm.tokenLimitBody':
    'Higher limits allow more thorough optimization but increase cost. A limit set at 0 is considered unlimited.',
  'aiConfigs.optimizationConfigForm.tokenOptimizationBody':
    'Run a post-process after a successful iteration to attempt to reduce total token spend for the run.',
  'aiConfigs.optimizationConfigForm.tokensPrefix': 'Tokens',
  'aiConfigs.optimizationConfigForm.useDefaultTargeted': 'Use default targeted variation',
  'aiConfigs.optimizationConfigForm.variation': 'Variation',
  'aiConfigs.optimizationConfigForm.variationOptionsLabel': 'Variation options',
  'aiConfigs.optimizationConfigForm.viewJudgeConfig': 'View judge config',
  'aiConfigs.optimizationCreate.agent': 'Agent',
  'aiConfigs.optimizationCreate.configuration': 'Configuration',
  'aiConfigs.optimizationCreate.key': 'Key',
  'aiConfigs.optimizationCreate.results': 'Results',
  'aiConfigs.optimizationCreate.resultsPlaceholder': 'Results will be available after your first run',
  'aiConfigs.optimizationDetail.access': 'Access',
  'aiConfigs.optimizationDetail.codeSample': 'Code sample',
  'aiConfigs.optimizationDetail.config': 'Config',
  'aiConfigs.optimizationDetail.configuration': 'Configuration',
  'aiConfigs.optimizationDetail.createScopedToken': 'Create scoped token',
  'aiConfigs.optimizationDetail.documentation': 'Documentation',
  'aiConfigs.optimizationDetail.noRunData': 'No results yet. Results appear here after the optimization runs.',
  'aiConfigs.optimizationDetail.optimizationKey': 'Optimization key',
  'aiConfigs.optimizationDetail.quickstartGuideLink': 'Quickstart guide',
  'aiConfigs.optimizationDetail.results': 'Results',
  'aiConfigs.optimizationDetail.status': 'Status',
  'aiConfigs.optimizationDetail.updated': 'Updated',
  'aiConfigs.optimizationDetail.viewSampleCode': 'View sample code',
  'aiConfigs.optimizationList.actionsLabel': 'Optimization actions',
  'aiConfigs.optimizationList.changeHistory': 'Change history',
  'aiConfigs.optimizationList.connectPrompt': 'Connect your app to get started with AgentControl.',
  'aiConfigs.optimizationList.copiedKey': 'Copied {value}',
  'aiConfigs.optimizationList.copyKey': 'Copy key',
  'aiConfigs.optimizationList.copyKeyTooltip': 'Copy {value} to clipboard',
  'aiConfigs.optimizationList.createOptimization': 'Create optimization',
  'aiConfigs.optimizationList.deleteFailed': 'Failed to delete optimization',
  'aiConfigs.optimizationList.deleteOptimization': 'Delete optimization',
  'aiConfigs.optimizationList.deleted': 'Optimization deleted',
  'aiConfigs.optimizationList.emptyStateBody':
    "Create an optimization config to iteratively improve your agent's instructions and parameters using judges and acceptance criteria.",
  'aiConfigs.optimizationList.emptyStateHeading': 'No optimization configs yet',
  'aiConfigs.optimizationList.evaluationModel': 'Evaluation model: {model}',
  'aiConfigs.optimizationList.itemTitle': '{configKey} – Opt v{version}',
  'aiConfigs.optimizationList.keyCopied': 'Optimization key copied to clipboard',
  'aiConfigs.optimizationList.learnMoreLink': 'Read about agent optimization',
  'aiConfigs.optimizationList.listLabel': 'Optimization configs list',
  'aiConfigs.optimizationList.noResults': 'No optimizations match your search.',
  'aiConfigs.optimizationList.placeholderItem': 'Optimization config {number}',
  'aiConfigs.optimizationList.searchLabel': 'Search optimizations',
  'aiConfigs.optimizationList.searchPlaceholder': 'Search fields by name, description or key',
  'aiConfigs.optimizationRunDetail.base': 'Base',
  'aiConfigs.optimizationRunDetail.collapse': 'Collapse',
  'aiConfigs.optimizationRunDetail.compare': 'Compare',
  'aiConfigs.optimizationRunDetail.configDiff': 'Config diff',
  'aiConfigs.optimizationRunDetail.configIterations': 'Config iterations',
  'aiConfigs.optimizationRunDetail.cost': 'Cost',
  'aiConfigs.optimizationRunDetail.expand': 'Expand',
  'aiConfigs.optimizationRunDetail.failLabel': 'Fail',
  'aiConfigs.optimizationRunDetail.failedLabel': 'Failed',
  'aiConfigs.optimizationRunDetail.failingLabel': 'Failing',
  'aiConfigs.optimizationRunDetail.goToTargeting': 'Go to targeting',
  'aiConfigs.optimizationRunDetail.hideAcceptanceStatementLabel': 'Hide acceptance statement',
  'aiConfigs.optimizationRunDetail.hideRationaleLabel': 'Hide rationale',
  'aiConfigs.optimizationRunDetail.inProgressLabel': 'In progress',
  'aiConfigs.optimizationRunDetail.iteration': 'Iteration',
  'aiConfigs.optimizationRunDetail.iterationPassed': 'Iteration {number} passed.',
  'aiConfigs.optimizationRunDetail.iterationPassedLink': '<link>Iteration {number}</link> passed.',
  'aiConfigs.optimizationRunDetail.latency': 'Latency',
  'aiConfigs.optimizationRunDetail.messages': 'Messages',
  'aiConfigs.optimizationRunDetail.model': 'Model',
  'aiConfigs.optimizationRunDetail.newAgentVariationReady': ' A new agent variation is ready to use.',
  'aiConfigs.optimizationRunDetail.newVariationReady': ' A new variation ({variationKey}) is ready to use.',
  'aiConfigs.optimizationRunDetail.notApplicableLabel': 'Not applicable',
  'aiConfigs.optimizationRunDetail.notSelectedLabel': 'Not selected',
  'aiConfigs.optimizationRunDetail.openVariation': 'Open variation',
  'aiConfigs.optimizationRunDetail.passLabel': 'Pass',
  'aiConfigs.optimizationRunDetail.passedLabel': 'Passed',
  'aiConfigs.optimizationRunDetail.passingLabel': 'Passing',
  'aiConfigs.optimizationRunDetail.pollingStoppedBody': 'Live updates stopped after repeated failures.',
  'aiConfigs.optimizationRunDetail.retry': 'Retry',
  'aiConfigs.optimizationRunDetail.scores': 'Scores',
  'aiConfigs.optimizationRunDetail.showAcceptanceStatementLabel': 'Show acceptance statement',
  'aiConfigs.optimizationRunDetail.showRationaleLabel': 'Show rationale',
  'aiConfigs.optimizationRunDetail.tokens': 'Tokens',
  'aiConfigs.optimizationRunDetail.totalTokenUsage': 'Total token usage',
  'aiConfigs.optimizationRunDetail.totalTokenUsageInfoLabel': 'Total token usage info',
  'aiConfigs.optimizationRunDetail.totalTokenUsageTooltip':
    'Total token usage includes all of your completions, judge calls, and variation generation during the optimization process.',
  'aiConfigs.optimizationRunDetail.variationDiffTitle': 'Variation',
  'aiConfigs.optimizationRunsList.collapseRunLabel': 'Collapse run {number}',
  'aiConfigs.optimizationRunsList.completedAt': 'Completed {when}',
  'aiConfigs.optimizationRunsList.expandRunLabel': 'Expand run {number}',
  'aiConfigs.optimizationRunsList.inProgress': 'In progress',
  'aiConfigs.optimizationRunsList.latestRunFailed': 'Latest run failed',
  'aiConfigs.optimizationRunsList.latestRunPassed': 'Latest run passed',
  'aiConfigs.optimizationRunsList.latestRunUnderway': 'Latest run is underway',
  'aiConfigs.optimizationRunsList.markComplete': 'Mark complete',
  'aiConfigs.optimizationRunsList.markFailed': 'Mark failed',
  'aiConfigs.optimizationRunsList.noRunsYet': 'No runs yet',
  'aiConfigs.optimizationRunsList.runActionsLabel': 'Run actions',
  'aiConfigs.optimizationRunsList.runHeading': 'Run {number}',
  'aiConfigs.optimizationRunsList.runStatusLabel': 'Run status',
  'aiConfigs.optimizationRunsList.runningProgressLabel': 'Running',
  'aiConfigs.optimizationScores.acceptance': 'Acceptance',
  'aiConfigs.optimizationScores.acceptanceNumbered': 'Acceptance {number}',
  'aiConfigs.optimizationScores.acceptanceScore': 'Acceptance score',
  'aiConfigs.optimizationScores.acceptanceScoreNumbered': 'Acceptance score {number}',
  'aiConfigs.optimizationScores.acceptanceStatement': 'Acceptance statement',
  'aiConfigs.optimizationScores.acceptanceStatementNumbered': 'Acceptance statement {number}',
  'aiConfigs.optimizationScores.costGate': 'Cost',
  'aiConfigs.optimizationScores.iterationNumbered': 'Iteration {number}',
  'aiConfigs.optimizationScores.latencyGate': 'Latency',
  'aiConfigs.optimizationScores.startingIteration': 'Starting iteration',
  'aiConfigs.optimizationScores.unknownProvider': 'Unknown',
  'aiConfigs.optimizationScoresChart.cost': 'Cost',
  'aiConfigs.optimizationScoresChart.costChartLabel': 'Optimization cost by iteration',
  'aiConfigs.optimizationScoresChart.failingLabel': 'Failing',
  'aiConfigs.optimizationScoresChart.iterationInProgress': 'Iteration {number} in progress…',
  'aiConfigs.optimizationScoresChart.latency': 'Latency',
  'aiConfigs.optimizationScoresChart.latencyChartLabel': 'Optimization latency by iteration',
  'aiConfigs.optimizationScoresChart.passingLabel': 'Passing',
  'aiConfigs.optimizationScoresChart.performanceOverIterations': 'Performance over iterations',
  'aiConfigs.optimizationScoresChart.provider': 'Provider: {provider}',
  'aiConfigs.optimizationScoresChart.scoreChartLabel': '{name} scores by iteration',
  'aiConfigs.optimizationScoresChart.tokens': 'Tokens',
  'aiConfigs.optimizationScoresChart.tokensChartLabel': 'Optimization tokens by iteration',
  'aiConfigs.optimizationStatus.complete': 'Complete',
  'aiConfigs.optimizationStatus.evaluating': 'Evaluating',
  'aiConfigs.optimizationStatus.failed': 'Failed',
  'aiConfigs.optimizationStatus.generating': 'Generating',
  'aiConfigs.optimizationStatus.generatingVariation': 'Generating variation',
  'aiConfigs.optimizationStatus.passed': 'Passed',
  'aiConfigs.optimizationStatus.pending': 'Pending',
  'aiConfigs.optimizationStatus.running': 'Running',
  'aiConfigs.optimizationStatus.statusWithActivity': '{status}: {activity}',
  'aiConfigs.page.title': 'Configs',
  'aiConfigs.parametersJsonEditor.invalidJson': 'Invalid JSON',
  'aiConfigs.payloadLimitAlert.approaching': 'Approaching payload limit: {size}kb of {limit}kb',
  'aiConfigs.payloadLimitAlert.cancel': 'Cancel',
  'aiConfigs.payloadLimitAlert.exceeded': 'Payload limit: {size}kb of {limit}kb',
  'aiConfigs.payloadLimitAlert.feedbackPlaceholder': 'Let us know your requirements',
  'aiConfigs.payloadLimitAlert.feedbackSubmitted': 'Feedback submitted. Thank you.',
  'aiConfigs.payloadLimitAlert.giveFeedback': 'Give feedback',
  'aiConfigs.payloadLimitAlert.needMore': 'Need more?',
  'aiConfigs.payloadLimitAlert.payloadLimitLabel': 'Payload limit',
  'aiConfigs.payloadLimitAlert.submit': 'Send feedback',
  'aiConfigs.playground.snippetLoadError':
    'Could not load snippet {snippetKey}. Confirm the snippet exists and try again.',
  'aiConfigs.playgroundBanners.dailyLimitReached':
    'You have reached your daily token limit. Wait until it resets, or ask your LaunchDarkly administrator to raise your limit.',
  'aiConfigs.playgroundBanners.dailyLimitReachedWithReset':
    'You have reached your daily token limit. Wait until it resets {countdown}, or ask your LaunchDarkly administrator to raise your limit.',
  'aiConfigs.playgroundBanners.thisPlaygroundHas': 'This playground has been archived.',
  'aiConfigs.playgroundConfigPane.addCompareLabel': 'Add compare column',
  'aiConfigs.playgroundConfigPane.addMessage': 'Add message',
  'aiConfigs.playgroundConfigPane.adjustParametersLabel': 'Adjust parameters',
  'aiConfigs.playgroundConfigPane.compare': 'Compare',
  'aiConfigs.playgroundConfigPane.compareErrorToast': 'Error creating compare playground',
  'aiConfigs.playgroundConfigPane.delete': 'Delete',
  'aiConfigs.playgroundConfigPane.duplicate': 'Duplicate',
  'aiConfigs.playgroundConfigPane.duplicateErrorToast': 'Error duplicating column',
  'aiConfigs.playgroundConfigPane.evaluationSaveFailed': 'Error saving evaluation',
  'aiConfigs.playgroundConfigPane.generationModelRequired': 'Generation model is required',
  'aiConfigs.playgroundConfigPane.messageCopied': 'Copied to clipboard',
  'aiConfigs.playgroundConfigPane.messagePlaceholder': 'You are a helpful assistant.',
  'aiConfigs.playgroundConfigPane.modelRequired': 'Model is required',
  'aiConfigs.playgroundConfigPane.overflowMenuLabel': 'Playground column actions',
  'aiConfigs.playgroundConfigPane.removeErrorToast': 'Error removing column',
  'aiConfigs.playgroundConfigPane.save': 'Save',
  'aiConfigs.playgroundConfigPane.saveAsNew': 'Save as new config',
  'aiConfigs.playgroundConfigPane.saveMenuLabel': 'Save options',
  'aiConfigs.playgroundConfigPane.saveToExisting': 'Save to existing config',
  'aiConfigs.playgroundConfigPane.unknownError': 'Unknown error',
  'aiConfigs.playgroundDetail.editNameLabel': 'Edit playground name: {name}',
  'aiConfigs.playgroundDetail.inlineEditHint':
    'Press Enter to edit playground name, or click to activate inline editing',
  'aiConfigs.playgroundDetail.nameErrorToast': 'Error updating playground name',
  'aiConfigs.playgroundDetail.namePlaceholder': 'Playground name',
  'aiConfigs.playgroundDetail.nameUpdatedToast': 'Playground name updated',
  'aiConfigs.playgroundDetail.playgrounds': 'Playgrounds',
  'aiConfigs.playgroundDetail.resizeLabel': 'Resize config and output panes',
  'aiConfigs.playgroundDetail.restore': 'Restore',
  'aiConfigs.playgroundDetail.restoreErrorToast': 'Failed to restore playground',
  'aiConfigs.playgroundDetail.restoreLabel': 'Restore playground',
  'aiConfigs.playgroundDetail.restoredToast': 'Playground restored',
  'aiConfigs.playgroundDetail.unknownError': 'Unknown error',
  'aiConfigs.playgroundHeaderActions.moreRunOptionsLabel': 'More run options',
  'aiConfigs.playgroundHeaderActions.run': 'Run',
  'aiConfigs.playgroundHeaderActions.runAll': 'Run all',
  'aiConfigs.playgroundHeaderActions.runAllOnTopRows': 'Run all on top {count} rows',
  'aiConfigs.playgroundHeaderActions.runCancelFailed': 'Error canceling run',
  'aiConfigs.playgroundHeaderActions.runCanceled': 'Run canceled',
  'aiConfigs.playgroundHeaderActions.runStartFailed': 'Error starting run',
  'aiConfigs.playgroundHeaderActions.runStarted': 'Run started',
  'aiConfigs.playgroundHeaderActions.runsCanceled': 'Runs canceled',
  'aiConfigs.playgroundHeaderActions.runsStarted': 'Runs started',
  'aiConfigs.playgroundHeaderActions.saveAndRun': 'Save and run',
  'aiConfigs.playgroundHeaderActions.saveAndRunAll': 'Save and run all',
  'aiConfigs.playgroundHeaderActions.someRunsFailed': 'Some runs failed to start',
  'aiConfigs.playgroundHeaderActions.stopRun': 'Stop run',
  'aiConfigs.playgroundHeaderActions.stopRuns': 'Stop runs',
  'aiConfigs.playgroundHeaderActions.unknownError': 'Unknown error',
  'aiConfigs.playgroundLoadConfigModal.closeLabel': 'Close',
  'aiConfigs.playgroundLoadConfigModal.couldnTLoad': "Couldn't load configs.",
  'aiConfigs.playgroundLoadConfigModal.goToConfigs': 'Go to configs',
  'aiConfigs.playgroundLoadConfigModal.loadAnExisting': 'Load an existing config',
  'aiConfigs.playgroundLoadConfigModal.loadConfig': 'Load config',
  'aiConfigs.playgroundLoadConfigModal.loadConfigTooltip': 'Load an existing config',
  'aiConfigs.playgroundLoadConfigModal.loading': 'Loading…',
  'aiConfigs.playgroundLoadConfigModal.loadingMore': 'Loading more…',
  'aiConfigs.playgroundLoadConfigModal.noConfigsMatch': 'No configs match your search.',
  'aiConfigs.playgroundLoadConfigModal.noConfigsYet': 'No configs yet.',
  'aiConfigs.playgroundLoadConfigModal.noDescription': 'No description',
  'aiConfigs.playgroundLoadConfigModal.noMessagesFor': 'This variation has no messages. Select another variation.',
  'aiConfigs.playgroundLoadConfigModal.noVariationDescription': 'No description',
  'aiConfigs.playgroundLoadConfigModal.searchLabel': 'Search configs',
  'aiConfigs.playgroundLoadConfigModal.searchPlaceholder': 'Search configs…',
  'aiConfigs.playgroundLoadConfigModal.selectAConfig':
    'Select a config and choose which variation to load into the playground.',
  'aiConfigs.playgroundLoadConfigModal.selectAConfig2': 'Select a config to preview its variations.',
  'aiConfigs.playgroundLoadConfigModal.selectAVariation': 'Select a variation to preview its messages.',
  'aiConfigs.playgroundLoadConfigModal.selectAVariationLabel': 'Select a variation',
  'aiConfigs.playgroundLoadConfigModal.tryAgain': 'Try again',
  'aiConfigs.playgroundLoadConfigModal.variation': 'Variation',
  'aiConfigs.playgroundModelSelector.manageApiKeys': 'Manage API keys',
  'aiConfigs.playgroundModelSelector.noModelsFound': 'No {provider} models found. Select a different provider.',
  'aiConfigs.playgroundModelSelector.searchLabel': 'Search models…',
  'aiConfigs.playgroundModelSelector.searchPlaceholder': 'Search…',
  'aiConfigs.playgroundModelSelector.selectModel': 'Select model',
  'aiConfigs.playgroundNavigationPrompt.body':
    'If you leave now, you will lose any changes since your last run. Re-run the playground to save your changes.',
  'aiConfigs.playgroundNavigationPrompt.cancel': 'Return to editing',
  'aiConfigs.playgroundNavigationPrompt.confirm': 'Leave page',
  'aiConfigs.playgroundNavigationPrompt.title': "You have changes that haven't been run",
  'aiConfigs.playgroundOnboardingModal.carouselLabel': 'Carousel navigation',
  'aiConfigs.playgroundOnboardingModal.configureAlt': 'Configure AI agents',
  'aiConfigs.playgroundOnboardingModal.configureDescription':
    'Define your agent’s model, prompt, and parameters in one place. Then update them instantly without redeploying your app.',
  'aiConfigs.playgroundOnboardingModal.evaluateAlt': 'Evaluate AI performance',
  'aiConfigs.playgroundOnboardingModal.evaluateDescription':
    'Compare performance and cost across all your agent use cases to make confident, data-backed promotion decisions.',
  'aiConfigs.playgroundOnboardingModal.getStarted': 'Get started',
  'aiConfigs.playgroundOnboardingModal.iterateAlt': 'Iterate with playground comparisons',
  'aiConfigs.playgroundOnboardingModal.iterateDescription':
    'Test model and prompt changes head-to-head in the playground before releasing them to a single real user.',
  'aiConfigs.playgroundOnboardingModal.monitorAlt': 'Monitor quality insights',
  'aiConfigs.playgroundOnboardingModal.monitorDescription':
    'Catch quality regressions the moment they happen with always-on monitoring that alerts you before users feel the impact.',
  'aiConfigs.playgroundOnboardingModal.releaseAlt': 'Release with rollout controls',
  'aiConfigs.playgroundOnboardingModal.releaseDescription':
    'New agent versions can be rolled out progressively, with automatic rollback triggered by quality, cost, or latency signals.',
  'aiConfigs.playgroundOnboardingModal.slideLabel': 'Show slide {number}: {title}',
  'aiConfigs.playgroundOnboardingModal.welcomeToAgentcontrol': 'Welcome to AgentControl',
  'aiConfigs.playgroundOutputToolbar.datasetPlaceholder': 'Select a dataset (optional)',
  'aiConfigs.playgroundOutputToolbar.failedOnly': 'Failed only',
  'aiConfigs.playgroundPage.playground': 'Playground',
  'aiConfigs.playgroundPage.title': 'Playground',
  'aiConfigs.playgroundParametersDrawer.closeLabel': 'Close',
  'aiConfigs.playgroundParametersDrawer.editorLabel': 'Model parameters',
  'aiConfigs.playgroundParametersDrawer.theseParametersOverride':
    'These parameters override the model’s configured defaults:',
  'aiConfigs.playgroundParametersDrawer.triggerLabel': 'Adjust parameters',
  'aiConfigs.playgroundParametersDrawer.triggerTooltip': 'Adjust parameters for the selected model',
  'aiConfigs.playgroundPicker.allPlaygrounds': 'All playgrounds',
  'aiConfigs.playgroundPicker.emptyState': 'No playgrounds yet',
  'aiConfigs.playgroundPicker.listLabel': 'Playgrounds',
  'aiConfigs.playgroundPicker.loadError': 'Couldn’t load playgrounds.',
  'aiConfigs.playgroundPicker.loadingMorePlaygrounds': 'Loading more playgrounds…',
  'aiConfigs.playgroundPicker.noSearchResults': 'No playgrounds match your search',
  'aiConfigs.playgroundPicker.panelCreate': 'Evaluate in new playground',
  'aiConfigs.playgroundPicker.panelHeading': 'Evaluate in playground',
  'aiConfigs.playgroundPicker.panelRecentHeading': 'Recent playgrounds',
  'aiConfigs.playgroundPicker.panelSearchPlaceholder': 'Search…',
  'aiConfigs.playgroundPicker.searchLabel': 'Search playgrounds',
  'aiConfigs.playgroundPicker.toolbarCreate': 'New playground',
  'aiConfigs.playgroundPicker.toolbarRecentHeading': 'Recent',
  'aiConfigs.playgroundPicker.toolbarSearchPlaceholder': 'Search playgrounds…',
  'aiConfigs.playgroundPicker.tryAgain': 'Try again',
  'aiConfigs.playgroundResults.compareEmptyState': 'Select <strong>Run all</strong> to generate a response.',
  'aiConfigs.playgroundResults.emptyState': 'Select <strong>Run</strong> to generate a response.',
  'aiConfigs.playgroundResults.loadingLatestRunLabel': 'Loading latest evaluation run',
  'aiConfigs.playgroundResults.loadingResultsLabel': 'Loading playground results',
  'aiConfigs.playgroundResults.loadingRunLabel': 'Loading evaluation run',
  'aiConfigs.playgroundResults.somethingWentWrong': 'Something went wrong while loading evaluation results.',
  'aiConfigs.playgroundResults.somethingWentWrong2': 'Something went wrong while loading playground results.',
  'aiConfigs.playgroundResults.somethingWentWrong3': 'Something went wrong while loading playground evaluations.',
  'aiConfigs.playgroundResults.unexpectedErrorLoading': 'Unexpected error loading evaluation results.',
  'aiConfigs.playgroundResults.unexpectedErrorLoading2': 'Unexpected error loading dataset metadata for row preview.',
  'aiConfigs.playgroundResults.unexpectedErrorLoading3': 'Unexpected error loading dataset row preview.',
  'aiConfigs.playgroundRunConfirm.highTokenCost': 'High token cost',
  'aiConfigs.playgroundRunConfirm.run': 'Run',
  'aiConfigs.playgroundRunConfirm.runAllRows': 'Run all rows',
  'aiConfigs.playgroundRunConfirm.runAllSelected': 'Run all selected',
  'aiConfigs.playgroundRunConfirm.runTopRows': 'Run top {count} rows',
  'aiConfigs.playgroundRunConfirm.thisEvaluationCould':
    'This evaluation could incur a high token cost. Are you sure you want to proceed?',
  'aiConfigs.playgroundRunResults.avgLatency': 'avg. {value}',
  'aiConfigs.playgroundRunResults.avgTokens': 'avg. {value}',
  'aiConfigs.playgroundRunResults.bulkResultsLabel': 'Bulk evaluation results',
  'aiConfigs.playgroundRunResults.criteriaResultsLabel': 'Acceptance criteria results',
  'aiConfigs.playgroundRunResults.criterionShowLessLabel': 'Show less for {criterion}',
  'aiConfigs.playgroundRunResults.criterionShowMoreLabel': 'Show more for {criterion}',
  'aiConfigs.playgroundRunResults.evaluating': 'Evaluating…',
  'aiConfigs.playgroundRunResults.evaluationTimeTooltip': 'Evaluation time',
  'aiConfigs.playgroundRunResults.evaluationTokensTooltip': 'Evaluation tokens',
  'aiConfigs.playgroundRunResults.expectedOutput': 'Expected Output',
  'aiConfigs.playgroundRunResults.expectedOutputCellContext': 'Expected output, row {row}',
  'aiConfigs.playgroundRunResults.generatedOutputCellContext': 'Generated output, row {row}',
  'aiConfigs.playgroundRunResults.inProgress': 'In progress',
  'aiConfigs.playgroundRunResults.input': 'Input',
  'aiConfigs.playgroundRunResults.inputCellContext': 'Input, row {row}',
  'aiConfigs.playgroundRunResults.loadingLabel': 'Loading evaluation run results',
  'aiConfigs.playgroundRunResults.loadingWithStatusLabel': '{status}. Loading evaluation run results.',
  'aiConfigs.playgroundRunResults.noEvaluationResults': 'No evaluation results found. Run the evaluation again.',
  'aiConfigs.playgroundRunResults.noOutputText':
    'This run returned no output text. Check your prompt and run it again.',
  'aiConfigs.playgroundRunResults.noResultsFound': 'No results found.',
  'aiConfigs.playgroundRunResults.outputTokensTooltip': 'Model output tokens',
  'aiConfigs.playgroundRunResults.responseTimeTooltip': 'Model response time',
  'aiConfigs.playgroundRunResults.row': 'Row',
  'aiConfigs.playgroundRunResults.showLess': 'Show less',
  'aiConfigs.playgroundRunResults.showLessLabel': 'Show less ({context})',
  'aiConfigs.playgroundRunResults.showMore': 'Show more',
  'aiConfigs.playgroundRunResults.showMoreLabel': 'Show more ({context})',
  'aiConfigs.playgroundRunResults.stateEvaluating': 'Evaluating',
  'aiConfigs.playgroundRunResults.stateFinalizing': 'Finalizing',
  'aiConfigs.playgroundRunResults.stateLoading': 'Loading',
  'aiConfigs.playgroundRunResults.stateLoadingResults': 'Loading results',
  'aiConfigs.playgroundRunResults.statePending': 'Pending',
  'aiConfigs.playgroundRunResults.thereAreNo': 'There are no results for this run.',
  'aiConfigs.playgroundRunResults.unexpectedErrorLoading': 'Unexpected error loading evaluation results.',
  'aiConfigs.playgroundRunResults.unexpectedErrorLoading2':
    'Unexpected error loading dataset metadata for row preview.',
  'aiConfigs.playgroundRunResults.unexpectedErrorLoading3':
    'Unexpected error loading dataset row preview or run results.',
  'aiConfigs.playgroundRunResults.unexpectedErrorLoading4': 'Unexpected error loading dataset row preview.',
  'aiConfigs.playgroundRunResults.unknownFailure': 'The evaluation has failed for an unknown reason.',
  'aiConfigs.playgroundRunResults.variableLabel': 'Variable: {key}',
  'aiConfigs.playgroundRunResults.variables': 'Variables',
  'aiConfigs.playgroundRunResults.viewResults': 'View results',
  'aiConfigs.playgroundSamplingControls.allRows': 'All rows',
  'aiConfigs.playgroundSamplingControls.modeLabel': 'Row selection mode',
  'aiConfigs.playgroundSamplingControls.newRandomSeed': 'New random seed',
  'aiConfigs.playgroundSamplingControls.percentLabel': 'Percent of dataset rows to evaluate (1 to 100)',
  'aiConfigs.playgroundSamplingControls.random': 'Random',
  'aiConfigs.playgroundSamplingControls.rowCountLabel': 'Number of rows to evaluate (1 to dataset row count)',
  'aiConfigs.playgroundSamplingControls.rows': 'Rows',
  'aiConfigs.playgroundSamplingControls.seed': 'Seed',
  'aiConfigs.playgroundSamplingControls.seedLabel': 'Random seed for row sampling (integer)',
  'aiConfigs.playgroundSamplingControls.top': 'Top',
  'aiConfigs.playgroundSamplingControls.unexpectedErrorLoading': 'Unexpected error loading dataset',
  'aiConfigs.playgroundSamplingControls.unitLabel': 'Interpret row limit as count or percent of dataset',
  'aiConfigs.playgroundSaveAsModal.loading': 'Loading…',
  'aiConfigs.playgroundSaveAsModal.modalTitle': 'Save as new config',
  'aiConfigs.playgroundSaveAsModal.triggerLabel': 'Save to existing config',
  'aiConfigs.playgroundSaveAsVariation.cancel': 'Cancel',
  'aiConfigs.playgroundSaveAsVariation.closeLabel': 'Close',
  'aiConfigs.playgroundSaveAsVariation.config': 'Config',
  'aiConfigs.playgroundSaveAsVariation.configOptionsLabel': 'Config options',
  'aiConfigs.playgroundSaveAsVariation.createFailed': 'Error creating variation',
  'aiConfigs.playgroundSaveAsVariation.createFailedDescription': 'Try again',
  'aiConfigs.playgroundSaveAsVariation.createVariation': 'Create variation',
  'aiConfigs.playgroundSaveAsVariation.goToConfigs': 'Go to configs',
  'aiConfigs.playgroundSaveAsVariation.lastModified': 'Last modified',
  'aiConfigs.playgroundSaveAsVariation.menuLabel': 'Available configs',
  'aiConfigs.playgroundSaveAsVariation.noConfigsMatch': 'No configs match your search.',
  'aiConfigs.playgroundSaveAsVariation.noConfigsYet': 'No configs yet.',
  'aiConfigs.playgroundSaveAsVariation.noDescription': 'No description',
  'aiConfigs.playgroundSaveAsVariation.saveToExisting': 'Save to existing config',
  'aiConfigs.playgroundSaveAsVariation.searchLabel': 'Search configs',
  'aiConfigs.playgroundSaveAsVariation.searchPlaceholder': 'Search configs…',
  'aiConfigs.playgroundSaveAsVariation.selectAConfig':
    'Select a config to save your current playground setup as a new variation.',
  'aiConfigs.playgroundSaveAsVariation.selectAConfig2': 'Select a config to see its details',
  'aiConfigs.playgroundSaveAsVariation.updatedSuccessfully': 'updated successfully',
  'aiConfigs.playgroundSaveSuccessModal.close': 'Close',
  'aiConfigs.playgroundSaveSuccessModal.configSaved': 'Config saved',
  'aiConfigs.playgroundSaveSuccessModal.connectYourApp': 'Connect your app',
  'aiConfigs.playgroundSaveSuccessModal.yourPromptHas':
    'Your prompt has been saved as a config. Connect your app to start serving it in production.',
  'aiConfigs.playgroundSidebar.addVariable': 'Add variable',
  'aiConfigs.playgroundSidebar.cancel': 'Cancel',
  'aiConfigs.playgroundSidebar.criteriaDescription':
    'Acceptance criteria define how AI-generated response quality is evaluated.',
  'aiConfigs.playgroundSidebar.criteriaTitle': 'Acceptance criteria',
  'aiConfigs.playgroundSidebar.customVariables': 'Custom variables',
  'aiConfigs.playgroundSidebar.customVariablesHelp':
    'Variables that are not supplied by the dataset stay editable here.',
  'aiConfigs.playgroundSidebar.dailyTokenUsage': 'Daily token usage',
  'aiConfigs.playgroundSidebar.datasetColumnsError':
    'Unable to load dataset columns. You can still edit variable values below.',
  'aiConfigs.playgroundSidebar.duplicateVariableError': 'A variable with this name already exists.',
  'aiConfigs.playgroundSidebar.evaluationModelDescription': 'This model scores the quality of each response.',
  'aiConfigs.playgroundSidebar.evaluationModelTitle': 'Evaluation model',
  'aiConfigs.playgroundSidebar.fromDataset': 'From dataset',
  'aiConfigs.playgroundSidebar.loadingLabel': 'Loading playground sidebar',
  'aiConfigs.playgroundSidebar.loadingVariables': 'Loading variables…',
  'aiConfigs.playgroundSidebar.noCustomVariables':
    'No custom variables yet. Add one below or reference one in prompts.',
  'aiConfigs.playgroundSidebar.noVariables': 'No variables yet. Reference {token} in prompts or add a variable below.',
  'aiConfigs.playgroundSidebar.removeVariableLabel': 'Remove variable {name}',
  'aiConfigs.playgroundSidebar.tokenLimitReached': 'You’ve reached your daily token limit. {reset}',
  'aiConfigs.playgroundSidebar.tokensUsedToday': 'Tokens you’ve used today. {reset}',
  'aiConfigs.playgroundSidebar.usageResetsDaily': 'Usage resets daily at 00:00 UTC.',
  'aiConfigs.playgroundSidebar.usageResetsIn': 'Usage resets {countdown}.',
  'aiConfigs.playgroundSidebar.variableNameLabel': 'Variable name',
  'aiConfigs.playgroundSidebar.variableNamePlaceholder': 'e.g. company.name',
  'aiConfigs.playgroundSidebar.variableValueLabel': 'Value for {name}',
  'aiConfigs.playgroundSidebar.variablesHelp':
    'Variables are placeholders for values in your prompts, like {token}. For nested values, use dot notation, like {nestedToken}.',
  'aiConfigs.playgroundSidebar.variablesTitle': 'Variables',
  'aiConfigs.playgroundTrialBanner.body': 'Try the playground with select models and {tokens} free tokens per day.',
  'aiConfigs.playgroundTrialBanner.connectProvider': 'Connect your AI provider',
  'aiConfigs.playgroundTrialBanner.heading': 'Compare prompts and run offline evals',
  'aiConfigs.playgroundVariation.loadErrorBody': 'Could not load the requested variation',
  'aiConfigs.playgroundVariation.loadErrorTitle': 'Error loading variation',
  'aiConfigs.playgroundsList.createErrorToast': 'Error creating playground',
  'aiConfigs.playgroundsList.createPlayground': 'New playground',
  'aiConfigs.playgroundsList.createdAtColumn': 'Created at',
  'aiConfigs.playgroundsList.description': 'Establish and retain baseline performance of your AI prompts and models.',
  'aiConfigs.playgroundsList.docsLink': 'Read more about playgrounds',
  'aiConfigs.playgroundsList.nameColumn': 'Name',
  'aiConfigs.playgroundsList.placeholderListLabel': 'Playgrounds list',
  'aiConfigs.playgroundsList.searchLabel': 'Search playgrounds',
  'aiConfigs.playgroundsList.searchPlaceholder': 'Search playgrounds…',
  'aiConfigs.playgroundsList.tableLabel': 'Playgrounds list',
  'aiConfigs.playgroundsList.unknownError': 'Unknown error',
  'aiConfigs.playgroundsList.untitledPlayground': 'Untitled playground',
  'aiConfigs.promptDetailDrawer.closeLabel': 'Close',
  'aiConfigs.promptDetailDrawer.dialogLabel': 'Prompt for {evaluationName}',
  'aiConfigs.promptDetailDrawer.noMessagesAvailable': 'No messages available',
  'aiConfigs.promptDetailDrawer.openInPlayground': 'Open in playground',
  'aiConfigs.promptEditor.assistantRoleDescription':
    'A message generated by the model itself. Can be used to provide example responses to the model.',
  'aiConfigs.promptEditor.assistantRoleLabel': 'Assistant',
  'aiConfigs.promptEditor.close': 'Close',
  'aiConfigs.promptEditor.copiedToClipboard': 'Copied to clipboard',
  'aiConfigs.promptEditor.copyToClipboard': 'Copy to clipboard',
  'aiConfigs.promptEditor.deleteMessage': 'Delete message',
  'aiConfigs.promptEditor.editInFull': 'Edit in full screen',
  'aiConfigs.promptEditor.editMessage': 'Edit message',
  'aiConfigs.promptEditor.editorLabel': 'Prompt editor',
  'aiConfigs.promptEditor.expandLabel': 'Expand',
  'aiConfigs.promptEditor.helpBody':
    "The role of the message, in conjunction with the message below, determines how the model will respond to the user's message. Not all roles are supported by all providers.",
  'aiConfigs.promptEditor.helpHeading': 'Message role',
  'aiConfigs.promptEditor.helpLabel': 'Prompt information help',
  'aiConfigs.promptEditor.messagePlaceholder': 'Write your message',
  'aiConfigs.promptEditor.promptTypeLabel': 'Prompt type',
  'aiConfigs.promptEditor.promptTypeSelectLabel': 'Select a prompt type',
  'aiConfigs.promptEditor.roleSummary': 'Role: {role}',
  'aiConfigs.promptEditor.roleValue': 'Role: <value>{role}</value>',
  'aiConfigs.promptEditor.systemRoleDescription':
    'Top-level instructions to the model, describing how it should generally behave and respond.',
  'aiConfigs.promptEditor.systemRoleLabel': 'System',
  'aiConfigs.promptEditor.userRoleDescription': "The user's message to the model, which the model will respond to.",
  'aiConfigs.promptEditor.userRoleLabel': 'User',
  'aiConfigs.promptMessageEditor.assistantRole': 'Assistant',
  'aiConfigs.promptMessageEditor.assistantRoleDescription':
    'A message generated by the model itself. Can be used to provide example responses to the model.',
  'aiConfigs.promptMessageEditor.copy': 'Copy',
  'aiConfigs.promptMessageEditor.copyLabel': 'Copy to clipboard',
  'aiConfigs.promptMessageEditor.deleteMessageLabel': 'Delete message',
  'aiConfigs.promptMessageEditor.editorLabel': 'Message editor',
  'aiConfigs.promptMessageEditor.expand': 'Expand',
  'aiConfigs.promptMessageEditor.expandLabel': 'Expand message editor',
  'aiConfigs.promptMessageEditor.loadSnippet': 'Load snippet',
  'aiConfigs.promptMessageEditor.messageLabel': 'Message',
  'aiConfigs.promptMessageEditor.messagePlaceholder': 'You are a helpful assistant.',
  'aiConfigs.promptMessageEditor.newSnippet': 'New snippet',
  'aiConfigs.promptMessageEditor.newVersionOf': 'New version of existing snippet',
  'aiConfigs.promptMessageEditor.roleHelpLabel': 'Message role help',
  'aiConfigs.promptMessageEditor.roleTooltipBody':
    "The role of the message, in conjunction with the message below, determines how the model will respond to the user's message. Not all roles are supported by all providers.",
  'aiConfigs.promptMessageEditor.roleTooltipTitle': 'Message role',
  'aiConfigs.promptMessageEditor.saveSnippetAs': 'Save snippet as',
  'aiConfigs.promptMessageEditor.saveSnippetAsMenuItem': 'Save snippet as…',
  'aiConfigs.promptMessageEditor.saveSnippetOptionsLabel': 'Save snippet options',
  'aiConfigs.promptMessageEditor.selectMessageRoleLabel': 'Select message role',
  'aiConfigs.promptMessageEditor.selectRoleLabel': 'Select role',
  'aiConfigs.promptMessageEditor.snippetSaveErrorBody': 'Failed to save snippet. Try again later.',
  'aiConfigs.promptMessageEditor.snippetSaveErrorTitle': 'Failed to save snippet',
  'aiConfigs.promptMessageEditor.snippetSavedToast': 'Snippet updated',
  'aiConfigs.promptMessageEditor.systemRole': 'System',
  'aiConfigs.promptMessageEditor.systemRoleDescription':
    'Top-level instructions to the model, describing how it should generally behave and respond.',
  'aiConfigs.promptMessageEditor.userRole': 'User',
  'aiConfigs.promptMessageEditor.userRoleDescription':
    "The user's message to the model, which the model will respond to.",
  'aiConfigs.promptMessageEditorModal.close': 'Close',
  'aiConfigs.promptMessageEditorModal.editorLabel': 'Message editor',
  'aiConfigs.promptMessageEditorModal.heading': 'Edit message',
  'aiConfigs.promptMessageEditorModal.messagePlaceholder': 'You are a helpful assistant.',
  'aiConfigs.promptOptimizer.aiTools': 'AI tools',
  'aiConfigs.promptOptimizer.assistant': 'Assistant',
  'aiConfigs.promptOptimizer.backToMessages': 'Back to messages',
  'aiConfigs.promptOptimizer.backToVariations': 'Back to variations',
  'aiConfigs.promptOptimizer.cancel': 'Cancel',
  'aiConfigs.promptOptimizer.changesApplied': 'Changes applied',
  'aiConfigs.promptOptimizer.compareToPrevious': 'Compare to previous messages',
  'aiConfigs.promptOptimizer.createVariations': 'Create variations',
  'aiConfigs.promptOptimizer.deleteMessageLabel': 'Delete message',
  'aiConfigs.promptOptimizer.generateAction': 'Generate',
  'aiConfigs.promptOptimizer.generateMessagesDescription':
    "Ask AI to generate messages for you. It's good practice to specify the input you expect to pass in, and the output you expect to receive from the AI",
  'aiConfigs.promptOptimizer.generateMessagesFieldDescription': 'Explain what kind of messages you want to generate',
  'aiConfigs.promptOptimizer.generateMessagesName': 'Generate messages',
  'aiConfigs.promptOptimizer.generateVariationsAction': 'Generate variations',
  'aiConfigs.promptOptimizer.generateVariationsDescription': 'Use AI to generate multiple variations of your messages',
  'aiConfigs.promptOptimizer.generateVariationsFieldDescription':
    'Describe how you want to generate multiple variations of your messages',
  'aiConfigs.promptOptimizer.generateVariationsName': 'Generate multiple variations',
  'aiConfigs.promptOptimizer.hidePreviousResults': 'Hide previous results',
  'aiConfigs.promptOptimizer.message': 'Message',
  'aiConfigs.promptOptimizer.nextAction': 'Next: {action}',
  'aiConfigs.promptOptimizer.nextVariation': 'Next: {name}',
  'aiConfigs.promptOptimizer.numVariationsFieldDescription': 'How many variations to generate (max 3)',
  'aiConfigs.promptOptimizer.optimizeAction': 'Optimize',
  'aiConfigs.promptOptimizer.optimizeMessagesDescription': 'Ask the AI to change your messages in some way',
  'aiConfigs.promptOptimizer.optimizeMessagesFieldDescription':
    'Describe how you want to optimize or change your messages',
  'aiConfigs.promptOptimizer.optimizeMessagesName': 'Optimize messages',
  'aiConfigs.promptOptimizer.previousVariation': 'Previous: {name}',
  'aiConfigs.promptOptimizer.reRun': 'Re-run',
  'aiConfigs.promptOptimizer.seePreviousResults': 'See previous results',
  'aiConfigs.promptOptimizer.structuredOutput': 'Structured output',
  'aiConfigs.promptOptimizer.structuredOutputAction': 'Generate structured output',
  'aiConfigs.promptOptimizer.structuredOutputDescription':
    'Use AI to generate a structured output from your generated messages or variations',
  'aiConfigs.promptOptimizer.structuredOutputFieldDescription':
    "If there are additional instructions for the output, or it's not clear from the messages, describe it here",
  'aiConfigs.promptOptimizer.structuredOutputName': 'Generate structured output',
  'aiConfigs.promptOptimizer.submitError': 'Error submitting prompt',
  'aiConfigs.promptOptimizer.submittingLabel': 'Submitting…',
  'aiConfigs.promptOptimizer.suggestedMessages': 'Suggested messages',
  'aiConfigs.promptOptimizer.suggestedModelTitle': 'Suggested model',
  'aiConfigs.promptOptimizer.summaryTitle': 'Summary',
  'aiConfigs.promptOptimizer.system': 'System',
  'aiConfigs.promptOptimizer.user': 'User',
  'aiConfigs.promptOptimizer.variationCounter': '#{index}/{total} - Name:',
  'aiConfigs.providerPicker.optionsLabel': '{name} options',
  'aiConfigs.providerPicker.searchLabel': 'Search {name}…',
  'aiConfigs.providerPicker.searchPlaceholder': 'Find a {name}',
  'aiConfigs.providerPicker.selectLabel': 'Select {name}',
  'aiConfigs.regionSelector.prefixLabel': 'AWS Bedrock region prefix',
  'aiConfigs.regionSelector.region': 'Region',
  'aiConfigs.regionSelector.selectLabel': 'Select region',
  'aiConfigs.restoreDatasetVersionDialog.body':
    'The rows from v{fromVersion} will be restored as a new version v{nextVersion}. Nothing is overwritten. Later versions stay in the history.',
  'aiConfigs.restoreDatasetVersionDialog.cancel': 'Cancel',
  'aiConfigs.restoreDatasetVersionDialog.dialogLabel': 'Restore v{version}',
  'aiConfigs.restoreDatasetVersionDialog.heading': 'Restore v{version}',
  'aiConfigs.restoreDatasetVersionDialog.restore': 'Restore',
  'aiConfigs.restoreDatasetVersionDialog.restoreFailedBody': 'Try again.',
  'aiConfigs.restoreDatasetVersionDialog.restoreFailedTitle': 'Failed to restore version',
  'aiConfigs.restoreDatasetVersionDialog.restoredToastTitle': 'Restored from v{version}',
  'aiConfigs.restoreModelVersionDialog.cancel': 'Cancel',
  'aiConfigs.restoreModelVersionDialog.dialogLabel': 'Restore to version {version}',
  'aiConfigs.restoreModelVersionDialog.errorToastDescription': 'Something went wrong while saving. Try again.',
  'aiConfigs.restoreModelVersionDialog.errorToastTitle': 'Could not restore model',
  'aiConfigs.restoreModelVersionDialog.heading': 'Restore v{version}',
  'aiConfigs.restoreModelVersionDialog.restore': 'Restore',
  'aiConfigs.restoreModelVersionDialog.restoredToast': 'Restored to v{version}',
  'aiConfigs.restoreModelVersionDialog.thisWillCreate':
    'This will create a new version based on this one and set it as the current version. You can still access all previous versions.',
  'aiConfigs.restoreVersionDialog.applySubmit': 'Apply changes to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.restoreVersionDialog.body':
    'This will create a new version based on this one and set it as the current version. You can still access all previous versions.',
  'aiConfigs.restoreVersionDialog.bulkFailureBody':
    'The snippet was restored to a new version, but applying it to every selected usage did not fully succeed.',
  'aiConfigs.restoreVersionDialog.bulkFailureTitle': 'Some configs were not updated',
  'aiConfigs.restoreVersionDialog.bulkRetryBody':
    'The snippet was restored to a new version, but applying it to selected usages failed. You can retry.',
  'aiConfigs.restoreVersionDialog.cancel': 'Cancel',
  'aiConfigs.restoreVersionDialog.dialogLabel': 'Restore to version {version}',
  'aiConfigs.restoreVersionDialog.heading': 'Restore to v{version}',
  'aiConfigs.restoreVersionDialog.loadingUsageLabel': 'Loading snippet usage',
  'aiConfigs.restoreVersionDialog.restore': 'Restore',
  'aiConfigs.restoreVersionDialog.restoreFailedBody': 'Something went wrong while saving. Try again.',
  'aiConfigs.restoreVersionDialog.restoreFailedTitle': 'Could not restore snippet',
  'aiConfigs.restoreVersionDialog.restored': 'Restored to v{version}',
  'aiConfigs.restoreVersionDialog.retrySubmit': 'Retry applying to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.reuploadDatasetDialog.cancel': 'Cancel',
  'aiConfigs.reuploadDatasetDialog.heading': 'Re-upload dataset',
  'aiConfigs.reuploadDatasetDialog.saveDataset': 'Save dataset',
  'aiConfigs.reviewChanges.addVariationPrefix': 'Add variation',
  'aiConfigs.reviewChanges.approvalFailedToast': 'Failed to create approval request',
  'aiConfigs.reviewChanges.approvalRequestedToast': 'Approval requested successfully and reviewers have been notified',
  'aiConfigs.reviewChanges.approvalRequiredToast': 'These changes require approval',
  'aiConfigs.reviewChanges.approvalRequiredToastDescription': 'Add a reviewer and a description, then request approval',
  'aiConfigs.reviewChanges.cancel': 'Cancel',
  'aiConfigs.reviewChanges.closeLabel': 'close modal',
  'aiConfigs.reviewChanges.judgeDiff': 'Judge: {key} | Sampling rate: {rate}',
  'aiConfigs.reviewChanges.messageDiff': '> Message {number}\nRole: {role}\n\n{content}',
  'aiConfigs.reviewChanges.noChangesToast': 'No changes to request approval for. Edit the config first.',
  'aiConfigs.reviewChanges.requestApproval': 'Request approval',
  'aiConfigs.reviewChanges.reviewAndSave': 'Review and save',
  'aiConfigs.reviewChanges.reviewChanges': 'Review changes',
  'aiConfigs.reviewChanges.saveChanges': 'Save changes',
  'aiConfigs.reviewChanges.saveFailedToast': 'Failed to save changes',
  'aiConfigs.reviewChanges.summaryOfChanges': 'Summary of changes',
  'aiConfigs.reviewChanges.tryAgainLater': 'Try again later',
  'aiConfigs.reviewChanges.viewApprovalRequest': 'View approval request',
  'aiConfigs.runEvalsFromCode.agentBody': 'Get both keys and an environment file your coding agent can load.',
  'aiConfigs.runEvalsFromCode.agentPrompt':
    'Install and configure the LaunchDarkly AI evals SDK in this repository.\n\n1. Add the dependency: npm install @launchdarkly/ai-server\n2. Read two credentials from the environment. Never hardcode them:\n   - LD_API_TOKEN: creates and accesses eval resources\n   - LD_SDK_KEY: sends eval results back for analysis\n3. Add a local script (npm run evals) that runs the evals and prints the scores.\n4. Run an existing eval by its key, or pass the dataset, judges, and eval configuration inline in the run call.\n5. Print a link to the run results at the end of the run.',
  'aiConfigs.runEvalsFromCode.agentPromptIntro':
    "Paste this into your coding agent. It doesn't contain your credentials.",
  'aiConfigs.runEvalsFromCode.agentTitle': 'Set up with an agent',
  'aiConfigs.runEvalsFromCode.back': 'Back',
  'aiConfigs.runEvalsFromCode.closeLabel': 'Close dialog',
  'aiConfigs.runEvalsFromCode.connectHeading': 'Connect your code',
  'aiConfigs.runEvalsFromCode.copyAgentPrompt': 'Copy prompt',
  'aiConfigs.runEvalsFromCode.copyExampleCode': 'Copy example code',
  'aiConfigs.runEvalsFromCode.copyInstallCommand': 'Copy install command',
  'aiConfigs.runEvalsFromCode.copyKeysIntro': 'Copy both keys into your .env file.',
  'aiConfigs.runEvalsFromCode.copyOnceWarning': 'LaunchDarkly shows these keys only once. Save both before you finish.',
  'aiConfigs.runEvalsFromCode.copySample': 'Copy sample',
  'aiConfigs.runEvalsFromCode.done': 'Finish',
  'aiConfigs.runEvalsFromCode.downloadEnvExample': 'Download .env',
  'aiConfigs.runEvalsFromCode.evalApiKeyBody': 'Creates and accesses evaluation resources',
  'aiConfigs.runEvalsFromCode.evalApiKeyTitle': 'Eval API key',
  'aiConfigs.runEvalsFromCode.envSample':
    '# Creates and accesses evaluation resources\nLD_API_TOKEN="{evalApiKey}"\n# Sends evaluation results for analysis\nLD_SDK_KEY="{sdkKey}"',
  'aiConfigs.runEvalsFromCode.envSampleIntro': 'Or add this file to your project folder.',
  'aiConfigs.runEvalsFromCode.envValuePlaceholder': 'your {name}',
  'aiConfigs.runEvalsFromCode.exampleCodeLabel': 'Example code:',
  'aiConfigs.runEvalsFromCode.finishSetupHeading': 'Finish setup',
  'aiConfigs.runEvalsFromCode.frameworkAnthropic': 'Anthropic',
  'aiConfigs.runEvalsFromCode.frameworkLabel': 'Framework',
  'aiConfigs.runEvalsFromCode.frameworkLangChain': 'LangChain',
  'aiConfigs.runEvalsFromCode.frameworkOpenAI': 'OpenAI',
  'aiConfigs.runEvalsFromCode.generateKeys': 'Generate keys',
  'aiConfigs.runEvalsFromCode.generateKeysBody':
    'Running evals from code requires an API key to manage eval resources and an SDK key to send results back for analysis. You can find and manage both keys later in <settings>Settings / Security</settings>.',
  'aiConfigs.runEvalsFromCode.generateKeysHeading': 'Generate keys',
  'aiConfigs.runEvalsFromCode.heading': 'Run evals from code',
  'aiConfigs.runEvalsFromCode.inlinePolicyRun': 'Run evaluations',
  'aiConfigs.runEvalsFromCode.inlinePolicySubmit': 'Submit inline datasets and configuration',
  'aiConfigs.runEvalsFromCode.inlinePolicyTitle': 'Inline policy',
  'aiConfigs.runEvalsFromCode.inlinePolicyUse': 'Use existing eval resources',
  'aiConfigs.runEvalsFromCode.installPackages': 'Install the packages. This example uses {framework}.',
  'aiConfigs.runEvalsFromCode.intro': 'Run evaluations from your code using the SDK.',
  'aiConfigs.runEvalsFromCode.languageLabel': 'Language',
  'aiConfigs.runEvalsFromCode.languagePython': 'Python',
  'aiConfigs.runEvalsFromCode.languageTypeScript': 'TypeScript',
  'aiConfigs.runEvalsFromCode.learnMore': 'Read about running evals from code',
  'aiConfigs.runEvalsFromCode.manualBody': 'Get both keys and an environment file to add to your project.',
  'aiConfigs.runEvalsFromCode.manualTitle': 'Set up manually',
  'aiConfigs.runEvalsFromCode.missingSdkKey': 'Could not find an SDK key for this environment.',
  'aiConfigs.runEvalsFromCode.sdkKeyBody': 'Sends evaluation results for analysis',
  'aiConfigs.runEvalsFromCode.sdkKeyTitle': 'SDK key',
  'aiConfigs.runEvalsFromCode.storedConfirmation': 'I’ve stored both keys in a safe place.',
  'aiConfigs.runEvalsFromCode.serviceTokenCheckboxLabel': 'Create a service token',
  'aiConfigs.runEvalsFromCode.serviceTokenHint':
    '(optional) Recommended for CI and automation. A service token’s permissions are fixed at creation and cannot be changed.',
  'aiConfigs.runEvalsFromCode.serviceTokenLabel': 'Create a service token',
  'aiConfigs.sampleConfigCallout.body':
    'New to AgentControl? Create your own sample AI Config with two ready-made variations, then try it live to see how AgentControl manages models and prompts at runtime.',
  'aiConfigs.sampleConfigCallout.cta': 'Get started with a sample config',
  'aiConfigs.sampleConfigPending.body': 'Setting up your first AgentControl config…',
  'aiConfigs.sampleConfigPending.progressLabel': 'Creating your first AgentControl config',
  'aiConfigs.sandbox.bannerConversationsRunningDescription':
    'This is a Sandbox environment. Synthetic users have realistic conversations until {endsAt, date, medium}. Open Traces to read them.',
  'aiConfigs.sandbox.bannerDescription':
    'This is a Sandbox environment. Its monitoring data comes from synthetic users, not real traffic.',
  'aiConfigs.sandbox.bannerRunningDescription':
    'This is a Sandbox environment. Synthetic users send its monitoring data until {endsAt, date, medium}.',
  'aiConfigs.sandbox.bannerStoppedDescription':
    'This is a Sandbox environment. Synthetic users have stopped, so the charts only show data they sent earlier.',
  'aiConfigs.sandbox.bannerTitle': 'Synthetic data',
  'aiConfigs.sandbox.cancel': 'Cancel',
  'aiConfigs.sandbox.dailyCapLabel': 'Generations per day',
  'aiConfigs.sandbox.dailyConversationCapLabel': 'Conversations per day',
  'aiConfigs.sandbox.dataModeConversations': 'Realistic conversations',
  'aiConfigs.sandbox.dataModeConversationsDescription':
    'Synthetic users chat with the model each variation uses, and every chat shows up in Traces. Uses your free AI allowance first, then your own provider key. OpenAI and Bedrock models need your own key.',
  'aiConfigs.sandbox.dataModeConversationsDisabled':
    'Enable AI features in your account settings to use realistic conversations.',
  'aiConfigs.sandbox.dataModeLabel': 'What synthetic users send',
  'aiConfigs.sandbox.dataModeMetrics': 'Numbers only',
  'aiConfigs.sandbox.dataModeMetricsDescription': 'Made-up metrics for the Monitoring charts. No AI model is called.',
  'aiConfigs.sandbox.dialogDescription':
    "Synthetic users send AI traffic to a separate Sandbox environment, so you can see monitoring data before you set up an SDK. Your other environments aren't affected.",
  'aiConfigs.sandbox.dialogTitle': 'Try it with synthetic users',
  'aiConfigs.sandbox.durationLabel': 'Run for (days)',
  'aiConfigs.sandbox.environmentTakenDescription':
    'This project already has an environment with the key "sandbox" that isn\'t a Sandbox environment.',
  'aiConfigs.sandbox.errorToast': "Couldn't start the Sandbox",
  'aiConfigs.sandbox.noVariationsDescription':
    'Synthetic users need at least one variation to send data. Add a variation to this AI Config to start them.',
  'aiConfigs.sandbox.personaAdd': 'Add persona',
  'aiConfigs.sandbox.personaDescription': 'Description',
  'aiConfigs.sandbox.personaName': 'Name',
  'aiConfigs.sandbox.personaRemove': 'Remove persona',
  'aiConfigs.sandbox.personaShare': 'Share of users (%)',
  'aiConfigs.sandbox.personasDefaultDescription':
    'Synthetic users act as 5 general customer personas. Suggest personas that fit this config, or add your own.',
  'aiConfigs.sandbox.personasDescription':
    'Each synthetic user acts as one of these personas. Shares are scaled to add up to 100%.',
  'aiConfigs.sandbox.personasSuggest': 'Suggest personas',
  'aiConfigs.sandbox.personasSuggestError': "Couldn't suggest personas",
  'aiConfigs.sandbox.personasTitle': 'Personas',
  'aiConfigs.sandbox.promptDescription': 'No SDK yet? See how monitoring works with synthetic users.',
  'aiConfigs.sandbox.runningDescription':
    'Synthetic users are sending data to the Sandbox environment until {endsAt, date, medium}.',
  'aiConfigs.sandbox.start': 'Start Sandbox',
  'aiConfigs.sandbox.startedToast': 'Sandbox started. Synthetic users are sending data.',
  'aiConfigs.sandbox.stopButton': 'Stop synthetic users',
  'aiConfigs.sandbox.stopConfirm': 'Stop',
  'aiConfigs.sandbox.stopDialogDescription':
    'No new synthetic data will be sent. Data that is already in the charts stays there. You can start again at any time.',
  'aiConfigs.sandbox.stopDialogTitle': 'Stop synthetic users',
  'aiConfigs.sandbox.stopErrorToast': 'Synthetic users could not be stopped. Try again.',
  'aiConfigs.sandbox.stoppedToast': 'Synthetic users stopped',
  'aiConfigs.sandbox.tryButton': 'Try it with synthetic users',
  'aiConfigs.saveAsNewConfig.configCreated': '<link>Config</link> created successfully',
  'aiConfigs.saveAsNewConfig.untitledName': 'Untitled',
  'aiConfigs.saveAsNewConfig.variationSaveError': 'Error saving variation to config',
  'aiConfigs.saveAsNewConfig.variationSaveErrorBody': 'Config created. LaunchDarkly could not save the variation.',
  'aiConfigs.saveAsNewConfigDialog.heading': 'Save as new config',
  'aiConfigs.saveAsNewVersionDialog.body': 'Select the snippet you want to override.',
  'aiConfigs.saveAsNewVersionDialog.cancel': 'Cancel',
  'aiConfigs.saveAsNewVersionDialog.cancelContinue': 'Cancel & continue',
  'aiConfigs.saveAsNewVersionDialog.closeLabel': 'Close dialog',
  'aiConfigs.saveAsNewVersionDialog.diffDescription': 'Diff: existing → new version',
  'aiConfigs.saveAsNewVersionDialog.diffDescriptionWithUsage':
    'Diff: existing → new version · Used in {count, plural, one {# config} other {# configs}}',
  'aiConfigs.saveAsNewVersionDialog.failureSummary':
    '{count, plural, one {# change} other {# changes}} could not be completed (see details below).',
  'aiConfigs.saveAsNewVersionDialog.heading': 'Save as new version',
  'aiConfigs.saveAsNewVersionDialog.loadingLabel': 'Loading…',
  'aiConfigs.saveAsNewVersionDialog.nestedSnippetReferences': 'Nested snippet references are not allowed',
  'aiConfigs.saveAsNewVersionDialog.retryLabel':
    'Retry {count, plural, one {# failed change} other {# failed changes}}',
  'aiConfigs.saveAsNewVersionDialog.save': 'Save',
  'aiConfigs.saveAsNewVersionDialog.statusAborted': 'Not attempted because processing was stopped early.',
  'aiConfigs.saveAsNewVersionDialog.statusApprovalRequested': 'Approval request created; update is pending review.',
  'aiConfigs.saveAsNewVersionDialog.statusError': 'An unexpected error occurred while processing this target.',
  'aiConfigs.saveAsNewVersionDialog.statusNotReferenced':
    'Skipped because this variation does not reference the snippet.',
  'aiConfigs.saveAsNewVersionDialog.statusPendingApproval':
    'Skipped because a pending approval request already exists for this AI config.',
  'aiConfigs.saveAsNewVersionDialog.statusUnknown': 'Unknown outcome ({status}).',
  'aiConfigs.saveAsNewVersionDialog.statusUpdated': 'Updated to the new snippet version.',
  'aiConfigs.saveAsNewVersionDialog.summaryApplied':
    'Applied the new snippet version to {count, plural, one {# AI config variation} other {# AI config variations}}.',
  'aiConfigs.saveAsNewVersionDialog.summaryApprovals':
    'Created {count, plural, one {# approval request} other {# approval requests}} for variations that require review before updating.',
  'aiConfigs.saveAsNewVersionDialog.summarySkipped':
    '{count, plural, one {# usage was} other {# usages were}} skipped because a pending approval request already exists for that AI config.',
  'aiConfigs.saveRunAsConfigDialog.heading': 'Save as new config',
  'aiConfigs.saveRunAsConfigDialog.loading': 'Loading…',
  'aiConfigs.scopedTokenModal.agentOptimization': 'Agent optimization',
  'aiConfigs.scopedTokenModal.aiConfig': 'AI config',
  'aiConfigs.scopedTokenModal.aiModelConfigs': 'AI model configs',
  'aiConfigs.scopedTokenModal.body':
    'Create a service token scoped to this optimization configuration. The token will have the following permissions:',
  'aiConfigs.scopedTokenModal.cancel': 'Cancel',
  'aiConfigs.scopedTokenModal.closeLabel': 'Close',
  'aiConfigs.scopedTokenModal.createFailed': 'Failed to create scoped token',
  'aiConfigs.scopedTokenModal.createRuns': 'create runs',
  'aiConfigs.scopedTokenModal.createScopedToken': 'Create scoped token',
  'aiConfigs.scopedTokenModal.createToken': 'Create token',
  'aiConfigs.scopedTokenModal.doneLabel': 'Done',
  'aiConfigs.scopedTokenModal.identifier': 'Identifier',
  'aiConfigs.scopedTokenModal.manageVariations': 'manage variations',
  'aiConfigs.scopedTokenModal.permissions': 'Permissions',
  'aiConfigs.scopedTokenModal.resource': 'Resource',
  'aiConfigs.scopedTokenModal.scopedTokenCreated': 'Scoped token created',
  'aiConfigs.scopedTokenModal.updateRunResults': 'update run results',
  'aiConfigs.sdkSetupDrawer.closeLabel': 'Close',
  'aiConfigs.sdkSetupDrawer.dialogLabel': 'Install AI SDK',
  'aiConfigs.sdkSetupDrawer.heading': "Let's hook up your app",
  'aiConfigs.sdkSetupFlow.agentControlQuickstart': 'AgentControl quickstart',
  'aiConfigs.sdkSetupFlow.allExamplesOnGithub': 'all examples on GitHub',
  'aiConfigs.sdkSetupFlow.alphaSdkNotice':
    'Note: The {sdk} SDK is still in alpha. For the best experience, we recommend Python or NodeJS.',
  'aiConfigs.sdkSetupFlow.back': 'Back',
  'aiConfigs.sdkSetupFlow.checkMyInstall': 'Check my install',
  'aiConfigs.sdkSetupFlow.checkMyInstallAgain': 'Check again',
  'aiConfigs.sdkSetupFlow.checkMyInstallError': 'The install check did not complete.',
  'aiConfigs.sdkSetupFlow.checkMyInstallErrorNext':
    'We could not confirm an SDK connection in {environment}. This does not mean the install failed. Check again. If it keeps failing, finish setup and return after your app is running.',
  'aiConfigs.sdkSetupFlow.checkMyInstallHint':
    'Run your app, then check whether {environment} has received an SDK connection.',
  'aiConfigs.sdkSetupFlow.checkMyInstallNo': 'We have not seen the SDK in {environment} yet.',
  'aiConfigs.sdkSetupFlow.checkMyInstallNoNext': 'Run your app in this environment, then check again.',
  'aiConfigs.sdkSetupFlow.checkMyInstallThisEnvironment': 'This environment',
  'aiConfigs.sdkSetupFlow.checkMyInstallYes': '{environment} has received an SDK connection.',
  'aiConfigs.sdkSetupFlow.checkMyInstallYesNext': 'Continue setup to finish activating AgentControl.',
  'aiConfigs.sdkSetupFlow.close': 'Close',
  'aiConfigs.sdkSetupFlow.connectedActive':
    "You're connected. Next, go to your config, change your prompt and watch real-time updates flow through to your application.",
  'aiConfigs.sdkSetupFlow.connectionWaiting':
    "We're waiting for your app to connect. After you initialize the AgentControl SDK, you can proceed.",
  'aiConfigs.sdkSetupFlow.continueSetup': 'Continue setup',
  'aiConfigs.sdkSetupFlow.copyLinkForLater': 'Copy link for later',
  'aiConfigs.sdkSetupFlow.copyLinkForLaterCopied': 'Link copied to clipboard',
  'aiConfigs.sdkSetupFlow.doThisLater': "I'll do this later",
  'aiConfigs.sdkSetupFlow.dotnetSampleBody':
    'Uses the .NET Server AI SDK examples in <repo></repo> (<code>server-sdk-ai</code>). Install the <sdk></sdk> if needed.',
  'aiConfigs.sdkSetupFlow.dotnetSdk': '.NET SDK',
  'aiConfigs.sdkSetupFlow.heading': "Let's hook up your app",
  'aiConfigs.sdkSetupFlow.installManually': 'Install manually',
  'aiConfigs.sdkSetupFlow.installMethodTabsLabel': 'Installation method',
  'aiConfigs.sdkSetupFlow.installWithAi': 'Install with AI',
  'aiConfigs.sdkSetupFlow.manualInstallModeLabel': 'Manual install mode',
  'aiConfigs.sdkSetupFlow.newAiSdkEnvBody':
    "The AI SDK reads your SDK key from <code>LD_SDK_KEY</code>. Add your provider's API key below.",
  'aiConfigs.sdkSetupFlow.newSampleBody':
    'Uses the AgentControl sample for the {language, select, python {Python} other {JavaScript}} AI SDK. Install <install></install> if needed.',
  'aiConfigs.sdkSetupFlow.nodeRequirements': 'Node.js 20+ and Yarn Classic',
  'aiConfigs.sdkSetupFlow.nodeSampleBody':
    "Uses the Node.js Server AI SDK examples in <repo></repo> (<code>packages/sdk/server-ai/examples</code>). If you work inside the full js-core repo, follow each example's README for monorepo setup (<code>yarn</code> / <code>yarn build</code>) before <code>npm run start</code>.",
  'aiConfigs.sdkSetupFlow.openPromptInAgent': 'Open the setup prompt in your AI coding agent',
  'aiConfigs.sdkSetupFlow.openSampleOnGithub': 'Open the sample on GitHub',
  'aiConfigs.sdkSetupFlow.orPastePrompt': "Or paste this prompt into your AI coding agent's chat",
  'aiConfigs.sdkSetupFlow.poetry': 'Poetry',
  'aiConfigs.sdkSetupFlow.providerNotListed': 'Provider not listed?',
  'aiConfigs.sdkSetupFlow.pythonExamplesNotice':
    'Step-by-step agent examples (OpenAI, AWS Strands, LangChain, Claude) in our docs are written for Python. Choose <strong>Python</strong> in step 1 to copy a full end-to-end example, or use your SDK with the product docs.',
  'aiConfigs.sdkSetupFlow.pythonSampleBody':
    'Uses the official Python examples (<examples></examples>). Install <poetry></poetry> if needed.',
  'aiConfigs.sdkSetupFlow.quickstartNotice':
    'Install commands and the code below follow the same examples as the <link></link>.',
  'aiConfigs.sdkSetupFlow.readAboutAgentControl': 'Read about AgentControl',
  'aiConfigs.sdkSetupFlow.readAboutFrameworkExamples': 'Read about framework examples',
  'aiConfigs.sdkSetupFlow.readAboutNewSdk':
    'Read about the {language, select, python {Python} other {JavaScript}} AI SDK',
  'aiConfigs.sdkSetupFlow.readFullQuickstart': 'Read the full quickstart and framework examples',
  'aiConfigs.sdkSetupFlow.sampleIntro':
    "Don't have an app yet? Clone a ready-to-run sample wired up to your agent-based config.",
  'aiConfigs.sdkSetupFlow.setupInstallsBody':
    'Setup installs packages and writes your LaunchDarkly SDK key and config key into <code>.env</code>.',
  'aiConfigs.sdkSetupFlow.startFromSample': 'Start from sample app',
  'aiConfigs.sdkSetupFlow.stepAddCode': '{step}. Add this code to load your config and run your agent',
  'aiConfigs.sdkSetupFlow.stepAddCredentials': '{step}. Add your credentials to a <code>.env</code> file',
  'aiConfigs.sdkSetupFlow.stepChooseIntegration': '{step}. Choose your agent integration',
  'aiConfigs.sdkSetupFlow.stepChooseProvider': '{step}. Choose your model provider',
  'aiConfigs.sdkSetupFlow.stepCloneSampleApp': '{step}. Clone the sample app',
  'aiConfigs.sdkSetupFlow.stepConnectContinue': '{step}. Check your install',
  'aiConfigs.sdkSetupFlow.stepDropEnvFile':
    '{step}. Drop these into a <code>.env</code> file (in the example folder; copy <code>.env.example</code> to <code>.env</code> when provided)',
  'aiConfigs.sdkSetupFlow.stepInitClient': '{step}. Initialize the LaunchDarkly AI client',
  'aiConfigs.sdkSetupFlow.stepInstallAndRun': '{step}. Install dependencies and run',
  'aiConfigs.sdkSetupFlow.stepInstallDeps': '{step}. Install dependencies',
  'aiConfigs.sdkSetupFlow.stepInstallSdk': '{step}. Install the SDK',
  'aiConfigs.sdkSetupFlow.stepInstallSdks': '{step}. Install the SDKs',
  'aiConfigs.sdkSetupFlow.stepPickLanguage': '{step}. Pick your language',
  'aiConfigs.sdkSetupFlow.stepRunSample': '{step}. Run the sample app',
  'aiConfigs.sdkSetupFlow.stepSetEnv': "{step}. Add your provider's API key to <code>.env</code>",
  'aiConfigs.sdkSetupFlow.subtitle':
    "We've created your first config, <config></config>, so you can update prompts and models in real time without redeploying.",
  'aiConfigs.sdkSetupFlow.useYourExisting': 'Use your existing app',
  'aiConfigs.sdkSetupFlow.worksWith':
    'Works with <cursor>Cursor</cursor>, <claude>Claude Code</claude>, <codex>Codex</codex>, <copilot>GitHub Copilot</copilot>, and more.',
  'aiConfigs.skillDetails.applyChanges': 'Apply changes',
  'aiConfigs.skillDetails.applyChangesToCount': 'Apply changes to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.skillDetails.bulkApplyError':
    'The new skill version was saved, but one or more configs could not be updated to use it. Use Back to review, then try again or update those configs from the skill library.',
  'aiConfigs.skillDetails.cancel': 'Cancel',
  'aiConfigs.skillDetails.configsNotUpdatedBody':
    '{count, plural, one {# config} other {# configs}} could not be updated to the new version.',
  'aiConfigs.skillDetails.couldNotLoad': 'Could not load skill usage. You can still save your changes to the skill.',
  'aiConfigs.skillDetails.delete': 'Delete',
  'aiConfigs.skillDetails.description': 'Description',
  'aiConfigs.skillDetails.descriptionPlaceholder': 'Brief description of this skill',
  'aiConfigs.skillDetails.descriptionTooLong': 'Description must be less than {max} characters',
  'aiConfigs.skillDetails.editTagsLabel': 'Edit tags',
  'aiConfigs.skillDetails.loadingUsageLabel': 'Loading skill usage',
  'aiConfigs.skillDetails.maintainer': 'Maintainer',
  'aiConfigs.skillDetails.markdownRequired': 'Markdown is required',
  'aiConfigs.skillDetails.markdownTooLarge': 'Markdown must not exceed {limit}KB',
  'aiConfigs.skillDetails.noTags': 'No tags',
  'aiConfigs.skillDetails.patchSaveError':
    'We could not create the new skill version. Check the notification for details, then use Back to return here and try again when you are ready.',
  'aiConfigs.skillDetails.save': 'Save',
  'aiConfigs.skillDetails.saveAndUpdateCount': 'Save and update {count, plural, one {# config} other {# configs}}',
  'aiConfigs.skillDetails.saveUnknownError':
    'We could not complete saving. Check the notification for details, then use Back to return here and try again when you are ready.',
  'aiConfigs.skillDetails.savedNotAppliedBody': 'Apply the new version to those configs from the skill library.',
  'aiConfigs.skillDetails.savedNotAppliedTitle': 'Skill updated, but configs were not updated',
  'aiConfigs.skillDetails.saving': 'Saving…',
  'aiConfigs.skillDetails.skillMd': 'SKILL.md',
  'aiConfigs.skillDetails.tagTooLong': 'Each tag must be at most {max} characters',
  'aiConfigs.skillDetails.tags': 'Tags',
  'aiConfigs.skillDetails.tagsLabel': 'Skill tags',
  'aiConfigs.skillDetails.title': '{name} · v{version}',
  'aiConfigs.skillDetails.tooManyTags': 'You can add at most {max} tags',
  'aiConfigs.skillDetails.updateFailedBody': 'Something went wrong while saving. Try again.',
  'aiConfigs.skillDetails.updateFailedTitle': 'Failed to update skill',
  'aiConfigs.skillDetails.updatedAndAppliedTitle':
    'Skill updated and applied to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.skillDetails.updatedTitle': 'Skill successfully updated',
  'aiConfigs.skillDetails.updatedTitleShort': 'Skill updated',
  'aiConfigs.skillDetails.updateToLatest': 'Update to v{version}',
  'aiConfigs.skillDetails.versionLabel': 'Skill version',
  'aiConfigs.skillDetails.versionTagValue': 'Version {version}',
  'aiConfigs.skillForm.keyTaken': 'A skill with this key already exists. Choose a different key.',
  'aiConfigs.skillMaintainerPicker.addMaintainer': 'Add maintainer',
  'aiConfigs.skillMaintainerPicker.maintainerLabel': 'Maintainer',
  'aiConfigs.skillMarkdownEditor.expandLabel': 'Expand',
  'aiConfigs.skillMarkdownEditor.fullScreenTitle': 'Edit SKILL.md',
  'aiConfigs.skillNotifications.createFailedBody': 'Failed to create skill. Try again later.',
  'aiConfigs.skillNotifications.createFailedTitle': 'Failed to create skill',
  'aiConfigs.skillNotifications.createdTitle': 'Skill added to <link>library</link>',
  'aiConfigs.skillNotifications.deleteFailedBody': 'Failed to delete skill. Try again later.',
  'aiConfigs.skillNotifications.deleteFailedTitle': 'Failed to delete skill',
  'aiConfigs.skillNotifications.deletedTitle': 'Skill deleted',
  'aiConfigs.skillOverview.newerVersionBody': 'This skill has a newer version available <strong>(v{version})</strong>',
  'aiConfigs.skillOverview.noSkillSelected': 'No skill selected',
  'aiConfigs.skillOverview.skillMd': 'SKILL.md',
  'aiConfigs.skillOverview.thisSkillIs': 'This skill is out of date',
  'aiConfigs.skillPicker.addSkill': 'Add skill',
  'aiConfigs.skillPicker.backToListLabel': 'Back to all skills',
  'aiConfigs.skillPicker.createSkill': 'Create skill',
  'aiConfigs.skillPicker.created': 'Created',
  'aiConfigs.skillPicker.findSkillLabel': 'Find a skill',
  'aiConfigs.skillPicker.loadingTitle': 'Loading…',
  'aiConfigs.skillPicker.noDescription': 'No description',
  'aiConfigs.skillPicker.selectSkillLabel': 'Select skill',
  'aiConfigs.skillPicker.title': 'Add skill',
  'aiConfigs.skillRestoreVersion.applyChangesLabel':
    'Apply changes to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.skillRestoreVersion.cancel': 'Cancel',
  'aiConfigs.skillRestoreVersion.couldNotLoad':
    "Could not load skill usage. You can still restore this version, but it won't be applied to any configs.",
  'aiConfigs.skillRestoreVersion.dialogLabel': 'Restore to version {version}',
  'aiConfigs.skillRestoreVersion.errorBody': 'Something went wrong while saving. Try again.',
  'aiConfigs.skillRestoreVersion.errorTitle': 'Could not restore skill',
  'aiConfigs.skillRestoreVersion.heading': 'Restore to v{version}',
  'aiConfigs.skillRestoreVersion.loadingUsageLabel': 'Loading skill usage',
  'aiConfigs.skillRestoreVersion.partialTitle': 'Some configs were not updated',
  'aiConfigs.skillRestoreVersion.restore': 'Restore',
  'aiConfigs.skillRestoreVersion.restoredFailedBody':
    'The skill was restored to a new version, but applying it to selected usages failed. You can retry.',
  'aiConfigs.skillRestoreVersion.restoredPartialBody':
    'The skill was restored to a new version, but applying it to every selected usage did not fully succeed.',
  'aiConfigs.skillRestoreVersion.restoredTitle': 'Restored to v{version}',
  'aiConfigs.skillRestoreVersion.retryApplyLabel':
    'Retry applying to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.skillRestoreVersion.retryRemainingBody':
    'Applying the restored skill version to every selected usage did not fully succeed. You can retry the remaining failures.',
  'aiConfigs.skillRestoreVersion.thisWillCreate':
    'This will create a new version based on this one and set it as the current version. You can still access all previous versions.',
  'aiConfigs.skillSaveConfirm.afterYouSave':
    'After you save, we wait until targeting details finish loading. Then we apply this new skill version to the affected AI config usages.',
  'aiConfigs.skillSaveConfirm.back': 'Back',
  'aiConfigs.skillSaveConfirm.cancel': 'Cancel',
  'aiConfigs.skillSaveConfirm.confirmDialogLabel': 'Create new skill version',
  'aiConfigs.skillSaveConfirm.couldNotSave': 'Could not save',
  'aiConfigs.skillSaveConfirm.createNewVersion': 'Create new version',
  'aiConfigs.skillSaveConfirm.createNewVersion2': 'Create new version',
  'aiConfigs.skillSaveConfirm.errorDialogLabel': 'Save skill version error',
  'aiConfigs.skillSaveConfirm.ifApprovalIs':
    'If approval is required first, we send a review request instead of applying immediately.',
  'aiConfigs.skillSaveConfirm.noteThisUpdate':
    'Note: This update does not automatically apply to configs currently using this skill. Manually update each config to use the new version.',
  'aiConfigs.skillSaveConfirm.savingWillCreateBody':
    'Saving will create <strong>v{version}</strong> and override the current version of this skill.',
  'aiConfigs.skillSaveConfirm.willUpdateSelectedBody':
    'We will update the {count, plural, one {# config} other {# configs}} you selected to use this new skill version whenever we can apply that change right away.',
  'aiConfigs.skillUsageDrawer.applyChanges': 'Apply changes',
  'aiConfigs.skillUsageDrawer.applyDisabledLabel': 'Select a different version to apply changes',
  'aiConfigs.skillUsageDrawer.cancel': 'Cancel',
  'aiConfigs.skillUsageDrawer.couldNotLoad': 'Could not load skill usage.',
  'aiConfigs.skillUsageDrawer.currentVersionOption': 'v{version}, current version',
  'aiConfigs.skillUsageDrawer.loadingReferencesLabel': 'Loading references',
  'aiConfigs.skillUsageDrawer.loadingTargeting': 'Loading targeting…',
  'aiConfigs.skillUsageDrawer.rowVersionChangeLabel':
    'Update skill version for {variationName} in {configName}. Changing from v{from} to v{to}.',
  'aiConfigs.skillUsageDrawer.rowVersionLabel':
    'Update skill version for {variationName} in {configName}. Currently v{version}.',
  'aiConfigs.skillUsageDrawer.rowVersionsListLabel': 'Skill versions for {variationName} in {configName}',
  'aiConfigs.skillUsageDrawer.staleVersionLabel': 'Not using the latest version (v{version})',
  'aiConfigs.skillUsageDrawer.updateAllLabel': 'Update all skill versions',
  'aiConfigs.skillUsageDrawer.updateAllTo': 'Update all to',
  'aiConfigs.skillUsageDrawer.updateTo': 'Update to',
  'aiConfigs.skillUsageDrawer.usedByLine':
    'Used by <link>{label}</link> ({kind, select, experiment {experiment} other {rollout}})',
  'aiConfigs.skillUsageDrawer.usedInSummary': 'Used in {count, plural, one {# config} other {# configs}}',
  'aiConfigs.skillUsageDrawer.versionChange': 'v{from} → v{to}',
  'aiConfigs.skillUsageDrawer.versionStatusLabel': 'Version status',
  'aiConfigs.skillUsageSelector.applyChangesTo': 'Apply changes to:',
  'aiConfigs.skillUsageSelector.applyRowLabel': 'Apply skill change to {configName}, {variationName}, {version}',
  'aiConfigs.skillUsageSelector.applyToAllLabel': 'Apply changes to all eligible usages',
  'aiConfigs.skillUsageSelector.approvalRequired': 'Approval required',
  'aiConfigs.skillUsageSelector.couldNotLoad': 'Could not load which configs reference this skill.',
  'aiConfigs.skillUsageSelector.loadingTargeting': 'Loading targeting…',
  'aiConfigs.skillUsageSelector.thisUsageCannot':
    'This usage cannot be included because the config is part of an active experiment or rollout.',
  'aiConfigs.skillUsageSelector.usagesLabel': 'Skill usages',
  'aiConfigs.skillUsageSelector.usedByBody':
    'This skill is used by <strong>{count, plural, one {# config} other {# configs}}</strong>. Select which configs should receive this update upon approval.',
  'aiConfigs.skillVersionHistory.copied': 'Copied to clipboard',
  'aiConfigs.skillVersionHistory.copyVersionLabel': 'Copy version {version} SKILL.md',
  'aiConfigs.skillVersionHistory.errorMessage': 'Could not load skill version history.',
  'aiConfigs.skillVersionPicker.triggerLabel': 'Select skill version, current version v{version}',
  'aiConfigs.skillVersionPicker.versionsLabel': 'Skill versions',
  'aiConfigs.skillsField.addSelected': 'Add {count, plural, one {# skill} other {# skills}}',
  'aiConfigs.skillsField.addSkills': 'Add skills',
  'aiConfigs.skillsField.allSkills': 'All skills',
  'aiConfigs.skillsField.applySelectionLabel': 'Apply skill selection',
  'aiConfigs.skillsField.attachSkillsLabel': 'Attach skills',
  'aiConfigs.skillsField.createNewSkill': 'Create new skill',
  'aiConfigs.skillsField.heading': 'Skills',
  'aiConfigs.skillsField.noSkillsFound': 'No skills found',
  'aiConfigs.skillsField.optionsLabel': 'Skill options',
  'aiConfigs.skillsField.removeAll': 'Remove all',
  'aiConfigs.skillsField.removeSkillLabel': 'Remove skill',
  'aiConfigs.skillsField.searchLabel': 'Search skills',
  'aiConfigs.skillsField.searchPlaceholder': 'Search skills…',
  'aiConfigs.skillsField.skillMd': 'SKILL.md',
  'aiConfigs.skillsField.thisSkillHas': 'This skill has a newer version available',
  'aiConfigs.skillsField.updateAllTo': 'Update all to latest version',
  'aiConfigs.skillsField.viewDetailsLabel': 'View skill details for {key}',
  'aiConfigs.skillsList.anUnexpectedError': 'An unexpected error occurred while loading agent skills',
  'aiConfigs.skillsList.configCount': '{count, plural, one {# config} other {# configs}}',
  'aiConfigs.skillsList.copyKeyLabel': 'Copy key to clipboard',
  'aiConfigs.skillsList.createReusableAgent': 'Create reusable agent instructions to use across your AI Configs.',
  'aiConfigs.skillsList.createSkill': 'Create skill',
  'aiConfigs.skillsList.created': 'Created',
  'aiConfigs.skillsList.currentVersionUsageLabel':
    'Show the {count, plural, one {# config} other {# configs}} using the current version of {name}',
  'aiConfigs.skillsList.deleteInUseTooltip':
    'This skill is referenced by one or more configs. Remove it from those configs before you delete it.',
  'aiConfigs.skillsList.deleteSkillLabel': 'Delete skill',
  'aiConfigs.skillsList.deleteUnknownUsageTooltip':
    "Couldn't check whether this skill is in use. Try again before deleting it.",
  'aiConfigs.skillsList.editSkillDrawerLabel': 'Edit skill',
  'aiConfigs.skillsList.editSkillLabel': 'Edit skill {name}',
  'aiConfigs.skillsList.failedToLoad': 'Failed to load skill.',
  'aiConfigs.skillsList.key': 'Key',
  'aiConfigs.skillsList.keyCopied': 'Key copied to clipboard',
  'aiConfigs.skillsList.listLabel': 'Agent skills list',
  'aiConfigs.skillsList.loadingMemberLabel': 'Loading member',
  'aiConfigs.skillsList.name': 'Name',
  'aiConfigs.skillsList.noSkillsFound': 'No skills found',
  'aiConfigs.skillsList.noSkillsYet': 'No skills yet',
  'aiConfigs.skillsList.owner': 'Owner',
  'aiConfigs.skillsList.reuseAgentInstructions': 'Reuse agent instructions across your configs.',
  'aiConfigs.skillsList.searchLabel': 'Search agent skills',
  'aiConfigs.skillsList.searchPlaceholder': 'Search skills…',
  'aiConfigs.skillsList.skillMd': 'Skill.md',
  'aiConfigs.skillsList.staleUsageTooltip':
    '{count, plural, one {# config is} other {# configs are}} not using the latest version (v{version})',
  'aiConfigs.skillsList.tags': 'Tags',
  'aiConfigs.skillsList.tagsLabel': 'Skill tags',
  'aiConfigs.skillsList.tryAdjustingYour': 'Try adjusting your search',
  'aiConfigs.skillsList.tryAgain': 'Try again',
  'aiConfigs.skillsList.usageDrawerLabel': 'Skill usage',
  'aiConfigs.skillsList.usageErrorTitle': 'Could not load usage',
  'aiConfigs.skillsList.usedIn': 'Used in',
  'aiConfigs.skillsList.version': 'Version',
  'aiConfigs.skillsList.versionHistoryLabel': 'Open version history for {name}, currently v{version}',
  'aiConfigs.skillsList.versionStatusLabel': 'Version status',
  'aiConfigs.skillsSdkRequirement.body': 'Skills require version 1.0.0 or later of the AI SDK for Python or Node.js.',
  'aiConfigs.skillsSdkRequirement.docsLink': 'Read the documentation',
  'aiConfigs.snippetBody.nestedReferenceError':
    'Snippet bodies cannot contain references to other snippets. Remove them or use variables instead.',
  'aiConfigs.snippetDetails.applyChanges': 'Apply changes',
  'aiConfigs.snippetDetails.applyChangesToConfigs':
    'Apply changes to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.snippetDetails.body': 'Body',
  'aiConfigs.snippetDetails.bodyRequired': 'Body is required',
  'aiConfigs.snippetDetails.bulkApplyError':
    'Saved the new snippet version, but LaunchDarkly could not update one or more configs to use it. Select Back to review, then try again or update those configs from the snippet library.',
  'aiConfigs.snippetDetails.cancel': 'Cancel',
  'aiConfigs.snippetDetails.delete': 'Delete',
  'aiConfigs.snippetDetails.deleteInUseTooltip':
    'This snippet is referenced by one or more configs. Remove it from those configs before you delete it.',
  'aiConfigs.snippetDetails.description': 'Description',
  'aiConfigs.snippetDetails.editTagsLabel': 'Edit tags',
  'aiConfigs.snippetDetails.loadingUsageLabel': 'Loading snippet usage',
  'aiConfigs.snippetDetails.maintainer': 'Maintainer',
  'aiConfigs.snippetDetails.noTags': 'No tags',
  'aiConfigs.snippetDetails.patchSaveError':
    'We could not create the new snippet version. Check the notification for details, then use Back to return here and try again when you are ready.',
  'aiConfigs.snippetDetails.save': 'Save',
  'aiConfigs.snippetDetails.saveUnknownError':
    'We could not complete saving. Check the notification for details, then use Back to return here and try again when you are ready.',
  'aiConfigs.snippetDetails.saving': 'Saving…',
  'aiConfigs.snippetDetails.tagTooLong': 'Each tag must be at most {max} characters',
  'aiConfigs.snippetDetails.tags': 'Tags',
  'aiConfigs.snippetDetails.tagsLabel': 'Snippet tags',
  'aiConfigs.snippetDetails.titleWithVersion': '{name} · v{version}',
  'aiConfigs.snippetDetails.tooManyTags': 'You can add at most {max} tags',
  'aiConfigs.snippetDetails.updated': 'Snippet successfully updated',
  'aiConfigs.snippetDetails.updatedAndApplied':
    'Snippet updated and applied to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.snippetDetails.usageLoadError':
    'Could not load snippet usage. You can still save your changes to the snippet body.',
  'aiConfigs.snippetDetails.versionLabel': 'Snippet version',
  'aiConfigs.snippetDetails.versionTagText': 'Version {version}',
  'aiConfigs.snippetDrawer.closeLabel': 'Close panel',
  'aiConfigs.snippetMaintainerPicker.addMaintainer': 'Add maintainer',
  'aiConfigs.snippetMaintainerPicker.maintainerLabel': 'Maintainer',
  'aiConfigs.snippetPicker.compareEmptyState': 'Select a snippet to compare versions',
  'aiConfigs.snippetPicker.loadError': 'Could not load prompt snippets.',
  'aiConfigs.snippetPicker.noSnippetsFound': 'No snippets found',
  'aiConfigs.snippetPicker.searchLabel': 'Search prompt snippets',
  'aiConfigs.snippetPicker.searchPlaceholder': 'Search prompt snippets…',
  'aiConfigs.snippetPicker.tagsLabel': 'Snippet tags',
  'aiConfigs.snippetSave.applied':
    'New version of "{name}" has been applied to {count, plural, one {# config} other {# configs}}.',
  'aiConfigs.snippetSave.appliedAndPendingApproval':
    'New version of "{name}" has been applied to {updated, plural, one {# config} other {# configs}}; {approval, plural, one {# config will} other {# configs will}} update once approved.',
  'aiConfigs.snippetSave.appliedAndSkipped':
    'New version of "{name}" has been applied to {updated, plural, one {# config} other {# configs}}. {pending, plural, one {# was skipped} other {# were skipped}} (pending approval already exists).',
  'aiConfigs.snippetSave.newVersionSaved': 'New version of "{name}" saved.',
  'aiConfigs.snippetSave.saved': 'Snippet saved',
  'aiConfigs.snippetSave.savedWithPending':
    'Saved new version of "{name}". {count, plural, one {# selected usage already has} other {# selected usages already have}} a pending approval request.',
  'aiConfigs.snippetSave.willApplyOnceApproved':
    'New version of "{name}" will be applied to {count, plural, one {# config} other {# configs}} once approved.',
  'aiConfigs.snippetSaveConfirm.applyNote':
    'After you save, we wait until targeting details finish loading. Then we apply this new snippet version to the affected AI config usages.',
  'aiConfigs.snippetSaveConfirm.approvalNote':
    'If approval is required first, we send a review request instead of applying immediately.',
  'aiConfigs.snippetSaveConfirm.back': 'Back',
  'aiConfigs.snippetSaveConfirm.body':
    'Saving will create <strong>v{version}</strong> and override the current version of this snippet.',
  'aiConfigs.snippetSaveConfirm.cancel': 'Cancel',
  'aiConfigs.snippetSaveConfirm.confirmLabel': 'Create new version',
  'aiConfigs.snippetSaveConfirm.errorHeading': 'Could not save',
  'aiConfigs.snippetSaveConfirm.heading': 'Create new version',
  'aiConfigs.snippetSaveConfirm.manualUpdateNote':
    'Note: This update does not automatically apply to configs currently using this snippet. Manually update each config to use the new version.',
  'aiConfigs.snippetSaveConfirm.selectedConfigsNote':
    'We will update the {count, plural, one {# config} other {# configs}} you selected to use this new snippet version whenever we can apply that change right away.',
  'aiConfigs.snippetUsageDrawer.aRollout': 'a rollout',
  'aiConfigs.snippetUsageDrawer.applyChanges': 'Apply changes',
  'aiConfigs.snippetUsageDrawer.applyDisabledLabel': 'Select a different version to apply changes',
  'aiConfigs.snippetUsageDrawer.cancel': 'Cancel',
  'aiConfigs.snippetUsageDrawer.loadError': 'Could not load snippet usage.',
  'aiConfigs.snippetUsageDrawer.loadingReferencesLabel': 'Loading references',
  'aiConfigs.snippetUsageDrawer.loadingTargeting': 'Loading targeting…',
  'aiConfigs.snippetUsageDrawer.rolloutLabel': 'Rollout {id}…',
  'aiConfigs.snippetUsageDrawer.rowVersionChangeLabel':
    'Update snippet version for {variationName} in {configName}. Changing from v{fromVersion} to v{toVersion}.',
  'aiConfigs.snippetUsageDrawer.rowVersionLabel':
    'Update snippet version for {variationName} in {configName}. Currently v{version}.',
  'aiConfigs.snippetUsageDrawer.rowVersionListLabel': 'Snippet versions for {variationName} in {configName}',
  'aiConfigs.snippetUsageDrawer.staleVersionLabel': 'Not using the latest version (v{version})',
  'aiConfigs.snippetUsageDrawer.updateAllLabel': 'Update all snippet versions',
  'aiConfigs.snippetUsageDrawer.updateAllTo': 'Update all to',
  'aiConfigs.snippetUsageDrawer.updateTo': 'Update to',
  'aiConfigs.snippetUsageDrawer.usedByARollout': 'Used by a rollout (rollout)',
  'aiConfigs.snippetUsageDrawer.usedByAnExperiment': 'Used by an experiment (experiment)',
  'aiConfigs.snippetUsageDrawer.usedByResource':
    'Used by <link>{label}</link> ({kind, select, experiment {experiment} other {rollout}})',
  'aiConfigs.snippetUsageDrawer.usedByRollout': 'Used by {label} (rollout)',
  'aiConfigs.snippetUsageDrawer.usedInSummary': 'Used in {count, plural, one {# config} other {# configs}}',
  'aiConfigs.snippetUsageDrawer.versionStatusLabel': 'Version status',
  'aiConfigs.snippetUsageSelector.applyAllLabel': 'Apply changes to all eligible usages',
  'aiConfigs.snippetUsageSelector.approvalRequired': 'Approval required',
  'aiConfigs.snippetUsageSelector.body':
    'This snippet is used by <strong>{count, plural, one {# config} other {# configs}}</strong>. Select which configs should receive this update upon approval.',
  'aiConfigs.snippetUsageSelector.heading': 'Apply changes to:',
  'aiConfigs.snippetUsageSelector.loadError': 'Could not load which configs reference this snippet.',
  'aiConfigs.snippetUsageSelector.loadingTargeting': 'Loading targeting…',
  'aiConfigs.snippetUsageSelector.lockedUsage':
    'This usage cannot be included because the config is part of an active experiment or rollout.',
  'aiConfigs.snippetUsageSelector.rowLabel': 'Apply body change to {configName}, {variationName}, {version}',
  'aiConfigs.snippetUsageSelector.usagesLabel': 'Snippet usages',
  'aiConfigs.snippetsList.body': 'Body',
  'aiConfigs.snippetsList.copyKeyLabel': 'Copy key to clipboard',
  'aiConfigs.snippetsList.createSnippet': 'Create snippet',
  'aiConfigs.snippetsList.deleteInUseTooltip':
    'This snippet is referenced by one or more configs. Remove it from those configs before you delete it.',
  'aiConfigs.snippetsList.deleteLabel': 'Delete snippet',
  'aiConfigs.snippetsList.description': 'Reuse prompt text across your configs.',
  'aiConfigs.snippetsList.editDrawerLabel': 'Edit prompt snippet',
  'aiConfigs.snippetsList.editSnippetLabel': 'Edit prompt {name}',
  'aiConfigs.snippetsList.emptyStateBody': 'Create reusable prompt text to use across your configs.',
  'aiConfigs.snippetsList.emptyStateHeading': 'No snippets yet',
  'aiConfigs.snippetsList.key': 'Key',
  'aiConfigs.snippetsList.keyCopied': 'Key copied to clipboard',
  'aiConfigs.snippetsList.listLoadError': 'An unexpected error occurred while loading prompt snippets',
  'aiConfigs.snippetsList.loadingMaintainerLabel': 'Loading member',
  'aiConfigs.snippetsList.name': 'Name',
  'aiConfigs.snippetsList.owner': 'Owner',
  'aiConfigs.snippetsList.rowLoadError': 'Failed to load prompt snippet.',
  'aiConfigs.snippetsList.searchEmptyStateBody': 'Try adjusting your search',
  'aiConfigs.snippetsList.searchEmptyStateHeading': 'No prompt snippets found',
  'aiConfigs.snippetsList.searchLabel': 'Search prompt snippets',
  'aiConfigs.snippetsList.searchPlaceholder': 'Search snippets…',
  'aiConfigs.snippetsList.staleUsageTooltip':
    '{count, plural, one {# config is} other {# configs are}} not using the latest version (v{version})',
  'aiConfigs.snippetsList.tableLabel': 'Prompt snippets list',
  'aiConfigs.snippetsList.tags': 'Tags',
  'aiConfigs.snippetsList.tagsLabel': 'Snippet tags',
  'aiConfigs.snippetsList.tryAgain': 'Try again',
  'aiConfigs.snippetsList.updated': 'Updated',
  'aiConfigs.snippetsList.usageDrawerLabel': 'Snippet usage',
  'aiConfigs.snippetsList.usageLoadErrorTitle': 'Could not load usage',
  'aiConfigs.snippetsList.usedIn': 'Used in',
  'aiConfigs.snippetsList.usedInCount': '{count, plural, one {# config} other {# configs}}',
  'aiConfigs.snippetsList.version': 'Version',
  'aiConfigs.snippetsList.versionHistoryLabel': 'Open version history for {name}, currently v{version}',
  'aiConfigs.starterCodeDrawer.heading': 'Generate starter code',
  'aiConfigs.starterConfigBanner.bodyConnect':
    "<name>{configName}</name> shows how you can update prompts and models in real time without redeploying. Edit a variation and <strong>try it live</strong>. When you're ready to connect your app, wire it up using our SDK.",
  'aiConfigs.starterConfigBanner.bodyRollOut':
    '<name>{configName}</name> shows how you can update prompts and models in real time without redeploying. Edit a variation, <strong>try it live</strong>, and roll changes out from Targeting.',
  'aiConfigs.starterConfigBanner.heading': "We've created your first config",
  'aiConfigs.starterConfigBanner.setUpSdk': 'Set up SDK',
  'aiConfigs.status.loading': 'Loading…',
  'aiConfigs.toolCustomParameters.customParameters': 'Custom parameters',
  'aiConfigs.toolCustomParameters.customParametersAre':
    'Custom parameters are not used directly by the model call, but are passed to the SDK as a JSON object',
  'aiConfigs.toolCustomParameters.invalidJson': 'Invalid JSON',
  'aiConfigs.toolCustomParameters.json': 'JSON',
  'aiConfigs.toolCustomParameters.jsonLabel': 'Tool custom parameters JSON',
  'aiConfigs.toolCustomParameters.visual': 'Visual',
  'aiConfigs.toolDetails.createHeading': 'Create tool',
  'aiConfigs.toolDetails.editHeading': 'Edit tool',
  'aiConfigs.toolDetails.viewVersions': 'View versions',
  'aiConfigs.toolDetails.viewVersionsLabel': 'View versions, current version v{version}',
  'aiConfigs.toolEditor.addMaintainer': 'Add maintainer',
  'aiConfigs.toolEditor.addTagLabel': 'Add tag',
  'aiConfigs.toolEditor.addTagsLabel': 'Add tags',
  'aiConfigs.toolEditor.cancel': 'Cancel',
  'aiConfigs.toolEditor.createTool': 'Create tool',
  'aiConfigs.toolEditor.description': 'Description',
  'aiConfigs.toolEditor.editingPastVersionBody':
    'You are editing <strong>v{version}</strong>. The latest version of this tool is <strong>v{latestVersion}</strong>',
  'aiConfigs.toolEditor.explainWhatThis':
    'Explain what this tool does and when to use it. The model relies on this to decide when to call it.',
  'aiConfigs.toolEditor.key': 'Key',
  'aiConfigs.toolEditor.keyMustBe': 'Key must be alphanumeric and can contain underscores and hyphens',
  'aiConfigs.toolEditor.maintainerLabel': 'Maintainer',
  'aiConfigs.toolEditor.manageTags': 'Manage tags',
  'aiConfigs.toolEditor.readAboutToolsLink': 'Read about tools',
  'aiConfigs.toolEditor.referencesFailedBody': 'Tool saved. LaunchDarkly could not update some config references.',
  'aiConfigs.toolEditor.referencesFailedTitle': 'Failed to update config references',
  'aiConfigs.toolEditor.referencesPartialBody':
    'LaunchDarkly could not update {count, plural, one {# reference} other {# references}}.',
  'aiConfigs.toolEditor.referencesUnconfirmedBody': 'Config reference updates could not be confirmed.',
  'aiConfigs.toolEditor.save': 'Save',
  'aiConfigs.toolEditor.saveAndUpdate': 'Save and update {count, plural, one {# config} other {# configs}}',
  'aiConfigs.toolEditor.saveFailedBody': 'Try again.',
  'aiConfigs.toolEditor.saveFailedTitle': 'Failed to save tool',
  'aiConfigs.toolEditor.saving': 'Saving…',
  'aiConfigs.toolEditor.startFromLatest': 'Start from latest version instead',
  'aiConfigs.toolEditor.tagCount': '{count, plural, one {# tag} other {# tags}}',
  'aiConfigs.toolEditor.tagTooLong': 'Each tag must be at most {max} characters',
  'aiConfigs.toolEditor.thisKeyIs': 'This key is already in use',
  'aiConfigs.toolEditor.tooManyTags': 'You can add at most {max} tags',
  'aiConfigs.toolEditor.toolsLetYour':
    'Tools let your configs call actions you define in code. Attach a tool to a variation to make it available to the model.',
  'aiConfigs.toolEditor.updatedTitle': 'Tool updated',
  'aiConfigs.toolEditor.useLettersNumbers':
    'Use letters, numbers, underscores, and hyphens. You cannot change the key after you create the tool.',
  'aiConfigs.toolEditor.validationErrorBody': 'Check form fields.',
  'aiConfigs.toolEditor.validationErrorTitle': 'Validation error',
  'aiConfigs.toolEditor.youAreEditing': 'You are editing a past version',
  'aiConfigs.toolNotifications.createErrorTitle': 'Error creating tool',
  'aiConfigs.toolNotifications.createForbidden':
    'You do not have permission to create an AI tool. Contact your administrator for more information.',
  'aiConfigs.toolNotifications.createdBody': 'Tool created successfully',
  'aiConfigs.toolNotifications.createdTitle': 'Tool created',
  'aiConfigs.toolNotifications.deleteErrorTitle': 'Error deleting tool',
  'aiConfigs.toolNotifications.deletedBody': 'Tool deleted successfully',
  'aiConfigs.toolNotifications.deletedTitle': 'Tool deleted',
  'aiConfigs.toolNotifications.tryAgain': 'Try again',
  'aiConfigs.toolNotifications.updateErrorTitle': 'Error updating tool',
  'aiConfigs.toolNotifications.updatedBody': 'Tool updated successfully',
  'aiConfigs.toolNotifications.updatedTitle': 'Tool updated',
  'aiConfigs.toolOverview.addNewVersion': 'Add new version',
  'aiConfigs.toolOverview.newerVersionBody': 'This tool has a newer version available <strong>(v{version})</strong>',
  'aiConfigs.toolOverview.noParametersSpecified': 'No parameters specified',
  'aiConfigs.toolOverview.noToolSelected': 'No tool selected',
  'aiConfigs.toolOverview.thisToolIs': 'This tool is out of date',
  'aiConfigs.toolOverview.versionHistoryLabel': 'Version history',
  'aiConfigs.toolPicker.backToDetailsLabel': 'Back to tool details',
  'aiConfigs.toolPicker.backToListLabel': 'Back to all tools',
  'aiConfigs.toolPicker.createTool': 'Create tool',
  'aiConfigs.toolPicker.created': 'Created',
  'aiConfigs.toolPicker.editTool': 'Edit tool',
  'aiConfigs.toolPicker.findToolLabel': 'Find a tool',
  'aiConfigs.toolPicker.linkTitle': 'Link tool to variation',
  'aiConfigs.toolPicker.linkToVariation': 'Link to variation',
  'aiConfigs.toolPicker.loadingTitle': 'Loading…',
  'aiConfigs.toolPicker.noDescription': 'No description',
  'aiConfigs.toolPicker.selectToolLabel': 'Select tool',
  'aiConfigs.toolRestoreVersion.applyChangesLabel':
    'Apply changes to {count, plural, one {# config} other {# configs}}',
  'aiConfigs.toolRestoreVersion.applyChangesTo': 'Apply changes to:',
  'aiConfigs.toolRestoreVersion.cancel': 'Cancel',
  'aiConfigs.toolRestoreVersion.couldNotLoad':
    'Could not load tool references. You can still restore the tool version.',
  'aiConfigs.toolRestoreVersion.dialogLabel': 'Restore to version {version}',
  'aiConfigs.toolRestoreVersion.errorBody': 'Something went wrong while saving. Try again.',
  'aiConfigs.toolRestoreVersion.errorTitle': 'Could not restore tool',
  'aiConfigs.toolRestoreVersion.heading': 'Restore to v{version}',
  'aiConfigs.toolRestoreVersion.loadingUsageLabel': 'Loading tool usage',
  'aiConfigs.toolRestoreVersion.noNewVersionBody':
    'LaunchDarkly could not determine the new version and did not update references.',
  'aiConfigs.toolRestoreVersion.partialUpdateBody':
    '{count, plural, one {# reference} other {# references}} could not be updated.',
  'aiConfigs.toolRestoreVersion.refMeta': '{variationName} · v{version}',
  'aiConfigs.toolRestoreVersion.restore': 'Restore',
  'aiConfigs.toolRestoreVersion.restoredTitle': 'Restored to v{version}',
  'aiConfigs.toolRestoreVersion.someFailedBody': 'LaunchDarkly could not update some references.',
  'aiConfigs.toolRestoreVersion.thisWillCreate':
    'This will create a new version based on this one and set it as the current version. You can still access all previous versions.',
  'aiConfigs.toolRestoreVersion.unknownStatusBody': 'Reference update status unknown.',
  'aiConfigs.toolRestoreVersion.usagesLabel': 'Tool usages',
  'aiConfigs.toolRestoreVersion.usedByBody':
    'This tool is used by <strong>{count, plural, one {# config} other {# configs}}</strong>. Select which configs should receive this update upon approval.',
  'aiConfigs.toolSaveConfirm.applyChangesTo': 'Apply changes to:',
  'aiConfigs.toolSaveConfirm.configReferencesLabel': 'Config references',
  'aiConfigs.toolSaveConfirm.loadingUsageLabel': 'Loading tool usage',
  'aiConfigs.toolSaveConfirm.refMeta': '{variationName} · v{version}',
  'aiConfigs.toolSaveConfirm.unableToLoad': 'Unable to load config references. You can still save the tool.',
  'aiConfigs.toolSaveConfirm.usagesLabel': 'Tool usages',
  'aiConfigs.toolSaveConfirm.usedByBody':
    'This tool is used by <strong>{count, plural, one {# config} other {# configs}}</strong>. Select which configs should be updated to the new version.',
  'aiConfigs.toolSchemaEditor.addChildPropertyLabel': 'Add child property',
  'aiConfigs.toolSchemaEditor.addNewProperty': 'Add new property',
  'aiConfigs.toolSchemaEditor.addSubProperty': 'Add sub-property',
  'aiConfigs.toolSchemaEditor.allowAdditionalProperties': 'Allow additional properties',
  'aiConfigs.toolSchemaEditor.arrayType': 'Array type',
  'aiConfigs.toolSchemaEditor.clearSelection': 'Clear selection',
  'aiConfigs.toolSchemaEditor.deletePropertyLabel': 'Delete property',
  'aiConfigs.toolSchemaEditor.descriptionLabel': 'Description',
  'aiConfigs.toolSchemaEditor.exampleLocationDescription': 'City and state, for example San Francisco, CA',
  'aiConfigs.toolSchemaEditor.exampleUnitDescription': 'Temperature unit, either celsius or fahrenheit',
  'aiConfigs.toolSchemaEditor.insertExampleSchema': 'Insert example schema',
  'aiConfigs.toolSchemaEditor.invalidJsonSchema': 'Invalid JSON schema',
  'aiConfigs.toolSchemaEditor.json': 'JSON',
  'aiConfigs.toolSchemaEditor.keyLabel': 'Key',
  'aiConfigs.toolSchemaEditor.notSet': 'Not set',
  'aiConfigs.toolSchemaEditor.optionalTooltip': 'Optional',
  'aiConfigs.toolSchemaEditor.propertiesDescribeThe':
    'Properties describe the arguments the model provides when it calls this tool. Mark a property required if the model must always provide it.',
  'aiConfigs.toolSchemaEditor.requiredTooltip': 'Required',
  'aiConfigs.toolSchemaEditor.schema': 'Schema',
  'aiConfigs.toolSchemaEditor.typeLabel': 'Type',
  'aiConfigs.toolSchemaEditor.typePlaceholder': 'Type',
  'aiConfigs.toolSchemaEditor.visual': 'Visual',
  'aiConfigs.toolSchemaView.itemsType': 'Items: {type}',
  'aiConfigs.toolSchemaView.json': 'JSON',
  'aiConfigs.toolSchemaView.notSet': 'Not set',
  'aiConfigs.toolSchemaView.required': 'Required',
  'aiConfigs.toolSchemaView.schema': 'Schema',
  'aiConfigs.toolSchemaView.visual': 'Visual',
  'aiConfigs.toolUsageDrawer.applyChanges': 'Apply changes',
  'aiConfigs.toolUsageDrawer.applyDisabledLabel': 'Select a different version to apply changes',
  'aiConfigs.toolUsageDrawer.applyToCount': 'Apply to {count, plural, one {# variation} other {# variations}}',
  'aiConfigs.toolUsageDrawer.cancel': 'Cancel',
  'aiConfigs.toolUsageDrawer.couldNotLoad': 'Could not load tool usage.',
  'aiConfigs.toolUsageDrawer.currentVersionOption': 'v{version}, current version',
  'aiConfigs.toolUsageDrawer.errorBody': 'Failed to update tool usage. Try again.',
  'aiConfigs.toolUsageDrawer.errorTitle': 'Error updating tool usage',
  'aiConfigs.toolUsageDrawer.loadingReferencesLabel': 'Loading references',
  'aiConfigs.toolUsageDrawer.partialBody': 'LaunchDarkly could not update some tool usages. Try again.',
  'aiConfigs.toolUsageDrawer.partialTitle': 'Partial update',
  'aiConfigs.toolUsageDrawer.rowVersionChangeLabel':
    'Update tool version for {variationName} in {configName}. Changing from v{from} to v{to}.',
  'aiConfigs.toolUsageDrawer.rowVersionLabel':
    'Update tool version for {variationName} in {configName}. Currently v{version}.',
  'aiConfigs.toolUsageDrawer.staleVersionLabel': 'Not using the latest version (v{version})',
  'aiConfigs.toolUsageDrawer.updateAllTo': 'Update all to',
  'aiConfigs.toolUsageDrawer.updateTo': 'Update to',
  'aiConfigs.toolUsageDrawer.updatedBody':
    'Updated "{toolKey}" version in {count, plural, one {# variation} other {# variations}}.',
  'aiConfigs.toolUsageDrawer.updatedTitle': 'Tool usage updated',
  'aiConfigs.toolUsageDrawer.usedInSummary': 'Used in {count, plural, one {# config} other {# configs}}',
  'aiConfigs.toolUsageDrawer.versionChange': 'v{from} → v{to}',
  'aiConfigs.toolVersionHistory.copied': 'Copied to clipboard',
  'aiConfigs.toolVersionHistory.copyVersionLabel': 'Copy version {version} content',
  'aiConfigs.toolVersionHistory.customParametersLabel': 'Custom parameters:',
  'aiConfigs.toolVersionHistory.descriptionLabel': 'Description:',
  'aiConfigs.toolVersionHistory.errorMessage': 'Could not load tool version history.',
  'aiConfigs.toolVersionHistory.noDescription': 'No description',
  'aiConfigs.toolVersionHistory.parametersLabel': 'Parameters:',
  'aiConfigs.toolVersionHistory.selectVersionLabel': 'Select version {version}',
  'aiConfigs.toolVersionPicker.triggerLabel': 'Select tool version, current version v{version}',
  'aiConfigs.toolVersionPicker.versionsLabel': 'Tool versions',
  'aiConfigs.toolsField.addSelected': 'Add {count, plural, one {# tool} other {# tools}}',
  'aiConfigs.toolsField.addTools': 'Add tools',
  'aiConfigs.toolsField.addingToolsWill': 'Adding tools will overwrite the model parameters.',
  'aiConfigs.toolsField.allTools': 'All tools',
  'aiConfigs.toolsField.applySelectionLabel': 'Apply tool selection',
  'aiConfigs.toolsField.attachToolsLabel': 'Attach tools',
  'aiConfigs.toolsField.createNewTool': 'Create new tool',
  'aiConfigs.toolsField.heading': 'Tools',
  'aiConfigs.toolsField.noToolsFound': 'No tools found',
  'aiConfigs.toolsField.optionsLabel': 'Tool options',
  'aiConfigs.toolsField.parameters': 'Parameters',
  'aiConfigs.toolsField.removeAll': 'Remove all',
  'aiConfigs.toolsField.removeToolLabel': 'Remove tool',
  'aiConfigs.toolsField.searchLabel': 'Search tools',
  'aiConfigs.toolsField.searchPlaceholder': 'Search tools…',
  'aiConfigs.toolsField.thisToolHas': 'This tool has a newer version available',
  'aiConfigs.toolsField.thisVariationUses': 'This variation uses legacy tools.',
  'aiConfigs.toolsField.updateAllTo': 'Update all to latest version',
  'aiConfigs.toolsField.viewDetailsLabel': 'View tool details for {key}',
  'aiConfigs.toolsList.configCount': '{count, plural, one {# config} other {# configs}}',
  'aiConfigs.toolsList.createTool': 'Create tool',
  'aiConfigs.toolsList.createdOn': 'Created on',
  'aiConfigs.toolsList.delete': 'Delete',
  'aiConfigs.toolsList.edit': 'Edit',
  'aiConfigs.toolsList.key': 'Key',
  'aiConfigs.toolsList.loadingMemberLabel': 'Loading member',
  'aiConfigs.toolsList.maintainer': 'Maintainer',
  'aiConfigs.toolsList.moreActionsLabel': 'More actions',
  'aiConfigs.toolsList.noToolsFound': 'No tools found',
  'aiConfigs.toolsList.noToolsYet': 'No tools yet',
  'aiConfigs.toolsList.powerYourConfigs': 'Power your configs with tools.',
  'aiConfigs.toolsList.readMoreLink': 'Read more about the tools library',
  'aiConfigs.toolsList.searchLabel': 'Search tools',
  'aiConfigs.toolsList.searchPlaceholder': 'Search tools…',
  'aiConfigs.toolsList.staleUsageTooltip':
    '{count, plural, one {# config is} other {# configs are}} not using the latest version (v{version})',
  'aiConfigs.toolsList.tags': 'Tags',
  'aiConfigs.toolsList.tagsLabel': 'Tool tags',
  'aiConfigs.toolsList.toolUsageLabel': 'Tool usage',
  'aiConfigs.toolsList.toolsLetConfigs': 'Tools let configs trigger actions you define in code',
  'aiConfigs.toolsList.tryAdjustingYour': 'Try adjusting your search',
  'aiConfigs.toolsList.usageErrorTitle': 'Could not load usage',
  'aiConfigs.toolsList.usedIn': 'Used in',
  'aiConfigs.toolsList.version': 'Version',
  'aiConfigs.toolsList.versionHistoryLabel': 'Open version history for {toolKey}, currently v{version}',
  'aiConfigs.toxicityCriteria.thresholdDescription':
    'Maximum allowed toxicity score (0.0 to 1.0). Responses with toxicity scores above this threshold will be flagged. Default is 0.5.',
  'aiConfigs.trialSplash.body':
    'Bring great AI products to market faster with the only control plane to release, monitor, and optimize GenAI experiences without redeploys.',
  'aiConfigs.trialSplash.docsLink': 'Read about AI Configs',
  'aiConfigs.trialSplash.heading': 'Get access to AI Configs',
  'aiConfigs.trialSplash.pendingBody': 'Your AI Configs trial is on the way. We’ll ping you when it’s good to go.',
  'aiConfigs.trialSplash.pendingHeading': 'Getting things ready for you',
  'aiConfigs.trialSplash.requestTrial': 'Request a free trial',
  'aiConfigs.tryItLive.closeLabel': 'Close',
  'aiConfigs.tryItLive.genericError': 'Something went wrong generating a response. Try again.',
  'aiConfigs.tryItLive.loadError': 'Couldn’t load this config. Close and reopen to try again.',
  'aiConfigs.tryItLive.loadingLabel': 'Loading your Config',
  'aiConfigs.tryItLive.messageLabel': 'Message',
  'aiConfigs.tryItLive.multiPanelHint':
    'Both variations receive the same message and respond independently. Edit and save your config to see responses change.',
  'aiConfigs.tryItLive.multiPanelPlaceholder': 'Message both variations…',
  'aiConfigs.tryItLive.noVariations': 'This config has no variations to try yet.',
  'aiConfigs.tryItLive.rateLimitError': 'You’ve reached today’s free message limit for the demo. Check back tomorrow.',
  'aiConfigs.tryItLive.reset': 'Reset',
  'aiConfigs.tryItLive.sendLabel': 'Send message',
  'aiConfigs.tryItLive.singlePanelHint': 'Edit and save your config to see responses change.',
  'aiConfigs.tryItLive.singlePanelPlaceholder': 'Send a message to your variation…',
  'aiConfigs.tryItLive.switchToModel': 'Switch this variation to this model: {model}.',
  'aiConfigs.tryItLive.switchToOneOfModels': 'Switch this variation to one of these models: {models}.',
  'aiConfigs.tryItLive.switchToOneOfProviders':
    'Switch this variation to a model from one of your configured providers: {providers}.',
  'aiConfigs.tryItLive.switchToProvider': 'Switch this variation to a model from your configured provider: {provider}.',
  'aiConfigs.tryItLive.switchToProviders': 'Switch this variation to a model from {providers}.',
  'aiConfigs.tryItLive.thinking': 'Thinking…',
  'aiConfigs.tryItLive.tryItLive': 'Try it live',
  'aiConfigs.tryItLive.tryYourConfig': 'Try your config live',
  'aiConfigs.tryItLive.unsupportedModelBody':
    '{guidance} Close this panel, choose a new model for the variation, and save your config.',
  'aiConfigs.tryItLive.unsupportedModelFallback': 'Switch this variation to a supported model.',
  'aiConfigs.tryItLive.unsupportedModelHeading': 'This variation’s model isn’t available in the demo',
  'aiConfigs.tryItLiveCompletionModal.benefitMeasure': 'Measure quality and cost for every variation.',
  'aiConfigs.tryItLiveCompletionModal.benefitNoRedeploy': 'Change prompts and models without redeploying.',
  'aiConfigs.tryItLiveCompletionModal.benefitRollout': 'Target changes to specific users and roll back at any time.',
  'aiConfigs.tryItLiveCompletionModal.body': 'Connect your app to do the same for your own agent:',
  'aiConfigs.tryItLiveCompletionModal.connectApp': 'Connect your app',
  'aiConfigs.tryItLiveCompletionModal.heading': 'Your change is live',
  'aiConfigs.tryItLiveCompletionModal.skipForNow': 'Skip for now',
  'aiConfigs.tryItLiveExitNudge.body':
    'This tour shows how prompt changes go live without a redeploy. Finish it, or skip ahead and set up the SDK in your app.',
  'aiConfigs.tryItLiveExitNudge.closeLabel': 'Close',
  'aiConfigs.tryItLiveExitNudge.continue': 'Continue tour',
  'aiConfigs.tryItLiveExitNudge.heading': 'Before you go',
  'aiConfigs.tryItLiveExitNudge.leave': 'Leave tour',
  'aiConfigs.tryItLiveExitNudge.setUpSdk': 'Set up SDK',
  'aiConfigs.tryItLiveOnboarding.carouselLabel': 'Carousel navigation',
  'aiConfigs.tryItLiveOnboarding.configureAlt': 'Configure AI agents',
  'aiConfigs.tryItLiveOnboarding.configureBody':
    'Define your agent’s model, prompt, and parameters in one place. Then update them instantly without redeploying your app.',
  'aiConfigs.tryItLiveOnboarding.evaluateAlt': 'Evaluate AI performance',
  'aiConfigs.tryItLiveOnboarding.evaluateBody':
    'Compare performance and cost across all your agent use cases to make confident, data-backed promotion decisions.',
  'aiConfigs.tryItLiveOnboarding.getStarted': 'Get started',
  'aiConfigs.tryItLiveOnboarding.heading': 'Welcome to AgentControl',
  'aiConfigs.tryItLiveOnboarding.iterateAlt': 'Iterate with playground comparisons',
  'aiConfigs.tryItLiveOnboarding.iterateBody':
    'Test model and prompt changes head-to-head in the playground before releasing them to a single real user.',
  'aiConfigs.tryItLiveOnboarding.monitorAlt': 'Monitor quality insights',
  'aiConfigs.tryItLiveOnboarding.monitorBody':
    'Catch quality regressions the moment they happen with always-on monitoring that alerts you before users feel the impact.',
  'aiConfigs.tryItLiveOnboarding.releaseAlt': 'Release with rollout controls',
  'aiConfigs.tryItLiveOnboarding.releaseBody':
    'New agent versions can be rolled out progressively, with automatic rollback triggered by quality, cost, or latency signals.',
  'aiConfigs.tryItLiveOnboarding.skip': 'Skip',
  'aiConfigs.tryItLiveOnboarding.slideLabel': 'Show slide {number}: {title}',
  'aiConfigs.uploadDatasetDialog.cancel': 'Cancel',
  'aiConfigs.uploadDatasetDialog.heading': 'Upload dataset',
  'aiConfigs.uploadDatasetDialog.keyLabel': 'Key',
  'aiConfigs.uploadDatasetDialog.keyPlaceholder': 'Enter dataset key',
  'aiConfigs.uploadDatasetDialog.keyRequired': 'Key is required',
  'aiConfigs.uploadDatasetDialog.nameLabel': 'Name',
  'aiConfigs.uploadDatasetDialog.namePlaceholder': 'Enter dataset name',
  'aiConfigs.uploadDatasetDialog.nameRequired': 'Name is required',
  'aiConfigs.uploadDatasetDialog.saveDataset': 'Save dataset',
  'aiConfigs.uploadDatasetRowsDialog.addingRowsBody': 'Adding rows…',
  'aiConfigs.uploadDatasetRowsDialog.addingRowsLabel': 'Adding rows',
  'aiConfigs.uploadDatasetRowsDialog.cancel': 'Cancel',
  'aiConfigs.uploadDatasetRowsDialog.heading': 'Upload rows',
  'aiConfigs.uploadDatasetRowsDialog.uploadingBody': 'Uploading file…',
  'aiConfigs.uploadDatasetRowsDialog.uploadingLabel': 'Uploading file',
  'aiConfigs.uploadProgressDialog.cancel': 'Cancel',
  'aiConfigs.uploadProgressDialog.close': 'Close',
  'aiConfigs.uploadProgressDialog.failedHeading': 'Upload failed',
  'aiConfigs.uploadProgressDialog.tryAgain': 'Try again',
  'aiConfigs.uploadProgressDialog.uploadingHeading': 'Uploading dataset',
  'aiConfigs.variationActions.archive': 'Archive',
  'aiConfigs.variationActions.archiveLabel': 'Archive version',
  'aiConfigs.variationActions.cannotArchiveInUse': 'Cannot archive a variation in use by targeting',
  'aiConfigs.variationActions.deleteLabel': 'Delete version',
  'aiConfigs.variationActions.deleteVariation': 'Delete variation',
  'aiConfigs.variationActions.duplicate': 'Duplicate',
  'aiConfigs.variationActions.duplicateLabel': 'Duplicate variation',
  'aiConfigs.variationActions.importFromPlayground': 'Import from playground',
  'aiConfigs.variationActions.importLabel': 'Import version',
  'aiConfigs.variationActions.menuLabel': 'Variation actions',
  'aiConfigs.variationActions.openInPlayground': 'Open in playground',
  'aiConfigs.variationActions.playgroundLabel': 'Playground',
  'aiConfigs.variationActions.restore': 'Restore',
  'aiConfigs.variationActions.viewHistory': 'View history',
  'aiConfigs.variationCard.actionsLabel': 'Variation actions',
  'aiConfigs.variationCard.addCustomModelAction': 'Add custom model',
  'aiConfigs.variationCard.addMessageAction': 'Add message',
  'aiConfigs.variationCard.addToolsAction': 'Add tools',
  'aiConfigs.variationCard.archive': 'Archive',
  'aiConfigs.variationCard.archiveBody':
    'This variation is used in targeting rules. To prevent unintended behavior, we recommend saving your edits in a new variation.',
  'aiConfigs.variationCard.archiveFailed': 'Failed to archive config variation. Try again later.',
  'aiConfigs.variationCard.archiveHeading': 'Archive variation',
  'aiConfigs.variationCard.archivedToast': 'Config variation archived',
  'aiConfigs.variationCard.archiving': 'Archiving…',
  'aiConfigs.variationCard.atLeastOneVariationError': 'At least one variation is required',
  'aiConfigs.variationCard.cannotDeleteDraft': 'Cannot delete draft variation',
  'aiConfigs.variationCard.cannotDeleteLast': 'Cannot delete last variation',
  'aiConfigs.variationCard.collapseLabel': 'Collapse variation',
  'aiConfigs.variationCard.copiedToast': 'Copied {key} to clipboard',
  'aiConfigs.variationCard.copyKeyLabel': 'Copy {key} to clipboard',
  'aiConfigs.variationCard.copyVariationKey': 'Copy variation key',
  'aiConfigs.variationCard.default': 'Default',
  'aiConfigs.variationCard.defaultUpdatedToast': 'Default variation updated',
  'aiConfigs.variationCard.delete': 'Delete',
  'aiConfigs.variationCard.deleteBody': 'Are you sure you want to delete this variation?',
  'aiConfigs.variationCard.deleteFailed': 'Failed to delete config variation. Try again later.',
  'aiConfigs.variationCard.deleteHeading': 'Delete variation',
  'aiConfigs.variationCard.deletedToast': 'Config variation deleted',
  'aiConfigs.variationCard.deleting': 'Deleting…',
  'aiConfigs.variationCard.discardChanges': 'Discard changes',
  'aiConfigs.variationCard.draft': 'Draft',
  'aiConfigs.variationCard.draftActionsLabel': 'Variation draft actions',
  'aiConfigs.variationCard.duplicate': 'Duplicate',
  'aiConfigs.variationCard.duplicateDisabledLabel': 'Restore the variation first to duplicate it',
  'aiConfigs.variationCard.duplicateNameError': 'All variation names must be unique',
  'aiConfigs.variationCard.editVariationKey': 'Edit variation key',
  'aiConfigs.variationCard.expandLabel': 'Expand variation',
  'aiConfigs.variationCard.importFromPlayground': 'Import from playground',
  'aiConfigs.variationCard.inUseCheckFailed':
    'LaunchDarkly could not check whether this variation is in use. Try again later.',
  'aiConfigs.variationCard.judgesTitle': 'Judges',
  'aiConfigs.variationCard.keyConflict': 'Key "{key}" would conflict with another new variation',
  'aiConfigs.variationCard.keyInUse': 'Key "{key}" is already in use by another variation',
  'aiConfigs.variationCard.keyInvalid':
    'Key must be lowercase letters, numbers, and hyphens only (e.g., "my-variation-1")',
  'aiConfigs.variationCard.keyLabel': 'Variation key',
  'aiConfigs.variationCard.liveBehaviorWarning': 'Editing this variation may affect live behavior',
  'aiConfigs.variationCard.messagesTitle': 'Messages',
  'aiConfigs.variationCard.modelConfigurationTitle': 'Model configuration',
  'aiConfigs.variationCard.modelRequiredError': 'Model selection is required',
  'aiConfigs.variationCard.nameLabel': 'Variation name',
  'aiConfigs.variationCard.nameNotUnique': 'Variation name must be unique',
  'aiConfigs.variationCard.namePlaceholder': 'Untitled variation*',
  'aiConfigs.variationCard.nameRequired': 'Variation name is required',
  'aiConfigs.variationCard.nameRequiredError': 'Name is required',
  'aiConfigs.variationCard.nameTooLong': 'Name length must not exceed 256 characters',
  'aiConfigs.variationCard.nameTooLongError': 'Name length must not exceed 256 characters',
  'aiConfigs.variationCard.notAuthorized': 'You are not authorized to make changes to this resource',
  'aiConfigs.variationCard.openInPlayground': 'Open in playground',
  'aiConfigs.variationCard.optimize': 'Optimize',
  'aiConfigs.variationCard.playgroundDisabledTooltip': 'Restore the variation first to open in playground',
  'aiConfigs.variationCard.restore': 'Restore',
  'aiConfigs.variationCard.restoreFailed': 'Failed to restore config variation. Try again later.',
  'aiConfigs.variationCard.restoredToast': 'Config variation restored',
  'aiConfigs.variationCard.setAsDefault': 'Set as default',
  'aiConfigs.variationCard.setAsDefaultAlreadyDefault': 'This variation is already the default for this config',
  'aiConfigs.variationCard.setAsDefaultArchived': 'Restore the variation first to set it as the default',
  'aiConfigs.variationCard.setAsDefaultFailed': 'Failed to update the default variation. Try again later.',
  'aiConfigs.variationCard.setAsDefaultLabel': 'Set as default variation',
  'aiConfigs.variationCard.toolsTitle': 'Tools',
  'aiConfigs.variationCard.tryInPlayground': 'Try in playground',
  'aiConfigs.variationCard.unsavedChangesTooltip':
    'You have unsaved changes. Are you sure you want to archive this variation?',
  'aiConfigs.variationCard.variationNameRequiredError': 'Variation name is required',
  'aiConfigs.variationCard.versionHistoryLabel': 'View version history',
  'aiConfigs.variationCard.viewHistory': 'View history',
  'aiConfigs.variationCompare.compareWithCurrent': 'Compare with current version (v{version})',
  'aiConfigs.variationCompare.modelNotSet': 'Not set',
  'aiConfigs.variationCompare.modelVersionSuffix': ' (version {version})',
  'aiConfigs.variationCompare.noVersionsSelected': 'No versions selected',
  'aiConfigs.variationCompare.promote': 'Promote v{version} to current',
  'aiConfigs.variationCompare.selectedModel': 'Selected model: {model}{versionSuffix}',
  'aiConfigs.variationCompare.versionOption': 'v{version}{isCurrent, select, true { (current)} other {}}',
  'aiConfigs.variationCompare.versionSelectLabel': 'Select a version',
  'aiConfigs.variationCompareModal.addVariationPrefix': 'Add variation',
  'aiConfigs.variationCompareModal.cancel': 'Cancel',
  'aiConfigs.variationCompareModal.closeLabel': 'close modal',
  'aiConfigs.variationCompareModal.deleting': 'Deleting…',
  'aiConfigs.variationCompareModal.instructionsTitle': 'Instructions',
  'aiConfigs.variationCompareModal.judgeConfigurationTitle': 'Judge configuration',
  'aiConfigs.variationCompareModal.messagesTitle': 'Messages',
  'aiConfigs.variationCompareModal.modelConfigTitle': 'Model config',
  'aiConfigs.variationCompareModal.requestApproval': 'Request approval',
  'aiConfigs.variationCompareModal.reviewAndSave': 'Review and save',
  'aiConfigs.variationCompareModal.reviewChanges': 'Review changes',
  'aiConfigs.variationCompareModal.saveChanges': 'Save changes',
  'aiConfigs.variationCompareModal.summaryOfChanges': 'Summary of changes',
  'aiConfigs.variationCompareModal.toolsTitle': 'Tools',
  'aiConfigs.variationPlaygroundPanel.createErrorBody': 'Unknown error',
  'aiConfigs.variationPlaygroundPanel.createErrorTitle': 'Error creating playground',
  'aiConfigs.variationPlaygroundPanel.emptyState': 'No playgrounds yet',
  'aiConfigs.variationPlaygroundPanel.loadError': "Couldn't load playgrounds.",
  'aiConfigs.variationPlaygroundPanel.newPlayground': 'New playground',
  'aiConfigs.variationPlaygroundPanel.noSearchResults': 'No playgrounds match your search',
  'aiConfigs.variationPlaygroundPanel.searchLabel': 'Search playgrounds',
  'aiConfigs.variationPlaygroundPanel.searchPlaceholder': 'Search playgrounds…',
  'aiConfigs.variationPlaygroundPanel.tryAgain': 'Try again',
  'aiConfigs.variationPlaygroundPanel.untitledPlayground': 'Untitled playground',
  'aiConfigs.variationServingStatus.goToEnvironmentTargeting': 'Go to {environment} targeting',
  'aiConfigs.variationServingStatus.goToTargeting': 'Go to targeting',
  'aiConfigs.variationServingStatus.notServed': 'Not served',
  'aiConfigs.variationServingStatus.notServedDescription':
    "This variation isn't serving traffic in any environment yet. Add it to a targeting rule or set it as the default to roll it out.",
  'aiConfigs.variationShadowTestStatus.environments': 'Shadowing environments',
  'aiConfigs.variationShadowTestStatus.label': 'Shadow tested',
  'aiConfigs.variationServingStatus.servedEnvironments': 'Serving environments',
  'aiConfigs.variationServingStatus.servedOn': 'Served on {environment}',
  'aiConfigs.variationServingStatus.servedOnMore': 'Served on {environment} + {count}',
  'aiConfigs.variationSnippetPicker.addSnippet': 'Add snippet',
  'aiConfigs.variationSnippetPicker.collapse': 'Collapse',
  'aiConfigs.variationSnippetPicker.collapseSnippetLabel': 'Collapse snippet',
  'aiConfigs.variationSnippetPicker.couldNotLoad': 'Could not load snippets.',
  'aiConfigs.variationSnippetPicker.createNewSnippet': 'Create new snippet',
  'aiConfigs.variationSnippetPicker.expand': 'Expand',
  'aiConfigs.variationSnippetPicker.expandSnippetLabel': 'Expand snippet',
  'aiConfigs.variationSnippetPicker.goToSnippet': 'Go to snippet library',
  'aiConfigs.variationSnippetPicker.loadingSnippet': 'Loading snippet…',
  'aiConfigs.variationSnippetPicker.loadingSnippets': 'Loading snippets…',
  'aiConfigs.variationSnippetPicker.matchingSnippetsLabel': 'Matching snippets',
  'aiConfigs.variationSnippetPicker.newSnippet': 'New snippet',
  'aiConfigs.variationSnippetPicker.noSnippetBody': 'This snippet is empty. Edit the snippet to add content.',
  'aiConfigs.variationSnippetPicker.noSnippetsFound': 'No snippets found',
  'aiConfigs.variationSnippetPicker.remove': 'Remove',
  'aiConfigs.variationSnippetPicker.removeSnippetLabel': 'Remove snippet',
  'aiConfigs.variationSnippetPicker.searchLabel': 'Search snippets',
  'aiConfigs.variationSnippetPicker.searchPlaceholder': 'Search snippets…',
  'aiConfigs.variationSnippetPicker.snippetsLabel': 'Snippets',
  'aiConfigs.variationSnippetPicker.versionLabel': 'V{version}',
  'aiConfigs.variationsEditor.activeCount': 'Active ({count})',
  'aiConfigs.variationsEditor.addVariation': 'Add variation',
  'aiConfigs.variationsEditor.addVariationLabel': 'Add new variation',
  'aiConfigs.variationsEditor.archivedCount': 'Archived ({count})',
  'aiConfigs.variationsEditor.atLeastOneVariation': 'At least one variation is required',
  'aiConfigs.variationsEditor.createdToast': '{count, plural, one {Variation} other {Variations}} created successfully',
  'aiConfigs.variationsEditor.expandAllLabel': 'Expand all variations',
  'aiConfigs.variationsEditor.limitBody':
    'You can manage up to {limit} variations in your free config. Upgrade to add more.',
  'aiConfigs.variationsEditor.limitHeading': "You've reached the variation limit for this config.",
  'aiConfigs.variationsEditor.savedToast': 'Variations saved successfully',
  'aiConfigs.variationsEditor.updatedToast': '{count, plural, one {Variation} other {Variations}} updated successfully',
  'aiConfigs.variationsEditor.viewToggleLabel': 'Variation view',
  'aiConfigs.variationsPage.backToSdk': 'Back to SDK setup',
  'aiConfigs.variationsPage.connectedNext':
    'Your app has connected and called this config. Now <link>go to the monitoring page</link> to see realtime data.',
  'aiConfigs.variationsPage.failedToLoad': 'Failed to load variation',
  'aiConfigs.variationsPage.failedToLoad2': 'Failed to load variations',
  'aiConfigs.variationsPage.loading': 'Loading…',
  'aiConfigs.variationsPage.sdkSetupComplete': '🎉 SDK setup complete',
  'aiConfigs.variationsPage.tryAgain': 'Try again',
  'aiConfigs.variationsPage.waitingForSdk': 'Waiting for SDK connection',
  'aiConfigs.variationsPage.weReWatching':
    "We're watching for your SDK to connect. Once you've initialized the LaunchDarkly client and made a call using your config, this will flip to connected.",
  'aiConfigs.versionHistory.closeLabel': 'Close',
  'aiConfigs.versionHistory.drawerLabel': 'Version history',
  'aiConfigs.versionHistory.failedToLoad': 'Failed to load version history',
  'aiConfigs.versionHistory.heading': 'Variation history',
  'aiConfigs.versionHistory.restoreFailed': 'Failed to restore version',
  'aiConfigs.versionHistory.tryAgain': 'Try again',
  'aiConfigs.versionHistory.versionLabel': 'Version {version}',
  'aiConfigs.versionHistory.viewItemLabel': 'View history item',
  'aiConfigs.versionHistoryModal.closeLabel': 'Close',
  'aiConfigs.versionHistoryModal.compare': 'Compare',
  'aiConfigs.versionHistoryModal.compareSelectFirst': 'Select the first version to compare',
  'aiConfigs.versionHistoryModal.compareSelectSecond': 'v{version} selected. Select a second version to compare.',
  'aiConfigs.versionHistoryModal.comparing': 'Comparing v{olderVersion} → v{newerVersion}',
  'aiConfigs.versionHistoryModal.copied': 'Copied to clipboard',
  'aiConfigs.versionHistoryModal.copyVersionLabel': 'Copy version {version} text',
  'aiConfigs.versionHistoryModal.currentBadge': 'current',
  'aiConfigs.versionHistoryModal.dialogLabel': '{name} version history',
  'aiConfigs.versionHistoryModal.exitCompare': 'Exit compare',
  'aiConfigs.versionHistoryModal.exitFullPageLabel': 'Exit full page',
  'aiConfigs.versionHistoryModal.expandFullPageLabel': 'Expand to full page',
  'aiConfigs.versionHistoryModal.headingSuffix': '- Version history',
  'aiConfigs.versionHistoryModal.loadError': 'Could not load snippet version history.',
  'aiConfigs.versionHistoryModal.loadingVersions': 'Loading versions',
  'aiConfigs.versionHistoryModal.restoreVersionLabel': 'Restore version {version}',
  'aiConfigs.versionHistoryModal.summary': '{versionCount} versions · Current: v{currentVersion}',
  'aiConfigs.versionHistoryModal.versionStatusLabel': 'Version status',
  'aiConfigs.wizardAgentStep.addTool': 'Add tool',
  'aiConfigs.wizardAgentStep.configName': 'Config name',
  'aiConfigs.wizardAgentStep.configNameDescription':
    'Describe what your agent does, for example, "Documentation summarizer"',
  'aiConfigs.wizardAgentStep.configNamePlaceholder': 'Enter config name',
  'aiConfigs.wizardAgentStep.createError': 'Failed to create the config. Try again.',
  'aiConfigs.wizardAgentStep.description': 'Description',
  'aiConfigs.wizardAgentStep.duplicateKeyError': 'A config with this key already exists. Choose a different name.',
  'aiConfigs.wizardAgentStep.editVariationNameLabel': 'Edit name for variation {number}',
  'aiConfigs.wizardAgentStep.instructions': 'Instructions',
  'aiConfigs.wizardAgentStep.instructionsPlaceholder': 'Describe what your agent should do…',
  'aiConfigs.wizardAgentStep.jsonSchema': 'JSON schema',
  'aiConfigs.wizardAgentStep.jsonSchemaDescription':
    "For supported providers, paste your JSON schema. Applied to model.parameters in the format required by each model's provider.",
  'aiConfigs.wizardAgentStep.keyPreview': 'Key: {key}',
  'aiConfigs.wizardAgentStep.modelsLabel': 'Models',
  'aiConfigs.wizardAgentStep.nameRequiredError': 'Name is required',
  'aiConfigs.wizardAgentStep.next': 'Next',
  'aiConfigs.wizardAgentStep.outputInstructions': 'Output instructions',
  'aiConfigs.wizardAgentStep.outputInstructionsDescription':
    'Saved as a snippet and included in the prompt for each variation.',
  'aiConfigs.wizardAgentStep.progressCreatingConfig': 'Creating agent config',
  'aiConfigs.wizardAgentStep.progressCreatingInstructionsSnippet': 'Creating instructions snippet',
  'aiConfigs.wizardAgentStep.progressCreatingStructuredOutputSnippet': 'Creating structured output snippet',
  'aiConfigs.wizardAgentStep.progressCreatingToolSnippets': 'Creating tool snippets',
  'aiConfigs.wizardAgentStep.progressCreatingTools': 'Creating tools',
  'aiConfigs.wizardAgentStep.progressCreatingVariations': 'Creating variations',
  'aiConfigs.wizardAgentStep.progressSettingUpTrafficSplit': 'Setting up traffic split',
  'aiConfigs.wizardAgentStep.recentlyUsed': 'Recently used',
  'aiConfigs.wizardAgentStep.removeVariationLabel': 'Remove variation {number}',
  'aiConfigs.wizardAgentStep.structuredOutputInstructionsLabel': 'Structured output instructions',
  'aiConfigs.wizardAgentStep.structuredOutputsHeading': 'Are you using structured outputs?',
  'aiConfigs.wizardAgentStep.toolDescriptionLabel': 'Tool {number} description',
  'aiConfigs.wizardAgentStep.toolDescriptionPlaceholder': 'What does this tool do?',
  'aiConfigs.wizardAgentStep.toolInstructions': 'Tool instructions',
  'aiConfigs.wizardAgentStep.toolInstructionsDescription':
    'Instructions for how the agent should use this tool. Saved as a snippet and included in the prompt.',
  'aiConfigs.wizardAgentStep.toolInstructionsLabel': 'Tool {number} instructions',
  'aiConfigs.wizardAgentStep.toolInstructionsPlaceholder': 'Describe when and how the agent should use this tool…',
  'aiConfigs.wizardAgentStep.toolName': 'Tool name',
  'aiConfigs.wizardAgentStep.toolNameLabel': 'Tool {number} name',
  'aiConfigs.wizardAgentStep.toolsHeading': 'Are you using tools?',
  'aiConfigs.wizardAgentStep.variationName': 'Variation {number}',
  'aiConfigs.wizardAgentStep.variationNameLabel': 'Variation {number} name',
  'aiConfigs.wizardCodeStep.back': 'Back',
  'aiConfigs.wizardCodeStep.body': 'Choose a language and framework for your starter application',
  'aiConfigs.wizardCodeStep.copy': 'Copy',
  'aiConfigs.wizardCodeStep.download': 'Download',
  'aiConfigs.wizardCodeStep.experiment': 'Experiment',
  'aiConfigs.wizardCodeStep.goToConfig': 'Go to config',
  'aiConfigs.wizardCodeStep.judgeLink': '{name} Judge',
  'aiConfigs.wizardExperimentStep.accountDefault': 'Use account default',
  'aiConfigs.wizardExperimentStep.advancedConfiguration': 'Advanced configuration',
  'aiConfigs.wizardExperimentStep.back': 'Back',
  'aiConfigs.wizardExperimentStep.bayesian': 'Bayesian',
  'aiConfigs.wizardExperimentStep.body':
    "Start an experiment to compare your {count, plural, one {# model variation} other {# model variations}} using the judge's score as the primary metric.",
  'aiConfigs.wizardExperimentStep.control': 'Control',
  'aiConfigs.wizardExperimentStep.controlVariation': 'Control variation',
  'aiConfigs.wizardExperimentStep.controlVariationDescription':
    'The baseline variation all others are compared against.',
  'aiConfigs.wizardExperimentStep.createError': 'Failed to create the experiment. Try again.',
  'aiConfigs.wizardExperimentStep.createExperiment': 'Create experiment',
  'aiConfigs.wizardExperimentStep.duplicateKeyError': 'An experiment with this key already exists.',
  'aiConfigs.wizardExperimentStep.experiment': 'Experiment',
  'aiConfigs.wizardExperimentStep.experimentName': 'Experiment name',
  'aiConfigs.wizardExperimentStep.experimentNamePlaceholder': 'My agent A/B test',
  'aiConfigs.wizardExperimentStep.frequentist': 'Frequentist',
  'aiConfigs.wizardExperimentStep.hypothesis': 'Hypothesis',
  'aiConfigs.wizardExperimentStep.hypothesisDescription': 'What outcome do you expect? (optional)',
  'aiConfigs.wizardExperimentStep.hypothesisPlaceholder':
    'The primary model will score higher on quality than the secondary model.',
  'aiConfigs.wizardExperimentStep.judgeLink': '{name} Judge',
  'aiConfigs.wizardExperimentStep.noPrimaryMetric': 'No primary metric selected. Search below to pick one.',
  'aiConfigs.wizardExperimentStep.primaryMetric': 'Primary metric',
  'aiConfigs.wizardExperimentStep.progressCreatingExperiment': 'Creating experiment',
  'aiConfigs.wizardExperimentStep.progressCreatingMetric': 'Creating judge score metric',
  'aiConfigs.wizardExperimentStep.progressPreparingExperiment': 'Preparing experiment',
  'aiConfigs.wizardExperimentStep.searchMetricsPlaceholder': 'Search metrics by name…',
  'aiConfigs.wizardExperimentStep.searchPrimaryMetricLabel': 'Search primary metric',
  'aiConfigs.wizardExperimentStep.searchSecondaryMetricsLabel': 'Search metrics',
  'aiConfigs.wizardExperimentStep.secondaryMetrics': 'Secondary metrics',
  'aiConfigs.wizardExperimentStep.secondaryMetricsDescription':
    'Add optional metrics to track alongside the primary judge score.',
  'aiConfigs.wizardExperimentStep.selectedPrimaryMetricLabel': 'Selected primary metric',
  'aiConfigs.wizardExperimentStep.selectedSecondaryMetricsLabel': 'Selected secondary metrics',
  'aiConfigs.wizardExperimentStep.skip': 'Skip',
  'aiConfigs.wizardExperimentStep.statisticalApproach': 'Statistical approach',
  'aiConfigs.wizardExperimentStep.statisticalApproachDescription':
    'Frequentist uses p-values; Bayesian uses credible intervals. Defaults to your account setting.',
  'aiConfigs.wizardExperimentStep.trafficPercentageDescription':
    'Percentage of flag traffic enrolled in the experiment. Defaults to 100%.',
  'aiConfigs.wizardExperimentStep.trafficPercentageHeading': '% of traffic in experiment',
  'aiConfigs.wizardExperimentStep.trafficPercentageLabel': 'Percentage of traffic in experiment',
  'aiConfigs.wizardExperimentStep.variations': 'Variations',
  'aiConfigs.wizardJudgeStep.addToCriteria': 'Add to criteria',
  'aiConfigs.wizardJudgeStep.back': 'Back',
  'aiConfigs.wizardJudgeStep.body':
    "A judge evaluates your agent's responses on a 0–1 scale. It will run automatically after each call once attached.",
  'aiConfigs.wizardJudgeStep.createError': 'Failed to create the judge. Try again.',
  'aiConfigs.wizardJudgeStep.duplicateKeyError': 'A judge config with this key already exists.',
  'aiConfigs.wizardJudgeStep.evaluationCriteria': 'Evaluation criteria',
  'aiConfigs.wizardJudgeStep.evaluationCriteriaDescription':
    'Describe what the judge should evaluate. The scoring rubric is pre-filled with placeholders for your custom criteria.',
  'aiConfigs.wizardJudgeStep.generatingSuggestedCriteria': 'Generating suggested criteria',
  'aiConfigs.wizardJudgeStep.metricKeyDescription':
    'The key used to reference this event in metrics and monitoring. Full key: <key></key>',
  'aiConfigs.wizardJudgeStep.metricName': 'Metric name',
  'aiConfigs.wizardJudgeStep.next': 'Next',
  'aiConfigs.wizardJudgeStep.progressAttachingJudge': 'Attaching judge to variations',
  'aiConfigs.wizardJudgeStep.progressCreatingCriteriaSnippet': 'Creating criteria snippet',
  'aiConfigs.wizardJudgeStep.progressCreatingJudgeConfig': 'Creating judge config',
  'aiConfigs.wizardJudgeStep.regenerateSuggestions': 'Regenerate suggestions',
  'aiConfigs.wizardJudgeStep.samplingRateDescription':
    'Percentage of responses the judge will evaluate. Use 100% while testing; lower in production to reduce latency.',
  'aiConfigs.wizardJudgeStep.samplingRateHeading': 'Sampling rate (%)',
  'aiConfigs.wizardJudgeStep.samplingRateLabel': 'Sampling rate',
  'aiConfigs.wizardJudgeStep.skip': 'Skip',
  'aiConfigs.wizardJudgeStep.suggestedCriteria': 'Suggested criteria',
  'aiConfigs.wizardModelPicker.allSection': 'All',
  'aiConfigs.wizardModelPicker.noModelsFound': 'No models found',
  'aiConfigs.wizardModelPicker.searchLabel': 'Search models',
  'aiConfigs.wizardModelPicker.searchPlaceholder': 'Search…',
  'aiConfigs.wizardModelPicker.selectedCount': '{count, plural, one {# model selected} other {# models selected}}',
  'aiConfigs.wizardModelPicker.selectedModelsLabel': 'Selected models',
  'aiConfigs.wizardModelPicker.triggerPlaceholder': 'Select models',
  'aiLibrary.datasetsPage.title': 'Dataset',
  'aiLibrary.datasetsPage.title2': 'Library | Datasets',
  'aiLibrary.judgetemplatesPage.title': 'Library | Judges',
  'aiLibrary.modelsPage.failedToLoad3': 'Failed to load model config',
  'aiLibrary.modelsPage.title': 'Library | Models',
  'aiLibrary.modelsPage.tryAgain': 'Try again',
  'aiLibrary.resourceDetails.showAll': 'Show all',
  'aiLibrary.resourceDetails.showLess': 'Show less',
  'aiLibrary.resourceDetails.usedIn': 'Used in {count, plural, one {# config} other {# configs}}',
  'aiLibrary.skillsPage.title': 'Library | Skills',
  'aiLibrary.snippetsNewPage.title': 'Library | Snippets',
  'aiLibrary.snippetsPage.title': 'Library | Snippets',
  'aiLibrary.toolPage.backToTools': 'Back to tools',
  'aiLibrary.toolPage.title': 'Tool not found',
  'aiLibrary.toolPage.title2': 'Library | Tools',
  'aiLibrary.toolsPage.title': 'Library | Tools',
  'aiLibrary_.page.breadcrumb': 'Library',
  'aiMonitoring.chartsPicker.judgeMetrics': 'Judge metrics',
  'aiMonitoring.chartsPicker.label': 'Charts:',
  'aiMonitoring.chartsPicker.performanceMetrics': 'Performance metrics',
  'aiMonitoring.contextFilter.contextKeyField': '{kind} key',
  'aiMonitoring.contextKindPicker.comboBoxLabel': 'Context kind',
  'aiMonitoring.contextKindPicker.label': 'Context kind:',
  'aiMonitoring.contextKindPicker.placeholder': 'Context kind',
  'aiMonitoring.contextKindPicker.showOptionsLabel': 'Show context kinds',
  'aiMonitoring.dateRange.customRangeFooter': 'Limited to the last {days} days.',
  'aiMonitoring.dateRange.outdatedLinkDescription': 'Try adjusting the range for newer data.',
  'aiMonitoring.dateRange.outdatedLinkHeading': 'This link uses an outdated date range',
  'aiMonitoring.diagnostics.lastMetricReceived': 'Last AI metrics received for this environment {receivedAt}.',
  'aiMonitoring.diagnostics.nextUpdate': 'Next update in {seconds} s.',
  'aiMonitoring.diagnostics.noConfigData': 'No data has been received in this environment in the past year.',
  'aiMonitoring.diagnostics.noMetrics': 'No AI metrics received for this environment in the past year.',
  'aiMonitoring.diagnostics.sdkNotConnected': 'The AI SDK is not connected in this environment.',
  'aiMonitoring.dimensionPicker.clearSearchLabel': 'clear',
  'aiMonitoring.dimensionPicker.menuLabel': 'Dimension identifiers',
  'aiMonitoring.dimensionPicker.noOptionsFound': 'No options found',
  'aiMonitoring.dimensionPicker.searchLabel': 'Dimension identifiers search',
  'aiMonitoring.dimensionPicker.searchPlaceholder': 'Search…',
  'aiMonitoring.dimensionPicker.selectAll': 'Select all',
  'aiMonitoring.dimensionPicker.selectPlaceholder': 'Select {dimension}',
  'aiMonitoring.dimensionPicker.selectedLabel': '{dimension}s:',
  'aiMonitoring.dimensionPicker.selectionLimit': 'Limited to {max} selections',
  'aiMonitoring.dimensionPicker.sortedByInvocationCount': 'Sorted by invocation count',
  'aiMonitoring.groupBy.agentGraph': 'Agent Graph',
  'aiMonitoring.groupBy.config': 'Config',
  'aiMonitoring.groupBy.context': 'Context',
  'aiMonitoring.groupBy.label': 'Group by:',
  'aiMonitoring.groupBy.model': 'Model',
  'aiMonitoring.groupBy.none': 'None (Global)',
  'aiMonitoring.groupBy.provider': 'Provider',
  'aiMonitoring.metrics.cost': 'Cost',
  'aiMonitoring.metrics.errorRate': 'Error rate',
  'aiMonitoring.metrics.latency': 'Request duration',
  'aiMonitoring.metrics.requests': 'Requests',
  'aiMonitoring.metrics.satisfaction': 'Satisfaction',
  'aiMonitoring.page.connectAction': 'Connect your app to get started with AgentControl.',
  'aiMonitoring.page.loadError': 'Failed to load configs. Try again',
  'aiMonitoring.page.title': 'AI insights',
  'aiMonitoring.page.tryAgain': 'Try again',
  'aiMonitoring.sidebar.activeConfigs': 'Active configs',
  'aiMonitoring.sidebar.activeExperiments': 'Active experim.',
  'aiMonitoring.sidebar.askVega': 'Ask Vega',
  'aiMonitoring.sidebar.averageSatisfaction': 'Avg. satisfaction (7d)',
  'aiMonitoring.sidebar.loadErrorDescription': 'Something went wrong.',
  'aiMonitoring.sidebar.loadErrorHeading': 'Unable to load data',
  'aiMonitoring.sidebar.modelDistribution': 'Model distribution (7d)',
  'aiMonitoring.sidebar.monthToDateSpend': 'Month-to-date spend',
  'aiMonitoring.sidebar.noInvocations': 'No invocations in the last 7 days',
  'aiMonitoring.sidebar.quickStats': 'Quick stats',
  'aiMonitoring.sidebar.tryAgain': 'Try again',
  'aiMonitoring.status.loading': 'Loading…',
  'aiMonitoring.trendsChart.averageOverPeriod': 'Average over period',
  'aiMonitoring.trendsChart.breakdownOverPeriod': 'Breakdown over period',
  'aiMonitoring.trendsChart.loadErrorDescription': 'Something went wrong.',
  'aiMonitoring.trendsChart.loadErrorHeading': 'Unable to load data',
  'aiMonitoring.trendsChart.totalOverPeriod': 'Total over period',
  'aiMonitoring.trendsChart.tryAgain': 'Try again',
  'aiMonitoring.trendsTable.allConfigs': 'All Configs',
  'aiMonitoring.trendsTable.allConfigsDescription': 'Aggregate metrics across all configs',
  'aiMonitoring.trendsTable.allProviderModels': 'All {provider} models',
  'aiMonitoring.trendsTable.collapseConfigBreakdownLabel': 'Collapse config breakdown',
  'aiMonitoring.trendsTable.collapseVariationsLabel': 'Collapse variations',
  'aiMonitoring.trendsTable.costColumn': 'Cost',
  'aiMonitoring.trendsTable.errorRateColumn': 'Error rate',
  'aiMonitoring.trendsTable.expandConfigBreakdownLabel': 'Expand config breakdown',
  'aiMonitoring.trendsTable.expandVariationsLabel': 'Expand variations',
  'aiMonitoring.trendsTable.latencyOverCap': '>6hr',
  'aiMonitoring.trendsTable.modelColumn': 'Model',
  'aiMonitoring.trendsTable.multiple': 'Multiple',
  'aiMonitoring.trendsTable.noMatchDescription': 'Try a different search term.',
  'aiMonitoring.trendsTable.noMatchHeading': 'No matching dimensions',
  'aiMonitoring.trendsTable.providerColumn': 'Provider',
  'aiMonitoring.trendsTable.requestDurationColumn': 'Request duration',
  'aiMonitoring.trendsTable.requestsColumn': 'Requests',
  'aiMonitoring.trendsTable.satisfactionColumn': 'Satisfaction',
  'aiMonitoring.trendsTable.searchLabel': 'Search dimensions',
  'aiMonitoring.trendsTable.searchPlaceholder': 'Search',
  'aiMonitoring.trendsTable.summary': 'Summary',
  'aiMonitoring.trendsTable.tableLabel': 'AI monitoring trends explorer table',
  'aiMonitoring.trendsTable.tokensInColumn': 'Tokens in',
  'aiMonitoring.trendsTable.tokensOutColumn': 'Tokens out',
  'alerts.detailPage.alertMayHaveBeenDeleted': 'This alert may have been deleted or you may not have access to it.',
  'alerts.detailPage.alertNotFound': 'Alert not found',
  'alerts.detailPage.backToAlerts': 'Back to alerts',
  'alerts.detailPage.viewingThisAlert':
    'Viewing this alert requires access to the project it belongs to. Contact your LaunchDarkly administrator to request access.',
  'alerts.detailPage.youDonT': "You don't have access to this alert",
  'alerts.editPage.breadcrumb': 'Edit',
  'alerts.editPage.title': 'Edit Alert',
  'alerts.newPage.breadcrumb': 'New',
  'alerts.newPage.title': 'New Alert',
  'alerts.page.title': 'Alerts',
  'alerts_.page.title': 'Alerts',
  'announcements.announcementPopover.copyCommand': 'Copy',
  'announcements.announcementPopover.dismissLabel': 'Dismiss announcement',
  'applications.adoptionChart.heading': 'Adoption by version',
  'applications.adoptionChart.headingWithName': 'Adoption of {name} by version',
  'applications.adoptionChart.loadingData': 'Loading data…',
  'applications.adoptionChart.supportedVersions': 'Supported versions',
  'applications.adoptionChart.tooltipAdoption': 'Adoption: {percentage}',
  'applications.adoptionChart.tooltipDevices': 'Devices: {deviceCount} / {totalDevices}',
  'applications.adoptionChart.unsupportedVersions': 'Unsupported versions',
  'applications.applicationForm.cancel': 'Cancel',
  'applications.applicationForm.createTitle': 'Create an application',
  'applications.applicationForm.descriptionLabel': 'Description',
  'applications.applicationForm.editTitle': 'Edit application',
  'applications.applicationForm.keyLabel': 'Key',
  'applications.applicationForm.kindLabel': 'Kind',
  'applications.applicationForm.maintainerLabel': 'Maintainer',
  'applications.applicationForm.maintainerPlaceholder': 'Select maintainer',
  'applications.applicationForm.nameLabel': 'Application name',
  'applications.applicationVersions.adoptionLoadError':
    'There was an unexpected error loading version adoption metrics',
  'applications.applicationVersions.devicesSupportedHint': 'Based on adoption of supported versions',
  'applications.applicationVersions.devicesSupportedLabel': 'devices supported',
  'applications.applicationVersions.searchPlaceholder': 'Find a version by name or key',
  'applications.applicationVersions.totalDevicesHint': 'Measured as unique pairs of apps and devices',
  'applications.applicationVersions.totalDevicesLabel': 'total devices',
  'applications.applicationsList.createApplication': 'Create application',
  'applications.applicationsList.createLabel': 'Create a new application',
  'applications.applicationsList.searchPlaceholder': 'Find an application by name or key',
  'applications.applicationsSelect.placeholder':
    '{count, plural, one {Select an application} other {Select applications}}',
  'applications.applicationsSelect.remove': 'Remove application',
  'applications.applicationsSelect.selectedCount':
    '{count, plural, one {# application} other {# applications}} selected',
  'applications.autoEnvAttributes.heading': 'Enable automatic environment attributes to display adoption stats',
  'applications.autoEnvAttributes.learnMore': 'Learn how to set up automatic environment attributes',
  'applications.deleteApplication.body':
    'The <strong>{name}</strong> application will be deleted permanently <strong>for all members of your organization. </strong> Its versions and associated support statuses will also be deleted which could impact targeting rules.',
  'applications.deleteApplication.cancel': 'Cancel',
  'applications.deleteApplication.confirm': 'Confirm',
  'applications.deleteApplication.delete': 'Delete',
  'applications.deleteApplication.deleteFailedToast': 'Failed to delete application. Try again later.',
  'applications.deleteApplication.deletedToast': 'Application deleted',
  'applications.deleteApplication.title': 'Delete this application?',
  'applications.deleteVersion.body':
    'The <strong>{name}</strong> version will be deleted permanently from this application <strong>for all members of your organization.</strong>',
  'applications.deleteVersion.cancel': 'Cancel',
  'applications.deleteVersion.confirm': 'Confirm',
  'applications.deleteVersion.delete': 'Delete',
  'applications.deleteVersion.deleteFailedToast': 'Failed to delete version. Try again later.',
  'applications.deleteVersion.deletedToast': 'Version deleted',
  'applications.deleteVersion.title': 'Delete this version?',
  'applications.detailsHeader.created': 'Created',
  'applications.detailsHeader.maintainedBy': 'Maintained by',
  'applications.detailsHeader.navLabel': 'Application details menu',
  'applications.detailsHeader.unassigned': 'Unassigned',
  'applications.detailsHeader.versionsTab': 'Versions',
  'applications.emptyState.checkForTypos': 'Check for typos.',
  'applications.emptyState.noApplicationsBody':
    'LaunchDarkly automatically adds your application here when it establishes a connection with a LaunchDarkly SDK.',
  'applications.emptyState.noApplicationsMatchTitle': 'No applications match. Try this:',
  'applications.emptyState.noApplicationsTitle': 'No applications have been added.',
  'applications.emptyState.noVersionsMatchTitle': 'No application versions match. Try this:',
  'applications.emptyState.noVersionsTitle': 'No application versions have been added.',
  'applications.emptyState.searchApplicationsHint': 'Search for an application by name or key.',
  'applications.emptyState.searchVersionsHint': 'Search for an application version by name or key.',
  'applications.errorBoundary.body':
    "We ran into an unexpected error displaying the applications table. We've been notified. Try again later or <support></support>.",
  'applications.errorBoundary.heading': 'Something went wrong',
  'applications.evaluatedFilter.searchPlaceholder': 'Search applications by name',
  'applications.formValidation.kindRequired': 'Required',
  'applications.formValidation.maxLength': 'Error: This field must be at most 256 characters',
  'applications.formValidation.required': 'Error: This field is required',
  'applications.history.searchPlaceholder': "Search this application's history",
  'applications.keyHelp.applicationLegend':
    'Key of your application. Use this key in your code. Keys must only contain letters, numbers, ., _ or -. You cannot use "new" as a key, and a key cannot be changed after creation.',
  'applications.keyHelp.tooltip':
    'Keys must only contain letters, numbers, ., _ or -. You cannot use "new" as a key, and a key cannot be changed after creation.',
  'applications.keyHelp.versionLegend':
    'Key of your application version. Use this key in your code. Keys must only contain letters, numbers, ., _ or -. You cannot use "new" as a key, and a key cannot be changed after creation.',
  'applications.kindTag.groupLabel': 'kind',
  'applications.list.actionsColumn': 'Actions',
  'applications.list.autoAdded': 'Auto added:',
  'applications.list.copyKeyLabel': 'Click to copy the key for this application ({key}) to your clipboard.',
  'applications.list.deleteApplication': 'Delete application',
  'applications.list.edit': 'Edit',
  'applications.list.manuallyAdded': 'Manually added:',
  'applications.list.nameColumn': 'Name',
  'applications.list.optionsLabel': '{name} options',
  'applications.list.tableSummary': 'A table showing a list of applications.',
  'applications.picker.defaultPlaceholder': 'Select application',
  'applications.picker.noApplicationsFound': 'No applications found',
  'applications.picker.searchPlaceholder': 'Search applications',
  'applications.picker.selectApplication': 'Select application',
  'applications.sdkUpdateRequired.body': 'Upgrade your client-side SDKs for application versions to appear.',
  'applications.sdkUpdateRequired.heading': 'SDK update required',
  'applications.sdkUpdateRequired.learnMore': 'Learn more about supported client-side SDKs',
  'applications.status.loading': 'Loading…',
  'applications.supportStatusForm.body':
    "Changing a version's support status may impact any feature flags targeting this value.",
  'applications.supportStatusForm.cancel': 'Cancel',
  'applications.supportStatusForm.comingSoonBody':
    'Soon you’ll be able to target all of your supported or unsupported application versions on your flags’ Targeting page.',
  'applications.supportStatusForm.comingSoonHeading': 'Coming soon: targeting by app version support status',
  'applications.supportStatusForm.save': 'Save',
  'applications.supportStatusForm.saving': 'Saving…',
  'applications.supportStatusForm.supportStatusLabel': 'Support status',
  'applications.supportStatusForm.supported': 'Supported',
  'applications.supportStatusForm.title': 'Edit version support status',
  'applications.supportStatusForm.unknownError': 'An unknown error occurred. Try again later.',
  'applications.supportStatusForm.unsupported': 'Unsupported',
  'applications.supportStatusForm.updatedToast': 'Version updated',
  'applications.targetingClause.applicationLabel': 'Application ID',
  'applications.targetingClause.applicationPlaceholder': 'Enter an application ID',
  'applications.targetingClause.liveSelection': '{label} has {versionCount}',
  'applications.targetingClause.supportedVersionCount':
    '{totalCount} supported {totalCount, plural, one {version} other {versions}}',
  'applications.targetingClause.unsupportedVersionCount':
    '{totalCount} unsupported {totalCount, plural, one {version} other {versions}}',
  'applications.toolbar.kindAll': 'All',
  'applications.toolbar.kindClearLabel': 'Clear last seen filter',
  'applications.toolbar.kindFilter': 'Kind',
  'applications.toolbar.sortCreatedNewest': 'Created newest',
  'applications.toolbar.sortCreatedOldest': 'Created oldest',
  'applications.toolbar.sortNameAsc': 'Name A-Z',
  'applications.toolbar.sortNameDesc': 'Name Z-A',
  'applications.versionForm.cancel': 'Cancel',
  'applications.versionForm.createTitle': 'Create a version',
  'applications.versionForm.editTitle': 'Edit version',
  'applications.versionForm.keyLabel': 'Key',
  'applications.versionForm.nameLabel': 'Version name',
  'applications.versions.adoptionColumn': 'Adoption (30 days)',
  'applications.versions.adoptionLoadingLabel': 'loading',
  'applications.versions.adoptionSummary': '{percentage} ({deviceCount} devices)',
  'applications.versions.adoptionTooltip': 'Adoption and device data are calculated from the last 30 days',
  'applications.versions.copyKeyLabel': 'Click to copy the key ({key}) for this application version to your clipboard.',
  'applications.versions.createVersion': 'Create version',
  'applications.versions.createVersionLabel': 'Create a new version',
  'applications.versions.delete': 'Delete',
  'applications.versions.editSupportStatus': 'Edit support status',
  'applications.versions.optionsColumn': 'Options',
  'applications.versions.optionsLabel': '{name} options',
  'applications.versions.supportStatusColumn': 'Support status',
  'applications.versions.supported': 'Supported',
  'applications.versions.tableSummary': 'A table showing a list of application versions.',
  'applications.versions.unsupported': 'Unsupported',
  'applications.versions.versionColumn': 'Version',
  'approvals.activity.actionBy': '<strong>{action}</strong> by <name>{displayName}</name>',
  'approvals.activity.approved': 'Approved',
  'approvals.activity.changesApplied': 'Changes applied',
  'approvals.activity.commentAdded': 'Comment added',
  'approvals.activity.created': 'Created',
  'approvals.activity.declined': 'Declined',
  'approvals.addReviewers.closeLabel': 'Close add more reviewers',
  'approvals.addReviewers.heading': 'Add reviewers',
  'approvals.addReviewers.openLabel': 'Click to add more reviewers',
  'approvals.addReviewers.searchPlaceholder': 'Select or type to search',
  'approvals.applyAccess.approvalsRemaining':
    'Requires {count, plural, one {# more approval} other {# more approvals}}.',
  'approvals.applyAccess.conflictsWarning':
    'You will not be able to apply the changes if the conflicts are left unresolved.',
  'approvals.applyAccess.declined': 'The request has been declined.',
  'approvals.applyAccess.declinedNotAllowed': 'You cannot apply declined changes in this environment.',
  'approvals.applyAccess.unresolvedConflicts':
    'You cannot apply changes because there are unresolved conflicts on this request.',
  'approvals.applyDialog.applyChangesInAllEnvironments':
    'This will make changes to the <strong>{resourceName}</strong> {resourceKind} in all environments.',
  'approvals.applyDialog.applyChangesInEnvironment':
    'This will make changes to the <strong>{resourceName}</strong> {resourceKind} in the <environment></environment> environment.',
  'approvals.applyDialog.commentLabel': 'Comment',
  'approvals.applyDialog.commentPlaceholder': 'Leave a comment',
  'approvals.applyDialog.confirm': 'Apply',
  'approvals.applyDialog.deleteScheduledChangeInAllEnvironments':
    'This will delete the scheduled change for the <strong>{resourceName}</strong> {resourceKind} in all environments.',
  'approvals.applyDialog.deleteScheduledChangeInEnvironment':
    'This will delete the scheduled change for the <strong>{resourceName}</strong> {resourceKind} in the <environment></environment> environment.',
  'approvals.applyDialog.reasonDescription': 'Required to save this change.',
  'approvals.applyDialog.reasonLabel': 'Reason',
  'approvals.applyDialog.reasonMissing': 'Provide a reason.',
  'approvals.applyDialog.reasonPlaceholder': 'Approved in the release review',
  'approvals.applyDialog.scheduleChangesInAllEnvironments':
    'This will schedule changes to the <strong>{resourceName}</strong> {resourceKind} in all environments for <date></date>.',
  'approvals.applyDialog.scheduleChangesInEnvironment':
    'This will schedule changes to the <strong>{resourceName}</strong> {resourceKind} in the <environment></environment> environment for <date></date>.',
  'approvals.applyDialog.title': 'Apply changes',
  'approvals.approvalRequestToast.description': 'Approval requested successfully and reviewers have been notified.',
  'approvals.approvalRequestToast.viewAction': 'View approval request',
  'approvals.commentForm.placeholder': 'Write comment here…',
  'approvals.commentForm.submit': 'Add comment',
  'approvals.configApprovals.add': 'Add',
  'approvals.configApprovals.addEnvironment': 'Add environment',
  'approvals.configApprovals.addLabel': 'add approval setting for environment',
  'approvals.configApprovals.approvalsRequiredTag': '{count, plural, one {# approval} other {# approvals}} required',
  'approvals.configApprovals.cancel': 'Cancel',
  'approvals.configApprovals.editLabel': 'edit approval setting',
  'approvals.configApprovals.environmentsHeading': 'Environments that require approvals',
  'approvals.configApprovals.formTitle': 'Approval settings for configs',
  'approvals.configApprovals.remove': 'Remove',
  'approvals.configApprovals.removeDialogBody':
    'This will make approvals optional for changes in the {environmentName} environment.',
  'approvals.configApprovals.removeDialogTitle': 'Remove approval requirement for {environmentName}?',
  'approvals.configApprovals.removeLabel': 'remove approval settings',
  'approvals.configApprovals.title': 'Config approvals',
  'approvals.configApprovals.updateErrorDescription': 'Failed to update approval settings. Try again later.',
  'approvals.configApprovals.updateErrorToast': 'Error updating approval settings for configs',
  'approvals.configApprovals.updatedToast': 'Approval settings for configs updated',
  'approvals.dashboard.apply': 'Apply',
  'approvals.dashboard.approvalSettingsFor': 'Approval settings for',
  'approvals.dashboard.assignedFilter': 'Assigned',
  'approvals.dashboard.changeDetails': 'Change details',
  'approvals.dashboard.configResourceKind': 'Config',
  'approvals.dashboard.copyLink': 'Copy link',
  'approvals.dashboard.deleteRequest': 'Delete request',
  'approvals.dashboard.emptyHeading': "This project doesn't have any approvals",
  'approvals.dashboard.environments': 'Environments',
  'approvals.dashboard.getADemo': 'Get a demo',
  'approvals.dashboard.learnMore': 'Learn more',
  'approvals.dashboard.linkCopiedToast': 'Copied approval request link',
  'approvals.dashboard.noMatchDescription': 'Adjust or clear your filters',
  'approvals.dashboard.noMatchHeading': 'No approvals match your search criteria',
  'approvals.dashboard.optionsLabel': 'Approval request options',
  'approvals.dashboard.request': 'Request',
  'approvals.dashboard.requestedFilter': 'Requested',
  'approvals.dashboard.requestSummary':
    '<requester>{requesterName}</requester> requested changes to <resource>{resourceName}</resource> <time></time>',
  'approvals.dashboard.reviewers': 'Reviewers',
  'approvals.dashboard.untitledRequest': 'View approval request',
  'approvals.dashboard.viewDetails': 'View details',
  'approvals.dashboard.viewPendingChanges': 'View pending changes',
  'approvals.deleteAccess.alreadyApplied': 'Approval requests cannot be deleted once applied.',
  'approvals.environmentMarker.allEnvironments': 'All environments',
  'approvals.flagSettingsForm.approvalSystemLabel': 'Approval system',
  'approvals.flagSettingsForm.bypassNote':
    'Members with permission to bypass required approvals will still be able to make flag changes. <link>Learn more</link>.',
  'approvals.flagSettingsForm.cancel': 'Cancel',
  'approvals.flagSettingsForm.closeLabel': 'close',
  'approvals.flagSettingsForm.comingSoon': 'Coming soon',
  'approvals.flagSettingsForm.enterpriseOnly': 'This is available for Enterprise plan customers only',
  'approvals.flagSettingsForm.integrationDisabledTooltip':
    'You must enable this integration configuration before using it',
  'approvals.flagSettingsForm.oauthRequiredTooltip': 'OAuth is required for this approval system',
  'approvals.flagSettingsForm.save': 'Save',
  'approvals.flagSettingsForm.saving': 'Saving settings',
  'approvals.flagSettingsForm.statusLabel': 'status',
  'approvals.flagSettingsForm.title': 'Approval settings for',
  'approvals.navBadge.accessibleName': 'Approvals, {count} pending',
  'approvals.navBadge.pendingCountLabel': 'Pending count',
  'approvals.notification.applyFailed': 'Failed to apply changes. Try again later.',
  'approvals.notification.changesApplied': 'Changes applied. {resourceKind} updated.',
  'approvals.notification.changesScheduled': 'Changes scheduled',
  'approvals.notification.commentAdded': 'Comment added successfully',
  'approvals.notification.commentFailed': 'Failed to add comment. Try again later.',
  'approvals.notification.experimentAction': 'Experiment {action}',
  'approvals.notification.requestDeclined': 'Request {reviewStatus}',
  'approvals.notification.reviewFailed': 'Failed to {reviewKind} request. Try again later.',
  'approvals.notification.reviewSucceeded': 'Request {reviewStatus} successfully',
  'approvals.notification.scheduledChangesDeleted': 'Scheduled changes deleted',
  'approvals.notification.updateFailed': '{resourceKind} update failed.',
  'approvals.notifications.applyFailed': 'Changes applied. Flag update failed.',
  'approvals.notifications.changesApplied': 'Changes applied. Flag updated.',
  'approvals.notifications.changesScheduled': 'Changes scheduled',
  'approvals.notifications.deleted': 'Approval request deleted',
  'approvals.notifications.scheduledChangesDeleted': 'Scheduled changes deleted',
  'approvals.page.all': 'All',
  'approvals.page.config': 'Config',
  'approvals.page.experiment': 'Experiment',
  'approvals.page.filters': 'Filters',
  'approvals.page.flag': 'Flag',
  'approvals.page.resourceKind': 'Resource kind',
  'approvals.page.segment': 'Segment',
  'approvals.page.status': 'Status',
  'approvals.releasePolicyBanner.defaultPolicyName': 'Release policy',
  'approvals.releasePolicyBanner.follows': 'This change follows the <policy>{policyName}</policy> policy.',
  'approvals.releasePolicyBanner.mostlyFollows': 'This change mostly follows the <policy>{policyName}</policy> policy.',
  'approvals.releasePolicyBanner.notFollowing':
    'This change is not following the <policy>{policyName}</policy> policy.',
  'approvals.requestActions.applyChanges': 'Apply changes',
  'approvals.requestActions.approve': 'Approve',
  'approvals.requestActions.decline': 'Decline',
  'approvals.requestActions.openLabel': 'open',
  'approvals.requestActions.reviewInIntegration': 'Review in {approvalSystem}',
  'approvals.requestActions.viewInIntegration': 'View in {approvalSystem}',
  'approvals.requestDescription.editLabel': 'Edit approval request description',
  'approvals.requestDescription.inputLabel': 'Approval request description',
  'approvals.requestDescription.placeholder': 'Add a description for this approval request',
  'approvals.requestDescription.updateErrorDescription': 'Try again.',
  'approvals.requestDescription.updateErrorToast': 'Unable to update description',
  'approvals.requestDetails.activity': 'Activity',
  'approvals.requestDetails.approvalStatus': 'Approval status',
  'approvals.requestDetails.autoApplyDisabled':
    'Changes need to be applied in LaunchDarkly after approval is granted in {approvalSystem}.',
  'approvals.requestDetails.autoApplyEnabled':
    'Changes will be automatically applied after approval is granted in {approvalSystem}.',
  'approvals.requestDetails.breadcrumb': 'Review approval request',
  'approvals.requestDetails.changes': 'Changes',
  'approvals.requestDetails.checkedOn': 'Checked on <date></date>',
  'approvals.requestDetails.customWorkflow': 'via <strong>{stage}</strong> of <strong>{workflowName}</strong> workflow',
  'approvals.requestDetails.documentTitle': 'Approval request: {description}',
  'approvals.requestDetails.experimentChangesLoadError': 'Error loading experiment changes',
  'approvals.requestDetails.flagCopySource':
    'These flag settings will be copied from the <environment></environment> environment.',
  'approvals.requestDetails.integrationStatusHeading': '{approvalSystem} status',
  'approvals.requestDetails.noStatus': 'No status',
  'approvals.requestDetails.requestedBy': 'Requested by',
  'approvals.requestDetails.requiresAtLeast': 'Requires at least {count, plural, one {# approval} other {# approvals}}',
  'approvals.requestDetails.requiresAtLeastWithDocs':
    'Requires at least {count, plural, one {# approval} other {# approvals}}. <link>Learn more</link>',
  'approvals.requestDetails.reviewers': 'Reviewers',
  'approvals.requestDetails.scheduledFor': 'Changes scheduled for <date></date>.',
  'approvals.requestDetails.scheduledIfApplied': 'If applied, changes will be scheduled for <date></date>.',
  'approvals.requestDetails.scheduledIfApprovedAndApplied':
    'If approved and applied, changes will be scheduled for <date></date>.',
  'approvals.requestDetails.testRunLoadError': 'Failed to load',
  'approvals.requestDetails.workflowStage': 'Stage {number}',
  'approvals.requestDialog.applyWhenApproved': 'When approved, apply these changes: <schedule></schedule>',
  'approvals.requestDialog.cancel': 'Cancel',
  'approvals.requestDialog.changes': 'Changes',
  'approvals.requestDialog.closeLabel': 'close',
  'approvals.requestDialog.moreOptionsLabel': 'More options',
  'approvals.requestDialog.reasonHint': 'A short description for the title of the approval request',
  'approvals.requestDialog.reasonLabel': 'Reason',
  'approvals.requestDialog.reasonPlaceholder': 'Describe these changes',
  'approvals.requestDialog.reasonRequired': 'required',
  'approvals.requestDialog.reviewersLabel': 'Request approval',
  'approvals.requestDialog.reviewersPlaceholder': 'Members and teams to notify about this request',
  'approvals.requestDialog.reviewersRequired': 'Required',
  'approvals.requestDialog.saveWithoutApproval': 'Save without approval',
  'approvals.requestDialog.submit': 'Request approval',
  'approvals.requestDialog.title': 'Request approval',
  'approvals.requestLoadError.body':
    'Something went wrong while getting the approval request details. If this error persists, you can try to delete and recreate the request, or <link>contact Support</link>.',
  'approvals.requestLoadError.delete': 'Delete',
  'approvals.requestLoadError.heading': 'Unable to load approval request',
  'approvals.requestLoadError.returnTo': 'Return to {resourceKind}',
  'approvals.requestParticipants.requestedOn': 'Requested on {date}',
  'approvals.requestRedirect.forbidden':
    "You don't have access to this approval request. It may belong to a project you can't view, or your role may not include approvals for that project.",
  'approvals.requestRedirect.notFound': 'Approval request with id "{approvalRequestId}" not found.',
  'approvals.requestRedirect.unreachable':
    'There was an error loading the approval request with id "{approvalRequestId}".',
  'approvals.requestRefreshError.body':
    'Unable to refresh this approval request. The details shown might be out of date.',
  'approvals.requestStatus.appliedOn': 'Changes applied on {date}',
  'approvals.requestStatus.applyFailedOn': 'Changes failed to be applied on {date}',
  'approvals.requestStatus.approved': 'Approved',
  'approvals.requestStatus.approvedAndApplied': 'Approved and applied',
  'approvals.requestStatus.approvedUnapplied': 'Approved with unapplied changes',
  'approvals.requestStatus.declined': 'Declined',
  'approvals.requestStatus.failed': 'Failed',
  'approvals.requestStatus.needsReview': 'Needs review',
  'approvals.requestTitle.in': 'in',
  'approvals.requestTitle.on': 'on',
  'approvals.requestTitle.project': 'project',
  'approvals.resourceKind.aiConfig': 'AI Config',
  'approvals.reviewAccess.ownRequest':
    'Comment only. You do not have permission to approve or decline your own request.',
  'approvals.reviewChangesDialog.open': 'Review and save',
  'approvals.reviewChangesDialog.title': 'Review changes',
  'approvals.reviewerPicker.me': 'Me',
  'approvals.reviewerPicker.placeholder': 'Select members and teams',
  'approvals.saveChangesButton.bypassNotAllowed': 'You do not have permission to bypass segment approvals',
  'approvals.saveChangesButton.moreOptionsLabel': 'More options',
  'approvals.saveChangesButton.openLabel': 'open',
  'approvals.saveChangesButton.requestApproval': 'Request approval',
  'approvals.saveChangesButton.saveChanges': 'Save changes',
  'approvals.saveChangesButton.saveWithoutApproval': 'Save without approval',
  'approvals.saveChangesButton.upgradeRequired': 'Upgrade your plan to use this feature',
  'approvals.scheduleButton.done': 'Done',
  'approvals.scheduleButton.immediately': 'immediately',
  'approvals.scheduleButton.onDate': 'on {date}',
  'approvals.scheduleButton.reset': 'Reset',
  'approvals.scheduledChange.deletedOn': 'Scheduled change was deleted on {date}.',
  'approvals.scheduledChange.notFound': 'Scheduled change not found.',
  'approvals.segmentSettingsForm.bypassNote':
    'Members with permission to bypass required approvals will still be able to make flag changes. <link>Learn more</link>.',
  'approvals.segmentSettingsForm.cancel': 'Cancel',
  'approvals.segmentSettingsForm.closeLabel': 'close',
  'approvals.segmentSettingsForm.save': 'Save',
  'approvals.segmentSettingsForm.saving': 'Saving settings',
  'approvals.segmentSettingsForm.title': 'Approval settings for',
  'approvals.settingsForm.bypassNote': 'Members with permission to bypass approvals will still be able to make changes',
  'approvals.settingsForm.canApplyDeclinedChanges': 'Allow applying changes even if reviewers have declined a request',
  'approvals.settingsForm.canReviewOwnRequest': 'Allow requesters to review their own request',
  'approvals.settingsForm.cancel': 'Cancel',
  'approvals.settingsForm.decreaseLabel': 'decrease minimum approvals',
  'approvals.settingsForm.environmentPlaceholder': 'Select an environment',
  'approvals.settingsForm.environmentScopeLabel': 'Environment scope',
  'approvals.settingsForm.increaseLabel': 'increase minimum approvals',
  'approvals.settingsForm.minApprovalsLabel': 'Minimum required approvals',
  'approvals.settingsForm.save': 'Save',
  'approvals.settingsPage.description':
    'Configure approval rules for resource changes, including how many approvals are required, what types of changes require review, and where those rules apply',
  'approvals.settingsPage.editFlagApprovalSetting': 'Edit approval setting',
  'approvals.settingsPage.editFlagApprovalSettingLabel': 'edit approval setting',
  'approvals.settingsPage.editSegmentApprovalSettings': 'Edit approval settings',
  'approvals.settingsPage.editSegmentApprovalSettingsLabel': 'edit approval setting',
  'approvals.settingsPage.environmentPlaceholder': 'Select an environment',
  'approvals.settingsPage.flagApprovalsTitle': 'Flag, guarded releases, and experiment approvals',
  'approvals.settingsPage.segmentApprovalsTitle': 'Segment approvals',
  'approvals.settingsPage.title': 'Approval settings',
  'approvals.status.loading': 'Loading…',
  'approvals.upsell.adoptConfidently': 'Confidently adopt feature flags without worrying about accidental updates.',
  'approvals.upsell.collaborate': 'Collaborate on updating flags with other members of your team.',
  'approvals.upsell.emailNotifications': 'Email notifications',
  'approvals.upsell.getNotified': 'Get notified right away and move away from long, complex approval cycles.',
  'approvals.upsell.heading': 'Get a peer review before making critical changes',
  'approvals.upsell.imageAlt': 'A message requesting approval.',
  'approvals.upsell.inAppNotifications': 'In-app notifications',
  'approvals.upsell.learnMore': 'Learn more',
  'approvals.upsell.newTabHint': '(Link opens in new tab)',
  'approvals.upsell.serviceNowIntegration': 'ServiceNow integration',
  'approvals.upsell.serviceNowLogoAlt': 'ServiceNow logo',
  'approvals.upsell.slackIntegration': 'Slack integration',
  'approvals.upsell.slackLogoAlt': 'Slack logo',
  'approvals.upsell.upgradePlan': 'Upgrade plan',
  'approvals.upsell.upgradeToday': 'Upgrade your plan today to get access to approvals.',
  'approvals_.page.title': 'Approvals',
  'auth.login.clickAgainToConfirm': 'Click again to confirm',
  'authorization.applicationsEmptyState.checkForTypos': 'Check for typos.',
  'authorization.applicationsEmptyState.emptyDescription':
    'Applications you authorize to access your LaunchDarkly account appear here.',
  'authorization.applicationsEmptyState.emptyHeading': 'There are no authorized applications yet.',
  'authorization.applicationsEmptyState.noMatchHeading': 'No applications match. Try this:',
  'authorization.applicationsEmptyState.searchByName': 'Search for an application by name.',
  'authorization.applicationsList.appColumn': 'App',
  'authorization.applicationsList.authorizedByColumn': 'Authorized by',
  'authorization.applicationsList.authorizedColumn': 'Authorized',
  'authorization.applicationsList.loadError': 'Something went wrong while loading OAuth applications. Try again.',
  'authorization.applicationsList.loadingLabel': 'Loading OAuth applications',
  'authorization.applicationsList.maintainedBy': 'by {name}',
  'authorization.applicationsList.maintainedByLink': 'by <link>{name}</link>',
  'authorization.applicationsList.noAuthorizedDateLabel': 'No authorization date',
  'authorization.applicationsList.noAuthorizerLabel': 'No authorizer',
  'authorization.applicationsList.noNameLabel': 'No application name',
  'authorization.applicationsList.paginationSummary':
    '<strong>{from}–{to}</strong> of <strong>{total}</strong> applications',
  'authorization.applicationsList.reviewApplication': 'Review application',
  'authorization.applicationsList.revokeApplication': 'Revoke application',
  'authorization.applicationsList.rowActionsLabel': '{name} options',
  'authorization.applicationsList.rowActionsLabelWithId': '{name} options ({id})',
  'authorization.applicationsList.tableLabel': 'OAuth applications',
  'authorization.applicationsList.tryAgain': 'Try again',
  'authorization.applicationsList.unnamedApplication': 'Application',
  'authorization.applicationsToolbar.authorizedByFilter': 'Authorized by',
  'authorization.applicationsToolbar.clearFilters': 'Clear filters',
  'authorization.applicationsToolbar.errorFallback': 'Something went wrong',
  'authorization.applicationsToolbar.nameAZ': 'Name (A–Z)',
  'authorization.applicationsToolbar.nameZA': 'Name (Z–A)',
  'authorization.applicationsToolbar.newestFirst': 'Newest first',
  'authorization.applicationsToolbar.oldestFirst': 'Oldest first',
  'authorization.applicationsToolbar.searchPlaceholder': 'Find an application',
  'authorization.applicationsToolbar.sort': 'Sort',
  'authorization.applicationsToolbar.sortLabel': 'Sort applications',
  'authorization.cloneTokenDialog.closeLabel': 'close',
  'authorization.cloneTokenDialog.errorToast': 'Failed to clone access token',
  'authorization.cloneTokenDialog.loadError':
    'We couldn’t load this access token. It may have been deleted, or you may not have permission to view it.',
  'authorization.cloneTokenDialog.loadingLabel': 'Loading token…',
  'authorization.cloneTokenDialog.title': 'Clone access token',
  'authorization.cloneTokenDialog.titleWithName': 'Clone "{name}"',
  'authorization.createTokenDialog.closeLabel': 'close',
  'authorization.createTokenDialog.errorToast': 'Failed to create access token',
  'authorization.createTokenDialog.title': 'Create access token',
  'authorization.deleteTokenDialog.body':
    'This access token will be deleted permanently. Any clients using this token must be updated to use another token.',
  'authorization.deleteTokenDialog.cancel': 'Cancel',
  'authorization.deleteTokenDialog.closeLabel': 'close',
  'authorization.deleteTokenDialog.confirmDescription': 'Type the name of the access token to confirm',
  'authorization.deleteTokenDialog.confirmLabel': 'Confirm',
  'authorization.deleteTokenDialog.delete': 'Delete',
  'authorization.deleteTokenDialog.errorToast': 'Failed to delete {name}',
  'authorization.deleteTokenDialog.errorToastUnnamed': 'Failed to delete token',
  'authorization.deleteTokenDialog.successToast': 'Deleted {name}',
  'authorization.deleteTokenDialog.successToastUnnamed': 'Token deleted',
  'authorization.deleteTokenDialog.title': 'Delete {name}?',
  'authorization.deleteTokenDialog.unnamedToken': 'token',
  'authorization.editTokenDialog.closeLabel': 'close',
  'authorization.editTokenDialog.errorToast': 'Failed to save {name}',
  'authorization.editTokenDialog.errorToastUnnamed': 'Failed to save token',
  'authorization.editTokenDialog.loadErrorBody': 'The token may have been deleted or you may not have access to it.',
  'authorization.editTokenDialog.loadErrorHeading': 'We couldn’t load this access token.',
  'authorization.editTokenDialog.loadingLabel': 'Loading access token',
  'authorization.editTokenDialog.successToast': 'Saved {name}',
  'authorization.editTokenDialog.successToastUnnamed': 'Token saved',
  'authorization.editTokenDialog.title': 'Edit access token',
  'authorization.editTokenDialog.titleWithName': 'Edit {name}',
  'authorization.editTokenDialog.tryAgain': 'Try again',
  'authorization.expiringTokensAlert.body':
    'Reset or extend these tokens if you want them to remain valid. Once a token expires it can no longer authenticate requests.',
  'authorization.expiringTokensAlert.heading':
    '{count, plural, one {# token} other {# tokens}} will expire within 7 days',
  'authorization.resetTokenDialog.body':
    'Resetting this access token invalidates the current key. Any clients using it must be updated to use the new token.',
  'authorization.resetTokenDialog.cancel': 'Cancel',
  'authorization.resetTokenDialog.closeLabel': 'close',
  'authorization.resetTokenDialog.dialogLabel': 'Reset access token',
  'authorization.resetTokenDialog.errorToast': 'Failed to reset {name}',
  'authorization.resetTokenDialog.errorToastUnnamed': 'Failed to reset access token',
  'authorization.resetTokenDialog.hoursError': 'Enter a whole number between 0 and {max} (1 month).',
  'authorization.resetTokenDialog.hoursLabel': 'Hours to keep the old access token active',
  'authorization.resetTokenDialog.hoursUnit': 'hours',
  'authorization.resetTokenDialog.keepActiveLabel': 'Keep the old access token active for',
  'authorization.resetTokenDialog.loadError':
    'We couldn’t load this access token. It may have been deleted, or you may not have permission to view it.',
  'authorization.resetTokenDialog.loadingLabel': 'Loading token…',
  'authorization.resetTokenDialog.resetToken': 'Reset token',
  'authorization.resetTokenDialog.resettingLabel': 'Resetting',
  'authorization.resetTokenDialog.title': 'Reset access token?',
  'authorization.resetTokenDialog.titleWithName': 'Reset "{name}"?',
  'authorization.revealTokenDialog.close': 'Close',
  'authorization.revealTokenDialog.closeLabel': 'close',
  'authorization.revealTokenDialog.copied': 'Token copied to clipboard',
  'authorization.revealTokenDialog.copyLabel': 'Copy token to clipboard',
  'authorization.revealTokenDialog.copyTooltip': 'Copy to clipboard',
  'authorization.revealTokenDialog.createdTitle': '"{name}" has been created',
  'authorization.revealTokenDialog.createdTitleUnnamed': 'Access token created',
  'authorization.revealTokenDialog.description':
    'After you leave this page, the token will be hidden. Make sure to copy and save it now.',
  'authorization.revealTokenDialog.done': 'Done',
  'authorization.revealTokenDialog.heading': 'Copy and save this token.',
  'authorization.revealTokenDialog.resetTitle': '"{name}" has been reset',
  'authorization.revealTokenDialog.resetTitleUnnamed': 'Access token reset',
  'authorization.revealTokenDialog.storedConfirmation': 'I’ve stored this token in a safe place',
  'authorization.revealTokenDialog.warning':
    'Copy this token now. You will not be able to see it again after you close this dialog.',
  'authorization.reviewApplication.accessStatement':
    '<name>{clientName}</name> has access to your LaunchDarkly account',
  'authorization.reviewApplication.authorizedBy': 'Authorized <time></time> by {name}',
  'authorization.reviewApplication.closeLabel': 'close',
  'authorization.reviewApplication.formerMember': 'a former member',
  'authorization.reviewApplication.loadErrorBody':
    'The application may have been revoked or you may not have access to it.',
  'authorization.reviewApplication.loadErrorHeading': 'We couldn’t load this application.',
  'authorization.reviewApplication.loadingLabel': 'Loading application',
  'authorization.reviewApplication.maintainedBy':
    '<name>{clientName}</name> is maintained by <maintainer></maintainer>',
  'authorization.reviewApplication.permissionAdmin': 'Add and remove team members',
  'authorization.reviewApplication.permissionAdminWriter':
    'Modify all LaunchDarkly data, including feature flags, goals, environments and more',
  'authorization.reviewApplication.permissionInfo': 'Read your full name and email address',
  'authorization.reviewApplication.permissionObservability':
    'Access observability data such as traces, logs, errors, and metrics',
  'authorization.reviewApplication.permissionReader': 'Read all LaunchDarkly data',
  'authorization.reviewApplication.permissionScim': "Use SCIM to manage your organization's users on LaunchDarkly",
  'authorization.reviewApplication.permissionVerboseReader':
    'Read all LaunchDarkly data, including feature flags, goals, environments and more',
  'authorization.reviewApplication.permissionWriter': 'Modify all LaunchDarkly data, excluding team management',
  'authorization.reviewApplication.permissionsLabel': 'Permissions',
  'authorization.reviewApplication.revoke': 'Revoke',
  'authorization.reviewApplication.title': 'Application access',
  'authorization.reviewApplication.tryAgain': 'Try again',
  'authorization.reviewApplication.unnamedApplication': 'This application',
  'authorization.revokeApplication.body':
    'This application will have all access revoked immediately. Any integrations using this application may stop working.',
  'authorization.revokeApplication.confirm': 'Revoke',
  'authorization.revokeApplication.errorToast': 'Failed to revoke {name}',
  'authorization.revokeApplication.errorToastUnnamed': 'Failed to revoke application',
  'authorization.revokeApplication.successToast': 'Revoked {name}',
  'authorization.revokeApplication.successToastUnnamed': 'Application access revoked',
  'authorization.revokeApplication.title': 'Revoke access for {name}?',
  'authorization.revokeApplication.unnamedApplication': 'this application',
  'authorization.tokenForm.apiVersionLabel': 'API version',
  'authorization.tokenForm.apiVersionLockedHint':
    'You may not change the version of an existing token. Either use the LD-API-Version header or create a new token.',
  'authorization.tokenForm.cancel': 'Cancel',
  'authorization.tokenForm.cloneToken': 'Clone token',
  'authorization.tokenForm.customRole': 'Custom',
  'authorization.tokenForm.customRoleRequired': 'Select at least one custom role',
  'authorization.tokenForm.customRolesLabel': 'Custom roles',
  'authorization.tokenForm.customRolesSelected': '{count, plural, one {# custom role} other {# custom roles}}',
  'authorization.tokenForm.inlinePolicy': 'Inline policy',
  'authorization.tokenForm.inlinePolicyInvalidJson': 'Inline policy must be valid JSON',
  'authorization.tokenForm.inlinePolicyNotArray': 'Inline policy must be an array of statements',
  'authorization.tokenForm.inlinePolicyRequired': 'An inline policy is required',
  'authorization.tokenForm.invalidValue': 'Invalid value',
  'authorization.tokenForm.loadingPolicyEditorLabel': 'Loading inline policy editor',
  'authorization.tokenForm.loadingRoles': 'Loading roles…',
  'authorization.tokenForm.nameLabel': 'Name',
  'authorization.tokenForm.namePlaceholder': 'Describe what this token will be used for',
  'authorization.tokenForm.nameRequired': 'Name is required',
  'authorization.tokenForm.noRolesFound': 'No roles found',
  'authorization.tokenForm.permissionCeilingHint':
    'A token can never do more than you can. Requests needing access you do not have will fail.',
  'authorization.tokenForm.permissionsHint':
    'Your tokens can <strong>never do more</strong> than you, but it’s a good idea to limit a token’s permissions as much as possible.',
  'authorization.tokenForm.readAboutApiVersioning': 'Read about API versioning',
  'authorization.tokenForm.readAboutServiceTokens': 'Read about service tokens',
  'authorization.tokenForm.roleLabel': 'Role',
  'authorization.tokenForm.saveToken': 'Save token',
  'authorization.tokenForm.searchRolesLabel': 'Search custom roles',
  'authorization.tokenForm.searchRolesPlaceholder': 'Search roles',
  'authorization.tokenForm.serviceTokenHint': 'Use a <strong>service token</strong> for long-term API integrations.',
  'authorization.tokenForm.serviceTokenLabel': 'This is a service token',
  'authorization.tokenForm.typeLockedHint': 'Changing the type of an existing token is not permitted',
  'authorization.tokenForm.upgradePlanHint': 'Upgrade your plan to use this feature.',
  'authorization.tokenForm.verifyEmailHint':
    'Verify your email to use this feature. To re-send the verification email or change your email address, go to your profile settings.',
  'authorization.tokensEmptyState.createToken': 'Create token',
  'authorization.tokensEmptyState.noActiveDescription':
    'All of your access tokens have expired. Create a token to authenticate against the LaunchDarkly API.',
  'authorization.tokensEmptyState.noActiveHeading': 'No active tokens',
  'authorization.tokensEmptyState.noExpiredDescription': 'None of your access tokens have expired.',
  'authorization.tokensEmptyState.noExpiredHeading': 'No expired tokens',
  'authorization.tokensEmptyState.noMatchDescription': 'Try adjusting your filters',
  'authorization.tokensEmptyState.noMatchHeading': 'No tokens match',
  'authorization.tokensEmptyState.noTokensDescription': 'Create a token to authenticate against the LaunchDarkly API.',
  'authorization.tokensEmptyState.noTokensHeading': "You don't have any access tokens yet",
  'authorization.tokensList.apiVersionColumn': 'API version',
  'authorization.tokensList.cloneToken': 'Clone token',
  'authorization.tokensList.creatorColumn': 'Creator',
  'authorization.tokensList.deleteToken': 'Delete token',
  'authorization.tokensList.editToken': 'Edit token',
  'authorization.tokensList.expiredAgo': '{days, plural, one {# day} other {# days}} ago',
  'authorization.tokensList.expiresIn': '{days, plural, one {# day} other {# days}} left',
  'authorization.tokensList.expiryColumn': 'Expiry',
  'authorization.tokensList.expiryLabel': 'Token expiry',
  'authorization.tokensList.inlinePolicy': 'Inline policy',
  'authorization.tokensList.lastModifiedColumn': 'Last modified',
  'authorization.tokensList.lastUsedColumn': 'Last used',
  'authorization.tokensList.loadError': 'Something went wrong while loading tokens. Try again.',
  'authorization.tokensList.loadingLabel': 'Loading authorization tokens',
  'authorization.tokensList.nameColumn': 'Name',
  'authorization.tokensList.never': 'Never',
  'authorization.tokensList.neverTooltip': 'This token has never been used.',
  'authorization.tokensList.noData': 'No data',
  'authorization.tokensList.noDataTooltip':
    'This token has not been used since LaunchDarkly began tracking last-used times.',
  'authorization.tokensList.noExpiration': 'No expiration',
  'authorization.tokensList.noTokenValueLabel': 'No token value',
  'authorization.tokensList.paginationSummary': '<strong>{from}–{to}</strong> of <strong>{total}</strong> tokens',
  'authorization.tokensList.resetToken': 'Reset token',
  'authorization.tokensList.rolesColumn': 'Role(s)',
  'authorization.tokensList.rolesLabel': 'Roles',
  'authorization.tokensList.rowActionsLabel': '{name} options',
  'authorization.tokensList.serviceToken': 'Service token',
  'authorization.tokensList.tableLabel': 'Authorization tokens',
  'authorization.tokensList.tokenColumn': 'Token',
  'authorization.tokensList.tryAgain': 'Try again',
  'authorization.tokensList.unnamedToken': 'Token',
  'authorization.tokensScopeNotice.adminsOnly': "Only admins and owners can view other members' personal tokens.",
  'authorization.tokensScopeNotice.mixedScope': "You're viewing your personal tokens and all service tokens.",
  'authorization.tokensScopeNotice.serviceScope': "You're viewing all service tokens.",
  'authorization.tokensToolbar.active': 'Active',
  'authorization.tokensToolbar.activeCount': '{count} active',
  'authorization.tokensToolbar.apiVersion': 'API version',
  'authorization.tokensToolbar.apiVersionFilter': 'API version: {version}',
  'authorization.tokensToolbar.apiVersionFilterLabel': 'Filter by API version',
  'authorization.tokensToolbar.apiVersionFilterMenuLabel': 'API version filter options',
  'authorization.tokensToolbar.apiVersionLatest': '{version} (latest)',
  'authorization.tokensToolbar.clearApiVersion': 'Clear',
  'authorization.tokensToolbar.clearFilters': 'Clear filters',
  'authorization.tokensToolbar.clearType': 'Clear',
  'authorization.tokensToolbar.createToken': 'Create token',
  'authorization.tokensToolbar.createdByMe': 'Created by: Me',
  'authorization.tokensToolbar.createdByMeTooltip': 'You can only view your own personal tokens.',
  'authorization.tokensToolbar.createdNewest': 'Created (newest)',
  'authorization.tokensToolbar.createdOldest': 'Created (oldest)',
  'authorization.tokensToolbar.errorFallback': 'Something went wrong',
  'authorization.tokensToolbar.expired': 'Expired',
  'authorization.tokensToolbar.expiredCount': '{count} expired',
  'authorization.tokensToolbar.expiresLatest': 'Expires (latest)',
  'authorization.tokensToolbar.expiresSoonest': 'Expires (soonest)',
  'authorization.tokensToolbar.lastModifiedNewest': 'Last modified (newest)',
  'authorization.tokensToolbar.lastModifiedOldest': 'Last modified (oldest)',
  'authorization.tokensToolbar.nameAZ': 'Name (A–Z)',
  'authorization.tokensToolbar.nameZA': 'Name (Z–A)',
  'authorization.tokensToolbar.personal': 'Personal',
  'authorization.tokensToolbar.searchPlaceholder': 'Find a token',
  'authorization.tokensToolbar.service': 'Service',
  'authorization.tokensToolbar.sort': 'Sort',
  'authorization.tokensToolbar.sortLabel': 'Sort tokens',
  'authorization.tokensToolbar.statusFilterLabel': 'Filter tokens by status',
  'authorization.tokensToolbar.type': 'Type',
  'authorization.tokensToolbar.typeFilter': 'Type: {type}',
  'authorization.tokensToolbar.typeFilterLabel': 'Filter by type',
  'authorization.tokensToolbar.typeFilterMenuLabel': 'Type filter options',
  'automationsSettings.additionalContextPlaceholder':
    'For example, flags are defined in example_file.tsx in the frontend and example_file.go in the backend.',
  'automationsSettings.agentConversationsOption': 'Agent conversations and manual flag cleanups',
  'automationsSettings.autoCleanupAdditionalContextTooltip':
    '(Optional) Give the cleanup agent extra context. It also follows the AGENTS.md file in each repository.',
  'automationsSettings.autoReleasingDescription':
    "Automatically progresses flag releases using this project's default release plan.",
  'automationsSettings.automatedCleanupDescription':
    'LaunchDarkly automatically runs cleanup on flags that are ready for code removal, based on your <link>lifecycle settings</link>.',
  'automationsSettings.automatedCleanupLoadError':
    'Automated cleanup settings could not load. Refresh the page and try again.',
  'automationsSettings.automaticArchivalDescription':
    'LaunchDarkly automatically archives flags that pass the readiness checks in your <link>lifecycle settings</link>.',
  'automationsSettings.automaticArchivalLoadError':
    'Automatic archival settings could not load. Refresh the page and try again.',
  'automationsSettings.connectGitHub': 'Connect GitHub',
  'automationsSettings.connectedRepositoriesHeading': 'Connected repositories',
  'automationsSettings.defaultModelDescription': 'Runs conversations and investigations.',
  'automationsSettings.description':
    'Configure how LaunchDarkly automatically flags, releases, and cleans up code changes.',
  'automationsSettings.disableForProject': 'Disable for {project}',
  'automationsSettings.enableForProject': 'Enable for {project}',
  'automationsSettings.githubEmptyStateDescription':
    'Automations work in your GitHub repositories. Connect your GitHub account to choose which repositories LaunchDarkly can use.',
  'automationsSettings.githubEmptyStateHeading': 'Connect GitHub to use automations',
  'automationsSettings.githubUnavailable':
    'LaunchDarkly could not reach GitHub, so your repositories did not load. Try again in a few minutes.',
  'automationsSettings.heading': 'Automations',
  'automationsSettings.inferenceDescription':
    'Inference is the AI processing LaunchDarkly runs to do the work behind each automation.',
  'automationsSettings.inferenceHeading': 'Inference',
  'automationsSettings.inferenceKeyDescription':
    "The AI provider account LaunchDarkly runs models on. Use LaunchDarkly's managed key, or a custom OpenAI or Anthropic key to choose the models yourself.",
  'automationsSettings.inferenceKeyDescriptionWithBedrock':
    "The AI provider account LaunchDarkly runs models on. Use LaunchDarkly's managed key, or a custom OpenAI, Anthropic, or Amazon Bedrock key to choose the models yourself.",
  'automationsSettings.inferenceSaveFailed': 'LaunchDarkly could not save the inference configuration. Try again.',
  'automationsSettings.manageReleasePlans': 'Manage release plans',
  'automationsSettings.manageRepositories': 'Manage in GitHub',
  'automationsSettings.modelsLoadError': "Couldn't load models. Refresh the page and try again.",
  'automationsSettings.modelsUnavailable':
    "Couldn't list the models this key can use. Check the permissions for this key in {provider}.",
  'automationsSettings.navigationLabel': 'Automations',
  'automationsSettings.noConnectedRepositories':
    'No repositories connected yet. <link>Connect repositories from GitHub</link> to use with automations.',
  'automationsSettings.noMatchingRepositories': 'No repositories match "{query}"',
  'automationsSettings.noMatchingRepositoriesDescription': 'Try a different search.',
  'automationsSettings.noRepositories':
    'No repositories connected yet. <link>Connect a repository from GitHub</link> to use it with this automation.',
  'automationsSettings.privateRepositoryLabel': 'Private repository',
  'automationsSettings.projectToggleLabel': 'Enable {capability} for {project}',
  'automationsSettings.projectToggleMixed': 'On for {enabledCount, number} of {repositoryCount, number} repositories',
  'automationsSettings.projectToggleNoRepositories': 'Add a repository to this project to turn this on.',
  'automationsSettings.publicRepositoryTooltip': 'Automations only work in private repositories.',
  'automationsSettings.pullRequestDetailsLoadError':
    'Pull request details could not load. Refresh the page and try again.',
  'automationsSettings.pullRequestPrefixDescription': 'Automatically added to pull request titles.',
  'automationsSettings.readMoreAboutCleanup': 'Read more',
  'automationsSettings.recentlyFinishedTab': 'Recently finished',
  'automationsSettings.repositoriesDescription':
    'LaunchDarkly is connected to all {count, plural, one {# repository} other {# repositories}} in your GitHub account. Automations can work in the private ones.',
  'automationsSettings.repositoriesHeading': 'Repositories',
  'automationsSettings.repositoriesLoadError': "Couldn't load repository settings.",
  'automationsSettings.repositoriesLoading': 'Loading repositories',
  'automationsSettings.repositoriesSaveFailed':
    "Couldn't save {count, plural, one {# repository} other {# repositories}}: {repositories}",
  'automationsSettings.repositoryConflict':
    '<link>{project}</link> already uses this repository. Repositories work in one project at a time, so enabling it here turns off its automations in that project.',
  'automationsSettings.repositoryConflictEnableHere': 'Enable here',
  'automationsSettings.repositoryConflictSaveDisabled':
    'Choose <strong>Enable here</strong> or <strong>Undo</strong> for each repository that another project uses.',
  'automationsSettings.repositoryConflictUndo': 'Undo',
  'automationsSettings.repositoryToggleLabel': 'Use {repository} for {capability}',
  'automationsSettings.retry': 'Retry',
  'automationsSettings.save': 'Save',
  'automationsSettings.saveFailed': 'LaunchDarkly could not save your automation settings. Try again.',
  'automationsSettings.saved': 'Automation settings saved',
  'automationsSettings.searchRepositoriesLabel': 'Search repositories',
  'automationsSettings.selectedRepositoriesLabel': 'Selected repositories',
  'automationsSettings.showAllRepositories': 'Show all {count, plural, one {# repository} other {# repositories}}',
  'automationsSettings.smallModelDescription': 'Answers quick requests, such as search queries.',
  'beastmode.reauthAlert.authenticate': 'Authenticate',
  'beastmode.reauthAlert.body': 'You may need to re-authenticate to update your profile.',
  'beastmode.reauthAlert.heading': 'Additional authentication required',
  'billing.accessTokensUpsell.description':
    'Add an extra layer of security by creating private tokens you can scope to your needs',
  'billing.accessTokensUpsell.title': 'Authenticate with the LaunchDarkly REST API',
  'billing.accruedCharges.balanceDueRow': 'Balance due',
  'billing.accruedCharges.billedQuantityColumn': 'Billed quantity',
  'billing.accruedCharges.close': 'Close',
  'billing.accruedCharges.closeLabel': 'Close modal',
  'billing.accruedCharges.costColumn': 'Cost',
  'billing.accruedCharges.discountRow': 'Discount',
  'billing.accruedCharges.lineDiscount': 'Discount',
  'billing.accruedCharges.loadErrorBody': 'Accrued invoices could not be loaded. Try refreshing the page.',
  'billing.accruedCharges.loadErrorHeading': 'Failed to load accrued invoices',
  'billing.accruedCharges.monthToDateTitle': 'Month-to-date charges',
  'billing.accruedCharges.overageChargesTitle': '{month} overage charges',
  'billing.accruedCharges.partiallyInvoicedRow': 'Partially invoiced',
  'billing.accruedCharges.productColumn': 'Product',
  'billing.accruedCharges.seeDetails': 'See details',
  'billing.accruedCharges.standardRate': 'Standard rate: ${rate}/each',
  'billing.accruedCharges.subtotalRow': 'Subtotal',
  'billing.accruedCharges.upgradePlan': 'Upgrade plan',
  'billing.accruedCharges.usageDetailsColumn': 'Usage details',
  'billing.accruedCharges.usageSummary': '{used} used / {included} included',
  'billing.accruedInvoice.amountColumn': 'Amount',
  'billing.accruedInvoice.balanceDueRow': 'Balance due',
  'billing.accruedInvoice.billedQuantityColumn': 'Billed quantity',
  'billing.accruedInvoice.discountRow': 'Discount',
  'billing.accruedInvoice.emptyState': 'You are not actively accruing usage charges.',
  'billing.accruedInvoice.lineDiscount': 'Discount',
  'billing.accruedInvoice.lineSummary': '{product} ({used} Used / {included} included)',
  'billing.accruedInvoice.partiallyInvoicedRow': 'Partially invoiced',
  'billing.accruedInvoice.productColumn': 'Product',
  'billing.accruedInvoice.subtotalRow': 'Subtotal',
  'billing.addSeatsBanner.body':
    '{count, plural, one {In order to proceed, you need to purchase an additional seat.} other {In order to proceed, you need to purchase additional seats.}}',
  'billing.addSeatsBanner.buySeats': 'Buy {count, plural, one {seat} other {seats}}',
  'billing.addSeatsBanner.buySeatsAndInvite':
    'Buy {count, plural, one {seat} other {seats}} + Invite {count, plural, one {member} other {members}}',
  'billing.addSeatsBanner.confirmPurchase': 'Confirm purchase',
  'billing.addSeatsBanner.heading':
    'Buy {seats} {count, plural, one {seat} other {seats}} for {cost} per seat per month',
  'billing.addSeatsBanner.loadingLabel': 'Loading…',
  'billing.addSeatsBanner.subscribingLabel': 'Subscribing and updating',
  'billing.addSeatsBanner.updateMyPlan': 'Update my plan',
  'billing.agentControlToggle.addAgentControl': 'Add AgentControl',
  'billing.agentControlToggle.addCodeControl': 'Add CodeControl',
  'billing.agentControlToggle.agentControlSubtext': 'Runtime control for your agent',
  'billing.agentControlToggle.codeControlSubtext': 'Feature flagging and experimentation',
  'billing.annualPlanInfo.annualPrepaidTag': 'Annual prepaid',
  'billing.annualPlanInfo.currentEntitlementsHeading': 'Current monthly entitlements',
  'billing.annualPlanInfo.entitlementsHeading': 'Monthly entitlements',
  'billing.annualPlanInfo.heading': 'Foundation',
  'billing.annualPlanInfo.modify': 'Modify',
  'billing.annualPlanInfo.pendingChangesTooltip':
    'Your plan was last updated on {updatedAt} and the changes are set to take effect on {effectiveDate}.',
  'billing.annualPlanInfo.pendingChangesTooltipLabel': 'Upcoming monthly entitlements tooltip',
  'billing.annualPlanInfo.planTagLabel': 'Foundation plan tag',
  'billing.annualPlanInfo.renewsOn': 'Renews on {date}',
  'billing.annualPlanInfo.upcomingEntitlementsHeading': 'Upcoming monthly entitlements',
  'billing.annualPlanInfo.validFrom': 'Valid from {start} ⎯ {end}',
  'billing.auditLogUpsell.description': 'Your plan is limited to {days} days.',
  'billing.auditLogUpsell.title': 'Upgrade to see the full history',
  'billing.billingHistory.dateColumn': 'Date',
  'billing.billingHistory.emptyState': "You don't have any invoices yet.",
  'billing.billingHistory.loadError': 'Invoice query failed. Refresh to try again.',
  'billing.billingHistory.receiptColumn': 'Receipt',
  'billing.billingInfo.billStarting': "We'll bill you starting {date}",
  'billing.billingInfo.creditBalanceNote': 'Credit balance will be applied to future invoices',
  'billing.billingInfo.creditTodayLabel': 'Credit today:',
  'billing.billingInfo.dueTodayLabel': 'Due today:',
  'billing.billingInfo.entitlementChangesBilledOn': 'Your entitlement changes will be billed and take effect on {date}',
  'billing.billingInfo.firstMonthProrated': 'Your first month is prorated',
  'billing.billingInfo.heading': 'Billing info',
  'billing.billingInfo.nextPaymentLabel': 'Next payment:',
  'billing.billingInfo.perMonth': '/month',
  'billing.billingInfo.perYear': '/year',
  'billing.billingInfo.promoCodeLabel': 'Promo Code:',
  'billing.billingInfo.renewsOn': 'Your account will renew on {date}',
  'billing.billingInfo.totalMonthlyAmountLabel': 'Total monthly amount:',
  'billing.billingInfo.totalYearlyAmountLabel': 'Total yearly amount:',
  'billing.billingInfo.trialBillingNote': "We'll bill you on {date} for your first prorated year",
  'billing.billingInfo.trialExpires': 'Your trial expires {date}',
  'billing.billingInterval.alreadyOnPlanTooltip': "You're already on this plan",
  'billing.billingInterval.billedAnnually': 'Billed annually',
  'billing.billingInterval.billedMonthly': 'Billed monthly',
  'billing.billingInterval.onAnnualPlanTooltip': "You're on an annual plan",
  'billing.billingMethodCard.accountCancelled': 'You cancelled your account.',
  'billing.billingMethodCard.addPaymentMethod': 'Add Payment Method',
  'billing.billingMethodCard.cancel': 'Cancel',
  'billing.billingMethodCard.contactRequiredTooltip': 'Update your billing contact to proceed',
  'billing.billingMethodCard.editPaymentMethod': 'Edit payment method',
  'billing.billingMethodCard.functionalUntil': 'Your account will remain fully functional until {date}.',
  'billing.billingMethodCard.heading': 'Billing method',
  'billing.billingMethodCard.invoiceMethod': 'Invoice',
  'billing.billingMethodCard.loadError': 'We ran into an unexpected error. Refresh the page or try again later.',
  'billing.billingMethodCard.nextBillingDate': 'Your next subscription billing date is {date}.',
  'billing.billingMethodCard.noCard': "There's no card associated with this account",
  'billing.billingMethodCard.paymentFailed': 'Your last payment failed.',
  'billing.billingMethodCard.reset': 'Reset',
  'billing.billingMethodCard.saveAndContinue': 'Save and continue',
  'billing.billingMethodCard.saveCard': 'Save card',
  'billing.billingMethodCard.saving': 'Saving',
  'billing.billingMethodCard.trialEndsOn': 'Your free trial ends on {date}',
  'billing.billingPage.billingHistoryHeading': 'Billing history',
  'billing.billingPage.loadError': 'Unable to load billing information. Try again later.',
  'billing.billingPage.maintenanceBody': 'Try again later.',
  'billing.billingPage.maintenanceHeading': 'Billing system is undergoing maintenance.',
  'billing.bulkFeaturesUpsell.imageAlt': 'A screenshot showing bulk user management features in action.',
  'billing.bulkFeaturesUpsell.title': 'Do more with bulk features',
  'billing.cancelSubscription.annualContinuesUntil':
    'Your subscription will continue until the end of your term on {endDate}.',
  'billing.cancelSubscription.annualOverageWarning':
    'Your account can still incur overage charges until the end of your subscription term. To avoid overages, discontinue use of any SDKs generating billable usage.',
  'billing.cancelSubscription.cancelMySubscription': 'Cancel my subscription',
  'billing.cancelSubscription.closeLabel': 'close',
  'billing.cancelSubscription.confirmTitle': 'Cancel your subscription?',
  'billing.cancelSubscription.detailsLabel': 'Can you tell us more?',
  'billing.cancelSubscription.endSubscriptionBody':
    'Your account will remain active through the end of your current billing cycle. No new charges will be posted to your account.',
  'billing.cancelSubscription.endSubscriptionHeading': 'End subscription',
  'billing.cancelSubscription.heading': 'We are sorry to see you go',
  'billing.cancelSubscription.keepMySubscription': 'Keep my subscription',
  'billing.cancelSubscription.legacyActiveUntil': 'Your subscription will be active until {endDate}.',
  'billing.cancelSubscription.legacyNoFurtherChanges':
    'After that, you will not be able to make any changes to your flags or account.',
  'billing.cancelSubscription.monthlyEndsImmediately':
    'Your subscription will end immediately and you will be charged for any remaining balance.',
  'billing.cancelSubscription.monthlyNoFurtherChanges':
    'You will not be able to make further changes to your flags or account.',
  'billing.cancelSubscription.reasonCapabilities': 'Capabilities',
  'billing.cancelSubscription.reasonCapabilitiesDescription': "The product doesn't do something I need it to.",
  'billing.cancelSubscription.reasonGettingStarted': 'Getting started',
  'billing.cancelSubscription.reasonGettingStartedDescription': 'It was hard to figure out how to get going.',
  'billing.cancelSubscription.reasonLabel': "What's the main reason you decided to cancel?",
  'billing.cancelSubscription.reasonOther': 'Other',
  'billing.cancelSubscription.reasonPlaceholder': 'Select a reason',
  'billing.cancelSubscription.reasonPrice': 'Price',
  'billing.cancelSubscription.reasonPriceDescription': "It's too expensive.",
  'billing.cancelSubscription.reasonTeamAdoption': 'Team adoption',
  'billing.cancelSubscription.reasonTeamAdoptionDescription': "My team didn't end up using it enough.",
  'billing.cancelSubscription.reasonTime': 'Time',
  'billing.cancelSubscription.reasonTimeDescription': "I didn't end up having time to use this.",
  'billing.cancelSubscription.reasonTool': 'Tool',
  'billing.cancelSubscription.reasonToolDescription': 'We have another solution we use.',
  'billing.cancelSubscription.requiredField': 'Required field',
  'billing.checkoutConfirmation.body': 'Explore your new capabilities. Everything below is available in your plan.',
  'billing.checkoutConfirmation.configsDescription': 'Configure AI behaviors in real time',
  'billing.checkoutConfirmation.configsName': 'Configs',
  'billing.checkoutConfirmation.experimentsDescription': 'Test, measure, and improve your releases',
  'billing.checkoutConfirmation.experimentsName': 'Experiments',
  'billing.checkoutConfirmation.flagsDescription': 'Manage and roll out features with confidence',
  'billing.checkoutConfirmation.flagsName': 'Flags',
  'billing.checkoutConfirmation.goToBillingSettings': 'Go to billing settings',
  'billing.checkoutConfirmation.heading': 'Your {planName} plan is ready to go',
  'billing.checkoutConfirmation.learnMore': 'Learn more',
  'billing.checkoutConfirmation.manageBillingPrompt': 'Want to manage your billing?',
  'billing.checkoutConfirmation.observabilityDescription': 'Monitor performance and detect issues in real time',
  'billing.checkoutConfirmation.observabilityName': 'Observability',
  'billing.checkoutSteps.backLabel': 'Go back',
  'billing.checkoutSteps.checkout': 'Check out',
  'billing.checkoutSteps.choosePlan': 'Choose a plan',
  'billing.checkoutSteps.customizePlan': 'Customize plan',
  'billing.codeReferencesUpsell.imageAlt':
    'A screenshot showing this feature being used in the LaunchDarkly interface.',
  'billing.codeReferencesUpsell.title': 'Reduce technical debt',
  'billing.contactCard.editContact': 'Edit contact',
  'billing.contactCard.heading': 'Billing contact',
  'billing.contactCard.noPermission':
    "You don't have permission to view or edit the billing contact. Contact an admin or account owner for billing inquiries.",
  'billing.contactForm.address1Label': 'Address 1',
  'billing.contactForm.address1Required': 'Address line 1 is required',
  'billing.contactForm.address2Label': 'Address 2',
  'billing.contactForm.cancel': 'Cancel',
  'billing.contactForm.cityLabel': 'City',
  'billing.contactForm.cityRequired': 'City is required',
  'billing.contactForm.companyLabel': 'Company',
  'billing.contactForm.countryLabel': 'Country',
  'billing.contactForm.countryRequired': 'Country is required',
  'billing.contactForm.emailInvalid': 'Invalid email address',
  'billing.contactForm.emailLabel': 'Email',
  'billing.contactForm.heading': 'Billing contact',
  'billing.contactForm.licenseAddressHeading': 'License address',
  'billing.contactForm.nameLabel': 'Name',
  'billing.contactForm.nameRequired': 'Name is required',
  'billing.contactForm.poNumberLabel': 'PO Number',
  'billing.contactForm.postalCodeLabel': 'Zip code',
  'billing.contactForm.postalCodeRequired': 'Postal code is required',
  'billing.contactForm.save': 'Save',
  'billing.contactForm.saveAndContinue': 'Save and continue',
  'billing.contactForm.saving': 'Saving…',
  'billing.contactForm.selectCountryLabel': 'Select a country',
  'billing.contactForm.stateLabel': 'State or province',
  'billing.contactForm.stateRequired': 'State or province is required',
  'billing.contactForm.taxAddressCheckbox': 'Use this address to calculate sales tax',
  'billing.contactForm.taxAddressHelp':
    'If the address where most of your LaunchDarkly users work is different than your billing address, you can specify it here',
  'billing.contactSales.cancel': 'Cancel',
  'billing.contactSales.closeLabel': 'close',
  'billing.contactSales.companyLabel': 'Company',
  'billing.contactSales.companyRequired': 'Company is required',
  'billing.contactSales.contactUs': 'Contact us',
  'billing.contactSales.dialogLabel': 'Contact LaunchDarkly',
  'billing.contactSales.disclaimer':
    'By submitting, I authorize LaunchDarkly to contact me with personalized marketing communications about our products and services. See our <privacyLink>Privacy Policy</privacyLink> or <optOutLink>Opt-Out</optOutLink> at any time.',
  'billing.contactSales.emailInvalid': 'Enter a valid email address',
  'billing.contactSales.emailLabel': 'Email',
  'billing.contactSales.emailPlaceholder': 'myemail@email.com',
  'billing.contactSales.errorToast':
    'Something went wrong submitting your request. Email trials@launchdarkly.com directly and our team will be in touch.',
  'billing.contactSales.firstNameLabel': 'First name',
  'billing.contactSales.firstNameRequired': 'First name is required',
  'billing.contactSales.heading': 'Reach out to build your perfect plan',
  'billing.contactSales.jobTitleLabel': 'Job title',
  'billing.contactSales.lastNameLabel': 'Last name',
  'billing.contactSales.lastNameRequired': 'Last name is required',
  'billing.contactSales.moreDetailsLabel': 'More details',
  'billing.contactSales.submit': 'Submit',
  'billing.contactSales.submitLabel': 'Submit contact us form',
  'billing.contactSales.successToast': 'An expert will be in touch as soon as possible.',
  'billing.contactSalesForm.companyLabel': 'Company name',
  'billing.contactSalesForm.companyRequired': 'Enter a company name',
  'billing.contactSalesForm.defaultHeading': "We're here to help.",
  'billing.contactSalesForm.detailsLabel': 'How can we help?',
  'billing.contactSalesForm.emailInvalid': 'Enter a valid email address',
  'billing.contactSalesForm.emailLabel': 'Email',
  'billing.contactSalesForm.firstNameLabel': 'First name',
  'billing.contactSalesForm.firstNameRequired': 'Enter a first name',
  'billing.contactSalesForm.lastNameLabel': 'Last name',
  'billing.contactSalesForm.lastNameRequired': 'Enter a last name',
  'billing.contactSalesForm.learnMoreBody': '<strong>Want to learn more?</strong> Head over to <pricing></pricing>',
  'billing.contactSalesForm.policyBody':
    'By submitting you agree to the LaunchDarkly <terms></terms> and <privacy></privacy>.',
  'billing.contactSalesForm.pricingLink': 'launchdarkly.com/pricing',
  'billing.contactSalesForm.privacyLink': 'privacy policies',
  'billing.contactSalesForm.responseTime': 'Our experts typically respond within 2 hours (Mon - Fri, 9am - 6pm PST).',
  'billing.contactSalesForm.submit': 'Submit',
  'billing.contactSalesForm.submitError':
    'Something went wrong submitting your request. Try again, or email trials@launchdarkly.com and our team will be in touch.',
  'billing.contactSalesForm.supportPrompt': 'Looking to contact our support team? <ticket></ticket>',
  'billing.contactSalesForm.supportTicketLink': 'Create a support ticket',
  'billing.contactSalesForm.termsLink': 'terms',
  'billing.contactSalesForm.thankYouBody': 'An expert will be in touch as soon as possible.',
  'billing.contactSalesForm.thankYouHeading': 'Thank you',
  'billing.contactSalesLegacy.defaultHeading': "We're here to help.",
  'billing.contactSalesLegacy.learnMoreBody':
    '<strong>Want to learn more?</strong> Head over to <pricing>launchdarkly.com/pricing</pricing>',
  'billing.contactSalesLegacy.policyBody':
    "By submitting you agree to LaunchDarkly's <terms></terms> and <privacy></privacy>.",
  'billing.contactSalesLegacy.privacyLink': 'privacy policies',
  'billing.contactSalesLegacy.responseTime': 'Our experts typically respond within 2 hours (Mon - Fri, 9am - 6pm PST).',
  'billing.contactSalesLegacy.submit': 'Submit',
  'billing.contactSalesLegacy.supportPrompt':
    'Looking to contact our support team? <ticket>Create a support ticket</ticket>',
  'billing.contactSalesLegacy.termsLink': 'terms',
  'billing.contactSalesLegacy.thankYouBody': 'An expert will be in touch as soon as possible.',
  'billing.contactSalesLegacy.thankYouHeading': 'Thank you',
  'billing.contactUsCta.contactSales': 'Contact sales',
  'billing.contactUsCta.contactUs': 'Contact us',
  'billing.contactUsCta.trialBanner': 'get in touch.',
  'billing.contactUsModal.closeLabel': 'close modal',
  'billing.convertToSegmentUpsell.imageAlt':
    'A screenshot showing the process of converting a list of users to a segment.',
  'billing.convertToSegmentUpsell.title': 'Convert to rules when things get complex',
  'billing.copySettingsUpsell.description': 'This lets you copy rules from one flag to another.',
  'billing.copySettingsUpsell.title': 'Upgrade to copy flag configuration settings',
  'billing.currentUsage.addMoreSeats': 'Add more seats',
  'billing.currentUsage.heading': 'Current usage',
  'billing.currentUsage.loadError': 'Error loading <suffix></suffix> usage',
  'billing.currentUsage.requestUpgrade': 'Request upgrade',
  'billing.currentUsage.resetsOn': 'Will reset on {date}',
  'billing.currentUsage.seats': 'seats',
  'billing.currentUsage.seatsWithSso': 'seats with SSO',
  'billing.currentUsage.unlimited': 'unlimited',
  'billing.currentUsage.upgradeYourPlan': 'Upgrade your plan',
  'billing.currentUsage.usageOfLimit': 'of {limit} <suffix></suffix>',
  'billing.customRolesUpsell.body':
    'Custom roles allow you to grant specific permissions to members or teams and enable your organization to complete mission-critical work while reducing risk. To learn more,',
  'billing.customRolesUpsell.customizeBody':
    'Build roles with permissions at the\nproject, environment, or flag level.',
  'billing.customRolesUpsell.customizeHeading': 'Customize permissions',
  'billing.customRolesUpsell.footerBody': 'Start building access policies for your exact workflow needs.',
  'billing.customRolesUpsell.provisionBody': 'Assign roles to a small set of trusted\nmembers or to the whole team.',
  'billing.customRolesUpsell.provisionHeading': 'Provision roles',
  'billing.customRolesUpsell.readDocumentation': 'read the documentation',
  'billing.customRolesUpsell.secureBody': 'Ensure consistent security and\nefficient development processes.',
  'billing.customRolesUpsell.secureHeading': 'Secure environments',
  'billing.customRolesUpsell.title': 'Precise control for security-minded teams',
  'billing.customizeDialog.cancel': 'Cancel',
  'billing.customizeDialog.commitDescription':
    '{monthlyPrice} per additional {isFirst, select, yes {monthly} other {}} {blockSize} {limitName} ({annualPrice}) annually',
  'billing.customizeDialog.decrementLabel': 'decrement',
  'billing.customizeDialog.includedQuantity': '{quantity} {limitName}',
  'billing.customizeDialog.includedQuantityPerMonth': '{quantity} {limitName} per month',
  'billing.customizeDialog.incrementHint': 'Enter a value in increments of {blockSize}',
  'billing.customizeDialog.incrementLabel': 'increment',
  'billing.customizeDialog.overageRate': '{price} per {blockSize} {limitName}',
  'billing.customizeDialog.overageRatesBilledMonthly': '{rates}, billed monthly',
  'billing.customizeDialog.payAsYouGoHeading': 'Pay-as-you-go for additional usage:',
  'billing.customizeDialog.perYear': '/ year',
  'billing.customizeDialog.planIncludes': 'Plan includes',
  'billing.customizeDialog.prepaySavingsHeading': 'Pre-pay annually to save {percent}%',
  'billing.customizeDialog.prepaySubtext': 'Add additional pre-paid usage and save',
  'billing.customizeDialog.priceBasedOnUsage': 'Price based on monthly usage',
  'billing.customizeDialog.usageAndRatesHeading': 'Usage and rates by products',
  'billing.customizeDialog.usageAndRatesSixProducts': 'Usage and rates for 6 products',
  'billing.customizePlan.addUsageToContinue': 'Add monthly usage to continue',
  'billing.customizePlan.cancel': 'Cancel',
  'billing.customizePlan.changeLimitToContinue': 'Change at least one limit to continue',
  'billing.customizePlan.continueToCheckout': 'Continue to checkout',
  'billing.customizePlan.perYear': '/ year',
  'billing.customizePlan.priceBasedOnUsage': 'Price based on monthly usage',
  'billing.customizePlan.usageAndRatesHeading': 'Usage and rates by product',
  'billing.dataExportCombined.both': 'Warehouse and Streaming',
  'billing.dataExportCombined.bothDelayed': 'Streaming and Warehouse data delayed',
  'billing.dataExportCombined.breakdownTitle': 'Total & top 5 breakdown of usage in the selected range',
  'billing.dataExportCombined.cardValueTooltip': '{title}: {total}',
  'billing.dataExportCombined.closeLabel': 'Close',
  'billing.dataExportCombined.combinedUnavailable':
    'Combined usage is unavailable because {source} usage could not be loaded. Select the other source to view it on its own.',
  'billing.dataExportCombined.description':
    'Data Export limits are measured in transfers to a streaming destination or warehouse. Each streamed event or exported row counts as 1 transfer.',
  'billing.dataExportCombined.destinationAvailableFrom': 'Destination breakdown available from {date}',
  'billing.dataExportCombined.destinationNotYetAvailable': 'Destination breakdown not yet available',
  'billing.dataExportCombined.eventKindStreamingOnly':
    'Event kind is only available for Streaming, so these totals don’t include Warehouse transfers.',
  'billing.dataExportCombined.latestRecordedUsage': 'Latest recorded usage: through {time} UTC',
  'billing.dataExportCombined.previousPeriodTooltip': 'Change relative to the previous period of the same length',
  'billing.dataExportCombined.reduceUsageLink': 'Optimize your Data Export usage',
  'billing.dataExportCombined.scopeLabel': 'Data export source',
  'billing.dataExportCombined.searchAllPlaceholder':
    '{count, plural, one {Search # value} other {Search all # values}}',
  'billing.dataExportCombined.sourceDestinationsAvailableFrom': '{source} destination breakdown available from {date}',
  'billing.dataExportCombined.sourceDestinationsNotYetAvailable': '{source} destination breakdown not yet available',
  'billing.dataExportCombined.streaming': 'Streaming',
  'billing.dataExportCombined.streamingDelayed': 'Streaming data delayed',
  'billing.dataExportCombined.streamingExports': 'Streaming exports',
  'billing.dataExportCombined.totalUsage': 'Total usage',
  'billing.dataExportCombined.transfersLabel': 'Data Export transfers',
  'billing.dataExportCombined.unattributed': 'Unattributed',
  'billing.dataExportCombined.usageOfLimitThisMonth': 'of {limit} Data Export transfers this month',
  'billing.dataExportCombined.viewGroupUsage': 'View {name} usage',
  'billing.dataExportCombined.warehouse': 'Warehouse',
  'billing.dataExportCombined.warehouseDelayed': 'Warehouse data delayed',
  'billing.dataExportCombined.warehouseExports': 'Warehouse exports',
  'billing.diagnosticUsageOverview.diagnosticOverview': 'Diagnostic overview',
  'billing.diagnosticUsageOverview.monthlyAccumulatedUsage': 'Monthly accumulated usage as of today',
  'billing.diagnosticUsagePage.loadError': 'Error occurred while loading diagnostic usage',
  'billing.diagnosticUsagePage.loadingLabel': 'Loading diagnostic usage data',
  'billing.diagnosticUsagePage.overviewTab': 'Overview',
  'billing.enterpriseTrial.cancel': 'Cancel',
  'billing.enterpriseTrial.confirm': 'Confirm',
  'billing.enterpriseTrial.featureAllIntegrations': 'All integrations',
  'billing.enterpriseTrial.featureCodeReferences': 'Code references',
  'billing.enterpriseTrial.featureCustomRoles': 'Custom roles & teams',
  'billing.enterpriseTrial.featureDataExport': 'Data Export',
  'billing.enterpriseTrial.featureEnterpriseIntegrations': 'Enterprise integrations',
  'billing.enterpriseTrial.featureExperimentation': 'Experimentation',
  'billing.enterpriseTrial.featureFlagPrerequisites': 'Flag prerequisites',
  'billing.enterpriseTrial.featureFlagTriggers': 'Flag triggers',
  'billing.enterpriseTrial.featureWorkflows': 'Workflows',
  'billing.enterpriseTrial.heading': 'Start a free, {days}-day Enterprise trial',
  'billing.enterpriseTrial.noChargesNote': 'No charges will be made to your Professional plan after the trial ends.',
  'billing.enterpriseTrial.startTrial': 'Start an Enterprise trial',
  'billing.enterpriseTrial.subheading': 'Try powerful Enterprise features, including:',
  'billing.enterpriseTrial.trialLength': 'Your trial will last {days} days and end on {date}.',
  'billing.experimentsUpsell.anyAudienceBody': 'Release to everyone, or\nto a targeted audience.',
  'billing.experimentsUpsell.anyAudienceHeading': 'Any audience',
  'billing.experimentsUpsell.anyFlagBody': 'Measure any change you\nrelease with a feature flag.',
  'billing.experimentsUpsell.anyFlagHeading': 'Any flag',
  'billing.experimentsUpsell.anyMetricsBody': 'Track in real-time the data\nyou need to make a decision.',
  'billing.experimentsUpsell.anyMetricsHeading': 'Any metrics',
  'billing.experimentsUpsell.description': 'Start measuring the impact of every feature you release.',
  'billing.experimentsUpsell.title': 'Deliver new features with confidence',
  'billing.featureUpsell.toLearnMore': 'To learn more,',
  'billing.featureUpsell.upgrade': 'Upgrade',
  'billing.featureUpsell.viewPlans': 'View plans',
  'billing.flagTriggersUpsell.description': 'Use triggers to remotely turn flag targeting on or off.',
  'billing.flagTriggersUpsell.title': 'Flag triggers',
  'billing.getDemoCta.docsLink': 'Learn more in docs',
  'billing.getDemoCta.getDemo': 'Get a demo',
  'billing.guardianPlanSummary.body':
    'Guardian plans can be customized to your business needs. Talk to one of our account executives to build the perfect plan for your organization.',
  'billing.guardianPlanSummary.customPricingAvailable': 'Custom pricing available',
  'billing.guardianPlanSummary.heading': 'Plan details',
  'billing.guardianPlanSummary.requestQuote': 'Request a quote',
  'billing.guardianPlanSummary.trialEnds': 'Your {planName} ends {date}.',
  'billing.guardianTrialHeader.compareAvailablePlans': 'Compare available plans',
  'billing.guardianTrialHeader.dataExplainerBody':
    'Your data persists in LaunchDarkly after your trial ends, and you will regain access to any Guardian-related features and configurations if you purchase an Guardian plan.',
  'billing.guardianTrialHeader.dataExplainerHeading': 'What happens to my data after trial ends?',
  'billing.guardianTrialHeader.extendTrial': 'Extend my Guardian trial',
  'billing.guardianTrialHeader.heading': 'Free Developer Plan with 14-Day Guardian Trial',
  'billing.guardianTrialHeader.learnMore': 'Learn more',
  'billing.guardianTrialHeader.trialDates': 'Guardian Trial: {start} - {end}',
  'billing.inlineUpsell.readAboutFeature': 'read about this feature',
  'billing.inlineUpsell.toLearnMore': 'To learn more,',
  'billing.inlineUpsell.viewPlans': 'view plans.',
  'billing.invoiceHistory.downloadLabel': 'Download invoice',
  'billing.invoiceHistory.downloadUnavailable': 'Invoice download is currently unavailable.',
  'billing.invoiceHistory.emptyState': "You don't have any invoices yet.",
  'billing.invoiceHistory.loadError':
    'Error loading invoices: {error}. <link>Contact support</link> if this issue persists.',
  'billing.invoiceHistory.selectAnInvoice': 'Select an invoice',
  'billing.invoiceHistory.selectedInvoiceLabel': 'Selected invoice date: {date}',
  'billing.invoiceHistory.title': 'Billing history',
  'billing.legacyBillingMethod.accountCanceled': 'You canceled your account.',
  'billing.legacyBillingMethod.addCard': 'Add card',
  'billing.legacyBillingMethod.editCard': 'Edit card',
  'billing.legacyBillingMethod.functionalUntil': 'Your account will remain fully functional until {date}.',
  'billing.legacyBillingMethod.heading': 'Billing method',
  'billing.legacyBillingMethod.paymentFailed': 'Your last payment failed.',
  'billing.legacyBillingMethod.saveCard': 'Save card',
  'billing.legacyBillingMethod.stripeCheckoutDescription': 'Feature management platform',
  'billing.legacyBillingMethod.unavailableTooltip': 'Currently unavailable. Try again later.',
  'billing.legacyBillingMethod.updatingCard': 'Updating card',
  'billing.legacyPlanSummary.environmentsLabel': 'Environments:',
  'billing.legacyPlanSummary.freeTrialEnds': 'Free trial — Ends {date}.',
  'billing.legacyPlanSummary.newPricingBody': 'We recently updated our pricing. Explore the new options.',
  'billing.legacyPlanSummary.newPricingHeading': 'Do you know about our new pricing plans?',
  'billing.legacyPlanSummary.projectsLabel': 'Projects:',
  'billing.legacyPlanSummary.seatsLabel': 'Seats:',
  'billing.legacyPlanSummary.selectPlan': 'Select plan',
  'billing.legacyPlanSummary.serverClientMauLabel': 'Server & client <mau></mau> combined:',
  'billing.legacyPlanSummary.trialEnded': 'Your trial ended on {date}.',
  'billing.legacyPlanSummary.viewNewPlanOptions': 'View new plan options',
  'billing.legacyPlanSummary.viewPlanOptions': 'View plan options',
  'billing.legacyUsage.environmentsUnit': 'environments',
  'billing.legacyUsage.mauBreakdown': '{server} server <mau></mau> & {client} client-side <mau></mau>',
  'billing.legacyUsage.projectsUnit': 'projects',
  'billing.legacyUsage.seatsUnit': '{count, plural, one {seat} other {seats}}',
  'billing.legacyUsage.totalUnit': 'total',
  'billing.legacyUsage.usageOfLimit': 'of <highlight>{limit}</highlight> {suffix}',
  'billing.managePlan.choosePlanTypeHeading': 'Choose a plan type',
  'billing.managePlan.overageBody':
    'If you exceed your allotted entitlements, your organization will incur charges for the excess usage. LaunchDarkly calculates this excess usage, and corresponding overage charge, at the end of each month. You can review the rates in the table below.',
  'billing.managePlan.overageEmailNotice':
    'We will inform you via email if you incur any overage. To keep track of your usage, visit your <link>usage</link> page.',
  'billing.managePlan.overageHeading': 'What if I exceed my monthly entitlements?',
  'billing.managePlan.pendingChangesNotice':
    'Your plan was last updated on {updatedAt} and the changes are set to take effect on {effectiveDate}. Any further modifications will replace the most recent updates.',
  'billing.managePlan.upcomingEntitlementsHeading': 'Upcoming monthly entitlements',
  'billing.managePlan.updatePlanHeading': 'Update your plan',
  'billing.marketplaceManage.bodyAws':
    '<strong>Your subscription is billed through AWS Marketplace.</strong> Plan changes and payment details are managed in your AWS Marketplace account. Your invoices are in the <link>AWS Billing and Cost Management console</link>.',
  'billing.marketplaceManage.bodyVercel':
    '<strong>Your subscription is billed through Vercel.</strong> Plan changes, payment details, and invoices are managed in your Vercel account.',
  'billing.marketplaceManage.contactSupport': 'To manage this subscription, <link>contact support</link>.',
  'billing.marketplaceManage.manageInAws': 'Manage in AWS',
  'billing.marketplaceManage.manageInVercel': 'Manage in Vercel',
  'billing.mau.abbr': 'MAU',
  'billing.mau.fullName': 'Monthly active users',
  'billing.monthToDateCharges.heading': 'Month-to-date charges',
  'billing.monthToDateCharges.overageChargesHeading': '{month} overage charges',
  'billing.monthToDateCharges.overagesBilledNextMonth': 'Overages will be billed at the beginning of the next month',
  'billing.monthToDateCharges.seeUsageDetails': 'See usage details',
  'billing.monthToDateCharges.trialAccrualNotice':
    'Your usage charges will begin aggregating after your trial ends on {date}.',
  'billing.notifications.billingMethodUpdated': 'Billing method updated',
  'billing.notifications.cancelSubscriptionFailed': 'Subscription deletion unsuccessful. Try again later.',
  'billing.notifications.planUpdated': 'Plan updated',
  'billing.notifications.seatsPurchased': '{countWord} {count, plural, one {seat} other {seats}} purchased',
  'billing.notifications.seatsPurchasedAndInvited':
    '{countWord} {count, plural, one {seat} other {seats}} purchased and {count, plural, one {invitation} other {invitations}} sent',
  'billing.notifications.subscribeFailed': 'Error updating your subscription. Try again later.',
  'billing.notifications.subscriptionCanceled': 'Subscription canceled',
  'billing.notifications.trialExtended': 'Trial extended',
  'billing.notifications.trialStarted': 'Your {campaignName} trial has started',
  'billing.overageInfo.annualPriceColumn': 'Price per month (billed annually)',
  'billing.overageInfo.authorizationBody':
    'I authorize LaunchDarkly to automatically charge my saved payment method (“Payment Method”) at the beginning of each month for any usage in excess of the free or prepaid entitlement from the prior month. LaunchDarkly reserves the right to suspend or terminate access to your account if we are unable to charge your Payment Method for any overage charges incurred in your account.',
  'billing.overageInfo.authorizationLink': 'Read automatic overage charge authorization',
  'billing.overageInfo.automaticBody':
    'By selecting this checkbox, I authorize LaunchDarkly to automatically charge my saved payment method ("Payment Method") at the beginning of each month for any usage in excess of the free or prepaid entitlement from the prior month. LaunchDarkly reserves the right to suspend or terminate access to my account if LaunchDarkly is unable to charge my Payment Method for any overage charges incurred in my account.',
  'billing.overageInfo.automaticHeading': 'Automatic overage charge authorization',
  'billing.overageInfo.close': 'Close',
  'billing.overageInfo.closeLabel': 'close',
  'billing.overageInfo.dialogLabel': 'Overage info modal',
  'billing.overageInfo.exceedEntitlementsBody':
    'If you exceed your allotted entitlements, your organization will incur charges for the excess usage. LaunchDarkly calculates this excess usage, and corresponding overage charge, at the end of each month. You can review the rates in the table below.',
  'billing.overageInfo.experimentationKeysDescription':
    'Keys used in experiments that measure impact and make data-informed decisions about your releases.',
  'billing.overageInfo.experimentationMauDescription':
    'MAU used in experiments that measure impact and make data-informed decisions about your releases.',
  'billing.overageInfo.hostsDescription':
    'A monthly connection to a server-side SDK (such as VM, Container, or Kubernetes pod).',
  'billing.overageInfo.increaseEntitlementsBody':
    'If you anticipate exceeding the limits set in your plan, you have the option to increase your entitlements. Any adjustments to your entitlements will take effect at the beginning of the next month.',
  'billing.overageInfo.mauDescription':
    'The number of monthly active client-side users, devices, or other targeted resources.',
  'billing.overageInfo.monthlyBody':
    'If you use more entitlements than your plan provides, your bill for that month may go up. LaunchDarkly calculates overages and the charges corresponding to them at the end of each month. You can review the rates in the table below.',
  'billing.overageInfo.monthlyHeading': 'What if I exceed my monthly entitlements?',
  'billing.overageInfo.notifications':
    '<strong>Overage notifications:</strong> You will be notified via email each month when your overages balance exceeds the following amounts: $100, $250, $500, $750, $1000, $1500, $2k, $3k, $4k, $5k, $6k',
  'billing.overageInfo.observabilityDescription': 'Observability includes sessions, errors, traces, and logs',
  'billing.overageInfo.observabilityHeader': 'Observability',
  'billing.overageInfo.overagePriceColumn': 'Overage price per month',
  'billing.overageInfo.resourceInfoLabel': 'Resource Type Info',
  'billing.overageInfo.resourceTypeColumn': 'Resource type',
  'billing.overageInfo.seePricingPage': 'See the pricing page',
  'billing.overageInfo.tableLabel': 'Overage pricing',
  'billing.overageRates.aiRunsRow': 'AI runs (per 1,000)',
  'billing.overageRates.annualPriceColumn': 'Price per month (billed annually)',
  'billing.overageRates.clientSideMauDescription':
    'The number of monthly active client-side users, devices, or other targeted resources.',
  'billing.overageRates.clientSideMauRow': 'Client-side MAU (per 1,000)',
  'billing.overageRates.experimentationKeysDescription':
    'Keys used in experiments that measure impact and make data-informed decisions about your releases.',
  'billing.overageRates.experimentationKeysRow': 'Experimentation keys (per 10,000)',
  'billing.overageRates.experimentationMauDescription':
    'MAU used in experiments that measure impact and make data-informed decisions about your releases.',
  'billing.overageRates.experimentationMauRow': 'Experimentation MAU (per 1,000)',
  'billing.overageRates.observability': 'Observability',
  'billing.overageRates.observabilityDescription': 'Observability includes sessions, errors, traces, and logs',
  'billing.overageRates.overagePriceColumn': 'Overage price per month',
  'billing.overageRates.resourceInfoLabel': 'Resource Type Info',
  'billing.overageRates.resourceTypeColumn': 'Resource type',
  'billing.overageRates.seePricingPage': 'See the pricing page',
  'billing.overageRates.serviceConnectionsDescription':
    'A monthly connection to a server-side SDK (such as VM, Container, or Kubernetes pod).',
  'billing.overageRates.serviceConnectionsRow': 'Service connections',
  'billing.overview.accountId': 'Account ID:',
  'billing.overview.billingContactCard': 'Billing contact',
  'billing.overview.changePlan': 'Change plan',
  'billing.overview.loadError': 'We ran into an unexpected error. Refresh the page or try again later.',
  'billing.overview.paymentMethodCard': 'Payment method',
  'billing.overview.title': 'Billing overview',
  'billing.paymentMethod.cancel': 'Cancel',
  'billing.paymentMethod.fetchingLabel': 'Fetching payment method',
  'billing.paymentMethod.loadCardError': 'Error loading payment card: {error}',
  'billing.paymentMethod.loadError': 'We ran into an unexpected error. Refresh the page or try again later.',
  'billing.paymentMethod.save': 'Save',
  'billing.paymentMethod.saveError': 'We were unable to save your card. Try again.',
  'billing.paymentMethod.saving': 'Saving…',
  'billing.paymentMethod.settingUpLabel': 'Setting up payment method',
  'billing.paymentMethod.unknownLoadCardError': 'Unknown error loading payment card: {error}',
  'billing.paymentMethodCard.addPaymentMethod': 'Add Payment Method',
  'billing.paymentMethodCard.cancel': 'Cancel',
  'billing.paymentMethodCard.contactRequiredTooltip': 'Update your billing contact to proceed',
  'billing.paymentMethodCard.editPaymentMethod': 'Edit payment method',
  'billing.paymentMethodCard.heading': 'Billing method',
  'billing.paymentMethodCard.loadError': 'We ran into an unexpected error. Refresh the page or try again later.',
  'billing.paymentMethodCard.noCard': "There's no card associated with this account",
  'billing.paymentMethodCard.noCardDetails': 'There is no card associated with this account.',
  'billing.paymentMethodCard.reset': 'Reset',
  'billing.paymentMethodCard.saveAndContinue': 'Save and continue',
  'billing.paymentMethodCard.saveCard': 'Save card',
  'billing.paymentMethodCard.saving': 'Saving',
  'billing.pendingChangesDialog.body':
    'Your plan was last updated on <strong>{updatedAt}</strong> and the changes are set to take effect on <strong>{effectiveDate}</strong>. Any further modifications will replace the most recent changes.',
  'billing.pendingChangesDialog.buttonsLabel': 'Pending Subscription Changes Buttons',
  'billing.pendingChangesDialog.closeLabel': 'close',
  'billing.pendingChangesDialog.dialogLabel': 'Pending Subscription Changes Modal',
  'billing.pendingChangesDialog.heading': 'There are pending changes to your plan',
  'billing.pendingChangesDialog.keepExistingChanges': 'Keep existing changes',
  'billing.pendingChangesDialog.modifyPlan': 'Modify plan',
  'billing.pendingChangesUpsell.title': 'More automation, less risk',
  'billing.planAddOns.addExperimentationCta': '<contactUs></contactUs> to add Experimentation to your plan.',
  'billing.planAddOns.addMoreExperimentationCta': '<contactUs></contactUs> to add more Experimentation to your plan.',
  'billing.planAddOns.addSso': 'Add SSO',
  'billing.planAddOns.contactUs': 'Contact us',
  'billing.planAddOns.dataExportDescription':
    'Events from your experiments that you export to your favorite data analytics tool.',
  'billing.planAddOns.dataExportEnterpriseOnly': 'Available with Enterprise plans.',
  'billing.planAddOns.dataExportTitle': 'Data Export events',
  'billing.planAddOns.experimentationEventsTitle': 'Experimentation events',
  'billing.planAddOns.experimentationMauDescription':
    'The number of users available to be targeted in experiments each month.',
  'billing.planAddOns.experimentationMauHeading': 'Experimentation <mau></mau>',
  'billing.planAddOns.experimentationMauTitle': 'Experimentation MAU',
  'billing.planAddOns.includedCount': '{count} included',
  'billing.planAddOns.learnMore': 'Learn more',
  'billing.planAddOns.removeSsoError': 'To remove SSO, turn it off on the <link>Security</link> tab.',
  'billing.planAddOns.ssoTitle': 'Single sign-on (SSO)',
  'billing.planComparison.comparePlans': 'Compare plans',
  'billing.planComparison.dialogTitle': 'Full plan comparison',
  'billing.planConfirmation.agreeToTerms': 'I agree to the subscription terms',
  'billing.planConfirmation.agreeToTermsWithRenewals':
    'I agree to the subscription terms, including automatic renewals',
  'billing.planConfirmation.cardRequired': 'Enter a credit card before proceeding',
  'billing.planConfirmation.contactRequired': 'Update your billing contact and payment method before proceeding',
  'billing.planConfirmation.editPlanDetails': 'Edit plan details',
  'billing.planConfirmation.entitlementChangesCurrentMonth':
    'Your entitlement changes are effective at the start of the current month',
  'billing.planConfirmation.entitlementChangesNextMonth':
    'Your entitlement changes will be billed and take effect at the start of the next month',
  'billing.planConfirmation.heading': 'Review your subscription changes',
  'billing.planConfirmation.overageAuthorizationCheckbox':
    'I understand and agree to the automatic overage charge authorization',
  'billing.planConfirmation.overageEmailTooltip':
    'You will be notified via email each month when your overage balance exceeds the following amounts: $100, $250, $500, $750, $1000, $1500, $2k, $3k, $4k, $5k, $6k',
  'billing.planConfirmation.overageTooltipLabel': 'Automatic overage tooltip icon',
  'billing.planConfirmation.questions': 'Questions?',
  'billing.planConfirmation.termsInstructions': 'To complete your purchase, you must accept the following:',
  'billing.planConfirmation.termsRequired': 'You must accept the terms above before proceeding',
  'billing.planConfirmation.updateSubscription': 'Update subscription',
  'billing.planConfirmation.updating': 'Updating subscription',
  'billing.planConfirmation.updatingBody': 'This may take a moment',
  'billing.planConfirmation.updatingHeading': 'Updating your subscription…',
  'billing.planDetailsCard.accountIdLabel': 'Account ID',
  'billing.planDetailsCard.campaignEnds': 'ends {date}.',
  'billing.planDetailsCard.choosePlan': 'Choose a plan',
  'billing.planDetailsCard.compareAvailablePlans': 'Compare available plans',
  'billing.planDetailsCard.ctaPromptHasPlan': 'Need different plan options?',
  'billing.planDetailsCard.ctaPromptNoPlan': 'Ready to sign up?',
  'billing.planDetailsCard.customPlanCta': "You're on a custom plan. <link>Contact us</link> to make changes.",
  'billing.planDetailsCard.dataExplainerBody':
    'Your data persists in LaunchDarkly after your trial ends, and you will regain access to any Enterprise-related features and configurations if you purchase an Enterprise plan.',
  'billing.planDetailsCard.dataExplainerHeading': 'What happens to my data after trial ends?',
  'billing.planDetailsCard.enterpriseFeaturesTooltip': 'View Enterprise features',
  'billing.planDetailsCard.enterpriseTrialActive': 'Enterprise trial active',
  'billing.planDetailsCard.enterpriseTrialDrawerCta': 'Enterprise trial',
  'billing.planDetailsCard.enterpriseTrialHeading': 'Enterprise trial',
  'billing.planDetailsCard.extendTrial': 'Extend my Enterprise trial',
  'billing.planDetailsCard.heading': 'Plan details',
  'billing.planDetailsCard.learnMore': 'Learn more',
  'billing.planDetailsCard.limitPerMonth': '{limit}/month',
  'billing.planDetailsCard.planHeading': '{planName} plan',
  'billing.planDetailsCard.seatsWithSso': '{limit} with SSO',
  'billing.planDetailsCard.trialEnded': 'Your trial ended on {date}.',
  'billing.planDetailsCard.trialStartsOn': 'starts on {date}.',
  'billing.planDetailsCard.trialValidFrom': 'Valid from {start} - {end}',
  'billing.planDetailsCard.viewPlanOptions': 'View plan options',
  'billing.planFeatures.accessAllIntegrations': 'Access to all integrations',
  'billing.planFeatures.allIntegrations': 'Access to all of our integrations',
  'billing.planFeatures.allSdks': 'Access to all 25+ SDKs',
  'billing.planFeatures.apiControls': 'API controls',
  'billing.planFeatures.apiControlsIntegrations': 'API controls + access to most integrations',
  'billing.planFeatures.approvalFlows': 'Required approval flows',
  'billing.planFeatures.automatedRollbacks': 'Automated rollbacks',
  'billing.planFeatures.bulkTargeting': 'Bulk user targeting',
  'billing.planFeatures.changeHistory': 'All-time change history',
  'billing.planFeatures.customRoles': 'Custom roles and permissions',
  'billing.planFeatures.enterpriseLocked': 'Enterprise features and configurations will be locked',
  'billing.planFeatures.flagDashboard': 'Feature flag dashboard',
  'billing.planFeatures.flagReviews': 'Flag reviews by peers',
  'billing.planFeatures.flagScheduling': 'Flag scheduling',
  'billing.planFeatures.flagTriggers': 'Automated flag triggers',
  'billing.planFeatures.includedFeaturesHeading': 'Included features',
  'billing.planFeatures.includesEverythingHeading': 'Includes everything in <strong>{planName}</strong>, plus:',
  'billing.planFeatures.includesFeaturesHeading': 'Includes <strong>{planName}</strong> features, plus',
  'billing.planFeatures.includesHeading': 'Includes:',
  'billing.planFeatures.largeSegments': 'Large list-based and synced segments',
  'billing.planFeatures.progressiveRollouts': 'Scheduled progressive rollouts',
  'billing.planFeatures.realTimeMonitoring': 'Real-time monitoring',
  'billing.planFeatures.regressionDetection': 'Regression detection and alerts',
  'billing.planFeatures.reusableSegments': 'Reusable segments',
  'billing.planFeatures.reusableSegmentsTargeting': 'Reusable segments to target individuals or groups',
  'billing.planFeatures.samlScim': 'SAML / SCIM',
  'billing.planFeatures.teamsSlackIntegrations': 'Integrations with Microsoft Teams and Slack',
  'billing.planFeatures.unlimitedFlagsEnvs': 'Unlimited feature flags & environments',
  'billing.planFeatures.unlimitedProjects': 'Unlimited projects',
  'billing.planFeatures.unlimitedProjectsEnvsFlags': 'Unlimited projects, environments & flags',
  'billing.planFeatures.unlimitedSeatsSso': 'Unlimited seats + SSO to enable your entire organization',
  'billing.planInclusions.agentControlPromo':
    '<strong>Try AgentControl free.</strong> No charges for AI runs through July 1, 2026.',
  'billing.planInclusions.annualDiscount':
    'Annual plan includes <strong>{discount}</strong>.\u00A0Add additional usage now at same discount.',
  'billing.planInclusions.heading': "What's included:",
  'billing.planInclusions.monthlyIncludes': 'Built-in monthly usage, plus <strong>100K experimentation MAU.</strong>',
  'billing.planInclusions.overageBilled': 'Usage beyond that is billed monthly at standard rates.',
  'billing.planInclusionsSummary.billedAnnually': 'Billed annually',
  'billing.planInclusionsSummary.developerFoundationPlanHeading': 'Developer / Foundation Plan',
  'billing.planInclusionsSummary.developerPlanHeading': 'Developer Plan',
  'billing.planInclusionsSummary.experimentationKeysPerMonth': '{quantity} experimentation keys per month',
  'billing.planInclusionsSummary.foundationPlanHeading': 'Foundation Plan',
  'billing.planInclusionsSummary.free': 'Free',
  'billing.planInclusionsSummary.freeDevExperimentationMau': '100K free experimentation MAU per month',
  'billing.planInclusionsSummary.freeExperimentationMauPerMonth': '{quantity} free experimentation MAU per month',
  'billing.planInclusionsSummary.freeProductPerMonth': '{quantity} free {product} per month',
  'billing.planInclusionsSummary.payAsYouGoMonthly': 'Pay as you go (monthly)',
  'billing.planInclusionsSummary.productPerMonth': '{quantity} {product} per month',
  'billing.planLimits.clientSideMauDescription':
    'The number of monthly active client-side users, devices, or other targeted resources.',
  'billing.planLimits.clientSideMauHeading': 'Client-side <mau></mau>',
  'billing.planLimits.clientSideMauTitle': 'Client-side MAU',
  'billing.planLimits.customizeEntitlementsHeading': 'Customize your monthly entitlements',
  'billing.planLimits.decrementButtonLabel': 'decrement',
  'billing.planLimits.hostsTitle': 'Hosts',
  'billing.planLimits.includedCount': '{included} included',
  'billing.planLimits.includedInStarterTooltip': 'Included in starter plan',
  'billing.planLimits.incrementButtonLabel': 'increment',
  'billing.planLimits.incrementLabel': 'Increment',
  'billing.planLimits.incrementerLabel': '{limitName} incrementer',
  'billing.planLimits.learnMore': 'Learn more',
  'billing.planLimits.needMoreThan': 'Need more than {max}?',
  'billing.planLimits.planNameProfessional': 'Professional',
  'billing.planLimits.planNameStarter': 'Starter',
  'billing.planLimits.pricingByUsageHeading': 'Pricing by usage',
  'billing.planLimits.seatDefinition': 'Each team member who logs in with a unique email counts as one seat.',
  'billing.planLimits.seats': 'Seats',
  'billing.planLimits.seatsMaxError': '{planName} plans allow a max of {max} seats',
  'billing.planLimits.seatsMinError': '{planName} plans require a minimum of {min} seats',
  'billing.planLimits.serviceConnectionsDescription':
    'A monthly connection to a server-side SDK (such as VM, Container, or Kubernetes pod).',
  'billing.planLimits.serviceConnectionsHeading': 'Service connections',
  'billing.planLimits.totalCount': '{total} total',
  'billing.planLimits.upgradeToEnterprise': 'Upgrade to an Enterprise plan.',
  'billing.planLimits.upgradeToProfessional': 'Upgrade to a Professional plan.',
  'billing.planLimits.yourUsage': 'Your usage: <strong>{usage} out of <limit></limit></strong>',
  'billing.planName.custom': 'Custom plan',
  'billing.planName.developer': 'Developer',
  'billing.planName.developerFoundation': 'Developer / Foundation',
  'billing.planName.enterprise': 'Enterprise',
  'billing.planName.enterpriseTrial': 'Enterprise trial',
  'billing.planName.foundation': 'Foundation',
  'billing.planName.foundationTrial': 'Foundation trial',
  'billing.planName.guardian': 'Guardian',
  'billing.planName.guardianTrial': 'Guardian trial',
  'billing.planName.legacy': 'Legacy',
  'billing.planName.legacyProfessional': 'Legacy professional',
  'billing.planName.partner': 'Partner',
  'billing.planName.professional': 'Professional',
  'billing.planName.professionalTrial': 'Professional trial',
  'billing.planName.starter': 'Starter',
  'billing.planName.trial': 'Trial',
  'billing.planNames.developerFoundation': 'Developer / Foundation',
  'billing.planPicker.accountId': 'Account ID',
  'billing.planPicker.afterTrialTagLabel': 'Plan after trial',
  'billing.planPicker.compareAllFeatures': 'Compare all features',
  'billing.planPicker.connectionDiscountLabel': 'Plan discount',
  'billing.planPicker.connectionDiscountTag': '<b>Discount:</b> K8s, Ruby & Python {discountedPrice}/connection',
  'billing.planPicker.connectionDiscountTagTextValue': 'Discount: K8s, Ruby & Python {discountedPrice}/connection',
  'billing.planPicker.contactUs': 'Contact us',
  'billing.planPicker.currentPlan': 'Current plan',
  'billing.planPicker.currentPlanSuffix': '(current plan)',
  'billing.planPicker.customizeAndSave': 'Customize And Save',
  'billing.planPicker.developer2025Subtext': 'A feature-rich foundation for small teams to start building',
  'billing.planPicker.developer2025TagLabel': 'Developer 2025',
  'billing.planPicker.developerSubtext': 'Get started with feature flags for small teams or passion projects',
  'billing.planPicker.developerTagLabel': 'Developer',
  'billing.planPicker.enterpriseSubtext':
    'Power consistent, secure software delivery practices across your entire organization',
  'billing.planPicker.featureInfoLabel': 'View tooltip',
  'billing.planPicker.foundation2025Subtext': 'A flexible platform for startups and growing teams',
  'billing.planPicker.foundationSubtext': 'Grow your business with scalable feature management and experimentation',
  'billing.planPicker.freeTag': 'Free',
  'billing.planPicker.getStarted': 'Get started',
  'billing.planPicker.guardianSubtext':
    'De-risk software releases by monitoring for regressions in real time. Instantly recover with alerts and auto-rollbacks',
  'billing.planPicker.heading': 'Choose a plan',
  'billing.planPicker.loadingPlans': 'Loading plans',
  'billing.planPicker.learnMore': 'Learn more',
  'billing.planPicker.legacyProfessionalSubtext':
    'To learn more about our new plans, read <link>our pricing page</link>',
  'billing.planPicker.loadingPlanTooltip': 'Wait while we load the selected plan',
  'billing.planPicker.mostPopular': 'Most popular',
  'billing.planPicker.noPlansDescription':
    'There are no plans you can switch to from your current plan. To change your plan, contact us.',
  'billing.planPicker.noPlansHeading': 'No plans available',
  'billing.planPicker.planNameDeveloper': 'Developer',
  'billing.planPicker.planNameEnterprise': 'Enterprise',
  'billing.planPicker.planNameFoundation': 'Foundation',
  'billing.planPicker.planNameGuardian': 'Guardian',
  'billing.planPicker.planNameLegacyProfessional': 'Legacy professional',
  'billing.planPicker.planNameProfessional': 'Professional',
  'billing.planPicker.planNameStarter': 'Starter',
  'billing.planPicker.planStatusTagLabel': 'Plan status',
  'billing.planPicker.professionalSubtext': 'Scalable feature management and experimentation for growing teams',
  'billing.planPicker.selectPlan': 'Select plan',
  'billing.planPicker.starterSubtext': 'Basic feature management tools for small teams',
  'billing.planPrice.additionalUsage': 'Additional usage',
  'billing.planPrice.annualExplainer':
    'Built-in and additional pre-paid usage included. Additional usage billed monthly at standard rates.',
  'billing.planPrice.billedOn': 'Billed on {date}',
  'billing.planPrice.currentPlanPrice': 'Current plan price',
  'billing.planPrice.free': 'FREE',
  'billing.planPrice.loadingNextPaymentLabel': 'Loading next payment',
  'billing.planPrice.loadingRenewalPaymentLabel': 'Loading renewal payment',
  'billing.planPrice.loadingTotalLabel': 'Loading total price',
  'billing.planPrice.monthlyExplainer': 'Additional usage billed at standard monthly rates.',
  'billing.planPrice.monthlyPrice': 'Monthly price',
  'billing.planPrice.nextPayment': 'Next payment',
  'billing.planPrice.overageBilledOn': 'This will be billed to your current payment method on {date}.',
  'billing.planPrice.overageChargesHeading': '{month} overage charges',
  'billing.planPrice.overageTotal': 'Total {amount}',
  'billing.planPrice.perMonth': '{price}/mo',
  'billing.planPrice.perYear': '{price}/yr',
  'billing.planPrice.renewalPayment': 'Renewal payment',
  'billing.planPrice.savings': 'Savings',
  'billing.planPrice.total': 'Total',
  'billing.planSummary.expandLabel': 'Open plan inclusions',
  'billing.planSummary.includedHeading': 'Included in your plan',
  'billing.planSummary.limitPerMonth': '{limitName} {quantity}/mo',
  'billing.planSummary.yourPlanHeading': 'Your {planName} plan',
  'billing.planSummaryCard.annualBillingContactBody':
    'Need annual billing? Talk to one of our technical account executives to learn about plan options and build the perfect plan for your organization.',
  'billing.planSummaryCard.annualSavings': "You're saving {amount} by paying annually",
  'billing.planSummaryCard.annualSavingsLabel': 'Annual savings',
  'billing.planSummaryCard.annualSubscriptionEnds': 'Your annual subscription ends on {date}',
  'billing.planSummaryCard.billedAnnuallyInterval': 'Billed annually',
  'billing.planSummaryCard.billedMonthlyInterval': 'Billed monthly',
  'billing.planSummaryCard.billingFrequencyNote':
    'You can change your billing frequency during the final month of your current subscription.',
  'billing.planSummaryCard.changesEffectiveCurrentMonth': 'Changes are effective at the start of the current month',
  'billing.planSummaryCard.changesEffectiveNextMonth':
    'Changes will be billed and take effect at the start of the next month',
  'billing.planSummaryCard.contactUs': 'Contact us',
  'billing.planSummaryCard.currentPlanSummary':
    'Your current {planName} plan is {price} <small>(billed {interval})</small>.',
  'billing.planSummaryCard.customPricingAvailable': 'Custom pricing available',
  'billing.planSummaryCard.customizeEnterpriseHeading': 'Customize your Enterprise plan',
  'billing.planSummaryCard.enterpriseDescription':
    'Enterprise plans are customizable to your business needs. Talk to one of our technical account executives to learn about plan options and build the perfect plan for your organization.',
  'billing.planSummaryCard.enterpriseFeatureApprovals': 'Required approvals',
  'billing.planSummaryCard.enterpriseFeatureBulkTargeting': 'Bulk user targeting',
  'billing.planSummaryCard.enterpriseFeatureCustomRoles': 'Custom roles and permissions',
  'billing.planSummaryCard.enterpriseFeatureScheduling': 'Flag scheduling',
  'billing.planSummaryCard.enterpriseFeatureSso': 'SSO / SCIM',
  'billing.planSummaryCard.enterpriseIncludesHeading': 'Enterprise plans include:',
  'billing.planSummaryCard.getInTouch': 'Get in touch',
  'billing.planSummaryCard.getQuote': 'Get a quote',
  'billing.planSummaryCard.getStartedFree': 'Get started for free',
  'billing.planSummaryCard.intervalAnnually': 'annually',
  'billing.planSummaryCard.intervalChangeDeadline': 'You may change your billing interval until {time}',
  'billing.planSummaryCard.intervalMonthly': 'monthly',
  'billing.planSummaryCard.keepCurrentPlan': 'Keep current plan',
  'billing.planSummaryCard.legacyEnterpriseDescription':
    'Our enterprise plans are customizable to your business needs. Talk to one of our technical account executives to set up the perfect plan for your goals.',
  'billing.planSummaryCard.monthlyBillingRestrictedTooltip': 'Contact us to switch to monthly billing',
  'billing.planSummaryCard.noEntitlementsSelected': 'Choose at least one monthly entitlement to proceed',
  'billing.planSummaryCard.payAnnuallyInterval': 'Pay annually',
  'billing.planSummaryCard.payAsYouGoDescription': "Billed monthly for the previous calendar month's usage.",
  'billing.planSummaryCard.payAsYouGoHeading': 'Pay as you go',
  'billing.planSummaryCard.payAsYouGoInterval': 'Pay as you go',
  'billing.planSummaryCard.paymentMethodRequired': 'Payment method required in case of overage',
  'billing.planSummaryCard.perMonthInterval': '\u00A0/\u00A0month',
  'billing.planSummaryCard.perYearInterval': '\u00A0/\u00A0year',
  'billing.planSummaryCard.planDetailsHeading': 'Plan details',
  'billing.planSummaryCard.planTotalHeading': 'Plan total',
  'billing.planSummaryCard.priceError': 'There was a problem retrieving the price for this plan: {error}',
  'billing.planSummaryCard.pricePerMonth': '{price} / month',
  'billing.planSummaryCard.proceedToCheckout': 'Proceed to checkout',
  'billing.planSummaryCard.promoCodeNote': '*Any promo codes will be applied at checkout.',
  'billing.planSummaryCard.questions': 'Questions?',
  'billing.planSummaryCard.requestQuote': 'Request a quote',
  'billing.planSummaryCard.savingsTag': 'Save 17%',
  'billing.planSummaryCard.startFreeTrial': 'Start free trial',
  'billing.planSummaryCard.trialBillingStart': "Your billing period won't start until your trial ends on {date}.",
  'billing.planSummaryCard.trialEnds': 'Your {planName} ends {date}.',
  'billing.planSummaryCard.trialNotBilledUntil': "You won't be billed until your trial ends on {date}.",
  'billing.planSummaryCard.twoMonthsFreeNote': '(Get 2 months free)',
  'billing.planSwitcher.coreQuestion': 'Just want the core functionality?',
  'billing.planSwitcher.fewerQuestion': 'Need fewer features?',
  'billing.planSwitcher.getPlan': 'Get the {planName} plan',
  'billing.planSwitcher.learnMorePricing': 'To learn more about our new plans, read <link>our pricing page</link>',
  'billing.planSwitcher.oldPlanNotice':
    'You are on an old plan. Get the new <starterLink>{starter}</starterLink> or <professionalLink>{professional}</professionalLink> plan',
  'billing.planSwitcher.scaleQuestion': 'Need a plan that can scale with you?',
  'billing.planUpgrade.upgrade': 'Upgrade',
  'billing.planUpgrade.viewPlans': 'View plans',
  'billing.planUsageOverview.monthlyUsageResets': 'Monthly usage resets on the first of the month UTC',
  'billing.planUsageOverview.overview': 'Overview',
  'billing.planUsageOverview.planUsage': 'Plan usage',
  'billing.planUsageOverview.seats': 'Seats',
  'billing.planUsagePage.loadError': 'Error occurred while loading plan usage',
  'billing.planUsagePage.loadingLabel': 'Loading plan usage data',
  'billing.planUsagePage.overviewTab': 'Overview',
  'billing.prerequisitesUpsell.imageAlt':
    'A screenshot showing flag prerequisites being used in the LaunchDarkly interface.',
  'billing.prerequisitesUpsell.title': 'Create flag prerequisites',
  'billing.priceSummary.annualFooter': 'Built-in and additional pre-paid usage included.',
  'billing.priceSummary.monthlyPrice': 'Monthly price',
  'billing.priceSummary.overageFooter': 'Additional usage billed monthly at standard rates.',
  'billing.priceSummary.perYear': '{price}/yr',
  'billing.priceSummary.savings': 'Savings',
  'billing.priceSummary.total': 'Total',
  'billing.priceSummary.usageBased': 'Usage-based',
  'billing.priceSummary.yearlyPrice': 'Yearly price',
  'billing.pricingConcierge.bannerBody':
    'Chat with our AI pricing concierge to get a personalized cost estimate based on your architecture and usage patterns.',
  'billing.pricingConcierge.bannerHeading': 'Need help estimating costs?',
  'billing.pricingConcierge.getEstimate': 'Get pricing estimate',
  'billing.pricingConcierge.toggleLabel': 'Toggle Pricing Concierge',
  'billing.pricingDescription.perBlock': '{monthlyPrice}/{blockSize}{asterisk} {units}, per month',
  'billing.pricingDescription.perBlockWithFree':
    'First {freeQuantity} free, then {monthlyPrice}/{blockSize}{asterisk} {units}, per month',
  'billing.pricingDescription.perUnit': '{monthlyPrice} per {unit}, per month',
  'billing.pricingDescription.perUnitWithFree': 'First {freeQuantity} free, then {monthlyPrice} per {unit}, per month',
  'billing.pricingDetails.clientSideMauAnnualRate': '$8.33 per 1,000 Client-side MAU, per month',
  'billing.pricingDetails.clientSideMauHeading': 'Client-side <mau></mau>',
  'billing.pricingDetails.clientSideMauMonthlyRate': '$10 per 1,000 Client-side MAU, per month',
  'billing.pricingDetails.clientSideMauOverageRate': '*Overages billed at $10 per 1,000 Client-side MAU, per month',
  'billing.pricingDetails.experimentationKeysAnnualRate': '$27.75 per 10,000 experimentation keys, per month',
  'billing.pricingDetails.experimentationKeysHeading': 'Experimentation keys',
  'billing.pricingDetails.experimentationKeysMonthlyRate': '$33 per 10,000 experimentation keys, per month',
  'billing.pricingDetails.experimentationKeysOverageRate':
    '*Overages billed at $33 per 10,000 experimentation keys, per month',
  'billing.pricingDetails.experimentationMauHeading': 'Experimentation <mau></mau>',
  'billing.pricingDetails.experimentationMauOverageRate':
    '*Overages billed at ${price} per 1,000 experimentation MAUs, per month',
  'billing.pricingDetails.experimentationMauRate': '${price} per 1,000 experimentation MAUs, per month',
  'billing.pricingDetails.firstFreeThen': 'First {count} free, then {rate}',
  'billing.pricingDetails.firstFreeThenAnnual': 'First {count} free, then {rate} (billed annually)',
  'billing.pricingDetails.freeExperimentationMauIncluded': '{count} free experimentation MAU included, per month',
  'billing.pricingDetails.heading': 'Pricing and overage details',
  'billing.pricingDetails.serviceConnectionAnnualRate': '$10 per service connection, per month',
  'billing.pricingDetails.serviceConnectionMonthlyRate': '$12 per service connection, per month',
  'billing.pricingDetails.serviceConnectionOverageRate': '*Overages billed at $12 per service connection, per month',
  'billing.pricingDetails.serviceConnectionsHeading': 'Service connections',
  'billing.pricingTable.addUsageColumn': 'Add monthly usage',
  'billing.pricingTable.annualDiscountTooltip': '2 months discounted from your yearly cost',
  'billing.pricingTable.annualFormLabel': 'Customize annual plan',
  'billing.pricingTable.bulkPricingDisclaimer': '*Save more with higher usage.',
  'billing.pricingTable.costPerYearColumn': 'Cost/yr',
  'billing.pricingTable.decrementDisabledTooltip':
    'Reducing commits on an annual plan is not allowed before your next renewal.',
  'billing.pricingTable.decrementLabel': 'decrement',
  'billing.pricingTable.discountedRateEach': '<s>{rate}</s> {discountedRate} each',
  'billing.pricingTable.discountedRateLabeledEach': 'K8s, Ruby & Python rate: <s>{rate}</s> {discountedRate} each',
  'billing.pricingTable.discountedRateLabeledPerBlock':
    'K8s, Ruby & Python rate: <s>{rate}</s> {discountedRate}/{blockSize}',
  'billing.pricingTable.discountedRatePerBlock': '<s>{rate}</s> {discountedRate}/{blockSize}',
  'billing.pricingTable.includedColumn': 'Included',
  'billing.pricingTable.includedPerMonth': 'Included: {quantity}/mo',
  'billing.pricingTable.incrementLabel': 'increment {limitName}',
  'billing.pricingTable.monthlyTableLabel': 'Monthly pricing table',
  'billing.pricingTable.perMonth': '{quantity}/mo',
  'billing.pricingTable.perYear': '{cost}/yr',
  'billing.pricingTable.prepaidUsageColumn': 'Pre-paid monthly usage',
  'billing.pricingTable.productColumn': 'Product',
  'billing.pricingTable.rateEach': '{rate} each',
  'billing.pricingTable.rateInfoLabel': 'standard rate info',
  'billing.pricingTable.ratePerBlock': '{rate}/{blockSize}',
  'billing.pricingTable.standardRateColumn': 'Standard rate',
  'billing.pricingTable.standardRateEach': 'Standard rate: {rate} each',
  'billing.pricingTable.standardRatePerBlock': 'Standard rate: {rate}/{blockSize}',
  'billing.pricingTable.tooltipAnnualUpgrade':
    'Upgrade to an annual Foundation plan to add additional pre-paid usage with {discount}',
  'billing.pricingTable.tooltipIncludedUsage': 'Your plan includes built-in usage for each product',
  'billing.pricingTable.tooltipOverageBilled': 'Usage beyond that is billed monthly at standard rates',
  'billing.pricingTable.usageFieldLabel': '{limitName} monthly usage',
  'billing.pricingTable.usageInputLabel': '{limitName} value',
  'billing.promoCode.appliedStatus': 'Promo code {code} applied.',
  'billing.promoCode.apply': 'Apply',
  'billing.promoCode.expiredStatus': 'Expired promo code.',
  'billing.promoCode.invalidStatus': 'Invalid promo code.',
  'billing.relayProxyUpsell.title': 'Enterprise ready with Relay Proxy',
  'billing.requestSeatsBanner.body': 'In order to proceed, you must contact your sales rep to request more seats.',
  'billing.requestSeatsBanner.heading': 'Add seats to your Enterprise plan',
  'billing.requireApprovalsUpsell.title': 'Upgrade to access all approval settings',
  'billing.resourceHistoryUpsell.description': 'Your plan is limited to {days} days of events.',
  'billing.resourceHistoryUpsell.title': 'Upgrade to see the full history',
  'billing.retrialBanner.contactSales': 'Contact sales',
  'billing.retrialBanner.explorePlans': 'Explore plans',
  'billing.retrialBanner.extendTrial': 'Extend trial',
  'billing.retrialBanner.extendYourTrial': 'Extend your trial',
  'billing.retrialBanner.talkToOurTeam': 'Talk to our team',
  'billing.retrialBanner.title': 'Trial extension available',
  'billing.retrialConfirmModal.body':
    'This adds {days} more days to your trial. When it ends, your account returns to the free Developer plan and your work stays in place.',
  'billing.retrialConfirmModal.closeLabel': 'Close',
  'billing.retrialConfirmModal.confirm': 'Extend my trial',
  'billing.retrialConfirmModal.extendErrorToast':
    'LaunchDarkly could not extend your trial. Try again or <link>talk to our team</link>.',
  'billing.retrialConfirmModal.extendedToast': 'Your trial has been extended by {days} days.',
  'billing.retrialConfirmModal.notYet': 'Not yet',
  'billing.retrialConfirmModal.title': 'Extend your trial',
  'billing.retrialPopover.body': 'Add {days} more days to your trial with full access to everything in LaunchDarkly.',
  'billing.retrialPopover.heading': 'Extend your trial',
  'billing.retrialPopover.questions': 'Questions?',
  'billing.retrialPopover.talkToOurTeam': 'Talk to our team',
  'billing.review.billingContactCard': 'Billing contact',
  'billing.review.confirmError':
    'Failed to confirm subscription. Try again. If the problem persists, contact support@launchdarkly.com.',
  'billing.review.confirmSubscription': 'Confirm subscription',
  'billing.review.confirming': 'Confirming…',
  'billing.review.creditApplied': 'Your credit will be applied to future invoices.',
  'billing.review.creditBalance': 'Credit balance:',
  'billing.review.dueToday': 'Due today:',
  'billing.review.firstMonthProrated': 'Your first month is prorated',
  'billing.review.firstYearBilledOn': 'Your first prorated year will be billed on {date}.',
  'billing.review.firstYearProrated': 'Your first year is prorated',
  'billing.review.loadingAmountLabel': 'Loading amount',
  'billing.review.nextPayment': 'Next payment:',
  'billing.review.payNow': 'Pay now',
  'billing.review.paymentMethodCard': 'Payment method',
  'billing.review.renewsOn': 'Subscription renews on {date}',
  'billing.review.usageChangesEffective': 'Your usage changes will be billed and take effect on {date}.',
  'billing.sdkAppIdAlert.body':
    'Set <code>applicationId</code> in {sdkDescription} application info to identify {meterNoun} by SDK App ID. Otherwise they appear as <strong>Unknown</strong>.',
  'billing.sdkAppIdAlert.defaultSdkDescription': 'your SDK’s',
  'billing.sdkAppIdAlert.readMore': 'Read more about application configuration',
  'billing.sdkApplication.allConnectionsHeading': 'All SDK connections ({count})',
  'billing.sdkApplication.backToApplications': 'Back to Applications',
  'billing.sdkApplication.connectionTypeColumn': 'Connection type',
  'billing.sdkApplication.environment': 'Environment:',
  'billing.sdkApplication.environmentColumn': 'Environment',
  'billing.sdkApplication.filterPlaceholder': 'Filter by any field…',
  'billing.sdkApplication.latest': 'Latest:',
  'billing.sdkApplication.needsAttentionHeading': 'SDKs Needing Attention ({count})',
  'billing.sdkApplication.noSdkConnections': 'No SDK connections found',
  'billing.sdkApplication.project': 'Project:',
  'billing.sdkApplication.projectColumn': 'Project',
  'billing.sdkApplication.relayVersionColumn': 'Relay version',
  'billing.sdkApplication.sdkNameColumn': 'SDK name',
  'billing.sdkApplication.sdkTypeColumn': 'SDK type',
  'billing.sdkApplication.showingConnections': 'Showing {shown} of {total} connections',
  'billing.sdkApplication.statusColumn': 'Status',
  'billing.sdkApplication.statusLabel': 'Status',
  'billing.sdkApplication.tableLabel': 'SDK connections',
  'billing.sdkApplication.unknownApplication': 'Unknown Application',
  'billing.sdkApplication.version': 'Version:',
  'billing.sdkApplication.versionColumn': 'Version',
  'billing.sdkApplication.viewDocs': 'View docs',
  'billing.sdkApplication.viewLatestRelease': 'View latest release',
  'billing.sdkConnections.applicationColumn': 'Application',
  'billing.sdkConnections.connectedSdksColumn': 'Connected SDKs',
  'billing.sdkConnections.monitorSdkConnections': 'Monitor SDK connections across your applications',
  'billing.sdkConnections.noApplicationsFound': 'No applications found',
  'billing.sdkConnections.searchPlaceholder': 'Search applications…',
  'billing.sdkConnections.showingApplications': 'Showing {shown} of {total} applications',
  'billing.sdkConnections.statusColumn': 'Status',
  'billing.sdkConnections.statusLabel': 'Status',
  'billing.sdkConnections.tableLabel': 'SDK connections',
  'billing.sdkConnections.title': 'SDK connections',
  'billing.seatLimitBanner.body': 'Your plan is limited to {count} members. To proceed, upgrade your plan.',
  'billing.seatLimitBanner.heading': 'No seats remaining',
  'billing.seatLimitBanner.reviewBilling': 'Review billing',
  'billing.serviceConnectionAttestation.backendRuntimeLabel': 'Where does your backend run?',
  'billing.serviceConnectionAttestation.checkboxLabel':
    'My SDKs run in a containerized or multi-process environment — Kubernetes, ECS, Nomad, Ruby, or Python.',
  'billing.serviceConnectionAttestation.description':
    'These architectures open many parallel SDK connections, so we bill them at a lower rate. Your plan includes {count, number} service connections. Every connection above that bills at {rate, number, percent} off the standard rate.',
  'billing.serviceConnectionAttestation.helperText': 'Answer both questions to apply the reduced rate.',
  'billing.serviceConnectionAttestation.nodeCountLabel': 'Roughly how many nodes?',
  'billing.serviceConnectionAttestation.nodes10To50': '10-50',
  'billing.serviceConnectionAttestation.nodes200Plus': '200+',
  'billing.serviceConnectionAttestation.nodes50To200': '50-200',
  'billing.serviceConnectionAttestation.nodesUnder10': 'Under 10',
  'billing.serviceConnectionAttestation.runtimeAks': 'AKS (Azure)',
  'billing.serviceConnectionAttestation.runtimeEcsFargate': 'ECS or Fargate',
  'billing.serviceConnectionAttestation.runtimeEks': 'EKS (Amazon)',
  'billing.serviceConnectionAttestation.runtimeGke': 'GKE (Google)',
  'billing.serviceConnectionAttestation.runtimeNomad': 'Nomad',
  'billing.serviceConnectionAttestation.runtimeOpenshift': 'OpenShift',
  'billing.serviceConnectionAttestation.runtimeSelfManagedK8s': 'Self-managed Kubernetes',
  'billing.serviceConnectionAttestation.runtimeVmsBareMetal': 'VMs or bare metal',
  'billing.serviceConnectionAttestation.selectPlaceholder': 'Select…',
  'billing.serviceConnectionAttestation.title': 'Running on Kubernetes, Ruby, or Python?',
  'billing.serviceConnectionAttestation.trueUpTerms':
    'LaunchDarkly may verify deployment characteristics from SDK connection metadata and re-bill misrepresented usage at standard rates.',
  'billing.ssoUpsell.title': 'Add SSO to your plan',
  'billing.status.loading': 'Loading…',
  'billing.status.loadingDots': 'Loading…',
  'billing.status.unknownError': 'Unknown error',
  'billing.subscriptionTerms.body':
    "By selecting this checkbox, you agree to LaunchDarkly's subscription terms, privacy policy, and an automatically recurring billing subscription with LaunchDarkly until you cancel. You can cancel at any time to avoid future charges by contacting <link>LaunchDarkly Support</link>.",
  'billing.subscriptionTerms.close': 'Close',
  'billing.subscriptionTerms.closeLabel': 'close',
  'billing.subscriptionTerms.heading': 'Subscription terms, including automatic renewals',
  'billing.subscriptionTerms.legacyBody':
    "By selecting this checkbox, you agree to LaunchDarkly's Subscription Terms, Privacy Policy, and an automatically recurring billing subscription with LaunchDarkly until you cancel. You can cancel at any time to avoid future charges by <link>contacting Support</link>.",
  'billing.subscriptionTerms.readSubscriptionTerms': 'Read subscription terms',
  'billing.teamsUpsell.imageAlt': 'An icon representing three members of a team',
  'billing.teamsUpsell.title': 'Adopt and manage LaunchDarkly more easily with teams',
  'billing.terms.agreeToOverageAuthorization':
    'I understand and agree to the <link>automatic overage charge authorization</link>.',
  'billing.terms.agreeToSubscriptionTerms': 'I agree to the <link>subscription terms</link>.',
  'billing.terms.agreeToSubscriptionTermsWithRenewals':
    'I agree to the <link>subscription terms</link>, including automatic renewals.',
  'billing.terms.instructions': 'To complete your purchase you must accept the following:',
  'billing.terms.overageAuthorizationLink': 'automatic overage charge authorization',
  'billing.terms.readFullTerms': 'Read full terms',
  'billing.terms.subscriptionTermsLink': 'subscription terms',
  'billing.trialBanner.contactSales': 'Contact sales',
  'billing.trialBanner.daysLeft': '{count, plural, one {# day} other {# days}} left',
  'billing.trialBanner.dismissLabel': 'Dismiss banner',
  'billing.trialBanner.explorePlans': 'Explore plans',
  'billing.trialBanner.paymentFailedTitle': "We couldn't process your last payment",
  'billing.trialBanner.subscriptionCanceled': 'Subscription canceled',
  'billing.trialBanner.title': 'LaunchDarkly trial',
  'billing.trialBanner.trialExpired': 'Trial expired',
  'billing.trialBanner.updatePaymentMethod': 'Update payment method',
  'billing.trialExtensionModal.closeLabel': 'Close',
  'billing.trialExtensionModal.errorAccountDisabled': 'Your account is not active, so your trial cannot be extended.',
  'billing.trialExtensionModal.errorAlreadyUsed': 'You already used your free trial extension.',
  'billing.trialExtensionModal.errorDefault': "Couldn't extend your trial. Try again or talk to our team.",
  'billing.trialExtensionModal.errorExtensionDisabled': 'Trial extensions are not available right now.',
  'billing.trialExtensionModal.errorNotEligibleAccount': 'Trial extensions are not available for your account.',
  'billing.trialExtensionModal.errorTrialActive': 'You already have an active trial.',
  'billing.trialExtensionModal.errorTrialExpired': 'Your trial has ended, so it can no longer be extended.',
  'billing.trialExtensionModal.errorWrongPlan': 'Trial extensions are not available for your current plan.',
  'billing.trialExtensionModal.extend': 'Extend',
  'billing.trialExtensionModal.extendedToast': 'Your trial was extended by {days} days.',
  'billing.trialExtensionModal.extendsThrough':
    'This extends your trial one time, adding {days} days of full access through <strong>{endDate}.</strong>',
  'billing.trialExtensionModal.movesToDeveloperPlan':
    'When your trial ends, your account moves to the free <link>Developer plan</link> and your work stays in place.',
  'billing.trialExtensionModal.notYet': 'Not yet',
  'billing.trialExtensionModal.title': 'Extend your trial',
  'billing.trialPopover.extendOffer': 'Extend your trial. Adds {days} days, no card required.',
  'billing.trialPopover.extendTrial': 'Extend trial',
  'billing.trialPopover.extensionUsed': 'Extension used.',
  'billing.trialPopover.extensionUsedWithDays': 'Extension used. Your trial was extended by {days} days.',
  'billing.trialPopover.needMoreTime': 'Need more time?',
  'billing.trialPopover.progressLabel': 'Trial progress',
  'billing.trialPopover.questions': 'Questions?',
  'billing.trialPopover.talkToOurTeam': 'Talk to our team',
  'billing.trialPopover.trialEnds': 'Trial ends {date}',
  'billing.trialStatus.daysLeft': '{count, plural, one {# day} other {# days}} left',
  'billing.trialStatus.daysLeftInEnterpriseTrial':
    '{count, plural, one {# day} other {# days}} left in Enterprise trial',
  'billing.trialStatus.daysLeftInGuardianTrial': '{count, plural, one {# day} other {# days}} left in Guardian trial',
  'billing.trialStatus.daysLeftInTrial': '{count, plural, one {# day} other {# days}} left in trial',
  'billing.trialStatus.explorePlans': 'Explore plans',
  'billing.trialStatus.paymentsDelayed': 'Account payments are delayed',
  'billing.trialStatus.planCanceled': 'Plan canceled',
  'billing.trialStatus.trialExpired': 'Trial expired',
  'billing.trialStatus.updatePaymentMethod': 'Update payment method',
  'billing.unlimitedProjectsUpsell.imageAlt': 'A screenshot showing multiple projects.',
  'billing.unlimitedProjectsUpsell.title': 'Get unlimited projects',
  'billing.unpaidInvoices.downloadLabel': 'Download invoice PDF',
  'billing.unpaidInvoices.dueDate': 'Due Date: {date}',
  'billing.unpaidInvoices.loadError': 'Error loading unpaid invoices: {error}.',
  'billing.unpaidInvoices.payNow': 'Pay now',
  'billing.unpaidInvoices.title': 'Unpaid invoices',
  'billing.upsellModal.closeLabel': 'Close',
  'billing.upsellTooltip.body': 'Upgrade your plan to use this feature.',
  'billing.upsellTooltip.cta': 'Click to learn more',
  'billing.usageBreakdown.anonymous': 'Anonymous',
  'billing.usageBreakdown.errorLoadingDimension': 'Error loading dimension data',
  'billing.usageBreakdown.invalidType': 'Invalid type',
  'billing.usageBreakdown.loadingLabel': 'Loading grouped usage data…',
  'billing.usageBreakdown.noDimensionMatches': 'No charts matching selected dimension.',
  'billing.usageBreakdown.noSearchMatches': 'No charts matching your search criteria.',
  'billing.usageBreakdown.notAnonymous': 'Not Anonymous',
  'billing.usageBreakdown.unknown': 'Unknown',
  'billing.usageBreakdown.unknownSdk': 'Unknown SDK',
  'billing.usageBreakdown.unknownVersion': 'Unknown version',
  'billing.usageCapBanner.approachingLimit': 'Approaching monthly limit',
  'billing.usageCapBanner.dismissLabel': 'Dismiss banner',
  'billing.usageCapBanner.forLimit': ' for {limitName}',
  'billing.usageCapBanner.limitReached': 'Monthly limit reached',
  'billing.usageCapBanner.ofLimit': ' of {limit} {limitName}',
  'billing.usageCapBanner.reviewUsage': 'Review usage',
  'billing.usageCapBanner.upgradePlan': 'Upgrade plan',
  'billing.usageCards.errorLoadingUsage': 'Error loading usage data',
  'billing.usageCards.limit': 'Limit: {limit}',
  'billing.usageCards.remainingThisMonth': 'Remaining this month',
  'billing.usageCards.unlimited': 'Unlimited',
  'billing.usageCards.viewDetails': 'View details',
  'billing.usageControls.aggregationLabel': 'Usage aggregation type',
  'billing.usageControls.allDimensions': 'All dimensions',
  'billing.usageControls.average': 'Average',
  'billing.usageControls.averageHourlyValue': 'Average hourly value',
  'billing.usageControls.countingResetsMonthly': 'Counting resets monthly',
  'billing.usageControls.cumulative': 'Cumulative',
  'billing.usageControls.dimensionLabel': 'Usage dimension',
  'billing.usageControls.dimensionPlaceholder': 'Dimension',
  'billing.usageControls.incremental': 'Incremental',
  'billing.usageControls.monthLabel': 'Usage month',
  'billing.usageControls.monthPlaceholder': 'Month',
  'billing.usageControls.monthToDate': 'Month-to-date',
  'billing.usageControls.mtd': 'MTD',
  'billing.usageControls.rolling30d': 'Rolling 30D',
  'billing.usageControls.rollingSumTooltip': '30-day rolling sum, reported daily',
  'billing.usageControls.rollingUniqueTooltip': '30-day rolling unique count, reported daily',
  'billing.usageControls.thirtyDays': '30D',
  'billing.usageControls.threeDays': '3D',
  'billing.usageControls.threeMonths': '3M',
  'billing.usageControls.timeRangeLabel': 'Usage time range',
  'billing.usageControls.uniqueCountingResets': 'Unique counting resets monthly',
  'billing.usageDetail.clearSearchLabel': 'clear search',
  'billing.usageDetail.searchLabel': 'Search…',
  'billing.usageDetail.searchPlaceholder': 'Search…',
  'billing.usageDetail.totalUsage': 'Total Usage',
  'billing.usageLimitBanner.accountReadOnly': 'Account is read-only',
  'billing.usageLimitBanner.approachingLimit': 'Approaching monthly limit',
  'billing.usageLimitBanner.codeControlGracePeriod': 'CodeControl grace period',
  'billing.usageLimitBanner.codeControlReadOnly': 'CodeControl is read-only',
  'billing.usageLimitBanner.dismissLabel': 'Dismiss banner',
  'billing.usageLimitBanner.forLimit': ' for {limitName}',
  'billing.usageLimitBanner.forMultipleResources': ' for multiple resources',
  'billing.usageLimitBanner.gracePeriod': 'Grace period',
  'billing.usageLimitBanner.gracePeriodContinues':
    '. {gracePeriodLabel} continues for <strong>{days, plural, one {# more day} other {# more days}}</strong>',
  'billing.usageLimitBanner.gracePeriodExpiresToday': '. {gracePeriodLabel} <strong>expires today</strong>',
  'billing.usageLimitBanner.limitReached': 'Monthly limit reached',
  'billing.usageLimitBanner.multipleLimitsReached': ' — multiple usage limits reached (resets next month)',
  'billing.usageLimitBanner.ofLimit': ' of {limit} {limitName}',
  'billing.usageLimitBanner.reviewUsage': 'Review usage',
  'billing.usageLimitBanner.severalNearingCapacity': ' — several resources nearing capacity',
  'billing.usageLimitBanner.singleLimitReached': ' — monthly usage limit reached (resets next month)',
  'billing.usageLimitBanner.upgradePlan': 'Upgrade plan',
  'billing.usageLimitBanner.upgradeToUnlock': 'Upgrade to unlock',
  'billing.usagePage.loadError': 'Error occurred while loading usage data',
  'billing.usagePage.loadingLabel': 'Loading usage data',
  'billing.usageSummary.asOfDate': 'Monthly accumulated usage as of {date}',
  'billing.usageSummary.asOfToday': 'Monthly accumulated usage as of today',
  'billing.usageSummary.breakdownAsOfDate': 'Total & top 5 breakdown of monthly accumulated usage as of {date}',
  'billing.usageSummary.breakdownAsOfToday': 'Total & top 5 breakdown of monthly accumulated usage as of today',
  'billing.usageSummary.errorLoadingDimension': 'Error loading dimension totals',
  'billing.usageSummary.errorLoadingSummary': 'Error loading summary data',
  'billing.usageSummary.loadingLabel': 'Loading summary data',
  'billing.usageSummary.percentChangeTooltip': 'Change relative to same date in the previous month',
  'billing.usageSummary.total': 'Total',
  'billing.usageTabs.more': 'More ({count})',
  'billing.usageTabs.moreMenuLabel': 'More tabs',
  'billing.usageTabs.moreTabsLabel': 'More tabs ({count})',
  'billing.workflowsUpsell.imageAlt': 'A screenshot showing this feature being used in the LaunchDarkly interface.',
  'billing.yourPlanDetails.contextsRow': 'Contexts',
  'billing.yourPlanDetails.experimentationEventsRow': 'Experimentation events',
  'billing.yourPlanDetails.experimentationKeysRow': 'Experimentation keys',
  'billing.yourPlanDetails.heading': 'Your plan details',
  'billing.yourPlanDetails.planNameLegacyProfessional': 'Legacy professional',
  'billing.yourPlanDetails.planNameProfessional': 'Professional',
  'billing.yourPlanDetails.planNameStarter': 'Starter',
  'billing.yourPlanDetails.planTypeRow': 'Plan Type',
  'billing.yourPlanDetails.seatsRow': 'Number of seats',
  'billing.yourPlanDetails.ssoAdded': 'Added',
  'billing.yourPlanDetails.ssoNotAdded': 'Not Added',
  'billing.yourPlanDetails.ssoRow': 'SSO',
  'changeHistory.actionFilter.allActions': 'All actions',
  'changeHistory.actionFilter.multipleActions': 'Multiple actions',
  'changeHistory.dateFilter.calendarLabel': 'calendar',
  'changeHistory.dateFilter.mostRecent': 'Most recent',
  'changeHistory.dateFilter.nextLabel': 'next',
  'changeHistory.dateFilter.presetLast12Months': 'Last 12 months',
  'changeHistory.dateFilter.presetLast3Months': 'Last 3 months',
  'changeHistory.dateFilter.presetLast6Months': 'Last 6 months',
  'changeHistory.dateFilter.presetLastMonth': 'Last month',
  'changeHistory.dateFilter.presetLastWeek': 'Last week',
  'changeHistory.dateFilter.presetMostRecent': 'Most recent',
  'changeHistory.dateFilter.presetToday': 'Today',
  'changeHistory.dateFilter.presetYesterday': 'Yesterday',
  'changeHistory.dateFilter.previousLabel': 'previous',
  'changeHistory.dateFilter.rangeLabel': 'Date range',
  'changeHistory.dateFilter.showLabel': 'Show',
  'changeHistory.dialog.addResource': 'Add resource',
  'changeHistory.dialog.closeLabel': 'close',
  'changeHistory.dialog.collapseAllDetails': 'Collapse all details',
  'changeHistory.dialog.copyLinkLabel': 'Copy history link',
  'changeHistory.dialog.copyLinkTooltip': 'Copy activity link',
  'changeHistory.dialog.copyPolicyStatements': 'Copy policy statements',
  'changeHistory.dialog.deleteFilterLabel': 'Delete resource filter',
  'changeHistory.dialog.expandAllDetails': 'Expand all details',
  'changeHistory.dialog.filterByResource': 'Filter by resource',
  'changeHistory.dialog.hideServiceTokenActivity': 'Hide service token activity',
  'changeHistory.dialog.searchPlaceholder': 'Search changes',
  'changeHistory.dialog.title': 'Change history',
  'changeHistory.entry.agentAvatarLabel': 'Avatar for {name}',
  'changeHistory.entry.agentFallbackName': 'Agent',
  'changeHistory.entry.changesLabel': 'Changes:',
  'changeHistory.entry.coAuthors': '<member>{memberName}</member> and {agentName}',
  'changeHistory.entry.comparingVersions': 'Comparing version {version} to version {currentVersion}',
  'changeHistory.entry.copyLinkLabel': 'Copy history link',
  'changeHistory.entry.copyLinkTooltip': 'Copy entry details link',
  'changeHistory.entry.hideDetails': 'Hide details',
  'changeHistory.entry.inParent': 'in <parent></parent>',
  'changeHistory.entry.integrationLogoAlt': 'A logo for this integration',
  'changeHistory.entry.relatedConfig': 'which updated the config <link>{configKey}</link>',
  'changeHistory.entry.relatedExperiment': 'due to updates to the experiment <link>{experimentKey}</link>',
  'changeHistory.entry.relatedFlag': 'which updated the flag <link>{flagKey}</link>',
  'changeHistory.entry.resourceFallback': 'Resource',
  'changeHistory.entry.serviceTokenActor': 'A service token',
  'changeHistory.entry.someoneActor': 'Someone',
  'changeHistory.entry.truncated': 'Some of the output was truncated',
  'changeHistory.entry.truncatedSeeDiff': 'Some of the output was truncated, check diff for full changes',
  'changeHistory.entry.usingToken': 'Using the token "{tokenName}" (…{tokenEnding})',
  'changeHistory.entry.viaApp': 'Via the app <link>{appName}</link>',
  'changeHistory.entry.viaScim': 'Via SCIM ({appName})',
  'changeHistory.entry.viewDetails': 'View details',
  'changeHistory.entry.viewFullDetailsLabel': 'View full change details',
  'changeHistory.entryDetails.authenticatedUsingToken': 'Authenticated using the token "{tokenName}" (…{tokenEnding})',
  'changeHistory.entryDetails.closeLabel': 'close',
  'changeHistory.entryDetails.currentVersion': 'Current version',
  'changeHistory.entryDetails.merge': 'Merge',
  'changeHistory.entryDetails.patch': 'Patch',
  'changeHistory.entryDetails.previousVersion': 'Previous version',
  'changeHistory.entryDetails.projectFallback': 'Project',
  'changeHistory.entryDetails.splitView': 'Split view',
  'changeHistory.entryDetails.stageThisVersion': 'Stage this version',
  'changeHistory.entryDetails.triggerBody': 'Trigger body',
  'changeHistory.entryDetails.unifiedView': 'Unified view',
  'changeHistory.entryList.detailsLink': 'Details',
  'changeHistory.entryList.loadMore': 'Load more',
  'changeHistory.entryList.noEntriesEmptyState': 'There are no entries available.',
  'changeHistory.entryList.noMatchesEmptyState': 'No entries match. Choose different dates and try again.',
  'changeHistory.entryList.searchPlaceholder': 'Search your audit log',
  'changeHistory.entryNotFound.body': "Sorry, we can't find the entry you're looking for.",
  'changeHistory.entryNotFound.heading': 'Entry not found',
  'changeHistory.entryViewer.changes': 'Changes:',
  'changeHistory.entryViewer.currentVersion': 'Current version:',
  'changeHistory.entryViewer.diffUnavailable': 'We couldn’t generate a diff for this update.',
  'changeHistory.entryViewer.lastVersion': 'Last version:',
  'changeHistory.entryViewer.tokenAuth': 'Authenticated using the token "{name}" (…{ending})',
  'changeHistory.invalidFilter.removeFilter': 'Remove filter',
  'changeHistory.invalidFilter.somethingWentWrong': 'Something went wrong',
  'changeHistory.list.changeEntry': 'Change entry',
  'changeHistory.list.emptyBody': 'Try adjusting your filters',
  'changeHistory.list.emptyHeading': 'No change history entries found',
  'changeHistory.list.entriesForDateLabel': 'Entries for {date}',
  'changeHistory.list.expandEntryLabel': 'expand entry',
  'changeHistory.list.loadNewer': 'Load Newer',
  'changeHistory.list.loadingMore': 'Loading more…',
  'changeHistory.list.organizationFallback': 'org',
  'changeHistory.list.projectFallback': 'Project',
  'changeHistory.list.viewEntryDetails': 'View entry details',
  'changeHistory.resourceFilter.actionCount': '{count} actions',
  'changeHistory.resourceFilter.actions': 'Actions',
  'changeHistory.resourceFilter.alert': 'Alert',
  'changeHistory.resourceFilter.all': 'All',
  'changeHistory.resourceFilter.config': 'Config',
  'changeHistory.resourceFilter.dashboard': 'Dashboard',
  'changeHistory.resourceFilter.dataSource': 'Data source',
  'changeHistory.resourceFilter.env': 'Env',
  'changeHistory.resourceFilter.environment': 'Environment',
  'changeHistory.resourceFilter.environmentCount': '{count} envs',
  'changeHistory.resourceFilter.flag': 'Flag',
  'changeHistory.resourceFilter.kindPrefix': 'Resource:',
  'changeHistory.resourceFilter.member': 'Member',
  'changeHistory.resourceFilter.metric': 'Metric',
  'changeHistory.resourceFilter.metricGroup': 'Metric group',
  'changeHistory.resourceFilter.optimization': 'Optimization',
  'changeHistory.resourceFilter.project': 'Project',
  'changeHistory.resourceFilter.segment': 'Segment',
  'changeHistory.resourceFilter.team': 'Team',
  'changeHistory.resourceFilter.view': 'View',
  'changeHistory.resourceHistory.loadError': 'An unexpected error occurred.',
  'changeHistory.resourceHistory.searchPlaceholder': 'Search history',
  'changeHistory.resourcePicker.actionsLabel': 'Actions',
  'changeHistory.resourcePicker.alertsLabel': 'Alerts',
  'changeHistory.resourcePicker.allActionsGroup': 'Actions',
  'changeHistory.resourcePicker.allOption': 'All',
  'changeHistory.resourcePicker.chooseAction': 'Choose action',
  'changeHistory.resourcePicker.chooseAlert': 'Choose alert',
  'changeHistory.resourcePicker.chooseConfig': 'Choose config',
  'changeHistory.resourcePicker.chooseDashboard': 'Choose dashboard',
  'changeHistory.resourcePicker.chooseDataSource': 'Choose data source',
  'changeHistory.resourcePicker.chooseEnvironment': 'Choose environment',
  'changeHistory.resourcePicker.chooseFlag': 'Choose flag',
  'changeHistory.resourcePicker.chooseHoldout': 'Choose holdout',
  'changeHistory.resourcePicker.chooseMember': 'Choose member',
  'changeHistory.resourcePicker.chooseMetric': 'Choose metric',
  'changeHistory.resourcePicker.chooseMetricGroup': 'Choose metric group',
  'changeHistory.resourcePicker.chooseMultiArmedBandit': 'Choose multi-armed bandit',
  'changeHistory.resourcePicker.chooseOptimization': 'Choose optimization',
  'changeHistory.resourcePicker.chooseProject': 'Choose project',
  'changeHistory.resourcePicker.chooseSegment': 'Choose segment',
  'changeHistory.resourcePicker.chooseTeam': 'Choose team',
  'changeHistory.resourcePicker.chooseView': 'Choose view',
  'changeHistory.resourcePicker.collaborationChangesGroup': 'Collaboration changes',
  'changeHistory.resourcePicker.configsLabel': 'Configs',
  'changeHistory.resourcePicker.dashboardsLabel': 'Observability Dashboards',
  'changeHistory.resourcePicker.dataSourcesLabel': 'Data sources',
  'changeHistory.resourcePicker.deselectAll': 'Deselect all',
  'changeHistory.resourcePicker.environmentSettingsChangesGroup': 'Environment settings changes',
  'changeHistory.resourcePicker.environmentsLabel': 'Environments',
  'changeHistory.resourcePicker.filterByMember': 'Filter by member',
  'changeHistory.resourcePicker.flagSettingsChangesGroup': 'Flag settings changes',
  'changeHistory.resourcePicker.flagTargetingChangesGroup': 'Flag targeting changes',
  'changeHistory.resourcePicker.flagsLabel': 'Flags',
  'changeHistory.resourcePicker.holdoutsLabel': 'Holdouts',
  'changeHistory.resourcePicker.infoTitle': 'Info',
  'changeHistory.resourcePicker.membersLabel': 'Members',
  'changeHistory.resourcePicker.metricGroupsLabel': 'Metric groups',
  'changeHistory.resourcePicker.metricsLabel': 'Metrics',
  'changeHistory.resourcePicker.multiArmedBanditsLabel': 'Multi-armed bandits',
  'changeHistory.resourcePicker.optimizationsLabel': 'Optimizations',
  'changeHistory.resourcePicker.otherActionsGroup': 'Other actions',
  'changeHistory.resourcePicker.pickEnvironmentFirst': 'You must pick an environment first.',
  'changeHistory.resourcePicker.pickProjectFirst': 'You must pick a project first.',
  'changeHistory.resourcePicker.projectsLabel': 'Projects',
  'changeHistory.resourcePicker.segmentsLabel': 'Segments',
  'changeHistory.resourcePicker.selectAll': 'Select all',
  'changeHistory.resourcePicker.selectAllActions': 'Select all actions',
  'changeHistory.resourcePicker.teamsLabel': 'Teams',
  'changeHistory.resourcePicker.viewsLabel': 'Views',
  'changeHistory.status.loading': 'Loading…',
  'changeTour.coachmark.back': 'Back',
  'changeTour.coachmark.closeLabel': 'Close tour',
  'changeTour.coachmark.finish': 'Finish',
  'changeTour.coachmark.next': 'Next',
  'chrome.accountNavigation.academy': 'Academy',
  'chrome.accountNavigation.account': 'Account',
  'chrome.accountNavigation.apiReferences': 'API references',
  'chrome.accountNavigation.askAQuestion': 'Ask a question',
  'chrome.accountNavigation.backToProject': 'Back to project',
  'chrome.accountNavigation.changelog': 'Changelog',
  'chrome.accountNavigation.connections': 'Connections',
  'chrome.accountNavigation.contactSupport': 'Contact support',
  'chrome.accountNavigation.documentation': 'Documentation',
  'chrome.accountNavigation.guides': 'Guides',
  'chrome.accountNavigation.help': 'Help',
  'chrome.accountNavigation.helpAndSupport': 'Help and support',
  'chrome.accountNavigation.installMcp': 'Install MCP',
  'chrome.accountNavigation.integrations': 'Integrations',
  'chrome.accountNavigation.keyboardShortcuts': 'Keyboard shortcuts',
  'chrome.accountNavigation.notifications': 'Notifications',
  'chrome.accountNavigation.organizationSettings': 'Organization settings',
  'chrome.accountNavigation.productDocs': 'Product docs',
  'chrome.accountNavigation.reportAnIssue': 'Report an issue',
  'chrome.accountNavigation.sdkKeys': 'SDK keys',
  'chrome.accountNavigation.settings': 'Settings',
  'chrome.accountNavigation.shareFeedback': 'Share feedback',
  'chrome.accountNavigation.support': 'Support',
  'chrome.agentControlPromo.cta': 'Try AgentControl',
  'chrome.agentControlPromo.dismissLabel': 'Dismiss banner',
  'chrome.agentControlPromo.heading': 'You control your flags. Now control your AI.',
  'chrome.agentHelp.contactSupport': 'Contact support',
  'chrome.agentHelp.discord': 'Join our Discord',
  'chrome.agentHelp.productDocs': 'Product docs',
  'chrome.agentHelp.trigger': 'Get help',
  'chrome.apiDeprecationPromo.body':
    'Your account still uses REST API versions that retire on Dec 31. Migrate to REST API version 20240415 to ensure uninterrupted API access.',
  'chrome.apiDeprecationPromo.cta': 'Read the API migration guide',
  'chrome.apiDeprecationPromo.dismissLabel': 'Dismiss banner',
  'chrome.apiDeprecationPromo.heading': 'Deprecated REST API versions retire on Dec 31',
  'chrome.changeHistory.showLabel': 'Show change history',
  'chrome.createMenu.agentConfig': 'Agent config',
  'chrome.createMenu.agentConfigDescription': 'Configure an agent or model.',
  'chrome.createMenu.chart': 'Chart',
  'chrome.createMenu.chartDescription': 'Chart how people use your product.',
  'chrome.createMenu.config': 'Config',
  'chrome.createMenu.contextKind': 'Context kind',
  'chrome.createMenu.contextKindDescription': 'Add a new type of context.',
  'chrome.createMenu.create': 'Create',
  'chrome.createMenu.experiment': 'Experiment',
  'chrome.createMenu.experimentDescription': 'A/B test against a metric.',
  'chrome.createMenu.flag': 'Flag',
  'chrome.createMenu.flagDescription': 'Roll out a feature safely.',
  'chrome.createMenu.label': 'Create a resource',
  'chrome.createMenu.metric': 'Metric',
  'chrome.createMenu.metricDescription': 'Track what a change affects.',
  'chrome.createMenu.multiArmedBandit': 'Multi-armed Bandit',
  'chrome.createMenu.releasePlan': 'Release plan',
  'chrome.createMenu.releasePlanDescription': 'Move flags through each stage.',
  'chrome.createMenu.segment': 'Segment',
  'chrome.createMenu.segmentDescription': 'Group contexts for targeting.',
  'chrome.createMenu.upgradeToAdd': 'Upgrade to add more',
  'chrome.createMenu.view': 'View',
  'chrome.createMenu.viewDescription': 'Organize flags by team.',
  'chrome.description.readLess': 'Read less',
  'chrome.description.readMore': 'Read more',
  'chrome.environmentActions.addToFavorites': 'Add to favorites',
  'chrome.environmentActions.clientSideId': 'Client-side ID',
  'chrome.environmentActions.copyMenu': 'Copy…',
  'chrome.environmentActions.editEnvironment': 'Edit environment',
  'chrome.environmentActions.environmentKey': 'Environment key',
  'chrome.environmentActions.mobileKey': 'Mobile key',
  'chrome.environmentActions.removeFromFavorites': 'Remove from favorites',
  'chrome.environmentActions.sdkKey': 'SDK key',
  'chrome.environmentActions.viewSdkKeys': 'View SDK keys',
  'chrome.environmentSelector.close': 'Close environment',
  'chrome.environmentSelector.closeNamed': 'Close environment {environmentName}',
  'chrome.environmentSelector.label': 'Selected environments',
  'chrome.environmentSelector.loadError': 'There was a problem fetching the environments. Try again later.',
  'chrome.environmentSelector.moreActions': 'More environment actions',
  'chrome.environmentSelector.moreActionsFor': 'More environment actions for {environmentName}',
  'chrome.environmentSelector.searchPlaceholder': 'Search environments',
  'chrome.environmentSelector.selectLabel': 'Select environment',
  'chrome.environmentSelector.swap': 'Swap environment',
  'chrome.environmentSelector.swapNamed': 'Swap environment {environmentName}',
  'chrome.environmentSelector.viewAnother': 'View another environment',
  'chrome.factoryBetaPromo.cta': 'Get early access',
  'chrome.factoryBetaPromo.dismissLabel': 'Dismiss banner',
  'chrome.factoryBetaPromo.heading': 'LaunchDarkly for the AI software factory',
  'chrome.featureBadge.beta': 'Beta',
  'chrome.featureBadge.earlyAccess': 'Early access',
  'chrome.featureBadge.internal': 'Internal',
  'chrome.featureBadge.label': 'Feature status',
  'chrome.featureBadge.updated': 'Updated',
  'chrome.featurePreviewOptIn.badge': 'Preview',
  'chrome.featurePreviewOptIn.body': 'Switch to the new version',
  'chrome.featurePreviewOptIn.heading': 'Try new version',
  'chrome.featurePreviewOptOut.body': 'Switch back to the previous version',
  'chrome.featurePreviewOptOut.heading': 'Preview',
  'chrome.featurePreviewOptOut.triggerLabel': 'Switch back',
  'chrome.featurePreviews.closeLabel': 'Close dialog',
  'chrome.featurePreviews.copyLinkTooltip': 'Copy feature preview link',
  'chrome.featurePreviews.documentation': 'Documentation',
  'chrome.featurePreviews.emptyBody':
    'There are no feature previews to try right now. Check back later for new previews.',
  'chrome.featurePreviews.emptyHeading': 'No feature previews available',
  'chrome.featurePreviews.enableLabel': 'Enable feature',
  'chrome.featurePreviews.flagVariationsRewriteDescription': "Edit a flag's variations on the rebuilt variations page.",
  'chrome.featurePreviews.flagVariationsRewriteName': 'New variations page',
  'chrome.featurePreviews.giveFeedback': 'Give feedback',
  'chrome.featurePreviews.heading': 'Feature preview',
  'chrome.featurePreviews.newNavigationDescription': 'Move around LaunchDarkly with a redesigned navigation.',
  'chrome.featurePreviews.newNavigationName': 'New navigation',
  'chrome.featurePreviews.optionsLabel': 'Feature preview options',
  'chrome.featurePreviews.visualPreviewAlt': 'Visual preview for {name}',
  'chrome.frontendVersion.clearOverride': 'Clear version override',
  'chrome.frontendVersion.invalidBody': 'A valid version is at least 9 characters long',
  'chrome.frontendVersion.invalidHeading': 'Invalid version:',
  'chrome.frontendVersion.invalidVersionLabel': 'Invalid version {version}',
  'chrome.frontendVersion.unknownBody':
    "The backend didn't recognize this version. Most likely there's no manifest in S3 for this version (yet). Check for typos.",
  'chrome.frontendVersion.unknownHeading': 'Unknown version:',
  'chrome.frontendVersion.unknownVersionLabel': 'Unknown version {version}',
  'chrome.frontendVersion.viewingVersion': 'Viewing FE version: <value>{version}</value>',
  'chrome.galaxyPromo.body': "Join us at Galaxy 2025 on May 14 — see what's in store for you at our virtual event",
  'chrome.galaxyPromo.cta': 'Register now',
  'chrome.galaxyPromo.dismissLabel': 'Dismiss banner',
  'chrome.ghosting.careful': 'CAREFUL!',
  'chrome.ghosting.impersonating': "You're impersonating another user.",
  'chrome.ghosting.stop': 'Stop ghosting',
  'chrome.installSdk.agentManagement': 'Agent management',
  'chrome.installSdk.agentManagementDescription': 'Configs and insights',
  'chrome.installSdk.aiLabel': 'Install AI SDK',
  'chrome.installSdk.featureManagement': 'Feature management',
  'chrome.installSdk.featureManagementDescription': 'Flags, rollouts, and experiments',
  'chrome.installSdk.label': 'Install SDK',
  'chrome.language.current': 'Language: {language}',
  'chrome.language.menuLabel': 'Select a language',
  'chrome.navMode.agents': 'Agents',
  'chrome.navMode.code': 'Code',
  'chrome.navMode.label': 'Navigation mode',
  'chrome.notificationsInbox.manageNotificationSettings': 'Manage notification settings',
  'chrome.notificationsInbox.optedOutBody':
    "You have opted out of receiving notifications in your LaunchDarkly Inbox. <settings></settings> so that you don't miss any notifications.",
  'chrome.notificationsInbox.triggerLabel': 'Notifications',
  'chrome.notificationsInbox.updateYourSettings': 'Update your settings',
  'chrome.projectNavigation.agentManagement': 'Agent management',
  'chrome.projectNavigation.approvals': 'Approvals',
  'chrome.projectNavigation.audiences': 'Audiences',
  'chrome.projectNavigation.backFrom': 'Back from {section}',
  'chrome.projectNavigation.backToAllSections': 'Back to all sections',
  'chrome.projectNavigation.backToProject': 'Back to project',
  'chrome.projectNavigation.collapse': 'Collapse navigation',
  'chrome.projectNavigation.create': 'Create',
  'chrome.projectNavigation.earlyAccess': 'Early access',
  'chrome.projectNavigation.eventDebugger': 'Event debugger',
  'chrome.projectNavigation.expand': 'Expand navigation',
  'chrome.projectNavigation.experimentation': 'Experimentation',
  'chrome.projectNavigation.factory': 'Factory',
  'chrome.projectNavigation.featureManagement': 'Feature management',
  'chrome.projectNavigation.home': 'Home',
  'chrome.projectNavigation.label': 'Project navigation',
  'chrome.projectNavigation.launchDarklyHome': 'LaunchDarkly home',
  'chrome.projectNavigation.measurement': 'Measurement',
  'chrome.projectNavigation.observability': 'Observability',
  'chrome.projectNavigation.primaryLabel': 'Primary navigation',
  'chrome.projectNavigation.releases': 'Releases',
  'chrome.projectNavigation.search': 'Search',
  'chrome.projectNavigation.searchOrJumpTo': 'Search or jump to…',
  'chrome.projectNavigation.searchShortcut': '{shortcut}',
  'chrome.projectNavigation.searchTooltip': 'Search {shortcut}',
  'chrome.projectNavigation.sectionsLabel': 'Navigation sections',
  'chrome.projectNavigation.vega': 'Vega',
  'chrome.projectNavigation.warehouseDataSources': 'Warehouse data sources',
  'chrome.projectSettingsNavigation.approvalSettings': 'Approval settings',
  'chrome.projectSettingsNavigation.contextKinds': 'Context kinds',
  'chrome.projectSettingsNavigation.environments': 'Environments',
  'chrome.projectSettingsNavigation.flagSettings': 'Flag settings',
  'chrome.projectSettingsNavigation.general': 'General',
  'chrome.projectSettingsNavigation.lifecycleSettings': 'Lifecycle settings',
  'chrome.projectSettingsNavigation.navigationLabel': 'Project settings navigation',
  'chrome.projectSettingsNavigation.observability': 'Observability',
  'chrome.projectSettingsNavigation.payloadFilters': 'Payload filters',
  'chrome.projectSettingsNavigation.releaseSettings': 'Release settings',
  'chrome.projectSettingsNavigation.views': 'Views',
  'chrome.projectSwitcher.actionsLabel': 'Project actions',
  'chrome.projectSwitcher.dialogLabel': 'See project settings or switch to another project',
  'chrome.projectSwitcher.expandLabel': 'Expand',
  'chrome.projectSwitcher.loadingProject': 'Loading project…',
  'chrome.projectSwitcher.searchLabel': 'Search for projects',
  'chrome.projectSwitcher.searchPlaceholder': 'Search projects…',
  'chrome.projectSwitcher.switchLabel': 'Switch project',
  'chrome.projectSwitcher.treeLabel': 'Projects and views',
  'chrome.projectSwitcher.viewAll': 'Show all',
  'chrome.restartBanner.dismissLabel': 'Dismiss banner',
  'chrome.restartBanner.learnMore': 'Learn more',
  'chrome.sessionMenu.featurePreview': 'Feature preview',
  'chrome.sessionMenu.insights': 'Insights',
  'chrome.sessionMenu.label': 'Account menu',
  'chrome.sessionMenu.personalSettings': 'Personal settings',
  'chrome.sessionMenu.sessionSection': 'Session',
  'chrome.sessionMenu.signOut': 'Sign out',
  'chrome.sessionMenu.switchOrganization': 'Switch organization',
  'chrome.settingsNavigation.actionResultLabel': '{action}, action in {page}',
  'chrome.settingsNavigation.allEnvironments': 'All environments',
  'chrome.settingsNavigation.allProjects': 'All projects',
  'chrome.settingsNavigation.backToProject': 'Back to project',
  'chrome.settingsNavigation.backToProjectName': 'Back to {projectName}',
  'chrome.settingsNavigation.flagsAndAutomation': 'Flags and automation',
  'chrome.settingsNavigation.headingResultLabel': '{heading}, in {page}',
  'chrome.settingsNavigation.monitoring': 'Monitoring',
  'chrome.settingsNavigation.noMatches': 'No settings match “{query}”',
  'chrome.settingsNavigation.resultsLabel': 'Settings search results',
  'chrome.settingsNavigation.searchPlaceholder': 'Search settings',
  'chrome.settingsNavigation.targeting': 'Targeting',
  'chrome.settingsNavigation.title': 'Settings',
  'chrome.settingsTour.allInOneBody':
    'Project and organization settings are listed together, grouped by topic, such as <b>Flags and automation</b> and <b>Access</b>.',
  'chrome.settingsTour.allInOneTitle': 'Settings are now in one place',
  'chrome.settingsTour.projectSettingsBody':
    'You can still rename a project, edit its tags, or delete it. Select the gear icon next to any project in <b>All projects</b>.',
  'chrome.settingsTour.projectSettingsTitle': 'Project details have moved',
  'chrome.settingsTour.searchBody': 'Search finds pages and the individual settings on them, such as <b>Tags</b>.',
  'chrome.settingsTour.searchTitle': 'Search for any setting',
  'chrome.settingsTour.switchProjectsBody':
    'Project settings apply to one project at a time. Select the project name to switch.',
  'chrome.settingsTour.switchProjectsTitle': 'Switch between projects',
  'chrome.shortcuts.clearSearch': 'Clear search',
  'chrome.shortcuts.deleteLabel': 'Delete shortcut',
  'chrome.shortcuts.deleteNamed': 'Delete {name}',
  'chrome.shortcuts.dragLabel': 'Drag to reorder shortcut',
  'chrome.shortcuts.emptyBody': 'Save a filtered list or page as a shortcut to open it here.',
  'chrome.shortcuts.emptyHeading': 'No shortcuts',
  'chrome.shortcuts.flagCountLabel': 'Number of flags',
  'chrome.shortcuts.loadError': 'Failed to load shortcuts',
  'chrome.shortcuts.noResults': 'No shortcuts match your search. Try a different name.',
  'chrome.shortcuts.removeLabel': 'Remove shortcut',
  'chrome.shortcuts.retryLabel': 'retry loading shortcuts',
  'chrome.shortcuts.saveHint': 'Save a shortcut to quickly access it here.',
  'chrome.shortcuts.searchLabel': 'Search shortcuts',
  'chrome.shortcuts.searchPlaceholder': 'Search shortcuts…',
  'chrome.shortcuts.title': 'Shortcuts',
  'chrome.shortcuts.tryAgain': 'Try again',
  'chrome.shortcuts.typeConfigs': 'Configs',
  'chrome.shortcuts.typeContexts': 'Contexts',
  'chrome.shortcuts.typeFlags': 'Flags',
  'chrome.shortcuts.typeSegments': 'Segments',
  'chrome.shortcuts.typeViews': 'Views',
  'chrome.sidebar.federalBadge': 'Federal',
  'chrome.sidebar.hide': 'Hide sidebar',
  'chrome.sidebar.launchDarklyHome': 'LaunchDarkly home',
  'chrome.sidebar.projectName': 'Project: {name}',
  'chrome.sidebar.show': 'Show sidebar',
  'chrome.skipToContent.skipToContent': 'Skip to content',
  'chrome.status.loading': 'Loading…',
  'chrome.theme.current': 'Theme: {theme}',
  'chrome.theme.dark': 'Dark',
  'chrome.theme.light': 'Light',
  'chrome.theme.spooky': 'Spooky',
  'chrome.theme.system': 'System',
  'chrome.webinarPromo.body': 'Controlling Code and Agents in the AI Era. Join us live on June 11.',
  'chrome.webinarPromo.cta': 'Register now',
  'chrome.webinarPromo.dismissLabel': 'Dismiss banner',
  'codeAutomation.settings.loadError': 'There was an issue loading your code automation settings.',
  'codeAutomation.settings.loadingLabel': 'Loading automation settings…',
  'codeAutomation.settings.retry': 'Try again',
  'commandPalette.dialog.label': 'Command palette',
  'commandPalette.footer.back': 'Back',
  'commandPalette.footer.esc': 'Esc',
  'commandPalette.primaryAction.shortcut': '{modKey} Enter',
  'commandPalette.results.label': 'Commands',
  'commandPalette.results.noMatches': 'No matches',
  'commandPalette.results.tryExample': 'Try <code>{example}</code>',
  'commandPalette.search.label': 'Search or jump to',
  'commandPalette.view.backLabel': 'Back to main palette',
  'commandPalette.view.escToGoBack': '<kbd>Esc</kbd> to go back',
  'commandPalette.view.learnMore': 'Learn more ↗',
  'compare.copyAction.cannotCopyFrom': 'The role does not allow you to copy from this configuration',
  'compare.copyAction.cannotCopyTo': 'The role does not allow you to copy to this configuration',
  'compare.copyAction.invalidSegments': 'Invalid segments referenced in new targeting rules',
  'compare.copyAction.noDifference': 'No difference between configurations',
  'compare.page.breadcrumb': 'Flag comparison',
  'composer.addTaskMenu.action': 'Action',
  'composer.addTaskMenu.actionDescription': 'Do something through a connection',
  'composer.addTaskMenu.addTask': 'Add task',
  'composer.addTaskMenu.buildFromScratch': 'Build one from scratch',
  'composer.addTaskMenu.condition': 'Condition',
  'composer.addTaskMenu.conditionDescription': 'Wait for an event to match',
  'composer.addTaskMenu.customAction': 'Custom action',
  'composer.addTaskMenu.customCondition': 'Custom condition',
  'composer.addTaskMenu.customNestedWorkflow': 'Custom nested workflow',
  'composer.addTaskMenu.nestedWorkflow': 'Nested workflow',
  'composer.addTaskMenu.nestedWorkflowDescription': 'A workflow nested inside this one',
  'composer.app.errorMessage': 'Something went wrong: {message}',
  'composer.app.loadingLabel': 'Loading workflows',
  'composer.app.tryAgain': 'Try again',
  'composer.app.unknownError': 'unknown error',
  'composer.builder.cancel': 'Cancel',
  'composer.builder.emptyHint': 'Add a task to get started — pick a template or build a custom one.',
  'composer.builder.saveAsTemplate': 'Save as template',
  'composer.builder.saving': 'Saving…',
  'composer.builder.startWorkflow': 'Start workflow',
  'composer.builder.starting': 'Starting…',
  'composer.builder.workflowNameLabel': 'Workflow name',
  'composer.builderCanvas.addTaskAfterLabel': 'Add task after {taskName}',
  'composer.builderCanvas.deleteTask': 'Delete task',
  'composer.builderCanvas.deleteTaskLabel': 'Delete {taskName}',
  'composer.builderCanvas.groupTaskCount': '{count, plural, one {# task} other {# tasks}}',
  'composer.builderCanvas.needsSetup': 'Needs setup',
  'composer.builderCanvas.openGroup': 'Open group',
  'composer.builderCanvas.openGroupLabel': 'Open {taskName}',
  'composer.builderCanvas.templateGroupMeta': '{templateKey} · {count, plural, one {# task} other {# tasks}}',
  'composer.builderCanvas.templateInputCount': '{templateKey} · {count, plural, one {# input} other {# inputs}}',
  'composer.customAction.connectionLabel': 'Connection',
  'composer.customAction.connectionPlaceholder': 'Select a connection',
  'composer.customAction.methodLabel': 'Method',
  'composer.customAction.methodPlaceholder': 'Select a method',
  'composer.customCondition.addClause': 'Add clause',
  'composer.customCondition.addFilter': 'Add filter',
  'composer.customCondition.connectionLabel': 'Connection',
  'composer.customCondition.connectionPlaceholder': 'Select a connection',
  'composer.customCondition.eventFilters': 'Event filters',
  'composer.customCondition.expressionHint':
    'Filter keys: <keys></keys>. Functions: <code>len()</code>, <code>last()</code>, <code>all()</code>.',
  'composer.customCondition.expressionLabel': 'Satisfy when expression',
  'composer.customCondition.expressionPlaceholder': 'e.g. len({filterKey}) >= 1',
  'composer.customCondition.expressionPlaceholderDefault': 'e.g. len(events) >= 1',
  'composer.customCondition.filterNameLabel': 'Filter name',
  'composer.customCondition.if': 'If',
  'composer.customCondition.nameAFilterHint': 'Name a filter above, then combine filters by name.',
  'composer.customCondition.operatorContains': 'contains',
  'composer.customCondition.operatorDoesNotContain': "doesn't contain",
  'composer.customCondition.operatorIs': 'is',
  'composer.customCondition.operatorIsGreaterThan': 'is greater than',
  'composer.customCondition.operatorIsLessThan': 'is less than',
  'composer.customCondition.operatorIsNot': 'is not',
  'composer.customCondition.operatorIsNotGreaterThan': 'is not greater than',
  'composer.customCondition.operatorIsNotLessThan': 'is not less than',
  'composer.customCondition.operatorIsNotOneOf': 'is not one of',
  'composer.customCondition.operatorIsOneOf': 'is one of',
  'composer.customCondition.operatorLabel': 'Operator',
  'composer.customCondition.operatorNot': 'not {operator}',
  'composer.customCondition.removeClause': 'Remove clause',
  'composer.customCondition.removeFilter': 'Remove filter',
  'composer.customCondition.satisfyWhen': 'Satisfy when',
  'composer.customCondition.triggerLabel': 'Trigger',
  'composer.customCondition.triggerPlaceholder': 'Choose a trigger',
  'composer.customCondition.valueLabel': 'Value',
  'composer.customCondition.valuePlaceholder': 'value…',
  'composer.customCondition.when': 'When',
  'composer.devFlags.hint': 'Story-only switches for comparing visual treatments',
  'composer.devFlags.title': 'Dev flags',
  'composer.relativeTime.daysAgo': '{count, plural, one {# day ago} other {# days ago}}',
  'composer.relativeTime.hoursAgo': '{count, plural, one {# hour ago} other {# hours ago}}',
  'composer.relativeTime.justNow': 'just now',
  'composer.relativeTime.minutesAgo': '{count, plural, one {# minute ago} other {# minutes ago}}',
  'composer.resourcePicker.environment': 'environment',
  'composer.resourcePicker.flag': 'flag',
  'composer.resourcePicker.project': 'project',
  'composer.resourcePicker.searchEnvironments': 'Search environments',
  'composer.resourcePicker.searchFlags': 'Search flags',
  'composer.resourcePicker.searchProjects': 'Search projects',
  'composer.runDag.collapseGroup': 'Collapse group',
  'composer.runDag.collapseGroupLabel': 'Collapse {taskName} tasks',
  'composer.runDag.expandGroup': 'Expand group',
  'composer.runDag.expandGroupLabel': 'Expand {taskName} tasks',
  'composer.runDag.loadingTasks': 'Loading tasks…',
  'composer.runDag.taskCount': '{count, plural, one {# task} other {# tasks}}',
  'composer.runDag.viewGroupTasks': 'View group tasks',
  'composer.runDag.viewGroupTasksLabel': 'View {taskName} tasks',
  'composer.runDetail.allWorkflows': 'All workflows',
  'composer.runDetail.back': 'Back',
  'composer.runDetail.collapseAll': 'Collapse all',
  'composer.runDetail.collapseAllLabel': 'Collapse all tasks',
  'composer.runDetail.collapseTaskTree': 'Collapse task tree',
  'composer.runDetail.expandAll': 'Expand all',
  'composer.runDetail.expandAllLabel': 'Expand all tasks',
  'composer.runDetail.expandTaskTree': 'Expand task tree',
  'composer.runDetail.loadingLabel': 'Loading workflow tasks',
  'composer.runDetail.openTemplateRuns': 'Open all {templateName} runs in a new tab',
  'composer.runDetail.statusStarted': '{status} · started {time}',
  'composer.runDetail.tasks': 'Tasks',
  'composer.runsFilters.status': 'Status',
  'composer.runsFilters.statusLabel': 'Filter by status',
  'composer.runsFilters.workflow': 'Workflow',
  'composer.runsFilters.workflowLabel': 'Filter by workflow',
  'composer.runsFilters.workflowSearchPlaceholder': 'Search workflows',
  'composer.runsList.allWorkflowsHeading': 'All workflows',
  'composer.runsList.allWorkflowsSubtitle': 'Showing runs from all workflows',
  'composer.runsList.loadingLabel': 'Loading runs',
  'composer.runsList.noRunsMatch': 'No runs match these filters.',
  'composer.runsList.noRunsYet': 'No runs yet. Start one from the New workflow builder.',
  'composer.runsList.runCount': '{count, plural, one {# workflow run} other {# workflow runs}}',
  'composer.runsList.startedManually': '· started manually',
  'composer.status.blocked': 'Blocked',
  'composer.status.cancelled': 'Cancelled',
  'composer.status.completed': 'Completed',
  'composer.status.failed': 'Failed',
  'composer.status.idle': 'Idle',
  'composer.status.inProgress': 'In progress',
  'composer.status.queued': 'Queued',
  'composer.status.skipped': 'Skipped',
  'composer.status.unknown': 'Unknown',
  'composer.taskConfig.closeConfiguration': 'Close configuration',
  'composer.taskConfig.durationLabel': '{name} (ms)',
  'composer.taskConfig.groupInputsNote': 'Its stage inputs apply to those tasks and will be editable here soon.',
  'composer.taskConfig.groupNote': 'Open this group from the canvas to edit its nested tasks.',
  'composer.taskConfig.nameLabel': 'Name',
  'composer.taskConfig.notEditableNote': "Can't be edited in the visual builder yet.",
  'composer.taskConfig.nounWithA': 'a {noun}',
  'composer.taskConfig.nounWithAn': 'an {noun}',
  'composer.taskConfig.panelLabel': 'Task configuration',
  'composer.taskConfig.required': 'Required',
  'composer.taskConfig.selectOptionPlaceholder': 'Select {label}',
  'composer.taskConfig.selectResource': 'Select {resource}',
  'composer.taskConfig.selectResourceFirst': 'Select {resource} first',
  'composer.taskConfig.templateLegend': 'Template · {templateName}',
  'composer.taskConfig.valueNoun': 'value',
  'composer.taskKind.action': 'Action',
  'composer.taskKind.condition': 'Condition',
  'composer.taskKind.group': 'Group',
  'composer.taskKind.workflow': 'Workflow',
  'composer.taskList.empty': 'No tasks at this level yet.',
  'composer.taskList.label': 'Tasks',
  'composer.taskList.openGroup': 'Open group',
  'composer.taskList.openGroupLabel': 'Open {taskName}',
  'composer.taskList.title': 'Tasks',
  'composer.taskName.eventFilterCount': '{count} event filters',
  'composer.taskName.unnamedAction': 'Unnamed action',
  'composer.taskName.unnamedCondition': 'Unnamed condition',
  'composer.taskName.unnamedGroup': 'Unnamed group',
  'composer.taskName.unnamedWorkflow': 'Unnamed workflow',
  'composer.taskName.waitForDelay': 'Wait for delay',
  'composer.taskName.waitHours': '{count, plural, one {Wait # hour} other {Wait # hours}}',
  'composer.taskName.waitMinutes': '{count, plural, one {Wait # minute} other {Wait # minutes}}',
  'composer.taskTree.collapseGroup': 'Collapse group',
  'composer.taskTree.collapseGroupLabel': 'Collapse {taskName}',
  'composer.taskTree.expandGroup': 'Expand group',
  'composer.taskTree.expandGroupLabel': 'Expand {taskName}',
  'composer.taskTree.label': 'Tasks',
  'composer.taskTree.loadingLabel': 'Loading tasks',
  'composer.taskTree.viewGroupTasks': 'View group tasks',
  'composer.taskTree.viewGroupTasksLabel': 'View {taskName} tasks',
  'composer.workflowsNav.allWorkflows': 'All workflows',
  'composer.workflowsNav.loadingLabel': 'Loading templates',
  'composer.workflowsNav.newWorkflow': 'New workflow',
  'composer.workflowsNav.title': 'Workflows',
  'contexts.attributeValue.rawValueLabel': 'Raw value',
  'contexts.attributesMenu.clearLabel': 'clear',
  'contexts.attributesMenu.noAttributesFound': 'No attributes found',
  'contexts.attributesMenu.searchLabel': 'Search attributes…',
  'contexts.attributesMenu.searchPlaceholder': 'Search attributes…',
  'contexts.attributesMenu.toggle': 'Attributes',
  'contexts.attributesMenu.toggleLabel': 'Attributes',
  'contexts.contextCard.copyKeyLabel': 'Copy to clipboard',
  'contexts.contextCard.keyLabel': 'Key',
  'contexts.contextCard.kindLabel': 'Kind',
  'contexts.contextCard.lastSeenLabel': 'Last seen',
  'contexts.contextCard.nameLabel': 'Name',
  'contexts.contextCard.viewDetails': 'View context details',
  'contexts.contextKindChip.archivedStatus': 'Archived',
  'contexts.contextKindChip.createdLabel': 'Created',
  'contexts.contextKindChip.descriptionLabel': 'Description',
  'contexts.contextKindChip.keyLabel': 'Key',
  'contexts.contextKindChip.lastSeenLabel': 'Last seen',
  'contexts.contextKindChip.loadError': 'Couldn’t load context kind details.',
  'contexts.contextKindChip.loadingLabel': 'Loading context kind details',
  'contexts.contextKindChip.nameLabel': 'Name',
  'contexts.contextKindChip.statusLabel': 'Status',
  'contexts.contextKindComboBox.heading': 'Select context kind',
  'contexts.contextKindComboBox.inputLabel': 'select context kind',
  'contexts.contextKindComboBox.noResults': 'No context kinds found',
  'contexts.contextKindComboBox.placeholder': 'Select a context kind',
  'contexts.contextKindMultiSelect.moreCount': '+{count} more',
  'contexts.contextKindMultiSelect.searchLabel': 'Search context kinds',
  'contexts.contextKindSelector.searchLabel': 'Search context kinds',
  'contexts.contextPicker.clearLabel': 'clear context',
  'contexts.contextPicker.heading': 'Choose a context to test',
  'contexts.contextPicker.openLabel': 'open context picker',
  'contexts.contextPicker.searchingLabel': 'Searching contexts',
  'contexts.contextPopover.copyContext': 'Copy context',
  'contexts.contextPopover.copyLabel': 'Copy context',
  'contexts.contextPopover.errorBody': 'Unable to load context data',
  'contexts.contextPopover.errorHeading': 'Context details',
  'contexts.contextPopover.fallbackCopyContext': 'Copy context',
  'contexts.contextPopover.fallbackHeading': 'Context details',
  'contexts.contextPopover.fallbackViewInContexts': 'View in Contexts',
  'contexts.contextPopover.heading': 'Context details',
  'contexts.contextPopover.loadError': 'Unable to load context data',
  'contexts.contextPopover.notSeenBody':
    'LaunchDarkly hasn’t seen this context in the past 30 days, so there are no attributes to show.',
  'contexts.contextPopover.notSeenCopyKey': 'Copy key',
  'contexts.contextPopover.notSeenHeading': 'Context not seen',
  'contexts.contextPopover.notSeenKeyLabel': 'Key',
  'contexts.contextPopover.notSeenKindLabel': 'Kind',
  'contexts.contextPopover.retry': 'Try again',
  'contexts.contextPopover.viewFullDetails': 'View full context details',
  'contexts.contextPopover.viewInContexts': 'View in Contexts',
  'contexts.contextPopover.viewLabel': 'View in Contexts',
  'contexts.contextPopover.viewTooltip': 'View full context details',
  'contexts.deleteInstance.confirm': 'Delete',
  'contexts.deleteInstance.confirmBody': 'This context instance {name} will be deleted from {environment}',
  'contexts.deleteInstance.confirmTitle': 'Delete context instance {name}',
  'contexts.deleteInstance.deleteFailed': 'Failed to delete context instance. Try again later.',
  'contexts.deleteInstance.deleteLabel': 'Delete instance',
  'contexts.deleteInstance.deleted': 'Context instance deleted',
  'contexts.deleteInstance.tooltip': 'Delete context instance {name}',
  'contexts.details.attributes': 'Attributes',
  'contexts.details.attributesLoadError': 'Failed to load context variations',
  'contexts.details.fromSource': 'From source:',
  'contexts.details.instances': 'Instances',
  'contexts.details.instancesLoadError': 'Failed to load context variations',
  'contexts.details.instancesTooltip': 'The number of unique context instances in which this context appeared',
  'contexts.details.kind': 'Kind: {kind}',
  'contexts.details.lastSeen': 'Last seen:',
  'contexts.details.multiKind': 'Multi',
  'contexts.details.relatedContexts': 'Related contexts',
  'contexts.details.relatedContextsLoadError': 'Failed to load related contexts',
  'contexts.details.relatedContextsTooltip': 'Contexts that have appeared together with this context',
  'contexts.details.segments': 'Segments',
  'contexts.details.segmentsTooltip': 'Segments that include this context',
  'contexts.details.singleInstance': 'This is a single instance.',
  'contexts.details.transientContexts': 'This instance contained the following transient context(s): {kinds}.',
  'contexts.details.variationsLoadError': 'Failed to load context variations',
  'contexts.evaluationReason.default': 'evaluation reasons can be found on this link.',
  'contexts.evaluationReason.defaultRule': 'Default rule',
  'contexts.evaluationReason.errorClientNotReady':
    'The default value will be served because the client is not able to establish a connection to LaunchDarkly',
  'contexts.evaluationReason.errorDefault': 'An error occurred',
  'contexts.evaluationReason.errorException':
    'The default variation will be served because an unexpected error stopped flag evaluation',
  'contexts.evaluationReason.errorFlagNotFound':
    'The default variation will be served because the flag key did not match any known flag',
  'contexts.evaluationReason.errorMalformedFlag':
    'The default variation will be served because there was an internal inconsistency in the flag data',
  'contexts.evaluationReason.errorUserNotSpecified':
    'The default variation will be served because the flag key did not match any known flag',
  'contexts.evaluationReason.errorWrongType':
    'The default variation will be served because the application code requested the flag value with a different data type than it actually is',
  'contexts.evaluationReason.fallthrough':
    'will be served because the context is not targeted individually or by a rule',
  'contexts.evaluationReason.off': 'will be served because flag targeting is off',
  'contexts.evaluationReason.prerequisiteFailed':
    'will be served because the prerequisite flag {flag} returned the off variation',
  'contexts.evaluationReason.ruleMatch': 'Rule match',
  'contexts.evaluationReason.ruleMatchReason': 'will be served because the context is targeted by rule {rule}',
  'contexts.evaluationReason.targetMatch': 'will be served because the context is individually targeted',
  'contexts.evaluationReason.tooltip': '<strong>{value} </strong>{reason}.',
  'contexts.expectedVariations.aiConfigEntity': 'AI config',
  'contexts.expectedVariations.checkForTypos': 'Check for typos.',
  'contexts.expectedVariations.configsTab': 'Configs',
  'contexts.expectedVariations.entityNameColumn': 'Entity name',
  'contexts.expectedVariations.evaluationReasonColumn': 'Evaluation reason',
  'contexts.expectedVariations.evaluationReasonHelp':
    'To learn more about evaluation reasons, <link>read the documentation.</link>',
  'contexts.expectedVariations.flagEntity': 'flag',
  'contexts.expectedVariations.flagOffRestriction':
    'You cannot change the variation from this page because the {entity} is off',
  'contexts.expectedVariations.flagsTab': 'Flags',
  'contexts.expectedVariations.heading': 'Expected variations',
  'contexts.expectedVariations.instanceRestriction': 'You cannot change the variation for a context instance',
  'contexts.expectedVariations.loadError': 'Failed to load context variations',
  'contexts.expectedVariations.migrationFlagRestriction':
    "You can't edit migration flags here, click on the flag and update the migration rollout for this context.",
  'contexts.expectedVariations.noAiConfigsMatch': 'No AI configs match. Try this:',
  'contexts.expectedVariations.noFlagsMatch': 'No flags match. Try this:',
  'contexts.expectedVariations.noOffVariation': 'No off variation set',
  'contexts.expectedVariations.paginationSummary': '<strong>{from}–{to}</strong> of <strong>{total}</strong>',
  'contexts.expectedVariations.privateAttributesWarning':
    'Due to use of private attributes, we are unable to determine which variation this context is receiving.',
  'contexts.expectedVariations.searchHint':
    "Search for a context's {entity} settings by the {entity}'s name, key or description.",
  'contexts.expectedVariations.searchLabel': 'Search by name, key or description',
  'contexts.expectedVariations.searchPlaceholder': 'Find by name, key or description',
  'contexts.expectedVariations.selectVariationLabel': 'Select Variation',
  'contexts.expectedVariations.settingsUpdated': 'Settings updated successfully',
  'contexts.expectedVariations.tabsLabel': 'Flag types',
  'contexts.expectedVariations.variationColumn': 'Variation',
  'contexts.filterBar.allContextKinds': 'All context kinds',
  'contexts.filterBar.contextKindLabel': 'Context kind',
  'contexts.filterBar.keyFieldDescription': 'The context key.',
  'contexts.filterBar.kindField': 'Context kind',
  'contexts.filterBar.kindFieldDescription': 'The context kind.',
  'contexts.filterBar.nameFieldDescription': 'The context name.',
  'contexts.filterBar.searchField': 'Search',
  'contexts.filterBar.searchFieldDescription': 'A full-text search across context keys, names, and attribute values.',
  'contexts.filterBar.searchLabel': 'Search contexts',
  'contexts.filterBar.searchPlaceholder': 'Search contexts',
  'contexts.filters.appliedFilterLabel': 'Applied filter',
  'contexts.filters.apply': 'Apply filter',
  'contexts.filters.applyLabel': 'apply attribute filter',
  'contexts.filters.attributeNameInputLabel': 'select attribute name',
  'contexts.filters.attributeNameLabel': 'Select attribute name',
  'contexts.filters.attributeNamePlaceholder': 'Select an attribute',
  'contexts.filters.attributeNameToggleLabel': 'Select attribute name',
  'contexts.filters.attributeValueInputLabel': 'select attribute value',
  'contexts.filters.attributeValueLabel': 'Select attribute value',
  'contexts.filters.attributeValuePlaceholder': 'Select a value',
  'contexts.filters.attributeValueToggleLabel': 'Select attribute value',
  'contexts.filters.contextKind': 'Context kind',
  'contexts.filters.heading': 'Filter by',
  'contexts.filters.is': 'Is',
  'contexts.filters.learnAdvancedFiltering': 'Learn about advanced filtering',
  'contexts.filters.noAttributeNames': 'No searchable attributes found',
  'contexts.filters.noAttributeValues': 'No searchable attributes found',
  'contexts.filters.noFiltersApplied': 'No filters applied',
  'contexts.filters.toggle': 'Filters',
  'contexts.filters.whereAttribute': 'Where attribute',
  'contexts.instances.contextColumn': 'Context',
  'contexts.instances.lastSeenAgo': '{time} ago',
  'contexts.instances.lastSeenColumn': 'Last seen',
  'contexts.instances.onlyShowingRecent': 'Only showing {shown} most recent of {total} total instances',
  'contexts.instances.someTime': 'some time',
  'contexts.instances.viewLess': 'View {count} less',
  'contexts.instances.viewMore': 'View {count} more',
  'contexts.keyCell.copied': 'Copied to clipboard',
  'contexts.keyCell.copy': 'Copy',
  'contexts.keyCell.copyKey': 'Copy key',
  'contexts.keyCell.noKeyAvailable': 'No key available',
  'contexts.kindForm.analysisScope': 'analysis',
  'contexts.kindForm.archive': 'Archive',
  'contexts.kindForm.archived': 'Context kind archived',
  'contexts.kindForm.archivedTitle': 'Archived context kind',
  'contexts.kindForm.availableFor': 'Available for {scope}',
  'contexts.kindForm.cancel': 'Cancel',
  'contexts.kindForm.cannotArchiveAvailable': "You can't archive context kinds available for {scope}",
  'contexts.kindForm.createKind': 'Create kind',
  'contexts.kindForm.descriptionLabel': 'Description',
  'contexts.kindForm.descriptionPlaceholder': 'Describe what this context kind is used for.',
  'contexts.kindForm.descriptionTooLong': 'context kind description is too long (max 1000 characters)',
  'contexts.kindForm.editTitle': 'Edit context kind',
  'contexts.kindForm.experimentsAvailabilityHint':
    'Allow experiments and guarded rollouts to use this context kind as a randomization unit for assigning traffic',
  'contexts.kindForm.experimentsScope': 'experiments and guarded rollouts',
  'contexts.kindForm.for': 'For',
  'contexts.kindForm.genericAvailabilityHint':
    'Make this context kind available for experiments, guarded rollouts, and analytics',
  'contexts.kindForm.keyHint':
    'This cannot be changed later. Keys must only contain letters, numbers, <code>.</code>, <code>_</code> or <code>-</code>. You cannot use <code>kind</code>, <code>multi</code> as a key.',
  'contexts.kindForm.keyInvalid':
    'Context kind key can only contain letters, numbers, ., _, or -. Key cannot be "kind" or "multi".',
  'contexts.kindForm.keyLabel': 'Key',
  'contexts.kindForm.keyPlaceholder': 'device',
  'contexts.kindForm.keyRequired': 'Context kind key is required.',
  'contexts.kindForm.keyTooLong': 'Context kind key is too long (max 256 characters).',
  'contexts.kindForm.keyUnique': 'Context kind key must be unique.',
  'contexts.kindForm.mustHaveActiveKind': 'You must have at least one active context kind',
  'contexts.kindForm.nameHint': 'A human-friendly name for your context kind.',
  'contexts.kindForm.nameLabel': 'Name',
  'contexts.kindForm.namePlaceholder': 'Device',
  'contexts.kindForm.nameRequired': 'context kind name is required',
  'contexts.kindForm.nameTooLong': 'context kind name is too long (max 256 characters)',
  'contexts.kindForm.newTitle': 'New context kind',
  'contexts.kindForm.onlyExperimentNotice':
    "You must have at least one context kind available for {scope}, so you can't edit this setting",
  'contexts.kindForm.onlyKindNotice': 'You only have one context kind set-up, so you can’t edit these settings.',
  'contexts.kindForm.onlyTargetNotice':
    "You must have at least one context kind available for targeting, so you can't edit this setting",
  'contexts.kindForm.restore': 'Restore',
  'contexts.kindForm.restored': 'Context kind restored',
  'contexts.kindForm.save': 'Save',
  'contexts.kindForm.saveBeforeArchiving': 'Save your configuration before archiving',
  'contexts.kindForm.saveFailed': 'Failed to save context kind',
  'contexts.kindForm.setAsDefault': 'Set as the default for experiments',
  'contexts.kindForm.settingsUpdated': 'Successfully updated experimentation settings',
  'contexts.kindForm.updated': 'Updated context kind',
  'contexts.kinds.actionsLabel': 'env actions',
  'contexts.kinds.addKind': 'Add kind',
  'contexts.kinds.adminOnlyTooltip': 'Context kind management is restricted to admin users.',
  'contexts.kinds.autoAdded': 'Auto added: {date}',
  'contexts.kinds.availableForExperiments': 'Available for experiments and guarded rollouts',
  'contexts.kinds.builtInDefault': 'Built in default',
  'contexts.kinds.copyKey': 'Copy key',
  'contexts.kinds.defaultUnit': 'Default',
  'contexts.kinds.description': 'Use context kinds to categorize context instances.',
  'contexts.kinds.edit': 'Edit',
  'contexts.kinds.inUse': 'In use',
  'contexts.kinds.keyCopied': 'Context kind key copied to clipboard',
  'contexts.kinds.lastSeenOn': 'Last seen {date}.',
  'contexts.kinds.learnMore': 'Learn more',
  'contexts.kinds.listLabel': 'kind list',
  'contexts.kinds.manuallyAdded': 'Manually added: {date}',
  'contexts.kinds.moreEnvironments': '+{count} more',
  'contexts.kinds.moreEnvironmentsLabel': 'More environments',
  'contexts.kinds.noKindsFound': 'No kinds found',
  'contexts.kinds.notAvailableForExperiments': 'Not available for experiments and guarded rollouts',
  'contexts.kinds.notInUse': 'Not in use',
  'contexts.kinds.paginationSummary': '<strong>{from}–{to}</strong> of <strong>{total}</strong> kinds',
  'contexts.kinds.searchPlaceholder': 'Find context kind',
  'contexts.kinds.selectToView': 'Select to view {kind} contexts in this environment.',
  'contexts.kinds.showArchived': 'Show archived',
  'contexts.kinds.title': 'Context kinds',
  'contexts.kindsListPage.title': 'Contexts',
  'contexts.list.copyKeyTooltip': 'Copy {key} to clipboard',
  'contexts.list.instancesColumn': 'Instances',
  'contexts.list.instancesHeading': 'Instances including {name}',
  'contexts.list.keyColumn': 'Key',
  'contexts.list.keyCopied': 'Copied {key}',
  'contexts.list.kindColumn': 'Kind',
  'contexts.list.lastSeenColumn': 'Last seen',
  'contexts.list.nameColumn': 'Name',
  'contexts.list.skeletonLabel': 'Context list skeleton loader',
  'contexts.list.tableLabel': 'Context List',
  'contexts.listEmptyState.body':
    'A context is a user, device, or other entity your flags serve. Configure a LaunchDarkly SDK in your app, and contexts appear here automatically.',
  'contexts.listEmptyState.clearFilters': 'Clear filters',
  'contexts.listEmptyState.filteredBody': 'Try adjusting your filters',
  'contexts.listEmptyState.filteredHeading': 'No contexts match your filter criteria',
  'contexts.listEmptyState.heading': 'Contexts appear when an SDK evaluates flags',
  'contexts.listEmptyState.readAboutContexts': 'Read about contexts',
  'contexts.listPage.connectAction': 'Connect your SDK to start evaluating flags and see real-time data.',
  'contexts.listPage.manageContextKinds': 'Manage context kinds',
  'contexts.notFound.backToList': 'Back to the list of contexts',
  'contexts.notFound.lead': 'Sorry, we can’t find what you’re looking for.',
  'contexts.notFound.title': 'Context not found',
  'contexts.notifications.contextDeleted': 'Deleted context',
  'contexts.notifications.contextKindArchived': 'Context kind archived',
  'contexts.notifications.contextKindRestored': 'Context kind restored',
  'contexts.notifications.contextKindUpdated': 'Updated context kind',
  'contexts.notifications.deleteContextFailed': 'Failed to delete context. Try again later',
  'contexts.notifications.experimentationSettingsUpdated': 'Successfully updated experimentation settings',
  'contexts.notifications.flagsUpdateFailed': 'There was a problem updating the flags',
  'contexts.notifications.flagsUpdated': 'Flags updated',
  'contexts.page.title': 'Contexts',
  'contexts.relatedContexts.contextColumn': 'Context',
  'contexts.relatedContexts.lastSeenAgo': '{time} ago',
  'contexts.relatedContexts.lastSeenColumn': 'Last seen',
  'contexts.relatedContexts.none': 'This context has no related contexts.',
  'contexts.relatedContexts.someTime': 'some time',
  'contexts.relatedContexts.viewLess': 'View {count} less',
  'contexts.relatedContexts.viewMore': 'View {count} more',
  'contexts.sdkCompatibility.savedWithIncompatibleSdks':
    'Your changes will be saved. We have detected SDKs that are not compatible with nested segments:',
  'contexts.sdkCompatibility.upgradeCta': '<docs>minimum version</docs> of {version}.',
  'contexts.sdkCompatibility.upgradeDescription': 'Targeting additional context kinds requires a ',
  'contexts.sdkCompatibility.upgradeTitle': 'Upgrade recommended: {sdkName}',
  'contexts.search.fieldLabel': 'Search contexts',
  'contexts.search.placeholder': 'Search contexts',
  'contexts.search.submit': 'Search',
  'contexts.search.submitLabel': 'Search contexts',
  'contexts.segmentMembership.amplitudeSynced': 'This synced segment is managed by Amplitude',
  'contexts.segmentMembership.individuallyTargeted': 'This selectedContext is individually targeted in this segment',
  'contexts.segmentMembership.none': 'This context is not part of any segments.',
  'contexts.segmentMembership.viewLess': 'View {count} less',
  'contexts.segmentMembership.viewMore': 'View {count} more',
  'contexts.status.loading': 'Loading…',
  'contexts.targetChip.expiringLabel': 'Target is scheduled to be removed',
  'contexts.targetChip.failedLabel': 'Failed to load target details',
  'contexts.targetChip.failedTooltip': 'Failed to load details for this target',
  'contexts.targetChip.loadError': 'Unable to load context data',
  'contexts.targetChip.loadingLabel': 'Loading target details',
  'contexts.targetChip.loadingTooltip': 'Loading details for this target',
  'contexts.targetChip.scheduledToBeRemovedAt': 'Scheduled to be removed at',
  'dashboard.graphEditPage.breadcrumb': 'Edit',
  'dashboard.graphEditPage.title': 'Edit Graph',
  'dashboard.newPage.title': 'Add graph',
  'dashboards.page.title': 'Dashboards',
  'dashboards_.page.title': 'Dashboards',
  'dataSources.archive.archiveFailedToast': 'Failed to archive: {error}',
  'dataSources.archive.archivedToast': 'Archived {name}',
  'dataSources.archive.assignmentBody':
    'This assignment data source will be archived from your LaunchDarkly account. Archiving a data source will not make changes to the warehouse integration it is linked to.',
  'dataSources.archive.assignmentTitle': 'Archive assignment data source',
  'dataSources.archive.attributeBody':
    'This attribute data source will be archived from your LaunchDarkly account. Archiving a data source will not make changes to the warehouse integration it is linked to.',
  'dataSources.archive.attributeTitle': 'Archive attribute data source',
  'dataSources.archive.confirm': 'Archive data source',
  'dataSources.archive.experimentBlockedTooltip':
    'Archive the experiments that use this source before archiving this source',
  'dataSources.archive.metricBlockedTooltip': 'Archive the metrics that use this source before archiving this source',
  'dataSources.archive.metricBody':
    'This metric data source will be archived from your LaunchDarkly account. Archiving a data source will not make changes to the warehouse integration it is linked to.',
  'dataSources.archive.metricTitle': 'Archive metric data source',
  'dataSources.archive.unarchiveBody': 'This data source will be restored to your active data sources list.',
  'dataSources.archive.unarchiveConfirm': 'Unarchive data source',
  'dataSources.archive.unarchiveFailedToast': 'Failed to unarchive: {error}',
  'dataSources.archive.unarchiveTitle': 'Unarchive "{dataSourceName}"',
  'dataSources.archive.unarchivedToast': 'Unarchived {name}',
  'dataSources.assignmentsConfigurationPage.title': 'Configuration',
  'dataSources.assignmentsConnectionsPage.title': 'Connections',
  'dataSources.assignmentsListPage.thereWasAn': 'There was an issue loading assignment data sources.',
  'dataSources.assignmentsListPage.title': 'Data sources | Assignments',
  'dataSources.assignmentsNewPage.thereWasAn2': 'There was an issue loading the create assignment data source form.',
  'dataSources.assignments_Page.breadcrumb': 'Assignments',
  'dataSources.attributeConnections.attributesColumn': 'Attributes from source',
  'dataSources.attributeConnections.emptyDescription':
    'Experiments that use attributes from this source to filter results appear here.',
  'dataSources.attributeConnections.emptyHeading': 'No experiments use this source yet',
  'dataSources.attributeConnections.iteration': 'Iteration {iterationNumber}',
  'dataSources.attributeConnections.iterationColumn': 'Iteration',
  'dataSources.attributeConnections.lastUpdatedColumn': 'Last updated',
  'dataSources.attributeConnections.tableLabel': 'Attribute data source connections',
  'dataSources.attributesConfigurationPage.title': 'Configuration',
  'dataSources.attributesConnectionsPage.title': 'Connections',
  'dataSources.attributesListPage.thereWasAn': 'There was an issue loading attribute data sources.',
  'dataSources.attributesListPage.title': 'Data sources | Attributes',
  'dataSources.attributesNewPage.loadError': 'There was an issue loading the create attribute data source form.',
  'dataSources.attributesNewPage.title': 'Create',
  'dataSources.attributes_Page.breadcrumb': 'Attributes',
  'dataSources.configuration.archivedTooltip': 'Archived data source cannot be edited',
  'dataSources.configuration.cancel': 'Cancel',
  'dataSources.configuration.checkFormDescription': 'Check form and try again.',
  'dataSources.configuration.checkRequiredFieldsDescription': 'Check required fields and try again.',
  'dataSources.configuration.collapseLabel': 'Collapse',
  'dataSources.configuration.completeRequiredFields': 'Complete required fields before saving.',
  'dataSources.configuration.discardChanges': 'Discard changes',
  'dataSources.configuration.editConfiguration': 'Edit configuration',
  'dataSources.configuration.expandLabel': 'Expand',
  'dataSources.configuration.invalidSubmissionTitle': 'Invalid submission',
  'dataSources.configuration.noChangesTooltip': 'No changes to save.',
  'dataSources.configuration.noRevisionDescription': 'Updated data source was not returned from the server.',
  'dataSources.configuration.reconnectWarehouseTooltip': 'Re-connect data warehouse before editing data source.',
  'dataSources.configuration.saveConfiguration': 'Save configuration',
  'dataSources.configuration.unsavedChangesBody': 'If you close this form, you will lose any unsaved changes.',
  'dataSources.configuration.updateErrorTitle': 'Error updating data source',
  'dataSources.configuration.updatedTitle': 'Updated data source',
  'dataSources.connectionStatus.asOf': '{status} as of {checkedAt}',
  'dataSources.connections.archivedTooltip': 'This data source is archived.',
  'dataSources.createDataSource.errorToastDescription': 'Check form and try again.',
  'dataSources.createDataSource.errorToastTitle': 'Error creating data source',
  'dataSources.createDataSource.invalidToastDescription': 'Check required fields and try again.',
  'dataSources.createDataSource.invalidToastTitle': 'Invalid submission',
  'dataSources.createPage.cancel': 'Cancel',
  'dataSources.createPage.checkFormDescription': 'Check form and try again.',
  'dataSources.createPage.checkRequiredFieldsDescription': 'Check required fields and try again.',
  'dataSources.createPage.completeRequiredFields': 'Complete required fields before saving.',
  'dataSources.createPage.createErrorTitle': 'Error creating data source',
  'dataSources.createPage.discardChanges': 'Discard changes',
  'dataSources.createPage.invalidSubmissionTitle': 'Invalid submission',
  'dataSources.createPage.save': 'Save',
  'dataSources.createPage.unsavedChangesBody': 'If you close this form, you will lose any unsaved changes.',
  'dataSources.detailsPage.archiveLabel': 'Archive data source',
  'dataSources.detailsPage.backToList': 'Back to data sources',
  'dataSources.detailsPage.configurationTab': 'Configuration',
  'dataSources.detailsPage.connectionsTab': 'Connections',
  'dataSources.detailsPage.copyLink': 'Copy link',
  'dataSources.detailsPage.loadError': 'There was an issue loading this data source.',
  'dataSources.detailsPage.unarchiveLabel': 'Unarchive data source',
  'dataSources.detailsSidebar.addTags': 'Add tag(s)',
  'dataSources.detailsSidebar.changeMaintainerLabel': 'Change maintainer',
  'dataSources.detailsSidebar.checkFormDescription': 'Check form and try again.',
  'dataSources.detailsSidebar.chooseMaintainer': 'Choose maintainer',
  'dataSources.detailsSidebar.connectionStatusLabel': 'Integration connection status',
  'dataSources.detailsSidebar.createdLabel': 'Created',
  'dataSources.detailsSidebar.descriptionFieldLabel': 'Enter a data source description',
  'dataSources.detailsSidebar.descriptionLabel': 'Description',
  'dataSources.detailsSidebar.descriptionPlaceholder': 'Add a description',
  'dataSources.detailsSidebar.descriptionTooLong': 'Description must be at most 1024 characters',
  'dataSources.detailsSidebar.editDescriptionLabel': 'Edit description',
  'dataSources.detailsSidebar.editNameLabel': 'Edit name',
  'dataSources.detailsSidebar.keyLabel': 'Key',
  'dataSources.detailsSidebar.lastModifiedLabel': 'Last modified',
  'dataSources.detailsSidebar.loadingMaintainersLabel': 'Loading maintainers',
  'dataSources.detailsSidebar.maintainerLabel': 'Maintainer',
  'dataSources.detailsSidebar.nameFieldLabel': 'Enter a data source name',
  'dataSources.detailsSidebar.nameLabel': 'Name',
  'dataSources.detailsSidebar.nameRequired': 'Required',
  'dataSources.detailsSidebar.nameTooLong': 'Name must be at most 128 characters',
  'dataSources.detailsSidebar.noDescription': 'None',
  'dataSources.detailsSidebar.noMaintainer': 'None',
  'dataSources.detailsSidebar.noTags': 'None',
  'dataSources.detailsSidebar.savingDescriptionLabel': 'Saving description',
  'dataSources.detailsSidebar.savingNameLabel': 'Saving name',
  'dataSources.detailsSidebar.tagsGroupLabel': 'Data source tags',
  'dataSources.detailsSidebar.tagsLabel': 'Tags',
  'dataSources.detailsSidebar.updateErrorToast': 'Error updating data source',
  'dataSources.detailsSidebar.updateTagsErrorToast': 'Failed to update tags',
  'dataSources.detailsSidebar.updatedTagsToast': 'Updated tags',
  'dataSources.detailsSidebar.updatedToast': 'Updated data source',
  'dataSources.detailsSidebar.warehouseTypeLabel': 'Warehouse type',
  'dataSources.emptyState.createAssignmentDescription':
    'Assignment sources draw from your existing data warehouse integrations.',
  'dataSources.emptyState.createAssignmentHeading': 'Create assignment source',
  'dataSources.emptyState.createAttributeDescription':
    'Attribute sources draw from your existing data warehouse integrations.',
  'dataSources.emptyState.createAttributeHeading': 'Create attribute source',
  'dataSources.emptyState.createMetricDescription':
    'Metric sources draw from your existing data warehouse integrations.',
  'dataSources.emptyState.createMetricHeading': 'Create metric source',
  'dataSources.emptyState.createSource': 'Create source',
  'dataSources.emptyState.filteredDescription': 'Adjust your filtering criteria and try again.',
  'dataSources.emptyState.integrationLogoAlt': '{integrationName} integration logo',
  'dataSources.emptyState.learnMore': 'Learn more.',
  'dataSources.emptyState.moreIntegrationsLabel': 'Show more integration options',
  'dataSources.emptyState.noArchivedHeading': 'No archived sources found',
  'dataSources.emptyState.noIntegrationsHeading': 'No integrations yet',
  'dataSources.emptyState.noManifestsAssignmentDescription':
    'Assignment data sources can be created from existing data warehouse integrations. Currently, there are no supported data warehouse integrations in your account.',
  'dataSources.emptyState.noManifestsAttributeDescription':
    'Attribute data sources can be created from existing data warehouse integrations. Currently, there are no supported data warehouse integrations in your account.',
  'dataSources.emptyState.noManifestsHeading': 'No supported data warehouse integrations',
  'dataSources.emptyState.noManifestsMetricDescription':
    'Metric data sources can be created from existing data warehouse integrations. Currently, there are no supported data warehouse integrations in your account.',
  'dataSources.emptyState.noResultsHeading': 'No data sources found',
  'dataSources.emptyState.resetFilters': 'Reset filters',
  'dataSources.emptyState.setUpAnyAssignmentDescription':
    'Set up an integration to analyze experiments direct from your warehouse data source.',
  'dataSources.emptyState.setUpAnyAttributeDescription':
    'Set up an integration to filter experiment results by attributes from your warehouse.',
  'dataSources.emptyState.setUpAnyMetricDescription':
    'Set up an integration to create metrics direct from your warehouse data source.',
  'dataSources.emptyState.setUpAssignmentDescription':
    'Set up a {integrationName} integration to analyze experiments direct from your warehouse data source.',
  'dataSources.emptyState.setUpAttributeDescription':
    'Set up a {integrationName} integration to filter experiment results by attributes from your warehouse.',
  'dataSources.emptyState.setUpIntegration': 'Set up integration',
  'dataSources.emptyState.setUpMetricDescription':
    'Set up a {integrationName} integration to create metrics direct from your warehouse data source.',
  'dataSources.emptyState.setUpNamedIntegration': 'Set up {integrationName}',
  'dataSources.experimentConnections.count': '{count, plural, one {# experiment} other {# experiments}}',
  'dataSources.experimentConnections.create': 'Create experiment',
  'dataSources.experimentConnections.emptyDescription':
    'Measure the impact of features, configurations, models, and prompts with flags you already use to ship code.',
  'dataSources.experimentConnections.emptyHeading': 'Create your first experiment',
  'dataSources.experimentConnections.heading': 'Experiments',
  'dataSources.experimentConnections.keyColumn': 'Key',
  'dataSources.experimentConnections.nameColumn': 'Name',
  'dataSources.experimentConnections.noPermissionTooltip': 'You do not have permission to create an experiment.',
  'dataSources.experimentConnections.statusColumn': 'Status',
  'dataSources.experimentConnections.statusTagLabel': 'Status',
  'dataSources.experimentConnections.tableLabel': 'Assignment data source connections',
  'dataSources.listPage.active': 'Active',
  'dataSources.listPage.archived': 'Archived',
  'dataSources.listPage.assignmentDescription': 'Reusable, warehouse-backed sources used to analyze experiments',
  'dataSources.listPage.assignmentRowLabel': 'Assignment data source: {key}',
  'dataSources.listPage.assignmentSearchPlaceholder': 'Find an assignment source by name or description',
  'dataSources.listPage.assignmentTableLabel': 'Assignment data sources table',
  'dataSources.listPage.assignmentsTab': 'Assignments',
  'dataSources.listPage.attributeDescription':
    'Reusable, warehouse-backed sources of attributes used to filter experiment results',
  'dataSources.listPage.attributeRowLabel': 'Attribute data source: {key}',
  'dataSources.listPage.attributeSearchPlaceholder': 'Find an attribute source by name or description',
  'dataSources.listPage.attributeTableLabel': 'Attribute data sources table',
  'dataSources.listPage.attributesColumn': 'Attributes',
  'dataSources.listPage.attributesTab': 'Attributes',
  'dataSources.listPage.attributesTagsLabel': 'Attributes in {name}',
  'dataSources.listPage.configurationRequiredLabel': 'Additional configuration required',
  'dataSources.listPage.configurationRequiredTooltip': 'Additional steps are required to configure data source.',
  'dataSources.listPage.contextKindsColumn': 'Context kinds',
  'dataSources.listPage.createAssignmentSource': 'Create assignment source',
  'dataSources.listPage.createAttributeSource': 'Create attribute source',
  'dataSources.listPage.createMetricSource': 'Create metric source',
  'dataSources.listPage.environmentColumn': 'Environment',
  'dataSources.listPage.experimentsColumn': 'Experiments',
  'dataSources.listPage.integrationBrokenLabel': 'Integration configuration broken',
  'dataSources.listPage.integrationBrokenTooltip': 'The integration for this data source appears to be broken.',
  'dataSources.listPage.integrationIconAlt': 'Warehouse integration icon',
  'dataSources.listPage.integrationSourceColumn': 'Integration source',
  'dataSources.listPage.keyCopiedToast': 'Data source key copied to clipboard',
  'dataSources.listPage.lastModifiedColumn': 'Last modified',
  'dataSources.listPage.loadError': 'There was an issue loading the data sources list.',
  'dataSources.listPage.metricDescription': 'Reusable, warehouse-backed sources used to create metrics',
  'dataSources.listPage.metricRowLabel': 'Metric data source: {key}',
  'dataSources.listPage.metricSearchPlaceholder': 'Find a metric source by name or description',
  'dataSources.listPage.metricTableLabel': 'Metric data sources table',
  'dataSources.listPage.metricsCreatedColumn': 'Metrics created',
  'dataSources.listPage.metricsTab': 'Metrics',
  'dataSources.listPage.moreAttributes': '+{count}',
  'dataSources.listPage.moreAttributesLabel': '{count, plural, one {# more attribute} other {# more attributes}}',
  'dataSources.listPage.nameColumn': 'Name',
  'dataSources.listPage.rowActionsLabel': 'Data source actions',
  'dataSources.listPage.searchPlaceholder': 'Find a data source',
  'dataSources.listPage.typeFilter': 'Type: <value>{type}</value>',
  'dataSources.mappingCard.addContextKind': 'Add another context kind',
  'dataSources.mappingCard.assignmentTitle': 'Map your assignment data to LaunchDarkly',
  'dataSources.mappingCard.attributeColumnsDescription':
    'Choose the columns to make available as context attributes in experiments.',
  'dataSources.mappingCard.attributeColumnsAllMapped':
    'All columns are mapped as a context or timestamp. Add a column to your query or change a mapping.',
  'dataSources.mappingCard.attributeColumnsLabel': 'Attribute columns',
  'dataSources.mappingCard.attributeColumnsUnavailable':
    'These selected columns are no longer available: {columns}. Choose different columns.',
  'dataSources.mappingCard.attributeTimestampDescription':
    'Choose the column that contains when each attribute value took effect.',
  'dataSources.mappingCard.attributeTitle': 'Map your attribute data to LaunchDarkly',
  'dataSources.mappingCard.attributeTypeDescription': 'Choose how the values in these columns change over time.',
  'dataSources.mappingCard.attributeTypeDynamic': 'Dynamic',
  'dataSources.mappingCard.attributeTypeDynamicDescription':
    'Values that can change, like <code>plan_tier</code>. Requires a timestamp column.',
  'dataSources.mappingCard.attributeTypeLabel': 'Attribute type',
  'dataSources.mappingCard.attributeTypeStatic': 'Static',
  'dataSources.mappingCard.attributeTypeStaticDescription':
    'Values that rarely change, like <code>user_country</code>. LaunchDarkly uses the first value it finds for each context.',
  'dataSources.mappingCard.columnNameLabel': 'Column name',
  'dataSources.mappingCard.columnNamesLabel': 'Column names',
  'dataSources.mappingCard.columnTypeLabel': 'Column type',
  'dataSources.mappingCard.completeAttributeQueryStep':
    'Complete <strong>Step 2: Connect and query your attribute data</strong> in order to map your data.',
  'dataSources.mappingCard.completeQueryStep':
    'Complete <strong>Step 2: Connect and query your assignment data</strong> in order to map your data.',
  'dataSources.mappingCard.contextColumnSelectLabel': 'Select context column',
  'dataSources.mappingCard.contextKindDescription':
    'Map a column containing unique identifiers for units to a context kind. For example, <code>user_id</code> to <code>user</code>.',
  'dataSources.mappingCard.contextKindLabel': 'Context kind',
  'dataSources.mappingCard.contextKindSelectLabel': 'Select context kind',
  'dataSources.mappingCard.contextKindStatusLabel': 'Context kind status',
  'dataSources.mappingCard.contextKindTip':
    'Tip: Add another context pair if your event record contains more than one context kind.',
  'dataSources.mappingCard.defaultTag': 'default',
  'dataSources.mappingCard.docLink': 'Read about mapping',
  'dataSources.mappingCard.editConfiguration': 'Edit configuration',
  'dataSources.mappingCard.experimentIdDescription':
    'Choose the column that contains the experiment identifier, such as <code>experiment_name</code> or <code>experiment_id</code>.',
  'dataSources.mappingCard.experimentIdLabel': 'Experiment ID',
  'dataSources.mappingCard.experimentIdSelectLabel': 'Select experiment ID column',
  'dataSources.mappingCard.metricTitle': 'Map your metric data to LaunchDarkly',
  'dataSources.mappingCard.noColumnsFound': 'No columns found.',
  'dataSources.mappingCard.noColumnsPlaceholder': 'No columns found',
  'dataSources.mappingCard.noMoreContextKindsTooltip':
    'No additional context kinds to add. Create more in project settings.',
  'dataSources.mappingCard.selectColumnPlaceholder': 'Select a column',
  'dataSources.mappingCard.subtext': 'Select which columns should map to the respective ID types in LaunchDarkly.',
  'dataSources.mappingCard.timestampDescription': 'Choose the column that contains when the event occurred.',
  'dataSources.mappingCard.timestampLabel': 'Timestamp',
  'dataSources.mappingCard.timestampSelectLabel': 'Select timestamp column',
  'dataSources.mappingCard.unmappedDescription':
    'This data source is missing column mappings. Make a selection after running the SQL query to fetch preview data from your warehouse.',
  'dataSources.mappingCard.unmappedHeading': 'Source columns not yet mapped',
  'dataSources.mappingCard.variantIdDescription':
    'Choose the column that contains the variant identifier, such as <code>control</code> or <code>treatment</code>.',
  'dataSources.mappingCard.variantIdLabel': 'Variant ID',
  'dataSources.mappingCard.variantIdSelectLabel': 'Select variant ID column',
  'dataSources.metricConnections.count': '{count, plural, one {# metric} other {# metrics}}',
  'dataSources.metricConnections.create': 'Create metric',
  'dataSources.metricConnections.emptyDescription':
    'Use metrics to measure audience behaviors affected by flags and experiments.',
  'dataSources.metricConnections.emptyHeading': 'Create your first metric',
  'dataSources.metricConnections.heading': 'Metrics',
  'dataSources.metricConnections.nameColumn': 'Name',
  'dataSources.metricConnections.noPermissionTooltip': 'You do not have permission to create a metric.',
  'dataSources.metricConnections.tableLabel': 'Metric data source connections',
  'dataSources.metricConnections.typeColumn': 'Type',
  'dataSources.metricsConfigurationPage.title': 'Configuration',
  'dataSources.metricsConnectionsNewMetricPage.title': 'Create metric',
  'dataSources.metricsConnectionsPage.title': 'Connections',
  'dataSources.metricsListPage.thereWasAn3': 'There was an issue loading metric data sources.',
  'dataSources.metricsListPage.title': 'Data sources | Metrics',
  'dataSources.metricsNewPage.cancel': 'Cancel',
  'dataSources.metricsNewPage.completeRequiredFields': 'Complete required fields before saving.',
  'dataSources.metricsNewPage.save': 'Save',
  'dataSources.metricsNewPage.thereWasAn4': 'There was an issue loading the create metric data source form.',
  'dataSources.metrics_Page.breadcrumb': 'Metrics',
  'dataSources.nameCard.assignmentDescriptionPlaceholder': 'Describe this assignment source',
  'dataSources.nameCard.assignmentTitle': 'Name your assignment data source',
  'dataSources.nameCard.attributeDescriptionPlaceholder': 'Describe this attribute source',
  'dataSources.nameCard.attributeTitle': 'Name your attribute data source',
  'dataSources.nameCard.descriptionFieldLabel': 'Enter a data source description',
  'dataSources.nameCard.descriptionLabel': 'Description',
  'dataSources.nameCard.emptyValue': 'None',
  'dataSources.nameCard.enterUniqueKey': 'Enter a unique key',
  'dataSources.nameCard.keyFieldLabel': 'Enter a unique data source key',
  'dataSources.nameCard.keyInUseTooltip': 'The data source <strong>{name}</strong> already uses this key.',
  'dataSources.nameCard.keyIsAvailable': 'Key is available',
  'dataSources.nameCard.keyLabel': 'Key',
  'dataSources.nameCard.keyLockedTooltip': 'You cannot modify the key after creation.',
  'dataSources.nameCard.keyPlaceholder': 'Data source key',
  'dataSources.nameCard.keyUnavailableLabel': 'Key is already in use by another data source',
  'dataSources.nameCard.metricDescriptionPlaceholder': 'Describe this metric source',
  'dataSources.nameCard.metricTitle': 'Name your metric data source',
  'dataSources.nameCard.nameFieldLabel': 'Enter a data source name',
  'dataSources.nameCard.nameLabel': 'Name',
  'dataSources.nameCard.namePlaceholder': 'Data source name',
  'dataSources.nameCard.subtext': 'Give this data source a name and description so others can easily find it.',
  'dataSources.page.failedToLoad5': 'Failed to load data sources. Try again',
  'dataSources.page.title': 'Data sources',
  'dataSources.page.tryAgain': 'Try again',
  'dataSources.queryCard.assignmentSubtext':
    'Identify the source data from your warehouse that will be used to create assignments.',
  'dataSources.queryCard.assignmentTitle': 'Connect and query your assignment data',
  'dataSources.queryCard.attributeSubtext':
    'Identify the source data from your warehouse that contains context attributes.',
  'dataSources.queryCard.attributeTitle': 'Connect and query your attribute data',
  'dataSources.queryCard.docLink': 'Read about querying',
  'dataSources.queryCard.metricSubtext':
    'Identify the source data from your warehouse that will be used to create metrics.',
  'dataSources.queryCard.metricTitle': 'Connect and query your metric data',
  'dataSources.rowMenu.archive': 'Archive',
  'dataSources.rowMenu.copyKey': 'Copy key',
  'dataSources.rowMenu.editConfiguration': 'Edit configuration',
  'dataSources.rowMenu.environmentLoadError':
    'The environment for this data source could not be loaded. Refresh the page and try again.',
  'dataSources.rowMenu.unarchive': 'Unarchive',
  'dataviz.areaChart.loadingData': 'Loading data…',
  'dataviz.areaChart.onlyShowing': 'Only showing',
  'dataviz.areaChart.summaryTooltip': 'After data for new and old are received, summaries are available',
  'dataviz.chart.columnChartLabel': 'Column chart visualization',
  'dataviz.chart.densityPlotLabel': 'Density plot visualization',
  'dataviz.chart.lineChartLabel': 'Line chart visualization',
  'dataviz.chart.loadingLabel': 'Loading chart data',
  'dataviz.chart.resetZoom': 'Reset',
  'dataviz.chart.resetZoomLabel': 'Reset zoom',
  'dataviz.emptyState.body': 'Try adjusting the filter settings',
  'dataviz.emptyState.heading': 'No data to show',
  'dataviz.legend.showLess': 'Show less',
  'dataviz.legend.showMore': '+{count} more',
  'dataviz.legend.toggleLabel': 'Toggle {name}',
  'dataviz.miniChart.columnChartLabel': 'Mini column chart visualization',
  'dataviz.miniChart.loadingLabel': 'Loading chart',
  'dataviz.miniChart.noDataLabel': 'No data',
  'dataviz.pieChart.noData': 'No data',
  'dataviz.pieChart.noDataLabel': 'Pie chart, no data',
  'dataviz.pieChart.sliceLabel': '{label} {percent}% ({value})',
  'dataviz.pieChart.summaryLabel': 'Pie chart showing {slices}',
  'environments.approvalSettings.bypassBody':
    'Members with permission to bypass required approvals will still be able to make flag changes.',
  'environments.approvalSettings.cancel': 'Cancel',
  'environments.approvalSettings.comingSoonTag': 'Coming soon',
  'environments.approvalSettings.disabledIntegrationTooltip':
    'You must enable this integration configuration before using it',
  'environments.approvalSettings.heading': 'Approval settings for',
  'environments.approvalSettings.learnMore': 'Learn more',
  'environments.approvalSettings.oauthRequiredTooltip': 'OAuth is required for this approval system',
  'environments.approvalSettings.save': 'Save changes',
  'environments.approvalSettings.savingText': 'Saving settings',
  'environments.approvalSettings.systemLabel': 'Approval system',
  'environments.createEnvironment.heading': 'Create an environment',
  'environments.criticalBadge.label': 'Critical',
  'environments.criticalBadge.tooltip':
    'Critical environments affect your customers or the health of your systems and may have added safeguards in place.',
  'environments.criticalEnvironmentDialog.body':
    'We marked Production as a critical environment. You can customize safeguards below or at a later time in environment settings.',
  'environments.criticalEnvironmentDialog.cancel': 'Cancel',
  'environments.criticalEnvironmentDialog.criticalEnvironmentLabel': 'Critical environment',
  'environments.criticalEnvironmentDialog.existingSafeguardsBody':
    'Keep or change your existing safeguards for this environment. You can also update these later in environment settings.',
  'environments.criticalEnvironmentDialog.getStarted': 'Get started',
  'environments.criticalEnvironmentDialog.learnMore': 'Learn more.',
  'environments.criticalEnvironmentDialog.markConfirm': 'Mark as critical',
  'environments.criticalEnvironmentDialog.markTitle': 'Mark environment as critical',
  'environments.criticalEnvironmentDialog.recommendedSafeguardsHeading': 'Recommended safeguards',
  'environments.criticalEnvironmentDialog.requireCommentsDescription':
    'In this environment, require the member to enter a comment for every flag or segment change before saving',
  'environments.criticalEnvironmentDialog.requireCommentsLabel': 'Require comments for flag and segment changes',
  'environments.criticalEnvironmentDialog.requireConfirmationDescription':
    'In this environment, require the member to confirm the name of the environment for every flag or segment change',
  'environments.criticalEnvironmentDialog.requireConfirmationLabel':
    'Require confirmation for flag and segment changes',
  'environments.criticalEnvironmentDialog.safeguardsBody':
    'Enable safeguards for environments that affect your customers or the health of your systems.',
  'environments.criticalEnvironmentDialog.title': 'Safeguarding critical environments',
  'environments.criticalEnvironmentDialog.unmarkConfirm': 'Unmark as critical',
  'environments.criticalEnvironmentDialog.unmarkTitle': 'Unmark environment as critical',
  'environments.deleteEnvironmentModal.blockedHeading': 'Environment cannot be deleted',
  'environments.deleteEnvironmentModal.cancel': 'Cancel',
  'environments.deleteEnvironmentModal.delete': 'Delete',
  'environments.deleteEnvironmentModal.releasePipelineBody':
    'You must delete the release pipeline associated with this environment first. This environment is being used in <pipeline></pipeline>.',
  'environments.deleteEnvironmentModal.title': 'Delete {name}?',
  'environments.deleteEnvironmentModal.vercelWarning':
    "This environment's SDK key is used by your <b>Vercel integration</b>. Deleting it will break flag evaluation in any linked projects.",
  'environments.deleteUsageAlert.flagCount': '{count} flags',
  'environments.deleteUsageAlert.heading': 'Deleting this environment may cause unintended effects',
  'environments.deleteUsageAlert.lastRequestedAgo': ' was requested {duration} ago.',
  'environments.deleteUsageAlert.mostRecentFlagPrefix': 'Most recent flag requested:',
  'environments.deleteUsageAlert.noRecentFlags': 'No flags requested in the last 7 days',
  'environments.deleteUsageAlert.recentlyRequestedSuffix':
    'in this environment have been requested in the last 7 days.',
  'environments.editEnvironment.deleteWarning':
    'This environment will be deleted permanently, and its SDK key will be invalidated <strong>for all members of your organization. </strong>All clients must be updated to use another SDK key immediately.',
  'environments.editEnvironment.drawerTitle': 'Edit {name} environment',
  'environments.editEnvironment.keepOldKeyPrefix': 'Keep the old SDK key active for',
  'environments.editEnvironment.keepOldKeySuffix': 'hours',
  'environments.editEnvironment.keysBody':
    'Resetting a key invalidates it, so any clients using it must be updated to use the new key. Optionally, choose how many hours to keep the current SDK key active.',
  'environments.editEnvironment.keysHeading': 'Keys',
  'environments.editEnvironment.resetConfirmBody':
    'Your old key will be invalidated, so any clients must be updated to use the new key.{withExpiry, select, true { Optionally, choose how many hours to keep the current SDK key active.} other {}}',
  'environments.editEnvironment.resetConfirmLabel': 'Reset',
  'environments.editEnvironment.resetConfirmTitle': 'Reset {keyName} key?',
  'environments.editEnvironment.resetMobileKey': 'Reset mobile key',
  'environments.editEnvironment.resetSdkKey': 'Reset SDK key',
  'environments.editEnvironment.sdkKeyExpiryHoursError': 'This should be a positive number up to 720 (1 month)',
  'environments.editEnvironment.sdkKeyExpiryHoursLabel': 'Number of hours before the old SDK key expires',
  'environments.environmentForm.confirmChangesHint':
    'In this environment, require the user to confirm the name of the environment for every flag or segment change',
  'environments.environmentForm.confirmChangesLabel': 'Require confirmation for flag and segment changes',
  'environments.environmentForm.criticalHint':
    'Enable safeguards for environments that affect your customers or the health of your systems.',
  'environments.environmentForm.criticalLabel': 'Critical environment',
  'environments.environmentForm.criticalLearnMore': 'Read about critical environments',
  'environments.environmentForm.keyHint':
    'We use the key to give you friendly URLs. Keys should be at most 20 characters and must only contain letters, numbers,',
  'environments.environmentForm.keyLabel': 'Key',
  'environments.environmentForm.nameLabel': 'Name',
  'environments.environmentForm.requireCommentsHint':
    'In this environment, require the user to enter a comment for every flag or segment change before saving',
  'environments.environmentForm.requireCommentsLabel': 'Require comments for flag and segment changes',
  'environments.environmentForm.reservedKeyHint': 'You cannot use <code>new</code> as a key.',
  'environments.environmentForm.secureModeHint':
    "Secure mode ensures that one user can't impersonate another when using our client-side SDKs.",
  'environments.environmentForm.secureModeLabel': 'Enable secure mode',
  'environments.environmentForm.settingsHeading': 'Environment settings',
  'environments.environmentForm.tagsButton': 'Tags: {tags}',
  'environments.environmentForm.trackEventsHint': 'This setting does not affect existing flags',
  'environments.environmentForm.trackEventsLabel':
    'Send detailed events to Data Export destinations by default for all new flags in this environment',
  'environments.environmentForm.ttlHint':
    'This sets the number of minutes that the PHP SDK can cache feature flag rules locally. Enter a value between 0 and 60. To learn more,',
  'environments.environmentForm.ttlLabel': 'TTL (0-60 minutes)',
  'environments.environmentLabel.environment': 'Environment |',
  'environments.environmentLabel.project': 'Project |',
  'environments.environmentPicker.addEnvironment': 'Add environment',
  'environments.environmentPicker.searchPlaceholder': 'Search environments',
  'environments.environmentPicker.selectLabel': '{count, plural, one {Select environment} other {Select environments}}',
  'environments.environmentPicker.selectedCount':
    '{count} {count, plural, one {environment} other {environments}} selected',
  'environments.notFound.body': "Sorry, we can't find the environment you're looking for.",
  'environments.notFound.heading': 'Environment not found',
  'environments.notifications.created': 'Environment created',
  'environments.notifications.deleted': 'Environment deleted',
  'environments.notifications.mobileKeyReset': 'Mobile key reset',
  'environments.notifications.sdkKeyReset': 'SDK key reset',
  'environments.notifications.updated': 'Environment updated',
  'environments.projectEnvironmentSelector.docsLink': 'Read about projects',
  'environments.projectEnvironmentSelector.environmentLoadingLabel': 'Loading…',
  'environments.projectEnvironmentSelector.environmentSearchLabel': 'Search environments',
  'environments.projectEnvironmentSelector.environmentsLoadingLabel': 'Loading environments',
  'environments.projectEnvironmentSelector.heading': 'Choose project and environment',
  'environments.projectEnvironmentSelector.menuLabel': 'Environment selector menu',
  'environments.projectEnvironmentSelector.projectLoadingLabel': 'Loading…',
  'environments.projectEnvironmentSelector.projectSearchLabel': 'Search projects',
  'environments.projectEnvironmentSelector.projectsLoadingLabel': 'Loading projects',
  'environments.projectEnvironmentSelector.projectsMenuLabel': 'Projects',
  'environments.projectEnvironmentSelector.tooltipBody':
    '<strong>Projects</strong> let you organize different business goals in one LaunchDarkly account. <strong>Environments</strong> help you manage flags across your development lifecycle, from local dev to production.',
  'environments.status.loading': 'Loading…',
  'environments.vercelIndicator.label': 'Vercel',
  'environments.vercelIndicator.tooltip': 'This environment is linked to Vercel',
  'errors.accessRestricted.body':
    'Connect from an allowed network, or contact your LaunchDarkly administrator to add your IP address to the allowlist.',
  'errors.accessRestricted.heading': 'Access restricted',
  'errors.accessRestricted.subheading': "Your IP address is not in your organization's IP allowlist.",
  'errors.accountUnavailable.body': 'See our <terms>terms of service</terms> or <support></support> for more details.',
  'errors.accountUnavailable.heading': 'Account unavailable',
  'errors.accountUnavailable.subheading': 'Your account has been disabled.',
  'errors.accountUnavailable.supportLink': 'contact our support team',
  'errors.genericError.body': "We ran into an unexpected error. We've been notified. Try again later or",
  'errors.genericError.heading': 'Uh oh, something went wrong',
  'errors.noProjectAccess.body':
    'Contact your LaunchDarkly administrator to request access, or <support></support> for help.',
  'errors.noProjectAccess.heading': 'No project access',
  'errors.noProjectAccess.subheading': "You don't have access to any projects in this account.",
  'errors.noProjectAccess.supportLink': 'contact our support team',
  'errors.notFound.approvalRequest': 'Approval request not found',
  'errors.notFound.environment': 'Environment {name} no longer exists',
  'errors.notFound.generic': 'Not found',
  'errors.notFound.project': 'Project {name} no longer exists',
  'errors.notFound.workflow': 'Workflow {name} no longer exists',
  'errors.notFound.workflowTemplate': 'Template with key "{key}" no longer exists',
  'errors.notFoundPage.404ErrorSorry': "404 error. Sorry, but the page you're looking for doesn't exist.",
  'errors.notFoundPage.alt': 'LaunchDarkly',
  'errors.notFoundPage.floatingAstronautIn':
    'Floating astronaut in LaunchDarkly branded sneakers with arms crossed behind their head',
  'errors.notFoundPage.label': 'Astronaut floating in dark space with stars and planets in the distance',
  'errors.notFoundPage.largePlanetIn': 'Large planet in the distance',
  'errors.notFoundPage.lostInSpace': 'Lost in space',
  'errors.notFoundPage.returnHome': 'Return home',
  'errors.notFoundPage.smallPlanetIn': 'Small planet in the distance',
  'errors.notFoundPage.threeShootingStars': 'Three shooting stars that have the appearance of a feature flag toggle',
  'errors.notFoundPage.title': 'Page not found',
  'errors.page.title': 'Errors',
  'errors.projectAccessDenied.projectBody':
    'Your role does not include access to the project <key>{projectKey}</key>. Contact your LaunchDarkly administrator to request access.',
  'errors.projectAccessDenied.resourceBody':
    'Your role does not include permission to view this part of the project <key>{projectKey}</key>. Contact your LaunchDarkly administrator to request access.',
  'errors.quietError.body': 'Something went wrong. Note that in production this message will not show.',
  'errors.request.accessDenied': 'Access denied. Refresh the page and try again.',
  'errors.request.copyDetails': 'Copy details',
  'errors.request.methodNotAllowed': 'This method is not allowed',
  'errors.request.notAuthorized': 'Not authorized. Log in to continue.',
  'errors.request.outOfDate':
    'Your {resource, select, flagConfig {flag configuration} segmentConfig {segment configuration} account {account} destination {destination} metric {metric configuration} starredEnvs {list of favorite environments} payloadFilter {payload filter} contextKind {context kind} member {member} other {resource}} is out of date. Reload the page and try again.',
  'errors.request.reloadAndTryAgain': 'Reload the page and try again.',
  'errors.request.tooManyRequests': "We've received too many requests from your account. Try again later.",
  'errors.request.unableToProcess': 'We were unable to process your request.',
  'errors.request.unexpected': 'An unexpected error occurred. Try again later.',
  'errors.retryFallback.body': 'Something went wrong.',
  'errors.retryFallback.retry': 'Try again',
  'eventExplorer.emptyState.createMetric': 'Create metric',
  'eventExplorer.emptyState.description':
    'Call the <code>track()</code> method in your SDK to start emitting events. If you’re using the JavaScript SDK, define a “Clicked/tapped” or “Page viewed” metric and the SDK emits these events automatically. If your events come from a data warehouse data source, LaunchDarkly checks it for new events every 15 minutes.',
  'eventExplorer.emptyState.heading': 'No events yet',
  'eventExplorer.emptyState.noMatchDescription': 'Adjust or clear your filters',
  'eventExplorer.emptyState.noMatchHeading': 'No events match your search criteria',
  'eventExplorer.eventDetails.contextKindsDescription': 'These following context kinds are associated with this event',
  'eventExplorer.eventDetails.contextKindsHeading': 'Context kinds',
  'eventExplorer.eventDetails.contextsTableLabel': 'table',
  'eventExplorer.eventDetails.environmentCount':
    '<strong>{count}</strong> {count, plural, one {environment} other {environments}}',
  'eventExplorer.eventDetails.environmentSearchPlaceholder': 'Find an environment',
  'eventExplorer.eventDetails.environmentSeenLabel': '{name} - Seen {timeAgo} ago',
  'eventExplorer.eventDetails.environmentsListLabel': 'Environments',
  'eventExplorer.eventDetails.kindColumn': 'Kind',
  'eventExplorer.eventDetails.lastSeenDescription': 'See when this event was last observed across all environments.',
  'eventExplorer.eventDetails.lastSeenHeading': 'Last seen event',
  'eventExplorer.eventDetails.metricCount': '<strong>{count}</strong> {count, plural, one {metric} other {metrics}}',
  'eventExplorer.eventDetails.metricSearchPlaceholder': 'Find a metric',
  'eventExplorer.eventDetails.metricsDescription': 'Metrics created from this event',
  'eventExplorer.eventDetails.metricsHeading': 'Metrics',
  'eventExplorer.eventDetails.metricsListLabel': 'Metrics',
  'eventExplorer.eventDetails.noEnvironmentsFound': 'No environments found',
  'eventExplorer.eventDetails.noMetricsFound': 'No metrics found',
  'eventExplorer.eventDetails.seenPrefix': 'Seen',
  'eventExplorer.eventDetails.trendDescription': 'Showing the count of events over the last 14 days.',
  'eventExplorer.eventDetails.trendHeading': '14 day trend',
  'eventExplorer.eventDetails.uniqueKeysColumn': 'Count of unique keys',
  'eventExplorer.eventDetails.uniqueKeysTooltip':
    'Count of unique keys over last 14 days excludes events from any data warehouse integrations.',
  'eventExplorer.eventDetails.uniqueKeysTooltipLabel': 'Event count info',
  'eventExplorer.eventDetails.uniqueKeysWarehouseTooltip':
    'Count of unique keys over last 14 days from any data warehouse integrations is not available to be shown at this time.',
  'eventExplorer.eventDrawer.closeLabel': 'close drawer',
  'eventExplorer.eventDrawer.copyEventKey': 'Copy event key',
  'eventExplorer.eventDrawer.createNewMetric': 'Create new metric',
  'eventExplorer.eventDrawer.customEventsOnlyTooltip': 'New metrics can only be created for custom events',
  'eventExplorer.eventDrawer.eventDetailsTab': 'Event details',
  'eventExplorer.eventDrawer.instancesLoadError':
    "An unexpected error has occurred while loading this event's instances",
  'eventExplorer.eventDrawer.learnMore': '<link>Learn More</link>.',
  'eventExplorer.eventDrawer.loadingLabel': 'Loading event details',
  'eventExplorer.eventDrawer.noDataBody': 'Event data from data warehouse integrations does not display here.',
  'eventExplorer.eventDrawer.noDataHeading': 'No data available',
  'eventExplorer.eventDrawer.previewTab': 'Preview',
  'eventExplorer.eventDrawer.tabsLabel': 'tabs',
  'eventExplorer.eventList.createMetricNoPermission': 'You do not have permission to create a metric',
  'eventExplorer.eventList.createNewMetric': 'Create new metric',
  'eventExplorer.eventList.eventCountColumn': 'Event count',
  'eventExplorer.eventList.eventCountTooltip':
    'Number of events over the last 14 days, and the % change from the previous 14-day period',
  'eventExplorer.eventList.eventCountTooltipLabel': 'Event count info',
  'eventExplorer.eventList.eventKeyColumn': 'Event key',
  'eventExplorer.eventList.eventKeyTooltip':
    'The {count} most recent event keys in the selected environment over the last 90 days',
  'eventExplorer.eventList.eventKeyTooltipLabel': 'Event count info',
  'eventExplorer.eventList.lastSeenColumn': 'Last seen',
  'eventExplorer.eventList.lastSeenPopoverHeading': 'Events last seen across environments',
  'eventExplorer.eventList.loadingEnvironment': 'Loading Environment',
  'eventExplorer.eventList.loadingEventCount': 'Loading event count',
  'eventExplorer.eventList.loadingEventKey': 'Loading Event Key',
  'eventExplorer.eventList.loadingLastOccurrence': 'Loading Last Occurance',
  'eventExplorer.eventList.loadingMetrics': 'Loading Metrics Associated with Key',
  'eventExplorer.eventList.loadingRowLabel': 'Loading Events Last Seen',
  'eventExplorer.eventList.metricsColumn': 'Metrics',
  'eventExplorer.eventList.metricsLoadError': 'Failed to load associated metrics',
  'eventExplorer.eventList.noEnvironmentsFound': 'No environments found',
  'eventExplorer.eventList.rowLabel': 'Event: {eventKey}',
  'eventExplorer.eventList.showLess': 'Show less',
  'eventExplorer.eventList.showMore': 'Show more results',
  'eventExplorer.eventList.showingCount': 'Showing {count} events',
  'eventExplorer.eventList.sourcesColumn': 'Sources',
  'eventExplorer.eventList.sourcesTooltip': 'Event sources in the selected environment over the last 14 days',
  'eventExplorer.eventList.sourcesTooltipLabel': 'Event count info',
  'eventExplorer.eventList.tableLabel': 'Event List',
  'eventExplorer.eventList.trendColumn': '14 day trend',
  'eventExplorer.filters.dataSourceField': 'Data source',
  'eventExplorer.filters.dataSourceFieldDescription': 'The data source the events come from.',
  'eventExplorer.filters.launchDarklyNative': 'LaunchDarkly native',
  'eventExplorer.filters.searchField': 'Search',
  'eventExplorer.filters.searchFieldDescription': 'A search across event keys.',
  'eventExplorer.loadError.message': 'Something went wrong while fetching events. Try again',
  'eventExplorer.loadError.refetch': 'Refetch',
  'eventExplorer.rowActions.copiedToast': 'Copied',
  'eventExplorer.rowActions.copyEventKey': 'Copy event key',
  'eventExplorer.rowActions.createNewMetric': 'Create new metric',
  'eventExplorer.rowActions.menuLabel': 'Event Actions',
  'eventExplorer.search.searchLabel': 'search events',
  'eventExplorer.search.searchPlaceholder': 'Find events by key',
  'eventExplorer.trendChart.barLabel': '{count} events on {date}',
  'eventExplorer.trendChart.chartLabel': '14 Day Trend',
  'eventExplorer.trendChart.loadingLabel': 'Loading Trend',
  'eventExplorer.trendChart.noEvents': 'No events in the last 14 days',
  'experiments.aaTestGuidance.heading': 'A/A test guidance',
  'experiments.aaTestGuidance.learnMore': 'Learn more',
  'experiments.addAttributesDialog.add': 'Add',
  'experiments.addAttributesDialog.addCustomAttribute': 'Add "{attributeKey}"',
  'experiments.addAttributesDialog.addError': 'Something went wrong while adding attributes',
  'experiments.addAttributesDialog.allFilter': 'All',
  'experiments.addAttributesDialog.allFilterLabel': 'All attributes',
  'experiments.addAttributesDialog.allGroupLabel': 'All attributes group',
  'experiments.addAttributesDialog.cancel': 'Cancel',
  'experiments.addAttributesDialog.clearSelection': 'Clear selection',
  'experiments.addAttributesDialog.closeLabel': 'close modal',
  'experiments.addAttributesDialog.currentlySelectedFilter': 'Currently selected',
  'experiments.addAttributesDialog.currentlySelectedFilterLabel': 'Currently selected attributes',
  'experiments.addAttributesDialog.eligibleFilter': 'Eligible',
  'experiments.addAttributesDialog.eligibleFilterLabel': 'Eligible attributes',
  'experiments.addAttributesDialog.eligibleGroupLabel': 'Eligible attributes group',
  'experiments.addAttributesDialog.emptyBody':
    'Attributes will become available when the experiment has enough exposures',
  'experiments.addAttributesDialog.emptyHeading': 'No attributes available yet',
  'experiments.addAttributesDialog.heading': 'Add attributes',
  'experiments.addAttributesDialog.listBoxLabel': 'Select attributes',
  'experiments.addAttributesDialog.loadError': 'Unexpected error loading iteration attributes.',
  'experiments.addAttributesDialog.menuLabel': 'Attributes menu',
  'experiments.addAttributesDialog.noMatches': 'No matching attributes',
  'experiments.addAttributesDialog.noneObserved': 'No attributes observed yet',
  'experiments.addAttributesDialog.notEligibleFilter': 'Not eligible',
  'experiments.addAttributesDialog.notEligibleFilterLabel': 'Not eligible attributes',
  'experiments.addAttributesDialog.searchLabel': 'Search attributes',
  'experiments.addAttributesDialog.selectedCount': '<highlight>{count}</highlight> selected',
  'experiments.addAttributesDialog.subtitle': 'Break down your results across more attributes.',
  'experiments.addMetricsDialog.add': 'Add',
  'experiments.addMetricsDialog.addError': 'Something went wrong while adding metrics',
  'experiments.addMetricsDialog.cancel': 'Cancel',
  'experiments.addMetricsDialog.closeLabel': 'close modal',
  'experiments.addMetricsDialog.heading': 'Add metrics',
  'experiments.addMetricsDialog.removeHint': 'To remove metrics, start a new iteration.',
  'experiments.addMetricsDialog.subtitle': 'Break down your results across more metrics.',
  'experiments.advancedSection.attributesDescription':
    'Attributes give you the ability to segment your results by context attribute after your experiment has started. You can select more attributes on the results page when an experiment is running.',
  'experiments.advancedSection.clearHoldoutLabel': 'Clear holdout selection',
  'experiments.advancedSection.heading': 'Advanced',
  'experiments.advancedSection.holdout': 'Holdout',
  'experiments.advancedSection.holdoutComboBoxLabel': 'Select a holdout',
  'experiments.advancedSection.holdoutDescription':
    'Include this experiment in a holdout to measure the overall effect of a multiple experiments against a control group.',
  'experiments.advancedSection.holdoutErrorBody': 'An error occurred while selecting the holdout.',
  'experiments.advancedSection.holdoutErrorTitle': 'Error selecting holdout',
  'experiments.advancedSection.holdoutIncompatibleTitle': 'Holdout not compatible',
  'experiments.advancedSection.holdoutNotReadyBody': 'Cannot select a holdout that has not been declared ready yet.',
  'experiments.advancedSection.holdoutPrerequisites':
    'You must select a flag and randomization unit before adding a holdout.',
  'experiments.advancedSection.holdoutResultsLabel': 'holdout search results',
  'experiments.advancedSection.holdoutSearchPlaceholder': 'Search a holdout',
  'experiments.advancedSection.holdoutUnitMismatchBody':
    'Cannot select a holdout with a different randomization unit than the experiment.',
  'experiments.advancedSection.holdoutWithLayerDisabled': 'You cannot add a holdout to an experiment with a layer.',
  'experiments.advancedSection.learnMore': 'Learn more',
  'experiments.advancedSection.loadingHoldoutsLabel': 'loading holdouts',
  'experiments.advancedSection.loadingHoldoutsPlaceholder': 'Loading holdouts…',
  'experiments.advancedSection.noHoldoutsFound': 'No holdouts found',
  'experiments.advancedSection.openHoldoutsLabel': 'Select holdout',
  'experiments.allocationChart.allocation': 'Allocation',
  'experiments.allocationChart.chartLabel': 'traffic chart exposures',
  'experiments.allocationChart.variation': 'Variation',
  'experiments.allocationPreview.controlLabel': 'Control',
  'experiments.allocationPreview.hideValues': 'Hide {type} values',
  'experiments.allocationPreview.showValues': 'Show {type} values',
  'experiments.allocationPreview.variationImageAlt': '{variation} variation preview',
  'experiments.allocationRequirementAlert.accountUpdateRequired': 'Account update required',
  'experiments.allocationRequirementAlert.body':
    'As of April 15, 2022, all new experiments must use experiment allocation rules to configure experiment audiences. Confirm your account meets <requirements></requirements>, and <support></support> to enable Experiment Allocation rules.',
  'experiments.allocationRequirementAlert.minimumRequirements': 'the minimum requirements',
  'experiments.amountOptions.custom': 'Custom',
  'experiments.amountOptions.customAmountLabel': 'Custom amount',
  'experiments.analysisCard.bayesianThresholdBody':
    "The threshold helps decide if a treatment variation performs meaningfully different from the control. If a treatment's probability to beat control meets or exceeds this threshold, you can consider it the best-performing variation. A common threshold used is 90%. The threshold doesn't change most of the reported numbers, except for the credible interval.",
  'experiments.analysisCard.bayesianThresholdTitle': 'Threshold',
  'experiments.analysisCard.cuped': 'CUPED',
  'experiments.analysisCard.cupedBody':
    "Improve the measurement of an experiment's outcome with covariate adjustments.",
  'experiments.analysisCard.cupedCaveat':
    'CUPED affects only average metrics. It does not apply to percentile or ratio metrics.',
  'experiments.analysisCard.cupedDetailsLabel': 'View CUPED details',
  'experiments.analysisCard.editLabel': 'edit button',
  'experiments.analysisCard.frequentistThresholdBody':
    "The significance level is a threshold used to determine whether a result is meaningful, or statistically significant, rather than just due to chance. If a treatment variation's p-value is equal to or smaller than the significance level, you can consider the result statistically significant. A common significance level is 0.05.",
  'experiments.analysisCard.frequentistThresholdTitle': 'Significance level',
  'experiments.analysisCard.funnelGroup': 'Funnel group',
  'experiments.analysisCard.learnMore': 'Learn more',
  'experiments.analysisCard.mcc': 'MCC',
  'experiments.analysisCard.mccDetailsLabel': 'View multiple comparison correction details',
  'experiments.analysisCard.mccHeading': 'Multiple comparison correction',
  'experiments.analysisCard.metricAnalysisError': 'Error loading analysis for metric {metricKey}',
  'experiments.analysisCard.primaryMetric': 'Primary metric',
  'experiments.analysisCard.resultsPending': 'Results pending',
  'experiments.analysisCard.secondaryMetrics': 'Secondary metrics',
  'experiments.analysisCard.sequentialTesting': 'Sequential testing',
  'experiments.analysisCard.sequentialTestingDetailsLabel': 'View sequential testing details',
  'experiments.analysisCard.thresholdDetailsLabel': 'View analysis threshold details',
  'experiments.analysisTable.adjusted': 'adjusted',
  'experiments.analysisTable.aggregatedExposuresLabel': 'Exposures',
  'experiments.analysisTable.aggregatedUnitsLabel': 'Units included in analysis',
  'experiments.analysisTable.analysisLoadError': 'Unexpected error loading analysis',
  'experiments.analysisTable.comparisonClauseDoesNotMeet': 'does not meet the threshold set for this experiment',
  'experiments.analysisTable.comparisonClauseMeets': 'meets the threshold set for this experiment',
  'experiments.analysisTable.comparisonClauseRegression':
    'is at or below 100% minus the threshold set for this experiment',
  'experiments.analysisTable.conversionRateColumn': 'Conversion rate',
  'experiments.analysisTable.conversionRateDescription':
    'The likely conversion rate if you were to release this variation',
  'experiments.analysisTable.conversionsColumn': 'Conversions',
  'experiments.analysisTable.conversionsDescription':
    'The total number of conversions (sum of unit values){hasFilters, select, true {. The conversion count will not include events filtered out by any filters applied to this metric.} other {}}',
  'experiments.analysisTable.desiredDirection': 'Desired direction',
  'experiments.analysisTable.desiredWord': 'desired',
  'experiments.analysisTable.directionNegative': 'negative',
  'experiments.analysisTable.directionPositive': 'positive',
  'experiments.analysisTable.doesNotMeetThreshold': 'Does not meet threshold',
  'experiments.analysisTable.expectedLossColumn': 'Expected loss',
  'experiments.analysisTable.expectedLossDescription':
    'The average potential difference in performance between this variation and control, measuring how much worse this variation might perform compared to the control.',
  'experiments.analysisTable.exposuresColumn': 'Exposures',
  'experiments.analysisTable.exposuresDescription':
    'The number of randomization units used to analyze this metric{hasFilters, select, true {. The exposure count will not change based on context attribute filters applied to this metric} other {}}',
  'experiments.analysisTable.formulaConversions': 'Conversions',
  'experiments.analysisTable.formulaTotalValue': 'Total value',
  'experiments.analysisTable.graphColumn': 'Graph',
  'experiments.analysisTable.learnMore': 'Learn more',
  'experiments.analysisTable.mccAdjusted': 'MCC-adjusted',
  'experiments.analysisTable.meanColumn': 'Mean',
  'experiments.analysisTable.meanDescriptionAverage': 'The likely average if you were to release this variation',
  'experiments.analysisTable.meanDescriptionPercentile':
    'The likely percentile value if you were to release this variation',
  'experiments.analysisTable.meetsThreshold': 'Meets threshold',
  'experiments.analysisTable.metricAnalysisLoadError': 'Unexpected error loading analysis metric',
  'experiments.analysisTable.metricValueLabelMean': 'The mean',
  'experiments.analysisTable.metricValueLabelPercentile': 'The {analysisType}',
  'experiments.analysisTable.metricValueLabelRatio': 'The ratio metric value',
  'experiments.analysisTable.mixedGranularityBody':
    'The selected analysis unit contains a mix of treatments, so results for this metric cannot be calculated. <link>Read about analysis units</link>',
  'experiments.analysisTable.mixedGranularityHeading': 'Unable to analyze results for this metric',
  'experiments.analysisTable.notAvailable': 'N/A',
  'experiments.analysisTable.notStatisticallySignificant': 'Not statistically significant',
  'experiments.analysisTable.pValueColumn': 'p-value',
  'experiments.analysisTable.pValueDescription':
    'The lower the p-value, the less likely the relative difference is due to chance alone. The significance level that you set determines if your results are significant.',
  'experiments.analysisTable.pbbColumn': 'Prob. to be best',
  'experiments.analysisTable.pbbDescription': 'The likelihood that this variation is best of the ones tested',
  'experiments.analysisTable.pbcColumn': 'Prob. to beat Control',
  'experiments.analysisTable.pbcDescription': 'The likelihood that this variation is better than the Control',
  'experiments.analysisTable.percentileNotSupported':
    'This data view is not supported because percentiles are in beta.',
  'experiments.analysisTable.percentileValueColumn': 'Percentile value',
  'experiments.analysisTable.posteriorMeanColumn': 'Posterior mean',
  'experiments.analysisTable.posteriorPercentileColumn': 'Posterior percentile',
  'experiments.analysisTable.primaryMetric': 'Primary metric',
  'experiments.analysisTable.rdcClosingBayesian':
    "<treatment></treatment>'s probability to beat <control></control> is <probability></probability>, which {comparisonClause} (<threshold></threshold>).",
  'experiments.analysisTable.rdcClosingBayesianFallback': 'The relative difference does not meet the threshold.',
  'experiments.analysisTable.rdcClosingFrequentist':
    'The relative difference is {significance} statistically significant.',
  'experiments.analysisTable.rdcColumn': 'Relative difference',
  'experiments.analysisTable.rdcDescription':
    'The relative difference between the variation’s {valueTerm} and control’s {valueTerm}',
  'experiments.analysisTable.rdcExplanation':
    '{metricValueLabel} for <treatment></treatment> differs {isRatio, select, true {from} other {by}} that of <control></control> by <rdc></rdc> in relative terms, with a {successCriteria} from <lower></lower> to <upper></upper>. <closing></closing>',
  'experiments.analysisTable.rdcFormulaDenominator': '/ Control’s {valueTerm}',
  'experiments.analysisTable.rdcFormulaNumerator': '( Variation’s {valueTerm} - Control’s {valueTerm} )',
  'experiments.analysisTable.rdcNotSignificantClause': 'not',
  'experiments.analysisTable.rdcSignificantClause': '{direction} (the {desired} direction) and',
  'experiments.analysisTable.resultExplanationBayesian':
    "<treatment></treatment>'s probability to beat <control></control> is <probability></probability> which is equal to or above the threshold set for this experiment{hasThreshold, select, true { (<threshold></threshold>)} other {}}.",
  'experiments.analysisTable.resultExplanationFrequentist':
    'This comparison is {significant, select, true {} other {not}} statistically significant because the p-value is {significant, select, true { less} other {greater}} than the {adjective} significance level of <value></value>.',
  'experiments.analysisTable.sampleSizeColumn': 'Sample size',
  'experiments.analysisTable.sampleSizeDescription':
    'Analysis units considered in experiment results{hasFilters, select, true {. The unit count will not change based on context attribute filters applied to this metric} other {}}',
  'experiments.analysisTable.sliceNotAvailableBody':
    'This metric does not have results for the attribute value you selected. Choose a different value, or view all attributes.',
  'experiments.analysisTable.sliceNotAvailableHeading': 'Not available for this metric',
  'experiments.analysisTable.slicePendingHeading': 'Results pending for this metric',
  'experiments.analysisTable.slicedAdjustmentsDetails':
    'Filtered attribute results are not adjusted for CUPED, MCC, or sequential testing. Use these results for exploration and hypothesis generation instead of final decision-making.',
  'experiments.analysisTable.slicedAdjustmentsHide': 'Hide details',
  'experiments.analysisTable.slicedAdjustmentsNotice':
    'Statistical adjustments not applied to individual attribute results.',
  'experiments.analysisTable.slicedAdjustmentsShow': 'Show details',
  'experiments.analysisTable.statisticallySignificant': 'Statistically significant',
  'experiments.analysisTable.thresholdSuffixMet': ' (≥{value}%)',
  'experiments.analysisTable.thresholdSuffixNotMet': ' (<{value}%)',
  'experiments.analysisTable.thresholdSuffixRegression': ' (≤{value}%)',
  'experiments.analysisTable.totalValueColumn': 'Total value',
  'experiments.analysisTable.totalValueDescription':
    "The posterior mean may not equal the total value divided by the number of exposures due to regularization using Bayesian priors{usingCuped, select, true { and covariate adjustment using CUPED} other {}}{hasFilters, select, true {. Total value will be calculated using only events that pass the metric's applied filters.} other {}}",
  'experiments.analysisTable.undesiredDirection': 'Undesired direction',
  'experiments.analysisTable.undesiredWord': 'undesired',
  'experiments.analysisTable.valueTermConversionRate': 'conversion rate',
  'experiments.analysisTable.valueTermMean': 'mean',
  'experiments.analysisTable.valueTermPercentileValue': 'percentile value',
  'experiments.analysisTable.valueTermPosteriorMean': 'posterior mean',
  'experiments.analysisTable.valueTermPosteriorPercentile': 'posterior percentile',
  'experiments.analysisTable.variationColumn': 'Variation',
  'experiments.analysisTable.waitingForWindowBody':
    'Units are shown in results only after their full window has completed.',
  'experiments.analysisTable.waitingForWindowHeading': 'Waiting for units to complete their window',
  'experiments.analysisTable.zeroBaselineExplanation':
    "LaunchDarkly can't compute statistical results on a relative difference basis because your baseline mean is exactly zero.",
  'experiments.analysisTableGraphCell.chartLoadError': 'An unexpected error has occurred loading this chart',
  'experiments.analysisTableGraphCell.graphLoadError': 'Failed to load graph',
  'experiments.analysisTableGraphCell.notSupportedForSlices': 'Not supported for sliced results',
  'experiments.analyticsExploreBanner.continueLink': 'Continue your analysis',
  'experiments.analyticsExploreBanner.continueSuffix':
    'in Product analytics, to build and customize a dashboard using these charts.',
  'experiments.analyticsExploreBanner.createDashboardError': 'Failed to create experiment dashboard',
  'experiments.analyticsExploreBanner.dashboardExists': 'A dashboard exists for this experiment.',
  'experiments.analyticsExploreBanner.heading': 'Want to explore deeper?',
  'experiments.analyticsExploreBanner.jumpBackLink': 'Jump back',
  'experiments.analyticsExploreBanner.jumpBackSuffix': 'into Product analytics to refine and customize it.',
  'experiments.analyticsTab.audienceLabel': 'Audience:',
  'experiments.analyticsTab.audienceSummary': '{percent}% of <unit></unit> contexts in this experiment',
  'experiments.analyticsTab.dateRangeOngoing': '{startDate} \u2192 Present',
  'experiments.analyticsTab.featureStatusLabel': 'Feature status',
  'experiments.analyticsTab.iterationLabel': 'Iteration {number}:',
  'experiments.analyticsTab.newBadge': 'New',
  'experiments.analyticsTab.noIterationsBody':
    'No experiment iterations have been started yet. Analytics will be available once an iteration begins.',
  'experiments.analyticsTab.notStarted': 'Not started',
  'experiments.analyticsTab.tabLabel': 'Analytics',
  'experiments.approvalChanges.addedToHoldout': 'Added to a {percent}% holdout,',
  'experiments.approvalChanges.configInformationNoChanges': 'Config information (no changes)',
  'experiments.approvalChanges.configTargetingRulesSummary': 'Config targeting rules',
  'experiments.approvalChanges.configTargetingSummary': 'Config targeting',
  'experiments.approvalChanges.dueToUpdates': 'Due to updates to the <strong>{experimentKey}</strong> {experimentKind}',
  'experiments.approvalChanges.flagInformationNoChanges': 'Flag information (no changes)',
  'experiments.approvalChanges.flagTargetingRulesSummary': 'Flag targeting rules',
  'experiments.approvalChanges.flagTargetingSummary': 'Flag targeting',
  'experiments.approvalChanges.heading': 'Changes',
  'experiments.approvalChanges.holdoutAllocationNote':
    'Absolute value of allocation reduced by {percent}% due to holdout,',
  'experiments.approvalChanges.jsonConfigTargetingTitle': 'Config targeting',
  'experiments.approvalChanges.jsonDiffError': 'Error rendering JSON diff',
  'experiments.approvalChanges.jsonFlagConfigurationTitle': 'Flag configuration',
  'experiments.approvalChanges.randomizedBy': 'Randomized by',
  'experiments.approvalChanges.randomizedByLower': 'randomized by',
  'experiments.approvalChanges.statusTagGroupLabel': 'experiment status',
  'experiments.approvalChanges.targetingIs': 'Targeting is',
  'experiments.approvalChanges.updateStatus': 'Update {experimentKind} to',
  'experiments.approvalChanges.updateStatusWithIteration': 'Update {experimentKind} to iteration {iterationNumber}',
  'experiments.approvalChanges.updateTargetingTo': 'Update targeting to',
  'experiments.approvalChanges.updateToIteration': 'Update {experimentKind} to iteration {iterationNumber}',
  'experiments.approvalChanges.updateVariationSplit':
    'Update targeting rule, <strong>{ruleName}</strong>, variation split to',
  'experiments.approvalChanges.variationAllocation':
    'Targeting rule, <strong>{ruleName}</strong>, variation allocation',
  'experiments.approvalFootnote.approvedChangeReady': 'An approved change for this {type} is ready to be applied',
  'experiments.approvalFootnote.approvedNewIteration': 'Request to start a new iteration of this {type} was approved',
  'experiments.approvalFootnote.approvedStart': 'Request to start {type} was approved',
  'experiments.approvalFootnote.approvedStop': 'Request to stop {type} was approved',
  'experiments.approvalFootnote.pendingChanges': 'There are pending changes for this {type}',
  'experiments.approvalFootnote.pendingNewIteration':
    'There is a pending request to start a new iteration of this {type}',
  'experiments.approvalFootnote.pendingStart': 'There is a pending request to start this {type}',
  'experiments.approvalFootnote.pendingStop': 'There is a pending request to stop this {type}',
  'experiments.approvalFootnote.viewApprovedChange': 'View approved change to apply it',
  'experiments.approvalFootnote.viewApprovedChanges': 'View approved changes to apply them',
  'experiments.approvalFootnote.viewPendingChange': 'View pending change',
  'experiments.approvalFootnote.viewPendingChanges': 'View pending changes',
  'experiments.approvalRequest.back': 'Back',
  'experiments.approvalRequest.cancel': 'Cancel',
  'experiments.approvalRequest.closeLabel': 'close',
  'experiments.approvalRequest.contextsWillReceive': 'contexts in this {experimentKind} will receive:',
  'experiments.approvalRequest.continue': 'Continue',
  'experiments.approvalRequest.environmentLabel': 'Environment',
  'experiments.approvalRequest.reasonDescription': 'Add a brief description of your changes.',
  'experiments.approvalRequest.reasonForChangeLabel': 'Reason for change',
  'experiments.approvalRequest.reasonLabel': 'Reason',
  'experiments.approvalRequest.reasonRequired': 'Reason is required',
  'experiments.approvalRequest.reasonTooLong': 'Reason must be {max} or fewer characters',
  'experiments.approvalRequest.reviewersDescription':
    'Select at least {count, plural, one {one reviewer} other {# reviewers}} to approve your changes.',
  'experiments.approvalRequest.reviewersLabel': 'Reviewers',
  'experiments.approvalRequest.saveAndStartDescription':
    'Saving changes will automatically start a new iteration. You will make changes to the <strong>{experimentName}</strong> {experimentKind} and the <strong>{flagName}</strong> on <environment></environment>.',
  'experiments.approvalRequest.saveAndStartHeading': 'Request approval to start new iteration',
  'experiments.approvalRequest.saveAndStartSubmit': 'Request to start new iteration',
  'experiments.approvalRequest.shipAllTraffic': '100% of',
  'experiments.approvalRequest.shippingPreamble': 'Shipping <strong>{variation}</strong> to 100% of traffic.',
  'experiments.approvalRequest.startDescription':
    'Starting this {experimentKind} will begin serving the variations specified and will start recording results. You will make changes to the <strong>{experimentName}</strong> {experimentKind} and the <strong>{flagName}</strong> flag on <strong>{environmentName}</strong>.',
  'experiments.approvalRequest.startHeading': 'Request approval to start {experimentKind}',
  'experiments.approvalRequest.startSubmit': 'Request to start {experimentKind}',
  'experiments.approvalRequest.step1Indicator': 'Step 1/2',
  'experiments.approvalRequest.step2Indicator': 'Step 2/2',
  'experiments.approvalRequest.stopDescription':
    'When you stop this {experimentKind}, results will stop recording and you will make changes to the <strong>{experimentName}</strong> {experimentKind} and the <strong>{flagName}</strong> flag on <environment></environment>.',
  'experiments.approvalRequest.stopHeading': 'Request to stop {experimentKind}',
  'experiments.approvalRequest.stopReasonLabel': 'Reason for stopping',
  'experiments.approvalRequest.stopReasonPlaceholder': 'Write your reason for stopping this {experimentKind}',
  'experiments.approvalRequest.stopReviewHeading': 'Request approval to stop {experimentKind}',
  'experiments.approvalRequest.stopSubmit': 'Request approval',
  'experiments.approvalRequest.variationRequired': 'Winning variation is required',
  'experiments.approvalRequest.variationToShip': 'Variation to ship',
  'experiments.archiveExperimentDialog.body':
    'This will archive your {entityType, select, mab {multi-armed bandit} other {experiment}} <name></name> in the <environment></environment> environment. You can restore this {entityType, select, mab {multi-armed bandit} other {experiment}} at a later time.',
  'experiments.archiveExperimentDialog.title':
    'Archive {entityType, select, mab {multi-armed bandit} other {experiment}}',
  'experiments.archivedFilter.label': 'Archived',
  'experiments.archivedFilter.no': 'No',
  'experiments.archivedFilter.yes': 'Yes',
  'experiments.attributeFilterMenu.allAttributes': 'All attributes',
  'experiments.attributeFilterMenu.filterLabel': 'Filter',
  'experiments.attributeFilterMenu.loadError': 'Unexpected error loading attribute values.',
  'experiments.attributeFilterMenu.noDataBody': 'We have not received traffic for this attribute',
  'experiments.attributeFilterMenu.noDataHeading': 'No data available, yet',
  'experiments.attributeFilterMenu.noResults': 'No matching values',
  'experiments.attributeFilterMenu.selectedCount': 'Attributes: {count}',
  'experiments.attributeFilterMenu.singleCategoryOnly': 'Attributes selection only supported for a single category',
  'experiments.attributePicker.addCustomAttribute': 'Add "{value}"',
  'experiments.attributePicker.attributeDataSourcesHeading': 'Attribute data sources',
  'experiments.attributePicker.availableListLabel': 'Available attributes',
  'experiments.attributePicker.emptyStateBody':
    "Check your SDK to confirm it's sending context attribute values to LaunchDarkly. Private attributes and attributes from anonymous contexts will not appear here. You can also type to add a custom attribute.",
  'experiments.attributePicker.emptyStateHeading': 'No attributes available yet',
  'experiments.attributePicker.flagEvaluationHeading': 'Flag evaluation attributes',
  'experiments.attributePicker.heading': 'Attributes',
  'experiments.attributePicker.limitNotice': 'Select up to {limit} attributes to analyze your results.',
  'experiments.attributePicker.limitReached': 'You have selected the maximum number of supported attributes.',
  'experiments.attributePicker.loadingLabel': 'Loading attributes',
  'experiments.attributePicker.noMatches': 'No attributes match your search',
  'experiments.attributePicker.searchLabel': 'Find an attribute by name',
  'experiments.attributePicker.selectedListLabel': 'Selected attributes',
  'experiments.attributePicker.showAttributesLabel': 'Show attributes',
  'experiments.attributePicker.sourceIncompatible':
    'Maps to {contextKinds}. This experiment randomizes by {randomizationUnit}.',
  'experiments.attributePicker.sourceNeedsRandomizationUnit': 'Choose a randomization unit to use this source.',
  'experiments.attributePicker.sourceSummary':
    '{contextKinds} · {count, plural, one {# attribute} other {# attributes}}',
  'experiments.audienceAllocationSection.disableReshufflingLabel':
    '<label>Disable reshuffling:</label> Keep <code>{unit}</code> contexts assigned to the same variation if you increase traffic in this experiment.',
  'experiments.audienceAllocationSection.heading': 'Audience allocation',
  'experiments.audienceAllocationSection.outsideVariationLabel':
    'Variation served to <contextKind></contextKind> contexts outside this {type}',
  'experiments.audienceAllocationSection.outsideVariationRunning':
    'You cannot change the variation to be served for a running {type}. Stop the {type} to change the variation.',
  'experiments.audienceAllocationSection.reshufflingDefaultAlert': 'Disable reshuffling is now on by default.',
  'experiments.audienceAllocationSection.sampleSizeFieldLabel':
    'Percent of <contextKind></contextKind> contexts in this {type}',
  'experiments.audienceAllocationSection.sampleSizeLabel': 'Audience sample size',
  'experiments.audienceStep.addEmail': 'Add "{email}"',
  'experiments.audienceStep.editLabel': 'Edit audience',
  'experiments.audienceStep.emailPlaceholder': 'Add an email',
  'experiments.audienceStep.emailsFieldLabel': 'Who will be in this experiment?',
  'experiments.audienceStep.emailsLabel': 'Audience emails',
  'experiments.audienceStep.intro':
    "Add yourself and a few teammates. You're the only ones who'll be in the experiment, so it's safe to run in production.",
  'experiments.audienceStep.loadingEmails': 'Loading emails',
  'experiments.audienceStep.minimumHint': 'Two users minimum.',
  'experiments.audienceStep.next': 'Next',
  'experiments.audienceStep.noAudienceSelected': 'No audience selected',
  'experiments.audienceStep.saveError': 'Could not save changes. Try again.',
  'experiments.audienceStep.saving': 'Saving…',
  'experiments.audienceStep.title': 'Audience',
  'experiments.audienceStep.typeAnEmail': 'Type an email',
  'experiments.audienceSummary.advancedHeading': 'Advanced',
  'experiments.audienceSummary.contextKind': '<unit></unit> contexts',
  'experiments.audienceSummary.defaultRule': 'Default rule',
  'experiments.audienceSummary.defaultRuleDescription':
    'Any traffic that does not match other targeting rules for the feature flag',
  'experiments.audienceSummary.otherContextKinds':
    'If this rule is evaluated for context kinds other than <contextKind></contextKind>, those contexts will be served',
  'experiments.audienceSummary.percentInExperiment':
    '<strong>{percent}%</strong> of <contextKind></contextKind> {future, select, true {will be } other {}}in the experiment',
  'experiments.audienceSummary.reassignmentWarning':
    'For more accurate experiment results, we do not recommend keeping <contextKind></contextKind> assigned to their initial variation. To learn more about variation assignments, <docLink></docLink>',
  'experiments.audienceSummary.remainAssigned':
    '<contextKind></contextKind> will remain assigned to their initial variation',
  'experiments.audienceSummary.remainingPercent':
    'The remaining <strong>{percent}%</strong> of <contextKind></contextKind> {future, select, true {will be } other {}}served',
  'experiments.audienceSummary.ruleNumber': 'Rule {number}',
  'experiments.audienceSummary.runningExperimentHeading': 'Running Experiment',
  'experiments.audienceSummary.targetingRuleHeading': 'Targeting Rule',
  'experiments.audienceSummary.trafficAllocationHeading': 'Traffic Allocation',
  'experiments.audienceTargetingSection.aaTestDescription':
    'Validate your experiment setup before running an A/B or A/B/n test in the next iteration',
  'experiments.audienceTargetingSection.aaTestGuidanceBody':
    'In your application, evaluate this flag as usual but serve the same experience for every variation. An A/A test measures your experiment setup, not a feature change.',
  'experiments.audienceTargetingSection.createCustomAudience': 'Create custom audience',
  'experiments.audienceTargetingSection.defaultRuleSummary':
    'All <contextKind></contextKind> contexts will be targeted',
  'experiments.audienceTargetingSection.edit': 'Edit',
  'experiments.audienceTargetingSection.learnMore': 'Learn more',
  'experiments.audienceTargetingSection.runAsAaTest': 'Run as A/A test',
  'experiments.audienceTargetingSection.runningExperimentDisabled':
    'You cannot change the audience for a running experiment. Stop the experiment to change the audience.',
  'experiments.audienceTargetingSection.showAaTestGuidance': 'Show A/A test guidance',
  'experiments.audienceTargetingSection.targetingRule': 'Targeting rule',
  'experiments.audienceTargetingSection.targetingRuleDescription':
    "The flag targeting rule determines the experiment audience. The rule must target contexts of the same context kind as this experiment's randomization unit to be included.",
  'experiments.banditAllocationFrequencySection.every': 'Every',
  'experiments.banditAllocationFrequencySection.fieldLabel': 'Update frequency',
  'experiments.banditAllocationFrequencySection.fieldPlaceholder': '1-24 hours',
  'experiments.banditAllocationFrequencySection.help':
    'This is the frequency at which LaunchDarkly will shift traffic based on observed outcomes.',
  'experiments.banditAllocationFrequencySection.rangeHelp': 'Type a value between 1 and 24.',
  'experiments.banditConversionRateChart.emptyTitle': 'No historical conversion rate data available',
  'experiments.banditConversionRateChart.loadErrorTitle': 'Failure loading historical conversion rate data',
  'experiments.banditConversionRateChart.noChartDataTitle': 'No historical conversion rate data available',
  'experiments.banditConversionRateChart.noDataTitle': 'No data',
  'experiments.banditCumulativeResults.hideChart': 'Hide probability density chart',
  'experiments.banditCumulativeResults.info':
    'Cumulative results are based on data gathered since the beginning of the bandit',
  'experiments.banditCumulativeResults.learnMore': 'Learn more',
  'experiments.banditCumulativeResults.showChart': 'Show probability density chart',
  'experiments.banditCumulativeResults.title': 'Cumulative results',
  'experiments.banditDraftPage.loadError': 'We were unable to load this multi-armed bandit.',
  'experiments.banditExposuresCard.heading': 'Cumulative exposures',
  'experiments.banditHistoricalResults.conversionRateBody':
    'The true average of conversion rates across variations. Results are from each update period.',
  'experiments.banditHistoricalResults.conversionRateFormula':
    'Total count of conversions across all arms divided by the total count of exposures across all arms.',
  'experiments.banditHistoricalResults.conversionRateHeading': 'Overall conversion rate across variations',
  'experiments.banditHistoricalResults.formulaHeading': 'Formula',
  'experiments.banditHistoricalResults.heading': 'Historical results',
  'experiments.banditHistoricalResults.learnMore': 'Learn more',
  'experiments.banditHistoricalResults.trafficAllocationBody':
    'Allocation shifts are updated based on the probability a variation will be the best performer. We use the Thompson Sampling algorithm to calculate these probabilities.',
  'experiments.banditHistoricalResults.trafficAllocationHeading': 'Traffic allocation',
  'experiments.banditRecommendation.andJoiner': 'and',
  'experiments.banditRecommendation.mostLikelyBest': '{past, select, true {was} other {is}} most likely to be best.',
  'experiments.banditRecommendation.notEnoughData':
    '{past, select, true {There wasn\u2019t enough data to know the leader.} other {Hang tight - there isn\u2019t enough data to know the leader.}}',
  'experiments.banditRecommendation.notStarted': 'You have no results because your multi-armed bandit has not started.',
  'experiments.banditRecommendation.primaryMetricPrefix':
    'Based on the primary metric <highlight>{metricName}</highlight>,',
  'experiments.banditRecommendation.primaryMetricTreatmentPrefix':
    'Based on the primary metric <highlight>{metricName}, <treatment></treatment></highlight>',
  'experiments.banditRecommendation.significantInDirection':
    '{plural, select, true {are} other {is}} significant in the desired direction.',
  'experiments.banditRecommendation.winningVariation': '{past, select, true {was} other {is}} the winning variation.',
  'experiments.banditSummary.durationHeading': 'Duration',
  'experiments.banditSummary.errorLoadingRecommendation': 'Error loading recommendation',
  'experiments.banditSummary.heading': 'Summary',
  'experiments.banditSummary.healthCheckError': 'Failed to load multi-armed bandit health check.',
  'experiments.banditSummary.keyTakeawaysHeading': 'Key takeaways',
  'experiments.banditSummary.keyTakeawaysInfoBody':
    "Key takeaways summarize the bandit's recommendation based on observed performance across variations.",
  'experiments.banditSummary.keyTakeawaysInfoLabel': 'Key takeaways info',
  'experiments.banditSummary.learnToInterpret': 'Learn to interpret results',
  'experiments.banditSummary.metricHeading': 'Metric',
  'experiments.banditSummary.noReasonProvided': 'No reason provided',
  'experiments.banditSummary.notStarted': 'You have no results because your multi-armed bandit has not started.',
  'experiments.banditSummary.optimizationGoalHeading': 'Optimization goal',
  'experiments.banditSummary.resultForLabel': 'Result for',
  'experiments.banditSummary.shippedVariationHeading': 'Shipped variation',
  'experiments.banditSummary.stoppedReasonHeading': 'Reason for stopping',
  'experiments.banditTrafficAllocationChart.emptyTitle': 'No historical allocation data available',
  'experiments.banditTrafficAllocationChart.loadErrorTitle': 'Failure loading historical allocation data',
  'experiments.banditTrafficAllocationChart.noDataTitle': 'No data',
  'experiments.banditsList.leadingVariation': 'Leading variation',
  'experiments.banditsList.maintainer': 'Maintainer',
  'experiments.banditsList.maintainerLoadingLabel': 'Loading maintainer',
  'experiments.banditsList.metric': 'Metric',
  'experiments.banditsList.name': 'Name',
  'experiments.banditsList.noMaintainerLabel': 'No maintainer',
  'experiments.banditsList.status': 'Status',
  'experiments.banditsListEmptyState.body':
    'Multi-armed bandits automatically shift traffic toward the best-performing option, so you get stronger results while the test runs.',
  'experiments.banditsListEmptyState.clearFilters': 'Clear filters',
  'experiments.banditsListEmptyState.createBandit': 'Create multi-armed bandit',
  'experiments.banditsListEmptyState.filteredBody': 'Try adjusting your filters',
  'experiments.banditsListEmptyState.filteredHeading': 'No multi-armed bandits match your filter criteria',
  'experiments.banditsListEmptyState.heading': 'Create your first multi-armed bandit',
  'experiments.banditsListEmptyState.readTheGuide': 'Read the guide',
  'experiments.builderActionBar.activeRolloutFootnote':
    'A progressive or guarded rollout is active for this flag. You must stop the rollout before starting this {type}.',
  'experiments.builderActionBar.approvalOptionsLabel': 'Approval options',
  'experiments.builderActionBar.archivedFootnote':
    'This {type} is archived. You must <link>restore this {type}</link> to start it.',
  'experiments.builderActionBar.cancel': 'Cancel',
  'experiments.builderActionBar.checkingAssignmentSource': 'Checking assignment source…',
  'experiments.builderActionBar.checkingMetricDataSource': 'Checking metric data source…',
  'experiments.builderActionBar.checkingMetrics': 'Checking metrics…',
  'experiments.builderActionBar.metricsUnsupportedRandomizationUnit':
    'All metrics must support the selected randomization unit',
  'experiments.builderActionBar.conflictingExperimentWarning':
    'An experiment that uses this flag rule is running. You must stop the other experiment using this rule before starting this one.',
  'experiments.builderActionBar.contactUsCta': 'Contact us to upgrade your plan.',
  'experiments.builderActionBar.designComplete': '{type} design is complete.',
  'experiments.builderActionBar.designIncomplete': '{type} design is not complete.',
  'experiments.builderActionBar.editDesign': 'Edit design',
  'experiments.builderActionBar.editingHeadline': 'Editing design',
  'experiments.builderActionBar.flagArchivedFootnote':
    'The flag this {type} uses is archived. You must restore the flag to start this {type}.',
  'experiments.builderActionBar.flagOffFootnote':
    'The flag this {type} uses is off. You must turn the flag on to start this {type}.',
  'experiments.builderActionBar.healthCheckLoadError': 'Was unable to load health check.',
  'experiments.builderActionBar.iteration': 'Iteration {number}',
  'experiments.builderActionBar.iterationRunning':
    'Iteration {number} is currently running. Editing and saving will start a new iteration.',
  'experiments.builderActionBar.iterationStopped': 'Iteration {number} is currently stopped.',
  'experiments.builderActionBar.missingRequirements': 'Complete the required fields below:',
  'experiments.builderActionBar.notStartedHeadline': 'Not started',
  'experiments.builderActionBar.readyToStartHeadline': 'Ready to start',
  'experiments.builderActionBar.requestApprovalTo': 'Request approval to start',
  'experiments.builderActionBar.runningHeadline': 'Running',
  'experiments.builderActionBar.save': 'Save',
  'experiments.builderActionBar.saveBeforeStartTooltip': 'Save iteration before starting',
  'experiments.builderActionBar.saveRequestApproval': 'Save & request approval for new iteration',
  'experiments.builderActionBar.saveStartIteration': 'Save & start new iteration',
  'experiments.builderActionBar.saveStartWithoutApproval': 'Save & start new iteration without approval',
  'experiments.builderActionBar.start': 'Start',
  'experiments.builderActionBar.startActiveRolloutTooltip':
    'You cannot start an {type} if there is an active progressive or guarded rollout',
  'experiments.builderActionBar.startArchivedTooltip': 'You cannot start an archived {type}.',
  'experiments.builderActionBar.startFlagArchivedTooltip': 'You cannot start an {type} if the flag is archived.',
  'experiments.builderActionBar.startFlagOffTooltip': 'You cannot start an {type} if the flag is off.',
  'experiments.builderActionBar.startNoPermissionTooltip': 'You do not have permission to start this {type}.',
  'experiments.builderActionBar.startOtherRunningTooltip':
    'You cannot start an {type} if there is another one running.',
  'experiments.builderActionBar.startTrialLapsedTooltip': 'You cannot start this {type} because your access has ended.',
  'experiments.builderActionBar.startWithoutApproval': 'Start without approval',
  'experiments.builderActionBar.startedOn': 'Started on',
  'experiments.builderActionBar.stopFlagArchivedTooltip': "You cannot stop an {type} if its flag is archived'",
  'experiments.builderActionBar.stoppedHeadline': 'Stopped',
  'experiments.builderActionBar.stoppedOn': 'Stopped on',
  'experiments.builderActionBar.trialLapsedStart':
    'Your access to experimentation has ended. You cannot start this {type}.',
  'experiments.builderActionBar.trialLapsedStop':
    'Your access to experimentation has ended. If you stop this {type}, you cannot restart it.',
  'experiments.builderActionBar.viewFlagSettings': 'View flag settings',
  'experiments.builderActionBar.viewFlagTargeting': 'View flag targeting',
  'experiments.builderActionBar.viewThisFlag': "View this flag's experiments",
  'experiments.builderMetrics.analysisUnitLabel': 'Analysis unit for {metricName}',
  'experiments.builderMetrics.cannotRemoveGroupTooltip': 'You cannot remove a group that contains the primary metric',
  'experiments.builderMetrics.cannotRemovePrimaryTooltip':
    'Experiments require a primary metric. To remove this one, set another metric as primary.',
  'experiments.builderMetrics.collapseGroupLabel': 'Collapse metric group {groupName}',
  'experiments.builderMetrics.dragHandleLabel': 'Drag to reorder {metricName}',
  'experiments.builderMetrics.dropIndicatorLabel': 'Drop indicator',
  'experiments.builderMetrics.eventsSeenIn': 'Events seen in:',
  'experiments.builderMetrics.expandGroupLabel': 'Expand metric group {groupName}',
  'experiments.builderMetrics.filtersAppliedBody':
    'When filtering by context attribute, the incoming metric events will be filtered, but the exposure count will remain unaffected.',
  'experiments.builderMetrics.filtersAppliedHeading': 'This metric has filters applied',
  'experiments.builderMetrics.funnelPrimaryStep': '{step} (Primary metric)',
  'experiments.builderMetrics.funnelStep': 'Step {step}',
  'experiments.builderMetrics.gridListLabel': 'Reorderable experiment metrics',
  'experiments.builderMetrics.groupLoadError': 'Failed to load this metric group',
  'experiments.builderMetrics.heading': 'Metrics',
  'experiments.builderMetrics.loadMetricsError': 'Failed to load metrics',
  'experiments.builderMetrics.loadingGroup': 'Loading metrics…',
  'experiments.builderMetrics.makePrimary': 'Make primary',
  'experiments.builderMetrics.partOfGroup': 'part of',
  'experiments.builderMetrics.primaryRadioGroupLabel': 'Primary metric',
  'experiments.builderMetrics.primaryTag': 'Primary',
  'experiments.builderMetrics.removeFunnelGroupLabel': 'Remove funnel metric group',
  'experiments.builderMetrics.removeGroupLabel': 'Remove metric group {groupName}',
  'experiments.builderMetrics.removeGroupTooltip': 'Remove group',
  'experiments.builderMetrics.setPrimaryLabel': 'Set {metricName} as the primary metric',
  'experiments.builderMetrics.singleAnalysisUnitTooltip': 'Only one analysis unit is available for this metric.',
  'experiments.builderMetrics.singleMetricsHeading': 'Single metrics',
  'experiments.builderMetrics.ungroupLabel': 'Explode metric group {groupName}',
  'experiments.builderMetrics.ungroupTooltip': 'Ungroup to treat each metric individually',
  'experiments.changePrimaryMetricDialog.title': 'Change primary metric',
  'experiments.chartTypeMenu.forestPlot': 'Forest plot',
  'experiments.chartTypeMenu.resultsTable': 'Results table',
  'experiments.cloneExperiment.alreadyExistsError':
    'Experiment "{experimentKey}" already exists in the {environmentKey} environment.',
  'experiments.cloneExperiment.cloneBandit': 'Clone multi-armed bandit',
  'experiments.cloneExperiment.cloneBanditError': 'Failed to clone multi-armed bandit: {reason}',
  'experiments.cloneExperiment.cloneExperiment': 'Clone experiment',
  'experiments.cloneExperiment.cloneExperimentError': 'Failed to clone experiment: {reason}',
  'experiments.cloneExperiment.cloning': 'Cloning…',
  'experiments.cloneExperiment.forbiddenError': 'You do not have permission to create experiments in this environment.',
  'experiments.cloneExperiment.genericError': 'Failed to clone experiment',
  'experiments.cloneExperiment.invalidDataError': 'Invalid experiment data. Check your configuration.',
  'experiments.cloneExperiment.unknownError': 'unknown error',
  'experiments.cloneExperimentDialog.anUnexpectedError': 'An unexpected error has occurred.',
  'experiments.cloneExperimentDialog.cancel': 'Cancel',
  'experiments.cloneExperimentDialog.cloningExperiment': 'Cloning <emphasis>{name}</emphasis>',
  'experiments.cloneExperimentDialog.closeLabel': 'Close dialog',
  'experiments.cloneExperimentDialog.dialogLabel': 'Clone environment',
  'experiments.cloneExperimentDialog.experimentClonedSuccessfully': 'Experiment cloned successfully',
  'experiments.cloneExperimentDialog.from': 'From',
  'experiments.cloneExperimentDialog.heading':
    '{kind, select, mab {Clone multi-armed bandit} other {Clone experiment}}',
  'experiments.cloneExperimentDialog.layerAndHoldout': 'Layer and holdout settings will not be transferred.',
  'experiments.cloneExperimentDialog.loadingEnvironmentLabel': 'Loading environment {environmentKey}',
  'experiments.cloneExperimentDialog.loadingEnvironmentsLabel': 'Loading environment',
  'experiments.cloneExperimentDialog.selectEnvironment': 'Select environment',
  'experiments.cloneExperimentDialog.to': 'to',
  'experiments.cloneExperimentDialog.viewExperiment': 'View experiment',
  'experiments.cloneExperimentPrompt.backToList': 'Back to {type}s',
  'experiments.cloneExperimentPrompt.candidateSelectLabel': 'Select candidate',
  'experiments.cloneExperimentPrompt.checkingOtherEnvironments': 'Checking other environments…',
  'experiments.cloneExperimentPrompt.cloneFrom': 'Clone <name></name> from <source></source>',
  'experiments.cloneExperimentPrompt.cloneSuccess': 'Experiment cloned successfully',
  'experiments.cloneExperimentPrompt.createExperiment': 'Create {type}',
  'experiments.cloneExperimentPrompt.layerAndHoldout': 'Layer and holdout settings will not be transferred.',
  'experiments.cloneExperimentPrompt.loadingCandidatesLabel': 'Loading {type} candidates',
  'experiments.conversionRateChart.chartLabel': 'traffic chart exposures',
  'experiments.conversionRateChart.conversionRate': 'Conversion rate',
  'experiments.conversionRateChart.overallConversionRate': 'Overall conversion rate',
  'experiments.conversionRateChart.variation': 'Variation',
  'experiments.createBanditDialog.addMore': 'Add more',
  'experiments.createBanditDialog.addTags': 'Add tags',
  'experiments.createBanditDialog.cancel': 'Cancel',
  'experiments.createBanditDialog.closeLabel': 'Close',
  'experiments.createBanditDialog.create': 'Create',
  'experiments.createBanditDialog.createError': 'Failed to create multi-armed bandit',
  'experiments.createBanditDialog.discardBody': 'If you close this form, you will lose any unsaved changes.',
  'experiments.createBanditDialog.discardConfirm': 'Discard changes',
  'experiments.createBanditDialog.goalLabel': 'Goal',
  'experiments.createBanditDialog.goalPlaceholder': 'Maximize homepage CTA click-through rate',
  'experiments.createBanditDialog.heading':
    'Create multi-armed bandit {hasEnvironment, select, true {in <environment></environment>} other {}}',
  'experiments.createBanditDialog.nameDescription': 'Describe a key aspect of your multi-armed bandit.',
  'experiments.createBanditDialog.nameLabel': 'Name',
  'experiments.createBanditDialog.namePlaceholder': 'Homepage CTA color',
  'experiments.createBanditDialog.nameRequired': 'Name is required',
  'experiments.createBanditDialog.nameTooShort': 'Name must be at least 4 characters',
  'experiments.createBanditDialog.noAccessBody':
    'Your user account does not have permission to create multi-armed bandits. Request the necessary permissions from your administrator.',
  'experiments.createBanditDialog.noAccessHeading': 'Multi-armed bandit creation is disabled',
  'experiments.createBanditDialog.selectedTagsLabel': 'Selected tags',
  'experiments.createBanditDialog.tagPlaceholder': 'Enter tag (e.g., frontend, experimental)',
  'experiments.createBanditDialog.tags': 'Tags',
  'experiments.createExperimentAction.needsVariationsTooltip':
    'At least 2 variations are required to create an experiment',
  'experiments.createExperimentDialog.addMaintainer': 'Add maintainer',
  'experiments.createExperimentDialog.addTag': 'Add tag',
  'experiments.createExperimentDialog.cancel': 'Cancel',
  'experiments.createExperimentDialog.closeLabel': 'Close',
  'experiments.createExperimentDialog.createExperiment': 'Create experiment',
  'experiments.createExperimentDialog.discardBody': 'If you close this form, you will lose any unsaved changes.',
  'experiments.createExperimentDialog.discardConfirm': 'Discard changes',
  'experiments.createExperimentDialog.heading': 'Create experiment',
  'experiments.createExperimentDialog.headingInEnvironment': 'Create experiment in {environment}',
  'experiments.createExperimentDialog.hypothesisLabel': 'Hypothesis',
  'experiments.createExperimentDialog.hypothesisPlaceholder':
    'If [I do something], then [something will happen] because [a reason].',
  'experiments.createExperimentDialog.maintainersLoadError': 'Unable to load maintainers',
  'experiments.createExperimentDialog.nameDescription':
    'Ensure your name describes a key aspect of your experiment. Ex: Homepage CTA color',
  'experiments.createExperimentDialog.nameLabel': 'Experiment name',
  'experiments.createExperimentDialog.namePlaceholder': 'Enter a name for your experiment',
  'experiments.createExperimentDialog.noAccessBody':
    'Your user account does not have permission to create experiments. Request the necessary permissions from your administrator.',
  'experiments.createExperimentDialog.noAccessHeading': 'Experiment creation is disabled',
  'experiments.createExperimentDialog.tagPlaceholder': 'Enter tag (e.g., frontend, experimental)',
  'experiments.createFlagModal.booleanFlagType': 'Flag Type: boolean',
  'experiments.createFlagModal.booleanVariationsBody':
    'Define a true and a false variation in code. You cannot add more variations later.',
  'experiments.createFlagModal.createFlag': 'Create flag',
  'experiments.createFlagModal.heading': 'Create flag for experiment',
  'experiments.createFlagModal.jsonFlagType': 'Flag Type: JSON',
  'experiments.createFlagModal.jsonVariationsBody':
    'Define variations with JSON. Control multiple parameters at once in complex scenarios.',
  'experiments.createFlagModal.numberFlagType': 'Flag Type: number',
  'experiments.createFlagModal.numberVariationsBody':
    'Define variations with numbers. Example use: testing pagination optimal list lengths.',
  'experiments.createFlagModal.stringFlagType': 'Flag Type: string',
  'experiments.createFlagModal.stringVariationsBody':
    'Define custom variations in code or define string values. Example: testing homepage copy options.',
  'experiments.createFlagModal.tagGroupLabel': 'Tag group',
  'experiments.createFlagModal.variationsDefinitionLabel': 'Variations definition radio group',
  'experiments.createFlagModal.variationsQuestion': 'How do you want to define your variations?',
  'experiments.createHoldoutDialog.cancel': 'Cancel',
  'experiments.createHoldoutDialog.closeLabel': 'Close',
  'experiments.createHoldoutDialog.create': 'Create',
  'experiments.createHoldoutDialog.createError': 'Failed to create holdout',
  'experiments.createHoldoutDialog.descriptionLabel': 'Description',
  'experiments.createHoldoutDialog.descriptionPlaceholder': 'Users held out from all Q3 experiments',
  'experiments.createHoldoutDialog.discardBody': 'If you close this form, you will lose any unsaved changes.',
  'experiments.createHoldoutDialog.discardConfirm': 'Discard changes',
  'experiments.createHoldoutDialog.flagCreateError': 'Error creating flag for holdout',
  'experiments.createHoldoutDialog.flagFetchError': 'Error retrieving flag for holdout',
  'experiments.createHoldoutDialog.heading':
    'Create holdout {hasEnvironment, select, true {in <environment></environment>} other {}}',
  'experiments.createHoldoutDialog.nameDescription': 'Describe a key aspect of your holdout.',
  'experiments.createHoldoutDialog.nameLabel': 'Name',
  'experiments.createHoldoutDialog.namePlaceholder': 'Homepage Q3',
  'experiments.createHoldoutDialog.nameRequired': 'Holdout name is required',
  'experiments.createHoldoutDialog.nameTooShort': 'Holdout name is required and must be at least 4 characters',
  'experiments.cupedDeprecationBanner.body':
    'We have updated how LaunchDarkly calculates CUPED-adjusted results. Iterations started before {cutoffDate} use the old method, and LaunchDarkly will stop adjusting those results on November 13, 2026. To use the updated adjustment method, start a new iteration.',
  'experiments.cupedDeprecationBanner.learnMore': 'Learn more',
  'experiments.cupedDeprecationBanner.updateToCuped': 'Update to CUPED',
  'experiments.customAssignments.actions': 'Actions',
  'experiments.customAssignments.addVariant': 'Add variant',
  'experiments.customAssignments.assignmentMethod': 'Assignment method',
  'experiments.customAssignments.assignmentMethodDescription':
    'Choose where assignment data comes from. LaunchDarkly evaluates a flag or config for you, or you can point to a warehouse table you already populate.',
  'experiments.customAssignments.assignmentSource': 'Assignment source',
  'experiments.customAssignments.assignmentSourceDescription':
    'The warehouse connection LaunchDarkly queries for assignment data. Manage sources at Data sources.',
  'experiments.customAssignments.assignmentSourcePlaceholder': 'Select assignment source',
  'experiments.customAssignments.cancel': 'Cancel',
  'experiments.customAssignments.control': 'Control',
  'experiments.customAssignments.controlVariantGroupLabel': 'Control variant',
  'experiments.customAssignments.controlVariantRowLabel': 'Control variant {index}',
  'experiments.customAssignments.createMetricSource': 'Create metric source',
  'experiments.customAssignments.dateRangeDescription':
    'Set the date range you want analyzed for your historical assignment data. Leave blank to include all available data.',
  'experiments.customAssignments.dateRangeHeading': 'Customize assignment date range',
  'experiments.customAssignments.deleteVariantLabel': 'Delete variant {index}',
  'experiments.customAssignments.displayName': 'Display name (optional)',
  'experiments.customAssignments.displayNamePlaceholder': 'Type display name',
  'experiments.customAssignments.displayNameRowLabel': 'Display name {index}',
  'experiments.customAssignments.experimentId': 'Experiment ID',
  'experiments.customAssignments.experimentIdDescription':
    'The value LaunchDarkly matches against the experiment_id column in your assignment table.',
  'experiments.customAssignments.experimentIdPlaceholder': 'e.g., homepage-redesign',
  'experiments.customAssignments.flagsDocsLink': 'Read more about flags and AgentControl configs',
  'experiments.customAssignments.loadingSourcesLabel': 'Loading assignment sources',
  'experiments.customAssignments.missingDataSourceBody':
    'Create a metric data source to be able to use this assignment source. <link>Learn more</link>',
  'experiments.customAssignments.missingDataSourceHeading': 'No {warehouse} metric data source detected',
  'experiments.customAssignments.selectDateS': 'Select date(s)',
  'experiments.customAssignments.sourceValue': 'Source value',
  'experiments.customAssignments.sourceValueDescription':
    'The value LaunchDarkly matches against the variant ID column in your assignment table. Each variant needs its own source value.',
  'experiments.customAssignments.sourceValueInfoLabel': 'Source value info',
  'experiments.customAssignments.sourceValuePlaceholder': 'Type source value',
  'experiments.customAssignments.variants': 'Variants',
  'experiments.customAssignments.warehouseDocsLink': 'Read more about external warehouses',
  'experiments.dashboardListPage.createExperiment': 'Create experiment',
  'experiments.dashboardListPage.tooltip': 'You do not have permission to create an experiment',
  'experiments.dashboardPage.connectAction':
    'Connect your SDK to measure and understand the impact of every feature you release.',
  'experiments.dashboardPage.label': 'Search for an experiment by name',
  'experiments.dashboardPage.loading': 'Loading…',
  'experiments.dashboardPage.placeholder': 'Find an experiment by name',
  'experiments.densityPlot.hiddenNoTraffic': '{variation} is hidden because it has received no traffic',
  'experiments.designDetails.durationLabel': 'Duration',
  'experiments.designDetails.noResultsNotStarted': 'You have no results because your experiment has not started',
  'experiments.designDetails.percentComplete': '{percent}% complete',
  'experiments.designDetails.readLess': 'Read less',
  'experiments.designDetails.readMore': 'Read more',
  'experiments.designDetails.recommendationLoadError': 'Error loading recommendation',
  'experiments.designDetails.sampleSizeProgressLabel': 'Sample size progress',
  'experiments.designDetails.showDetails': 'Show details',
  'experiments.designDetails.targetLabel': 'Target',
  'experiments.designDetails.underOnePercentComplete': '<1% complete',
  'experiments.designHealthCheck.analysisUnit': 'Analysis unit',
  'experiments.designHealthCheck.assignmentSource': 'Assignment source',
  'experiments.designHealthCheck.currently': 'Currently:',
  'experiments.designHealthCheck.decoupledMetricsWarning':
    'Some metrics are analyzed by a unit other than <code>{unit}</code>, the experiment’s randomization unit. Mixing granularities can make results inconclusive. <link>Read about analysis units</link>',
  'experiments.designHealthCheck.forThePast': 'For the past 90 days:',
  'experiments.designHealthCheck.targetingRuleAudience': 'Targeting rule / audience',
  'experiments.designPreview.shrinkagePriors': 'The treatment variations use <strong>shrinkage</strong> priors.',
  'experiments.designPreview.uninformativePriors':
    'The treatment variations use <strong>uninformative</strong> priors.',
  'experiments.designSteps.audienceDescription': 'The people who will be in your experiment.',
  'experiments.designSteps.goToStepLabel': 'Go to {step} step',
  'experiments.designSteps.launchDescription': 'Wire up the experiment.',
  'experiments.designSteps.loadError': 'Could not load experiment design page. Try refreshing the page.',
  'experiments.designSteps.preFilledSettingsDescription': 'Edit anything or leave it as-is.',
  'experiments.designSteps.variationsDescription': "What you'll serve in your experiment.",
  'experiments.detailPage.analytics': 'Analytics',
  'experiments.detailPage.design': 'Design',
  'experiments.detailPage.exposures': 'Exposures',
  'experiments.detailPage.iterations': 'Iterations',
  'experiments.detailPage.results': 'Results',
  'experiments.detailPage.traffic': 'Traffic',
  'experiments.detailSidebar.addTags': 'Add tag(s)',
  'experiments.detailSidebar.approachLabel': 'Approach',
  'experiments.detailSidebar.changeMaintainerLabel': 'Change maintainer',
  'experiments.detailSidebar.chooseMaintainer': 'Choose maintainer',
  'experiments.detailSidebar.configTitle': 'Config',
  'experiments.detailSidebar.copyKeyLabel': 'Copy {experimentKey} to your clipboard.',
  'experiments.detailSidebar.copyKeyTooltip': 'Copy multi-armed bandit key',
  'experiments.detailSidebar.createdLabel': 'Created',
  'experiments.detailSidebar.detailsTab': 'Details',
  'experiments.detailSidebar.direction': 'Direction',
  'experiments.detailSidebar.discussionTab': 'Discussion',
  'experiments.detailSidebar.flagTitle': 'Flag',
  'experiments.detailSidebar.holdoutLabel': 'Holdout',
  'experiments.detailSidebar.integrationHealthLabel': 'Integration health',
  'experiments.detailSidebar.keyLabel': 'Key',
  'experiments.detailSidebar.layerLabel': 'Layer',
  'experiments.detailSidebar.loadingMaintainersLabel': 'Loading maintainers',
  'experiments.detailSidebar.loadingPendingApprovalsLabel': 'Loading pending approvals',
  'experiments.detailSidebar.maintainerLabel': 'Maintainer',
  'experiments.detailSidebar.pendingApprovalsError': 'Failed to load pending approvals',
  'experiments.detailSidebar.pendingApprovalsRetry': 'Try again',
  'experiments.detailSidebar.pendingChangesLabel': 'Pending changes',
  'experiments.detailSidebar.randomizeByInfoBody':
    'The unit the experiment will randomize on to split contexts between variants.',
  'experiments.detailSidebar.randomizeByInfoLabel': 'Randomize by info',
  'experiments.detailSidebar.randomizeByLabel': 'Randomize by',
  'experiments.detailSidebar.randomizeByLearnMore': 'Learn more',
  'experiments.detailSidebar.significanceLevelLabel': 'Significance level',
  'experiments.detailSidebar.statisticalApproachLabel': 'Statistical approach and success',
  'experiments.detailSidebar.tagsLabel': 'Tags',
  'experiments.detailSidebar.threshold': 'Threshold',
  'experiments.detailSidebar.treatmentPrior': 'Treatment prior',
  'experiments.detailSidebar.treatmentPriorShrinkage': 'Shrinkage',
  'experiments.detailSidebar.treatmentPriorUninformative': 'Uninformative',
  'experiments.detailsActionBar.activeRollout':
    'A progressive or guarded rollout is active for this {entity}. You must stop the rollout before starting this {type}.',
  'experiments.detailsActionBar.approvalOptionsLabel': 'Approval options',
  'experiments.detailsActionBar.archived':
    'This {type} is archived. You must <link>restore this {type}</link> to start it.',
  'experiments.detailsActionBar.bayesianRecommendation':
    'Current results show that <diamond></diamond> {treatment} has a probability to be best ≥ {threshold} and has the highest probability to be best of all treatments.',
  'experiments.detailsActionBar.bothRunning':
    'Both an experiment and a multi-armed bandit are running on this {entity} rule. You must stop them before starting this one.',
  'experiments.detailsActionBar.contactUsCta': 'Contact us to upgrade your plan.',
  'experiments.detailsActionBar.designNotComplete': '{type} design is not complete.',
  'experiments.detailsActionBar.editDesign': 'Edit design',
  'experiments.detailsActionBar.experimentRunning':
    'An experiment is running on this {entity} rule. You must stop it before starting this one.',
  'experiments.detailsActionBar.flagArchived':
    'The {entity} this {type} uses is archived. You must restore the {entity} to start this {type}.',
  'experiments.detailsActionBar.flagOff':
    'The {entity} this {type} uses is off. You must turn the {entity} on to start this {type}.',
  'experiments.detailsActionBar.flagSettingsLink': 'View {entity} settings',
  'experiments.detailsActionBar.flagTargetingLink': 'View {entity} targeting',
  'experiments.detailsActionBar.iterationLabel': 'Iteration {number}',
  'experiments.detailsActionBar.mabRunning':
    'A multi-armed bandit is running on this {entity} rule. You must stop it before starting this one.',
  'experiments.detailsActionBar.otherExperimentRunning':
    'An {type} that uses this {entity} rule is running. You must stop the other {type} using this rule before starting this one.',
  'experiments.detailsActionBar.otherRunningLink': "View this {entity}'s experiments",
  'experiments.detailsActionBar.requestApproval': 'Request approval to start',
  'experiments.detailsActionBar.start': 'Start',
  'experiments.detailsActionBar.startDisabledActiveRollout':
    'You cannot start an {type} if there is an active progressive or guarded rollout',
  'experiments.detailsActionBar.startDisabledArchived': 'You cannot start an archived {type}.',
  'experiments.detailsActionBar.startDisabledDesignIncomplete':
    'You cannot start this {type} because the {type} design is not complete.',
  'experiments.detailsActionBar.startDisabledFlagArchived': 'You cannot start an {type} if the {entity} is archived.',
  'experiments.detailsActionBar.startDisabledFlagOff': 'You cannot start an {type} if the {entity} is off.',
  'experiments.detailsActionBar.startDisabledNoPermission': 'You do not have permission to start this {type}.',
  'experiments.detailsActionBar.startDisabledOtherRunning':
    'You cannot start an {type} if there is another one running.',
  'experiments.detailsActionBar.startDisabledTrialLapsed':
    'You cannot start this {type} because your access has ended.',
  'experiments.detailsActionBar.startWithoutApproval': 'Start without approval',
  'experiments.detailsActionBar.startedOn': 'Started on',
  'experiments.detailsActionBar.stoppedOn': 'Stopped on',
  'experiments.detailsActionBar.trialLapsed': 'Your access to experimentation has ended. You cannot start this {type}.',
  'experiments.detailsActionBar.trialLapsedStop':
    'Your access to experimentation has ended. If you stop this {type}, you cannot restart it.',
  'experiments.differenceChart.noDataAvailable': 'No data available',
  'experiments.differenceChart.seriesName': 'Relative Difference',
  'experiments.draftExperimentPage.loadError': 'We were unable to load this experiment.',
  'experiments.draftExperimentPreview.allocation': 'Allocation',
  'experiments.draftExperimentPreview.allocationSummary':
    '<allocation>{percent}%</allocation> of <contextKind></contextKind> contexts are in this {type}. The remaining <remaining>{remainingPercent}%</remaining> will get',
  'experiments.draftExperimentPreview.assignmentDataEnd': 'Assignment data end:',
  'experiments.draftExperimentPreview.assignmentDataStart': 'Assignment data start:',
  'experiments.draftExperimentPreview.audienceTargeting': 'Audience targeting',
  'experiments.draftExperimentPreview.configLabel': 'Config',
  'experiments.draftExperimentPreview.defaultRuleSummary': 'All <contextKind></contextKind> contexts will be targeted',
  'experiments.draftExperimentPreview.downloadFile': 'Download file',
  'experiments.draftExperimentPreview.experimentId': 'Experiment ID:',
  'experiments.draftExperimentPreview.experimentType': 'experiment',
  'experiments.draftExperimentPreview.flagLabel': 'Flag',
  'experiments.draftExperimentPreview.holdout': 'Holdout',
  'experiments.draftExperimentPreview.holdoutLoadError': 'Holdout information did not load',
  'experiments.draftExperimentPreview.holdoutSummary':
    '<emphasis>{percent}%</emphasis> of <contextKind></contextKind> contexts are held out from this {type} using <link>{name}</link>. <br></br>Of the remaining <emphasis>{remainingPercent}%</emphasis>,',
  'experiments.draftExperimentPreview.hypothesisHeading': 'Hypothesis',
  'experiments.draftExperimentPreview.initialSplitHeading': 'Initial split',
  'experiments.draftExperimentPreview.layerSummary':
    'Of the <emphasis>{percent}%</emphasis> of <contextKind></contextKind> contexts that visit {name},',
  'experiments.draftExperimentPreview.loadingAssignmentSourceLabel': 'Loading assignment source',
  'experiments.draftExperimentPreview.loadingHoldoutLabel': 'loading holdout',
  'experiments.draftExperimentPreview.mabType': 'multi-armed bandit',
  'experiments.draftExperimentPreview.mutualExclusionLayer': 'Mutual exclusion layer',
  'experiments.draftExperimentPreview.optimizationGoalHeading': 'Optimization goal',
  'experiments.draftExperimentPreview.preview': 'Preview',
  'experiments.draftExperimentPreview.reallocationFrequency':
    'Your traffic will be re-allocated every <emphasis>{frequency} {unit}</emphasis>.',
  'experiments.draftExperimentPreview.showAbsolutePercentage': 'Show absolute percentage',
  'experiments.draftExperimentPreview.source': 'Source',
  'experiments.draftExperimentPreview.splitHeading': 'Split',
  'experiments.draftExperimentPreview.stratifiedSamplingSummary':
    '<strong>Stratified sampling</strong> will be applied using',
  'experiments.draftExperimentPreview.targetingRule': 'Targeting rule:',
  'experiments.draftPreview.collapseLabel': 'Collapse',
  'experiments.draftPreview.expandLabel': 'Expand',
  'experiments.draftPreview.formLabel': 'Draft Form',
  'experiments.draftPreview.loadError': 'We were unable to load the preview.',
  'experiments.draftPreview.sectionInfoLabel': '{section} info',
  'experiments.draftPreview.tryAgain': 'Try again',
  'experiments.emptyState.body':
    "Measure the impact of features, configurations, models, or prompts with the flags you already use to ship code. Test changes on real users, compare outcomes, and promote the winning variation to production when you're ready.",
  'experiments.emptyState.createExperiment': 'Create experiment',
  'experiments.emptyState.exploreExample': 'Explore example',
  'experiments.emptyState.heading': 'Make the right decision, sooner',
  'experiments.emptyState.readAbout': 'Read about experimentation',
  'experiments.environmentConfirmationDialog.environmentLabel': 'Environment',
  'experiments.experimentAlert.createForbidden':
    "Your role does not have permission to create experiments. Update your role's permissions to proceed.",
  'experiments.experimentAlert.duplicateName':
    'Experiment name already exists. Change the experiment name to create a new experiment.',
  'experiments.experimentAlert.editForbidden':
    "Your role does not have permission to edit experiments. Update your role's permissions to proceed.",
  'experiments.experimentAlert.flagInUse':
    'An experiment that uses this flag is running. You must stop the other experiment before starting this one.',
  'experiments.experimentApprovalForm.createError': 'Failed to create approval request. Try again later.',
  'experiments.experimentApprovalForm.environmentMismatch': 'The environment name or key entered is incorrect',
  'experiments.experimentApprovalForm.prepareError': 'Failed to prepare approval request. Try again later.',
  'experiments.experimentApprovalForm.reasonRequired': 'Provide a reason.',
  'experiments.experimentApprovalForm.requested': 'Approval requested',
  'experiments.experimentApprovalForm.reviewersRequired':
    'At least {count} {count, plural, one {reviewer} other {reviewers}} required',
  'experiments.experimentCard.loadingLabel': 'Loading experiment data',
  'experiments.experimentDurationTooltip.created': 'Created',
  'experiments.experimentDurationTooltip.iterationCount': 'Iteration {count}',
  'experiments.experimentDurationTooltip.started': 'Started',
  'experiments.experimentDurationTooltip.stopped': 'Stopped',
  'experiments.experimentFormToast.layerRevertFailed':
    'The experiment was not updated, and the layer changes could not be undone. Review the layer before you try again.',
  'experiments.experimentFormToast.updateError':
    'Failed to update {isMab, select, true {multi-armed bandit} other {experiment}}',
  'experiments.experimentFormToast.updateForbidden':
    'You do not have permission to update this {isMab, select, true {multi-armed bandit} other {experiment}}',
  'experiments.experimentFormToast.updated': '{isMab, select, true {Multi-armed bandit} other {Experiment}} updated',
  'experiments.experimentFormToast.updatedAndStarted':
    '{isMab, select, true {Multi-armed bandit} other {Experiment}} updated and iteration started',
  'experiments.experimentIterationPage.loadError': 'Error: {message}',
  'experiments.experimentIterationToast.startFailed':
    'Failed to start {isMab, select, true {multi-armed bandit} other {experiment}} iteration',
  'experiments.experimentIterationToast.stopFailed':
    'Failed to stop {isMab, select, true {multi-armed bandit} other {experiment}} iteration',
  'experiments.experimentIterationToast.stopped':
    '{isMab, select, true {Multi-armed bandit} other {Experiment}} iteration stopped',
  'experiments.experimentIterationToast.unknownError': 'An unknown error occurred',
  'experiments.experimentLink.loadError': 'Error loading experiment',
  'experiments.experimentLink.loadingLabel': 'Loading experiment to generate link',
  'experiments.experimentMaintainerToast.followError': 'Failed to follow experiment',
  'experiments.experimentMaintainerToast.updateError': 'Failed to update maintainer',
  'experiments.experimentMaintainerToast.updated': 'Updated maintainer',
  'experiments.experimentMetricsTooltip.primaryMetric': 'Primary metric',
  'experiments.experimentMetricsTooltip.secondaryMetrics': 'Secondary metrics',
  'experiments.experimentResults.analyzingData': 'Currently analyzing data',
  'experiments.experimentResults.loadError': 'Could not load experiment results',
  'experiments.experimentRollout.advanced': 'Advanced',
  'experiments.experimentRollout.allocationError': 'Error: Allocation can not exceed 100%.',
  'experiments.experimentRollout.increaseUsersWarning':
    'If you increase the number of users in a variation, we recommend you start a new experiment iteration for more accurate results. To learn more about changing variations,<link>read the documentation</link>',
  'experiments.experimentRollout.noMetricAttached':
    'You don’t have a metric attached to this experiment. First, <link>add a metric</link>.',
  'experiments.experimentRollout.percentInExperiment': 'will be in the experiment.',
  'experiments.experimentRollout.preventReassignment': 'Prevent variation reassignment when increasing traffic',
  'experiments.experimentRollout.reassignmentHelp':
    'By default, users remain assigned to their experiment variations for the duration of an experiment iteration, and may be reassigned when a new iteration is started. To learn more,',
  'experiments.experimentRollout.remainingServed': 'The remaining <percent></percent> will be served',
  'experiments.experimentRollout.variationWeightLabel': 'Variation {number} rollout weight',
  'experiments.experimentSkillCard.body':
    'No hypothesis, metric, or flag yet? Your coding agent can scan your code, suggest what to test, and draft the experiment for you. It asks before it creates anything.',
  'experiments.experimentSkillCard.footnote':
    'Works with Claude Code, Cursor, and other agents that support skills. Uses your LaunchDarkly MCP server or an API token.',
  'experiments.experimentSkillCard.heading': 'Build an experiment with your coding agent',
  'experiments.experimentSkillCard.promptSnippetTitle': 'Prompt',
  'experiments.experimentStatusTooltip.decisionMade': 'Decision made',
  'experiments.experimentStatusTooltip.loadingLabel': 'Loading experiment analysis',
  'experiments.experimentStatusTooltip.meetsThreshold': 'Meets threshold',
  'experiments.experimentStatusTooltip.noTreatmentsMeetThreshold': 'No treatments meet the threshold.',
  'experiments.experimentStatusTooltip.treatmentMetThreshold':
    '<name></name> has met the threshold {hasThreshold, select, true {of <threshold></threshold>% } other {}}for the primary metric.',
  'experiments.experimentStatusTooltip.treatmentShipped': '<name></name> was shipped because:',
  'experiments.experimentTagsToast.updateError': 'Failed to update tags',
  'experiments.experimentTagsToast.updated': 'Updated tags',
  'experiments.experimentsList.addTagsLabel': 'add tags button',
  'experiments.experimentsList.duration': 'Duration',
  'experiments.experimentsList.durationDays': '{count, plural, one {# day} other {# days}}',
  'experiments.experimentsList.durationHours': '{count, plural, one {# hour} other {# hours}}',
  'experiments.experimentsList.durationMinutes': '{count, plural, one {# minute} other {# minutes}}',
  'experiments.experimentsList.durationTooltipHeading': 'Duration',
  'experiments.experimentsList.durationUnderMinute': '< 1 minute',
  'experiments.experimentsList.goalTooltipHeading': 'Goal',
  'experiments.experimentsList.holdoutName': 'Holdout: {name}',
  'experiments.experimentsList.hypothesisTooltipHeading': 'Hypothesis',
  'experiments.experimentsList.hypothesisTooltipLabel': 'Hypothesis tooltip',
  'experiments.experimentsList.layerName': 'Layer: {name}',
  'experiments.experimentsList.maintainer': 'Maintainer',
  'experiments.experimentsList.maintainerLoadingLabel': 'Loading member',
  'experiments.experimentsList.maintainerTooltipHeading': 'Maintainer',
  'experiments.experimentsList.maintainerTooltipLabel': 'Maintainer tooltip',
  'experiments.experimentsList.metricUsedColumn': 'Metric used',
  'experiments.experimentsList.metricUsedTooltipHeading': 'Metric used',
  'experiments.experimentsList.metricsUsedColumn': 'Metrics used',
  'experiments.experimentsList.metricsUsedTooltipHeading': 'Metrics used',
  'experiments.experimentsList.name': 'Name',
  'experiments.experimentsList.showMoreTagsLabel': 'Show {count} more tags',
  'experiments.experimentsList.started': 'Started',
  'experiments.experimentsList.status': 'Status',
  'experiments.experimentsList.statusTagsLabel': 'Status',
  'experiments.experimentsList.statusTooltipHeading': 'Status',
  'experiments.experimentsList.stopped': 'Stopped',
  'experiments.experimentsList.tags': 'Tags',
  'experiments.experimentsList.tagsEmptyState': 'None',
  'experiments.experimentsList.tagsLabel': 'Experiment tags',
  'experiments.experimentsListActions.archive': 'Archive',
  'experiments.experimentsListActions.archiveDisabledTooltip':
    'Only non-running experiments without a layer can be archived.',
  'experiments.experimentsListActions.archiveError': 'Failed to archive experiment {name}',
  'experiments.experimentsListActions.archived': 'Experiment {name} archived',
  'experiments.experimentsListActions.cloneExperiment': 'Clone experiment',
  'experiments.experimentsListActions.copyKey': 'Copy key',
  'experiments.experimentsListActions.copyLink': 'Copy link',
  'experiments.experimentsListActions.keyCopied': 'Experiment key copied to clipboard',
  'experiments.experimentsListActions.linkCopied': 'Experiment link copied to clipboard',
  'experiments.experimentsListActions.menuLabel': 'Experiment actions',
  'experiments.experimentsListDisplayPreferences.columnsHeading': 'Show these columns',
  'experiments.experimentsListDisplayPreferences.display': 'Display',
  'experiments.experimentsListDisplayPreferences.duration': 'Duration',
  'experiments.experimentsListDisplayPreferences.maintainer': 'Maintainer',
  'experiments.experimentsListDisplayPreferences.metricsUsed': 'Metrics used',
  'experiments.experimentsListDisplayPreferences.started': 'Started',
  'experiments.experimentsListDisplayPreferences.status': 'Status',
  'experiments.experimentsListDisplayPreferences.stopped': 'Stopped',
  'experiments.experimentsListDisplayPreferences.tags': 'Tags',
  'experiments.experimentsListEmptyState.body':
    'Use experiments to measure and understand the impact of every feature you release',
  'experiments.experimentsListEmptyState.clearFilters': 'Clear filters',
  'experiments.experimentsListEmptyState.createExperiment': 'Create experiment',
  'experiments.experimentsListEmptyState.filteredBody': 'Try adjusting your filters',
  'experiments.experimentsListEmptyState.filteredHeading': 'No experiments match your filter criteria',
  'experiments.experimentsListEmptyState.heading': "This environment doesn't have any experiments",
  'experiments.experimentsListEmptyState.restrictedTooltip': 'You do not have permission to create an experiment',
  'experiments.experimentsListEmptyState.viewGuide': 'View guide',
  'experiments.exposureDetailDialog.attributes': 'Attributes',
  'experiments.exposureDetailDialog.attributesSource': 'Attributes source',
  'experiments.exposureDetailDialog.close': 'Close',
  'experiments.exposureDetailDialog.experiment': 'Experiment',
  'experiments.exposureDetailDialog.exposureDetails': 'Exposure details',
  'experiments.exposureDetailDialog.firstEvaluation': 'First evaluation',
  'experiments.exposureDetailDialog.flagAgentcontrolConfig': 'Flag / AgentControl config',
  'experiments.exposureDetailDialog.key': 'Key',
  'experiments.exposureDetailDialog.sessionReplay': 'Session replay',
  'experiments.exposureDetailDialog.variation': 'Variation',
  'experiments.exposureDisparityWarning.body':
    'This number does not match the original number of exposures. This typically only happens when there is a unit cut, but no cut was detected.',
  'experiments.exposureDisparityWarning.heading': 'Mismatch from original number of exposures',
  'experiments.exposureDisparityWarning.learnMore': 'Learn more',
  'experiments.exposuresCard.exposureDetails': 'Exposure details',
  'experiments.exposuresCard.heading': 'Exposures',
  'experiments.exposuresCard.percentage': 'Percentage',
  'experiments.exposuresCard.showTable': 'Show table',
  'experiments.exposuresCard.tableLabel': 'Experiment exposures table',
  'experiments.exposuresCard.variation': 'Variation',
  'experiments.exposuresChart.analyzingSubtitle': 'Exposures appear after the assignment data is analyzed',
  'experiments.exposuresChart.analyzingTitle': 'Currently analyzing data',
  'experiments.exposuresChart.emptyTitle': 'No exposures yet',
  'experiments.exposuresChart.notStartedSubtitle': 'Exposures will appear after you start the iteration',
  'experiments.exposuresCsvExport.attributesColumn': 'Attributes',
  'experiments.exposuresCsvExport.contextKindColumn': 'Context kind',
  'experiments.exposuresCsvExport.experimentColumn': 'Experiment',
  'experiments.exposuresCsvExport.failed': 'Failed to export exposures to CSV. Try again in a moment.',
  'experiments.exposuresCsvExport.flagColumn': 'Flag / AgentControl config',
  'experiments.exposuresCsvExport.keyColumn': 'Key',
  'experiments.exposuresCsvExport.nameColumn': 'Name',
  'experiments.exposuresCsvExport.noExposures': 'No exposures to export.',
  'experiments.exposuresCsvExport.sessionIdColumn': 'Session ID',
  'experiments.exposuresCsvExport.succeeded': 'Exposures exported to CSV.',
  'experiments.exposuresCsvExport.timestampColumn': 'Timestamp',
  'experiments.exposuresCsvExport.truncated':
    'This CSV includes the most recent {limit} exposures, the maximum available for export.',
  'experiments.exposuresCsvExport.variationColumn': 'Variation',
  'experiments.exposuresFilters.dateRange': 'Date range',
  'experiments.exposuresFilters.searchPlaceholder': 'Search by name or key',
  'experiments.exposuresFilters.variation': 'Variation',
  'experiments.exposuresPage.loadErrorDescription': 'Something went wrong loading exposures. Try again in a moment.',
  'experiments.exposuresPage.loadErrorHeading': "Couldn't load exposures",
  'experiments.exposuresPageChart.downloadCsv': 'Download CSV',
  'experiments.exposuresPageChart.noExposureData': 'No exposure data to chart',
  'experiments.exposuresPageChart.uniqueCountsLabel': 'Unique counts of',
  'experiments.exposuresTable.emptyDescription':
    'No evaluations match your filters. Try adjusting the filter settings.',
  'experiments.exposuresTable.emptyHeading': 'No exposures found',
  'experiments.exposuresTable.firstEvaluationColumn': 'First evaluation',
  'experiments.exposuresTable.keyColumn': 'Key',
  'experiments.exposuresTable.nameColumn': 'Name',
  'experiments.exposuresTable.sessionReplayColumn': 'Session replay',
  'experiments.exposuresTable.tableLabel': 'Experiment exposures',
  'experiments.exposuresTable.variationColumn': 'Variation',
  'experiments.filters.activeCountLabel': '{count, plural, one {# active filter} other {# active filters}}',
  'experiments.filters.clear': 'Clear',
  'experiments.filters.dialogLabel': 'Experiment Filters',
  'experiments.filters.loadError': 'Something went wrong while loading filters.',
  'experiments.filters.loadErrorNotified': "We've been notified.",
  'experiments.filters.startedHeading': 'Started',
  'experiments.filters.trigger': 'Filters',
  'experiments.filters.tryAgain': 'Try again',
  'experiments.filtersDialog.clear': 'Clear',
  'experiments.filtersDialog.dialogLabel': 'Experiment Filters',
  'experiments.filtersDialog.loadError': 'Something went wrong while loading filters.',
  'experiments.filtersDialog.loadErrorNotified': "We've been notified.",
  'experiments.filtersDialog.started': 'Started',
  'experiments.filtersDialog.tryAgain': 'Try again',
  'experiments.flagExperimentCard.loadError': 'Failed to load experiment',
  'experiments.flagOrConfigPicker.clearSearchLabel': 'Clear search',
  'experiments.flagOrConfigPicker.configLockedNotice':
    'The flag or config cannot be changed after starting the {type}.',
  'experiments.flagOrConfigPicker.configTag': 'Config',
  'experiments.flagOrConfigPicker.dialogLabel': 'Select flag or config',
  'experiments.flagOrConfigPicker.edit': 'Edit',
  'experiments.flagOrConfigPicker.experimentType': 'experiment',
  'experiments.flagOrConfigPicker.flag': 'Flag',
  'experiments.flagOrConfigPicker.flagFieldLabel': 'Flag',
  'experiments.flagOrConfigPicker.flagLockedNotice': 'The flag cannot be changed after starting the {type}.',
  'experiments.flagOrConfigPicker.flagOrConfigFieldLabel': 'Flag or config',
  'experiments.flagOrConfigPicker.learnMore': 'Learn more',
  'experiments.flagOrConfigPicker.loadingLabel': 'loading',
  'experiments.flagOrConfigPicker.mabType': 'multi-armed bandit',
  'experiments.flagOrConfigPicker.noConfigMatches': 'No flags or configs match your search',
  'experiments.flagOrConfigPicker.noConfigsFound': 'No configs found.',
  'experiments.flagOrConfigPicker.noFlagMatches': 'No flags match your search',
  'experiments.flagOrConfigPicker.noFlagsFound': 'No flags found.',
  'experiments.flagOrConfigPicker.noFlagsOrConfigsFound': 'No flags or configs found.',
  'experiments.flagOrConfigPicker.pickerInfo': 'Select or create a flag to define variations to test.',
  'experiments.flagOrConfigPicker.pickerInfoWithConfigs':
    'Select or create a flag or config to define variations to test. Configs test LLMs within your generative AI applications.',
  'experiments.flagOrConfigPicker.resultsLabel': 'Flag or config',
  'experiments.flagOrConfigPicker.searchConfigsPlaceholder': 'Find a flag or config by name or key',
  'experiments.flagOrConfigPicker.searchEmptyStateHint': 'Try searching in All',
  'experiments.flagOrConfigPicker.searchFlagsPlaceholder': 'Find a flag by name or key',
  'experiments.flagOrConfigPicker.searchLabel': 'Select a flag or config',
  'experiments.flagOrConfigPicker.twoVariationsRequired':
    '- You must have at least two variations to create an experiment.',
  'experiments.flagOrConfigPicker.typeFilterLabel': 'Filter by type',
  'experiments.flagRuleExperimentInfo.body':
    "This rule has been added to an experiment. The experiment's traffic allocation will appear here if it is running.",
  'experiments.flagRuleExperimentInfo.bodyWithNames':
    "This rule has been added to {count, plural, one {the experiment} other {the experiments}} <experiments></experiments>. {count, plural, one {The experiment's traffic allocation} other {Each experiment's traffic allocation}} will appear here if it is running.",
  'experiments.flagRuleVariationSelector.fallthroughLabel': 'and if experiment is not running, serve',
  'experiments.flagRuleVariationSelector.saveHint': 'Save to create an experiment from this rule',
  'experiments.flagRuleVariationSelector.variationPlaceholder': 'Select variation',
  'experiments.forestPlot.adjustedForSequential': 'Adjusted for sequential testing',
  'experiments.forestPlot.learnMore': 'Learn more',
  'experiments.forestPlot.relativeDifference': 'Relative difference',
  'experiments.forestPlot.significanceLevel': 'Significance level',
  'experiments.forestPlot.threshold': 'Threshold',
  'experiments.forestPlot.tooltipLoadError': 'Something went wrong',
  'experiments.forestPlot.variation': 'Variation',
  'experiments.forestPlot.zeroBaselineExplanation':
    "LaunchDarkly can't compute statistical results on a relative difference basis because your baseline mean is exactly zero.",
  'experiments.formulaPopover.calculation': 'Calculation',
  'experiments.formulaPopover.cupedDataMean':
    'Data mean = ( {conversions} / Exposures ) - ( Correlation ) ( SD ratio ) ( Covariate imbalance )',
  'experiments.formulaPopover.dataMean': 'Data mean = {conversions} / Exposures',
  'experiments.formulaPopover.dataPercentile': 'Data percentile = The observed percentile from the data',
  'experiments.formulaPopover.formula': 'Formula',
  'experiments.formulaPopover.frequentistCupedFormula':
    '( {conversions} / Exposures ) - ( Correlation ) ( SD ratio ) ( Covariate imbalance )',
  'experiments.formulaPopover.frequentistFormula': '{conversions} / Exposures',
  'experiments.formulaPopover.meanWeight':
    'Weight = A number between 0 and 1 that scales with the precision of the mean in this arm. As more data arrives, the weight given to the data mean increases.',
  'experiments.formulaPopover.percentileWeight':
    'Weight = A number between 0 and 1 that scales with the precision of the percentile in this arm. As more data arrives, the weight given to the data percentile increases.',
  'experiments.formulaPopover.priorMean':
    'Prior mean = The {kind, select, binary {posterior} other {data}} mean for the control variation',
  'experiments.formulaPopover.priorMeanControl':
    'Prior mean = {priorMean} for the control variation, which always uses an uninformative prior',
  'experiments.formulaPopover.priorMeanUninformative': 'Prior mean = {priorMean} for an uninformative prior',
  'experiments.formulaPopover.priorPercentile': 'Prior percentile = The estimated percentile for the control variation',
  'experiments.formulaPopover.priorPercentileUninformative': 'Prior percentile = 0 for an uninformative prior',
  'experiments.formulaPopover.weightedMean': '( Weight ) ( Data mean ) + ( 1 - Weight ) ( Prior mean )',
  'experiments.formulaPopover.weightedPercentile':
    '( Weight ) ( Data percentile ) + ( 1 - Weight ) ( Prior percentile )',
  'experiments.formulaPopover.where': 'Where',
  'experiments.fullReasonDialog.closeLabel': 'Close',
  'experiments.getStarted.body':
    'Test which button label gets more clicks in your app. Only you and the teammates you add will see this experiment.',
  'experiments.getStarted.createFailedTitle': 'Could not create experiment',
  'experiments.getStarted.heading': 'A sample experiment, ready to go',
  'experiments.getStarted.startFromScratch': 'or start from scratch',
  'experiments.getStarted.trySampleExperiment': 'Try sample experiment',
  'experiments.getStarted.unknownError': 'An unknown error occurred. Try again.',
  'experiments.historicalResultsChart.controlLabel': 'Control',
  'experiments.historicalResultsChart.noDataMessage': 'No historical results data available',
  'experiments.historicalResultsChart.sliceNotSupported': 'Not supported for sliced results',
  'experiments.historicalResultsChart.variation': 'Variation',
  'experiments.holdoutAmountSection.fieldLabel': 'Holdout amount',
  'experiments.holdoutAnalysisBanner.body':
    'These results include both winning and non-winning experiment treatments, because there are experiments still running.',
  'experiments.holdoutAnalysisSection.bayesian': 'Bayesian',
  'experiments.holdoutAnalysisSection.frequentist': 'Frequentist',
  'experiments.holdoutAnalysisSection.learnMore': 'Learn more',
  'experiments.holdoutAnalysisSection.methodologyHelp':
    'If you are familiar with p-values, choose frequentist. Otherwise, choose Bayesian.',
  'experiments.holdoutAnalysisSection.methodologyLabel': 'Statistical approach',
  'experiments.holdoutAnalysisSection.oneSided': 'One-sided',
  'experiments.holdoutAnalysisSection.sampleSizeCalculator':
    'Use the <link>sample size calculator</link> to determine your sample size.',
  'experiments.holdoutAnalysisSection.significanceLevelHelp':
    "The significance level is a threshold used to determine whether a result is meaningful, or statistically significant, rather than just due to chance. If a treatment variation's p-value is equal to or smaller than the significance level, the result is considered statistically significant. A common significance level is 0.05.",
  'experiments.holdoutAnalysisSection.significanceLevelLabel': 'Significance level',
  'experiments.holdoutAnalysisSection.significanceThresholdLabel': 'Significance threshold',
  'experiments.holdoutAnalysisSection.testDirectionHelp':
    'If you want to see whether a treatment variation performs statistically significantly better or worse than the control, choose a two-sided test. If you only want to see improvement over the control, choose a one-sided test.',
  'experiments.holdoutAnalysisSection.testDirectionLabel': 'Direction of hypothesis test',
  'experiments.holdoutAnalysisSection.thresholdHelp':
    "The threshold helps decide if a treatment variation performs meaningfully different from the control. If a treatment's probability to beat control meets or exceeds this threshold, it can be considered the best-performing variation. A common threshold used is 95%. The threshold doesn't change most of the reported numbers, except for the credible interval.",
  'experiments.holdoutAnalysisSection.thresholdLabel': 'Threshold',
  'experiments.holdoutAnalysisSection.twoSided': 'Two-sided',
  'experiments.holdoutAttributesSection.help':
    'Attributes give you the ability to segment your results by context attribute after your experiment has started.',
  'experiments.holdoutAttributesSection.learnMore': 'Learn more',
  'experiments.holdoutBandits.addLabel': 'Add Multi-armed Bandit',
  'experiments.holdoutBandits.cardLoadError': 'Failed to load Multi-armed Bandit',
  'experiments.holdoutBandits.emptyState': 'None',
  'experiments.holdoutBandits.failedToLoad': 'Failed to load Multi-armed Bandits',
  'experiments.holdoutBandits.heading': 'Multi-armed bandits in this holdout',
  'experiments.holdoutBandits.tryAgain': 'Try again',
  'experiments.holdoutDescriptionSection.fieldAriaLabel': 'Holdout description',
  'experiments.holdoutDescriptionSection.fieldLabel': 'Description',
  'experiments.holdoutDescriptionSection.fieldPlaceholder': 'Describe the purpose and scope of this holdout…',
  'experiments.holdoutDetails.analysisError': "An unexpected error has occurred rendering the experiment's analysis",
  'experiments.holdoutDetails.analyzingFootnote':
    "When you end analysis of this holdout, you can't restart. Ending will ship the winning treatments to all held out contexts.",
  'experiments.holdoutDetails.analyzingFootnoteRunning':
    'This holdout includes running experiments. To end this holdout you must stop all included experiments.',
  'experiments.holdoutDetails.analyzingHeadline': 'Analyzing',
  'experiments.holdoutDetails.completeHeadline': 'Complete',
  'experiments.holdoutDetails.createdDescription':
    'Holdout configuration is complete. Ready to include experiments and multi-armed bandits.',
  'experiments.holdoutDetails.createdHeadline': 'Created',
  'experiments.holdoutDetails.declareReady': 'Declare ready',
  'experiments.holdoutDetails.edit': 'Edit',
  'experiments.holdoutDetails.enabledFootnote':
    'To begin analysis of this holdout, start an experiment or multi-armed bandit that is included in it.',
  'experiments.holdoutDetails.end': 'End',
  'experiments.holdoutDetails.endedOn': 'This holdout was ended on',
  'experiments.holdoutDetails.incompleteDescription': 'Holdout design is not complete.',
  'experiments.holdoutDetails.percentileMetricsError':
    "An unexpected error has occurred rendering the experiment's percentile metrics",
  'experiments.holdoutDetails.readyDescription':
    'Holdout configuration complete. Declare ready to include experiments and multi-armed bandits.',
  'experiments.holdoutDetails.readyHeadline': 'Ready',
  'experiments.holdoutDetails.startedLabel': 'Started:',
  'experiments.holdoutDetails.stop': 'Stop',
  'experiments.holdoutDetails.stopDisabledTooltip': 'You cannot stop a holdout that has running experiments',
  'experiments.holdoutEmptyState.completeHeading':
    'Include experiments and multi-armed bandits when the design is ready',
  'experiments.holdoutEmptyState.create': 'Create',
  'experiments.holdoutEmptyState.createdHeading': 'Ready to include experiments and multi-armed bandits',
  'experiments.holdoutEmptyState.enabledHeading': 'No results yet',
  'experiments.holdoutEmptyState.endedBody':
    'This holdout was stopped before any experiments or multi-armed bandits were included.',
  'experiments.holdoutEmptyState.endedHeading': 'No results',
  'experiments.holdoutEmptyState.experiment': 'Experiment',
  'experiments.holdoutEmptyState.multiArmedBandit': 'Multi-armed bandit',
  'experiments.holdoutEmptyState.notStartedHeading':
    'Include experiments and multi-armed bandits when the design is complete',
  'experiments.holdoutExperiments.addExperiment': 'Add experiment',
  'experiments.holdoutExperiments.cardLoadError': 'Failed to load experiment',
  'experiments.holdoutExperiments.emptyState': 'None',
  'experiments.holdoutExperiments.failedToLoad': 'Failed to load experiments',
  'experiments.holdoutExperiments.heading': 'Experiments in this holdout',
  'experiments.holdoutExperiments.tryAgain': 'Try again',
  'experiments.holdoutFilter.any': 'Any',
  'experiments.holdoutFilter.chooseHoldout': 'Choose holdout',
  'experiments.holdoutFilter.label': 'Holdouts',
  'experiments.holdoutForm.advancedTitle': 'Advanced',
  'experiments.holdoutForm.amountDescription': 'The percentage of traffic allocated to the holdout variation.',
  'experiments.holdoutForm.amountRecommendation': 'We recommend a holdout of between 1 to 5%.',
  'experiments.holdoutForm.amountTitle': 'Holdout amount',
  'experiments.holdoutForm.analysisTitle': 'Statistical approach and success criteria',
  'experiments.holdoutForm.formLabel': 'Draft Holdout Form',
  'experiments.holdoutForm.intro':
    'A holdout is a small, ongoing control group kept on the old experience to measure the lasting impact of a change.',
  'experiments.holdoutForm.learnMore': 'Learn more',
  'experiments.holdoutForm.loadError': 'We were unable to load this holdout.',
  'experiments.holdoutForm.metricsTitle': 'Metrics',
  'experiments.holdoutForm.saveError': 'Failed to save holdout',
  'experiments.holdoutForm.updateForbidden': 'You do not have permission to update this holdout',
  'experiments.holdoutForm.updatedToast': 'Holdout updated',
  'experiments.holdoutFormActionBar.cancel': 'Cancel',
  'experiments.holdoutFormActionBar.completeDescription':
    'Holdout configuration complete. Declare ready to include experiments and multi-armed bandits.',
  'experiments.holdoutFormActionBar.createdHeadline': 'Created',
  'experiments.holdoutFormActionBar.declareReady': 'Declare ready',
  'experiments.holdoutFormActionBar.editingHeadline': 'Editing design',
  'experiments.holdoutFormActionBar.incompleteDescription': 'Holdout design is not complete.',
  'experiments.holdoutFormActionBar.missingRequirements': 'Fill out the following to complete your design:',
  'experiments.holdoutFormActionBar.save': 'Save',
  'experiments.holdoutMetricsSection.dialogContent':
    'Add metrics or metric groups to assign primary and secondary metrics. <link>Learn more</link>',
  'experiments.holdoutMetricsSection.fieldLabel': 'Metrics',
  'experiments.holdoutMetricsSection.searchLabel': 'Select metrics',
  'experiments.holdoutMetricsSection.selectButton': 'Select metrics or metric groups',
  'experiments.holdoutNameSection.fieldAriaLabel': 'Holdout name',
  'experiments.holdoutNameSection.fieldLabel': 'Name',
  'experiments.holdoutNameSection.fieldPlaceholder': 'e.g., Homepage holdout',
  'experiments.holdoutNotFound.createHoldout': 'Create holdout',
  'experiments.holdoutNotFound.heading': 'Holdout does not exist in this environment',
  'experiments.holdoutNotFound.lead': 'Holdouts are environment-specific.',
  'experiments.holdoutNotFound.orGoBack': 'or go <link>back to your holdouts</link>',
  'experiments.holdoutPreview.descriptionHeading': 'Description',
  'experiments.holdoutPreview.inHoldout': 'In holdout',
  'experiments.holdoutPreview.outsideHoldout': 'Outside holdout',
  'experiments.holdoutPreview.preview': 'Preview',
  'experiments.holdoutPreview.splitTitle': 'Holdout split',
  'experiments.holdoutRandomizationSection.dialogContent':
    'Experiments randomly sort traffic into variations by randomization unit',
  'experiments.holdoutRandomizationSection.fieldLabel': 'Randomize by',
  'experiments.holdoutRandomizationSection.fieldPlaceholder': 'Select a randomization unit',
  'experiments.holdoutSidebar.actionLabel': 'entity action',
  'experiments.holdoutSidebar.analysisHeading': 'Statistical approach and success',
  'experiments.holdoutSidebar.archiveBody': 'Permanently archive this holdout across all environments in this project',
  'experiments.holdoutSidebar.archiveConfirm': 'Archive holdout',
  'experiments.holdoutSidebar.archiveDisabledTooltip': 'You can only archive after the experiment has ended.',
  'experiments.holdoutSidebar.archiveLabel': 'Archive holdout',
  'experiments.holdoutSidebar.archiveTitle': 'Archive "{name}"?',
  'experiments.holdoutSidebar.copyLinkTooltip': 'Copy holdout link',
  'experiments.holdoutSidebar.descriptionHeading': 'Description',
  'experiments.holdoutSidebar.maintainerHeading': 'Maintainer',
  'experiments.holdoutSidebar.randomizationUnitHeading': 'Randomization unit',
  'experiments.holdoutsEmptyState.body':
    'Holdouts keep a share of your audience out of all experiments, so you can measure their combined impact over time.',
  'experiments.holdoutsEmptyState.clearFilters': 'Clear filters',
  'experiments.holdoutsEmptyState.createHoldout': 'Create holdout',
  'experiments.holdoutsEmptyState.filteredBody': 'Try adjusting your filters',
  'experiments.holdoutsEmptyState.filteredHeading': 'No holdouts match your filter criteria',
  'experiments.holdoutsEmptyState.heading': 'Create your first holdout',
  'experiments.holdoutsList.createdColumn': 'Created',
  'experiments.holdoutsList.tableLabel': 'holdout list',
  'experiments.hypothesisAssistPanel.aaTest': 'A/A test',
  'experiments.hypothesisAssistPanel.agentPath': 'Agent',
  'experiments.hypothesisAssistPanel.back': 'Back',
  'experiments.hypothesisAssistPanel.cancel': 'Cancel',
  'experiments.hypothesisAssistPanel.changesHint': 'Type in the changes.',
  'experiments.hypothesisAssistPanel.clear': 'Clear',
  'experiments.hypothesisAssistPanel.describeHint':
    'Describe what you want to test. More details means stronger suggestions.',
  'experiments.hypothesisAssistPanel.fastApi': 'Fast API',
  'experiments.hypothesisAssistPanel.fixAndGrade': 'Fix and grade',
  'experiments.hypothesisAssistPanel.generating': 'Generating your hypothesis…',
  'experiments.hypothesisAssistPanel.hypothesisLabel': 'Hypothesis',
  'experiments.hypothesisAssistPanel.rubricChange': 'Change',
  'experiments.hypothesisAssistPanel.rubricMeasurement': 'Measurement',
  'experiments.hypothesisAssistPanel.rubricRationale': 'Rationale',
  'experiments.hypothesisAssistPanel.saveNote': 'Saving the experiment will save your hypothesis.',
  'experiments.imagePreviewDialog.closeLabel': 'Close',
  'experiments.imagePreviewDialog.heading': 'Image Preview',
  'experiments.imagePreviewDialog.previewAlt': 'Preview',
  'experiments.imageUploadModal.cancel': 'Cancel',
  'experiments.imageUploadModal.cancelLabel': 'Cancel image upload',
  'experiments.imageUploadModal.closeLabel': 'Close',
  'experiments.imageUploadModal.deleteImageLabel': 'delete image',
  'experiments.imageUploadModal.dropHint': 'or drag and drop',
  'experiments.imageUploadModal.fileRequirements': 'Upload a PNG, JPEG, or WebP (max 5MB)',
  'experiments.imageUploadModal.save': 'Save',
  'experiments.imageUploadModal.saveLabel': 'Save images',
  'experiments.imageUploadModal.uploadImage': 'Upload image',
  'experiments.imageUploadModal.uploadingLabel': 'Uploading image',
  'experiments.integrationHealth.failed': 'Failed. {reason}',
  'experiments.integrationHealth.failedAt': 'Failed at {timestamp}. {reason}',
  'experiments.integrationHealth.healthy': 'Healthy',
  'experiments.integrationHealth.healthyAsOf': 'Healthy as of {timestamp}',
  'experiments.integrationHealth.loadError': 'Error loading integration health status',
  'experiments.integrationHealth.loadingLabel': 'Loading integration health status',
  'experiments.integrationHealth.multipleConfigured':
    'Multiple warehouse integrations are configured for this environment',
  'experiments.integrationHealth.notInitialized': 'Not yet initialized',
  'experiments.iterationDetailsPage.backToIterations': 'Back to iterations list',
  'experiments.iterationDetailsPage.duration': 'Duration',
  'experiments.iterationDetailsPage.hypothesis': 'Hypothesis',
  'experiments.iterationDetailsPage.keyTakeaways': 'Key takeaways',
  'experiments.iterationDetailsPage.loadingAnalysisLabel': 'Loading analysis…',
  'experiments.iterationDetailsPage.pValue': 'P-value: {value}',
  'experiments.iterationDetailsPage.primaryMetricResult': 'Primary metric result',
  'experiments.iterationDetailsPage.probabilityToBeBest': 'Probability to be best: {value}',
  'experiments.iterationDetailsPage.probabilityToBeatControl': 'Probability to beat control: {value}',
  'experiments.iterationDetailsPage.reasonForStoppingHeading': 'Reason for stopping',
  'experiments.iterationDetailsPage.relativeDifference': 'Relative difference:',
  'experiments.iterationDetailsPage.relativeDifferenceTagLabel': 'Relative difference',
  'experiments.iterationDetailsPage.setup': 'Setup',
  'experiments.iterationDetailsPage.shippedVariation': 'Shipped variation',
  'experiments.iterationDetailsPage.summaryHeading': 'Summary',
  'experiments.iterationResults.analysisRenderError':
    "An unexpected error has occurred rendering the {entity, select, mab {multi-armed bandit} other {experiment}}'s analysis",
  'experiments.iterationResults.healthCheckError': 'Failed to load multi-armed bandit health check.',
  'experiments.iterationResults.percentileRenderError':
    "An unexpected error has occurred rendering the {entity, select, mab {multi-armed bandit} other {experiment}}'s percentile metrics",
  'experiments.iterationResultsEmptyState.body':
    'This {entity, select, mab {multi-armed bandit} other {experiment}} is not started so there are no {contentType, select, traffic {traffic} other {results}} to report.',
  'experiments.iterationResultsEmptyState.heading': 'No {contentType, select, traffic {traffic} other {results}} yet',
  'experiments.iterationResultsEmptyState.hint': 'To begin, click "Start" in the action bar.',
  'experiments.iterationTraffic.loadError': 'Failed to fetch traffic',
  'experiments.iterationsCard.dateEnded': 'Date ended',
  'experiments.iterationsCard.dateStarted': 'Date started',
  'experiments.iterationsCard.duration': 'Duration',
  'experiments.iterationsCard.emptyState': 'There are no past iterations for this {type}.',
  'experiments.iterationsCard.iterationLabel': 'Iteration {number}',
  'experiments.iterationsCard.iterationNumber': 'Iteration number',
  'experiments.iterationsCard.loadingLabel': 'loading',
  'experiments.iterationsCard.readMore': 'Read more at iteration',
  'experiments.iterationsCard.rowLoadError': 'Unexpected error',
  'experiments.iterationsCard.shippedBecause': '<color></color> {name} was shipped because:',
  'experiments.iterationsCard.shippedVariation': 'Shipped variation',
  'experiments.iterationsPage.errorLoadingIteration': 'Error loading iteration results',
  'experiments.iterationsTab.description':
    "An iteration describes an {type, select, mab {Multi-armed bandit} other {experiment}}'s configuration and results over a specific time period. To learn more about iterations,",
  'experiments.launchStep.aiIntro':
    "Paste this into your AI coding assistant. Two things will happen in your code: the button reads the flag to get its label, and it sends a click event to the metric. That's the full loop.",
  'experiments.launchStep.aiPromptTitle': 'AI Prompt',
  'experiments.launchStep.appConnected': 'App connected',
  'experiments.launchStep.chooseYourLanguage': 'Choose your language',
  'experiments.launchStep.commonCasesLink': 'Common cases and how to handle them',
  'experiments.launchStep.connectManually': 'Connect manually',
  'experiments.launchStep.connectWithAi': 'Connect with AI',
  'experiments.launchStep.copyPromptHeading': 'Copy prompt for AI coding assistant',
  'experiments.launchStep.emailAttributeNote':
    'Include an <strong>email</strong> attribute in your context — the Test users targeting rule matches on email.',
  'experiments.launchStep.evaluateFlagHeading': 'Evaluate the flag and track the metric',
  'experiments.launchStep.extraCodeNote': 'Some setups need extra code to track experiments correctly.',
  'experiments.launchStep.initializeSdkHeading': 'Initialize the SDK',
  'experiments.launchStep.installPackageHeading': 'Install the package',
  'experiments.launchStep.manualIntro':
    'Initialize the SDK so your app can talk to LaunchDarkly. Read the flag and track clicks on your button. This is the experiment-specific part.',
  'experiments.launchStep.tabsLabel': 'Add the flag and metric to your existing setup',
  'experiments.launchStep.title': 'Launch',
  'experiments.launchStep.unableToVerify': 'Unable to verify connection',
  'experiments.launchStep.viewDetailsLabel': 'View launch details',
  'experiments.launchStep.waitingForApp': 'Waiting for app to connect…',
  'experiments.layerFilter.any': 'Any',
  'experiments.layerFilter.chooseLayer': 'Choose layer',
  'experiments.layerFilter.label': 'Layers',
  'experiments.layerSelection.addToLayer': 'Add {type} to mutual exclusion layer',
  'experiments.layerSelection.edit': 'Edit',
  'experiments.layerSelection.holdoutConflict': 'You cannot add a layer to an {type} with a holdout.',
  'experiments.layerSelection.layerInfo':
    'Mutual exclusion layers prevent a given end user from being included in multiple {type}s at the same time, such as when you run multiple {type}s on the same page of your app.',
  'experiments.layerSelection.learnMore': 'Learn more',
  'experiments.layerSelection.name': 'Name',
  'experiments.layerSelection.reservation': 'Reservation',
  'experiments.layerSelection.reservationPercentLabel': 'Reservation percent',
  'experiments.layerSelection.reservationSummary':
    '<strong>{percent}%</strong> of the {name} layer is reserved for this experiment',
  'experiments.layerSelection.reserveExperimentTraffic': 'Reserve experiment traffic to this layer',
  'experiments.layerSelection.runningConflict':
    'You must stop the {type} iteration before you can change its layer assignment.',
  'experiments.layerSelection.stratifiedSamplingConflict':
    'You cannot add a layer to an {type} with stratified sampling.',
  'experiments.layerSelection.thisExperiment': 'This experiment',
  'experiments.layerSelection.trafficTableLabel': 'Layer traffic allocation',
  'experiments.leaveConfirmation.heading': 'Are you sure you want to leave?',
  'experiments.maintainerFilter.anyone': 'Anyone',
  'experiments.maintainerFilter.chooseMaintainer': 'Choose maintainer',
  'experiments.maintainerFilter.label': 'Maintainer',
  'experiments.metricEventStatus.checkInterval': 'LaunchDarkly checks this data source for new events every 15 minutes',
  'experiments.metricEventStatus.confirmQuery':
    'If events still do not appear, confirm your query returns rows for this metric',
  'experiments.metricEventStatus.neverSeenIn': 'Never seen in {source}',
  'experiments.metricEventStatus.noEventsSeen': 'No events seen yet',
  'experiments.metricEventStatus.readDocs': 'Read about metric events',
  'experiments.metricPicker.noMetricsFound': 'No metrics found',
  'experiments.metricPicker.searchLabel': 'Search metrics',
  'experiments.metricPicker.searchPlaceholder': 'Search metrics…',
  'experiments.metricPicker.title': 'Choose metrics',
  'experiments.metricSelector.customCategory': 'Custom',
  'experiments.metricSelector.incompatibleMetricsWarning':
    'One or more metrics are incompatible with the selected randomization unit.',
  'experiments.metricSelector.incompatibleRandomizationUnit':
    'Incompatible randomization unit: <unit></unit> must be included in the metric definition.',
  'experiments.metricSelector.metricGroupsHeading': 'Metric groups',
  'experiments.metricSelector.metricGroupsLoadError': "There was a problem loading this project's metric groups.",
  'experiments.metricSelector.metricLabel': 'Metric',
  'experiments.metricSelector.metricsLabel': 'Metrics',
  'experiments.metricSelector.metricsLoadError': "There was a problem loading this project's metrics.",
  'experiments.metricSelector.placeholder': 'Type metric name',
  'experiments.metricSourceStatus.lastUpdated': 'Results last updated on {timestamp}',
  'experiments.metricSourceStatus.loadError': 'Error loading experiment last updated status',
  'experiments.metricSourceStatus.loadingLabel': 'Loading experiment last updated status',
  'experiments.metricSourceStatus.noDataBody':
    "We haven't received any data yet for this experiment. Data may be delayed for experiments that recently started. If you think this is an error, we recommend checking your warehouse integration configuration.",
  'experiments.metricSourceStatus.noDataHeading': 'Results not updated yet',
  'experiments.metricSourceStatus.readDocs': 'Read documentation',
  'experiments.metricVersionAdoptBanner.experimentUsesPinnedVersion': 'This experiment uses <version></version>.',
  'experiments.metricVersionAdoptBanner.newerMetricVersion': 'Newer metric version available',
  'experiments.metricVersionAdoptBanner.openChangeHistory': 'Open change history',
  'experiments.metricVersionAdoptBanner.resultsUsePinnedVersion': 'These results use <version></version>.',
  'experiments.metricVersionAdoptBanner.update': 'Update',
  'experiments.metricVersionAdoptDialog.body':
    'This recalculates results for the current iteration. Previous results cannot be recovered. Other metrics are not affected.',
  'experiments.metricVersionAdoptDialog.cancel': 'Cancel',
  'experiments.metricVersionAdoptDialog.closeLabel': 'close modal',
  'experiments.metricVersionAdoptDialog.errorTitle': 'Failed to update the metric',
  'experiments.metricVersionAdoptDialog.heading': 'Update this metric to the latest version',
  'experiments.metricVersionAdoptDialog.successDescription': 'Results will appear after the next scheduled analysis.',
  'experiments.metricVersionAdoptDialog.successTitle': 'Metric updated to the latest version',
  'experiments.metricVersionAdoptDialog.unknownError': 'An unknown error occurred',
  'experiments.metricVersionAdoptDialog.update': 'Update',
  'experiments.metricsDialog.heading': 'Choose metrics',
  'experiments.metricsDialog.noMetricsFound': 'No metrics found',
  'experiments.metricsDialog.searchLabel': 'Search metrics',
  'experiments.metricsDialog.searchPlaceholder': 'Search metrics…',
  'experiments.metricsFilter.any': 'Any',
  'experiments.metricsFilter.chooseMetrics': 'Choose metrics',
  'experiments.metricsFilter.label': 'Metrics used',
  'experiments.metricsPreview.additionalHeading': 'Additional Metrics',
  'experiments.metricsPreview.additionalMetricsLabel': 'Additional metrics',
  'experiments.metricsPreview.funnelMetricsLabel': 'Primary funnel metrics',
  'experiments.metricsPreview.metricHeading': 'Metric',
  'experiments.metricsPreview.metricsHeading': 'Metrics',
  'experiments.metricsPreview.primaryAndAdditionalMetricsLabel': 'Primary and additional metrics',
  'experiments.metricsPreview.primaryTag': 'Primary',
  'experiments.metricsPreview.sourceHeading': 'Source',
  'experiments.metricsPreview.sourceLaunchDarkly': 'LaunchDarkly',
  'experiments.metricsSection.dataExportConfigureLink': 'Configure Data Export',
  'experiments.metricsSection.dataExportDescription': 'in this environment to automatically export data.',
  'experiments.metricsSection.dataExportLearnMoreLink': 'Learn More to configure Data Export',
  'experiments.metricsSection.dataExportOnly': 'Data Export only',
  'experiments.metricsSection.decoupledFrequentistOnly':
    "Metrics that don't support analyzing by the randomization unit of the iteration require the frequentist approach.",
  'experiments.metricsSection.findMetricButton': 'Find a metric by name',
  'experiments.metricsSection.incompatibleMetricGroupKind': 'Incompatible metric group type',
  'experiments.metricsSection.incompatibleRandomizationUnit':
    'Incompatible randomization unit: "{unit}" must be included in the metric definition.',
  'experiments.metricsSection.incompatibleWarehouseMetricType':
    'Incompatible metric type for warehouse native data source',
  'experiments.metricsSection.learnMore': 'Learn more',
  'experiments.metricsSection.mabSimpleMeanOnly':
    "Multi-armed bandits don't support ratio or percentile metrics, or metrics that can't be analyzed using the iteration's randomization unit.",
  'experiments.metricsSection.metricCreatedNotAddedTitle': 'Metric was created but not added to this experiment',
  'experiments.metricsSection.metricGroupCreatedNotAddedTitle':
    'Metric group was created but not added to this experiment',
  'experiments.metricsSection.metricHelpBody':
    'To get help now, <docs>read about metrics</docs> or <booking>book a call with a product manager</booking>.',
  'experiments.metricsSection.metricHelpBodyDocsOnly': 'To get help now, <docs>read about metrics</docs>.',
  'experiments.metricsSection.metricHelpError': 'LaunchDarkly could not send your answer. Try again.',
  'experiments.metricsSection.metricHelpHeading': "What's making it hard to pick a metric?",
  'experiments.metricsSection.metricHelpOtherLabel': 'Your reason',
  'experiments.metricsSection.metricHelpReasonFit': "I'm not sure which metric fits what I'm testing",
  'experiments.metricsSection.metricHelpReasonMissing': "I can't find the metric I need",
  'experiments.metricsSection.metricHelpReasonOther': 'Something else',
  'experiments.metricsSection.metricHelpReasonRecentValues': "I need to see a metric's recent values before I pick",
  'experiments.metricsSection.metricHelpReasonTrust': "I'm not sure which of these metrics my team trusts",
  'experiments.metricsSection.metricHelpSent': 'Answer sent',
  'experiments.metricsSection.metricHelpSubmit': 'Send',
  'experiments.metricsSection.metricLabel': 'Metric',
  'experiments.metricsSection.metricSource': 'Metric source',
  'experiments.metricsSection.metricSourceDescription': 'The source of the metrics for the experiment.',
  'experiments.metricsSection.metricsLabel': 'Metrics',
  'experiments.metricsSection.optimizeDescription': 'The measurement you are trying to optimize.',
  'experiments.metricsSection.percentileFrequentistOnly':
    'Percentile metrics currently require the frequentist approach.',
  'experiments.metricsSection.primaryMetricDescription': 'The primary metric measures what your hypothesis is testing.',
  'experiments.metricsSection.randomizationUnitPlaceholder': 'Select a randomization unit',
  'experiments.metricsSection.randomizeBy': 'Randomize by',
  'experiments.metricsSection.randomizeByVariantsInfo':
    'The unit the experiment will randomize on to split contexts between variants.',
  'experiments.metricsSection.randomizeByVariationsInfo':
    'The unit the experiment will randomize on to split contexts between variations.',
  'experiments.metricsSection.ratioFrequentistOnly': 'Ratio metrics currently require the frequentist approach.',
  'experiments.metricsSection.ratioUnsupportedSource': 'Ratio metrics are not supported on this data source',
  'experiments.metricsSection.secondaryMetricsDescription':
    'Secondary metrics can answer other questions you might have. Often, they are "guardrail" metrics that can make sure you don\'t improve a primary metric by breaking something else.',
  'experiments.metricsSection.selectMetricsButton': 'Select metrics or metric groups',
  'experiments.metricsSection.selectMetricsLabel': 'Select metrics',
  'experiments.metricsSection.sourceBigQuery': 'Warehouse native (BigQuery)',
  'experiments.metricsSection.sourceClickHouse': 'Warehouse native (ClickHouse)',
  'experiments.metricsSection.sourceDatabricks': 'Warehouse native (Databricks)',
  'experiments.metricsSection.sourceLaunchDarkly': 'LaunchDarkly hosted',
  'experiments.metricsSection.sourceRedshift': 'Warehouse native (Redshift)',
  'experiments.metricsSection.sourceSnowflake': 'Warehouse native (Snowflake)',
  'experiments.metricsSection.traceMetricUnsupported':
    'Metrics from OpenTelemetry traces are not supported in experiments.',
  'experiments.metricsSection.warehouseDenominatorOnLdHosted':
    "This metric's denominator comes from your data warehouse. This experiment supports only metrics from LaunchDarkly SDKs.",
  'experiments.metricsSection.warehouseMetricOnLdHosted':
    'This metric comes from your data warehouse. This experiment supports only metrics from LaunchDarkly SDKs.',
  'experiments.multipleComparisonCorrection.correction': 'Multiple comparisons correction',
  'experiments.multipleComparisonCorrection.description':
    'Reduce false positives in your experiment by using the Bonferroni or the Benjamini-Hochberg procedure.',
  'experiments.multipleComparisonCorrection.learnMore': 'Learn more',
  'experiments.multipleComparisonCorrection.methodLabel': 'Multiple comparisons correction method',
  'experiments.multipleComparisonCorrection.scopeLabel': 'Multiple comparisons correction scope',
  'experiments.nameAndHypothesisSection.aaTestDescription':
    'The ideal outcome of an A/A test is that there is no difference between the variations.',
  'experiments.nameAndHypothesisSection.aaTestGuidanceBody':
    'Ideally, an A/A test shows no difference between variations.',
  'experiments.nameAndHypothesisSection.aaTestHypothesisTemplate':
    'If we split traffic evenly between two identical variations, key metrics will show no meaningful difference, because random assignment is the only difference.',
  'experiments.nameAndHypothesisSection.aiAssist': 'AI assist',
  'experiments.nameAndHypothesisSection.aiAssistInfoLabel': 'Why is AI assist unavailable?',
  'experiments.nameAndHypothesisSection.applyAaTestHypothesis': 'Apply A/A test hypothesis',
  'experiments.nameAndHypothesisSection.genAiDisabled':
    'AI assist needs generative AI features turned on for your account. An account admin can enable them in <link>Security settings</link>.',
  'experiments.nameAndHypothesisSection.goalDescription':
    'A goal is a statement about what you want to achieve. For example, "To optimize the landing page copy so that more people will click the Buy Now button"',
  'experiments.nameAndHypothesisSection.goalHeading': 'Name and optimization goal',
  'experiments.nameAndHypothesisSection.goalLabel': 'Goal',
  'experiments.nameAndHypothesisSection.hypothesisDescription':
    'A hypothesis is a theory or assumption that can be tested with an experiment. You can phrase a hypothesis for a LaunchDarkly experiment as: "If [I make a specific change to our codebase], then [one or more measurable metrics will improve] because [the change had this effect]".',
  'experiments.nameAndHypothesisSection.hypothesisHeading': 'Name and hypothesis',
  'experiments.nameAndHypothesisSection.hypothesisLabel': 'Hypothesis',
  'experiments.nameAndHypothesisSection.learnMore': 'Learn more',
  'experiments.nameAndHypothesisSection.nameFieldLabel': 'Experiment name',
  'experiments.nameAndHypothesisSection.nameInfo':
    "Give your {type} a descriptive name that helps your team understand what you're testing.",
  'experiments.nameAndHypothesisSection.nameLabel': 'Name',
  'experiments.nameAndHypothesisSection.namePlaceholder': 'e.g., Homepage redesign',
  'experiments.nameAndHypothesisSection.showAaTestGuidanceHint':
    'Also labels this experiment as an A/A test in the preview.',
  'experiments.nameAndHypothesisSection.undoAaTestHypothesis': 'Undo',
  'experiments.newPage.title': 'Create experiment',
  'experiments.notFoundPage.description':
    '{type} does not exist in this environment because {typeLowercase}s are environment-specific',
  'experiments.notFoundPage.heading': '{type} not found for this environment',
  'experiments.notifications.archiveFailed': 'Error archiving experiment',
  'experiments.notifications.archived': 'Experiment archived',
  'experiments.notifications.createFailed': 'There was a problem creating your experiment. Try again.',
  'experiments.notifications.iterationCreateFailed': 'There was a problem creating the iteration. Try again.',
  'experiments.notifications.metricsReset': 'Metrics reset',
  'experiments.notifications.restoreFailed': 'Error restoring experiment',
  'experiments.notifications.restored': 'Experiment restored',
  'experiments.notifications.settingsSaved': 'Experiment settings saved',
  'experiments.notifications.trafficAllocationConflict':
    "You can't change an experiment's traffic allocation from the flag. Instead, update the experiment design.",
  'experiments.onboardingBanner.liveNote':
    "Your experiment is live. Click the button in your app to see your first event appear here within a minute. Get your teammates clicking too: each person's clicks flow in as real exposure and conversion data.",
  'experiments.onboardingBanner.scaleNote':
    "The setup is real, just at small scale. The numbers won't reach statistical significance, but the configuration and data flow match a production experiment.",
  'experiments.page.title': 'Experiments',
  'experiments.participatingUnitsPopover.allUnitsIncluded': 'All units are included in the analysis',
  'experiments.participatingUnitsPopover.excludesHeading': 'This metric calculation excludes:',
  'experiments.participatingUnitsPopover.excludesIncompleteWindow': 'Units with incomplete measurement window',
  'experiments.participatingUnitsPopover.excludesNoEvent': 'Units that did not send a metric event',
  'experiments.participatingUnitsPopover.exposures': 'Exposures',
  'experiments.participatingUnitsPopover.heading': 'Units included in analysis',
  'experiments.participatingUnitsPopover.learnMore': 'Learn more',
  'experiments.participatingUnitsPopover.resultsComingIn': 'Results are still coming in',
  'experiments.participatingUnitsPopover.someUnitsExcluded': 'Not all units are included in the analysis',
  'experiments.pdfCoverPage.duration': 'Duration',
  'experiments.pdfCoverPage.durationNotStarted': 'Not started',
  'experiments.pdfCoverPage.durationRunning': '{startDate} - (Running)',
  'experiments.pdfCoverPage.experimentResults': 'Experiment Results',
  'experiments.pdfCoverPage.generatedOn': 'Generated on',
  'experiments.pdfCoverPage.logoAlt': 'LaunchDarkly logo',
  'experiments.pdfCoverPage.maintainerLoading': 'Loading…',
  'experiments.pdfCoverPage.maintainerNotSpecified': 'Not specified',
  'experiments.pdfCoverPage.maintainerUnknown': 'Unknown',
  'experiments.pdfCoverPage.owner': 'Owner',
  'experiments.pdfCoverPage.status': 'Status',
  'experiments.pdfExport.loadError':
    'An error occurred while loading the experiment data. Close this tab and try again later.',
  'experiments.pdfExport.loadingLabel': 'Loading experiment data…',
  'experiments.pdfExport.logoAlt': 'LaunchDarkly logo',
  'experiments.pdfHypothesisPage.advancedDetails': 'Advanced details',
  'experiments.pdfHypothesisPage.cupedRow': 'CUPED: <emphasis>{status}</emphasis>',
  'experiments.pdfHypothesisPage.durationRow': 'Duration: <emphasis>{duration}</emphasis>',
  'experiments.pdfHypothesisPage.endDateRow': 'End date: <emphasis></emphasis>',
  'experiments.pdfHypothesisPage.experimentDetails': 'Experiment details',
  'experiments.pdfHypothesisPage.exposures': 'Exposures',
  'experiments.pdfHypothesisPage.hypothesis': 'Hypothesis',
  'experiments.pdfHypothesisPage.iterationRow': 'Iteration: <emphasis>{number}</emphasis>',
  'experiments.pdfHypothesisPage.keyTakeaways': 'Key takeaways',
  'experiments.pdfHypothesisPage.mccRow':
    'MCC: <emphasis>{applied, select, true {Applied} other {Not applied}}</emphasis>',
  'experiments.pdfHypothesisPage.notStarted': 'Not started',
  'experiments.pdfHypothesisPage.running': 'Running',
  'experiments.pdfHypothesisPage.sequentialTestingRow':
    'Sequential testing: <emphasis>{enabled, select, true {Enabled} other {Disabled}}</emphasis>',
  'experiments.pdfHypothesisPage.srmRow':
    'SRM: <emphasis>{detected, select, true {Detected} other {None detected}}</emphasis>',
  'experiments.pdfHypothesisPage.startDateRow': 'Start date: <emphasis></emphasis>',
  'experiments.pdfHypothesisPage.statusRow': 'Status: <emphasis>{status}</emphasis>',
  'experiments.pdfHypothesisPage.thresholdRow':
    '{methodology, select, frequentist {Significance level} other {Threshold}}: <emphasis>{value}</emphasis>',
  'experiments.pdfHypothesisPage.timeline': 'Timeline',
  'experiments.pdfHypothesisPage.treatmentsTruncated': 'Showing 7 of {count} treatments',
  'experiments.pdfMetricResults.graphType': 'Graph type: <icon></icon> {chartType}',
  'experiments.pdfMetricResults.metricResults': 'Metric results',
  'experiments.pdfMetricResults.treatmentsTruncated': 'Showing {limit} of {count} treatments',
  'experiments.pdfNotes.reasonForStopping': 'Reason for stopping',
  'experiments.pdfVariations.exposureCountRow': 'Exposure count: <emphasis>{count}</emphasis>',
  'experiments.pdfVariations.splitRow': 'Split: <emphasis>{split}%</emphasis>',
  'experiments.pdfVariations.treatmentTagsLabel': 'treatment tags',
  'experiments.pdfVariations.valueRow': 'Value: <emphasis>{value}</emphasis>',
  'experiments.pdfVariations.variations': 'Variations',
  'experiments.pendingApprovalAlert.checkFlagChanges':
    'If there were unwanted changes to the <link>{flagName}</link> flag before requesting this approval',
  'experiments.pendingApprovalAlert.checkPendingApprovals':
    'Pending approvals in the right sidebar once you close this dialog',
  'experiments.pendingApprovalAlert.conflictBody':
    'There’s already a pending approval, which may conflict with this change. Check pending approvals in the right sidebar once you close this dialog.',
  'experiments.pendingApprovalAlert.conflictHeading': 'Pending approval exists',
  'experiments.pendingApprovalAlert.flagChangesBody':
    'Experiment might have stopped in order to make flag changes. Check if there were unwanted changes to the <link>{flagName}</link> flag before requesting this approval.',
  'experiments.pendingApprovalAlert.flagChangesHeading': 'Potential flag changes',
  'experiments.pendingApprovalAlert.stoppedConflictBody':
    'There’s already a pending approval, which may conflict with this change. We recommend you check:',
  'experiments.pendingApprovalsModal.body':
    "You will save changes to an experiment that has pending approvals. Check that your changes don't unintentionally override configuration meant to be approved.",
  'experiments.pendingApprovalsModal.cancel': 'Cancel',
  'experiments.pendingApprovalsModal.closeLabel': 'close modal',
  'experiments.pendingApprovalsModal.heading': 'Pending approvals exist',
  'experiments.pendingApprovalsModal.pendingApprovals': 'Pending approvals',
  'experiments.pendingApprovalsModal.saveChangesAnyway': 'Save changes anyway',
  'experiments.pendingExperimentMessage.body':
    'Saving changes will take you to creating an experiment from the rule you updated.',
  'experiments.percentileAnalysisCard.betaNotice':
    'Percentile results do not support attribute filters or CUPED because percentiles are in beta.',
  'experiments.percentileAnalysisCard.learnMore': 'Learn more',
  'experiments.percentileAnalysisCard.variation': 'Variation',
  'experiments.percentileBoxPlot.confidenceInterval': '{confidence}% confidence interval:',
  'experiments.percentileBoxPlot.variation': 'Variation:',
  'experiments.postAccessBanner.body':
    'Upgrade your plan to continue measuring and understanding the impact of every feature you release.',
  'experiments.postAccessBanner.heading': 'Your experimentation access has ended.',
  'experiments.preFilledSettings.edit': 'Edit',
  'experiments.preFilledSettings.editLabel': 'Edit pre-filled settings',
  'experiments.preFilledSettings.flag': 'Flag',
  'experiments.preFilledSettings.flagRule':
    "The flag's rule,<link>{ruleName}</link>, is wired to the audience input at step {step}.",
  'experiments.preFilledSettings.flagVariations': '<link>{flagName}</link> provides the {variationCount} variations,',
  'experiments.preFilledSettings.hypothesis': 'Hypothesis',
  'experiments.preFilledSettings.intro':
    "You're setting up a real A/B test on a button in your app and we'll measure which gets more clicks. Your real users will not see it, only you and your teammates you add on the next step will see the new button label.",
  'experiments.preFilledSettings.looksGood': 'Looks good',
  'experiments.preFilledSettings.metricDescription':
    '<link>{metricName}</link> is a conversion metric: it measures the percentage of users who clicked the button.',
  'experiments.preFilledSettings.primaryMetric': 'Primary metric',
  'experiments.preFilledSettings.readMoreStatistics': 'Read more about statistical methods',
  'experiments.preFilledSettings.statisticalDefaults':
    "We've set standard statistical defaults: a frequentist A/B test with a p-value of 0.05 and a 95% confidence interval.",
  'experiments.preFilledSettings.statisticalMethod': 'Statistical method',
  'experiments.preFilledSettings.stepsInfo':
    'Your inputs at Steps {audienceStep} and {variationsStep} will provide who will be shown the experiment and the values for Control and Treatment.',
  'experiments.preFilledSettings.title': 'Review pre-filled settings',
  'experiments.preFilledSettings.view': 'View',
  'experiments.probabilityDistributionChart.chartLoadError': 'An unexpected error has occurred loading this chart',
  'experiments.probabilityDistributionChart.metricMissing': 'Metric missing from analysis',
  'experiments.probabilityDistributionChart.noDataAvailable': 'No data available',
  'experiments.probabilityDistributionChart.notEnoughData': 'Not enough data to show chart',
  'experiments.randomizationUnitSelector.learnMore': 'Learn more',
  'experiments.randomizationUnitSelector.loadError': "There was a problem loading this project's randomization units.",
  'experiments.randomizationUnitSelector.unitLabel': 'Randomization unit',
  'experiments.randomizationUnitSelector.unitPlaceholder': 'Select randomization unit',
  'experiments.randomizationUnitSelector.unitsLabel': 'Randomization units',
  'experiments.randomizationUnitSelector.unitsPlaceholder': 'Select randomization units',
  'experiments.readOnlyExperimentRule.banditHeading': 'multi-arm bandit',
  'experiments.readOnlyExperimentRule.banditLocked': 'You cannot edit this rule while a multi-arm bandit is running.',
  'experiments.readOnlyExperimentRule.banditSectionLabel': 'Read only multi-arm bandit rule',
  'experiments.readOnlyExperimentRule.experimentHeading': 'experiment',
  'experiments.readOnlyExperimentRule.experimentLocked': 'You cannot edit this rule while an experiment is running.',
  'experiments.readOnlyExperimentRule.experimentSectionLabel': 'Read only experiment rule',
  'experiments.resourceFilter.aiConfig': 'AI config',
  'experiments.resourceFilter.any': 'Any',
  'experiments.resourceFilter.flag': 'Flag',
  'experiments.resourceFilter.label': 'Resource',
  'experiments.resultsAddMenu.add': 'Add',
  'experiments.resultsAddMenu.attributesItem': 'Attributes',
  'experiments.resultsAddMenu.attributesUnavailable':
    'You cannot add attributes to iterations started before <date></date>. To add attributes to this experiment, you must start a new iteration.',
  'experiments.resultsAddMenu.metricsItem': 'Metrics',
  'experiments.resultsAddMenu.toAddAttributes':
    '. To add attributes to this experiment, you must start a new iteration.',
  'experiments.resultsAddMenu.triggerLabel': 'Add Metric or Attributes Anytime',
  'experiments.resultsAddMenu.youCannotAdd': 'You cannot add attributes to iterations started before',
  'experiments.resultsAiSummary.generate': 'Generate',
  'experiments.resultsAiSummary.heading': 'Summary of results',
  'experiments.resultsAiSummary.prompt': 'Generate an AI summary of your iteration results',
  'experiments.resultsAnalyzedBy.label': 'Analyzed by:',
  'experiments.resultsAnalyzedBy.tagLabel': 'Analyzed by: {unit}',
  'experiments.resultsDateRangePicker.apply': 'Apply',
  'experiments.resultsDateRangePicker.applyOrCancelLabel': 'apply or cancel date range',
  'experiments.resultsDateRangePicker.cancel': 'Cancel',
  'experiments.resultsDateRangePicker.customRange': 'Custom range',
  'experiments.resultsDateRangePicker.customRangeTextValue': 'custom date range',
  'experiments.resultsDateRangePicker.lastFourteenDays': 'Last 14 days',
  'experiments.resultsDateRangePicker.lastSevenDays': 'Last 7 days',
  'experiments.resultsDateRangePicker.lastSixtyDays': 'Last 60 days',
  'experiments.resultsDateRangePicker.lastThirtyDays': 'Last 30 days',
  'experiments.resultsDateRangePicker.openPickerLabel': 'open date range picker',
  'experiments.resultsDateRangePicker.pickerLabel': 'select custom or preset date range',
  'experiments.resultsDateRangePicker.presetOptionsLabel': 'Preset date range options',
  'experiments.resultsDateRangePicker.selectedPresetLabel': 'selected preset date range',
  'experiments.resultsGraphDialog.probabilityDensity': 'Probability density',
  'experiments.resultsGraphDialog.relativeDifference': 'Relative difference',
  'experiments.resultsGraphDialog.variationEstimates': 'Variation estimates',
  'experiments.resultsHealthCheck.copyIterationIdLabel': 'Copy iteration ID {iterationId} to your clipboard.',
  'experiments.resultsHealthCheck.copyIterationIdTooltip': 'Copy iteration ID',
  'experiments.resultsHealthCheck.currentlyLabel': 'Currently:',
  'experiments.resultsHealthCheck.exposuresChecking': 'Checking exposures…',
  'experiments.resultsHealthCheck.exposuresLabel': 'Exposures',
  'experiments.resultsHealthCheck.exposuresLoadError': 'Loading exposures failed.',
  'experiments.resultsHealthCheck.exposuresNone':
    'No variation has been exposed. Normal if you have recently started your experiment.',
  'experiments.resultsHealthCheck.exposuresPass':
    'At least one <code>{randomizationUnit}</code> context has been exposed to at least one variation.',
  'experiments.resultsHealthCheck.iterationIdLabel': 'Iteration ID: {iterationId}',
  'experiments.resultsHealthCheck.notStartedBody': 'Start the experiment to see health checks.',
  'experiments.resultsHealthCheck.past90DaysLabel': 'In the past 90 days:',
  'experiments.resultsHealthCheck.sinceStartLabel': 'Since the start of the experiment:',
  'experiments.resultsHealthCheck.srmChecking': 'Checking mismatch…',
  'experiments.resultsHealthCheck.srmDetected':
    'Your results may be invalid. LaunchDarkly has detected a mismatch between the audience allocation in your experiment design and the observed exposures in the results.',
  'experiments.resultsHealthCheck.srmLabel': 'Sample ratio mismatch',
  'experiments.resultsHealthCheck.srmLearnMore': 'Learn more',
  'experiments.resultsHealthCheck.srmOk': 'No mismatch detected.',
  'experiments.resultsHealthCheck.srmUnavailable': 'Mismatch could not be checked.',
  'experiments.resultsHealthCheck.targetingRuleLabel': 'Targeting rule / audience',
  'experiments.resultsHealthCheck.whenStoppedLabel': 'When the experiment stopped:',
  'experiments.resultsPopover.explanation': 'Explanation',
  'experiments.resultsPopover.learnMore': 'Learn more',
  'experiments.resultsRecommendation.andJoiner': 'and ',
  'experiments.resultsRecommendation.mostLikelyBest': '{past, select, true {was} other {is}} most likely to be best.',
  'experiments.resultsRecommendation.noSignificantDifference':
    'there {past, select, true {was} other {is}} no statistically significant difference between variations.',
  'experiments.resultsRecommendation.notEnoughData':
    "There {past, select, true {wasn't} other {isn't}} enough data to determine results.",
  'experiments.resultsRecommendation.primaryMetricPrefix':
    'Based on the primary metric <highlight>{metricName}</highlight>,',
  'experiments.resultsRecommendation.primaryMetricTreatmentPrefix':
    'Based on the primary metric <highlight>{metricName}, <treatment></treatment></highlight>',
  'experiments.resultsRecommendation.significantInDirection':
    '{tense, select, pastPlural {were} pastSingular {was} presentPlural {are} other {is}} significant in the desired direction.',
  'experiments.resultsRecommendation.winningVariation': '{past, select, true {was} other {is}} the winning variation.',
  'experiments.resultsRefresh.disabledDisconnected': 'Connect the warehouse integration to refresh results',
  'experiments.resultsRefresh.disabledMultiple':
    'Keep one warehouse integration for this environment to refresh results',
  'experiments.resultsRefresh.disabledNoAccess': 'Refreshing results requires permission to update this experiment',
  'experiments.resultsRefresh.disabledRunning': 'Refresh already in progress',
  'experiments.resultsRefresh.disabledRunningScheduled': 'Scheduled update in progress',
  'experiments.resultsRefresh.errorToast': 'Results could not be refreshed. Try again in a few minutes.',
  'experiments.resultsRefresh.progress': '{completed} of {total} computations finished',
  'experiments.resultsRefresh.refresh': 'Refresh',
  'experiments.resultsRefresh.refreshing': 'Refreshing',
  'experiments.resultsRefresh.reloadPage': 'Reload the page',
  'experiments.resultsRefresh.statusLoading': 'Checking refresh status',
  'experiments.resultsRefresh.successToast': 'Results refreshed. <reloadLink></reloadLink> to see them.',
  'experiments.resultsRefresh.triggeredAgo': 'Refresh triggered {duration} ago',
  'experiments.resultsRefresh.triggeredJustNow': 'Refresh triggered just now',
  'experiments.resultsRefresh.triggerLabel': 'Refresh results',
  'experiments.resultsSettingsDialog.cancel': 'Cancel',
  'experiments.resultsSettingsDialog.cancelLabel': 'cancel button',
  'experiments.resultsSettingsDialog.cupedDescription':
    'Improve the measurement of an experiment’s outcome with covariant adjustments.',
  'experiments.resultsSettingsDialog.cupedDocsLink': 'Read about CUPED',
  'experiments.resultsSettingsDialog.cupedHeading': 'CUPED',
  'experiments.resultsSettingsDialog.cupedPopoverBody':
    'A variance reduction technique that makes experiments more sensitive without increasing sample size. If enabled, adjustments will be applied to results if relevant pre-experiment data is available. Percentile metrics, ratio metrics, and attribute slices are not supported.',
  'experiments.resultsSettingsDialog.cupedStatusLabel': 'cuped status',
  'experiments.resultsSettingsDialog.heading': 'Results settings',
  'experiments.resultsSettingsDialog.multipleComparisonsDescription':
    'Reduce false positives in your experiment by applying the {method, select, bonferroni {Bonferroni} other {Benjamini-Hochberg}} method across {scope, select, metrics {metrics} variations {treatments} variations_and_metrics {treatments and metrics} other {}} for this experiment.',
  'experiments.resultsSettingsDialog.multipleComparisonsHeading': 'Multiple comparisons correction',
  'experiments.resultsSettingsDialog.save': 'Save',
  'experiments.resultsSettingsDialog.sequentialTestingDescription':
    'Compute confidence intervals and p-values that you can act upon at any time during your experiment, rather than at a fixed sample size.',
  'experiments.resultsSettingsDialog.sequentialTestingHeading': 'Sequential testing',
  'experiments.resultsSummary.analysisInProgress': 'Analysis in progress…',
  'experiments.resultsSummary.analysisLoadError': 'Error loading experiment analysis',
  'experiments.resultsSummary.downloadPdf': 'Download PDF',
  'experiments.resultsSummary.downloadPdfLabel': 'Download PDF file',
  'experiments.resultsSummary.durationHeading': 'Duration',
  'experiments.resultsSummary.heading': 'Summary',
  'experiments.resultsSummary.hypothesisHeading': 'Hypothesis',
  'experiments.resultsSummary.hypothesisHeadingLegacy': 'Hypothesis',
  'experiments.resultsSummary.keyTakeawaysHeading': 'Key takeaways',
  'experiments.resultsSummary.keyTakeawaysHeadingLegacy': 'Key takeaways',
  'experiments.resultsSummary.keyTakeawaysInfoBody':
    'Key takeaways summarize how variations perform against the primary metric, based on your statistical approach.',
  'experiments.resultsSummary.keyTakeawaysInfoLabel': 'Key takeaways info',
  'experiments.resultsSummary.launchedToLabel': 'Launched to',
  'experiments.resultsSummary.learnToInterpret': 'Learn to interpret results',
  'experiments.resultsSummary.noReasonProvided': 'No reason provided',
  'experiments.resultsSummary.notStarted': 'You have no results because your experiment has not started',
  'experiments.resultsSummary.primaryMetricResultHeading': 'Primary metric result',
  'experiments.resultsSummary.readToggle': 'Read {expanded, select, true {less} other {more}}',
  'experiments.resultsSummary.recommendationLoadError': 'Error loading recommendation',
  'experiments.resultsSummary.sampleSizeTargetHeading': 'Sample size target',
  'experiments.resultsSummary.shipWinningVariation': 'Ship winning variation',
  'experiments.resultsSummary.shippedVariationHeading': 'Shipped variation',
  'experiments.resultsSummary.stoppedReasonHeading': 'Reason for stopping',
  'experiments.resultsSummary.viewRelatedAnalytics': 'View related analytics',
  'experiments.resultsSummaryPanel.banditExposuresError':
    "An unexpected error has occurred showing multi-armed bandit's exposures",
  'experiments.resultsSummaryPanel.banditSummaryError':
    "An unexpected error has occurred showing multi-armed bandit's summary",
  'experiments.resultsSummaryPanel.exposuresError': "An unexpected error has occurred showing experiment's exposures",
  'experiments.resultsSummaryPanel.summaryError': "An unexpected error has occurred showing experiment's summary",
  'experiments.resultsTable.control': 'Control',
  'experiments.resultsTable.insufficientData': 'Insufficient data',
  'experiments.resultsTable.insufficientDataTooltip':
    'There have not been sufficient exposures to calculate results for this metric yet.',
  'experiments.resultsTable.leadingVariation': 'Leading variation',
  'experiments.resultsTableControls.display': 'Display',
  'experiments.resultsTableControls.noItems': 'No items',
  'experiments.resultsTableControls.selectColumnsLabel': 'Select columns',
  'experiments.richTextPreview.readFullReason': 'Read full reason',
  'experiments.sampleExperimentAudience.addEmail': 'Add "{email}"',
  'experiments.sampleExperimentAudience.editLabel': 'Edit audience',
  'experiments.sampleExperimentAudience.emailPlaceholder': 'Add an email',
  'experiments.sampleExperimentAudience.emailsHint': 'Two users minimum.',
  'experiments.sampleExperimentAudience.emailsLabel': 'Who will be in this experiment?',
  'experiments.sampleExperimentAudience.emailsListLabel': 'Audience emails',
  'experiments.sampleExperimentAudience.intro':
    "Add yourself and a few teammates. You're the only ones who'll be in the experiment, so it's safe to run in production.",
  'experiments.sampleExperimentAudience.loadingEmails': 'Loading emails',
  'experiments.sampleExperimentAudience.next': 'Next',
  'experiments.sampleExperimentAudience.noAudience': 'No audience selected',
  'experiments.sampleExperimentAudience.saveError': 'Could not save changes. Try again.',
  'experiments.sampleExperimentAudience.saving': 'Saving…',
  'experiments.sampleExperimentAudience.title': 'Audience',
  'experiments.sampleExperimentAudience.typeAnEmail': 'Type an email',
  'experiments.sampleExperimentEmptyState.body':
    'Test which button label gets more clicks in your app. Only you and the teammates you add will see this experiment.',
  'experiments.sampleExperimentEmptyState.createErrorDescription': 'An unknown error occurred. Try again.',
  'experiments.sampleExperimentEmptyState.createErrorTitle': 'Could not create experiment',
  'experiments.sampleExperimentEmptyState.heading': 'A sample experiment, ready to go',
  'experiments.sampleExperimentEmptyState.startFromScratch': 'or start from scratch',
  'experiments.sampleExperimentEmptyState.trySample': 'Try sample experiment',
  'experiments.sampleExperimentLaunch.aiIntro':
    "Paste this into your AI coding assistant. Two things will happen in your code: the button reads the flag to get its label, and it sends a click event to the metric. That's the full loop.",
  'experiments.sampleExperimentLaunch.aiPromptTitle': 'AI Prompt',
  'experiments.sampleExperimentLaunch.chooseLanguage': 'Choose your language',
  'experiments.sampleExperimentLaunch.connectManually': 'Connect manually',
  'experiments.sampleExperimentLaunch.connectWithAi': 'Connect with AI',
  'experiments.sampleExperimentLaunch.connected': 'App connected',
  'experiments.sampleExperimentLaunch.connectionError': 'Unable to verify connection',
  'experiments.sampleExperimentLaunch.contextNote':
    'Include an <strong>email</strong> attribute in your context — the Test users targeting rule matches on email.',
  'experiments.sampleExperimentLaunch.copyPrompt': 'Copy prompt for AI coding assistant',
  'experiments.sampleExperimentLaunch.evaluateAndTrack': 'Evaluate the flag and track the metric',
  'experiments.sampleExperimentLaunch.heading': 'Add the flag and metric to your existing setup',
  'experiments.sampleExperimentLaunch.initializeSdk': 'Initialize the SDK',
  'experiments.sampleExperimentLaunch.installPackage': 'Install the package',
  'experiments.sampleExperimentLaunch.manualIntro':
    'Initialize the SDK so your app can talk to LaunchDarkly. Read the flag and track clicks on your button. This is the experiment-specific part.',
  'experiments.sampleExperimentLaunch.summaryBody': 'Some setups need extra code to track experiments correctly.',
  'experiments.sampleExperimentLaunch.summaryReadMore': 'Common cases and how to handle them',
  'experiments.sampleExperimentLaunch.title': 'Launch',
  'experiments.sampleExperimentLaunch.viewLabel': 'View launch details',
  'experiments.sampleExperimentLaunch.waiting': 'Waiting for app to connect…',
  'experiments.sampleExperimentLive.liveBody':
    "Your experiment is live. Click the button in your app to see your first event appear here within a minute. Get your teammates clicking too: each person's clicks flow in as real exposure and conversion data.",
  'experiments.sampleExperimentLive.scaleBody':
    "The setup is real, just at small scale. The numbers won't reach statistical significance, but the configuration and data flow match a production experiment.",
  'experiments.sampleExperimentSettings.edit': 'Edit',
  'experiments.sampleExperimentSettings.editLabel': 'Edit pre-filled settings',
  'experiments.sampleExperimentSettings.flag': 'Flag',
  'experiments.sampleExperimentSettings.flagVariations':
    '<link>{flagName}</link> provides the {variationCount} variations,',
  'experiments.sampleExperimentSettings.hypothesis': 'Hypothesis',
  'experiments.sampleExperimentSettings.inputsNote':
    'Your inputs at Steps {audienceStep} and {variationsStep} will provide who will be shown the experiment and the values for Control and Treatment.',
  'experiments.sampleExperimentSettings.intro':
    "You're setting up a real A/B test on a button in your app and we'll measure which gets more clicks. Your real users will not see it, only you and your teammates you add on the next step will see the new button label.",
  'experiments.sampleExperimentSettings.looksGood': 'Looks good',
  'experiments.sampleExperimentSettings.metricDescription':
    '<link>{metricName}</link> is a conversion metric: it measures the percentage of users who clicked the button.',
  'experiments.sampleExperimentSettings.primaryMetric': 'Primary metric',
  'experiments.sampleExperimentSettings.readMoreStatistics': 'Read more about statistical methods',
  'experiments.sampleExperimentSettings.ruleWired':
    "The flag's rule,<link>{ruleName}</link>, is wired to the audience input at step {step}.",
  'experiments.sampleExperimentSettings.statisticalDefaults':
    "We've set standard statistical defaults: a frequentist A/B test with a p-value of 0.05 and a 95% confidence interval.",
  'experiments.sampleExperimentSettings.statisticalMethod': 'Statistical method',
  'experiments.sampleExperimentSettings.title': 'Review pre-filled settings',
  'experiments.sampleExperimentSettings.view': 'View',
  'experiments.sampleExperimentSetup.audienceDescription': 'The people who will be in your experiment.',
  'experiments.sampleExperimentSetup.goToStepLabel': 'Go to {step} step',
  'experiments.sampleExperimentSetup.launchDescription': 'Wire up the experiment.',
  'experiments.sampleExperimentSetup.loadError': 'Could not load experiment design page. Try refreshing the page.',
  'experiments.sampleExperimentSetup.settingsDescription': 'Edit anything or leave it as-is.',
  'experiments.sampleExperimentSetup.variationsDescription': "What you'll serve in your experiment.",
  'experiments.sampleExperimentStatus.close': 'Close',
  'experiments.sampleExperimentStatus.completeSetupTooltip': 'Complete the experiment setup before starting.',
  'experiments.sampleExperimentStatus.connectSdkTooltip': 'Connect your SDK before starting the experiment.',
  'experiments.sampleExperimentStatus.inProgress': 'Set up in progress',
  'experiments.sampleExperimentStatus.inProgressDescription': 'Finish to launch your experiment.',
  'experiments.sampleExperimentStatus.readyDescription': 'Experiment setup is complete.',
  'experiments.sampleExperimentStatus.readyToStart': 'Ready to start',
  'experiments.sampleExperimentStatus.runningDescription': 'Experiment is running.',
  'experiments.sampleExperimentStatus.start': 'Start',
  'experiments.sampleExperimentStatus.startErrorDescription': 'An unknown error occurred. Try again.',
  'experiments.sampleExperimentStatus.startErrorTitle': 'Could not start experiment',
  'experiments.sampleExperimentStatus.started': 'Started',
  'experiments.sampleExperimentVariations.control': 'Control',
  'experiments.sampleExperimentVariations.controlCopyLabel': 'Current button copy',
  'experiments.sampleExperimentVariations.editLabel': 'Edit variations',
  'experiments.sampleExperimentVariations.intro':
    'Choose one button in your app. Enter its current label and an alternative to test against. Half of your audience will randomly be served Control. The other, Treatment.',
  'experiments.sampleExperimentVariations.next': 'Next',
  'experiments.sampleExperimentVariations.saveError': 'Could not save changes. Try again.',
  'experiments.sampleExperimentVariations.saving': 'Saving…',
  'experiments.sampleExperimentVariations.title': 'Variations',
  'experiments.sampleExperimentVariations.treatmentCopyLabel': 'New button copy',
  'experiments.sampleSizeCalculator.baselineMeanDescription': 'Average metric value for the control',
  'experiments.sampleSizeCalculator.baselineMeanLabel': 'Baseline mean',
  'experiments.sampleSizeCalculator.baselineMeanMissing': 'Enter a baseline mean.',
  'experiments.sampleSizeCalculator.baselineMeanOutOfRange': 'Must be greater than 0.',
  'experiments.sampleSizeCalculator.baselineRateDescription': 'Current conversion rate for the control',
  'experiments.sampleSizeCalculator.baselineRateLabel': 'Baseline conversion rate',
  'experiments.sampleSizeCalculator.baselineRateMissing': 'Enter a baseline conversion rate.',
  'experiments.sampleSizeCalculator.baselineRateOutOfRange': 'Must be greater than 0% and less than 100%.',
  'experiments.sampleSizeCalculator.baselineStdDevDescription': 'How much the metric values vary across units',
  'experiments.sampleSizeCalculator.baselineStdDevLabel': 'Standard deviation',
  'experiments.sampleSizeCalculator.baselineStdDevMissing': 'Enter a standard deviation.',
  'experiments.sampleSizeCalculator.baselineStdDevOutOfRange': 'Must be greater than 0.',
  'experiments.sampleSizeCalculator.cancel': 'Cancel',
  'experiments.sampleSizeCalculator.close': 'Close',
  'experiments.sampleSizeCalculator.closeLabel': 'Close',
  'experiments.sampleSizeCalculator.dailyTrafficDescription': 'Total eligible units per day',
  'experiments.sampleSizeCalculator.dailyTrafficLabel': 'Estimated daily traffic',
  'experiments.sampleSizeCalculator.dailyTrafficMissing': 'Enter your estimated daily traffic.',
  'experiments.sampleSizeCalculator.dailyTrafficOutOfRange': 'Must be at least 1.',
  'experiments.sampleSizeCalculator.dialogTitle': 'Sample size calculator',
  'experiments.sampleSizeCalculator.discardBody':
    'If you leave now, your changes to the sample size calculator will be lost.',
  'experiments.sampleSizeCalculator.discardCancel': 'Keep editing',
  'experiments.sampleSizeCalculator.discardConfirm': 'Discard',
  'experiments.sampleSizeCalculator.discardTitle': 'Discard unsaved changes',
  'experiments.sampleSizeCalculator.durationLabel': 'Duration',
  'experiments.sampleSizeCalculator.durationSubtitle': 'at {rate} units/day',
  'experiments.sampleSizeCalculator.estimatesHeading': 'Estimates',
  'experiments.sampleSizeCalculator.estimatesPlaceholder': 'Enter your inputs to see the estimated sample size.',
  'experiments.sampleSizeCalculator.mdeDescription': 'Smallest relative change worth detecting',
  'experiments.sampleSizeCalculator.mdeLabel': 'Minimum detectable effect',
  'experiments.sampleSizeCalculator.mdeMissing': 'Enter a minimum detectable effect.',
  'experiments.sampleSizeCalculator.mdeOutOfRange': 'Must be greater than 0%.',
  'experiments.sampleSizeCalculator.metricDescription':
    "Define the primary metric you're powering the experiment with.",
  'experiments.sampleSizeCalculator.metricHeading': 'Metric',
  'experiments.sampleSizeCalculator.metricTypeConversion': 'Conversion',
  'experiments.sampleSizeCalculator.metricTypeLabel': 'Metric type',
  'experiments.sampleSizeCalculator.metricTypeNumeric': 'Numeric',
  'experiments.sampleSizeCalculator.multipleVariationsCaveat':
    'Sample size estimates may be inaccurate for experiments with more than two variations.',
  'experiments.sampleSizeCalculator.powerLabel': 'Statistical power',
  'experiments.sampleSizeCalculator.reset': 'Reset',
  'experiments.sampleSizeCalculator.sampleSizeLabel': 'Sample size',
  'experiments.sampleSizeCalculator.sampleSizeSubtitle': 'total across {variations} variations',
  'experiments.sampleSizeCalculator.saveAndApply': 'Save and apply',
  'experiments.sampleSizeCalculator.significanceLevelLabel': 'Significance level',
  'experiments.sampleSizeCalculator.significanceLevelMissing': 'Enter a significance level.',
  'experiments.sampleSizeCalculator.significanceLevelOutOfRange': 'Must be greater than 0 and less than 1.',
  'experiments.sampleSizeCalculator.statisticalSettingsDescription': 'Tune the confidence and power for the analysis.',
  'experiments.sampleSizeCalculator.statisticalSettingsHeading': 'Statistical settings',
  'experiments.sampleSizeCalculator.subtitle':
    "Calculate sample sizes for frequentist experiments that don't use sequential testing. <link>Read more about the sample size calculator</link>",
  'experiments.sampleSizeCalculator.testDirectionLabel': 'Direction of hypothesis test',
  'experiments.sampleSizeCalculator.testDirectionOneSided': 'One-sided',
  'experiments.sampleSizeCalculator.testDirectionTwoSided': 'Two-sided',
  'experiments.sampleSizeCalculator.trafficAllocationDescription': 'Share of eligible traffic entering the experiment',
  'experiments.sampleSizeCalculator.trafficAllocationLabel': 'Percent of users in this experiment',
  'experiments.sampleSizeCalculator.trafficAllocationMissing': 'Enter a traffic allocation.',
  'experiments.sampleSizeCalculator.trafficAllocationOutOfRange': 'Must be greater than 0% and up to 100%.',
  'experiments.sampleSizeCalculator.trafficDescription': 'Estimate how much traffic the experiment will receive.',
  'experiments.sampleSizeCalculator.trafficHeading': 'Traffic',
  'experiments.sampleSizeTarget.attachCalculation': 'Attach calculation',
  'experiments.sampleSizeTarget.edit': 'Edit',
  'experiments.sampleSizeTarget.heading': 'Sample size target',
  'experiments.sampleSizeTarget.multipleVariationsCaveat':
    'Sample size estimates may be inaccurate for experiments with more than two variations.',
  'experiments.sampleSizeTarget.percentileMetricTooltip':
    'Sample size cannot be added when the primary metric is a percentile metric.',
  'experiments.sampleSizeTarget.remove': 'Remove',
  'experiments.sampleSizeTarget.selectMetricTooltip': 'Select a primary metric to calculate a sample size.',
  'experiments.saveExperimentDialog.cancel': 'Cancel',
  'experiments.saveExperimentDialog.closeLabel': 'close modal',
  'experiments.saveExperimentDialog.heading': 'Save changes and start new iteration',
  'experiments.saveExperimentDialog.reasonForChanges': 'Reason for changes',
  'experiments.saveExperimentDialog.reasonPlaceholder': 'Enter your reason for making these changes',
  'experiments.saveExperimentDialog.stratifiedSamplingNotice':
    "We're setting up stratified sampling to make sure your experiment groups are well balanced — this can take a bit longer than usual.",
  'experiments.saveExperimentDialog.subtitle': 'Saving changes will automatically start a new iteration',
  'experiments.segmentsOfInterest.emptyState': 'No segments of interest found',
  'experiments.segmentsOfInterest.heading': 'Segments of interest',
  'experiments.segmentsOfInterest.interval': 'Interval',
  'experiments.segmentsOfInterest.learnAboutSegments': 'Learn about segments of interest',
  'experiments.segmentsOfInterest.loadError': 'Failure loading segments of interest data',
  'experiments.segmentsOfInterest.noAttributes': 'No attributes',
  'experiments.segmentsOfInterest.relativeDifference': 'Relative difference',
  'experiments.segmentsOfInterest.tooltipBody':
    'Some very helpful instructions that assist the user in making decisions.',
  'experiments.segmentsOfInterest.tooltipHeading': 'Header text',
  'experiments.segmentsOfInterest.tooltipLoadError': 'Something went wrong',
  'experiments.segmentsOfInterest.units': 'Units',
  'experiments.selectLayersModal.availablePercent': '{percent}% available',
  'experiments.selectLayersModal.cancel': 'Cancel',
  'experiments.selectLayersModal.createLayer': 'Create Layer',
  'experiments.selectLayersModal.createOrAdd': 'Create or add to layer',
  'experiments.selectLayersModal.descriptionFieldLabel': 'Layer description',
  'experiments.selectLayersModal.descriptionLabel': 'Description',
  'experiments.selectLayersModal.layerComboBoxLabel': 'Select layer',
  'experiments.selectLayersModal.loadError': 'There was an unexpected error loading layers',
  'experiments.selectLayersModal.loadingLabel': 'Loading layers',
  'experiments.selectLayersModal.mutualExclusionHint':
    'Experiments in a layer group are mutually exclusive from each other',
  'experiments.selectLayersModal.nameFieldLabel': 'Layer name',
  'experiments.selectLayersModal.nameLabel': 'Name',
  'experiments.selectLayersModal.noLayersHeading': 'No layers found. Create a new layer to get started.',
  'experiments.selectLayersModal.reservationLimitError': 'Total reservations cannot exceed 100%',
  'experiments.selectLayersModal.saveLayer': 'Save layer',
  'experiments.selectLayersModal.searchPlaceholder': 'Find a layer by name',
  'experiments.selectLayersModal.selectLayer': 'Select Layer',
  'experiments.selectLayersModal.showLayersLabel': 'Show list of layers',
  'experiments.selectLayersModal.unavailableLayersHeading':
    'Layers with a randomization unit not matching the iteration randomization unit',
  'experiments.selectedHoldout.amountLabel': 'holdout amount',
  'experiments.selectedHoldout.holdoutAmount': 'Holdout amount',
  'experiments.selectedHoldout.loadError': 'Holdout information did not load',
  'experiments.selectedHoldout.loadErrorBody': 'Try again later',
  'experiments.selectedHoldout.loadingLabel': 'loading holdout',
  'experiments.setupStatus.close': 'Close',
  'experiments.setupStatus.completeDescription': 'Experiment setup is complete.',
  'experiments.setupStatus.completeSetupTooltip': 'Complete the experiment setup before starting.',
  'experiments.setupStatus.connectSdkTooltip': 'Connect your SDK before starting the experiment.',
  'experiments.setupStatus.finishDescription': 'Finish to launch your experiment.',
  'experiments.setupStatus.inProgress': 'Set up in progress',
  'experiments.setupStatus.readyToStart': 'Ready to start',
  'experiments.setupStatus.runningDescription': 'Experiment is running.',
  'experiments.setupStatus.start': 'Start',
  'experiments.setupStatus.startFailedTitle': 'Could not start experiment',
  'experiments.setupStatus.started': 'Started',
  'experiments.setupStatus.unknownError': 'An unknown error occurred. Try again.',
  'experiments.shipItDialog.afterShippingSummary':
    '100% of {hasUnit, select, true {<unit></unit> } other {}}contexts{hasRule, select, true { that match the targeting rule <rule></rule>} other {}} will be served: <treatment></treatment>',
  'experiments.shipItDialog.cancel': 'Cancel',
  'experiments.shipItDialog.heading': 'Ship the leading variation',
  'experiments.shipItDialog.learnMore': 'Learn more',
  'experiments.shipItDialog.moreInfoLabel': 'Show more information',
  'experiments.shipItDialog.servedAfterShipping': 'Served after shipping',
  'experiments.shipItDialog.shipIt': 'Ship it',
  'experiments.shipItDialog.subheader': 'When you ship this experiment, results will stop recording.',
  'experiments.shipItDialog.updateError': 'Error updating experiment',
  'experiments.shipItDialog.variation': 'Variation',
  'experiments.shipItDialog.winningStatement':
    '<treatment></treatment> has a probability to beat control ≥ {threshold}% and has the highest probability to be best of all treatments',
  'experiments.shippedBanner.body':
    "<diamond></diamond> {name}'s probability to be best was ≥ {pBest}%. Therefore, it was shipped to 100% of <randomizationUnit></randomizationUnit> contexts targeted in this experiment.",
  'experiments.shippedBanner.leadingVariationShipped': 'Leading variation shipped',
  'experiments.sidebarActions.alreadyArchived': 'This {experimentKind} is already archived.',
  'experiments.sidebarActions.archiveConfirmBody':
    'This will archive your {experimentKind} in the <environment></environment> environment. You can restore this {experimentKind} at a later time.',
  'experiments.sidebarActions.archiveConfirmTitle': 'Archive "{name}"?',
  'experiments.sidebarActions.archiveLabel': 'Archive {experimentKind}',
  'experiments.sidebarActions.cannotArchiveInLayer': 'You cannot archive an {experimentKind} that is in a layer.',
  'experiments.sidebarActions.cannotArchiveNoAccess': 'You do not have permission to archive this {experimentKind}.',
  'experiments.sidebarActions.cannotArchiveRunning': 'You cannot archive a running {experimentKind}.',
  'experiments.sidebarActions.cloneExperiment':
    '{kind, select, mab {Clone multi-armed bandit} other {Clone experiment}}',
  'experiments.sidebarActions.copyLinkLabel': 'Copy link',
  'experiments.sidebarActions.detailsTab': 'Details',
  'experiments.sidebarActions.discussionTab': 'Discussion',
  'experiments.sidebarActions.downloadResultsPdf': 'Download results PDF',
  'experiments.sidebarActions.kindExperiment': 'experiment',
  'experiments.sidebarActions.kindMultiArmedBandit': 'multi-armed bandit',
  'experiments.sidebarDiscussion.deleteCommentLabel': 'Delete comment',
  'experiments.sidebarDiscussion.loadMoreLabel': 'Loading more comments',
  'experiments.sidebarDiscussion.loading': 'Loading…',
  'experiments.sidebarDiscussion.todayLabel': 'Today',
  'experiments.sidebarDiscussion.usersMenuLabel': 'Users',
  'experiments.simpleMode.advanced': 'Full setup',
  'experiments.simpleMode.allAssignmentData': 'All assignment data',
  'experiments.simpleMode.assignmentDateRange': 'Assignment date range',
  'experiments.simpleMode.attributeCount': '{count, plural, one {# attribute} other {# attributes}}',
  'experiments.simpleMode.attributes': 'Attributes',
  'experiments.simpleMode.bayesianApproach': 'Bayesian, {threshold}% threshold',
  'experiments.simpleMode.controlVariation': 'Control variation',
  'experiments.simpleMode.customDateRange': 'Custom date range',
  'experiments.simpleMode.customRule': 'Custom rule',
  'experiments.simpleMode.dataExportOnly': 'Data Export only',
  'experiments.simpleMode.defaultRule': 'Default rule',
  'experiments.simpleMode.evenSplit': 'Even split across {count, plural, one {# variation} other {# variations}}',
  'experiments.simpleMode.frequentistApproach': 'Frequentist, {threshold}% significance',
  'experiments.simpleMode.frequentistSequentialApproach': 'Frequentist, {threshold}% significance, sequential testing',
  'experiments.simpleMode.heading': 'Other settings',
  'experiments.simpleMode.holdout': 'Holdout',
  'experiments.simpleMode.inHoldout': 'In a holdout',
  'experiments.simpleMode.launchDarklyAssignment': 'LaunchDarkly flag or config',
  'experiments.simpleMode.layer': 'Mutual exclusion layer',
  'experiments.simpleMode.layerSummary': '{name}, {percent}% reserved',
  'experiments.simpleMode.mode': 'Experiment builder mode',
  'experiments.simpleMode.none': 'None',
  'experiments.simpleMode.on': 'On',
  'experiments.simpleMode.percentOfContexts': '{percent}% of contexts',
  'experiments.simpleMode.simple': 'Quick setup',
  'experiments.simpleMode.variationsSplit': 'Variations split',
  'experiments.simpleMode.warehouseAssignment': 'Warehouse assignment source',
  'experiments.srmBanner.learnMore': 'Learn more',
  'experiments.srmBanner.mismatchDetected': 'Sample ratio mismatch detected.',
  'experiments.srmBanner.noMismatchData': 'No sample ratio mismatch data',
  'experiments.srmBanner.noMismatchDetected': 'No sample ratio mismatch detected.',
  'experiments.startExperimentDialog.cancel': 'Cancel',
  'experiments.startExperimentDialog.closeLabel': 'close modal',
  'experiments.startExperimentDialog.heading': 'Start {type}',
  'experiments.startExperimentDialog.holdoutHeading': 'Declare ready to include experiments and multi-armed bandits?',
  'experiments.startExperimentDialog.holdoutHeadingLabel': 'Declare holdout ready',
  'experiments.startExperimentDialog.holdoutSubtitle':
    "You will no longer be able to make changes to your holdout's configuration except for the name, description, and statistical approach.",
  'experiments.startExperimentDialog.stratifiedSamplingNotice':
    "We're setting up stratified sampling to make sure your experiment groups are well balanced — this can take a bit longer than usual.",
  'experiments.startExperimentDialog.subtitle':
    'This {type} will begin serving the variations you specified when you set the {type} audience, and will start recording results.',
  'experiments.startExperimentDialog.warehouseSubtitle':
    'This {type} will analyze the assignments and start recording results.',
  'experiments.startedFilter.anytime': 'Anytime',
  'experiments.startedFilter.label': 'Started',
  'experiments.startedFilter.within1Month': 'Within 1 month',
  'experiments.startedFilter.within1Week': 'Within 1 week',
  'experiments.startedFilter.within1Year': 'Within 1 year',
  'experiments.startedFilter.within3Months': 'Within 3 months',
  'experiments.startedFilter.within6Months': 'Within 6 months',
  'experiments.statisticalAnalysisPreview.bayesianIntro':
    'This is a <name>{methodology}</name> {designType} {entityType} {funnelMetricGroup} {cuped} with a threshold of <emphasis>{threshold}</emphasis>.',
  'experiments.statisticalAnalysisPreview.confidenceInterval':
    'The confidence interval will be <emphasis>{value}</emphasis>.',
  'experiments.statisticalAnalysisPreview.credibleInterval':
    'The credible interval will be <emphasis>{value}%</emphasis>.',
  'experiments.statisticalAnalysisPreview.cupedOn': 'with CUPED turned on',
  'experiments.statisticalAnalysisPreview.estimatedSampleSize':
    'The estimated sample size is <emphasis>{size}</emphasis>.',
  'experiments.statisticalAnalysisPreview.estimatedSampleSizeWithDuration':
    'The estimated sample size is <emphasis>{size}</emphasis> with an estimated duration of <emphasis>{duration}</emphasis>.',
  'experiments.statisticalAnalysisPreview.fixedHorizon': 'using the <emphasis>fixed horizon</emphasis> method.',
  'experiments.statisticalAnalysisPreview.frequentistIntro':
    'This is a <name>{methodology}</name> {designType} {entityType} {funnelMetricGroup} {cuped}',
  'experiments.statisticalAnalysisPreview.funnelMetricGroup': 'using a funnel metric group',
  'experiments.statisticalAnalysisPreview.mccApplied':
    'Multiple comparisons correction will be applied using the <emphasis>{method, select, bonferroni {Bonferroni} other {Benjamini-Hochberg}}</emphasis> method across <emphasis>{scope, select, metrics {metrics} variations {treatments} other {treatments and metrics}}</emphasis>.',
  'experiments.statisticalAnalysisPreview.sequentialTesting': 'with <emphasis>sequential testing</emphasis> enabled.',
  'experiments.statisticalAnalysisPreview.significanceLevel':
    'The significance level is set to <emphasis>{value}</emphasis> using a <emphasis>{testDirection}</emphasis> hypothesis test.',
  'experiments.statisticalAnalysisSection.aaTestGuidanceBody':
    'Frequentist is recommended for A/A tests because it strictly controls the false-positive rate.',
  'experiments.statisticalAnalysisSection.applyCupedTo': 'Apply CUPED to all applicable metrics',
  'experiments.statisticalAnalysisSection.approachDescription':
    'If you are familiar with p-values, choose frequentist. Otherwise, choose Bayesian.',
  'experiments.statisticalAnalysisSection.changeToFrequentist': 'Change to frequentist',
  'experiments.statisticalAnalysisSection.checkingMetricsLabel': 'Checking selected metrics',
  'experiments.statisticalAnalysisSection.cupedDescription':
    'CUPED is a variance reduction technique that makes experiments more sensitive without increasing sample size. If enabled, adjustments will be applied to results if relevant pre-experiment data is available. Percentile metrics, ratio metrics, and attribute slices are not supported.',
  'experiments.statisticalAnalysisSection.enableSequentialTesting': 'Enable sequential testing',
  'experiments.statisticalAnalysisSection.fixedHorizonGuidance':
    'Use the <calculator>sample size calculator</calculator> to determine your sample size for your fixed horizon test. <docs>Learn more</docs>',
  'experiments.statisticalAnalysisSection.heading': 'Statistical approach and success criteria',
  'experiments.statisticalAnalysisSection.learnMore': 'Learn more',
  'experiments.statisticalAnalysisSection.sequentialTestingDescription':
    'Compute confidence intervals and p-values that you can act upon at any time during your experiment, rather than at a fixed sample size.',
  'experiments.statisticalAnalysisSection.significanceLevel': 'Significance level',
  'experiments.statisticalAnalysisSection.significanceLevelDescription':
    "The significance level is a threshold used to determine whether a result is meaningful, or statistically significant, rather than just due to chance. If a treatment variation's p-value is equal to or smaller than the significance level, the result is considered statistically significant. A common significance level is 0.05.",
  'experiments.statisticalAnalysisSection.significanceThresholdLabel': 'Significance threshold',
  'experiments.statisticalAnalysisSection.statisticalApproach': 'Statistical approach',
  'experiments.statisticalAnalysisSection.testDirection': 'Direction of hypothesis test',
  'experiments.statisticalAnalysisSection.testDirectionDescription':
    'If you want to see whether a treatment variation performs statistically significantly better or worse than the control, choose a two-sided test. If you only want to see improvement over the control, choose a one-sided test.',
  'experiments.statisticalAnalysisSection.threshold': 'Threshold',
  'experiments.statisticalAnalysisSection.thresholdDescription':
    "The threshold helps decide if a treatment variation performs meaningfully different from the control. If a treatment's probability to beat control meets or exceeds this threshold, it can be considered the best-performing variation. A common threshold used is 95%. The threshold doesn't change most of the reported numbers, except for the credible interval.",
  'experiments.statisticalAnalysisSection.uninformativePriorsDescription':
    'Treatment variations use shrinkage priors by default, which pull them toward the control. Use uninformative priors to let treatment results reflect only the data collected. The control variation always uses an uninformative prior.',
  'experiments.statisticalAnalysisSection.useUninformativePriors': 'Use uninformative priors for treatments',
  'experiments.status.loading': 'Loading…',
  'experiments.statusFilter.any': 'Any',
  'experiments.statusFilter.label': 'Status',
  'experiments.statusFilter.notStarted': 'Not started',
  'experiments.statusFilter.running': 'Running',
  'experiments.statusFilter.stopped': 'Stopped',
  'experiments.statusIndicator.analyzing': 'Analyzing',
  'experiments.statusIndicator.complete': 'Complete',
  'experiments.statusIndicator.created': 'Created',
  'experiments.statusIndicator.iconLabel': 'Experiment status',
  'experiments.statusIndicator.notStarted': 'Not started',
  'experiments.statusIndicator.ready': 'Ready',
  'experiments.statusIndicator.running': 'Running',
  'experiments.statusIndicator.stopped': 'Stopped',
  'experiments.stopExperimentDialog.cancel': 'Cancel',
  'experiments.stopExperimentDialog.closeLabel': 'close modal',
  'experiments.stopExperimentDialog.reasonForStopping': 'Reason for stopping',
  'experiments.stopExperimentDialog.reasonPlaceholder': 'Write your reason for stopping this experiment',
  'experiments.stopExperimentDialog.stopExperiment': 'Stop experiment',
  'experiments.stopExperimentDialog.targetingRuleExplanation':
    '100% of <contextKind></contextKind> contexts that match the targeting rule, <rule></rule>, will be served:',
  'experiments.stopExperimentDialog.trialLapsedNotice':
    'Your experimentation access has ended. If stopped, this experiment cannot be restarted until you upgrade your plan.',
  'experiments.stopHoldoutDialog.body':
    "You won't be able to add experiments or multi-armed bandits to this holdout, or restart it.",
  'experiments.stopHoldoutDialog.cancel': 'Cancel',
  'experiments.stopHoldoutDialog.closeLabel': 'close modal',
  'experiments.stopHoldoutDialog.endError': 'Error ending holdout',
  'experiments.stopHoldoutDialog.endHoldout': 'End holdout',
  'experiments.stopHoldoutDialog.endedToast': 'Holdout ended',
  'experiments.stopHoldoutDialog.genericError': 'Error',
  'experiments.stopMenu.controlLabel': 'Control',
  'experiments.stopMenu.controlTreatment': 'Control',
  'experiments.stopMenu.disabledFlagArchived': 'You cannot stop an experiment if its flag is archived',
  'experiments.stopMenu.disabledNoExperiment': 'You cannot stop an experiment without experiment',
  'experiments.stopMenu.disabledNoFlag': 'You cannot stop an experiment without flag',
  'experiments.stopMenu.disabledNoIteration': 'You cannot stop an experiment without iteration',
  'experiments.stopMenu.durationRange': '{days} days, {start} to {end}',
  'experiments.stopMenu.durationSince': '{days} days since {start}',
  'experiments.stopMenu.requestApproval': 'Request approval to stop',
  'experiments.stopMenu.stopWithoutApproval': 'Stop without approval',
  'experiments.stopMenu.treatmentLabel': 'Treatment {number}',
  'experiments.stopMenu.winningTag': 'Winning',
  'experiments.stopMenu.winningVariationLabel': 'Winning variation',
  'experiments.stratifiedSampling.applyStratifiedSampling': 'Apply stratified sampling',
  'experiments.stratifiedSampling.description':
    'Stratified sampling means splitting users into groups (like small vs. large) and ensuring each is fairly represented in control and treatment(s), so no group skews the results.',
  'experiments.stratifiedSampling.downloadFile': 'Download file',
  'experiments.stratifiedSampling.emptyStateBody':
    'No file uploaded yet. Upload a CSV that maps your context IDs to covariate values.',
  'experiments.stratifiedSampling.layerConflict': 'You cannot use stratified sampling with layers.',
  'experiments.stratifiedSampling.learnMore': 'Learn more',
  'experiments.stratifiedSampling.pendingFileNote': 'This file will be deleted after saving the experiment changes.',
  'experiments.stratifiedSampling.uploadedAt': 'Uploaded at {date}',
  'experiments.stratifiedSamplingUpload.cancel': 'Cancel',
  'experiments.stratifiedSamplingUpload.deleteFileLabel': 'Delete file',
  'experiments.stratifiedSamplingUpload.fileTooLarge': 'File must not exceed {maxSize}MB',
  'experiments.stratifiedSamplingUpload.formatDescription':
    "The file's first column must be the context key, and the second column must be the covariate value. Subsequent columns may contain data, but will be ignored.",
  'experiments.stratifiedSamplingUpload.formattingExample': 'Formatting example',
  'experiments.stratifiedSamplingUpload.heading': 'Upload CSV for stratified sampling',
  'experiments.stratifiedSamplingUpload.instructions': 'Instructions',
  'experiments.stratifiedSamplingUpload.overwriteWarning': 'Uploading a file will overwrite the existing file.',
  'experiments.stratifiedSamplingUpload.selectCsv': 'Select CSV',
  'experiments.stratifiedSamplingUpload.uploadCsv': 'Upload CSV',
  'experiments.stratifiedSamplingUpload.uploadFile': 'Upload file',
  'experiments.tagsFilter.addTags': 'Add tag(s)',
  'experiments.tagsFilter.any': 'Any',
  'experiments.tagsFilter.label': 'Tags',
  'experiments.trafficEstimate.calculatorLink': 'Try the online sample size calculator',
  'experiments.trafficEstimate.calculatorLinkLabel': 'Try the sample size calculator',
  'experiments.trafficEstimate.estimateExplanation':
    'LaunchDarkly calculates this estimate based on existing traffic for your primary metric, and updates it as more events are received.',
  'experiments.trafficEstimate.insufficientTraffic':
    'Not enough traffic. 100 exposures per treatment are necessary to calculate estimate for additional traffic',
  'experiments.trafficEstimate.moreContextsNeeded':
    '<emphasis>{count}</emphasis> more {hasUnit, select, true {<unit></unit> } other {}}contexts needed to reach a probability to be best of <emphasis>{threshold}</emphasis>',
  'experiments.trafficEstimate.unableToCalculate':
    'LaunchDarkly was unable to calculate the estimated additional necessary traffic because this experiment has missing values',
  'experiments.turnOnFlagAlert.body':
    'Toggle on the <link>{flagName}</link> {entity} to start the {type}{canRequestApproval, select, true {, or cancel to request approval first} other {}}.',
  'experiments.turnOnFlagAlert.heading': "The {type}'s {entity} is off",
  'experiments.turnOnFlagAlert.toggleOn': 'Toggle on {entity}',
  'experiments.turnOnFlagAlert.turnedOn': 'The {entity} has been turned on. You can now start the {type}.',
  'experiments.turnOnFlagToast.error':
    'Failed to turn on the {isAiConfig, select, true {config} other {flag}}. Try again or turn it on from the {isAiConfig, select, true {config} other {flag}} targeting page.',
  'experiments.typeFilter.any': 'Any',
  'experiments.typeFilter.bigqueryNative': 'BigQuery native',
  'experiments.typeFilter.clickhouseNative': 'ClickHouse native',
  'experiments.typeFilter.dataExportOnly': 'Data Export only',
  'experiments.typeFilter.databricksNative': 'Databricks native',
  'experiments.typeFilter.featureChange': 'Feature change',
  'experiments.typeFilter.funnelOptimization': 'Funnel optimization',
  'experiments.typeFilter.label': 'Type',
  'experiments.typeFilter.redshiftNative': 'Redshift native',
  'experiments.typeFilter.snowflakeNative': 'Snowflake native',
  'experiments.unitCutPopover.body':
    'This unit cut is due to the metric having been configured to not include units with no events in the analysis',
  'experiments.unitCutPopover.calculationExposures': 'Number of exposures for metric calculation',
  'experiments.unitCutPopover.explanation': 'Explanation',
  'experiments.unitCutPopover.heading': 'A unit cut has been applied',
  'experiments.unitCutPopover.learnMore': 'Learn more',
  'experiments.unitCutPopover.originalExposures': 'Original number of exposures',
  'experiments.variationImageThumbnail.defaultAlt': 'Image Thumbnail',
  'experiments.variationImageThumbnail.loadingLabel': 'Loading image',
  'experiments.variationImageThumbnail.previewLabel': 'Image Preview',
  'experiments.variationImageUpload.dropZoneLabel': 'Click or drag to upload file',
  'experiments.variationImageUpload.fileTriggerLabel': 'Upload file',
  'experiments.variationLabel.imageAlt': '{variation} variation image',
  'experiments.variationLabel.tagsLabel': 'treatment tags',
  'experiments.variationSplitModal.aaTestDescription':
    'Variations are split equally when running A/A tests. Select the variations you want to include in this A/A test.',
  'experiments.variationSplitModal.allocation': 'Allocation',
  'experiments.variationSplitModal.allocationFieldLabel': '{variation} allocation percentage',
  'experiments.variationSplitModal.cancel': 'Cancel',
  'experiments.variationSplitModal.cancelLabel': 'Cancel audience split changes',
  'experiments.variationSplitModal.closeLabel': 'Close',
  'experiments.variationSplitModal.controlRequired': 'The control must be in the experiment',
  'experiments.variationSplitModal.controlTagGroupLabel': 'Control',
  'experiments.variationSplitModal.experimentType': 'experiment',
  'experiments.variationSplitModal.includeIn': 'Include in {type}',
  'experiments.variationSplitModal.mabType': 'multi-armed bandit',
  'experiments.variationSplitModal.saveAudienceSplit': 'Save audience split',
  'experiments.variationSplitModal.saveLabel': 'Save audience split changes',
  'experiments.variationSplitModal.splitEqually': 'Split equally',
  'experiments.variationSplitModal.variant': 'Variant',
  'experiments.variationSplitSection.aaTestGuidanceBody':
    'A/A tests typically split traffic equally between variations.',
  'experiments.variationSplitSection.allocationMismatch':
    'Flag variations no longer match allocations. Update allocation splits.',
  'experiments.variationSplitSection.control': 'Control',
  'experiments.variationSplitSection.detectSampleRatio': 'Detect sample ratio mismatches',
  'experiments.variationSplitSection.documentItAlongside': 'Document it alongside your analysis',
  'experiments.variationSplitSection.edit': 'Edit',
  'experiments.variationSplitSection.expectedSplitDescription':
    'The split is determined by your assignment data, not LaunchDarkly. Declare the expected split to:',
  'experiments.variationSplitSection.uploadImageLabel': 'upload image',
  'experiments.variationsStep.control': 'Control',
  'experiments.variationsStep.currentCopyLabel': 'Current button copy',
  'experiments.variationsStep.editLabel': 'Edit variations',
  'experiments.variationsStep.intro':
    'Choose one button in your app. Enter its current label and an alternative to test against. Half of your audience will randomly be served Control. The other, Treatment.',
  'experiments.variationsStep.newCopyLabel': 'New button copy',
  'experiments.variationsStep.next': 'Next',
  'experiments.variationsStep.saveError': 'Could not save changes. Try again.',
  'experiments.variationsStep.saving': 'Saving…',
  'experiments.variationsStep.title': 'Variations',
  'experiments.warehouseCard.connected': 'Connected',
  'experiments.warehouseCard.disconnected': 'Disconnected',
  'experiments.warehouseCard.disconnectedReason': 'Disconnected. {reason}',
  'experiments.warehouseCard.disconnectedSince': 'Disconnected since {timestamp}. {reason}',
  'experiments.warehouseCard.heading': 'Warehouse',
  'experiments.warehouseCard.loadError': 'Error loading warehouse status',
  'experiments.warehouseCard.loadingLabel': 'Loading warehouse status',
  'experiments.warehouseCard.nextUpdate': 'Next update',
  'experiments.warehouseCard.notAvailable': 'Not available',
  'experiments.warehouseCard.pending': 'Pending',
  'experiments.warehouseCard.rampUpInfoLabel': 'About the current update frequency',
  'experiments.warehouseCard.refreshFailed':
    'The last attempted refresh failed. {reason} If the issue continues, contact your admin.',
  'experiments.warehouseCard.refreshFailedAt':
    'Last attempted refresh at {timestamp} failed. {reason} If the issue continues, contact your admin.',
  'experiments.warehouseCard.resultsUpdated': 'Results updated',
  'experiments.warehouseCard.running': 'Refresh in progress',
  'experiments.warehouseCard.updateFrequency': 'Update frequency',
  'experiments.warehouseCard.updateFrequencyRampUp':
    'Every {rampLabel} until {rampUpEndsAt}, then every {cadenceLabel}',
  'experiments.warehouseMetricsCheck.body': 'Check not available for warehouse native experiments.',
  'experiments.warehouseMetricsCheck.heading': 'Metrics',
  'factory.flagReleaseDashboard.category.all': 'All releases',
  'factory.flagReleaseDashboard.category.readyForCleanup': 'Ready for cleanup',
  'factory.flagReleaseDashboard.category.regressions': 'Regressions',
  'factory.flagReleaseDashboard.column.created': 'Flag created',
  'factory.flagReleaseDashboard.column.currentStatus': 'Current status',
  'factory.flagReleaseDashboard.column.flag': 'Feature flag',
  'factory.flagReleaseDashboard.column.maintainer': 'Maintainer',
  'factory.flagReleaseDashboard.column.pullRequest': 'Pull request',
  'factory.flagReleaseDashboard.column.releasePlan': 'Release plan',
  'factory.flagReleaseDashboard.column.tags': 'Tags',
  'factory.flagReleaseDashboard.emptyBody': 'Try a different search or clear your filters.',
  'factory.flagReleaseDashboard.emptyState.body':
    'Use release plans to configure how flags are delivered and ensure great releases are the default mode. Set up auto-release to recommend release plans from CI and automatically start pending deployment signals.',
  'factory.flagReleaseDashboard.emptyState.createReleasePlan': 'Create release plan',
  'factory.flagReleaseDashboard.emptyState.heading': 'Releases that run themselves',
  'factory.flagReleaseDashboard.emptyState.readAboutLink': 'Read about releases',
  'factory.flagReleaseDashboard.emptyState.setUpAutoRelease': 'Set up auto-release',
  'factory.flagReleaseDashboard.emptyTitle': 'No flag releases match',
  'factory.flagReleaseDashboard.filters.heading': 'Filter releases by…',
  'factory.flagReleaseDashboard.hover.evaluations': 'Evaluations in last 7 days',
  'factory.flagReleaseDashboard.hover.lifecycleChecks': 'Lifecycle checks',
  'factory.flagReleaseDashboard.hover.lifecycleChecksError': 'LaunchDarkly could not load the lifecycle checks.',
  'factory.flagReleaseDashboard.hover.lifecycleChecksLoading': 'Loading lifecycle checks…',
  'factory.flagReleaseDashboard.hover.releaseDetailsError': 'LaunchDarkly could not load the release details.',
  'factory.flagReleaseDashboard.hover.releaseDetailsLoading': 'Loading release details…',
  'factory.flagReleaseDashboard.hover.releasePlan': 'Release plan: <strong>{name}</strong>',
  'factory.flagReleaseDashboard.hover.started': 'Started {time}. Running for {duration}.',
  'factory.flagReleaseDashboard.hover.startedPhase':
    'Started phase {current} of {total} {time}. Running for {duration}.',
  'factory.flagReleaseDashboard.loadError': "Couldn't load flag releases.",
  'factory.flagReleaseDashboard.loadingLabel': 'Loading flag releases',
  'factory.flagReleaseDashboard.manageReleasePlans': 'Manage release plans',
  'factory.flagReleaseDashboard.pagination': 'Showing <strong>{from}–{to}</strong>',
  'factory.flagReleaseDashboard.scope.allEnvironments': 'All environments',
  'factory.flagReleaseDashboard.scope.codeCleanup': 'Code cleanup',
  'factory.flagReleaseDashboard.scope.flagArchival': 'Flag archival',
  'factory.flagReleaseDashboard.searchLabel': 'Search flag releases',
  'factory.flagReleaseDashboard.searchPlaceholder': 'Search by name, description, or key',
  'factory.flagReleaseDashboard.statusDescription.cleanupSkipped': 'Permanent flag. This step will never run.',
  'factory.flagReleaseDashboard.statusDescription.completed': 'The release is finished.',
  'factory.flagReleaseDashboard.statusDescription.notReadyForArchival':
    'Code references are gone. The flag can be archived once it passes its lifecycle checks.',
  'factory.flagReleaseDashboard.statusDescription.notReadyForCodeRemoval':
    'This flag must have no targeting changes for 14 days and serve only 1 variation across all environments.',
  'factory.flagReleaseDashboard.statusDescription.paused':
    "Automation is paused and won't advance until someone resumes it.",
  'factory.flagReleaseDashboard.statusDescription.pending': 'The release has been triggered and will start shortly.',
  'factory.flagReleaseDashboard.statusDescription.pendingDeploy':
    'Waiting for a deploy signal before the rollout starts.',
  'factory.flagReleaseDashboard.statusDescription.pendingFlagEvals':
    'Waiting for this flag to be evaluated before the rollout starts.',
  'factory.flagReleaseDashboard.statusDescription.pendingManualStart': 'Waiting for someone to start the rollout.',
  'factory.flagReleaseDashboard.statusDescription.pendingPreviousEnvironment':
    "Starts automatically after the previous environment's release completes.",
  'factory.flagReleaseDashboard.statusDescription.pendingTrigger':
    "Waiting for this environment's rollout to be triggered.",
  'factory.flagReleaseDashboard.statusDescription.readyForArchival':
    'Archiving stops the flag from being evaluated and moves it out of the active list.',
  'factory.flagReleaseDashboard.statusDescription.readyForCodeRemoval':
    'The flag serves one variation everywhere. Remove its code references before archiving it.',
  'factory.flagReleaseDashboard.statusDescription.regressionDetected':
    'The guarded rollout detected a regression and stopped advancing. Review its metrics before continuing.',
  'factory.flagReleaseDashboard.statusDescription.releasing': 'A rollout is in progress and being monitored.',
  'factory.flagReleaseDashboard.statusDescription.rolledBack': 'The rollout was rolled back to the original variation.',
  'factory.flagReleaseDashboard.step.label': '{name}: {state}',
  'factory.flagReleaseDashboard.step.notRequired': 'Not required',
  'factory.flagReleaseDashboard.summaryLabel': 'Filter by release status',
  'factory.flagReleaseDashboard.tableLabel': 'Flag releases',
  'factory.flagReleaseDashboard.title': 'Releases',
  'featureMonitoring.aiInsights.analysisError': 'Failed to analyze eval data for this flag. Try again.',
  'featureMonitoring.aiInsights.analyzingLabel': 'Analyzing flag data',
  'featureMonitoring.aiInsights.highSeverityLabel': 'High severity',
  'featureMonitoring.aiInsights.infoLabel': 'Info',
  'featureMonitoring.aiInsights.mediumSeverityLabel': 'Medium severity',
  'featureMonitoring.aiInsights.noInsights': 'No insights found for this flag in the selected time range.',
  'featureMonitoring.aiInsights.phraseAnalyzingEvaluations': 'Analyzing flag evaluations…',
  'featureMonitoring.aiInsights.phraseComputingInsights': 'Computing insights…',
  'featureMonitoring.aiInsights.phraseCorrelatingRollout': 'Correlating rollout data…',
  'featureMonitoring.aiInsights.phraseCrunchingNumbers': 'Crunching the numbers…',
  'featureMonitoring.aiInsights.phraseDetectingAnomalies': 'Detecting anomalies…',
  'featureMonitoring.aiInsights.phraseExaminingHealth': 'Examining evaluation health…',
  'featureMonitoring.aiInsights.phraseIdentifyingTrends': 'Identifying trends…',
  'featureMonitoring.aiInsights.phraseLookingForInteresting': 'Looking for something interesting…',
  'featureMonitoring.aiInsights.phraseReviewingPatterns': 'Reviewing variation patterns…',
  'featureMonitoring.aiInsights.phraseScanningSignals': 'Scanning for signals…',
  'featureMonitoring.aiInsights.title': 'AI Monitoring Insights',
  'featureMonitoring.chartEmptyState.noData': 'No data to show',
  'featureMonitoring.chartMenu.copyLink': 'Copy link',
  'featureMonitoring.chartTooltip.flagChanges': 'Flag Changes',
  'featureMonitoring.chartTooltip.noData': 'No data',
  'featureMonitoring.chartTooltip.updatedTheFlag': 'Updated the flag',
  'featureMonitoring.chartTooltip.variationTypeMismatch':
    "{marker} Variation type does not match the flag's known variations which can be caused by the SDK fallback.",
  'featureMonitoring.correlationTable.globalEventsVsEvals': 'Global Events vs Evals',
  'featureMonitoring.correlationTable.heading': 'Correlation Analysis',
  'featureMonitoring.correlationTable.notAvailable': 'N/A',
  'featureMonitoring.correlationTable.scopedEventsVsEvals': 'Scoped Events vs Evals',
  'featureMonitoring.correlationTable.scopedEventsVsGlobalEvents': 'Scoped Events vs Global Events',
  'featureMonitoring.correlationTable.tableLabel': 'Correlation analysis',
  'featureMonitoring.correlationTable.variation': 'Variation',
  'featureMonitoring.errorEventMenu.autogeneratedErrors': 'Autogenerated errors',
  'featureMonitoring.errorEventMenu.autogeneratedErrorsDescription': 'Autogenerated LD telemetry error events',
  'featureMonitoring.errorEventMenu.customCriticalSeverity': 'Custom critical severity errors',
  'featureMonitoring.errorEventMenu.customCriticalSeverityDescription':
    'Custom frontend error events with critical severity level',
  'featureMonitoring.errorEventMenu.customHighSeverity': 'Custom high severity errors',
  'featureMonitoring.errorEventMenu.customHighSeverityDescription':
    'Custom frontend error events with high severity level',
  'featureMonitoring.errorEventMenu.customMediumSeverity': 'Custom medium severity errors',
  'featureMonitoring.errorEventMenu.customMediumSeverityDescription':
    'Custom frontend error events with medium severity level',
  'featureMonitoring.errorsChart.description':
    'Measures the percentage of users that encountered a front-end error at least once.',
  'featureMonitoring.errorsChart.infoLabel': 'Options',
  'featureMonitoring.errorsChart.optionsLabel': 'Options',
  'featureMonitoring.errorsChart.title': 'Total front-end errors',
  'featureMonitoring.errorsChart.total': 'Total: {count}',
  'featureMonitoring.errorsChart.viewAll': 'View all',
  'featureMonitoring.errorsChartMenu.countDescription':
    'Count of every error event recorded across all {contextKinds} in the selected period',
  'featureMonitoring.errorsChartMenu.errorCountBy': 'Error count by <code>{contextKind}</code>',
  'featureMonitoring.errorsChartMenu.errorCountBySubmenu': 'Error count by…',
  'featureMonitoring.errorsChartMenu.errorRateBy': 'Error rate by <code>{contextKind}</code>',
  'featureMonitoring.errorsChartMenu.errorRateBySubmenu': 'Error rate by…',
  'featureMonitoring.errorsChartMenu.errorsPer': 'Errors per <code>{contextKind}</code>',
  'featureMonitoring.errorsChartMenu.errorsPerSubmenu': 'Errors per…',
  'featureMonitoring.errorsChartMenu.perDescription': 'Average number of errors experienced per {contextKind}',
  'featureMonitoring.errorsChartMenu.rateDescription':
    'Percentage of {contextKinds} that experienced at least one error',
  'featureMonitoring.evaluationsBreakdown.attributionLabel':
    '{name} accounts for {percent} percent of total evaluations',
  'featureMonitoring.evaluationsBreakdown.attributionLabelUnderOne':
    '{name} accounts for less than 1 percent of total evaluations',
  'featureMonitoring.evaluationsBreakdown.configureApplicationMetadata': 'Configure application metadata',
  'featureMonitoring.evaluationsBreakdown.configureApplicationMetadataLink': 'Configure application metadata',
  'featureMonitoring.evaluationsBreakdown.noApplicationData': 'No application data available',
  'featureMonitoring.evaluationsBreakdown.paginationSummary':
    '<strong>{from}–{to}</strong> of <strong>{total}</strong> charts',
  'featureMonitoring.evaluationsBreakdown.singleItemNotice':
    "The breakdown compares multiple client-side SDKs or applications, and with just one present you're already seeing the entire dataset.",
  'featureMonitoring.evaluationsBreakdown.unattributed': 'Unattributed',
  'featureMonitoring.evaluationsChart.chartLabel': 'Flag evaluations chart',
  'featureMonitoring.evaluationsChart.contextCountsUnavailable':
    'Context counts are not available. This could be due to using an older SDK version or no client-side evaluations in the selected time range. <link>Learn more</link>',
  'featureMonitoring.evaluationsChart.evaluations': 'Evaluations',
  'featureMonitoring.evaluationsChart.groupByApplication': 'Group by application',
  'featureMonitoring.evaluationsChart.groupByNone': 'None',
  'featureMonitoring.evaluationsChart.groupByPlaceholder': 'Group by…',
  'featureMonitoring.evaluationsChart.groupBySdk': 'Group by SDK',
  'featureMonitoring.evaluationsChart.groupingLabel': 'Select chart grouping',
  'featureMonitoring.evaluationsChart.infoLabel': 'Info',
  'featureMonitoring.evaluationsChart.loadError': 'Failed to load evaluation data. Refresh to try again.',
  'featureMonitoring.evaluationsChart.optionsLabel': 'Options',
  'featureMonitoring.evaluationsChart.serverSideOnlyContextCounts':
    'Because this flag is not configured for client-side SDKs, only evaluation counts display here. Context counts, such as unique users, are currently tracked only for client-side SDKs. <link>Learn more</link>',
  'featureMonitoring.evaluationsChart.totalEvaluationsLabel': 'Total evaluations: {total}',
  'featureMonitoring.evaluationsChart.uniqueCountOf': 'Unique count of <code>{contextKind}</code>',
  'featureMonitoring.evaluationsChart.variationMismatch':
    "Some evaluations don't match this flag's variation values. This can happen when an SDK falls back to a default value. Check your SDK configuration.",
  'featureMonitoring.flagHistory.chartsUpdated': 'Charts updated with new data',
  'featureMonitoring.flagHistory.emptyBody':
    'This view shows targeting and variation changes. For a complete record of all flag activity, visit the audit log.',
  'featureMonitoring.flagHistory.flagSpecificHeading': 'Flag-specific',
  'featureMonitoring.flagHistory.historyHeading': 'Flag history',
  'featureMonitoring.flagHistory.projectWideDescription':
    'These metrics are not scoped to the flag, but contain annotations of changes for this flag.',
  'featureMonitoring.flagHistory.projectWideHeading': 'Project-wide',
  'featureMonitoring.flagHistory.projectWideInfoLabel': 'Options',
  'featureMonitoring.flagHistory.telemetryPrompt':
    'Get real-time insights with feature flag monitoring. <link>Set up Telemetry</link>',
  'featureMonitoring.flagHistory.viewAuditLog': 'View audit log',
  'featureMonitoring.historyTable.actions': 'Actions',
  'featureMonitoring.historyTable.copyDescriptionLabel': 'Copy description',
  'featureMonitoring.historyTable.date': 'Date',
  'featureMonitoring.historyTable.description': 'Description',
  'featureMonitoring.historyTable.event': 'Event',
  'featureMonitoring.historyTable.eventFlagSettingsUpdated': 'Flag settings updated',
  'featureMonitoring.historyTable.eventRulesUpdated': 'Rules updated',
  'featureMonitoring.historyTable.eventTargetsUpdated': 'Targets updated',
  'featureMonitoring.historyTable.eventToggledFlag': 'Toggled flag',
  'featureMonitoring.historyTable.eventVariationUpdated': 'Variation updated',
  'featureMonitoring.historyTable.initiatedBy': 'Initiated by',
  'featureMonitoring.historyTable.limitedByCountNotice': 'Showing 200 most recent entries. <link>View all</link>.',
  'featureMonitoring.historyTable.noEntriesFound': 'No entries found',
  'featureMonitoring.historyTable.paginationSummary':
    '<strong>{from}–{to}</strong> of <strong>{total}</strong> entries',
  'featureMonitoring.historyTable.pastThreeMonthsNotice': 'Showing past three months. <link>View all</link>.',
  'featureMonitoring.historyTable.pinToChart': 'Pin to chart',
  'featureMonitoring.historyTable.searchHint': 'Search by event name, description, or author',
  'featureMonitoring.historyTable.searchLabel': 'Search flag history',
  'featureMonitoring.historyTable.searchPlaceholder': 'Search history…',
  'featureMonitoring.historyTable.system': 'System',
  'featureMonitoring.historyTable.tableLabel': 'table',
  'featureMonitoring.historyTable.unknownEvent': 'Unknown',
  'featureMonitoring.historyTable.unpin': 'Unpin',
  'featureMonitoring.historyTable.viewDetails': 'View details',
  'featureMonitoring.observabilityError.loadFailedTitle': 'Failed to load {dataType}',
  'featureMonitoring.observabilityError.networkBody':
    'Could not connect to load {dataType}. Check your connection and try again.',
  'featureMonitoring.observabilityError.retentionBody':
    'Data is not available for this time range. Try selecting a more recent period.',
  'featureMonitoring.observabilityError.retentionTitle': 'Data not available',
  'featureMonitoring.observabilityError.timeoutBody': 'Query timed out. Consider narrowing the time range.',
  'featureMonitoring.observabilityError.timeoutTitle': 'Request timed out',
  'featureMonitoring.observabilityError.tooManyBody':
    'There are too many results for this time range. Try a shorter time window to load results.',
  'featureMonitoring.observabilityError.tooManyTitle': 'Too many {dataType} to display',
  'featureMonitoring.observabilityError.unknownBody':
    'Something went wrong loading {dataType}. Try again or select a different time range.',
  'featureMonitoring.observabilitySetup.body':
    'Connect the Observability SDK to see errors, logs, traces, and sessions tied to your feature flag variations.',
  'featureMonitoring.observabilitySetup.exploreSandbox': 'Explore sandbox',
  'featureMonitoring.observabilitySetup.heading': 'Set up Observability',
  'featureMonitoring.observabilitySetup.viewDocumentation': 'View documentation',
  'featureMonitoring.observabilityTabs.errorsHeading': 'Errors',
  'featureMonitoring.observabilityTabs.logsHeading': 'Logs',
  'featureMonitoring.observabilityTabs.noDataFound': 'No {dataType} found for this flag in the selected time range.',
  'featureMonitoring.observabilityTabs.sessionsHeading': 'Sessions',
  'featureMonitoring.observabilityTabs.tracesHeading': 'Traces',
  'featureMonitoring.page.errorTitle': 'Something went wrong',
  'featureMonitoring.page.errorsWindowNotice': 'Showing the past hour of error data. <link>See all errors</link>',
  'featureMonitoring.page.loadError': 'There was an error loading feature monitoring data',
  'featureMonitoring.page.refreshLabel': 'Refresh',
  'featureMonitoring.page.tryAgain': 'Try again',
  'featureMonitoring.releasesEmptyState.body': 'Ready to release your flag?',
  'featureMonitoring.releasesEmptyState.heading': 'No releases yet',
  'featureMonitoring.releasesEmptyState.shipIt': 'Ship it',
  'featureMonitoring.status.loadingDots': 'Loading…',
  'featureMonitoring.tabs.audience': 'Audience',
  'featureMonitoring.tabs.audienceLabel': 'Audience tab',
  'featureMonitoring.tabs.errors': 'Errors',
  'featureMonitoring.tabs.errorsLabel': 'Errors tab',
  'featureMonitoring.tabs.feedback': 'Feedback',
  'featureMonitoring.tabs.feedbackLabel': 'Feedback tab',
  'featureMonitoring.tabs.flagHistory': 'Flag history',
  'featureMonitoring.tabs.flagHistoryLabel': 'Flag history tab',
  'featureMonitoring.tabs.logs': 'Logs',
  'featureMonitoring.tabs.logsLabel': 'Logs tab',
  'featureMonitoring.tabs.releases': 'Releases',
  'featureMonitoring.tabs.releasesLabel': 'Releases tab',
  'featureMonitoring.tabs.sessions': 'Sessions',
  'featureMonitoring.tabs.sessionsLabel': 'Sessions tab',
  'featureMonitoring.tabs.traces': 'Traces',
  'featureMonitoring.tabs.tracesLabel': 'Traces tab',
  'featureMonitoring.tabs.viewLabel': 'Select monitoring view',
  'featureMonitoring.timeControls.customRange': 'Select custom range',
  'featureMonitoring.timeControls.durationLabel': 'Select time duration',
  'featureMonitoring.timeControls.oneDay': '1D',
  'featureMonitoring.timeControls.oneDayLabel': '1 day duration',
  'featureMonitoring.timeControls.oneHour': '1H',
  'featureMonitoring.timeControls.oneHourLabel': '1 hour duration',
  'featureMonitoring.timeControls.oneMonth': '1M',
  'featureMonitoring.timeControls.oneMonthLabel': '1 month duration',
  'featureMonitoring.timeControls.refresh': 'Refresh',
  'featureMonitoring.timeControls.refreshLabel': 'Refresh data',
  'featureMonitoring.timeControls.refreshing': 'Refreshing',
  'featureMonitoring.timeControls.sevenDays': '7D',
  'featureMonitoring.timeControls.sevenDaysLabel': '7 days duration',
  'featureMonitoring.timeControls.share': 'Share',
  'featureMonitoring.timeControls.twoWeeks': '2W',
  'featureMonitoring.timeControls.twoWeeksLabel': '2 weeks duration',
  'featureMonitoring.timeRangeFilter.past2Months': 'Past 2 months',
  'featureMonitoring.timeRangeFilter.past2Weeks': 'Past 2 weeks',
  'featureMonitoring.timeRangeFilter.past3Months': 'Past 3 months',
  'featureMonitoring.timeRangeFilter.pastDay': 'Past day',
  'featureMonitoring.timeRangeFilter.pastHour': 'Past hour',
  'featureMonitoring.timeRangeFilter.pastMonth': 'Past month',
  'featureMonitoring.timeRangeFilter.pastWeek': 'Past week',
  'featureMonitoring.trendChip.defaultPeriod': '7 days',
  'featureMonitoring.trendChip.tooltip': 'Change from previous {period}',
  'featureMonitoring.variationSelector.all': 'All',
  'featureMonitoring.variationSelector.clearLabel': 'clear',
  'featureMonitoring.variationSelector.searchLabel': 'Search variations',
  'featureMonitoring.variationSelector.searchPlaceholder': 'Search variations',
  'featureMonitoring.variationSelector.selectLabel': 'Select variation',
  'featureMonitoring.webVitals.average': 'Avg: {value}',
  'featureMonitoring.webVitals.clsDescription':
    'Measures the cumulative impact of layout shifts on the user experience.',
  'featureMonitoring.webVitals.clsTitle': 'Cumulative Layout Shift',
  'featureMonitoring.webVitals.infoLabel': 'Options',
  'featureMonitoring.webVitals.inpDescription':
    'Measures how quickly, in milliseconds, your app responds to user interactions.',
  'featureMonitoring.webVitals.inpTitle': 'Interaction to Next Paint',
  'featureMonitoring.webVitals.lcpDescription': 'Measures how quickly, in milliseconds, your app renders page content.',
  'featureMonitoring.webVitals.lcpTitle': 'Largest Contentful Paint',
  'featureMonitoring.webVitals.noData': 'No data',
  'featureMonitoring.webVitals.optionsLabel': 'Options',
  'feedback.aiGroups.bugs': 'Bugs',
  'feedback.aiGroups.featureRequests': 'Feature requests',
  'feedback.aiGroups.kudos': 'Kudos',
  'feedback.aiGroups.lastGenerated': 'Last generated {relativeTime}',
  'feedback.aiGroups.noGroupsToDisplay': 'No groups to display.',
  'feedback.aiGroups.noGroupsYet': 'No groups yet. Click <strong>Update groupings</strong> to analyze your feedback.',
  'feedback.aiGroups.title': 'AI feedback groups',
  'feedback.aiGroups.updateGroupings': 'Update groupings',
  'feedback.aiGroups.updatedGroupsReady': 'Updated groups are ready — click to apply.',
  'feedback.aiGroups.updatedGroupsReadyLabel': 'Updated groups are ready — click to apply',
  'feedback.contextualDialog.closeLabel': 'Close',
  'feedback.contextualDialog.heading': 'Give feedback',
  'feedback.contextualTrigger.giveFeedback': 'Give feedback',
  'feedback.contextualWidget.commentLabel': 'Additional feedback',
  'feedback.contextualWidget.commentPlaceholder': 'What could we do better?',
  'feedback.contextualWidget.commentPrompt': 'Anything else to share?',
  'feedback.contextualWidget.dismissLabel': 'Dismiss feedback widget',
  'feedback.contextualWidget.ratingLabel': 'Rate your experience',
  'feedback.contextualWidget.shareEmail': 'Share my email with LaunchDarkly so they can follow up with me',
  'feedback.contextualWidget.thanks': 'Thanks for your feedback',
  'feedback.contextualWidget.thumbsDownLabel': 'Thumbs down',
  'feedback.contextualWidget.thumbsUpLabel': 'Thumbs up',
  'feedback.contextualWidget.widgetLabel': 'Share your feedback',
  'feedback.feedbackForm.commentPlaceholder': 'Tell us what you think…',
  'feedback.feedbackForm.formLabel': 'Feedback',
  'feedback.feedbackForm.thumbsDownLabel': 'Thumbs down',
  'feedback.feedbackForm.thumbsUpLabel': 'Thumbs up',
  'feedback.filters.anySentiment': 'Any sentiment',
  'feedback.filters.clearSearchLabel': 'clear search',
  'feedback.filters.negative': 'Negative',
  'feedback.filters.neutral': 'Neutral',
  'feedback.filters.past2Years': 'Past 2 years',
  'feedback.filters.past3Months': 'Past 3 months',
  'feedback.filters.past6Months': 'Past 6 months',
  'feedback.filters.pastDay': 'Past day',
  'feedback.filters.pastHour': 'Past hour',
  'feedback.filters.pastMonth': 'Past month',
  'feedback.filters.pastWeek': 'Past week',
  'feedback.filters.pastYear': 'Past year',
  'feedback.filters.positive': 'Positive',
  'feedback.filters.searchLabel': 'Search feedback',
  'feedback.filters.searchPlaceholder': 'Search…',
  'feedback.filters.sentiment': 'Sentiment',
  'feedback.giveFeedback.feedbackLabel': 'Your feedback',
  'feedback.giveFeedback.feedbackPlaceholder': 'Tell us what you think…',
  'feedback.giveFeedback.sendLabel': 'Send',
  'feedback.giveFeedback.sentimentOptional': 'Sentiment (optional)',
  'feedback.giveFeedback.thumbsDownLabel': 'Thumbs down',
  'feedback.giveFeedback.thumbsUpLabel': 'Thumbs up',
  'feedback.giveFeedback.topicLabel': "What's this about?",
  'feedback.giveFeedback.topicPlaceholder': 'Select a topic…',
  'feedback.groupFilter.clear': 'Clear',
  'feedback.groupFilter.showingFeedbackFor': 'Showing feedback for: <strong>{groupTitle}</strong>',
  'feedback.sentimentDistribution.closeSectionLabel': 'close section',
  'feedback.sentimentDistribution.collapseLabel': 'Collapse',
  'feedback.sentimentDistribution.expandLabel': 'Expand',
  'feedback.sentimentDistribution.negative': 'Negative',
  'feedback.sentimentDistribution.neutral': 'Neutral',
  'feedback.sentimentDistribution.noSentimentData': 'No sentiment data available for the selected time range.',
  'feedback.sentimentDistribution.openSectionLabel': 'open section',
  'feedback.sentimentDistribution.paginationSummary': '<strong>{from}–{to}</strong> of <strong>{total}</strong>',
  'feedback.sentimentDistribution.positive': 'Positive',
  'feedback.sentimentDistribution.summary': '{count} {contextKind} gave feedback ({percentage}% of {total})',
  'feedback.sentimentDistribution.title': 'Sentiment distribution',
  'feedback.sentimentDistribution.total': 'Total: {count}',
  'feedback.setupGuide.beforeYouStart': 'Before you start',
  'feedback.setupGuide.beforeYouStartBody':
    "You'll need to have our SDK installed and configured. We also recommend installing the Observability SDK to enhance the feedback experience with richer insight capabilities like Session Replay, but it isn't required. <link>View documentation</link> or install with NPM:",
  'feedback.setupGuide.callSendFeedback':
    'Call your <code>sendFeedback</code> function wherever you want to collect feedback.',
  'feedback.setupGuide.closeLabel': 'Close modal',
  'feedback.setupGuide.defaultPrompt': 'Tell us what you think…',
  'feedback.setupGuide.defineSendFeedback':
    'To send feedback via the SDK, define a <code>sendFeedback</code> function. <link>Read about user feedback</link>, or copy the code below:',
  'feedback.setupGuide.feedbackPopover': 'Feedback popover',
  'feedback.setupGuide.feedbackPrompt': 'Feedback prompt',
  'feedback.setupGuide.javascript': 'JavaScript',
  'feedback.setupGuide.language': 'Language',
  'feedback.setupGuide.popoverDescription':
    'A feedback popover offers a lightweight way to collect feedback. It appears next to the trigger button and is ideal for quick, contextual feedback.',
  'feedback.setupGuide.reactExamples': 'React examples',
  'feedback.setupGuide.sdkSetup': 'SDK setup',
  'feedback.setupGuide.selectComponentType': 'Select a component type',
  'feedback.setupGuide.smileyDescription':
    'A smiley face widget offers three sentiment options: happy, neutral, and frowny faces. This provides more granular feedback than binary options.',
  'feedback.setupGuide.smileyFaces': 'Smiley faces',
  'feedback.setupGuide.tabsLabel': 'tabs',
  'feedback.setupGuide.thumbsDescription':
    'A thumbs up/down widget provides a simple binary feedback mechanism. Users can quickly indicate satisfaction and optionally provide additional details.',
  'feedback.setupGuide.thumbsUpOrDown': 'Thumbs up or down',
  'feedback.setupGuide.title': 'Feedback setup guide',
  'feedback.setupGuide.typescript': 'TypeScript',
  'feedback.setupGuide.viewSetup': 'View setup',
  'feedback.setupGuidePreview.defaultPrompt': 'Tell us what you think…',
  'feedback.setupGuidePreview.frownyFaceLabel': 'Frowny face',
  'feedback.setupGuidePreview.giveFeedback': 'Give feedback',
  'feedback.setupGuidePreview.happyFaceLabel': 'Happy face',
  'feedback.setupGuidePreview.neutralFaceLabel': 'Neutral face',
  'feedback.setupGuidePreview.send': 'Send',
  'feedback.setupGuidePreview.thanksForYourFeedback': 'Thanks for your feedback',
  'feedback.setupGuidePreview.thumbsDownLabel': 'Thumbs down',
  'feedback.setupGuidePreview.thumbsUpLabel': 'Thumbs up',
  'feedback.setupGuidePreview.wasThisHelpful': 'Was this helpful?',
  'feedback.status.loadingDots': 'Loading…',
  'feedback.table.actionsLabel': 'Feedback actions',
  'feedback.table.conversation': 'Conversation',
  'feedback.table.copyFeedbackLabel': 'Copy feedback',
  'feedback.table.cursorPaginationSummary': 'Showing <strong>{from}–{to}</strong> items',
  'feedback.table.failedToLoad': 'Failed to load feedback',
  'feedback.table.failedToLoadLabel': 'Failed to load',
  'feedback.table.feedback': 'Feedback',
  'feedback.table.fixWithVega': 'Fix with Vega',
  'feedback.table.key': 'Key',
  'feedback.table.newToFeedback': 'New to feedback?',
  'feedback.table.noDateAvailable': 'No date available',
  'feedback.table.noFeedbackAvailable': 'No feedback available',
  'feedback.table.noFeedbackFound': 'No feedback found',
  'feedback.table.noFeedbackMatches':
    'No feedback matches your filters for {dateRange}. Try a different date range or fewer filters.',
  'feedback.table.noSentimentData': 'No sentiment data',
  'feedback.table.noVariationData': 'No variation data',
  'feedback.table.observabilityPrompt':
    'Add the Observability SDK to get <strong>session replays</strong> directly from this table.',
  'feedback.table.openSetupGuide': 'Open setup guide',
  'feedback.table.paginationSummary': '<strong>{from}–{to}</strong> of <strong>{total}</strong> items',
  'feedback.table.pullRequest': 'Pull request',
  'feedback.table.received': 'Received',
  'feedback.table.retry': 'Retry',
  'feedback.table.retryLabel': 'retry',
  'feedback.table.sentiment': 'Sentiment',
  'feedback.table.sentimentTagsLabel': 'sentiment',
  'feedback.table.sourceGitHub': 'GitHub',
  'feedback.table.sourceTagsLabel': 'source',
  'feedback.table.sourceUi': 'UI',
  'feedback.table.session': 'Session',
  'feedback.table.source': 'Source',
  'feedback.table.tableLabel': 'Feedback table',
  'feedback.table.variation': 'Variation',
  'feedback.table.variationMissingSampled': 'Variation value may be missing due to sampling',
  'flags.addFlagLinkDialog.cancel': 'Cancel',
  'flags.addFlagLinkDialog.descriptionField': 'Description',
  'flags.addFlagLinkDialog.heading': 'Add link to flag',
  'flags.addFlagLinkDialog.save': 'Save',
  'flags.addFlagLinkDialog.savingLabel': 'Saving link',
  'flags.addFlagLinkDialog.titleField': 'Title',
  'flags.addFlagLinkDialog.urlError': 'Must be an absolute url.',
  'flags.addFlagLinkDialog.urlField': 'URL',
  'flags.addFlagLinkDialog.urlPlaceholder': 'Eg. https://www.example.com',
  'flags.addTargetingRule.addLabel': 'Add {label}',
  'flags.addTargetingRule.addRuleLabel': 'Add rule',
  'flags.addTargetingRule.upsellHeading': 'Upgrade to unlock more ways to target',
  'flags.addToCodePage.title': 'Add to code',
  'flags.aiInsightsCard.exploreInVega': 'Explore further in Vega',
  'flags.aiInsightsCard.generating': 'Generating…',
  'flags.aiInsightsCard.loadingLabel': 'Loading AI insights',
  'flags.approvalsPage.title': 'Review request',
  'flags.archiveChecks.additionalEnvironmentsRow': 'Additional environments',
  'flags.archiveChecks.blockedByExperiments': 'Flag cannot be archived because it has running experiments:',
  'flags.archiveChecks.blockedByPrerequisites': 'Flag cannot be archived because it is a prerequisite of these flags:',
  'flags.archiveChecks.changeCriteria': 'Change the archive criteria in <link>lifecycle settings</link>.',
  'flags.archiveChecks.generalChecksRow': 'General checks',
  'flags.archiveChecks.loadError': 'An unexpected error has occurred.',
  'flags.archiveChecks.loadingCriticalEnvironmentsRow': 'Loading critical environments…',
  'flags.archiveChecks.loadingGeneralChecksRow': 'Loading general checks…',
  'flags.archiveChecks.loadingMoreEnvironmentsRow': 'Loading more critical environments…',
  'flags.archiveChecks.tableLabel': 'Flag archive checks per environment',
  'flags.archiveChecksForm.cancel': 'Cancel',
  'flags.archiveChecksForm.reasonLabel': 'Reason',
  'flags.archiveChecksForm.reasonPlaceholder': 'Let people know why this flag was archived',
  'flags.archiveChecksModal.body': "We've run the checks for you, so you can safely archive this flag.",
  'flags.archiveChecksModal.closeLabel': 'close modal',
  'flags.archiveChecksModal.heading': '"{flagName}" is ready to archive',
  'flags.archiveChecksPopover.showChecks': 'Show checks',
  'flags.archiveChecksStatus.codeRefsConnectNow': 'Connect now',
  'flags.archiveChecksStatus.codeRefsEnterpriseOnly': 'Available in Enterprise',
  'flags.archiveChecksStatus.codeRefsFound': 'Code references found',
  'flags.archiveChecksStatus.codeRefsNotConnected': 'Code references not connected',
  'flags.archiveChecksStatus.codeRefsNotFound': 'No code references found',
  'flags.archiveChecksStatus.criticalEnvironmentsLabel': 'critical environments',
  'flags.archiveChecksStatus.enterpriseFeaturesLabel': 'Enterprise features',
  'flags.archiveChecksStatus.evaluatingRecently': 'Evaluating in the past 7 days',
  'flags.archiveChecksStatus.flagIsOldEnough': 'Flag is 30 days or older',
  'flags.archiveChecksStatus.flagIsTemporary': 'Flag is temporary',
  'flags.archiveChecksStatus.noDependentFlags': 'No unarchived flags use this flag as a prerequisite',
  'flags.archiveChecksStatus.noRecentEvaluations': 'No evaluations in the past 7 days',
  'flags.archiveChecksStatus.servingOneVariation': 'Serving only one variation',
  'flags.archiveChecksTag.label': 'Archive checks',
  'flags.archiveChecksTag.readyForCodeRemoval': 'Ready for code removal',
  'flags.archiveChecksTag.readyToArchive': 'Ready to archive',
  'flags.archiveFlagDialog.archive': 'Archive',
  'flags.archiveFlagDialog.archiveHeading': 'Archive flag',
  'flags.archiveFlagDialog.archivedPrerequisites':
    '{count, plural, one {# prerequisite of this flag is archived. Restore it or remove it from this flag first, or this flag will serve its off variation.} other {# prerequisites of this flag are archived. Restore them or remove them from this flag first, or this flag will serve its off variation.}}',
  'flags.archiveFlagDialog.codeReferencesFound': '{count} code references found.',
  'flags.archiveFlagDialog.commentPlaceholder': 'Leave a comment',
  'flags.archiveFlagDialog.commentRequiredPlaceholder':
    'Comments are required for changes in one or more environments. This change affects all environments in this project.',
  'flags.archiveFlagDialog.confirmLabel': 'Confirm',
  'flags.archiveFlagDialog.evaluatedRecently': 'Flag has been evaluated in the last 7 days.',
  'flags.archiveFlagDialog.evaluatedRecentlyArchive':
    'Flag has been evaluated in the last 7 days. Remove it from your code before you archive.',
  'flags.archiveFlagDialog.evaluatedRecentlyRestore':
    'Flag has been evaluated in the last 7 days. Remove it from your code before you restore',
  'flags.archiveFlagDialog.evaluationStatusUnavailable':
    'Flag evaluation status is not enabled for this environment. You will need to manually confirm this flag is not being evaluated before archiving.',
  'flags.archiveFlagDialog.notAPrerequisite': 'Flag is not a prerequisite of other flags',
  'flags.archiveFlagDialog.notEvaluatedRecently': 'Flag has not been evaluated in the last 7 days',
  'flags.archiveFlagDialog.prerequisiteOf': 'Flag is a prerequisite of',
  'flags.archiveFlagDialog.restore': 'Restore',
  'flags.archiveFlagDialog.restoreHeading': 'Restore flag',
  'flags.archiveFlagDialog.servingDifferentVariations': 'Flag serves multiple variations in the selected environments',
  'flags.archiveFlagDialog.servingSameVariation': 'Flag is serving the same variation in the selected environments',
  'flags.archiveFlagDialog.unknownCodeDefault':
    'LaunchDarkly could not determine the default variation set in your code for this environment',
  'flags.archiveFlagDialog.viewCodeReferences': 'View',
  'flags.archiveFlagModal.archiveFlag': 'Archive flag',
  'flags.archiveFlagModal.blockedBody':
    'This flag must pass all checks before it can be archived. Review the checks below.',
  'flags.archiveFlagModal.blockedHeading': 'Archiving is blocked',
  'flags.archiveFlagModal.cancel': 'Cancel',
  'flags.archiveFlagModal.closeLabel': 'close modal',
  'flags.archiveFlagModal.description':
    'Archiving retires this flag in all environments without deleting it. If your application evaluates the flag after you archive it, LaunchDarkly serves the fallback value defined in your code.',
  'flags.archiveFlagModal.docsLink': 'Read about archiving flags',
  'flags.archiveFlagModal.loadError': 'An unexpected error has occurred.',
  'flags.archiveFlagModal.manageEnvironments': 'Manage environments',
  'flags.archiveFlagModal.noChecksBody':
    'No checks were run because you don’t have any critical environments set up. You may still archive the flag, but this may have unintended consequences.',
  'flags.archiveFlagModal.noChecksHeading': 'No critical environments to check',
  'flags.archiveFlagModal.reason': 'Reason',
  'flags.archiveFlagModal.reasonPlaceholder': 'Let people know why this flag was archived',
  'flags.archiveFlagPanel.archiveChecksBody':
    'Choose production environments to confirm there are no unintended effects in archiving your flag. This flag will be archived in all environments.',
  'flags.archiveFlagPanel.archiveChecksHeading': 'Archive readiness checks',
  'flags.archiveFlagPanel.archiveExplanation':
    'This will archive <name>{flagName}</name>, and the value defined in your code will be returned for any subsequent evaluations of this flag. Remove code references to <copyKey>{flagKey}</copyKey> from your application before archiving.',
  'flags.archiveFlagPanel.archiveHeading': 'Archive this flag?',
  'flags.archiveFlagPanel.environmentsPlaceholder': 'Select at least one environment to see flag status…',
  'flags.archiveFlagPanel.multipleVariationsTooltip':
    'LaunchDarkly is actively receiving different variations of this flag is this environment',
  'flags.archiveFlagPanel.noFlagConfiguration':
    'No flag configuration found for environment with key "{environmentKey}"',
  'flags.archiveFlagPanel.offNoOffVariationTooltip': 'This flag is off and does not have an off variation set',
  'flags.archiveFlagPanel.prerequisiteBlocked': 'You can’t archive while other flags use this flag as a prerequisite',
  'flags.archiveFlagPanel.restoreChecksBody':
    'Choose production environments to confirm there are no unintended effects in restoring your flag. This flag will be restored in all environments.',
  'flags.archiveFlagPanel.restoreChecksHeading': 'Verify flag state before restoring',
  'flags.archiveFlagPanel.restoreExplanation':
    "This will restore <name>{flagName}</name> <copyKey>{flagKey}</copyKey>, and it will resume being evaluated if there are references in your code. Review this flag's variations and targeting rules in your production environments before restoring.",
  'flags.archiveFlagPanel.restoreHeading': 'Restore this flag?',
  'flags.archiveFlagPanel.selectEnvironmentsLabel': 'Select environments',
  'flags.archiveFlagPanel.unknownVariation': 'unknown variation',
  'flags.archivedFlagBanner.archived': 'This flag is archived.',
  'flags.archivedFlagBanner.archivedOnPrefix': 'This flag was archived on',
  'flags.archivedFlagBanner.restore': 'Restore this flag',
  'flags.attributePicker.addValue': 'Add "<strong>{value}</strong>"',
  'flags.attributePicker.heading': 'Attribute',
  'flags.attributePicker.leadingWhitespaceTooltip': 'This attribute name contains leading whitespace',
  'flags.attributePicker.listLabel': 'Attributes',
  'flags.attributePicker.loadingLabel': 'Loading attributes',
  'flags.attributePicker.pickLabel': 'Pick attribute',
  'flags.attributePicker.placeholder': 'Select an attribute',
  'flags.attributePicker.selectLabel': 'Select attribute',
  'flags.attributePicker.trailingWhitespaceTooltip': 'This attribute name contains trailing whitespace',
  'flags.attributeValues.heading': 'Values',
  'flags.attributeValues.largeNumberNote':
    'Numbers with more than 16 digits are supported as strings to avoid rounding errors',
  'flags.attributeValues.leadingWhitespaceWarning': 'This value contains leading whitespace',
  'flags.attributeValues.stringOnlyTooltip': '{attribute} attribute can only target string types',
  'flags.attributeValues.stringType': 'String',
  'flags.attributeValues.trailingWhitespaceWarning': 'This value contains trailing whitespace',
  'flags.audienceChart.collapseChartLabel': 'Collapse chart section',
  'flags.audienceChart.empty': 'No audience found with selected filters',
  'flags.audienceChart.expandChartLabel': 'Expand chart section',
  'flags.audienceChart.loadError': 'Unable to load chart data. Try again later.',
  'flags.audienceChart.noEvaluations': 'No evaluations:',
  'flags.audienceChart.uniqueCountsOf': 'Unique counts of',
  'flags.audienceFilters.contextKindEmpty': 'N/A',
  'flags.audienceFilters.guardedRolloutFilterLabel': 'Guarded rollout filter',
  'flags.audienceFilters.selectGuardedRolloutFilterLabel': 'Select guarded rollout filter',
  'flags.audiencePage.title': 'Audience',
  'flags.audienceTable.emptyBody': 'No evaluations match your filters. Try adjusting the filter settings.',
  'flags.audienceTable.emptyHeading': 'No audience found',
  'flags.audienceTable.loadMore': 'Load more',
  'flags.audienceTable.loading': 'Loading…',
  'flags.audienceTable.loadingMoreLabel': 'Loading more results',
  'flags.audienceTable.partialViewBody':
    "You're seeing a partial view up to {maxResults} results, but filters check your whole audience. Try filtering to find exactly what you need.",
  'flags.audienceTable.partialViewHeading': 'Looking for something specific?',
  'flags.audienceTable.tableLabel': 'Audience data table',
  'flags.audienceToolbar.exportCsv': 'Export CSV',
  'flags.automaticArchival.cadenceDaily': 'Daily',
  'flags.automaticArchival.cadenceHourly': 'Hourly',
  'flags.automaticArchival.cadenceLabel': 'Check for eligible flags',
  'flags.automaticArchival.cadenceWeekly': 'Weekly',
  'flags.automaticArchival.cancel': 'Cancel',
  'flags.automaticArchival.confirmBody':
    'Automatic archival will archive eligible flags in this project. You can review ready-to-archive flags from the lifecycle settings page.',
  'flags.automaticArchival.confirmEnable': 'Enable automatic archival',
  'flags.automaticArchival.confirmHeading': 'Enable automatic archival?',
  'flags.automaticArchival.decrementLabel': 'Decrease maximum archivals',
  'flags.automaticArchival.description': 'Automatically archive flags that pass lifecycle readiness checks.',
  'flags.automaticArchival.enableLabel': 'Enable automatic archival',
  'flags.automaticArchival.heading': 'Automatic archival',
  'flags.automaticArchival.incrementLabel': 'Increase maximum archivals',
  'flags.automaticArchival.lastConfiguredBy': 'Last configured by {name} on {date}',
  'flags.automaticArchival.loadingLabel': 'Loading automatic archival settings',
  'flags.automaticArchival.maximumLabel': 'Maximum archivals per run',
  'flags.automaticArchival.requireApprovalLabel': 'Require approval before archiving',
  'flags.automaticArchival.save': 'Save',
  'flags.automaticArchival.saveError': 'Failed to update automatic archival settings',
  'flags.automaticArchival.saved': 'Automatic archival settings updated',
  'flags.automaticArchival.saving': 'Saving…',
  'flags.automaticArchival.viewAutoArchivedFlags': 'View auto-archived flags',
  'flags.automaticArchival.viewReadyToArchiveFlags': 'View ready to archive flags',
  'flags.automatedCleanup.addContext': 'Add context',
  'flags.automatedCleanup.additionalContextLabel': 'Additional context',
  'flags.automatedCleanup.additionalContextPlaceholder':
    'e.g., Flags are defined in example_file.tsx in the frontend and example_file.go in the backend.',
  'flags.automatedCleanup.additionalContextTooltip':
    "Optionally provide extra context for the cleanup agent. Vega will also respect your repo's agents.md file.",
  'flags.automatedCleanup.alsoCleanUpLiveFlags': 'Also clean up live flags',
  'flags.automatedCleanup.connectGitHub': 'Connect GitHub to select repositories',
  'flags.automatedCleanup.decrementLabel': 'Decrease',
  'flags.automatedCleanup.description':
    'Automatically run Vega flag cleanup on flags that are <strong>ready for code removal</strong>.',
  'flags.automatedCleanup.descriptionWithLive':
    'Automatically run Vega flag cleanup on flags that are <strong>ready for code removal</strong> and <strong>live</strong>.',
  'flags.automatedCleanup.enableLabel': 'Enable automated flag cleanup',
  'flags.automatedCleanup.heading': 'Automated flag cleanup',
  'flags.automatedCleanup.incrementLabel': 'Increase',
  'flags.automatedCleanup.lastConfiguredBy': 'Last configured by <avatar></avatar><strong>{name}</strong> on {date}',
  'flags.automatedCleanup.maxJobsLabel': 'Max cleanup jobs per run',
  'flags.automatedCleanup.readAboutLink': 'Read about Vega cleanup',
  'flags.automatedCleanup.repositoriesDescription': 'Select repositories to use for automated flag cleanup.',
  'flags.automatedCleanup.repositoriesHeading': 'GitHub repositories',
  'flags.automatedCleanup.reviewCleanups': 'Review cleanups',
  'flags.automatedCleanup.save': 'Save',
  'flags.automatedCleanup.saveError': 'Failed to update automated cleanup settings',
  'flags.automatedCleanup.saved': 'Automated cleanup settings updated',
  'flags.automatedCleanup.saving': 'Saving…',
  'flags.automatedCleanup.schedule1Day': '1 day',
  'flags.automatedCleanup.schedule30Days': '30 days',
  'flags.automatedCleanup.schedule7DaysDefault': '7 days (default)',
  'flags.automatedCleanup.scheduleEvery10Minutes': 'Every 10 minutes (testing)',
  'flags.automatedCleanup.scheduleLabel': 'Schedule frequency',
  'flags.automatedCleanup.selectRepository': 'Select at least one repository',
  'flags.automatedCleanup.selectRepositoryToEnable': 'Select at least one repository to enable',
  'flags.bulkActions.addTags': 'Add tags',
  'flags.bulkActions.addToView': 'Link to views',
  'flags.bulkActions.archive': 'Archive',
  'flags.bulkActions.clearSelection': 'Clear flag selection',
  'flags.bulkActions.flagColumn': 'Flag',
  'flags.bulkActions.groupLabel': 'Bulk flag actions',
  'flags.bulkActions.keyColumn': 'Key',
  'flags.bulkActions.more': 'More',
  'flags.bulkActions.moreActions': 'More bulk actions',
  'flags.bulkActions.selectedCount':
    '<badge>{count, number}</badge><label>{count, plural, one {selected flag} other {selected flags}}</label>',
  'flags.bulkActions.selectedCountLabel': '{count, plural, one {# selected flag} other {# selected flags}}',
  'flags.bulkActions.selectAll': 'Select all flags',
  'flags.bulkActions.statusColumn': 'Status',
  'flags.bulkActions.updateMaintainer': 'Update maintainer',
  'flags.bulkActions.vegaCleanup': 'Vega cleanup',
  'flags.bulkActions.vegaCleanupDisabled': 'Vega cleanup is not enabled for this account.',
  'flags.bulkAddTags.add': 'Add tags',
  'flags.bulkAddTags.auditComment': 'Added tags from the flag list bulk actions',
  'flags.bulkAddTags.body':
    '{count, plural, one {Add tags to the selected flag.} other {Add tags to the # selected flags.}} Existing tags will not be removed.',
  'flags.bulkAddTags.cancel': 'Cancel',
  'flags.bulkAddTags.closeLabel': 'Close',
  'flags.bulkAddTags.failure':
    '{count, plural, one {# flag could not be updated} other {# flags could not be updated}}. Try again.',
  'flags.bulkAddTags.heading': 'Add tags to selected flags',
  'flags.bulkAddTags.noAccess':
    '{count, plural, one {You do not have permission to add tags to this flag:} other {You do not have permission to add tags to these # flags:}}',
  'flags.bulkAddTags.selectedTagsLabel': 'Selected tags',
  'flags.bulkAddTags.success':
    '{tagCount, plural, one {Added # tag} other {Added # tags}} to {flagCount, plural, one {# flag} other {# flags}}',
  'flags.bulkAddToView.auditComment': 'Added from the flag list bulk actions',
  'flags.bulkAddToView.body':
    'Use views to group flags according to the teams in your organization and the features they work on.',
  'flags.bulkAddToView.cancel': 'Cancel',
  'flags.bulkAddToView.closeLabel': 'Close',
  'flags.bulkAddToView.dialogLabel': 'Link selected flags to a view',
  'flags.bulkAddToView.failure': 'The selected flags could not be linked to the selected views. Try again.',
  'flags.bulkAddToView.heading': 'Link {count, plural, one {# flag} other {# flags}} to views',
  'flags.bulkAddToView.link': 'Link to views',
  'flags.bulkAddToView.loading': 'Loading views…',
  'flags.bulkAddToView.partialFailure': 'Some flags could not be linked to every selected view. Try again.',
  'flags.bulkAddToView.selectedViewCount': '{count, plural, one {# view selected} other {# views selected}}',
  'flags.bulkAddToView.selectViews': 'Select views',
  'flags.bulkAddToView.success':
    '{flagCount, plural, one {Linked # flag} other {Linked # flags}} to {viewCount, plural, one {# view} other {# views}}',
  'flags.bulkArchiveDialog.alreadyArchived': 'Already archived',
  'flags.bulkArchiveDialog.archive': 'Archive flags',
  'flags.bulkArchiveDialog.archiveValid': '{count, plural, one {Archive # valid flag} other {Archive # valid flags}}',
  'flags.bulkArchiveDialog.blockedBody': 'Resolve the listed blockers, then try again.',
  'flags.bulkArchiveDialog.blockedHeading':
    '{count, plural, one {# selected flag cannot be archived} other {# selected flags cannot be archived}}',
  'flags.bulkArchiveDialog.blockedPartialBody':
    'Blocked flags will be skipped. You can still archive the {count, plural, one {# valid flag} other {# valid flags}}.',
  'flags.bulkArchiveDialog.body':
    '{count, plural, one {Review the selected flag before archiving it.} other {Review the # selected flags before archiving them.}} You can restore archived flags later.',
  'flags.bulkArchiveDialog.cancel': 'Cancel',
  'flags.bulkArchiveDialog.checksUnavailable': 'Archive checks unavailable',
  'flags.bulkArchiveDialog.closeLabel': 'Close',
  'flags.bulkArchiveDialog.dependentFlags':
    '{count, plural, one {Used as a prerequisite by # flag} other {Used as a prerequisite by # flags}}',
  'flags.bulkArchiveDialog.failure':
    '{count, plural, one {# flag could not be archived} other {# flags could not be archived}}. Try again.',
  'flags.bulkArchiveDialog.heading': 'Archive selected flags',
  'flags.bulkArchiveDialog.loadError': 'The archive checks could not be loaded. Try again.',
  'flags.bulkArchiveDialog.loading': 'Loading archive checks…',
  'flags.bulkArchiveDialog.noAccess': 'You do not have permission to archive this flag',
  'flags.bulkArchiveDialog.notReady': 'Does not meet the project’s archive criteria',
  'flags.bulkArchiveDialog.ready': 'Ready',
  'flags.bulkArchiveDialog.reason': 'Reason',
  'flags.bulkArchiveDialog.reasonPlaceholder': 'No longer used in production',
  'flags.bulkArchiveDialog.reasonRequired': 'Enter a reason.',
  'flags.bulkArchiveDialog.runningExperiments':
    '{count, plural, one {Used by # running experiment} other {Used by # running experiments}}',
  'flags.bulkArchiveDialog.selectFlagLabel': 'Select {flagName}',
  'flags.bulkArchiveDialog.selectedFlagsLabel': 'Selected flags and archive status',
  'flags.bulkArchiveDialog.selectionColumn': 'Selection',
  'flags.bulkArchiveDialog.statusDetailsBlocked': 'Resolve these blockers before archiving this flag.',
  'flags.bulkArchiveDialog.statusDetailsHeading': 'Archive status for {flagName}',
  'flags.bulkArchiveDialog.statusDetailsReady': 'This flag has no blockers and can be archived.',
  'flags.bulkArchiveDialog.success': '{count, plural, one {Archived # flag} other {Archived # flags}}',
  'flags.bulkArchiveDialog.warning': 'Warning',
  'flags.bulkCleanupDialog.additionalContextHelpLabel': 'About additional context',
  'flags.bulkCleanupDialog.additionalContextLabel': 'Add context',
  'flags.bulkCleanupDialog.additionalContextPlaceholder': 'Additional context or instructions for the cleanup agent…',
  'flags.bulkCleanupDialog.additionalContextTooltip':
    'Optionally provide extra context for the cleanup agent. The default prompt and instructions from your repository’s AGENTS.md file are always included.',
  'flags.bulkCleanupDialog.body':
    'Vega starts a separate cleanup job for each selected flag. Review the flags and optionally add context before starting the cleanup jobs.',
  'flags.bulkCleanupDialog.cancel': 'Cancel',
  'flags.bulkCleanupDialog.closeLabel': 'Close',
  'flags.bulkCleanupDialog.emptySelectionError': 'Select at least one flag before starting cleanup.',
  'flags.bulkCleanupDialog.environmentEvaluationSummary':
    '{evaluationCount, plural, one {# evaluation} other {# evaluations}}, {variationCount, plural, one {# active variation} other {# active variations}}',
  'flags.bulkCleanupDialog.errorHeading': 'Vega could not start cleanups',
  'flags.bulkCleanupDialog.evaluationSummary':
    '{evaluationCount, plural, one {# evaluation} other {# evaluations}} across {environmentCount, plural, one {# critical environment} other {# critical environments}} in the past 7 days.',
  'flags.bulkCleanupDialog.evaluationSummaryHeading': 'Evaluation summary',
  'flags.bulkCleanupDialog.heading': 'Clean up flags with Vega',
  'flags.bulkCleanupDialog.readDocs': 'Read cleanup docs',
  'flags.bulkCleanupDialog.readinessColumn': 'Ready for cleanup',
  'flags.bulkCleanupDialog.readinessError': 'Evaluation data is unavailable',
  'flags.bulkCleanupDialog.readinessLoading': 'Loading evaluation data',
  'flags.bulkCleanupDialog.readinessNoData': 'No data',
  'flags.bulkCleanupDialog.readinessReady': 'Ready',
  'flags.bulkCleanupDialog.readinessWarning': 'Warning',
  'flags.bulkCleanupDialog.rejectedError': 'Vega did not accept the cleanup request. Try again.',
  'flags.bulkCleanupDialog.repositoriesLabel': 'Repositories',
  'flags.bulkCleanupDialog.requestError': 'Vega could not start the selected cleanups. Try again.',
  'flags.bulkCleanupDialog.selectFlagLabel': 'Select {flagName}',
  'flags.bulkCleanupDialog.selectedFlagsHeading': 'Selected flags ({count, number})',
  'flags.bulkCleanupDialog.selectionColumn': 'Selection',
  'flags.bulkCleanupDialog.start': 'Start cleanups',
  'flags.bulkCleanupDialog.startedToast':
    '{count, plural, one {Cleanup started for <link># flag</link>} other {Cleanups started for <link># flags</link>}}',
  'flags.bulkImportDialog.body':
    "We're exploring a way to bring your existing flags over from another tool in one step. Tell us if it's something you'd use.",
  'flags.bulkImportDialog.close': 'Close',
  'flags.bulkImportDialog.heading': 'Interested in bulk flag import?',
  'flags.bulkImportDialog.importFlags': 'Import flags',
  'flags.bulkImportDialog.notifyMe': 'Notify me',
  'flags.bulkImportDialog.supportedTools':
    'Coming from {tools}? You can import those today. <link>View import integrations</link>',
  'flags.bulkImportDialog.supportedToolsList': 'Split, Unleash, or Statsig',
  'flags.bulkImportDialog.thanksToast': "Thanks — we'll let you know if bulk import becomes available.",
  'flags.bulkImportDialog.toolQuestion': 'Which tool would you import from?',
  'flags.bulkUpdateMaintainer.auditComment': 'Updated the maintainer from the flag list bulk actions',
  'flags.bulkUpdateMaintainer.body':
    '{count, plural, one {Choose a maintainer for the selected flag.} other {Choose one maintainer for the # selected flags.}}',
  'flags.bulkUpdateMaintainer.cancel': 'Cancel',
  'flags.bulkUpdateMaintainer.closeLabel': 'Close',
  'flags.bulkUpdateMaintainer.failure':
    '{count, plural, one {# flag could not be updated} other {# flags could not be updated}}. Try again.',
  'flags.bulkUpdateMaintainer.heading': 'Update maintainer for selected flags',
  'flags.bulkUpdateMaintainer.noAccess':
    '{count, plural, one {You do not have permission to update the maintainer for this flag:} other {You do not have permission to update the maintainer for these # flags:}}',
  'flags.bulkUpdateMaintainer.placeholder': 'Select a maintainer',
  'flags.bulkUpdateMaintainer.success':
    '{count, plural, one {Updated the maintainer for # flag} other {Updated the maintainer for # flags}}',
  'flags.bulkUpdateMaintainer.update': 'Update maintainer',
  'flags.changeSummary.addDisabledRule': 'Add disabled {ruleLabel}',
  'flags.changeSummary.addRuleProgressiveRollout': 'Add rule with progressive rollout',
  'flags.changeSummary.allPrerequisitesRemoved': 'All prerequisites will be removed',
  'flags.changeSummary.allRulesRemoved': 'All {ruleLabel}s will be removed',
  'flags.changeSummary.allTargetsRemoved': 'All {contextKind} targets will be removed',
  'flags.changeSummary.allUsersRemoved': 'All users will be removed',
  'flags.changeSummary.experimentRule': '{verb} experiment {ruleLabel}',
  'flags.changeSummary.experimentServing': '{text} experiment serving',
  'flags.changeSummary.stopAutomatedRelease': 'Stop automated release',
  'flags.changeSummary.updateKind': 'Update {kind}',
  'flags.changeSummary.updateProgressiveRollout': 'Update with progressive rollout',
  'flags.clauseEditor.attributePlaceholder': 'Select an attribute',
  'flags.clauseEditor.contextHeading': 'Context',
  'flags.clauseEditor.contextKindLabel': 'Context kind',
  'flags.clauseEditor.deleteClauseLabel': 'Delete clause {index}',
  'flags.clauseEditor.operatorHeading': 'Operator',
  'flags.clauseEditor.operatorLabel': 'Select an operator',
  'flags.clauseEditor.operatorPlaceholder': 'Select an operator',
  'flags.clauseEditor.operatorSuggestionsLabel': 'Show suggestions',
  'flags.clauseEditor.valueHeading': 'Value',
  'flags.clauseEditor.valueLabel': 'Select a value',
  'flags.clauseEditor.valuePlaceholder': 'Select values',
  'flags.cleanupPreview.activityData': 'Activity: {days} days',
  'flags.cleanupPreview.activityNeverData': 'Activity: never evaluated',
  'flags.cleanupPreview.activityShort': '{value, select, inactive {inactive} other {active}}',
  'flags.cleanupPreview.ageColumn': 'Flag Age',
  'flags.cleanupPreview.ageColumnTooltip': 'Time since the flag was created.',
  'flags.cleanupPreview.ageData': 'Age: {days} days',
  'flags.cleanupPreview.ageDaysShort': '{days}d',
  'flags.cleanupPreview.ageYearsShort': '{years}y',
  'flags.cleanupPreview.closeColumnsMenuLabel': 'Close columns menu',
  'flags.cleanupPreview.closeFilterLabel': 'Close filter',
  'flags.cleanupPreview.codeRefsColumn': 'Has Coderefs',
  'flags.cleanupPreview.codeRefsData': 'Code references: {value, select, present {present} other {none}}',
  'flags.cleanupPreview.codeRefsTooltip':
    'Whether code references to this flag still exist. Code refs must be present to be ready for code removal, and gone to be ready to archive.',
  'flags.cleanupPreview.columnsAutoTitle': 'Columns are set automatically in Diffs only mode',
  'flags.cleanupPreview.columnsMenu': 'Columns',
  'flags.cleanupPreview.countReadyForCodeRemoval': 'Ready for code removal',
  'flags.cleanupPreview.countReadyToArchive': 'Ready to archive',
  'flags.cleanupPreview.currentRequirement': 'Current: {requirement}',
  'flags.cleanupPreview.currentSettingsColumn': 'Current Settings',
  'flags.cleanupPreview.currentSettingsRow': 'Current settings',
  'flags.cleanupPreview.currentStateColumnTooltip': "The flag's cleanup eligibility under your saved settings.",
  'flags.cleanupPreview.dayCount': '{count, plural, one {# day} other {# days}}',
  'flags.cleanupPreview.description':
    'Check which flags are eligible for cleanup under your current settings. Select <strong>Show preview</strong> to compare against any changes you make above.',
  'flags.cleanupPreview.diffRow': 'Diff',
  'flags.cleanupPreview.diffsOnly': 'Diffs only',
  'flags.cleanupPreview.emptyFiltered': 'No flags match the current filters',
  'flags.cleanupPreview.emptyProject': 'No flags in this project',
  'flags.cleanupPreview.filterAll': 'All',
  'flags.cleanupPreview.filterArchive': 'Archive',
  'flags.cleanupPreview.filterByCodeRefsLabel': 'Filter by code refs',
  'flags.cleanupPreview.filterByColumnLabel': 'Filter by {column}',
  'flags.cleanupPreview.filterByCriticalActivityLabel': 'Filter by critical activity',
  'flags.cleanupPreview.filterByCurrentStateLabel': 'Filter by current state',
  'flags.cleanupPreview.filterByFlagTypeLabel': 'Filter by flag type',
  'flags.cleanupPreview.filterByPrerequisiteLabel': 'Filter by prerequisite',
  'flags.cleanupPreview.filterByProposedStateLabel': 'Filter by proposed state',
  'flags.cleanupPreview.filterByTargetingRulesLabel': 'Filter by targeting rules',
  'flags.cleanupPreview.filterCodeRemoval': 'Code removal',
  'flags.cleanupPreview.filterNo': 'No',
  'flags.cleanupPreview.filterYes': 'Yes',
  'flags.cleanupPreview.flagColumn': 'Flag',
  'flags.cleanupPreview.flagColumnTooltip':
    "The flag key. Click it to see this flag's per-environment evaluation details.",
  'flags.cleanupPreview.heading': 'Lifecycle preview',
  'flags.cleanupPreview.inactiveColumn': 'Critical Activity',
  'flags.cleanupPreview.inactiveColumnTooltip': 'Whether the flag is inactive in every critical environment.',
  'flags.cleanupPreview.inactiveColumnTrail': 'Required for archival.',
  'flags.cleanupPreview.lastChangeData': 'Last change: {days} days',
  'flags.cleanupPreview.loadingLabel': 'Loading flag lifecycle preview…',
  'flags.cleanupPreview.minimumAgeRequirement': 'Requires at least {days}.',
  'flags.cleanupPreview.mustBeTemporaryRequirement': 'Must be temporary.',
  'flags.cleanupPreview.mustNotBePrerequisiteRequirement': 'Must not be a prerequisite.',
  'flags.cleanupPreview.neverModifiedTooltip': "This flag's targeting has never been modified.",
  'flags.cleanupPreview.noChangesRequirement': 'Requires no changes for {days}.',
  'flags.cleanupPreview.noEvaluationsRequirement': 'Requires no evaluations for {days}.',
  'flags.cleanupPreview.noRecentChangesColumn': 'Last Change',
  'flags.cleanupPreview.noRecentChangesColumnTooltip':
    "Time since the flag's targeting last changed, across every critical environment.",
  'flags.cleanupPreview.noRecentChangesColumnTrail':
    'Required for code removal. "n/a" means it has never been modified.',
  'flags.cleanupPreview.notApplicable': 'n/a',
  'flags.cleanupPreview.notRequiredForCleanup': 'Not required for cleanup.',
  'flags.cleanupPreview.prerequisiteColumn': 'Is Prereq',
  'flags.cleanupPreview.prerequisiteColumnTooltip':
    'Whether the flag is a prerequisite for another flag in any critical environment.',
  'flags.cleanupPreview.prerequisiteData': 'Prerequisite: {value, select, yes {yes} other {no}}',
  'flags.cleanupPreview.proposedRequirement': 'Proposed: {requirement}',
  'flags.cleanupPreview.proposedSettingsColumn': 'Proposed Settings',
  'flags.cleanupPreview.proposedSettingsRow': 'Proposed settings',
  'flags.cleanupPreview.proposedStateColumnTooltip':
    "The flag's cleanup eligibility under the previewed (unsaved) form settings.",
  'flags.cleanupPreview.requiredForCodeRemoval': 'Required for code removal.',
  'flags.cleanupPreview.requirement': '{lead} {requirement}',
  'flags.cleanupPreview.requirementCompared': '{lead} Current: {current} Proposed: {proposed}',
  'flags.cleanupPreview.searchPlaceholder': 'Search…',
  'flags.cleanupPreview.showPreview': 'Show preview',
  'flags.cleanupPreview.singleVariationColumn': 'Targeting Rules',
  'flags.cleanupPreview.singleVariationColumnTooltip':
    'Whether the flag is serving a single variation (fully rolled out) in every critical environment.',
  'flags.cleanupPreview.sortColumnLabel': 'Sort column (currently {state})',
  'flags.cleanupPreview.sortDirection': '{direction, select, asc {ascending} desc {descending} other {none}}',
  'flags.cleanupPreview.sortTitle': 'Sort (currently {state})',
  'flags.cleanupPreview.stateArchivable': 'Archivable',
  'flags.cleanupPreview.stateLive': 'Live',
  'flags.cleanupPreview.stateRemovable': 'Removable',
  'flags.cleanupPreview.targetingData':
    'Targeting: {value, select, single {single variation} other {multiple variations}}',
  'flags.cleanupPreview.targetingShort': '{value, select, single {single} other {multi}}',
  'flags.cleanupPreview.temporaryColumn': 'Flag Type',
  'flags.cleanupPreview.temporaryColumnTooltip': 'Whether the flag is permanent or temporary.',
  'flags.cleanupPreview.typeData': 'Type: {type, select, temporary {temporary} other {permanent}}',
  'flags.cleanupPreview.typeShort': '{type, select, temporary {temp} other {perm}}',
  'flags.cleanupPreview.updatePreview': 'Update preview',
  'flags.cleanupPreview.yesNoShort': '{value, select, yes {yes} other {no}}',
  'flags.cleanupsDialog.cleanUp': 'Clean up',
  'flags.cleanupsDialog.closeLabel': 'Close',
  'flags.cleanupsDialog.collapseLabel': 'Collapse {flagKey}',
  'flags.cleanupsDialog.dispatchError': 'LaunchDarkly could not start the cleanup. Try again.',
  'flags.cleanupsDialog.dispatched': 'Cleanup started for {count, plural, one {# flag} other {# flags}}',
  'flags.cleanupsDialog.dueNow': 'Due now',
  'flags.cleanupsDialog.expandLabel': 'Expand {flagKey}',
  'flags.cleanupsDialog.failedTab': 'Failed',
  'flags.cleanupsDialog.finishedTab': 'Finished',
  'flags.cleanupsDialog.flagColumn': 'Flag',
  'flags.cleanupsDialog.inProgressTab': 'In progress',
  'flags.cleanupsDialog.lastRunColumn': 'Last run',
  'flags.cleanupsDialog.loadingFailedLabel': 'Loading failed cleanups…',
  'flags.cleanupsDialog.loadingFinishedLabel': 'Loading finished cleanups…',
  'flags.cleanupsDialog.loadingInProgressLabel': 'Loading in-progress cleanups…',
  'flags.cleanupsDialog.loadingUpcomingLabel': 'Loading upcoming cleanups…',
  'flags.cleanupsDialog.nextRunColumn': 'Next run',
  'flags.cleanupsDialog.noFailedBody': 'All flag cleanups have completed successfully.',
  'flags.cleanupsDialog.noFailedHeading': 'No failed cleanups',
  'flags.cleanupsDialog.noFinishedBody': 'Flags that have completed automated cleanup will appear here.',
  'flags.cleanupsDialog.noFinishedHeading': 'No finished cleanups',
  'flags.cleanupsDialog.noInProgressBody': 'Flags currently being cleaned up by the agent will appear here.',
  'flags.cleanupsDialog.noInProgressHeading': 'No cleanups in progress',
  'flags.cleanupsDialog.noUpcomingBody': 'There are no flags queued for the next automated cleanup run.',
  'flags.cleanupsDialog.noUpcomingHeading': 'No upcoming cleanups',
  'flags.cleanupsDialog.notEnabledBody':
    'Enable automated flag cleanup and choose a repository to see upcoming cleanups.',
  'flags.cleanupsDialog.notEnabledHeading': "Automated cleanup isn't enabled",
  'flags.cleanupsDialog.retry': 'Retry',
  'flags.cleanupsDialog.retrying': 'Retrying…',
  'flags.cleanupsDialog.runCount': '{count} runs',
  'flags.cleanupsDialog.statusColumn': 'Cleanup status',
  'flags.cleanupsDialog.tableLabel': 'Flag cleanups',
  'flags.cleanupsDialog.tabsLabel': 'Cleanups tabs',
  'flags.cleanupsDialog.title': 'Cleanups',
  'flags.cleanupsDialog.triggerNow': 'Trigger now',
  'flags.cleanupsDialog.triggering': 'Triggering…',
  'flags.cleanupsDialog.tryAgain': 'Try again',
  'flags.cleanupsDialog.upcomingLoadErrorBody': 'Something went wrong while checking for upcoming cleanups.',
  'flags.cleanupsDialog.upcomingLoadErrorHeading': "Couldn't load upcoming cleanups",
  'flags.cleanupsDialog.upcomingTab': 'Upcoming',
  'flags.cleanupsDialog.upcomingTableLabel': 'Upcoming flag cleanups',
  'flags.cleanupsDialog.viewAttempt': 'View attempt',
  'flags.cloneFlag.body':
    'Cloning the <flagLink>{flagName}</flagLink> flag will copy the full targeting configuration for all environments (including on/off state) from the original flag to the new flag. Experiment information such as iterations and metrics will not be copied over.',
  'flags.cloneFlag.loadingViews': 'Loading views',
  'flags.cloneFlag.title': 'Clone feature flag',
  'flags.codePage.title': 'Code',
  'flags.codeReferences.branchNotSynced':
    'The branch <strong>{branch}</strong> does not exist or has not been synced to LaunchDarkly for <repoName></repoName>.',
  'flags.codeReferences.collapseAll': 'Collapse all',
  'flags.codeReferences.collapseAllLabel': 'Collapse',
  'flags.codeReferences.expandAll': 'Expand all',
  'flags.codeReferences.expandAllLabel': 'Expand',
  'flags.codeReferences.expandReferenceLabel': 'Expand code reference',
  'flags.codeReferences.filterDisabledTooltip': 'This filter is only available when all repositories are displayed',
  'flags.codeReferences.increaseContextLines':
    'Show source code by increasing your lines of context. <link>Learn more</link>',
  'flags.codeReferences.lastReferenceRemoved':
    'The last reference to this flag in <repoName></repoName> was removed on <time></time> in <revision></revision>',
  'flags.codeReferences.lineLabel': '{count, plural, one {Line} other {Lines}}',
  'flags.codeReferences.loadError': 'Failed to load code references. Refresh and try again.',
  'flags.codeReferences.loadMore': 'Load more',
  'flags.codeReferences.noCodeReferences': 'No code references found in this repository for this flag.',
  'flags.codeReferences.noMatchesForRepository':
    'No code references match your filters for the repository <repoName></repoName>.',
  'flags.codeReferences.noReferences': 'No code references found for this flag.',
  'flags.codeReferences.noReferencesMatchFilters': 'No code references match your filters for this flag.',
  'flags.codeReferences.noRepositoriesConnected': 'There are no Git repositories connected to LaunchDarkly.',
  'flags.codeReferences.paginationSummary': 'Showing <count>{shown} of {total}</count> {noun}',
  'flags.codeReferences.repositoryLoadError':
    'Error retrieving code references for <repoName></repoName>. Try again later.',
  'flags.codeReferences.repositoryNotConnected':
    'The selected repository has not been connected. Clear your filters and try again.',
  'flags.codeReferences.showRepositoriesWith': 'Show repositories with no references',
  'flags.codeReferences.syncedOn': 'Synced on',
  'flags.codeReferences.tryAnotherBranch': ' Try filtering to another branch to continue.',
  'flags.codeReferences.viewInSource': 'View in source',
  'flags.codeReferences.viewInSourceLabel': 'View in source {path}',
  'flags.codeRefsPage.title': 'Code references',
  'flags.codeRemovalModal.body': 'Removing references keeps your code clean and prepares this flag for archiving.',
  'flags.codeRemovalModal.checksLoadError': 'We couldn’t load the lifecycle checks for this flag.',
  'flags.codeRemovalModal.checksLoadingLabel': 'Loading lifecycle checks…',
  'flags.codeRemovalModal.cleanUpWith': 'Clean up with Vega',
  'flags.codeRemovalModal.closeLabel': 'close modal',
  'flags.codeRemovalModal.criteriaNote': 'Change the code removal criteria in <link>lifecycle settings</link>.',
  'flags.codeRemovalModal.heading': 'Ready for cleanup',
  'flags.codeRemovalModal.showCodeReferences': 'Show code references',
  'flags.compareEnvironmentPicker.sourceEnvironment': 'Source environment',
  'flags.compareEnvironmentPicker.sourcePlaceholder': 'Select a source environment',
  'flags.compareEnvironmentPicker.swapEnvironments': 'Swap environments',
  'flags.compareEnvironmentPicker.targetEnvironment': 'Target environment',
  'flags.compareEnvironmentPicker.targetPlaceholder': 'Select a target environment',
  'flags.compareList.allFlagsDiffer': 'There are differences in all flags',
  'flags.compareList.checkForTypos': 'Check for typos.',
  'flags.compareList.legendDiffers': 'Differences exist between environments',
  'flags.compareList.legendIdentical': 'No differences between environments',
  'flags.compareList.legendTitle': 'Key:',
  'flags.compareList.nameColumn': 'Name',
  'flags.compareList.noDifferencesLead': 'Choose a different environment to compare flag settings.',
  'flags.compareList.noDifferencesTitle': 'There are no differences between these environments.',
  'flags.compareList.noFlagsTitle': 'There are no flags in this project yet.',
  'flags.compareList.noMatchTitle': 'No feature flags match. Try this:',
  'flags.compareList.searchHint': 'Search for a flag by name, key, or description.',
  'flags.compareList.settingDiffers':
    'The {setting} for {flagKey} {form, select, plural {are} other {is}} different between the {sourceEnvironmentKey} and {targetEnvironmentKey} environments.',
  'flags.compareList.settingMatches':
    'The {setting} for {flagKey} {form, select, plural {are} other {is}} the same in the {sourceEnvironmentKey} and {targetEnvironmentKey} environments.',
  'flags.compareList.someFlagsDiffer': 'There are differences in {differences} out of {total} flags',
  'flags.compareList.tagsLabel': 'tags',
  'flags.connectFlagDialog.body': 'Connect a feature flag to <strong>{issueKey}</strong>',
  'flags.connectFlagDialog.flag': 'Flag',
  'flags.connectFlagDialog.heading': 'Link a feature flag',
  'flags.connectFlagDialog.project': 'Project',
  'flags.convertToSegment.createRule': 'Create a new rule targeting this segment to <variation></variation>',
  'flags.convertToSegment.individualDescription': 'Turn this rule into a segment you can use on other flags.',
  'flags.convertToSegment.individualTitle': 'Convert individual targets to segment',
  'flags.convertToSegment.removeTargets':
    'Remove {count} {contextKind} {count, plural, one {target} other {targets}} from <variation></variation>',
  'flags.convertToSegment.ruleDescription': "Add this segment to the flag's targeting as a new rule.",
  'flags.convertToSegment.ruleTitle': 'Convert targeting rule to segment',
  'flags.convertToSegment.targetingNote': "Creating a segment does not change this flag's targeting.",
  'flags.copySettings.afterCopyColumn': 'Settings after copy from <environment>{environmentName}</environment>',
  'flags.copySettings.chooseSettings':
    'Choose settings to copy to <environment>{environmentName}</environment>. The settings you select will be overwritten.',
  'flags.copySettings.commentPlaceholder': 'Leave a comment',
  'flags.copySettings.commentRequiredPlaceholder': 'Comments are required for changes in this environment',
  'flags.copySettings.confirmTitle': 'Update {flagName}?',
  'flags.copySettings.copiedHeading': 'Flag settings copied',
  'flags.copySettings.copySettings': 'Copy settings',
  'flags.copySettings.currentSettingsColumn': 'Current settings in <environment>{environmentName}</environment>',
  'flags.copySettings.experimentAlertBody':
    'A running experiment is using these variations, so you cannot delete variations or change their values.',
  'flags.copySettings.experimentAlertHeading': 'Changes to this flag may affect a running experiment',
  'flags.copySettings.experimentRunningTooltip': 'Cannot copy while running an experiment',
  'flags.copySettings.heading': '{flagName} settings',
  'flags.copySettings.invalidSegmentAlert':
    'Your targeting rules cannot be copied because the segment <strong>{segment}</strong> does not exist in your {environmentName} environment. View your <link>segments</link> to update.',
  'flags.copySettings.invalidSegmentsAlert':
    'Your targeting rules cannot be copied because the segments <strong>{head}</strong> and <strong>{last}</strong> do not exist in your {environmentName} environment. View your <link>segments</link> to update.',
  'flags.copySettings.noFieldsTooltip': 'No fields available for selection',
  'flags.copySettings.overwriteWarning':
    'This will overwrite the following flag {count, plural, one {setting} other {settings}} in your <environment>{environmentName}</environment> environment',
  'flags.copySettings.saveChanges': 'Save changes',
  'flags.copySettings.selectAll': 'Select all',
  'flags.copySettings.selectSettingsTooltip': 'Select one or more settings to copy',
  'flags.copySettings.upgradeRequired': 'Upgrade your plan to use this feature.',
  'flags.copySettings.viewFlagIn': 'View flag in <environment>{environmentName}</environment>',
  'flags.copySettings.viewFlagSettings': 'View flag settings',
  'flags.copySettings.viewOptionsLabel': '{flagName} view options',
  'flags.createButton.createFlag': 'Create flag',
  'flags.createButton.createFlagLabel': 'Create flag',
  'flags.createButton.fromTemplateLabel': 'Create flag from template',
  'flags.createButton.noPermissionTooltip':
    'You do not have permission to create flags. Contact your administrator for more information.',
  'flags.createDialog.closeLabel': 'close',
  'flags.createDialog.title': 'Create flag',
  'flags.createForm.addJiraIssue': 'Add Jira issue',
  'flags.createForm.addJsonSchema': 'Add JSON schema validation',
  'flags.createForm.addMaintainer': 'Add maintainer',
  'flags.createForm.addPrerequisite': 'Add prerequisite',
  'flags.createForm.addSchemaLabel': 'Add schema',
  'flags.createForm.addTag': 'Add tag',
  'flags.createForm.addViews': 'Add views',
  'flags.createForm.atLeastTwoVariations': 'At least 2 variations are required',
  'flags.createForm.clientSideSdks': 'Client-side SDKs',
  'flags.createForm.clientSideSdksLabel': 'Available on client-side SDKs',
  'flags.createForm.contextKindFallbackLabel': 'Context Kind',
  'flags.createForm.contextKindLabel': 'Context kind',
  'flags.createForm.contextKindRequired': 'Context kind is required for 6-stage migrations',
  'flags.createForm.create': 'Create',
  'flags.createForm.descriptionLabel': 'Description',
  'flags.createForm.descriptionTooLong': 'Description must be fewer than {max} characters',
  'flags.createForm.discardBody': 'You will lose your changes to this flag.',
  'flags.createForm.discardCancel': 'Keep editing',
  'flags.createForm.discardConfirm': 'Discard',
  'flags.createForm.discardTitle': 'Discard your changes',
  'flags.createForm.flagDurationLabel': 'Flag duration',
  'flags.createForm.flagDurationTooltip': 'Flag duration: {duration}',
  'flags.createForm.jiraIssuePlaceholder': 'Enter Jira issue key (e.g., REL-9450)',
  'flags.createForm.keyExists': 'A flag already exists with this key.',
  'flags.createForm.keyImmutableHint': 'You cannot change a key after creation.',
  'flags.createForm.keyInvalid': 'Flag keys can only contain letters, numbers, periods, hyphens, and underscores.',
  'flags.createForm.keyInvalidFormat': 'Invalid key format',
  'flags.createForm.keyInvalidGeneric': 'Invalid key',
  'flags.createForm.keyLabel': 'Key',
  'flags.createForm.keyPlaceholderExample': 'my feature',
  'flags.createForm.keyRequired': 'Enter a flag key.',
  'flags.createForm.keyReservedNew': 'You cannot use "new" as a key',
  'flags.createForm.keyTooLong': 'Key must be fewer than {max} characters',
  'flags.createForm.keyUnavailable': 'A flag already exists with this key. Choose a different key.',
  'flags.createForm.loadingContextKinds': 'Loading context kinds…',
  'flags.createForm.loadingProjects': 'Loading projects…',
  'flags.createForm.maintainersLoadError': 'Unable to load maintainers',
  'flags.createForm.mobileSdks': 'Mobile SDKs',
  'flags.createForm.mobileSdksLabel': 'Available on mobile SDKs',
  'flags.createForm.nameInvalidGeneric': 'Invalid name',
  'flags.createForm.nameLabel': 'Name',
  'flags.createForm.nameRequired': 'Enter a flag name.',
  'flags.createForm.nameTooLong': 'Name must be fewer than {max} characters',
  'flags.createForm.permanent': 'Permanent',
  'flags.createForm.permanentDescription': 'Long-term feature control',
  'flags.createForm.prerequisite': 'Prerequisite',
  'flags.createForm.prerequisites': 'Prerequisites',
  'flags.createForm.projectLabel': 'Project',
  'flags.createForm.schema': 'Schema',
  'flags.createForm.sdkAvailability': 'SDK availability',
  'flags.createForm.sdkAvailabilityAll': 'All SDKs',
  'flags.createForm.sdkAvailabilityServerOnly': 'Server only',
  'flags.createForm.sdkAvailabilitySome': 'Some SDKs',
  'flags.createForm.selectAtLeastOneView': 'Select at least one view',
  'flags.createForm.selectTeam': 'Select a team',
  'flags.createForm.serverSdks': 'Server SDKs',
  'flags.createForm.serverSdksDescription': 'Always available',
  'flags.createForm.serverSdksLabel': 'Available on server SDKs',
  'flags.createForm.submitError': 'An unexpected error has occurred while attempting to submit the flag. Try again',
  'flags.createForm.tagCount': '{count} tags',
  'flags.createForm.tagPlaceholder': 'Enter tag (e.g., frontend, experimental)',
  'flags.createForm.teamMaintainerIsRequired': 'Team maintainer is required',
  'flags.createForm.teamMaintainerLabel': 'Team maintainer',
  'flags.createForm.teamMaintainerRequired': 'Team maintainer required',
  'flags.createForm.templateInvalid': 'Invalid template',
  'flags.createForm.temporary': 'Temporary',
  'flags.createForm.temporaryDescription': 'Short-lived flag that will be removed',
  'flags.createForm.validationFailed': 'Validation failed',
  'flags.createForm.valueMustBeJson': 'This must be valid JSON',
  'flags.createForm.valueMustBeNumber': 'This must be a number',
  'flags.createForm.valueMustBeString': 'This must be a string',
  'flags.createForm.valueRequired': 'Value is required',
  'flags.createForm.variationTypeBoolean': 'Boolean',
  'flags.createForm.variationTypeBooleanDescription': 'true or false values',
  'flags.createForm.variationTypeInvalid': 'Invalid variation type',
  'flags.createForm.variationTypeJson': 'JSON',
  'flags.createForm.variationTypeJsonDescription': 'structured json objects',
  'flags.createForm.variationTypeNumber': 'Number',
  'flags.createForm.variationTypeNumberDescription': 'numeric values',
  'flags.createForm.variationTypeString': 'String',
  'flags.createForm.variationTypeStringDescription': 'text based values',
  'flags.createForm.variationValuesUnique': 'Variation values must be unique',
  'flags.createForm.variationsLabel': 'Variations',
  'flags.createForm.viewCount': '{count} views',
  'flags.createForm.viewRequired': 'At least one view is required',
  'flags.createForm.views': 'Views',
  'flags.createReleaseDialog.body':
    'LaunchDarkly releases this flag to the environments listed below in order, following each environment’s release plan.',
  'flags.createReleaseDialog.bodyReleasePolicy':
    'LaunchDarkly releases this flag to the environments listed below in order, following each environment’s release policy.',
  'flags.createReleaseDialog.closeLabel': 'Close',
  'flags.createReleaseDialog.customRelease': 'Custom release',
  'flags.createReleaseDialog.customize': 'Customize',
  'flags.createReleaseDialog.endingVariationLabel': 'Target variation',
  'flags.createReleaseDialog.flagAlreadyOn':
    'The flag is already on in this environment, so it may already be released.',
  'flags.createReleaseDialog.flagEvalWarning':
    'This release starts automatically when LaunchDarkly detects evaluations of this flag, whether or not your code is ready to release.',
  'flags.createReleaseDialog.flagEvalWarningPerEnvironment':
    'Environments set to On flag evaluations start automatically when LaunchDarkly detects evaluations, whether or not your code is ready to release.',
  'flags.createReleaseDialog.followsReleasePlan': 'Follows release plan',
  'flags.createReleaseDialog.followsReleasePolicy': 'Follows release policy',
  'flags.createReleaseDialog.heading': 'Create flag release',
  'flags.createReleaseDialog.loadingSuggestedRolloutLabel': 'Loading suggested release',
  'flags.createReleaseDialog.metricCount': '{count, plural, one {# metric} other {# metrics}}',
  'flags.createReleaseDialog.noDefaultConfig':
    'This project doesn’t have a default release configuration, so there’s no suggested release. Select <b>Customize</b> to set one up.',
  'flags.createReleaseDialog.noDefaultConfigWithoutCustomize':
    'This project doesn’t have a default release configuration, so there’s no suggested release.',
  'flags.createReleaseDialog.phaseCount': '{count, plural, one {# phase} other {# phases}}',
  'flags.createReleaseDialog.removeEnvironmentLabel': 'Remove {environment} from release',
  'flags.createReleaseDialog.removeEnvironmentTooltip': 'Remove environment from release',
  'flags.createReleaseDialog.servesVariation': 'Serves {variation} to all contexts',
  'flags.createReleaseDialog.startRelease': 'Create release',
  'flags.createReleaseDialog.startingVariationLabel': 'Original variation',
  'flags.createReleaseDialog.triggerFlagEval': 'On flag evaluations',
  'flags.createReleaseDialog.triggerFlagEvalDescription':
    'After {environment} finishes, starts on the first flag evaluation in this environment',
  'flags.createReleaseDialog.triggerFlagEvalFirstDescription':
    'Starts on the first flag evaluation in this environment',
  'flags.createReleaseDialog.triggerLabel': 'Release trigger for {environment}',
  'flags.createReleaseDialog.triggerManual': 'Manual start',
  'flags.createReleaseDialog.triggerManualDescription': 'After {environment} finishes, waits for someone to start it',
  'flags.createReleaseDialog.triggerManualFirstDescription': 'Waits for someone to start it',
  'flags.createReleaseDialog.triggerPromotion': 'After {environment}',
  'flags.createReleaseDialog.triggerPromotionDescription': 'Starts as soon as {environment} finishes',
  'flags.createReleaseDialog.variationsHelpText':
    'This flag is not a boolean flag, so choose which variation it releases from and which variation it releases to.',
  'flags.createReleaseDialog.variationsMustDiffer': 'Choose two different variations.',
  'flags.creationDateFilter.anytime': 'Anytime',
  'flags.creationDateFilter.label': 'Created',
  'flags.creationDateFilter.moreThanOneMonthAgo': 'More than 1 month ago',
  'flags.creationDateFilter.moreThanOneWeekAgo': 'More than 1 week ago',
  'flags.creationDateFilter.moreThanOneYearAgo': 'More than 1 year ago',
  'flags.creationDateFilter.moreThanSixMonthsAgo': 'More than 6 months ago',
  'flags.creationDateFilter.moreThanThreeMonthsAgo': 'More than 3 months ago',
  'flags.creationDateFilter.moreThanTwoMonthsAgo': 'More than 2 months ago',
  'flags.creationDateFilter.oneMonthAgo': '1 month ago',
  'flags.creationDateFilter.oneWeekAgo': '1 week ago',
  'flags.creationDateFilter.oneYearAgo': '1 year ago',
  'flags.creationDateFilter.sixMonthsAgo': '6 months ago',
  'flags.creationDateFilter.threeMonthsAgo': '3 months ago',
  'flags.creationDateFilter.twoMonthsAgo': '2 months ago',
  'flags.criticalEnvironments.checksRunInCritical': 'These checks run in your critical environments.',
  'flags.criticalEnvironments.checksRunInCriticalIntro': 'These checks run in your critical environments:',
  'flags.criticalEnvironments.manageEnvironments': 'Manage environments',
  'flags.criticalEnvironments.more': 'more',
  'flags.criticalEnvironments.moreCount': '{count} more',
  'flags.criticalEnvironments.moreTextValue': 'More',
  'flags.criticalEnvironments.noneHeading': 'No critical environments to check',
  'flags.criticalEnvironments.showAllLabel': 'Show all critical environments',
  'flags.criticalEnvironments.tagGroupLabel': 'Critical environments',
  'flags.customProperties.body':
    'Custom properties are used to associate feature flags with LaunchDarkly integrations. See our <docsLink></docsLink> for more details.',
  'flags.customProperties.builtInIntegration': 'This property is used for a built-in integration.',
  'flags.customProperties.customProperties': 'Custom properties',
  'flags.customProperties.deleteTooltip': 'Delete custom property',
  'flags.customProperties.docsLinkText': 'documentation',
  'flags.customProperties.key': 'Key',
  'flags.customProperties.name': 'Name',
  'flags.customProperties.typeHeading': 'Select a property type',
  'flags.customProperties.typePlaceholder': 'Select a property type…',
  'flags.customProperties.values': 'Values',
  'flags.customPropertiesDialog.addValue': 'Add value',
  'flags.customPropertiesDialog.addValueShortcut': 'Shift+Enter',
  'flags.customPropertiesDialog.body':
    'Custom properties are used to associate feature flags with LaunchDarkly integrations. See our <docsLink></docsLink> for more details.',
  'flags.customPropertiesDialog.closeLabel': 'Close',
  'flags.customPropertiesDialog.commentLabel': 'Comment',
  'flags.customPropertiesDialog.createNew': 'Create new: {value}',
  'flags.customPropertiesDialog.deleteValueLabel': 'Delete value',
  'flags.customPropertiesDialog.docsLinkText': 'documentation',
  'flags.customPropertiesDialog.duplicateValues': 'All items must be unique, no duplicate values allowed',
  'flags.customPropertiesDialog.keyLabel': 'Key',
  'flags.customPropertiesDialog.keyNotUnique': 'Custom property key must be unique',
  'flags.customPropertiesDialog.keyRequired': 'Key is required',
  'flags.customPropertiesDialog.keyTooLong': 'Key must be less than 64 characters',
  'flags.customPropertiesDialog.nameLabel': 'Name',
  'flags.customPropertiesDialog.nameRequired': 'Name is required',
  'flags.customPropertiesDialog.nameTooLong': 'Name must be less than 64 characters',
  'flags.customPropertiesDialog.save': 'Save',
  'flags.customPropertiesDialog.tooManyValues': 'There must be less than 64 values',
  'flags.customPropertiesDialog.typeFieldLabel': 'Type',
  'flags.customPropertiesDialog.typeInfoLabel': '{type} info',
  'flags.customPropertiesDialog.typeLabel': 'Custom property type',
  'flags.customPropertiesDialog.undo': 'Undo',
  'flags.customPropertiesDialog.valueRequired': 'Value is required',
  'flags.customPropertiesDialog.valueTooLong': 'Value must be less than 65536 characters',
  'flags.customPropertiesDialog.values': 'Values',
  'flags.customPropertiesDialog.valuesRequired': 'Values are required',
  'flags.customPropertiesForm.duplicateKey': "You can't create custom properties with the same key",
  'flags.customPropertyEntity.editLabel': 'Edit {name}',
  'flags.customPropertyEntity.valuesLabel': '{name} values',
  'flags.defaultVariations.kindPickerLabel': '{kind, select, on {Default on variation} other {Default off variation}}',
  'flags.defaultVariations.notSet': 'Not set',
  'flags.defaultVariations.offVariationHeading': 'Variation to serve when off',
  'flags.defaultVariations.offVariationPickerLabel': 'Variation to serve when off',
  'flags.defaultVariations.onVariationHeading': 'Variation to serve when on',
  'flags.defaultVariations.onVariationPickerLabel': 'Variation to serve when on',
  'flags.defaultVariations.serve': 'Serve',
  'flags.defaultVariations.whenOff': 'when the flag is off, and',
  'flags.defaultVariations.whenOn': 'when the flag is on.',
  'flags.deleteFlagButton.confirmBody':
    'This will delete the <strong>{name}</strong> flag from all environments. Your rules will be deleted, and feature flag value defined in code will be returned for all your users. You should remove any references to the feature flag from your application code before deleting it here.',
  'flags.deleteFlagButton.confirmLabel': 'Delete',
  'flags.deleteFlagButton.confirmTitle': 'Delete this flag?',
  'flags.deleteFlagButton.delete': 'Delete',
  'flags.deleteFlagLinkDialog.body': 'will be permanently deleted from all environments.',
  'flags.deleteFlagLinkDialog.cancel': 'Cancel',
  'flags.deleteFlagLinkDialog.delete': 'Delete',
  'flags.deleteFlagLinkDialog.heading': 'Delete link to flag',
  'flags.dependentFlags.archived': 'Archived',
  'flags.dependentFlags.archivedColumn': 'Archived status',
  'flags.dependentFlags.archivedInline': '(Archived)',
  'flags.dependentFlags.environmentsColumn': 'Environments',
  'flags.dependentFlags.flagColumn': 'Flag',
  'flags.dependentFlags.loadError': 'Error loading flag dependencies',
  'flags.dependentFlags.loading': 'Loading flag dependencies…',
  'flags.dependentFlags.statusLabel': 'status',
  'flags.dependentFlags.tableLabel': 'Dependent flags',
  'flags.dependentFlags.targetingImpact': 'Changes to targeting may impact the variations served by these flags',
  'flags.deprecateFlag.successToastDescription': 'Use the view switcher to find deprecated flags in the table.',
  'flags.deprecateFlag.successToastTitle': 'Flag deprecated',
  'flags.deprecateFlag.viewAllAction': 'View all deprecated flags',
  'flags.deprecateFlagModal.cancel': 'Cancel',
  'flags.deprecateFlagModal.deprecate': 'Deprecate',
  'flags.deprecateFlagModal.environmentSearchPlaceholder': 'Search for environments',
  'flags.deprecateFlagModal.evaluationsNote': "Here's how your flag was evaluated in the past 7 days",
  'flags.deprecateFlagModal.lastEvaluatedLabel': 'Last evaluated',
  'flags.deprecateFlagModal.neverEvaluated': 'Never',
  'flags.deprecateFlagModal.noEnvironmentsSelected': 'No environments selected',
  'flags.deprecateFlagModal.reasonLabel': 'Reason (required)',
  'flags.deprecateFlagModal.reasonPlaceholder':
    'Let others know why this flag was {action, select, restored {restored} other {deprecated}}',
  'flags.deprecateFlagModal.reasonRequired': 'Provide a reason.',
  'flags.deprecateFlagModal.removeEnvironmentLabel': 'Remove environment {name}',
  'flags.deprecateFlagModal.requestsLabel': 'Requests in past 7 days',
  'flags.deprecateFlagModal.restore': 'Restore',
  'flags.deprecateFlagModal.servingLabel': 'Serving',
  'flags.deprecateFlagModal.statusLabel': 'status',
  'flags.deprecateFlagModal.targetingLabel': 'Targeting',
  'flags.deprecateFlagModal.targetingOff': 'Off',
  'flags.deprecateFlagModal.targetingOn': 'On',
  'flags.deprecatedFlagBanner.archiveLink': 'Archive this flag',
  'flags.deprecatedFlagBanner.deprecatedOnPrefix': 'This flag was deprecated on',
  'flags.deprecatedFlagBanner.explanation':
    'Deprecated flags can still be evaluated but do not show up by default in the flag list.',
  'flags.detailPage.audience': 'Audience',
  'flags.detailPage.backToAll': 'Back to all flags',
  'flags.detailPage.code': 'Code',
  'flags.detailPage.feedback': 'Feedback',
  'flags.detailPage.monitoring': 'Monitoring',
  'flags.detailPage.overview': 'Release',
  'flags.detailPage.targeting': 'Targeting',
  'flags.detailPage.title': 'Advanced controls',
  'flags.detailPage.title2': 'Environment not found',
  'flags.detailPage.variations': 'Variations',
  'flags.emptyState.body':
    'Feature flags give you control over how and when changes reach users. Roll out gradually, limit blast radius, and roll back instantly without waiting on deploys.',
  'flags.emptyState.createFlag': 'Create flag',
  'flags.emptyState.exploreExample': 'Explore example',
  'flags.emptyState.heading': 'Release code safely in production',
  'flags.emptyState.learnMore': 'Read about feature flags',
  'flags.entityKey.copiedToast': 'Copied {value}',
  'flags.entityKey.copyLabel': 'Copy {value} to clipboard',
  'flags.environmentFilters.clear': 'Clear',
  'flags.environmentFilters.heading': 'Filter flags by…',
  'flags.environmentFilters.triggerLabel': 'Environment filters',
  'flags.environmentFilters.triggerTooltip': 'Filter environment',
  'flags.environmentsPage.bucketingSalt': 'Bucketing salt',
  'flags.environmentsPage.comment': 'Comment',
  'flags.environmentsPage.confirm': 'Confirm',
  'flags.environmentsPage.confirmationIsRequired': 'Confirmation is required for changes in this environment',
  'flags.environmentsPage.consistencyCheckRatio': 'Consistency check ratio',
  'flags.environmentsPage.dataExport': 'Data Export',
  'flags.environmentsPage.dataExportDisabled': 'Data Export disabled',
  'flags.environmentsPage.exportingEventsFor': 'Exporting events for experiment rule.',
  'flags.environmentsPage.exportingEventsFrom':
    'Exporting events from <link>{active} of {total} targeting rules</link>.',
  'flags.environmentsPage.flagBucketingSalt': 'Flag bucketing salt',
  'flags.environmentsPage.flagEventsAre':
    "Flag events are summarized by default. Enable Data Export for all flags in an environment in the environment's Settings.",
  'flags.environmentsPage.generateRandomSalt': 'Generate random salt',
  'flags.environmentsPage.loading': 'Loading…',
  'flags.environmentsPage.migrationSettings': 'Migration settings',
  'flags.environmentsPage.oneOutOf':
    'One out of every n invocations is randomly chosen to be a representative sample for consistency checks. Higher ratio values mean relatively fewer samples are taken and each sample makes a larger statistical contribution. To learn more,',
  'flags.environmentsPage.placeholder': 'Describe your change',
  'flags.environmentsPage.saveBucketingSalt': 'Save bucketing salt',
  'flags.environmentsPage.saveSetting': 'Save setting',
  'flags.environmentsPage.saveSettings': 'Save settings',
  'flags.environmentsPage.sendDetailedEvents': 'Send detailed events to Data Export destinations',
  'flags.environmentsPage.sendsAllInformation':
    'LaunchDarkly sends all information by default. <strong>Save changes</strong> to choose a smaller group.',
  'flags.environmentsPage.text': 'read documentation on migration settings',
  'flags.environmentsPage.theSaltIs': 'The salt is used for bucketing users in percentage rollouts.',
  'flags.environmentsPage.title': 'Environment configuration',
  'flags.evaluatedApplicationFilter.anyApplication': 'Any application',
  'flags.evaluatedApplicationFilter.choose': 'Choose application',
  'flags.evaluatedApplicationFilter.dialogTitle': 'Choose application',
  'flags.evaluatedApplicationFilter.label': 'Evaluated application',
  'flags.evaluatedApplicationFilter.listLabel': 'Applications',
  'flags.evaluatedApplicationFilter.noItemsFound': 'No items found',
  'flags.evaluationDateFilter.anytime': 'Anytime',
  'flags.evaluationDateFilter.inTheLast14Days': 'In the last 14 days',
  'flags.evaluationDateFilter.inTheLast24Hours': 'In the last 24 hours',
  'flags.evaluationDateFilter.inTheLast30Days': 'In the last 30 days',
  'flags.evaluationDateFilter.inTheLast60Days': 'In the last 60 days',
  'flags.evaluationDateFilter.inTheLast60Minutes': 'In the last 60 minutes',
  'flags.evaluationDateFilter.inTheLast7Days': 'In the last 7 days',
  'flags.evaluationDateFilter.label': 'Evaluated',
  'flags.evaluationDateFilter.last14Days': 'Last 14 days',
  'flags.evaluationDateFilter.last24Hours': 'Last 24 hours',
  'flags.evaluationDateFilter.last30Days': 'Last 30 days',
  'flags.evaluationDateFilter.last60Days': 'Last 60 days',
  'flags.evaluationDateFilter.last60Minutes': 'Last 60 minutes',
  'flags.evaluationDateFilter.last7Days': 'Last 7 days',
  'flags.eventSettings.affectsFlag': 'These settings affect the <name>{flagName}</name> flag in',
  'flags.eventSettings.chooseRules':
    'Choose targeting rules to determine which users are included in {destinations}. To learn more',
  'flags.eventSettings.heading': 'Configure event settings',
  'flags.eventSettings.loadError':
    'Something went wrong while loading event settings. Close this panel to return to targeting, then try again.',
  'flags.eventSettingsForm.allTargetingRules': 'All targeting rules',
  'flags.eventSettingsForm.customSelectionDoes': 'Custom selection (does not include individual targets)',
  'flags.eventSettingsForm.customSelectionTooltip':
    'Select <strong>Custom selection</strong> to change which targeting rules LaunchDarkly uses in experiments and data export destinations',
  'flags.eventSettingsForm.defaultRule': 'Default rule',
  'flags.eventSettingsForm.individualTargets': 'Individual targets',
  'flags.eventSettingsForm.noIndividualTargets': 'No individual targets',
  'flags.eventSettingsForm.noIndividualTargetsTooltip': 'There are no individual targets.',
  'flags.eventSettingsForm.noRulesDefined': 'No rules defined',
  'flags.eventSettingsForm.noTargetingRulesTooltip': 'Create a custom targeting rule to enable this field.',
  'flags.eventSettingsForm.noUsersTargeted': 'No users targeted for this variation',
  'flags.eventSettingsForm.savingLabel': 'Saving settings',
  'flags.eventSettingsForm.selectAllRulesTooltip':
    'Select <strong>All targeting rules</strong> to send information from individual users',
  'flags.eventSettingsForm.targetCount': '{count, plural, one {# user} other {# users}}',
  'flags.eventSettingsForm.targetingRule': 'Targeting rule',
  'flags.eventSettingsForm.targetsLabel': 'Targets:',
  'flags.eventSettingsSummary.edit': 'Edit',
  'flags.eventSettingsSummary.editDisabledTooltip':
    "Save or discard changes to the flag's targeting rules before editing",
  'flags.eventSettingsSummary.includedIn': 'Included in {destinations}.',
  'flags.expiringTargetCard.editExpirationLabel': 'Edit expiration date',
  'flags.expiringTargetCard.scheduledForRemoval': 'Scheduled for removal on',
  'flags.expiringTargetCard.variationLabel': 'Variation',
  'flags.expiringTargetDetail.notFoundTitle': 'Target not found',
  'flags.expiringTargetsDrawer.emptyTitle': 'No expiring targets',
  'flags.expiringTargetsDrawer.heading': 'Expiring targets',
  'flags.expiryField.customDate': 'Custom date',
  'flags.expiryField.customDateLabel': 'Custom expiration date',
  'flags.expiryField.heading': 'Expiration',
  'flags.expiryField.nextMonthLabel': 'Next month',
  'flags.expiryField.noExpiration': 'No expiration',
  'flags.expiryField.previousMonthLabel': 'Previous month',
  'flags.filters.activeCountLabel': '{count} active {count, plural, one {filter} other {filters}}',
  'flags.filters.clear': 'Clear',
  'flags.filters.heading': 'Filter flags by…',
  'flags.filters.loadError': 'Something went wrong while loading filters.',
  'flags.filters.loadErrorNotified': "We've been notified.",
  'flags.filters.trigger': 'Filters',
  'flags.filters.tryAgain': 'Try again',
  'flags.flagAcrossEnvironments.allPinned': 'All environments are pinned.',
  'flags.flagAcrossEnvironments.defaultVariationColumn': 'Default variation',
  'flags.flagAcrossEnvironments.description':
    'Review information for <strong>{flagName}</strong> across all environments.',
  'flags.flagAcrossEnvironments.evaluatedLabel': 'Evaluated',
  'flags.flagAcrossEnvironments.heading': 'Overview across environments',
  'flags.flagAcrossEnvironments.offVariationColumn': 'Off variation',
  'flags.flagAcrossEnvironments.pinHint': 'Pin environments for easier comparison',
  'flags.flagAcrossEnvironments.pinPlaceholder': 'Select environments to pin',
  'flags.flagAcrossEnvironments.targetingColumn': 'Targeting',
  'flags.flagAcrossEnvironments.variationsServedColumn': 'Variations served',
  'flags.flagAdvancedControls.changesHeading': 'Changes:',
  'flags.flagAdvancedControls.clientSdkAvailability': 'Available on client-side SDKs',
  'flags.flagAdvancedControls.clientSdkChange':
    'Client-side SDK availability: <strong>{state, select, enabled {Enabled} other {Disabled}}</strong>',
  'flags.flagAdvancedControls.clientSdkNoPermission':
    "You don't have permission to update client-side SDK availability for this flag",
  'flags.flagAdvancedControls.commentOptionalPlaceholder':
    'Add an optional comment about these changes. These changes affect all environments in this project.',
  'flags.flagAdvancedControls.commentRequiredPlaceholder':
    'Comments are required for changes in one or more environments. These changes affect all environments in this project.',
  'flags.flagAdvancedControls.confirmBody':
    'This will make changes to the {flagName} feature flag in all environments.',
  'flags.flagAdvancedControls.confirmLabel': 'Confirm',
  'flags.flagAdvancedControls.confirmTitle': 'Confirmation required',
  'flags.flagAdvancedControls.flagStatusChange':
    'Flag status: <strong>{state, select, temporary {Temporary} other {Permanent}}</strong>',
  'flags.flagAdvancedControls.mobileSdkAvailability': 'Available on mobile SDKs',
  'flags.flagAdvancedControls.mobileSdkChange':
    'Mobile SDK availability: <strong>{state, select, enabled {Enabled} other {Disabled}}</strong>',
  'flags.flagAdvancedControls.mobileSdkNoPermission':
    "You don't have permission to update mobile SDK availability for this flag",
  'flags.flagAdvancedControls.permanentFlag': 'Permanent flag',
  'flags.flagAudience.emptyBody':
    "We haven't received any evaluations from client-side SDKs. Support for additional SDKs is coming soon.",
  'flags.flagAudience.emptyHeading': 'No data yet',
  'flags.flagAudience.loadError': 'Failed to load audience data. Try again later.',
  'flags.flagAudience.loadErrorTitle': 'Failed to load data',
  'flags.flagAudience.searchPlaceholder': 'Search fields by key or name',
  'flags.flagBreadcrumb.commentOptionalPlaceholder':
    'Add an optional comment about these changes. These changes affect all environments in this project.',
  'flags.flagBreadcrumb.commentRequiredPlaceholder':
    'Comments are required for changes in one or more environments. These changes affect all environments in this project.',
  'flags.flagBreadcrumb.confirmBody':
    'Change flag name from <strong>"{oldValue}"</strong> to <strong>"{newValue}"</strong>?',
  'flags.flagBreadcrumb.confirmTitle': 'Confirm flag name change',
  'flags.flagCleanup.cleanUp': 'Clean up',
  'flags.flagCleanup.generatingPr': 'Generating PR',
  'flags.flagCleanup.generatingPrLabel': 'Generating PR…',
  'flags.flagCleanup.removeReferenceLabel': 'Remove flag reference',
  'flags.flagCleanup.viewCleanupRequest': 'View cleanup request',
  'flags.flagDescription.addDescription': 'Add description',
  'flags.flagDescription.commentOptionalPlaceholder':
    'Add an optional comment about these changes. These changes affect all environments in this project.',
  'flags.flagDescription.commentRequiredPlaceholder':
    'Comments are required for changes in one or more environments. These changes affect all environments in this project.',
  'flags.flagDescription.commentTitle': 'Leave a comment',
  'flags.flagDescription.confirm': 'Confirm',
  'flags.flagDescription.confirmBody':
    'This will make changes to the <strong>{flagName}</strong> flag in all environments.',
  'flags.flagDescription.confirmTitle': 'Confirmation required',
  'flags.flagDescription.emptyText': 'None',
  'flags.flagDescription.heading': 'Description',
  'flags.flagDescription.inputLabel': 'Flag description',
  'flags.flagDescription.placeholder': 'Add a description for this flag',
  'flags.flagDescription.saveChanges': 'Save changes',
  'flags.flagEnvironmentCard.evaluationBreakdownLabel': 'Evaluation breakdown by variation',
  'flags.flagEnvironmentCard.evaluationsInLast': 'Evaluations in last 7 days',
  'flags.flagEnvironmentCard.failedToLoad': 'failed to load',
  'flags.flagEnvironmentCard.statusesLabel': 'Flag statuses',
  'flags.flagEnvironmentDetails.dialogLabel': 'Environment details for {flagKey}',
  'flags.flagEnvironmentDetails.empty': 'No per-environment details available.',
  'flags.flagEnvironmentDetails.enabledColumn': 'Enabled',
  'flags.flagEnvironmentDetails.environmentColumn': 'Environment',
  'flags.flagEnvironmentDetails.fullyRolledOutColumn': 'Fully rolled out',
  'flags.flagEnvironmentDetails.lastEvaluatedColumn': 'Last evaluated',
  'flags.flagEnvironmentDetails.lastModifiedColumn': 'Last modified',
  'flags.flagEnvironmentDetails.loadError': 'Failed to load per-environment details.',
  'flags.flagEnvironmentDetails.loadingLabel': 'Loading details for {flagKey}…',
  'flags.flagEnvironmentDetails.no': 'No',
  'flags.flagEnvironmentDetails.openLabel': 'Show environment details for {flagKey}',
  'flags.flagEnvironmentDetails.servingVariationsColumn': 'Serving variations',
  'flags.flagEnvironmentDetails.statusColumn': 'Status',
  'flags.flagEnvironmentDetails.yes': 'Yes',
  'flags.flagErrorBoundary.genericError': 'Something went wrong',
  'flags.flagErrorBoundary.notFoundError': 'Flag not found. Refresh the page.',
  'flags.flagErrorBoundary.tryAgain': 'Try again',
  'flags.flagEvalsMiniChart.viewDetails': 'View full details on the Monitoring tab',
  'flags.flagEvaluations.activeVariations': 'Active variations',
  'flags.flagEvaluations.breakdownLabel': 'Evaluation breakdown by variation',
  'flags.flagEvaluations.collapseLabel': 'Collapse',
  'flags.flagEvaluations.emptyState': 'No recent requests',
  'flags.flagEvaluations.expandLabel': 'Expand',
  'flags.flagEvaluations.heading': 'Evaluations',
  'flags.flagEvaluations.inactiveVariations': 'Inactive variations',
  'flags.flagEvaluations.openSectionLabel': 'open section',
  'flags.flagEvaluations.refreshLabel': 'Refresh',
  'flags.flagEvaluations.statusLabel': 'Flag status',
  'flags.flagEvaluations.toggleSectionLabel': '{state, select, open {open} other {close}} section',
  'flags.flagEvaluations.tooltipFlagChanges': 'Flag changes',
  'flags.flagEvaluations.tooltipHeading': 'Flag evaluations',
  'flags.flagEvaluations.totalEvaluationsLabel': 'Total evaluations: {total}',
  'flags.flagEvaluations.unknownVariation': 'Unknown variation',
  'flags.flagEvaluations.variationsNotShown': '{count} variations not shown.',
  'flags.flagEvaluations.viewDetails': 'View details',
  'flags.flagLinkPopover.createdBy': 'Created by <strong>{creator}</strong>',
  'flags.flagLinkPopover.inRepo': 'in {repo}',
  'flags.flagLinkPopover.linkedBy': 'Linked by <strong>{creator}</strong>',
  'flags.flagLinkPopover.openedBy': 'Opened by <strong>{author}</strong>',
  'flags.flagLinkPopover.postedIn': 'Posted in <strong>#{channel}</strong>',
  'flags.flagLinkPopover.viewIn': 'View in {platform}',
  'flags.flagLinks.addLink': 'Add link',
  'flags.flagLinks.addLinkLabel': 'Add link',
  'flags.flagLinks.avatarAlt': 'The avatar',
  'flags.flagLinks.deleteLinkLabel': 'Delete link',
  'flags.flagLinks.emptyStateImageAlt': 'Shows an example flag link for a Jira issue and a Slack conversation.',
  'flags.flagLinks.emptyStateLead':
    'Shared links to this flag are automatically tracked here for compatible integrations. You can also track references from any URL by adding a link manually.',
  'flags.flagLinks.emptyStateTitle': 'Keep track of where this feature flag is shared',
  'flags.flagLinks.emptyText': 'None',
  'flags.flagLinks.heading': 'Links',
  'flags.flagLinks.iconAlt': 'The icon',
  'flags.flagLinks.imageAlt': 'The image',
  'flags.flagLinks.listLabel': 'Flag links',
  'flags.flagLinks.loadError': 'Failed to load links',
  'flags.flagLinks.manuallyAddedBy': 'Manually added by {memberName}',
  'flags.flagLinks.noCustomLinksBody': 'You can track references from any URL by adding a custom link.',
  'flags.flagLinks.noCustomLinksHeading': 'No one has added a custom link to this flag.',
  'flags.flagLinks.noPermissionTooltip': "Your permissions don't allow you to perform this action",
  'flags.flagLinks.noResultsTipFilters': 'Modify your filter selection.',
  'flags.flagLinks.noResultsTipSearch': 'Search for a link by integration name, URL, author or metadata.',
  'flags.flagLinks.noResultsTipTypos': 'Check for typos.',
  'flags.flagLinks.noResultsTitle': 'No links found with "{searchText}". Try this:',
  'flags.flagLinks.searchLabel': 'Search for a flag link by keywords',
  'flags.flagLinks.searchPlaceholder': 'Find links by keywords',
  'flags.flagLinks.toLearnMore': 'To learn more,',
  'flags.flagLinks.tryAgain': 'Try again',
  'flags.flagList.actionsLabel': 'Flag actions',
  'flags.flagList.copied': 'Copied {value}',
  'flags.flagList.copyToClipboard': 'Copy {value} to clipboard',
  'flags.flagList.createdOn': 'Created on {date}',
  'flags.flagList.featureStatusLabel': 'Feature status',
  'flags.flagList.linkToViews': 'Link to views',
  'flags.flagList.listLabel': 'flag list',
  'flags.flagList.maintainedBy': 'Maintained by',
  'flags.flagList.newBadge': 'New',
  'flags.flagList.tagsLabel': 'Tags',
  'flags.flagList.viewsLabel': 'Views',
  'flags.flagListDisplayPreferences.archiveChecks': 'Archive checks',
  'flags.flagListDisplayPreferences.createdDate': 'Created date',
  'flags.flagListDisplayPreferences.description': 'Description',
  'flags.flagListDisplayPreferences.heading': 'Display',
  'flags.flagListDisplayPreferences.key': 'Key',
  'flags.flagListDisplayPreferences.maintainer': 'Maintainer',
  'flags.flagListDisplayPreferences.propertiesHeading': 'Show these properties',
  'flags.flagListDisplayPreferences.tags': 'Tags',
  'flags.flagListDisplayPreferences.useKeyInstead': 'Use key instead of name',
  'flags.flagListDisplayPreferences.views': 'Views',
  'flags.flagListEmptyState.addFlags': 'Add flags',
  'flags.flagListEmptyState.createFlag': 'Create flag',
  'flags.flagListEmptyState.thisProjectDoesn': "This project doesn't have any flags",
  'flags.flagListEmptyState.useFlagsTo': 'Use flags to release new features to your customers',
  'flags.flagListToolbar.noSettingFilter': 'None',
  'flags.flagListToolbar.searchLabel': 'Search for a feature flag by name, key or description',
  'flags.flagListToolbar.searchPlaceholder': 'Find a flag by name, key or description',
  'flags.flagListToolbar.tagsButton': 'Tags: {tags}',
  'flags.flagMaintainer.title': 'Maintainer',
  'flags.flagOrdering.createdNewest': 'Created newest',
  'flags.flagOrdering.createdOldest': 'Created oldest',
  'flags.flagOrdering.evaluatedNewest': 'Evaluated newest',
  'flags.flagOrdering.evaluatedOldest': 'Evaluated oldest',
  'flags.flagOrdering.nameAZ': 'Name A-Z',
  'flags.flagOrdering.nameZA': 'Name Z-A',
  'flags.flagOrdering.sort': 'Sort',
  'flags.flagOverview.archiveFlag': 'Archive flag',
  'flags.flagOverview.archivedBody': 'This flag has been archived.',
  'flags.flagOverview.archivedReleaseAlert': 'This flag release is archived. To continue, create a new flag release.',
  'flags.flagOverview.cancel': 'Cancel',
  'flags.flagOverview.cancelledReleaseAlert':
    'This flag release was canceled when its pull request closed. To continue, create a new flag release.',
  'flags.flagOverview.cleanupPr': 'Cleanup PR: <link>{label}</link>',
  'flags.flagOverview.closeLabel': 'Close',
  'flags.flagOverview.codeCleanupStage': 'Code cleanup',
  'flags.flagOverview.codeCleanupUnavailable': 'Code cleanup isn’t available yet.',
  'flags.flagOverview.createRelease': 'Create release',
  'flags.flagOverview.detectedTrigger': 'Detected {trigger}',
  'flags.flagOverview.estimatedEnd': 'est. <time></time>',
  'flags.flagOverview.expiredReleaseAlert':
    'This flag release expired after 3 weeks without completing. To continue, create a new flag release.',
  'flags.flagOverview.flagArchivalStage': 'Flag archival',
  'flags.flagOverview.flagArchivalUnavailable': 'Flag archival isn’t available yet.',
  'flags.flagOverview.flagRelease': 'Flag release',
  'flags.flagOverview.lifecycleChecksError': 'We couldn’t load the lifecycle checks for this flag.',
  'flags.flagOverview.loadError': "Couldn't load release overview: {reason}. Try reloading the page.",
  'flags.flagOverview.loadingLifecycleChecksLabel': 'Loading lifecycle checks…',
  'flags.flagOverview.loadingOverviewLabel': 'Loading release overview',
  'flags.flagOverview.monitorMetrics':
    '{tense, select, past {Monitored} other {Monitor}} {count, plural, one {# metric} other {# metrics}}',
  'flags.flagOverview.noPrFound': 'No PR found',
  'flags.flagOverview.noRelease': 'Create a release for this flag',
  'flags.flagOverview.noReleaseDescription': 'LaunchDarkly rolls this flag out across each environment automatically.',
  'flags.flagOverview.noReleaseInEnvironment': 'No release in this environment yet.',
  'flags.flagOverview.pauseConfirm': 'Pause auto-release',
  'flags.flagOverview.pauseDialogBody':
    'Auto-release stops advancing this flag through the environments that haven’t started yet. You can resume it at any time.',
  'flags.flagOverview.pauseDialogTitle': 'Pause auto-release',
  'flags.flagOverview.pauseEffectAlreadyPaused': 'Already paused',
  'flags.flagOverview.pauseEffectAlreadyStarted': 'Already started, so it continues to completion',
  'flags.flagOverview.pauseEffectWillPause': 'Pauses before its release starts',
  'flags.flagOverview.pendingFlagReleaseCompletion': 'Pending flag release completion.',
  'flags.flagOverview.readyForCodeCleanup': 'This flag is ready for code cleanup.',
  'flags.flagOverview.releaseSummary':
    '{tense, select, past {Released} other {Release}} from <from></from> to <to></to> ({method, select, guarded {guarded release} progressive {progressive release} immediate {immediate release} other {release}})',
  'flags.flagOverview.readyToArchive': 'This flag is ready to archive.',
  'flags.flagOverview.startConfirm': 'Start release',
  'flags.flagOverview.startDialogBody':
    'This starts the release in {environment} as soon as any earlier environment in the plan completes.',
  'flags.flagOverview.startDialogTitle': 'Start release in {environment}',
  'flags.flagOverview.startError':
    'The release could not be started. Refresh the page to check its status, then try again.',
  'flags.flagOverview.startRelease': 'Start release',
  'flags.flagOverview.startSuccess': 'Release started',
  'flags.flagOverview.triggerDeployment': 'deployment',
  'flags.flagOverview.triggerFlagEvaluations': 'flag evaluations',
  'flags.flagOverview.triggerManualStarted': 'Started manually',
  'flags.flagOverview.triggerManualWaiting': 'Waiting on a manual start',
  'flags.flagOverview.triggerPromotionStarted': 'Started after the previous environment completed',
  'flags.flagOverview.triggerPromotionWaiting': 'Waiting on the previous environment to complete',
  'flags.flagOverview.unknownError': 'Unknown error',
  'flags.flagOverview.updateDefaultRule': 'Update default rule',
  'flags.flagOverview.viewPr': 'View PR',
  'flags.flagOverview.waitingOnTrigger': 'Waiting on {trigger} to start release',
  'flags.flagSaveBar.saveChanges': 'Save changes',
  'flags.flagSaveBar.savingText': 'Saving changes',
  'flags.flagSaveBar.validationTooltip': 'Fix the sections highlighted below',
  'flags.flagSettings.archive': 'Archive',
  'flags.flagSettings.archiveBody':
    'This flag will be archived across all environments and will only appear in your list when filtered for.',
  'flags.flagSettings.archiveHeading': 'Archive flag',
  'flags.flagSettings.body': 'Changes to these settings will impact this flag across all environments.',
  'flags.flagSettings.cloneBody':
    'Cloning the flag will copy its targeting configuration and on/off state for all environments. Experiment information such as iterations and metrics will not be copied over.',
  'flags.flagSettings.cloneButton': 'Clone this flag',
  'flags.flagSettings.cloneHeading': 'Clone flag',
  'flags.flagSettings.deleteBlockedByExperiments':
    'This flag cannot be deleted because it is used in <link>experiments</link>.',
  'flags.flagSettings.deleteBody': 'This flag will be deleted permanently across all environments in this project.',
  'flags.flagSettings.deleteButton': 'Delete flag',
  'flags.flagSettings.deleteDisabledTooltip': 'You cannot delete flags used by experiments',
  'flags.flagSettings.deleteHeading': 'Delete this flag',
  'flags.flagSettings.deprecate': 'Deprecate',
  'flags.flagSettings.deprecateBody':
    'Use to mark flags that are not ready to be archived or deleted, but are no longer actively supported by your organization.',
  'flags.flagSettings.deprecateHeading': 'Deprecate flag',
  'flags.flagSettings.heading': 'Settings for all environments',
  'flags.flagSettings.restore': 'Restore',
  'flags.flagSettings.restoreBody': 'This flag will be restored across all environments.',
  'flags.flagSettings.restoreHeading': 'Restore flag',
  'flags.flagSettingsForm.addCustomProperty': 'Add custom property',
  'flags.flagSettingsForm.clientSideBody': 'Control which client-side SDKs can use this flag. To learn more,',
  'flags.flagSettingsForm.clientSideHeading': 'Client-side SDK availability',
  'flags.flagSettingsForm.clientSideIdLabel': 'SDKs using Client-side ID',
  'flags.flagSettingsForm.descriptionHint': 'Descriptions can include Markdown and in-line Confluence links.',
  'flags.flagSettingsForm.descriptionLabel': 'Description',
  'flags.flagSettingsForm.descriptionPlaceholder': 'Describe what this feature flag controls',
  'flags.flagSettingsForm.jiraIssuesProperty': 'Jira issues',
  'flags.flagSettingsForm.keyHint': 'You cannot change the key.',
  'flags.flagSettingsForm.keyLabel': 'Key',
  'flags.flagSettingsForm.keyTooltip': 'Flag keys are set on initial flag creation. You cannot modify a flag key.',
  'flags.flagSettingsForm.mobileKeyLabel': 'SDKs using Mobile key',
  'flags.flagSettingsForm.nameLabel': 'Name',
  'flags.flagSettingsForm.savingText': 'Saving settings',
  'flags.flagSettingsForm.temporaryHint':
    "Use temporary flags for releases or short-term controls. Use permanent flags for entitlements or long-term controls. We won't prompt you to remove permanent flags, even if one variation is rolled out to everyone.",
  'flags.flagSettingsForm.temporaryLabel': 'This is a temporary flag',
  'flags.flagSidebar.created': 'Created',
  'flags.flagSidebar.createdBy': '<time></time> by {name}',
  'flags.flagSidebar.description': 'Description',
  'flags.flagSidebar.editVariationsLabel': 'Edit variations',
  'flags.flagSidebar.failedToLoad': 'Failed to load custom properties',
  'flags.flagSidebar.key': 'Key',
  'flags.flagSidebar.manageWorkflows': 'Manage workflows',
  'flags.flagSidebar.schema': 'Schema',
  'flags.flagSidebar.schemaStatusLabel': 'JSON schema status',
  'flags.flagSidebar.tryAgain': 'Try again',
  'flags.flagSidebar.variations': 'Variations',
  'flags.flagSidebar.workflows': 'Workflows',
  'flags.flagSidebarActions.copyLinkTooltip': 'Copy flag link',
  'flags.flagSidebarActions.entityActionLabel': 'entity action',
  'flags.flagSidebarCodeReferences.codeReferences': 'Code references',
  'flags.flagSidebarCodeReferences.failedToLoad': 'Failed to load code references',
  'flags.flagSidebarCodeReferences.noneOnDefaultBranch': 'None on default branch',
  'flags.flagSidebarCodeReferences.tryAgain': 'Try again',
  'flags.flagSidebarCodeReferences.viewAll': 'View all',
  'flags.flagSidebarExperiments.addExperiment': 'Add experiment',
  'flags.flagSidebarExperiments.experiments': 'Experiments',
  'flags.flagSidebarExperiments.failedToLoad': 'Failed to load experiments',
  'flags.flagSidebarExperiments.none': 'None',
  'flags.flagSidebarExperiments.tryAgain': 'Try again',
  'flags.flagSidebarLifecycle.lifecycleStatus': 'Lifecycle status',
  'flags.flagSidebarLifecycle.live': 'Live',
  'flags.flagSidebarLifecycle.showChecks': 'Show checks',
  'flags.flagSidebarPendingChanges.failedToLoad': 'Failed to load pending approvals',
  'flags.flagSidebarPendingChanges.loadingLabel': 'Loading pending approvals',
  'flags.flagSidebarPendingChanges.pendingChanges': 'Pending changes',
  'flags.flagSidebarPendingChanges.tryAgain': 'Try again',
  'flags.flagSidebarTags.addTags': 'Add tag(s)',
  'flags.flagSidebarTags.tags': 'Tags',
  'flags.flagStatusTooltip.activeBody': 'Contexts are receiving variations of this flag',
  'flags.flagStatusTooltip.evaluatedAgo': 'Evaluated {time}',
  'flags.flagStatusTooltip.inactiveArchiveHint': 'If you have removed it from your code, you may archive it.',
  'flags.flagStatusTooltip.inactiveBody': 'This flag hasn’t been evaluated in the past 7 days.',
  'flags.flagStatusTooltip.inactiveSummary': 'Evaluated over 7 days ago',
  'flags.flagStatusTooltip.label': 'Flag status',
  'flags.flagStatusTooltip.launchedBody': 'All contexts are receiving one variation of this flag.',
  'flags.flagStatusTooltip.launchedRemoveHint': 'You may be able to remove it from your code.',
  'flags.flagStatusTooltip.neverEvaluatedSummary': 'Yet to be evaluated',
  'flags.flagStatusTooltip.newBody': 'This flag is new, and hasn’t been evaluated yet',
  'flags.flagsTable.closeEnvironment': 'Close environment',
  'flags.flagsTable.closeEnvironmentLabel': 'Close {name} environment',
  'flags.flagsTable.columnOptionsLabel': '{column} column options',
  'flags.flagsTable.copyDotKeyTooltip': 'Copy {dotKey} to clipboard',
  'flags.flagsTable.copyKeyLabel': 'Copy {flagKey} to clipboard',
  'flags.flagsTable.copyKeyTooltip': 'Copy {flagKey} to clipboard',
  'flags.flagsTable.created': 'Created',
  'flags.flagsTable.environmentOptionsLabel': '{name} environment options',
  'flags.flagsTable.environmentStateLabel': 'Environment state',
  'flags.flagsTable.environmentsLoadError': 'There was a problem fetching the environments. Try again later.',
  'flags.flagsTable.evaluationBreakdownLabel': 'Evaluation breakdown by variation',
  'flags.flagsTable.filterEnvironmentLabel': 'Filter {name}',
  'flags.flagsTable.hideProperty': 'Hide property',
  'flags.flagsTable.hidePropertyLabel': 'Hide {column} property',
  'flags.flagsTable.key': 'Key',
  'flags.flagsTable.maintainer': 'Maintainer',
  'flags.flagsTable.moveLeft': 'Move left',
  'flags.flagsTable.moveRight': 'Move right',
  'flags.flagsTable.name': 'Name',
  'flags.flagsTable.rowActionsLabel': 'Actions for {name}',
  'flags.flagsTable.selectedEnvironmentsLabel': 'Selected environments',
  'flags.flagsTable.swapEnvironment': 'Swap environment',
  'flags.flagsTable.tableLabel': 'Flags',
  'flags.flagsTable.tags': 'Tags',
  'flags.flagsTable.tagsLabel': 'Tags for {name}',
  'flags.flagsTable.variationCountLabel': '{count, plural, one {# variation} other {# variations}}',
  'flags.flagsTable.views': 'Views',
  'flags.guardedRolloutFilter.any': 'Any',
  'flags.guardedRolloutFilter.archived': 'Archived',
  'flags.guardedRolloutFilter.completed': 'Completed',
  'flags.guardedRolloutFilter.label': 'Guarded rollout status',
  'flags.guardedRolloutFilter.monitoring': 'Monitoring',
  'flags.guardedRolloutFilter.none': 'None',
  'flags.guardedRolloutFilter.regressed': 'Regressed',
  'flags.guardedRolloutFilter.rolledBack': 'Rolled back',
  'flags.holdoutDialog.chooseLabel': 'Choose holdout',
  'flags.holdoutDialog.filterLabel': 'Filter holdouts',
  'flags.holdoutDialog.searchPlaceholder': 'Search holdouts…',
  'flags.jsonSchemaPanel.addSchema': 'Add schema',
  'flags.jsonSchemaPanel.editSchema': 'Edit schema',
  'flags.jsonSchemaPanel.heading': 'JSON schema',
  'flags.jsonSchemaPanel.noSchemaDescription':
    'No schema is defined. Variation values are accepted without validation.',
  'flags.jsonSchemaPanel.schemaDescription': 'Variation values are validated against this schema.',
  'flags.keyConventions.cannotUseNew': 'Cannot use "new" as a flag key',
  'flags.keyConventions.mustStartWithPrefix': 'Key must start with prefix "{prefix}"',
  'flags.keyConventions.notAvailable': 'Naming conventions not available',
  'flags.keyConventions.validationError': 'Naming conventions validation error',
  'flags.keyConventionsTooltip.cannotChangeKey': 'You cannot change a key after creation',
  'flags.keyConventionsTooltip.cannotUseNew': 'Cannot use "new" as a key',
  'flags.keyConventionsTooltip.example': 'Example: {example}',
  'flags.keyConventionsTooltip.exampleText': 'MyFeature',
  'flags.keyConventionsTooltip.heading': 'Your admin has set the following key conventions:',
  'flags.keyConventionsTooltip.mustStartWithPrefix': 'Must start with prefix "{prefix}"',
  'flags.keyConventionsTooltip.mustUseCase': 'Must use {caseConvention}',
  'flags.layerDialog.chooseLabel': 'Choose layer',
  'flags.layerDialog.filterLabel': 'Filter layers',
  'flags.layerDialog.searchPlaceholder': 'Search layers…',
  'flags.lifecycle.codeRemovalDescription':
    'These run only before the flag can be removed from code. Turn on automated cleanup in your ',
  'flags.lifecycle.vegaCleanupLink': 'Vega settings',
  'flags.lifecycleChecks.generalChecksLabel': 'General checks',
  'flags.lifecycleChecks.neutralChecksCount': '{count, plural, one {# neutral check} other {# neutral checks}}',
  'flags.lifecycleChecksDialog.body':
    'These checks determine when a flag is ready for code removal or to archive. The flag will change its lifecycle state once all checks pass.',
  'flags.lifecycleChecksDialog.close': 'Close',
  'flags.lifecycleChecksDialog.lifecycleChecks': 'Lifecycle checks',
  'flags.lifecycleChecksDialog.lifecycleSettings': 'Lifecycle settings',
  'flags.lifecycleChecksDialog.loadError': 'We couldn’t load the lifecycle checks for this flag.',
  'flags.lifecycleChecksDialog.loadingLabel': 'Loading lifecycle checks…',
  'flags.lifecycleFilter.archived': 'Archived',
  'flags.lifecycleFilter.deprecated': 'Deprecated',
  'flags.lifecycleFilter.label': 'Lifecycle',
  'flags.lifecycleFilter.live': 'Live',
  'flags.lifecycleFilter.readyForCodeRemoval': 'Ready for code removal',
  'flags.lifecycleFilter.readyToArchive': 'Ready to archive',
  'flags.lifecycleSettings.beforeArchiving': 'Before archiving',
  'flags.lifecycleSettings.beforeArchivingDescription': 'These run only before the flag is archived.',
  'flags.lifecycleSettings.beforeCodeRemoval': 'Before code removal',
  'flags.lifecycleSettings.beforeCodeRemovalDescription': 'These run only before the flag is removed from code.',
  'flags.lifecycleSettings.custom': 'Custom',
  'flags.lifecycleSettings.customValueLabel': '{label} (Custom value)',
  'flags.lifecycleSettings.decrementLabel': 'decrement',
  'flags.lifecycleSettings.description':
    "Set the criteria a flag must meet before it's ready to be removed from your code or archived.",
  'flags.lifecycleSettings.environmentsLoadError':
    'Unable to load environments for this project. Refresh and try again.',
  'flags.lifecycleSettings.generalChecks': 'General checks',
  'flags.lifecycleSettings.generalChecksDescription':
    'A flag must pass these checks before it can be removed from code or archived.',
  'flags.lifecycleSettings.incrementLabel': 'increment',
  'flags.lifecycleSettings.loadError': 'An unexpected error has occurred loading lifecycle settings.',
  'flags.lifecycleSettings.minimumFlagAgeLabel': 'Minimum flag age',
  'flags.lifecycleSettings.minimumOneDay': 'Must be at least 1 day',
  'flags.lifecycleSettings.mustBeServingOneVariation': 'Must be serving one variation',
  'flags.lifecycleSettings.mustBeTemporary': 'Must be temporary',
  'flags.lifecycleSettings.mustHaveEvaluations': 'Must have evaluations in each critical environment',
  'flags.lifecycleSettings.mustNotBePrerequisite': 'Must not be a prerequisite for other flags',
  'flags.lifecycleSettings.mustPassAllChecksToArchive': 'Must pass all checks to archive',
  'flags.lifecycleSettings.noCriticalEnvironmentHelp':
    'Mark an environment as critical to use this setting. Most readiness checks only run in critical environments.',
  'flags.lifecycleSettings.noEvaluationsLabel': 'No evaluations for at least',
  'flags.lifecycleSettings.noTargetingChangesLabel': 'No targeting changes for at least',
  'flags.lifecycleSettings.numberOfDays': 'Number of days',
  'flags.lifecycleSettings.option14Days': '14 days',
  'flags.lifecycleSettings.option180Days': '180 days',
  'flags.lifecycleSettings.option30Days': '30 days',
  'flags.lifecycleSettings.option30DaysDefault': '30 days (default)',
  'flags.lifecycleSettings.option60Days': '60 days',
  'flags.lifecycleSettings.option7DaysDefault': '7 days (default)',
  'flags.lifecycleSettings.option90Days': '90 days',
  'flags.lifecycleSettings.previewLoadError': 'Failed to load flag cleanup preview.',
  'flags.lifecycleSettings.readAboutLink': 'Read about flag lifecycle settings',
  'flags.lifecycleSettings.resetToDefault': 'Reset to default',
  'flags.lifecycleSettings.save': 'Save',
  'flags.lifecycleSettings.saveError': 'Failed to update flag lifecycle settings',
  'flags.lifecycleSettings.saved': 'Flag lifecycle settings updated',
  'flags.maintainerFilter.anyone': 'Anyone',
  'flags.maintainerFilter.choose': 'Choose maintainer',
  'flags.maintainerFilter.label': 'Maintainer',
  'flags.maintainerFilter.noMaintainer': 'No maintainer',
  'flags.manageVariations.addVariation': 'Add variation',
  'flags.manageVariations.body': 'The defined variations this flag uses in each environment in this project.',
  'flags.manageVariations.clickToEdit': 'Click to edit',
  'flags.manageVariations.defaultVariationsHeading': 'Default variations for new environments',
  'flags.manageVariations.defaultVariationsHint':
    'Changes to these default rules will only apply to newly created environments. Flag configuration in existing environments will not be changed.',
  'flags.manageVariations.delete': 'Delete',
  'flags.manageVariations.deleteDefaultReason': 'You cannot delete this variation because it is a default variation',
  'flags.manageVariations.deleteInUseReason': 'You cannot delete this variation because it is currently in use',
  'flags.manageVariations.deletePrerequisiteReason':
    'You cannot delete this variation because other flags use this flag as a prerequisite',
  'flags.manageVariations.dependentFlags': 'Dependent flags',
  'flags.manageVariations.dependentFlagsAlert':
    "You can't delete variations because other flags use them as prerequisites",
  'flags.manageVariations.generatedWithAi': '✨ Generated with AI',
  'flags.manageVariations.heading': 'Variations',
  'flags.manageVariations.inUseLoadError':
    "We couldn't check which variations are in use. Refresh the page to try again.",
  'flags.manageVariations.jsonValueEditLabel': 'Select to edit JSON value',
  'flags.manageVariations.jsonValueReadOnlyLabel': 'JSON value (read-only)',
  'flags.manageVariations.loadError': "We couldn't load this flag's variations. Refresh the page to try again.",
  'flags.manageVariations.migrationReadOnlyReason': 'Migration flag variations cannot be modified',
  'flags.manageVariations.nameMustBeUnique': 'Name must be unique',
  'flags.manageVariations.runningExperimentAlert':
    'A running experiment is using these variations, so you cannot delete variations or change their values.',
  'flags.manageVariations.runningExperimentsHeading': 'Changes to this flag may affect a running experiment',
  'flags.manageVariations.schemaFailureCount':
    '{count, plural, one {# variation does} other {# variations do}} not match the schema.',
  'flags.manageVariations.schemaMalformed':
    'The JSON schema for this flag is malformed and cannot be compiled. {error}',
  'flags.manageVariations.schemaMismatch': "Variation values don't match this flag's JSON schema",
  'flags.manageVariations.usedInExperimentTooltip':
    'You cannot delete this variation because it has been used in an experiment.',
  'flags.manageVariations.valueMustBeUnique': 'Value must be unique',
  'flags.manageVariations.valueRequired': 'Value is required',
  'flags.manageVariations.variationInUse': 'Variation in use',
  'flags.manageVariations.variationInUseBlocked': 'Variation in use - cannot be modified or deleted',
  'flags.manageVariations.variationIndex': 'Variation {index}',
  'flags.manageVariations.variationType':
    '{type, select, json {JSON} number {Number} boolean {Boolean} other {String}}',
  'flags.manageVariations.variationTypeLabel': 'Variation type',
  'flags.manageVariations.variationTypeTooltip':
    'This flag will return {type, select, json {JSON} number {numbers} boolean {booleans} other {strings}}',
  'flags.migrationSettings.checkRatioAtLeast': 'This must be an integer greater than {min}',
  'flags.migrationSettings.checkRatioAtMost': 'This must be an integer less than {max}',
  'flags.migrationSettings.checkRatioBetween': 'This must be an integer between {min} and {max}',
  'flags.migrationSettings.checkRatioInteger': 'This must be an integer',
  'flags.monitoringPage.title': 'Monitoring',
  'flags.multiArmedBandits.addBandit': 'Add multi-armed bandit',
  'flags.multiArmedBandits.addBanditLabel': 'Add multi-armed bandit',
  'flags.multiArmedBandits.cardEmptyText': 'None',
  'flags.multiArmedBandits.heading': 'Multi-armed bandits',
  'flags.multiArmedBandits.loadError': 'Failed to load multi-armed bandits',
  'flags.multiArmedBandits.tryAgain': 'Try again',
  'flags.newPage.title': 'Create flag',
  'flags.notFound.backToDashboard': 'Back to the dashboard',
  'flags.notFound.body': 'Sorry, we can’t find the flag you’re looking for.',
  'flags.notFound.createFlag': 'Create flag',
  'flags.notFound.heading': 'Flag not found',
  'flags.notFound.orGoPrefix': 'or go',
  'flags.notifications.archived': 'Flag archived',
  'flags.notifications.deleteFailed': 'Flag could not be deleted.',
  'flags.notifications.deleted': 'Flag deleted',
  'flags.notifications.deprecateFailed': 'Flag could not be deprecated.',
  'flags.notifications.deprecatedRestoreFailed': 'Deprecated flag could not be restored.',
  'flags.notifications.deprecatedRestored': 'Deprecated flag restored',
  'flags.notifications.environmentSettingsSaved': 'Environment settings saved',
  'flags.notifications.linkCreated': 'Link created',
  'flags.notifications.linkDeleted': 'Link deleted',
  'flags.notifications.restored': 'Flag restored',
  'flags.notifications.saved': 'Flag saved',
  'flags.notifications.scheduledChangesDeleted': 'Scheduled changes deleted',
  'flags.notifications.scheduledChangesUpdated': 'Scheduled changes updated',
  'flags.notifications.settingsSaved': 'Flag settings saved',
  'flags.overviewPage.title': 'Release',
  'flags.page.connectAction': 'Connect your SDK to start evaluating flags and see real-time data.',
  'flags.page.label': 'Search flags',
  'flags.page.placeholder': 'Search flags by name, description or key',
  'flags.page.somethingWentWrong': 'Something went wrong',
  'flags.picker.clearSearch': 'clear',
  'flags.picker.loadError': 'Error loading flags',
  'flags.picker.loading': 'Loading',
  'flags.picker.loadingMore': 'Loading more flags',
  'flags.picker.searchFlags': 'Search flags',
  'flags.picker.selectFlag': 'Select flag',
  'flags.picker.statusLabel': 'Flag status',
  'flags.picker.statusLoading': 'loading',
  'flags.picker.targetingOff': 'Off',
  'flags.picker.targetingOn': 'On',
  'flags.picker.unknownFlag': 'Unknown flag: {flagKey}',
  'flags.prerequisiteAccess.emptyBody': 'There are no prerequisites for this flag.',
  'flags.prerequisiteAccess.upsell': 'Add prerequisites',
  'flags.prerequisiteValidation.flagRequired': 'You must select a feature flag',
  'flags.prerequisiteValidation.flagUnavailable': 'This prerequisite flag no longer exists',
  'flags.prerequisiteValidation.variationNotFound': 'The variation referenced by this prerequisite no longer exists',
  'flags.prerequisites.addLabel': 'Add prerequisite',
  'flags.prerequisites.deleteLabel': "delete prerequisite flag ''{name}''",
  'flags.prerequisites.flagLabel': 'Flag',
  'flags.prerequisites.holdoutRestriction':
    'You cannot edit a prerequisite flag when it is included in an experiment holdout',
  'flags.prerequisites.sectionTooltip': 'Prerequisites',
  'flags.prerequisites.variationLabel': 'Variation',
  'flags.prerequisites.viewFlagLink': 'View flag',
  'flags.releaseStep.reason.afterRollbackIn': 'After rollback in {environment}',
  'flags.releaseStep.reason.archived': 'Archived',
  'flags.releaseStep.reason.automatically': 'Automatically',
  'flags.releaseStep.reason.cleanupFailed': 'Failed',
  'flags.releaseStep.reason.cleanupNeedsInput': 'Vega needs input',
  'flags.releaseStep.reason.codeRemovalInProgress': 'Code removal in progress',
  'flags.releaseStep.reason.flagChanged': 'Flag changed',
  'flags.releaseStep.reason.insufficientData': 'Insufficient data',
  'flags.releaseStep.reason.invalidAnalysisUnit': 'Invalid analysis unit',
  'flags.releaseStep.reason.manualStart': 'Manual start',
  'flags.releaseStep.reason.manually': 'Manually',
  'flags.releaseStep.reason.nextCleanupRun': 'Next cleanup run',
  'flags.releaseStep.reason.nextPhaseReady': 'Next phase ready',
  'flags.releaseStep.reason.prAwaitingMerge': 'PR awaiting merge',
  'flags.releaseStep.reason.prClosed': 'PR closed',
  'flags.releaseStep.reason.prMerged': 'PR merged',
  'flags.releaseStep.reason.regression': 'Regression',
  'flags.releaseStep.reason.regressionDetected': 'Regression detected',
  'flags.releaseStep.reason.sampleRatioMismatch': 'Sample ratio mismatch',
  'flags.releaseStep.reason.startingSoon': 'Starting soon',
  'flags.releaseStep.reason.startsAt': 'Starts at {startsAt, time, short}',
  'flags.releaseStep.reason.startsOn': 'Starts {startsAt, date, medium}, {startsAt, time, short}',
  'flags.releaseStep.reason.waitingForDeploy': 'Waiting for deploy',
  'flags.releaseStep.reason.waitingForFlagEvaluations': 'Waiting for flag evaluations',
  'flags.releaseStep.reason.waitingOnArchivalChecks': 'Waiting on archival checks',
  'flags.releaseStep.reason.waitingOnCleanupChecks': 'Waiting on cleanup checks',
  'flags.releaseStep.reason.waitingOnEnvironment': 'Waiting on {environment}',
  'flags.releaseStep.reason.waitingOnPreviousEnvironment': 'Waiting on previous environment',
  'flags.releaseStep.status.completed': 'Completed',
  'flags.releaseStep.status.inProgress': 'In progress',
  'flags.releaseStep.status.needsAttention': 'Needs attention',
  'flags.releaseStep.status.paused': 'Paused',
  'flags.releaseStep.status.pending': 'Pending',
  'flags.releaseStep.status.ready': 'Ready',
  'flags.releaseStep.status.rolledBack': 'Rolled back',
  'flags.releaseStep.statusWithReason': '{status}, {reason}',
  'flags.rolloutEditor.attributeLockedTooltip': 'When moving data, a rollout can only assign by <code>key</code>',
  'flags.rolloutEditor.bucketByNote':
    'If this rule is evaluated for context kinds other than the one selected, those contexts will be served the first variation',
  'flags.rolloutEditor.byHeading': 'By',
  'flags.rolloutEditor.contextKindLabel': 'Context kind',
  'flags.rolloutEditor.contextKindLockedTooltip':
    "When moving data, rollouts must reference the migration's context kind",
  'flags.rolloutEditor.decreaseWeightLabel': 'decrease rollout weight',
  'flags.rolloutEditor.fieldLockedTooltip': 'This field cannot be modified',
  'flags.rolloutEditor.increaseWeightLabel': 'increase rollout weight',
  'flags.rolloutEditor.variationWeightLabel': 'Variation {index} rollout weight',
  'flags.ruleExclusion.dataExport': 'data export',
  'flags.ruleExclusion.dataExportDestinations': 'data export destinations',
  'flags.ruleExclusion.experiments': 'experiments',
  'flags.ruleExclusion.experimentsAndDataExport': 'experiments and data export',
  'flags.ruleExclusion.experimentsAndDataExportDestinations': 'experiments and data export destinations',
  'flags.ruleExclusion.experimentsRequireRule':
    'This flag is participating in one or more experiments, so you must choose at least one targeting rule.',
  'flags.ruleExclusion.noRuleSelected':
    'You did not select a targeting rule. If you save this setting, LaunchDarkly will not send detailed events to data export destinations.',
  'flags.sampledTag.label': 'Sampled tag',
  'flags.sampledTag.text': 'Sampled',
  'flags.sampledTag.tooltip': 'Events are being sampled due to large volume. Data reflects approximate values',
  'flags.scheduleInput.afterLabel': 'After',
  'flags.scheduleKind.later': 'Later',
  'flags.scheduleKind.specificDateTime': 'Specific date and time',
  'flags.schemaEditDialog.addTitle': 'Add JSON schema',
  'flags.schemaEditDialog.approvalNote': 'Schema edits are not yet shown in approval reviews — coming soon.',
  'flags.schemaEditDialog.cancel': 'Cancel',
  'flags.schemaEditDialog.editTitle': 'Edit JSON schema',
  'flags.schemaEditDialog.mismatchHeading': 'Schema does not match current variations:',
  'flags.schemaEditDialog.previewFailed': 'Failed to preview schema',
  'flags.schemaEditDialog.previewValidDescription': 'All current variations match this schema.',
  'flags.schemaEditDialog.previewValidTitle': 'Schema is valid',
  'flags.schemaEditDialog.removeConfirm':
    'Remove JSON schema from this flag? Future variation edits will not be validated.',
  'flags.schemaEditDialog.removeSchema': 'Remove schema',
  'flags.schemaEditDialog.removedDescription': 'Future variation edits will not be validated.',
  'flags.schemaEditDialog.removedTitle': 'Schema removed',
  'flags.schemaEditDialog.save': 'Save',
  'flags.schemaEditDialog.saveFailed': 'Failed to save schema',
  'flags.schemaEditDialog.schemaPlaceholder': 'Enter a JSON Schema to validate variations against',
  'flags.schemaEditDialog.testSchema': 'Test schema against current variations',
  'flags.schemaEditDialog.testing': 'Testing…',
  'flags.schemaEditDialog.updatedDescription': 'New variation edits will be validated against the schema.',
  'flags.schemaEditDialog.updatedTitle': 'Schema updated',
  'flags.schemaEditDialog.variationIndex': 'Variation {index}:',
  'flags.schemaEditor.formatLabel': 'Format JSON schema',
  'flags.schemaEditor.formatTooltip': 'Format JSON',
  'flags.schemaEditor.placeholder': 'Enter JSON schema',
  'flags.schemaEditor.suggestLabel': 'Suggest a JSON schema from current variations',
  'flags.schemaEditor.suggestTooltip': 'Suggest schema from variations',
  'flags.schemaField.description': 'Define a schema for all rules using <link>JSON Schema</link>',
  'flags.schemaField.heading': 'Schema validation',
  'flags.schemaField.placeholder': 'Enter JSON schema',
  'flags.schemaField.removeSchema': 'Remove schema',
  'flags.schemaPreviewError.anyDialect': 'any JSON Schema dialect',
  'flags.schemaPreviewError.conflict': 'Someone else updated this flag. Reload and try again.',
  'flags.schemaPreviewError.externalRef':
    'External `$ref` isn’t supported. Use intra-document refs only (e.g. `#/$defs/Foo`).',
  'flags.schemaPreviewError.forbidden': "You don't have permission to edit the schema for this flag.",
  'flags.schemaPreviewError.invalidSchema': "This isn't a valid JSON Schema. {detail}",
  'flags.schemaPreviewError.schemaInvalid': 'The JSON Schema is invalid.',
  'flags.schemaPreviewError.tooLarge': 'Schema is too large. Maximum is 64KB.',
  'flags.schemaPreviewError.unsupportedDialect': 'Unsupported JSON Schema dialect. This flag accepts {dialects}.',
  'flags.schemaPreviewError.valueMismatch': 'Value does not match the schema.',
  'flags.schemaSuggest.ambiguousTypes': 'Inferred schemas have ambiguous types.',
  'flags.schemaSuggest.arrayItemsContext': 'array items',
  'flags.schemaSuggest.cannotMergeBoolean': 'Cannot merge boolean schema with object schema.',
  'flags.schemaSuggest.couldNotInfer': 'Could not infer a schema from these variations.',
  'flags.schemaSuggest.itemContext': 'item {index}',
  'flags.schemaSuggest.namedVariationLabel': '{name} (variation {number})',
  'flags.schemaSuggest.noVariations': 'Add at least one variation value before suggesting a schema.',
  'flags.schemaSuggest.prefixed': '{context}: {message}',
  'flags.schemaSuggest.propertyContext': 'property "{key}"',
  'flags.schemaSuggest.tooDeep': 'Schema is nested too deeply to infer (max {max} levels).',
  'flags.schemaSuggest.typeMismatch': 'type mismatch: {a} vs {b}. The same key cannot vary in type across variations.',
  'flags.schemaSuggest.unsupportedType': 'Unsupported value of type "{type}".',
  'flags.schemaSuggest.variationLabel': 'Variation {number}',
  'flags.schemaValidation.cannotBeCompiled': 'Schema cannot be compiled',
  'flags.schemaValidation.couldNotCompile': 'Schema could not be compiled',
  'flags.schemaValidation.dialectPolicy':
    'This flag accepts only the {list} JSON Schema {count, plural, one {dialect} other {dialects}}. This schema declares {declared}.',
  'flags.schemaValidation.invalidJsonSchema': 'Invalid JSON Schema: {error}',
  'flags.schemaValidation.mustBeValidJson': 'Schema must be valid JSON',
  'flags.schemaValidation.mustBeValidJsonSchema': 'Schema must be a valid JSON Schema',
  'flags.schemaValidation.notObjectOrBoolean': 'Schema must be a JSON object or boolean',
  'flags.schemaValidation.unsupportedDialect': 'This schema declares a JSON Schema version that is not supported here.',
  'flags.schemaValidation.vacuousSchema':
    'Schema has no JSON Schema constraints (e.g. `type`, `properties`). Did you mean to wrap your fields in `{example}`?',
  'flags.schemaValidation.valueMismatch': 'value does not match schema',
  'flags.sdkAvailabilityFilter.allClientSideSdks': 'All client-side SDKs',
  'flags.sdkAvailabilityFilter.allSdks': 'All SDKs',
  'flags.sdkAvailabilityFilter.clientSideId': 'SDKs using Client-side ID',
  'flags.sdkAvailabilityFilter.label': 'SDK availability',
  'flags.sdkAvailabilityFilter.mobileKey': 'SDKs using Mobile key',
  'flags.sdkAvailabilityFilter.noClientSideSdks': 'No client-side SDKs',
  'flags.settings.addCustomProperty': 'Add custom property',
  'flags.settings.archive': 'Archive',
  'flags.settings.clone': 'Clone this flag',
  'flags.settings.delete': 'Delete',
  'flags.settings.deprecate': 'Deprecate',
  'flags.settings.menuLabel': 'View flag settings',
  'flags.settings.restore': 'Restore',
  'flags.settingsDiff.clauseAnd': 'and',
  'flags.settingsDiff.clauseIf': 'if',
  'flags.settingsDiff.defaultRule': 'Default rule',
  'flags.settingsDiff.individualTargets': 'Individual targets',
  'flags.settingsDiff.loadingPrerequisites': 'Loading prerequisites…',
  'flags.settingsDiff.noIndividualTargets': 'No individual targets',
  'flags.settingsDiff.noPrerequisitesDefined': 'No prerequisites defined',
  'flags.settingsDiff.noRulesDefined': 'No rules defined',
  'flags.settingsDiff.noVariationDefined': 'No variation defined',
  'flags.settingsDiff.offVariation': 'If targeting is off, serve',
  'flags.settingsDiff.prerequisiteLoadError': 'Trouble getting information for {flagKey}',
  'flags.settingsDiff.prerequisites': 'Prerequisites',
  'flags.settingsDiff.rolloutContextKind': 'for <marker>{contextKind}</marker>',
  'flags.settingsDiff.serve': 'serve',
  'flags.settingsDiff.servePercentageRollout': 'serve a percentage rollout:',
  'flags.settingsDiff.targetingRules': 'Targeting rules',
  'flags.settingsDiff.targetingState': 'Targeting state',
  'flags.settingsPage.title': 'Settings',
  'flags.singleRuleCard.disabledNote': 'This rule is disabled and will not be used in evaluations.',
  'flags.singleRuleCard.enableRule': 'Enable rule',
  'flags.singleRuleCard.experimentRuleHeading':
    '{legacy, select, yes {Legacy Experiment rule} other {Experiment rule}}',
  'flags.singleRuleCard.sendEventsTo': 'Send events to',
  'flags.singleRuleCard.trackEventsRestrictedTooltip': 'Events are being sent for all targeting rules.',
  'flags.status.loading': 'Loading…',
  'flags.statusFilter.active': 'Active',
  'flags.statusFilter.any': 'Any',
  'flags.statusFilter.hasEvaluations': 'Has evaluations',
  'flags.statusFilter.hasNoEvaluations': 'Has no evaluations',
  'flags.statusFilter.inactive': 'Inactive',
  'flags.statusFilter.label': 'Status',
  'flags.statusFilter.launched': 'Launched',
  'flags.statusFilter.new': 'New',
  'flags.tagFilter.emptyState': 'There are no tags',
  'flags.tagFilter.selectLabel': 'Select tags',
  'flags.targetingRules.createExperimentDisabledApproval': 'Needs approval. Save this rule and request approval.',
  'flags.targetingRules.createExperimentDisabledUnsavedChanges':
    'Review and save rule changes before creating an experiment',
  'flags.targetingRules.createExperimentDisabledVariations':
    'At least 2 variations are required to create an experiment',
  'flags.templateMenu.flagSettings': 'Flag settings',
  'flags.templateMenu.fromScratchDescription': 'Start with a blank boolean flag and set everything yourself.',
  'flags.templateMenu.fromScratchName': 'Create from scratch',
  'flags.templateMenu.menuLabel': 'Flag templates',
  'flags.templateMenu.templatesHeading': 'Templates',
  'flags.templatePicker.customDescription':
    "A blank flag for specialized or complex use cases the templates don't cover.",
  'flags.templatePicker.customName': 'Custom',
  'flags.templatePicker.experimentDescription':
    'A flag that tests a hypothesis using A/B tests or multi-armed bandits.',
  'flags.templatePicker.experimentName': 'Experiment',
  'flags.templatePicker.fallbackNotice': 'Templates are unavailable right now',
  'flags.templatePicker.loadingLabel': 'Loading flag templates',
  'flags.templatePicker.killSwitchDescription':
    'A permanent flag for shutting off non-core functionality or third-party tools in an emergency.',
  'flags.templatePicker.killSwitchName': 'Kill Switch',
  'flags.templatePicker.migration2StageDescription':
    "For migrations where you can't run the old and new systems at the same time.",
  'flags.templatePicker.migration2StageName': '2 stage migration',
  'flags.templatePicker.migration4StageDescription':
    'For migrations where you can run the old and new systems at the same time.',
  'flags.templatePicker.migration4StageName': '4 stage migration',
  'flags.templatePicker.migration6StageDescription': 'For migrations where you migrate reads and writes separately.',
  'flags.templatePicker.migration6StageName': '6 stage migration',
  'flags.templatePicker.migrationHeading': 'Migration',
  'flags.templatePicker.releaseDescription':
    'A temporary flag that serves false to everyone, then gradually rolls out true to 100%.',
  'flags.templatePicker.releaseName': 'Release',
  'flags.templatePicker.selectedTooltip': 'Template: {templateName}',
  'flags.triggerConfirmDialog.cancel': 'Cancel',
  'flags.triggerConfirmDialog.confirm': '{action, select, reset {Reset} other {Delete}}',
  'flags.triggerConfirmDialog.description':
    '{action, select, reset {You cannot revert to the old URL after you reset it. You must update any clients using this URL to use the new URL.} other {You cannot restore a trigger after you delete it. You must update any clients using this URL to use a new URL.}}',
  'flags.triggerConfirmDialog.title': '{action, select, reset {Reset trigger URL?} other {Delete trigger?}}',
  'flags.triggerConfirmDialog.typeToConfirm': 'Type "{name}" to confirm.',
  'flags.triggerList.copyUrlHeading': 'Copy and save this URL.',
  'flags.triggerList.created': 'Created',
  'flags.triggerList.createdBy': 'by {name}',
  'flags.triggerList.createdByYou': 'you',
  'flags.triggerList.deleteTrigger': 'Delete trigger',
  'flags.triggerList.disableTrigger': 'Disable trigger',
  'flags.triggerList.disabledTag': 'DISABLED',
  'flags.triggerList.enableTrigger': 'Enable trigger',
  'flags.triggerList.executedCount': 'Executed {count, plural, one {# time} other {# times}}',
  'flags.triggerList.failedStatus': 'Failed',
  'flags.triggerList.lastFailedLabel': 'Last failed',
  'flags.triggerList.logoAlt': '{name}-logo',
  'flags.triggerList.optionsLabel': '{name} trigger options',
  'flags.triggerList.reEnableTooltip': 'Reset failed state and re-enable this trigger.',
  'flags.triggerList.reEnableTrigger': 'Re-enable trigger',
  'flags.triggerList.resetUrl': 'Reset trigger URL',
  'flags.triggerList.resetUrlDisabledTooltip':
    'Reset trigger URL is unavailable while this trigger is failed. Re-enable the trigger first.',
  'flags.triggerList.targetingUpdatesTo': 'Trigger updates flag targeting to',
  'flags.triggerList.triggerName': '{name} trigger',
  'flags.triggerList.urlHiddenBody': 'After you leave this page, the URL will be hidden. To learn more,',
  'flags.triggerSettings.actionLabel': 'Trigger action',
  'flags.triggerSettings.addTrigger': 'Add trigger',
  'flags.triggerSettings.body': 'Use triggers to remotely turn flag targeting on or off. To learn more,',
  'flags.triggerSettings.cancelTooltip': 'Cancel trigger creation',
  'flags.triggerSettings.createHeading': 'Create trigger',
  'flags.triggerSettings.formLabel': 'Create flag trigger',
  'flags.triggerSettings.heading': 'Triggers',
  'flags.triggerSettings.save': 'Save Trigger',
  'flags.triggerSettings.typeLabel': 'Trigger type',
  'flags.triggerSettings.updateTargetingOption': 'Update flag targeting to',
  'flags.typeFilter.any': 'Any',
  'flags.typeFilter.label': 'Type',
  'flags.typeFilter.permanent': 'Permanent',
  'flags.typeFilter.temporary': 'Temporary',
  'flags.userFeedbackPage.title': 'Feedback',
  'flags.variationDialog.booleanValueTooltip': 'Boolean variation values cannot be edited',
  'flags.variationDialog.cancel': 'Cancel',
  'flags.variationDialog.delete': 'Delete',
  'flags.variationDialog.formatJsonLabel': 'format json',
  'flags.variationDialog.formatJsonTooltip': 'Format JSON',
  'flags.variationDialog.heading': 'Configure variation',
  'flags.variationDialog.jsonPlaceholder': 'Enter JSON value',
  'flags.variationDialog.nameLabel': 'Name',
  'flags.variationDialog.namePlaceholder': 'Enter name',
  'flags.variationDialog.numberPlaceholder': 'Enter number',
  'flags.variationDialog.numberValueLabel': 'Variation value',
  'flags.variationDialog.save': 'Save',
  'flags.variationDialog.stringPlaceholder': 'Enter string value',
  'flags.variationDialog.valueLabel': 'Value',
  'flags.variationEditor.addVariation': 'Add variation',
  'flags.variationEditor.notSet': 'Not set',
  'flags.variationFields.description': 'Description',
  'flags.variationFields.descriptionLabel': 'Description (optional)',
  'flags.variationFields.emptyStringPlaceholder': '(empty string)',
  'flags.variationFields.name': 'Name',
  'flags.variationFields.nameLabel': 'Name (optional)',
  'flags.variationFields.value': 'Value',
  'flags.variationFields.valueInputLabel': 'variation value',
  'flags.variationFields.valueLabel': 'Value',
  'flags.variationMultiSelect.label': 'Variation',
  'flags.variationOrRollout.bucketByNote':
    'If this rule is evaluated for context kinds other than the one selected, those contexts will be served the first variation -',
  'flags.variationOrRollout.byLabel': 'By',
  'flags.variationOrRollout.on': 'On',
  'flags.variationOrRollout.optionsListLabel': 'Variations, rollouts, and optimize',
  'flags.variationOrRollout.percentageRollout': 'A percentage rollout',
  'flags.variationOrRollout.percentageRolloutLabel': 'Percentage rollout',
  'flags.variationOrRollout.selectLabel': 'Select variation',
  'flags.variationOrRollout.selectPlaceholder': 'Select…',
  'flags.variationOrRollout.serve': 'Serve',
  'flags.variationOrRollout.trackEventsWarning':
    'Editing this rule will change which information LaunchDarkly sends to existing {destinations}',
  'flags.variationOrRollout.unknownVariation': 'Unknown variation',
  'flags.variationPicker.heading': 'Select a variation to serve',
  'flags.variationPicker.listLabel': 'Variations',
  'flags.variationPicker.placeholder': 'Select a variation',
  'flags.variationPicker.searchLabel': 'search variations',
  'flags.variationPicker.selectLabel': 'Select variation',
  'flags.variationPreview.baselineLabel': 'Baseline variation',
  'flags.variationPreview.comparisonLabel': 'Comparison variation',
  'flags.variationPreview.displayModeLabel': 'Display mode',
  'flags.variationPreview.errorBanner': 'Preview failed: {error}',
  'flags.variationPreview.heading': 'Variation preview',
  'flags.variationPreview.noRunBody': 'No preview run yet — click "Run preview" to compare how this variation renders.',
  'flags.variationPreview.overlayMode': 'Overlay',
  'flags.variationPreview.runCompleteBody': 'Run complete — results will appear shortly.',
  'flags.variationPreview.runningBanner': 'Running preview…',
  'flags.variationPreview.runningBannerWithStep': 'Running preview — {step}…',
  'flags.variationPreview.serviceConfigHeading': 'Preview service configuration',
  'flags.variationPreview.serviceUrlLabel': 'Preview service URL',
  'flags.variationPreview.singleVariationBody': 'This flag has only one variation — nothing to compare.',
  'flags.variationPreview.splitMode': 'Split',
  'flags.variationPreview.versus': 'vs.',
  'flags.variationPreviewModal.afterAlt': 'after — step {step}',
  'flags.variationPreviewModal.beforeAlt': 'before — step {step}',
  'flags.variationPreviewModal.changeCountLabel': 'Change count',
  'flags.variationPreviewModal.closeLabel': 'Close',
  'flags.variationPreviewModal.dialogLabel': 'Step {step} preview',
  'flags.variationPreviewModal.diffAlt': 'diff — step {step}',
  'flags.variationPreviewModal.displayModeLabel': 'Display mode',
  'flags.variationPreviewModal.overlayMode': 'Overlay',
  'flags.variationPreviewModal.splitMode': 'Split',
  'flags.variationPreviewModal.stepLabel': 'Step {step}',
  'flags.variationPreviewSession.autoPick': 'Auto-pick',
  'flags.variationPreviewSession.label': 'Session',
  'flags.variationPreviewStep.changeStatusLabel': 'Change status',
  'flags.variationPreviewStep.noChanges': 'No changes',
  'flags.variationPreviewStep.openLabel': 'Open step {step} preview',
  'flags.variationPreviewStep.stepLabel': 'Step {step}',
  'flags.variationValidation.mustBeBoolean': 'This must be true or false',
  'flags.variationValidation.mustBeJson': 'This must be valid JSON',
  'flags.variationValidation.mustBeNumber': 'This must be a number',
  'flags.variationValidation.valueRequired': 'A value is required',
  'flags.variationsForm.duplicateName': "You can't create variations with the same name.",
  'flags.variationsForm.duplicateValue': "You can't create variations with the same value",
  'flags.variationsInUse.body': 'Changing the values of variations in use may impact the following environments:',
  'flags.variationsInUse.environmentCount': '{count, plural, one {# environment} other {# environments}}',
  'flags.variationsInUse.showAll': 'Show all {count, plural, one {# variation} other {# variations}} in use',
  'flags.variationsInUseWarning.body':
    'Changing the values of variations in use may impact the following environments:',
  'flags.variationsInUseWarning.environmentCount': '{count, plural, one {# environment} other {# environments}}',
  'flags.variationsInUseWarning.heading': 'Variations in use',
  'flags.variationsInUseWarning.showAll': 'Show all {count, plural, one {# variation} other {# variations}} in use',
  'flags.variationsLabel.noOffVariation': '(No off variation set)',
  'flags.variationsLabel.offVariationSuffix': '- off variation',
  'flags.variationsPage.title': 'Variations',
  'flags.variationsReview.addVariation': 'Add variation',
  'flags.variationsReview.approvalRequested': 'Approval requested successfully and reviewers have been notified.',
  'flags.variationsReview.changeDefault': 'Change default',
  'flags.variationsReview.closeLabel': 'close',
  'flags.variationsReview.defaultOff': 'Off',
  'flags.variationsReview.defaultOn': 'On',
  'flags.variationsReview.defaultStatusLabel': 'status',
  'flags.variationsReview.defaultVariationTo': 'variation to',
  'flags.variationsReview.defaultVariationToNew': 'variation to newly added variation',
  'flags.variationsReview.defaultVariationsCategory': 'Default variation for new environments',
  'flags.variationsReview.defaultVariationsJoin': 'and',
  'flags.variationsReview.integrationApprovalRequested': 'Approval requested successfully.',
  'flags.variationsReview.loading': 'Loading affected environments',
  'flags.variationsReview.noInstructions': "These changes can't be saved. Undo them and try again.",
  'flags.variationsReview.pendingChangesDataError':
    "We couldn't load the environments this change affects, so it can't be saved. Close this dialog and try again.",
  'flags.variationsReview.removeVariation': 'Remove variation',
  'flags.variationsReview.saveFailed': "Your changes weren't saved. Try again.",
  'flags.variationsReview.updateVariation': 'Update variation',
  'flags.variationsReview.variationsCategory': 'Variations',
  'flags.variationsRewritePreview.enable': 'Enable',
  'flags.variationsRewritePreview.enableMessage': 'Help us dogfood the variations rewrite',
  'flags.variationsRewritePreview.reportIssue': 'Report an issue',
  'flags.variationsRewritePreview.reportIssueBody':
    'Tell us what went wrong on the new variations page. Reports go to the team building it.',
  'flags.variationsRewritePreview.reportIssueCancel': 'Cancel',
  'flags.variationsRewritePreview.reportIssueFailed': "Couldn't send your report. Try again.",
  'flags.variationsRewritePreview.reportIssueHeading': 'Report an issue',
  'flags.variationsRewritePreview.reportIssueLabel': 'What went wrong?',
  'flags.variationsRewritePreview.reportIssueSent': 'Report sent',
  'flags.variationsRewritePreview.reportIssueSubmit': 'Send report',
  'flags.variationsRewritePreview.switchBack': 'Switch back',
  'flags.variationsRewritePreview.switchBackMessage': 'Using the variations rewrite',
  'flags.variationsSaveBar.duplicateNames': 'All variation names must be unique',
  'flags.variationsSaveBar.duplicateValues': 'All variation values must be unique',
  'flags.variationsSaveBar.incomplete': 'Your configuration is incomplete',
  'flags.variationsSaveBar.typeMismatch': 'All variations must have the same type',
  'flags.variationsTable.actions': 'Actions',
  'flags.variationsTable.addVariation': 'Add variation',
  'flags.variationsTable.ai': '✨ AI',
  'flags.variationsTable.defaultVariationsHint':
    'Changes to these default rules will only apply to newly created environments. Flag configuration in existing environments will not be changed.',
  'flags.variationsTable.delete': 'Delete',
  'flags.variationsTable.description': 'Description',
  'flags.variationsTable.descriptionPlaceholder': 'Add description…',
  'flags.variationsTable.generatedWithAiLabel': 'Generated with AI',
  'flags.variationsTable.name': 'Name',
  'flags.variationsTable.tableLabel': 'Variations',
  'flags.variationsTable.value': 'Value',
  'flags.variationsTable.variationDescriptionLabel': 'Variation {index} description',
  'flags.variationsTable.variationNameLabel': 'Variation {index} name',
  'flags.variationsTable.variationValueLabel': 'Variation {index} value',
  'flags.workflowsPage.title': 'Workflows',
  'flags_.page.title': 'Flags',
  'githubConnect.accountChangedBody':
    'Your active LaunchDarkly account changed during setup. Choose the account to link, then try again.',
  'githubConnect.accountFallback': 'this account',
  'githubConnect.approvalPendingBody':
    'An organization owner must approve this installation request in GitHub before you can link the app to LaunchDarkly.',
  'githubConnect.approvalPendingHeading': 'GitHub App not linked',
  'githubConnect.authorizeRedirecting': 'Redirecting to GitHub…',
  'githubConnect.body': 'LaunchDarkly will link the repositories in this GitHub App installation to “{accountName}”.',
  'githubConnect.chooseAccount': 'Choose account',
  'githubConnect.conflictBody':
    'This GitHub App installation is linked to a different LaunchDarkly account. To use it, switch to that account. If it is linked to the wrong account, <supportLink>contact support</supportLink>.',
  'githubConnect.conflictHeading': 'GitHub App already linked',
  'githubConnect.errorHeading': 'GitHub App not linked',
  'githubConnect.expiredBody': 'This setup session expired or was already used. Start setup again.',
  'githubConnect.githubAccessBody':
    'GitHub could not confirm that you have access to this GitHub App installation. Ask a GitHub organization administrator to finish setup.',
  'githubConnect.githubExchangeBody': 'LaunchDarkly could not complete authorization with GitHub. Try again.',
  'githubConnect.heading': 'Link GitHub to LaunchDarkly',
  'githubConnect.invalidBody': 'LaunchDarkly could not verify this setup session. Start setup again.',
  'githubConnect.linkAccount': 'Link account',
  'githubConnect.pendingBody': 'The GitHub App is installed but not linked to “{accountName}”.',
  'githubConnect.pendingHeading': 'Finish linking the GitHub App',
  'githubConnect.retry': 'Try again',
  'githubConnect.returnToIntegration': 'Back to integration',
  'githubConnect.startFailed': 'LaunchDarkly could not start setup with GitHub. Try again.',
  'githubConnect.switchAccount': 'Switch account',
  'guardedRollouts.contextKindField.loading': 'Loading context kinds…',
  'guardedRollouts.contextKindField.pickerLabel': 'Context kind',
  'guardedRollouts.contextKindField.pickerPlaceholder': 'Select a context kind',
  'guardedRollouts.contextKindField.targetBy': 'Target by',
  'guardedRollouts.durationEditor.addStepLabel': 'Add step below',
  'guardedRollouts.durationEditor.deleteStepLabel': 'Delete',
  'guardedRollouts.durationEditor.errors': 'Errors',
  'guardedRollouts.durationEditor.intervalQuantityLabel': 'interval quantity',
  'guardedRollouts.durationEditor.intervalUnitLabel': 'interval unit',
  'guardedRollouts.durationEditor.rolloutDuration': 'Rollout duration',
  'guardedRollouts.durationEditor.rolloutPercentageLabel': 'step {step} rollout percentage',
  'guardedRollouts.durationEditor.totalDuration': 'Total duration',
  'guardedRollouts.page.connectAction': 'Connect your SDK to safely release with guarded rollouts.',
  'guardedRollouts.page.label': 'Search flags',
  'guardedRollouts.page.placeholder': 'Search flags by name, description or key',
  'guardedRollouts.page.somethingWentWrong': 'Something went wrong loading flags',
  'guardedRollouts.page.somethingWentWrong2': 'Something went wrong',
  'guardedRollouts.page.title': 'Guarded rollouts',
  'guardedRollouts.phaseMetricsTable.analyzeBy': 'Analyze by',
  'guardedRollouts.phaseMetricsTable.autoRollback': 'Auto rollback',
  'guardedRollouts.phaseMetricsTable.metric': 'Metric',
  'guardedRollouts.phaseMetricsTable.tableLabel': 'Rollout metrics',
  'guardedRollouts.phaseMetricsTable.threshold': 'Threshold',
  'guardedRollouts.phaseReview.byContextKind': 'by <code>{kind}</code>',
  'guardedRollouts.phaseReview.cleanupDescription': 'Remove rules and update default rule to serve',
  'guardedRollouts.phaseReview.cleanupPhaseLabel': 'Cleanup phase',
  'guardedRollouts.phaseReview.metricsLabel': 'Metrics',
  'guardedRollouts.phaseReview.originalVariationLabel': 'Original variation',
  'guardedRollouts.phaseReview.rolloutDurationLabel': 'Rollout duration',
  'guardedRollouts.phaseReview.rolloutLabel': 'Rollout',
  'guardedRollouts.phaseReview.stepCountLabel': 'step count',
  'guardedRollouts.phaseReview.variationNotFound': 'VARIATION NOT FOUND',
  'guardedRollouts.phaseStagesTable.duration': 'Duration',
  'guardedRollouts.phaseStagesTable.step': 'Step',
  'guardedRollouts.phaseStagesTable.tableLabel': 'Rollout steps',
  'guardedRollouts.phaseStagesTable.weight': 'Weight',
  'guardedRollouts.phaseTargetSelector.emptyPlaceholder': 'No valid context kinds',
  'guardedRollouts.phaseTargetSelector.loadingLabel': 'loading context kinds',
  'guardedRollouts.phaseTargetSelector.showingAvailableContext': 'Showing available context kinds',
  'guardedRollouts.phaseTargetSelector.targetBy': 'Target by',
  'guardedRollouts.stepValidation.durationInvalid': 'Enter a duration',
  'guardedRollouts.stepValidation.durationMax': 'Decrease duration to no more than {quantity} {unit}',
  'guardedRollouts.stepValidation.durationMin': 'Increase duration to at least {quantity} {unit}',
  'guardedRollouts.stepValidation.durationRequired': 'Required',
  'guardedRollouts.stepValidation.keyRequired': 'step key is required',
  'guardedRollouts.stepValidation.rolloutWeightGuardedMax':
    'To balance audience measurement, step cannot exceed 50% when metrics are attached',
  'guardedRollouts.stepValidation.rolloutWeightIncreasing': 'Increase rollout percentage from previous step',
  'guardedRollouts.stepValidation.rolloutWeightInteger': 'Set rollout percentage to be an integer',
  'guardedRollouts.stepValidation.rolloutWeightMin': 'Increase rollout percentage to at least 0.001%',
  'guardedRollouts.stepValidation.rolloutWeightRequired': 'Set rollout percentage',
  'guardedRollouts.stepValidation.totalDurationMax':
    'Total rollout duration ({total}) exceeds the maximum allowed duration ({max})',
  'guardedRollouts.validation.monitoringWindowMax': 'Guarded rollouts cannot be monitored for more than {duration}',
  'guardedRollouts.validation.monitoringWindowMin': 'Guarded rollouts require a monitoring window greater than zero',
  'guardedRollouts.validation.randomizationUnitRequired': 'Guarded rollouts require a randomization unit',
  'guardedRollouts.validation.trafficMax':
    'Guarded rollouts cannot allocate more than {percent}% of traffic to the test variation',
  'guardedRollouts.validation.trafficMin': 'Guarded rollouts require a traffic allocation greater than zero',
  'healthCheck.contextKindCheck.checking': 'Checking {displayName} evaluation status…',
  'healthCheck.contextKindCheck.evaluating': '<code>{contextKind}</code> contexts are evaluating your {displayName}.',
  'healthCheck.contextKindCheck.loadError': 'Loading {displayName} evaluation status failed. Try again later.',
  'healthCheck.contextKindCheck.noEvaluations':
    '{name} is not receiving evaluations. Normal if your {displayName} has been created recently.',
  'healthCheck.contextKindCheck.noSelection': 'No flag or config selected.',
  'healthCheck.contextKindCheck.notEvaluating':
    '<code>{contextKind}</code> contexts are not evaluating your {displayName}.',
  'healthCheck.contextKindCheck.unavailable': '{name} evaluation status is not available.',
  'healthCheck.flagCheck.archived': '{name} is archived.',
  'healthCheck.flagCheck.checking': 'Checking {displayName} status…',
  'healthCheck.flagCheck.deprecated': '{name} is deprecated.',
  'healthCheck.flagCheck.evaluating': '<code>{contextKind}</code> contexts are evaluating your {displayName}.',
  'healthCheck.flagCheck.loadError': 'Loading {displayName} status failed. Try again later.',
  'healthCheck.flagCheck.noEvaluations':
    '{name} is not receiving evaluations. Normal if your {displayName} has been created recently.',
  'healthCheck.flagCheck.noSelection': 'No flag or config selected.',
  'healthCheck.flagCheck.notEvaluating': '<code>{contextKind}</code> contexts are not evaluating your {displayName}.',
  'healthCheck.flagCheck.turnOnLink': 'Turn on the {displayName}.',
  'healthCheck.flagCheck.turnedOff': '{name} is turned off.',
  'healthCheck.flagCheck.turnedOn': '{name} is turned on.',
  'healthCheck.flagCheck.viewArchived': 'View archived {displayName}.',
  'healthCheck.flagCheck.viewDeprecated': 'View deprecated {displayName}.',
  'healthCheck.flagStatusCheck.archived': '{name} is archived.',
  'healthCheck.flagStatusCheck.checking': 'Checking {displayName} status…',
  'healthCheck.flagStatusCheck.deprecated': '{name} is deprecated.',
  'healthCheck.flagStatusCheck.loadError': 'Loading {displayName} status failed. Try again later.',
  'healthCheck.flagStatusCheck.noSelection': 'No flag or config selected.',
  'healthCheck.flagStatusCheck.turnOnLink': 'Turn on the {displayName}.',
  'healthCheck.flagStatusCheck.turnedOff': '{name} is turned off.',
  'healthCheck.flagStatusCheck.turnedOn': '{name} is turned on.',
  'healthCheck.flagStatusCheck.viewArchived': 'View archived {displayName}.',
  'healthCheck.flagStatusCheck.viewDeprecated': 'View deprecated {displayName}.',
  'healthCheck.metricsCheck.checking': 'Checking metric activity…',
  'healthCheck.metricsCheck.loadError': 'Loading metric activity failed.',
  'healthCheck.metricsCheck.noMetricsSelected': 'No metrics selected.',
  'healthCheck.metricsCheck.noRecentEvents': 'No recently seen events for',
  'healthCheck.metricsCheck.notTrackedBy': '<code>{contextKind}</code> is not tracked by',
  'healthCheck.metricsCheck.trackedBy': '<code>{contextKind}</code> is tracked by',
  'healthCheck.panel.loadingLabel': 'Loading health check status',
  'healthCheck.panel.reload': 'Reload',
  'healthCheck.panel.retryLabel': 'Retry health check',
  'healthCheck.panel.statusLabel': 'Health check status',
  'healthCheck.targetingRuleCheck.checking': 'Checking targeting rule status…',
  'healthCheck.targetingRuleCheck.loadError': 'Loading targeting rule status failed.',
  'healthCheck.targetingRuleCheck.matchesUnit':
    "The rule's context kind, <code>{contextKind}</code>, matches this experiment's randomization unit.",
  'healthCheck.targetingRuleCheck.noMatch':
    "{count, plural, one {The rule's context kind does not match the experiment's randomization unit.} other {The rule's context kinds do not match the experiment's randomization unit.}}",
  'healthCheck.targetingRuleCheck.noSelection': 'No targeting rule selected.',
  'healthCheck.targetingRuleCheck.partialMatch':
    "The rule's context kinds partially match this experiment's randomization unit.",
  'healthCheck.targetingRuleCheck.segmentWarning':
    "Targeting rule uses a segment, which may mix context kinds. Confirm the segment's kinds match the experiment's randomization unit.",
  'healthCheck.turnOnFlagModal.body':
    "This serves the {displayName}'s targeting to your audience and starts recording results.",
  'healthCheck.turnOnFlagModal.cancel': 'Cancel',
  'healthCheck.turnOnFlagModal.closeLabel': 'close modal',
  'healthCheck.turnOnFlagModal.confirm': 'Toggle on {displayName}',
  'healthCheck.turnOnFlagModal.heading': 'Turn on {displayName}?',
  'healthCheck.turnOnFlagModal.trigger': 'Turn on the {displayName}.',
  'holdouts.dashboardListPage.createHoldout': 'Create holdout',
  'holdouts.dashboardListPage.tooltip': 'You do not have permission to create a holdout',
  'holdouts.dashboardPage.connectAction2':
    'Connect your SDK to measure and understand the impact of every feature you release.',
  'holdouts.dashboardPage.createHoldout': 'Create holdout',
  'holdouts.dashboardPage.description': 'Filter based on status of holdout experiment',
  'holdouts.dashboardPage.placeholder': 'Search by name',
  'holdouts.dashboardPage.searchForA': 'Search for a holdout by name',
  'holdouts.dashboardPage.statusFilter': 'Status: {status}',
  'holdouts.dashboardPage.title': 'Holdouts',
  'holdouts.dashboardPage.tooltip': 'You do not have permission to create a holdout',
  'holdouts.detailPage.design': 'Design',
  'holdouts.detailPage.results': 'Results',
  'holdouts.newPage.title': 'Create Holdout',
  'holdouts.page.title': 'Holdouts',
  'home.autopilotRoadmap.approveBlurb': 'LaunchDarkly rolls it out after merge.',
  'home.autopilotRoadmap.approveTitle': 'Approve the release plan',
  'home.autopilotRoadmap.approveWhat':
    'Reply <code>@launchdarkly approve</code> on the pull request, or approve on the LaunchDarkly review page. LaunchDarkly creates the flag and pushes a commit that wraps your change. Once the pull request merges, it releases the change following each environment’s release plan.',
  'home.autopilotRoadmap.githubAppBlurb': 'Install it and authorize your account.',
  'home.autopilotRoadmap.githubAppCodeIntro': 'Install the Release autopilot onboarding skill instead.',
  'home.autopilotRoadmap.githubAppCodeIntroLead': 'Using an AI coding agent?',
  'home.autopilotRoadmap.githubAppCodeLabel': 'Terminal',
  'home.autopilotRoadmap.githubAppCta': 'Connect GitHub',
  'home.autopilotRoadmap.githubAppTitle': 'Connect the GitHub App',
  'home.autopilotRoadmap.githubAppWhat':
    'Install the GitHub App on your organization and authorize your account so LaunchDarkly can review and update your pull requests.',
  'home.autopilotRoadmap.mapReposBlurb': 'Choose where autopilot runs.',
  'home.autopilotRoadmap.mapReposCta': 'Map repositories',
  'home.autopilotRoadmap.mapReposTitle': 'Map repositories to projects',
  'home.autopilotRoadmap.mapReposWhat':
    'In Release autopilot settings, map each repository to the LaunchDarkly project its flags belong to. A project’s automations run only on the repositories mapped to it.',
  'home.autopilotRoadmap.plansBlurb': 'Set how each environment releases.',
  'home.autopilotRoadmap.plansCta': 'Create release environment plan',
  'home.autopilotRoadmap.plansTitle': 'Create release environment plans',
  'home.autopilotRoadmap.plansWhat':
    'A mapped project needs a release environment plan for each environment autopilot releases to. A plan sets the release method, for example a guarded rollout for Production, so every release from a pull request follows the same guardrails. Release policies are being replaced by these plans.',
  'home.autopilotRoadmap.pullRequestBlurb': 'Ask for a plan with @launchdarkly plan.',
  'home.autopilotRoadmap.pullRequestCodeIntro': 'Commands you can comment on the pull request:',
  'home.autopilotRoadmap.pullRequestCodeLabel': 'Pull request comment',
  'home.autopilotRoadmap.pullRequestTitle': 'Open a pull request',
  'home.autopilotRoadmap.pullRequestWhat':
    'Open a pull request on a mapped repository as usual. Ask LaunchDarkly for a plan in a comment: it reviews the change and replies with a proposed flag and release plan. Answer its questions and adjust the plan with more comments.',
  'home.inlineCreate.createdChip': 'Created {flagKey}',
  'home.inlineCreate.experimentCreatedBody':
    'That’s a complete experiment running on a sample flag and metric, so you’ve seen the shape end to end. Now set up one on your own product.',
  'home.inlineCreate.experimentCreatedHeading': 'Scaffolded “Better button copy”',
  'home.inlineCreate.experimentSummaryHeading': 'What we built for you',
  'home.inlineCreate.flagCreatedBody': 'Nice, that’s a live flag. On its own it does nothing yet, so let’s keep going.',
  'home.inlineCreate.flagKeyLabel': 'Flag key',
  'home.inlineCreate.flagTypeSummary': 'Boolean · on / off',
  'home.inlineCreate.ideasHeading': 'Ideas to test',
  'home.inlineCreate.nextStep': 'Next: {title}',
  'home.inlineCreate.scaffoldExperimentSub': 'Frequentist, 50/50, randomized by user',
  'home.inlineCreate.scaffoldExperimentTitle': 'Experiment · {experimentKey}',
  'home.inlineCreate.scaffoldFlagSub': 'Control “{control}” · Treatment “{treatment}”',
  'home.inlineCreate.scaffoldFlagTitle': 'Flag · {flagKey}',
  'home.inlineCreate.scaffoldMetricSub': 'Binary conversion',
  'home.inlineCreate.scaffoldMetricTitle': 'Metric · {metricKey}',
  'home.observabilityRoadmap.pluginsBlurb': 'Session replay and observability for your SDK.',
  'home.observabilityRoadmap.pluginsCta': 'Add plugins',
  'home.observabilityRoadmap.pluginsIdeaBrowser': 'JS SDK + 2 plugins',
  'home.observabilityRoadmap.pluginsIdeaServer': 'Node / Python server',
  'home.observabilityRoadmap.pluginsIdeasLabel': 'Install paths',
  'home.observabilityRoadmap.pluginsTitle': 'Add the plugins',
  'home.observabilityRoadmap.pluginsWhat':
    'Add the observability and session replay plugins to the SDK you installed. It then captures replays, errors, console, network, and performance automatically.',
  'home.observabilityRoadmap.sdkBlurb': 'One init call connects your app.',
  'home.observabilityRoadmap.sdkCta': 'Install SDK',
  'home.observabilityRoadmap.sdkIdeaJavaScript': 'JavaScript',
  'home.observabilityRoadmap.sdkIdeaNode': 'Node',
  'home.observabilityRoadmap.sdkIdeaPython': 'Python',
  'home.observabilityRoadmap.sdkIdeaReact': 'React',
  'home.observabilityRoadmap.sdkIdeaReactNative': 'React Native',
  'home.observabilityRoadmap.sdkIdeasLabel': 'Install with',
  'home.observabilityRoadmap.sdkTitle': 'Install the SDK and connect your app',
  'home.observabilityRoadmap.sdkWhat':
    'Install the SDK in your app and initialize it. To record session replays, use a client-side or mobile SDK.',
  'home.page.title': 'Home',
  'home.productPicker.featureStatusLabel': 'Feature status',
  'home.productPicker.newBadge': 'New',
  'home.roadmap.body': 'Pick whatever matters most today. Set up the rest over time, in any order.',
  'home.roadmap.heading': 'Where do you want to start?',
  'home.roadmap.starterApp': 'Get the starter app',
  'home.roadmap.starterAppLabel': 'Get the starter app on GitHub (opens in a new tab)',
  'foundationCostCalculator.agentControl.allowance': '{free} runs included',
  'foundationCostCalculator.agentControl.annualLimit':
    'AgentControl runs are billed monthly based on usage. Switch to monthly billing to see cost estimates for runs beyond the included {free} per month.',
  'foundationCostCalculator.agentControl.cost':
    '{billable} runs over the free {free} × {rate} per {blockSize} = <strong>{cost}</strong>/mo',
  'foundationCostCalculator.agentControl.costWithinFree': '<strong>{cost}</strong>/mo within the free {free}',
  'foundationCostCalculator.agentControl.heading': 'Production AI runs',
  'foundationCostCalculator.agentControl.inputLabel': 'Production AI runs per month',
  'foundationCostCalculator.agentControl.subheading':
    'One run = one tracked LLM request or one automated quality check (online eval). First {free} free each month, then {rate} per {blockSize}, billed monthly on every plan. LaunchDarkly keeps run data for 30 days on Foundation.',
  'foundationCostCalculator.agentControl.title': 'AgentControl',
  'foundationCostCalculator.agentControl.unit': 'runs per month',
  'foundationCostCalculator.clientTraffic.cost':
    '{billable} billable × {rate} per {blockSize} = <strong>{cost}</strong>/mo',
  'foundationCostCalculator.clientTraffic.formulaManyVisits':
    '<num>{people}</num> people × <num>{visits}</num> visits, each visit a new key = <num>{mau}</num> MAU. Storing the ID would make it {people}.',
  'foundationCostCalculator.clientTraffic.formulaNone':
    'No web or mobile app usage, so 0 MAU. Backend services never create MAU.',
  'foundationCostCalculator.clientTraffic.formulaOneVisit':
    '<num>{people}</num> people × <num>1</num> visit each, every visit a new key = <num>{mau}</num> MAU',
  'foundationCostCalculator.clientTraffic.formulaStoredId':
    '<num>{people}</num> people, each counted once because the ID is stored = <num>{mau}</num> MAU',
  'foundationCostCalculator.clientTraffic.heading': 'Client-side traffic',
  'foundationCostCalculator.clientTraffic.peopleHint':
    'Signed-in and anonymous together. LaunchDarkly bills unique keys, so if your SDK keys on device, count devices. Your analytics tool shows monthly active users or unique devices. Enter 0 if flags run only on the backend.',
  'foundationCostCalculator.clientTraffic.peopleLabel':
    'Unique people or devices your web or mobile app sees per month',
  'foundationCostCalculator.clientTraffic.peopleUnit': 'per month',
  'foundationCostCalculator.clientTraffic.persistenceHint':
    'A cookie or localStorage key that persists between visits. Without one, every visit counts as a new MAU.',
  'foundationCostCalculator.clientTraffic.persistenceLabel': 'Stored ID for anonymous visitors',
  'foundationCostCalculator.clientTraffic.persistenceNo': 'No',
  'foundationCostCalculator.clientTraffic.persistenceUnsure': 'Not sure',
  'foundationCostCalculator.clientTraffic.persistenceYes': 'Yes',
  'foundationCostCalculator.clientTraffic.subheading':
    'One MAU = one unique person or device your app evaluates flags for in a month. First {free} free, then {rate} per {blockSize}.',
  'foundationCostCalculator.clientTraffic.unit': 'MAU',
  'foundationCostCalculator.clientTraffic.unsureRange':
    'You picked Not sure, so the estimate uses the higher case. Your real number is between {low} and {high} MAU.',
  'foundationCostCalculator.clientTraffic.visitsHint':
    'Each visit becomes a new key when no ID is stored. An estimate is fine.',
  'foundationCostCalculator.clientTraffic.visitsLabel': 'Visits per visitor per month',
  'foundationCostCalculator.clientTraffic.visitsUnit': 'visits',
  'foundationCostCalculator.controls.decrease': 'Decrease {label}',
  'foundationCostCalculator.controls.increase': 'Increase {label}',
  'foundationCostCalculator.countDisclosure.label': 'How LaunchDarkly counts this',
  'foundationCostCalculator.firstWeek.applicationIdBody':
    'Otherwise your usage page says “Unknown” and you cannot see which service is using connections.',
  'foundationCostCalculator.firstWeek.applicationIdTitle': 'Set an application ID on every service',
  'foundationCostCalculator.firstWeek.devServerBody':
    'It serves flags from a local process, so those hours create no connections.',
  'foundationCostCalculator.firstWeek.devServerTitle': 'Point laptops and CI at the dev server',
  'foundationCostCalculator.firstWeek.heading': 'Your first week after you buy',
  'foundationCostCalculator.firstWeek.sdkClientBody':
    'Not per request. Forked workers each need their own. Threads in one process share it.',
  'foundationCostCalculator.firstWeek.sdkClientTitle': 'Create one SDK client per process, at startup',
  'foundationCostCalculator.flags.allowance': 'Included',
  'foundationCostCalculator.flags.heading':
    'Unlimited seats, flags, projects, and environments. Experimentation included.',
  'foundationCostCalculator.flags.subheading': 'No platform fee. You pay for the 2 usage meters below.',
  'foundationCostCalculator.flags.title': 'Feature flags and experimentation',
  'foundationCostCalculator.interval.annual': 'Pay yearly',
  'foundationCostCalculator.interval.label': 'Billing interval',
  'foundationCostCalculator.interval.monthly': 'Pay monthly',
  'foundationCostCalculator.limits.body':
    'Foundation bought online on a yearly plan goes up to {limits}. To prepay for more, talk to sales.',
  'foundationCostCalculator.limits.contactSales': 'Talk to sales',
  'foundationCostCalculator.limits.errors': '{limit} errors',
  'foundationCostCalculator.limits.logs': '{limit} log lines',
  'foundationCostCalculator.limits.monthlyActiveUsers': '{limit} MAU',
  'foundationCostCalculator.limits.serviceConnections': '{limit} service connections',
  'foundationCostCalculator.limits.sessions': '{limit} session replays',
  'foundationCostCalculator.limits.traces': '{limit} trace spans',
  'foundationCostCalculator.noChange.clientSdksBody':
    'They never create a connection. They count as monthly active users instead.',
  'foundationCostCalculator.noChange.clientSdksTitle': 'Your browser, mobile, and edge SDKs',
  'foundationCostCalculator.noChange.flagsBody':
    '10 flags and 10,000 flags cost the same. Backend requests do not count. The one exception is PHP, which polls instead of streaming: 732 polls a month equals 1 connection.',
  'foundationCostCalculator.noChange.flagsTitle': 'How many flags you have, or how often you evaluate them',
  'foundationCostCalculator.noChange.heading': 'What does not change your bill',
  'foundationCostCalculator.noChange.reconnectsBody':
    'A dropped stream that reconnects does not start a new charge. Only connected minutes count. A rolling deploy counts the few minutes old and new instances overlap.',
  'foundationCostCalculator.noChange.reconnectsTitle': 'Reconnects and rolling deploys',
  'foundationCostCalculator.noChange.relayBody':
    'LaunchDarkly counts the SDKs behind the proxy, not the proxy. Neither changes your count or your rate.',
  'foundationCostCalculator.noChange.relayTitle': 'The Relay Proxy or daemon mode',
  'foundationCostCalculator.noChange.serverSizeBody':
    'CPU and memory do not count, on Kubernetes or anywhere else. What counts is how many instances run, and for how long.',
  'foundationCostCalculator.noChange.serverSizeTitle': 'How big your servers are',
  'foundationCostCalculator.observability.allowance': 'Included up to limits',
  'foundationCostCalculator.observability.cost': '<strong>{cost}</strong>/mo',
  'foundationCostCalculator.observability.disclosure': 'Add expected volume',
  'foundationCostCalculator.observability.errorsInputLabel': 'Errors per month',
  'foundationCostCalculator.observability.errorsLabel': 'Errors',
  'foundationCostCalculator.observability.heading': 'Session replays, errors, logs, and traces',
  'foundationCostCalculator.observability.logsInputLabel': 'Log lines per month',
  'foundationCostCalculator.observability.logsLabel': 'Log lines',
  'foundationCostCalculator.observability.rateHint': '{rate} per {blockSize} after {free}',
  'foundationCostCalculator.observability.rateHintWithTiers':
    '{rate} per {blockSize} after {free}. Volume tiers lower this above {tierStart} a month',
  'foundationCostCalculator.observability.sessionsInputLabel': 'Session replays per month',
  'foundationCostCalculator.observability.sessionsLabel': 'Session replays',
  'foundationCostCalculator.observability.subheadingAnnual':
    'Free each month: {sessions} session replays, {errors} errors, {logs} log lines, and {traces} trace spans. Above that, you prepay a volume for the year at the rates below. Anything over it bills monthly at the pay-monthly rates.',
  'foundationCostCalculator.observability.subheadingMonthly':
    'Free each month: {sessions} session replays, {errors} errors, {logs} log lines, and {traces} trace spans. Overages are billed at the rates below.',
  'foundationCostCalculator.observability.title': 'Observability',
  'foundationCostCalculator.observability.tracesInputLabel': 'Trace spans per month',
  'foundationCostCalculator.observability.tracesLabel': 'Trace spans',
  'foundationCostCalculator.observability.unit': 'per month',
  'foundationCostCalculator.page.disclaimer':
    'Estimate only. Your bill is based on measured usage: connected minutes divided by {minutesPerMonth} for connections, unique context keys per month for MAU.',
  'foundationCostCalculator.page.heading': 'Estimate your Foundation cost',
  'foundationCostCalculator.page.loading': 'Loading Foundation pricing',
  'foundationCostCalculator.page.subtitle':
    'Rough estimates are fine. The calculator converts them to what LaunchDarkly bills and shows every assumption.',
  'foundationCostCalculator.page.title': 'Foundation cost calculator',
  'foundationCostCalculator.serviceConnections.assumptionColdStarts':
    'Assumed: 1 cold start per {runsPerColdStart} runs ({coldStarts} a month), {warmMinutes} warm minutes each = {connections} connections. <edit>Edit</edit>',
  'foundationCostCalculator.serviceConnections.assumptionDevServer':
    'Assumed: laptops and CI use the dev server, so they add 0 connections. <edit>Edit</edit>',
  'foundationCostCalculator.serviceConnections.assumptionLaptopsAndCi':
    'Assumed: {engineers} engineers × {laptopHours} hours a day on a laptop = {laptops} connections, and {ciRuns} CI jobs × {ciMinutes} minutes = {ci}. Both 0 with the dev server. <edit>Edit</edit>',
  'foundationCostCalculator.serviceConnections.assumptionNonProd':
    'Assumed: non-production environments run {ratio, select, half {about half the instances of} same {the same number of instances as} other {about 1.5× the instances of}} production. <edit>Edit</edit>',
  'foundationCostCalculator.serviceConnections.assumptionUptime':
    'Assumed: instances run {uptime, select, allMonth {all month} halfTime {about half the time} other {business hours only}}. <edit>Edit</edit>',
  'foundationCostCalculator.serviceConnections.ciRunsHint':
    'Unit tests that mock the SDK or use a test data source never connect, so leave them out. Count only jobs that boot your app against LaunchDarkly. Parallel jobs each count, and the estimate assumes {ciMinutes} minutes each.',
  'foundationCostCalculator.serviceConnections.ciRunsLabel': 'CI jobs per month that connect a real SDK client',
  'foundationCostCalculator.serviceConnections.ciRunsUnit': 'jobs',
  'foundationCostCalculator.serviceConnections.coldStartsHint':
    'After a cold start, an SDK client you create outside the handler stays warm about {warmMinutes} minutes. Leave this on auto to assume 1 cold start per {runsPerColdStart} runs, or enter the number from your cloud console.',
  'foundationCostCalculator.serviceConnections.coldStartsLabel': 'Cold starts per month',
  'foundationCostCalculator.serviceConnections.coldStartsPlaceholder': 'auto',
  'foundationCostCalculator.serviceConnections.coldStartsUnit': 'cold starts',
  'foundationCostCalculator.serviceConnections.copiesHint':
    'Monthly average, not peak, of containers, pods, VMs, or replicas running a server-side SDK. Add up Kubernetes replicas, ECS tasks, or autoscaling groups. Enter 0 if fully serverless.',
  'foundationCostCalculator.serviceConnections.copiesLabel': 'Instances running in production',
  'foundationCostCalculator.serviceConnections.copiesUnit': 'instances',
  'foundationCostCalculator.serviceConnections.cost': '{billable} billable × {rate} = <strong>{cost}</strong>/mo',
  'foundationCostCalculator.serviceConnections.devTargetDevServer': 'The dev server (free)',
  'foundationCostCalculator.serviceConnections.devTargetHint':
    'The LaunchDarkly dev server serves flags from a local process, so laptops and CI pointed at it create no connections. Pointing at LaunchDarkly directly counts every connected hour.',
  'foundationCostCalculator.serviceConnections.devTargetLabel': 'Flag source for laptops and CI',
  'foundationCostCalculator.serviceConnections.devTargetLaunchDarkly': 'LaunchDarkly directly',
  'foundationCostCalculator.serviceConnections.durationHint':
    'LaunchDarkly adds up actual run time, sampled each minute, and does not round a short run up to a minute. Your cloud console reports this as average duration.',
  'foundationCostCalculator.serviceConnections.durationLabel': 'Average run time',
  'foundationCostCalculator.serviceConnections.durationUnit': 'seconds',
  'foundationCostCalculator.serviceConnections.engineersHint':
    'Each engineer running the backend locally against LaunchDarkly counts for the hours connected. Head count is fine.',
  'foundationCostCalculator.serviceConnections.engineersLabel': 'Engineers working on this backend',
  'foundationCostCalculator.serviceConnections.engineersUnit': 'people',
  'foundationCostCalculator.serviceConnections.environmentsHint':
    'Production plus staging, QA, and dev. Every environment bills at the same rate, so each one multiplies the count above.',
  'foundationCostCalculator.serviceConnections.environmentsLabel': 'Environments this runs in',
  'foundationCostCalculator.serviceConnections.environmentsUnit': 'environments',
  'foundationCostCalculator.serviceConnections.everythingGroup': 'Applies to everything above',
  'foundationCostCalculator.serviceConnections.formulaDevServer': '+ laptops and CI: <num>0</num> (dev server)',
  'foundationCostCalculator.serviceConnections.formulaEnvironments':
    '× <num>{envFactor}</num> for {environments} environments (non-production at {nonProdRatio}× production) = <num>{production}</num>',
  'foundationCostCalculator.serviceConnections.formulaFunctions':
    '<num>{runs}</num> runs × <num>{duration}</num> s = {runMinutes} min, + <num>{coldStarts}</num> cold starts × {warmMinutes} warm min = {coldStartMinutes} min. {functionMinutes} min ÷ {minutesPerMonth} = <num>{production}</num> in production',
  'foundationCostCalculator.serviceConnections.formulaLaptopsAndCi':
    '+ laptops: {engineers} engineers × {laptopHours} h × {workingDays} days ÷ {hoursPerMonth} h = <num>{laptops}</num>, + CI: {ciRuns} jobs × {ciMinutes} min ÷ {minutesPerMonth} = <num>{ci}</num>',
  'foundationCostCalculator.serviceConnections.formulaLongRunning':
    '<num>{copies}</num> instances × <num>{processes}</num> worker processes × <num>{uptime}</num> of the month = <num>{production}</num> in production',
  'foundationCostCalculator.serviceConnections.heading': 'Server-side usage',
  'foundationCostCalculator.serviceConnections.invocationsHint':
    'Invocations of functions that use a server-side SDK, such as Lambda or Cloud Run. Your cloud console shows last month’s invocation count, and a rough number is fine.',
  'foundationCostCalculator.serviceConnections.invocationsLabel': 'Function runs per month',
  'foundationCostCalculator.serviceConnections.invocationsUnit': 'runs',
  'foundationCostCalculator.serviceConnections.laptopHoursHint':
    'Average across the team. Many people run the backend only some of the time. A rough number is fine.',
  'foundationCostCalculator.serviceConnections.laptopHoursLabel': 'Laptop hours per engineer per working day',
  'foundationCostCalculator.serviceConnections.laptopHoursUnit': 'hours',
  'foundationCostCalculator.serviceConnections.longRunningGroup': 'Long-running services',
  'foundationCostCalculator.serviceConnections.nonProdHalf': 'About half the size',
  'foundationCostCalculator.serviceConnections.nonProdHint':
    'Each environment bills at the same rate and multiplies the totals above.',
  'foundationCostCalculator.serviceConnections.nonProdLabel': 'Non-production size compared with production',
  'foundationCostCalculator.serviceConnections.nonProdLarger': 'Larger',
  'foundationCostCalculator.serviceConnections.nonProdSame': 'About the same',
  'foundationCostCalculator.serviceConnections.processesHint':
    'Each forked worker or sidecar (gunicorn, Puma, Celery, Sidekiq, Kafka consumers) opens its own connection. Threads share one. Python and Ruby servers usually run <em>3–5</em>. Enter 1 if nothing forks.',
  'foundationCostCalculator.serviceConnections.processesLabel': 'Worker processes per instance',
  'foundationCostCalculator.serviceConnections.processesUnit': 'per instance',
  'foundationCostCalculator.serviceConnections.serverlessGroup': 'Serverless functions (leave at 0 if you have none)',
  'foundationCostCalculator.serviceConnections.subheading':
    'One connection = one server-side SDK connected to one environment for a month. First {free} free, then {rate} each.',
  'foundationCostCalculator.serviceConnections.unit': 'connections',
  'foundationCostCalculator.serviceConnections.uptimeAllMonth': 'All month, 24x7',
  'foundationCostCalculator.serviceConnections.uptimeBusinessHours': 'Business hours (9h x 5d)',
  'foundationCostCalculator.serviceConnections.uptimeHalfTime': 'About half the time',
  'foundationCostCalculator.serviceConnections.uptimeHint':
    'LaunchDarkly bills connected minutes, so instances that run part-time cost less. Pick the closest option.',
  'foundationCostCalculator.serviceConnections.uptimeLabel': 'Time running each month',
  'foundationCostCalculator.total.aiRuns': 'AI runs',
  'foundationCostCalculator.total.comparisonAnnual':
    'About {monthlyAmount} a month at this usage, or about {payMonthly} a month ({payMonthlyYearly} a year) paid monthly with nothing prepaid.',
  'foundationCostCalculator.total.comparisonMonthly':
    '{yearlyAmount} a year at this usage, or about {payYearly} ({payYearlyMonthly} a month) paid yearly.',
  'foundationCostCalculator.total.cta': 'Start on Foundation',
  'foundationCostCalculator.total.included':
    '{connections} connections, {mau} MAU, {aiRuns} AI runs, observability up to the limits above',
  'foundationCostCalculator.total.includedLabel': 'Included every month',
  'foundationCostCalculator.total.includedWithoutObservability':
    '{connections} connections, {mau} MAU, {aiRuns} AI runs',
  'foundationCostCalculator.total.label': 'Estimated total',
  'foundationCostCalculator.total.lineValue': '{cost}/mo',
  'foundationCostCalculator.total.monthlyActiveUsers': 'Client-side MAU',
  'foundationCostCalculator.total.observability': 'Observability',
  'foundationCostCalculator.total.perMonth': 'per month',
  'foundationCostCalculator.total.perYear': 'per year, billed once',
  'foundationCostCalculator.total.purchaseAnnual':
    'Buy online by card. You prepay this usage level for 12 months at the yearly rate. LaunchDarkly charges anything above it at the start of the next month at the pay-monthly rate ({connectionRate} per connection, {mauRate} per {mauBlockSize} MAU). Raise your level at any time. It takes effect next month.',
  'foundationCostCalculator.total.purchaseMonthly':
    'Buy online by card, with nothing prepaid. Each month, LaunchDarkly charges for last month’s usage above what’s included.',
  'foundationCostCalculator.total.serviceConnections': 'Server-side connections',
  'insights.page.title': 'Launch Insights',
  'integrations.azureEventHubsDestinationForm.eventHubNameLabel': 'Event Hub Name',
  'integrations.azureEventHubsDestinationForm.namespaceLabel': 'Event Hub Namespace Name',
  'integrations.azureEventHubsDestinationForm.needHelp':
    'Need help configuring your Azure Event Hubs destination? Read our <docs>documentation</docs> or <support>create a support ticket</support>.',
  'integrations.azureEventHubsDestinationForm.policyKeyHint':
    'Find your policy key in your Azure Event Hubs Namespace\'s settings. You cannot edit this field after you click "Save destination."',
  'integrations.azureEventHubsDestinationForm.policyKeyLabel': 'Shared Access Signature Key',
  'integrations.azureEventHubsDestinationForm.policyKeyTooltip':
    'Find your policy key in your Azure Event Hubs Namespace’s settings',
  'integrations.azureEventHubsDestinationForm.policyNameHint':
    "Find your policy name in your Azure Event Hubs Namespace's settings.",
  'integrations.azureEventHubsDestinationForm.policyNameLabel': 'Shared Access Signature Policy Name',
  'integrations.bigQueryDataExport.authMethodHint': 'How LaunchDarkly authenticates to BigQuery.',
  'integrations.bigQueryDataExport.authMethodHintLocked':
    'How LaunchDarkly authenticates to BigQuery. The method cannot be changed after saving.',
  'integrations.bigQueryDataExport.authMethodLabel': 'Authentication method',
  'integrations.bigQueryDataExport.copyExportServiceAccountLabel': 'Copy export service account email',
  'integrations.bigQueryDataExport.destinationServiceAccountDescription':
    'A service account that holds the BigQuery dataset and GCS staging permissions. Cannot be changed after saving.',
  'integrations.bigQueryDataExport.destinationServiceAccountLabel': 'Destination service account email',
  'integrations.bigQueryDataExport.exportDatasetIdDescription':
    'Name of dataset to create and write data to. It will be created automatically if the dataset does not exist. Cannot be changed after saving.',
  'integrations.bigQueryDataExport.exportDatasetIdInvalid':
    'Export dataset ID can only contain letters, numbers, and underscores',
  'integrations.bigQueryDataExport.exportDatasetIdLabel': 'Export dataset ID',
  'integrations.bigQueryDataExport.exportDatasetIdRequired': 'Export dataset ID is required',
  'integrations.bigQueryDataExport.exportDatasetIdTooLong': 'Export dataset ID must be at most 1024 characters',
  'integrations.bigQueryDataExport.exportServiceAccountHint':
    'This service account must be <link>granted access</link> to the destination service account with the Service Account User role assigned.',
  'integrations.bigQueryDataExport.exportServiceAccountLabel': 'Export service account email',
  'integrations.bigQueryDataExport.gcsBucketNameLabel': 'GCS bucket name',
  'integrations.bigQueryDataExport.gcsBucketNameRequired': 'GCS bucket name is required',
  'integrations.bigQueryDataExport.gcsBucketRegionDescription':
    'The region of the staging GCS bucket. Must be a valid GCS bucket location. Cannot be changed after saving.',
  'integrations.bigQueryDataExport.gcsBucketRegionLabel': 'GCS bucket region',
  'integrations.bigQueryDataExport.gcsBucketRegionRequired': 'GCS bucket region is required',
  'integrations.bigQueryDataExport.prerequisiteProject': 'You must have a Google Cloud project with BigQuery enabled',
  'integrations.bigQueryDataExport.prerequisiteStagingBucket':
    'You must create a GCS staging bucket and grant access to the destination service account',
  'integrations.bigQueryDataExport.projectIdDescription':
    'The Google Cloud project that holds your BigQuery resources. Legacy domain-scoped project IDs are also supported. Cannot be changed after saving.',
  'integrations.bigQueryDataExport.projectIdInvalid':
    'Must begin with a lowercase letter and contain only lowercase letters, numbers, and hyphens',
  'integrations.bigQueryDataExport.projectIdLabel': 'GCP project ID',
  'integrations.bigQueryDataExport.projectIdRequired': 'Project ID is required',
  'integrations.bigQueryDataExport.selectEnvironmentFirstPlaceholder': 'Select an environment first',
  'integrations.bigQueryDataExport.serviceAccountEmailCopiedToast': 'Service account email copied',
  'integrations.bigQueryDataExport.serviceAccountEmailInvalid':
    'Must be a valid Google service account email ending with gserviceaccount.com',
  'integrations.bigQueryDataExport.serviceAccountEmailRequired': 'Destination service account email is required',
  'integrations.bigQueryDataExport.serviceAccountImpersonation': 'Service account impersonation',
  'integrations.bigQueryDataExport.wifAudienceDescription':
    'The full resource name of your workload identity pool provider. Find it in the Google Cloud console under Workload Identity Federation.',
  'integrations.bigQueryDataExport.wifAudienceLabel': 'Workload identity provider resource name',
  'integrations.bigQueryDataExport.wifAudienceRequired': 'Workload identity provider resource name is required',
  'integrations.bigQueryDataExport.wifClientIdDescription':
    'The OAuth2 client ID your identity provider issued for LaunchDarkly. Cannot be changed after saving.',
  'integrations.bigQueryDataExport.wifClientIdLabel': 'Client ID',
  'integrations.bigQueryDataExport.wifClientIdRequired': 'Client ID is required',
  'integrations.bigQueryDataExport.wifClientSecretDescription':
    'The OAuth2 client secret paired with the client ID. Cannot be changed after saving.',
  'integrations.bigQueryDataExport.wifClientSecretLabel': 'Client secret',
  'integrations.bigQueryDataExport.wifClientSecretRequired': 'Client secret is required',
  'integrations.bigQueryDataExport.wifScopesDescription':
    "You can enter multiple scopes, separated by spaces. Leave blank if your identity provider doesn't require any. Cannot be changed after saving.",
  'integrations.bigQueryDataExport.wifScopesLabel': 'Identity provider scopes (optional)',
  'integrations.bigQueryDataExport.wifTokenUrlDescription': 'The OAuth2 token endpoint of your identity provider.',
  'integrations.bigQueryDataExport.wifTokenUrlInvalid': 'Must be an https:// URL',
  'integrations.bigQueryDataExport.wifTokenUrlLabel': 'Identity provider token URL',
  'integrations.bigQueryDataExport.wifTokenUrlRequired': 'Identity provider token URL is required',
  'integrations.bigQueryDataExport.workloadIdentityFederation': 'Workload identity federation',
  'integrations.bigQueryDestinationForm.copyExportServiceAccountLabel': 'Copy export service account email',
  'integrations.bigQueryDestinationForm.destinationServiceAccountLabel': 'Destination service account email',
  'integrations.bigQueryDestinationForm.exportServiceAccountHint':
    'This service account must be <link>granted access</link> to the destination service account with the Service Account User role assigned.',
  'integrations.bigQueryDestinationForm.exportServiceAccountLabel': 'Export service account email',
  'integrations.bigQueryDestinationForm.gcsBucketNameLabel': 'GCS bucket name',
  'integrations.bigQueryDestinationForm.gcsBucketRegionHint':
    'The region of the staging GCS bucket. Must be a valid GCS bucket location.',
  'integrations.bigQueryDestinationForm.gcsBucketRegionLabel': 'GCS bucket region',
  'integrations.bigQueryDestinationForm.projectIdHint':
    'The ID of the Google Cloud project to use for the BigQuery destination.',
  'integrations.bigQueryDestinationForm.projectIdLabel': 'Project ID',
  'integrations.bigQueryDestinationForm.schemaHint':
    'Name of dataset to create and write data to. It will be created automatically if the dataset does not exist.',
  'integrations.bigQueryDestinationForm.schemaLabel': 'Schema',
  'integrations.bigQueryExperimentation.authMethodHint': 'How LaunchDarkly authenticates to BigQuery.',
  'integrations.bigQueryExperimentation.authMethodHintLocked':
    'How LaunchDarkly authenticates to BigQuery. The method cannot be changed after saving.',
  'integrations.bigQueryExperimentation.authMethodLabel': 'Authentication method',
  'integrations.bigQueryExperimentation.authStepHeading': 'Step 2. Configure authentication',
  'integrations.bigQueryExperimentation.authStepIntroWif':
    'Enter the connection details from your identity provider. Then generate the setup script and run it in <link>Google Cloud Shell</link> to grant the required permissions.',
  'integrations.bigQueryExperimentation.cancel': 'Cancel',
  'integrations.bigQueryExperimentation.cancelEditServiceAccountKeyLabel': 'Cancel editing service account key',
  'integrations.bigQueryExperimentation.copySetupScriptLabel': 'Copy BigQuery setup script',
  'integrations.bigQueryExperimentation.datasetIdInvalid':
    'Dataset ID can only contain letters, numbers, and underscores',
  'integrations.bigQueryExperimentation.datasetIdTooLong': 'Dataset ID must be at most 1024 characters',
  'integrations.bigQueryExperimentation.datasetLocationDescription':
    'BigQuery region for your datasets. All datasets must be in the same location. Cannot be changed after saving.',
  'integrations.bigQueryExperimentation.datasetLocationLabel': 'Dataset location',
  'integrations.bigQueryExperimentation.datasetLocationRequired': 'Dataset location is required',
  'integrations.bigQueryExperimentation.editServiceAccountKeyLabel': 'Edit service account key',
  'integrations.bigQueryExperimentation.environmentDescription':
    'Only environments with a BigQuery Data Export destination will be available. You cannot edit this after you save the configuration.',
  'integrations.bigQueryExperimentation.environmentLabel': 'Select project and environment',
  'integrations.bigQueryExperimentation.exportDatasetIdDescription':
    'The dataset where your BigQuery Data Export destination writes event data. This is the same dataset you configured in Data Export.',
  'integrations.bigQueryExperimentation.exportDatasetIdInvalid':
    'Export dataset ID can only contain letters, numbers, and underscores',
  'integrations.bigQueryExperimentation.exportDatasetIdLabel': 'Export dataset ID',
  'integrations.bigQueryExperimentation.exportDatasetIdRequired': 'Export dataset ID is required',
  'integrations.bigQueryExperimentation.exportDatasetIdTooLong': 'Export dataset ID must be at most 1024 characters',
  'integrations.bigQueryExperimentation.generateSetupScript': 'Generate setup script',
  'integrations.bigQueryExperimentation.httpsUrlRequired': 'Must be an https:// URL',
  'integrations.bigQueryExperimentation.metricsDatasetIdDescription':
    'Restricts read access to this dataset only. If left blank, the service account gets project-level read access. Cannot be changed after saving.',
  'integrations.bigQueryExperimentation.metricsDatasetIdLabel': 'Metrics dataset ID (optional)',
  'integrations.bigQueryExperimentation.prefilledFromDataExport':
    'Fetched from your BigQuery data export configuration.',
  'integrations.bigQueryExperimentation.prerequisiteCloudShell':
    'Access to <link>Google Cloud Shell</link>, or gcloud CLI and bq CLI installed locally',
  'integrations.bigQueryExperimentation.prerequisiteDataExport':
    'You must have Data Export set up for BigQuery on your selected project and environment',
  'integrations.bigQueryExperimentation.prerequisitePermissions':
    'You need permission to create service accounts, datasets, and IAM bindings in Google Cloud',
  'integrations.bigQueryExperimentation.projectIdDescription':
    'The Google Cloud project that holds your BigQuery resources. Legacy domain-scoped project IDs are also supported.',
  'integrations.bigQueryExperimentation.projectIdInvalid':
    'Must be a valid GCP project ID using lowercase letters, digits, and hyphens. Legacy domain-scoped IDs are also supported.',
  'integrations.bigQueryExperimentation.projectIdLabel': 'GCP project ID',
  'integrations.bigQueryExperimentation.projectIdTooShort': 'Project ID must be at least 6 characters',
  'integrations.bigQueryExperimentation.resultsDatasetIdDescription':
    'Dataset where LaunchDarkly will write experimentation results. The setup script creates this for you (letters, numbers, underscores only).',
  'integrations.bigQueryExperimentation.resultsDatasetIdLabel': 'Results dataset ID',
  'integrations.bigQueryExperimentation.resultsDatasetIdRequired': 'Results dataset ID is required',
  'integrations.bigQueryExperimentation.saveServiceAccountKeyLabel': 'Save service account key',
  'integrations.bigQueryExperimentation.selectEnvironmentToPrefillPlaceholder':
    'Select an environment to auto-fill this from your data export',
  'integrations.bigQueryExperimentation.serviceAccountKey': 'Service account key',
  'integrations.bigQueryExperimentation.serviceAccountKeyDescription':
    'Paste the full contents of the JSON key file downloaded from Google Cloud.',
  'integrations.bigQueryExperimentation.serviceAccountKeyHint':
    'After running the setup script, download the JSON key file and paste its contents below. Find instructions in the <link>Google Cloud documentation</link>.',
  'integrations.bigQueryExperimentation.serviceAccountKeyInvalidJson':
    'Must be valid JSON — paste the full contents of the downloaded key file',
  'integrations.bigQueryExperimentation.serviceAccountKeyLabel': 'Service account key (JSON)',
  'integrations.bigQueryExperimentation.serviceAccountKeyRequired': 'Service account key is required',
  'integrations.bigQueryExperimentation.serviceAccountKeyRotateDescription':
    'Paste a new JSON key here to rotate the service account key. Leave the obfuscated value untouched to keep the current key.',
  'integrations.bigQueryExperimentation.serviceAccountKeyStepHeading': 'Step 2. Add your service account key',
  'integrations.bigQueryExperimentation.setupScriptCopiedToast': 'BigQuery setup script copied to clipboard',
  'integrations.bigQueryExperimentation.setupScriptHint':
    'Run this script with gcloud and bq installed locally to create the service account and grant the required permissions. Alternatively, no local install needed, run it in the <link>Cloud Shell editor terminal</link>. Prefer the Cloud Console UI? <manualLink>Follow the manual setup steps</manualLink>.',
  'integrations.bigQueryExperimentation.setupScriptHintWif':
    'Run this script with gcloud and bq installed locally to create the results dataset, grant your service account the required permissions, and add the workload identity binding. Alternatively, no local install needed, run it in the <link>Cloud Shell editor terminal</link>. Prefer the Cloud Console UI? <manualLink>Follow the manual setup steps</manualLink>.',
  'integrations.bigQueryExperimentation.setupScriptLabel': 'Google Cloud setup script',
  'integrations.bigQueryExperimentation.setupScriptViewerLabel': 'BigQuery Setup Script',
  'integrations.bigQueryExperimentation.step1Heading': 'Step 1. Configure your BigQuery resources',
  'integrations.bigQueryExperimentation.step1Intro':
    'Enter your Google Cloud project and dataset details. Then continue to authentication below to generate the setup script for these resources.',
  'integrations.bigQueryExperimentation.step1IntroWithScript':
    'Enter your Google Cloud project and dataset details. Then generate the setup script and run it in <link>Google Cloud Shell</link> to create the service account and grant the required permissions.',
  'integrations.bigQueryExperimentation.wifAudienceDescription':
    'The full resource name of your workload identity pool provider. Find it in the Google Cloud console under Workload Identity Federation.',
  'integrations.bigQueryExperimentation.wifAudienceLabel': 'Workload identity provider resource name',
  'integrations.bigQueryExperimentation.wifAudienceRequired': 'Workload identity provider resource name is required',
  'integrations.bigQueryExperimentation.wifClientIdDescription':
    'The OAuth2 client ID your identity provider issued for LaunchDarkly. Cannot be changed after saving.',
  'integrations.bigQueryExperimentation.wifClientIdLabel': 'Client ID',
  'integrations.bigQueryExperimentation.wifClientIdRequired': 'Client ID is required',
  'integrations.bigQueryExperimentation.wifClientSecretDescription':
    'The OAuth2 client secret paired with the client ID. Cannot be changed after saving.',
  'integrations.bigQueryExperimentation.wifClientSecretLabel': 'Client secret',
  'integrations.bigQueryExperimentation.wifClientSecretRequired': 'Client secret is required',
  'integrations.bigQueryExperimentation.wifScopesDescription':
    "You can enter multiple scopes, separated by spaces. Leave blank if your identity provider doesn't require any. Cannot be changed after saving.",
  'integrations.bigQueryExperimentation.wifScopesLabel': 'Identity provider scopes (optional)',
  'integrations.bigQueryExperimentation.wifServiceAccountDescription':
    'An existing service account that holds the BigQuery roles. The setup script grants the required roles and workload identity binding.',
  'integrations.bigQueryExperimentation.wifServiceAccountInvalid': 'Must be a service account email address',
  'integrations.bigQueryExperimentation.wifServiceAccountLabel': 'Service account email',
  'integrations.bigQueryExperimentation.wifServiceAccountRequired': 'Service account email is required',
  'integrations.bigQueryExperimentation.wifStsEndpointDescription':
    "Leave blank to use Google's standard STS endpoint.",
  'integrations.bigQueryExperimentation.wifStsEndpointLabel': 'STS endpoint override (optional)',
  'integrations.bigQueryExperimentation.wifTokenUrlDescription': 'The OAuth2 token endpoint of your identity provider.',
  'integrations.bigQueryExperimentation.wifTokenUrlLabel': 'Identity provider token URL',
  'integrations.bigQueryExperimentation.wifTokenUrlRequired': 'Identity provider token URL is required',
  'integrations.bigQueryExperimentation.workloadIdentityFederation': 'Workload identity federation',
  'integrations.bigSegmentStoreForm.deleteError': 'Failed to delete integration',
  'integrations.bigSegmentStoreForm.deletedToast': 'Integration deleted',
  'integrations.bigSegmentStoreForm.environmentHint':
    'Your LaunchDarkly environment. You cannot edit this field after you click Save configuration.',
  'integrations.bigSegmentStoreForm.environmentLabel': 'Environment',
  'integrations.bigSegmentStoreForm.environmentLockedHint':
    'Your LaunchDarkly environment. You cannot edit this field after your configuration is created. Create a new integration in the environment you wish to use.',
  'integrations.bigSegmentStoreForm.nameLabel': 'Name',
  'integrations.bigSegmentStoreForm.saveConfiguration': 'Save configuration',
  'integrations.bigSegmentStoreForm.savingLabel': 'Saving {name} configuration',
  'integrations.bigSegmentStoreForm.statusLabel': 'Status',
  'integrations.bigSegmentStoreForm.termsAgreement':
    'I have read and agree to the <link>Integration Terms and Conditions</link>',
  'integrations.bigSegmentStoreList.configurationNameColumn': 'Configuration name',
  'integrations.bigSegmentStoreList.configurationOptionsLabel': '{name} configuration options',
  'integrations.bigSegmentStoreList.degraded': 'Degraded',
  'integrations.bigSegmentStoreList.editConfiguration': 'Edit integration configuration',
  'integrations.bigSegmentStoreList.editRestrictedTooltip':
    'You are not allowed to edit this integration. Contact your Administrator for more information.',
  'integrations.bigSegmentStoreList.healthy': 'Healthy',
  'integrations.bigSegmentStoreList.lastSuccessfulSyncColumn': 'Last successful sync',
  'integrations.bigSegmentStoreList.nextSyncHint': 'Next sync to Big Segment store in 60 seconds',
  'integrations.bigSegmentStoreList.noMatch': 'No integrations match',
  'integrations.bigSegmentStoreList.roleRestrictionTooltip':
    'The {role} role does not allow you to edit this integration.',
  'integrations.bigSegmentStoreList.seeError': 'See error',
  'integrations.bigSegmentStoreList.stateColumn': 'State',
  'integrations.bigSegmentStoreList.statusUnavailable':
    'Status N/A because Big Segment store integration is turned off.',
  'integrations.bigSegmentStoreList.syncing': 'Syncing',
  'integrations.bigSegmentStoreList.syncingHint':
    'SDKs evaluating flags with Big Segments may see stale results until the sync is complete.',
  'integrations.bigSegmentStoreList.viewErrorLog': 'View error log',
  'integrations.clickHouseDataExport.allowlistIpDescription':
    'Required only if your ClickHouse instance restricts inbound connections by IP.',
  'integrations.clickHouseDataExport.allowlistIpLabel': 'Allowlist the LaunchDarkly IP',
  'integrations.clickHouseDataExport.configureIamAccess': 'Configure IAM access',
  'integrations.clickHouseDataExport.copyIamPermissionsPolicyLabel': 'Copy IAM permissions policy',
  'integrations.clickHouseDataExport.copyIamTrustPolicyLabel': 'Copy IAM trust policy',
  'integrations.clickHouseDataExport.copyS3BucketNameLabel': 'Copy S3 bucket name',
  'integrations.clickHouseDataExport.copySetupScriptLabel': 'Copy setup SQL script',
  'integrations.clickHouseDataExport.customNamesHint':
    'The following database objects will be created in ClickHouse. You can customise their names.',
  'integrations.clickHouseDataExport.databaseNameDescription':
    "The database in ClickHouse where LaunchDarkly stores this environment's exported data. Use a different database for each environment that exports to the same ClickHouse service.",
  'integrations.clickHouseDataExport.databaseNameLabel': 'Database name',
  'integrations.clickHouseDataExport.destinationAlreadyExists': 'ClickHouse Data Export destination already exists',
  'integrations.clickHouseDataExport.generatePolicies': 'Generate IAM permission and trust policies',
  'integrations.clickHouseDataExport.generatePoliciesFailed': 'Failed to generate IAM policies',
  'integrations.clickHouseDataExport.generateScript': 'Generate script',
  'integrations.clickHouseDataExport.generateScriptFailed': 'Failed to generate setup script',
  'integrations.clickHouseDataExport.hostDescription':
    "The hostname of your ClickHouse instance, without the protocol. In ClickHouse Cloud, find it on your service's Connect screen.",
  'integrations.clickHouseDataExport.hostInvalid':
    'Enter the hostname without https:// (e.g. my-instance.clickhouse.cloud)',
  'integrations.clickHouseDataExport.hostLabel': 'Host',
  'integrations.clickHouseDataExport.hostRequired': 'Host is required',
  'integrations.clickHouseDataExport.iamPermissionsPolicyCopiedToast': 'IAM permissions policy copied to clipboard',
  'integrations.clickHouseDataExport.iamPermissionsPolicyLabel': 'IAM permissions policy',
  'integrations.clickHouseDataExport.iamRoleArnDescription':
    'Paste the ARN of the IAM role you created with the above steps.',
  'integrations.clickHouseDataExport.iamRoleArnInvalid': 'Must be a valid IAM Role ARN ({example})',
  'integrations.clickHouseDataExport.iamRoleArnLabel': 'IAM role ARN',
  'integrations.clickHouseDataExport.iamRoleArnRequired': 'IAM Role ARN is required',
  'integrations.clickHouseDataExport.iamStep1': '1. Generate IAM Permission policy and Trust Policy below.',
  'integrations.clickHouseDataExport.iamStep2':
    '2. Create a new policy in your IAM console using the permissions policy below.',
  'integrations.clickHouseDataExport.iamStep3':
    '3. Create an IAM role using the trust policy below, then attach the permissions policy from step 2.',
  'integrations.clickHouseDataExport.iamTrustPolicyCopiedToast': 'IAM trust policy copied to clipboard',
  'integrations.clickHouseDataExport.iamTrustPolicyLabel': 'IAM trust policy',
  'integrations.clickHouseDataExport.identifierInvalid':
    'Only letters, digits, and underscores allowed (must start with a letter)',
  'integrations.clickHouseDataExport.nameRequired': 'Name is required',
  'integrations.clickHouseDataExport.passwordDescription':
    'Choose a password for the export user. Use only letters, digits, dashes (-), and underscores (_). It will be embedded in the generated script.',
  'integrations.clickHouseDataExport.passwordInvalid':
    'Password may only contain alphanumeric characters, dashes, and underscores',
  'integrations.clickHouseDataExport.passwordLabel': 'Password',
  'integrations.clickHouseDataExport.passwordRequired': 'Password is required',
  'integrations.clickHouseDataExport.portDescription': 'The native TLS port. The default is 9440 for ClickHouse Cloud.',
  'integrations.clickHouseDataExport.portLabel': 'Port',
  'integrations.clickHouseDataExport.portNotNumber': 'Port must be a number',
  'integrations.clickHouseDataExport.portOutOfRange': 'Port must be between 1 and 65535',
  'integrations.clickHouseDataExport.portRequired': 'Port is required',
  'integrations.clickHouseDataExport.prerequisiteDataExport':
    'Data Export enabled for the selected project and environment',
  'integrations.clickHouseDataExport.prerequisiteIamPermissions':
    'Permission to create IAM roles, policies, and an S3 bucket in your AWS account',
  'integrations.clickHouseDataExport.prerequisiteInstance': 'A running ClickHouse instance',
  'integrations.clickHouseDataExport.regionPlaceholder': 'Select a region',
  'integrations.clickHouseDataExport.s3BucketNameCopiedToast': 'S3 bucket name copied to clipboard',
  'integrations.clickHouseDataExport.s3BucketNameDescription':
    'Create an S3 bucket in your AWS account with this exact name. You can customise this.',
  'integrations.clickHouseDataExport.s3BucketNameInvalid':
    'Bucket name must be 3-63 characters, lowercase letters, digits, or hyphens, and start and end with a letter or digit',
  'integrations.clickHouseDataExport.s3BucketNameLabel': 'S3 staging bucket name',
  'integrations.clickHouseDataExport.s3BucketRegionDescription':
    'The AWS region of the S3 bucket that stages data before loading into ClickHouse. This must match the region where you create the bucket.',
  'integrations.clickHouseDataExport.s3BucketRegionLabel': 'S3 staging bucket region',
  'integrations.clickHouseDataExport.s3BucketRegionRequired': 'S3 bucket region is required',
  'integrations.clickHouseDataExport.selectEnvironmentFirst': 'Select a project and environment first',
  'integrations.clickHouseDataExport.selectProjectAndEnvironment': 'Select a project and environment',
  'integrations.clickHouseDataExport.serviceRoleArnHint':
    'In <link>ClickHouse Cloud</link>, go to Settings → Network security → View service details.',
  'integrations.clickHouseDataExport.serviceRoleArnLabel': 'ClickHouse Cloud service role ARN',
  'integrations.clickHouseDataExport.setupScriptCopiedToast': 'Setup script copied to clipboard',
  'integrations.clickHouseDataExport.setupScriptEmpty': 'Setup script could not be generated. Try again.',
  'integrations.clickHouseDataExport.setupScriptHint':
    'Run the generated script in your ClickHouse SQL console to create the database and user, and grant the permissions LaunchDarkly needs to write exported data.',
  'integrations.clickHouseDataExport.setupScriptLabel': 'Setup SQL script',
  'integrations.clickHouseDataExport.step1Heading': 'Step 1. Set up your ClickHouse connection and staging bucket',
  'integrations.clickHouseDataExport.step2Heading': 'Step 2. Custom rename resources (optional)',
  'integrations.clickHouseDataExport.step3Heading': 'Step 3. Set up AWS access',
  'integrations.clickHouseDataExport.step4Heading': 'Step 4. Generate setup script',
  'integrations.clickHouseDataExport.termsRequired': 'You must accept the Integration Terms and Conditions',
  'integrations.clickHouseDataExport.userLabel': 'User',
  'integrations.clickHouseDataExport.usernameDescription':
    'The ClickHouse user account that LaunchDarkly uses to connect and export data.',
  'integrations.clickHouseDataExport.usernameLabel': 'Username',
  'integrations.clickHouseDestinationForm.createError': 'Failed to create ClickHouse destination. Try again later.',
  'integrations.clickHouseDestinationForm.createdToast': 'ClickHouse destination created',
  'integrations.clickHouseDestinationForm.updateError': 'Failed to update ClickHouse destination. Try again later.',
  'integrations.clickHouseExperimentation.cancel': 'Cancel',
  'integrations.clickHouseExperimentation.completeSetupAuthFailedHint':
    'If you ran an earlier setup script for this user, enter the password you used then and generate the script again, or choose a different user name.',
  'integrations.clickHouseExperimentation.completeSetupFailed':
    "Couldn't verify the ClickHouse setup. Check that you ran the setup script, then try again.",
  'integrations.clickHouseExperimentation.copySetupScriptLabel': 'Copy setup script',
  'integrations.clickHouseExperimentation.destinationDescription':
    'Select a data export destination to map this experimentation setup to.',
  'integrations.clickHouseExperimentation.destinationLabel': 'Data export destination',
  'integrations.clickHouseExperimentation.emptyStateAction': 'Configure data export',
  'integrations.clickHouseExperimentation.emptyStateBody':
    'A ClickHouse data export integration needs to be configured to continue with this integration.',
  'integrations.clickHouseExperimentation.emptyStateTitle': 'Data Export not configured',
  'integrations.clickHouseExperimentation.environmentDescription':
    'Only environments with a ClickHouse Data Export destination are available. You cannot edit this after you save the configuration.',
  'integrations.clickHouseExperimentation.environmentLabel': 'Select project and environment',
  'integrations.clickHouseExperimentation.fetchedFromDataExportDescription':
    'Fetched from your ClickHouse data export configuration.',
  'integrations.clickHouseExperimentation.generateBeforeSaving':
    'Generate and run the setup script before saving this configuration.',
  'integrations.clickHouseExperimentation.generateFailed': 'Failed to generate the setup script. Try again later.',
  'integrations.clickHouseExperimentation.generateScript': 'Generate script',
  'integrations.clickHouseExperimentation.hostLabel': 'Host',
  'integrations.clickHouseExperimentation.identifierInvalid':
    'Names cannot contain backslashes, line breaks, or other control characters',
  'integrations.clickHouseExperimentation.metricsDatabaseDescription':
    "The ClickHouse database that contains your metric event data. You can set a custom name, which can't be changed after saving.",
  'integrations.clickHouseExperimentation.metricsDatabaseIsResultsDatabase':
    'This is the results database, where LaunchDarkly can drop and alter tables. Enter the database that holds your metric events.',
  'integrations.clickHouseExperimentation.metricsDatabaseLabel': 'Metrics database name',
  'integrations.clickHouseExperimentation.metricsDatabaseReserved':
    'This is a ClickHouse system database. Enter the database that holds your metric events.',
  'integrations.clickHouseExperimentation.nameRequired': 'Name is required',
  'integrations.clickHouseExperimentation.pairedDestinationHint':
    'Fetched from the paired ClickHouse Data Export destination.',
  'integrations.clickHouseExperimentation.passwordDescription':
    'Choose a password for the experimentation user. Use only letters, digits, dashes (-), and underscores (_).',
  'integrations.clickHouseExperimentation.passwordInvalid': 'Use only letters, digits, dashes (-), and underscores (_)',
  'integrations.clickHouseExperimentation.passwordLabel': 'Password',
  'integrations.clickHouseExperimentation.passwordLockedDescription':
    "The password can't be changed after saving. To use a new one, delete this integration and create it again.",
  'integrations.clickHouseExperimentation.passwordPlaceholder': 'Enter a password',
  'integrations.clickHouseExperimentation.passwordRequired': 'Password is required',
  'integrations.clickHouseExperimentation.portLabel': 'Port',
  'integrations.clickHouseExperimentation.prerequisites':
    'You must have Data Export set up for ClickHouse on your selected project and environment, a running ClickHouse instance, and permission to create a database and user in ClickHouse.',
  'integrations.clickHouseExperimentation.resultsDatabaseDescription':
    'The database in ClickHouse where LaunchDarkly writes experimentation results. LaunchDarkly creates it if it does not exist.',
  'integrations.clickHouseExperimentation.resultsDatabaseLabel': 'Results database',
  'integrations.clickHouseExperimentation.selectEnvironmentToPrefillPlaceholder':
    'Select an environment to auto-fill this',
  'integrations.clickHouseExperimentation.setupPrerequisiteDataExport':
    'Configure Data Export for your selected environment',
  'integrations.clickHouseExperimentation.setupPrerequisiteUser':
    'Have a ClickHouse user LaunchDarkly can use to read metrics and write results',
  'integrations.clickHouseExperimentation.setupPrerequisitesHeading': 'Setup prerequisites',
  'integrations.clickHouseExperimentation.setupScriptCopied': 'Setup script copied to clipboard',
  'integrations.clickHouseExperimentation.setupScriptLabel': 'Setup script',
  'integrations.clickHouseExperimentation.step1Body':
    'LaunchDarkly connects to the ClickHouse instance your Data Export writes to.',
  'integrations.clickHouseExperimentation.step1Heading': 'Step 1. Configure connection',
  'integrations.clickHouseExperimentation.step2Body':
    'Tell LaunchDarkly where your metric event data lives. LaunchDarkly is granted read access to only these resources.',
  'integrations.clickHouseExperimentation.step2Heading': 'Step 2. Configure resource names',
  'integrations.clickHouseExperimentation.step3Body':
    'Choose a password for the experimentation user, then generate the SQL script. Run it in your ClickHouse SQL console to grant LaunchDarkly access.',
  'integrations.clickHouseExperimentation.step3BodyReadOnly':
    "The SQL script that grants LaunchDarkly access for this integration. It leaves out the user's password.",
  'integrations.clickHouseExperimentation.step3Heading': 'Step 3. Generate setup script',
  'integrations.clickHouseExperimentation.userDescription':
    "The ClickHouse user account that LaunchDarkly uses to connect and run experimentation queries. You can set a custom name, which can't be changed after saving.",
  'integrations.clickHouseExperimentation.userInvalid':
    'Use only letters, digits, and underscores, starting with a letter',
  'integrations.clickHouseExperimentation.userLabel': 'User',
  'integrations.clickHouseExperimentation.userRequired': 'User is required',
  'integrations.clickHouseExperimentation.userReserved':
    'This user is reserved by ClickHouse. Choose a different name.',
  'integrations.codeReferenceForm.deleteCodeReference': 'Delete Code Reference',
  'integrations.codeReferenceForm.saveSettings': 'Save settings',
  'integrations.codeReferenceForm.savingLabel': 'Saving settings',
  'integrations.codeReferenceForm.statusLabel': 'Status',
  'integrations.codeReferenceForm.title': 'Edit code reference',
  'integrations.codeReferencesList.roleRestrictionTooltip':
    'The "{role}" role does not allow you to edit this integration',
  'integrations.configurationForm.actionDeniedTooltip': "Your permissions don't allow you to perform this action",
  'integrations.configurationForm.additionalFormVariablesLabel': 'Additional form variables',
  'integrations.configurationForm.cancelEditNameLabel': 'Cancel editing name',
  'integrations.configurationForm.chooseMaintainer': 'Choose maintainer',
  'integrations.configurationForm.clickHouseDestinationsLoadError':
    'ClickHouse Data Export destinations could not be loaded. Try again.',
  'integrations.configurationForm.connectionStatusHeading': 'Connection status',
  'integrations.configurationForm.copyLabel': 'Copy {name}',
  'integrations.configurationForm.createFailedToast': 'Failed to create integration configuration. Try again later.',
  'integrations.configurationForm.createdNamedToast': '{name} integration configuration created',
  'integrations.configurationForm.createdToast': 'Integration configuration created',
  'integrations.configurationForm.dataExportNotConfigured': 'Data Export is not configured',
  'integrations.configurationForm.defaultValueMarker': '(Default)',
  'integrations.configurationForm.defaultValuePlaceholder': '{value} (Default)',
  'integrations.configurationForm.delete': 'Delete',
  'integrations.configurationForm.deleteConfigurationBody': 'This configuration will be deleted permanently.',
  'integrations.configurationForm.deleteConfigurationHeading': 'Delete configuration',
  'integrations.configurationForm.deleteConfirmBody': 'This configuration will be deleted permanently.',
  'integrations.configurationForm.deleteConfirmLabel': 'Delete',
  'integrations.configurationForm.deleteConfirmTitle': 'Delete configuration?',
  'integrations.configurationForm.deleteFailedToast': 'Failed to delete integration configuration',
  'integrations.configurationForm.deletedToast': 'Integration configuration deleted',
  'integrations.configurationForm.duplicateKey':
    "Duplicate value ''{value}'' found in the field ''key''. Ensure each key is unique.",
  'integrations.configurationForm.duplicateName':
    "Duplicate value ''{value}'' found in the field ''name''. Ensure each name is unique.",
  'integrations.configurationForm.editJsonLabel': 'Edit JSON data',
  'integrations.configurationForm.editNameLabel': 'Edit name',
  'integrations.configurationForm.formVariablesDocumentationLink': 'formVariables documentation',
  'integrations.configurationForm.integrationAlreadyExists': 'Integration already exists',
  'integrations.configurationForm.invalidJsonArray': 'Input should be a valid JSON array',
  'integrations.configurationForm.invalidJsonStructure':
    'The JSON array does not match the required structure. Refer to the {documentationLink} for detailed definitions.',
  'integrations.configurationForm.loadingDestinationsLabel': 'Loading destinations',
  'integrations.configurationForm.loadingMaintainerLabel': 'Loading maintainer',
  'integrations.configurationForm.loadingMembersLabel': 'Loading members',
  'integrations.configurationForm.loadingRoleBuilderLabel': 'Loading role builder',
  'integrations.configurationForm.maintainerLabel': 'Maintainer',
  'integrations.configurationForm.maintainerUpdatedToast': 'Maintainer updated',
  'integrations.configurationForm.modifyFieldDeniedTooltip': "Your permissions don't allow you to modify this field",
  'integrations.configurationForm.nameDescription': 'Give this integration a unique name.',
  'integrations.configurationForm.nameHint': 'Give this integration a unique name',
  'integrations.configurationForm.nameLabel': 'Name',
  'integrations.configurationForm.noMaintainer': 'None',
  'integrations.configurationForm.oauthDisconnectedTooltip':
    'The connection to {integrationName} has been disconnected. Reconnect to continue using this integration.',
  'integrations.configurationForm.optionsLoadError': 'There was a problem fetching options.',
  'integrations.configurationForm.policyHint':
    'Add a policy to select and filter the events sent to {integrationName}. Need help writing a policy? Read <link>our documentation</link> or <supportLink>create a support ticket</supportLink>.',
  'integrations.configurationForm.projectEnvironmentKey': 'Project/environment key',
  'integrations.configurationForm.projectEnvironmentKeyHint':
    'Copy this key into the Snowflake LaunchDarkly Experimentation app in step 4',
  'integrations.configurationForm.publicKeyHint':
    'Select the checkbox below and save your configuration to generate a public key.',
  'integrations.configurationForm.requiredField': 'This field is required',
  'integrations.configurationForm.restrictedFieldTooltip':
    'You are not permitted to edit this field. Contact your administrator for more information.',
  'integrations.configurationForm.retry': 'Retry',
  'integrations.configurationForm.saveAndValidate': 'Save and Validate',
  'integrations.configurationForm.saveConfiguration': 'Save configuration',
  'integrations.configurationForm.saveNameLabel': 'Save name',
  'integrations.configurationForm.savingConfiguration': 'Saving configuration',
  'integrations.configurationForm.sendEvent': 'Send event',
  'integrations.configurationForm.sendTestEventHint':
    'Check your configuration by sending a test event from LaunchDarkly to your warehouse.',
  'integrations.configurationForm.sendTestEventLabel': 'Send a test event',
  'integrations.configurationForm.statusLabel': 'Status',
  'integrations.configurationForm.termsAgreement':
    'I have read and agree to the <link>Integration Terms and Conditions</link>',
  'integrations.configurationForm.testConnectionAndSave': 'Test connection and save',
  'integrations.configurationForm.testingConnection': 'Testing connection',
  'integrations.configurationForm.unknownError': 'An unknown error occurred. Try again later.',
  'integrations.configurationForm.updateMaintainerFailedToast': 'Failed to update maintainer. Try again later.',
  'integrations.configurationForm.updatedNamedToast': '{name} integration configuration updated',
  'integrations.configurationForm.updatedToast': 'Integration configuration updated',
  'integrations.configurationForm.validateConnection': 'Validate connection',
  'integrations.configurationForm.validateSaveFirstTooltip':
    'Your configuration must be saved before validating the connection',
  'integrations.configurationForm.validateTooltip':
    'Check your configuration by sending a test event from LaunchDarkly to {integrationName}.',
  'integrations.configurationList.emptyTitle': 'No integration configurations found',
  'integrations.configurationPage.deleteConfirmLabel': 'Delete',
  'integrations.configurationPage.deleteConfirmTitle': 'Warning',
  'integrations.configurationPage.deleteConfirmValidationLabel': 'To complete deletion type DELETE in Uppercase',
  'integrations.configurationPage.deleteIntegration': 'Delete integration',
  'integrations.configurationPage.deleteIntegrationBody':
    'This integration will be deleted permanently. Any dependent objects will be impacted.',
  'integrations.configurationPage.editDrawerTitle': 'Edit {integrationName} configuration',
  'integrations.configurationPage.learnMore':
    'To learn more about the {integrationName} integration, <link>read the documentation</link>.',
  'integrations.configurationPage.notFound': 'Integration configuration not found',
  'integrations.configurationPage.prerequisitesTitle': 'Prerequisites',
  'integrations.configurationPage.raiseSupportTicket': 'Raise a support ticket',
  'integrations.configurationPage.viewDocumentation': 'View documentation',
  'integrations.connectionStatus.actionsLabel': 'Integration Status Actions',
  'integrations.connectionStatus.connected': 'Connected',
  'integrations.connectionStatus.contentLabel': 'Integration Status Content',
  'integrations.connectionStatus.degraded': 'Degraded',
  'integrations.connectionStatus.disconnected': 'Disconnected',
  'integrations.connectionStatus.failed': 'This connection failed. {error}',
  'integrations.connectionStatus.failedAt': 'This connection failed at {timestamp}. {error}',
  'integrations.connectionStatus.healthy': 'This connection is healthy',
  'integrations.connectionStatus.healthyAsOf': 'This connection is healthy as of {timestamp}',
  'integrations.connectionStatus.notConnected': 'Not connected',
  'integrations.connectionStatus.notInitialized':
    'This connection has not yet been initialized. If the connection is not initialized within 30 minutes, <link>Contact Support</link>.',
  'integrations.connectionStatus.regionLabel': 'Integration Status',
  'integrations.connectionStatus.tagGroupLabel': 'connection status',
  'integrations.createDestination.title': 'Create a destination',
  'integrations.createIntegration.learnMore':
    'To learn more about the {name} integration, <link>read the documentation</link>.',
  'integrations.createIntegration.title': 'Create {name} configuration',
  'integrations.createSlackWebhook.title': 'Create a Slack incoming webhook',
  'integrations.createWebhook.title': 'Create a webhook',
  'integrations.dataExportForm.cancel': 'Cancel',
  'integrations.dataExportForm.destinationAlreadyExists': 'Data Export destination already exists',
  'integrations.dataExportForm.destinationExists': 'Destination already exists',
  'integrations.dataExportForm.enableDataExportBody':
    "You need to enable Data Export for flags. To enable it for an individual flag, edit the flag's Settings tab. To enable it for all flags in an environment, edit the environment. <link>Learn more</link>",
  'integrations.dataExportForm.environmentDescription':
    'Select a project and environment for this data export destination.',
  'integrations.dataExportForm.environmentLabel': 'Select project and environment',
  'integrations.dataExportForm.findEnvironmentPlaceholder': 'Find a project or environment',
  'integrations.dataExportForm.loadingEnvironmentsPlaceholder': 'Loading environments and destinations…',
  'integrations.dataExportForm.saveButton': 'Test connection and save',
  'integrations.dataExportForm.savingButton': 'Saving configuration',
  'integrations.databricksDataExport.accessTokenDescription':
    'A Databricks personal access token (PAT). It is used to connect and is not stored after setup.',
  'integrations.databricksDataExport.accessTokenLabel': 'Access token',
  'integrations.databricksDataExport.accessTokenRequired': 'Access token is required',
  'integrations.databricksDataExport.authMethodHint': 'How LaunchDarkly authenticates to Databricks.',
  'integrations.databricksDataExport.authMethodHintLocked':
    'How LaunchDarkly authenticates to Databricks. The method cannot be changed after saving.',
  'integrations.databricksDataExport.authMethodLabel': 'Authentication method',
  'integrations.databricksDataExport.bucketSecretKeyDescription':
    'The AWS secret access key for the bucket access ID. It is not stored after setup.',
  'integrations.databricksDataExport.bucketSecretKeyLabel': 'Bucket secret key',
  'integrations.databricksDataExport.bucketSecretKeyRequired': 'Bucket secret key is required',
  'integrations.databricksDataExport.catalogDescription':
    'The Databricks catalog where LaunchDarkly writes exported data.',
  'integrations.databricksDataExport.catalogLabel': 'Catalog',
  'integrations.databricksDataExport.catalogNameInvalid':
    'Catalog name must contain only letters, numbers, and underscores',
  'integrations.databricksDataExport.catalogRequired': 'Catalog is required',
  'integrations.databricksDataExport.clientIdDescription':
    'The Application ID of your Databricks service principal (the OAuth M2M client ID).',
  'integrations.databricksDataExport.clientIdLabel': 'Client ID',
  'integrations.databricksDataExport.clientIdRequired': 'Client ID is required',
  'integrations.databricksDataExport.clientSecretDescription':
    'The OAuth secret generated for the service principal. It is used to connect and is not stored after setup.',
  'integrations.databricksDataExport.clientSecretLabel': 'Client secret',
  'integrations.databricksDataExport.clientSecretRequired': 'Client secret is required',
  'integrations.databricksDataExport.destinationAlreadyExists': 'Databricks Data Export destination already exists',
  'integrations.databricksDataExport.hostDescription': 'The hostname of your Databricks workspace.',
  'integrations.databricksDataExport.hostHttpScheme':
    'Enter the hostname without http:// (e.g. dbc-1234.cloud.databricks.com or https://dbc-1234.cloud.databricks.com, not http://dbc-1234.cloud.databricks.com)',
  'integrations.databricksDataExport.hostInvalid': 'Enter a valid hostname (e.g. dbc-1234.cloud.databricks.com)',
  'integrations.databricksDataExport.hostLabel': 'Server hostname',
  'integrations.databricksDataExport.hostRequired': 'Server hostname is required',
  'integrations.databricksDataExport.httpPathDescription': 'The HTTP path of your Databricks SQL warehouse.',
  'integrations.databricksDataExport.httpPathLabel': 'HTTP path',
  'integrations.databricksDataExport.httpPathRequired': 'HTTP path is required',
  'integrations.databricksDataExport.invalidHostname': 'Enter a valid Databricks workspace hostname or URL',
  'integrations.databricksDataExport.invalidHttpPath':
    'HTTP path must start with /sql/ (e.g. /sql/1.0/warehouses/abc123)',
  'integrations.databricksDataExport.invalidIdentifier': 'Must contain only letters, numbers, underscores, and hyphens',
  'integrations.databricksDataExport.metastoreDescription': 'The Databricks metastore where exported data is written.',
  'integrations.databricksDataExport.metastoreLabel': 'Metastore',
  'integrations.databricksDataExport.metastoreRequired': 'Metastore is required',
  'integrations.databricksDataExport.nameRequired': 'Name is required',
  'integrations.databricksDataExport.oauthM2m': 'OAuth M2M',
  'integrations.databricksDataExport.personalAccessToken': 'Personal access token',
  'integrations.databricksDataExport.prerequisiteServicePrincipal':
    'A Databricks service principal with credentials (an OAuth client ID and secret, or a personal access token) that can write to the target catalog and schema',
  'integrations.databricksDataExport.prerequisiteWarehouse': 'A running Databricks SQL warehouse',
  'integrations.databricksDataExport.regionPlaceholder': 'Select a region',
  'integrations.databricksDataExport.s3AccessIdDescription':
    'The AWS access key ID with permission to write to the staging bucket.',
  'integrations.databricksDataExport.s3AccessIdLabel': 'Bucket access ID',
  'integrations.databricksDataExport.s3AccessIdRequired': 'Bucket access ID is required',
  'integrations.databricksDataExport.s3BucketNameDescription':
    'The S3 bucket that stages data before loading into Databricks.',
  'integrations.databricksDataExport.s3BucketNameLabel': 'S3 bucket name',
  'integrations.databricksDataExport.s3BucketNameRequired': 'S3 bucket name is required',
  'integrations.databricksDataExport.s3BucketRegionDescription':
    'The AWS region of the S3 bucket that stages data before loading into Databricks.',
  'integrations.databricksDataExport.s3BucketRegionLabel': 'S3 bucket region',
  'integrations.databricksDataExport.s3BucketRegionRequired': 'S3 bucket region is required',
  'integrations.databricksDataExport.schemaDescription': 'The schema where LaunchDarkly writes exported data.',
  'integrations.databricksDataExport.schemaLabel': 'Schema',
  'integrations.databricksDataExport.schemaNameInvalid':
    'Schema name must contain only letters, numbers, and underscores',
  'integrations.databricksDataExport.schemaRequired': 'Schema is required',
  'integrations.databricksDataExport.selectProjectAndEnvironment': 'Select a project and environment',
  'integrations.databricksDataExport.termsRequired': 'You must accept the Integration Terms and Conditions',
  'integrations.databricksDestinationForm.accessTokenHint': 'Personal Access Token (PAT) for Databricks.',
  'integrations.databricksDestinationForm.accessTokenLabel': 'Access token',
  'integrations.databricksDestinationForm.bucketSecretKeyLabel': 'Bucket secret key',
  'integrations.databricksDestinationForm.catalogHint': 'The Databricks catalog.',
  'integrations.databricksDestinationForm.catalogLabel': 'Catalog',
  'integrations.databricksDestinationForm.createError': 'Failed to create Databricks destination. Try again later.',
  'integrations.databricksDestinationForm.createdToast': 'Databricks destination created',
  'integrations.databricksDestinationForm.hive': 'Hive',
  'integrations.databricksDestinationForm.hostHint': 'The hostname of your Databricks server.',
  'integrations.databricksDestinationForm.hostLabel': 'Server hostname',
  'integrations.databricksDestinationForm.httpPathHint': 'The HTTP path of your Databricks server.',
  'integrations.databricksDestinationForm.httpPathLabel': 'HTTP path',
  'integrations.databricksDestinationForm.metastoreLabel': 'Metastore',
  'integrations.databricksDestinationForm.s3AccessIdLabel': 'Bucket access ID',
  'integrations.databricksDestinationForm.s3BucketNameLabel': 'S3 bucket name',
  'integrations.databricksDestinationForm.s3BucketRegionLabel': 'S3 bucket region',
  'integrations.databricksDestinationForm.schemaHint': 'Your chosen schema name.',
  'integrations.databricksDestinationForm.schemaLabel': 'Schema',
  'integrations.databricksDestinationForm.unityCatalog': 'Unity Catalog',
  'integrations.databricksDestinationForm.updateError': 'Failed to update Databricks destination. Try again later.',
  'integrations.databricksExperimentation.accessTokenDescription':
    'A Databricks personal access token with access to the catalog and schema below.',
  'integrations.databricksExperimentation.accessTokenLabel': 'Personal access token',
  'integrations.databricksExperimentation.accessTokenPlaceholder': 'dapi…',
  'integrations.databricksExperimentation.accessTokenRequired': 'Personal access token is required',
  'integrations.databricksExperimentation.accessTokenRotateDescription':
    'Enter a new token here to rotate it. Leave the obfuscated value untouched to keep the current token.',
  'integrations.databricksExperimentation.authMethodHint':
    'Choose how LaunchDarkly authenticates to your Databricks workspace. It cannot be changed after you save.',
  'integrations.databricksExperimentation.authMethodHintLocked':
    'The authentication method chosen when this integration was created. It cannot be changed after you save.',
  'integrations.databricksExperimentation.authMethodLabel': 'Authentication method',
  'integrations.databricksExperimentation.authMethodOauth': 'OAuth M2M',
  'integrations.databricksExperimentation.authMethodPat': 'Personal access token',
  'integrations.databricksExperimentation.cancel': 'Cancel',
  'integrations.databricksExperimentation.cancelEditAccessTokenLabel': 'Cancel editing personal access token',
  'integrations.databricksExperimentation.catalogDescription':
    'The Unity Catalog catalog LaunchDarkly writes experiment results to.',
  'integrations.databricksExperimentation.catalogLabel': 'Catalog',
  'integrations.databricksExperimentation.catalogRequired': 'Catalog is required',
  'integrations.databricksExperimentation.clientIdDescription':
    'The OAuth client ID (application ID) of your Databricks service principal.',
  'integrations.databricksExperimentation.clientIdLabel': 'Client ID',
  'integrations.databricksExperimentation.clientIdLockedDescription':
    'The OAuth client ID of your Databricks service principal. This cannot be changed after you save; recreate the integration to use different credentials.',
  'integrations.databricksExperimentation.clientIdRequired': 'Client ID is required',
  'integrations.databricksExperimentation.clientSecretDescription':
    'The OAuth secret generated for your Databricks service principal.',
  'integrations.databricksExperimentation.clientSecretLabel': 'Client secret',
  'integrations.databricksExperimentation.clientSecretLockedDescription':
    'This cannot be changed after you save; recreate the integration to rotate it.',
  'integrations.databricksExperimentation.clientSecretPlaceholder': 'dose…',
  'integrations.databricksExperimentation.clientSecretRequired': 'Client secret is required',
  'integrations.databricksExperimentation.destinationDescription':
    'Select a data export destination to map this experimentation setup to.',
  'integrations.databricksExperimentation.destinationLabel': 'Data export destination',
  'integrations.databricksExperimentation.editAccessTokenLabel': 'Edit personal access token',
  'integrations.databricksExperimentation.emptyStateAction': 'Configure data export',
  'integrations.databricksExperimentation.emptyStateBody':
    'A Databricks data export integration needs to be configured to continue with this integration.',
  'integrations.databricksExperimentation.emptyStateTitle': 'Data Export not configured',
  'integrations.databricksExperimentation.environmentDescription':
    'Only environments with a Databricks Data Export destination will be available. You cannot edit this after you save the configuration.',
  'integrations.databricksExperimentation.environmentLabel': 'Select project and environment',
  'integrations.databricksExperimentation.httpPathDescription':
    'The HTTP path of your SQL warehouse, found under its connection details. Prefilled from your data export; change it to query through a different warehouse. You cannot edit this after you save the configuration.',
  'integrations.databricksExperimentation.httpPathInvalid':
    'HTTP path must start with /sql/ (e.g. /sql/1.0/warehouses/abc123)',
  'integrations.databricksExperimentation.httpPathLabel': 'HTTP path',
  'integrations.databricksExperimentation.httpPathRequired': 'HTTP path is required',
  'integrations.databricksExperimentation.identifierInvalid':
    'Must contain only letters, numbers, underscores, and hyphens',
  'integrations.databricksExperimentation.metricsCatalogDescription':
    'The catalog holding the data your metrics query, if it differs from the one above.',
  'integrations.databricksExperimentation.metricsCatalogLabel': 'Metrics catalog',
  'integrations.databricksExperimentation.metricsCatalogOptionalLabel': 'Metrics catalog (optional)',
  'integrations.databricksExperimentation.metricsSchemaDescription':
    'The schema holding the data your metrics query, if it differs from the one above.',
  'integrations.databricksExperimentation.metricsSchemaLabel': 'Metrics schema',
  'integrations.databricksExperimentation.metricsSchemaOptionalLabel': 'Metrics schema (optional)',
  'integrations.databricksExperimentation.prerequisiteCredentialsOauth':
    'You need a SQL warehouse and credentials with permission to read and write in the catalog below — an OAuth client ID and secret from a Databricks service principal, or a personal access token',
  'integrations.databricksExperimentation.prerequisiteCredentialsPat':
    'You need a SQL warehouse and credentials with permission to read and write in the catalog below — a personal access token',
  'integrations.databricksExperimentation.prerequisiteDataExport':
    'You must have Data Export set up for Databricks on your selected project and environment',
  'integrations.databricksExperimentation.prerequisiteUnityCatalog': 'Your Databricks workspace must use Unity Catalog',
  'integrations.databricksExperimentation.saveAccessTokenLabel': 'Save personal access token',
  'integrations.databricksExperimentation.schemaDescription':
    'The schema within that catalog LaunchDarkly writes experiment results to.',
  'integrations.databricksExperimentation.schemaLabel': 'Schema',
  'integrations.databricksExperimentation.schemaRequired': 'Schema is required',
  'integrations.databricksExperimentation.selectEnvironmentToPrefillPlaceholder':
    'Select an environment to fill this from your data export',
  'integrations.databricksExperimentation.setupPrerequisiteDataExport':
    'You must configure Data Export for your selected environment',
  'integrations.databricksExperimentation.setupPrerequisiteMetricsDatabase':
    'You must have a metrics database set up in Databricks',
  'integrations.databricksExperimentation.setupPrerequisitePat':
    'You must have a Databricks personal access token (PAT)',
  'integrations.databricksExperimentation.setupPrerequisitesHeading': 'Setup prerequisites',
  'integrations.databricksExperimentation.step1Heading': 'Step 1. Connect to your Databricks workspace',
  'integrations.databricksExperimentation.step1IntroOauth':
    'Enter the SQL warehouse LaunchDarkly should connect to, and choose how it authenticates — with an OAuth client ID and secret from a Databricks service principal, or a personal access token. See the <oauthLink>Databricks OAuth M2M docs</oauthLink> or the <patLink>personal access token docs</patLink>.',
  'integrations.databricksExperimentation.step1IntroPat':
    'Enter the SQL warehouse LaunchDarkly should connect to, and a personal access token it can authenticate with. See the <link>Databricks personal access token docs</link> for how to create one.',
  'integrations.databricksExperimentation.step2Heading': 'Step 2. Choose where LaunchDarkly reads and writes data',
  'integrations.databricksExperimentation.step2Intro':
    'LaunchDarkly writes experiment results to the catalog and schema below. Point it at your metrics data too if that lives somewhere else. None of these can be edited after you save.',
  'integrations.databricksExperimentation.workspaceUrlDescription':
    'Taken from your Databricks Data Export destination, so both read from the same workspace.',
  'integrations.databricksExperimentation.workspaceUrlInvalid':
    'Enter the hostname without a URL scheme (e.g. dbc-1234.cloud.databricks.com, not https://dbc-1234.cloud.databricks.com)',
  'integrations.databricksExperimentation.workspaceUrlLabel': 'Workspace URL',
  'integrations.databricksExperimentation.workspaceUrlRequired': 'Workspace URL is required',
  'integrations.databricksForm.identifierFormat': 'Must contain only letters, numbers, and underscores',
  'integrations.deleteCodeReferenceDialog.body':
    'This repository connection and all data associated with it will be deleted permanently. If automated code reference updates are still being sent over, this repository may be recreated automatically.',
  'integrations.deleteCodeReferenceDialog.confirm': 'Delete',
  'integrations.deleteCodeReferenceDialog.title': 'Delete Code Reference repository?',
  'integrations.deleteConfigurationDialog.body': 'This configuration will be deleted permanently.',
  'integrations.deleteConfigurationDialog.confirm': 'Delete',
  'integrations.deleteConfigurationDialog.title': 'Delete configuration?',
  'integrations.deleteDestinationDialog.body': 'This destination will be deleted permanently.',
  'integrations.deleteDestinationDialog.confirm': 'Delete',
  'integrations.deleteDestinationDialog.title': 'Delete destination?',
  'integrations.deleteIntegrationDialog.body':
    'This action cannot be undone. This will delete the integration and all of its content.',
  'integrations.deleteIntegrationDialog.confirm': 'Delete',
  'integrations.deleteIntegrationDialog.title': 'Are you sure you want to delete this integration?',
  'integrations.deleteSlackWebhookDialog.body': 'This incoming webhook will be deleted permanently.',
  'integrations.deleteSlackWebhookDialog.confirm': 'Delete',
  'integrations.deleteSlackWebhookDialog.title': 'Delete Slack incoming webhook?',
  'integrations.deleteWebhookDialog.body': 'This webhook will be deleted permanently.',
  'integrations.deleteWebhookDialog.confirm': 'Delete',
  'integrations.deleteWebhookDialog.title': 'Delete webhook?',
  'integrations.destinationForm.accessError': 'There was a problem accessing this integration.',
  'integrations.destinationForm.createError': 'Failed to create destination. Try again later.',
  'integrations.destinationForm.createdToast': 'Destination created',
  'integrations.destinationForm.createdToastWithName': '{name} destination created',
  'integrations.destinationForm.deleteError': 'Failed to delete destination',
  'integrations.destinationForm.deletedToast': 'Destination deleted',
  'integrations.destinationForm.destinationTypeHint': 'Choose one of the supported destination types',
  'integrations.destinationForm.destinationTypeLabel': 'Destination type',
  'integrations.destinationForm.destinationTypePlaceholder': 'Select type',
  'integrations.destinationForm.duplicateError':
    'A destination already exists for this project, environment, and integration type. Select a different project or environment.',
  'integrations.destinationForm.enableDataExportNotice':
    "You need to enable Data Export. To enable it for an individual flag, edit the flag's Settings tab. To enable it for all flags in an environment, edit the environment.",
  'integrations.destinationForm.environmentPlaceholder': 'Select an environment',
  'integrations.destinationForm.loadingDestinationsPlaceholder': 'Loading data export destinations…',
  'integrations.destinationForm.nameLabel': 'Name',
  'integrations.destinationForm.projectAndEnvironmentHint':
    'Your LaunchDarkly project and environment. This cannot be edited after this destination is saved.',
  'integrations.destinationForm.projectAndEnvironmentLabel': 'Project and environment',
  'integrations.destinationForm.projectAndEnvironmentLockedTooltip':
    'You cannot edit the LaunchDarkly project and environment after you click Save destination. Create a new destination in the project and environment you wish to use.',
  'integrations.destinationForm.projectAndEnvironmentPlaceholder': 'Find a project or environment',
  'integrations.destinationForm.saveDestination': 'Save destination',
  'integrations.destinationForm.savingLabel': 'Saving destination',
  'integrations.destinationForm.statusLabel': 'Status',
  'integrations.destinationForm.termsAgreement':
    'I have read and agree to the <link>Integration Terms and Conditions</link>',
  'integrations.destinationForm.updateError': 'Failed to update destination. Try again later.',
  'integrations.destinationForm.updatedToast': 'Destination updated',
  'integrations.destinationForm.updatedToastWithName': '{name} destination updated',
  'integrations.destinationForm.viewDocsOrSupport':
    'View <docs>documentation</docs> or <support>create a support ticket</support>.',
  'integrations.destinationHealthAlert.configurationError': 'Configuration error',
  'integrations.destinationHealthAlert.credentialFailure': 'Credential failure',
  'integrations.destinationHealthAlert.internalError': 'Internal error',
  'integrations.destinationHealthAlert.permissionError': 'Permission error',
  'integrations.destinationHealthAlert.unknownError': 'Unknown error',
  'integrations.destinationKind.azureEventHubs': 'Azure Event Hubs',
  'integrations.destinationKind.bigqueryDataExport': 'BigQuery Data Export',
  'integrations.destinationKind.bigqueryExperimentation': 'BigQuery Native Experimentation',
  'integrations.destinationKind.clickhouseDataExport': 'ClickHouse Data Export',
  'integrations.destinationKind.clickhouseExperimentation': 'ClickHouse Native Experimentation',
  'integrations.destinationKind.databricksDataExport': 'Databricks Data Export',
  'integrations.destinationKind.databricksExperimentation': 'Databricks Native Experimentation',
  'integrations.destinationKind.kinesis': 'Amazon Kinesis',
  'integrations.destinationKind.mparticle': 'mParticle',
  'integrations.destinationKind.pubsub': 'Google Cloud PubSub',
  'integrations.destinationKind.redshiftDataExport': 'Redshift Data Export',
  'integrations.destinationKind.redshiftExperimentation': 'Redshift Native Experimentation',
  'integrations.destinationKind.s3DataExport': 'S3 Data Export',
  'integrations.destinationKind.segment': 'Segment',
  'integrations.destinationKind.snowflake': 'Snowflake',
  'integrations.destinationKind.snowflakeExperimentation': 'Snowflake Native Experimentation',
  'integrations.destinationKind.snowflakeExport': 'Snowflake Export',
  'integrations.destinationNotFound.body': "Sorry, we can't find the destination you're looking for.",
  'integrations.destinationNotFound.title': 'Destination not found',
  'integrations.destinationsList.roleRestrictionTooltip':
    'The "{role}" role does not allow you to edit this integration.',
  'integrations.destinationsList.unknownEnvironment': 'Unknown environment',
  'integrations.detailsPanel.connected': 'Connected',
  'integrations.detailsPanel.connectionStatus': 'Connection status',
  'integrations.detailsPanel.created': 'Created',
  'integrations.detailsPanel.dataSource': 'Data source',
  'integrations.detailsPanel.degraded': 'Degraded',
  'integrations.detailsPanel.disconnected': 'Disconnected',
  'integrations.detailsPanel.lastSuccessfulExport': 'Last successful export',
  'integrations.detailsPanel.listLabel': 'Integration details',
  'integrations.detailsPanel.maintainer': 'Maintainer',
  'integrations.detailsPanel.notConnected': 'Not connected',
  'integrations.detailsPanel.pending': 'Pending',
  'integrations.editDestination.deleteDestination': 'Delete destination',
  'integrations.editDestination.deleteDestinationHeading': 'Delete destination',
  'integrations.editDestination.deleteWarning': 'This destination will be deleted permanently.',
  'integrations.editDestination.sendEvent': 'Send event',
  'integrations.editDestination.sendEventDisabledTooltip':
    'The destination must be enabled before sending a test event',
  'integrations.editDestination.sendTestEventBody':
    'Check your configuration by sending a test event from LaunchDarkly that will be visible in your {destination} destination.',
  'integrations.editDestination.sendTestEventHeading': 'Send a test event',
  'integrations.editDestination.testEventError': 'Failed to send test event. Try again later.',
  'integrations.editDestination.testEventSentToast': 'Test event sent',
  'integrations.editDestination.title': 'Edit destination',
  'integrations.editIntegration.delete': 'Delete',
  'integrations.editIntegration.deleteHeading': 'Delete configuration',
  'integrations.editIntegration.deleteWarning': 'This configuration will be deleted permanently.',
  'integrations.editIntegration.learnMore':
    'To learn more about the {name} integration, <link>read the documentation</link>.',
  'integrations.editIntegration.loadError': 'Failed to load integration configuration: {message}',
  'integrations.editIntegration.notFound': 'Integration configuration not found',
  'integrations.editIntegration.restrictedFieldTooltip': 'Your permissions don’t allow you to modify this field',
  'integrations.editIntegration.title': 'Edit {name} configuration',
  'integrations.editSlackWebhook.deleteHeading': 'Delete Slack incoming webhook',
  'integrations.editSlackWebhook.deleteIncomingWebhook': 'Delete incoming webhook',
  'integrations.editSlackWebhook.deleteWarning': 'This incoming webhook will be deleted permanently.',
  'integrations.editSlackWebhook.title': 'Edit Slack incoming webhook',
  'integrations.editWebhook.deleteHeading': 'Delete webhook',
  'integrations.editWebhook.deleteWarning': 'This webhook will be deleted permanently.',
  'integrations.editWebhook.title': 'Edit webhook',
  'integrations.environmentPicker.environmentSearchPlaceholder': 'Search environments…',
  'integrations.environmentPicker.loadingEnvironmentsLabel': 'Loading environments',
  'integrations.environmentPicker.loadingProjectsLabel': 'Loading projects',
  'integrations.environmentPicker.noEnvironmentsAvailable': 'No environments available',
  'integrations.environmentPicker.noProjectsAvailable': 'No projects available',
  'integrations.environmentPicker.placeholder': 'Select an environment',
  'integrations.environmentPicker.projectSearchLabel': 'Search',
  'integrations.environmentPicker.projectSearchPlaceholder': 'Search projects…',
  'integrations.errorLog.body':
    'This event log shows the most recent errors received by this configuration. To learn more about configuring the {name} integration, <link>read the documentation</link>.',
  'integrations.errorLog.emptyBody': 'This configuration has never received any errors.',
  'integrations.errorLog.emptyTitle': 'Nice work',
  'integrations.errorLog.eventsRegisterBody':
    'This event log shows the most recent errors received by this configuration. To learn more about configuring the events register for {name} integration, <link>read the documentation</link>.',
  'integrations.errorLog.statusCode': 'Status Code: {code}',
  'integrations.errorLog.statusCodeUnavailable': 'N/A',
  'integrations.errorLog.timeoutBody': 'The HTTP request to {name} timed out',
  'integrations.errorLog.title': '{name} error log',
  'integrations.featureStoreForm.environmentHint':
    'Your LaunchDarkly environment. You cannot edit this field after you click Save configuration.',
  'integrations.featureStoreForm.environmentLabel': 'Environment',
  'integrations.featureStoreForm.environmentLockedTooltip':
    'You cannot edit the LaunchDarkly environment after you click Save configuration. Create a new integration in the environment you wish to use.',
  'integrations.featureStoreForm.nameLabel': 'Name',
  'integrations.featureStoreForm.saveConfiguration': 'Save configuration',
  'integrations.featureStoreForm.savingLabel': 'Saving {name} configuration',
  'integrations.featureStoreForm.statusCode': 'Status code: {code}',
  'integrations.featureStoreForm.statusLabel': 'Status',
  'integrations.featureStoreForm.termsAgreement':
    'I have read and agree to the <link>Integration Terms and Conditions</link>',
  'integrations.featureStoreForm.testRequestTooltip': 'Check your configuration by sending a test request to {name}.',
  'integrations.featureStoreForm.validateConnection': 'Validate connection',
  'integrations.featureStoreForm.validateDisabledTooltip':
    'Your configuration must be saved before validating the connection',
  'integrations.featureStoreList.noMatch': 'No integrations match.',
  'integrations.flagImportForm.maintainerHint':
    'The LaunchDarkly Member to be marked as flag maintainer for flags created by the integration.',
  'integrations.flagImportForm.maintainerLabel': 'LaunchDarkly Maintainer',
  'integrations.flagImportForm.maintainerPlaceholder': 'Select flag maintainer',
  'integrations.flagImportForm.maintainerSelectLabel': 'Select flag maintainer',
  'integrations.flagImportForm.nameLabel': 'Name',
  'integrations.flagImportForm.projectHint':
    'The project you are importing to. You cannot edit this field after initial creation.',
  'integrations.flagImportForm.projectLabel': 'Project',
  'integrations.flagImportForm.projectLockedTooltip':
    'You cannot edit the LaunchDarkly project after you click Save configuration. Create a new integration in the project you wish to use.',
  'integrations.flagImportForm.saveConfiguration': 'Save configuration',
  'integrations.flagImportForm.savingLabel': 'Saving {name} configuration',
  'integrations.flagImportForm.termsAgreement':
    'I have read and agree to the <link>Integration Terms and Conditions</link>',
  'integrations.flagImportForm.triggerImport': 'Trigger import',
  'integrations.flagImportForm.triggerImportTooltip': 'Trigger a new integration run with your current configuration',
  'integrations.flagImportForm.validateDisabledTooltip':
    'Your configuration must be saved before validating the connection',
  'integrations.flagImportList.configurationNameColumn': 'Configuration name',
  'integrations.flagImportList.configurationOptionsLabel': '{name} configuration options',
  'integrations.flagImportList.editConfiguration': 'Edit integration configuration',
  'integrations.flagImportList.editRestrictedTooltip':
    'You are not allowed to edit this integration. Contact your Administrator for more information.',
  'integrations.flagImportList.lastSuccessfulImportColumn': 'Last successful import',
  'integrations.flagImportList.noMatch': 'No integrations match.',
  'integrations.flagImportList.stateColumn': 'State',
  'integrations.flagImportList.viewErrorLog': 'View error log',
  'integrations.githubAppDrawer.accountLinkedLabel': 'Your GitHub account linked',
  'integrations.githubAppDrawer.authorizationUnknownDescription':
    "We couldn't check your GitHub authorization. Try again, or <support>contact support</support> if this keeps happening.",
  'integrations.githubAppDrawer.authorizeAction': 'Authorize GitHub',
  'integrations.githubAppDrawer.authorizeDescription': 'Personally authorize your GitHub identity.',
  'integrations.githubAppDrawer.authorizeUnavailableDescription': 'Available once the GitHub App is installed.',
  'integrations.githubAppDrawer.authorizedDescription': 'Your GitHub identity is connected.',
  'integrations.githubAppDrawer.body':
    'Install the LaunchDarkly GitHub App to create feature flags, start releases, and clean up code from your pull requests.',
  'integrations.githubAppDrawer.configLoadError':
    'There was an issue loading the GitHub App integration configuration.',
  'integrations.githubAppDrawer.connectedTag': 'Connected',
  'integrations.githubAppDrawer.githubUnavailableDescription':
    "GitHub couldn't be reached. Try again; signing in won't fix this.",
  'integrations.githubAppDrawer.inaccessibleDescription':
    "Your GitHub account is linked, but it can't access the GitHub organization where this app is installed.",
  'integrations.githubAppDrawer.installAction': 'Install GitHub App',
  'integrations.githubAppDrawer.installDescription': 'Install the app for your LaunchDarkly account.',
  'integrations.githubAppDrawer.installedDescription': 'Installed for your LaunchDarkly account.',
  'integrations.githubAppDrawer.installedLabel': 'GitHub App installed',
  'integrations.githubAppDrawer.loadingStatusLabel': 'Loading GitHub App status…',
  'integrations.githubAppDrawer.manageReposAction': 'Manage repositories',
  'integrations.githubAppDrawer.notConnectedTag': 'Not connected',
  'integrations.githubAppDrawer.settingsBody':
    'Manage which repositories run automation and customize account defaults on the <link>Release Autopilot settings page</link>.',
  'integrations.githubAppDrawer.signInDifferentAccountAction': 'Sign in with a different GitHub account',
  'integrations.githubAppDrawer.statusLabel': '{label} status',
  'integrations.githubAppDrawer.statusLoadError': 'There was an issue loading your GitHub connection status.',
  'integrations.githubAppDrawer.title': 'GitHub App integration',
  'integrations.githubAppDrawer.tryAgain': 'Try again',
  'integrations.githubRepoPicker.manageRepositories': 'Manage repositories',
  'integrations.githubRepoPicker.publicHeading': 'Public repositories',
  'integrations.githubRepoPicker.publicHint': 'Public repositories are not supported for security reasons',
  'integrations.googlePubSubDestinationForm.needHelp':
    'Need help configuring your Google PubSub destination? Read our <docs>documentation</docs> or <support>create a support ticket</support>.',
  'integrations.googlePubSubDestinationForm.projectHint': 'The Google PubSub project id',
  'integrations.googlePubSubDestinationForm.projectLabel': 'Project',
  'integrations.googlePubSubDestinationForm.topicHint': 'The Google PubSub topic id',
  'integrations.googlePubSubDestinationForm.topicLabel': 'Topic',
  'integrations.integrationCard.actionLabel': '{action} {name}',
  'integrations.integrationCard.addIntegration': 'Add integration',
  'integrations.integrationCard.addNew': 'Add new',
  'integrations.integrationCard.collapseLabel': 'Collapse integration details',
  'integrations.integrationCard.configurationMenuLabel': 'integration configuration',
  'integrations.integrationCard.configure': 'Configure',
  'integrations.integrationCard.configureLabel': 'Configure {name}',
  'integrations.integrationCard.delete': 'Delete',
  'integrations.integrationCard.expandLabel': 'Expand integration details',
  'integrations.integrationCard.goToEnvironment': 'Go to environment',
  'integrations.integrationCard.healthWarningBody': 'An error has occurred in an integration',
  'integrations.integrationCard.healthWarningLabel': 'Health warning',
  'integrations.integrationCard.moreInformationLabel': 'more information',
  'integrations.integrationCard.noCreatePermissionTooltip': 'You do not have permission to create a new integration',
  'integrations.integrationCard.partnerBuiltBody':
    'This integration was built by a verified LaunchDarkly Technology Partner.',
  'integrations.integrationCard.viewAll': 'View all ({count})',
  'integrations.integrationConfigPicker.listLabel': 'Integration configurations',
  'integrations.integrationConfigPicker.logoAlt': '{name} integration logo',
  'integrations.integrationConfigPicker.optionLabel': 'Integration configuration option',
  'integrations.integrationConfigPicker.searchLabel': 'Search integrations',
  'integrations.integrationConfigPicker.searchPlaceholder': 'Search integrations…',
  'integrations.integrationConfigPicker.triggerLabel': 'Select integration configuration',
  'integrations.integrationConfigPicker.triggerPlaceholder': 'Choose from available integrations',
  'integrations.integrationDetails.learnMore':
    'To learn more about the {name} integration, <link>read the documentation</link>.',
  'integrations.integrationDetails.logoLabel': '{name} logo',
  'integrations.integrationDetails.manageIntegration': 'Manage integration',
  'integrations.integrationDetails.managedExternally': 'This integration is managed externally.',
  'integrations.integrationDetails.setUpBody':
    'Set up the integration now to start making informed decisions directly within LaunchDarkly.',
  'integrations.integrationDetails.triggerName': '{name} trigger',
  'integrations.integrationForm.disconnectedWarning':
    'The connection to {name} has been disconnected. Reconnect to continue using this integration.',
  'integrations.integrationForm.loadingRoleBuilderLabel': 'Loading role builder',
  'integrations.integrationForm.nameLabel': 'Name',
  'integrations.integrationForm.optionsError': 'Error fetching options. Try again later.',
  'integrations.integrationForm.policyHint':
    'Add a policy to select and filter the events sent to {name}. Need help writing a policy? Read <docs>our documentation</docs> or <support>create a support ticket</support>.',
  'integrations.integrationForm.restrictedFieldTooltip':
    'You are not permitted to edit this field. Contact your administrator for more information.',
  'integrations.integrationForm.saveAndValidate': 'Save and Validate',
  'integrations.integrationForm.saveConfiguration': 'Save configuration',
  'integrations.integrationForm.savingLabel': 'Saving {name} configuration',
  'integrations.integrationForm.statusLabel': 'Status',
  'integrations.integrationForm.termsAgreement':
    'I have read and agree to the <link>Integration Terms and Conditions</link>',
  'integrations.integrationForm.testEventTooltip':
    'Check your configuration by sending a test event from LaunchDarkly to {name}.',
  'integrations.integrationForm.validateConnection': 'Validate connection',
  'integrations.integrationForm.validateDisabledTooltip':
    'Your configuration must be saved before validating the connection',
  'integrations.integrationNotFound.body': 'Sorry, we can’t find the configuration you’re looking for.',
  'integrations.integrationNotFound.title': 'Configuration not found',
  'integrations.integrationSubscriptions.configurationColumn': 'Configuration',
  'integrations.integrationSubscriptions.editConfiguration': 'Edit integration configuration',
  'integrations.integrationSubscriptions.editRestrictedTooltip':
    'You are not allowed to edit this integration. Contact your Administrator for more information.',
  'integrations.integrationSubscriptions.errorsColumn': 'Errors',
  'integrations.integrationSubscriptions.eventSuccessfullySent': 'Event successfully sent',
  'integrations.integrationSubscriptions.mostRecentErrorColumn': 'Most recent error',
  'integrations.integrationSubscriptions.mostRecentSuccessColumn': 'Most recent success',
  'integrations.integrationSubscriptions.optionsLabel': '{name} subscription options',
  'integrations.integrationSubscriptions.roleRestrictionTooltip':
    'The "{role}" role does not allow you to edit this integration.',
  'integrations.integrationSubscriptions.successEventsColumn': 'Success events',
  'integrations.integrationSubscriptions.viewErrorLog': 'View recent error log',
  'integrations.integrationsFilters.categoryAi': 'AI',
  'integrations.integrationsFilters.categoryApprovals': 'Approvals',
  'integrations.integrationsFilters.categoryAudienceSync': 'Audience sync',
  'integrations.integrationsFilters.categoryAutomation': 'Automation',
  'integrations.integrationsFilters.categoryBigSegmentStore': 'Big segment store',
  'integrations.integrationsFilters.categoryCustomerDataPlatform': 'Customer data platform',
  'integrations.integrationsFilters.categoryCustomerSupport': 'Customer support',
  'integrations.integrationsFilters.categoryDataWarehouse': 'Data warehouse',
  'integrations.integrationsFilters.categoryDeveloperTools': 'Developer tools',
  'integrations.integrationsFilters.categoryEventStreaming': 'Event streaming',
  'integrations.integrationsFilters.categoryField': 'Category',
  'integrations.integrationsFilters.categoryFieldDescription': 'The integration category.',
  'integrations.integrationsFilters.categoryFlagImport': 'Flag import',
  'integrations.integrationsFilters.categoryIncidentResponse': 'Incident response',
  'integrations.integrationsFilters.categoryInfrastructure': 'Infrastructure',
  'integrations.integrationsFilters.categoryIssueTracking': 'Issue tracking',
  'integrations.integrationsFilters.categoryLogManagement': 'Log management',
  'integrations.integrationsFilters.categoryMessaging': 'Messaging',
  'integrations.integrationsFilters.categoryMigration': 'Migration',
  'integrations.integrationsFilters.categoryMonitoring': 'Monitoring',
  'integrations.integrationsFilters.categoryProductAnalytics': 'Product analytics',
  'integrations.integrationsFilters.categorySingleSignOn': 'Single sign-on',
  'integrations.integrationsFilters.categorySourceControl': 'Source control',
  'integrations.integrationsFilters.clearCategoriesLabel': 'Remove all categories',
  'integrations.integrationsFilters.connected': 'Connected',
  'integrations.integrationsFilters.connectionStatusField': 'Connection status',
  'integrations.integrationsFilters.connectionStatusFieldDescription': 'Whether the integration is connected.',
  'integrations.integrationsFilters.notConnected': 'Not connected',
  'integrations.integrationsFilters.searchField': 'Search',
  'integrations.integrationsFilters.searchFieldDescription':
    'A search across integration names, categories, and descriptions.',
  'integrations.integrationsPage.checkForTypos': 'Check for typos.',
  'integrations.integrationsPage.clearFilters': 'Clear filters',
  'integrations.integrationsPage.clearSearch': 'Clear search',
  'integrations.integrationsPage.clearSearchAndFilters': 'Clear search and filters',
  'integrations.integrationsPage.collaborateBanner':
    'Collaborate with your team. Connect LaunchDarkly to <slack>Slack</slack> or <teams>Microsoft Teams</teams> .',
  'integrations.integrationsPage.loadingLabel': 'Loading integrations',
  'integrations.integrationsPage.noMatch': 'No integrations match.',
  'integrations.integrationsPage.noMatchFilters': 'No integrations match your filters',
  'integrations.integrationsPage.noMatchSearch': 'No integrations match your search',
  'integrations.integrationsPage.noMatchSearchAndFilters': 'No integrations match your search and filters',
  'integrations.integrationsPage.noMatchSearchHint': 'Search for an integration by name, category, or description.',
  'integrations.integrationsPage.noMatchTitle': 'No integrations match. Try this:',
  'integrations.integrationsPage.searchLabel': 'Search integrations',
  'integrations.integrationsPage.searchPlaceholder': 'Find an integration',
  'integrations.integrationsPage.tryAdjustingFilters': 'Try adjusting or clearing your filters.',
  'integrations.integrationsPage.tryAdjustingSearchOrFilters': 'Try adjusting your search or filters.',
  'integrations.integrationsPage.tryAnotherSearch': 'Try another search term or clear the current search.',
  'integrations.kinesisDestinationForm.needHelp':
    'Need help configuring your Kinesis destination? Read our <docs>documentation</docs> or <support>create a support ticket</support>.',
  'integrations.kinesisDestinationForm.regionHint': "The Kinesis stream's AWS region key",
  'integrations.kinesisDestinationForm.regionLabel': 'AWS Kinesis stream region',
  'integrations.kinesisDestinationForm.regionPlaceholder': 'Choose region',
  'integrations.kinesisDestinationForm.regionSelectLabel': 'Choose region',
  'integrations.kinesisDestinationForm.roleAddressHint': 'The address of the AWS role that will be writing to Kinesis',
  'integrations.kinesisDestinationForm.roleAddressLabel': 'Role address',
  'integrations.kinesisDestinationForm.roleAddressPlaceholder': 'ex: arn:aws:iam::874699288871:role/example-role',
  'integrations.kinesisDestinationForm.streamNameLabel': 'AWS Kinesis stream name',
  'integrations.mParticleDestinationForm.addAMapping': 'Add a mapping',
  'integrations.mParticleDestinationForm.addMapping': 'Add mapping',
  'integrations.mParticleDestinationForm.apiKeyHint':
    'Your mParticle API key. You cannot edit this field after you click "Save destination."',
  'integrations.mParticleDestinationForm.apiKeyLabel': 'API key',
  'integrations.mParticleDestinationForm.apiKeyTooltip': 'Find your mParticle API key in your mParticle account.',
  'integrations.mParticleDestinationForm.apiSecretHint':
    'Your mParticle API secret. You cannot edit this field after you click "Save destination."',
  'integrations.mParticleDestinationForm.apiSecretLabel': 'API secret',
  'integrations.mParticleDestinationForm.apiSecretTooltip': 'Find your mParticle secret in your mParticle account.',
  'integrations.mParticleDestinationForm.cancel': 'Cancel',
  'integrations.mParticleDestinationForm.contextAlreadyMapped': 'Context "{context}" already used in a mapping',
  'integrations.mParticleDestinationForm.contextLabel': 'LaunchDarkly Context',
  'integrations.mParticleDestinationForm.deleteMapping': 'Delete',
  'integrations.mParticleDestinationForm.deprecatedAnonymousIdentifierHint':
    'Select the type of identifier you use to identify your anonymous users in mParticle.',
  'integrations.mParticleDestinationForm.deprecatedAnonymousIdentifierLabel': 'Anonymous User Identifier DEPRECATED',
  'integrations.mParticleDestinationForm.deprecatedAnonymousIdentifierPlaceholder': 'Select anonymous user identity',
  'integrations.mParticleDestinationForm.deprecatedTooltip': 'This field is deprecated and can not be edited.',
  'integrations.mParticleDestinationForm.deprecatedUserIdentifierHint':
    'Select the type of identifier you use to identify your users in mParticle.',
  'integrations.mParticleDestinationForm.deprecatedUserIdentifierLabel': 'User Identifier DEPRECATED',
  'integrations.mParticleDestinationForm.environmentLabel': 'mParticle Environment',
  'integrations.mParticleDestinationForm.environmentPlaceholder': 'Select mParticle environment',
  'integrations.mParticleDestinationForm.needHelp':
    'Need help configuring your mParticle destination? Read our <docs>documentation</docs> or <support>create a support ticket</support>.',
  'integrations.mParticleDestinationForm.noMappings': 'You have no mappings',
  'integrations.mParticleDestinationForm.selectProjectFirstTooltip': 'Choose a Project and Environment first.',
  'integrations.mParticleDestinationForm.userIdentifierLabel': 'mParticle User Identifier',
  'integrations.mParticleDestinationForm.userIdentifierPlaceholder': 'Select user identity',
  'integrations.mParticleDestinationForm.userIdentifiersLabel': 'User Identifiers',
  'integrations.mParticleDestinationForm.userIdentitiesColumn': 'mParticle User Identities',
  'integrations.notifications.cloudflareKvValidated': '{integrationName} KV Namespace validated',
  'integrations.notifications.connectionValidationFailed':
    'There was a problem validating the connection to {integrationName}',
  'integrations.notifications.destinationCreated': 'Destination created',
  'integrations.notifications.destinationDeleted': 'Destination deleted',
  'integrations.notifications.destinationUpdated': 'Destination updated',
  'integrations.notifications.featureStoreValidated': '{integrationName} validated',
  'integrations.notifications.featureStoreValidationFailed':
    'There was problem validating the connection to {integrationName}',
  'integrations.notifications.oauthConnected': 'OAuth service connected',
  'integrations.notifications.oauthDisconnected': 'OAuth service disconnected',
  'integrations.notifications.slackCreated': 'Slack integration created',
  'integrations.notifications.slackDeleted': 'Slack integration deleted',
  'integrations.notifications.slackUpdated': 'Slack integration updated',
  'integrations.notifications.testEventSent': 'Test event sent',
  'integrations.notifications.triggerCreated': 'Trigger created',
  'integrations.notifications.triggerDeleted': 'Trigger deleted',
  'integrations.notifications.triggerDisabled': 'Trigger disabled',
  'integrations.notifications.triggerEnabled': 'Trigger enabled',
  'integrations.notifications.triggerUpdated': 'Trigger updated',
  'integrations.notifications.triggerUrlReset': 'Trigger URL reset',
  'integrations.notifications.webhookCreated': 'Webhook created',
  'integrations.notifications.webhookDeleted': 'Webhook deleted',
  'integrations.notifications.webhookUpdated': 'Webhook updated',
  'integrations.oauthAuthorize.aiDataNotice':
    'This app makes LaunchDarkly data available to MCP clients, including AI models. Flag descriptions, comments and logs may include prompt injection instructions that the model could follow. <link>Learn more</link>',
  'integrations.oauthAuthorize.authorize': 'Authorize',
  'integrations.oauthAuthorize.clientHost': 'Hosted at <strong>{host}</strong>',
  'integrations.oauthAuthorize.deny': 'Deny',
  'integrations.oauthAuthorize.footerBody':
    'Your LaunchDarkly credentials and billing information will never be shared. For more information, see <link>OAuth documentation</link>.',
  'integrations.oauthAuthorize.greeting': 'Hi {name},',
  'integrations.oauthAuthorize.permissionsIntro': '<strong>{name}</strong> would like permission to:',
  'integrations.oauthAuthorize.redirectHost': 'After you authorize, you will be redirected to <strong>{host}</strong>.',
  'integrations.oauthAuthorize.unverifiedWarning':
    'This application has not been verified by LaunchDarkly. Only authorize it if you trust the source.',
  'integrations.oauthProviderConfig.appDynamicsBaseUriHint':
    'Your {provider} host URL. For example <strong>https://example.saas.appdynamics.com</strong>',
  'integrations.oauthProviderConfig.appDynamicsBaseUriLabel': '{provider} host URL',
  'integrations.oauthProviderConfig.appDynamicsClientIdHint':
    'Your {provider} Client ID in the format: <strong>{format}</strong>',
  'integrations.oauthProviderConfig.baseUriHint':
    'Your {provider} host URL. For example, https://subdomain.example.com',
  'integrations.oauthProviderConfig.baseUriLabel': '{provider} Host URL',
  'integrations.oauthProviderConfig.cancel': 'Cancel',
  'integrations.oauthProviderConfig.clientIdHint': '{provider} OAuth Client ID',
  'integrations.oauthProviderConfig.clientIdLabel': 'Client ID',
  'integrations.oauthProviderConfig.clientSecretHint': '{provider} OAuth Client Secret',
  'integrations.oauthProviderConfig.clientSecretLabel': 'Client Secret',
  'integrations.oauthProviderConfig.heading': 'Register {provider} OAuth provider',
  'integrations.oauthProviderConfig.redirectUrlBody':
    'Copy and paste this URL. When you register a new OAuth client in {provider}, you will use this as the <strong>Redirect URL</strong>.',
  'integrations.oauthProviderConfig.register': 'Register OAuth Provider',
  'integrations.oauthProviderConfig.registerHint': 'After you register, complete the form below.',
  'integrations.oauthProviderConfig.serviceNowBaseUriHint':
    'Your {provider} host URL. For example, https://subdomain.service-now.com',
  'integrations.redshiftDataExport.artifactsLoadError':
    "Couldn't load the setup artifacts for this destination. Refresh to try again.",
  'integrations.redshiftDataExport.awsAccountIdDescription': 'The AWS account ID that owns the Redshift cluster.',
  'integrations.redshiftDataExport.awsAccountIdLabel': 'AWS account ID',
  'integrations.redshiftDataExport.clusterIdentifierDescription':
    "Your Redshift cluster identifier. Find it on the Redshift cluster's General information page.",
  'integrations.redshiftDataExport.clusterIdentifierLabel': 'Cluster identifier',
  'integrations.redshiftDataExport.clusterRegionDescription': 'The AWS region where your Redshift cluster lives.',
  'integrations.redshiftDataExport.clusterRegionLabel': 'Cluster region',
  'integrations.redshiftDataExport.copyIamPermissionsPolicyLabel': 'Copy IAM permissions policy',
  'integrations.redshiftDataExport.copyIamTrustPolicyLabel': 'Copy IAM trust policy',
  'integrations.redshiftDataExport.copyS3BucketNameLabel': 'Copy S3 staging bucket name',
  'integrations.redshiftDataExport.copySetupScriptLabel': 'Copy setup SQL script',
  'integrations.redshiftDataExport.customBucketGenerateFirstHint': 'Generate the setup script in Step 4 to continue',
  'integrations.redshiftDataExport.customBucketHint':
    'LaunchDarkly writes exported data to this S3 bucket as a staging area before loading it into Redshift. Choose a name and region, then create the bucket in your AWS account.',
  'integrations.redshiftDataExport.customBucketNameDescription':
    'S3 bucket names are unique across AWS. Change the suggested name if it is already taken. Create the bucket in your AWS account with this exact name.',
  'integrations.redshiftDataExport.customBucketStep3Heading': 'Step 3. Choose your S3 staging bucket',
  'integrations.redshiftDataExport.customBucketStep4Heading': 'Step 4. Generate setup script',
  'integrations.redshiftDataExport.customNamesHint':
    'The following Redshift objects will be created on your cluster. You can customize their names.',
  'integrations.redshiftDataExport.databaseNameDescription':
    'The name of the database in Redshift where LaunchDarkly stores your exported data. Use lowercase letters, numbers, or underscores, up to 127 characters.',
  'integrations.redshiftDataExport.databaseNameLabel': 'Database name',
  'integrations.redshiftDataExport.endpointDescription':
    'Your Redshift cluster endpoint hostname, without a protocol. You can include an explicit port and database path.',
  'integrations.redshiftDataExport.endpointLabel': 'Cluster endpoint',
  'integrations.redshiftDataExport.exportGrantDestinationLoadError':
    "We couldn't load the Data Export destination for this environment, so the script that grants access to your exported data can't be generated. It needs that destination's database, schema, and user names to grant on the right objects.",
  'integrations.redshiftDataExport.exportGrantDestinationMissing':
    "The Data Export destination this integration was set up against no longer exists for this environment, so the script that grants access to your exported data can't be regenerated. Recreate the Data Export destination for this environment, then reopen this page.",
  'integrations.redshiftDataExport.exportGrantInstructions':
    'Connect to the {database} database and run this script to grant permissions for experimentation',
  'integrations.redshiftDataExport.exportGrantStepName': 'Part 3: Grant permissions to {database} database',
  'integrations.redshiftDataExport.generateFirstHint': 'Generate the setup script in Step 3 to continue',
  'integrations.redshiftDataExport.generateFirstPlaceholder': 'Generate the setup script to populate this',
  'integrations.redshiftDataExport.generateScriptFailed': 'Failed to generate setup script. Try again later.',
  'integrations.redshiftDataExport.generateSetupScript': 'Generate setup script',
  'integrations.redshiftDataExport.iamPermissionsPolicyCopiedToast': 'IAM permissions policy copied to clipboard',
  'integrations.redshiftDataExport.iamPermissionsPolicyLabel': 'IAM permissions policy',
  'integrations.redshiftDataExport.iamRoleArnDescription': 'Paste the ARN of the IAM role you created in AWS.',
  'integrations.redshiftDataExport.iamRoleArnLabel': 'IAM role ARN',
  'integrations.redshiftDataExport.iamRoleCompletedDescription':
    'This destination uses the IAM role below to access your S3 staging bucket and load data into Redshift.',
  'integrations.redshiftDataExport.iamRoleCreateDescription':
    'Create a new IAM role in AWS using the trust policy and permissions policy below, then paste its ARN here.',
  'integrations.redshiftDataExport.iamTrustPolicyCopiedToast': 'IAM trust policy copied to clipboard',
  'integrations.redshiftDataExport.iamTrustPolicyLabel': 'IAM trust policy',
  'integrations.redshiftDataExport.invalidIamRoleArn':
    'Enter a valid IAM role ARN (e.g. arn:aws:iam::123456789012:role/MyRole)',
  'integrations.redshiftDataExport.prerequisiteAwsAccessAndCluster':
    'Access to the AWS Management Console or AWS CLI, and a running Amazon Redshift cluster',
  'integrations.redshiftDataExport.prerequisiteCluster':
    'A running Amazon Redshift cluster reachable from the public internet',
  'integrations.redshiftDataExport.prerequisiteDataExportEnabled':
    'You must have Data Export enabled for your selected project and environment',
  'integrations.redshiftDataExport.prerequisiteIamPermissions':
    'Permission to create IAM roles, policies, and an S3 staging bucket in your AWS account',
  'integrations.redshiftDataExport.prerequisiteIamPermissionsNeeded':
    'You need permission to create IAM roles and policies, and an S3 bucket in your AWS account',
  'integrations.redshiftDataExport.refresh': 'Refresh',
  'integrations.redshiftDataExport.refreshArtifactsLabel': 'Refresh setup artifacts',
  'integrations.redshiftDataExport.refreshStep3Placeholder': 'Refresh Step 3 to reload',
  'integrations.redshiftDataExport.regionPlaceholder': 'Select a region',
  'integrations.redshiftDataExport.s3BucketHint':
    'LaunchDarkly will write exported data to this S3 bucket as a staging area before loading into Redshift. The bucket is auto-named below; create it in your AWS account and choose its region.',
  'integrations.redshiftDataExport.s3BucketNameCopiedToast': 'S3 staging bucket name copied to clipboard',
  'integrations.redshiftDataExport.s3BucketNameDescription':
    'Create an S3 bucket in your AWS account with this exact name.',
  'integrations.redshiftDataExport.s3BucketNameLabel': 'S3 staging bucket name',
  'integrations.redshiftDataExport.s3BucketRegionDescription':
    'The AWS region of the S3 bucket. This must match the region where you create the bucket.',
  'integrations.redshiftDataExport.s3BucketRegionLabel': 'S3 staging bucket region',
  'integrations.redshiftDataExport.schemaNameDescription': 'The Redshift schema where export tables will be created.',
  'integrations.redshiftDataExport.schemaNameLabel': 'Schema name',
  'integrations.redshiftDataExport.setupScriptCopiedToast': 'Setup SQL script copied to clipboard',
  'integrations.redshiftDataExport.setupScriptEmpty': 'Setup script was empty. Try again.',
  'integrations.redshiftDataExport.setupScriptHint':
    'Run the generated script in your Amazon Redshift query editor to create the database, user and grants LaunchDarkly needs to write exported data.',
  'integrations.redshiftDataExport.setupScriptLabel': 'Setup SQL script',
  'integrations.redshiftDataExport.step1Heading': 'Step 1. Configure your Redshift cluster',
  'integrations.redshiftDataExport.step2Heading': 'Step 2. Customize resource names (optional)',
  'integrations.redshiftDataExport.step3Heading': 'Step 3. Generate setup script',
  'integrations.redshiftDataExport.step4Heading': 'Step 4. Create your S3 staging bucket',
  'integrations.redshiftDataExport.step5Heading': 'Step 5. Set up AWS IAM access',
  'integrations.redshiftDataExport.transferInProgressBody':
    'Your initial data transfer may take 15–60 minutes to complete.',
  'integrations.redshiftDataExport.transferInProgressTitle': 'Initial data transfer in progress',
  'integrations.redshiftDataExport.userNameDescription':
    'The Redshift user that LaunchDarkly will use to write exported data.',
  'integrations.redshiftDataExport.userNameLabel': 'User name',
  'integrations.redshiftDestinationForm.completeSetupError':
    'Failed to complete setup for Redshift destination. Try again later.',
  'integrations.redshiftDestinationForm.createError': 'Failed to create Redshift destination. Try again later.',
  'integrations.redshiftDestinationForm.createdToast': 'Redshift destination created',
  'integrations.redshiftDestinationForm.invalidAccountId':
    'AWS account ID must be the 12-digit ID of the AWS account that hosts your Redshift cluster.',
  'integrations.redshiftDestinationForm.invalidEndpoint': 'Enter a valid Redshift endpoint.',
  'integrations.redshiftDestinationForm.s3BucketNameConsecutivePeriods':
    'The bucket name cannot contain two periods in a row.',
  'integrations.redshiftDestinationForm.s3BucketNameInvalid':
    'Use 3 to 63 lowercase letters, numbers, periods, or hyphens. Start and end with a letter or number.',
  'integrations.redshiftDestinationForm.s3BucketNameIpAddress':
    'The bucket name cannot be formatted as an IP address, such as 192.168.5.4.',
  'integrations.redshiftDestinationForm.s3BucketNameRequired': 'Enter an S3 staging bucket name.',
  'integrations.redshiftDestinationForm.s3BucketNameReservedPrefix':
    'The bucket name cannot start with {prefix}, which AWS reserves.',
  'integrations.redshiftDestinationForm.s3BucketNameReservedSuffix':
    'The bucket name cannot end with {suffix}, which AWS reserves.',
  'integrations.redshiftDestinationForm.updateError': 'Failed to update Redshift destination. Try again later.',
  'integrations.redshiftExperimentation.prerequisiteAdminAccess': 'You must have admin access to your Redshift cluster',
  'integrations.redshiftExperimentation.prerequisiteDataExport':
    'You must have Data Export set up for your selected project and environment',
  'integrations.redshiftExperimentation.prerequisiteMetricsDatabase':
    'You must have a metrics database set up in Redshift',
  'integrations.redshiftNativeExperimentationForm.additionalFieldsHeading': 'Additional fields (optional)',
  'integrations.redshiftNativeExperimentationForm.additionalFieldsHint':
    'Rename the database, schema, permission groups, and service users that LaunchDarkly creates. Leave any field blank to use the default name.',
  'integrations.redshiftNativeExperimentationForm.artifactsLoadError':
    "Couldn't load the setup scripts for this integration. Refresh to try again.",
  'integrations.redshiftNativeExperimentationForm.awsAccountIdDescription':
    'The AWS account ID that owns the Redshift cluster.',
  'integrations.redshiftNativeExperimentationForm.awsAccountIdFormatError':
    'AWS account ID must be the 12-digit ID of the AWS account that hosts your Redshift cluster.',
  'integrations.redshiftNativeExperimentationForm.awsAccountIdLabel': 'AWS account ID',
  'integrations.redshiftNativeExperimentationForm.cancel': 'Cancel',
  'integrations.redshiftNativeExperimentationForm.clusterEndpointDescription':
    'Your Redshift cluster endpoint hostname, without a protocol. You can include an explicit port and database path.',
  'integrations.redshiftNativeExperimentationForm.clusterEndpointLabel': 'Cluster endpoint',
  'integrations.redshiftNativeExperimentationForm.clusterIdentifierDescription':
    "Your Redshift cluster identifier. Find it on the Redshift cluster's General information page.",
  'integrations.redshiftNativeExperimentationForm.clusterIdentifierLabel': 'Cluster identifier',
  'integrations.redshiftNativeExperimentationForm.clusterRegionDescription':
    'The AWS region where your Redshift cluster lives.',
  'integrations.redshiftNativeExperimentationForm.clusterRegionLabel': 'Cluster region',
  'integrations.redshiftNativeExperimentationForm.clusterRegionPlaceholder': 'Select a region',
  'integrations.redshiftNativeExperimentationForm.copyArtifactLabel': 'Copy {label}',
  'integrations.redshiftNativeExperimentationForm.dataSourceRoleNameDescription':
    'The permission group LaunchDarkly uses to read your metrics.',
  'integrations.redshiftNativeExperimentationForm.dataSourceRoleNameLabel': 'Data source group name',
  'integrations.redshiftNativeExperimentationForm.dataSourceUserNameDescription':
    'The service user LaunchDarkly authenticates as to read your metrics.',
  'integrations.redshiftNativeExperimentationForm.dataSourceUserNameLabel': 'Data source user name',
  'integrations.redshiftNativeExperimentationForm.environmentDescription':
    'Only environments with Data Export set up for Redshift are available. You cannot edit this after you save the configuration.',
  'integrations.redshiftNativeExperimentationForm.environmentLabel': 'Select project and environment',
  'integrations.redshiftNativeExperimentationForm.experimentationDatabaseNameDescription':
    'The database LaunchDarkly creates and connects to for experimentation.',
  'integrations.redshiftNativeExperimentationForm.experimentationDatabaseNameLabel': 'Experimentation database name',
  'integrations.redshiftNativeExperimentationForm.experimentationRoleNameDescription':
    'The permission group LaunchDarkly uses to write experiment results.',
  'integrations.redshiftNativeExperimentationForm.experimentationRoleNameLabel': 'Experimentation group name',
  'integrations.redshiftNativeExperimentationForm.experimentationUserNameDescription':
    'The service user LaunchDarkly authenticates as to write experiment results.',
  'integrations.redshiftNativeExperimentationForm.experimentationUserNameLabel': 'Experimentation user name',
  'integrations.redshiftNativeExperimentationForm.fieldRequiredError': '{label} is required',
  'integrations.redshiftNativeExperimentationForm.generateScripts': 'Generate policies and scripts',
  'integrations.redshiftNativeExperimentationForm.iamRoleArnDescription':
    'Paste the ARN of the IAM role you created in AWS.',
  'integrations.redshiftNativeExperimentationForm.iamRoleArnLabel': 'IAM role ARN',
  'integrations.redshiftNativeExperimentationForm.identifierFormatError':
    'Only letters, digits, underscore, and $ are allowed, and it cannot start with a digit.',
  'integrations.redshiftNativeExperimentationForm.metricsDatabaseDescription':
    'The Redshift database that contains your metric event data. LaunchDarkly is granted read access to this database.',
  'integrations.redshiftNativeExperimentationForm.metricsDatabaseLabel': 'Metrics database name',
  'integrations.redshiftNativeExperimentationForm.metricsSchemaDescription':
    'Optional. The schema within your metrics database that contains your metric event data. Leave blank to grant LaunchDarkly read access to all schemas in the database.',
  'integrations.redshiftNativeExperimentationForm.metricsSchemaLabel': 'Metrics schema name',
  'integrations.redshiftNativeExperimentationForm.nameDescription': 'Give this integration a unique name.',
  'integrations.redshiftNativeExperimentationForm.nameLabel': 'Name',
  'integrations.redshiftNativeExperimentationForm.nameRequiredError': 'Name is required',
  'integrations.redshiftNativeExperimentationForm.nameTooLongError': 'Name must be at most {max} characters',
  'integrations.redshiftNativeExperimentationForm.objectIdentifierDuplicateError':
    'Each group and user must be unique, but this resolves to "{name}", which another field also uses.',
  'integrations.redshiftNativeExperimentationForm.objectIdentifierFormatError':
    'Only lowercase letters, digits, and underscore are allowed, and it cannot start with a digit.',
  'integrations.redshiftNativeExperimentationForm.objectIdentifierTooLongError': 'Must be at most {max} characters.',
  'integrations.redshiftNativeExperimentationForm.permissionsPolicyLabel': 'IAM permissions policy',
  'integrations.redshiftNativeExperimentationForm.refresh': 'Refresh',
  'integrations.redshiftNativeExperimentationForm.refreshArtifactsLabel': 'Refresh setup scripts',
  'integrations.redshiftNativeExperimentationForm.resultsSchemaNameDescription':
    'The schema, inside the experimentation database, where LaunchDarkly writes computed experiment results.',
  'integrations.redshiftNativeExperimentationForm.resultsSchemaNameLabel': 'Results schema name',
  'integrations.redshiftNativeExperimentationForm.scriptStepLabel': 'Step {step}: {label}',
  'integrations.redshiftNativeExperimentationForm.setupScript1':
    'Create the experimentation database, groups, and users',
  'integrations.redshiftNativeExperimentationForm.setupScript2': 'Grant access to the results schema',
  'integrations.redshiftNativeExperimentationForm.setupScript3': 'Grant access to the Data Export schema',
  'integrations.redshiftNativeExperimentationForm.setupScript4': 'Grant LaunchDarkly read access to your metrics',
  'integrations.redshiftNativeExperimentationForm.setupScriptFallback': 'SQL setup script {step}',
  'integrations.redshiftNativeExperimentationForm.step1Body':
    'These connect to the same cluster as your Redshift data export. They are filled in automatically from that configuration when you select an environment above.',
  'integrations.redshiftNativeExperimentationForm.step1Heading': 'Step 1. Configure your Redshift cluster',
  'integrations.redshiftNativeExperimentationForm.step2Body':
    'Tell LaunchDarkly where your metric event data lives. LaunchDarkly is granted read access to only these resources.',
  'integrations.redshiftNativeExperimentationForm.step2Heading': 'Step 2. Configure resource names',
  'integrations.redshiftNativeExperimentationForm.step3Body':
    'Generate the IAM policies and SQL scripts from the details above. Run them in AWS and Redshift to grant LaunchDarkly access.',
  'integrations.redshiftNativeExperimentationForm.step3BodyReadOnly':
    'The SQL scripts that were run in Redshift to grant LaunchDarkly access for this integration.',
  'integrations.redshiftNativeExperimentationForm.step3Heading': 'Step 3. Generate policies and scripts',
  'integrations.redshiftNativeExperimentationForm.step4Body':
    'Create a new IAM role in AWS using the trust policy and permissions policy below, then paste its ARN here.',
  'integrations.redshiftNativeExperimentationForm.step4EmptyState':
    'Generate the policies and scripts in Step 3 to continue',
  'integrations.redshiftNativeExperimentationForm.step4Heading': 'Step 4. Set up AWS IAM access',
  'integrations.redshiftNativeExperimentationForm.termsLink': 'Integration Terms and Conditions',
  'integrations.redshiftNativeExperimentationForm.termsPrefix': 'I have read and agree to the',
  'integrations.redshiftNativeExperimentationForm.toggleAdditionalFieldsLabel': 'Toggle additional fields',
  'integrations.redshiftNativeExperimentationForm.trustPolicyLabel': 'IAM trust policy',
  'integrations.refreshFrequency.cancel': 'Cancel',
  'integrations.refreshFrequency.confirm': 'Update',
  'integrations.refreshFrequency.confirmBody':
    'Changing the refresh frequency to {frequency} affects how often LaunchDarkly reloads metrics for every experiment linked to this integration.',
  'integrations.refreshFrequency.confirmClose': 'Close',
  'integrations.refreshFrequency.confirmHeading': 'Update refresh frequency',
  'integrations.refreshFrequency.customMinutes': '{minutes} minutes',
  'integrations.refreshFrequency.edit': 'Edit refresh frequency',
  'integrations.refreshFrequency.helpBody':
    'How often LaunchDarkly queries your warehouse to refresh experiment results.',
  'integrations.refreshFrequency.helpLabel': 'About refresh frequency',
  'integrations.refreshFrequency.option12h': '12 hours',
  'integrations.refreshFrequency.option15m': '15 minutes',
  'integrations.refreshFrequency.option1h': '1 hour',
  'integrations.refreshFrequency.option24h': '24 hours',
  'integrations.refreshFrequency.option30m': '30 minutes',
  'integrations.refreshFrequency.option3h': '3 hours',
  'integrations.refreshFrequency.option6h': '6 hours',
  'integrations.refreshFrequency.optionsLabel': 'Refresh frequency options',
  'integrations.refreshFrequency.term': 'Refresh frequency',
  'integrations.refreshFrequency.updateFailed': 'Failed to update refresh frequency. Try again later.',
  'integrations.refreshFrequency.updated': 'Refresh frequency updated to {frequency}',
  'integrations.repositoryNotFound.body': "Sorry, we can't find the repository you're looking for.",
  'integrations.repositoryNotFound.title': 'Repository not found',
  'integrations.s3DataExport.bucketNameDescription': 'The name of the S3 bucket where exported data will be written.',
  'integrations.s3DataExport.bucketNameInvalid':
    'Bucket name must be 3-63 characters using lowercase letters, digits, periods, and hyphens, and start and end with a letter or digit',
  'integrations.s3DataExport.bucketNameLabel': 'Bucket name',
  'integrations.s3DataExport.bucketNameRequired': 'Bucket name is required',
  'integrations.s3DataExport.bucketRegionDescription': 'The AWS region where your S3 bucket lives.',
  'integrations.s3DataExport.bucketRegionLabel': 'Bucket region',
  'integrations.s3DataExport.bucketRegionRequired': 'Bucket region is required',
  'integrations.s3DataExport.configureIamAccess': 'Configure IAM access',
  'integrations.s3DataExport.copyIamPermissionsPolicyLabel': 'Copy IAM permissions policy',
  'integrations.s3DataExport.copyIamTrustPolicyLabel': 'Copy IAM trust policy',
  'integrations.s3DataExport.destinationAlreadyExists': 'S3 Data Export destination already exists',
  'integrations.s3DataExport.enterBucketNameFirst': 'Enter a bucket name first',
  'integrations.s3DataExport.fileFormatCsv': 'CSV',
  'integrations.s3DataExport.fileFormatDescription': 'The file format LaunchDarkly writes exported data in.',
  'integrations.s3DataExport.fileFormatJson': 'JSON',
  'integrations.s3DataExport.fileFormatJsonl': 'JSONL',
  'integrations.s3DataExport.fileFormatLabel': 'Exported file format',
  'integrations.s3DataExport.fileFormatParquet': 'Parquet (recommended)',
  'integrations.s3DataExport.fileFormatPlaceholder': 'Select a file format',
  'integrations.s3DataExport.fileFormatRequired': 'Select a file format',
  'integrations.s3DataExport.folderDescription':
    'An optional prefix (folder path) within the bucket to write exported files under.',
  'integrations.s3DataExport.folderLabel': 'Folder name',
  'integrations.s3DataExport.generatePolicies': 'Generate policies',
  'integrations.s3DataExport.generatePoliciesFailed': 'Failed to generate IAM policies',
  'integrations.s3DataExport.iamPermissionsPolicyCopiedToast': 'IAM permissions policy copied to clipboard',
  'integrations.s3DataExport.iamPermissionsPolicyLabel': 'IAM permissions policy',
  'integrations.s3DataExport.iamRoleArnDescription':
    'The ARN of the IAM role LaunchDarkly will assume to write to your bucket.',
  'integrations.s3DataExport.iamRoleArnInvalid': 'Must be a valid IAM role ARN ({example})',
  'integrations.s3DataExport.iamRoleArnLabel': 'IAM role ARN',
  'integrations.s3DataExport.iamRoleArnRequired': 'IAM role ARN is required',
  'integrations.s3DataExport.iamStep1': '1. Generate the IAM permissions policy and trust policy below.',
  'integrations.s3DataExport.iamStep2': '2. Create a new policy in your IAM console using the permissions policy.',
  'integrations.s3DataExport.iamStep3':
    '3. Create an IAM role using the trust policy, then attach the permissions policy from step 2.',
  'integrations.s3DataExport.iamTrustPolicyCopiedToast': 'IAM trust policy copied to clipboard',
  'integrations.s3DataExport.iamTrustPolicyLabel': 'IAM trust policy',
  'integrations.s3DataExport.nameRequired': 'Name is required',
  'integrations.s3DataExport.prerequisiteAwsAccess': 'Access to the AWS Management Console or AWS CLI',
  'integrations.s3DataExport.prerequisiteDataExport': 'Data Export enabled for your selected project and environment',
  'integrations.s3DataExport.prerequisiteIamPermissions':
    'Permission to create IAM roles and policies, and an S3 bucket in your AWS account',
  'integrations.s3DataExport.regionPlaceholder': 'Select a region',
  'integrations.s3DataExport.selectEnvironmentFirst': 'Select a project and environment first',
  'integrations.s3DataExport.selectProjectAndEnvironment': 'Select a project and environment',
  'integrations.s3DataExport.termsRequired': 'You must accept the Integration Terms and Conditions',
  'integrations.s3DataExport.transferInProgressBody':
    'This will take about 30 to 60 minutes. Contact Support if you continue seeing this message after 60 minutes.',
  'integrations.s3DataExport.transferInProgressTitle': 'Initial data transfer in progress',
  'integrations.s3DestinationForm.completeSetupError': 'Failed to complete setup for S3 destination. Try again later.',
  'integrations.s3DestinationForm.createError': 'Failed to create S3 destination. Try again later.',
  'integrations.s3DestinationForm.createdToast': 'S3 destination created',
  'integrations.s3DestinationForm.updateError': 'Failed to update S3 destination. Try again later.',
  'integrations.segmentDestinationForm.anonymousIdContextKindHint':
    'Select which LaunchDarkly Context Kind to map to <link>Anonymous ID</link>.',
  'integrations.segmentDestinationForm.anonymousIdContextKindLabel': 'Anonymous ID Context Kind',
  'integrations.segmentDestinationForm.needHelp':
    'Need help configuring your Segment destination? Read our <docs>documentation</docs> or <support>create a support ticket</support>.',
  'integrations.segmentDestinationForm.selectProjectFirstTooltip':
    'Select a Project and Environment to edit this field.',
  'integrations.segmentDestinationForm.userIdContextKindHint':
    'Select which LaunchDarkly Context Kind to map to <link>User ID</link>.',
  'integrations.segmentDestinationForm.userIdContextKindLabel': 'User ID Context Kind',
  'integrations.segmentDestinationForm.writeKeyHint':
    '<link>Find your Segment write key</link>. You cannot edit this field after you click "Save destination."',
  'integrations.segmentDestinationForm.writeKeyLabel': 'Write key',
  'integrations.segmentDestinationForm.writeKeyLockedTooltip':
    'You cannot edit this field after the destination has been created.',
  'integrations.sentryDrawer.createMapping': 'Create',
  'integrations.sentryDrawer.deleteMapping': 'Delete',
  'integrations.sentryDrawer.eventKeyHint':
    'Enter the event key of your metric to use as the Sentry error event. If you have not created a metric for each project, do so by navigating to the metrics section.',
  'integrations.sentryDrawer.eventKeyLabel': 'Metric event key',
  'integrations.sentryDrawer.eventKeyPlaceholder': "Event key… (example: 'sentry errors')",
  'integrations.sentryDrawer.learnMore':
    'To learn more about the {name} integration, <link>read the documentation</link>.',
  'integrations.sentryDrawer.mappingsBody':
    'Map your Sentry environments to your LaunchDarkly environments to receive errors from Sentry. You can map each Sentry environment only once.',
  'integrations.sentryDrawer.mappingsHeading': 'Environment mappings',
  'integrations.sentryDrawer.metricRequirementBody':
    'There must be a conversion metric with the chosen event key in all mapped projects. If no metrics exist, create one <strong>for each project</strong>.',
  'integrations.sentryDrawer.noMetricBody':
    'There is not a metric set for this integration. Select or create a conversion metric to track the number of errors that Sentry observes.',
  'integrations.sentryDrawer.noMetricForEventKey': 'No metric with selected event key in this project.',
  'integrations.sentryDrawer.notInstalledBody':
    'This integration is not installed. Install it from the <link>Sentry public integrations list</link>.',
  'integrations.sentryDrawer.projectEnvironmentColumn': 'Project - Environment',
  'integrations.sentryDrawer.saveEventKey': 'Save',
  'integrations.sentryDrawer.saveMappingError': 'Could not save environment mapping',
  'integrations.sentryDrawer.saveMetricError': 'Could not save metric selection',
  'integrations.sentryDrawer.sentryEnvironmentInputLabel': 'Sentry environment input',
  'integrations.sentryDrawer.sentryProjectEnvironmentColumn': 'Sentry Project - Environment',
  'integrations.sentryDrawer.title': 'Sentry integration',
  'integrations.sentryDrawer.usingEventKey': 'Using <code>{eventKey}</code> as the metric event key.',
  'integrations.slackApp.kind': 'Application',
  'integrations.slackApp.roleRestrictionTooltip': 'The "{role}" role does not allow you to edit this webhook',
  'integrations.slackApp.title': 'LaunchDarkly Slack App',
  'integrations.slackSubscriptions.channelCount': '{count, plural, one {# channel} other {# channels}}',
  'integrations.slackSubscriptions.filterLabel': 'Slack Channels',
  'integrations.slackSubscriptions.loadError': 'Failed to load Slack subscriptions. Try again later.',
  'integrations.slackSubscriptions.noSearchResults': 'No channels match your search',
  'integrations.slackSubscriptions.noSubscribePermission': "You don't have permission to subscribe to this channel",
  'integrations.slackSubscriptions.noUnsubscribePermission':
    "You don't have permission to unsubscribe from this channel",
  'integrations.slackSubscriptions.notAuthenticatedBody':
    'The Slack app needs to be authorized. <setup>Follow the setup instructions</setup> to connect your Slack workspace.',
  'integrations.slackSubscriptions.notInstalledBody':
    'The LaunchDarkly Slack app is not installed. <install>Install the Slack app</install> and <authorize>authorize it</authorize> to get started.',
  'integrations.slackSubscriptions.searchPlaceholder': 'Start typing to search channels',
  'integrations.slackSubscriptions.subheader': 'Select up to ten channels to receive feedback',
  'integrations.slackSubscriptions.subscribe': 'Subscribe',
  'integrations.slackSubscriptions.subscribeErrorToast': 'Failed to subscribe to {channel}',
  'integrations.slackSubscriptions.subscribedToast': 'Subscribed to {channel}',
  'integrations.slackSubscriptions.subscriptionLimitReached': 'Maximum of {limit} channel subscriptions reached',
  'integrations.slackSubscriptions.unsubscribeErrorToast': 'Failed to unsubscribe from {channel}',
  'integrations.slackSubscriptions.unsubscribedToast': 'Unsubscribed from {channel}',
  'integrations.slackWebhookForm.loadingPolicyEditorLabel': 'Loading policy editor',
  'integrations.slackWebhookForm.nameLabel': 'Name',
  'integrations.slackWebhookForm.policyHint':
    'Add a policy to select and filter the events sent to Slack. Need help writing a policy? Read <docs>our documentation</docs> or <support>create a support ticket</support>.',
  'integrations.slackWebhookForm.saveIncomingWebhook': 'Save incoming webhook',
  'integrations.slackWebhookForm.savingLabel': 'Saving incoming webhook',
  'integrations.slackWebhookForm.statusLabel': 'Status',
  'integrations.slackWebhookForm.termsAgreement':
    'I have read and agree to the <link>Integration Terms and Conditions</link>',
  'integrations.slackWebhookForm.urlHint': 'Enter the URL from one of your <link>Slack incoming webhooks</link>.',
  'integrations.slackWebhookForm.urlLabel': 'URL',
  'integrations.slackWebhookNotFound.body': 'Sorry, we can’t find the hook you’re looking for.',
  'integrations.slackWebhookNotFound.title': 'Slack incoming hook not found',
  'integrations.slackWebhooksList.roleRestrictionTooltip':
    'The "{role}" role does not allow you to edit this integration.',
  'integrations.snowflakeDataExport.accountUrlDescription':
    'Your Snowflake account URL, available in the Account menu.',
  'integrations.snowflakeDataExport.accountUrlLabel': 'Snowflake account URL',
  'integrations.snowflakeDataExport.allowlistIpDescription':
    'Only required if your Snowflake account uses network policies.',
  'integrations.snowflakeDataExport.allowlistIpLabel': 'Allowlist the LaunchDarkly IP',
  'integrations.snowflakeDataExport.copySetupScriptLabel': 'Copy Snowflake Setup Script',
  'integrations.snowflakeDataExport.databaseNameDescription':
    'The name of the database in Snowflake where LaunchDarkly stores your exported data.',
  'integrations.snowflakeDataExport.databaseNameLabel': 'Database name',
  'integrations.snowflakeDataExport.generateScriptFailed': 'Failed to generate setup script. Try again later.',
  'integrations.snowflakeDataExport.generateSetupScriptLabel': 'Generate setup script',
  'integrations.snowflakeDataExport.generateSql': 'Generate SQL',
  'integrations.snowflakeDataExport.prerequisiteAccountAdmin': 'You must have the ACCOUNTADMIN user role in Snowflake',
  'integrations.snowflakeDataExport.roleDescription':
    'The name of the role with the required privileges on the database and warehouse.',
  'integrations.snowflakeDataExport.roleLabel': 'Role',
  'integrations.snowflakeDataExport.schemaNameDescription':
    'The name of the schema within the database where export tables will be created.',
  'integrations.snowflakeDataExport.schemaNameLabel': 'Schema name',
  'integrations.snowflakeDataExport.selectEnvironmentFirstPlaceholder': 'Select an environment first',
  'integrations.snowflakeDataExport.setupScriptCopiedToast': 'Snowflake Setup Script copied to clipboard',
  'integrations.snowflakeDataExport.setupScriptEmpty': 'Setup script was empty',
  'integrations.snowflakeDataExport.setupScriptHint':
    'Run the generated script in Snowflake to create your schema, user, and grant permissions for data export.',
  'integrations.snowflakeDataExport.setupScriptViewerLabel': 'Snowflake Setup Script Viewer',
  'integrations.snowflakeDataExport.usernameDescription':
    'The Snowflake user account that LaunchDarkly will use to connect and export data.',
  'integrations.snowflakeDataExport.usernameLabel': 'Username',
  'integrations.snowflakeDataExport.warehouseNameDescription':
    'The name of the warehouse in Snowflake to store your LaunchDarkly data.',
  'integrations.snowflakeDataExport.warehouseNameLabel': 'Warehouse name',
  'integrations.snowflakeDestinationForm.accountIdLabel': 'Snowflake Account Id',
  'integrations.snowflakeDestinationForm.needHelp':
    'Need help configuring your Snowflake destination? Read our <docs>documentation</docs> or <support>create a support ticket</support>.',
  'integrations.snowflakeExperimentation.accountUrlLabel': 'Snowflake Account/Server URL',
  'integrations.snowflakeExperimentation.cancel': 'Cancel',
  'integrations.snowflakeExperimentation.completeSetupFailed':
    'Failed to complete setup for this integration configuration. Try again later.',
  'integrations.snowflakeExperimentation.copyLoginNameCheckLabel': 'Copy login name check SQL',
  'integrations.snowflakeExperimentation.copySetupScriptLabel': 'Copy Snowflake Setup Script',
  'integrations.snowflakeExperimentation.dataSourceRoleDescription':
    "The name of the role LaunchDarkly uses to read your metric events. Must be different from the role. You can set a custom name, which can't be changed after saving.",
  'integrations.snowflakeExperimentation.dataSourceRoleLabel': 'Data source role',
  'integrations.snowflakeExperimentation.dataSourceRoleNameField': 'data source role name',
  'integrations.snowflakeExperimentation.dataSourceRoleSameAsRole':
    'The data source role cannot be the same as the role',
  'integrations.snowflakeExperimentation.databaseNameDescription':
    "The name of the database in Snowflake where LaunchDarkly stores experiment results. You can set a custom name, which can't be changed after saving.",
  'integrations.snowflakeExperimentation.databaseNameField': 'database name',
  'integrations.snowflakeExperimentation.databaseNameLabel': 'Database name',
  'integrations.snowflakeExperimentation.environmentDescription':
    'Only environments with Data Export setup for Snowflake will be available. You cannot edit this after you save the destination',
  'integrations.snowflakeExperimentation.environmentLabel': 'Select project and environment',
  'integrations.snowflakeExperimentation.generateScript': 'Generate script',
  'integrations.snowflakeExperimentation.generateScriptFailed': 'Failed to generate setup script. Try again later.',
  'integrations.snowflakeExperimentation.generateScriptFirst':
    'Generate and run your Snowflake setup script before saving this configuration.',
  'integrations.snowflakeExperimentation.loginNameCheckHint':
    "Not sure? Run this in a Snowflake worksheet to compare the user's <code>NAME</code> and <code>LOGIN_NAME</code>",
  'integrations.snowflakeExperimentation.loginNameCheckViewerLabel': 'Login name check SQL viewer',
  'integrations.snowflakeExperimentation.loginNameDescription':
    "The <code>LOGIN_NAME</code> LaunchDarkly authenticates with instead of the user name. It can't be changed after saving.",
  'integrations.snowflakeExperimentation.loginNameDiffersLabel':
    "The service user's login name (LOGIN_NAME) is different from its user name (NAME)",
  'integrations.snowflakeExperimentation.loginNameInvalid': 'Only letters, digits, and _ $ @ . + - are allowed',
  'integrations.snowflakeExperimentation.loginNameLabel': 'Login name',
  'integrations.snowflakeExperimentation.loginNameReadOnlyDescription':
    'The <code>LOGIN_NAME</code> LaunchDarkly authenticates with instead of the user name.',
  'integrations.snowflakeExperimentation.loginNameRequired':
    'Enter the login name, or uncheck the box if it matches the user name',
  'integrations.snowflakeExperimentation.loginNameTooLong': 'The login name must be {max} characters or fewer',
  'integrations.snowflakeExperimentation.metricsDatabaseNameDescription':
    'The Snowflake database that contains your metric event data. LaunchDarkly will be granted read access to this database.',
  'integrations.snowflakeExperimentation.metricsDatabaseNameLabel': 'Metrics database name',
  'integrations.snowflakeExperimentation.metricsSchemaNameDescription':
    'The schema within your metrics database. Leave blank to grant access to all schemas in the database.',
  'integrations.snowflakeExperimentation.metricsSchemaNameLabel': 'Metrics schema name',
  'integrations.snowflakeExperimentation.nameInvalid':
    'Only letters, digits, underscore, and $ are allowed (no spaces), starting with a letter or underscore',
  'integrations.snowflakeExperimentation.nameReserved': '{name} is a reserved {fieldLabel} and cannot be used',
  'integrations.snowflakeExperimentation.nameTooLong': 'The {fieldLabel} must be {max} characters or fewer',
  'integrations.snowflakeExperimentation.prefilledFromDataExport':
    'Fetched from your Snowflake data export configuration.',
  'integrations.snowflakeExperimentation.prerequisiteAccountAdmin':
    'You must have the ACCOUNTADMIN user role in Snowflake',
  'integrations.snowflakeExperimentation.prerequisiteDataExport':
    'You must have Data Export set up for your selected project and environment',
  'integrations.snowflakeExperimentation.prerequisiteMetricsDatabase':
    'You must have a metrics database set up in Snowflake',
  'integrations.snowflakeExperimentation.roleDescription':
    "The name of the role with the required privileges on the database and warehouse. You can set a custom name, which can't be changed after saving. Snowflake system roles aren't allowed.",
  'integrations.snowflakeExperimentation.roleLabel': 'Role',
  'integrations.snowflakeExperimentation.roleNameField': 'role name',
  'integrations.snowflakeExperimentation.schemaNameDescription':
    "The name of the schema within the database where experiment results will be written. You can set a custom name, which can't be changed after saving.",
  'integrations.snowflakeExperimentation.schemaNameField': 'schema name',
  'integrations.snowflakeExperimentation.schemaNameLabel': 'Schema name',
  'integrations.snowflakeExperimentation.selectEnvironmentToPrefillPlaceholder':
    'Select an environment to auto-fill this from your data export',
  'integrations.snowflakeExperimentation.setupScriptCopiedToast': 'Snowflake Setup Script copied to clipboard',
  'integrations.snowflakeExperimentation.setupScriptHint':
    'Run this script in snowflake to create your database, schema, user and grant permissions for Experimentation',
  'integrations.snowflakeExperimentation.setupScriptViewerLabel': 'Snowflake Setup Script Viewer',
  'integrations.snowflakeExperimentation.sqlLabel': 'SQL',
  'integrations.snowflakeExperimentation.sqlSnippetCopiedToast': 'SQL snippet copied to clipboard',
  'integrations.snowflakeExperimentation.usernameDescription':
    "The Snowflake user account that LaunchDarkly will use to connect and run experimentation queries. You can set a custom name, which can't be changed after saving.",
  'integrations.snowflakeExperimentation.usernameLabel': 'Username',
  'integrations.snowflakeExperimentation.warehouseNameDescription':
    "The name of the warehouse in Snowflake that LaunchDarkly uses to run experimentation queries. You can set a custom name, which can't be changed after saving.",
  'integrations.snowflakeExperimentation.warehouseNameField': 'warehouse name',
  'integrations.snowflakeExperimentation.warehouseNameLabel': 'Warehouse name',
  'integrations.snowflakeV2DestinationForm.accountUrlHint':
    'Find the account URL in your Snowflake > Account menu, in the Snowflake UI',
  'integrations.snowflakeV2DestinationForm.accountUrlLabel': 'Snowflake account URL',
  'integrations.snowflakeV2DestinationForm.continueInSnowflake': 'Continue setup in Snowflake',
  'integrations.snowflakeV2DestinationForm.copySqlScriptLabel': 'Copy Snowflake SQL script',
  'integrations.snowflakeV2DestinationForm.keyPairError':
    'There was a failure generating your warehouse destination key pair.',
  'integrations.snowflakeV2DestinationForm.requiredRolesNote': 'You must be a Snowflake SECURITYADMIN and SYSADMIN.',
  'integrations.snowflakeV2DestinationForm.runScriptBody':
    'Run the script in Snowflake to prepare your Snowflake account for Data Export, then return to this page.',
  'integrations.snowflakeV2DestinationForm.setupHint':
    'The Snowflake SQL script appears below after you complete the required fields.',
  'integrations.snowflakeV2DestinationForm.setupLabel': 'Setup your data in Snowflake',
  'integrations.status.loading': 'Loading…',
  'integrations.status.loadingDots': 'Loading…',
  'integrations.testConnection.confirmConnectionHint':
    'Before saving, confirm that this integration has been set up successfully',
  'integrations.testConnection.confirmConnectionLabel': 'Confirm connection',
  'integrations.testConnection.error': 'Error: {message}',
  'integrations.testConnection.success': 'Success. Test connection confirmed.',
  'integrations.testConnection.testConnection': 'Test connection',
  'integrations.warehouseExperimentation.configureDataExport': 'Configure Data Export',
  'integrations.warehouseExperimentation.connectionDegradedTitle': 'Connection degraded',
  'integrations.warehouseExperimentation.connectionErrorCredentials':
    'The warehouse credentials are invalid. Delete this integration and set it up again.',
  'integrations.warehouseExperimentation.connectionErrorGeneric':
    'Check your warehouse connection, or contact support if the issue continues.',
  'integrations.warehouseExperimentation.connectionErrorMissingObject':
    'A required warehouse database, schema, or table is missing or inaccessible. Grant access again or re-run the setup script to restore experiments.',
  'integrations.warehouseExperimentation.connectionErrorNetworkPolicy':
    'LaunchDarkly IP addresses are blocked by your warehouse network policy. Add them to your allowlist to restore the connection.',
  'integrations.warehouseExperimentation.connectionErrorPermissions':
    'LaunchDarkly is missing required permissions on your warehouse. Grant the necessary access to restore experiments.',
  'integrations.warehouseExperimentation.connectionErrorQuotedIdentifiers':
    'The QUOTED_IDENTIFIERS_IGNORE_CASE setting is enabled on your warehouse account. Turn off this setting for the export user to restore experiments.',
  'integrations.warehouseExperimentation.connectionFailedTitle': 'Connection failed',
  'integrations.warehouseExperimentation.createFailed':
    'Failed to create this integration configuration. Try again later.',
  'integrations.warehouseExperimentation.destinationIncompleteBody':
    'A Data Export destination for this environment is still being set up. Finish setup before continuing.',
  'integrations.warehouseExperimentation.destinationMissingBody':
    'A Data Export destination has not been configured for the selected project and environment.',
  'integrations.warehouseExperimentation.findEnvironmentPlaceholder': 'Find a project or environment',
  'integrations.warehouseExperimentation.finishDataExportSetup': 'Finish Data Export setup',
  'integrations.warehouseExperimentation.loadingEnvironmentsPlaceholder': 'Loading environments and integrations…',
  'integrations.warehouseExperimentation.noEnvironments': 'No environments in this project.',
  'integrations.warehouseExperimentation.saveButton': 'Test connection and save',
  'integrations.warehouseExperimentation.savingButton': 'Saving configuration',
  'integrations.warehouseHealthBanner.degradedBody':
    '{kind} integration <strong>{name}</strong> has encountered an error and requires attention.',
  'integrations.warehouseHealthBanner.disconnectedBody':
    '{kind} integration <strong>{name}</strong> is disconnected and requires attention.',
  'integrations.warehouseHealthBanner.genericKind': 'Warehouse integration',
  'integrations.warehouseHealthBanner.goToIntegration': 'Go to integration',
  'integrations.warehouseHealthBanner.showLess': 'Show less',
  'integrations.warehouseHealthBanner.showMore': '+ {count, plural, one {# more error} other {# more errors}}',
  'integrations.webhookForm.loadingPolicyEditorLabel': 'Loading policy editor',
  'integrations.webhookForm.nameLabel': 'Name',
  'integrations.webhookForm.policyHint':
    'Add a policy to select and filter the events sent to this webhook. Need help writing a policy? Read <docs>our documentation</docs> or <support>create a support ticket</support>.',
  'integrations.webhookForm.saveSettings': 'Save settings',
  'integrations.webhookForm.savingLabel': 'Saving settings',
  'integrations.webhookForm.secretHint':
    'LaunchDarkly signs webhook payloads so you can verify that requests are generated by LaunchDarkly and not a third-party pretending to be LaunchDarkly. <link>Learn more</link>.',
  'integrations.webhookForm.secretLabel': 'Secret',
  'integrations.webhookForm.signThisWebhook': 'Sign this webhook',
  'integrations.webhookForm.statusLabel': 'Status',
  'integrations.webhookForm.termsAgreement':
    'I have read and agree to the <link>Integration Terms and Conditions</link>',
  'integrations.webhookForm.urlLabel': 'URL',
  'integrations.webhookNotFound.body': "Sorry, we can't find the webhook you're looking for.",
  'integrations.webhookNotFound.title': 'Webhook not found',
  'integrations.webhooksList.roleRestrictionTooltip': 'The "{role}" role does not allow you to edit this webhook',
  'keyboardShortcuts.shortcutsDialog.heading': 'Keyboard shortcuts',
  'launchInsights.activeMembersCard.description':
    '<strong>{activeCount}</strong> out of {totalCount, plural, one {# member} other {# members}}',
  'launchInsights.activeMembersCard.title': 'Active members',
  'launchInsights.activeMembersCard.viewMembers': 'View members',
  'launchInsights.activity.heading': 'Activity',
  'launchInsights.activityFilter.allProjects': 'All projects',
  'launchInsights.activityFilter.currentProject': 'Current project',
  'launchInsights.activityFilter.filterLabel': 'filter by activity',
  'launchInsights.activityFilter.searchLabel': 'search projects',
  'launchInsights.activityFilter.searchPlaceholder': 'Search projects',
  'launchInsights.activityFilter.viewActivityFor': 'View activity for…',
  'launchInsights.chartEmptyState.description': 'Try updating the selected date range to view more data',
  'launchInsights.chartEmptyState.heading': 'No activity',
  'launchInsights.chartLegend.allProjects': 'All Projects',
  'launchInsights.chartTooltip.allProjects': 'All projects: {value}',
  'launchInsights.dateRangeFilter.customDateRange': 'Custom date range',
  'launchInsights.dateRangeFilter.last30Days': 'Last 30 days',
  'launchInsights.dateRangeFilter.last60Days': 'Last 60 days',
  'launchInsights.dateRangeFilter.last7Days': 'Last 7 days',
  'launchInsights.dateRangeFilter.nextMonthLabel': 'next',
  'launchInsights.dateRangeFilter.previousMonthLabel': 'previous',
  'launchInsights.dateRangeFilter.scoreDataUnavailable': 'Score data before {date} is unavailable',
  'launchInsights.dateRangeFilter.selectDateRange': 'Select date range',
  'launchInsights.dateRangeFilter.utc': 'UTC',
  'launchInsights.dateRangeFilter.utcTooltip': 'All data is shown in Coordinated Universal Time (UTC)',
  'launchInsights.environmentPicker.criticalEnvironment': 'Critical environment',
  'launchInsights.errorFallback.returnToProject': 'Return to project',
  'launchInsights.flagsReadyForCodeRemovalCard.loadingLabel': 'Loading flag cleanup metrics',
  'launchInsights.flagsReadyForCodeRemovalCard.ofAllFlags':
    '<strong>{readyCount}</strong> out of {count, plural, one {# flag} other {# flags}}',
  'launchInsights.flagsReadyForCodeRemovalCard.ofTemporaryFlags':
    '<strong>{readyCount}</strong> out of {count, plural, one {# temporary flag} other {# temporary flags}}',
  'launchInsights.flagsReadyForCodeRemovalCard.title': 'Flags ready for code removal',
  'launchInsights.flagsReadyToArchiveCard.loadingLabel': 'Loading flag cleanup metrics',
  'launchInsights.flagsReadyToArchiveCard.ofAllFlags':
    '<strong>{readyCount}</strong> out of {count, plural, one {# flag} other {# flags}}',
  'launchInsights.flagsReadyToArchiveCard.ofTemporaryFlags':
    '<strong>{readyCount}</strong> out of {count, plural, one {# temporary flag} other {# temporary flags}}',
  'launchInsights.flagsReadyToArchiveCard.title': 'Flags ready to archive',
  'launchInsights.gaugeCard.errorMeterLabel': 'loading data',
  'launchInsights.gaugeCard.loadError': 'Error loading data',
  'launchInsights.gaugeCard.loadingMeterLabel': 'loading data',
  'launchInsights.gaugeCard.meterLabel': 'active members',
  'launchInsights.gaugeCard.placeholderMeterLabel': 'gauge fallback meter',
  'launchInsights.gaugeCard.retry': 'Retry',
  'launchInsights.gaugeCard.retryLabel': 'retry',
  'launchInsights.metricTab.vsPriorPeriod': 'vs. prior period',
  'launchInsights.monitorCard.automaticRollbacks': 'Automatic rollbacks',
  'launchInsights.monitorCard.chartKindLabel': 'Select rollout chart kind',
  'launchInsights.monitorCard.completedRollouts': 'Completed rollouts',
  'launchInsights.monitorCard.manualRollbacks': 'Manual rollbacks',
  'launchInsights.monitorCard.title': 'Monitor',
  'launchInsights.monitorCard.totalRollouts': 'Total rollouts',
  'launchInsights.optimizeCard.experimentsRun': 'Experiments run',
  'launchInsights.optimizeCard.title': 'Optimize',
  'launchInsights.pagination.firstPage': 'First page',
  'launchInsights.pagination.firstPageLabel': 'view first page',
  'launchInsights.pagination.lastPage': 'Last page',
  'launchInsights.pagination.lastPageLabel': 'view last page',
  'launchInsights.pagination.nextPage': 'Next page',
  'launchInsights.pagination.nextPageLabel': 'view next page',
  'launchInsights.pagination.previousPage': 'Previous page',
  'launchInsights.pagination.previousPageLabel': 'view previous page',
  'launchInsights.pagination.rangeOfTotal': '{start}-{end} of {total}',
  'launchInsights.projectBreakdown.automaticRollbacks': 'Automatic rollbacks',
  'launchInsights.projectBreakdown.completedRollouts': 'Completed rollouts',
  'launchInsights.projectBreakdown.experimentsRun': 'Experiments run',
  'launchInsights.projectBreakdown.flagsCreated': 'Flags created',
  'launchInsights.projectBreakdown.manualRollbacks': 'Manual rollbacks',
  'launchInsights.projectBreakdown.monitorTableLabel': 'Project monitor metrics',
  'launchInsights.projectBreakdown.optimizeTableLabel': 'Project optimize metrics',
  'launchInsights.projectBreakdown.project': 'Project',
  'launchInsights.projectBreakdown.releaseTableLabel': 'Project release metrics',
  'launchInsights.projectBreakdown.score': 'Score',
  'launchInsights.projectBreakdown.totalRollouts': 'Total rollouts',
  'launchInsights.projectBreakdown.usingPipelines': 'Using pipelines',
  'launchInsights.projectBreakdown.usingRules': 'Using rules',
  'launchInsights.projectBreakdown.usingSegments': 'Using segments',
  'launchInsights.projectError.accessDenied': 'Access to project with key <code>{projectKey}</code> is denied',
  'launchInsights.projectError.notFound': 'Project with key <code>{projectKey}</code> was not found',
  'launchInsights.projectError.reload': 'Reload',
  'launchInsights.projectFilter.filterBy': 'Filter by…',
  'launchInsights.projectFilter.loadingProjectsLabel': 'Loading projects',
  'launchInsights.projectFilter.projects': 'Projects',
  'launchInsights.projectFilter.searchLabel': 'Search projects',
  'launchInsights.projectFilter.searchPlaceholder': 'Search projects',
  'launchInsights.projectFilter.selectUpTo': 'Select up to {max} projects.',
  'launchInsights.projectPicker.chooseProjectLabel': 'choose project',
  'launchInsights.projectPicker.searchPlaceholder': 'Search projects',
  'launchInsights.projectUsage.selectedProjectsLabel': 'selected projects',
  'launchInsights.projectUsage.usageAcrossAllProjects': 'Usage across all projects',
  'launchInsights.projectUsage.usageFor': 'Usage for:',
  'launchInsights.releaseCard.flagsCreated': 'Flags created',
  'launchInsights.releaseCard.title': 'Release',
  'launchInsights.releaseCard.usingPipelines': 'Using pipelines',
  'launchInsights.releaseCard.usingRules': 'Using rules',
  'launchInsights.releaseCard.usingSegments': 'Using segments',
  'launchInsights.releaseScore.excellent': 'Excellent',
  'launchInsights.releaseScore.fair': 'Fair',
  'launchInsights.releaseScore.good': 'Good',
  'launchInsights.releaseScore.meterLabel': 'Release score: {score}',
  'launchInsights.releaseScore.needsAttention': 'Needs attention',
  'launchInsights.releaseScore.none': 'None',
  'launchInsights.scoreCta.chooseEnvironmentHeading': 'Choose environment',
  'launchInsights.scoreCta.chooseEnvironmentLabel': 'choose environment',
  'launchInsights.scoreCta.chooseProjectLabel': 'choose project',
  'launchInsights.scoreCta.createEnvironmentIn': 'Create environment in…',
  'launchInsights.scoreCta.createExperiment': 'Create experiment',
  'launchInsights.scoreCta.createExperimentIn': 'Create experiment in…',
  'launchInsights.scoreCta.createPipeline': 'Create pipeline',
  'launchInsights.scoreCta.createPipelineIn': 'Create pipeline in…',
  'launchInsights.scoreCta.createSegment': 'Create segment',
  'launchInsights.scoreCta.createSegmentIn': 'Create segment in…',
  'launchInsights.scoreCta.searchEnvironmentsPlaceholder': 'Search environments',
  'launchInsights.scoreCta.searchProjectsPlaceholder': 'Search projects',
  'launchInsights.scoreDetails.customRulesLink': 'Learn more about custom rules',
  'launchInsights.scoreDetails.greatReleasesLink': 'Learn more about great releases',
  'launchInsights.scoreDetails.guardedReleasesLink': 'Learn more about guarded releases',
  'launchInsights.scoreDetails.insufficientData': 'Insufficient data',
  'launchInsights.scoreDetails.monitorExcellent':
    "<strong>Congrats!</strong> You're regularly guarding releases and proactively identifying performance issues early, with the capability to roll back regressions as needed",
  'launchInsights.scoreDetails.monitorFair':
    '<strong>Improve your score:</strong> Expand your usage of guarded releases to minimize risk by proactively identifying performance issues and automatically rolling them back',
  'launchInsights.scoreDetails.monitorGood':
    '<strong>Maintain your score:</strong> Continue to guard your releases, ensuring risks are minimized and performance issues are quickly addressed with automatic rollbacks',
  'launchInsights.scoreDetails.monitorNeedsAttention':
    '<strong>Improve your score:</strong> Guard your releases to minimize risk by identifying performance issues early and enabling automatic rollback of regressions',
  'launchInsights.scoreDetails.optimizeExcellent':
    '<strong>Good job!</strong> Keep leveraging experiments to ensure your software changes consistently deliver the best outcomes and remain aligned with user needs',
  'launchInsights.scoreDetails.optimizeFair':
    '<strong>Improve your score:</strong> Increase your use of <link>experiments</link> to better measure software changes and fine-tune outcomes for more targeted, data-driven improvements',
  'launchInsights.scoreDetails.optimizeGood':
    '<strong>Maintain your score:</strong> Continue running experiments to refine software changes and optimize user outcomes with deeper data insights',
  'launchInsights.scoreDetails.optimizeNeedsAttention':
    '<strong>Improve your score:</strong> Run <link>experiments</link> to measure the impact of software changes and identify the best outcomes for your users, driving data-driven decisions and product success',
  'launchInsights.scoreDetails.releaseExcellent':
    "<strong>Well done!</strong> You're leveraging segments, custom rules, and Release Assistant to refine your processes, ensuring optimal personalization, minimized risk, and consistent governance in your releases",
  'launchInsights.scoreDetails.releaseFair':
    '<strong>Improve your score:</strong> Use custom rules to target user attributes, minimizing risk and enabling instant rollbacks without the need to redeploy',
  'launchInsights.scoreDetails.releaseGood':
    '<strong>Maintain your score:</strong> Utilize Release Assistant to streamline your release process with automation, ensuring governance and consistent rollouts',
  'launchInsights.scoreDetails.releaseNeedsAttention':
    '<strong>Improve your score:</strong> Use <link>segments</link> to target specific user groups across multiple flags or experiments, enhancing personalization and optimizing testing impact',
  'launchInsights.scoreDetails.triggerLabel': 'Release score details',
  'launchInsights.scoreDetails.updateDateRange':
    'Update the selected date range to include dates after {date} to see your score',
  'launchInsights.scoreExplanation.averageUsageScores': 'Average usage scores across projects',
  'launchInsights.scoreExplanation.experimentation': 'Experimentation',
  'launchInsights.scoreExplanation.flags': 'Flags',
  'launchInsights.scoreExplanation.guardedRollouts': 'Guarded rollouts',
  'launchInsights.scoreExplanation.overallScore': 'Overall score',
  'launchInsights.scoreExplanation.overallScoreDescription':
    'Overall rating for adopting risk mitigation features. Average of account scores for flag, guarded rollout and experimentation usage across projects.',
  'launchInsights.scoreExplanation.usageSummary': 'Usage Summary:',
  'launchInsights.status.gatheringData': 'Gathering data',
  'launchInsights.status.gatheringDataLabel': 'gathering data',
  'launchInsights.status.loading': 'Loading…',
  'launchInsights.toolbar.overallScore': 'Overall score:',
  'launchInsights.toolbar.returnToProject': 'Return to project',
  'launchInsights.toolbar.viewDetails': 'View details',
  'launchInsights.topFlagCreators.heading': 'Top flag creators',
  'launchInsights.topFlagCreators.loadingLabel': 'loading top flag creators',
  'launchInsights.topFlagCreators.member': 'Member',
  'launchInsights.topFlagCreators.paginationUnit': 'member',
  'launchInsights.topFlagCreators.serviceToken': 'Service token',
  'launchInsights.topFlagCreators.tableLabel': 'top flag creators',
  'launchInsights.topFlagCreators.totalFlagsCreated': 'Total flags created',
  'launchInsights.usageSummary.activeMembers': '{count, plural, one {# active member} other {# active members}}',
  'launchInsights.usageSummary.body':
    '<activeMembers></activeMembers> created <flagsCreated></flagsCreated>. Of all flags, <flagUsage></flagUsage>. <guardedRollouts></guardedRollouts> and <experimentsRun></experimentsRun>.',
  'launchInsights.usageSummary.experimentsRun': '{count, plural, one {# experiment} other {# experiments}} run',
  'launchInsights.usageSummary.flagUsage':
    '{segments} used segments, {rules} used custom rules, and {pipelines} used release pipelines',
  'launchInsights.usageSummary.flagsCreated': '{count, plural, one {# flag} other {# flags}}',
  'launchInsights.usageSummary.guardedRollouts':
    'There were {count, plural, one {# guarded rollout} other {# guarded rollouts}}',
  'launchInsights.usageSummary.loading': 'Loading summary…',
  'launchInsights.viewFlagsCta.flagsReady': '{count, plural, one {# flag} other {# flags}} ready',
  'launchInsights.viewFlagsCta.loading': 'loading…',
  'launchInsights.viewFlagsCta.viewFlags': 'View flags',
  'launchInsights.viewFlagsCta.viewFlagsIn': 'View flags in…',
  'launchlitely.analyzeForm.advancedOptions': 'Advanced options',
  'launchlitely.analyzeForm.assignmentsColumns': 'Columns: user_id, time, group',
  'launchlitely.analyzeForm.assignmentsCsv': 'Assignments CSV',
  'launchlitely.analyzeForm.assignmentsDropZoneLabel': 'Assignments CSV drop zone',
  'launchlitely.analyzeForm.assignmentsFileHint':
    'An assignments file contains user IDs, exposure time, and the experiment group they were assigned to.',
  'launchlitely.analyzeForm.assignmentsInfoLabel': 'More information about Assignments CSV',
  'launchlitely.analyzeForm.assignmentsRequiredColumns': '<strong>Required:</strong> user_id, time, group',
  'launchlitely.analyzeForm.body':
    'Upload raw assignment and metric data to compute experiment results in the browser. Results use a two-sided frequentist analysis. Open advanced options to adjust the significance level, enable sequential testing, or apply a Bonferroni correction across variations.',
  'launchlitely.analyzeForm.bonferroni': 'Bonferroni correction across variations',
  'launchlitely.analyzeForm.bonferroniHint':
    'Divides α across treatment-vs-control comparisons to control the family-wise error rate.',
  'launchlitely.analyzeForm.controlVariation': 'Control variation',
  'launchlitely.analyzeForm.controlVariationHint': 'Other variations are compared against this control.',
  'launchlitely.analyzeForm.description':
    'Upload your assignment and metric CSVs and get statistically rigorous results in seconds.',
  'launchlitely.analyzeForm.downloadSampleCsv': 'Download sample CSV',
  'launchlitely.analyzeForm.downloadTemplate': 'Download template',
  'launchlitely.analyzeForm.dropZoneHint': 'Drag & drop or choose file here',
  'launchlitely.analyzeForm.fileSizeHint': 'File size < 5 MB',
  'launchlitely.analyzeForm.heading': 'Analyze your experiment',
  'launchlitely.analyzeForm.metricsColumns': 'Columns: user_id, time, metric_name, metric_value',
  'launchlitely.analyzeForm.metricsCsv': 'Metrics CSV',
  'launchlitely.analyzeForm.metricsDropZoneLabel': 'Metrics CSV drop zone',
  'launchlitely.analyzeForm.metricsFileHint':
    'A metrics file contains user IDs, metric event time, metric names and values.',
  'launchlitely.analyzeForm.metricsInfoLabel': 'More information about Metrics CSV',
  'launchlitely.analyzeForm.metricsRequiredColumns':
    '<strong>Required:</strong> user_id, time, metric_name, metric_value',
  'launchlitely.analyzeForm.optionalConfiguration': 'Optional configuration',
  'launchlitely.analyzeForm.sequentialTesting': 'Sequential testing',
  'launchlitely.analyzeForm.sequentialTestingHint':
    'Always-valid inference for repeated peeking. Confidence intervals widen at the same α.',
  'launchlitely.analyzeForm.significanceLevel': 'Significance level (α)',
  'launchlitely.analyzeForm.significanceLevelHint':
    'Lower values make the test more conservative. Default 0.05, range 0.01–0.20 in 0.01 steps.',
  'launchlitely.analyzeForm.subheading': 'Instant experiment results, entirely in your browser.',
  'launchlitely.page.title': 'LaunchLitely',
  'launchlitely.resultsExposures.chartLabel': 'Variation exposures',
  'launchlitely.resultsExposures.exposuresOverTime': 'Exposures over time',
  'launchlitely.resultsExposures.overTime': 'Over time',
  'launchlitely.resultsExposures.total': 'Total',
  'launchlitely.resultsPage.debugHeading': 'Raw stateless-stats-service response (debug)',
  'launchlitely.resultsPage.results': 'Results',
  'launchlitely.uploadErrors.duplicateMetricRow':
    'User "{userId}" has more than one row for metric "{metricName}". Each user must have at most one row per metric.',
  'launchlitely.uploadErrors.multipleVariations':
    'User "{userId}" is assigned to multiple variations ("{first}" and "{second}"). Each user must appear in exactly one variation.',
  'launchlitely.uploadErrors.negativeValue':
    'metric_value cannot be negative (found {value} for user "{userId}"). All metric values must be ≥ 0.',
  'launchlitely.uploadErrors.notCsv':
    '"{name}" is not a CSV file. Export your spreadsheet as CSV and upload that file.',
  'launchlitely.uploadErrors.tooLarge': 'File is too large ({size} MB). Maximum allowed size is {max} MB.',
  'live.page.queryAndExplore':
    'Query and explore historical events across a configurable time range, or filter by flag key or event key.',
  'live.page.title': 'Live events',
  'login.acceptInvitation.authTimedOut': 'Authentication timed out',
  'login.acceptInvitation.createAccount': 'Create your account',
  'login.acceptInvitation.creatingAccount': 'Creating your account',
  'login.acceptInvitation.firstNameLabel': 'First name',
  'login.acceptInvitation.heading': 'Join LaunchDarkly',
  'login.acceptInvitation.lastNameLabel': 'Last name',
  'login.acceptInvitation.mfaHeading': 'Enable multi-factor authentication',
  'login.acceptInvitation.mfaIntro':
    'Set up multi-factor authentication before you log in. To learn more, <link>read the documentation</link>.',
  'login.acceptInvitation.passwordLabel': 'Password',
  'login.agentControlBanner.body':
    'Stay on top of agent behavior in production. Access is included — log in for details.',
  'login.agentControlBanner.heading': 'Try AgentControl free.',
  'login.agentControlLaunchPromo.body':
    'Join LaunchDarkly leaders for a candid discussion on how AI is changing software development and the new approaches leading teams are implementing to control code and agents in production.',
  'login.agentControlLaunchPromo.cta': 'Register today',
  'login.agentControlLaunchPromo.dateLabel': 'JUNE 11',
  'login.agentControlLaunchPromo.heading': 'Controlling Code and Agents in the AI Era',
  'login.agentControlLaunchPromo.kicker': 'Tune in live',
  'login.agentControlLaunchPromo.speaker1Name': 'Edith Harbaugh',
  'login.agentControlLaunchPromo.speaker1Title': 'CEO',
  'login.agentControlLaunchPromo.speaker2Name': 'Cameron Etezadi',
  'login.agentControlLaunchPromo.speaker2Title': 'CTO',
  'login.agentControlLaunchPromo.speaker3Name': 'Marek Poliks',
  'login.agentControlLaunchPromo.speaker3Title': 'Head of AI',
  'login.agentControlLaunchPromo.timeEastern': '1 PM ET',
  'login.agentControlLaunchPromo.timePacific': '10 AM PT',
  'login.agentControlWorkshopPromo.body':
    'Stop guessing how your agents behave in production. Join LaunchDarkly for a hands-on workshop on using AgentControl to update prompts, route users to different models, and trigger automatic rollback—all without redeploying.',
  'login.agentControlWorkshopPromo.cta': 'Register today',
  'login.agentControlWorkshopPromo.dateLabel': 'Aug 5',
  'login.agentControlWorkshopPromo.heading': 'Hands-On with AgentControl',
  'login.agentControlWorkshopPromo.kicker': 'Live workshop',
  'login.agentControlWorkshopPromo.timeEastern': '12 PM ET',
  'login.agentControlWorkshopPromo.timePacific': '9 AM PT',
  'login.aiAgentsWorkshopPromo.body':
    'Join us for a live, hands-on workshop and take control of your agents in production. Learn how to create a config, update prompts, models, and tools without redeploying, ship changes behind a guarded rollout, and much more.',
  'login.aiAgentsWorkshopPromo.cta': 'Save my seat',
  'login.aiAgentsWorkshopPromo.dateLabel': 'Sep 17',
  'login.aiAgentsWorkshopPromo.heading': 'Managing AI Agents in Production',
  'login.aiAgentsWorkshopPromo.kicker': 'Live workshop',
  'login.aiAgentsWorkshopPromo.timeEastern': '12 PM ET',
  'login.aiAgentsWorkshopPromo.timePacific': '9 AM PT',
  'login.chooseOrganization.heading': 'Select an organization to sign in to',
  'login.chooseOrganization.notListed': "Don't see your organization listed?",
  'login.chooseOrganization.organizationsFor': 'Organizations for <strong>{email}</strong>',
  'login.chooseOrganization.signInFailed':
    "We couldn't sign you in to that organization. Try again, or contact your account administrator if the problem continues.",
  'login.chooseOrganization.signInWithAnotherEmail': 'Sign in with another email',
  'login.chooseOrganization.signInWithVercel': 'Sign in with Vercel',
  'login.chooseOrganization.signedIn': 'Signed in',
  'login.chooseOrganization.troubleshootingTips': 'Read our troubleshooting tips',
  'login.chooseOrganization.vercelDashboardTooltip': 'Sign in to this organization from your Vercel dashboard',
  'login.cliAuthorization.allowAccess': 'Allow access',
  'login.cliAuthorization.body': 'Verify that the pairing code below matches the one shown in the CLI login command.',
  'login.cliAuthorization.contactUsCta': 'let us know',
  'login.cliAuthorization.denyAccess': 'Deny access',
  'login.cliAuthorization.denyRequestBody':
    'If you did not initiate this request, <contact></contact> and deny the request.',
  'login.cliAuthorization.heading': 'Allow the LaunchDarkly CLI to access your account information?',
  'login.cliAuthorization.pairingCodeLoading': 'Loading pairing code',
  'login.confirmEmailOwnership.body':
    "You're almost there. Before you can continue using Single Sign-On (SSO) to access our partner apps, we need to verify your email address. Check your email at <strong>{email}</strong> for a verification link.",
  'login.controlPanelPromo.body':
    'Join LaunchDarkly and Ramp to learn how teams go from generating code with AI to running an AI software factory. Plus, be the first to see new capabilities that make this possible: automatic flag creation, autonomous releases, and intelligent flag cleanup.',
  'login.controlPanelPromo.cta': 'Save my seat',
  'login.controlPanelPromo.dateLabel': 'Nov 5',
  'login.controlPanelPromo.heading': 'Automate Your Software Factory',
  'login.controlPanelPromo.seasonLabel': "Fall '26",
  'login.controlPanelPromo.speaker1Name': 'Kellye King',
  'login.controlPanelPromo.speaker1Title': 'Senior Product Marketing Manager',
  'login.controlPanelPromo.speaker2Name': 'Aaron Montana',
  'login.controlPanelPromo.speaker2Title': 'Head of Experimentation and Product Analytics',
  'login.controlPanelPromo.speaker3Name': 'Jay Khatri',
  'login.controlPanelPromo.speaker3Title': 'Head of Product, Observability',
  'login.controlPanelPromo.speakerNov1Name': 'Jonathan Nolen',
  'login.controlPanelPromo.speakerNov1Title': 'Chief Product Officer',
  'login.controlPanelPromo.speakerNov1Logo': 'LaunchDarkly',
  'login.controlPanelPromo.speakerNov2Name': 'Pranjal Vachaspati',
  'login.controlPanelPromo.speakerNov2Title': 'Senior Staff Engineer',
  'login.controlPanelPromo.speakerNov2Logo': 'Ramp',
  'login.controlPanelPromo.speakerNov3Name': 'Tom Totenberg',
  'login.controlPanelPromo.speakerNov3Title': 'Director, Product Specialists',
  'login.controlPanelPromo.speakerNov3Logo': 'LaunchDarkly',
  'login.controlPanelPromo.timeEastern': '1 PM ET',
  'login.controlPanelPromo.timePacific': '10 AM PT',
  'login.createOrJoin.alreadyHaveAccount': 'Already have an account? <link>Sign in</link>',
  'login.createOrJoin.createAccountHeading': 'Create your free account',
  'login.createOrJoin.joinHeading': 'Request an invitation from your administrator',
  'login.createOrJoin.joinTab': 'Join an existing organization',
  'login.createOrJoin.trialHeading': 'Sign up and create a new trial',
  'login.createOrJoin.trialTab': 'Start a trial',
  'login.emailVerification.newDeviceBody':
    "We don't recognize the device you're signing in from, so we sent a verification link to your email (<strong>{email}</strong>).",
  'login.emailVerification.signupBody': "You're almost there. We sent a verification link to <strong>{email}</strong>.",
  'login.enrollMfa.footer': 'This step is required to access LaunchDarkly.',
  'login.enrollMfa.heading': 'Multi-factor authentication is now required',
  'login.enrollMfa.logInToContinue': 'Log in to continue',
  'login.enrollMfa.recoveryStepDescription': 'Store your backup codes in case you lose access to your device.',
  'login.enrollMfa.recoveryStepHeading': 'Step 2 of 2: Save your recovery codes',
  'login.enrollMfa.recoveryStepTitle': 'Save your recovery codes',
  'login.enrollMfa.required': 'You must enroll in MFA to continue.',
  'login.enrollMfa.sessionExpiredBody':
    'Your session may have timed out, or you may have arrived here directly. Log back in to pick up where you left off - it only takes a minute.',
  'login.enrollMfa.sessionExpiredHeading': 'Your enrollment session is no longer active',
  'login.enrollMfa.setupStepDescription': 'Scan the QR code and verify with a six-digit code.',
  'login.enrollMfa.setupStepHeading': 'Step 1 of 2: Set up your authenticator app',
  'login.enrollMfa.setupStepTitle': 'Set up your authenticator app',
  'login.enrollMfa.signInWithDifferentAccount': 'Sign in with a different account',
  'login.enrollMfa.subheading':
    'LaunchDarkly now requires multi-factor authentication (MFA) to help keep your account secure.',
  'login.facilitatedTrialExpired.body': 'The signup link you clicked is invalid or has expired.',
  'login.facilitatedTrialExpired.title': 'Error: Link expired or invalid',
  'login.facilitatedTrialSignup.getADemo': 'Get a demo',
  'login.facilitatedTrialSignup.gifted': "You've been gifted a free trial.",
  'login.facilitatedTrialSignup.heading': 'Welcome to LaunchDarkly',
  'login.facilitatedTrialSignup.headingWithName': 'Welcome to LaunchDarkly, {firstName}',
  'login.facilitatedTrialSignup.linkOpensInNewTab': '(Link opens in new tab)',
  'login.facilitatedTrialSignup.passwordLabel': 'Password',
  'login.facilitatedTrialSignup.submit': 'Submit',
  'login.facilitatedTrialSignup.submitting': 'Submitting',
  'login.facilitatedTrialSignup.termsAgreement':
    'By clicking "{submitButtonText}", you agree to our <terms>Terms</terms> and <privacy>Privacy Policies</privacy>.',
  'login.facilitatedTrialSignup.trialDetails':
    "When you start your trial, you'll have 14 days to try out all our features. Enjoy.",
  'login.forgotPassword.backToSignIn': 'Back to sign in',
  'login.forgotPassword.confirmationBody':
    'We sent password reset instructions to <strong>{email}</strong>. The link will expire in 1 hour.',
  'login.forgotPassword.confirmationHeading': 'Check your email',
  'login.forgotPassword.didntGetIt': "Didn't get it? Check your spam folder or <link>try a different email</link>.",
  'login.forgotPassword.emailLabel': 'Email address',
  'login.forgotPassword.goBackToSignIn': 'Go back to sign in',
  'login.forgotPassword.heading': 'Reset your password',
  'login.forgotPassword.legacyConfirmationBody':
    "We've sent you an e-mail with instructions on how to reset your password.",
  'login.forgotPassword.legacyConfirmationHeading': 'Instructions sent',
  'login.forgotPassword.legacyHeading': 'Reset password',
  'login.forgotPassword.rememberedPrompt': 'Remembered it? <link>Back to sign in</link>',
  'login.forgotPassword.resetMyPassword': 'Reset my password',
  'login.forgotPassword.sendResetLink': 'Send reset link',
  'login.forgotPassword.sendingConfirmationEmail': 'Sending confirmation email',
  'login.forgotPassword.sendingResetLink': 'Sending reset link',
  'login.forgotPassword.subheading': "Enter your email and we'll send you a link to choose a new password.",
  'login.forgotPassword.usingSso': 'Using single sign-on (SSO)?',
  'login.galaxyPromo.body':
    'Check out the recap of technical deep dives, hands-on learning, and real-world stories from engineering and product leaders who are redefining how software and AI applications get shipped—faster, safer, and smarter.',
  'login.galaxyPromo.cta': 'Watch the replays',
  'login.inviteStatus.acceptedBody': 'Sign in with the email address that received this invitation.',
  'login.inviteStatus.acceptedHeading': "You've already joined this team",
  'login.inviteStatus.expiredBody': 'Invitations expire after 7 days. Ask your account admin to resend the invitation.',
  'login.inviteStatus.expiredHeading': 'This invitation expired',
  'login.inviteStatus.invalidBody': 'Check the link in your email or ask your admin for a new invitation.',
  'login.inviteStatus.invalidHeading': "This invitation link isn't valid",
  'login.inviteStatus.signIn': 'Sign in',
  'login.mfaVerification.heading':
    '{isRecovery, select, true {Multi-factor recovery} other {Multi-factor authentication}}',
  'login.mfaVerification.noPhoneHeading': 'Don’t have your phone?',
  'login.mfaVerification.useRecoveryCode': 'Enter your MFA recovery code',
  'login.oauthButtons.continueWithGithub': 'Continue with GitHub',
  'login.oauthButtons.orDivider': 'or',
  'login.oauthSignIn.signingIn': 'Signing in to LaunchDarkly',
  'login.oauthSignup.createFreeTrial': 'Create free trial',
  'login.oauthSignup.creatingFreeTrial': 'Creating free trial',
  'login.oauthSignup.heading': 'Complete profile',
  'login.oauthSignup.signUp': 'Sign up',
  'login.oauthSignup.signingUp': 'Signing up',
  'login.organizationCard.membersCount': '{count, plural, one {# member} other {# members}}',
  'login.organizationCard.requestingAccess': 'Requesting access…',
  'login.organizationCard.requestingAccessLabel': 'Requesting access',
  'login.organizationCard.signInTypeLabel': 'sign-in type',
  'login.organizationCard.ssoTag': 'SSO',
  'login.organizationCard.vercelTag': 'Vercel',
  'login.organizationPassword.enterPasswordAs': 'Enter password to sign in as <strong>{email}</strong>',
  'login.organizationPassword.forgotPassword': 'Forgot your password?',
  'login.organizationPassword.heading': 'Sign in to {orgName}',
  'login.organizationPassword.passwordLabel': 'Password',
  'login.organizationPassword.selectAnotherOrganization': 'Select another organization',
  'login.organizationPassword.signIn': 'Sign in',
  'login.organizationPassword.signingIn': 'Signing in',
  'login.reinventPromo.body':
    'Meet us at AWS re:Invent to learn how runtime control and safer releases help developers rest easy.',
  'login.reinventPromo.boothLabel': 'Booth #1339',
  'login.reinventPromo.cta': 'Book a meeting',
  'login.reinventPromo.dateLabel': 'Dec 1-5',
  'login.reinventPromo.eventName': 'AWS re:Invent',
  'login.reinventPromo.heading': 'Meet us in Vegas.',
  'login.requestInvitation.adminEmailLabel': "Admin's email",
  'login.requestInvitation.confirmationBody':
    "We've sent your team admin an e-mail with your request to join their LaunchDarkly team. If they don't receive an e-mail shortly, <supportLink>create a support ticket</supportLink>.",
  'login.requestInvitation.confirmationHeading': 'Instructions sent',
  'login.requestInvitation.emailLabel': 'Your email',
  'login.requestInvitation.firstNameLabel': 'First name',
  'login.requestInvitation.lastNameLabel': 'Last name',
  'login.requestInvitation.sendingRequest': 'Sending request',
  'login.requestInvitation.ssoNotice':
    "If your organization has enabled <strong>single sign-on</strong>, you'll need to request access through your identity provider.",
  'login.requestInvitation.submit': 'Request invitation',
  'login.resetPassword.confirmPasswordLabel': 'Confirm password',
  'login.resetPassword.heading': 'Reset your password',
  'login.resetPassword.passwordLabel': 'Password',
  'login.resetPassword.resetMyPassword': 'Reset my password',
  'login.resetPassword.resettingPassword': 'Resetting your password',
  'login.resetPassword.subheading': 'Choose a new password for your account.',
  'login.resetPassword.updatePassword': 'Update password',
  'login.resetPassword.updatingPassword': 'Updating password',
  'login.signIn.authTimedOut': 'Authentication timed out',
  'login.signIn.backToSettings': 'Go back to <link>account settings</link>.',
  'login.signIn.commercialAccountPrompt': 'Have a commercial account? <link>Sign in here</link>',
  'login.signIn.continue': 'Continue',
  'login.signIn.emailLabel': 'Email',
  'login.signIn.escalationError': 'Something went wrong. Try again.',
  'login.signIn.federalAccountPrompt': 'Have a federal account? <link>Sign in here</link>',
  'login.signIn.forgotPassword': 'Forgot your password?',
  'login.signIn.heading': 'Sign in',
  'login.signIn.joinExistingPrompt': 'Want to join an existing account? <link>Request invitation</link>',
  'login.signIn.noAccountPrompt': "Don't have an account? <link>Sign up</link>",
  'login.signIn.oauthError':
    'Error signing in with {provider, select, google {Google} github {Github} other {Vercel}}. If you have an existing account, sign in with your email address.',
  'login.signIn.passwordLabel': 'Password',
  'login.signIn.reauthenticate': 'Reauthenticate to continue.',
  'login.signIn.reauthenticateTooltip':
    'You must reauthenticate your credentials for this email to manage your account',
  'login.signIn.rememberMe': 'Remember me',
  'login.signIn.signIn': 'Sign in',
  'login.signIn.signingIn': 'Signing in',
  'login.signup.signingUp': 'Signing up',
  'login.signup.startingTrial': 'Starting free trial',
  'login.signup.subheading':
    'Your Developer account is free forever, and includes 14 days of unlimited access to all LaunchDarkly capabilities. No credit card required to start.',
  'login.signup.submit': 'Start my trial now',
  'login.signupForm.acceptTermsLabel':
    'Accept the <terms>Terms</terms> and <privacy>Privacy Policies</privacy> in order to proceed',
  'login.signupForm.firstNameLabel': 'First name',
  'login.signupForm.getADemo': 'Get a demo',
  'login.signupForm.joinExistingPrompt': 'Want to join an existing account? <link>Request invitation</link>',
  'login.signupForm.lastNameLabel': 'Last name',
  'login.signupForm.linkOpensInNewTab': '(Link opens in new tab)',
  'login.signupForm.marketingEmailsHint': 'You can change your preference at any time.',
  'login.signupForm.marketingEmailsLabel': "We'd love to send you (occasional) emails about new features & events",
  'login.signupForm.oauthUserIdLabel': 'OAuth user ID',
  'login.signupForm.organizationLabel': 'Organization',
  'login.signupForm.passwordLabel': 'Password',
  'login.signupForm.submit': 'Submit',
  'login.signupForm.submitting': 'Submitting',
  'login.signupForm.termsAgreement':
    'By clicking "{submitButtonText}", you agree to our <terms>Terms</terms> and <privacy>Privacy Policies</privacy>.',
  'login.signupForm.workEmailLabel': 'Work email',
  'login.springSignupPromo.heading': 'Build products customers love.',
  'login.status.loading': 'Loading…',
  'login.teamUsesSso.body': 'Continue to SSO to join them, or create a separate account.',
  'login.teamUsesSso.bootstrapError': 'Unable to continue signup. Start over from the signup page.',
  'login.teamUsesSso.continueToSso': 'Continue to SSO',
  'login.teamUsesSso.createSeparateAccount': 'Create separate account',
  'login.teamUsesSso.heading': 'Your team uses SSO',
  'login.trialLimit.body':
    'To request an increase to your limit for no additional cost, <supportLink>create a support ticket</supportLink>.',
  'login.trialLimit.title': 'You have reached the limit of LaunchDarkly trial accounts',
  'login.trustThisDevice.body': 'If you trust this browser, you can skip verifying your identity the next time.',
  'login.trustThisDevice.bodyWithEmail':
    'If you trust this browser, you can skip verifying your identity the next time you sign in as <strong>{email}</strong>.',
  'login.trustThisDevice.dontTrust': "Don't trust",
  'login.trustThisDevice.expires': 'This expires in {relativeDate}.',
  'login.trustThisDevice.heading': 'Trust this browser?',
  'login.trustThisDevice.publicComputers': "Don't use this option on public or shared computers.",
  'login.trustThisDevice.trustThisBrowser': 'Trust this browser',
  'login.verificationExpired.body':
    'The verification link you clicked is invalid or has expired. You can <resendLink>{resendLabel}</resendLink> or <signInLink>sign in with a different email</signInLink>.',
  'login.verificationExpired.resendLink': 'resend verification email',
  'login.verificationExpired.sending': 'Sending…',
  'login.verificationExpired.title': 'Error: Link expired or invalid',
  'login.welcome.body': "We're here to help you release features your customers will love. Let's get started.",
  'login.welcome.heading': 'Great',
  'logs.page.title': 'Logs',
  'maintainers.maintainerField.editLabel': 'Edit maintainer',
  'maintainers.maintainerField.editTriggerLabel': 'Edit maintainer',
  'maintainers.maintainerField.fieldLabel': 'Maintainer',
  'maintainers.maintainerField.loadError': 'Failed to load maintainers',
  'maintainers.maintainerField.retry': 'Try again',
  'maintainers.picker.errorHeading': 'Something went wrong',
  'maintainers.picker.filterLabel': 'Filter items',
  'maintainers.picker.loadingTags': 'Loading…',
  'maintainers.picker.loadingTeamLabel': 'Loading team {teamKey}',
  'maintainers.picker.moreTags': 'More tags',
  'maintainers.picker.noMaintainer': 'No maintainer',
  'maintainers.picker.noOptions': 'No options',
  'maintainers.picker.noResultsFor': 'No results found for: {query}',
  'maintainers.picker.otherFiltersLabel': 'Other maintainer filters',
  'maintainers.status.loading': 'Loading…',
  'members.accessEmptyState.createCustomRole': 'Create custom role',
  'members.accessEmptyState.description': 'This member has not been given access to any custom roles directly.',
  'members.accessEmptyState.heading': 'Give this member access',
  'members.accessEmptyState.noRolesDescription': 'This member has not been given any roles directly.',
  'members.accessPreview.emptyMessage': 'Select roles or teams to view a permissions summary.',
  'members.accessPreview.heading': 'Permissions summary',
  'members.accessPreview.loadError':
    'LaunchDarkly could not load the permissions summary. You can still invite these members.',
  'members.accessPreview.noAccessMessage': 'These members will have no access.',
  'members.accessPreview.noProjectAccessDetail':
    '{count, plural, one {this member} other {these members}} will not be able to use LaunchDarkly',
  'members.accessPreview.noProjectAccessLead': 'No project access',
  'members.accessPreview.noProjectRolesDetail':
    'Choose projects for the selected roles, or add a role with project access.',
  'members.accessPreview.refreshingLabel': 'Updating permissions summary',
  'members.accessSummary.emptyMessage': 'This member has no project access.',
  'members.accessSummary.noPermission': "You do not have permission to view this member's access summary.",
  'members.accessSummary.stem': '{subject} can:',
  'members.accessTable.actionsCellLabel': 'Actions',
  'members.accessTable.actionsColumn': 'Actions',
  'members.accessTable.actionsMenuLabel': 'Member actions',
  'members.accessTable.editAccess': 'Edit access',
  'members.accessTable.removeAccess': 'Remove access',
  'members.accessTable.resourcesCellLabel': 'resources',
  'members.accessTable.resourcesColumn': 'Resources',
  'members.accessTable.roleAttributeKeyCellLabel': 'role attributes key',
  'members.accessTable.roleAttributeKeyColumn': 'Role attribute key',
  'members.accessTable.roleNameCellLabel': 'role name',
  'members.accessTable.roleNameColumn': 'Role name',
  'members.accessTable.teamCellLabel': 'team',
  'members.accessTable.teamColumn': 'Team',
  'members.addToTeamsDialog.cancel': 'Cancel',
  'members.addToTeamsDialog.fallbackName': 'member',
  'members.addToTeamsDialog.manageTeams': 'Manage teams',
  'members.addToTeamsDialog.submit': 'Add member to teams',
  'members.addToTeamsDialog.title': 'Add {name} to teams',
  'members.bulkAddToTeamsDialog.cancel': 'Cancel',
  'members.bulkAddToTeamsDialog.manageTeams': 'Manage teams',
  'members.bulkAddToTeamsDialog.submit':
    'Add {memberCount, plural, one {member} other {members}} to {teamCount, plural, =0 {team} one {team} other {teams}}',
  'members.bulkAddToTeamsDialog.title':
    'Add {memberCount, plural, one {# member} other {# members}} to {teamCount, plural, =0 {team} one {# team} other {# teams}}',
  'members.bulkEdit.addToTeams': 'Add to teams',
  'members.bulkEdit.deselectAllTooltip': 'Deselect all members',
  'members.bulkEdit.editMemberRoles': 'Edit member roles',
  'members.bulkEdit.editMembers': '{count, plural, one {Edit member} other {Edit # members}}',
  'members.bulkEdit.partialSelectionLabel':
    '{selectedCount} of {totalCount} members have been selected. Click to deselect all and select a new set of members to bulk edit.',
  'members.bulkEdit.selectAllLabel': 'Select all members',
  'members.bulkEdit.selectAllTooltip': 'Select all members',
  'members.bulkEditRoles.alreadyAssigned':
    '{count, plural, one {# member was} other {# members were}} already assigned {roleName}.',
  'members.bulkEditRoles.customRoleCount': '{count, plural, one {# custom role} other {# custom roles}}',
  'members.bulkEditRoles.notUpdatedMultipleRoles':
    "{count, plural, one {# member's} other {# members''}} roles were not updated.",
  'members.bulkEditRoles.notUpdatedSingleRole':
    "{count, plural, one {# member's} other {# members''}} role was not updated.",
  'members.bulkEditRoles.theRole': 'the {roleName} role',
  'members.bulkEditRoles.theseCustomRoles': 'these custom roles',
  'members.bulkEditRoles.thisCustomRole': 'this custom role',
  'members.bulkEditRoles.updated': '{count, plural, one {# member was} other {# members were}} updated to {roleName}.',
  'members.deleteMemberDialog.body': 'This member will be deleted permanently from your LaunchDarkly account.',
  'members.deleteMemberDialog.confirm': 'Delete',
  'members.deleteMemberDialog.personalTokensWarning':
    'Deleting this member will deactivate the <link>{count, plural, one {# personal API access token} other {# personal API access tokens}}</link> they created. <strong>Confirm no critical services are using {count, plural, one {this token} other {these tokens}} before you delete this member.</strong>',
  'members.deleteMemberDialog.serviceTokensNote':
    'The <link>{count, plural, one {# service token} other {# service tokens}}</link> this member created will still be available to your account.',
  'members.deleteMemberDialog.title': 'Delete {name}?',
  'members.directAccess.count':
    '{roleCount, plural, one {# role} other {# roles}} and {resourceCount, plural, one {# resource} other {# resources}}',
  'members.directAccess.countLabel': 'roles and resources count',
  'members.directAccess.description': 'This member has access to the following project roles and resources directly.',
  'members.directAccess.emptyDescription':
    "This member has not been given access to any project roles directly. Update this member's organization role on the Settings tab.",
  'members.directAccess.emptyHeading': 'No project roles assigned',
  'members.directAccess.heading': 'Assigned directly',
  'members.directAccess.loading': 'Loading roles…',
  'members.directAccess.searchLabel': 'Search roles',
  'members.directAccess.searchPlaceholder': 'Find an assigned role or resource',
  'members.editRolesDialog.assignCustomRoles': 'Assign custom roles',
  'members.editRolesDialog.bulkInstructions': 'Select a built-in or custom role to replace members’ existing roles',
  'members.editRolesDialog.bulkTitle': 'Edit individual roles for {count, plural, one {# member} other {# members}}',
  'members.editRolesDialog.cancel': 'Cancel',
  'members.editRolesDialog.instructions': 'Select a built-in role or custom role.',
  'members.editRolesDialog.manageCustomRoles': 'Manage custom roles',
  'members.editRolesDialog.replace': '{count, plural, =0 {Replace role} one {Replace role} other {Replace roles}}',
  'members.editRolesDialog.save': '{count, plural, =0 {Save role} one {Save role} other {Save roles}}',
  'members.editRolesDialog.selectRoles': 'Select roles',
  'members.editRolesDialog.selectedRoles': '{count, plural, one {# role} other {# roles}} selected',
  'members.history.description': "Member history includes updates made to the member's settings in LaunchDarkly.",
  'members.history.searchPlaceholder': "Search your member's history",
  'members.inviteMembers.accessSectionTitle': 'Access',
  'members.inviteMembers.addMembersDocsLink': 'Read about adding members',
  'members.inviteMembers.adminOnlyWarning':
    '{count, plural, one {This member} other {These members}} will not be able to use LaunchDarkly, including organization settings, until they can view at least one project.',
  'members.inviteMembers.breadcrumbTitle': 'Members',
  'members.inviteMembers.cancel': 'Cancel',
  'members.inviteMembers.changeRoleType': 'Change role type',
  'members.inviteMembers.classicRolesLabel': 'Classic roles',
  'members.inviteMembers.closeLabel': 'Close',
  'members.inviteMembers.customRolesLabel': 'Role',
  'members.inviteMembers.emailsLabel': 'Email addresses (required)',
  'members.inviteMembers.emailsPlaceholder': 'joe@example.com, jane@example.com, jill@example.com',
  'members.inviteMembers.heading': 'Invite members',
  'members.inviteMembers.invalidEmails':
    '{count, plural, one {Invalid email address} other {Invalid email addresses}}: {addresses}{remaining, plural, =0 {} other {, and # more}}',
  'members.inviteMembers.invite': 'Invite',
  'members.inviteMembers.memberRoleHelper':
    "Applies across the whole organization and can't be scoped to specific projects.",
  'members.inviteMembers.memberRoleLabel': 'Member role',
  'members.inviteMembers.noMatchingRoles': 'No roles match your search. Try a different term.',
  'members.inviteMembers.noOrganizationRoles': 'No organization roles are available for this account.',
  'members.inviteMembers.noProjectAccessConfirmBody':
    '{count, plural, one {This member} other {These members}} will not be able to use any part of LaunchDarkly, including organization settings, until they can view at least one project. To give them access, add them to a team or assign them a project role.',
  'members.inviteMembers.noProjectAccessConfirmHeading': 'Invite without project access',
  'members.inviteMembers.noProjectAccessConfirmRolesBody':
    '{count, plural, one {This member} other {These members}} will not be able to use any part of LaunchDarkly, including organization settings, until they can view at least one project. To give them access, choose projects for the selected roles or add a role with project access.',
  'members.inviteMembers.noProjectAccessConfirmSubmit': '{count, plural, one {Invite member} other {Invite members}}',
  'members.inviteMembers.organizationRoleLabel': 'Organization role',
  'members.inviteMembers.projectAccessHelper':
    'Directly assign project permissions to members across all projects in the organization.',
  'members.inviteMembers.projectAccessLabel': 'Project access',
  'members.inviteMembers.projectRolesLoadingLabel': 'Loading project roles',
  'members.inviteMembers.rateLimitBody':
    'LaunchDarkly limits how many invitations you can send at a time. Invite these members again later.',
  'members.inviteMembers.rateLimitTitle':
    'Rate limit reached for {count, plural, one {# invitation} other {# invitations}}',
  'members.inviteMembers.roleSearchLabel': 'Search roles',
  'members.inviteMembers.roleSearchPlaceholder': 'Search roles',
  'members.inviteMembers.rolesLoadingLabel': 'Loading roles',
  'members.inviteMembers.selectRole': 'Select role',
  'members.inviteMembers.teamsHelper': 'Members inherit project permissions from the teams they’re assigned to.',
  'members.inviteMembers.teamsLabel': 'Assign members to teams',
  'members.inviteMembers.unavailableBody':
    'Each of these addresses either already belongs to a LaunchDarkly account or appears more than once in your list. Remove or correct them, then invite again.',
  'members.inviteMembers.unavailableTitle': '{count, plural, one {# address} other {# addresses}} could not be invited',
  'members.inviteMembersDialog.cancel': 'Cancel',
  'members.inviteMembersDialog.emailsLabel': 'Email addresses',
  'members.inviteMembersDialog.emailsPlaceholder': 'Separate emails by comma or new line',
  'members.inviteMembersDialog.failedBody':
    '{count, plural, one {This email already exists} other {These emails already exist}} within this account. <link>More details</link>',
  'members.inviteMembersDialog.failedHeading':
    '{failedCount, plural, one {# invite} other {# invites}} failed to send, {successfulCount} sent successfully.',
  'members.inviteMembers.noSeatsBody':
    'Your plan is limited to {count, plural, one {# member} other {# members}}. To proceed, upgrade your plan.',
  'members.inviteMembers.noSeatsHeading': 'No seats remaining',
  'members.inviteMembers.reviewBilling': 'Review billing',
  'members.inviteMembersDialog.invitingCount': 'Inviting {count, plural, one {# member} other {# members}}',
  'members.inviteMembersDialog.noMembersFound': 'No members found.',
  'members.inviteMembersDialog.noMembersOrTeamsFound': 'No members or teams found.',
  'members.inviteMembersDialog.notEnoughSeats': 'Not enough seats',
  'members.inviteMembersDialog.rateLimited': 'You are being rate limited. Invite the following members later.',
  'members.inviteMembersDialog.rateLimitedHeading': '{count, plural, one {# invite} other {# invites}} failed to send',
  'members.inviteMembersDialog.roleLabel': 'Member role',
  'members.inviteMembersDialog.seatsAdded': '{count, plural, one {# seat} other {# seats}} added to subscription',
  'members.inviteMembersDialog.seatsAvailable': '{count, plural, one {# more seat} other {# more seats}} available',
  'members.inviteMembersDialog.seatsNeeded': '{count, plural, one {# seat} other {# seats}} needed',
  'members.inviteMembersDialog.ssoWarning':
    'Adding members through this modal when SSO is enabled will have undesirable results. Use the SAML SSO flow to add members.',
  'members.inviteMembersDialog.submit': 'Invite {count, plural, one {member} other {members}}',
  'members.inviteMembersDialog.submitLoading': 'Inviting members',
  'members.inviteMembersDialog.teamsLabel': 'Teams',
  'members.inviteMembersDialog.title': 'Invite members',
  'members.inviteMembersDialog.viewProjectAlert':
    'None of the selected roles grant <code>viewProject</code> permissions. You must grant {count, plural, =0 {members additional access} one {this member additional access} other {these members additional access}} before they can take any action in LaunchDarkly.',
  'members.lastSeenCell.apiTokenTooltip':
    "This member's most recent activity was an API request using an access token.",
  'members.lastSeenCell.never': 'Never',
  'members.lastSeenCell.neverTooltip': 'This member has never logged in.',
  'members.lastSeenCell.noData': 'No data',
  'members.lastSeenCell.noDataTooltip':
    'This member has not been active since LaunchDarkly began tracking last seen times.',
  'members.lastSeenCell.viaApiToken': 'via <link>API token</link>',
  'members.lastSeenFilter.all': 'All',
  'members.lastSeenFilter.clearLabel': 'Clear last seen filter',
  'members.lastSeenFilter.disabledOptionTooltip':
    'LaunchDarkly has not collected enough data to support this filter option yet.',
  'members.lastSeenFilter.name': 'Last seen',
  'members.lastSeenFilter.never': 'Never',
  'members.lastSeenFilter.noData': 'No data',
  'members.lastSeenFilter.over1Year': '> 1 year ago',
  'members.lastSeenFilter.over2Months': '> 2 months ago',
  'members.lastSeenFilter.over30Days': '> 30 days ago',
  'members.lastSeenFilter.over3Months': '> 3 months ago',
  'members.lastSeenFilter.over4Months': '> 4 months ago',
  'members.lastSeenFilter.over6Months': '> 6 months ago',
  'members.list.allCustomRolesLabel': "Go to a complete list of {name}'s custom roles.",
  'members.list.allTeamsLabel': "Go to a complete list of {name}'s teams.",
  'members.list.exportCsv': 'Export CSV',
  'members.list.lastSeenColumn': 'Last seen',
  'members.list.memberCount': '{count, plural, one {member} other {members}}',
  'members.list.memberRoleColumn': 'Member role',
  'members.list.nameColumn': 'Name',
  'members.list.organizationRoleClassicTooltip':
    'Has a classic role of no access. Requires an assigned project role that grants "viewProject" permissions.',
  'members.list.organizationRoleColumn': 'Organization role',
  'members.list.organizationRoleTooltip':
    'Has a base role of "no_access". Requires an assigned project role that grants "viewProject" permissions.',
  'members.list.projectRoleClassicTooltip': 'Has a classic role of no access',
  'members.list.projectRoleColumn': 'Project role',
  'members.list.projectRoleTooltip': 'Has a base role of "no_access"',
  'members.list.searchPlaceholder': 'Search members',
  'members.list.selectMemberLabel': 'Select this member for bulk editing',
  'members.list.tableLabel': 'Members list',
  'members.list.teamsColumn': 'Teams',
  'members.maintainerTag.loadingLabel': 'Loading member {maintainerId}',
  'members.memberAvatar.alt': 'Member avatar',
  'members.memberPage.accessSummaryTab': 'Access summary',
  'members.memberPage.accessTab': 'Access',
  'members.memberPage.classicRoleChip': 'Classic role: {role}',
  'members.memberPage.customRoleCount': '{count, plural, one {# custom role} other {# custom roles}}',
  'members.memberPage.invited': 'Invited',
  'members.memberPage.menuTitle': 'Member menu',
  'members.memberPage.noAccess': 'You do not have access to this member.',
  'members.memberPage.organizationRoleChip': 'Organization role: {role}',
  'members.memberPage.pendingInviteLabel': 'pending invite',
  'members.memberPage.projectRoleCount': '{count, plural, one {# project role} other {# project roles}}',
  'members.memberPage.roleLabel': 'Member role',
  'members.memberPage.rolesTab': 'Roles',
  'members.memberPage.settingsTab': 'Settings',
  'members.memberPage.teamCount': '{count, plural, one {# team} other {# teams}}',
  'members.memberPage.teamsTab': 'Teams',
  'members.memberSettings.addOrganizationRole': 'Add organization role',
  'members.memberSettings.baseRoleLabel': 'Base role',
  'members.memberSettings.classicRoleLabel': 'Classic role',
  'members.memberSettings.customRolesLabel': 'Custom roles',
  'members.memberSettings.dateJoinedLabel': 'Date joined',
  'members.memberSettings.deleteMember': 'Delete member',
  'members.memberSettings.deleteMemberBody': 'This member will be deleted permanently from your LaunchDarkly account.',
  'members.memberSettings.deleteMemberHeading': 'Delete member',
  'members.memberSettings.editBaseRoleLabel': 'edit',
  'members.memberSettings.editOrganizationRoleLabel': 'edit',
  'members.memberSettings.emailLabel': 'Email',
  'members.memberSettings.generalHeading': 'General',
  'members.memberSettings.invitationHeading': 'Invitation',
  'members.memberSettings.invitationStatus': '<strong>Status:</strong> Has not accepted',
  'members.memberSettings.lastActiveLabel': 'Last active',
  'members.memberSettings.mfaHeading': 'Multi-factor authentication',
  'members.memberSettings.organizationRoleHelp':
    "Take advantage of LaunchDarkly's new role-based access control. To learn more,",
  'members.memberSettings.organizationRoleHelpLink': 'view the documentation.',
  'members.memberSettings.organizationRoleLabel': 'Organization role',
  'members.memberSettings.resendInvitation': 'Resend invitation',
  'members.memberSettings.resetMfa': 'Reset MFA',
  'members.memberSettings.roleLabel': 'Role',
  'members.memberSettings.sendMfaRequest': 'Send request to enable',
  'members.memberSettings.sendRecoveryCode': 'Send recovery code',
  'members.notifications.addedToTeams': '{name} added to {count, plural, one {# team} other {# teams}}',
  'members.notifications.approveRequestFailed': 'Unable to approve request',
  'members.notifications.invitationResent': 'Resent invitation to {name}',
  'members.notifications.invitationsSent':
    '{count, plural, one {Invitation sent} other {Invitations sent to # new members}}',
  'members.notifications.inviteFailed': 'Uh oh. Something went wrong. Try again later.',
  'members.notifications.invitePermissionDenied': 'You don’t have permissions to invite members. Contact an admin.',
  'members.notifications.inviteRequestFailed': 'There was a problem inviting members. Try again.',
  'members.notifications.inviteRequestSent': 'Your invite request was sent to your admin',
  'members.notifications.mfaReset': 'MFA reset',
  'members.notifications.notified': 'Notified {name}',
  'members.notifications.notifyFailed': 'Failed to notify {name}. Try again later.',
  'members.notifications.removed': 'Team member removed',
  'members.notifications.requestApproved': 'Request approved',
  'members.notifications.resendInvitationFailed': 'Failed to resend invitation to {name}. Try again later.',
  'members.notifications.roleUpdateFailed': 'Role update unsuccessful. Try again later.',
  'members.notifications.rolesUpdated': 'Member {count, plural, one {role} other {roles}} updated',
  'members.notifications.settingsUpdated': 'Member settings updated',
  'members.pendingRequestsDialog.accept': 'Accept',
  'members.pendingRequestsDialog.acceptSelected': 'Accept selected',
  'members.pendingRequestsDialog.decline': 'Decline',
  'members.pendingRequestsDialog.denySelected': 'Deny selected',
  'members.pendingRequestsDialog.errorToast': 'An unknown error occurred. Try again later.',
  'members.pendingRequestsDialog.loadingStatus': 'Loading…',
  'members.pendingRequestsDialog.managePendingRequests': 'Manage pending requests',
  'members.pendingRequestsDialog.processedToast': 'Request(s) successfully processed',
  'members.pendingRequestsDialog.reloadHint': '<link>Reload</link> the page to see updates',
  'members.pendingRequestsDialog.requestCount': '{count, plural, one {# request} other {# requests}}',
  'members.pendingRequestsDialog.roleColumn': 'Role',
  'members.pendingRequestsDialog.roleLabel': 'Member role',
  'members.pendingRequestsDialog.statusColumn': 'Request status',
  'members.pendingRequestsDialog.triggerLabel': 'Pending requests',
  'members.resetMfaDialog.body':
    'This clears the multi-factor authentication configuration for this member, including their TOTP secret and recovery codes. They must set up MFA again the next time they sign in.',
  'members.resetMfaDialog.confirm': 'Reset MFA',
  'members.resetMfaDialog.title': 'Reset MFA for {name}',
  'members.selectMember.removeLabel': 'Remove {name}',
  'members.status.loading': 'Loading…',
  'members.suggestInvitesDialog.cancel': 'Cancel',
  'members.suggestInvitesDialog.emailsLabel': 'Email addresses',
  'members.suggestInvitesDialog.emailsPlaceholder': 'Separate emails by comma or new line',
  'members.suggestInvitesDialog.submit': 'Invite {count, plural, one {member} other {members}}',
  'members.suggestInvitesDialog.submitLoading': 'Inviting members',
  'members.suggestInvitesDialog.title': 'Invite members',
  'members.teamAccess.count':
    '{roleCount, plural, one {# role} other {# roles}} and {resourceCount, plural, one {# resource} other {# resources}}',
  'members.teamAccess.countLabel': 'roles and resources count',
  'members.teamAccess.description':
    'This member inherits the following roles and access through membership in the following teams. To make changes, visit the relevant Teams page.',
  'members.teamAccess.emptyDescription':
    "You can go to this member's <link>teams page</link> to update their team membership.",
  'members.teamAccess.heading': 'Assigned through team',
  'members.teamAccess.learnAboutTeams': 'Learn about teams',
  'members.teamAccess.noTeamRolesHeading':
    'Although this member is on {count, plural, one {# team} other {# teams}}, no team roles are assigned to them.',
  'members.teamAccess.noTeamsHeading': 'This member is not on any teams.',
  'members.teamAccess.searchLabel': 'Search roles',
  'members.teamAccess.searchPlaceholder': 'Find an assigned role or resource',
  'members.teamRolesTable.customType': 'Custom',
  'members.teamRolesTable.roleNameColumn': 'Role name',
  'members.teamRolesTable.summary': 'The roles assigned to this member based on roles given to teams they’re on',
  'members.teamRolesTable.teamColumn': 'Team',
  'members.teamRolesTable.typeColumn': 'Type',
  'members.teams.addMemberToTeams': 'Add member to teams',
  'members.teams.addToTeam': 'Add to team',
  'members.teams.emptyDescription': 'Add this member to a team to control their access to LaunchDarkly.',
  'members.teams.emptyHeading': 'This member is not on any teams',
  'members.teams.learnMoreAboutTeams': 'Learn more about teams',
  'members.teams.nameColumn': 'Name',
  'members.teams.tableLabel': 'Member Teams',
  'metricDataSources.detailsPage.title': 'Data source',
  'metricDataSources.listPage.title': 'Metric Data Sources',
  'metricDataSources.newPage.title': 'Create',
  'metricDataSources_.page.title': 'Metric Data Sources',
  'metrics.archiveMetric.archive': 'Archive',
  'metrics.archiveMetric.body':
    'This will archive {name}, and the metric will no longer appear on the list of metrics. You can restore an archived metric at any time.',
  'metrics.archiveMetric.confirmHeading': 'Archive "{name}"?',
  'metrics.archiveMetric.connectedBody':
    'This metric is connected to {connections}. All historical data will remain available even after being archived.',
  'metrics.archiveMetric.failed': "Metric ''{key}'' archive failed: {message}",
  'metrics.archiveMetric.sectionDescription':
    'This will archive the metric across all environments in this project. All connections and related historical data will remain available even after this metric is archived. You can restore an archived metric at any time.',
  'metrics.archiveMetric.sectionTitle': 'Archive metric',
  'metrics.archiveMetric.success': 'Metric archived',
  'metrics.assignmentDataSourceForm.columnsRequired': 'Run a SQL query to retrieve column names for mapping',
  'metrics.assignmentDataSourceForm.contextColumnRequired': 'Select a column name',
  'metrics.assignmentDataSourceForm.contextKindRequired': 'Select a context kind',
  'metrics.assignmentDataSourceForm.contextMappingRequired': 'At least one context mapping is required',
  'metrics.assignmentDataSourceForm.descriptionTooLong': 'Description must be at most 1024 characters',
  'metrics.assignmentDataSourceForm.environmentKeyRequired': 'Environment key is required',
  'metrics.assignmentDataSourceForm.experimentIdColumnRequired': 'Select a column containing experiment IDs',
  'metrics.assignmentDataSourceForm.integrationRequired': 'Select a data warehouse integration',
  'metrics.assignmentDataSourceForm.keyPattern':
    "Key must start with a letter or number and only contain letters, numbers, '.', '_' or '-'",
  'metrics.assignmentDataSourceForm.keyTooLong': 'Key must be at most 128 characters',
  'metrics.assignmentDataSourceForm.maintainerRequired': 'Maintainer ID is required',
  'metrics.assignmentDataSourceForm.nameTooLong': 'Name must be at most 128 characters',
  'metrics.assignmentDataSourceForm.required': 'Required',
  'metrics.assignmentDataSourceForm.tableNameTooLong': 'Table name must be at most {max} characters',
  'metrics.assignmentDataSourceForm.timestampColumnRequired': 'Select a column containing timestamps',
  'metrics.assignmentDataSourceForm.variantIdColumnRequired': 'Select a column containing variant IDs',
  'metrics.attributeDataSourceForm.contextColumnUnavailable': 'Select a context column returned by your query',
  'metrics.attributeDataSourceForm.selectedColumnsRequired': 'Select at least one attribute column',
  'metrics.attributeDataSourceForm.selectedColumnsUnavailable': 'Select attribute columns returned by your query',
  'metrics.clickTargets.selectorHelp': 'Enter one or more comma-separated CSS selectors to record clicks',
  'metrics.clickTargets.selectorLabel': 'Click target(s)',
  'metrics.clickTargets.selectorPlaceholder': 'Enter one or more CSS selectors',
  'metrics.clickTargets.selectorRequired': 'Click target(s) are required',
  'metrics.columnMapping.addContextPair': 'Add another context pair',
  'metrics.columnMapping.columnName': 'Column name',
  'metrics.columnMapping.columnNamesLabel': 'Column names',
  'metrics.columnMapping.columnPlaceholder': 'Select a column',
  'metrics.columnMapping.columnTypeLabel': 'Column type',
  'metrics.columnMapping.completeStepTwo':
    'Complete <b>Step 2: Connect and query your metric data</b> in order to map your data.',
  'metrics.columnMapping.contextHelp':
    'Map a column containing unique identifiers for units to a context kind. For example, <code>user_id</code> to <code>user</code>',
  'metrics.columnMapping.contextKey': 'Context key',
  'metrics.columnMapping.contextKind': 'Context kind',
  'metrics.columnMapping.contextPairTip':
    'Tip: Add another context pair if your event record contains more than one context kind.',
  'metrics.columnMapping.defaultRandomizationUnitLabel': 'Default randomization unit',
  'metrics.columnMapping.editConfiguration': 'Edit configuration',
  'metrics.columnMapping.eventKeyHelp':
    'The name of the event type, such as <code>purchase_completed</code> or <code>page_view</code>.',
  'metrics.columnMapping.eventKeyLabel': 'Event key',
  'metrics.columnMapping.eventValueHelp':
    'A numeric value representing the event’s measurement, such as <code>order_amount</code> or <code>1</code> for conversions.',
  'metrics.columnMapping.eventValueLabel': 'Event value',
  'metrics.columnMapping.missingBody':
    'This data source is missing column mappings. Make a selection after running the SQL query to fetch preview data from your warehouse.',
  'metrics.columnMapping.missingHeading': 'Source columns not yet mapped',
  'metrics.columnMapping.noColumns': 'No columns found',
  'metrics.columnMapping.noColumnsEmptyState': 'No columns found.',
  'metrics.columnMapping.noMapping': 'No mapping',
  'metrics.columnMapping.noMoreContextKinds': 'No additional context kinds to add. Create more in project settings.',
  'metrics.columnMapping.none': 'None',
  'metrics.columnMapping.selectContextColumnLabel': 'Select context column',
  'metrics.columnMapping.selectContextKindLabel': 'Select context kind',
  'metrics.columnMapping.selectEventKeyLabel': 'Select event key column',
  'metrics.columnMapping.selectEventValueLabel': 'Select event value column',
  'metrics.columnMapping.selectTimestampLabel': 'Select timestamp column',
  'metrics.columnMapping.timestampHelp': 'The timestamp indicating when the event occurred.',
  'metrics.columnMapping.timestampLabel': 'Timestamp',
  'metrics.configuration.clickTargets': 'Click target(s)',
  'metrics.configuration.copyEventKeyLabel': 'Copy {eventKey} to your clipboard.',
  'metrics.configuration.copyEventKeyTooltip': 'Copy event key',
  'metrics.configuration.dataSource': 'Data source',
  'metrics.configuration.definition': 'Definition',
  'metrics.configuration.denominator': 'Denominator',
  'metrics.configuration.eventFilter': 'Event filter',
  'metrics.configuration.eventFilterLeadIn': 'Where event is filtered by',
  'metrics.configuration.eventKey': 'Event key',
  'metrics.configuration.eventSourcesLabel': 'event sources',
  'metrics.configuration.exploreEvent': 'Explore event',
  'metrics.configuration.heading': 'Configuration',
  'metrics.configuration.measurementWindow': 'Measurement window',
  'metrics.configuration.metricKindLabel': 'metric kind',
  'metrics.configuration.metricType': 'Metric type',
  'metrics.configuration.noSources': 'None',
  'metrics.configuration.noWindow': 'No window',
  'metrics.configuration.numerator': 'Numerator',
  'metrics.configuration.numericSpanAttribute': 'Numeric span attribute',
  'metrics.configuration.numericValueColumn': 'Numeric value column',
  'metrics.configuration.spanQuery': 'Span query',
  'metrics.configuration.subtitle':
    'Changing this metric configuration will not affect any active or past experiments or guarded rollouts.',
  'metrics.configuration.successCriteria': 'Success criteria',
  'metrics.configuration.targetUrls': 'Target URL(s)',
  'metrics.configuration.unitOfMeasure': 'Unit of measure',
  'metrics.configuration.unitsWithoutEvents': 'Units without events',
  'metrics.configuration.viewTraces': 'View traces',
  'metrics.configuration.windowEnds': 'Ends <strong>{value}</strong> {unit} after exposure',
  'metrics.configuration.windowStartAndEnd':
    'Start <strong>{startValue}</strong> {startUnit} after exposure and end <strong>{endValue}</strong> {endUnit} after exposure',
  'metrics.configuration.winsorization': 'Winsorization',
  'metrics.connections.allEnvironments': 'All environments',
  'metrics.connections.archived': 'Archived',
  'metrics.connections.archivedExperiments': 'Archived Experiments',
  'metrics.connections.connectionNoun': '{count, plural, one {connection} other {connections}}',
  'metrics.connections.createExperiment': 'Create experiment',
  'metrics.connections.effectSizeFromControl': 'Effect size from control',
  'metrics.connections.effectSizeNegativeLabel': 'Effect size (negative)',
  'metrics.connections.effectSizeNeutralLabel': 'Effect size (neutral)',
  'metrics.connections.effectSizePositiveLabel': 'Effect size (positive)',
  'metrics.connections.effectSizeUnavailable': 'Can not calculate effect size',
  'metrics.connections.environment': 'Environment',
  'metrics.connections.environmentsUnresolved': 'Unable to resolve environments',
  'metrics.connections.experiment': 'Experiment',
  'metrics.connections.experimentLoadError': 'An unexpected error occurred loading this experiment. Try again later.',
  'metrics.connections.experimentNotStarted': 'Experiment not started',
  'metrics.connections.experimentResultsLoadError': 'Could not load experiment results',
  'metrics.connections.experimentStatus': 'Experiment status',
  'metrics.connections.experimentsTab': 'Experiments',
  'metrics.connections.experimentsTableLabel': 'Experiments connections',
  'metrics.connections.flagName': 'Flag name',
  'metrics.connections.guardedRolloutsTab': 'Guarded rollouts',
  'metrics.connections.guardedRolloutsTableLabel': 'Guarded rollouts connections',
  'metrics.connections.heading': 'Connections',
  'metrics.connections.leadingTreatment': 'Leading: <b>{treatment}</b>',
  'metrics.connections.legacy': 'Legacy',
  'metrics.connections.legacyConnections': 'Legacy Connections',
  'metrics.connections.legacyTableLabel': 'Legacy connections',
  'metrics.connections.loadError': 'An unexpected error has occurred while loading the connections for this metric',
  'metrics.connections.metricGroup': 'Metric group',
  'metrics.connections.metricGroupType': 'Metric group type',
  'metrics.connections.metricGroupsTab': 'Metric groups',
  'metrics.connections.metricGroupsTableLabel': 'Metric groups connections',
  'metrics.connections.metricLastSeen': 'Metric last seen',
  'metrics.connections.metricStatus': 'Metric status',
  'metrics.connections.metricStatusLoadError': 'Could not load metric status',
  'metrics.connections.metricVersion': 'Metric version',
  'metrics.connections.metricVersionNotUsed': "The current iteration of this experiment doesn't use this metric.",
  'metrics.connections.metricVersionNotUsedLabel': 'Why the metric version is not available',
  'metrics.connections.multiArmedBandit': 'Multi-armed bandit',
  'metrics.connections.multiArmedBanditLoadError':
    'An unexpected error occurred loading this multi-armed bandit. Try again later.',
  'metrics.connections.multiArmedBanditNotStarted': 'Multi-armed bandit has not been started',
  'metrics.connections.multiArmedBanditStatus': 'Multi-armed bandit status',
  'metrics.connections.multiArmedBanditsTab': 'Multi-armed bandits',
  'metrics.connections.multiArmedBanditsTableLabel': 'Multi-armed bandits connections',
  'metrics.connections.noReleasePolicies': 'This metric is not connected to any release policies.',
  'metrics.connections.notApplicable': 'N/A',
  'metrics.connections.notConnected':
    'This metric is not connected to {connectionType, select, experiments {any experiments} guardedRollouts {any guarded rollouts} metricGroups {any metric groups} multiArmedBandits {any multi-armed bandits} other {anything}}.',
  'metrics.connections.policyEnvironmentsLabel': 'Release policy environments',
  'metrics.connections.policyName': 'Policy name',
  'metrics.connections.policyTagsLabel': 'Release policy tags',
  'metrics.connections.releasePoliciesTab': 'Release policies',
  'metrics.connections.releasePoliciesTableLabel': 'Release policies connections',
  'metrics.connections.rolledOutTreatment': 'Rolled out: <b>{treatment}</b>',
  'metrics.connections.rolloutCount': 'Rollout count',
  'metrics.connections.rolloutStatus': 'Rollout status',
  'metrics.connections.tags': 'Tags',
  'metrics.connections.updateToVersion': 'Update to v{version}',
  'metrics.connections.updateToVersionLabel': 'Update {experimentName} to version {version}',
  'metrics.connections.versionAlertBody':
    'Version {version} is the latest version of this metric. Results for {count, plural, one {# running experiment} other {# running experiments}} use an earlier version.',
  'metrics.connections.versionAlertHeading': 'Newer version available',
  'metrics.connections.versionAlertUpdate': 'Update',
  'metrics.connections.versionAlertUpdateAll': 'Update all',
  'metrics.connections.versionAlertUpdateCount': 'Update {count, plural, one {# experiment} other {# experiments}}',
  'metrics.connections.versionDialogBody':
    'This recalculates results for the current iteration of <b>{experimentName}</b>. Previous results cannot be recovered. Other metrics in this experiment are not affected.',
  'metrics.connections.versionDialogBodyCount':
    'This recalculates results for the current iteration of {count} experiments. Previous results cannot be recovered. Other metrics in these experiments are not affected.',
  'metrics.connections.versionDialogBodyMultiple':
    'This recalculates results for the current iteration of {experimentNames}. Previous results cannot be recovered. Other metrics in these experiments are not affected.',
  'metrics.connections.versionDialogCancel': 'Cancel',
  'metrics.connections.versionDialogCloseLabel': 'Close',
  'metrics.connections.versionDialogErrorDescription':
    '{count, plural, one {This experiment still uses} other {These experiments still use}} an earlier version: {experimentNames}.',
  'metrics.connections.versionDialogErrorTitle':
    '{count, plural, one {Could not update # experiment} other {Could not update # experiments}}',
  'metrics.connections.versionDialogHeading':
    '{count, plural, one {Update this metric to the latest version} other {Update this metric in # experiments}}',
  'metrics.connections.versionDialogSuccessDescription': 'Results will appear after the next scheduled analysis.',
  'metrics.connections.versionDialogSuccessTitle':
    '{count, plural, one {Metric updated to the latest version} other {Metric updated in # experiments}}',
  'metrics.connections.versionDialogUpdate': 'Update',
  'metrics.createButton.createMetric': 'Create metric',
  'metrics.createButton.createMetricGroup': 'Create metric group',
  'metrics.createMetricGroupPage.title': 'Create metric group',
  'metrics.createModal.cancel': 'Cancel',
  'metrics.createModal.clickedOrTapped': 'Clicked or tapped',
  'metrics.createModal.create': 'Create',
  'metrics.createModal.createError': 'Error creating metric',
  'metrics.createModal.creationDisabledBody':
    'Your user account does not have permission to create metrics. Request the necessary permissions from your administrator.',
  'metrics.createModal.creationDisabledHeading': 'Metric creation is disabled',
  'metrics.createModal.custom': 'Custom',
  'metrics.createModal.discardChanges': 'Discard changes',
  'metrics.createModal.eventKindLabel': 'Event kind',
  'metrics.createModal.heading': 'Create metric',
  'metrics.createModal.intro': 'Metrics measure audience behaviors and performance. <link>Read about metrics</link>.',
  'metrics.createModal.loadErrorBody': 'An unexpected error occurred while loading the metric form. Try again.',
  'metrics.createModal.loadErrorHeading': 'Something went wrong',
  'metrics.createModal.pageViewed': 'Page viewed',
  'metrics.createModal.ratio': 'Ratio',
  'metrics.createModal.simple': 'Simple',
  'metrics.createModal.structureLabel': 'Select metric structure',
  'metrics.createModal.unsavedChangesBody': 'If you close this form, you will lose any unsaved changes.',
  'metrics.dataSourceBadge.integrationLogoAlt': 'Integration logo',
  'metrics.dataSourceBadge.source': 'Source',
  'metrics.dataSourceBadge.sourceNotFound': 'Source not found.',
  'metrics.dataSourceChanges.archived': 'Archived data source',
  'metrics.dataSourceChanges.contextMapping': 'Updated context mapping',
  'metrics.dataSourceChanges.description': 'Updated description',
  'metrics.dataSourceChanges.experimentIdColumn': 'Updated experiment ID column',
  'metrics.dataSourceChanges.keyColumn': 'Updated key column',
  'metrics.dataSourceChanges.maintainer': 'Updated maintainer',
  'metrics.dataSourceChanges.name': 'Updated name',
  'metrics.dataSourceChanges.restored': 'Restored data source',
  'metrics.dataSourceChanges.selectedColumns': 'Updated attribute columns',
  'metrics.dataSourceChanges.sqlQuery': 'Updated SQL query',
  'metrics.dataSourceChanges.timestampColumn': 'Updated timestamp column',
  'metrics.dataSourceChanges.valueColumn': 'Updated value column',
  'metrics.dataSourceChanges.variantIdColumn': 'Updated variant ID column',
  'metrics.dataSourceForm.collapseLabel': 'Collapse',
  'metrics.dataSourceForm.columnsRequired': 'Run a SQL query to retrieve column names for mapping',
  'metrics.dataSourceForm.connectIntegration': 'Connect {name}',
  'metrics.dataSourceForm.contextColumnRequired': 'Select a column name',
  'metrics.dataSourceForm.contextKindRequired': 'Select a context kind',
  'metrics.dataSourceForm.contextMappingRequired': 'At least one context mapping is required',
  'metrics.dataSourceForm.defineStepBody':
    'Identify the source data from your warehouse that will be used to create metrics.',
  'metrics.dataSourceForm.defineStepHeading': 'Define data source',
  'metrics.dataSourceForm.descriptionInputLabel': 'Enter a data source description',
  'metrics.dataSourceForm.descriptionLabel': 'Description',
  'metrics.dataSourceForm.descriptionPlaceholder': 'Describe this metric data source',
  'metrics.dataSourceForm.descriptionTooLong': 'Description must be at most 1024 characters',
  'metrics.dataSourceForm.environmentKeyRequired': 'Environment key is required',
  'metrics.dataSourceForm.expandLabel': 'Expand',
  'metrics.dataSourceForm.inEnvironmentPrefix': 'in environment: ',
  'metrics.dataSourceForm.inputMethodLabel': 'Select input method',
  'metrics.dataSourceForm.integrationConfigNotFound': '{name} configuration not found.',
  'metrics.dataSourceForm.integrationFallbackName': 'Integration',
  'metrics.dataSourceForm.integrationHeading': 'Warehouse integration',
  'metrics.dataSourceForm.integrationIconAlt': '{name} icon',
  'metrics.dataSourceForm.integrationLabel': 'Select warehouse integration',
  'metrics.dataSourceForm.integrationLocked':
    'You cannot modify the warehouse integration used to create the metric data source.',
  'metrics.dataSourceForm.integrationMissing': "Don't see your integration?",
  'metrics.dataSourceForm.integrationRequired': 'Select a data warehouse integration',
  'metrics.dataSourceForm.keyColumnRequired': 'String must contain at least 1 character(s)',
  'metrics.dataSourceForm.keyInUseBy': 'The data source <b>{name}</b> already uses this key.',
  'metrics.dataSourceForm.keyInUseLabel': 'Key is already in use by another data source',
  'metrics.dataSourceForm.keyInputLabel': 'Enter a unique data source key',
  'metrics.dataSourceForm.keyIsAvailable': 'Key is available',
  'metrics.dataSourceForm.keyLabel': 'Key',
  'metrics.dataSourceForm.keyLocked': 'You cannot modify the key after creation.',
  'metrics.dataSourceForm.keyNotUnique': 'Enter a unique key',
  'metrics.dataSourceForm.keyPattern':
    "Key must start with a letter or number and only contain letters, numbers, '.', '_' or '-'",
  'metrics.dataSourceForm.keyPlaceholder': 'Data source key',
  'metrics.dataSourceForm.keyTooLong': 'Key must be at most 128 characters',
  'metrics.dataSourceForm.maintainedBy': 'Maintained by:',
  'metrics.dataSourceForm.maintainerRequired': 'Maintainer ID is required',
  'metrics.dataSourceForm.mapStepBody':
    'Select which columns should map to the respective ID types in LaunchDarkly. <link>Read more.</link>',
  'metrics.dataSourceForm.mapStepHeading': 'Map your data in LaunchDarkly',
  'metrics.dataSourceForm.nameInputLabel': 'Enter a data source name',
  'metrics.dataSourceForm.nameLabel': 'Name',
  'metrics.dataSourceForm.namePlaceholder': 'Data source name',
  'metrics.dataSourceForm.nameStepBody': 'Give this data source a name and description so others can easily find it.',
  'metrics.dataSourceForm.nameStepHeading': 'Name your data source',
  'metrics.dataSourceForm.nameTooLong': 'Name must be at most 128 characters',
  'metrics.dataSourceForm.noMaintainerFound': 'No maintainer found',
  'metrics.dataSourceForm.noMaintainerSelected': 'No maintainer selected',
  'metrics.dataSourceForm.orphanedAction': 'Create a new connection to continue receiving data.',
  'metrics.dataSourceForm.orphanedBody':
    'No integration configuration found for the following project and environment:',
  'metrics.dataSourceForm.orphanedHeading': 'Warehouse connection lost',
  'metrics.dataSourceForm.previewEmpty':
    'Enter a SQL query above and press the run query button to preview results from your data warehouse integration.',
  'metrics.dataSourceForm.previewNoRows':
    'Query returned no rows. Update your SQL filters or run against a table with matching events.',
  'metrics.dataSourceForm.previewResults': 'Preview results',
  'metrics.dataSourceForm.previewRunHint': 'Run the query to load columns for mapping.',
  'metrics.dataSourceForm.previewStale': 'Query has changed. Re-run before mapping your data.',
  'metrics.dataSourceForm.query': 'Query',
  'metrics.dataSourceForm.queryError': 'Error running SQL query',
  'metrics.dataSourceForm.queryTooLong': 'This query is too long to run. Shorten it and try again.',
  'metrics.dataSourceForm.required': 'Required',
  'metrics.dataSourceForm.runQuery': 'Run query',
  'metrics.dataSourceForm.runQueryLabel': 'Run SQL query',
  'metrics.dataSourceForm.setUpConnection': 'Set up a warehouse connection',
  'metrics.dataSourceForm.sqlEditableLabel': 'Enter SQL query to fetch columns',
  'metrics.dataSourceForm.sqlInputLabel': 'Enter a SQL query',
  'metrics.dataSourceForm.sqlLabel': 'SQL Query',
  'metrics.dataSourceForm.sqlPlaceholder': 'SELECT * FROM my_table;',
  'metrics.dataSourceForm.table': 'Table',
  'metrics.dataSourceForm.tableLabel': 'Select table',
  'metrics.dataSourceForm.tableNameTooLong': 'Table name must be at most {max} characters',
  'metrics.dataSourceForm.timestampColumnRequired': 'Select a column containing timestamps',
  'metrics.dataSourceForm.valueColumnRequired': "Select a column containing the event's value",
  'metrics.dataSourceInUse.experimentsAffected':
    'Changing this source can affect results for the following active experiments:',
  'metrics.dataSourceInUse.experimentsUsing':
    '<link>{count} {count, plural, one {experiment} other {experiments}}</link> {count, plural, one {uses} other {use}} this assignment data source.',
  'metrics.dataSourceInUse.heading': 'Data source currently in use',
  'metrics.dataSourceInUse.metricsCreated':
    '<link>{count} {count, plural, one {metric} other {metrics}}</link> {count, plural, one {has} other {have}} been created from this data source.',
  'metrics.dataSourceInUse.metricsInExperiments':
    'Some of these metrics are currently in use in the following active experiments:',
  'metrics.dataSourcePicker.apply': 'Apply',
  'metrics.dataSourcePicker.assignmentNoSupportedIntegrations':
    'Assignment data sources can be created from existing data warehouse integrations. Currently, there are no supported data warehouse integrations in your account.',
  'metrics.dataSourcePicker.assignmentSetUpIntegration':
    'Set up an integration to analyze experiments direct from your warehouse data source.',
  'metrics.dataSourcePicker.clear': 'Clear',
  'metrics.dataSourcePicker.clearSearchLabel': 'clear',
  'metrics.dataSourcePicker.create': 'Create',
  'metrics.dataSourcePicker.createAssignmentSource': 'Create assignment source',
  'metrics.dataSourcePicker.createMetricDataSource': 'Create metric data source',
  'metrics.dataSourcePicker.fromEnvironment': 'from <environment></environment> environment.',
  'metrics.dataSourcePicker.inAnyEnvironment': 'in any environment.',
  'metrics.dataSourcePicker.inEnvironment': 'in environment: <environment></environment>',
  'metrics.dataSourcePicker.integrationIconLabel': 'Integration manifest icon',
  'metrics.dataSourcePicker.integrationLogoAlt': '{name} integration logo',
  'metrics.dataSourcePicker.learnMore': 'Learn more',
  'metrics.dataSourcePicker.listLabel': 'Data sources',
  'metrics.dataSourcePicker.loadingIntegrationsLabel': 'Loading integrations',
  'metrics.dataSourcePicker.metricNoSupportedIntegrations':
    'Metric data sources can be created from existing data warehouse integrations. Currently, there are no supported data warehouse integrations in your account.',
  'metrics.dataSourcePicker.metricSetUpIntegration':
    'Set up an integration to create metrics direct from your warehouse data source.',
  'metrics.dataSourcePicker.noAssignmentSources':
    'No assignment sources found. Create one to use your warehouse assignment data to run an experiment.',
  'metrics.dataSourcePicker.noDataSourcesFound': 'No data sources found.',
  'metrics.dataSourcePicker.noIntegrations': 'No integrations yet',
  'metrics.dataSourcePicker.noMetricDataSources':
    'No metric data sources found. Create one to connect your warehouse metric data.',
  'metrics.dataSourcePicker.noSupportedIntegrations': 'No supported data warehouse integrations',
  'metrics.dataSourcePicker.otherDataSources': 'Other data sources',
  'metrics.dataSourcePicker.searchLabel': 'Search data sources…',
  'metrics.dataSourcePicker.setUpIntegration': 'Set up integration',
  'metrics.dataSourcePicker.warehouseExportDocs': 'Learn more about Warehouse data export',
  'metrics.definitionSummary.analysisUnitPlaceholder': '[analysis unit]',
  'metrics.definitionSummary.average': 'Average',
  'metrics.definitionSummary.averagePrefix': 'Average',
  'metrics.definitionSummary.clicksPer': 'clicks per',
  'metrics.definitionSummary.columnOn': 'on',
  'metrics.definitionSummary.countDistinctPrefix': 'Count distinct',
  'metrics.definitionSummary.countDistinctSentence':
    '{hasColumn, select, true { of <column></column>} other {}}{hasUnits, select, true { per <units></units>, then compute the <analysisType></analysisType> of those counts} other {}}. {criteria}.',
  'metrics.definitionSummary.countPrefix': 'Count',
  'metrics.definitionSummary.excluded': 'Excluded',
  'metrics.definitionSummary.higherIsBetter': 'higher is better',
  'metrics.definitionSummary.includedWithValue': 'Included with a value of {value}',
  'metrics.definitionSummary.lowerBound': 'Lower {value}%',
  'metrics.definitionSummary.lowerIsBetter': 'lower is better',
  'metrics.definitionSummary.measureUnit': '{kind, select, trace {span} other {event}}',
  'metrics.definitionSummary.ofCountPer': 'of {kind, select, trace {span} other {event}} <code>count</code> per ',
  'metrics.definitionSummary.pageViewsPer': 'page views per',
  'metrics.definitionSummary.percentPrefix': 'Percent',
  'metrics.definitionSummary.percentSentence': 'of <units></units> units that sent the event. {criteria}.',
  'metrics.definitionSummary.percentSentenceNoUnits': 'of units that sent the event. {criteria}.',
  'metrics.definitionSummary.percentageOf': 'Percentage of',
  'metrics.definitionSummary.percentile': 'P{value}',
  'metrics.definitionSummary.ratioCountDistinct': 'Count Distinct',
  'metrics.definitionSummary.sumPrefix': 'Sum',
  'metrics.definitionSummary.termEventsPer': '{term, select, numerator {numerator} other {denominator}} events per',
  'metrics.definitionSummary.unitsJoiner': ', ',
  'metrics.definitionSummary.unitsLastJoiner': ' or ',
  'metrics.definitionSummary.unitsOr': 'or',
  'metrics.definitionSummary.unitsThatClicked': ' units that clicked{where, select, true {, where } other {}}',
  'metrics.definitionSummary.unitsThatSawPage': ' units that saw the page{where, select, true {, where } other {}}',
  'metrics.definitionSummary.unitsThatSent':
    ' units that sent {kind, select, trace {a matching span} other {the event}}{where, select, true {, where } other {}}',
  'metrics.definitionSummary.upperBound': 'Upper {value}%',
  'metrics.definitionSummary.valueOfThePer': '<code>value</code> of the per ',
  'metrics.definitionSummary.valueSentence':
    '{noun, select, count {events} other {event values}}{hasUnits, select, true { per <units></units>} other {}}, then compute the <analysisType></analysisType>{hasUnits, select, true { of those values} other {}}. {criteria}.',
  'metrics.definitionSummary.whereConnector': ' where ',
  'metrics.definitionSummary.windowDays': '{count, plural, one {day} other {days}}',
  'metrics.definitionSummary.windowEnds': 'Ends <strong>{value}</strong> {unit} after exposure',
  'metrics.definitionSummary.windowEndsDefault': 'Ends <strong>{value}</strong> {unit} after exposure (default)',
  'metrics.definitionSummary.windowHours': '{count, plural, one {hour} other {hours}}',
  'metrics.definitionSummary.windowMinutes': '{count, plural, one {minute} other {minutes}}',
  'metrics.definitionSummary.windowStartAndEnd':
    'Start <strong>{startValue}</strong> {startUnit} – End <strong>{endValue}</strong> {endUnit} after exposure',
  'metrics.deleteMetric.body': 'This metric will be deleted permanently across all environments in this project.',
  'metrics.deleteMetric.confirmHeading': 'Delete "{name}"',
  'metrics.deleteMetric.delete': 'Delete',
  'metrics.deleteMetric.failed': "Metric ''{key}'' deletion failed: {message}",
  'metrics.deleteMetric.sectionTitle': 'Delete metric',
  'metrics.deleteMetric.success': 'Metric "{name}" deleted.',
  'metrics.detailsPage.activityLoadError':
    'An unexpected error has occurred while loading the event activity for this metric',
  'metrics.detailsPage.activityTab': 'Activity',
  'metrics.detailsPage.activityTabLabel': 'Activity tab',
  'metrics.detailsPage.addDescription': 'Add description',
  'metrics.detailsPage.backToMetrics': 'Back to metrics',
  'metrics.detailsPage.cancelFieldLabel': 'Cancel {field}',
  'metrics.detailsPage.connectionsHeading': 'Connections',
  'metrics.detailsPage.descriptionField': 'metric description',
  'metrics.detailsPage.descriptionFieldLabel': 'Metric description',
  'metrics.detailsPage.detailsLoadError': 'An unexpected error has occurred while loading the details for this metric',
  'metrics.detailsPage.detailsTab': 'Details',
  'metrics.detailsPage.detailsTabLabel': 'Details tab',
  'metrics.detailsPage.editFieldLabel': 'Edit {field}',
  'metrics.detailsPage.editMaintainerLabel': 'Edit maintainer',
  'metrics.detailsPage.errorBody':
    "We ran into an unexpected error displaying this metric. We've been notified. Try again later or",
  'metrics.detailsPage.errorHeading': 'Something went wrong',
  'metrics.detailsPage.eventKind': 'Event kind',
  'metrics.detailsPage.impactLoadError': 'An unexpected error has occurred while loading the impact for this metric',
  'metrics.detailsPage.impactTab': 'Impact',
  'metrics.detailsPage.impactTabLabel': 'Impact tab',
  'metrics.detailsPage.label': 'Edit name',
  'metrics.detailsPage.label2': 'Edit maintainer',
  'metrics.detailsPage.maintainedBy': 'Maintained by',
  'metrics.detailsPage.maintainerLoadError': 'Unexpected error loading maintainer',
  'metrics.detailsPage.nameField': 'metric name',
  'metrics.detailsPage.nameFieldLabel': 'Metric name',
  'metrics.detailsPage.noMaintainer': 'Anyone •<link>Choose maintainer</link>',
  'metrics.detailsPage.saveFieldLabel': 'Save {field}',
  'metrics.detailsPage.tabsLabel': 'Metric details tabs',
  'metrics.detailsPage.thereWasAn5': 'There was an issue loading the details for this metric.',
  'metrics.detailsPage.updateError': 'Could not update metric',
  'metrics.displayPreferences.display': 'Display',
  'metrics.displayPreferences.showTheseColumns': 'Show these columns',
  'metrics.editModal.cancel': 'Cancel',
  'metrics.editModal.description': 'Metrics measure audience behaviors affected by flags in experiments.',
  'metrics.editModal.discardChanges': 'Discard changes',
  'metrics.editModal.discardPromptBody': 'If you close this form, you will lose any unsaved changes.',
  'metrics.editModal.formLoadError': 'An unexpected error occurred while loading the metric form. Try again.',
  'metrics.editModal.formLoadErrorHeading': 'Something went wrong',
  'metrics.editModal.heading': 'Edit metric',
  'metrics.editModal.learnMore': 'Learn more',
  'metrics.editModal.learnMoreBody':
    'Metrics measure audience behaviors and performance. To learn more about how to create metrics and use them, <link>read the documentation.</link>',
  'metrics.editModal.noUpdatePermission':
    "You don't have permission to update metrics. Contact your admin to request update access.",
  'metrics.editModal.saveMetric': 'Save metric',
  'metrics.emptyState.clearFilters': 'Clear filters',
  'metrics.emptyState.createFirstMetric': 'Create your first metric',
  'metrics.emptyState.createFirstMetricGroup': 'Create your first metric group',
  'metrics.emptyState.filteredBody': 'Try adjusting your filters',
  'metrics.emptyState.metricGroupsBody': 'Metric groups organize related metrics for experiments.',
  'metrics.emptyState.metricsBody': 'Metrics measure the audience behaviors your flags and experiments affect.',
  'metrics.emptyState.noMetricGroupsFound': 'No metric groups found',
  'metrics.emptyState.noMetricsFound': 'No metrics found',
  'metrics.environmentLabel.unknown': 'Unknown',
  'metrics.eventActivity.clearFilters': 'Clear filters',
  'metrics.eventActivity.contextKeys': 'Context keys',
  'metrics.eventActivity.dataWarehouseBody':
    'Individual events from a data warehouse data source do not appear here. LaunchDarkly checks the data source for new events every 15 minutes and updates the last-seen time for this metric.',
  'metrics.eventActivity.dataWarehouseHeading': 'Event activity is not available for data warehouse sources',
  'metrics.eventActivity.dataWarehouseNotice':
    "Individual events from this metric's data warehouse data source do not appear here. LaunchDarkly checks the data source for new events every 15 minutes.",
  'metrics.eventActivity.dateLabel': 'Select a date and time',
  'metrics.eventActivity.emptyBody': 'After your app starts sending events, activity appears here.',
  'metrics.eventActivity.emptyHeading': 'Send events to populate this metric',
  'metrics.eventActivity.fetchError': 'Failed to fetch metric events: {message}',
  'metrics.eventActivity.filteredBody':
    'Unable to find metric events. Try adjusting the date range or selecting a different metric.',
  'metrics.eventActivity.filteredHeading': 'No event activity found',
  'metrics.eventActivity.filtersNotice':
    'Context attribute and event property filters included in the metric definition are not reflected in the list of events below.',
  'metrics.eventActivity.readAboutMetrics': 'Read about metrics',
  'metrics.eventActivity.showingEventsRange': 'Showing events from {start} to {end}',
  'metrics.eventActivity.showingLatestEvents': 'Showing latest events',
  'metrics.eventActivity.tableLabel': 'Metric event activity results',
  'metrics.eventActivity.timestamps': 'Timestamps',
  'metrics.eventActivity.value': 'Value',
  'metrics.eventDataPreview.collapseLabel': 'Collapse event preview table',
  'metrics.eventDataPreview.contextKindColumn': 'Context kind',
  'metrics.eventDataPreview.emptyBody': 'No event data available to preview.',
  'metrics.eventDataPreview.eventDataColumn': 'Event data',
  'metrics.eventDataPreview.eventDetailsColumn': 'Event details',
  'metrics.eventDataPreview.eventKeyColumn': 'Event key',
  'metrics.eventDataPreview.eventPropertiesColumn': 'Event properties',
  'metrics.eventDataPreview.expandLabel': 'Expand event preview table',
  'metrics.eventDataPreview.learnMore': 'Learn more',
  'metrics.eventDataPreview.loadingLabel': 'Loading recent events',
  'metrics.eventDataPreview.pageUrlColumn': 'Page URL',
  'metrics.eventDataPreview.selectorColumn': 'Selector',
  'metrics.eventDataPreview.sourceColumn': 'Source',
  'metrics.eventDataPreview.tableLabel': 'Event data preview',
  'metrics.eventDataPreview.timeRange': 'Showing events received in the last 90 days',
  'metrics.eventDataPreview.timestampColumn': 'Timestamp',
  'metrics.eventDataPreview.toggle': 'Preview event data',
  'metrics.eventDataPreview.valueColumn': 'Value',
  'metrics.eventDataPreview.warehouseExcludedNote':
    'Event data only available from sources sent directly to LaunchDarkly and excludes events from data warehouse. <link>Learn more</link>',
  'metrics.eventDataPreview.warehouseOnlyBody':
    'Event data from data warehouse integrations does not display here. <link>Learn more</link>.',
  'metrics.eventDataPreview.warehouseOnlyHeading': 'No data available',
  'metrics.eventDetailsModal.contextKeys': 'Context keys',
  'metrics.eventDetailsModal.eventAttributes': 'Event attributes',
  'metrics.eventDetailsModal.heading': 'Event details',
  'metrics.eventDetailsModal.value': 'Value',
  'metrics.eventFilters.aboutContextFilters': 'About context attribute filters',
  'metrics.eventFilters.aboutEventPropertyFilters': 'About event property filters',
  'metrics.eventFilters.aboutFilters': 'About event filters',
  'metrics.eventFilters.aboutFiltersHelp':
    'Filter which events count toward this metric. Click the bar and pick an event property or context attribute to add a condition, then choose an operator and value. Combine conditions with AND or OR — only events matching every condition are included.',
  'metrics.eventFilters.addAnotherFilter': 'Add another filter',
  'metrics.eventFilters.addFilter': 'Add filter',
  'metrics.eventFilters.addValue': 'Add value: <i>{name}</i>',
  'metrics.eventFilters.addValueLabel': 'Add value',
  'metrics.eventFilters.allContextKinds': 'All context kinds',
  'metrics.eventFilters.and': 'And',
  'metrics.eventFilters.changeBooleanToString': 'Change type from boolean to string',
  'metrics.eventFilters.changeNumberToString': 'Change type from number to string',
  'metrics.eventFilters.changeStringToBoolean': 'Change type from string to boolean',
  'metrics.eventFilters.changeStringToNumber': 'Change type from string to number',
  'metrics.eventFilters.chooseValues': 'Choose value(s)',
  'metrics.eventFilters.contextAttribute': 'Context attribute',
  'metrics.eventFilters.contextAttributeHelp':
    'Filter the event by an attribute of a context kind e.g. <code>user</code> or <code>account</code> associated with this event.',
  'metrics.eventFilters.contextAttributePlaceholder': 'Select a context attribute',
  'metrics.eventFilters.contextAttributesCategory': 'Context attributes',
  'metrics.eventFilters.contextKindLabel': 'Select a context kind',
  'metrics.eventFilters.eventPropertiesCategory': 'Event properties',
  'metrics.eventFilters.eventProperty': 'Event property',
  'metrics.eventFilters.eventPropertyHelp':
    "Filter the event by metadata sent in the SDK's track method e.g. <code>client.track('eventKey');</code>",
  'metrics.eventFilters.eventPropertyPlaceholder': 'Choose an event property',
  'metrics.eventFilters.fillRequired': 'Fill all required fields',
  'metrics.eventFilters.filterByContext': 'Filter by context attributes',
  'metrics.eventFilters.filterByEvent': 'Filter by event properties',
  'metrics.eventFilters.filterEventBy': 'Filter event by',
  'metrics.eventFilters.filterEvents': 'Filter events',
  'metrics.eventFilters.filteredByHeading': 'Events filtered by',
  'metrics.eventFilters.multipleContextKinds': 'Filters must use a single context kind.',
  'metrics.eventFilters.noPinnedAttributes': 'No sample attributes found for pinned context kind.',
  'metrics.eventFilters.operatorLabel': 'Select an operator',
  'metrics.eventFilters.or': 'Or',
  'metrics.eventFilters.pinnedConflict': 'Filter conflicts with pinned context kind',
  'metrics.eventFilters.pinnedContextKindLabel': 'Pinned context kind',
  'metrics.eventFilters.removeFilterLabel': 'Remove filter',
  'metrics.eventFilters.removeTagLabel': 'Remove tag',
  'metrics.eventFilters.selectValues': 'Select values',
  'metrics.eventFilters.typeToAdd': 'Type to add values',
  'metrics.eventFilters.valueTooLong': 'Value length of {length} is greater than the maximum 140 characters.',
  'metrics.eventFilters.warehouseFilterHelp': "Filter the event by the columns of your data source's table.",
  'metrics.eventFiltersSummary.and': 'AND',
  'metrics.eventFiltersSummary.andGroupLabel': 'AND group',
  'metrics.eventFiltersSummary.heading': 'Where event is filtered by',
  'metrics.eventFiltersSummary.listLabel': 'Filter list',
  'metrics.eventFiltersSummary.or': 'OR',
  'metrics.eventFiltersSummary.orGroupLabel': 'OR group',
  'metrics.eventKeyField.addKeyHint':
    'Add this event key to your codebase to start sending it, or enter the key to continue.',
  'metrics.eventKeyField.contexts': 'Contexts:',
  'metrics.eventKeyField.controlCharacters': 'Event keys may not contain control characters',
  'metrics.eventKeyField.dataSourceLabel': 'Source:',
  'metrics.eventKeyField.help':
    'Add this event key to your codebase to let your SDK track actions taken in your app as events',
  'metrics.eventKeyField.keyLabel': 'Event key',
  'metrics.eventKeyField.keyPlaceholder': 'Enter new or existing event key',
  'metrics.eventKeyField.lastSeen': 'Last seen: {date}',
  'metrics.eventKeyField.loadError': 'Something went wrong while fetching recent events.',
  'metrics.eventKeyField.loadingRecent': 'Loading Recent Events',
  'metrics.eventKeyField.loadingRecentLabel': 'Loading recent Event Keys',
  'metrics.eventKeyField.more': '+{count} more',
  'metrics.eventKeyField.noEventsFromSource': 'No events from this data source yet',
  'metrics.eventKeyField.noEventsWithKey': 'No events with this key yet',
  'metrics.eventKeyField.recentResults':
    'Showing the {count} most recently seen {count, plural, one {event} other {events}}',
  'metrics.eventKeyField.relevantResults': 'Showing the most relevant {count} results',
  'metrics.eventKeyField.required': 'Event key is required',
  'metrics.eventKeyField.resultsLabel': 'Event Keys',
  'metrics.eventKeyField.seenIn': 'Seen in:',
  'metrics.eventKeyField.sources': '{count, plural, one {Source} other {Sources}}:',
  'metrics.eventKeyField.tooLong': 'Event keys may not exceed {max} characters',
  'metrics.eventKeyField.warehouseHelp':
    'Identify this metric by specifying the event key column in your metric data source configuration',
  'metrics.eventKeyField.warehousePollingHint': 'LaunchDarkly checks this data source for new events every 15 minutes.',
  'metrics.eventSources.emptyFallback': 'None',
  'metrics.eventSources.loadError': 'Failed to load sources',
  'metrics.groupConnections.archived': 'Archived',
  'metrics.groupConnections.connections': 'Connections',
  'metrics.groupConnections.createExperiment': 'Create experiment',
  'metrics.groupConnections.environment': 'Environment',
  'metrics.groupConnections.experimentLoadError':
    'An unexpected error occurred loading this experiment. Try again later.',
  'metrics.groupConnections.experimentLoading': 'Loading…',
  'metrics.groupConnections.experimentName': 'Experiment name',
  'metrics.groupConnections.experimentStatusLabel': 'Experiment status',
  'metrics.groupConnections.experiments': 'Experiments',
  'metrics.groupConnections.flagName': 'Flag name',
  'metrics.groupConnections.guardedReleases': 'Guarded releases',
  'metrics.groupConnections.loadError':
    'An unexpected error has occurred while loading the connections for this metric group',
  'metrics.groupConnections.noConnections': 'This metric group has no connections.',
  'metrics.groupConnections.noExperiments': 'This metric group is not connected to any experiments.',
  'metrics.groupConnections.noGuardedReleases': 'This metric group is not connected to any guarded releases.',
  'metrics.groupConnections.noReleasePolicies': 'This metric group is not connected to any release policies.',
  'metrics.groupConnections.notStarted': 'Not started',
  'metrics.groupConnections.recentLimit': 'Showing the 10 most recent connections.',
  'metrics.groupConnections.releasePolicies': 'Release policies',
  'metrics.groupConnections.running': 'Running',
  'metrics.groupConnections.status': 'Status',
  'metrics.groupConnections.stopped': 'Stopped',
  'metrics.groupConnections.unknownEnvironment': 'Unknown',
  'metrics.groupDeleteModal.body':
    'This will permanently delete this metric group across all environments in this project.',
  'metrics.groupDeleteModal.cancel': 'Cancel',
  'metrics.groupDeleteModal.cancelLabel': 'Cancel metric group deletion',
  'metrics.groupDeleteModal.confirmHeading': 'Delete "{name}"',
  'metrics.groupDeleteModal.confirmLabel': 'Confirm metric group deletion',
  'metrics.groupDeleteModal.delete': 'Delete',
  'metrics.groupDeleteModal.failed': "Metric group ''{key}'' deletion failed: {message}",
  'metrics.groupDeleteModal.hasConnections':
    'You cannot delete a metric group when your metric group has one or more connections.',
  'metrics.groupDeleteModal.heading': 'Delete metric group',
  'metrics.groupDeleteModal.openLabel': 'Delete this metric group',
  'metrics.groupEditor.addAnotherMetric': 'Add another metric',
  'metrics.groupEditor.addMetricsHeading': 'Add metrics',
  'metrics.groupEditor.alreadySelected': 'Already selected',
  'metrics.groupEditor.cancel': 'Cancel',
  'metrics.groupEditor.createMetrics': 'Create metrics',
  'metrics.groupEditor.dragPreviewLabel': '{name} metric',
  'metrics.groupEditor.dragToReorderEmptyLabel': 'Drag to reorder metric',
  'metrics.groupEditor.dragToReorderLabel': 'Drag to reorder {name}',
  'metrics.groupEditor.dropIndicatorLabel': 'Drop indicator',
  'metrics.groupEditor.duplicateMetrics': 'Duplicate metrics are not allowed',
  'metrics.groupEditor.editMetrics': 'Edit metrics',
  'metrics.groupEditor.editMetricsHeading': 'Edit metrics',
  'metrics.groupEditor.emptySlots': 'All metric slots must be filled',
  'metrics.groupEditor.errorInfoLabel': 'Error info',
  'metrics.groupEditor.funnelTableLabel': 'Funnel group metrics',
  'metrics.groupEditor.keyRequired': 'Key is required',
  'metrics.groupEditor.limitReached':
    'The limit of {max} metrics per {kind, select, funnel {funnel} other {metric}} group has been reached',
  'metrics.groupEditor.listLabel': 'Metric list',
  'metrics.groupEditor.maxMetrics': 'Maximum {max} metrics allowed',
  'metrics.groupEditor.maxSelected': "You''ve selected the maximum number of {max} metrics.",
  'metrics.groupEditor.metricColumn': 'Metric',
  'metrics.groupEditor.metricsHeading': 'Metrics',
  'metrics.groupEditor.minMetrics': 'At least {min} metrics are required',
  'metrics.groupEditor.nameRequired': 'Name is required',
  'metrics.groupEditor.noCommonUnits': 'No common analysis units found across selected metrics',
  'metrics.groupEditor.noCountInFunnel':
    'Count metrics cannot be used in funnel metric groups. Funnel metrics must use the "Count distinct units (Percent)" aggregation method.',
  'metrics.groupEditor.noEditPermission': 'You do not have permission to edit metrics.',
  'metrics.groupEditor.noNestedGroups': 'Metric groups cannot be added to metric groups',
  'metrics.groupEditor.noNumericInFunnel': 'Numeric metrics cannot be used in funnel metric groups',
  'metrics.groupEditor.noWarehouseMetrics': 'Warehouse data source metrics are not available',
  'metrics.groupEditor.notEnoughBody':
    'A metric group requires {count} <link>{count, plural, one {Metric} other {Metrics}}</link> which measure events according to the same randomization unit.',
  'metrics.groupEditor.notEnoughHeading': "Not enough metrics. Let's create some.",
  'metrics.groupEditor.removeMetricLabel': 'Remove metric',
  'metrics.groupEditor.removeNamedMetricLabel': 'Remove metric {name}',
  'metrics.groupEditor.removeSlotLabel': 'Remove slot',
  'metrics.groupEditor.save': 'Save metric group',
  'metrics.groupEditor.saving': 'Saving changes…',
  'metrics.groupEditor.selectMetric': 'Select a metric',
  'metrics.groupEditor.selectMinMetrics':
    'Select {count} {count, plural, one {metric} other {metrics}} to save this group.',
  'metrics.groupEditor.stepColumn': 'Step',
  'metrics.groupEditor.stepNameRequired': 'All funnel metrics must have a step name',
  'metrics.groupEditor.stepNumber': 'Step {number}',
  'metrics.groupEditor.stepSelectMetricLabel': 'Step {number}: Select metric',
  'metrics.groupEditor.tableLabel': 'Metric group metrics',
  'metrics.groupEditor.typeColumn': 'Type',
  'metrics.groupEditor.unitsMustInclude': "Metric''s analysis units must include at least one of: {units}",
  'metrics.groupsDetailsPage.backToMetric': 'Back to metric groups',
  'metrics.groupsDetailsPage.created': 'Created',
  'metrics.groupsDetailsPage.descriptionLabel': 'Metric group description',
  'metrics.groupsDetailsPage.descriptionPlaceholder': 'Add description',
  'metrics.groupsDetailsPage.detailsLoadError': 'There was an issue loading the details for this metric group.',
  'metrics.groupsDetailsPage.editFormLabel': 'Edit metric group',
  'metrics.groupsDetailsPage.editMaintainerLabel': 'Edit maintainer',
  'metrics.groupsDetailsPage.loadError': 'Unexpected error loading metric group',
  'metrics.groupsDetailsPage.maintainedBy': 'Maintained by',
  'metrics.groupsDetailsPage.maintainerLoadError': 'Unable to load maintainer',
  'metrics.groupsDetailsPage.maintainerModalHeading': 'Assign a person as the metric group maintainer',
  'metrics.groupsDetailsPage.nameLabel': 'Metric group name',
  'metrics.groupsDetailsPage.thereWasAn6': 'There was an issue loading the details for this metric group.',
  'metrics.groupsDetailsPage.updateError': "Metric group ''{name}'' update failed",
  'metrics.groupsDetailsPage.updateSuccess': "Metric group ''{name}'' updated",
  'metrics.groupsList.actions': 'Actions',
  'metrics.groupsList.actionsLabel': 'Metric group actions',
  'metrics.groupsList.copyKey': 'Copy key',
  'metrics.groupsList.createdAt': '{date} at {time}',
  'metrics.groupsList.createdDateLabel': 'Metric group created date and maintainer',
  'metrics.groupsList.createdOnColumn': 'Created on',
  'metrics.groupsList.createdOnColumnTooltip': 'Shows the date the metric group was created.',
  'metrics.groupsList.creationDate': 'Creation Date',
  'metrics.groupsList.experimentsColumn': 'Experiments',
  'metrics.groupsList.experimentsColumnTooltip':
    'Shows the number of currently running experiments and the total number of experiments that have used the metric group.',
  'metrics.groupsList.guardedReleasesColumn': 'Guarded releases',
  'metrics.groupsList.guardedReleasesColumnTooltip':
    'Shows the number of currently monitored guarded releases and the total number of guarded rollouts that have used the metric group.',
  'metrics.groupsList.keyCopied': 'Metric group key copied to clipboard',
  'metrics.groupsList.kindLabel': 'metric group kind',
  'metrics.groupsList.lastUsedInColumn': 'Last used in',
  'metrics.groupsList.lastUsedInColumnTooltip':
    'Shows the most recent experiment or guarded releases where the metric group was used.',
  'metrics.groupsList.loadingLabel': 'Loading metric groups',
  'metrics.groupsList.loadingValueText': 'loading metric groups',
  'metrics.groupsList.name': 'Name',
  'metrics.groupsList.totalConnectionsColumn': 'Total connections',
  'metrics.groupsList.totalConnectionsColumnTooltip':
    'Shows the total number of connections this metric group has across experiments and guarded releases.',
  'metrics.groupsNewPage.cancel': 'Cancel',
  'metrics.groupsNewPage.configuration': 'Configuration',
  'metrics.groupsNewPage.createMetricGroup': 'Create metric group',
  'metrics.groupsNewPage.descriptionLabel': 'Metric group description',
  'metrics.groupsNewPage.descriptionPlaceholder': 'Describe this metric group',
  'metrics.groupsNewPage.funnelDescription':
    'Funnel metric groups track user progression through a series of steps with two or more metrics.',
  'metrics.groupsNewPage.funnelType': 'Funnel',
  'metrics.groupsNewPage.intro':
    'A metric group is a reusable set of metrics to use for measurement. <link>Learn more</link>.',
  'metrics.groupsNewPage.label': 'Loading…',
  'metrics.groupsNewPage.nameLabel': 'Metric group name',
  'metrics.groupsNewPage.namePlaceholder': 'Enter metric group name',
  'metrics.groupsNewPage.selectTypeLabel': 'Select {type} metric group type',
  'metrics.groupsNewPage.standardDescription':
    'Standard metric groups are reusable metric groups with two or more metrics.',
  'metrics.groupsNewPage.standardType': 'Standard',
  'metrics.groupsNewPage.typeLabel': 'Metric group type selection',
  'metrics.healthCheck.allNumericValues': 'All sample events included numeric values in the last 30 days',
  'metrics.healthCheck.allUnitsHaveEvents': 'All randomization units have events in the last {days} days',
  'metrics.healthCheck.eventsReceived90Days': 'Events received in the last 90 days',
  'metrics.healthCheck.loadError': 'Health check failed to load.',
  'metrics.healthCheck.noEventKey': 'No event key selected',
  'metrics.healthCheck.noEvents90Days': 'No events received in the last 90 days',
  'metrics.healthCheck.noNumericValues': 'No events with numeric values received in the last 30 days',
  'metrics.healthCheck.noRandomizationUnits': 'No randomization units selected',
  'metrics.healthCheck.noSampleNumericValues': 'No sample events have included numeric values in the last 30 days',
  'metrics.healthCheck.noUnitEvents':
    'No events available to evaluate selected randomization units in the last {days} days',
  'metrics.healthCheck.someNumericValues': 'Not all sample events included numeric values in the last 30 days',
  'metrics.healthCheck.someUnitsHaveEvents': 'Some randomization units have events in the last {days} days',
  'metrics.healthCheck.unitsNoEvents': 'Selected randomization units have not received events in the last {days} days',
  'metrics.impact.containerLabel': 'Metric impact container',
  'metrics.impact.control': 'Control',
  'metrics.impact.dateEnded': 'Date ended',
  'metrics.impact.emptyBody':
    'This page will show the impact of released experiments. You can use the relative difference to understand if your metric is moving in the desired direction, and by how much.',
  'metrics.impact.emptyHeading': 'You have no released experiments yet',
  'metrics.impact.experiment': 'Experiment',
  'metrics.impact.experimentLoadError': 'An unexpected error occurred loading this experiment. Try again later.',
  'metrics.impact.flagLabel': 'Flag: {flagName}',
  'metrics.impact.iteration': 'Iteration {number}',
  'metrics.impact.loadError': 'Could not load measurement connections',
  'metrics.impact.loadingLabel': 'Loading metric impact',
  'metrics.impact.readTheDocumentation': 'Read the documentation',
  'metrics.impact.relativeDifference': 'Relative difference',
  'metrics.impact.relativeDifferenceLabel': 'Relative difference to control',
  'metrics.impact.relativeDifferenceTagLabel': 'Relative difference tag',
  'metrics.impact.releasedExperimentCount': '<strong>{count}</strong> released experiments',
  'metrics.impact.switchEnvironment': 'Switch to {environment} environment',
  'metrics.impact.tableLabel': 'Metric impact table',
  'metrics.impact.unavailableBody':
    "This metric was created through a data source originating from a data warehouse integration, and is only available in that data source's specified environment.",
  'metrics.impact.unavailableHeading': 'Metric unavailable in selected environment',
  'metrics.impact.variationToServe': 'Variation to serve',
  'metrics.infoPopover.copyEventKey': 'Copy event key',
  'metrics.infoPopover.copyEventKeyLabel': 'Copy {eventKey} to your clipboard.',
  'metrics.infoPopover.customWindowNote': 'Metric uses events with custom measurement window applied',
  'metrics.infoPopover.denominatorValueColumn': 'Denominator value column',
  'metrics.infoPopover.eventFiltersApplied': 'Event filters applied',
  'metrics.infoPopover.eventKey': 'Event key',
  'metrics.infoPopover.loadError': "Error fetching this metric's information",
  'metrics.infoPopover.measurementWindow': 'Measurement window',
  'metrics.infoPopover.metricDefinition': 'Metric definition',
  'metrics.infoPopover.numeratorValueColumn': 'Numerator value column',
  'metrics.infoPopover.numericValueColumn': 'Numeric value column',
  'metrics.infoPopover.ratioSentence':
    '<numeratorAgg></numeratorAgg> <numeratorTerm></numeratorTerm>{hasUnits, select, true { per <units></units>} other {}} and <denominatorAgg></denominatorAgg> <denominatorTerm></denominatorTerm>{hasUnits, select, true { per <units></units>} other {}}, then compute numerator total divided by denominator total.',
  'metrics.infoPopover.ratioTermColumn': '{term, select, numerator {numerator} other {denominator}} <code></code>',
  'metrics.infoPopover.ratioTermNoun': '{term, select, numerator {numerator} other {denominator}} {noun}',
  'metrics.infoPopover.successCriteria': 'Success criteria',
  'metrics.infoPopover.unitOfMeasure': 'Unit of measure',
  'metrics.infoPopover.unitsJoiner': ', ',
  'metrics.infoPopover.unitsLastJoiner': ' or ',
  'metrics.infoPopover.unitsWithout': 'Units without {kind, select, trace {spans} other {events}}',
  'metrics.infoPopover.viewDetails': 'Open details',
  'metrics.infoPopover.viewDetailsForLabel': 'View details for {name}',
  'metrics.infoPopover.winsorization': 'Winsorization',
  'metrics.lastSeen.fetchError': 'Cannot fetch the last seen time for this metric',
  'metrics.lastSeen.noEvents': "This metric hasn't received any events",
  'metrics.lifecycle.activeExperimentCount':
    '{count} {count, plural, one {active experiment} other {active experiments}}',
  'metrics.lifecycle.activeGuardedRolloutCount':
    '{count} {count, plural, one {active guarded rollout} other {active guarded rollouts}}',
  'metrics.lifecycle.attachedFlagCount': '{count} attached {count, plural, one {flag} other {flags}}',
  'metrics.lifecycle.cancel': 'Cancel',
  'metrics.lifecycle.cancelLabel': 'Cancel action',
  'metrics.lifecycle.cannotArchive': 'This metric cannot be archived because it is connected to {connections}.',
  'metrics.lifecycle.cannotDelete': 'This metric cannot be deleted because it is connected to {connections}.',
  'metrics.lifecycle.closeLabel': 'Close modal',
  'metrics.lifecycle.confirmLabel': 'Confirm action',
  'metrics.lifecycle.connectedTo': 'This metric is connected to {connections}.',
  'metrics.lifecycle.contactAdmin': 'Contact your account admin.',
  'metrics.lifecycle.experimentCount': '{count} {count, plural, one {experiment} other {experiments}}',
  'metrics.lifecycle.guardedRolloutCount': '{count} {count, plural, one {guarded rollout} other {guarded rollouts}}',
  'metrics.lifecycle.manyConnections': '{rest} and {last}',
  'metrics.lifecycle.metricGroupCount': '{count} metric {count, plural, one {group} other {groups}}',
  'metrics.lifecycle.noPermission': "You don't have permission to do this action.",
  'metrics.lifecycle.performLabel': 'Perform action',
  'metrics.lifecycle.releasePolicyCount': '{count} release {count, plural, one {policy} other {policies}}',
  'metrics.lifecycle.twoConnections': '{first} and {second}',
  'metrics.lifecycle.unknownError': 'Unknown error',
  'metrics.listFilters.activeFilterCountLabel':
    '{count, plural, one {{count} active filter} other {{count} active filters}}',
  'metrics.listFilters.addDataSource': 'Add data source(s)',
  'metrics.listFilters.anyDataSource': 'Any data source',
  'metrics.listFilters.anyMaintainer': 'Anyone',
  'metrics.listFilters.anyType': 'Any',
  'metrics.listFilters.archived': 'Archived',
  'metrics.listFilters.archivedChip': 'Archived',
  'metrics.listFilters.archivedField': 'Archived',
  'metrics.listFilters.archivedFieldDescription': 'Show archived metrics.',
  'metrics.listFilters.archivedValue': 'Archived',
  'metrics.listFilters.chooseMaintainer': 'Choose maintainer',
  'metrics.listFilters.clear': 'Clear',
  'metrics.listFilters.dataSourceField': 'Data source',
  'metrics.listFilters.dataSourceFieldDescription': 'The data source that provides events for this metric.',
  'metrics.listFilters.dataSourceIconAlt': 'Data source',
  'metrics.listFilters.dataSourcesLabel': 'Data sources',
  'metrics.listFilters.either': 'Either',
  'metrics.listFilters.filterMetricGroupsBy': 'Filter metric groups by…',
  'metrics.listFilters.filterMetricsBy': 'Filter metrics by…',
  'metrics.listFilters.filters': 'Filters',
  'metrics.listFilters.hasConnections': 'Has connections',
  'metrics.listFilters.hasConnectionsChip': 'Has connections',
  'metrics.listFilters.hasConnectionsField': 'Has connections',
  'metrics.listFilters.hasConnectionsFieldDescription':
    'Whether the metric has connections to experiments or guarded rollouts.',
  'metrics.listFilters.launchDarklyHosted': 'LaunchDarkly hosted',
  'metrics.listFilters.launchDarklyNative': 'LaunchDarkly native',
  'metrics.listFilters.loadError': 'Something went wrong while loading filters.',
  'metrics.listFilters.loadingMaintainers': 'Loading maintainers…',
  'metrics.listFilters.maintainerField': 'Maintainer',
  'metrics.listFilters.maintainerFieldDescription': 'The metric maintainer.',
  'metrics.listFilters.maintainerLabel': 'Maintainer',
  'metrics.listFilters.maintainerLoadError': 'Something went wrong while loading the maintainer filter.',
  'metrics.listFilters.metricGroupsSearchPlaceholder': 'Find a metric group by name or description',
  'metrics.listFilters.metricUsedIn': 'Metric used in',
  'metrics.listFilters.metricsSearchPlaceholder': 'Find a single metric by name or description',
  'metrics.listFilters.moreSelectedChip': '{name} +{count}',
  'metrics.listFilters.no': 'No',
  'metrics.listFilters.noConnectionsChip': "Doesn't have connections",
  'metrics.listFilters.notArchivedChip': 'Not archived',
  'metrics.listFilters.notUsedAnywhere': 'Not used anywhere',
  'metrics.listFilters.notified': "We've been notified.",
  'metrics.listFilters.searchField': 'Search',
  'metrics.listFilters.searchFieldDescription': 'A fuzzy search across metric key and name.',
  'metrics.listFilters.tagsField': 'Tags',
  'metrics.listFilters.tagsFieldDescription': 'The metric tags.',
  'metrics.listFilters.tryAgain': 'Try again',
  'metrics.listFilters.type': 'Type',
  'metrics.listFilters.typeField': 'Type',
  'metrics.listFilters.typeFieldDescription': 'The metric type.',
  'metrics.listFilters.typeLoading': 'Loading…',
  'metrics.listFilters.usedIn': 'Used in {target}',
  'metrics.listFilters.usedInAny': 'Any',
  'metrics.listFilters.usedInExperiments': 'Experiments',
  'metrics.listFilters.usedInField': 'Metric used in',
  'metrics.listFilters.usedInFieldDescription': 'Filter by where the metric is used.',
  'metrics.listFilters.usedInGuardedRollouts': 'Guarded rollouts',
  'metrics.listFilters.usedInNone': 'None',
  'metrics.listFilters.usedInReleasePolicies': 'Release policies',
  'metrics.listFilters.yes': 'Yes',
  'metrics.listPage.createMetric': 'Create metric',
  'metrics.listPage.createMetricData': 'Create metric data source',
  'metrics.listPage.createMetricGroup': 'Create metric group',
  'metrics.listPage.fewestConnections': 'Fewest connections',
  'metrics.listPage.label': 'open menu',
  'metrics.listPage.manageMetricData': 'Manage metric data sources',
  'metrics.listPage.metricActionsMenu': 'Metric actions menu',
  'metrics.listPage.metricDataSources': 'Metric data sources',
  'metrics.listPage.metricGroupsLoadError': 'An unexpected error has occurred while loading metric groups',
  'metrics.listPage.metricGroupsTab': 'Metric groups',
  'metrics.listPage.metricsLoadError': 'An unexpected error has occurred while loading metrics',
  'metrics.listPage.metricsTab': 'Metrics',
  'metrics.listPage.mostActiveConnections': 'Most active connections',
  'metrics.listPage.mostConnections': 'Most connections',
  'metrics.listPage.nameAZ': 'Name A-Z',
  'metrics.listPage.nameZA': 'Name Z-A',
  'metrics.listPage.newest': 'Newest',
  'metrics.listPage.oldest': 'Oldest',
  'metrics.listPage.sort': 'Sort',
  'metrics.maintainerModal.assignMaintainer': 'Assign maintainer',
  'metrics.maintainerModal.cancel': 'Cancel',
  'metrics.maintainerModal.heading': 'Assign a person as the metric maintainer',
  'metrics.maintainerModal.selectPlaceholder': 'Select maintainer',
  'metrics.measurementWindow.addLabel': 'Add custom measurement window',
  'metrics.measurementWindow.afterExposure': '{unit} after exposure',
  'metrics.measurementWindow.description':
    'Defines how long after first exposure metric events are included in experiment analysis. This setting applies only when the metric is used in an experiment.',
  'metrics.measurementWindow.enableLabel': 'Enable custom measurement window',
  'metrics.measurementWindow.end': 'End',
  'metrics.measurementWindow.endBeforeStart': 'End ({end}) must be greater than start ({start})',
  'metrics.measurementWindow.endNotInteger': 'End must be a whole number',
  'metrics.measurementWindow.endRequired': 'End is required when using a custom window',
  'metrics.measurementWindow.endTooLarge': 'End cannot exceed {maxDays} days ({max} {unit})',
  'metrics.measurementWindow.help': 'Units appear in results only after their full window completes.',
  'metrics.measurementWindow.removeLabel': 'Remove custom measurement window',
  'metrics.measurementWindow.start': 'Start',
  'metrics.measurementWindow.startNegative': 'Start must be 0 or greater',
  'metrics.measurementWindow.startNotInteger': 'Start must be a whole number',
  'metrics.measurementWindow.startRequired': 'Start is required when using a custom window',
  'metrics.measurementWindow.startTooLarge': 'Start cannot exceed {maxDays} days ({max} {unit})',
  'metrics.measurementWindow.title': 'Custom measurement window',
  'metrics.measurementWindow.unitLabel': 'Window measured in',
  'metrics.measurementWindow.unitName': '{unit, select, days {Days} hours {Hours} minutes {Minutes} other {{unit}}}',
  'metrics.metricDefinition.aggregateAsLabel': 'Aggregate as',
  'metrics.metricDefinition.aggregationAverage': 'Average',
  'metrics.metricDefinition.aggregationCount': 'Count',
  'metrics.metricDefinition.aggregationCountDistinct': 'Count distinct',
  'metrics.metricDefinition.aggregationEventPercentile': 'Percentile of event values',
  'metrics.metricDefinition.aggregationMethodLabel': 'Aggregation method',
  'metrics.metricDefinition.aggregationOccurrence': 'Occurrence (Percent)',
  'metrics.metricDefinition.aggregationPlaceholder': 'Select aggregation',
  'metrics.metricDefinition.aggregationSum': 'Sum',
  'metrics.metricDefinition.analysisTypeLabel': 'Analysis type',
  'metrics.metricDefinition.analysisTypePickerLabel': 'Choose a method for unit to sample aggregation',
  'metrics.metricDefinition.analysisUnitLabel': 'Analysis unit',
  'metrics.metricDefinition.analysisUnitPlaceholder': '[analysis unit]',
  'metrics.metricDefinition.analysisUnitsLabel': 'Analysis units',
  'metrics.metricDefinition.analysisUnitsLoadError': 'Failed to load analysis units',
  'metrics.metricDefinition.analysisUnitsSelectLabel': 'Analysis unit multi select',
  'metrics.metricDefinition.average': 'Average',
  'metrics.metricDefinition.averageDescription':
    'Average value per unit ex: If a customer makes 3 purchases, divide their total by 3',
  'metrics.metricDefinition.clickCountDescription': 'Number of times a target was clicked',
  'metrics.metricDefinition.clickOccurrenceDescription': 'Whether a target was clicked',
  'metrics.metricDefinition.columnNamePlaceholder': '[column name]',
  'metrics.metricDefinition.columnNamesLabel': 'Column names',
  'metrics.metricDefinition.columnPlaceholder': 'Select a column',
  'metrics.metricDefinition.columnRequired': 'Column is required',
  'metrics.metricDefinition.columnTypeLabel': 'Column type',
  'metrics.metricDefinition.contextKindSettingsLink': 'Context kind settings',
  'metrics.metricDefinition.countDescription': 'Number of times an event occurred ex: count of purchases made',
  'metrics.metricDefinition.countDistinctDescription':
    'Number of unique values in a column ex: distinct order IDs or session IDs',
  'metrics.metricDefinition.countDistinctHelp':
    'Counts the number of unique values in the column you select. The list shows only columns your data source exposes.',
  'metrics.metricDefinition.countDistinctOnLabel': 'Count distinct on',
  'metrics.metricDefinition.countDistinctSentence':
    'of <column></column> per <units></units>, then compute the <analysisType></analysisType> of those counts where <criteria></criteria>',
  'metrics.metricDefinition.countDistinctSentenceSectioned':
    'of <column></column> per <units></units>, then compute the <analysisType></analysisType> <clause>of those counts, where</clause><strong>{criteriaText}.</strong>',
  'metrics.metricDefinition.countNoun':
    '{kind, select, pageview {page views} click {clicks} trace {matching spans} other {events}}',
  'metrics.metricDefinition.dataSourceConfigurationLink': 'Data source configuration',
  'metrics.metricDefinition.dataSourceLabel': 'Metric data source',
  'metrics.metricDefinition.dataSourcePlaceholder': 'Select data source',
  'metrics.metricDefinition.definitionLabel': 'Metric definition',
  'metrics.metricDefinition.denominatorLabel': 'Denominator',
  'metrics.metricDefinition.eventPercentileDescription':
    'Each event counts on its own, not grouped by randomization unit first ex: the latency of every API request',
  'metrics.metricDefinition.eventPercentileSentence':
    'Compute the <percentile></percentile> of all event values. {criteria, select, LowerThanBaseline {Lower is better.} other {Higher is better.}}',
  'metrics.metricDefinition.excludeUnits':
    'Exclude units that generate no {kind, select, trace {matching spans} other {events}}',
  'metrics.metricDefinition.excluded': 'Excluded',
  'metrics.metricDefinition.higherIsBetter': 'higher is better',
  'metrics.metricDefinition.includeUnits': 'Include units and set the value to 0',
  'metrics.metricDefinition.includedWithZero': 'Included with value set to 0',
  'metrics.metricDefinition.noColumns': 'No columns found',
  'metrics.metricDefinition.noColumnsEmptyState': 'No columns found.',
  'metrics.metricDefinition.noNumericColumns': 'No numeric columns found',
  'metrics.metricDefinition.noContextKindsEnabled':
    'No context kinds are enabled on this data source. Enable one to continue.',
  'metrics.metricDefinition.noDataSources': 'No metric data sources found',
  'metrics.metricDefinition.numeratorLabel': 'Numerator',
  'metrics.metricDefinition.occurrenceClause':
    '{kind, select, pageview {units that saw the page} click {units that clicked} trace {units with a matching span} other {units that sent the event}}',
  'metrics.metricDefinition.occurrenceDescription': 'Whether an event is seen at least one time per unit ex: Errors',
  'metrics.metricDefinition.occurrenceSentence': 'of <units></units> {clauseText} where <criteria></criteria>',
  'metrics.metricDefinition.occurrenceSentenceSectioned':
    'of <units></units> <clause>{clauseText}, where</clause><strong>{criteriaText}.</strong>',
  'metrics.metricDefinition.pageViewCountDescription': 'Number of times a page was viewed',
  'metrics.metricDefinition.pageViewOccurrenceDescription': 'Whether a page was viewed',
  'metrics.metricDefinition.percentileLevelCustom': 'Custom',
  'metrics.metricDefinition.percentileLevelCustomLabel': 'Custom percentile level',
  'metrics.metricDefinition.percentileLevelCustomPrefix': 'P',
  'metrics.metricDefinition.percentileLevelInvalid': 'Enter an integer from 1 to 99',
  'metrics.metricDefinition.percentileLevelLabel': 'Percentile level',
  'metrics.metricDefinition.percentilePlaceholder': '[percentile]',
  'metrics.metricDefinition.prefixPercent': 'Percent',
  'metrics.metricDefinition.ratioAverageDescription': 'Average value per unit ex: average revenue per purchase',
  'metrics.metricDefinition.ratioSumDescription': 'Total value per unit ex: total revenue across all purchases',
  'metrics.metricDefinition.ratioUnsupportedSource': 'Ratio metrics are not supported on this data source',
  'metrics.metricDefinition.selectPlaceholder': 'Select',
  'metrics.metricDefinition.selectionRequired': 'Selection is required',
  'metrics.metricDefinition.showSpanAttributesLabel': 'Show numeric span attributes',
  'metrics.metricDefinition.spanAttributeHelp':
    'The numeric span attribute to use as the measured value. ex: <code>http.response_content_length</code>',
  'metrics.metricDefinition.spanAttributeLabel': 'Choose a numeric span attribute',
  'metrics.metricDefinition.spanAttributePlaceholder': 'Select a span attribute',
  'metrics.metricDefinition.spanAttributeRequired': 'Span attribute is required',
  'metrics.metricDefinition.spanAttributeRowLabel': 'Numeric span attribute',
  'metrics.metricDefinition.spanAttributesListLabel': 'Numeric span attributes',
  'metrics.metricDefinition.successCriteriaLabel': 'Success criteria',
  'metrics.metricDefinition.successCriteriaPickerLabel': 'Choose a success criteria',
  'metrics.metricDefinition.sumDescription':
    'Total value per unit ex: If a customer makes 3 purchases, add up their total',
  'metrics.metricDefinition.traceAverageDescription': 'Average span attribute value per unit',
  'metrics.metricDefinition.traceCountDescription': 'Number of matching spans',
  'metrics.metricDefinition.traceOccurrenceDescription': 'Whether a matching span is seen at least once',
  'metrics.metricDefinition.traceSumDescription': 'Total span attribute value per unit',
  'metrics.metricDefinition.unitMissing':
    'The {unitName} analysis unit no longer exists. Select a different analysis unit.',
  'metrics.metricDefinition.unitMissingNoAlternatives':
    'The {unitName} analysis unit no longer exists in this project. Add a context kind to continue.',
  'metrics.metricDefinition.unitMissingSelectOnly':
    'The {unitName} analysis unit no longer exists. Select {onlyAlternative} instead.',
  'metrics.metricDefinition.unitNotEnabled':
    'The {unitName} context kind is not enabled on this data source. Select one that is, or enable {unitName} on the data source.',
  'metrics.metricDefinition.unitNotEnabledSelectOnly':
    'The {unitName} context kind is not enabled on this data source. Select {onlyAlternative} instead, or enable {unitName} on the data source.',
  'metrics.metricDefinition.unitsJoiner': 'or',
  'metrics.metricDefinition.unitsSearchPlaceholder': 'Search for units',
  'metrics.metricDefinition.unitsWithoutEventsFieldLabel':
    'Choose how to handle units that do not generate {kind, select, trace {a matching span} other {the event}}',
  'metrics.metricDefinition.unitsWithoutEventsLabel': 'Units without events',
  'metrics.metricDefinition.unitsWithoutEventsPickerLabel': 'Choose a default behavior for events',
  'metrics.metricDefinition.valueAggregationRequired': 'Event value aggregation is required',
  'metrics.metricDefinition.valueColumnHelp':
    'LaunchDarkly reads this column from each row in your data source to get that event’s numeric value, such as <code>order_amount</code>.',
  'metrics.metricDefinition.valueColumnLabel': 'Numeric value column',
  'metrics.metricDefinition.valueColumnLocked':
    'This field is set on the metric data source and can’t be changed here.',
  'metrics.metricDefinition.valueColumnRequired': "Select a column containing the event's value",
  'metrics.metricDefinition.valueNoun': '{kind, select, trace {span attribute values} other {event values}}',
  'metrics.metricDefinition.valueSentence':
    '{noun} per <units></units>, then compute the <analysisType></analysisType> of those values where <criteria></criteria>',
  'metrics.metricDefinition.valueSentenceSectioned':
    '{noun} per <units></units>, then compute the <analysisType></analysisType> <clause>of those values, where</clause><strong>{criteriaText}.</strong>',
  'metrics.metricForm.addMaintainer': 'Add maintainer',
  'metrics.metricForm.addTag': 'Add tag',
  'metrics.metricForm.dataSourceLabel': 'Metric data source',
  'metrics.metricForm.dataSourceMissing': 'First create a metric data source to measure external events',
  'metrics.metricForm.dataSourcePlaceholder': 'Select data source',
  'metrics.metricForm.dataSourceRequired': 'This field is required',
  'metrics.metricForm.descriptionLabel': 'Description',
  'metrics.metricForm.descriptionPlaceholder': 'Add a description',
  'metrics.metricForm.integrationLogoLabel': '{name} integration logo',
  'metrics.metricForm.keyExists': 'Metric key already exists',
  'metrics.metricForm.keyHelp':
    'We use the key to give you friendly URLs. Keys should be at most 256 characters and must only contain letters, numbers, <code>.</code>, <code>_</code> or <code>-</code>.',
  'metrics.metricForm.keyLabel': 'Metric key',
  'metrics.metricForm.keyPlaceholder': 'Metric key will be auto generated here',
  'metrics.metricForm.keyRequired': 'Metric key is required',
  'metrics.metricForm.keyTooLong': 'Key must be fewer than {max} characters',
  'metrics.metricForm.launchDarklyHosted': 'LaunchDarkly hosted',
  'metrics.metricForm.learnMore': 'Learn more',
  'metrics.metricForm.loadingDataSources': 'Loading data sources…',
  'metrics.metricForm.loadingDataSourcesLabel': 'Loading data sources',
  'metrics.metricForm.maintainerLabel': 'Maintainer',
  'metrics.metricForm.maintainerRequired': 'Maintainer is required',
  'metrics.metricForm.maintainersLoadError': 'Failed to load maintainers',
  'metrics.metricForm.manageTags': 'Manage tags',
  'metrics.metricForm.nameLabel': 'Metric name',
  'metrics.metricForm.namePlaceholder': 'ex: Button average click rate',
  'metrics.metricForm.nameRequired': 'Metric name is required',
  'metrics.metricForm.noDataSources': 'No metric data sources found for this project.',
  'metrics.metricForm.sourceLabel': 'Define metric using',
  'metrics.metricForm.traces': 'Traces',
  'metrics.metricForm.tryAgain': 'Try again',
  'metrics.metricForm.unitLabel': 'Unit of measure',
  'metrics.metricForm.unitPlaceholder': 'ex: bytes, ms',
  'metrics.metricForm.warehouseNative': 'Warehouse native',
  'metrics.metricForm.warehouseNativeVendor': 'Warehouse native ({vendor})',
  'metrics.metricLabel.groupTypeLabel': 'Metric group type',
  'metrics.metricStatus.never': 'Never',
  'metrics.metricTags.filterByLabel': 'Filter by {tag}',
  'metrics.metricTags.listLabel': 'Metric tags',
  'metrics.metricsList.actions': 'Actions',
  'metrics.metricsList.actionsLabel': 'Metric actions',
  'metrics.metricsList.activeCount': '{count} active ',
  'metrics.metricsList.activeExperimentsHeading': '{count, plural, one {Active experiment} other {Active experiments}}',
  'metrics.metricsList.activeRolloutsHeading':
    '{count, plural, one {Active guarded rollout} other {Active guarded rollouts}}',
  'metrics.metricsList.archive': 'Archive',
  'metrics.metricsList.archiveDisabledTooltip':
    'Archiving is only allowed if the metric has no active connections and is not part of a group or release policy.',
  'metrics.metricsList.cloneMetric': 'Clone metric',
  'metrics.metricsList.connectionCountLabel':
    '{type, select, experiment {experiment} other {rollout}} connection count details',
  'metrics.metricsList.copyKey': 'Copy key',
  'metrics.metricsList.createdAt': '{date} at {time}',
  'metrics.metricsList.createdDateLabel': 'Metric created date and maintainer',
  'metrics.metricsList.createdOnColumn': 'Created on',
  'metrics.metricsList.creationDate': 'Creation Date',
  'metrics.metricsList.eventsSeenColumn': 'Events seen',
  'metrics.metricsList.eventsSeenColumnTooltip':
    'Shows when LaunchDarkly last received an event for the metric. For filtered metrics, this reflects the latest unfiltered event.',
  'metrics.metricsList.experimentsColumn': 'Experiments',
  'metrics.metricsList.experimentsColumnTooltip':
    'Shows the number of running experiments and the total number of experiments that have used the metric.',
  'metrics.metricsList.guardedReleasesColumn': 'Guarded releases',
  'metrics.metricsList.guardedReleasesColumnTooltip':
    'Shows the number of currently monitored guarded releases and the total number of guarded rollouts that have used the metric.',
  'metrics.metricsList.keyCopied': 'Metric key copied to clipboard',
  'metrics.metricsList.kindLabel': 'metric kind',
  'metrics.metricsList.lastUsedInColumn': 'Last used in',
  'metrics.metricsList.lastUsedInColumnTooltip':
    'Shows the most recent experiment or guarded rollouts where the metric was used.',
  'metrics.metricsList.loadingLabel': 'Loading metrics',
  'metrics.metricsList.loadingValueText': 'loading metrics',
  'metrics.metricsList.maintainer': 'Maintainer',
  'metrics.metricsList.name': 'Name',
  'metrics.metricsList.noMaintainer': 'No maintainer',
  'metrics.metricsList.noneActive': 'None active',
  'metrics.metricsList.paginationSummary': '<strong>{from}–{to}</strong> of <strong>{total}</strong> metrics',
  'metrics.metricsList.sourcesColumn': 'Sources',
  'metrics.metricsList.totalConnectionsColumn': 'Total connections',
  'metrics.metricsList.totalConnectionsColumnTooltip':
    'Shows the total number of connections this metric has across metric groups, multi-armed bandits, experiments, and guarded releases.',
  'metrics.metricsList.totalExperiments':
    '{count, plural, one {{count} total experiment} other {{count} total experiments}}',
  'metrics.metricsList.totalRollouts': '{count, plural, one {{count} total rollout} other {{count} total rollouts}}',
  'metrics.metricsSearch.agentControlCategory': 'AgentControl',
  'metrics.metricsSearch.allMetricsCategory': 'All metrics',
  'metrics.metricsSearch.autogeneratedCategory': 'Autogenerated',
  'metrics.metricsSearch.createFormLoadError': 'An unexpected error occurred while loading the create metric form.',
  'metrics.metricsSearch.currentlySelectedCategory': 'Currently selected',
  'metrics.metricsSearch.dismiss': 'Dismiss',
  'metrics.metricsSearch.funnelGroupsCategory': 'Funnel groups',
  'metrics.metricsSearch.loadError': 'An unexpected error occurred while loading the metrics search.',
  'metrics.metricsSearch.loadingDetailsLabel': 'Loading metric details',
  'metrics.metricsSearch.metricGroupsSection': 'Metric groups',
  'metrics.metricsSearch.metricsSection': 'Metrics',
  'metrics.metricsSearch.mostUsedCategory': 'Most used',
  'metrics.metricsSearch.noMatchesIn': "No matches in ''{category}''",
  'metrics.metricsSearch.notSeenRecently': 'Not seen recently',
  'metrics.metricsSearch.recentlyAddedCategory': 'Recently added',
  'metrics.metricsSearch.releaseGuardrailsCategory': 'Release guardrails',
  'metrics.metricsSearch.relevantMatchesIn': "Relevant matches in ''{category}''",
  'metrics.metricsSearch.relevantMatchesInAll': "Relevant matches in ''All metrics''",
  'metrics.metricsSearch.resultsLabel': 'Search results',
  'metrics.metricsSearch.searchPlaceholder': 'Search metrics',
  'metrics.metricsSearch.selectLabel': 'Select metrics',
  'metrics.metricsSearch.standardGroupsCategory': 'Standard groups',
  'metrics.metricsSearch.triggerButton': 'Select metrics',
  'metrics.metricsSearch.userDefinedCategory': 'User-defined',
  'metrics.metricsSearch.warehouseNativeCategory': 'Warehouse native',
  'metrics.metricsTable.tableLabel': 'Metrics table',
  'metrics.page.label': 'search metrics',
  'metrics.productAnalyticsLink.analyzeMetric': 'Analyze metric',
  'metrics.productAnalyticsLink.unsupported': 'This metric is not supported in Product Analytics',
  'metrics.restoreMetric.archivedOn':
    'This metric was archived on {date}. Existing connections remain unaffected and all historical metric data is still available.',
  'metrics.restoreMetric.body':
    'This will restore <strong>{name}</strong> making it available for use in experiments and guarded rollouts.',
  'metrics.restoreMetric.confirmHeading': 'Restore "{name}"',
  'metrics.restoreMetric.contactAdmin': 'Contact your account admin.',
  'metrics.restoreMetric.failed': "Metric ''{key}'' restore failed: {message}",
  'metrics.restoreMetric.noPermission': "You don't have permission to do this action.",
  'metrics.restoreMetric.restore': 'Restore',
  'metrics.restoreMetric.restoreLabel': 'Restore metric',
  'metrics.restoreMetric.success': 'Metric restored',
  'metrics.searchDetailsPanel.copyEventKey': 'Copy event key',
  'metrics.searchDetailsPanel.copyEventKeyLabel': 'Copy {eventKey} to your clipboard.',
  'metrics.searchDetailsPanel.definition': 'Definition',
  'metrics.searchDetailsPanel.description': 'Description',
  'metrics.searchDetailsPanel.eventFiltersApplied': 'Event filters applied',
  'metrics.searchDetailsPanel.eventKey': 'Event key',
  'metrics.searchDetailsPanel.exploreEvent': 'Explore event',
  'metrics.searchDetailsPanel.groupLoadError':
    "An unexpected error occurred while loading this metric group's details.",
  'metrics.searchDetailsPanel.loadError': 'Failed to load metric details',
  'metrics.searchDetailsPanel.loadingLabel': 'Loading metric details',
  'metrics.searchDetailsPanel.measurementWindow': 'Measurement window',
  'metrics.searchDetailsPanel.metricStructure': 'Metric structure',
  'metrics.searchDetailsPanel.metricsInGroup': 'Metrics in group',
  'metrics.searchDetailsPanel.none': 'None',
  'metrics.searchDetailsPanel.notSeenRecently': 'Not seen recently',
  'metrics.searchDetailsPanel.ratioStructure': 'Ratio',
  'metrics.searchDetailsPanel.releasePolicies': 'Release policies',
  'metrics.searchDetailsPanel.simpleStructure': 'Simple',
  'metrics.searchDetailsPanel.source': 'Source',
  'metrics.searchDetailsPanel.successCriteria': 'Success criteria',
  'metrics.searchDetailsPanel.unavailableForSelection': 'Unavailable for selection',
  'metrics.searchDetailsPanel.unitsWithout': 'Units without {kind, select, trace {spans} other {events}}',
  'metrics.searchDetailsPanel.viewGroupLabel': 'View metric group details',
  'metrics.searchDetailsPanel.viewMetricLabel': 'View metric details',
  'metrics.searchDetailsPanel.winsorization': 'Winsorization',
  'metrics.searchEmptyState.allMetrics': 'All Metrics',
  'metrics.searchEmptyState.createMetric': 'Create metric',
  'metrics.searchEmptyState.createMetricDataSource': 'Create metric data source',
  'metrics.searchEmptyState.createMetricGroup': 'Create metric group',
  'metrics.searchEmptyState.loadingDataSourcesLabel': 'Loading metric data sources',
  'metrics.searchEmptyState.metricGroups': 'Metric Groups',
  'metrics.searchEmptyState.noAgentControlBody':
    'LaunchDarkly generates these metrics from AI SDK events. <link>Learn more</link>',
  'metrics.searchEmptyState.noAgentControlHeading': "This project doesn't have any AgentControl metrics.",
  'metrics.searchEmptyState.noAutogeneratedBody':
    'LaunchDarkly automatically generates metrics from select metric event sources. <link>Learn more</link>',
  'metrics.searchEmptyState.noAutogeneratedHeading': "This project doesn't have any autogenerated metrics.",
  'metrics.searchEmptyState.noGroupsBody': 'Use metric groups to organize metrics and run experiments',
  'metrics.searchEmptyState.noGroupsFound': 'No metric groups found.',
  'metrics.searchEmptyState.noGroupsHeading': "This project doesn't have any metric groups.",
  'metrics.searchEmptyState.noGuardrailsBody':
    'Metrics added to a release policy get automatically added to all guarded rollouts across environments',
  'metrics.searchEmptyState.noGuardrailsHeading': 'No release guardrail metrics found.',
  'metrics.searchEmptyState.noMetricDataSources':
    'No metric data sources found. Create one to connect your warehouse metric data.',
  'metrics.searchEmptyState.noMetricsBody':
    'Use metrics to measure audience<br></br>behaviors affected by flags and experiments',
  'metrics.searchEmptyState.noMetricsFound': 'No metrics found.',
  'metrics.searchEmptyState.noMetricsHeading': "This project doesn't have any metrics.",
  'metrics.searchEmptyState.noUserDefinedHeading': 'This project has no user-defined metrics.',
  'metrics.searchEmptyState.noWarehouseMetricsBody':
    'Create a metric from your warehouse data source to use warehouse data in experiments. <link>Learn more</link>',
  'metrics.searchEmptyState.noWarehouseMetricsHeading': "This project doesn't have any warehouse native metrics.",
  'metrics.searchEmptyState.noneSelectedBody': 'Select metrics or metric groups from other categories to see them here',
  'metrics.searchEmptyState.noneSelectedHeading': 'No metrics or metric groups selected',
  'metrics.searchEmptyState.trySearchingIn': 'Try searching in',
  'metrics.selectedMetricsTable.actions': 'Actions',
  'metrics.selectedMetricsTable.addMetricsAbove': 'Add metrics above',
  'metrics.selectedMetricsTable.analysisUnitColumn': 'Analysis unit',
  'metrics.selectedMetricsTable.autoRollback': 'Auto rollback',
  'metrics.selectedMetricsTable.metricNameColumn': 'Metric name',
  'metrics.selectedMetricsTable.setAsPrimary': 'Set as primary',
  'metrics.sidebar.copyLink': 'Copy link to this metric',
  'metrics.sidebar.copyMetricKey': 'Copy metric key',
  'metrics.sidebar.copyMetricKeyLabel': 'Copy {metricKey} to your clipboard.',
  'metrics.sidebar.description': 'Description',
  'metrics.sidebar.editMetricLabel': 'Edit metric',
  'metrics.sidebar.healthCheck': 'Health check',
  'metrics.sidebar.maintainer': 'Maintainer',
  'metrics.sidebar.metricKey': 'Metric key',
  'metrics.sidebar.metricStructure': 'Metric structure',
  'metrics.sidebar.noTags': 'None',
  'metrics.sidebar.ratio': 'Ratio',
  'metrics.sidebar.simple': 'Simple',
  'metrics.sidebar.sources': 'Sources',
  'metrics.sidebar.tags': 'Tags',
  'metrics.sidebar.tagsLabel': 'metric tags',
  'metrics.sidebar.versionNumber': 'Version number',
  'metrics.sourceTags.dataSourceLabel': 'Data source',
  'metrics.sourceTags.releaseGuardrail': 'Release guardrail',
  'metrics.sourceTags.snowflake': 'Snowflake',
  'metrics.sourceTags.versionLabel': 'Version {version}',
  'metrics.spanPreview.beta': 'Beta',
  'metrics.spanPreview.changeTimeRange': 'Change time range',
  'metrics.spanPreview.collapseLabel': 'Collapse preview',
  'metrics.spanPreview.disclaimer':
    'Preview results may differ from spans returned in guarded releases and experiments.',
  'metrics.spanPreview.environments': 'Environments',
  'metrics.spanPreview.expandLabel': 'Expand preview',
  'metrics.spanPreview.expandSpanSample': 'Expand span sample',
  'metrics.spanPreview.featureStatusLabel': 'Feature status',
  'metrics.spanPreview.filterToEnvironment': 'Filter to this environment',
  'metrics.spanPreview.filterToSpan': 'Filter to this span',
  'metrics.spanPreview.loadMore': 'Load more',
  'metrics.spanPreview.loadingLabel': 'Loading preview',
  'metrics.spanPreview.loadingSampleForLabel': 'Loading sample trace for {spanName}',
  'metrics.spanPreview.loadingSampleLabel': 'Loading sample trace',
  'metrics.spanPreview.noSampleTrace': 'No sample trace available.',
  'metrics.spanPreview.noTraceData': 'No trace data available to preview.',
  'metrics.spanPreview.previewMatchingSpans': 'Preview matching spans',
  'metrics.spanPreview.span': 'Span',
  'metrics.spanPreview.spanCountSeries': 'Span count',
  'metrics.spanPreview.timeRangeLabel': 'Chart time range',
  'metrics.spanPreview.total': 'Total',
  'metrics.spanQuery.anySpan': 'Any span',
  'metrics.spanQuery.anySpan2': 'Any span 2',
  'metrics.spanQuery.anySpan2Description':
    'Second independent any span condition. Use when you need multiple separate span filters on the same trace.',
  'metrics.spanQuery.anySpan3': 'Any span 3',
  'metrics.spanQuery.anySpan3Description':
    'Third independent any span condition. Use when you need multiple separate span filters on the same trace.',
  'metrics.spanQuery.anySpanDescription': 'Match spans where any span in the same trace satisfies the filter.',
  'metrics.spanQuery.noSpan': 'No span',
  'metrics.spanQuery.noSpanDescription': 'Match spans where no span in the same trace satisfies the filter.',
  'metrics.spanQuery.required': 'Span query is required',
  'metrics.spanQuery.spansLabel': 'Filter to spans',
  'metrics.spanQuery.viewTraces': 'View traces',
  'metrics.status.loading': 'Loading…',
  'metrics.targetUrls.absoluteUrlRequired': 'Must be an absolute URL',
  'metrics.targetUrls.addTargetUrl': 'Add target URL',
  'metrics.targetUrls.addUrlLabel': 'Add URL',
  'metrics.targetUrls.exactMatch': 'Exact match',
  'metrics.targetUrls.exactMatchHint':
    'Use "exact match" to target a single URL that specifies a query or hash parameter',
  'metrics.targetUrls.regularExpression': 'Regular expression',
  'metrics.targetUrls.regularExpressionHint':
    'Use "regular expression" to target all URLs that match a regular expression',
  'metrics.targetUrls.removeUrlLabel': 'Remove URL {number}',
  'metrics.targetUrls.required': 'This field is required',
  'metrics.targetUrls.requiredShort': 'Required',
  'metrics.targetUrls.simpleMatch': 'Simple match',
  'metrics.targetUrls.simpleMatchHint':
    'Use "simple match" to target a single URL that does not include query or hash parameters',
  'metrics.targetUrls.substringMatch': 'Substring match',
  'metrics.targetUrls.substringMatchHint':
    'Use "substring match" to target all URLs that contain a specific string of text',
  'metrics.targetUrls.typeLabel': 'Target Type',
  'metrics.targetUrls.urlGroupLabel': 'Target URL {number}',
  'metrics.targetUrls.urlLabel': 'Target URL',
  'metrics.targetUrls.urlPlaceholder': 'Enter URL',
  'metrics.winsorization.boundAboveHundred':
    '{bound, select, Lower {Lower} other {Upper}} bound cannot be greater than 100',
  'metrics.winsorization.boundBelowZero': '{bound, select, Lower {Lower} other {Upper}} bound cannot be less than 0',
  'metrics.winsorization.boundRequired': '{bound, select, Lower {Lower} other {Upper}} bound is required',
  'metrics.winsorization.boundsHelp': 'Outlier values are clamped to percentile bounds before metric calculation.',
  'metrics.winsorization.description':
    'Cap outlier values at specified percentiles to reduce the impact of extreme measurements on experiment results.',
  'metrics.winsorization.enableLabel': 'Enable winsorization',
  'metrics.winsorization.lowerAboveUpper': 'Lower bound must be less than upper bound',
  'metrics.winsorization.lowerBound': 'Lower bound',
  'metrics.winsorization.removeLabel': 'Remove winsorization',
  'metrics.winsorization.title': 'Winsorization',
  'metrics.winsorization.upperBelowLower': 'Upper bound must be greater than lower bound',
  'metrics.winsorization.upperBound': 'Upper bound',
  'metrics_.page.title': 'Metrics',
  'mfa.enableMfa.cantScan': "Can't scan the QR code?",
  'mfa.enableMfa.codeLabel': 'Enter the six-digit code from the application',
  'mfa.enableMfa.complete': 'Complete',
  'mfa.enableMfa.continue': 'Continue',
  'mfa.enableMfa.enabledHeading': 'MFA enabled',
  'mfa.enableMfa.invalidCode': 'Invalid code. Try again.',
  'mfa.enableMfa.qrCodeLabel': 'QR Code',
  'mfa.enableMfa.recoveryCodesBody':
    '<strong>Store these recovery codes in a secure location</strong> in case your MFA device is lost or stolen.',
  'mfa.enableMfa.scanBody': 'Scan this QR code with your <link>authenticator app</link>:',
  'mfa.enableMfa.secretKeyLabel': 'Use this secret key instead:',
  'mfa.enableMfa.verificationError': 'Verification failed. Try again, or refresh the page to generate a new QR code.',
  'mfa.mfaPrompt.cancel': 'Cancel',
  'mfa.mfaPrompt.lastRecoveryCodeBody':
    "You've used your last recovery code. Reset your multi-factor authentication credentials.",
  'mfa.mfaPrompt.timedOutHeading': 'Authentication timed out',
  'mfa.profileMfa.disable': 'Disable MFA',
  'mfa.profileMfa.disableConfirmBody': 'This reduces the security of your account and is not recommended',
  'mfa.profileMfa.disableConfirmLabel': 'Disable',
  'mfa.profileMfa.disableConfirmTitle': 'Disable multi-factor authentication?',
  'mfa.profileMfa.enable': 'Enable MFA',
  'mfa.profileMfa.lastRecoveryCodeBody': "You've used your last recovery code.",
  'mfa.profileMfa.reset': 'Reset MFA',
  'mfa.profileMfa.resetBody': 'Reset your multi-factor authentication credentials.',
  'mfa.profileMfa.statusOff': 'Status: <value>Off</value>',
  'mfa.profileMfa.statusOn': 'Status: <value>On</value>',
  'mfa.profileMfa.verifyEmailTooltip': 'Verify your email to use this feature.',
  'mfa.recoveryForm.body':
    'Enter your recovery code to verify your identity. If you have lost your recovery code you will need to contact your account owner.',
  'mfa.recoveryForm.codeLabel': 'Recovery code',
  'mfa.recoveryForm.verify': 'Verify recovery code',
  'mfa.recoveryForm.verifyingText': 'Verifying recovery code',
  'mfa.status.loading': 'Loading…',
  'mfa.verificationForm.body':
    'Enter the code from the multi-factor authentication app on your device to verify your identity.',
  'mfa.verificationForm.codeLabel': 'Authentication code',
  'mfa.verificationForm.verify': 'Verify authentication code',
  'mfa.verificationForm.verifyingText': 'Verifying authentication code',
  'migrations.flagMetrics.consistencyRateHeading': 'Consistency rate',
  'migrations.flagMetrics.errorRateHeading': 'Error rate',
  'migrations.flagMetrics.latencyHeading': 'Latency',
  'migrations.flagMetrics.loadError': 'Could not load metrics at this time',
  'migrations.flagMetrics.noData': 'No data received yet',
  'migrations.metricsChart.flagChangesLabel': 'Flag changes',
  'migrations.metricsFooter.last7Days': 'Last 7 days',
  'migrations.metricsFooter.loadError': 'Could not load metrics at this time',
  'migrations.metricsFooter.loadingLabel': 'Loading…',
  'migrations.metricsFooter.noData': 'No data received yet',
  'migrations.rollout.bucketBy': 'By <code>{contextKind}</code> <code>{bucketBy}</code>',
  'migrations.rolloutEdit.decreaseWeightLabel': 'decrease rollout weight',
  'migrations.rolloutEdit.increaseWeightLabel': 'increase rollout weight',
  'migrations.rolloutEdit.weightLabel': 'Variation {variation} rollout weight',
  'migrations.safetyIssues.actionEditNewReadRule': 'Editing cohort',
  'migrations.safetyIssues.actionEditOldOnlyReadRule': 'Editing cohort',
  'migrations.safetyIssues.actionReorderRuleDown': 'Reordering cohort',
  'migrations.safetyIssues.actionReorderRuleUp': 'Reordering cohort',
  'migrations.safetyIssues.actionRuleAdded': 'Adding cohort',
  'migrations.safetyIssues.actionRuleRemoved': 'Removing cohort',
  'migrations.safetyIssues.backfillNewSystem':
    '<b>{affectedRules}</b> will have new traffic reading from the new system. Ensure that you have backfilled the new system.',
  'migrations.safetyIssues.backfillOldSystem':
    '<b>{affectedRules}</b> will have new traffic reading from the old system. Ensure that you have backfilled the old system.',
  'migrations.safetyIssues.dualwriteSkipped':
    '<b>{affectedRules}</b> will have new traffic reading from the new system before writes have occurred in that system.',
  'migrations.safetyIssues.editNewReadRule':
    '<b>{causingRule}</b> may cause traffic from <b>{affectedRules}</b> to go to <b>{causingRule}</b>.',
  'migrations.safetyIssues.editOldOnlyReadRule':
    '<b>{causingRule}</b>, may cause traffic to enter <b>{affectedRules}</b>.',
  'migrations.safetyIssues.heading': 'This change may cause data integrity issues',
  'migrations.safetyIssues.loadingLabel': 'Loading…',
  'migrations.safetyIssues.reorderRuleDown': '<b>{causingRule}</b> may cause traffic to enter <b>{affectedRules}</b>.',
  'migrations.safetyIssues.reorderRuleUp':
    '<b>{causingRule}</b> may cause traffic from <b>{affectedRules}</b> to go to <b>{causingRule}</b>.',
  'migrations.safetyIssues.ruleAddedNewSystem':
    '<b>{causingRule}</b> may cause traffic from <b>{affectedRules}</b> to go to <b>{causingRule}</b>, which may read from the new system before the data is consistent with the old system.',
  'migrations.safetyIssues.ruleAddedOldSystem':
    '<b>{causingRule}</b> may cause traffic from <b>{affectedRules}</b> to go to <b>{causingRule}</b>, which may read from the old system after the migration has completed for that traffic.',
  'migrations.safetyIssues.ruleRemoved': '<b>{causingRule}</b> may cause traffic to enter <b>{affectedRules}</b>.',
  'migrations.safetyIssues.trafficReadsNewSystem':
    'This traffic may read from the new system before data is consistent with the old system.',
  'migrations.safetyIssues.trafficReadsOldSystem':
    'This traffic may read from the old system after the migration has completed for that traffic.',
  'multiArmedBandits.dashboardListPage.createMultiArmed': 'Create multi-armed bandit',
  'multiArmedBandits.dashboardListPage.tooltip': 'You do not have permission to create a multi-armed bandit',
  'multiArmedBandits.dashboardPage.connectAction':
    'Connect your SDK to measure and understand the impact of every feature you release.',
  'multiArmedBandits.dashboardPage.label': 'Search for a multi-armed bandit by name',
  'multiArmedBandits.dashboardPage.loading': 'Loading…',
  'multiArmedBandits.dashboardPage.placeholder': 'Search by name',
  'multiArmedBandits.dashboardPage.totalCount': '<strong>{count}</strong> multi-armed bandits',
  'multiArmedBandits.detailPage.design': 'Design',
  'multiArmedBandits.detailPage.exposures': 'Exposures',
  'multiArmedBandits.detailPage.iterations': 'Iterations',
  'multiArmedBandits.detailPage.results': 'Results',
  'multiArmedBandits.iterationsPage.errorLoadingIteration': 'Error loading iteration results',
  'multiArmedBandits.newPage.title': 'Create Multi-armed bandit',
  'multiArmedBandits.notifications.archived': 'Multi-armed bandit archived',
  'multiArmedBandits.notifications.restored': 'Multi-armed bandit restored',
  'multiArmedBandits.page.title': 'Multi-armed bandits',
  'observability.adaptiveTriggers.adaptiveTriggers': 'Adaptive triggers',
  'observability.adaptiveTriggers.viewResource': '{isAIConfig, select, true {View config} other {View flag}}',
  'observability.adaptiveTriggers.whenThisAlert':
    'When this alert fires, the following flag actions will execute automatically.',
  'observability.adaptiveTriggers.willServe': 'will serve',
  'observability.advancedSearchForm.addFunction': 'Add function',
  'observability.advancedSearchForm.advancedQuery': 'Options',
  'observability.advancedSearchForm.bucketByLabel': 'Bucket by',
  'observability.advancedSearchForm.bucketByTooltip':
    'The method for determining the bucket sizes - can be a fixed interval or fixed count.',
  'observability.advancedSearchForm.bucketIntervalLabel': 'Bucket interval',
  'observability.advancedSearchForm.bucketIntervalTooltip': 'The time interval for each bucket.',
  'observability.advancedSearchForm.functionLabel': 'Function',
  'observability.advancedSearchForm.functionTooltip':
    'Determines how data points are aggregated. If the function requires a numeric field as input, one can be chosen.',
  'observability.advancedSearchForm.groupByLabel': 'Group by',
  'observability.advancedSearchForm.groupByTooltip': 'A categorical field for grouping results into separate series.',
  'observability.advancedSearchForm.help': 'Help',
  'observability.advancedSearchForm.limitByLabel': 'By',
  'observability.advancedSearchForm.limitByTooltip': 'The function used to determine which groups are included.',
  'observability.advancedSearchForm.limitLabel': 'Limit',
  'observability.advancedSearchForm.limitPlaceholder': 'Enter limit',
  'observability.advancedSearchForm.limitTooltip':
    'The maximum number of groups to include. Currently, the max is 100.',
  'observability.advancedSearchForm.timeBucketsLabel': 'Time buckets',
  'observability.advancedSearchForm.timeBucketsPlaceholder': 'Enter bucket count',
  'observability.advancedSearchForm.timeBucketsTooltip':
    'The number of time buckets on the X-axis. A higher value will display smaller, more granular buckets. Currently, the max is 100.',
  'observability.advancedSearchGraph.addToDashboard': 'Add to dashboard',
  'observability.advancedSearchGraph.barChart': 'Bar chart',
  'observability.advancedSearchGraph.createAlert': 'Create alert',
  'observability.advancedSearchGraph.downloadCsv': 'Download CSV',
  'observability.advancedSearchGraph.exportCompletedBody': 'Graph "{title}" has been exported',
  'observability.advancedSearchGraph.exportCompletedToast': 'CSV export completed',
  'observability.advancedSearchGraph.exportFailedBody': 'There was an error exporting the graph data',
  'observability.advancedSearchGraph.exportFailedToast': 'CSV export failed',
  'observability.advancedSearchGraph.lineChart': 'Line chart',
  'observability.advancedSearchGraph.noDataToExportBody': 'The graph has no data available for export',
  'observability.advancedSearchGraph.noDataToExportToast': 'No data to export',
  'observability.advancedSearchGraph.type': 'Type',
  'observability.advancedSearchGraph.writeAQuery': 'Write a query to see data',
  'observability.agentInsightsCodeSearch.noCodeSearch': 'No code search results',
  'observability.agentInsightsDashboards.chartAddedTo': 'Chart Added to Dashboard',
  'observability.agentInsightsDashboards.chartsHeading': 'Charts ({count}):',
  'observability.agentInsightsDashboards.dashboardCreated': 'Dashboard Created',
  'observability.agentInsightsDashboards.dashboardId': 'Dashboard ID: {dashboardId}',
  'observability.agentInsightsDashboards.listHeading': 'Dashboards ({count})',
  'observability.agentInsightsDashboards.noDashboardData': 'No dashboard data found',
  'observability.agentInsightsDashboards.noDashboardsFound': 'No dashboards found',
  'observability.agentInsightsDashboards.openDashboardLabel': 'Open dashboard in new tab',
  'observability.agentInsightsDashboards.timeRange': 'Time range:',
  'observability.agentInsightsDashboards.unknownDashboardData': 'Unknown dashboard data format',
  'observability.agentInsightsDashboards.updated': 'Updated {date}',
  'observability.agentInsightsEnvironments.critical': 'Critical',
  'observability.agentInsightsEnvironments.emptyState': 'No critical environments found',
  'observability.agentInsightsEnvironments.emptyStateWithTotal':
    'No critical environments found ({count} total environments)',
  'observability.agentInsightsEnvironments.environment': 'Environment',
  'observability.agentInsightsEnvironments.heading': 'Critical environments',
  'observability.agentInsightsEnvironments.headingWithProject': 'Critical environments · {projectKey}',
  'observability.agentInsightsEnvironments.status': 'Status',
  'observability.agentInsightsErrors.errorDetails': 'Error Details',
  'observability.agentInsightsErrors.errorType': 'Error Type',
  'observability.agentInsightsErrors.message': 'Message',
  'observability.agentInsightsErrors.noErrorsFound': 'No errors found',
  'observability.agentInsightsErrors.openErrorGroupLabel': 'Open error group in new tab',
  'observability.agentInsightsErrors.openThisQuery': 'Open this query in the Errors page',
  'observability.agentInsightsErrors.timestamp': 'Timestamp',
  'observability.agentInsightsFlagDetails.description': 'Description',
  'observability.agentInsightsFlagDetails.key': 'Key',
  'observability.agentInsightsFlagDetails.kind': 'Kind',
  'observability.agentInsightsFlagDetails.tags': 'Tags',
  'observability.agentInsightsFlagDetails.variationIndex': 'Variation {index}',
  'observability.agentInsightsFlagDetails.variations': 'Variations',
  'observability.agentInsightsFlagEvaluations.evaluationTime': 'Evaluation time',
  'observability.agentInsightsFlagEvaluations.flagEvaluations': 'Flag Evaluations',
  'observability.agentInsightsFlagEvaluations.flagKey': 'Flag key',
  'observability.agentInsightsFlagEvaluations.flagValue': 'Flag value',
  'observability.agentInsightsFlagEvaluations.noFlagEvaluations': 'No flag evaluations found',
  'observability.agentInsightsFlagVariations.evaluations': 'Evaluations',
  'observability.agentInsightsFlagVariations.heading': 'Flag evaluations · {flagKey}',
  'observability.agentInsightsFlagVariations.headingWithEnvironment': 'Flag evaluations · {flagKey} · {environmentKey}',
  'observability.agentInsightsFlagVariations.noEvaluationData': 'No evaluation data for {flagKey}',
  'observability.agentInsightsFlagVariations.noEvaluationDataInEnvironment':
    'No evaluation data for {flagKey} in {environmentKey}',
  'observability.agentInsightsFlagVariations.noVariationData': 'No variation data for {flagKey}',
  'observability.agentInsightsFlagVariations.noVariationDataInEnvironment':
    'No variation data for {flagKey} in {environmentKey}',
  'observability.agentInsightsFlagVariations.variation': 'Variation',
  'observability.agentInsightsGraphPreview.noDataAvailable': 'No data available for preview',
  'observability.agentInsightsGraphPreview.noPreviewData': 'No preview data available',
  'observability.agentInsightsGraphPreview.noTableData': 'No table data available',
  'observability.agentInsightsGraphPreview.openInEditor': 'Open in editor',
  'observability.agentInsightsGraphPreview.openInEditorLabel': 'Open in graph editor',
  'observability.agentInsightsKeys.emptyState': 'No keys found for {productType}',
  'observability.agentInsightsKeys.heading': 'Available Keys for {productType} ({count})',
  'observability.agentInsightsLogs.logDetails': 'Log Details',
  'observability.agentInsightsLogs.noLogsFound': 'No logs found',
  'observability.agentInsightsLogs.openThisQuery': 'Open this query in the Logs page',
  'observability.agentInsightsMetrics.dataPointSummary':
    '{count, plural, one {# data point} other {# data points}} • {seriesCount} series',
  'observability.agentInsightsMetrics.metricsVisualization': 'Metrics Visualization',
  'observability.agentInsightsMetrics.noDataLabel': 'No metrics data found',
  'observability.agentInsightsPullRequest.additions': 'Additions:',
  'observability.agentInsightsPullRequest.deletions': 'Deletions:',
  'observability.agentInsightsPullRequest.description': 'Description:',
  'observability.agentInsightsPullRequest.files': 'Files:',
  'observability.agentInsightsPullRequest.heading': 'Pull Request #{number}',
  'observability.agentInsightsPullRequest.noPullRequest': 'No pull request data found',
  'observability.agentInsightsPullRequestSearch.heading':
    'Found {count, plural, one {# pull request} other {# pull requests}}',
  'observability.agentInsightsPullRequestSearch.noPullRequests': 'No pull requests found',
  'observability.agentInsightsRepositories.created': 'Created',
  'observability.agentInsightsRepositories.language': 'Language',
  'observability.agentInsightsRepositories.noRepositoriesFound': 'No repositories found',
  'observability.agentInsightsRepositories.repository': 'Repository',
  'observability.agentInsightsRepositories.repositoryDetails': 'Repository Details',
  'observability.agentInsightsRepositories.updated': 'Updated',
  'observability.agentInsightsSessions.environment': 'Environment',
  'observability.agentInsightsSessions.noSessionsFound': 'No sessions found',
  'observability.agentInsightsSessions.openSessionLabel': 'Open session in new tab',
  'observability.agentInsightsSessions.openThisQuery': 'Open this query in the Sessions page',
  'observability.agentInsightsSessions.sessionDetails': 'Session Details',
  'observability.agentInsightsSessions.timestamp': 'Timestamp',
  'observability.agentInsightsSessions.user': 'User',
  'observability.agentInsightsTimeline.description': 'Description',
  'observability.agentInsightsTimeline.event': 'Event',
  'observability.agentInsightsTimeline.noPayloadData': 'No payload data available',
  'observability.agentInsightsTimeline.noTimelineEvents': 'No timeline events found',
  'observability.agentInsightsTimeline.openSessionAtTimestampLabel': 'Open session at this timestamp in new tab',
  'observability.agentInsightsTimeline.timelineEvents': 'Timeline Events',
  'observability.agentInsightsTimeline.timestamp': 'Timestamp',
  'observability.agentInsightsToolOutput.truncated':
    'Output too large to display in full ({sizeLabel}). The agent has access to the entire response.',
  'observability.agentInsightsTraces.noTracesFound': 'No traces found',
  'observability.agentInsightsTraces.openThisQuery': 'Open this query in the Traces page',
  'observability.agentInsightsTraces.traceDetails': 'Trace Details',
  'observability.aiAccessDialog.cancel': 'Cancel',
  'observability.aiAccessDialog.close': 'Close',
  'observability.aiAccessDialog.closeLabel': 'Close modal',
  'observability.aiAccessDialog.enableBody':
    'To use AI-powered {product}, you need to enable generative AI use cases in your security settings.',
  'observability.aiAccessDialog.enableTitle': 'Enable AI Features',
  'observability.aiAccessDialog.goToSettings': 'Go to Settings',
  'observability.aiAccessDialog.requestAccessBody':
    "Vega isn't enabled for your account yet. Reach out to your LaunchDarkly account team to ask about getting access to Vega. Note that enabling Vega requires an additional order form to accept LaunchDarkly's AI feature terms.",
  'observability.aiAccessDialog.requestAccessTitle': 'Get access to AI features',
  'observability.aiAccessDialog.vegaEligibility': 'Vega eligibility',
  'observability.aiConfigOverlayPicker.allConfigs': 'All configs',
  'observability.aiConfigOverlayPicker.itemDescription': '{configKey} · Last updated {lastUpdated}',
  'observability.aiConfigOverlayPicker.maxSelected': 'Maximum {count} configs selected',
  'observability.aiConfigOverlayPicker.neverModified': 'Never modified',
  'observability.aiConfigOverlayPicker.quickSelectLabel': 'Quick selection options',
  'observability.aiConfigOverlayPicker.searchLabel': 'Search configs',
  'observability.aiConfigOverlayPicker.searchPlaceholder': 'Search configs…',
  'observability.aiConfigOverlayPicker.triggerAll': 'Configs: All',
  'observability.aiConfigOverlayPicker.triggerCount': 'Configs: {count} selected',
  'observability.aiConfigOverlayPicker.triggerLabel': 'Select configs for overlay',
  'observability.aiConfigOverlayPicker.triggerOne': 'Configs: {configName}',
  'observability.aiErrorSuggestion.body':
    'Investigate this error with the help of {botName}. Get a head start on finding the root cause and potential fixes.',
  'observability.aiErrorSuggestion.enable': 'Enable {botName}',
  'observability.aiErrorSuggestion.getAccess': 'Get access to {botName}',
  'observability.aiErrorSuggestion.heading': 'Investigate with {botName}',
  'observability.aiErrorSuggestion.openChat': 'Open {botName} chat',
  'observability.aiErrorSuggestion.product': 'error suggestions',
  'observability.alertActionsMenu.alertsWithActive': 'Alerts with active adaptive triggers cannot be disabled',
  'observability.alertActionsMenu.editAlert': 'Edit alert',
  'observability.alertActionsMenu.menuLabel': 'Alert actions',
  'observability.alertActionsMenu.toggleAlert': '{disabled, select, true {Enable alert} other {Disable alert}}',
  'observability.alertAutoRemediation.agentModeLabel': 'Agent mode',
  'observability.alertAutoRemediation.agentModeTooltip':
    'Read-only: Analyzes your observability data to find the underlying issue. Agent: Analyzes issues and fixes them by creating GitHub pull requests.',
  'observability.alertAutoRemediation.cooldownLabel': 'Investigation cooldown',
  'observability.alertAutoRemediation.cooldownTooltip':
    'The time period after an auto-investigation is triggered before a new one can be triggered.',
  'observability.alertAutoRemediation.customContextLabel': 'Custom context',
  'observability.alertAutoRemediation.customContextPlaceholder':
    'e.g., Focus on database connection pool exhaustion. Check recent deploy commits for connection handling changes…',
  'observability.alertAutoRemediation.customContextTooltip':
    'Optional context or instructions to include when Vega investigates this alert. This is prepended to the auto-generated investigation prompt.',
  'observability.alertAutoRemediation.disclosure':
    'All vega results are visible to project members and will be sent to slack if configured. Selected repositories may be cloned, and these repo details may appear in vega results. <link>Learn more</link>',
  'observability.alertAutoRemediation.enableInfo': 'Automatically run {botName} investigation when this alert fires',
  'observability.alertAutoRemediation.enableLabel': 'Auto remediation',
  'observability.alertAutoRemediation.modeAgent': 'Agent',
  'observability.alertAutoRemediation.modeReadOnly': 'Read-only',
  'observability.alertAutoRemediation.reposLabel': 'Repos',
  'observability.alertAutoRemediation.reposTooltip':
    'Select which GitHub repositories the AI agent can access when fixing issues.',
  'observability.alertDetails.activeAlertsLabel': 'Active alerts',
  'observability.alertDetails.alertHistory': 'Alert history',
  'observability.alertDetails.alertsInTimeframeLabel': 'Alerts in timeframe',
  'observability.alertDetails.cancel': 'Cancel',
  'observability.alertDetails.deleteAlert': 'Delete alert',
  'observability.alertDetails.deleteConfirmBody':
    '{kind, select, sentry_monitor {This deletes the alert. The Monitor in Sentry is not changed.} other {This deletes the alert. The external monitor is not changed.}}',
  'observability.alertDetails.deleteConfirmTitle': 'Delete alert?',
  'observability.alertDetails.edit': 'Edit',
  'observability.alertDetails.groupByColumn': 'Group by',
  'observability.alertDetails.historyEmptyTitle': 'No alert notifications found',
  'observability.alertDetails.historyLoadErrorBody':
    'There was an error loading your alerts. Reach out to us if this might be a bug.',
  'observability.alertDetails.historyLoadErrorTitle': 'Failed to load alerts',
  'observability.alertDetails.historyTableLabel': 'Alert history',
  'observability.alertDetails.lastAlertedLabel': 'Last alerted',
  'observability.alertDetails.linkCopied': 'Copied link',
  'observability.alertDetails.reloadAlerts': 'Reload alerts',
  'observability.alertDetails.testAlert': '{loading, select, true {Sending…} other {Send test alert}}',
  'observability.alertDetails.testAlertFailed': 'Failed to send test alert',
  'observability.alertDetails.testAlertSent': 'Test alert sent',
  'observability.alertDetails.timeColumn': 'Time',
  'observability.alertDetails.viewProduct': 'View {productType}',
  'observability.alertDetails.viewRemediation': 'View remediation',
  'observability.alertEnableToggle.disabledTooltip': 'Alerts with active adaptive triggers cannot be disabled',
  'observability.alertEnableToggle.state': '{disabled, select, true {Alert disabled} other {Alert enabled}}',
  'observability.alertForm.alertTitle': 'Alert title',
  'observability.alertForm.cancel': 'Cancel',
  'observability.alertForm.configuredByOn': '<member></member> on {date}',
  'observability.alertForm.createFailed': 'Failed to create alert',
  'observability.alertForm.createdToast': '{name} created',
  'observability.alertForm.deleteAlert': 'Delete alert',
  'observability.alertForm.deleteBlockedTooltip':
    'This alert has an active adaptive trigger. Delete the adaptive trigger before deleting the alert.',
  'observability.alertForm.deleteFailed': 'Failed to delete alert',
  'observability.alertForm.deleteUnknownTooltip': 'Unable to verify linked triggers. Refresh the page and try again.',
  'observability.alertForm.deletedToast': 'Alert deleted',
  'observability.alertForm.ingestDelayConsider':
    'To capture late-arriving data, consider increasing the evaluation delay.',
  'observability.alertForm.ingestDelayIncrease': 'To capture late-arriving data, increase the evaluation delay.',
  'observability.alertForm.ingestDelayOk': 'Ingest delay is within the evaluation window.',
  'observability.alertForm.lastConfiguredBy': 'Last configured by',
  'observability.alertForm.linkedTriggersBody':
    '{count, plural, one {Changes you make here will affect when the trigger fires.} other {Changes you make here will affect when they fire.}}',
  'observability.alertForm.linkedTriggersHeading':
    '{count, plural, one {This alert is linked to an adaptive trigger} other {This alert is linked to # adaptive triggers}}',
  'observability.alertForm.queryBuilder': 'Query builder',
  'observability.alertForm.queryEditorLabel': 'Query editor',
  'observability.alertForm.readDocs': 'Read docs',
  'observability.alertForm.sqlEditor': 'SQL editor',
  'observability.alertForm.testAlert': '{loading, select, true {Sending…} other {Send test alert}}',
  'observability.alertForm.testAlertFailed': 'Failed to send test alert',
  'observability.alertForm.testAlertSent': 'Test alert sent',
  'observability.alertForm.timeFromEvent': 'Time from event to received:',
  'observability.alertForm.titlePlaceholder': 'Untitled alert',
  'observability.alertForm.untitledAlert': 'Untitled alert',
  'observability.alertForm.updateFailed': 'Failed to update alert',
  'observability.alertForm.updatedToast': '{name} updated',
  'observability.alertMetricSource.analysisUnitLabel': 'Analysis unit',
  'observability.alertMetricSource.analysisUnitTooltip':
    'The context kind the metric is measured per. This metric supports more than one.',
  'observability.alertMetricSource.contextFilterNoAttribute':
    'This metric has a context attribute filter with no attribute.',
  'observability.alertMetricSource.environmentLabel': 'Environment',
  'observability.alertMetricSource.environmentTooltip': 'The environment the metric events come from.',
  'observability.alertMetricSource.errorLoadingMetrics': 'Error loading metrics',
  'observability.alertMetricSource.eventFilterNoAttribute':
    'This metric has an event property filter with no attribute.',
  'observability.alertMetricSource.filterNoValues': 'This metric has a filter with no values.',
  'observability.alertMetricSource.loadMetricFailed': 'Could not load the selected metric.',
  'observability.alertMetricSource.loadingMetric': 'Loading the selected metric.',
  'observability.alertMetricSource.metricLabel': 'Metric',
  'observability.alertMetricSource.metricTooltip': 'The metric the alert measures.',
  'observability.alertMetricSource.selectEnvironment': 'Select an environment.',
  'observability.alertMetricSource.selectMetric': 'Select a metric.',
  'observability.alertMetricSource.sourceName': 'LaunchDarkly hosted metrics',
  'observability.alertMetricSource.unsupportedAnalysisUnit': 'Select an analysis unit that this metric measures.',
  'observability.alertMetricSource.unsupportedCountDistinct': 'Alerts do not support count distinct unit aggregation.',
  'observability.alertMetricSource.unsupportedDataSource': 'Alerts support LaunchDarkly-hosted metrics only.',
  'observability.alertMetricSource.unsupportedGroup': 'Alerts support custom event metrics only.',
  'observability.alertMetricSource.unsupportedKind': 'Alerts support custom event metrics only.',
  'observability.alertMetricSource.unsupportedMetricTitle': 'This metric cannot be alerted on',
  'observability.alertMetricSource.unsupportedNoAnalysisUnit': 'Alerts support metrics with an analysis unit only.',
  'observability.alertMetricSource.unsupportedNoEventKey': 'Alerts support metrics with an event key only.',
  'observability.alertMetricSource.unsupportedPercentile':
    'Alerts support the P50, P75, P90, P95, and P99 percentiles only.',
  'observability.alertMetricSource.unsupportedWinsorization': 'Alerts do not support metrics that use winsorization.',
  'observability.alertNotifications.addChannel': 'Add {channel}',
  'observability.alertNotifications.addNotification': 'Add notification',
  'observability.alertNotifications.advancedSettings': 'Advanced notification settings',
  'observability.alertNotifications.boldButton': 'B',
  'observability.alertNotifications.boldTitle': 'Bold',
  'observability.alertNotifications.channelMembers': 'members',
  'observability.alertNotifications.channelSlack': 'Slack channels',
  'observability.alertNotifications.channelWebhooks': 'webhooks',
  'observability.alertNotifications.codeTitle': 'Code',
  'observability.alertNotifications.hideGraphLabel': 'Hide graph',
  'observability.alertNotifications.highCardinalityResolveWarning':
    'This alert has many groups. Recovered groups are detected by re-checking them, which can take an extra evaluation.',
  'observability.alertNotifications.italicButton': 'I',
  'observability.alertNotifications.italicTitle': 'Italic',
  'observability.alertNotifications.jiraCadenceNote':
    'A new Jira issue is created each time this alert notifies. Use cooldown and alert window to limit how often that happens.',
  'observability.alertNotifications.jiraIssueTypePlaceholder': 'Select issue type',
  'observability.alertNotifications.jiraLabel': 'Jira project and issue type',
  'observability.alertNotifications.jiraProjectPlaceholder': 'Select Jira project',
  'observability.alertNotifications.jiraSetupNote':
    'Configure the Jira integration for this account so LaunchDarkly can create issues when this alert fires.',
  'observability.alertNotifications.linkTitle': 'Link',
  'observability.alertNotifications.markdownPlaceholder': 'Enter markdown content…',
  'observability.alertNotifications.membersLabel': 'Members to notify',
  'observability.alertNotifications.membersPlaceholder': 'Select members',
  'observability.alertNotifications.messageContentLabel': 'Message content',
  'observability.alertNotifications.messageContentPlaceholder': 'Customize the message to include with this alert',
  'observability.alertNotifications.messageContentTooltip':
    'Markdown-formatted message to include with alert notifications.',
  'observability.alertNotifications.notificationSettings': 'Notification settings',
  'observability.alertNotifications.notifyOnResolveLabel': 'Notify on resolve',
  'observability.alertNotifications.removeEmailLabel': 'Remove email notifications',
  'observability.alertNotifications.removeJiraLabel': 'Remove Jira notifications',
  'observability.alertNotifications.removeSlackLabel': 'Remove Slack notifications',
  'observability.alertNotifications.removeWebhookLabel': 'Remove webhook notifications',
  'observability.alertNotifications.slackAuthorizeStep':
    '2. <link>Authorize the app</link> by running the <code>/launchdarkly account</code> command in your Slack workspace.',
  'observability.alertNotifications.slackConnectAction':
    '{installed, select, true {Authenticate Slack app} other {Install Slack app}} to send alerts to channels.',
  'observability.alertNotifications.slackInstallStep': '1. Install the <link>LaunchDarkly Slack app</link>.',
  'observability.alertNotifications.slackLabel': 'Slack channels to notify',
  'observability.alertNotifications.slackPlaceholder': 'Search Slack channels',
  'observability.alertNotifications.strikethroughButton': 'S',
  'observability.alertNotifications.strikethroughTitle': 'Strikethrough',
  'observability.alertNotifications.variableAlertingGroup': 'Alerting group',
  'observability.alertNotifications.variableAlertingValue': 'Alerting value',
  'observability.alertNotifications.variableCondition': 'Condition',
  'observability.alertNotifications.variableCooldown': 'Cooldown',
  'observability.alertNotifications.variableFilters': 'Filters',
  'observability.alertNotifications.variableFunctionColumn': 'Function column',
  'observability.alertNotifications.variableFunctionOperation': 'Function operation',
  'observability.alertNotifications.variableGroupByKey': 'Group by key',
  'observability.alertNotifications.variableSource': 'Source',
  'observability.alertNotifications.variableThreshold': 'Threshold',
  'observability.alertNotifications.variableThresholdType': 'Threshold type',
  'observability.alertNotifications.variableTitle': 'Title',
  'observability.alertNotifications.variableWindow': 'Window',
  'observability.alertNotifications.variables': 'Variables',
  'observability.alertNotifications.webhooksLabel': 'Webhooks to notify',
  'observability.alertNotifications.webhooksPlaceholder': 'Enter webhook URLs',
  'observability.alertQuery.addFilter': 'Add filter',
  'observability.alertQuery.addFilters': 'Add filters',
  'observability.alertQuery.errorsProductInfo': 'Alerts every time an open error matches the specified conditions.',
  'observability.alertQuery.externalMonitorProductInfo':
    'Alerts every time the external monitor reports a breach. The monitor is evaluated outside LaunchDarkly.',
  'observability.alertQuery.filterLabel': 'Filter',
  'observability.alertQuery.filtersLabel': 'Filters',
  'observability.alertQuery.filtersTooltip': 'The filters to apply to the query.',
  'observability.alertQuery.function': 'Function',
  'observability.alertQuery.functionTooltip':
    'Determines how data points are aggregated. If the function requires a numeric field as input, one can be chosen.',
  'observability.alertQuery.groupBy': 'Group by',
  'observability.alertQuery.groupByTooltip':
    'One or more categorical fields for grouping results into separate series.',
  'observability.alertQuery.percentOfUnits': 'Percent of units',
  'observability.alertQuery.populationAggregation': 'Population aggregation',
  'observability.alertQuery.populationFilterError':
    'Percent of units requires a population filter or a population window.',
  'observability.alertQuery.populationFilterLabel': 'Population filter',
  'observability.alertQuery.populationFilterTooltip':
    'An optional filter selecting the events whose senders form the population (the denominator). Uses the same filter language as the alert query. When empty, the population is the units observed by the alert query.',
  'observability.alertQuery.populationFunctionLabel': 'Population function',
  'observability.alertQuery.populationFunctionTooltip':
    'A second aggregation applied across the group-by units. Values are first aggregated per unit with the function above, then combined across units with this function, e.g. P99 of the sum per user.',
  'observability.alertQuery.populationWindowError':
    'Population window must be greater than or equal to the alert window.',
  'observability.alertQuery.populationWindowLabel': 'Population window',
  'observability.alertQuery.populationWindowTooltip':
    'The rolling lookback used to determine the population. Must be greater than or equal to the alert window. Defaults to the alert window.',
  'observability.alertQuery.productCalloutTitle': '{productType} alerts',
  'observability.alertQuery.removeFilterLabel': 'Remove filter',
  'observability.alertQuery.removeGroupByLabel': 'Remove group by',
  'observability.alertQuery.removePopulationAggregationLabel': 'Remove population aggregation',
  'observability.alertQuery.sameAsAlertWindow': 'Same as alert window',
  'observability.alertQuery.sessionsProductInfo':
    "Alerts once for every session that matches the condition''s filters.",
  'observability.alertQuery.source': 'Source',
  'observability.alertQuery.sourceTooltip': 'The resource being queried.',
  'observability.alertTriggers.advancedSettings': 'Advanced threshold settings',
  'observability.alertTriggers.alertThreshold': 'Alert threshold',
  'observability.alertTriggers.alertWindowLabel': 'Alert window',
  'observability.alertTriggers.alertWindowTooltip': 'The time period over which the alert is evaluated',
  'observability.alertTriggers.conditionAbove': 'Above (>)',
  'observability.alertTriggers.conditionAboveOrEqual': 'Above or equal (≥)',
  'observability.alertTriggers.conditionBelow': 'Below (<)',
  'observability.alertTriggers.conditionBelowOrEqual': 'Below or equal (≤)',
  'observability.alertTriggers.conditionEqual': 'Equal (=)',
  'observability.alertTriggers.conditionLabel': 'Condition',
  'observability.alertTriggers.conditionNotEqual': 'Not equal (≠)',
  'observability.alertTriggers.conditionOutside': 'Outside',
  'observability.alertTriggers.conditionTooltip': 'The evaluation rule for the alert to be triggered',
  'observability.alertTriggers.cooldownLabel': 'Cooldown',
  'observability.alertTriggers.cooldownTooltip':
    'The time period after an alert is triggered before it can be triggered again',
  'observability.alertTriggers.evaluationDelayLabel': 'Evaluation delay (minutes)',
  'observability.alertTriggers.evaluationDelayTooltip':
    'The time period to wait after a window ends before the alert is evaluated. Useful when data arrives late.',
  'observability.alertTriggers.noDataBehaviorLabel': 'No data behavior',
  'observability.alertTriggers.noDataBehaviorTooltip':
    'How the alert should behave when the query returns no data during an evaluation window',
  'observability.alertTriggers.noDataNoChange': 'No change',
  'observability.alertTriggers.noDataResolve': 'Resolve to normal',
  'observability.alertTriggers.noDataTrigger': 'Trigger alert',
  'observability.alertTriggers.removeWarnThresholdLabel': 'Remove warn threshold',
  'observability.alertTriggers.threshold': 'Threshold',
  'observability.alertTriggers.typeAnomalyDescription': 'Triggers on abnormal behavior',
  'observability.alertTriggers.typeConstantDescription': 'Triggers at a fixed threshold',
  'observability.alertTriggers.typeLabel': 'Type',
  'observability.alertTriggers.typeTooltip':
    'Constant alerts fire when a value exceeds a fixed number. Anomaly alerts fire when a value deviates from its baseline.',
  'observability.alertTriggers.warnAboveError':
    'Warning threshold must be above the alert threshold for below conditions.',
  'observability.alertTriggers.warnBelowError':
    'Warning threshold must be below the alert threshold for above conditions.',
  'observability.alertTriggers.warnThreshold': 'Warn threshold',
  'observability.alertTriggers.warningThreshold': 'Warning threshold',
  'observability.alertsList.activityColumn': 'Activity',
  'observability.alertsList.adaptiveTriggersColumn': 'Adaptive triggers',
  'observability.alertsList.adaptiveTriggersLabel': 'Adaptive triggers',
  'observability.alertsList.alertDescription': 'Get alerted when alert conditions are met.',
  'observability.alertsList.alertsNotifyYou':
    'Alerts notify you when errors spike or a metric crosses a threshold you set.',
  'observability.alertsList.allAlerts': 'All alerts',
  'observability.alertsList.channels': 'Channels',
  'observability.alertsList.createAlert': 'Create alert',
  'observability.alertsList.createNewAlert': 'Create new alert',
  'observability.alertsList.createYourFirst': 'Create your first alert',
  'observability.alertsList.destinationsColumn': 'Destinations',
  'observability.alertsList.destinationsLabel': 'Alert destinations',
  'observability.alertsList.lastUpdateColumn': 'Last status update',
  'observability.alertsList.nameColumn': 'Alert name',
  'observability.alertsList.noNotificationsEnabled': 'No notifications enabled',
  'observability.alertsList.none': 'None',
  'observability.alertsList.open': 'Open',
  'observability.alertsList.readAboutAlerts': 'Read about alerts',
  'observability.alertsList.searchLabel': 'Search alerts',
  'observability.alertsList.searchPlaceholder': 'Search by alert name',
  'observability.alertsList.statusColumn': 'Status',
  'observability.alertsList.statusLabel': 'Alert status: {status}',
  'observability.alertsList.tableLabel': 'Alerts',
  'observability.alertsList.triggerCount': '{count, plural, one {1 trigger} other {# triggers}}',
  'observability.alertsList.view': 'View',
  'observability.alertsList.webhookEnabled': 'Webhook enabled',
  'observability.analyticalReport.emptyBody':
    'This report doesn’t have any content yet. Use the Edit JSON button in the toolbar to add Brief content.',
  'observability.analyticalReport.emptyHeading': 'Analytical Report is empty',
  'observability.artifactChip.openExternallyLabel': 'Open {typeLabel} externally',
  'observability.artifactChip.tooltip': '{typeLabel}: {title}',
  'observability.artifactChip.tooltipWithStatus': '{typeLabel}: {title} · {status}',
  'observability.artifactDetail.artifactNotFound': 'Artifact not found',
  'observability.artifactDetail.askBot': 'Ask {botName}',
  'observability.artifactDetail.couldnTLoad': "Couldn't load this artifact",
  'observability.artifactDetail.created': 'Created {timestamp}',
  'observability.artifactDetail.eventHistory': 'Event history',
  'observability.artifactDetail.linkedConversations': 'Linked conversations',
  'observability.artifactDetail.loading': 'Loading…',
  'observability.artifactDetail.noConversationsHave': 'No conversations have referenced this artifact yet.',
  'observability.artifactDetail.noEventsRecorded': 'No events recorded for this artifact yet.',
  'observability.artifactDetail.open': 'Open',
  'observability.artifactDetail.openExternally': 'Open externally',
  'observability.artifactDetail.somethingWentWrong':
    'Something went wrong fetching this artifact. Refresh to try again.',
  'observability.artifactDetail.thatArtifactMay':
    "That artifact may have been deleted, or you don't have access to it.",
  'observability.artifactDetail.updated': '· Updated {timestamp}',
  'observability.artifactDetail.viewConversation': 'View conversation',
  'observability.artifactEvents.briefUpdated': 'Brief updated',
  'observability.artifactEvents.cleanupSubject': 'Cleanup started',
  'observability.artifactEvents.commented': 'Comment added',
  'observability.artifactEvents.edited': 'Updated',
  'observability.artifactEvents.factorySubject': 'Linked as Release Autopilot input',
  'observability.artifactEvents.flagArchived': 'Flag archived',
  'observability.artifactEvents.flagCreated': 'Flag created',
  'observability.artifactEvents.flagEdited': 'Flag updated',
  'observability.artifactEvents.graphAdded': 'Graph added',
  'observability.artifactEvents.pullRequestClosed': 'Pull request closed',
  'observability.artifactEvents.pullRequestMerged': 'Pull request merged',
  'observability.artifactEvents.pullRequestOpened': 'Pull request opened',
  'observability.artifactEvents.releaseCompleted': 'Release completed',
  'observability.artifactEvents.releaseRegressed': 'Release rolled back',
  'observability.artifactEvents.releaseStarted': 'Release started',
  'observability.artifactEvents.rolloutConfigured': 'Release configured',
  'observability.artifactEvents.statusChanged': 'Status changed',
  'observability.askVega.acceptQuery': 'Accept query',
  'observability.askVega.actionLabel': 'AI assist: {query}',
  'observability.askVega.actionLabelEmpty': 'Ask AI assist',
  'observability.askVega.actionPrefix': 'AI assist:',
  'observability.askVega.cancel': 'Cancel',
  'observability.askVega.editPrompt': 'Edit prompt',
  'observability.askVega.errorGeneric': 'Something went wrong with AI assist. Try again.',
  'observability.askVega.errorNotEnabledForAccount':
    'AI features are not enabled for this account. An admin can enable them in account settings.',
  'observability.askVega.errorNotEnabledForProject': 'AI assist is not enabled for this project.',
  'observability.askVega.errorOverLimit': 'This workspace is over its AI run limit.',
  'observability.askVega.errorStillWorking': 'AI assist is still finishing your last question. Try again in a moment.',
  'observability.askVega.errorStopped': 'AI assist stopped before it finished. Try again.',
  'observability.askVega.errorTimeout': 'AI assist took too long. Try again or ask a simpler question.',
  'observability.askVega.errorUnreachable': 'AI assist could not be reached. Try again.',
  'observability.askVega.errorUnusable': 'AI assist could not turn that into a query. Try rephrasing your question.',
  'observability.askVega.notSearchQuery': 'This does not look like a new search query. Try again.',
  'observability.askVega.regenerate': 'Regenerate',
  'observability.askVega.resultActionsLabel': 'Query result actions',
  'observability.askVega.retry': 'Retry',
  'observability.askVega.stop': 'Stop',
  'observability.askVega.thinking': 'AI assist is thinking…',
  'observability.askVegaAgent.allResults': 'Showing all results',
  'observability.askVegaAgent.applied': 'Updated the search',
  'observability.askVegaAgent.appliedUnchanged': 'Checked the search',
  'observability.askVegaAgent.chatLabel': 'AI assist chat',
  'observability.askVegaAgent.emptyBody':
    'Describe what you want to find. Ask follow-up questions to refine the query or load more data.',
  'observability.askVegaAgent.emptyTitle': 'Ask AI assist',
  'observability.askVegaAgent.followUpPlaceholder': 'Ask a follow-up question',
  'observability.askVegaAgent.fromVega': 'From Vega',
  'observability.askVegaAgent.handedOff': 'Handed off to Vega',
  'observability.askVegaAgent.handingOff': 'Handing off to Vega…',
  'observability.askVegaAgent.handoffFailed': "Vega couldn't finish",
  'observability.askVegaAgent.handoffFailedReply': "Vega couldn't finish this, so here's what AI Assist found.",
  'observability.askVegaAgent.handoffReasonAction': 'Vega can take this action for you.',
  'observability.askVegaAgent.handoffReasonDataSummary': 'Vega can summarize more data than AI Assist.',
  'observability.askVegaAgent.handoffReasonHeading': 'Why Vega',
  'observability.askVegaAgent.handoffReasonNativeExhausted': "AI Assist couldn't find an answer on its own.",
  'observability.askVegaAgent.handoffReasonNeedsCode': 'Vega can read your code to answer this.',
  'observability.askVegaAgent.handoffReasonRootCause': 'Vega can dig into the root cause.',
  'observability.askVegaAgent.handoffStillRunning': 'Vega is still working on this. Open it in Vega to follow along.',
  'observability.askVegaAgent.historyLabel': 'Conversation',
  'observability.askVegaAgent.interimLabel': 'What AI Assist found so far',
  'observability.askVegaAgent.modeToggle': 'AI assist',
  'observability.askVegaAgent.noReply': "That didn't finish. Try again, or describe what you want to find.",
  'observability.askVegaAgent.openInVega': 'Open in Vega',
  'observability.askVegaAgent.promptLabel': 'Message AI assist',
  'observability.askVegaAgent.promptPlaceholder': 'Describe what you want to find',
  'observability.askVegaAgent.replyAlreadyShowing':
    'Your search already matches that — check out the results on the right.',
  'observability.askVegaAgent.replyQuery': 'Just updated your search query — check out the results on the right.',
  'observability.askVegaAgent.replyQueryAndRange':
    'Just updated your search query and set the time range to {range} — check out the results on the right.',
  'observability.askVegaAgent.replyRange': 'Just set the time range to {range} — check out the results on the right.',
  'observability.askVegaAgent.resizeChat': 'Resize AI assist chat',
  'observability.askVegaAgent.send': 'Send',
  'observability.askVegaAgent.stepCheckedResults':
    '{count, plural, one {Checked # matching result} other {Checked # matching results}}',
  'observability.askVegaAgent.stepCountTitleErrors':
    '{count, plural, one {# matching error} other {# matching errors}}',
  'observability.askVegaAgent.stepCountTitleLogs': '{count, plural, one {# matching log} other {# matching logs}}',
  'observability.askVegaAgent.stepCountTitleMetrics':
    '{count, plural, one {# matching datapoint} other {# matching datapoints}}',
  'observability.askVegaAgent.stepCountTitleResults':
    '{count, plural, one {# matching result} other {# matching results}}',
  'observability.askVegaAgent.stepCountTitleSessions':
    '{count, plural, one {# matching session} other {# matching sessions}}',
  'observability.askVegaAgent.stepCountTitleTraces':
    '{count, plural, one {# matching trace} other {# matching traces}}',
  'observability.askVegaAgent.stepFoundFields':
    '{count, plural, one {Found # matching field} other {Found # matching fields}}',
  'observability.askVegaAgent.stepFoundValues': '{count, plural, one {Found # value} other {Found # values}} for {key}',
  'observability.askVegaAgent.stepKeysHeading': 'Matching fields',
  'observability.askVegaAgent.stepKeysUnfiltered': 'Listed the most common fields',
  'observability.askVegaAgent.stepMore': '{count, plural, one {# more} other {# more}}',
  'observability.askVegaAgent.stepNoMatches': 'No matches',
  'observability.askVegaAgent.stepQueryHeading': 'Query',
  'observability.askVegaAgent.stepReasonHeading': 'Needs a change',
  'observability.askVegaAgent.stepRefining': 'Refining the search',
  'observability.askVegaAgent.stepResultAnonymous': 'Anonymous user',
  'observability.askVegaAgent.stepResultsHeading': 'Results',
  'observability.askVegaAgent.stepValidated': 'Validated the search',
  'observability.askVegaAgent.stepValuesHeading': 'Top values',
  'observability.askVegaAgent.stepValuesUnfiltered': 'Listed the most common values',
  'observability.askVegaAgent.stopped': 'Stopped.',
  'observability.askVegaAgent.vegaWorking': 'Vega is working…',
  'observability.attributesTable.attribute': 'Attribute',
  'observability.attributesTable.content': 'Content',
  'observability.attributesTable.copyAttribute': 'Copy attribute',
  'observability.attributesTable.copyObject': 'Copy object',
  'observability.attributesTable.deprecatedTooltip':
    'This attribute is deprecated and was remapped to <strong>{attribute}</strong>.',
  'observability.attributesTable.inferredTooltip': 'Inferred from deprecated attribute: <strong>{attribute}</strong>',
  'observability.attributesTable.learnMore': 'Learn more →',
  'observability.attributesTable.searchPlaceholder': 'Search attributes or content',
  'observability.autoresolveStaleErrors.body':
    'Turning this on resolves all existing errors that have been inactive for the time period you set.',
  'observability.autoresolveStaleErrors.durationLabel': 'Resolve errors not seen for',
  'observability.autoresolveStaleErrors.sectionLabel': 'Auto-resolve stale errors',
  'observability.backendDashboard.overview': 'Overview',
  'observability.backendDashboard.queries': 'Queries',
  'observability.barChart.barLabel': '{age} {xAxis}(s) ago, {value} {yAxis}(s)',
  'observability.barChart.barTooltip': '{age} {xAxis}(s) ago\n {value} {yAxis}(s)',
  'observability.brief.funnelNoStages': 'No stages',
  'observability.brief.funnelOverallPct': '{percent} of top',
  'observability.brief.funnelStagePct': '{percent} of prev',
  'observability.brief.issueCount': '{count, plural, one {# issue} other {# issues}} found.',
  'observability.brief.lineChartLabel': 'Line chart',
  'observability.brief.scatterChartLabel': 'Scatter chart',
  'observability.brief.showRawPayload': 'Show raw payload',
  'observability.brief.sparklineLabel': 'Sparkline',
  'observability.brief.validationHeading': 'Brief failed schema validation',
  'observability.briefEditDialog.body':
    'Paste a Brief JSON payload. Validation against the full Brief schema happens on render; the server only checks for syntactically valid JSON.',
  'observability.briefEditDialog.cancel': 'Cancel',
  'observability.briefEditDialog.heading': 'Edit Brief JSON (debug)',
  'observability.briefEditDialog.reset': 'Reset',
  'observability.briefEditDialog.save': 'Save',
  'observability.browserUsagePanel.browser': 'Browser',
  'observability.browserUsagePanel.emptyState': 'No browser data found for the selected time range',
  'observability.browserUsagePanel.heading': 'Browser Usage',
  'observability.browserUsagePanel.loadError': 'Error loading browser usage data',
  'observability.browserUsagePanel.percentage': 'Percentage',
  'observability.browserUsagePanel.sessions': 'Sessions',
  'observability.columnActionsMenu.copySearchKey': 'Copy search key',
  'observability.columnActionsMenu.hideColumn': 'Hide {columnId}',
  'observability.columnActionsMenu.menuLabel': 'Column actions',
  'observability.columnActionsMenu.moveToLeft': 'Move to left column',
  'observability.columnActionsMenu.moveToRight': 'Move to right column',
  'observability.columnActionsMenu.removeSort': 'Remove Sort',
  'observability.columnActionsMenu.resetSize': 'Reset size',
  'observability.columnActionsMenu.sortAscending': 'Sort Ascending',
  'observability.columnActionsMenu.sortDescending': 'Sort Descending',
  'observability.columnActionsMenu.updateLabel': 'Update label',
  'observability.columnLabelDialog.cancel': 'Cancel',
  'observability.columnLabelDialog.confirm': 'Update label',
  'observability.columnLabelDialog.heading': 'Update {columnId} Label',
  'observability.columnLabelDialog.nameLabel': 'Label Name',
  'observability.columnLabelDialog.namePlaceholder': 'Type name…',
  'observability.columnPicker.notEnoughSpace': 'Not enough space',
  'observability.columnPicker.selectedColumnsLabel': 'Selected columns',
  'observability.contextErrors.count': '{count}×',
  'observability.contextErrors.countAtLeast': '{count}+×',
  'observability.contextErrors.empty': 'No errors recorded for this context in the last 30 days.',
  'observability.contextErrors.lastSeen': 'Last seen {datetime}',
  'observability.contextErrors.loadError': 'Errors couldn’t be loaded.',
  'observability.contextErrors.occurrences':
    '{count, plural, one {# occurrence for this context} other {# occurrences for this context}}',
  'observability.contextErrors.occurrencesAtLeast':
    '{count, plural, one {At least # occurrence for this context} other {At least # occurrences for this context}}',
  'observability.contextErrors.openError': 'Open error {title}, {occurrences}, last seen {datetime}',
  'observability.contextErrors.retry': 'Try again',
  'observability.contextErrors.setupAction': 'Get started',
  'observability.contextErrors.setupBody':
    'Capture the errors this context hits in your app, each linked to the session where it happened.',
  'observability.contextErrors.title': 'Recent errors',
  'observability.contextErrors.titleTooltip':
    'Errors from the last 30 days in sessions where this context was identified in this environment',
  'observability.contextErrors.viewAll': 'View in the Errors list',
  'observability.contextSessions.empty': 'No sessions recorded for this context in the last 30 days.',
  'observability.contextSessions.errors': 'Errors',
  'observability.contextSessions.live': 'Live',
  'observability.contextSessions.loadError': 'Sessions couldn’t be loaded.',
  'observability.contextSessions.playSession': 'Play session from {datetime}',
  'observability.contextSessions.retry': 'Try again',
  'observability.contextSessions.setupAction': 'Get started',
  'observability.contextSessions.setupBody':
    'Record and replay this context’s sessions, tagged with the flag variations it was served.',
  'observability.contextSessions.title': 'Recent sessions',
  'observability.contextSessions.titleTooltip':
    'Sessions from the last 30 days in this environment where this context was identified',
  'observability.contextSessions.viewAll':
    '{count, plural, one {View # session in the Sessions list} other {View all # in the Sessions list}}',
  'observability.conversationPanel.copyIdLabel': 'Copy conversation ID',
  'observability.conversationPanel.messages': 'Messages',
  'observability.conversationPanel.notFoundTitle': 'Conversation not found',
  'observability.conversationPanel.partialTitle': 'Showing this trace only',
  'observability.conversationPanel.theRestOf': 'The rest of the conversation could not be loaded.',
  'observability.conversationPanel.viewsLabel': 'Conversation views',
  'observability.conversationPanel.waterfall': 'Waterfall',
  'observability.conversationTranscript.dimensions': 'Dimensions',
  'observability.conversationTranscript.durationLabel': 'Duration {duration}',
  'observability.conversationTranscript.errorBadge': 'Error',
  'observability.conversationTranscript.errorCount': '{count} {count, plural, one {error} other {errors}}',
  'observability.conversationTranscript.input': 'Input',
  'observability.conversationTranscript.judgeEvalCount': '{count} {count, plural, one {eval} other {evals}}',
  'observability.conversationTranscript.judgeEvalCountLabel':
    '{count} judge {count, plural, one {evaluation} other {evaluations}}',
  'observability.conversationTranscript.judgeScoreLabel': 'Judge score {score}',
  'observability.conversationTranscript.llmCallsLabel': '{count} LLM',
  'observability.conversationTranscript.modelCount': '{count} {count, plural, one {model} other {models}}',
  'observability.conversationTranscript.noToolIo': 'No input or output recorded',
  'observability.conversationTranscript.output': 'Output',
  'observability.conversationTranscript.showAsWaterfall': 'Show as waterfall',
  'observability.conversationTranscript.showTraceDetails': 'Show trace details',
  'observability.conversationTranscript.stepCount': '{count} {count, plural, one {step} other {steps}}',
  'observability.conversationTranscript.system': 'System',
  'observability.conversationTranscript.tokensLabel': '{tokens} tokens',
  'observability.conversationTranscript.tokensShort': '{tokens} tok',
  'observability.conversationTranscript.toolCount': '{count} {count, plural, one {tool} other {tools}}',
  'observability.conversationTranscript.traceCount': '{count} {count, plural, one {trace} other {traces}}',
  'observability.conversationTranscript.turn': 'Turn',
  'observability.conversationTranscript.viewDetails': 'View details',
  'observability.conversationsList.docsLink': 'View LLM observability docs',
  'observability.conversationsList.emptyBody':
    'Conversations group related LLM spans across traces into a multi-turn AI interaction. Try expanding your time range, or set up LLM observability to start capturing conversation data.',
  'observability.conversationsList.emptyBodyFiltered':
    'Conversations group related LLM spans across traces into a multi-turn AI interaction. None match your search. Try broadening your query, removing filters, or expanding the time range.',
  'observability.conversationsList.emptyHeading': 'No conversations found',
  'observability.conversationsList.errorBody':
    'There was an error loading your conversations. Reach out to us if this might be a bug.',
  'observability.conversationsList.errorTitle': 'Failed to load conversations',
  'observability.conversationsList.open': 'Open',
  'observability.createDashboardModal.cancel': 'Cancel',
  'observability.createDashboardModal.create': 'Create',
  'observability.createDashboardModal.heading': 'Create new dashboard',
  'observability.createDashboardModal.nameLabel': 'Name',
  'observability.createDashboardModal.namePlaceholder': 'Untitled dashboard',
  'observability.createDashboardModal.typeLabel': 'Dashboard type',
  'observability.createJiraIssueDialog.cancel': 'Cancel',
  'observability.createJiraIssueDialog.createIssue': 'Create issue',
  'observability.createJiraIssueDialog.createJiraIssue': 'Create Jira issue',
  'observability.createJiraIssueDialog.createWithVega': 'Create with Vega',
  'observability.createJiraIssueDialog.description': 'Description',
  'observability.createJiraIssueDialog.issueType': 'Issue type',
  'observability.createJiraIssueDialog.labels': 'Labels',
  'observability.createJiraIssueDialog.labelsPlaceholder': 'highlight, severity:warning',
  'observability.createJiraIssueDialog.loadingJiraProjects': 'Loading Jira projects…',
  'observability.createJiraIssueDialog.noJiraProjects': 'No Jira projects are available for this workspace.',
  'observability.createJiraIssueDialog.project': 'Project',
  'observability.createJiraIssueDialog.titleLabel': 'Title',
  'observability.customDashboardToolbar.addGraph': 'Add graph',
  'observability.customDashboardToolbar.copyJson': 'Copy as JSON',
  'observability.customDashboardToolbar.deleteConfirm': 'Delete',
  'observability.customDashboardToolbar.deleteConfirmBody':
    'This action is irreversible. Do you want to delete {dashboardName}?',
  'observability.customDashboardToolbar.deleteConfirmCancel': 'Cancel',
  'observability.customDashboardToolbar.deleteConfirmFallbackName': 'this dashboard',
  'observability.customDashboardToolbar.deleteConfirmHeading': 'Delete Dashboard',
  'observability.customDashboardToolbar.deleteMenuItem': 'Delete dashboard',
  'observability.customDashboardToolbar.favoriteLabel': 'Favorite',
  'observability.customDashboardToolbar.jsonCopied': 'Copied dashboard JSON',
  'observability.customDashboardToolbar.jsonCopyFailed': 'Failed to copy dashboard JSON',
  'observability.customDashboardToolbar.linkCopied': 'Copied link',
  'observability.customDashboardToolbar.moreActionsLabel': 'More actions',
  'observability.customDashboardToolbar.sendFeedback': 'Send feedback',
  'observability.customDashboardToolbar.settings': 'Settings',
  'observability.customDashboardToolbar.share': 'Share',
  'observability.customDashboardToolbar.unfavoriteLabel': 'Unfavorite',
  'observability.dashboard.addGraph': 'Add graph',
  'observability.dashboard.dragToReorderLabel': 'Drag to reorder',
  'observability.dashboard.editLabel': 'Edit',
  'observability.dashboard.emptyBody': 'It seems like this dashboard is empty. Get started by adding your first graph.',
  'observability.dashboard.emptyHeading': 'No visualizations created',
  'observability.dashboard.expandLabel': 'Expand',
  'observability.dashboard.learnMore': 'Learn more',
  'observability.dashboardFilterBar.configsNone': 'Configs: None',
  'observability.dashboardFilterBar.editJson': 'Edit JSON',
  'observability.dashboardFilterBar.flagsNone': 'Flags: None',
  'observability.dashboardFilterBar.variables': 'Variables',
  'observability.dashboardFilterBar.vega': 'Vega',
  'observability.dashboardGraphMenu.clone': 'Clone',
  'observability.dashboardGraphMenu.createAlert': 'Create alert',
  'observability.dashboardGraphMenu.expand': 'Expand',
  'observability.dashboardLayout.addGroup': 'Add group',
  'observability.dashboardLayout.collapseGroup': 'Collapse {title}',
  'observability.dashboardLayout.deleteGroup': 'Delete group',
  'observability.dashboardLayout.emptyGroup':
    "No graphs in this group yet. Use a graph's Move to group action to add one.",
  'observability.dashboardLayout.expandGroup': 'Expand {title}',
  'observability.dashboardLayout.graphCount': '{count, plural, one {# graph} other {# graphs}}',
  'observability.dashboardLayout.groupActionsLabel': 'Group actions',
  'observability.dashboardLayout.groupNameLabel': 'Group name',
  'observability.dashboardLayout.loadFailed': 'Could not load the dashboard layout.',
  'observability.dashboardLayout.loadingLabel': 'Loading dashboard layout',
  'observability.dashboardLayout.moveGroupDown': 'Move down',
  'observability.dashboardLayout.moveGroupUp': 'Move up',
  'observability.dashboardLayout.moveToGroupLabel': 'Move to group',
  'observability.dashboardLayout.newGroupTitle': 'New group',
  'observability.dashboardLayout.problemGraph': '{graph}: {field} {message}',
  'observability.dashboardLayout.problemsHeading': 'This dashboard has problems',
  'observability.dashboardLayout.renameGroup': 'Rename',
  'observability.dashboardLayout.renameGroupCancel': 'Cancel',
  'observability.dashboardLayout.renameGroupHeading': 'Rename group',
  'observability.dashboardLayout.renameGroupSave': 'Save',
  'observability.dashboardLayout.retryLoad': 'Try again',
  'observability.dashboardLayout.saveFailed': 'Failed to save dashboard layout',
  'observability.dashboardLayout.untitledGroup': 'Untitled group',
  'observability.dashboardLayout.warningsHeading': 'Some graphs need attention',
  'observability.dashboardNotifications.briefSaveFailed': 'Failed to save brief content',
  'observability.dashboardNotifications.briefSaved': 'Brief content saved',
  'observability.dashboardNotifications.dashboardDeleteFailed': 'Failed to delete dashboard',
  'observability.dashboardNotifications.dashboardDeleted': 'Dashboard deleted',
  'observability.dashboardNotifications.graphDeleteFailed': 'Failed to delete graph',
  'observability.dashboardNotifications.graphDeleted': 'Graph deleted',
  'observability.dashboardNotifications.graphOrderUpdateFailed': 'Failed to update graph order',
  'observability.dashboardNotifications.graphOrderUpdated': 'Graph order updated',
  'observability.dashboardNotifications.graphUpdateFailed': 'Failed to update graph',
  'observability.dashboardSettingsModal.cancel': 'Cancel',
  'observability.dashboardSettingsModal.defaultTimeRangeLabel': 'Default time range',
  'observability.dashboardSettingsModal.defaultTimeRangePlaceholder': 'Select the default time range',
  'observability.dashboardSettingsModal.descriptionLabel': 'Description',
  'observability.dashboardSettingsModal.descriptionPlaceholder': 'What this dashboard is for',
  'observability.dashboardSettingsModal.heading': 'Dashboard settings',
  'observability.dashboardSettingsModal.nameLabel': 'Name',
  'observability.dashboardSettingsModal.namePlaceholder': 'Untitled dashboard',
  'observability.dashboardSettingsModal.save': 'Save',
  'observability.dashboardsEmptyState.body':
    'Open a dashboard from the sidebar to explore its graphs and metrics, or create a new one.',
  'observability.dashboardsEmptyState.heading': 'Select a dashboard to get started',
  'observability.dashboardsListHeader.vega': 'Vega',
  'observability.dashboardsSidebar.backendDashboard': 'Backend',
  'observability.dashboardsSidebar.clearSearchLabel': 'Clear search',
  'observability.dashboardsSidebar.createDashboardLabel': 'Create dashboard',
  'observability.dashboardsSidebar.createNewDashboard': 'Create new dashboard',
  'observability.dashboardsSidebar.customDashboardsTitle': 'Custom dashboards',
  'observability.dashboardsSidebar.databasesDashboard': 'Databases',
  'observability.dashboardsSidebar.emptyState': 'No dashboards found',
  'observability.dashboardsSidebar.favorites': 'Favorites',
  'observability.dashboardsSidebar.favoritesEmptyState': 'Favorite a custom dashboard to keep it here.',
  'observability.dashboardsSidebar.frontendDashboard': 'Frontend',
  'observability.dashboardsSidebar.hide': 'Hide dashboard list',
  'observability.dashboardsSidebar.insights': 'Insights',
  'observability.dashboardsSidebar.llmAgentsDashboard': 'LLM agents',
  'observability.dashboardsSidebar.searchPlaceholder': 'Search dashboards',
  'observability.dashboardsSidebar.serviceMapDashboard': 'Service map',
  'observability.dashboardsSidebar.servicesDashboard': 'Services',
  'observability.dashboardsSidebar.show': 'Show dashboard list',
  'observability.databaseQueries.avgDuration': 'Avg Duration',
  'observability.databaseQueries.count': 'Count',
  'observability.databaseQueries.database': 'Database',
  'observability.databaseQueries.databaseQueries': 'Database Queries',
  'observability.databaseQueries.errorLoadingDatabase': 'Error loading database queries',
  'observability.databaseQueries.errorRate': 'Error Rate',
  'observability.databaseQueries.noDatabaseQueries': 'No database queries found for the selected time range',
  'observability.databaseQueries.p95Duration': 'P95 Duration',
  'observability.databaseQueries.statement': 'Statement',
  'observability.databases.change.approximateTooltip': 'Estimated from sampled data.',
  'observability.databases.change.estimated': '~{value}',
  'observability.databases.change.insufficientTooltip':
    'Not enough volume to compare. Both this window and the previous one need at least {count, number} executions.',
  'observability.databases.change.new': 'New',
  'observability.databases.change.newTooltip': 'No executions in the previous window of the same length.',
  'observability.databases.change.percentTooltip': '{value} compared with the previous window of the same length.',
  'observability.databases.chart.errorExecutions': 'Error executions',
  'observability.databases.chart.latency': 'Latency',
  'observability.databases.chart.throughput': 'Throughput',
  'observability.databases.column.database': 'Database',
  'observability.databases.column.duration': 'Duration',
  'observability.databases.column.environment': 'Environment',
  'observability.databases.column.errorRate': 'Error rate',
  'observability.databases.column.executions': 'Executions',
  'observability.databases.column.namespace': 'Namespace',
  'observability.databases.column.p90': 'P90',
  'observability.databases.column.p99': 'P99',
  'observability.databases.column.rows': 'Rows',
  'observability.databases.column.service': 'Service',
  'observability.databases.column.status': 'Status',
  'observability.databases.column.system': 'System',
  'observability.databases.column.table': 'Table',
  'observability.databases.column.template': 'Query',
  'observability.databases.column.time': 'Time',
  'observability.databases.column.totalTime': 'Total time',
  'observability.databases.column.trace': 'Trace',
  'observability.databases.copyQuery': 'Copy query',
  'observability.databases.createAlert': 'Create alert',
  'observability.databases.emptyBody':
    'Normalized database queries appear here after instrumentation sends db.query.text or db.query.summary, or an operation and a collection. Widen the time range if this window is empty.',
  'observability.databases.emptyDatabasesTitle': 'No databases in this time range',
  'observability.databases.emptyHierarchyBody':
    'Instrumentation must provide database and table attributes. Widen the time range or change the filters.',
  'observability.databases.emptyTablesTitle': 'No tables in this time range',
  'observability.databases.emptyTitle': 'No database queries in this time range',
  'observability.databases.header.namespace': 'Namespace',
  'observability.databases.header.queryText': 'Query text',
  'observability.databases.header.status': 'Status',
  'observability.databases.header.system': 'System',
  'observability.databases.header.template': 'Template',
  'observability.databases.hierarchyLabel': 'Database hierarchy',
  'observability.databases.instances.empty': 'No queries matched this grouping in the selected time range.',
  'observability.databases.instances.heading': 'Individual queries',
  'observability.databases.instances.loading': 'Loading queries',
  'observability.databases.instances.sampled': 'This list is a sample. Some queries in the range may be missing.',
  'observability.databases.loadError': 'Database queries could not be loaded. Try again.',
  'observability.databases.loading': 'Loading database queries',
  'observability.databases.pageTitle': 'Databases',
  'observability.databases.profileSignature.cpu': 'User CPU (thread-summed)',
  'observability.databases.profileSignature.cpuDescription': 'User-mode CPU summed across processing threads.',
  'observability.databases.profileSignature.elapsed': 'Elapsed {duration}',
  'observability.databases.profileSignature.guidance':
    "Timers overlap and are summed across threads. They don't add up to elapsed time.",
  'observability.databases.profileSignature.largest': 'Largest timer · {label} · {factor}× elapsed',
  'observability.databases.profileSignature.markLoad': 'Mark load',
  'observability.databases.profileSignature.markLoadDescription': 'Time loading marks; can overlap S3 reads.',
  'observability.databases.profileSignature.notRecorded': 'Not recorded',
  'observability.databases.profileSignature.outside': 'Outside these timers',
  'observability.databases.profileSignature.outsideDescription': 'Wall time not covered by the largest recorded timer.',
  'observability.databases.profileSignature.partial':
    'Partial profile. The query ended with an error, so these timers can stop early.',
  'observability.databases.profileSignature.primaryKey': 'Primary key',
  'observability.databases.profileSignature.primaryKeyDescription': 'Time filtering parts by primary key.',
  'observability.databases.profileSignature.s3Read': 'S3 read',
  'observability.databases.profileSignature.s3ReadDescription': 'GET and HEAD time summed across threads.',
  'observability.databases.profileSignature.s3Setup': 'S3 request init',
  'observability.databases.profileSignature.s3SetupDescription': 'Request initialization; overlaps S3 read.',
  'observability.databases.profileSignature.timer': '{label}: {timer}. {description}',
  'observability.databases.profileSignature.timerNotRecorded': '{label}: not recorded on this span.',
  'observability.databases.profileSignature.timerOverflow': '{label}: {timer} · {factor}× elapsed. {description}',
  'observability.databases.queryPageTitle': 'Query',
  'observability.databases.queryCopied': 'Query copied to clipboard',
  'observability.databases.repeatHint': 'This query ran {count, number} times under the same parent span.',
  'observability.databases.sampledLabel': 'Sampled',
  'observability.databases.sampledNote': 'Counts are estimated from a sample. Percentiles describe that sample.',
  'observability.databases.sampledTooltip': 'Some executions in the range may be omitted.',
  'observability.databases.scatter.error': 'Error',
  'observability.databases.scatter.label': 'Query executions by time and duration',
  'observability.databases.scatter.closeTooltip': 'Close execution details',
  'observability.databases.scatter.openExecution': 'Open {service} execution, {duration}, status {status}',
  'observability.databases.scatter.ok': 'OK',
  'observability.databases.showSpan': 'Span {index, number}',
  'observability.databases.statementLabel': 'Query',
  'observability.databases.summary.errorRate': 'Error rate',
  'observability.databases.summary.executions': 'Executions',
  'observability.databases.summary.p99': 'P99',
  'observability.databases.summary.totalTime': 'DB time',
  'observability.databases.unknownDatabase': 'Unknown database',
  'observability.databases.unknownTable': 'Unknown table',
  'observability.databases.traceQueries.errorCount': 'Errors',
  'observability.databases.traceQueries.errorCountTooltip': 'Database spans in this trace that recorded an error.',
  'observability.databases.traceQueries.repeatCount': 'Repeated groups',
  'observability.databases.traceQueries.repeatCountTooltip':
    'Queries run {count, number} or more times under the same parent span, a common sign of an N+1 pattern. Batched queries are not counted.',
  'observability.databases.traceQueries.share': '% of trace time',
  'observability.databases.traceQueries.spanCount': 'Database spans',
  'observability.databases.traceQueries.spanCountTooltip': 'Spans in this trace that call a database.',
  'observability.databases.traceQueries.tab': 'Queries',
  'observability.databases.traceQueries.totalTime': 'Database time',
  'observability.databases.traceQueries.totalTimeTooltip':
    'Sum of database span durations in this trace, counting nested database spans once. The percentage is its share of total trace time, capped at 100% because concurrent queries can overlap.',
  'observability.databases.traceQueries.totalTimeWithShare': '{duration} ({share})',
  'observability.databases.viewDetails': 'View query details',
  'observability.databases.viewDatabase': 'View database',
  'observability.databases.viewTable': 'View table',
  'observability.datasetPicker.addToDatasetLabel': 'Add to dataset',
  'observability.datasetPicker.listLabel': 'Datasets',
  'observability.datasetPicker.searchLabel': 'Search datasets',
  'observability.dateRangePicker.customRange': 'Custom range',
  'observability.dateRangePicker.customRangeTextValue': 'custom date range',
  'observability.dateRangePicker.durationDays': '{count}d',
  'observability.dateRangePicker.durationHours': '{count}h',
  'observability.dateRangePicker.durationMinutes': '{count}m',
  'observability.dateRangePicker.durationMonths': '{count}mo',
  'observability.dateRangePicker.live': 'Live',
  'observability.dateRangePicker.liveModeTextValue': 'Live mode',
  'observability.dateRangePicker.nextLabel': 'next',
  'observability.dateRangePicker.openLabel': 'open date range picker',
  'observability.dateRangePicker.pickerLabel': 'select custom or preset date range',
  'observability.dateRangePicker.presetFallback': 'Last {quantity} {unit}',
  'observability.dateRangePicker.presetsLabel': 'Preset date range options',
  'observability.dateRangePicker.previousLabel': 'previous',
  'observability.dateRangePicker.typedRangeLabel': 'Type a time range, for example last 7 days',
  'observability.dateRangePicker.typedRangePlaceholder': 'e.g. last 7 days',
  'observability.deleteSavedSearchDialog.body':
    'This will permanently delete this saved search. You cannot undo this action.',
  'observability.deleteSavedSearchDialog.bodyNamed':
    'This will permanently delete "{name}." You cannot undo this action.',
  'observability.deleteSavedSearchDialog.cancel': 'Cancel',
  'observability.deleteSavedSearchDialog.delete': 'Delete',
  'observability.deleteSavedSearchDialog.heading': 'Delete saved search',
  'observability.detailActionsMenu.createHeatmap': 'Create heatmap',
  'observability.detailActionsMenu.createJiraIssue': 'Create Jira issue',
  'observability.detailActionsMenu.giveFeedback': 'Give feedback',
  'observability.detailActionsMenu.menuLabel': 'More actions',
  'observability.detailActionsMenu.share': 'Share',
  'observability.devTools.autoScroll': 'Auto-scroll',
  'observability.devTools.closeLabel': 'Close dev tools',
  'observability.devTools.disableLiveMode': 'Disable live mode',
  'observability.devTools.hideDevTools': 'Hide dev tools',
  'observability.devTools.liveModeFlagEvaluations': 'Flag evaluations',
  'observability.devTools.liveModeLogs': 'Logs',
  'observability.devTools.liveModePerformanceGraphs': 'Performance graphs',
  'observability.devTools.liveModeTraces': 'Traces',
  'observability.devTools.liveModeUnavailableBody':
    '{product} are not available in live mode. You can disable live mode to view {product}.',
  'observability.devTools.liveSessionTitle': 'You’re watching a live session',
  'observability.devTools.menuLabel': 'Dev tools menu',
  'observability.devTools.playerTimeChanged': 'Changed player time to {time}',
  'observability.devTools.searchPlaceholder': 'Search…',
  'observability.devTools.showInViewer': 'Show in viewer',
  'observability.devTools.showInternalTraces': 'Show internal traces',
  'observability.devTools.viewErrorsOnly': 'View errors only',
  'observability.devToolsConsole.help': 'Help',
  'observability.devToolsConsole.loadErrorBody':
    'There was an error loading your logs. Reach out to us if this might be a bug.',
  'observability.devToolsConsole.loadErrorTitle': 'Failed to load logs',
  'observability.devToolsConsole.reloadQuery': 'Reload query',
  'observability.devToolsConsole.timestampColumn': 'Timestamp',
  'observability.devToolsMetadata.jira': 'Jira',
  'observability.devToolsMetadata.searchSessionsForValue': 'Search sessions for this value',
  'observability.devToolsPerformance.browserMemoryTitle': 'Browser Memory Usage',
  'observability.devToolsPerformance.renderFpsTitle': 'Render FPS',
  'observability.devToolsPerformance.renderJankTitle': 'Render Jank',
  'observability.devToolsPlaylist.searchPlaceholder': 'Search sessions…',
  'observability.devToolsTraces.help': 'Help',
  'observability.devToolsTraces.loadErrorBody':
    'There was an error loading your traces. Reach out to us if this might be a bug.',
  'observability.devToolsTraces.loadErrorTitle': 'Failed to load traces',
  'observability.devToolsTraces.reloadQuery': 'Reload query',
  'observability.devToolsTraces.timestampColumn': 'Timestamp',
  'observability.earlyAccessSplash.beAmongThe':
    'Be among the first to leverage observability tools such as session replay, error monitoring, logs and traces by using our observability SDK.',
  'observability.earlyAccessSplash.heading': '{product} Early access',
  'observability.earlyAccessSplash.signUpFor': 'Sign up for early access',
  'observability.earlyAccessSplash.weLlLet':
    "We'll let you know as soon as observability is ready for you. Keep an eye on your inbox for updates.",
  'observability.earlyAccessSplash.youReOn': "You're on the waitlist",
  'observability.environmentFilter.allEnvironments': 'All environments',
  'observability.environmentFilter.quickSelectionLabel': 'Quick selection',
  'observability.environmentFilter.searchLabel': 'Search environments',
  'observability.environmentFilter.searchPlaceholder': 'Search environments…',
  'observability.environmentFilter.showOnlyLabel': 'Show only {name}',
  'observability.environmentFilter.triggerLabel': 'Select environments',
  'observability.environmentPicker.allEnvironments': 'All environments',
  'observability.environmentPicker.quickSelectLabel': 'Quick selection options',
  'observability.environmentPicker.searchLabel': 'Search environments',
  'observability.environmentPicker.searchPlaceholder': 'Search environments…',
  'observability.environmentPicker.triggerLabel': 'Select environments',
  'observability.errorBadge.errorLabel': 'An error occurred',
  'observability.errorDetail.affectedUsers': 'Affected Users',
  'observability.errorDetail.instances': 'Instances',
  'observability.errorDetail.last30Days': 'Last 30 days',
  'observability.errorDetail.lastFirstOccurrence': 'Last/first occurrence',
  'observability.errorDetail.latest': 'Latest',
  'observability.errorDetail.showLess': 'Show less',
  'observability.errorDetail.showMore': 'Show more',
  'observability.errorDetail.usersImpacted': 'Users impacted',
  'observability.errorDetail.weeklyChangeTooltip': 'weekly change',
  'observability.errorDistributions.browsersTitle': 'Browsers',
  'observability.errorDistributions.copiedToClipboard': 'Copied to clipboard',
  'observability.errorDistributions.copyFailed': 'Failed to copy to clipboard',
  'observability.errorDistributions.copyToClipboardTitle': 'Copy to clipboard',
  'observability.errorDistributions.environmentsTitle': 'Environments',
  'observability.errorDistributions.operatingSystemsTitle': 'Operating Systems',
  'observability.errorDistributions.servicesTitle': 'Services',
  'observability.errorDistributions.versionsTitle': 'Versions',
  'observability.errorFeed.newBadge': 'New',
  'observability.errorFeed.snoozed': 'Snoozed',
  'observability.errorFeed.snoozedUntil': 'Snoozed until',
  'observability.errorFeedHistogram.errors': 'errors',
  'observability.errorGroupPanel.failedToLoad': 'Failed to load error group.',
  'observability.errorGroupPanel.failedToLoad2': 'Failed to load error instance.',
  'observability.errorGroupPanel.instanceIndex': 'Instance {index}',
  'observability.errorInstance.errorEventData': 'Error event data',
  'observability.errorInstance.instanceErrorBody': 'Instance error body',
  'observability.errorInstance.instanceMetadata': 'Instance metadata',
  'observability.errorInstance.jira': 'Jira',
  'observability.errorInstance.loadErrorBody': 'There was an error loading this error instance.',
  'observability.errorInstance.loadErrorTitle': 'Failed to load error instance',
  'observability.errorInstance.seeAllInstancesLabel': 'See all instances',
  'observability.errorInstance.stacktrace': 'Stacktrace',
  'observability.errorInstance.viewLogs': 'View logs',
  'observability.errorInstance.viewSession': 'View session',
  'observability.errorInstance.viewTrace': 'View trace',
  'observability.errorInstances.emptyStateTitle': 'No error instances found',
  'observability.errorInstances.eventColumn': 'Event',
  'observability.errorInstances.loadErrorBody': 'There was an error loading error instances.',
  'observability.errorInstances.loadErrorTitle': 'Failed to load error instances',
  'observability.errorInstances.noSession': 'no session',
  'observability.errorInstances.noValue': '(no value)',
  'observability.errorInstances.serviceColumn': 'Service',
  'observability.errorInstances.sessionColumn': 'Session',
  'observability.errorInstances.tableLabel': 'Error instances',
  'observability.errorInstances.timestampColumn': 'Timestamp',
  'observability.errorInstances.versionColumn': 'Version',
  'observability.errorSearchKeys.browser': 'Browser name and version.',
  'observability.errorSearchKeys.browserLanguage': 'Language setting of the browser.',
  'observability.errorSearchKeys.bytesWritten': 'Number of bytes written before the error occurred.',
  'observability.errorSearchKeys.columnNumber': 'Column number where the error occurred.',
  'observability.errorSearchKeys.containerId': 'Unique identifier of the container.',
  'observability.errorSearchKeys.dbOperation': 'The type of database operation (e.g., SELECT, INSERT).',
  'observability.errorSearchKeys.dbRowsAffected': 'Number of rows affected by the database operation.',
  'observability.errorSearchKeys.dbSqlTable': 'The database table being accessed.',
  'observability.errorSearchKeys.dbStatement': 'The database statement that caused the error.',
  'observability.errorSearchKeys.dbSystem': 'Database system being used (e.g., PostgreSQL, MySQL).',
  'observability.errorSearchKeys.entryMethod': 'Entry method where the error occurred.',
  'observability.errorSearchKeys.entryRoute': 'Entry route where the error occurred.',
  'observability.errorSearchKeys.environment': 'The environment where the error occurred (e.g., production, staging).',
  'observability.errorSearchKeys.error': 'Error message or flag indicating an error occurred.',
  'observability.errorSearchKeys.exceptionMessage': 'Message associated with the exception.',
  'observability.errorSearchKeys.exceptionStacktrace': 'Full stack trace of the exception.',
  'observability.errorSearchKeys.exceptionType': 'Type or class of the exception.',
  'observability.errorSearchKeys.featureFlagContextId': 'Unique identifier for the evaluation context.',
  'observability.errorSearchKeys.featureFlagContextKeys': 'Keys identifying the context used for flag evaluation.',
  'observability.errorSearchKeys.featureFlagKey': 'The unique key identifying a specific feature flag.',
  'observability.errorSearchKeys.featureFlagLaunchdarklyApplicationVersion': 'Version of the LaunchDarkly application.',
  'observability.errorSearchKeys.featureFlagProviderName': 'The name of the feature flag provider or service.',
  'observability.errorSearchKeys.featureFlagResultReasonErrorKind':
    'Type of error that occurred during flag evaluation, if any.',
  'observability.errorSearchKeys.featureFlagResultReasonInExperiment':
    'Whether this flag evaluation is part of an experiment.',
  'observability.errorSearchKeys.featureFlagResultReasonKind':
    'The reason category for why a particular flag value was returned.',
  'observability.errorSearchKeys.featureFlagResultValue': 'The evaluated value of the feature flag for this context.',
  'observability.errorSearchKeys.featureFlagResultVariationIndex':
    'Index of the variation returned for this flag evaluation.',
  'observability.errorSearchKeys.featureFlagSetId': 'The id of the environment where the flag was evaluated.',
  'observability.errorSearchKeys.featureFlagTelemetrySdkName':
    'Name of the feature flag SDK used to generate telemetry.',
  'observability.errorSearchKeys.featureFlagTelemetrySdkVersion':
    'Version of the feature flag SDK used to generate telemetry.',
  'observability.errorSearchKeys.graphqlFieldArguments': 'Arguments passed to the GraphQL field.',
  'observability.errorSearchKeys.graphqlFieldName': 'Name of the GraphQL field where the error occurred.',
  'observability.errorSearchKeys.graphqlObjectType': 'Type of the GraphQL object where the error occurred.',
  'observability.errorSearchKeys.hasSession': 'Whether the error is associated with a user session.',
  'observability.errorSearchKeys.hostName': 'Hostname of the machine where the error occurred.',
  'observability.errorSearchKeys.lineNumber': 'Line number where the error occurred.',
  'observability.errorSearchKeys.osDescription': 'Description of the operating system.',
  'observability.errorSearchKeys.osName': 'Operating system name.',
  'observability.errorSearchKeys.osType': 'Type of operating system.',
  'observability.errorSearchKeys.otelScopeName': 'Name of the OpenTelemetry instrumentation scope.',
  'observability.errorSearchKeys.processExecutableName': 'Name of the process executable.',
  'observability.errorSearchKeys.processExecutablePath': 'Full path to the process executable.',
  'observability.errorSearchKeys.processOwner': 'Owner of the process.',
  'observability.errorSearchKeys.processPid': 'Process ID of the application.',
  'observability.errorSearchKeys.processRuntimeDescription': 'Description of the runtime environment.',
  'observability.errorSearchKeys.processRuntimeName': 'Name of the runtime environment.',
  'observability.errorSearchKeys.processRuntimeVersion': 'Version of the runtime environment.',
  'observability.errorSearchKeys.serviceName': 'Name of the service where the error occurred.',
  'observability.errorSearchKeys.serviceVersion': 'Version of the service where the error occurred.',
  'observability.errorSearchKeys.stackTraceColumnNumber': 'Column number in the source file where the error occurred.',
  'observability.errorSearchKeys.stackTraceError': 'The error message from the stack trace.',
  'observability.errorSearchKeys.stackTraceFileName': 'Name of the source file where the error occurred.',
  'observability.errorSearchKeys.stackTraceFunctionName': 'Name of the function where the error occurred.',
  'observability.errorSearchKeys.stackTraceLineContent': 'Content of the line where the error occurred.',
  'observability.errorSearchKeys.stackTraceLineNumber': 'Line number in the source file where the error occurred.',
  'observability.errorSearchKeys.stackTraceLinesAfter': 'Lines of code after the error location for context.',
  'observability.errorSearchKeys.stackTraceLinesBefore': 'Lines of code before the error location for context.',
  'observability.errorSearchKeys.telemetryDistroName': 'Name of the OpenTelemetry distribution.',
  'observability.errorSearchKeys.telemetryDistroVersion': 'Version of the OpenTelemetry distribution.',
  'observability.errorSearchKeys.telemetrySdkLanguage': 'Programming language of the telemetry SDK.',
  'observability.errorSearchKeys.telemetrySdkName': 'Name of the telemetry SDK.',
  'observability.errorSearchKeys.telemetrySdkVersion': 'Version of the telemetry SDK.',
  'observability.errorSearchKeys.userAgentOriginal': 'Original user agent string from the request.',
  'observability.errorStackTrace.atLine': ' at line ',
  'observability.errorStackTrace.learnMore': 'Learn More',
  'observability.errorStackTrace.noStacktraceBody':
    "This error was captured with no stacktrace. If you expect one, double check your instrumentation to ensure it's included.",
  'observability.errorStackTrace.noStacktraceTitle': 'No stacktrace available',
  'observability.errorStackTrace.sourcemapSettings': 'Sourcemap Settings',
  'observability.errorStackTrace.uploadSourcemapsBody':
    'If there are sourcemaps tied to your code, you can upload them to LaunchDarkly in CI/CD to get enhanced stack traces.',
  'observability.errorStackTrace.uploadSourcemapsTitle':
    'This stack trace could be more informative if you upload sourcemaps',
  'observability.errorStats.firstSeen': 'First seen',
  'observability.errorStats.instances': 'Instances',
  'observability.errorStats.lastSeen': 'Last seen',
  'observability.errorStats.usersImpacted': 'Users impacted',
  'observability.errorStatus.ignoredMessage':
    'This error is set to Ignored. You will not receive any alerts even if a new error gets thrown.',
  'observability.errorStatus.openMessage':
    'This error is set to Open. You will receive alerts when a new error gets thrown.',
  'observability.errorStatus.otherMessage': 'This error is set to {state}.',
  'observability.errorStatus.placeholder': 'Select day and time',
  'observability.errorStatus.resolvedMessage':
    'This error is set to Resolved. You will receive alerts when a new error gets thrown.',
  'observability.errorStatus.snooze': 'Snooze',
  'observability.errorStatus.snoozeNextWeek': 'Next Week',
  'observability.errorStatus.snoozeOneHour': '1 Hour',
  'observability.errorStatus.snoozeTomorrow': 'Tomorrow',
  'observability.errorStatus.snoozedMessage':
    'This error is snoozed until {date}. You will not receive any alerts even if a new error gets thrown.',
  'observability.errorStatus.snoozedUntilSuffix': '(Snoozed until {date})',
  'observability.errorStatus.status': 'Status',
  'observability.errorStatus.until': 'Until',
  'observability.errorStatus.updateFailed': 'There was an issue updating the state of this error. Try again.',
  'observability.errorsBreadcrumbs.navLabel': 'Breadcrumb navigation for this page.',
  'observability.errorsEmptyState.body': 'View a recent error or find a specific error message, URL, or segment.',
  'observability.errorsEmptyState.title': "Let's squash some bugs",
  'observability.errorsNotIntegrated.body': 'Follow the instructions to setup error monitoring in your app.',
  'observability.errorsNotIntegrated.learnMore': 'Learn more',
  'observability.errorsNotIntegrated.title': 'Errors are not integrated yet',
  'observability.errorsPage.extendRetentionPeriod': 'Extend retention period',
  'observability.errorsPage.notFoundBody': 'This error does not exist or has not been made public.',
  'observability.errorsPage.notFoundTitle': "Can't load error",
  'observability.errorsPage.retentionExpiredBody':
    'This error occurred before your current data retention window. To access older error data and prevent future data loss, consider extending your retention period.',
  'observability.errorsPage.retentionExpiredTitle': 'Error data expired',
  'observability.errorsSearchPanel.emptyStateTitle': 'No errors detected',
  'observability.errorsSearchPanel.loadErrorBody':
    'There was an error loading your errors. Reach out to us if this might be a bug.',
  'observability.errorsSearchPanel.loadErrorTitle': 'Failed to load errors',
  'observability.errorsSettings.errors': 'Errors',
  'observability.errorsTable.openLabel': 'Open',
  'observability.errorsTableCell.open': 'Open',
  'observability.errorsTableView.actionsLabel': 'Error group actions',
  'observability.errorsTableView.affectedUsersColumn': 'Affected users (30d)',
  'observability.errorsTableView.askVega': 'Ask {botName}',
  'observability.errorsTableView.copyLink': 'Copy link',
  'observability.errorsTableView.countNoun': 'error group',
  'observability.errorsTableView.errorBodyColumn': 'Error body',
  'observability.errorsTableView.firstOccurrenceColumn': 'First occurrence',
  'observability.errorsTableView.instancesColumn': 'Instances (30d)',
  'observability.errorsTableView.lastOccurrenceColumn': 'Last occurrence',
  'observability.errorsTableView.linkCopied': 'Link copied',
  'observability.errorsTableView.open': 'Open',
  'observability.errorsTableView.openInNewTab': 'Open in new tab',
  'observability.errorsTableView.stateIgnored': 'Ignored',
  'observability.errorsTableView.stateOpen': 'Open',
  'observability.errorsTableView.stateResolved': 'Resolved',
  'observability.errorsTableView.statusColumn': 'Status',
  'observability.errorsTableView.trendColumn': 'Trend (7d)',
  'observability.eventPicker.allEvents': 'All events',
  'observability.eventPicker.clearSearchLabel': 'Clear search',
  'observability.eventPicker.eventCountTooltip': '{count, number} events',
  'observability.eventPicker.loadingEvents': 'Loading events…',
  'observability.eventPicker.noEventsFound': 'No events found',
  'observability.eventPicker.searchPlaceholder': 'Search…',
  'observability.eventPicker.useQuery': 'Use “{query}”',
  'observability.eventPicker.wildcard': 'Wildcard',
  'observability.eventStream.clear': 'Clear',
  'observability.eventStream.descriptionColumn': 'Description',
  'observability.eventStream.emptyStateBody': 'No events found.',
  'observability.eventStream.emptyStateHeading': 'No events found',
  'observability.eventStream.emptyStateQueryBody': 'No events found for the current query.',
  'observability.eventStream.eventColumn': 'Event',
  'observability.eventStream.eventType': 'Event type',
  'observability.eventStream.eventTypeFiltersLabel': 'Event type filters',
  'observability.eventStream.filterEventsBy': 'Filter events by…',
  'observability.eventStream.hiddenEvents': '{count, plural, one {# event…} other {# events…}}',
  'observability.eventStream.matchCount': '{count, plural, one {# match} other {# matches}}',
  'observability.eventStream.matchModeFilter': 'Filter',
  'observability.eventStream.matchModeHighlight': 'Highlight',
  'observability.eventStream.matchModeLabel': 'Show matching events',
  'observability.eventStream.openLabel': 'Open',
  'observability.eventStream.playerTimeChanged': 'Changed player time to {time}',
  'observability.eventStream.searchPlaceholder': 'Search',
  'observability.eventStream.timestampColumn': 'Timestamp',
  'observability.excludedUsers.fieldLabel': 'Context keys or emails',
  'observability.excludedUsers.invalidRegex': '{value} is not a valid regular expression',
  'observability.excludedUsers.sectionLabel': 'Excluded users',
  'observability.externalMonitor.breachColumn': 'Breach',
  'observability.externalMonitor.breachReason':
    '{reason, select, opened {Issue opened} regressed {Issue regressed} other {Breached}}',
  'observability.externalMonitor.description':
    '{kind, select, sentry_monitor {This alert fires each time Sentry reports the Monitor breached: when Sentry opens an issue for it, or the issue regresses. Each breach fires once, then the alert returns to normal.} other {This alert fires each time the external monitor reports a breach, then returns to normal.}}',
  'observability.externalMonitor.editHint':
    '{kind, select, sentry_monitor {Change what counts as a breach on the Monitor in Sentry.} other {Change what counts as a breach in the external monitor.}}',
  'observability.externalMonitor.editTriggerHint':
    'To change what happens on a breach, edit the <triggerLink>adaptive trigger</triggerLink>.',
  'observability.externalMonitor.issueColumn': '{kind, select, sentry_monitor {Sentry issue} other {Issue}}',
  'observability.externalMonitor.issueId': 'Issue {issueId}',
  'observability.externalMonitor.observedValue':
    '{observed}, {condition, select, gt {above} gte {at or above} lt {below} lte {at or below} other {threshold}} {threshold}',
  'observability.externalMonitor.priority':
    '{priority, select, high {High} medium {Medium} low {Low} other {{priority}}}',
  'observability.externalMonitor.priorityColumn': 'Priority',
  'observability.externalMonitor.sourceName':
    '{kind, select, sentry_monitor {Sentry Monitor} other {External monitor}}',
  'observability.externalMonitor.valueColumn': 'Observed value',
  'observability.externalMonitor.viewIssue': '{kind, select, sentry_monitor {View issue in Sentry} other {View issue}}',
  'observability.externalMonitor.viewMonitor':
    '{kind, select, sentry_monitor {View Monitor in Sentry} other {View monitor}}',
  'observability.feedError.help': 'Help',
  'observability.feedError.reloadQuery': 'Reload query',
  'observability.filterRuleAttributesPreview.enterAFilter': 'Enter a filter query to see preview',
  'observability.filterRuleAttributesPreview.help': 'Help',
  'observability.filterRuleAttributesPreview.loadErrorTitle': 'Failed to load attributes',
  'observability.filterRuleAttributesPreview.thereWasAn': 'There was an error loading attributes for your query.',
  'observability.filterRuleDialog.attributeMode': 'Attribute mode',
  'observability.filterRuleDialog.closeLabel': 'Close',
  'observability.filterRuleDialog.deleteRule': 'Delete rule',
  'observability.filterRuleDialog.dropAttributes': 'Drop attributes',
  'observability.filterRuleDialog.exclusion': 'Exclusion',
  'observability.filterRuleDialog.exclusionTooltip': '<b>Exclusion</b> rules that match will drop {product}.',
  'observability.filterRuleDialog.filterPlaceholder': 'Filter…',
  'observability.filterRuleDialog.includeOnlyAttributes': 'Include only attributes',
  'observability.filterRuleDialog.inclusion': 'Inclusion',
  'observability.filterRuleDialog.inclusionTooltip': '<b>Inclusion</b> rules that match will ingest {product}.',
  'observability.filterRuleDialog.lastModified': 'Last modified:',
  'observability.filterRuleDialog.lastModifiedLabel': 'Last modified timestamp',
  'observability.filterRuleDialog.moreOptionsLabel': 'More options',
  'observability.filterRuleDialog.operationInfoLabel': 'Operation more information',
  'observability.filterRuleDialog.operationTooltip':
    '{product} support <b>{operation, select, include {inclusion} other {exclusion}}</b> operations.',
  'observability.filterRuleDialog.preview': 'Preview',
  'observability.filterRuleDialog.rateInfoLabel': 'Rate more information',
  'observability.filterRuleDialog.ruleFilterOperation': 'Rule filter operation',
  'observability.filterRuleDialog.ruleName': 'Rule name',
  'observability.filterRuleDialog.statistics': 'Statistics',
  'observability.filterRuleDialog.tabsLabel': 'Filter rule tabs',
  'observability.filterRuleMenu.clickTooltip': '<b>Click</b> to open menu',
  'observability.filterRuleMenu.deleteRule': 'Delete rule',
  'observability.filterRuleMenu.dragTooltip': '<b>Drag</b> to move',
  'observability.filterRuleMenu.duplicateRule': 'Duplicate rule',
  'observability.filterRuleMenu.edit': 'Edit',
  'observability.filterRuleMenu.triggerLabel': 'Rule options and drag',
  'observability.filterRuleMetricsPreview.count': 'Count',
  'observability.filterRuleMetricsPreview.enterAFilter': 'Enter a filter query to see preview',
  'observability.filterRuleMetricsPreview.help': 'Help',
  'observability.filterRuleMetricsPreview.loadErrorTitle': 'Failed to load metrics',
  'observability.filterRuleMetricsPreview.metric': 'Metric',
  'observability.filterRuleMetricsPreview.service': 'Service',
  'observability.filterRuleMetricsPreview.thereWasAn':
    'There was an error loading your metrics. Reach out to us if you think this is a bug.',
  'observability.filterRuleMetricsPreview.type': 'Type',
  'observability.filterRulePreview.help': 'Help',
  'observability.filterRulePreview.loadErrorBody':
    'There was an error loading your {resourceType}. Reach out to us if you think this is a bug.',
  'observability.filterRulePreview.loadErrorTitle': 'Failed to load {resourceType}',
  'observability.filterRulePreview.preview': 'Preview',
  'observability.filterRuleStatistics.chartTitle': 'Rule metrics',
  'observability.filterRuleStatistics.descriptionLabel': '{label} description',
  'observability.filterRuleStatistics.lastWeekTotal': 'Last week total: {value}',
  'observability.filterRuleStatistics.loadingMetricLabel': 'Loading metric',
  'observability.filterRuleStatistics.statistics': 'Statistics',
  'observability.filterRuleStatistics.statisticsLabel': '{label} statistics',
  'observability.filterRuleStatistics.thisWeekTotal': 'This week total: {value}',
  'observability.filterRulesEditor.addRule': 'Add rule',
  'observability.filterRulesEditor.back': 'Back',
  'observability.filterRulesEditor.docs': 'Docs',
  'observability.filterRulesEditor.dropMatchingMetrics': 'Drop matching metrics',
  'observability.filterRulesEditor.heading': '{product} filter rules',
  'observability.filterRulesEditor.includeExcludeAttributes': 'Include/Exclude attributes',
  'observability.filterRulesEditor.listLabel': 'Filter rules',
  'observability.filterRulesEditor.rule': 'Rule',
  'observability.filterRulesEditor.ruleName': 'Rule name',
  'observability.filterRulesEditor.save': 'Save',
  'observability.filterRulesEditor.saving': 'Saving…',
  'observability.filterRulesEditor.statisticsHeading': '{product} filter statistics',
  'observability.filterRulesNotifications.ruleCreated': 'Rule created successfully',
  'observability.filterRulesNotifications.ruleDeleteFailed': 'Failed to delete rule',
  'observability.filterRulesNotifications.ruleDeleted': 'Rule deleted successfully',
  'observability.filterRulesNotifications.ruleDisabled': 'Rule disabled',
  'observability.filterRulesNotifications.ruleEnabled': 'Rule enabled',
  'observability.filterRulesNotifications.ruleSaveFailed': 'Failed to save rule',
  'observability.filterRulesNotifications.ruleToggleFailed': 'Failed to toggle rule',
  'observability.filterRulesNotifications.ruleUpdated': 'Rule updated successfully',
  'observability.filterRulesNotifications.settingsSaveFailed': 'Failed to save settings',
  'observability.filterRulesNotifications.settingsSaved': 'Settings saved',
  'observability.filterRulesPanel.edit': 'Edit',
  'observability.filterRulesPanel.filters': 'Filters',
  'observability.filterRulesPanel.listLabel': 'Filter rules',
  'observability.filterRulesPanel.product': 'Product',
  'observability.filterRulesPanel.ruleCount': '{count, plural, =0 {No rules} one {# rule} other {# rules}}',
  'observability.filterRulesPanel.rules': 'Rules',
  'observability.filtersPanel.addFilter': 'Add filter',
  'observability.filtersPanel.attributes': 'Attributes',
  'observability.filtersPanel.attributesBody':
    'Manage the saved attributes for quick access and recommendations to be used.',
  'observability.filtersPanel.hideFilter': 'Hide filter',
  'observability.filtersPanel.moveFilterDown': 'Move filter down',
  'observability.filtersPanel.moveFilterUp': 'Move filter up',
  'observability.filtersPanel.onlyShowTop': 'Only show top level spans',
  'observability.filtersPanel.searchValues': 'Search values',
  'observability.filtersPanel.showLlmSpans': 'Show LLM spans only',
  'observability.filtersPanel.showMore': 'Show more',
  'observability.filtersPanel.showMoreValuesLabel': 'Show more values',
  'observability.filtersPanel.suggestionsErrorTitle': 'Failed to load suggestions',
  'observability.flagChangeDetails.change': 'Change',
  'observability.flagChangeDetails.comment': 'Comment',
  'observability.flagChangeDetails.environment': 'Environment',
  'observability.flagChangeDetails.viewDetails': 'View details',
  'observability.flagChangesPopover.emptyState': 'No changes found.',
  'observability.flagChangesPopover.unknownConfig': 'Unknown config',
  'observability.flagChangesPopover.unknownFlag': 'Unknown flag',
  'observability.flagChangesPopover.viewDetailsLabel': 'View details for {itemLabel}',
  'observability.flagEvaluations.activeFilterCountLabel':
    '{count, plural, one {# active filter} other {# active filters}}',
  'observability.flagEvaluations.addTags': 'Add tag(s)',
  'observability.flagEvaluations.clear': 'Clear',
  'observability.flagEvaluations.collapseAll': 'Collapse all',
  'observability.flagEvaluations.copyJson': 'Copy json',
  'observability.flagEvaluations.countColumn': 'Count',
  'observability.flagEvaluations.emptyStateBody': 'No flag evaluations found in this session',
  'observability.flagEvaluations.evaluationHistory': 'Evaluation History',
  'observability.flagEvaluations.expandAll': 'Expand all',
  'observability.flagEvaluations.filterFlagEvaluations': 'Filter flag evaluations by…',
  'observability.flagEvaluations.filtersButton': 'Filters',
  'observability.flagEvaluations.firstEvaluation': 'First Evaluation',
  'observability.flagEvaluations.flagKey': 'Flag Key',
  'observability.flagEvaluations.historyCountColumn': 'Count',
  'observability.flagEvaluations.noDataFound': 'No data found for this flag evaluation.',
  'observability.flagEvaluations.noEvaluationHistory': 'No evaluation history found',
  'observability.flagEvaluations.noMatchesBody': 'No flag evaluations match your filters',
  'observability.flagEvaluations.openAiConfig': 'Open AI Config',
  'observability.flagEvaluations.openContext': 'Open context',
  'observability.flagEvaluations.openFlag': 'Open flag',
  'observability.flagEvaluations.playerTimeChanged': 'Changed player time to {time}',
  'observability.flagEvaluations.searchPlaceholder': 'Search flags…',
  'observability.flagEvaluations.tagsColumn': 'Tags',
  'observability.flagEvaluations.tagsFilterLabel': 'Tags',
  'observability.flagEvaluations.timestamp': 'Timestamp',
  'observability.flagEvaluations.value': 'Value',
  'observability.flagOverlayPicker.allFlags': 'All flags',
  'observability.flagOverlayPicker.flagsMaintainedBy': 'Flags maintained by me',
  'observability.flagOverlayPicker.itemDescription': '{flagKey} · Last updated {lastUpdated}',
  'observability.flagOverlayPicker.maxSelected': 'Maximum {count} flags selected',
  'observability.flagOverlayPicker.neverModified': 'Never modified',
  'observability.flagOverlayPicker.quickSelectLabel': 'Quick selection options',
  'observability.flagOverlayPicker.searchLabel': 'Search flags',
  'observability.flagOverlayPicker.searchPlaceholder': 'Search flags…',
  'observability.flagOverlayPicker.triggerAll': 'Flags: All',
  'observability.flagOverlayPicker.triggerCount': 'Flags: {count} selected',
  'observability.flagOverlayPicker.triggerLabel': 'Select flags for overlay',
  'observability.flagOverlayPicker.triggerOne': 'Flags: {flagName}',
  'observability.formField.optionalBadge': 'Optional',
  'observability.frontendDashboard.network': 'Network',
  'observability.frontendDashboard.overview': 'Overview',
  'observability.frontendDashboard.resources': 'Resources',
  'observability.frontendDashboard.webVitals': 'Web Vitals',
  'observability.funnelChart.addFunnelSteps': 'Add funnel steps to see the visualization',
  'observability.funnelChart.dropoff': '↓ {dropped} dropped ({percent}%)',
  'observability.funnelChart.overallConversion': 'Overall conversion',
  'observability.funnelSteps.addFilter': 'Add filter',
  'observability.funnelSteps.addStep': 'Add step',
  'observability.funnelSteps.allEvents': 'All events',
  'observability.funnelSteps.allViews': 'All views',
  'observability.funnelSteps.deleteStep': 'Delete step',
  'observability.funnelSteps.editStepLabel': 'Edit step label',
  'observability.funnelSteps.moveDown': 'Move down',
  'observability.funnelSteps.moveUp': 'Move up',
  'observability.funnelSteps.searchSectionTooltip':
    'Define the ordered steps of the funnel. Each step is a search query, such as step=signup.',
  'observability.funnelSteps.sectionLabel': 'Steps',
  'observability.funnelSteps.sectionTooltip':
    'Define the ordered steps of the funnel. Each step matches an event. Use * as a wildcard or add filters to select multiple events.',
  'observability.funnelSteps.stepBadge': 'Step {number}',
  'observability.funnelSteps.stepFallback': 'Step',
  'observability.funnelSteps.stepLabelPlaceholder': 'Step label',
  'observability.funnelSteps.stepQueryLabel': 'Step query',
  'observability.graph.exemplarsLabel': 'Metric exemplars',
  'observability.graph.fetchMetricsError': 'An error occurred while fetching metrics',
  'observability.graph.genericError': 'An error occurred',
  'observability.graph.loadingLabel': 'Loading',
  'observability.graph.otherSeriesLabel': 'Other',
  'observability.graph.thresholdTooltip':
    'This value is {condition, select, above {above} below {below} other {near}} the alert threshold.',
  'observability.graph.truncatedLabel': 'Results truncated',
  'observability.graph.truncationTooltip':
    'Results limited to top {limit} groups. Narrow your time range or add filters to see all data.',
  'observability.graph.untitledGraph': 'Untitled graph',
  'observability.graph.viewExemplarTitle': 'View {kind}',
  'observability.graphActionBar.refreshLabel': 'Refresh',
  'observability.graphActionBar.templates': 'Templates',
  'observability.graphActionsMenu.addToDashboard': 'Add to dashboard',
  'observability.graphActionsMenu.barChart': 'Bar chart',
  'observability.graphActionsMenu.chartTypeLabel': 'Type',
  'observability.graphActionsMenu.cloneGraph': 'Clone graph',
  'observability.graphActionsMenu.createAlert': 'Create alert',
  'observability.graphActionsMenu.deleteGraph': 'Delete graph',
  'observability.graphActionsMenu.downloadCsv': 'Download CSV',
  'observability.graphActionsMenu.lineChart': 'Line chart',
  'observability.graphActionsMenu.moreActions': 'More actions',
  'observability.graphChartSettings.compareToLabel': 'Compare to',
  'observability.graphChartSettings.compareToTooltip':
    'Choose whether percentages are calculated relative to the first step or the previous step.',
  'observability.graphChartSettings.nullHandlingLabel': 'Null handling',
  'observability.graphChartSettings.nullsLabel': 'Nulls',
  'observability.graphChartSettings.nullsTooltip': 'Determines how null / empty values are handled.',
  'observability.graphChartSettings.orderingLabel': 'Ordering',
  'observability.graphChartSettings.orderingTooltip':
    "When strict, a session only counts at each step if its events occurred in the funnel's order. When any, steps count regardless of timing.",
  'observability.graphChartSettings.stackAreaLabel': 'Stack area',
  'observability.graphChartSettings.stackBarsLabel': 'Stack bars',
  'observability.graphEditor.addGraph': 'Add graph',
  'observability.graphEditor.byLabel': 'By',
  'observability.graphEditor.byTooltip': 'The function used to determine which groups are included.',
  'observability.graphEditor.cancel': 'Cancel',
  'observability.graphEditor.comboboxClearLabel': 'Clear selection',
  'observability.graphEditor.comboboxLoading': 'Loading…',
  'observability.graphEditor.comboboxOptionsLabel': 'Select options',
  'observability.graphEditor.comboboxSearchLabel': 'Search options',
  'observability.graphEditor.comboboxSearchPlaceholder': 'Search…',
  'observability.graphEditor.editGraphs': 'Edit graphs',
  'observability.graphEditor.functionLabel': 'Function',
  'observability.graphEditor.functionTooltip':
    'Determines how data points are aggregated. If the function requires a numeric field as input, one can be chosen.',
  'observability.graphEditor.groupByLabel': 'Group by',
  'observability.graphEditor.groupByTooltip':
    'A categorical field for grouping results into separate series. This applies to all graphs.',
  'observability.graphEditor.hideAllFilters': 'Hide all filters',
  'observability.graphEditor.limitLabel': 'Limit',
  'observability.graphEditor.limitPlaceholder': 'Enter limit',
  'observability.graphEditor.limitTooltip': 'The maximum number of groups to include. Currently, the max is 100.',
  'observability.graphEditor.maxGraphsTooltip':
    'Only 3 graphs can be active at once here. If you want to view more visualizations simultaneously, visit the <link>dashboards page</link>.',
  'observability.graphEditor.multiSwitchLabel': 'Options',
  'observability.graphEditor.optionDropdownLabel': 'Select option',
  'observability.graphEditor.readDocs': 'Read docs',
  'observability.graphEditor.revertToDefault': 'Revert to default',
  'observability.graphEditor.save': 'Save',
  'observability.graphEditor.showAllFilters': 'Show all filters',
  'observability.graphEditorForm.addFunction': 'Add function',
  'observability.graphEditorForm.breakdownByLabel': 'Breakdown by',
  'observability.graphEditorForm.breakdownByTooltip':
    'Split the funnel into cohorts by one or more event attributes (e.g. country, browser). Leave off for a single overall funnel.',
  'observability.graphEditorForm.bucketByLabel': 'Bucket by',
  'observability.graphEditorForm.bucketByTooltip':
    'The method for determining the bucket sizes — can be a fixed interval or fixed count.',
  'observability.graphEditorForm.bucketCountPlaceholder': 'Enter bucket count',
  'observability.graphEditorForm.bucketIntervalLabel': 'Bucket interval',
  'observability.graphEditorForm.bucketIntervalTooltip':
    'The time interval for each bucket. A smaller interval will display more granular data.',
  'observability.graphEditorForm.categoryCountLabel': 'Category count',
  'observability.graphEditorForm.categoryCountPlaceholder': 'Enter category count',
  'observability.graphEditorForm.categoryCountTooltip':
    'The number of categories to display. A higher value will show more categories. Currently, the max is 100.',
  'observability.graphEditorForm.categoryFieldLabel': 'Category field',
  'observability.graphEditorForm.categoryFieldTooltip': 'The field used to group results into categories.',
  'observability.graphEditorForm.contextKeyRequired': 'At least one context key is required for funnel queries.',
  'observability.graphEditorForm.addSeries': 'Add series',
  'observability.graphEditorForm.colorLabel': 'Color',
  'observability.graphEditorForm.colorTooltip':
    'Color used to plot this series. Auto assigns a color from the chart palette.',
  'observability.graphEditorForm.delete': 'Delete',
  'observability.graphEditorForm.deleteReferencedSeriesBody':
    'Formula series {references} reference this letter. Delete it anyway?',
  'observability.graphEditorForm.deleteSeriesAriaLabel': 'Delete series {letter}',
  'observability.graphEditorForm.deleteSeriesBody': 'Delete series {letter}?',
  'observability.graphEditorForm.deleteSeriesHeading': 'Delete series {letter}',
  'observability.graphEditorForm.filterAppliedAriaLabel': 'Filter applied',
  'observability.graphEditorForm.formulaAriaLabel': 'Formula',
  'observability.graphEditorForm.formulaLabel': 'Formula',
  'observability.graphEditorForm.formulaPlaceholder': 'A / B * 100',
  'observability.graphEditorForm.formulaTooltip':
    'Math over product-source letters, e.g. A / B * 100. Formula series cannot reference other formulas.',
  'observability.graphEditorForm.funnelGroupKeyTooltip':
    'The key that identifies each entity moving through the funnel, such as a session or a context key attribute.',
  'observability.graphEditorForm.funnelSourceTooltip':
    'What the funnel steps match: events, or search queries over logs or traces.',
  'observability.graphEditorForm.hiddenAriaLabel': 'Hidden',
  'observability.graphEditorForm.lastVisibleSeriesInfo': 'At least one series must stay on the graph.',
  'observability.graphEditorForm.rowsPlaceholder': 'Rows',
  'observability.graphEditorForm.selectMetricPlaceholder': 'Select metric…',
  'observability.graphEditorForm.seriesFilterTooltip': 'Filter for series {letter}.',
  'observability.graphEditorForm.seriesLabel': 'Series',
  'observability.graphEditorForm.seriesListAriaLabel': 'Graph series',
  'observability.graphEditorForm.seriesNameAriaLabel': 'Series name',
  'observability.graphEditorForm.seriesNamePlaceholder': 'Series {letter}',
  'observability.graphEditorForm.seriesNameTooltip':
    'Shown in the legend and the series list. The letter is how formulas reference this series.',
  'observability.graphEditorForm.seriesSourceTooltip': 'The resource being queried, or a calculated expression.',
  'observability.graphEditorForm.seriesTooltip':
    'Define the series plotted on this graph. Each series is a data query or a calculated expression. The letter is how formulas reference that series.',
  'observability.graphEditorForm.unitsAriaLabel': 'Units',
  'observability.graphEditorForm.unitsLabel': 'Units',
  'observability.graphEditorForm.unitsPlaceholder': '%, By/s',
  'observability.graphEditorForm.unitsTooltip': 'Display unit for the formula result, e.g. % or By/s.',
  'observability.graphEditorForm.visibilityLabel': 'Visibility',
  'observability.graphEditorForm.visibilityTooltip':
    'Visible plots this series. Hidden keeps it available as a formula input.',
  'observability.graphEditorForm.yAxisLabel': 'Y-axis',
  'observability.graphEditorForm.yAxisTooltip':
    'Left or Right plots this series. Hidden keeps it available as a formula input.',
  'observability.graphEditorForm.eventsLabel': 'Events',
  'observability.graphEditorForm.eventsTooltip':
    'Select the events to query. Use * as a wildcard or add filters to select multiple events.',
  'observability.graphEditorForm.exemplarsLabel': 'Exemplars',
  'observability.graphEditorForm.exemplarsTooltip':
    'Overlay representative trace and session exemplars on the chart. Clamp keeps the Y-axis on the metric series; Fit expands it to include exemplar points.',
  'observability.graphEditorForm.filterLabel': 'Filter',
  'observability.graphEditorForm.filterTooltip':
    'The search query used to filter which data points are included before aggregating.',
  'observability.graphEditorForm.filtersLabel': 'Filters',
  'observability.graphEditorForm.functionLabel': 'Function',
  'observability.graphEditorForm.functionTooltip':
    'Determines how data points are aggregated. If the function requires a numeric field as input, one can be chosen.',
  'observability.graphEditorForm.funnelFiltersTooltip': 'Applied to all funnel steps',
  'observability.graphEditorForm.heatmapFiltersTooltip':
    'The search query used to filter which clicks are included in the heatmap.',
  'observability.graphEditorForm.limitFunctionLabel': 'By',
  'observability.graphEditorForm.limitFunctionTooltip': 'The function used to determine which groups are included.',
  'observability.graphEditorForm.limitLabel': 'Limit',
  'observability.graphEditorForm.limitPlaceholder': 'Enter limit',
  'observability.graphEditorForm.limitTooltip': 'The maximum number of groups to include (max {max}).',
  'observability.graphEditorForm.nameLabel': 'Name',
  'observability.graphEditorForm.namePlaceholder': 'Untitled graph',
  'observability.graphEditorForm.queryBuilder': 'Query builder',
  'observability.graphEditorForm.relatedDashboardLabel': 'Related dashboard',
  'observability.graphEditorForm.removeExpressionLabel': 'Remove expression',
  'observability.graphEditorForm.sessionLabel': 'Session',
  'observability.graphEditorForm.sessionLockTooltip':
    'Lock the session background to the current page. This will prevent the session background from changing when the filters are changed.',
  'observability.graphEditorForm.sessionLockedLabel': 'Session background locked',
  'observability.graphEditorForm.showOtherLabel': 'Show other',
  'observability.graphEditorForm.showOtherTooltip':
    'Combine the groups past the limit into one Other series, so the groups add up to the total.',
  'observability.graphEditorForm.showOtherUnsupportedTooltip': 'Show other is available for Count, Sum, Min, and Max.',
  'observability.graphEditorForm.sourceLabel': 'Source',
  'observability.graphEditorForm.sourceTooltip': 'The resource being queried.',
  'observability.graphEditorForm.sqlEditor': 'SQL editor',
  'observability.graphEditorForm.styleLabel': 'Style',
  'observability.graphEditorForm.timeBucketsLabel': 'Time buckets',
  'observability.graphEditorForm.timeBucketsTooltip':
    'The number of time buckets on the X-axis. A higher value will display smaller, more granular buckets. Currently, the max is 100.',
  'observability.graphEditorForm.typeLabel': 'Type',
  'observability.graphEditorForm.urlPathLabel': 'URL Path',
  'observability.graphMenu.askVega': 'Ask Vega',
  'observability.graphMenu.cancel': 'Cancel',
  'observability.graphMenu.clamp': 'Clamp',
  'observability.graphMenu.cloneGraph': 'Clone graph',
  'observability.graphMenu.createAlert': 'Create alert',
  'observability.graphMenu.deleteBody':
    'This permanently deletes <name></name> from the dashboard. This cannot be undone.',
  'observability.graphMenu.deleteBodyUnnamed':
    'This permanently deletes the graph from the dashboard. This cannot be undone.',
  'observability.graphMenu.deleteGraph': 'Delete graph',
  'observability.graphMenu.deleteThisGraph': 'Delete this graph',
  'observability.graphMenu.downloadCsv': 'Download CSV',
  'observability.graphMenu.exemplars': 'Exemplars',
  'observability.graphMenu.fit': 'Fit',
  'observability.graphMenu.hide': 'Hide',
  'observability.graphMenu.moreActionsLabel': 'More actions',
  'observability.graphMenu.share': 'Share',
  'observability.graphPage.askVega': 'Ask Vega',
  'observability.graphPage.back': 'Back',
  'observability.graphPage.edit': 'Edit',
  'observability.graphPage.share': 'Share',
  'observability.heatmap.clickCount': '{count, plural, one {# click} other {# clicks}}',
  'observability.heatmap.loadingLabel': 'Loading',
  'observability.heatmap.noClicksLabel': 'No clicks found',
  'observability.heatmap.selectUrlPathLabel': 'Select a URL path',
  'observability.heatmap.sessionCount': '{count, plural, one {# session} other {# sessions}}',
  'observability.heatmap.showValuesTitle': 'Show values',
  'observability.heatmap.spotFallbackElement': 'Element',
  'observability.heatmap.spotLabel': 'Heatmap spot: {element}, {count} clicks',
  'observability.heatmap.tableEmptyLabel': 'No clicked elements found',
  'observability.heatmap.tableLoadingLabel': 'Loading clicked elements…',
  'observability.heatmap.thisElementWas': 'This element was not found in the session image above.',
  'observability.heatmap.userCount': '{count, plural, one {# user} other {# users}}',
  'observability.heatmap.viewLabel': 'View',
  'observability.heatmap.viewSessions': 'View sessions',
  'observability.javascriptSetup.configureBody': 'Configure the plugins when initializing the LaunchDarkly client:',
  'observability.javascriptSetup.docsLink': 'Read about the JavaScript SDK',
  'observability.javascriptSetup.heading': 'JavaScript and TypeScript setup',
  'observability.javascriptSetup.installBody':
    'Install the <code>SessionReplay</code> and <code>Observability</code> plugins:',
  'observability.javascriptSetup.installObservabilityBody': 'Install the <code>Observability</code> plugin:',
  'observability.jsonViewer.applyAsFilter': 'Apply as filter',
  'observability.jsonViewer.copyToYour': 'Copy to your clipboard',
  'observability.jsonViewer.downloadThisAs': 'Download this as JSON',
  'observability.jsonViewer.expandValueLabel': 'Expand large value',
  'observability.liveModeTooltip.newItemsPer': 'New items per second:',
  'observability.liveModeTooltip.pleaseNoteThat':
    'The proportion of new items is an approximation and may change over time depending on the rate of new items.',
  'observability.liveModeTooltip.proportionOfNew': 'Proportion of new items loaded:',
  'observability.llmAgentDashboard.bannerCatchRegressions': 'Catch AI regressions before your users do.',
  'observability.llmAgentDashboard.bannerCta': 'Explore AgentControl',
  'observability.llmAgentDashboard.bannerFixInMinutes': 'Fix bad AI responses in minutes, not deploys.',
  'observability.llmAgentDashboard.bannerMeasureThenImprove':
    'Measure your agents here. Make them better in AgentControl.',
  'observability.llmAgentDashboard.latencyHeading': 'Latency (ms)',
  'observability.llmAgentDashboard.llmCallsHeading': 'LLM Calls',
  'observability.llmModelsPanel.avgDuration': 'Avg Duration',
  'observability.llmModelsPanel.calls': 'Calls',
  'observability.llmModelsPanel.emptyState': 'No LLM model data found for the selected time range',
  'observability.llmModelsPanel.errorRate': 'Error Rate',
  'observability.llmModelsPanel.heading': 'Models',
  'observability.llmModelsPanel.inputTokens': 'Input Tokens',
  'observability.llmModelsPanel.loadError': 'Error loading model metrics',
  'observability.llmModelsPanel.model': 'Model',
  'observability.llmModelsPanel.outputTokens': 'Output Tokens',
  'observability.llmModelsPanel.p95Duration': 'P95 Duration',
  'observability.llmModelsPanel.provider': 'Provider',
  'observability.llmToolsPanel.avgDuration': 'Avg Duration',
  'observability.llmToolsPanel.emptyState': 'No tool execution data found for the selected time range',
  'observability.llmToolsPanel.errorRate': 'Error Rate',
  'observability.llmToolsPanel.executions': 'Executions',
  'observability.llmToolsPanel.heading': 'Tool Executions',
  'observability.llmToolsPanel.loadError': 'Error loading tool execution metrics',
  'observability.llmToolsPanel.p95Duration': 'P95 Duration',
  'observability.llmToolsPanel.toolName': 'Tool Name',
  'observability.llmTraceDetails.allAttributes': 'All Attributes',
  'observability.llmTraceDetails.function': 'FUNCTION',
  'observability.llmTraceDetails.llm': 'LLM',
  'observability.llmTraceDetails.llmSummary': 'LLM Summary',
  'observability.llmTraceDetails.properties': 'Properties',
  'observability.llmTraceDetails.requestTitle': 'Request',
  'observability.llmTraceDetails.responseTitle': 'Response',
  'observability.llmTraceDetails.review': 'Review',
  'observability.llmTraceDetails.spanIdTag': 'Span ID: {spanId}',
  'observability.llmTraceDetails.viewInContext': 'View in context',
  'observability.loadingBox.loading': 'Loading…',
  'observability.logDetails.addAsColumn': 'Add as column',
  'observability.logDetails.applyAsFilter': 'Apply as filter',
  'observability.logDetails.askAiLabel': 'Ask {botName}',
  'observability.logDetails.collapseLabel': 'Collapse',
  'observability.logDetails.copyJsonLabel': 'Copy json',
  'observability.logDetails.copyLinkLabel': 'Copy link',
  'observability.logDetails.expandLabel': 'Expand',
  'observability.logDetails.linkCopied': 'Copied link',
  'observability.logDetails.logCopied': 'Copied log',
  'observability.logDetails.removeAsColumn': 'Remove as column',
  'observability.logDetails.viewInContextLabel': 'View in context',
  'observability.logRelatedResources.noConversationTooltip': 'No conversation found for this log.',
  'observability.logRelatedResources.noSessionTooltip': 'No session found for this log.',
  'observability.logRelatedResources.sessionExcludedTooltip': 'The session for this log was excluded.',
  'observability.logRelatedResources.viewConversation': 'View conversation',
  'observability.logRelatedResources.viewConversations': 'View conversations',
  'observability.logRelatedResources.viewError': 'View error',
  'observability.logRelatedResources.viewSession': 'View session',
  'observability.logRelatedResources.viewTrace': 'View trace',
  'observability.logSearchKeys.browserLanguage': 'Language setting of the browser.',
  'observability.logSearchKeys.codeFilepath': 'File path where the log was generated.',
  'observability.logSearchKeys.codeFunction': 'Function or method where the log was generated.',
  'observability.logSearchKeys.codeLineno': 'Line number where the log was generated.',
  'observability.logSearchKeys.codeStacktrace': 'Stack trace of the error or log location.',
  'observability.logSearchKeys.containerId': 'Unique identifier of the container.',
  'observability.logSearchKeys.dbOperation': 'The type of database operation (e.g., SELECT, INSERT).',
  'observability.logSearchKeys.dbRowsAffected': 'Number of rows affected by the database operation.',
  'observability.logSearchKeys.dbSqlTable': 'The database table being accessed.',
  'observability.logSearchKeys.dbStatement': 'The database statement being executed.',
  'observability.logSearchKeys.dbSystem': 'Database system being used (e.g., PostgreSQL, MySQL).',
  'observability.logSearchKeys.environment': 'The environment where the log was generated (e.g., production, staging).',
  'observability.logSearchKeys.error': 'Error message or flag indicating an error occurred.',
  'observability.logSearchKeys.exceptionMessage': 'Message associated with the exception.',
  'observability.logSearchKeys.exceptionType': 'Type or class of the exception.',
  'observability.logSearchKeys.featureFlagContextId': 'Unique identifier for the evaluation context.',
  'observability.logSearchKeys.featureFlagContextKeys': 'Keys identifying the context used for flag evaluation.',
  'observability.logSearchKeys.featureFlagKey': 'The unique key identifying a specific feature flag.',
  'observability.logSearchKeys.featureFlagLaunchdarklyApplicationVersion': 'Version of the LaunchDarkly application.',
  'observability.logSearchKeys.featureFlagProviderName': 'The name of the feature flag provider or service.',
  'observability.logSearchKeys.featureFlagResultReasonErrorKind':
    'Type of error that occurred during flag evaluation, if any.',
  'observability.logSearchKeys.featureFlagResultReasonInExperiment':
    'Whether this flag evaluation is part of an experiment.',
  'observability.logSearchKeys.featureFlagResultReasonKind':
    'The reason category for why a particular flag value was returned.',
  'observability.logSearchKeys.featureFlagResultValue': 'The evaluated value of the feature flag for this context.',
  'observability.logSearchKeys.featureFlagResultVariationIndex':
    'Index of the variation returned for this flag evaluation.',
  'observability.logSearchKeys.featureFlagSetId': 'The id of the environment where the flag was evaluated.',
  'observability.logSearchKeys.featureFlagTelemetrySdkName': 'Name of the feature flag SDK used to generate telemetry.',
  'observability.logSearchKeys.featureFlagTelemetrySdkVersion':
    'Version of the feature flag SDK used to generate telemetry.',
  'observability.logSearchKeys.graph': 'GraphQL graph identifier.',
  'observability.logSearchKeys.graphqlOperationName': 'Name of the GraphQL operation.',
  'observability.logSearchKeys.hostName': 'Hostname of the machine running the service.',
  'observability.logSearchKeys.identifier': 'Unique identifier for the log entry.',
  'observability.logSearchKeys.level': 'The severity level of the log (e.g., INFO, ERROR, DEBUG).',
  'observability.logSearchKeys.name': 'Name of the log entry or entity.',
  'observability.logSearchKeys.osDescription': 'Description of the operating system.',
  'observability.logSearchKeys.osType': 'Type of operating system.',
  'observability.logSearchKeys.otelScopeName': 'Name of the OpenTelemetry instrumentation scope.',
  'observability.logSearchKeys.processExecutableName': 'Name of the process executable.',
  'observability.logSearchKeys.processExecutablePath': 'Full path to the process executable.',
  'observability.logSearchKeys.processOwner': 'Owner of the process.',
  'observability.logSearchKeys.processPid': 'Process ID of the application.',
  'observability.logSearchKeys.processRuntimeDescription': 'Description of the runtime environment.',
  'observability.logSearchKeys.processRuntimeName': 'Name of the runtime environment.',
  'observability.logSearchKeys.processRuntimeVersion': 'Version of the runtime environment.',
  'observability.logSearchKeys.serviceName': 'Name of the service that generated the log.',
  'observability.logSearchKeys.serviceVersion': 'Version of the service that generated the log.',
  'observability.logSearchKeys.source': 'The source or origin of the log entry.',
  'observability.logSearchKeys.telemetryDistroName': 'Name of the OpenTelemetry distribution.',
  'observability.logSearchKeys.telemetryDistroVersion': 'Version of the OpenTelemetry distribution.',
  'observability.logSearchKeys.telemetrySdkLanguage': 'Programming language of the telemetry SDK.',
  'observability.logSearchKeys.telemetrySdkName': 'Name of the telemetry SDK.',
  'observability.logSearchKeys.telemetrySdkVersion': 'Version of the telemetry SDK.',
  'observability.logSearchKeys.userAgentOriginal': 'Original user agent string from the request.',
  'observability.logsByLevelChart.heading': 'Logs by Level',
  'observability.logsCount.count': '{formattedCount} {count, plural, one {log} other {logs}}',
  'observability.logsEmptyState.ctaBody': 'Follow the steps below to connect your app.',
  'observability.logsEmptyState.ctaHeading': 'Start sending logs',
  'observability.logsEmptyState.heading': 'No logs detected',
  'observability.logsNoResults.body': 'Widen the time range, broaden your search, or clear any filters.',
  'observability.logsNoResults.docsLinkText': 'Read about log search syntax',
  'observability.logsNoResults.heading': 'No logs found',
  'observability.logsPage.teamHistory': 'Team history',
  'observability.logsTable.livePausedBody':
    'Live mode has been paused to conserve system resources. Refresh the page to re-enable it.',
  'observability.logsTable.loadErrorBody':
    'There was an error loading your logs. Reach out to us if this might be a bug.',
  'observability.logsTable.loadErrorTitle': 'Failed to load logs',
  'observability.logsTableCell.goTo': 'Go to',
  'observability.longhornOverlay.bevo': 'BEVO',
  'observability.longhornOverlay.hookEm': "HOOK 'EM! 🤘",
  'observability.markdown.blockedLinkTitle': 'Link to an untrusted destination was blocked',
  'observability.markdown.continueInVegaTitle': 'Continue this conversation in Vega',
  'observability.markdown.openSessionTitle': 'Open session',
  'observability.metricPanel.openInDashboardLabel': 'Open in dashboard edit view',
  'observability.metricsPage.countBadge': '{formattedCount} {count, plural, one {metric} other {metrics}}',
  'observability.metricsPage.description':
    'There was an error loading your metrics. Reach out to us if this might be a bug.',
  'observability.metricsPage.excludeMetric': 'Exclude this metric',
  'observability.metricsPage.filterToMetric': 'Filter to this metric',
  'observability.metricsPage.metricColumn': 'Metric',
  'observability.metricsPage.noMetricsFound': 'No metrics found for the selected time range',
  'observability.metricsPage.open': 'Open',
  'observability.metricsPage.serviceColumn': 'Service',
  'observability.metricsPage.tableLabel': 'Metrics',
  'observability.metricsPage.title': 'Failed to load metrics',
  'observability.metricsPage.typeColumn': 'Type',
  'observability.metricsPage.volumeColumn': 'Volume',
  'observability.multiTabSessionDropdown.allRelatedTabs': 'All related tabs',
  'observability.multiTabSessionDropdown.sessionId': 'SessionID: {sessionId}',
  'observability.multiTabSessionDropdown.tabLabel': 'Tab {number}',
  'observability.multiTabSessionDropdown.triggerLabel': '{tab}, select related session',
  'observability.networkRequests.avgDuration': 'Avg Duration',
  'observability.networkRequests.count': 'Count',
  'observability.networkRequests.domainOperation': 'Domain/Operation',
  'observability.networkRequests.errorLoadingNetwork': 'Error loading network requests',
  'observability.networkRequests.errorRate': 'Error Rate',
  'observability.networkRequests.method': 'Method',
  'observability.networkRequests.networkRequests': 'Network Requests',
  'observability.networkRequests.noNetworkRequests': 'No network requests found for the selected time range',
  'observability.networkRequests.noOperationsFound': 'No operations found',
  'observability.networkRequests.p95Duration': 'P95 Duration',
  'observability.networkRequests.viewTracesLabel': 'View Traces',
  'observability.nodeSetup.configureBody':
    'Configure the plugin when initializing the LaunchDarkly server-side client:',
  'observability.nodeSetup.docsLink': 'Read about the Node.js SDK',
  'observability.nodeSetup.heading': 'Node.js server-side setup',
  'observability.nodeSetup.installBody': 'Install the <code>@launchdarkly/observability-node</code> plugin:',
  'observability.observabilitySettings.loadingLabel': 'Loading observability settings',
  'observability.observabilitySettingsHeader.heading': 'Observability settings',
  'observability.observabilitySettingsHeader.save': 'Save',
  'observability.observabilitySettingsNotifications.saveFailed': 'Error updating observability settings',
  'observability.observabilitySettingsNotifications.saved': 'Successfully updated observability settings',
  'observability.observabilitySetup.addObservabilityPlugins': 'Add observability plugins to your SDK',
  'observability.observabilitySetup.copyCodeLabel': 'Copy code',
  'observability.observabilitySetup.docsLinkText': 'Follow our docs',
  'observability.observabilitySetup.docsPrompt': 'Looking for more details? <docsLink></docsLink> instead.',
  'observability.observabilitySetup.environmentLabel': 'Environment',
  'observability.observabilitySetup.environmentValue': 'Environment: {label}',
  'observability.observabilitySetup.importStepTitle': 'Import plugins into your code',
  'observability.observabilitySetup.initializeStepTitle': 'Initialize the SDK client with plugins',
  'observability.observabilitySetup.installStepTitle': 'Install plugins',
  'observability.observabilitySetup.languageLabel': 'Language',
  'observability.observabilitySetup.packageManagerLabel': 'Package Manager',
  'observability.operationsPanel.count': 'Count',
  'observability.operationsPanel.emptyState': 'No operations found for the selected time range',
  'observability.operationsPanel.errorRate': 'Error Rate',
  'observability.operationsPanel.errors': 'Errors',
  'observability.operationsPanel.heading': 'Operations',
  'observability.operationsPanel.loadError': 'Error loading operations',
  'observability.operationsPanel.next': 'Next',
  'observability.operationsPanel.operationName': 'Operation Name',
  'observability.operationsPanel.p50Duration': 'P50 Duration',
  'observability.operationsPanel.p95Duration': 'P95 Duration',
  'observability.operationsPanel.p99Duration': 'P99 Duration',
  'observability.operationsPanel.pagination': 'Page {current} of {total}',
  'observability.operationsPanel.previous': 'Previous',
  'observability.panelSearch.clearLabel': 'Clear filter',
  'observability.payloadViewer.copyToClipboard': 'Copy to Clipboard',
  'observability.payloadViewer.downloadThisAs': 'Download this as JSON',
  'observability.payloadViewer.payloadIsLarge': 'Payload is large and could be difficult to view in the browser.',
  'observability.payloadViewer.showPayload': 'Show payload',
  'observability.playerControls.absoluteTimeLabel': 'Absolute time',
  'observability.playerControls.autoplayLabel': 'Autoplay',
  'observability.playerControls.backToSession': 'Back to session settings',
  'observability.playerControls.close': 'Close',
  'observability.playerControls.downloadVideo': 'Download video',
  'observability.playerControls.exportError': 'An error occurred exporting the session: {error}',
  'observability.playerControls.exportQueued': 'Session export will appear in your project settings once ready.',
  'observability.playerControls.mouseTrailLabel': 'Mouse trail',
  'observability.playerControls.settingsLabel': 'Player settings',
  'observability.playerControls.skipInactiveLabel': 'Skip inactive',
  'observability.playerControls.speed': 'Speed +/-',
  'observability.playerControls.viewProjectSettings': 'View project settings',
  'observability.productQueryBar.viewDocs': 'View docs',
  'observability.pullRequestSnapshot.changedFiles': '· {count, plural, one {# file} other {# files}}',
  'observability.pullRequestSnapshot.checks': 'Checks <count></count>',
  'observability.pullRequestSnapshot.checksFailing': '· {count} failing',
  'observability.pullRequestSnapshot.checksPending': '· {count} pending',
  'observability.pullRequestSnapshot.description': 'Description',
  'observability.pullRequestSnapshot.pullRequestSnapshot': 'Pull request snapshot',
  'observability.pullRequestSnapshot.reviews': 'Reviews',
  'observability.pullRequestSnapshot.updated': 'Updated {relative}',
  'observability.pythonSetup.configureBody':
    'Configure the observability plugin when initializing the LaunchDarkly client:',
  'observability.pythonSetup.docsLink': 'Read about the Python SDK',
  'observability.pythonSetup.heading': 'Python server-side setup',
  'observability.pythonSetup.installBody': 'Install the <code>ldobserve</code> package:',
  'observability.pythonSetup.recordLogsBody': 'Record logs and start spans:',
  'observability.rageClicksSettings.elapsedTimeLabel': 'Elapsed time (seconds)',
  'observability.rageClicksSettings.minimumClicksLabel': 'Minimum clicks',
  'observability.rageClicksSettings.radiusLabel': 'Radius (pixels)',
  'observability.rageClicksSettings.sectionLabel': 'Rage clicks',
  'observability.reactNativeSetup.alternativeBody': 'Alternative initialization with ReactNativeLDClient:',
  'observability.reactNativeSetup.configureBody': 'Configure the plugin when initializing the LaunchDarkly client:',
  'observability.reactNativeSetup.docsLink': 'Read about the React Native SDK',
  'observability.reactNativeSetup.heading': 'React Native setup',
  'observability.reactNativeSetup.installBody':
    'Install the <code>@launchdarkly/observability-react-native</code> plugin:',
  'observability.reactSetup.configureBody': 'Configure the plugins when initializing the LaunchDarkly client:',
  'observability.reactSetup.docsLink': 'Read about the React SDK',
  'observability.reactSetup.errorBoundaryBody':
    'Forward errors from an <link>Error Boundary</link> component to LaunchDarkly:',
  'observability.reactSetup.heading': 'React and Next.js setup',
  'observability.reactSetup.installBody':
    'Install the <code>SessionReplay</code> and <code>Observability</code> plugins:',
  'observability.reactSetup.installObservabilityBody': 'Install the <code>Observability</code> plugin:',
  'observability.relatedResourcesMenu.relatedResources': 'Related resources',
  'observability.relatedResourcesTable.emptyStateTitle': 'No matching {resourceType} found',
  'observability.relatedResourcesTable.help': 'Help',
  'observability.relatedResourcesTable.loadErrorBody':
    'There was an error loading your {resourceType}. Reach out to us if you think this is a bug.',
  'observability.relatedResourcesTable.loadErrorTitle': 'Failed to load {resourceType}',
  'observability.relatedResourcesTable.searchSpecification': 'Search specification',
  'observability.relatedResourcesTable.tryUsingA': 'Try using a more generic search query or removing filters.',
  'observability.resourceMetricsCharts.loadTimeHeading': 'Resource Load Time',
  'observability.resourceMetricsCharts.requestsHeading': 'Resource Requests',
  'observability.resourcePanelHeader.backLabel': 'Go back',
  'observability.resourcePanelHeader.closeLabel': 'Close',
  'observability.resourcePanelHeader.copyLink': 'Copy link',
  'observability.resourcePanelHeader.openFullPageLabel': 'Open in full page',
  'observability.resultsCountHeader.downloadCsv': 'Download CSV',
  'observability.resultsCountHeader.downloadCsvLabel': 'Download {noun}s as CSV',
  'observability.resultsCountHeader.thisResultIs': 'This result is an estimation from a subset of the data.',
  'observability.resultsEmptyState.heading': "Couldn''t find any relevant {kind}",
  'observability.resultsEmptyState.ifYouThink': "If you think something's wrong, feel free to reach out to us",
  'observability.resultsEmptyState.learnMore': 'Learn more',
  'observability.resultsHistogram.loading': 'Loading',
  'observability.resultsPagination.nextLabel': 'Next',
  'observability.resultsPagination.previousLabel': 'Previous',
  'observability.saveSearchDialog.body':
    'Saved searches allow you to save queries that target a specific set of {context}s.',
  'observability.saveSearchDialog.cancel': 'Cancel',
  'observability.saveSearchDialog.filtersLabel': 'Filters',
  'observability.saveSearchDialog.nameLabel': 'Name',
  'observability.saveSearchDialog.namePlaceholder': 'Type name…',
  'observability.savedSearchMenu.clearSavedSearch': 'Clear saved search',
  'observability.savedSearchMenu.delete': 'Delete',
  'observability.savedSearchMenu.duplicate': 'Duplicate',
  'observability.savedSearchMenu.editName': 'Edit name',
  'observability.savedSearchMenu.menuLabel': 'Segment options',
  'observability.savedSearchMenu.newSavedSearch': 'New saved search',
  'observability.savedSearchMenu.reset': 'Reset',
  'observability.savedSearchMenu.save': 'Save',
  'observability.savedSearchMenu.savedSearchSettings': 'Saved search settings',
  'observability.savedSearchMenu.selectSavedSearch': 'Select saved search',
  'observability.savedSearchMenu.update': 'Update',
  'observability.savedSearchMenu.updateErrorToast': 'Error updating saved search',
  'observability.savedSearchMenu.updatedToast': "Updated ''{name}''",
  'observability.savedSearchModal.createErrorToast': 'Error updating saved search: {message}',
  'observability.savedSearchModal.createdToast': "Created ''{name}'' saved search",
  'observability.savedSearchModal.deleteErrorToast': 'Error deleting saved search',
  'observability.savedSearchModal.deletedToast': 'Deleted saved search',
  'observability.savedSearchModal.renameErrorToast': 'Error updating segment: {message}',
  'observability.savedSearchModal.renamedToast': "Changed ''{name}'' name to ''{newName}''",
  'observability.savedSegmentQuery.empty': 'Empty',
  'observability.searchBar.copyLinkTo': 'Copy link to current query',
  'observability.searchBar.createAlert': 'Create alert',
  'observability.searchBar.createGraph': 'Create graph',
  'observability.searchBar.linkCopiedToast': 'Link copied to clipboard',
  'observability.searchBar.monitor': 'Monitor',
  'observability.searchDetailsPanel.attributeMetadataLabel': 'Attribute metadata',
  'observability.searchDetailsPanel.builtIn': 'Built-in',
  'observability.searchDetailsPanel.description': 'Description',
  'observability.searchDetailsPanel.example': 'Example',
  'observability.searchDetailsPanel.lastSearched': 'Last searched {timeAgo}',
  'observability.searchDetailsPanel.mostUsedValues': 'Most used values',
  'observability.searchDetailsPanel.rowCount': '{count} rows',
  'observability.searchDetailsPanel.sessionEventTypeDescription':
    'The type of event recorded during the session, such as Click, Navigate, or Track.',
  'observability.searchSuggestions.enterKeyLabel': 'Enter',
  'observability.searchSuggestions.loading': 'Loading…',
  'observability.searchSuggestions.navigate': 'Navigate',
  'observability.searchSuggestions.recent': 'Recent',
  'observability.searchSuggestions.relationalFilters': 'Relational Filters',
  'observability.searchSuggestions.select': 'Select',
  'observability.searchSuggestions.selectNew': 'Select+New',
  'observability.searchSuggestions.showAllResults': 'Show all results for',
  'observability.searchSuggestions.viewDocs': 'View docs',
  'observability.select.searchPlaceholder': 'Search…',
  'observability.serviceAlerts.empty': 'No alerts reference this service.',
  'observability.serviceAlerts.filterPlaceholder': 'Filter alerts',
  'observability.serviceAlerts.loadError': 'Unable to load alerts',
  'observability.serviceAlerts.noMatches': 'No matches for “{search}”',
  'observability.serviceAlerts.searchPlaceholder': 'Search for alerts',
  'observability.serviceDependencies.dragToMove': 'Drag to move',
  'observability.serviceDependencies.noServiceDependency': 'No service dependency data available',
  'observability.serviceDependencies.scrollToZoom': '{modifier} + scroll to zoom',
  'observability.serviceDependencies.unableToLoad': 'Unable to load dependencies',
  'observability.serviceDetails.loadError': 'Unable to load data',
  'observability.serviceDetails.monitoring': 'Monitoring',
  'observability.serviceDetails.settings': 'Settings',
  'observability.serviceErrors.empty': 'No errors found for this service in this time range.',
  'observability.serviceErrors.eventColumn': 'Event',
  'observability.serviceErrors.filterPlaceholder': 'Filter errors',
  'observability.serviceErrors.lastOccurrenceColumn': 'Last occurrence',
  'observability.serviceErrors.loadError': 'Unable to load errors',
  'observability.serviceErrors.noMatches': 'No matches for “{search}”',
  'observability.serviceErrors.searchPlaceholder': 'Search for errors',
  'observability.serviceErrors.stateColumn': 'State',
  'observability.serviceErrors.typeColumn': 'Type',
  'observability.serviceEvaluations.noMatches': 'No matches for “{search}”',
  'observability.serviceFilter.allServices': 'All services',
  'observability.serviceFilter.quickSelectionLabel': 'Quick selection',
  'observability.serviceFilter.searchLabel': 'Search services',
  'observability.serviceFilter.searchPlaceholder': 'Search services…',
  'observability.serviceFilter.showOnlyLabel': 'Show only {name}',
  'observability.serviceFilter.triggerLabel': 'Select services',
  'observability.serviceMapDashboard.backToOverview': '← Back to overview',
  'observability.serviceMapDashboard.backToOverviewLabel': 'Back to overview',
  'observability.serviceMapDashboard.emptyState': 'No service dependencies found for this time range.',
  'observability.serviceMapDashboard.loadError': 'Error loading Service Map',
  'observability.serviceMonitoring.aiConfigs': 'Configs',
  'observability.serviceMonitoring.aiConfigsEmpty': 'No configs found for this service in this time range',
  'observability.serviceMonitoring.aiConfigsLoadError': 'Unable to load configs',
  'observability.serviceMonitoring.aiConfigsSearchPlaceholder': 'Search for configs',
  'observability.serviceMonitoring.alerts': 'Alerts',
  'observability.serviceMonitoring.dependencies': 'Dependencies',
  'observability.serviceMonitoring.durationTitle': 'Duration (ms)',
  'observability.serviceMonitoring.errors': 'Errors',
  'observability.serviceMonitoring.flags': 'Flags',
  'observability.serviceMonitoring.flagsEmpty': 'No flags found for this service in this time range',
  'observability.serviceMonitoring.flagsLoadError': 'Unable to load flags',
  'observability.serviceMonitoring.flagsSearchPlaceholder': 'Search for flags',
  'observability.serviceMonitoring.inputTokensSeries': 'Input Tokens',
  'observability.serviceMonitoring.operations': 'Operations',
  'observability.serviceMonitoring.outputTokensSeries': 'Output Tokens',
  'observability.serviceMonitoring.queryDurationTitle': 'Query Duration (ms)',
  'observability.serviceMonitoring.queryErrorRateTitle': 'Query Error Rate',
  'observability.serviceMonitoring.queryRateTitle': 'Query Rate',
  'observability.serviceMonitoring.tokenUsageTitle': 'Token Usage',
  'observability.serviceOperations.countColumn': 'Count',
  'observability.serviceOperations.empty': 'No operations found for the selected time range',
  'observability.serviceOperations.errorLoadingOperations': 'Error loading operations',
  'observability.serviceOperations.errorRateColumn': 'Error rate',
  'observability.serviceOperations.errorsColumn': 'Errors',
  'observability.serviceOperations.filterPlaceholder': 'Filter operations',
  'observability.serviceOperations.next': 'Next',
  'observability.serviceOperations.noMatches': 'No matches for “{search}”',
  'observability.serviceOperations.operationNameColumn': 'Operation name',
  'observability.serviceOperations.p50Column': 'P50 duration',
  'observability.serviceOperations.p95Column': 'P95 duration',
  'observability.serviceOperations.p99Column': 'P99 duration',
  'observability.serviceOperations.pageIndicator': 'Page {currentPage} of {totalPages}',
  'observability.serviceOperations.previous': 'Previous',
  'observability.serviceOperations.searchPlaceholder': 'Search for operations',
  'observability.servicePicker.searchLabel': 'Search services',
  'observability.servicePicker.searchPlaceholder': 'Search services…',
  'observability.servicePicker.triggerLabel': 'Select services',
  'observability.serviceSettings.loadErrorBody': "Something went wrong loading this service's settings.",
  'observability.serviceSettings.loadErrorTitle': 'Failed to load service settings',
  'observability.serviceSettings.rootSpanFilter': 'Root Span Filter',
  'observability.serviceSettings.saveFailed': 'Failed to save settings',
  'observability.serviceSettings.savedToast': 'Settings saved',
  'observability.servicesList.clearFilterLabel': 'Clear filter',
  'observability.servicesList.emptyState': 'No services found',
  'observability.servicesList.errorsColumn': 'Errors',
  'observability.servicesList.filterLabel': 'Filter services',
  'observability.servicesList.loadError': 'Error loading services',
  'observability.servicesList.noResults': 'No services match your filter',
  'observability.servicesList.open': 'Open',
  'observability.servicesList.requestsColumn': 'Requests',
  'observability.servicesList.serviceNameColumn': 'Service name',
  'observability.servicesList.tableLabel': 'Services',
  'observability.servicesPage.list': 'List',
  'observability.servicesPage.map': 'Map',
  'observability.sessionActionsMenu.createHeatmap': 'Create heatmap',
  'observability.sessionActionsMenu.createJiraIssue': 'Create Jira issue',
  'observability.sessionActionsMenu.giveFeedback': 'Give feedback',
  'observability.sessionActionsMenu.label': 'Session actions',
  'observability.sessionActionsMenu.share': 'Share',
  'observability.sessionEmptyState.body': 'View a recent session or find a specific identifier, URL, or segment.',
  'observability.sessionEmptyState.title': 'Ready to see your app?',
  'observability.sessionExports.download': 'Download',
  'observability.sessionExports.emptyState': 'Sessions you export appear here.',
  'observability.sessionExports.emptyStateTitle': 'No session exports',
  'observability.sessionExports.loadingLabel': 'Loading session exports',
  'observability.sessionExports.openSession': 'Open session',
  'observability.sessionExports.requestTime': 'Request time',
  'observability.sessionExports.sessionLength': 'Session length',
  'observability.sessionExports.status': 'Status',
  'observability.sessionExports.tableLabel': 'Session exports',
  'observability.sessionFeedCard.filterByErrorsLabel': 'Filter by sessions with errors',
  'observability.sessionFeedCard.filterByFirstTimeUsersLabel': 'Filter by first time users',
  'observability.sessionFeedCard.filterByLiveLabel': 'Filter by live sessions',
  'observability.sessionFeedCard.filterByRageClicksLabel': 'Filter by sessions with rage clicks',
  'observability.sessionFeedCard.filterByUnviewedLabel': 'Filter by unviewed sessions',
  'observability.sessionFeedCard.live': 'Live',
  'observability.sessionFeedHistogram.activeHours': 'active hours',
  'observability.sessionFeedHistogram.inactiveHours': 'inactive hours',
  'observability.sessionFeedHistogram.sessions': 'sessions',
  'observability.sessionFeedHistogram.sessionsWithErrors': 'w/errors',
  'observability.sessionFeedSettings.dateFormatLabel': 'Date format',
  'observability.sessionFeedSettings.histogramFormatLabel': 'Session Histogram format',
  'observability.sessionFeedSettings.querySettings': 'Query settings',
  'observability.sessionFeedSettings.resultFormatLabel': 'Result format',
  'observability.sessionFeedSettings.sortByLabel': 'Sort by',
  'observability.sessionMetadataOverlay.copiedToClipboard': 'Copied to clipboard',
  'observability.sessionMetadataOverlay.copyFieldLabel': 'Copy {field}',
  'observability.sessionMetadataOverlay.copyIdentityLabel': 'Copy {value}',
  'observability.sessionMetadataOverlay.locationLabel': 'Session location',
  'observability.sessionMetadataOverlay.notIdentifiedLabel': 'Session not identified',
  'observability.sessionMetadataOverlay.notIdentifiedTooltip':
    'This session was not identified. The user information is inferred from a similar session in the same browser. Click to learn more.',
  'observability.sessionMetadataOverlay.overlayLabel': 'Session details',
  'observability.sessionMetadataOverlay.startTimeLabel': 'Session start time',
  'observability.sessionMetadataOverlay.viewportLabel': 'Viewport and browser',
  'observability.sessionPlayer.loadErrorTitle': "Can't load session",
  'observability.sessionPlayer.manualStopBody':
    '<docsLink>H.stop()</docsLink> was called during the session. Calling this method stops the session recording. If you expect the recording to continue, check where you are calling <docsLink>H.stop()</docsLink>.',
  'observability.sessionPlayer.manualStopTitle': 'Session recording manually stopped',
  'observability.sessionPlayer.noMoreSessions': 'No more sessions to play.',
  'observability.sessionPlayer.notReadyBody':
    'We need more time to process this session. If this looks like a bug, reach out to us.',
  'observability.sessionPlayer.notReadyTitle': "Session isn't ready to view yet 😔",
  'observability.sessionPlayer.playerTimeChangedToError': 'Changed player time to where error was thrown at {time}.',
  'observability.sessionPlayer.processingBody':
    'We are processing the data and will show the recording here soon. Come back in a minute.',
  'observability.sessionPlayer.processingTitle': 'This session is on the way',
  'observability.sessionPlayerHeader.copy': 'Copy',
  'observability.sessionPlayerHeader.detailsLabel': 'Session details',
  'observability.sessionPlayerHeader.openDevToolsLabel': 'Open dev tools',
  'observability.sessionPlayerHeader.openInNew': 'Open in new tab',
  'observability.sessionPlayerHeader.tools': 'Tools',
  'observability.sessionReplayCell.failedToLoad': 'Failed to load session replay.',
  'observability.sessionReplayCell.loadingLabel': 'Loading session',
  'observability.sessionReplayCell.replayLabel': 'Session replay',
  'observability.sessionReplayCell.unavailableLabel': 'Session replay unavailable',
  'observability.sessionReplayCell.viewReplayLabel': 'View session replay',
  'observability.sessionReplayCta.dismissLabel': 'Dismiss recommendation',
  'observability.sessionReplayCta.replayRealUser': 'Replay real user sessions on what you just shipped',
  'observability.sessionReplayCta.sessionsIncluded': '{count} sessions a month included',
  'observability.sessionReplayCta.setUpSession': 'Set up session replay',
  'observability.sessionReplayCta.watchRecordingsOf':
    'Watch recordings of real users on this flag, tagged with the flag variation they were served. Errors, logs, and traces turn on with the same setup.',
  'observability.sessionReplayUnavailable.docsLinkText': 'Read about LaunchDarkly in the European Union (EU)',
  'observability.sessionReplayUnavailable.heading': 'Session replay is not available in the EU instance',
  'observability.sessionSearchKeys.browserName': 'Name of the browser being used.',
  'observability.sessionSearchKeys.browserVersion': 'Version of the browser being used.',
  'observability.sessionSearchKeys.city': 'City where the user is located.',
  'observability.sessionSearchKeys.clickSelector': 'CSS selector of the clicked element.',
  'observability.sessionSearchKeys.clickTarget': 'The DOM element that was clicked.',
  'observability.sessionSearchKeys.clickTextContent': 'Text content of the clicked element.',
  'observability.sessionSearchKeys.country': 'Country where the user is located.',
  'observability.sessionSearchKeys.data': 'Custom data or payload associated with the event.',
  'observability.sessionSearchKeys.deviceId': 'Unique identifier for the device.',
  'observability.sessionSearchKeys.devicePlatform': 'Platform of the device (e.g., iOS, Android, Desktop).',
  'observability.sessionSearchKeys.domain': 'Domain name of the website.',
  'observability.sessionSearchKeys.email': 'Email address associated with the event.',
  'observability.sessionSearchKeys.environment':
    'The environment where the event occurred (e.g., production, staging).',
  'observability.sessionSearchKeys.event': 'The type or name of the tracked event.',
  'observability.sessionSearchKeys.exitPage': 'The page from which the user exited.',
  'observability.sessionSearchKeys.featureFlagContextId': 'Unique identifier for the evaluation context.',
  'observability.sessionSearchKeys.featureFlagContextKeys': 'Keys identifying the context used for flag evaluation.',
  'observability.sessionSearchKeys.featureFlagKey': 'The unique key identifying a specific feature flag.',
  'observability.sessionSearchKeys.featureFlagLaunchdarklyApplicationVersion':
    'Version of the LaunchDarkly application.',
  'observability.sessionSearchKeys.featureFlagProviderName': 'The name of the feature flag provider or service.',
  'observability.sessionSearchKeys.featureFlagResultReasonErrorKind':
    'Type of error that occurred during flag evaluation, if any.',
  'observability.sessionSearchKeys.featureFlagResultReasonInExperiment':
    'Whether this flag evaluation is part of an experiment.',
  'observability.sessionSearchKeys.featureFlagResultReasonKind':
    'The reason category for why a particular flag value was returned.',
  'observability.sessionSearchKeys.featureFlagResultValue': 'The evaluated value of the feature flag for this context.',
  'observability.sessionSearchKeys.featureFlagResultVariationIndex':
    'Index of the variation returned for this flag evaluation.',
  'observability.sessionSearchKeys.featureFlagSetId': 'The id of the environment where the flag was evaluated.',
  'observability.sessionSearchKeys.featureFlagTelemetrySdkName':
    'Name of the feature flag SDK used to generate telemetry.',
  'observability.sessionSearchKeys.featureFlagTelemetrySdkVersion':
    'Version of the feature flag SDK used to generate telemetry.',
  'observability.sessionSearchKeys.identifiedEmail': 'Email address of the identified user.',
  'observability.sessionSearchKeys.identifier': 'A unique identifier for the tracked entity.',
  'observability.sessionSearchKeys.ip': 'IP address of the user.',
  'observability.sessionSearchKeys.isMobile': 'Whether the session is from a mobile device.',
  'observability.sessionSearchKeys.key': 'A generic key field for custom tracking data.',
  'observability.sessionSearchKeys.landingPage': 'The first page visited in the session.',
  'observability.sessionSearchKeys.osName': 'Operating system name.',
  'observability.sessionSearchKeys.osVersion': 'Version of the operating system.',
  'observability.sessionSearchKeys.path': 'URL path of the page.',
  'observability.sessionSearchKeys.referrer': 'The referring URL that led to this page.',
  'observability.sessionSearchKeys.reload': 'Indicates if the page was reloaded.',
  'observability.sessionSearchKeys.serviceName': 'Name of the service generating the event.',
  'observability.sessionSearchKeys.serviceVersion': 'Version of the service generating the event.',
  'observability.sessionSearchKeys.session': 'Session identifier.',
  'observability.sessionSearchKeys.state': 'State or province where the user is located.',
  'observability.sessionSearchKeys.telemetrySdkName': 'Name of the telemetry SDK.',
  'observability.sessionSearchKeys.telemetrySdkVersion': 'Version of the telemetry SDK.',
  'observability.sessionSearchKeys.url': 'Full URL of the page.',
  'observability.sessionSearchKeys.user': 'User identifier or username.',
  'observability.sessionSearchKeys.userAgent': 'The browser user agent string.',
  'observability.sessionSearchKeys.value': 'A generic value field for custom tracking data.',
  'observability.sessionSearchKeys.visitedUrl': 'URL that was visited by the user.',
  'observability.sessionStatsChart.heading': 'Sessions by Error Status',
  'observability.sessionStatsChart.noErrorsSeries': 'No Errors',
  'observability.sessionStatsChart.withErrorsSeries': 'With Errors',
  'observability.sessionsBreadcrumbs.copyUrlLabel': 'Copy URL to clipboard',
  'observability.sessionsBreadcrumbs.navLabel': 'Breadcrumb navigation for this page.',
  'observability.sessionsNotIntegrated.body': 'Record and replay real user sessions to see how people use your app.',
  'observability.sessionsNotIntegrated.readAboutSession': 'Read about session replay',
  'observability.sessionsNotIntegrated.title': 'Set up session replay',
  'observability.sessionsPage.askVega': 'Ask {botName}',
  'observability.sessionsSearchActions.copyLinkTo': 'Copy link to current query',
  'observability.sessionsSearchActions.csvError': 'Failed to generate the CSV report: {error}',
  'observability.sessionsSearchActions.csvPreparing': 'Preparing CSV report, this may take a bit…',
  'observability.sessionsSearchActions.csvReady': 'CSV report prepared, downloading…',
  'observability.sessionsSearchActions.exportToCsv': 'Export to CSV file',
  'observability.sessionsSearchActions.linkCopied': 'Link copied to clipboard',
  'observability.sessionsSearchActions.moreActions': 'More actions',
  'observability.sessionsSearchPanel.emptyStateTitle': 'No sessions detected',
  'observability.sessionsSearchPanel.loadErrorBody':
    'There was an error loading your sessions. Reach out to us if this might be a bug.',
  'observability.sessionsSearchPanel.loadErrorTitle': 'Failed to load sessions',
  'observability.sessionsSettings.sessions': 'Sessions',
  'observability.sessionsTable.emptyStateTitle': 'No sessions detected',
  'observability.sessionsTable.loadErrorBody':
    'There was an error loading your sessions. Reach out to us if this might be a bug.',
  'observability.sessionsTable.loadErrorTitle': 'Failed to load sessions',
  'observability.sessionsTable.openLabel': 'Open',
  'observability.setupEmptyState.installPluginTo': 'Install plugin to get results',
  'observability.setupEmptyState.readDocs': 'Read docs',
  'observability.setupInstructions.askAnExpert': 'Ask an expert',
  'observability.setupInstructions.installationGuide': 'Installation guide',
  'observability.setupInstructions.jsHtml': 'JS/HTML',
  'observability.setupInstructions.nodeJs': 'Node.js',
  'observability.setupInstructions.python': 'Python',
  'observability.setupInstructions.react': 'React',
  'observability.setupInstructions.reactNative': 'React Native',
  'observability.setupInstructions.readDocs': 'Read docs',
  'observability.setupInstructions.selectLanguage': 'Select language',
  'observability.shareSessionDialog.closeLabel': 'Close',
  'observability.shareSessionDialog.copy': 'Copy',
  'observability.shareSessionDialog.linkFieldLabel': 'Session link',
  'observability.shareSessionDialog.share': 'Share',
  'observability.shareSessionDialog.startAt': 'Start at <time>{timestamp}</time>',
  'observability.slowestResourcesPanel.count': 'Count',
  'observability.slowestResourcesPanel.emptyState': 'No resources found for the selected time range',
  'observability.slowestResourcesPanel.heading': 'Slowest Resources',
  'observability.slowestResourcesPanel.loadError': 'Error loading slowest resources',
  'observability.slowestResourcesPanel.p50Load': 'P50 Load',
  'observability.slowestResourcesPanel.p95Load': 'P95 Load',
  'observability.slowestResourcesPanel.p99Load': 'P99 Load',
  'observability.slowestResourcesPanel.resource': 'Resource',
  'observability.slowestResourcesPanel.type': 'Type',
  'observability.sourcemapErrors.copiedToClipboard': 'Copied to clipboard',
  'observability.sourcemapErrors.fileNameMissing':
    "There was an issue accessing the original file for this error. We couldn't find a filename associated with this stack frame",
  'observability.sourcemapErrors.fileParseTitle': "Couldn't Parse File",
  'observability.sourcemapErrors.fileSizeLimitTitle': 'File Size Limit Error',
  'observability.sourcemapErrors.hideMetadata': 'Hide metadata',
  'observability.sourcemapErrors.mappedColumnNumberLabel': 'Mapped Column Number',
  'observability.sourcemapErrors.mappedLineNumberLabel': 'Mapped Line Number',
  'observability.sourcemapErrors.minifiedColumnNumberLabel': 'Minified Column Number',
  'observability.sourcemapErrors.minifiedFetchStrategyLabel': 'Minified Fetch Strategy',
  'observability.sourcemapErrors.minifiedFileMissing':
    "There was an issue accessing the original file for this error. We couldn't find the minified file in LaunchDarkly storage at path <code>{path}</code> or at URL <code>{url}</code>",
  'observability.sourcemapErrors.minifiedFileSizeLabel': 'Minified File Size',
  'observability.sourcemapErrors.minifiedFileTooLarge':
    "We couldn't fetch these files due to size limits. Minified file <code>{path}</code> larger than our max supported size 128MB",
  'observability.sourcemapErrors.minifiedLineNumberLabel': 'Minified Line Number',
  'observability.sourcemapErrors.minifiedPathLabel': 'Minified Path',
  'observability.sourcemapErrors.noMappingFound':
    "Sourcemap library didn't find a valid mapping to the original source with line <code>{line}</code> and col <code>{column}</code>",
  'observability.sourcemapErrors.originalFileTitle': 'Original File Access Error',
  'observability.sourcemapErrors.showMetadata': 'Show metadata',
  'observability.sourcemapErrors.sourcemapFetchStrategyLabel': 'Sourcemap Fetch Strategy',
  'observability.sourcemapErrors.sourcemapFetchedPathLabel': 'Sourcemap Fetched Path',
  'observability.sourcemapErrors.sourcemapFileMissing':
    "There was an issue accessing the sourcemap file for this error. We couldn't find the sourcemap file in Highlight storage at path <code>{path}</code> or at URL <code>{url}</code>",
  'observability.sourcemapErrors.sourcemapFileSizeLabel': 'Sourcemap File Size',
  'observability.sourcemapErrors.sourcemapFileTitle': 'Sourcemap File Error',
  'observability.sourcemapErrors.sourcemapFileTooLarge':
    "We couldn't fetch these files due to size limits. Sourcemap file <code>{path}</code> larger than our max supported size 128MB",
  'observability.sourcemapErrors.sourcemapFilenameParseError':
    "There was an issue accessing the sourcemap file for this error. We couldn't parse the sourcemap filename X <code>{path}</code>",
  'observability.sourcemapErrors.sourcemapMissingInStorage':
    "There was an issue accessing the sourcemap file for this error. We couldn't find sourcemap file using the 'file://' syntax in cloud storage at path <code>{path}</code>",
  'observability.sourcemapErrors.sourcemapParseError':
    'There was an error parsing the source map file <code>{url}</code>',
  'observability.sourcemapErrors.sourcemapSettings': 'Sourcemap settings',
  'observability.sourcemapErrors.sourcemapUrlConstructError':
    'Failed to construct the sourcemap URL from the stacktrace <code>{url}</code>',
  'observability.sourcemapErrors.sourcemapUrlLabel': 'Sourcemap URL',
  'observability.sourcemapErrors.stackTraceFileUrlLabel': 'Stack Trace File URL',
  'observability.sourcemapErrors.stackTraceUrlParseError':
    "There was an issue accessing the original file for this error. We couldn't parse the stack trace file name at URL <code>{url}</code>",
  'observability.sourcemapSettings.emptyState': 'No sourcemaps found',
  'observability.sourcemapSettings.emptyStateTitle': 'Upload your first sourcemap',
  'observability.sourcemapSettings.info':
    'Sourcemaps undo JavaScript minification in your error traces, so stack traces map to your original source. <link>Read about sourcemaps</link>',
  'observability.sourcemapSettings.noResults': 'No sourcemap files match your search.',
  'observability.sourcemapSettings.noResultsTitle': 'Nothing to see here',
  'observability.sourcemapSettings.searchPlaceholder': 'Search files',
  'observability.sourcemapSettings.sectionLabel': 'Sourcemaps',
  'observability.sourcemapSettings.selectVersionTitle': 'Select a version',
  'observability.sourcemapSettings.sourcemapColumn': 'Sourcemap',
  'observability.sourcemapSettings.versionFieldName': 'Sourcemap app version',
  'observability.sourcemapSettings.versionHelpText':
    'This project has sourcemaps for multiple app versions. Choose one to see its files.',
  'observability.sourcemapSettings.versionPlaceholder': 'Select a version',
  'observability.spanAttributes.applyAsFilter': 'Apply as filter',
  'observability.spanAttributes.duration': 'Duration',
  'observability.spanAttributes.spanId': 'Span ID',
  'observability.spanAttributes.viewInContext': 'View in context',
  'observability.spanDetails.breadcrumbsLabel': 'Span path',
  'observability.spanDetails.closeDetailsLabel': 'Close details',
  'observability.spanDetails.closeTabLabel': 'Close {spanName}',
  'observability.spanDetails.identityLabel': 'Span identity',
  'observability.spanDetails.metricsLabel': 'Span metrics',
  'observability.spanDetails.model': 'Model',
  'observability.spanDetails.provider': 'Provider',
  'observability.spanDetails.showSkippedLabel': 'Show skipped spans',
  'observability.spanDetails.skippedSpansLabel': 'Skipped spans',
  'observability.spanDetails.spanIdLabel': 'Span ID {spanId}',
  'observability.spanDetails.tabsLabel': 'Span details',
  'observability.spanDetails.traceIdLabel': 'Trace ID {traceId}',
  'observability.spanDetails.viewSpanLabel': 'View span {name}',
  'observability.spanDetails.viewTraceLabel': 'View trace {traceId}',
  'observability.spanObservations.configsTableLabel': 'Configs on this trace',
  'observability.spanObservations.flagsTableLabel': 'Feature flags on this trace',
  'observability.spanObservations.noConfigsOn': 'No configs on this trace',
  'observability.spanObservations.noFeatureFlags': 'No feature flags on this trace',
  'observability.spanObservations.occurrenceSpansLabel': 'Spans with this observation',
  'observability.spanObservations.open': 'Open',
  'observability.spanObservations.spanCount': '{count} {count, plural, one {span} other {spans}}',
  'observability.spanObservations.viewSpanLabel': 'View span {name}',
  'observability.spanObservations.viewSpansLabel': 'View {count} {count, plural, one {span} other {spans}}',
  'observability.spanReview.correct': 'Correct',
  'observability.spanReview.correctnessLabel': 'Correctness',
  'observability.spanReview.expectedOutputHint':
    'Optional. Without a correction, a dataset row promoted from this span keeps the incorrect output as its expected output.',
  'observability.spanReview.expectedOutputLabel': 'Expected output',
  'observability.spanReview.explanationLabel': 'Explanation',
  'observability.spanReview.explanationPlaceholder': 'Optional context for the next reviewer.',
  'observability.spanReview.heading': 'Review',
  'observability.spanReview.incorrect': 'Incorrect',
  'observability.spanReview.insertActualOutput': 'Insert actual output',
  'observability.spanReview.loadError': 'Couldn’t load this span’s review. Reload and try again.',
  'observability.spanReview.loading': 'Loading review…',
  'observability.spanReview.save': 'Save review',
  'observability.spanReview.tabLabel': 'Review',
  'observability.spansEmptyState.body': 'No spans found. Try updating your time range to see more spans.',
  'observability.spansEmptyState.bodyFiltered':
    'No spans found for the current search query. Try using a more generic search query, removing filters, or updating the time range to see more spans.',
  'observability.spansEmptyState.docsLink': 'View search docs',
  'observability.spansEmptyState.heading': 'No spans found',
  'observability.spansList.addAsColumn': 'Add as column',
  'observability.spansList.applyAsFilter': 'Apply as filter',
  'observability.spansList.askAi': 'Ask {botName}',
  'observability.spansList.collapse': 'Collapse',
  'observability.spansList.copyJson': 'Copy json',
  'observability.spansList.copyJsonTooltip': 'Copy JSON',
  'observability.spansList.copyLink': 'Copy link',
  'observability.spansList.errorBody': 'There was an error loading your spans. Reach out to us if this might be a bug.',
  'observability.spansList.errorTitle': 'Failed to load spans',
  'observability.spansList.expand': 'Expand',
  'observability.spansList.linkCopiedToast': 'Copied link',
  'observability.spansList.livePausedBody':
    'Live mode has been paused to conserve system resources. Refresh the page to re-enable it.',
  'observability.spansList.noSessionBody': 'No session found for this trace.',
  'observability.spansList.relatedSession': 'Related Session',
  'observability.spansList.removeAsColumn': 'Remove as column',
  'observability.spansList.sessionExcludedBody': 'The session for this trace was excluded.',
  'observability.spansList.traceCopiedToast': 'Copied trace',
  'observability.sqlEditor.messages': 'Messages',
  'observability.tableCell.empty': 'Empty',
  'observability.tableList.showLess': 'Show less',
  'observability.tableList.showMore': 'Show more',
  'observability.tableSearch.placeholder': 'Search…',
  'observability.teamQueryHistory.noPreview': 'No preview',
  'observability.teamQueryHistory.noTeamQueries': 'No team queries yet.',
  'observability.teamQueryHistory.teamQueryHistory': 'Team query history',
  'observability.templateMenu.templates': 'Templates',
  'observability.topResourcesChart.heading': 'Top 5 Resources (P95 Load Time)',
  'observability.totalSessionsCard.errorRate': 'Error Rate',
  'observability.totalSessionsCard.heading': 'Total Sessions',
  'observability.totalSessionsCard.noErrors': 'No errors',
  'observability.totalSessionsCard.previousPeriod': 'from <strong>{previousCount}</strong> in previous period',
  'observability.totalSessionsCard.sessions': 'Sessions',
  'observability.totalSessionsCard.withErrors': 'With errors',
  'observability.traceOverview.copiedToast': 'Copied to clipboard',
  'observability.traceOverview.copyToClipboard': 'Copy to clipboard',
  'observability.traceOverview.excludeGroup': 'Exclude this group',
  'observability.traceOverview.filterToGroup': 'Filter to this group',
  'observability.traceOverview.noGroup': 'No group',
  'observability.tracePanel.copyIdLabel': 'Copy trace ID',
  'observability.tracePanel.copyJson': 'Copy json',
  'observability.tracePanel.notFoundTitle': 'Trace not found',
  'observability.tracePanel.partialTitle': 'Showing this trace only',
  'observability.tracePanel.theRestOf': 'The rest of the conversation could not be loaded.',
  'observability.traceQueryBar.searchPlaceholder': 'Search…',
  'observability.traceQueryBar.viewDocs': 'View docs',
  'observability.traceSearchKeys.browserLanguage': 'Language setting of the browser.',
  'observability.traceSearchKeys.clickhouseQueryId':
    'ClickHouse query ID. Use it to find this query in system.query_log.',
  'observability.traceSearchKeys.containerId': 'Unique identifier of the container.',
  'observability.traceSearchKeys.contentType': 'Content type of the request or response.',
  'observability.traceSearchKeys.dbNamespace': 'Database name, fully qualified within the server address and port.',
  'observability.traceSearchKeys.dbOperation': 'The type of database operation (e.g., SELECT, INSERT).',
  'observability.traceSearchKeys.dbQuerySummary':
    'Low-cardinality summary of a database query, such as an operation and its tables.',
  'observability.traceSearchKeys.dbResponseStatusCode': 'Status code returned by the database, usually an error code.',
  'observability.traceSearchKeys.dbRowsAffected': 'Number of rows affected by the database operation.',
  'observability.traceSearchKeys.dbSqlTable': 'The database table being accessed.',
  'observability.traceSearchKeys.dbStatement': 'The database statement being executed.',
  'observability.traceSearchKeys.dbSystem':
    'Deprecated. Use db.system.name. Database system being used (e.g., PostgreSQL, MySQL).',
  'observability.traceSearchKeys.dbSystemName':
    'Database product the client identifies (e.g., postgresql, clickhouse, redis).',
  'observability.traceSearchKeys.deploymentEnvironmentName': 'Name of the deployment environment.',
  'observability.traceSearchKeys.entryMethod': 'Entry method of the request.',
  'observability.traceSearchKeys.entryRoute': 'Entry route of the request.',
  'observability.traceSearchKeys.environment':
    'The environment where the span was generated (e.g., production, staging).',
  'observability.traceSearchKeys.errorType':
    'Class of error the operation ended with. Set only when the operation failed.',
  'observability.traceSearchKeys.eventName': 'Screen name of a mobile screen_view event.',
  'observability.traceSearchKeys.eventText': 'Visible text/label of a clicked element.',
  'observability.traceSearchKeys.eventsAttributesFeatureFlagContextId':
    'Feature flag context ID from span event attributes.',
  'observability.traceSearchKeys.eventsAttributesFeatureFlagContextKeys':
    'Feature flag context keys from span event attributes.',
  'observability.traceSearchKeys.eventsAttributesFeatureFlagKey': 'Feature flag key from span event attributes.',
  'observability.traceSearchKeys.eventsAttributesFeatureFlagProviderName':
    'Feature flag provider name from span event attributes.',
  'observability.traceSearchKeys.eventsAttributesFeatureFlagResultReasonKind':
    'Feature flag result reason from span event attributes.',
  'observability.traceSearchKeys.eventsAttributesFeatureFlagResultValue':
    'Feature flag result value from span event attributes.',
  'observability.traceSearchKeys.eventsAttributesFeatureFlagResultVariationIndex':
    'Feature flag variation index from span event attributes.',
  'observability.traceSearchKeys.eventsAttributesFeatureFlagSetId':
    'The id of the environment where the flag was evaluated.',
  'observability.traceSearchKeys.eventsAttributesLaunchdarklyApplicationVersion':
    'LaunchDarkly application version from span event attributes.',
  'observability.traceSearchKeys.eventsAttributesTelemetrySdkName': 'Telemetry SDK name from span event attributes.',
  'observability.traceSearchKeys.eventsAttributesTelemetrySdkVersion':
    'Telemetry SDK version from span event attributes.',
  'observability.traceSearchKeys.eventsName': 'Name of the span event.',
  'observability.traceSearchKeys.featureFlagContextId': 'Unique identifier for the evaluation context.',
  'observability.traceSearchKeys.featureFlagContextKeys': 'Keys identifying the context used for flag evaluation.',
  'observability.traceSearchKeys.featureFlagKey': 'The unique key identifying a specific feature flag.',
  'observability.traceSearchKeys.featureFlagLaunchdarklyApplicationVersion': 'Version of the LaunchDarkly application.',
  'observability.traceSearchKeys.featureFlagProviderName': 'The name of the feature flag provider or service.',
  'observability.traceSearchKeys.featureFlagResultReasonKind':
    'The reason category for why a particular flag value was returned.',
  'observability.traceSearchKeys.featureFlagResultValue': 'The evaluated value of the feature flag for this context.',
  'observability.traceSearchKeys.featureFlagResultVariationIndex':
    'Index of the variation returned for this flag evaluation.',
  'observability.traceSearchKeys.featureFlagSetId': 'The id of the environment where the flag was evaluated.',
  'observability.traceSearchKeys.featureFlagTelemetrySdkName':
    'Name of the feature flag SDK used to generate telemetry.',
  'observability.traceSearchKeys.featureFlagTelemetrySdkVersion':
    'Version of the feature flag SDK used to generate telemetry.',
  'observability.traceSearchKeys.graphqlFieldName': 'Name of the GraphQL field being queried.',
  'observability.traceSearchKeys.graphqlObjectType': 'Type of the GraphQL object.',
  'observability.traceSearchKeys.handlerName': 'Name of the handler.',
  'observability.traceSearchKeys.handlerRoute': 'Route handled by this handler.',
  'observability.traceSearchKeys.hasErrors': 'Whether the span contains errors.',
  'observability.traceSearchKeys.hostName': 'Hostname of the machine running the service.',
  'observability.traceSearchKeys.httpHost': 'HTTP host header value.',
  'observability.traceSearchKeys.httpMethod': 'HTTP request method (e.g., GET, POST).',
  'observability.traceSearchKeys.httpRequestHeaders': 'HTTP request headers.',
  'observability.traceSearchKeys.httpRequestMethod': 'HTTP request method.',
  'observability.traceSearchKeys.httpResponseContentLength': 'Length of the HTTP response content in bytes.',
  'observability.traceSearchKeys.httpResponseContentLengthUncompressed':
    'Uncompressed length of the HTTP response content.',
  'observability.traceSearchKeys.httpResponseError': 'Error information from the HTTP response.',
  'observability.traceSearchKeys.httpResponseStatusCode': 'HTTP response status code.',
  'observability.traceSearchKeys.httpRoute': 'HTTP route pattern (e.g., /users/:id).',
  'observability.traceSearchKeys.httpScheme': 'HTTP scheme (e.g., http, https).',
  'observability.traceSearchKeys.httpStatusCode': 'HTTP status code of the response.',
  'observability.traceSearchKeys.httpStatusText': 'HTTP status text (e.g., OK, Not Found).',
  'observability.traceSearchKeys.httpUserAgent': 'User agent string from the HTTP request.',
  'observability.traceSearchKeys.kafkaKey': 'Kafka message key.',
  'observability.traceSearchKeys.kafkaMessages': 'Number of Kafka messages.',
  'observability.traceSearchKeys.kafkaTopic': 'Kafka topic name.',
  'observability.traceSearchKeys.launchdarklyDbQueryGroup':
    'Low-cardinality template for a database query, derived at ingest from db.query.summary or the statement with literals replaced. Group queries on this key instead of the raw statement.',
  'observability.traceSearchKeys.osDescription': 'Description of the operating system.',
  'observability.traceSearchKeys.osType': 'Type of operating system.',
  'observability.traceSearchKeys.processExecutableName': 'Name of the process executable.',
  'observability.traceSearchKeys.processExecutablePath': 'Full path to the process executable.',
  'observability.traceSearchKeys.processOwner': 'Owner of the process.',
  'observability.traceSearchKeys.processPid': 'Process ID of the application.',
  'observability.traceSearchKeys.processRuntimeDescription': 'Description of the runtime environment.',
  'observability.traceSearchKeys.processRuntimeName': 'Name of the runtime environment.',
  'observability.traceSearchKeys.processRuntimeVersion': 'Version of the runtime environment.',
  'observability.traceSearchKeys.requestMethod': 'Method of the request.',
  'observability.traceSearchKeys.requestPath': 'Path of the request.',
  'observability.traceSearchKeys.resourceName': 'The primary resource or operation name for this span.',
  'observability.traceSearchKeys.responseStatusCode': 'Status code of the response.',
  'observability.traceSearchKeys.serverAddress': 'Host name or address of the server the client connected to.',
  'observability.traceSearchKeys.serviceName': 'Name of the service that generated the span.',
  'observability.traceSearchKeys.serviceVersion': 'Version of the service that generated the span.',
  'observability.traceSearchKeys.spanKind': 'The type of span (e.g., CLIENT, SERVER, INTERNAL, PRODUCER, CONSUMER).',
  'observability.traceSearchKeys.spanName': 'The name or operation of the span.',
  'observability.traceSearchKeys.sqlCommand': 'The SQL command being executed.',
  'observability.traceSearchKeys.telemetryDistroName': 'Name of the OpenTelemetry distribution.',
  'observability.traceSearchKeys.telemetryDistroVersion': 'Version of the OpenTelemetry distribution.',
  'observability.traceSearchKeys.telemetrySdkLanguage': 'Programming language of the telemetry SDK.',
  'observability.traceSearchKeys.telemetrySdkName': 'Name of the telemetry SDK.',
  'observability.traceSearchKeys.telemetrySdkVersion': 'Version of the telemetry SDK.',
  'observability.traceSearchKeys.traceState': 'Additional vendor-specific trace identification information.',
  'observability.traceSearchKeys.urlFull': 'Full URL including scheme, host, path, and query.',
  'observability.traceSearchKeys.urlPath': 'Path component of the URL.',
  'observability.traceSearchKeys.urlQuery': 'Query string of the URL.',
  'observability.traceSearchKeys.urlQueryParams': 'Parsed query parameters from the URL.',
  'observability.traceSearchKeys.userAgentOriginal': 'Original user agent string from the request.',
  'observability.traceSearchKeys.viewName':
    'Name of a view event — page path (web page_view) or screen name (mobile screen_view).',
  'observability.traceSummariesList.open': 'Open',
  'observability.traceWaterfall.clearFocus': 'Clear focus',
  'observability.traceWaterfall.collapseAll': 'Collapse all',
  'observability.traceWaterfall.copyLinkTo': 'Copy link to span',
  'observability.traceWaterfall.copySpanAnd': 'Copy span and child spans as json',
  'observability.traceWaterfall.copySpanAs': 'Copy span as json',
  'observability.traceWaterfall.durationLabel': 'Duration {duration}',
  'observability.traceWaterfall.errorCount': '{count} {count, plural, one {error} other {errors}}',
  'observability.traceWaterfall.expandAll': 'Expand all',
  'observability.traceWaterfall.linkCopiedToast': 'Link copied to clipboard',
  'observability.traceWaterfall.resetZoom': 'Reset zoom',
  'observability.traceWaterfall.searchPlaceholder': 'Search span names',
  'observability.traceWaterfall.serviceCount': '{count} {count, plural, one {service} other {services}}',
  'observability.traceWaterfall.serviceLabel': 'Service {name}',
  'observability.traceWaterfall.showAllSpans': 'Show all spans',
  'observability.traceWaterfall.showSpanAttributes': 'Show span attributes',
  'observability.traceWaterfall.spanCount': '{count} {count, plural, one {span} other {spans}}',
  'observability.traceWaterfall.spanError': 'Error',
  'observability.traceWaterfall.spanJsonCopiedToast': 'Span JSON copied to clipboard',
  'observability.traceWaterfall.spanName': 'Span name',
  'observability.traceWaterfall.spansJsonCopiedToast': '{count} spans copied as JSON',
  'observability.traceWaterfall.tokensLabel': '{tokens} tokens',
  'observability.traceWaterfall.turnFocus': 'Showing spans from this turn',
  'observability.traceWaterfall.viewInContext': 'View in context',
  'observability.tracesPage.conversations': 'Conversations',
  'observability.tracesPage.overview': 'Overview',
  'observability.tracesPage.spans': 'Spans',
  'observability.tracesPage.tabsLabel': 'Trace views',
  'observability.tracesPage.teamHistory': 'Team history',
  'observability.tracesPage.traces': 'Traces',
  'observability.tracesSetupCta.heading': 'No traces detected',
  'observability.tracesSetupCta.setupBody': 'Follow the steps below or view docs for more advanced setup steps.',
  'observability.tracesSetupCta.setupHeading': 'Traces are not integrated yet',
  'observability.tracesTableCell.goTo': 'Go to',
  'observability.tracesTableCell.open': 'Open',
  'observability.usageBar.askAnExpert': 'Ask an expert',
  'observability.usageBar.helpLabel': 'Help',
  'observability.usageBar.thisShowsThe': 'This shows the current usage for your workspace.',
  'observability.usageBar.upgradePlan': 'Upgrade plan',
  'observability.usageBar.upgradeSentence': '<link>Upgrade your plan</link> to increase this limit.',
  'observability.usageBar.usageInfoLabel': 'Usage information',
  'observability.usageBar.used': 'Used',
  'observability.usageBar.viewDocs': 'View docs',
  'observability.usageOverage.body':
    "You''ve used <b>{used}</b> of your <b>{quota}</b> {productType} allotment. Upgrade your plan to increase your limit.",
  'observability.usageOverage.hide': 'Hide',
  'observability.usageOverage.upgradePlan': 'Upgrade plan',
  'observability.variablesBar.editVariables': 'Edit variables',
  'observability.variablesModal.defaultValueColumn': 'Default value',
  'observability.variablesModal.delete': 'Delete',
  'observability.variablesModal.emptyBody':
    'Add a variable to create dynamic queries that can be reused across multiple graphs.',
  'observability.variablesModal.emptyHeading': 'No variables added',
  'observability.variablesModal.heading': 'Variables',
  'observability.variablesModal.nameColumn': 'Name',
  'observability.variablesModal.namePlaceholder': 'Variable name',
  'observability.variablesModal.newVariable': 'New variable',
  'observability.variablesModal.save': 'Save',
  'observability.variablesModal.suggestionFieldColumn': 'Suggestion field',
  'observability.variablesModal.suggestionTypeColumn': 'Suggestion type',
  'observability.vegaConversation.notFoundBody':
    'There is no Vega chat with this ID. It may have been deleted, or this conversation may have been recorded by an instrumented app rather than started in Vega.',
  'observability.vegaConversation.notFoundTitle': 'Conversation not found',
  'observability.vegaConversation.notSharedBody':
    'This conversation has not been shared by its owner. The owner can share it using the share button in the header.',
  'observability.vegaConversation.notSharedTitle': 'Conversation not available',
  'observability.vegaConversation.startANew': 'Start a new chat',
  'observability.vegaConversation.tryAgain': 'Try again',
  'observability.vegaConversation.unknownBody':
    'Something went wrong loading this Vega chat. This is usually temporary.',
  'observability.vegaConversation.unknownTitle': 'Could not load this conversation',
  'observability.webVitals.clsDescription': 'Measures visual stability by tracking unexpected layout shifts',
  'observability.webVitals.clsName': 'Cumulative Layout Shift',
  'observability.webVitals.fcpDescription': 'Time until the first text or image is painted',
  'observability.webVitals.fcpName': 'First Contentful Paint',
  'observability.webVitals.inpDescription': 'Time from user interaction to next visual update',
  'observability.webVitals.inpName': 'Interaction to Next Paint',
  'observability.webVitals.lcpDescription': 'Time until the largest content element is rendered',
  'observability.webVitals.lcpName': 'Largest Contentful Paint',
  'observability.webVitals.learnMoreLink': 'Learn more about optimizing {metric}.',
  'observability.webVitals.metricInfoLabel': 'More information about {metric}',
  'observability.webVitals.ttfbDescription': 'Time from navigation to when the first byte is received',
  'observability.webVitals.ttfbName': 'Time to First Byte',
  'observability.webVitalsRoutes.count': 'COUNT',
  'observability.webVitalsRoutes.heading': 'Performance overview of the most popular pages',
  'observability.webVitalsRoutes.noDataAvailable': 'No data available for the selected time range',
  'observability.webVitalsRoutes.p75Cls': 'P75 CLS',
  'observability.webVitalsRoutes.p75Fcp': 'P75 FCP',
  'observability.webVitalsRoutes.p75Inp': 'P75 INP',
  'observability.webVitalsRoutes.p75Lcp': 'P75 LCP',
  'observability.webVitalsRoutes.viewName': 'VIEW NAME',
  'observabilityMetrics.page.title': 'Observability metrics',
  'observe.page.connectAction3': 'Connect your SDK to observe events for your app.',
  'observe.page.title': 'Event explorer',
  'onboarding.agentControlHero.headingLine1': 'Your agents.',
  'onboarding.agentControlHero.headingLine2': 'Under control.',
  'onboarding.agentControlHero.subheading': 'Try AgentControl free.',
  'onboarding.agentControlHero.trustedBy': 'Built on a platform trusted by 5,500+ customers',
  'onboarding.aiConfigModelSelector.optionsLabel': 'Provider options',
  'onboarding.aiConfigModelSelector.searchLabel': 'Search models…',
  'onboarding.aiConfigModelSelector.searchPlaceholder': 'Find a model',
  'onboarding.aiConfigModelSelector.triggerLabel': 'Select a model',
  'onboarding.aiConfigProviderSelector.optionsLabel': 'Provider options',
  'onboarding.aiConfigProviderSelector.triggerLabel': 'Select a provider',
  'onboarding.awsHero.awsLogoAlt': 'AWS',
  'onboarding.awsHero.goalLabel': 'Goal:',
  'onboarding.awsHero.goalValue': 'lift checkout conversion 3%',
  'onboarding.awsHero.headingLine1': 'You set a goal.',
  'onboarding.awsHero.headingLine2': 'Your agents ship it.',
  'onboarding.awsHero.kiroLogoAlt': 'Kiro',
  'onboarding.awsHero.launchDarklyLogoAlt': 'LaunchDarkly',
  'onboarding.awsHero.stepApproveBody': 'Last required click of the day.',
  'onboarding.awsHero.stepApproveLead': 'Approve the plan.',
  'onboarding.awsHero.stepCodeFlag': 'flag checkout-v2.',
  'onboarding.awsHero.stepCodeLead': 'Writes the code',
  'onboarding.awsHero.stepCodeOpens': ', opens',
  'onboarding.awsHero.stepCodePr': 'PR #482',
  'onboarding.awsHero.stepGoalBody': 'and the metrics that matter.',
  'onboarding.awsHero.stepGoalLead': 'Set the goal',
  'onboarding.awsHero.stepHypothesis': 'Defines the hypothesis.',
  'onboarding.awsHero.stepLaunchBody': '50/50 A/B test on 10% of traffic, measuring checkout conversion.',
  'onboarding.awsHero.stepLaunchLead': 'Starts the experiment:',
  'onboarding.awsHero.stepLogBody': ', queues the next hypothesis…',
  'onboarding.awsHero.stepLogLead': 'Logs the result',
  'onboarding.awsHero.stepResult': 'Variant wins → Guarded Release to 100%.',
  'onboarding.awsHero.subheading':
    'Here’s what one morning looks like when Kiro, DevOps Agent, and LaunchDarkly run the loop for you.',
  'onboarding.awsHero.trialFooter': 'LaunchDarkly: full platform, free for {days} days',
  'onboarding.enterpriseDemoForm.companyLabel': 'Company name',
  'onboarding.enterpriseDemoForm.emailError': 'Error: This is not a valid email address',
  'onboarding.enterpriseDemoForm.emailLabel': 'Work email',
  'onboarding.enterpriseDemoForm.firstNameLabel': 'First name',
  'onboarding.enterpriseDemoForm.jobTitleLabel': 'Job title',
  'onboarding.enterpriseDemoForm.lastNameLabel': 'Last name',
  'onboarding.enterpriseDemoForm.requiredError': 'Error: This field is required',
  'onboarding.enterpriseDemoForm.submit': 'Contact Enterprise Sales',
  'onboarding.enterpriseDemoForm.submitError':
    'Something went wrong submitting your request. Try again, or email trials@launchdarkly.com and our team will be in touch.',
  'onboarding.enterpriseDemoForm.successBody': 'Thanks — our team will be in touch soon.',
  'onboarding.enterpriseDemoFormLegacy.companyLabel': 'Company name',
  'onboarding.enterpriseDemoFormLegacy.emailError': 'Error: This is not a valid email address',
  'onboarding.enterpriseDemoFormLegacy.emailLabel': 'Work email',
  'onboarding.enterpriseDemoFormLegacy.firstNameLabel': 'First name',
  'onboarding.enterpriseDemoFormLegacy.jobTitleLabel': 'Job title',
  'onboarding.enterpriseDemoFormLegacy.lastNameLabel': 'Last name',
  'onboarding.enterpriseDemoFormLegacy.requiredError': 'Error: This field is required',
  'onboarding.enterpriseDemoFormLegacy.submit': 'Contact Enterprise Sales',
  'onboarding.enterpriseDemoFormLegacy.submitting': 'Submitting…',
  'onboarding.enterpriseDemoFormLegacy.successBody': 'Thanks — our team will be in touch soon.',
  'onboarding.flowsList.allFilter': 'All',
  'onboarding.flowsList.emptyHeading': 'No flows found',
  'onboarding.flowsList.searchLabel': 'Search flows',
  'onboarding.flowsList.searchPlaceholder': 'Search flows…',
  'onboarding.flowsPanel.completed': 'Completed',
  'onboarding.flowsPanel.emptyHeading': 'No flows found',
  'onboarding.flowsPanel.start': 'Start',
  'onboarding.guidance.back': 'Back',
  'onboarding.guidance.closeLabel': 'Close guidance system',
  'onboarding.guidance.done': 'Done',
  'onboarding.guidance.next': 'Next',
  'onboarding.guideDrawer.closeLabel': 'close onboarding guide',
  'onboarding.guideDrawer.heading': 'Getting Started',
  'onboarding.installSnippet.copyLabel': 'Copy code snippet',
  'onboarding.installSnippet.copyTooltip': 'Copy to clipboard',
  'onboarding.installSnippet.heading': 'Install feature flag',
  'onboarding.inviteApiKeyOwner.copyLink': 'Copy link',
  'onboarding.inviteApiKeyOwner.copyLinkTooltip': 'Copy to clipboard',
  'onboarding.inviteApiKeyOwner.hint':
    'Invite a teammate who has access to create LLM API keys so they can configure this information in your LaunchDarkly account (optional)',
  'onboarding.inviteApiKeyOwner.linkCopied': 'Link copied',
  'onboarding.inviteApiKeyOwner.linkLabel': 'Or share a signup link',
  'onboarding.inviteTeammates.emailsLabel': 'Email addresses',
  'onboarding.inviteTeammates.emailsPlaceholder': 'Separate emails by comma or new line',
  'onboarding.inviteTeammates.invalidEmail': 'Invalid email address',
  'onboarding.inviteTeammates.inviteCount': 'Inviting {count, plural, one {# member} other {# members}}',
  'onboarding.inviteTeammates.tooManyEmails': 'You can invite up to {max} teammates',
  'onboarding.joinOrganization.heading': 'Join your organization',
  'onboarding.joinOrganization.itemTextValue': '{organizationName}, {memberCount}',
  'onboarding.joinOrganization.listLabel': 'Organizations matching your email domain',
  'onboarding.joinOrganization.loadErrorBody': 'Try again later or <link>contact support</link>',
  'onboarding.joinOrganization.loadErrorHeading': "We couldn't load organizations",
  'onboarding.joinOrganization.memberCount': '{count, plural, one {# member} other {# members}}',
  'onboarding.joinOrganization.orDivider': 'or',
  'onboarding.joinOrganization.requestAccess': 'Request access',
  'onboarding.joinOrganization.requestingAccess': 'Requesting Access…',
  'onboarding.joinOrganization.requestingAccessLabel': 'Requesting access',
  'onboarding.joinOrganization.ssoItemTextValue': '{organizationName}, SSO, {memberCount}',
  'onboarding.joinOrganization.ssoLogin': 'Log In With SSO',
  'onboarding.joinOrganization.ssoTag': 'SSO',
  'onboarding.joinOrganization.ssoTagLabel': 'Single sign-on',
  'onboarding.joinOrganization.ssoTooltip':
    'Organizations with SSO must be assigned to you by an admin within your company. To access this organization for the first time, sign in from your company’s SSO provider.',
  'onboarding.joinOrganization.startTrial': 'Start your own {days}-day trial',
  'onboarding.joinOrganization.subheading':
    'We found organizations that match your email domain. Join your teammates who are already using LaunchDarkly.',
  'onboarding.pendingInvite.acceptInvitation': 'Accept invitation',
  'onboarding.pendingInvite.continuingSignupLabel': 'Continuing signup',
  'onboarding.pendingInvite.createTrial': 'Create a trial account',
  'onboarding.pendingInvite.errorHeading': 'Something went wrong',
  'onboarding.pendingInvite.heading': 'You have a pending invitation',
  'onboarding.pendingInvite.loadError': "We couldn't load your pending invitations. Refresh the page to try again.",
  'onboarding.pendingInvite.loadingLabel': 'Loading pending invitations',
  'onboarding.pendingInvite.loggedInAs': 'Logged in as <strong>{email}</strong>',
  'onboarding.pendingInvite.orDivider': 'or',
  'onboarding.pendingInvite.signupError': "We couldn't complete your trial signup. Refresh the page to try again.",
  'onboarding.pendingInvite.subheading': 'Join your team, or create your own trial account.',
  'onboarding.pendingInvite.switchAccount': 'Not you? Switch account',
  'onboarding.pendingInvite.unknownOrganization': 'an organization',
  'onboarding.requestSent.academyLink': 'LaunchDarkly Academy →',
  'onboarding.requestSent.body': "You'll get access to <strong>{organizationName}</strong> once an admin approves it.",
  'onboarding.requestSent.defaultOrganizationName': 'your organization',
  'onboarding.requestSent.heading': 'Your request has been sent',
  'onboarding.requestSent.learnMoreHeading': 'Learn more about LaunchDarkly',
  'onboarding.requestSent.openSandbox': 'Open sandbox',
  'onboarding.requestSent.openSandboxLabel': 'Open sandbox (opens in a new tab)',
  'onboarding.requestSent.sandboxBody': 'Browse a fully configured, read-only project.',
  'onboarding.requestSent.sandboxHeading': 'Explore the LaunchDarkly sandbox',
  'onboarding.requestSent.youtubeLink': 'LaunchDarkly on YouTube →',
  'onboarding.sdkConnection.connectYourSdk': 'Connect your SDK',
  'onboarding.sdkConnection.heading': 'SDK not connected',
  'onboarding.sdkConnection.setUpSdk': 'Set up SDK',
  'onboarding.sdkConnectionCheck.checkingBody': 'Checking your connection…',
  'onboarding.sdkConnectionCheck.connectedBody': 'You are connected. We received an event from you.',
  'onboarding.sdkConnectionCheck.contactSupport': 'contact Support',
  'onboarding.sdkConnectionCheck.correctKey':
    'Did you use the correct SDK key for the <name>{environmentName}</name> environment? The key is <code>{sdkKey}</code>.',
  'onboarding.sdkConnectionCheck.errorTitle': 'Error checking your connection',
  'onboarding.sdkConnectionCheck.flutterKeys':
    'Use Mobile key for desktop and mobile apps <code>{mobileKey}</code> and Client-side ID for web <code>{clientSideId}</code>',
  'onboarding.sdkConnectionCheck.loadingLabel': 'loading SDK activation',
  'onboarding.sdkConnectionCheck.notActiveTitle': 'SDK not active',
  'onboarding.sdkConnectionCheck.tryAgain': 'Try again',
  'onboarding.sdkConnectionCheck.waitingBody': 'We will start checking your connection in a moment.',
  'onboarding.sdkInstall.chooseSdkHeading': 'Choose an SDK',
  'onboarding.sdkInstall.credentialsBody': 'Get your SDK credentials any time in',
  'onboarding.sdkInstall.csharpTab': 'C#',
  'onboarding.sdkInstall.followStepsBody': 'Follow the steps on',
  'onboarding.sdkInstall.gradleTab': 'Gradle',
  'onboarding.sdkInstall.initializeHeading': 'Initialize the SDK',
  'onboarding.sdkInstall.installChoiceHeading': 'Then, choose whether to install the SDK with AI or with a command:',
  'onboarding.sdkInstall.installCommandBody': 'Install the LaunchDarkly SDK with this command:',
  'onboarding.sdkInstall.installWithAi': '<new>(New)</new> Install with AI',
  'onboarding.sdkInstall.manualInstallHeading': 'Or initialize the SDK with the instructions below',
  'onboarding.sdkInstall.projectSettingsLink': 'Project settings > Environments',
  'onboarding.sdkInstall.trackToConfirm':
    'Additionally ensure you call <code>track("{profileId}")</code> to let us know you are connected.',
  'onboarding.sdkInstall.verifyHeading': 'Make sure the SDK is active',
  'onboarding.sdkInstall.xmlTab': 'XML',
  'onboarding.setupGuide.closeLabel': 'close Setup guide',
  'onboarding.setupGuide.heading': 'Starter guide',
  'onboarding.setupGuide.regionLabel': 'Setup guide',
  'onboarding.signUpHero.headingLine1': 'Take control of',
  'onboarding.signUpHero.headingLine2': 'every feature.',
  'onboarding.signUpHero.trustedBy': 'Trusted by 5,500+ customers',
  'onboarding.trialAside.agentControlDescription': 'Runtime control for AI agents',
  'onboarding.trialAside.agentControlTitle': 'AgentControl',
  'onboarding.trialAside.experimentationDescription': 'A/B test anything behind a flag',
  'onboarding.trialAside.experimentationTitle': 'Experimentation',
  'onboarding.trialAside.eyebrow': '14-day free trial · then free forever',
  'onboarding.trialAside.flagsDescription': 'Unlimited seats during trial',
  'onboarding.trialAside.flagsTitle': 'Feature flags',
  'onboarding.trialAside.footnote':
    'When your trial ends, you land on the <strong>Developer plan — free forever.</strong> No credit card, nothing turns off without warning.',
  'onboarding.trialAside.guardedDescription': 'Auto-detect and roll back bad releases',
  'onboarding.trialAside.guardedTitle': 'Guarded releases',
  'onboarding.trialAside.headingLine1': 'Try the whole platform.',
  'onboarding.trialAside.headingLine2': 'Keep what’s free forever.',
  'onboarding.trialAside.includesLead': 'Your trial includes everything on our top plans:',
  'onboarding.trialAside.listLabel': 'What your trial includes',
  'onboarding.trialAside.observabilityDescription': 'Session replay, errors, and metrics built in',
  'onboarding.trialAside.observabilityTitle': 'Observability',
  'onboarding.welcomeSurvey.answerContainsHtml': 'Remove any HTML or code tags from your answer before submitting.',
  'onboarding.welcomeSurvey.answerTooLong': 'Answer is too long. Max {maxLength} characters.',
  'onboarding.welcomeSurvey.companyNameLabel': 'Company name',
  'onboarding.welcomeSurvey.companyNamePlaceholder': 'e.g. LaunchDarkly',
  'onboarding.welcomeSurvey.companyNameQuestion': "What's your company name?",
  'onboarding.welcomeSurvey.companyNameRequired': 'Company name is required',
  'onboarding.welcomeSurvey.companyNameTooLong': 'Company name must be less than {maxLength} characters',
  'onboarding.welcomeSurvey.existingCustomerIntent': 'Your company already uses LaunchDarkly. What brings you here?',
  'onboarding.welcomeSurvey.firstActionAutopilotTooltip': 'Flags your pull requests and rolls them out after merge.',
  'onboarding.welcomeSurvey.firstActionFeatureStatusLabel': 'Feature status',
  'onboarding.welcomeSurvey.firstActionNewBadge': 'New',
  'onboarding.welcomeSurvey.firstActionQuestion': 'What do you want to set up first?',
  'onboarding.welcomeSurvey.getStarted': 'Get started',
  'onboarding.welcomeSurvey.heading': 'Welcome to LaunchDarkly',
  'onboarding.welcomeSurvey.reasonQuestion': 'How will you use LaunchDarkly?',
  'onboarding.welcomeSurvey.roleQuestion': "What's your role?",
  'onboarding.welcomeSurvey.selectPlaceholder': 'Select an option',
  'onboarding.welcomeSurvey.subheading': "Let's get you set up with a few quick questions",
  'onboarding.welcomeSurvey.submitError': 'Something went wrong. Try again.',
  'onboarding.welcomeSurvey.submittingLabel': 'Submitting',
  'pendingChanges.approvalRequestCard.deleteBody':
    'Deleting this approval request will permanently remove it from <strong>{flagName}</strong>.',
  'pendingChanges.approvalRequestCard.deleteHeading': 'Delete approval request?',
  'pendingChanges.approvalRequestCard.heading': 'Approval requested',
  'pendingChanges.approvalRequestCard.view': 'View',
  'pendingChanges.approvalRequestCard.viewInIntegration': 'View in {integrationName}',
  'pendingChanges.changeConflicts.cannotApply':
    'LaunchDarkly cannot apply these changes. Resolve the issues below, then try again.',
  'pendingChanges.changeConflicts.pendingChangeWillFail':
    'This change conflicts with a pending change and will cause it to fail.',
  'pendingChanges.changeConflicts.scheduledChangeWillFail':
    'This change conflicts with a scheduled change and will cause it to fail.',
  'pendingChanges.changeList.clearOffVariation': 'Clear off variation',
  'pendingChanges.changeList.ruleAlreadyRemoved': 'Rule {ruleId} already removed',
  'pendingChanges.changeList.viewConflict': 'View conflict',
  'pendingChanges.confirmation.changesHeading': 'Changes',
  'pendingChanges.confirmation.experimentsBody': '{description} {runningOnFlag} before proceeding.',
  'pendingChanges.confirmation.title': 'Save changes',
  'pendingChanges.confirmationAlerts.conflictsErrorBody':
    "We couldn't check for conflicts with scheduled changes. You can still proceed with your changes, but be aware that conflicts may exist.",
  'pendingChanges.confirmationAlerts.conflictsErrorHeading': 'Error checking for conflicts',
  'pendingChanges.confirmationAlerts.deprecatedBody': 'Deprecated flags are still evaluated.',
  'pendingChanges.confirmationAlerts.deprecatedHeading': 'You are making changes to a deprecated flag',
  'pendingChanges.confirmationAlerts.healthCheckBody':
    'Some health checks related to your guarded rollout configuration are failing. Proceeding may result in insufficient data collection associated with your rollout.',
  'pendingChanges.confirmationAlerts.healthCheckHeading': 'Health check warnings',
  'pendingChanges.confirmationAlerts.multipleApprovalsBody':
    'Approval will be requested separately for flag targeting updates and updates to the scheduled removal of individual targets.',
  'pendingChanges.confirmationAlerts.multipleApprovalsHeading': 'This will create {count} approval requests.',
  'pendingChanges.confirmationAlerts.targetingOff':
    'Your changes will be saved, but the {resourceKind, select, aiConfig {AI config} other {flag}} will serve the off variation to all traffic until it is turned on.',
  'pendingChanges.contextsConfirmation.changesHeading': 'Changes',
  'pendingChanges.contextsConfirmation.defaultResourceName': 'Context',
  'pendingChanges.contextsConfirmation.requestIntegrationApprovalTitle': 'Request {approvalSystem} approval',
  'pendingChanges.deleteApprovalRequest.body':
    'Deleting this approval request will permanently remove it from <strong>{resourceName}</strong>',
  'pendingChanges.deleteApprovalRequest.cancel': 'Cancel',
  'pendingChanges.deleteApprovalRequest.closeLabel': 'close',
  'pendingChanges.deleteApprovalRequest.heading': 'Delete approval request?',
  'pendingChanges.drawer.changeLabel': 'Change',
  'pendingChanges.drawer.changesThatWillFail': 'Changes that will fail',
  'pendingChanges.drawer.conflictingChangesHeading': 'Conflicting pending changes for <strong>{flagName}</strong>',
  'pendingChanges.drawer.conflictingChangesHeadingLabel': 'Conflicting pending changes for {flagName}',
  'pendingChanges.drawer.conflictsBlockingAlert':
    'Unresolved conflicts are blocking your pending changes. Resolve these conflicts now, or continue and resolve them later.',
  'pendingChanges.drawer.conflictsBlockingBody':
    "Unresolved conflicts are blocking your pending changes. Resolve these conflicts now, or continue and resolve them later. Click on '…' to edit or delete the changes.",
  'pendingChanges.drawer.conflictsFoundAlert':
    'Unresolved conflicts found. Resolve the conflicted changes to continue.',
  'pendingChanges.drawer.continueSaving': 'Continue with saving/scheduling changes',
  'pendingChanges.drawer.emptyDescriptionScheduledOnly':
    'This drawer will show changes that are scheduled for this flag. To schedule a change select schedule changes from the save changes dropdown after you have made updates to the flag.',
  'pendingChanges.drawer.emptyDescriptionWithApprovals':
    'This drawer will show scheduled changes and approval requests. Select the option to schedule or request approval from the save changes dropdown after you have made updates to the flag.',
  'pendingChanges.drawer.emptyImageAlt': "Save changes button with the drop down option 'Schedule changes' selected",
  'pendingChanges.drawer.failureReasonLabel': 'Failure reason',
  'pendingChanges.drawer.noConflictsForChange':
    '<instructions></instructions> does not have any conflicts. Close this panel to continue.',
  'pendingChanges.drawer.noConflictsForInstruction': 'No conflicts found for this instruction',
  'pendingChanges.drawer.noPendingChanges': 'No pending changes',
  'pendingChanges.drawer.notFound': "Sorry, we can't find the pending change you're looking for.",
  'pendingChanges.drawer.pendingChangesHeading': 'Pending changes for <strong>{flagName}</strong>',
  'pendingChanges.drawer.pendingChangesHeadingLabel': 'Pending changes for {flagName}',
  'pendingChanges.drawer.scheduledChangeHeading': 'Scheduled change for <strong>{flagName}</strong>',
  'pendingChanges.drawer.scheduledChangeHeadingLabel': 'Scheduled change for {flagName}',
  'pendingChanges.drawer.serviceToken': 'Service token',
  'pendingChanges.drawer.viewUnresolvedConflicts': 'View unresolved conflicts',
  'pendingChanges.experimentDrawer.cardHeading': 'Approval requested',
  'pendingChanges.experimentDrawer.copyLink': 'Copy link',
  'pendingChanges.experimentDrawer.emptyDescription':
    'This drawer will show approval requests for this {experimentKind}.',
  'pendingChanges.experimentDrawer.flagItem': 'Flag <strong>{flagName}</strong>',
  'pendingChanges.experimentDrawer.heading': 'Pending changes for:',
  'pendingChanges.experimentDrawer.headingLabel':
    'Pending changes for {experimentKind} {experimentName} and flag {flagName}',
  'pendingChanges.experimentDrawer.noPendingChanges': 'No pending changes',
  'pendingChanges.experimentDrawer.optionsLabel': 'Pending changes options',
  'pendingChanges.experimentDrawer.view': 'View',
  'pendingChanges.experimentRollout.canReshuffle': 'and users can be reshuffled.',
  'pendingChanges.experimentRollout.cannotReshuffle': 'and users cannot be reshuffled.',
  'pendingChanges.experimentRollout.remaining': 'and remaining {percent}% to',
  'pendingChanges.expiringTargetChange.deleteScheduledRemoval': 'Delete scheduled removal',
  'pendingChanges.expiringTargetChange.forDate': 'for',
  'pendingChanges.expiringTargetChange.ofContext': 'of {contextKind}',
  'pendingChanges.expiringTargetChange.onFlag': 'on',
  'pendingChanges.expiringTargetChange.scheduleRemoval': 'Schedule removal',
  'pendingChanges.expiringTargetChange.toDate': 'to',
  'pendingChanges.expiringTargetChange.updateScheduledRemoval': 'Update scheduled removal',
  'pendingChanges.flagCopyConfirmation.description':
    'This will overwrite the following flag settings in your <environment></environment> environment.',
  'pendingChanges.flagCopyConfirmation.title': 'Update {flagName}?',
  'pendingChanges.impactedEnvironments.approvalRequiredBody':
    'This change impacts environments where approvals are required.',
  'pendingChanges.impactedEnvironments.approvalRequiredHeading': 'Approval required',
  'pendingChanges.impactedEnvironments.impactBody':
    'This change impacts environments where the variation being modified is served.',
  'pendingChanges.impactedEnvironments.impactHeading': 'Impact to served variations',
  'pendingChanges.impactedEnvironments.label': 'Impacted environments:',
  'pendingChanges.impactedEnvironments.learnMore': 'Learn more',
  'pendingChanges.impactedEnvironments.more': '{count} more',
  'pendingChanges.nestedWorkflow.scheduledFor': 'Scheduled for',
  'pendingChanges.notificationLink.viewApprovalRequest': 'View approval request',
  'pendingChanges.notificationLink.viewPendingChanges': 'View pending changes',
  'pendingChanges.notificationLink.viewScheduledChange': 'View scheduled change',
  'pendingChanges.pendingChangesButton.viewPendingChanges': 'View pending changes',
  'pendingChanges.pendingChangesCard.copyLink': 'Copy link',
  'pendingChanges.pendingChangesCard.delete': 'Delete',
  'pendingChanges.pendingChangesCard.optionsLabel': 'Pending changes options',
  'pendingChanges.pendingChangesSidebar.allEnvironments': 'All environments',
  'pendingChanges.pendingChangesSidebar.approvalRequested': 'Approval requested',
  'pendingChanges.pendingChangesSidebar.failed': 'Failed',
  'pendingChanges.pendingChangesSidebar.failedOn': 'Failed to apply on {date}',
  'pendingChanges.pendingChangesSidebar.none': 'None',
  'pendingChanges.pendingChangesSidebar.scheduled': 'Scheduled',
  'pendingChanges.pendingChangesSidebar.scheduledFor': 'Scheduled for {date}',
  'pendingChanges.pendingChangesSidebar.serviceToken': 'Service token',
  'pendingChanges.pendingChangesSidebar.showMore': 'Show more',
  'pendingChanges.releaseChange.defaultRuleName': 'default rule',
  'pendingChanges.releaseChange.ruleName': 'Rule {number}',
  'pendingChanges.releaseChange.ruleNotFound': 'Rule not found',
  'pendingChanges.releaseChange.startRelease': 'Start release on {ruleName}',
  'pendingChanges.releaseChange.variationNotFound': 'VARIATION NOT FOUND',
  'pendingChanges.resourceInfo.body':
    'Changes to <strong>{resourceName}</strong> on <strong><environment></environment></strong>',
  'pendingChanges.reviewAndSave.acknowledgeConflicts':
    'I acknowledge saving this change will introduce conflicts with pending changes',
  'pendingChanges.reviewAndSave.acknowledgeConflictsError': 'Acknowledge conflicts is required',
  'pendingChanges.reviewAndSave.back': 'Back',
  'pendingChanges.reviewAndSave.cancel': 'Cancel',
  'pendingChanges.reviewAndSave.commentDescription': 'Add a brief description of your changes.',
  'pendingChanges.reviewAndSave.commentLabel': 'Comment',
  'pendingChanges.reviewAndSave.commentRequiredError': 'Comment is required',
  'pendingChanges.reviewAndSave.commentTooLongError': 'Comment must be less than 5000 characters',
  'pendingChanges.reviewAndSave.confirmDescription': 'Type <strong>{resource}</strong> to confirm.',
  'pendingChanges.reviewAndSave.confirmLabel': 'Confirm',
  'pendingChanges.reviewAndSave.confirmationRequiredError': 'Resource confirmation is required',
  'pendingChanges.reviewAndSave.delete': 'Delete',
  'pendingChanges.reviewAndSave.executionDateError': 'Execution date must be in the future',
  'pendingChanges.reviewAndSave.formErrorsLabel': 'Form errors',
  'pendingChanges.reviewAndSave.maxReviewersError': 'You may only specify at most {max} reviewers',
  'pendingChanges.reviewAndSave.minReviewersError':
    'At least {count, plural, one {# reviewer is} other {# reviewers are}} required to approve your changes.',
  'pendingChanges.reviewAndSave.requestApproval': 'Request approval',
  'pendingChanges.reviewAndSave.save': 'Save',
  'pendingChanges.reviewAndSave.saveWithoutApproval': 'Save without approval',
  'pendingChanges.reviewAndSave.scheduleChanges': 'Schedule changes',
  'pendingChanges.reviewAndSave.scheduleWithoutApproval': 'Schedule without approval',
  'pendingChanges.reviewAndSave.updateScheduledChange': 'Update scheduled change',
  'pendingChanges.reviewStatus.approved': 'Approved',
  'pendingChanges.reviewStatus.declined': 'Declined',
  'pendingChanges.reviewStatus.groupLabel': 'status',
  'pendingChanges.reviewStatus.pendingReview': 'Pending review',
  'pendingChanges.reviewToolbar.collapseAll': 'Collapse all changes',
  'pendingChanges.reviewToolbar.expandAll': 'Expand all changes',
  'pendingChanges.reviewToolbar.json': 'JSON',
  'pendingChanges.reviewToolbar.jsonConflictsTooltip': 'Cannot render JSON with conflicts',
  'pendingChanges.reviewToolbar.jsonLabel': 'JSON editing',
  'pendingChanges.reviewToolbar.testRun': 'Test run',
  'pendingChanges.reviewToolbar.testRunConflictsTooltip': 'Cannot run test run with conflicts',
  'pendingChanges.reviewToolbar.visual': 'Visual',
  'pendingChanges.reviewToolbar.visualLabel': 'UI editing',
  'pendingChanges.reviewersField.bypassApprovalsLabel': 'Bypass approvals',
  'pendingChanges.reviewersField.loadError': 'Unable to load reviewers',
  'pendingChanges.reviewersField.optionalDescription': 'Add an optional reviewer to approve your changes.',
  'pendingChanges.reviewersField.requestApproval': 'Request approval',
  'pendingChanges.reviewersField.requestReviewersLabel': 'Request reviewers',
  'pendingChanges.reviewersField.requiredDescription':
    'Select {count, plural, one {# reviewer} other {# reviewers}} to approve your changes',
  'pendingChanges.reviewersField.reviewersLabel': 'Reviewer(s)',
  'pendingChanges.reviewersField.saveWithoutApproval': 'Save without approval',
  'pendingChanges.reviewersField.selectReviewersPlaceholder': 'Select reviewers',
  'pendingChanges.reviewersPicker.clearTag': 'Clear tag',
  'pendingChanges.reviewersPicker.loading': 'Loading',
  'pendingChanges.reviewersPicker.me': 'Me',
  'pendingChanges.reviewersPicker.membersOrTeamsPlaceholder': ' Select members or teams',
  'pendingChanges.reviewersPicker.membersPlaceholder': 'Select members',
  'pendingChanges.reviewersPicker.noMembers': 'No members found',
  'pendingChanges.reviewersPicker.noMembersOrTeams': 'No members or teams found',
  'pendingChanges.reviewersPicker.reviewersGroup': 'Members with permission to review approval requests',
  'pendingChanges.reviewersPicker.reviewersWithTeamsGroup':
    'Members and teams with permission to review approval requests',
  'pendingChanges.reviewersPicker.teamOption': '{teamName} ({count, plural, one {# person} other {# people}})',
  'pendingChanges.reviewersPicker.viewersGroup': 'Members who can view approval requests',
  'pendingChanges.reviewersPicker.viewersWithTeamsGroup': 'Members and teams that can view approval requests',
  'pendingChanges.saveOptions.cannotScheduleExpiringTargets':
    'Cannot schedule to remove an individual context at a future date',
  'pendingChanges.saveOptions.upgradeRequired': 'Upgrade your plan to use this feature',
  'pendingChanges.scheduleDate.label': 'Schedule change for:',
  'pendingChanges.schedulePopover.removeScheduleLabel': 'Remove schedule',
  'pendingChanges.schedulePopover.scheduleChanges': 'Schedule changes',
  'pendingChanges.schedulePopover.scheduledLabel': 'Schedule changes, currently scheduled in {date}',
  'pendingChanges.schedulePopover.update': 'Update',
  'pendingChanges.scheduledChangeCard.deleteBody':
    'Deleting this scheduled change will permanently remove it from <strong>{flagName}</strong> and the changes will not be applied at the scheduled date.',
  'pendingChanges.scheduledChangeCard.deleteBodyNested':
    'This pending change for <strong>{flagName}</strong> will be permanently removed and the changes will not be applied at the scheduled date and time',
  'pendingChanges.scheduledChangeCard.edit': 'Edit',
  'pendingChanges.scheduledChangeCard.editRequiresApproval':
    'You cannot edit a scheduled change when an approval is required',
  'pendingChanges.scheduledChangeCard.scheduledFor': 'Scheduled for',
  'pendingChanges.scheduledChangeCard.willFailOn': 'Scheduled change will fail on',
  'pendingChanges.status.loading': 'Loading…',
  'pendingChanges.targetChange.addLabel': 'Add {targets}{extra} to variation {variation}',
  'pendingChanges.targetChange.addVerb': 'Add',
  'pendingChanges.targetChange.allTargets': 'all {contextKind} targets',
  'pendingChanges.targetChange.allUsersLabel': 'all users',
  'pendingChanges.targetChange.extraTargets': ', and {count} more',
  'pendingChanges.targetChange.forLabel': 'for',
  'pendingChanges.targetChange.fromPreposition': 'from',
  'pendingChanges.targetChange.removeLabel': 'Remove {targets}{extra} from variation {variation}',
  'pendingChanges.targetChange.removeVerb': 'Remove',
  'pendingChanges.targetChange.replaceLabel': '{targets}{extra} to variation {variation}',
  'pendingChanges.targetChange.toPreposition': 'to',
  'pendingChanges.targetChange.unknownLabel': '{targets}{extra} variation {variation}',
  'pendingChanges.targetChange.variationNotFound': 'Variation not found',
  'pendingChanges.targetingEntry.body':
    '{description} {direction, select, add {to} other {from}} the variation<variation></variation> for <link>{flagName}</link>',
  'pendingChanges.targetingEntry.label':
    '{description} {targets} {direction, select, add {to} other {from}} the variation {variationName}',
  'pendingChanges.testRun.added': 'Added',
  'pendingChanges.testRun.allApplications': 'All applications',
  'pendingChanges.testRun.applicationLabel': 'Select application',
  'pendingChanges.testRun.applicationListLabel': 'Filter by application',
  'pendingChanges.testRun.configureMetadata': '<link>Configure application metadata</link> for more robust results.',
  'pendingChanges.testRun.contextKindsNote': 'The context kinds are limited to: <kinds></kinds>.',
  'pendingChanges.testRun.individualTargetingNote': 'Individual targeting accounts for {percentage} of the breakdown',
  'pendingChanges.testRun.intro':
    'An estimate of how each rule may evaluate, based on a sampling of contexts LaunchDarkly has seen in the last seven days.',
  'pendingChanges.testRun.learnMore': 'Learn more',
  'pendingChanges.testRun.loadError': 'Failed to load',
  'pendingChanges.testRun.modified': 'Modified',
  'pendingChanges.testRun.noChange': 'No change',
  'pendingChanges.testRun.noClientSideEvaluations':
    "We haven't received any evaluations from client-side SDKs. Support for additional SDKs is coming soon.",
  'pendingChanges.testRun.noEvaluations':
    'There are no evaluations{hasKinds, select, true { for <kinds></kinds> contexts } other {}}{inApplication, select, true { in this application} other {}}.',
  'pendingChanges.testRun.notCalculated': 'Not calculated',
  'pendingChanges.testRun.percentageColumn': 'Percentage',
  'pendingChanges.testRun.rolloutLabel': '{rolloutType} rollout',
  'pendingChanges.testRun.rolloutTooltip': 'A {rolloutType} rollout',
  'pendingChanges.testRun.ruleColumn': 'Rule',
  'pendingChanges.testRun.ruleName': 'Rule {number}',
  'pendingChanges.testRun.ruleStatusLabel': 'Change status for rule {description}',
  'pendingChanges.testRun.rulesHeading': 'Breakdown by rules',
  'pendingChanges.testRun.tableLabel': 'table',
  'pendingChanges.testRun.variationColumn': 'Variation',
  'pendingChanges.testRun.variationsHeading': 'Variations',
  'pendingChanges.variationChange.changeMetadata': 'Change {field, select, name {name} other {description}}',
  'pendingChanges.variationChange.changeValue': 'Change value',
  'pendingChanges.variationChange.changeValueOfVariation': 'Change value of variation <name>{variationName}</name>',
  'pendingChanges.variationChange.descriptionLabel': 'Description:',
  'pendingChanges.variationChange.errorRenderingJson': 'Error rendering JSON diff',
  'pendingChanges.variationChange.fromLabel': 'from:',
  'pendingChanges.variationChange.jsonDiffKeysAdded': '{count, plural, one {# key} other {# keys}} added',
  'pendingChanges.variationChange.jsonDiffKeysRemoved': '{count, plural, one {# key} other {# keys}} removed',
  'pendingChanges.variationChange.jsonDiffNoChanges': 'No changes',
  'pendingChanges.variationChange.jsonDiffNoValuesChanged': 'no existing values changed',
  'pendingChanges.variationChange.jsonDiffValuesChanged':
    '{count, plural, one {# existing value} other {# existing values}} changed',
  'pendingChanges.variationChange.nameLabel': 'Name:',
  'pendingChanges.variationChange.namedWithValue': '<nameText>{name}</nameText> with value:',
  'pendingChanges.variationChange.removeMetadata':
    'Remove {field, select, name {name} other {description}} <valueText>{value}</valueText>',
  'pendingChanges.variationChange.setMetadata':
    'Set {field, select, name {name} other {description}} to <valueText>{value}</valueText>',
  'pendingChanges.variationChange.toLabel': 'to:',
  'pendingChanges.variationChange.valueLabel': 'Value:',
  'pendingChanges.variationChange.withValue': 'with value:',
  'pendingChanges.variationConfirmation.summaryHeading': 'Summary of changes',
  'pendingChanges.variationConfirmation.title': 'Save changes',
  'pendingChanges.variationSave.moreOptionsLabel': 'More options',
  'pendingChanges.variationSave.reviewAndSave': 'Review and save',
  'permissions.accessRestricted.actionDenied': "Your permissions don't allow you to perform this action",
  'permissions.accessRestricted.actionDeniedByRole': 'The "{roleName}" role does not allow you to perform this action',
  'permissions.accessRestricted.fieldDenied': 'Your permissions don’t allow you to modify this field',
  'permissions.accessRestricted.fieldDeniedByRole': 'The "{roleName}" role does not allow you to modify this field',
  'permissions.accessRestricted.settingDenied': 'Your permissions don’t allow you to change this setting',
  'permissions.accessRestricted.settingDeniedByRole': 'The "{roleName}" role does not allow you to change this setting',
  'permissions.accessSummary.assignedByColumn': 'Assigned by',
  'permissions.accessSummary.copyAttributeLabel': 'Copy {attribute} to clipboard',
  'permissions.accessSummary.heading': 'Roles',
  'permissions.accessSummary.memberDescription':
    "All permissions this member has across roles and teams. You can edit or remove only directly assigned roles. To change a role inherited from a team, go to that team's page.",
  'permissions.accessSummary.none': 'None',
  'permissions.accessSummary.resourcesColumn': 'Resources',
  'permissions.accessSummary.resourcesTooltip':
    'Number of resources this role attribute is scoped to. “None” means no resources are assigned for this {kind, select, member {member} other {team}}; it doesn’t restrict the role’s permissions.',
  'permissions.accessSummary.roleAttributeKeyColumn': 'Role attribute key',
  'permissions.accessSummary.roleCount': '{count, plural, one {role} other {roles}}',
  'permissions.accessSummary.roleCountLabel': 'roles count',
  'permissions.accessSummary.roleNameColumn': 'Role name',
  'permissions.accessSummary.searchLabel': 'Search roles',
  'permissions.accessSummary.searchPlaceholder': 'Search by role or attribute name',
  'permissions.accessSummary.teamDescription':
    'All permissions this team has across roles. You can edit or remove only directly assigned roles.',
  'permissions.assignAccessDialog.cancel': 'Cancel',
  'permissions.assignAccessDialog.closeLabel': 'close modal',
  'permissions.assignAccessDialog.openButton': 'Assign access',
  'permissions.assignAccessDialog.projectRoleLabel': 'Select a project role',
  'permissions.assignAccessDialog.roleKindLabel': 'Role kind',
  'permissions.assignAccessDialog.roleLabel': 'Select a role',
  'permissions.assignAccessDialog.submit': 'Assign access',
  'permissions.assignAccessDialog.teamsRecommendation':
    'To give more than 1 member access to the same set of resources, we recommend assigning access from the Teams page.',
  'permissions.assignAccessDialog.title': 'Assign access',
  'permissions.assignAccessDialog.updateError': 'Failed to update access. Try again later.',
  'permissions.assignAccessDialog.updated': 'Access updated',
  'permissions.assignLegacyRoleDialog.baseRoleLabel': 'Select a base role',
  'permissions.assignLegacyRoleDialog.body':
    'Select a base role to replace the existing role for {memberIdCount, plural, one {this} other {these}} {count, plural, one {member} other {members}}.',
  'permissions.assignLegacyRoleDialog.cancel': 'Cancel',
  'permissions.assignLegacyRoleDialog.classicBody':
    'Select a classic role to replace the existing role for {memberIdCount, plural, one {this} other {these}} {count, plural, one {member} other {members}}.',
  'permissions.assignLegacyRoleDialog.classicRoleLabel': 'Select a classic role',
  'permissions.assignLegacyRoleDialog.classicTitle':
    'Edit classic role for {count, plural, one {# member} other {# members}}',
  'permissions.assignLegacyRoleDialog.closeLabel': 'close modal',
  'permissions.assignLegacyRoleDialog.openButton': 'Replace {count, plural, one {role} other {roles}}',
  'permissions.assignLegacyRoleDialog.submit': 'Replace {count, plural, one {role} other {roles}}',
  'permissions.assignLegacyRoleDialog.title': 'Edit base role for {count, plural, one {# member} other {# members}}',
  'permissions.assignLegacyRoleDialog.updateError':
    'Failed to update member {count, plural, one {role} other {roles}}. Try again later.',
  'permissions.assignLegacyRoleDialog.updated': '{count, plural, one {Member} other {Members}} updated',
  'permissions.organizationRoleDialog.addTitle': 'Add organization role',
  'permissions.organizationRoleDialog.body': 'Select an organization role to assign to this member.',
  'permissions.organizationRoleDialog.cancel': 'Cancel',
  'permissions.organizationRoleDialog.closeLabel': 'close modal',
  'permissions.organizationRoleDialog.editTitle': 'Edit organization role',
  'permissions.organizationRoleDialog.rolesLabel': 'Organization roles',
  'permissions.organizationRoleDialog.save': 'Save changes',
  'permissions.organizationRoleDialog.updateError': 'Failed to update organization role. Try again later.',
  'permissions.organizationRoleDialog.updated': 'Organization role updated',
  'permissions.organizationRoleMenu.description':
    'Take advantage of our new role-based access control to provide invitees access to your organization’s LaunchDarkly account.',
  'permissions.organizationRoleMenu.title': 'Organization roles',
  'permissions.permissionsCard.warningAnnouncement': '{lead}, {detail}',
  'permissions.permissionsCard.warningSentenceAnnouncement': '{lead}. {detail}',
  'permissions.permissionsCard.warningSentenceStatement': '<emphasis>{lead}.</emphasis> {detail}',
  'permissions.permissionsCard.warningStatement': '<emphasis>{lead}</emphasis>, {detail}',
  'permissions.removeAccessDialog.body':
    'This action will remove access to this role. This may affect other roles for this member if this role has assigned attribute values and other roles are using the same values. Are you sure you want to remove access?',
  'permissions.removeAccessDialog.cancel': 'Cancel',
  'permissions.removeAccessDialog.confirm': 'Remove access',
  'permissions.removeAccessDialog.removeFromMemberError': 'Failed to remove role from member. Try again later.',
  'permissions.removeAccessDialog.removeFromTeamError': 'Failed to remove role from team. Try again later.',
  'permissions.removeAccessDialog.removedFromMember': 'Role successfully removed from member',
  'permissions.removeAccessDialog.removedFromTeam': 'Role successfully removed from team',
  'permissions.removeAccessDialog.removingLabel': 'Removing access',
  'permissions.removeAccessDialog.title': 'Remove access',
  'permissions.restrictedControl.checkError': 'Failed to check access',
  'permissions.restrictedControl.continue': 'Continue',
  'permissions.restrictedControl.defaultTooltip':
    'You are not allowed to perform this action. Contact your administrator for more information.',
  'permissions.restrictedControl.denied': "Your permissions don't allow you to perform this action",
  'permissions.restrictedControl.partialDenialBody':
    'You are unable to perform this action. If you believe this is an error, click below to continue.',
  'permissions.restrictedControl.verifyError': 'Could not verify permissions. Refresh and try again.',
  'permissions.roleActions.actionsLabel': 'Role actions',
  'permissions.roleActions.editAccess': 'Edit access',
  'permissions.roleActions.manageAtTeam': 'Manage at "{team}"',
  'permissions.roleActions.memberHeading': 'Member',
  'permissions.roleActions.removeAccess': 'Remove access',
  'permissions.roleActions.teamsHeading': 'Teams',
  'permissions.roleAttributesForm.description':
    'Select the specific resource(s) that will apply in place of the defined role attributes:',
  'permissions.roleAttributesForm.resourceHelpText': 'Type a resource key, pressing "Enter" after each key',
  'permissions.roleAttributesForm.resourcesColumn': 'Resources',
  'permissions.roleAttributesForm.resourcesLabel': 'Enter resources for {attribute}',
  'permissions.roleAttributesForm.resourcesPlaceholder': 'Resource keys',
  'permissions.roleAttributesForm.roleAttributeColumn': 'Role attribute',
  'permissions.roleAttributesForm.tableLabel': 'Role attributes and resources',
  'permissions.roleAttributesForm.tagHelpText': 'Type a tag, pressing "Enter" after each tag',
  'permissions.roleAttributesForm.viewHelpText': 'Type a view key, pressing "Enter" after each key',
  'permissions.roleAttributesSummary.assignedByColumn': 'Assigned by',
  'permissions.roleAttributesSummary.expandLabel': 'View {attribute} resources',
  'permissions.roleAttributesSummary.heading': 'Permissions',
  'permissions.roleAttributesSummary.memberDescription':
    'All resources this member can access through their role attributes.',
  'permissions.roleAttributesSummary.resourceCount': '{count, plural, one {resource} other {resources}}',
  'permissions.roleAttributesSummary.resourceCountLabel': 'resources count',
  'permissions.roleAttributesSummary.resourcesColumn': 'Resources',
  'permissions.roleAttributesSummary.resourcesTagsLabel': 'Resources assigned by {source} for {attribute}',
  'permissions.roleAttributesSummary.teamDescription':
    'All resources this team can access through its role attributes.',
  'permissions.roleAttributesSummary.valuesTableLabel': 'Attribute values by assignment source',
  'permissions.roleFilter.allRoles': 'Role: All',
  'permissions.roleFilter.baseRolesHeading': 'Base roles',
  'permissions.roleFilter.classicRolesHeading': 'Classic roles',
  'permissions.roleFilter.clearLabel': 'Clear filter',
  'permissions.roleFilter.dialogLabel': 'Role filter',
  'permissions.roleFilter.loadError': 'An error occurred. Our team has been notified.',
  'permissions.roleFilter.menuLabel': 'Role filter menu',
  'permissions.roleFilter.noRolesFound': 'No additional roles found',
  'permissions.roleFilter.organizationRolesHeading': 'Organization roles',
  'permissions.roleFilter.projectRolesHeading': 'Project roles',
  'permissions.roleFilter.searchLabel': 'Search roles',
  'permissions.roleFilter.searchPlaceholder': 'Search roles',
  'permissions.roleFilter.selectedCount': '{count, plural, one {# role} other {# roles}}',
  'permissions.rolePresetsAlert.reviewDescription': 'Predefined roles are available for you to review and accept.',
  'permissions.rolePresetsAlert.reviewHeading': 'Review predefined roles',
  'permissions.rolePresetsAlert.updatesDescription':
    'The role policies for predefined LaunchDarkly roles have been updated. View the changes before rolling out this update across members in your organization.',
  'permissions.rolePresetsAlert.updatesHeading': 'Updates to predefined roles',
  'permissions.rolePresetsDialog.accept': 'Accept',
  'permissions.rolePresetsDialog.applyAllChanges': 'Apply all changes',
  'permissions.rolePresetsDialog.diffTitle': '{category} role: {name} ',
  'permissions.rolePresetsDialog.loadError': 'Failed to load role presets. Try again later.',
  'permissions.rolePresetsDialog.loadingRoles': 'Loading roles…',
  'permissions.rolePresetsDialog.loadingUpdates': 'Loading role updates…',
  'permissions.rolePresetsDialog.nextRoleLabel': 'Next role',
  'permissions.rolePresetsDialog.predefinedRolesTitle': 'Predefined roles',
  'permissions.rolePresetsDialog.previousRoleLabel': 'Previous role',
  'permissions.rolePresetsDialog.review': 'Review',
  'permissions.rolePresetsDialog.reviewBody': 'Review and accept what each role policy grants access to.',
  'permissions.rolePresetsDialog.reviewingProgress': 'Reviewing {current} of {total}',
  'permissions.rolePresetsDialog.skipForNow': 'Skip for now',
  'permissions.rolePresetsDialog.updateError': 'Failed to update role presets. Try again later.',
  'permissions.rolePresetsDialog.updated': 'Role presets updated',
  'permissions.rolePresetsDialog.updatedRolesTitle': 'Updated roles',
  'permissions.rolePresetsDialog.updatesBody':
    'Inspect what changed in each role policy. Then, apply the changes. All members who have been assigned these roles will receive the updates.',
  'permissions.rolePresetsDialog.viewChanges': 'View changes',
  'permissions.roleSelect.clearLabel': 'Clear',
  'permissions.roleSelect.noRolesFound': 'No roles found',
  'permissions.roleSelect.pickerLabel': 'Select roles',
  'permissions.roleSelect.searchLabel': 'Search roles',
  'permissions.roleSelect.searchPlaceholder': 'Search roles',
  'permissions.roleSelect.selectRole': 'Select role',
  'permissions.roleSelect.selectRoles': 'Select roles',
  'permissions.roleSelect.selectedCount': '{count, plural, one {# role} other {# roles}} selected',
  'permissions.status.loading': 'Loading…',
  'permissions.tagAccessAlert.body': "You've made changes to tags that will limit your ability to edit these settings",
  'permissions.tagInput.addLabel': 'Add',
  'permissions.tagInput.selectedItemsLabel': 'selected items',
  'productAnalytics.addContentMenu.loadingLabel': 'Loading add content button',
  'productAnalytics.addContentMenu.textItem': 'Text',
  'productAnalytics.addContentMenu.triggerLabel': 'Add content',
  'productAnalytics.analyticsLayout.createChart': 'Create chart',
  'productAnalytics.analyticsOnboarding.awaitingDescription': 'This can take a few minutes to complete.',
  'productAnalytics.analyticsOnboarding.awaitingTitle': "We're setting up your <highlight>data connection</highlight>",
  'productAnalytics.analyticsOnboarding.setupDescription':
    'Bring in events via SDK or by integrating your CDP or warehouse.',
  'productAnalytics.analyticsOnboarding.setupTitleLine1': 'Import <highlight>events</highlight>',
  'productAnalytics.analyticsOnboarding.setupTitleLine2': 'to analytics',
  'productAnalytics.analyticsTabs.cohorts': 'Cohorts',
  'productAnalytics.analyticsTabs.dashboards': 'Dashboards',
  'productAnalytics.analyticsTabs.events': 'Events',
  'productAnalytics.analyticsTabs.userActivity': 'User Activity',
  'productAnalytics.analyticsTabs.userAttributes': 'User attributes',
  'productAnalytics.bigQueryForm.credentialsHint':
    'Upload a Service Account key file with access to BigQuery. You can generate this in the IAM & Admin → Service Accounts section by creating a new key (type: JSON). Follow the <link>setup guide for step-by-step instructions</link>.',
  'productAnalytics.bigQueryForm.credentialsLabel': 'BigQuery credentials (JSON file)',
  'productAnalytics.bigQueryForm.heading': 'Configure {warehouse} warehouse',
  'productAnalytics.bigQueryForm.nameLabel': 'Name',
  'productAnalytics.bigQueryForm.namePlaceholder': 'Enter a descriptive name for this integration',
  'productAnalytics.bigQueryForm.projectIdHint':
    'Enter your GCP Project ID. This is used to locate your BigQuery dataset. Follow the <link>setup guide</link>for help.',
  'productAnalytics.bigQueryForm.projectIdLabel': 'Project ID',
  'productAnalytics.bigQueryForm.projectIdPlaceholder': 'Enter your Project ID',
  'productAnalytics.bigQueryForm.save': 'Save configuration',
  'productAnalytics.bigQueryForm.saving': 'Saving…',
  'productAnalytics.bigQueryForm.schemaExistsHint':
    'Important: The dataset must exist in your BigQuery project before proceeding.',
  'productAnalytics.bigQueryForm.schemaHint': "Enter your BigQuery dataset name. If left empty, we'll use: {schema}",
  'productAnalytics.bigQueryForm.schemaLabel': 'Schema name',
  'productAnalytics.bigQueryForm.schemaPlaceholder': 'Enter your schema name',
  'productAnalytics.bigQueryForm.subheading':
    'Enter credentials to connect to your {warehouse} warehouse. Before filling out this form, make sure you complete all the steps mentioned in the <link>documentation</link>.',
  'productAnalytics.bigQueryForm.terms': 'I have read and agree to the <link>Integration Terms and Conditions</link>',
  'productAnalytics.blockError.body':
    'Something went wrong. Try refreshing the page. If this keeps happening, delete the content or contact Support.',
  'productAnalytics.blockError.heading': 'Error',
  'productAnalytics.blockError.reconfigure': 'Reconfigure',
  'productAnalytics.blockError.reload': 'Reload',
  'productAnalytics.blockError.retry': 'Re-configure',
  'productAnalytics.blockError.visualizationBody': 'Something went wrong while loading the visualization',
  'productAnalytics.blockOptionsMenu.assetChart': 'chart',
  'productAnalytics.blockOptionsMenu.assetContent': 'content',
  'productAnalytics.blockOptionsMenu.assetDashboard': 'dashboard',
  'productAnalytics.blockOptionsMenu.assetTextContent': 'text content',
  'productAnalytics.blockOptionsMenu.copyLink': 'Copy link to {asset}',
  'productAnalytics.blockOptionsMenu.copyToDashboard': 'Copy to dashboard',
  'productAnalytics.blockOptionsMenu.delete': 'Delete',
  'productAnalytics.blockOptionsMenu.deleteConfirmBody':
    'This action cannot be undone. This will delete the {asset} and all of its content.',
  'productAnalytics.blockOptionsMenu.deleteConfirmHeading': 'Are you sure you want to delete this {asset}?',
  'productAnalytics.blockOptionsMenu.deleteConfirmLabel': 'Delete',
  'productAnalytics.blockOptionsMenu.deleteErrorToast': "Couldn't delete the {asset}. Try again",
  'productAnalytics.blockOptionsMenu.deletedToast': '{asset} deleted',
  'productAnalytics.blockOptionsMenu.duplicate': 'Duplicate {asset}',
  'productAnalytics.blockOptionsMenu.duplicateErrorToast': "Couldn't duplicate the {asset}. Try again.",
  'productAnalytics.blockOptionsMenu.duplicatedToast': '{asset} duplicated successfully',
  'productAnalytics.blockOptionsMenu.editChart': 'Edit chart',
  'productAnalytics.blockOptionsMenu.linkCopiedToast': 'Link copied to clipboard',
  'productAnalytics.blockOptionsMenu.renameChart': 'Rename chart',
  'productAnalytics.chartBreakdown.addBreakdownLabel': 'Add breakdown',
  'productAnalytics.chartBreakdown.breakdown': 'Breakdown',
  'productAnalytics.chartBreakdown.optionsLabel': 'Breakdown options',
  'productAnalytics.chartBreakdown.removeBreakdownLabel': 'Remove breakdown',
  'productAnalytics.chartBreakdown.searchPlaceholder': 'Search…',
  'productAnalytics.chartBreakdown.unsupportedPropertyWarning':
    'Breakdown not supported for this property due to very high number of values',
  'productAnalytics.chartBuilder.addEventLabel': 'Add event',
  'productAnalytics.chartBuilder.archivedCohortBody':
    'This cohort may need to be updated because one or more cohorts referenced here have been archived. Check the referenced cohorts.',
  'productAnalytics.chartBuilder.archivedCohortHeading': 'Contains archived cohort(s)',
  'productAnalytics.chartBuilder.dragHandleLabel': 'Drag to move',
  'productAnalytics.chartBuilder.flowsUnavailableError': 'Flows are not available in this environment.',
  'productAnalytics.chartBuilder.invalidIterationFiltersWarning':
    'The chart is showing results for the filters that are not valid in the current iteration',
  'productAnalytics.chartBuilder.lastCalculated': 'Data last calculated {time}',
  'productAnalytics.chartBuilder.refreshData': 'Refresh Data',
  'productAnalytics.chartBuilder.refreshDataLabel': 'Refresh data',
  'productAnalytics.chartConfigPanel.archivedCohortWarning':
    'This cohort may need to be updated because one or more cohorts referenced here have been archived. Check\n              the referenced cohorts.',
  'productAnalytics.chartConfigPanel.learnMore': 'Learn more',
  'productAnalytics.chartConfigPanel.tabsLabel': 'charts tabs',
  'productAnalytics.chartDataTable.downloadCsvLabel': 'Download CSV',
  'productAnalytics.chartDataTable.resetSelection': 'Reset selection',
  'productAnalytics.chartDataTable.searchLabel': 'Search table rows',
  'productAnalytics.chartDataTable.searchPlaceholder': 'Search…',
  'productAnalytics.chartDataTable.selectCellLabel': 'Select {value}',
  'productAnalytics.chartDataTable.sortByLabel': 'Sort by {column}',
  'productAnalytics.chartDataTable.tableLabel': 'Chart data table',
  'productAnalytics.chartDataTable.toggleSelectionLabel': 'Toggle selection for {row}',
  'productAnalytics.chartDateFilter.apply': 'Apply',
  'productAnalytics.chartDateFilter.closeLabel': 'Close date filter',
  'productAnalytics.chartDateFilter.dataAvailableRange': 'Event data is available from - {startDate} - {endDate}',
  'productAnalytics.chartDateFilter.selectADate': 'Select a date range',
  'productAnalytics.chartEditor.chartSaved': 'Chart saved',
  'productAnalytics.chartEditor.loadError': 'Something went wrong, if this keeps happening contact support.',
  'productAnalytics.chartEditorHeader.goBack': 'Go back',
  'productAnalytics.chartEditorHeader.label': 'More save options',
  'productAnalytics.chartEditorHeader.save': 'Save',
  'productAnalytics.chartEditorHeader.saveAsNew': 'Save as new',
  'productAnalytics.chartEditorHeader.saveChangesTooltip': 'Save changes',
  'productAnalytics.chartEditorHeader.saveChart': 'Save chart',
  'productAnalytics.chartFilter.addFilterLabel': 'Add Filter',
  'productAnalytics.chartFilter.addFilterTooltip': 'Add filter',
  'productAnalytics.chartFilter.and': 'And',
  'productAnalytics.chartFilter.applyFilter': 'Apply filter',
  'productAnalytics.chartFilter.booleanHelpFalse': 'False: If the property does not apply or is not present.',
  'productAnalytics.chartFilter.booleanHelpIntro': 'Select the appropriate option for this event property:',
  'productAnalytics.chartFilter.booleanHelpTrue': 'True: If the property applies or is present.',
  'productAnalytics.chartFilter.clearSelection': 'Clear selection',
  'productAnalytics.chartFilter.cohortFilterTypeLabel': 'Cohort filter type',
  'productAnalytics.chartFilter.dashboardLockedTooltip':
    "This filter is set at the dashboard level and can't be changed here.",
  'productAnalytics.chartFilter.dateHelpAfter': "'After': Includes the selected date and all dates afterwards.",
  'productAnalytics.chartFilter.dateHelpBefore': "'Before': Includes the selected date and all dates before.",
  'productAnalytics.chartFilter.dateHelpBetween': 'Select is between or is not between operator to create a date range',
  'productAnalytics.chartFilter.dateHelpIntro': 'To filter by date, select the appropriate option:',
  'productAnalytics.chartFilter.dimensionOperatorPlaceholder': 'Select Operator',
  'productAnalytics.chartFilter.dimensionPlaceholder': 'Select a dimension',
  'productAnalytics.chartFilter.disabledValuePlaceholder': 'Select attribute or property to enable',
  'productAnalytics.chartFilter.disabledValueTooltip': 'Select attribute or property to enable this option',
  'productAnalytics.chartFilter.filter': 'Filter',
  'productAnalytics.chartFilter.filterOptionsLabel': 'Filter options',
  'productAnalytics.chartFilter.invalidVariantWarning': 'Select a valid experiment variant',
  'productAnalytics.chartFilter.operatorPlaceholder': 'Select an operator',
  'productAnalytics.chartFilter.removeFilterLabel': 'remove filter',
  'productAnalytics.chartFilter.removeFilterTooltip': 'Click to remove filter',
  'productAnalytics.chartFilter.searchPlaceholder': 'Search…',
  'productAnalytics.chartFilter.selectFilterLabel': 'Select filter',
  'productAnalytics.chartFilter.selectValuePlaceholder': 'Select…',
  'productAnalytics.chartFilter.typeToSearchHint': 'Type to search and add values',
  'productAnalytics.chartFilter.userAttributePlaceholder': 'Select a user attribute',
  'productAnalytics.chartFilter.usersIn': 'Users in',
  'productAnalytics.chartFilter.usersNotIn': 'Users not in',
  'productAnalytics.chartFormula.addAnother': 'Add another',
  'productAnalytics.chartFormula.addFirst': 'Add formula',
  'productAnalytics.chartFormula.addFormulaLabel': 'Add formula',
  'productAnalytics.chartFormula.appliedFormula': 'Applied formula',
  'productAnalytics.chartFormula.editFormulaLabel': 'Edit Formula',
  'productAnalytics.chartFormula.editorPlaceholder': 'Use @ to add events and metrics in the formula editor.',
  'productAnalytics.chartFormula.formula': 'Formula',
  'productAnalytics.chartFormula.formulaNameLabel': 'Formula name',
  'productAnalytics.chartFormula.formulaNamePlaceholder': 'Name your formula',
  'productAnalytics.chartFormula.measuring': 'Measuring',
  'productAnalytics.chartFormula.removeAll': 'Remove all',
  'productAnalytics.chartFormula.removeFormulaLabel': 'Remove formula',
  'productAnalytics.chartFormula.save': 'Save',
  'productAnalytics.chartFormula.selectEntityLabel': 'Select an entity',
  'productAnalytics.chartFormula.symbolPlaceholder': 'Select a symbol',
  'productAnalytics.chartLoader.proTipLabel': 'Pro-tip:',
  'productAnalytics.chartModal.close': 'Close',
  'productAnalytics.chartModal.closeLabel': 'Close modal',
  'productAnalytics.chartModal.copyLink': 'Copy link',
  'productAnalytics.chartModal.copyLinkLabel': 'Copy chart link',
  'productAnalytics.chartModal.linkCopiedToast': 'Chart link copied to clipboard',
  'productAnalytics.chartModal.moreSaveOptionsLabel': 'More save options',
  'productAnalytics.chartModal.openDetailedView': 'Open detailed view',
  'productAnalytics.chartModal.save': 'Save',
  'productAnalytics.chartModal.saveAsNew': 'Save as new',
  'productAnalytics.chartModal.saveChart': 'Save chart',
  'productAnalytics.chartModal.savedToast': 'Chart saved',
  'productAnalytics.chartModal.untitledChart': 'Untitled Chart',
  'productAnalytics.chartSqlViewer.aboutThisChart': 'About this chart',
  'productAnalytics.chartSqlViewer.closeLabel': 'Close Sidebar',
  'productAnalytics.chartSqlViewer.copySqlLabel': 'Copy SQL Query',
  'productAnalytics.chartSqlViewer.description':
    '<kind></kind> chart showing <events></events>{hasBreakdown, select, true {, grouped by <breakdown></breakdown>} other { }}{hasFilter, select, true {, filtered for <filter></filter>} other { }} {hasGrouping, select, true {and} other {over}} a time range of <range></range>.',
  'productAnalytics.chartSqlViewer.dialogLabel': 'Chart SQL Query Viewer Sidebar',
  'productAnalytics.chartSqlViewer.editorLabel': 'Chart SQL Query Viewer',
  'productAnalytics.chartSqlViewer.sqlQuery': 'SQL query',
  'productAnalytics.chartTitleBreadcrumb.titleLabel': 'Chart title',
  'productAnalytics.chartsNewPage.title': 'Create chart',
  'productAnalytics.cohortActions.archiveCohort': 'Archive cohort',
  'productAnalytics.cohortActions.createChartWith': 'Create chart with cohort',
  'productAnalytics.cohortActions.downloadCohortAs': 'Download cohort as .CSV',
  'productAnalytics.cohortActions.duplicateCohort': 'Duplicate cohort',
  'productAnalytics.cohortActions.editCohort': 'Edit cohort',
  'productAnalytics.cohortActions.linkCopied': 'Link copied to clipboard',
  'productAnalytics.cohortActions.restoreCohort': 'Restore cohort',
  'productAnalytics.cohortActions.shareLinkTo': 'Share link to cohort',
  'productAnalytics.cohortDefinition.actionDid': 'did',
  'productAnalytics.cohortDefinition.actionDidNot': 'did not',
  'productAnalytics.cohortDefinition.actionHaveDone': 'have done',
  'productAnalytics.cohortDefinition.actionHaveNotDone': 'have not done',
  'productAnalytics.cohortDefinition.actionIsPartOfCohort': 'is part of cohort',
  'productAnalytics.cohortDefinition.actionWhere': 'Where',
  'productAnalytics.cohortDefinition.actionWho': 'Who',
  'productAnalytics.cohortDefinition.addDateFilter': 'Click to add date filter',
  'productAnalytics.cohortDefinition.addFilterGroup': 'Add filter group',
  'productAnalytics.cohortDefinition.addFilterToUsers': 'Add filter to users',
  'productAnalytics.cohortDefinition.addFilterToUsersTooltip': 'Add more conditions to filter this user group',
  'productAnalytics.cohortDefinition.addFilters': 'Add filters',
  'productAnalytics.cohortDefinition.addPropertyTo': 'Add property to filter the event',
  'productAnalytics.cohortDefinition.addUserGroup': 'Add user group',
  'productAnalytics.cohortDefinition.addUserGroupTooltip': 'Add a user group with conditions to this cohort',
  'productAnalytics.cohortDefinition.apply': 'Apply',
  'productAnalytics.cohortDefinition.closeLabel': 'Close',
  'productAnalytics.cohortDefinition.createdAt': 'Created at',
  'productAnalytics.cohortDefinition.dateRangeLimit':
    'The selected date range exceeds the {limit}-{unit} limit. Specify a range of up to {limit} {unit}s.',
  'productAnalytics.cohortDefinition.description': 'Description',
  'productAnalytics.cohortDefinition.description2': 'This cohort is archived, visit the cohort page to restore it.',
  'productAnalytics.cohortDefinition.errorEmptyFilters': 'Cannot create cohort with empty filters',
  'productAnalytics.cohortDefinition.errorInvalidEventFilters':
    'Filter group {group} has a missing or invalid definition. Update all fields for the event filter(s).',
  'productAnalytics.cohortDefinition.errorMissingCount':
    'Filter group {group} has a missing definition. Update value for count.',
  'productAnalytics.cohortDefinition.errorMissingDimension':
    'Filter group {group} has a missing definition. Select a user property, operator and value.',
  'productAnalytics.cohortDefinition.errorMissingEvent':
    'Filter group {group} has a missing definition. Select an event',
  'productAnalytics.cohortDefinition.errorNoFilters': 'Cannot create cohort without any filters',
  'productAnalytics.cohortDefinition.eventFilterAnd': 'And',
  'productAnalytics.cohortDefinition.eventProperty': 'Event Property',
  'productAnalytics.cohortDefinition.filter': 'Filter',
  'productAnalytics.cohortDefinition.goToCohort': 'Go to Cohort',
  'productAnalytics.cohortDefinition.goToCohortPage': "Go to cohort's page",
  'productAnalytics.cohortDefinition.label': 'Remove filter',
  'productAnalytics.cohortDefinition.label2': 'Event properties',
  'productAnalytics.cohortDefinition.label3': 'User attributes',
  'productAnalytics.cohortDefinition.label4': 'Delete',
  'productAnalytics.cohortDefinition.label5': 'Remove filter group',
  'productAnalytics.cohortDefinition.loadError': 'Something went wrong while loading cohorts. Try refreshing the page',
  'productAnalytics.cohortDefinition.message': 'Contains archived cohort(s)',
  'productAnalytics.cohortDefinition.message2': 'Archived Cohort',
  'productAnalytics.cohortDefinition.nonEditable':
    'Cohort definition created from {type, select, from_funnel {funnels} from_retention {retention} other {CSV upload}} cannot be edited',
  'productAnalytics.cohortDefinition.operatorAnd': 'AND',
  'productAnalytics.cohortDefinition.operatorOnOrAfter': 'on or after',
  'productAnalytics.cohortDefinition.operatorOnOrBefore': 'on or before',
  'productAnalytics.cohortDefinition.operatorOr': 'OR',
  'productAnalytics.cohortDefinition.performPlaceholder': 'perform',
  'productAnalytics.cohortDefinition.placeholder': 'Search',
  'productAnalytics.cohortDefinition.placeholder2': 'Select a property',
  'productAnalytics.cohortDefinition.placeholder3': 'Select an operator',
  'productAnalytics.cohortDefinition.placeholder4': 'event name',
  'productAnalytics.cohortDefinition.placeholder5': 'aggregation type',
  'productAnalytics.cohortDefinition.placeholder6': 'aggregation operator',
  'productAnalytics.cohortDefinition.placeholder7': 'Enter a value',
  'productAnalytics.cohortDefinition.placeholder8': 'Too many values to show. Enter a number.',
  'productAnalytics.cohortDefinition.placeholder9': 'Type a number and select the number to add',
  'productAnalytics.cohortDefinition.remove': 'Remove',
  'productAnalytics.cohortDefinition.selectOperator': 'Select an operator',
  'productAnalytics.cohortDefinition.selectOperatorTooltip': 'Select an operator from the dropdown options',
  'productAnalytics.cohortDefinition.selectOperatorTooltip2': 'Select an operator form the dropdown options',
  'productAnalytics.cohortDefinition.switchOperatorTooltip': 'Click to switch between and/or condition',
  'productAnalytics.cohortDefinition.tabCohorts': 'Cohorts',
  'productAnalytics.cohortDefinition.tabEvents': 'Events',
  'productAnalytics.cohortDefinition.tabUserAttributes': 'User attributes',
  'productAnalytics.cohortDefinition.title': 'Filter by',
  'productAnalytics.cohortDefinition.title2': 'Click to remove filter',
  'productAnalytics.cohortDefinition.title3': 'Add event filter',
  'productAnalytics.cohortDefinition.title4': 'Click to remove filter group',
  'productAnalytics.cohortDefinition.title5': 'This cohort is archived.',
  'productAnalytics.cohortDefinition.title6':
    'This cohort may need to be updated because one or more cohorts referenced here have been archived. Check\n              the referenced cohorts.',
  'productAnalytics.cohortDefinition.title7': 'Click cohort to see the definition',
  'productAnalytics.cohortDefinition.typeToAddAndSelect': 'Type to add and select',
  'productAnalytics.cohortDefinition.users': 'Users',
  'productAnalytics.cohortDetails.archivedCohort': 'Archived cohort',
  'productAnalytics.cohortDetails.containsArchivedCohort': 'Contains archived cohort(s)',
  'productAnalytics.cohortDetails.definition': 'Definition',
  'productAnalytics.cohortDetails.definitionIntro': 'Users {isReference, select, true {who…} other {}}',
  'productAnalytics.cohortDetails.general': 'General',
  'productAnalytics.cohortDetails.getUserCount': 'Get user count',
  'productAnalytics.cohortDetails.label': 'Name',
  'productAnalytics.cohortDetails.label10': 'User count',
  'productAnalytics.cohortDetails.label11': 'Sync user count',
  'productAnalytics.cohortDetails.label2': 'Description',
  'productAnalytics.cohortDetails.label3': 'Key',
  'productAnalytics.cohortDetails.label4': 'Copy key',
  'productAnalytics.cohortDetails.label5': 'Created by',
  'productAnalytics.cohortDetails.label6': 'Created on',
  'productAnalytics.cohortDetails.label7': 'Updated on',
  'productAnalytics.cohortDetails.label8': 'Last uploaded',
  'productAnalytics.cohortDetails.label9': 'Version',
  'productAnalytics.cohortDetails.loadError': 'Something went wrong while loading cohorts. Try refreshing the page',
  'productAnalytics.cohortDetails.moreActions': 'More actions',
  'productAnalytics.cohortDetails.restore': 'Restore',
  'productAnalytics.cohortDetails.retrieved': 'Retrieved {time}',
  'productAnalytics.cohortDetails.thisCohortIs': 'This cohort is archived, restore to use it for your analysis.',
  'productAnalytics.cohortDetails.thisCohortMay':
    'This cohort may need to be updated because one or more cohorts referenced here have been archived. Check the referenced cohorts.',
  'productAnalytics.cohortDetails.tooltip': 'Copy key to clipboard',
  'productAnalytics.cohortDetails.userCount': '{count} users',
  'productAnalytics.cohortDetailsModal.close': 'Close',
  'productAnalytics.cohortDetailsModal.definition': 'Definition',
  'productAnalytics.cohortDetailsModal.general': 'General',
  'productAnalytics.cohortDetailsModal.label': 'Description',
  'productAnalytics.cohortDetailsModal.label2': 'Created on',
  'productAnalytics.cohortDetailsModal.viewCohort': 'View cohort',
  'productAnalytics.cohortForm.cancel': 'Cancel',
  'productAnalytics.cohortForm.createError': 'Something went wrong while creating the cohort. Try again.',
  'productAnalytics.cohortForm.createStaticError': 'Something went wrong while creating the cohort, try again',
  'productAnalytics.cohortForm.created': 'Cohort created successfully',
  'productAnalytics.cohortForm.csvFile': 'CSV file',
  'productAnalytics.cohortForm.csvRequired': 'CSV file is required',
  'productAnalytics.cohortForm.definition': 'Definition',
  'productAnalytics.cohortForm.definitionRequired': 'Definition is required',
  'productAnalytics.cohortForm.description': 'Description',
  'productAnalytics.cohortForm.description2':
    'Cohorts should be uploaded as CSV\n        containing one ID per line not more than 100k rows and 20MB.',
  'productAnalytics.cohortForm.descriptionRequired': 'Description is required',
  'productAnalytics.cohortForm.disabledHeading': '{title} is disabled',
  'productAnalytics.cohortForm.duplicateName':
    'A cohort with the same name already exists. Provide a different cohort name',
  'productAnalytics.cohortForm.general': 'General',
  'productAnalytics.cohortForm.itLooksLike':
    'It looks like you are trying to create a cohort similar to one that already exists:',
  'productAnalytics.cohortForm.key': 'Key',
  'productAnalytics.cohortForm.keyRequired': 'Key is required',
  'productAnalytics.cohortForm.label': 'Cohort name',
  'productAnalytics.cohortForm.label2': 'Cohort key',
  'productAnalytics.cohortForm.label3': 'Cohort description',
  'productAnalytics.cohortForm.label4': 'Validate cohort form',
  'productAnalytics.cohortForm.leaveCreateBody': 'The cohort will not be saved. Are you sure you want to leave?',
  'productAnalytics.cohortForm.leaveEditBody': 'The changes will not be saved. Are you sure you want to leave?',
  'productAnalytics.cohortForm.loadError': 'Something went wrong while loading cohorts. Try refreshing the page',
  'productAnalytics.cohortForm.missingFields': 'Fill in the following required fields: {fields}',
  'productAnalytics.cohortForm.name': 'Name',
  'productAnalytics.cohortForm.nameRequired': 'Name is required',
  'productAnalytics.cohortForm.newCohortTitle': 'New cohort',
  'productAnalytics.cohortForm.save': 'Save',
  'productAnalytics.cohortForm.similarCohortsTitle': 'Similar cohorts exist',
  'productAnalytics.cohortForm.title': 'Leave this page?',
  'productAnalytics.cohortForm.updateError': 'Something went wrong while updating the cohort. Try again.',
  'productAnalytics.cohortForm.updated': 'Cohort updated successfully',
  'productAnalytics.cohortFromChart.selectDateRange':
    'Select a date range on the date filter to create a cohort from this chart',
  'productAnalytics.cohortsEditPage.breadcrumb': 'Edit',
  'productAnalytics.cohortsEditPage.title': 'Edit cohort',
  'productAnalytics.cohortsList.activeCohorts': 'Active cohorts',
  'productAnalytics.cohortsList.archivedCohorts': 'Archived cohorts',
  'productAnalytics.cohortsList.cohortName': 'Cohort name',
  'productAnalytics.cohortsList.createdBy': 'Created by',
  'productAnalytics.cohortsList.label': 'Search for a cohort by name or description',
  'productAnalytics.cohortsList.label2': 'Cohort data warning',
  'productAnalytics.cohortsList.lastModified': 'Last modified',
  'productAnalytics.cohortsList.loadError': 'Something went wrong while loading cohorts. Try refreshing the page',
  'productAnalytics.cohortsList.newCohort': 'New cohort',
  'productAnalytics.cohortsList.paginationSummary': '<range>{from}–{to}</range> of <count>{total}</count> cohorts',
  'productAnalytics.cohortsList.placeholder': 'Find a cohort by name or description',
  'productAnalytics.cohortsList.thisCohortMay':
    'This cohort may need to be updated because one or more cohorts referenced here have been archived. Check the referenced cohorts.',
  'productAnalytics.cohortsList.viewFilter': 'View: {filter}',
  'productAnalytics.cohortsNewPage.title': 'New cohort',
  'productAnalytics.cohortsPage.title': 'Cohorts',
  'productAnalytics.cohorts_Page.breadcrumb': 'Cohorts',
  'productAnalytics.comparePeriod.apply': 'Apply',
  'productAnalytics.comparePeriod.body': 'Compare with previous periods or an arbitrary time range.',
  'productAnalytics.comparePeriod.clearAll': 'Clear all',
  'productAnalytics.comparePeriod.closeLabel': 'Close',
  'productAnalytics.comparePeriod.compareButton': 'Compare:{label}',
  'productAnalytics.comparePeriod.dataAvailableRange': 'Event data is available from - {startDate} - {endDate}',
  'productAnalytics.comparePeriod.heading': 'Compare (Only available on steps & table view)',
  'productAnalytics.comparePeriod.or': 'Or',
  'productAnalytics.comparePeriod.periodPlaceholder': 'Choose a period to compare',
  'productAnalytics.comparePeriod.removeTooltip': 'Remove compare period from visualization',
  'productAnalytics.compositeEventEditor.accessDeniedHeading':
    '{mode, select, create {Creating composite events} other {Editing composite events}} is disabled',
  'productAnalytics.compositeEventEditor.cancel': 'Cancel',
  'productAnalytics.compositeEventEditor.checkingKeyLabel': 'Checking key availability',
  'productAnalytics.compositeEventEditor.checkingKeyMessage': 'Checking key availability…',
  'productAnalytics.compositeEventEditor.copyKeyLabel': 'Copy event key',
  'productAnalytics.compositeEventEditor.copyKeyTooltip': 'Copy event key to clipboard',
  'productAnalytics.compositeEventEditor.createDialogLabel': 'Create Composite Event',
  'productAnalytics.compositeEventEditor.createErrorToast':
    'Something went wrong while creating the composite event, try again',
  'productAnalytics.compositeEventEditor.createHeading': 'New composite event',
  'productAnalytics.compositeEventEditor.createSuccessToast': 'Composite event created successfully',
  'productAnalytics.compositeEventEditor.definitionLabel': 'Events definition',
  'productAnalytics.compositeEventEditor.definitionRequiredToast':
    'Composite event definition should include at least one event',
  'productAnalytics.compositeEventEditor.descriptionLabel': 'Event description',
  'productAnalytics.compositeEventEditor.descriptionRequiredToast': 'Provide a description',
  'productAnalytics.compositeEventEditor.duplicateKeyToast':
    'An event with the same key already exists. Provide a different event key',
  'productAnalytics.compositeEventEditor.duplicateNameToast':
    'An event with the same name already exists. Provide a different event name',
  'productAnalytics.compositeEventEditor.editDialogLabel': 'Edit Composite Event',
  'productAnalytics.compositeEventEditor.invalidDefinitionToast':
    'Event {step} has a missing or invalid definition. Update the fields for the event filter(s).',
  'productAnalytics.compositeEventEditor.keyLabel': 'Event key',
  'productAnalytics.compositeEventEditor.nameLabel': 'Event name',
  'productAnalytics.compositeEventEditor.nameRequiredToast': 'Provide a name',
  'productAnalytics.compositeEventEditor.save': 'Save',
  'productAnalytics.compositeEventEditor.update': 'Update',
  'productAnalytics.compositeEventEditor.updateErrorToast':
    'Something went wrong while updating the composite event. Try again.',
  'productAnalytics.compositeEventEditor.updateSuccessToast': 'Composite event updated successfully',
  'productAnalytics.connectionAlert.defaultBody':
    'Check to see that the fields were filled out correctly and try again.',
  'productAnalytics.connectionAlert.goToIntegrations': 'Go to integrations',
  'productAnalytics.connectionAlert.heading': 'Connection unsuccessful',
  'productAnalytics.connectionSuccessDialog.body':
    'Warehouse is now connected and you will soon be able to see data directly from your warehouse in LaunchDarkly.',
  'productAnalytics.connectionSuccessDialog.confirm': 'Go to environment',
  'productAnalytics.connectionSuccessDialog.heading': 'Connection successful',
  'productAnalytics.copyToDashboardMenu.assetChart': 'chart',
  'productAnalytics.copyToDashboardMenu.assetDashboard': 'dashboard',
  'productAnalytics.copyToDashboardMenu.assetTextContent': 'text content',
  'productAnalytics.copyToDashboardMenu.copiedToast': '{title} copied successfully',
  'productAnalytics.copyToDashboardMenu.copyErrorToast': "Couldn't copy the {asset}. Try again",
  'productAnalytics.copyToDashboardMenu.copyingToast': 'Copying {asset}…',
  'productAnalytics.copyToDashboardMenu.loadingDashboards': 'Loading dashboards…',
  'productAnalytics.copyToDashboardMenu.noDashboards': 'No dashboards found',
  'productAnalytics.copyToDashboardMenu.searchPlaceholder': 'Search dashboard',
  'productAnalytics.copyToDashboardMenu.selectLabel': 'Select dashboard',
  'productAnalytics.createChartButton.addTextBlock': 'Add text block',
  'productAnalytics.createChartButton.createChart': 'Create chart',
  'productAnalytics.createChartButton.loadingLabel': 'Loading create chart button',
  'productAnalytics.createChartButton.moreOptionsLabel': 'More options',
  'productAnalytics.createChartButton.readOnlyTooltip': 'This dashboard is read-only',
  'productAnalytics.createChartButton.textBlock': 'Text block',
  'productAnalytics.createChartButton.unavailableTooltip': 'This action is currently unavailable',
  'productAnalytics.createCohortModal.buildACohort':
    'Build a cohort using rules target groups of users individually or by attributes',
  'productAnalytics.createCohortModal.createNewCohort': 'Create new cohort',
  'productAnalytics.createCohortModal.ruleBasedCohort': 'Rule-based cohort',
  'productAnalytics.createCohortModal.uploadACsv': 'Upload a CSV',
  'productAnalytics.createCohortModal.uploadACsv2': 'Upload a CSV file with a list of user IDs to create a cohort.',
  'productAnalytics.dashboard.blockCreated': '{blockType} created successfully',
  'productAnalytics.dashboard.blockTypeChart': 'Chart',
  'productAnalytics.dashboard.blockTypeContent': 'Content',
  'productAnalytics.dashboard.filterBy': 'Filter by:',
  'productAnalytics.dashboard.filters': 'Filters',
  'productAnalytics.dashboardBlock.delete': 'Delete',
  'productAnalytics.dashboardBlock.dragToMove': 'Drag to move',
  'productAnalytics.dashboardBlock.unsupported': "This content isn't supported.",
  'productAnalytics.dashboardCohortFilter.addFilter': 'Add cohort filter',
  'productAnalytics.dashboardCohortFilter.and': 'And',
  'productAnalytics.dashboardCohortFilter.apply': 'Apply',
  'productAnalytics.dashboardCohortFilter.chooseCohortS':
    'Choose cohort(s) to narrow your results to only a subset of users. Not applied to flows.',
  'productAnalytics.dashboardCohortFilter.filterApplied': 'Cohort filter applied',
  'productAnalytics.dashboardCohortFilter.loadError':
    'Something went wrong while loading cohorts. Try refreshing the page',
  'productAnalytics.dashboardCohortFilter.reset': 'Reset',
  'productAnalytics.dashboardCohortFilter.usersIn': 'Users in',
  'productAnalytics.dashboardCohortFilter.usersNotIn': 'Users not in',
  'productAnalytics.dashboardCreator.cancel': 'Cancel',
  'productAnalytics.dashboardCreator.dashboardName': 'Dashboard name',
  'productAnalytics.dashboardCreator.defaultConfirm': 'Create',
  'productAnalytics.dashboardCreator.defaultTitle': 'Create dashboard',
  'productAnalytics.dashboardCreator.editKey': 'Edit key',
  'productAnalytics.dashboardCreator.key': 'Key',
  'productAnalytics.dashboardCreator.keyFieldLabel': 'Dashboard key',
  'productAnalytics.dashboardCreator.keyRequired': 'Key is required',
  'productAnalytics.dashboardCreator.loading': 'Loading…',
  'productAnalytics.dashboardCreator.nameHint': 'Choose a name that helps you and your team recognize this dashboard.',
  'productAnalytics.dashboardCreator.namePlaceholder': 'For example: New onboarding flow performance',
  'productAnalytics.dashboardCreator.nameRequired': 'Name is required',
  'productAnalytics.dashboardCreator.nameTooLong': 'Name must be less than 256 characters',
  'productAnalytics.dashboardDateFilter.addFilter': 'Add date range filter',
  'productAnalytics.dashboardDateFilter.apply': 'Apply',
  'productAnalytics.dashboardDateFilter.availableRange': 'Event data is available from - {start} - {end}',
  'productAnalytics.dashboardDateFilter.clear': 'Clear',
  'productAnalytics.dashboardDateFilter.invalidRange': 'Date filter is empty or invalid. Update the date filter.',
  'productAnalytics.dashboardEmptyState.addContent': 'Add content',
  'productAnalytics.dashboardEmptyState.noContentYet': 'No content yet',
  'productAnalytics.dashboardEmptyState.startByAdding':
    'Start by adding a chart to explore product usage and customer behaviour.',
  'productAnalytics.dashboardHeader.createdBy': 'Created by',
  'productAnalytics.dashboardHeader.label': 'Dashboard name',
  'productAnalytics.dashboardHeader.lastEditedBy': 'Last edited by',
  'productAnalytics.dashboardHeader.untitled': 'Untitled dashboard',
  'productAnalytics.dashboardMoreOptions.copyKey': 'Copy key',
  'productAnalytics.dashboardMoreOptions.copyLink': 'Copy link',
  'productAnalytics.dashboardMoreOptions.deleteBody':
    'This action cannot be undone. This will delete the dashboard and all of its content.',
  'productAnalytics.dashboardMoreOptions.deleteConfirm': 'Delete dashboard',
  'productAnalytics.dashboardMoreOptions.deleteDashboard': 'Delete dashboard',
  'productAnalytics.dashboardMoreOptions.deleteHeading': 'Are you sure to delete this dashboard?',
  'productAnalytics.dashboardMoreOptions.duplicateDashboard': 'Duplicate dashboard',
  'productAnalytics.dashboardMoreOptions.editName': 'Edit name',
  'productAnalytics.dashboardMoreOptions.linkCopied': 'Link copied to clipboard',
  'productAnalytics.dashboardMoreOptions.refreshData': 'Refresh data',
  'productAnalytics.dashboardNotFound.backToDashboards': 'Back to dashboards',
  'productAnalytics.dashboardNotFound.dashboardNotFound': 'Dashboard not found',
  'productAnalytics.dashboardNotFound.theDashboardYou': 'The dashboard you are looking for does not exist.',
  'productAnalytics.dashboardPicker.actionTooltip': '{action} to {dashboard}',
  'productAnalytics.dashboardZeroScreen.addTextBlock': 'Add text block',
  'productAnalytics.dashboardZeroScreen.addYourFirst':
    'Add your first chart or text block to start visualising your event data — no setup needed.',
  'productAnalytics.dashboardZeroScreen.createChart': 'Create chart',
  'productAnalytics.dashboardZeroScreen.eventsInstrumented': 'Your <link>events</link> are now instrumented.',
  'productAnalytics.dashboardZeroScreen.heading': 'This is your first <highlight>dashboard</highlight>',
  'productAnalytics.dashboardZeroScreen.label': 'Product analytics dashboard preview',
  'productAnalytics.dashboardsList.autoCreatedTitle': 'Untitled Dashboard',
  'productAnalytics.dashboardsList.createdBy': 'Created by',
  'productAnalytics.dashboardsList.dashboardName': 'Dashboard name',
  'productAnalytics.dashboardsList.deleteBody':
    'This action cannot be undone. This will delete the dashboard and all of its content.',
  'productAnalytics.dashboardsList.deleteConfirm': 'Delete dashboard',
  'productAnalytics.dashboardsList.deleteDashboard': 'Delete dashboard',
  'productAnalytics.dashboardsList.deleteHeading': 'Are you sure you want to delete this dashboard?',
  'productAnalytics.dashboardsList.duplicateDashboard': 'Duplicate dashboard',
  'productAnalytics.dashboardsList.duplicateDisabledTooltip': 'Cannot duplicate experiment-specific dashboards',
  'productAnalytics.dashboardsList.label': 'Search for a dashboard by name',
  'productAnalytics.dashboardsList.lastModified': 'Last modified',
  'productAnalytics.dashboardsList.linkedToExperiment': 'Linked to experiment',
  'productAnalytics.dashboardsList.newDashboard': 'New dashboard',
  'productAnalytics.dashboardsList.paginationSummary':
    '<range>{from}–{to}</range> of <count>{total}</count> dashboards',
  'productAnalytics.dashboardsList.placeholder': 'Find a dashboard by name',
  'productAnalytics.dashboardsList.untitled': 'Untitled',
  'productAnalytics.dashboardsNewPage.title': 'Create Dashboard',
  'productAnalytics.dashboardsPage.title': 'Dashboards',
  'productAnalytics.dashboards_Page.breadcrumb': 'Dashboards',
  'productAnalytics.dataExportAlert.body':
    'In order to use the LaunchDarkly SDK to set up events, you will first need to return to LaunchDarkly to set up {warehouse} Data Export.',
  'productAnalytics.dataExportAlert.heading': 'Set up Data Export',
  'productAnalytics.dataExportAlert.setUpDataExport': 'Set up data export',
  'productAnalytics.dataExportAlert.unavailableBody':
    "Export to this warehouse isn't supported at the moment. To start using Product Analytics, choose the CDP/Custom SDK option. We'll notify you as soon as this becomes available.",
  'productAnalytics.dataExportAlert.unavailableHeading': 'Data export not available yet',
  'productAnalytics.databricksForm.catalogExistsHint':
    'Important: The catalog must exist in your Databricks warehouse before proceeding.',
  'productAnalytics.databricksForm.catalogHint':
    "Enter your Databricks catalog name. If left empty, we'll use: {catalog}",
  'productAnalytics.databricksForm.catalogLabel': 'Catalog name',
  'productAnalytics.databricksForm.catalogPlaceholder': 'Enter your catalog name',
  'productAnalytics.databricksForm.clientIdHint':
    'Enter the Application (client) ID of your Databricks service principal. This is used for authentication.',
  'productAnalytics.databricksForm.clientIdLabel': 'Client ID',
  'productAnalytics.databricksForm.clientIdPlaceholder': 'Enter your Client ID',
  'productAnalytics.databricksForm.clientSecretHint':
    'Enter the secret key associated with your Databricks service principal. This is used for authentication.',
  'productAnalytics.databricksForm.clientSecretLabel': 'Client Secret',
  'productAnalytics.databricksForm.clientSecretPlaceholder': 'Enter your Client Secret',
  'productAnalytics.databricksForm.clusterPathHint':
    'Enter the cluster ID. You can find this under the "Compute" section in your Databricks workspace.',
  'productAnalytics.databricksForm.clusterPathLabel': 'Cluster Path',
  'productAnalytics.databricksForm.clusterPathPlaceholder': 'Enter your Cluster Path',
  'productAnalytics.databricksForm.heading': 'Configure {warehouse} warehouse',
  'productAnalytics.databricksForm.hostHint': 'Enter the URL of your Databricks workspace, e.g., {example}',
  'productAnalytics.databricksForm.hostLabel': 'Host',
  'productAnalytics.databricksForm.hostPlaceholder': 'Enter your Host',
  'productAnalytics.databricksForm.nameLabel': 'Name',
  'productAnalytics.databricksForm.namePlaceholder': 'Enter a descriptive name for this integration',
  'productAnalytics.databricksForm.save': 'Save configuration',
  'productAnalytics.databricksForm.saving': 'Saving…',
  'productAnalytics.databricksForm.schemaExistsHint':
    'Important: The schema must exist in your Databricks warehouse before proceeding.',
  'productAnalytics.databricksForm.schemaHint': "Enter your Databricks schema name. If left empty, we'll use: {schema}",
  'productAnalytics.databricksForm.schemaLabel': 'Schema name',
  'productAnalytics.databricksForm.schemaPlaceholder': 'Enter your schema name',
  'productAnalytics.databricksForm.subheading':
    'Enter credentials to connect to your {warehouse} warehouse. Before filling out this form, make sure you complete all the steps mentioned in the <link>documentation</link>.',
  'productAnalytics.databricksForm.terms': 'I have read and agree to the <link>Integration Terms and Conditions</link>',
  'productAnalytics.dateFilterPicker.addOffset': 'Add Offset',
  'productAnalytics.dateFilterPicker.lastCompleteHint': '* Last {count} complete {units} and current {unit}',
  'productAnalytics.dateFilterPicker.lastNPlaceholder': 'Last n',
  'productAnalytics.dateFilterPicker.offsetBy': 'Offset by',
  'productAnalytics.dateFilterPicker.periodPlaceholder': 'Period',
  'productAnalytics.dateFilterPicker.presetPlaceholder': 'Select a preset',
  'productAnalytics.dateFilterPicker.removeOffsetLabel': 'Remove Offset',
  'productAnalytics.dateFilterPicker.unitDay': 'day',
  'productAnalytics.dateFilterPicker.unitDays': 'days',
  'productAnalytics.dateFilterPicker.unitMonth': 'month',
  'productAnalytics.dateFilterPicker.unitMonths': 'months',
  'productAnalytics.dateFilterPicker.unitQuarter': 'quarter',
  'productAnalytics.dateFilterPicker.unitQuarters': 'quarters',
  'productAnalytics.dateFilterPicker.unitWeek': 'week',
  'productAnalytics.dateFilterPicker.unitWeeks': 'weeks',
  'productAnalytics.dateFilterPicker.unitYear': 'year',
  'productAnalytics.dateFilterPicker.unitYears': 'years',
  'productAnalytics.duplicateDashboard.copyName': 'Copy of {title}',
  'productAnalytics.duplicateDashboard.errorToast': "Couldn't duplicate dashboard, try again",
  'productAnalytics.earlyAccessSplash.beAmongThe':
    'Be among the first to leverage warehouse-native product analytics by connecting direct to your data warehouse. Gain insights about user behavior without ever having to move the data to LaunchDarkly.',
  'productAnalytics.earlyAccessSplash.getEarlyAccess': 'Get early access to Product analytics',
  'productAnalytics.earlyAccessSplash.last30Days': 'Last 30 days',
  'productAnalytics.earlyAccessSplash.signUpFor': 'Sign up for early access',
  'productAnalytics.earlyAccessSplash.weLlLet':
    "We'll let you know as soon as product analytics is ready for you. Keep an eye on your inbox for updates.",
  'productAnalytics.earlyAccessSplash.websiteVisitsVs': 'Website visits vs Product views vs Purchases',
  'productAnalytics.earlyAccessSplash.youReOn': "You're on the waitlist",
  'productAnalytics.entityPicker.all': 'All',
  'productAnalytics.entityPicker.earlyAccessBody':
    "Thanks for signing up. We'll reach out when metrics become available for your integration.",
  'productAnalytics.entityPicker.earlyAccessHeading': "You're on the list",
  'productAnalytics.entityPicker.filterBy': 'Filter by',
  'productAnalytics.entityPicker.filterEntitiesLabel': 'Filter entities',
  'productAnalytics.entityPicker.noMetricsBody': 'To analyze metrics here, you need a supported warehouse integration.',
  'productAnalytics.entityPicker.noMetricsHeading': "Metrics aren't available for this project and environment",
  'productAnalytics.entityPicker.noResultsFound': 'No results found',
  'productAnalytics.entityPicker.selectEntityLabel': 'Select an entity',
  'productAnalytics.entityPicker.signUp': 'Sign up for early access',
  'productAnalytics.entityPicker.unchartableMetric': 'This metric isn’t available for charting',
  'productAnalytics.environmentPicker.noEnvironmentsAvailable': 'No environments available',
  'productAnalytics.environmentPicker.noProjectsAvailable': 'No projects available',
  'productAnalytics.environmentPicker.searchEnvironmentsLabel': 'Search',
  'productAnalytics.environmentPicker.searchEnvironmentsPlaceholder': 'Search environments…',
  'productAnalytics.environmentPicker.searchProjectsLabel': 'Search',
  'productAnalytics.environmentPicker.searchProjectsPlaceholder': 'Search projects…',
  'productAnalytics.environmentPicker.trigger': 'Select an environment',
  'productAnalytics.eventCalculation.calculationLabel': 'Select a calculation',
  'productAnalytics.eventCalculation.measuring': 'Measuring',
  'productAnalytics.eventCalculation.propertyPlaceholder': 'Select an attribute or property',
  'productAnalytics.eventDetailsSidebar.chartEvent': 'Chart event',
  'productAnalytics.eventDetailsSidebar.closeLabel': 'Close Sidebar',
  'productAnalytics.eventDetailsSidebar.detailsTab': 'Details',
  'productAnalytics.eventDetailsSidebar.dialogLabel': 'Event Sidebar',
  'productAnalytics.eventDetailsSidebar.editEvent': 'Edit event',
  'productAnalytics.eventDetailsSidebar.loadErrorToast':
    'Something went wrong while loading events. Try refreshing the page',
  'productAnalytics.eventDetailsSidebar.loadFailed': 'Unable to load composite event details. Try again.',
  'productAnalytics.eventDetailsSidebar.propertiesDescription':
    'Event-specific metadata captured when the event occurs',
  'productAnalytics.eventDetailsSidebar.propertiesTab': 'Properties',
  'productAnalytics.eventDetailsSidebar.propertiesTableLabel': 'Event properties table',
  'productAnalytics.eventDetailsSidebar.userAttributesDescription': 'User attributes associated with this event',
  'productAnalytics.eventDetailsSidebar.userAttributesTab': 'User Attributes',
  'productAnalytics.eventDetailsSidebar.userAttributesTableLabel': 'User attributes table',
  'productAnalytics.eventFilters.addValueOption': 'Add "{value}"',
  'productAnalytics.eventFilters.betweenValuePlaceholder': 'Enter a value',
  'productAnalytics.eventFilters.containsTooltip':
    'Enter a value to display the results that {negated, select, true {do not contain} other {contain}} the value',
  'productAnalytics.eventFilters.datePlaceholder': 'Select a date',
  'productAnalytics.eventFilters.endDatePlaceholder': 'End date',
  'productAnalytics.eventFilters.enterValuePlaceholder': 'Enter value',
  'productAnalytics.eventFilters.isOperator': 'Is',
  'productAnalytics.eventFilters.loadingProperties': 'Loading…',
  'productAnalytics.eventFilters.multiSelectTooltip': 'You can select multiple values from the dropdown options',
  'productAnalytics.eventFilters.noOperatorMatches': 'No operators match your search',
  'productAnalytics.eventFilters.noOperators': 'No operators available',
  'productAnalytics.eventFilters.noProperties': 'No properties available',
  'productAnalytics.eventFilters.noPropertyMatches': 'No properties match your search',
  'productAnalytics.eventFilters.numberPlaceholder': 'Enter a number.',
  'productAnalytics.eventFilters.numberSearchPlaceholder': 'Type number to add and select',
  'productAnalytics.eventFilters.numericValueHint': 'Enter a numeric value',
  'productAnalytics.eventFilters.operatorPlaceholder': 'Select an operator',
  'productAnalytics.eventFilters.propertyPlaceholder': 'Select attribute or property',
  'productAnalytics.eventFilters.removeEventLabel': 'Remove event',
  'productAnalytics.eventFilters.removeFilter': 'Remove filter',
  'productAnalytics.eventFilters.searchValuesHint': 'Type to search and add values',
  'productAnalytics.eventFilters.selectValueOption': 'Select value: {value}',
  'productAnalytics.eventFilters.singleSelectTooltip': 'Select a value from the dropdown options',
  'productAnalytics.eventFilters.startDatePlaceholder': 'Start date',
  'productAnalytics.eventFilters.startTypingHint': 'Start typing and select the value to add',
  'productAnalytics.eventFilters.tooManyValues': 'There are too many values to show. Enter a number.',
  'productAnalytics.eventFilters.valuePlaceholder': 'Select…',
  'productAnalytics.eventFilters.valueResultsCount': 'value found in {count} results',
  'productAnalytics.eventLabelRename.fieldLabel': 'Rename label',
  'productAnalytics.eventLabelRename.fieldPlaceholder': 'Custom event label',
  'productAnalytics.eventLabelRename.invalidLabelToast':
    'Custom labels can only contain letters, digits, _ or whitespace',
  'productAnalytics.eventLabelRename.reset': 'Reset',
  'productAnalytics.eventLabelRename.saveLabel': 'Save label',
  'productAnalytics.eventPropertiesTable.exampleValueColumn': 'Example value',
  'productAnalytics.eventPropertiesTable.nameColumn': 'Name',
  'productAnalytics.eventPropertiesTable.uniqueValuesColumn': '# of unique values',
  'productAnalytics.eventQuickView.description': 'Description: {description}',
  'productAnalytics.eventQuickView.hint': 'Click on the eye button to view more details.',
  'productAnalytics.eventQuickView.volume': 'Event volume: {count}',
  'productAnalytics.eventQuickView.volumeTooltip': 'Times the definition event appeared in the last 30 days',
  'productAnalytics.eventSelector.addFilter': 'Add filter',
  'productAnalytics.eventSelector.addFilterLabel': 'Add Filter',
  'productAnalytics.eventSelector.calculationLabel': '{aggregation} of {criteria}',
  'productAnalytics.eventSelector.clearFirstTimeFilter': 'Clear first time filter',
  'productAnalytics.eventSelector.compositeEventUnsupported': 'Composite events are not supported for this chart type',
  'productAnalytics.eventSelector.dragTooltip': 'Drag to rearrange steps',
  'productAnalytics.eventSelector.filterFirstTimeOnly': 'Filter by first time only',
  'productAnalytics.eventSelector.firstTimeOnly': 'Include first time only',
  'productAnalytics.eventSelector.invalidLabelToast': 'Custom labels can only contain letters, digits, _ or whitespace',
  'productAnalytics.eventSelector.metricsTrendsOnly': 'Metrics are only supported for Trends charts',
  'productAnalytics.eventSelector.orOperator': 'Or',
  'productAnalytics.eventSelector.orOperatorTooltip':
    'Only or operator is supported on composite event creation. Created composite event will be counted if any of the selected event matches.',
  'productAnalytics.eventSelector.relabelEvent': 'Relabel event',
  'productAnalytics.eventSelector.relabelStep': 'Relabel step',
  'productAnalytics.eventSelector.resetLabelAction': 'Reset label',
  'productAnalytics.eventsList.all': 'All',
  'productAnalytics.eventsList.allActiveEvents': 'All active events',
  'productAnalytics.eventsList.chartEvent': 'Chart event',
  'productAnalytics.eventsList.compositeEvents': 'Composite events',
  'productAnalytics.eventsList.dataAvailableRange': 'Data available from {start}\u00a0 to {end}',
  'productAnalytics.eventsList.editEvent': 'Edit event',
  'productAnalytics.eventsList.eventCount': 'Event count',
  'productAnalytics.eventsList.eventCountAllTime': 'Total event count (all time)',
  'productAnalytics.eventsList.eventCountLast30Days': 'Total event count (last 30 days)',
  'productAnalytics.eventsList.eventName': 'Event name',
  'productAnalytics.eventsList.hiddenEvents': 'Hidden events',
  'productAnalytics.eventsList.hiddenToastDescription': 'Unhide to view details',
  'productAnalytics.eventsList.hiddenToastTitle': 'Event is hidden',
  'productAnalytics.eventsList.hideEvent': 'Hide event',
  'productAnalytics.eventsList.label': 'More options',
  'productAnalytics.eventsList.label2': 'Search for an event by name or description',
  'productAnalytics.eventsList.label3': 'Events table',
  'productAnalytics.eventsList.label4': 'Event count information',
  'productAnalytics.eventsList.last24Hours': 'Last 24 hours',
  'productAnalytics.eventsList.last30Days': 'Last 30 days',
  'productAnalytics.eventsList.last7Days': 'Last 7 days',
  'productAnalytics.eventsList.lastSeen': 'Last seen',
  'productAnalytics.eventsList.lastSeenAll': 'Last seen: All',
  'productAnalytics.eventsList.loadError': 'Something went wrong while loading events. Try refreshing the page',
  'productAnalytics.eventsList.newCompositeEvent': 'New composite event',
  'productAnalytics.eventsList.paginationSummary': '<range>{from}–{to}</range> of <count>{total}</count> events',
  'productAnalytics.eventsList.placeholder': 'Find an event by name, description or key',
  'productAnalytics.eventsList.trackedEvents': 'Tracked events',
  'productAnalytics.eventsList.unhide': 'Unhide',
  'productAnalytics.eventsList.unhideEvent': 'Unhide event',
  'productAnalytics.eventsList.viewFilter': 'View: {filter}',
  'productAnalytics.eventsPage.loadErrorToast': 'Something went wrong while loading events. Refresh the page.',
  'productAnalytics.eventsPage.title': 'Events',
  'productAnalytics.experimentDashboard.iterationDateRange': 'From {startDate} - {endDate}',
  'productAnalytics.experimentDashboard.iterationOption': 'Iteration {number} ({status})',
  'productAnalytics.experimentDashboard.label': 'Iteration selector',
  'productAnalytics.experimentDashboard.linkedExperimentTooltip':
    'This dashboard is linked to the experiment "{name}". Click to go to experiment results',
  'productAnalytics.experimentDashboard.selectedIteration': 'Iteration {number} ({status}): {startDate}',
  'productAnalytics.experimentDashboard.switchIterationBody':
    'Switching to another iteration may change the charts displayed on this dashboard, based on data available in the selected iteration.',
  'productAnalytics.experimentDashboard.switchIterationCancel': 'Return to current',
  'productAnalytics.experimentDashboard.switchIterationConfirm': 'Switch iteration',
  'productAnalytics.experimentDashboard.switchIterationHeading': 'You are switching iterations',
  'productAnalytics.experimentDashboard.switchToLatest': 'Switch to latest iteration',
  'productAnalytics.experimentDashboard.viewExperiment': 'View experiment',
  'productAnalytics.fileUpload.dropzoneBody': 'Click or drag file to this area to upload',
  'productAnalytics.fileUpload.dropzoneLabel': 'File upload dropzone',
  'productAnalytics.fileUpload.removeFileLabel': 'Remove file',
  'productAnalytics.flowsChart.advancedOptions': 'Advanced options',
  'productAnalytics.flowsChart.dateRangeLimit':
    'Flows analysis is only allowed on {limit} days of data. Current{detail}',
  'productAnalytics.flowsChart.dateRangeLimitChart':
    ' The date filter applied on the chart has more than {limit} days. Update or remove the date filter.',
  'productAnalytics.flowsChart.dateRangeLimitDashboard':
    ' The dashboard filter applied has a range more than {limit} days. Remove the dashboard filter on the chart or update the dashboard filter to limit analysis for {limit} days.',
  'productAnalytics.flowsChart.eventsOfInterest': 'EVENTS OF INTEREST',
  'productAnalytics.flowsChart.eventsOfInterestPlaceholder': 'Choose events between those steps…',
  'productAnalytics.flowsChart.highlightEvent': 'HIGHLIGHT EVENT',
  'productAnalytics.flowsChart.highlightEventPlaceholder': 'Choose which events to highlight…',
  'productAnalytics.flowsChart.loadError': "Couldn't load flows data. Try again.",
  'productAnalytics.flowsChart.rows': 'Rows',
  'productAnalytics.flowsChart.selectAllEvents': 'Select all events',
  'productAnalytics.flowsChart.show': 'Show',
  'productAnalytics.flowsChart.stepsAfter': 'steps after',
  'productAnalytics.flowsChart.stepsBeforeAnd': 'steps before and',
  'productAnalytics.flowsChart.tooManyPathsWarning':
    'Flows has too many paths to show. All these paths might not be helpful at one glance. User flows path highlighting is disabled.',
  'productAnalytics.funnelsChart.allSteps': 'All Steps',
  'productAnalytics.funnelsChart.apply': 'Apply',
  'productAnalytics.funnelsChart.conversion': 'Conversion',
  'productAnalytics.funnelsChart.conversionCriteria': 'Conversion Criteria',
  'productAnalytics.funnelsChart.conversionStepTooltip': 'Select a conversion step to view conversion rate over time',
  'productAnalytics.funnelsChart.conversionWindowLabel': 'Conversion window interval',
  'productAnalytics.funnelsChart.count': 'Count',
  'productAnalytics.funnelsChart.counting': 'Counting',
  'productAnalytics.funnelsChart.createCohort': 'Create cohort',
  'productAnalytics.funnelsChart.defineIntervalTooltip': 'Define Interval',
  'productAnalytics.funnelsChart.downloadCsvTooltip': 'Download user data as csv',
  'productAnalytics.funnelsChart.downloadUsersList': 'Download users list',
  'productAnalytics.funnelsChart.firstStep': 'First step',
  'productAnalytics.funnelsChart.funnelTrends': 'Funnel Trends',
  'productAnalytics.funnelsChart.funnelTrendsTooltip': 'View conversion rate over time',
  'productAnalytics.funnelsChart.granularityTooltip': 'Select a time granularity to view conversion rate over time',
  'productAnalytics.funnelsChart.intervalSize': 'Interval Size',
  'productAnalytics.funnelsChart.intervalSizeButton': 'Interval Size: {size} {unit}',
  'productAnalytics.funnelsChart.invalidIntervalTooltip': 'Invalid interval. Number of intervals cannot exceed 90.',
  'productAnalytics.funnelsChart.loadError': "Couldn't load funnels data. Try again.",
  'productAnalytics.funnelsChart.maximum': 'Maximum',
  'productAnalytics.funnelsChart.minimum': 'Minimum',
  'productAnalytics.funnelsChart.previousStep': 'Previous step',
  'productAnalytics.funnelsChart.property1': 'Property 1',
  'productAnalytics.funnelsChart.showConversionsFromTooltip': 'Show conversions from',
  'productAnalytics.funnelsChart.stepsView': 'Steps view',
  'productAnalytics.funnelsChart.stepsViewTooltip': 'View conversion rate between funnel steps',
  'productAnalytics.funnelsChart.tableView': 'Table view',
  'productAnalytics.funnelsChart.tableViewTooltip': 'View funnels data in a table view',
  'productAnalytics.funnelsChart.timeToConvert': 'Time to Convert',
  'productAnalytics.funnelsChart.timeToConvertTooltip':
    'View a distribution of the time it takes users to convert through the funnel',
  'productAnalytics.funnelsChart.totalConversion': 'Total Conversion',
  'productAnalytics.funnelsChart.within': 'Within',
  'productAnalytics.inlineTextEditor.editLabel': 'Edit text',
  'productAnalytics.integrationsStatus.computingBody':
    'The integration to bring event data directly from your warehouse into LaunchDarkly has been set up. Check back soon to start analyzing data.',
  'productAnalytics.integrationsStatus.computingHeading': 'Waiting for data…',
  'productAnalytics.integrationsStatus.configure': 'Configure',
  'productAnalytics.integrationsStatus.emptyBody':
    'Use event data from your data warehouse to run Product Analytics in LaunchDarkly.',
  'productAnalytics.integrationsStatus.emptyHeading': "This environment doesn't have product analytics connected",
  'productAnalytics.integrationsStatus.goToIntegration': 'Go to integration',
  'productAnalytics.leaveSetupPrompt.body':
    'You will be redirected to the integrations page. Are you sure you want to leave?',
  'productAnalytics.leaveSetupPrompt.title': 'Leave this page?',
  'productAnalytics.listEmptyState.adjustFilters': 'Try adjusting your filters',
  'productAnalytics.listEmptyState.assetAttribute': 'attribute',
  'productAnalytics.listEmptyState.assetAttributes': 'attributes',
  'productAnalytics.listEmptyState.assetCohort': 'cohort',
  'productAnalytics.listEmptyState.assetCohorts': 'cohorts',
  'productAnalytics.listEmptyState.assetDashboard': 'dashboard',
  'productAnalytics.listEmptyState.assetDashboards': 'dashboards',
  'productAnalytics.listEmptyState.assetEvent': 'event',
  'productAnalytics.listEmptyState.assetEvents': 'events',
  'productAnalytics.listEmptyState.assetUserActivity': 'user activity',
  'productAnalytics.listEmptyState.clearFilters': 'Clear filters',
  'productAnalytics.listEmptyState.filteredHeading': 'No {assetName} match your filter criteria',
  'productAnalytics.listEmptyState.heading': 'No {assetName} in this environment yet',
  'productAnalytics.listEmptyState.newAsset': 'New {assetName}',
  'productAnalytics.metricCard.label': 'METRIC {label}',
  'productAnalytics.multiSelectMenu.clearLabel': 'Clear',
  'productAnalytics.multiSelectMenu.menuLabel': 'Selection menu',
  'productAnalytics.multiSelectMenu.noItemsFound': 'No items found',
  'productAnalytics.multiSelectMenu.searchLabel': 'Search items',
  'productAnalytics.multiSelectMenu.searchPlaceholder': 'Search…',
  'productAnalytics.removeEventPopover.deleteAnyway': 'Delete Anyway',
  'productAnalytics.removeEventPopover.editFormula': 'Edit Formula',
  'productAnalytics.removeEventPopover.formulaWarning':
    '{entity, select, metric {Metric} other {Event}} already being used in Formula. Deleting this {entity} will remove the formula. Edit instead?',
  'productAnalytics.removeEventPopover.removeEvent': 'Remove event',
  'productAnalytics.removeEventPopover.warning': 'Warning',
  'productAnalytics.retentionChart.apply': 'Apply',
  'productAnalytics.retentionChart.breakdownHeatmapTooltip': 'Breakdown by {name}',
  'productAnalytics.retentionChart.breakdownTrendsTooltip': 'Breakdow by {name} for each {granularity}',
  'productAnalytics.retentionChart.bucketLabel': 'Bucket {index}',
  'productAnalytics.retentionChart.bucketSizeLabel': 'Bucket {index} size',
  'productAnalytics.retentionChart.bucketSizePlaceholder': 'Enter number',
  'productAnalytics.retentionChart.createCohort': 'Create Cohort',
  'productAnalytics.retentionChart.curve': 'Curve',
  'productAnalytics.retentionChart.curveTooltipTitle': '{interval} average retention',
  'productAnalytics.retentionChart.customBucket': 'Custom bucket',
  'productAnalytics.retentionChart.customBucketTooltip': 'Click to set custom retention buckets',
  'productAnalytics.retentionChart.customRetentionTooltip': 'Custom Retention',
  'productAnalytics.retentionChart.downloadUsersList': 'Download users list',
  'productAnalytics.retentionChart.granularityTooltip': 'View chart in selected time granularity',
  'productAnalytics.retentionChart.heatmap': 'Heatmap',
  'productAnalytics.retentionChart.loadError': "Couldn't load retention data. Try again.",
  'productAnalytics.retentionChart.reset': 'Reset',
  'productAnalytics.retentionChart.topBreakdownValuesTooltip': 'Showing top {count} breakdown values',
  'productAnalytics.retentionChart.totalUsersTooltip': 'Total Users - 100%',
  'productAnalytics.retentionChart.totalUsersValue': 'Total Users - {count}',
  'productAnalytics.retentionChart.trends': 'Trends',
  'productAnalytics.retentionChart.trendsTooltipTitle': 'Retention: {series}',
  'productAnalytics.retentionChart.zoomHint':
    'Use mouse scroll to scroll and shift + scroll to zoom in. If the visualization appears too big, try resizing it.',
  'productAnalytics.sampleCsv.label': 'Sample CSV data',
  'productAnalytics.sampleCsv.sampleCsv': 'Sample CSV',
  'productAnalytics.sampleCsv.viewSample': 'View sample',
  'productAnalytics.saveChartToDashboard.confirmSave': 'Save',
  'productAnalytics.saveChartToDashboard.createDashboard': 'Create dashboard',
  'productAnalytics.saveChartToDashboard.createError': "Couldn't create the dashboard. Try again",
  'productAnalytics.saveChartToDashboard.label': 'Create new dashboard',
  'productAnalytics.saveChartToDashboard.loadingDashboards': 'Loading dashboards…',
  'productAnalytics.saveChartToDashboard.noDashboards': "You don't have any dashboard, create one",
  'productAnalytics.saveChartToDashboard.noSearchResults': 'No dashboard named "{query}" found.',
  'productAnalytics.saveChartToDashboard.placeholder': 'Search for dashboard',
  'productAnalytics.saveChartToDashboard.saveError': "Couldn't save the dashboard & content. Try again",
  'productAnalytics.saveChartToDashboard.saveToDashboard': 'Save to dashboard',
  'productAnalytics.saveChartToDashboard.title': 'Save to a new dashboard',
  'productAnalytics.saveChartToDashboard.title2': 'Save chart to dashboard',
  'productAnalytics.selectMenu.all': 'All',
  'productAnalytics.selectMenu.filterBy': 'Filter by',
  'productAnalytics.selectMenu.filterOptionsLabel': 'Filter options',
  'productAnalytics.selectMenu.searchLabel': 'Search…',
  'productAnalytics.selectMenu.selectOptionLabel': 'Select an option',
  'productAnalytics.snowflakeForm.accountUrlHint':
    'Find the account URL in the {warehouse} UI under the {warehouse} > Account menu.',
  'productAnalytics.snowflakeForm.accountUrlLabel': '{warehouse} account URL',
  'productAnalytics.snowflakeForm.adminNote': 'You must be a {warehouseId} SECURITYADMIN and SYSADMIN.',
  'productAnalytics.snowflakeForm.continueBody':
    'Run the script in {warehouse} to connect your {warehouse} account for product analytics. Then, return to this page.',
  'productAnalytics.snowflakeForm.continueHeading': 'Continue setup in {warehouse}',
  'productAnalytics.snowflakeForm.copiedToast': 'Copied',
  'productAnalytics.snowflakeForm.copyTooltip': 'Copy to clipboard',
  'productAnalytics.snowflakeForm.databaseExistsHint':
    'Important: The database must exist in your Snowflake warehouse before proceeding.',
  'productAnalytics.snowflakeForm.databaseHint':
    "Enter your Snowflake database name. If left empty, we'll use: {database}",
  'productAnalytics.snowflakeForm.databaseLabel': 'Database name',
  'productAnalytics.snowflakeForm.databasePlaceholder': 'Enter your database name',
  'productAnalytics.snowflakeForm.heading': 'Configure {warehouse} warehouse',
  'productAnalytics.snowflakeForm.nameLabel': 'Name',
  'productAnalytics.snowflakeForm.namePlaceholder': 'Enter a descriptive name for this integration',
  'productAnalytics.snowflakeForm.retry': 'Try again',
  'productAnalytics.snowflakeForm.save': 'Save configuration',
  'productAnalytics.snowflakeForm.saving': 'Saving…',
  'productAnalytics.snowflakeForm.schemaExistsHint':
    'Important: The schema must exist in your Snowflake warehouse before proceeding.',
  'productAnalytics.snowflakeForm.schemaHint': "Enter your Snowflake schema name. If left empty, we'll use: {schema}",
  'productAnalytics.snowflakeForm.schemaLabel': 'Schema name',
  'productAnalytics.snowflakeForm.schemaPlaceholder': 'Enter your schema name',
  'productAnalytics.snowflakeForm.scriptErrorHeading': 'Script generation failed',
  'productAnalytics.snowflakeForm.scriptHint':
    'The {warehouse} SQL script appears below after you complete the required fields.',
  'productAnalytics.snowflakeForm.scriptSectionLabel': 'Setup your data in {warehouse}',
  'productAnalytics.snowflakeForm.subheading':
    'Enter credentials to connect to your {warehouse} warehouse. Before filling out this form, make sure you complete all the steps mentioned in the <link>documentation</link>.',
  'productAnalytics.snowflakeForm.terms': 'I have read and agree to the <link>Integration Terms and Conditions</link>',
  'productAnalytics.status.loading': 'Loading…',
  'productAnalytics.stickinessChart.frequencyTooltip': 'Choose to analyze on a weekly or monthly frequency',
  'productAnalytics.stickinessChart.loadError': "Couldn't load stickiness data. Try again.",
  'productAnalytics.stickinessChart.renderError': 'Error loading stickiness chart. Try refreshing the page.',
  'productAnalytics.stickinessChart.tooltipTitle': 'Average Stickiness: Day {day}',
  'productAnalytics.tablePagination.firstPageLabel': 'first page',
  'productAnalytics.tablePagination.lastPageLabel': 'last page',
  'productAnalytics.tablePagination.next': 'Next',
  'productAnalytics.tablePagination.nextPageLabel': 'next page',
  'productAnalytics.tablePagination.previous': 'Previous',
  'productAnalytics.tablePagination.previousPageLabel': 'previous page',
  'productAnalytics.tablePagination.rangeOfTotal': '{start}-{end} of {total} items',
  'productAnalytics.tablePagination.rangeSummary': '<range>{from}–{to}</range> of <count>{total}</count>',
  'productAnalytics.textBlockEditor.alignmentLabel': 'Text alignment',
  'productAnalytics.textBlockEditor.backgroundColorLabel': 'Background color',
  'productAnalytics.textBlockEditor.blockQuoteLabel': 'Block quote',
  'productAnalytics.textBlockEditor.boldLabel': 'Bold',
  'productAnalytics.textBlockEditor.bulletedListLabel': 'Bulleted list',
  'productAnalytics.textBlockEditor.cancel': 'Cancel',
  'productAnalytics.textBlockEditor.clearFormattingLabel': 'Clear formatting',
  'productAnalytics.textBlockEditor.codeBlockLabel': 'Code block',
  'productAnalytics.textBlockEditor.decreaseIndentLabel': 'Decrease indent',
  'productAnalytics.textBlockEditor.editLabel': 'Edit text block',
  'productAnalytics.textBlockEditor.formattingTip': 'Tip: Select text to see formatting options',
  'productAnalytics.textBlockEditor.increaseIndentLabel': 'Increase indent',
  'productAnalytics.textBlockEditor.italicLabel': 'Italic',
  'productAnalytics.textBlockEditor.legacyPlaceholder': 'Use a text content to share additional context',
  'productAnalytics.textBlockEditor.linkLabel': 'Link',
  'productAnalytics.textBlockEditor.notesPlaceholder': 'Add notes for this dashboard',
  'productAnalytics.textBlockEditor.orderedListLabel': 'Ordered list',
  'productAnalytics.textBlockEditor.save': 'Save',
  'productAnalytics.textBlockEditor.strikethroughLabel': 'Strikethrough',
  'productAnalytics.textBlockEditor.textColorLabel': 'Text color',
  'productAnalytics.textBlockEditor.textStyleLabel': 'Text style',
  'productAnalytics.textBlockEditor.underlineLabel': 'Underline',
  'productAnalytics.trendsChart.addFormulaTooltip': 'Add a new formula',
  'productAnalytics.trendsChart.apply': 'Apply',
  'productAnalytics.trendsChart.bar': 'Bar',
  'productAnalytics.trendsChart.closeFormulasLabel': 'Close',
  'productAnalytics.trendsChart.comparedWithPeriod': 'Compared with period from {period}',
  'productAnalytics.trendsChart.createCohort': 'Create Cohort',
  'productAnalytics.trendsChart.createCohortUnsupported': 'Create Cohort is not supported for this trend',
  'productAnalytics.trendsChart.deleteFormulaTooltip': 'Delete',
  'productAnalytics.trendsChart.downloadUnsupported': 'User list download is not supported for this trend',
  'productAnalytics.trendsChart.downloadUsersList': 'Download users list',
  'productAnalytics.trendsChart.formula': 'Formula',
  'productAnalytics.trendsChart.formulaEditorPlaceholder':
    'Use the labels next to the events above as variables to create a formula.\neg. (A-B)/(A+B) * 100',
  'productAnalytics.trendsChart.formulaLabelHeading': 'Label',
  'productAnalytics.trendsChart.formulaLabelPlaceholder': 'label eg. Rate',
  'productAnalytics.trendsChart.formulaUnitPlaceholder': 'Select unit',
  'productAnalytics.trendsChart.formulas': 'Formulas',
  'productAnalytics.trendsChart.formulasDocsLink': 'Read more on LaunchDarkly Docs',
  'productAnalytics.trendsChart.formulasHelpBody':
    'Use Formulas to calculate and compare custom metrics on the same chart. Use simple arithmetic operators to write formulas that are applied to the selected events.',
  'productAnalytics.trendsChart.granularityTooltip':
    'Select a time granularity to view data by. Choose days, weeks, or months.',
  'productAnalytics.trendsChart.kpi': 'KPI',
  'productAnalytics.trendsChart.line': 'Line',
  'productAnalytics.trendsChart.loadError': "Couldn't load trends data. Try again.",
  'productAnalytics.trendsChart.newFormula': 'New Formula',
  'productAnalytics.trendsChart.percentage': 'Percentage:',
  'productAnalytics.trendsChart.percentageHelpBody':
    'The % share is calculated per event for the period. Any ratio formulae won’t show the correct % share.',
  'productAnalytics.trendsChart.previousPeriod': 'previous period',
  'productAnalytics.trendsChart.rowActionsTooltip': 'Actions',
  'productAnalytics.trendsChart.stackedLine': 'Stacked line',
  'productAnalytics.trendsChart.table': 'Table',
  'productAnalytics.trendsChart.tableViewHelpBody':
    'The measures in the table view are counts of the (event/formula)-dimension combination, aggregated over the selected date range.',
  'productAnalytics.trendsChart.viewSwitcherTooltip':
    'Switch between different trend views like bar chart, table, and line charts.',
  'productAnalytics.userActivity.demoUser': 'User {number}',
  'productAnalytics.userActivity.demoUsers': 'Demo Users:',
  'productAnalytics.userActivity.emptyStateMessage': 'To view user activity, enter a user ID in the field above',
  'productAnalytics.userActivity.enterUserId': 'Enter a User ID to start analyzing their product usage',
  'productAnalytics.userActivity.errorSubtitle': 'Try again or enter another user ID',
  'productAnalytics.userActivity.eventsStream': 'Events Stream',
  'productAnalytics.userActivity.loadMoreEvents': 'Load More Events',
  'productAnalytics.userActivity.placeholder': 'Search user ID',
  'productAnalytics.userActivity.selectDateRange': 'Select a date range',
  'productAnalytics.userActivity.showActivity': 'Show Activity',
  'productAnalytics.userActivity.showingMostRecent': 'Showing most recent {count} events',
  'productAnalytics.userActivity.specifyDateRange':
    'Specify a date range. To display all available data, choose a date range larger than your data set',
  'productAnalytics.userActivity.thatSAll': "That's all. All events loaded",
  'productAnalytics.userActivity.timezoneBanner': 'Timeline is in {zone} ',
  'productAnalytics.userActivity.title': 'Failed to load activity for the specified user',
  'productAnalytics.userActivityFilters.apply': 'Apply',
  'productAnalytics.userActivityFilters.clearAll': 'Clear All',
  'productAnalytics.userActivityFilters.dateFilter': 'Date filter',
  'productAnalytics.userActivityFilters.hide': 'Hide',
  'productAnalytics.userActivityFilters.highlight': 'Highlight',
  'productAnalytics.userActivityFilters.in': 'In',
  'productAnalytics.userActivityFilters.noEventsMatch': 'No events match your search criteria',
  'productAnalytics.userActivityFilters.onlyShow': 'Only Show',
  'productAnalytics.userActivityFilters.placeholder': 'Search',
  'productAnalytics.userActivityFilters.selectAll': 'Select All',
  'productAnalytics.userActivityFilters.selectEvents': 'Select Event(s)',
  'productAnalytics.userActivityFilters.selectEventsTooltip': 'Click to select events',
  'productAnalytics.userActivityFilters.selectedEventCount': '{count, plural, one {# Event} other {# Events}}',
  'productAnalytics.userActivityFilters.selectedEventsTruncated': '{events}, + {more} More',
  'productAnalytics.userActivityFilters.showAll': 'Show All',
  'productAnalytics.userActivityFilters.title': 'Click to set a date filter',
  'productAnalytics.userActivityFilters.title2': 'Select event stream filter',
  'productAnalytics.userActivityPage.title': 'User activity',
  'productAnalytics.userAttributes.attributeName': 'Attribute name',
  'productAnalytics.userAttributes.countOfUnique': 'Count of unique values',
  'productAnalytics.userAttributes.exampleValue': 'Example value',
  'productAnalytics.userAttributes.label': 'Search for an attribute by name',
  'productAnalytics.userAttributes.paginationSummary':
    '<range>{from}–{to}</range> of <count>{total}</count> attributes',
  'productAnalytics.userAttributes.placeholder': 'Find an attribute by name',
  'productAnalytics.userAttributesPage.title': 'User attributes',
  'productAnalytics.waitingScreen.checkAgain':
    'You can <link>check again</link> to see if setup is complete. If this takes longer than expected, review your connection settings or reach out to our team for help.',
  'productAnalytics.waitingScreen.connectViaSdk': 'Connect via SDK',
  'productAnalytics.waitingScreen.integrateWithThird': 'Integrate with third-party',
  'productAnalytics.waitingScreen.label': 'Product analytics dashboard preview',
  'productAnalytics.waitingScreen.noEventsDescription': 'Ensure your integration is sending events and try again.',
  'productAnalytics.waitingScreen.noEventsTitle': 'No events detected yet',
  'productAnalytics.waitingScreen.or': 'OR',
  'productAnalytics.warehouseSetup.cancel': 'Cancel',
  'productAnalytics.warehouseSetup.customSdkTileSubtitle':
    "You're using a CDP (like Segment, etc.) or your own SDK to track events.",
  'productAnalytics.warehouseSetup.customSdkTileTitle': 'Using CDP/Custom SDK',
  'productAnalytics.warehouseSetup.ldSdkTileSubtitle': "You're tracking events with the LaunchDarkly SDK.",
  'productAnalytics.warehouseSetup.ldSdkTileTitle': 'Using LaunchDarkly SDK',
  'productAnalytics.warehouseSetup.nextStep': 'Next step',
  'productAnalytics.warehouseSetup.previousStep': 'Previous step',
  'productAnalytics.warehouseSetup.selectEnvironmentHeading': 'Select environment for setup',
  'productAnalytics.warehouseSetup.selectEnvironmentSubheading':
    "Choose the specific project and environment you'd like to setup {warehouse}-native Product Analytics in.",
  'productAnalytics.warehouseSetup.stepConfigureConnection': 'Configure connection',
  'productAnalytics.warehouseSetup.stepSelectEnvironment': 'Select environment',
  'productAnalytics.warehouseSetup.stepSelectTrackingMethod': 'Select tracking method',
  'productAnalytics.warehouseSetup.trackingMethodHeading': 'Pick your current event tracking method',
  'productAnalytics.warehouseSetup.trackingMethodSubheading':
    'Your selection helps tailor the next step to your existing event tracking setup.',
  'productAnalytics.warehouseSetup.unsupportedWarehouse': 'Unsupported warehouse type: {warehouseId}',
  'productAnalyticsIntegration.page.title': 'Product Analytics Integration',
  'productAnalytics_.page.title': 'Product analytics',
  'profile.emailHint.managedByGithub': 'You cannot change your email address as it is connected to GitHub.',
  'profile.emailHint.managedByGoogle': 'You cannot change your email address as it is connected to Google.',
  'profile.emailHint.managedBySso':
    'Single sign-on manages your email address. Update it through your identity provider instead.',
  'profile.emailHint.managedByVercel': 'You cannot change your email address as it is connected to Vercel.',
  'profile.emailHint.signInEmail': "This is the email address you'll use to sign in.",
  'profile.emailVerification.cancelChange': 'Cancel change',
  'profile.emailVerification.changeBody':
    'To change your email address to <strong>{email}</strong>, you must verify this address belongs to you.',
  'profile.emailVerification.heading': 'Check your email for a verification email from LaunchDarkly',
  'profile.emailVerification.resendEmail': 'Resend email',
  'profile.emailVerification.unverifiedBody': 'Your email is not yet verified.',
  'profile.imageUpload.errorPrefix': 'Error: {error}',
  'profile.imageUpload.fileTooBig': 'File is too big. Max file size: 2MB',
  'profile.imageUpload.loading': 'Loading…',
  'profile.imageUpload.previewAlt': 'Preview',
  'profile.imageUpload.previewImage': 'Preview image',
  'profile.imageUpload.saveImage': 'Save image',
  'profile.imageUpload.savedImage': 'Saved image',
  'profile.imageUpload.uploading': 'Uploading your image',
  'profile.notifications.emailChangeCanceled': 'Email change canceled',
  'profile.notifications.emailVerified': 'Email verified',
  'profile.notifications.imageUploaded': 'Profile image uploaded',
  'profile.notifications.notificationPreferencesSaveFailed': 'Error saving notification preferences',
  'profile.notifications.notificationPreferencesUpdated': 'Notification preferences updated',
  'profile.notifications.passwordUpdated': 'Password updated',
  'profile.notifications.resendVerificationFailed': 'Failed to resend verification email to {email}. Try again later.',
  'profile.notifications.updated': 'Profile updated',
  'profile.notifications.verificationEmailSent': 'Verification email sent to {email}',
  'profile.notificationsUpsell.chatAppsBody':
    'Set up the <slack></slack> or <msTeams></msTeams> integration to manage notification preferences in your preferred chat app.',
  'profile.notificationsUpsell.heading':
    'Receive notifications in {msTeams, select, true {Slack or MS Teams} other {Slack}}',
  'profile.notificationsUpsell.msTeamsLink': 'MS Teams',
  'profile.notificationsUpsell.setUpThe': 'Set up the',
  'profile.notificationsUpsell.slackAppLink': 'LaunchDarkly Slack app',
  'profile.notificationsUpsell.slackBody':
    "With the <slack></slack>, you can view, monitor, and control feature flags directly from your team's Slack workspace.",
  'profile.notificationsUpsell.slackLink': 'Slack',
  'profile.notificationsUpsell.withThe': 'With the',
  'profile.notificationsUpsell.youCanView':
    ", you can view, monitor, and control feature flags directly from your team's Slack workspace.",
  'profile.passwordForm.allFieldsRequired': 'All fields are required.',
  'profile.passwordForm.confirmNewPassword': 'Confirm new password',
  'profile.passwordForm.currentPassword': 'Current password',
  'profile.passwordForm.newPassword': 'New password',
  'profile.passwordForm.passwordTip': 'Your password must be at least {count} characters long{complexity}',
  'profile.passwordForm.update': 'Update',
  'profile.passwordForm.updating': 'Updating your password',
  'profile.profileForm.emailAddress': 'Email address',
  'profile.profileForm.emailAliasBody':
    'If an account uses a different email address than the one you want to use to sign in, you can update it here.',
  'profile.profileForm.emailAliasHeading': 'Use a single email address to sign in to multiple accounts',
  'profile.profileForm.firstName': 'First name',
  'profile.profileForm.formLabel': 'Update profile',
  'profile.profileForm.lastName': 'Last name',
  'profile.profileForm.loading': 'Loading…',
  'profile.profileForm.unlockEmailLabel': 'Click to unlock email address for editing',
  'profile.profileForm.update': 'Update',
  'profile.profileForm.updating': 'Updating your info',
  'profile.status.loading': 'Loading…',
  'profile.userProfile.accountHeading': 'Account',
  'profile.userProfile.accountIdLabel': 'Account ID',
  'profile.userProfile.heading': 'Profile',
  'profile.userProfile.imageLabel': 'Profile image',
  'profile.userProfile.imageTypesHint': 'Supported files types: png, jpeg',
  'profile.userProfile.maxFileSize': 'Max file size: 2MB',
  'profile.userProfile.memberIdLabel': 'Member ID',
  'profile.userProfile.memberPageBody': 'Visit your <link>Member</link> page to review your roles and permissions.',
  'profile.userProfile.mfaBody':
    'Multi-factor authentication (MFA) adds an extra layer of security to your account so if your password is compromised or stolen, only you can log in.',
  'profile.userProfile.mfaHeading': 'Multi-factor authentication',
  'profile.userProfile.mfaLearnMore': 'Read about multi-factor authentication',
  'profile.userProfile.notificationsBody': 'Manage how you receive notifications from LaunchDarkly.',
  'profile.userProfile.notificationsHeading': 'Notification settings',
  'profile.userProfile.passwordLabel': 'Password',
  'projectSettings.approvalsPage.failedToLoad7': 'Failed to load approval settings',
  'projectSettings.approvalsPage.title': 'Approval settings',
  'projectSettings.approvalsPage.tryAgain': 'Try again',
  'projectSettings.contextKindsPage.title': 'Context kinds',
  'projectSettings.environmentsPage.approvals': 'Approvals',
  'projectSettings.environmentsPage.createEnvironment': 'Create environment',
  'projectSettings.environmentsPage.createEnvironmentFor': 'Create environment {projectName}',
  'projectSettings.environmentsPage.loadingEnvironment': 'Loading environment',
  'projectSettings.environmentsPage.overview': 'Overview',
  'projectSettings.environmentsPage.sdkKeys': 'SDK keys',
  'projectSettings.environments_Page.title': 'Environments',
  'projectSettings.flagsPage.title': 'Flag settings',
  'projectSettings.lifecyclePage.automaticArchivalLoadError': 'Failed to load automatic archival settings.',
  'projectSettings.lifecyclePage.failedToLoad8': 'Failed to load automated cleanup settings.',
  'projectSettings.lifecyclePage.flagLifecycleSettings': 'Flag lifecycle settings',
  'projectSettings.lifecyclePage.title': 'Lifecycle settings',
  'projectSettings.listPage.title': 'General',
  'projectSettings.observabilityFilterRulesPage.failedToLoad9': 'Failed to load filter rules',
  'projectSettings.observabilityFilterRulesPage.tryAgain': 'Try again',
  'projectSettings.observabilityPage.failedToLoad2': 'Failed to load variations',
  'projectSettings.observabilityPage.tryAgain': 'Try again',
  'projectSettings.observability_Page.title': 'Observability settings',
  'projectSettings.page.title': 'Project settings',
  'projectSettings.payloadFilters_Page.title': 'Payload filters',
  'projectSettings.releasePipelinesNewPage.title': 'Create release pipeline',
  'projectSettings.releasePipelinesPage.breadcrumb': 'Release pipelines',
  'projectSettings.releasesListPage.tabs.environmentReleasePlans': 'Environment release plans',
  'projectSettings.releasesListPage.tabs.releaseAutomation': 'Release automation',
  'projectSettings.releasesListPage.tabs.releasePolicies': 'Release policies',
  'projectSettings.releasesListPage.title': 'Release settings',
  'projectSettings.releasesReleasePipelinesNewPage.title': 'Create release pipeline',
  'projectSettings.releasesReleasePipelinesPage.breadcrumb': 'Release settings',
  'projectSettings.releasesReleasePoliciesNewPage.title': 'Release settings',
  'projectSettings.releasesReleasePoliciesPage.title': 'Release settings',
  'projectSettings.viewsLinkedResourcesPage.findResource': 'Find resource',
  'projectSettings.viewsLinkedResourcesPage.linkResources': 'Link resources',
  'projectSettings.viewsLinkedResourcesPage.loadingLinkedResources': 'Loading linked resources',
  'projectSettings.viewsLinkedResourcesPage.noMatches': 'No {resources} match your filters',
  'projectSettings.viewsLinkedResourcesPage.noneFound': 'No {resources} found',
  'projectSettings.viewsLinkedResourcesPage.viewOptions': 'View options',
  'projectSettings.viewsListPage.findView': 'Find view',
  'projectSettings.viewsPage.aiConfigs': 'AI Configs',
  'projectSettings.viewsPage.application': 'Application',
  'projectSettings.viewsPage.createView': 'Create view',
  'projectSettings.viewsPage.flags': 'Flags',
  'projectSettings.viewsPage.flagsAndSegments':
    'Flags and segments that are created after this setting is enabled will be required to be linked to at least one view. Existing flags and segments will be unaffected.',
  'projectSettings.viewsPage.label': 'Resource count',
  'projectSettings.viewsPage.loadingView': 'Loading view',
  'projectSettings.viewsPage.metrics': 'Metrics',
  'projectSettings.viewsPage.overview': 'Overview',
  'projectSettings.viewsPage.requireNewResources': 'Require new resources to be linked to views',
  'projectSettings.viewsPage.segments': 'Segments',
  'projectSettings.viewsPage.settings': 'Settings',
  'projectSettings.viewsPage.text': 'Read about views',
  'projectSettings.viewsPage.title': 'Views',
  'projectSettings.viewsPage.useViewsTo':
    'Use views to group flags and segments according to the teams in your organization and the features they work on.',
  'projectSettings.viewsPage.views': 'Views',
  'projects.createProject.addTagsLabel': 'Add tags',
  'projects.createProject.closeLabel': 'Close dialog',
  'projects.createProject.createProject': 'Create project',
  'projects.createProject.keyLabel': 'Key',
  'projects.createProject.nameLabel': 'Name',
  'projects.createProject.noPermission': 'You do not have the necessary permissions to create new projects.',
  'projects.createProject.tags': 'Tags',
  'projects.createProject.tagsLabel': 'project tags',
  'projects.environmentActions.actionsLabel': 'env actions',
  'projects.environmentActions.approvalSettings': 'Approval settings',
  'projects.environmentActions.clientSideId': 'Client-side ID',
  'projects.environmentActions.copyMenu': 'Copy…',
  'projects.environmentActions.deleteEnvironment': 'Delete environment',
  'projects.environmentActions.editEnvironment': 'Edit environment',
  'projects.environmentActions.environmentKey': 'Environment key',
  'projects.environmentActions.markCritical': 'Mark as critical',
  'projects.environmentActions.mobileKey': 'Mobile key',
  'projects.environmentActions.sdkKey': 'SDK key',
  'projects.environmentActions.showSdkKeys': 'Show SDK keys',
  'projects.environmentActions.unmarkCritical': 'Unmark as critical',
  'projects.environmentActions.viewSdkKeys': 'View SDK keys',
  'projects.environmentForm.changeTemplateRequired': 'Change template is required',
  'projects.environmentForm.selectApprovalTags': 'Select one or more tags',
  'projects.environmentKeys.closeLabel': 'close modal',
  'projects.environmentKeys.moreInfoLabel': 'View more information about the {keyType}',
  'projects.environmentKeys.readAbout': 'Read about environment keys',
  'projects.environmentKeys.sdkKeysFor': 'SDK keys for',
  'projects.environmentKeys.useTheseSdk': 'Use these SDK keys to configure LaunchDarkly.',
  'projects.environments.actionsLabel': 'env actions',
  'projects.environments.addEnvironment': 'Add environment',
  'projects.environments.clear': 'Clear',
  'projects.environments.critical': 'Critical',
  'projects.environments.criticalLabel': 'Critical environment designation',
  'projects.environments.environments': 'Environments',
  'projects.environments.filterEnvironmentsBy': 'Filter environments by',
  'projects.environments.filters': 'Filters',
  'projects.environments.listLabel': 'environment list',
  'projects.environments.moreTagsLabel': 'Additional environment tags',
  'projects.environments.noEnvironmentsFound': 'No environments found',
  'projects.environments.readAbout': 'Read about environments',
  'projects.environments.searchLabel': 'Search for environments',
  'projects.environments.searchPlaceholder': 'Find environment',
  'projects.environments.tags': 'Tags',
  'projects.environments.tagsFilter': 'Tags: {selection}',
  'projects.environments.tagsLabel': 'Environment tags',
  'projects.environments.thisEnvironmentHas': 'This environment has been marked critical.',
  'projects.environments.useEnvironmentsTo':
    'Use environments to maintain separate rollout rules in different contexts, from local to production.',
  'projects.flagKeyConventions.addPrefix': 'Add prefix',
  'projects.flagKeyConventions.casingConvention': 'Casing convention',
  'projects.flagKeyConventions.conventionPlaceholder': 'Select naming convention',
  'projects.flagKeyConventions.enterYourFlag': 'Enter your flag naming convention prefix',
  'projects.flagKeyConventions.failedToLoad': 'Failed to load project flag key conventions',
  'projects.flagKeyConventions.prefix': 'Prefix',
  'projects.flagKeyConventions.save': 'Save',
  'projects.flagKeyConventions.selectYourKey': 'Select your key convention from the dropdown menu below',
  'projects.flagSettings.clientSideAvailability': 'Client-side availability',
  'projects.flagSettings.enforceNamingConventions': 'Enforce naming conventions for flag keys in this project.',
  'projects.flagSettings.flagKeyConventions': 'Flag key conventions',
  'projects.flagSettings.flagTemplates': 'Flag templates',
  'projects.flagSettings.ifEnabledAll':
    'If enabled, all new flags will be available to client-side SDKs by default. You can still override this at the flag level.',
  'projects.flagSettings.ifEnabledNew':
    "If enabled, new flags in this project must have a team as their maintainer. Individual members won't be suggested as maintainers when creating a flag.",
  'projects.flagSettings.learnMore': 'Learn more',
  'projects.flagSettings.requireFlagsAnd': 'Require flags and configs to be linked to a view',
  'projects.flagSettings.requireNewFlags': 'Require new flags to have a team as their maintainer',
  'projects.flagSettings.requireSegmentsTo': 'Require segments to be linked to a view',
  'projects.flagSettings.sdksUsingClient': 'SDKs using Client-side ID',
  'projects.flagSettings.sdksUsingMobile': 'SDKs using Mobile Key',
  'projects.flagSettings.teamMaintainerRequirement': 'Team maintainer requirement',
  'projects.flagSettings.theseAreThe':
    'These are the default settings for flag templates. You select one of these templates when you create a flag.',
  'projects.flagTemplates.cancel': 'Cancel',
  'projects.flagTemplates.close': 'Close',
  'projects.flagTemplates.defaultVariations': 'Default variations',
  'projects.flagTemplates.editTagsLabel': 'edit tags',
  'projects.flagTemplates.editTemplate': 'Edit {templateName} flag template',
  'projects.flagTemplates.editTemplateLabel': 'edit {templateName} flag template',
  'projects.flagTemplates.listLabel': 'flag defaults list',
  'projects.flagTemplates.markFlagAs': 'Mark flag as temporary?',
  'projects.flagTemplates.metadata': 'Metadata',
  'projects.flagTemplates.nameDescription': 'Choose a unique name',
  'projects.flagTemplates.nameLabel': 'Name',
  'projects.flagTemplates.offVariationLabel': 'Off variation',
  'projects.flagTemplates.onVariationLabel': 'On variation',
  'projects.flagTemplates.save': 'Save',
  'projects.flagTemplates.settings': 'Settings',
  'projects.flagTemplates.tags': 'Tags',
  'projects.flagTemplates.temporaryNo': 'No',
  'projects.flagTemplates.temporaryNoDescription': 'This flag will serve as a permanent control',
  'projects.flagTemplates.temporaryYes': 'Yes',
  'projects.flagTemplates.temporaryYesDescription': 'This flag will be used temporarily to release a feature',
  'projects.flagTemplates.valueLabel': 'Value',
  'projects.flagTemplates.variationLegend': 'Variation {number}',
  'projects.flagTemplates.variationNameLabel': 'variation {number} name',
  'projects.flagTemplates.variations': 'Variations',
  'projects.generalSettings.addTagsLabel': 'Add tags',
  'projects.generalSettings.deleteConfirmBody':
    'This project, including all of its environments, will be deleted permanently <strong>for all members of your organization</strong>.',
  'projects.generalSettings.deleteProject': 'Delete project',
  'projects.generalSettings.deleteProjectButton': 'Delete',
  'projects.generalSettings.deleteWarning':
    'Permanently delete this project for all members of your organization. <strong>This cannot be undone.</strong>',
  'projects.generalSettings.keyLabel': 'Key',
  'projects.generalSettings.nameAndKey': 'Name and key',
  'projects.generalSettings.nameLabel': 'Name',
  'projects.generalSettings.namePlaceholder': 'project name',
  'projects.generalSettings.projectName': 'Project name',
  'projects.generalSettings.save': 'Save',
  'projects.generalSettings.tags': 'Tags',
  'projects.generalSettings.toDeleteThis': 'To delete this project, use the LaunchDarkly integration in Vercel.',
  'projects.generalSettings.youCannotDelete':
    'You cannot delete this project because it is the only project remaining in your organization.',
  'projects.notFound.body': "Sorry, we can't find the project you're looking for.",
  'projects.notFound.heading': 'Project not found',
  'projects.notFound.selectAnother': 'Project with key "{projectKey}" not found. Select another project to continue.',
  'projects.notifications.deleted': 'Project deleted',
  'projects.notifications.payloadFilterArchiveFailed': 'There was a problem archiving this payload filter',
  'projects.notifications.payloadFilterArchived': 'Payload filter archived',
  'projects.notifications.payloadFilterCreated': 'Payload filter created',
  'projects.notifications.payloadFilterOutOfDate': 'Your payload filter is out of date. Reload the page and try again.',
  'projects.notifications.payloadFilterUpdated': 'Payload filter updated',
  'projects.payloadFilterActions.actionsLabel': 'env actions',
  'projects.payloadFilterActions.copyKey': 'Copy key',
  'projects.payloadFilterActions.edit': 'Edit',
  'projects.payloadFilterForm.addRule': 'Add rule',
  'projects.payloadFilterForm.addRules': 'Add rules',
  'projects.payloadFilterForm.cancel': 'Cancel',
  'projects.payloadFilterForm.createTitle': 'Create payload filter',
  'projects.payloadFilterForm.creatingLabel': 'Creating payload filter',
  'projects.payloadFilterForm.deleteRuleLabel': 'Delete payload filter rule',
  'projects.payloadFilterForm.description': 'Description',
  'projects.payloadFilterForm.editTitle': 'Edit payload filter',
  'projects.payloadFilterForm.editingLabel': 'Editing payload filter',
  'projects.payloadFilterForm.enterRegex': 'Enter regex to complete this rule condition',
  'projects.payloadFilterForm.flagsWillBe': 'Flags will be',
  'projects.payloadFilterForm.keyLabel': 'Key',
  'projects.payloadFilterForm.nameLabel': 'Name',
  'projects.payloadFilterForm.regexLabel': 'Payload filter regex text area',
  'projects.payloadFilterForm.rules': 'Rules',
  'projects.payloadFilterForm.save': 'Save',
  'projects.payloadFilterForm.selectAction': 'Select whether flags are included or excluded for this rule',
  'projects.payloadFilterForm.selectFlags': 'Select flags to be included in this payload filter',
  'projects.payloadFilterForm.selectMatchKind': 'Select how flags will be matched for this rule',
  'projects.payloadFilterForm.selectRuleTags': 'Select tags to complete this rule condition',
  'projects.payloadFilterForm.tagsPlaceholder': 'Select tags',
  'projects.payloadFilterForm.thereAreNo': 'There are no rules for this payload filter.',
  'projects.payloadFilterForm.warning': 'Warning',
  'projects.payloadFilters.copyKeyLabel': 'Copy the key for this payload ({key}) to your clipboard',
  'projects.payloadFilters.created': 'Created',
  'projects.payloadFilters.emptyTitle': 'There are no payload filters in this project yet.',
  'projects.payloadFilters.listLabel': 'filter list',
  'projects.payloadFilters.makeSureYou': 'Make sure you spelled the search terms correctly.',
  'projects.payloadFilters.noFiltersFound': 'No filters found',
  'projects.payloadFilters.noMatchTitle': 'No payload filters match your search. Try this:',
  'projects.payloadFilters.paginationSummary':
    '<strong>{from}–{to}</strong> of <strong>{total}</strong> payload filters',
  'projects.payloadFilters.payloadFilters': 'Payload Filters',
  'projects.payloadFilters.searchByName': 'Search by name or key.',
  'projects.payloadFilters.searchPlaceholder': 'Find filter',
  'projects.payloadFilters.showArchived': 'Show archived',
  'projects.payloadFilters.usePayloadFilters': 'Use payload filters to initialize your SDK efficiently.',
  'projects.projectsList.actions': 'Actions',
  'projects.projectsList.clearFilters': 'Clear filters',
  'projects.projectsList.copyKeyLabel': 'Click to copy the key for this project ({projectKey}) to your clipboard.',
  'projects.projectsList.current': 'Current',
  'projects.projectsList.environments': 'Environments',
  'projects.projectsList.flags': 'Flags',
  'projects.projectsList.goToLabel': 'Go to {projectName}',
  'projects.projectsList.manageProjectSettings': 'Manage project settings',
  'projects.projectsList.nameLabel': 'Name',
  'projects.projectsList.noMatchBody': 'Try adjusting your search or filters',
  'projects.projectsList.noMatchTitle': 'No projects match your filter criteria',
  'projects.projectsList.statusLabel': 'Project status',
  'projects.projectsList.tableSummary':
    'A table with five columns, showing a list of Projects, any tags they have, the number of Environments and Flags belonging to those Projects, and actions for each Project.',
  'projects.projectsList.tags': 'Tags',
  'projects.projectsPicker.searchPlaceholder': 'Search projects',
  'projects.projectsPicker.selectLabel': '{count, plural, one {Select project} other {Select projects}}',
  'projects.projectsPicker.selectedCount': '{count, plural, one {# project} other {# projects}} selected',
  'projects.settingsDrawer.closeLabel': 'Close project settings',
  'projects.settingsDrawer.heading': 'Project details',
  'projects.settingsDrawer.loadError':
    'This project couldn’t be loaded. It may have been deleted, or you may not have access to it.',
  'projects.settingsDrawer.loadingLabel': 'Loading project settings',
  'projects.status.loading': 'Loading…',
  'projects.toolbar.payloadFilterSearchPlaceholder': 'Find a payload filter by name or key',
  'projects.toolbar.projectSearchPlaceholder': 'Find a project by name or key',
  'projects.toolbar.tagsFilter': 'Tags: {selection}',
  'queryBuilder.autocomplete.fieldsCategory': 'Fields',
  'queryBuilder.autocomplete.loadingLabel': 'Loading options…',
  'queryBuilder.autocomplete.noOptions': 'No options',
  'queryBuilder.autocomplete.operatorsCategory': 'Operators',
  'queryBuilder.autocomplete.optionsLabel': 'Options',
  'queryBuilder.autocomplete.popoverLabel': 'Filter options',
  'queryBuilder.autocomplete.previewCategory': 'Preview',
  'queryBuilder.autocomplete.relationalFieldsCategory': 'Relational Fields',
  'queryBuilder.expressionGroup.convertTooltip': 'Convert logical group to relational filter',
  'queryBuilder.expressionGroup.logicalGroup': 'Logical Group',
  'queryBuilder.expressionGroup.logicalGroupDescription': 'Logical grouping of filter expressions to evaluate first.',
  'queryBuilder.expressionGroup.relationalFilterTooltip': '{type} relational filter.',
  'queryBuilder.expressionGroup.removeGroupTooltip': 'Remove logical group',
  'queryBuilder.expressionGroup.removeLabel': 'Remove expression group',
  'queryBuilder.expressionGroup.removeRelationalTooltip': 'Remove relational field',
  'queryBuilder.expressionGroup.typeLabel': 'Expression type: {type}',
  'queryBuilder.filterBar.andDescription': 'Match when both filter expressions are true.',
  'queryBuilder.filterBar.clearLabel': 'Clear filter',
  'queryBuilder.filterBar.gridLabel': 'Create a search query',
  'queryBuilder.filterBar.notDescription': 'Exclude matches for the next filter expression.',
  'queryBuilder.filterBar.orDescription': 'Match when either filter expression is true.',
  'queryBuilder.filterBar.relationalGroupDescription': 'Wrap the next expressions in a {type}(…) group',
  'queryBuilder.filterBar.searchForField': 'Search for field: {text}',
  'queryBuilder.filterBar.searchForFieldPrefix': 'Search for field:',
  'queryBuilder.filterBar.searchQueryLabel': 'Search query',
  'queryBuilder.filterBar.searchTermLabel': 'Add a search term',
  'queryBuilder.filterBar.selectionHandlerLabel': 'Selection handler',
  'queryBuilder.filterBar.spaceLabel': 'Space',
  'queryBuilder.filterBar.thenDescription': 'Match sessions where the next event happens after the previous one.',
  'queryBuilder.filterToken.editField': 'Edit field',
  'queryBuilder.filterToken.editFieldLabel': 'Edit key for filter: {field}',
  'queryBuilder.filterToken.editFilter': 'Edit filter',
  'queryBuilder.filterToken.editOperator': 'Edit operator',
  'queryBuilder.filterToken.editOperatorLabel': 'Edit operator for filter: {field}',
  'queryBuilder.filterToken.editValue': 'Edit value',
  'queryBuilder.filterToken.editValueLabel': 'Edit value for filter: {field}',
  'queryBuilder.filterToken.editValuesLabel': 'Edit values for filter: {field}',
  'queryBuilder.filterToken.editValuesPopoverLabel': 'Edit values for {field}',
  'queryBuilder.filterToken.moreCount': '+{count} more',
  'queryBuilder.filterToken.moreValues': '{first} +{count} more',
  'queryBuilder.filterToken.regexExcludePrefix': 'Does not match regex:',
  'queryBuilder.filterToken.regexExcludedDescription': 'Results which are matched by this regex will be excluded',
  'queryBuilder.filterToken.regexIncludedDescription': 'Results which are matched by this regex will be included',
  'queryBuilder.filterToken.regexMatchPrefix': 'Matches regex:',
  'queryBuilder.filterToken.removeFilter': 'Remove filter',
  'queryBuilder.filterToken.removeFilterLabel': 'Remove filter: {field}',
  'queryBuilder.filterToken.searchForDuration': 'Search for duration: {text}',
  'queryBuilder.filterToken.searchForNumber': 'Search for number: {text}',
  'queryBuilder.filterToken.searchForQuotedValue': 'Search for value: "{text}"',
  'queryBuilder.filterToken.searchForValue': 'Search for value: {text}',
  'queryBuilder.filterToken.toggleFilter': 'Toggle filter',
  'queryBuilder.filterToken.toggleLabel': 'Toggle {field}',
  'queryBuilder.filterToken.valueTypeBoolean': 'bool',
  'queryBuilder.filterToken.valueTypeBooleanToString': 'Change type from boolean to string',
  'queryBuilder.filterToken.valueTypeNumber': 'num',
  'queryBuilder.filterToken.valueTypeNumberToString': 'Change type from number to string',
  'queryBuilder.filterToken.valueTypeString': 'str',
  'queryBuilder.filterToken.valueTypeStringToBoolean': 'Change type from string to boolean',
  'queryBuilder.filterToken.valueTypeStringToNumber': 'Change type from string to number',
  'queryBuilder.filterValues.apply': 'Apply',
  'queryBuilder.filterValues.filterValueLabel': 'Filter value',
  'queryBuilder.filterValues.loading': 'Loading…',
  'queryBuilder.filterValues.noOptions': 'No options',
  'queryBuilder.filterValues.popoverLabel': 'Select values',
  'queryBuilder.filterValues.searchPlaceholder': 'Search {field}…',
  'queryBuilder.filterValues.searchValuesPlaceholder': 'Search values…',
  'queryBuilder.filterValues.selectValues': 'Select values',
  'queryBuilder.filterValues.valuesLabel': '{field} values',
  'queryBuilder.inlineFilterMenu.addFilter': 'Add filter',
  'queryBuilder.inlineFilterMenu.filter': 'Filter',
  'queryBuilder.logicOperator.label': 'Logic operator {operator}',
  'queryBuilder.logicOperator.removeLabel': 'Remove operator',
  'queryBuilder.logicOperator.removeTooltip': 'Remove logic operator',
  'queryBuilder.logicOperator.toggleLabel': 'Logic operator {operator}, press to toggle',
  'queryBuilder.logicOperator.toggleTooltip': 'Toggle to {operator}',
  'queryBuilder.textSearch.clearLabel': 'Clear search',
  'queryBuilder.textSearch.searchLabel': 'Search',
  'relay.rulePolicy.resourceRequired': 'Resource is required',
  'relayProxy.configForm.allProjectsAnd': 'All projects and environments',
  'relayProxy.configForm.createHeading': 'Create a Relay Proxy configuration',
  'relayProxy.configForm.descriptionPlaceholder': 'Describe what this configuration will be used for',
  'relayProxy.configForm.docsLinkText': 'documentation',
  'relayProxy.configForm.docsSentence':
    'Enable a seamless and reliable Relay Proxy. See <docsLink></docsLink> for more information.',
  'relayProxy.configForm.editHeading': 'Edit {name}',
  'relayProxy.configForm.inlinePolicy': 'Inline policy',
  'relayProxy.configForm.keyWarning':
    'Copy and save the configuration key that appears on the next screen. It will be obscured on subsequent views.',
  'relayProxy.configForm.loadingText': 'Saving',
  'relayProxy.configForm.name': 'Name',
  'relayProxy.configForm.policyEditorLoadingLabel': 'Loading policy editor',
  'relayProxy.configForm.rule': 'Rule',
  'relayProxy.configForm.saveConfiguration': 'Save configuration',
  'relayProxy.configForm.scopeHint': 'Relay Proxy configurations can only be scoped to projects and environments',
  'relayProxy.configList.actions': 'Actions',
  'relayProxy.configList.copyKeyNow': 'Copy and save this key now.',
  'relayProxy.configList.createConfiguration': 'Create configuration',
  'relayProxy.configList.created': 'Created',
  'relayProxy.configList.createdByYou': 'Created by you',
  'relayProxy.configList.creator': 'Creator',
  'relayProxy.configList.emptyStateBody':
    'Connect multiple servers to a local stream using the Relay Proxy. The Relay Proxy helps reduce outbound connections, minimize redundant database traffic, and lets you use Big Segments with server-side SDKs.',
  'relayProxy.configList.environments': 'Environments',
  'relayProxy.configList.keyObscuredHint': 'The key will be obscured after you leave this page.',
  'relayProxy.configList.lastModified': 'Last Modified',
  'relayProxy.configList.name': 'Name',
  'relayProxy.configList.rowActionsLabel': '{name} rule options',
  'relayProxy.configList.searchPlaceholder': 'Find a Relay Proxy configuration',
  'relayProxy.notifications.configCreated': 'Relay Proxy configuration "{name}" created',
  'relayProxy.notifications.configDeleted': 'Relay Proxy configuration deleted',
  'relayProxy.notifications.configUpdated': 'Relay Proxy configuration "{name}" updated',
  'relayProxy.resetDialog.body':
    'Resetting a configuration invalidates it, and any clients must be updated to use the new configuration.',
  'relayProxy.resetDialog.confirmBody':
    'Are you sure you want to reset your <strong>{name}</strong> configuration? Your old configuration will be invalidated, so any clients must be updated to use the new configuration.',
  'relayProxy.resetDialog.confirmTitle': 'Reset this configuration?',
  'relayProxy.resetDialog.durationHint': 'This should be a positive number up to 720 (1 month)',
  'relayProxy.resetDialog.keepActiveAfter': 'hours',
  'relayProxy.resetDialog.keepActiveBefore': 'Keep the old configuration active for',
  'relayProxy.resetDialog.keyWarning':
    'Copy and save the configuration key that appears on the next screen. It will be obscured on subsequent views.',
  'relayProxy.resetDialog.resetConfiguration': 'Reset configuration',
  'relayProxy.status.loading': 'Loading…',
  'releaseAutopilotReview.approve': 'Approve',
  'releaseAutopilotReview.approved': 'Approved',
  'releaseAutopilotReview.chat.label': 'Conversation',
  'releaseAutopilotReview.chat.closed': 'This pull request is closed, so chat is turned off here.',
  'releaseAutopilotReview.chat.merged': 'This pull request has merged, so chat is turned off here.',
  'releaseAutopilotReview.chat.viewConversation': 'View conversation',
  'releaseAutopilotReview.chat.notShared': "This conversation hasn't been shared with you.",
  'releaseAutopilotReview.flag.approveBoth': 'Approve both',
  'releaseAutopilotReview.flag.approveBothDescription': 'Creates the flag now. The release starts after the PR merges.',
  'releaseAutopilotReview.flag.approveFlagOnly': 'Approve flag only',
  'releaseAutopilotReview.flag.approveFlagOnlyDescription':
    'Creates the flag now. You can approve the release plan later.',
  'releaseAutopilotReview.flag.approveOptions': 'Approval options',
  'releaseAutopilotReview.flag.applying': 'Creating the flag and pushing a commit',
  'releaseAutopilotReview.flag.descriptionLabel': 'Description',
  'releaseAutopilotReview.flag.descriptionPlaceholder': 'Add a description',
  'releaseAutopilotReview.flag.editDescription': 'Edit flag description',
  'releaseAutopilotReview.flag.editKey': 'Edit flag key',
  'releaseAutopilotReview.flag.editMaintainer': 'Edit maintainer',
  'releaseAutopilotReview.flag.editName': 'Edit flag name',
  'releaseAutopilotReview.flag.existing': 'Uses an existing flag',
  'releaseAutopilotReview.flag.heading': 'Proposed flag',
  'releaseAutopilotReview.flag.keyInvalid':
    "Use only letters, numbers, '.', '_' or '-', and start with a letter or number",
  'releaseAutopilotReview.flag.keyLabel': 'Key',
  'releaseAutopilotReview.flag.loading': 'Loading proposed flag',
  'releaseAutopilotReview.flag.maintainerPlaceholder': 'Select maintainer',
  'releaseAutopilotReview.flag.maintainerLabel': 'Maintainer',
  'releaseAutopilotReview.flag.teamMaintainerRequired': 'This project requires a team maintainer.',
  'releaseAutopilotReview.flag.nameLabel': 'Name',
  'releaseAutopilotReview.flag.awaitingAnswer': 'Answer the question in the chat so LaunchDarkly can propose a flag.',
  'releaseAutopilotReview.flag.noChanges':
    'LaunchDarkly found nothing to flag. To get a proposal, ask LaunchDarkly in the chat.',
  'releaseAutopilotReview.flag.none': 'No new flag proposed for this pull request.',
  'releaseAutopilotReview.flag.rejected': 'Flag rejected',
  'releaseAutopilotReview.header': '{owner}/{repository} · PR #{pullNumber}',
  'releaseAutopilotReview.loadError': "Couldn't load this review. Refresh the page to try again.",
  'releaseAutopilotReview.page.title': 'Review',
  'releaseAutopilotReview.reject': 'Reject',
  'releaseAutopilotReview.rejectFeedback.heading': 'Why are you rejecting this?',
  'releaseAutopilotReview.rejectFeedback.notePlaceholder': 'Tell us more (optional)',
  'releaseAutopilotReview.rejectFeedback.reason.alreadyFlagged': 'Already flagged',
  'releaseAutopilotReview.rejectFeedback.reason.doesntNeedFlag': "Doesn't need a flag",
  'releaseAutopilotReview.rejectFeedback.reason.rolloutTooFast': 'Rollout too fast',
  'releaseAutopilotReview.rejectFeedback.reason.rolloutTooSlow': 'Rollout too slow',
  'releaseAutopilotReview.rejectFeedback.reason.wrongEnvironments': 'Wrong environments',
  'releaseAutopilotReview.rejectFeedback.reason.wrongMaintainer': 'Wrong maintainer',
  'releaseAutopilotReview.rejectFeedback.reason.wrongNameOrKey': 'Wrong name or key',
  'releaseAutopilotReview.rejectFeedback.reason.wrongOrder': 'Wrong order',
  'releaseAutopilotReview.rejectFeedback.send': 'Send feedback',
  'releaseAutopilotReview.rejectFeedback.sendError': "Your feedback wasn't sent. Try again.",
  'releaseAutopilotReview.rejectFeedback.skip': 'Skip',
  'releaseAutopilotReview.rejected': 'This plan was rejected. To get a different one, ask LaunchDarkly in the chat.',
  'releaseAutopilotReview.rejecting': 'Rejecting the plan',
  'releaseAutopilotReview.release.applying': 'Approving the release plan',
  'releaseAutopilotReview.release.approve': 'Approve release',
  'releaseAutopilotReview.release.approvedSummary':
    '{count, plural, one {# environment} other {# environments}}, sequential after merge',
  'releaseAutopilotReview.release.description': 'Sequential rollout after merge.',
  'releaseAutopilotReview.release.awaitingAnswer': 'LaunchDarkly will propose a release after you answer its question.',
  'releaseAutopilotReview.release.noChanges':
    'LaunchDarkly will propose a release if you ask it for a plan in the chat.',
  'releaseAutopilotReview.release.empty': 'No release proposed for this pull request.',
  'releaseAutopilotReview.release.heading': 'Release plan',
  'releaseAutopilotReview.release.held': "Won't be released",
  'releaseAutopilotReview.release.heldReason.incompletePolicy':
    "This environment's release policy is missing stages or metrics, so it won't be released until the policy is fixed.",
  'releaseAutopilotReview.release.heldReason.missingPolicy':
    "No release policy matches this environment, so it won't be released until one does.",
  'releaseAutopilotReview.release.heldReason.other': "This environment won't be released.",
  'releaseAutopilotReview.release.loading': 'Loading environments',
  'releaseAutopilotReview.release.rejected': 'Release plan rejected',
  'releaseAutopilotReview.release.method.guarded': 'Guarded rollout',
  'releaseAutopilotReview.release.method.immediate': 'Immediate release',
  'releaseAutopilotReview.release.method.progressive': 'Progressive rollout',
  'releaseAutopilotReview.revert': 'Revert',
  'releaseAutopilotReview.reverting': 'Reverting the flag, the release plan and the commit',
  'releaseAutopilotReview.whatHappens.afterMerge': 'After merge',
  'releaseAutopilotReview.whatHappens.approveRelease': 'Approve the release plan so LaunchDarkly rolls it out',
  'releaseAutopilotReview.whatHappens.createFlag': 'Create the flag in LaunchDarkly',
  'releaseAutopilotReview.whatHappens.declined': 'Declined',
  'releaseAutopilotReview.whatHappens.existingFlag': 'Use the flag that is already in LaunchDarkly',
  'releaseAutopilotReview.whatHappens.heading': 'What happens',
  'releaseAutopilotReview.whatHappens.merged': 'Merged',
  'releaseAutopilotReview.whatHappens.awaitingAnswer': 'Nothing to approve until LaunchDarkly has a plan',
  'releaseAutopilotReview.whatHappens.noChanges': 'Nothing to approve until you ask LaunchDarkly for a plan',
  'releaseAutopilotReview.whatHappens.nothingToApprove': 'Nothing to approve on this pull request',
  'releaseAutopilotReview.whatHappens.now': 'Now',
  'releaseAutopilotReview.whatHappens.pushCommit':
    'Push a commit to this pull request that wraps the change in the flag',
  'releaseAutopilotReview.whatHappens.releaseMerged':
    'Pull request merged. The release starts with {environment} once the change deploys.',
  'releaseAutopilotReview.whatHappens.releaseMergedNoEnvironment':
    'Pull request merged. The release starts once the change deploys.',
  'releaseAutopilotReview.whatHappens.releaseScheduled':
    'Release plan approved. Release starts after merge, beginning with {environment}.',
  'releaseAutopilotReview.whatHappens.releaseScheduledNoEnvironment':
    'Release plan approved. Release starts after merge.',
  'releaseAutopilotReview.whatHappens.scheduled': 'Scheduled',
  'releaseAutopilotReview.whatHappens.viewCommit': 'View commit {sha}',
  'releaseAutopilotReview.whatHappens.viewFlag': 'View flag',
  'releaseAutopilotReview.why.existingHeading': 'Why use the existing flag?',
  'releaseAutopilotReview.why.heading': 'Why is a flag suggested?',
  'releaseAutopilotReview.why.noChangesHeading': 'Why are no changes suggested?',
  'releasePipelines.approvals.fieldLabel': 'Approvals',
  'releasePipelines.approvals.loadingIntegrationLabel': 'Loading integration approvals',
  'releasePipelines.approvals.minimumApprovals':
    'The environment selected requires a minimum of {count, plural, one {# approval} other {# approvals}}',
  'releasePipelines.approvals.requireApprovalBy': 'Require approval by',
  'releasePipelines.approvals.requireApprovalIn': 'Require approval in',
  'releasePipelines.approvals.requireApprovalToStart': 'Require approval to start',
  'releasePipelines.approvals.reviewIn': 'Review in',
  'releasePipelines.approvals.reviewerCount': '{count} reviewers',
  'releasePipelines.approvals.reviewersFieldLabel': 'Reviewers',
  'releasePipelines.approvals.reviewersTagGroupLabel': 'audience reviewers',
  'releasePipelines.approvals.selectedReviewersLabel': 'selected reviewers',
  'releasePipelines.approvals.unavailableReviewer': 'Unavailable {type}',
  'releasePipelines.approvals.viewReviewersTooltip': 'View reviewers',
  'releasePipelines.audience.completeRequiredTooltip': 'Complete required fields',
  'releasePipelines.audience.delete': 'Delete',
  'releasePipelines.audience.deleteLabel': 'delete audience',
  'releasePipelines.audience.done': 'Done',
  'releasePipelines.audience.howHeading': 'How do you want to release?',
  'releasePipelines.audience.oneOrMoreRequired': '1 or more required',
  'releasePipelines.audience.releaseTo':
    'Release {strategy, select, immediate {immediately to} guarded {with guarded rollout to} other {to}}',
  'releasePipelines.audience.summaryLabel': '{name} summary',
  'releasePipelines.audience.whoHeading': 'Who do you want to release to?',
  'releasePipelines.audiencePicker.chooseEnvironmentFirst': 'First, choose option in Environment field',
  'releasePipelines.audiencePicker.everyone': 'Everyone',
  'releasePipelines.audiencePicker.fieldLabel': 'Audience',
  'releasePipelines.audiencePicker.noSegmentsDescription': 'Environment has no segments',
  'releasePipelines.audiencePicker.placeholder': 'Choose option…',
  'releasePipelines.audiencePicker.segments': 'Segments',
  'releasePipelines.builder.addDescription': 'Add description',
  'releasePipelines.builder.addDescriptionLabel': 'Add release pipeline description',
  'releasePipelines.builder.cancel': 'Cancel',
  'releasePipelines.builder.copyKeyLabel': 'copy release pipeline key to clipboard',
  'releasePipelines.builder.copyKeyTooltip': 'Copy key',
  'releasePipelines.builder.createReleasePipeline': 'Create release pipeline',
  'releasePipelines.builder.createdToast': 'Release pipeline "{name}" created',
  'releasePipelines.builder.descriptionFieldLabel': 'release pipeline description',
  'releasePipelines.builder.descriptionIconLabel': 'release pipeline description',
  'releasePipelines.builder.descriptionTooltip': 'Description',
  'releasePipelines.builder.editDescriptionLabel': 'Edit release pipeline description',
  'releasePipelines.builder.error': 'Error',
  'releasePipelines.builder.invalidError': 'Invalid release pipeline',
  'releasePipelines.builder.isDefaultTooltip': 'Default release pipeline',
  'releasePipelines.builder.keyCopiedToast': '{key} copied to clipboard',
  'releasePipelines.builder.keyFieldLabel': 'release pipeline key',
  'releasePipelines.builder.keyFormatDescription': '{keyFormat}. You cannot use "new" as a key.',
  'releasePipelines.builder.keyFormatLabel': 'key format description',
  'releasePipelines.builder.keyReadOnlyDescription': 'Key cannot be modified',
  'releasePipelines.builder.markDefaultTooltip': 'Mark default',
  'releasePipelines.builder.nameFieldLabel': 'release pipeline name',
  'releasePipelines.builder.namePlaceholder': 'Release pipeline name',
  'releasePipelines.builder.projectDefaultLabel': 'mark as project default',
  'releasePipelines.builder.saveReleasePipeline': 'Save release pipeline',
  'releasePipelines.builder.savedToast': 'Release pipeline saved',
  'releasePipelines.builder.tagsIconLabel': 'release pipeline tags',
  'releasePipelines.builder.tagsTooltip': 'Tags',
  'releasePipelines.builder.unmarkDefaultTooltip': 'Unmark default',
  'releasePipelines.deleteDialog.confirm': 'Delete',
  'releasePipelines.deleteDialog.impactedFlags':
    '<strong>{count, plural, one {# flag} other {# flags}}</strong> will be removed from the release pipeline. The targeting for {count, plural, one {this flag} other {these flags}} will not be affected.',
  'releasePipelines.deleteDialog.title': 'Delete release pipeline?',
  'releasePipelines.deleteDialog.validationLabel': "Enter the release pipeline's name or key to confirm",
  'releasePipelines.environmentPicker.chooseLabel': 'Choose environment',
  'releasePipelines.environmentPicker.clearSearchLabel': 'clear search',
  'releasePipelines.environmentPicker.fieldLabel': 'Environment',
  'releasePipelines.environmentPicker.listLabel': 'environments list',
  'releasePipelines.environmentPicker.loadingLabel': 'loading environments',
  'releasePipelines.environmentPicker.noEnvironmentsFound': 'No environments found',
  'releasePipelines.environmentPicker.placeholder': 'Choose environment…',
  'releasePipelines.environmentPicker.searchLabel': 'search environments',
  'releasePipelines.environmentPicker.searchPlaceholder': 'Search environments',
  'releasePipelines.errorsPopover.heading': 'Errors',
  'releasePipelines.errorsPopover.incompleteTooltip': 'Release pipeline incomplete. Click for details',
  'releasePipelines.errorsPopover.phaseFallback': 'Phase {number}',
  'releasePipelines.errorsPopover.pipelineGroup': 'Release pipeline',
  'releasePipelines.guardedRollout.custom': 'Custom',
  'releasePipelines.guardedRollout.customDurationLabel': 'Custom duration',
  'releasePipelines.guardedRollout.customUnitLabel': 'custom duration unit',
  'releasePipelines.guardedRollout.days': 'Day(s)',
  'releasePipelines.guardedRollout.decrementLabel': 'decrement',
  'releasePipelines.guardedRollout.durationLabel': 'Duration',
  'releasePipelines.guardedRollout.hours': 'Hour(s)',
  'releasePipelines.guardedRollout.incrementLabel': 'increment',
  'releasePipelines.guardedRollout.minutes': 'Minute(s)',
  'releasePipelines.guardedRollout.oneHour': '1 hour',
  'releasePipelines.guardedRollout.oneHourDescription':
    'For code changes with high expected traffic that you want to release quickly',
  'releasePipelines.guardedRollout.rollbackCheckbox': 'Automatically roll back if a regression is detected',
  'releasePipelines.guardedRollout.trafficAllocationLabel': 'Traffic allocation',
  'releasePipelines.guardedRollout.twentyFourHours': '24 hours',
  'releasePipelines.guardedRollout.twentyFourHoursDescription':
    'For code changes with moderate expected traffic that want to balance risk and safety',
  'releasePipelines.guardedRollout.twoDays': '2 days',
  'releasePipelines.guardedRollout.twoDaysDescription':
    'For code changes with low expected traffic or that require increased safety',
  'releasePipelines.listPage.betaTag': 'Beta',
  'releasePipelines.listPage.createReleasePipeline': 'Create release pipeline',
  'releasePipelines.listPage.defaultTag': 'Default',
  'releasePipelines.listPage.deleteError': 'Failed to delete release pipeline. Try again later.',
  'releasePipelines.listPage.deletedToast': 'Release pipeline deleted',
  'releasePipelines.listPage.description':
    'Use release pipelines to automate the release process for your feature flags.',
  'releasePipelines.listPage.flagCount': '{count, plural, one {# flag} other {# flags}}',
  'releasePipelines.listPage.learnMore': 'Learn more',
  'releasePipelines.listPage.listLabel': 'pipeline list',
  'releasePipelines.listPage.maxReachedTooltip':
    'This project has reached the maximum of {count, plural, one {# release pipeline} other {# release pipelines}}.',
  'releasePipelines.listPage.noPipelinesFound': 'No pipelines found',
  'releasePipelines.listPage.paginationSummary': '<strong>{from}–{to}</strong> of <strong>{total}</strong> pipelines',
  'releasePipelines.listPage.searchPlaceholder': 'Find pipeline',
  'releasePipelines.listPage.tagsLabel': 'Pipeline tags',
  'releasePipelines.listPage.title': 'Release pipelines',
  'releasePipelines.phases.actionsLabel': 'phase actions for {phaseName} phase',
  'releasePipelines.phases.addPhaseLabel': 'add phase',
  'releasePipelines.phases.addPhaseTooltip': 'Add phase',
  'releasePipelines.phases.addReleaseStrategy': 'Add release strategy',
  'releasePipelines.phases.andDivider': 'AND',
  'releasePipelines.phases.configurePhaseHeading': 'Configure phase',
  'releasePipelines.phases.deletePhase': 'Delete phase',
  'releasePipelines.phases.editAutomation': 'Edit automation',
  'releasePipelines.phases.moveLabel': 'move {phaseName} phase {direction, select, left {left} other {right}}',
  'releasePipelines.phases.moveTooltip': 'Move phase {direction, select, left {left} other {right}}',
  'releasePipelines.phases.nameFieldLabel': 'Name',
  'releasePipelines.phases.namePlaceholder': 'Phase name',
  'releasePipelines.phases.phaseNameLabel': 'phase name',
  'releasePipelines.phases.releasedDescription': 'This phase is designated for flags that are released.',
  'releasePipelines.phases.releasedHeading': 'Released',
  'releasePipelines.pipelineMenu.alreadyDefaultTooltip': 'This is already the default release pipeline.',
  'releasePipelines.pipelineMenu.defaultUpdateError': 'Failed to update default release pipeline. Try again later.',
  'releasePipelines.pipelineMenu.defaultUpdatedToast': 'Default release pipeline updated',
  'releasePipelines.pipelineMenu.delete': 'Delete',
  'releasePipelines.pipelineMenu.deleteActiveReleasesTooltip': 'Cannot delete release pipeline with active releases',
  'releasePipelines.pipelineMenu.deleteDefaultTooltip': 'Default release pipelines cannot be deleted',
  'releasePipelines.pipelineMenu.deleteLoadErrorTooltip': 'Unexpected error. Refresh page and try again.',
  'releasePipelines.pipelineMenu.deleteNoPermissionTooltip':
    'You do not have permission to delete this release pipeline',
  'releasePipelines.pipelineMenu.deletePendingTooltip': 'Wait',
  'releasePipelines.pipelineMenu.duplicate': 'Duplicate',
  'releasePipelines.pipelineMenu.duplicateNoPermissionTooltip':
    'You do not have permission to duplicate this release pipeline',
  'releasePipelines.pipelineMenu.edit': 'Edit',
  'releasePipelines.pipelineMenu.makeDefault': 'Make default',
  'releasePipelines.pipelineMenu.makeDefaultNoPermissionTooltip':
    'You do not have permission to set the default release pipeline.',
  'releasePipelines.pipelineMenu.optionsLabel': '{name} options',
  'releasePipelines.pipelineMenu.viewReleases': 'View releases',
  'releasePipelines.releaseStrategy.chooseEnvironmentFirst': 'First, choose option in Environment field',
  'releasePipelines.releaseStrategy.doNotRollBack': 'Do not roll back',
  'releasePipelines.releaseStrategy.duration': 'Duration',
  'releasePipelines.releaseStrategy.durationDays': '{count, plural, one {# day} other {# days}}',
  'releasePipelines.releaseStrategy.durationHours': '{count, plural, one {# hour} other {# hours}}',
  'releasePipelines.releaseStrategy.durationMinutes': '{count, plural, one {# minute} other {# minutes}}',
  'releasePipelines.releaseStrategy.fieldLabel': 'Strategy',
  'releasePipelines.releaseStrategy.guardedRollout': 'Guarded rollout',
  'releasePipelines.releaseStrategy.placeholder': 'Choose option…',
  'releasePipelines.releaseStrategy.regressionBehavior': 'Regression behavior',
  'releasePipelines.releaseStrategy.releaseImmediately': 'Release immediately',
  'releasePipelines.releaseStrategy.rollBack': 'Roll back',
  'releasePipelines.releaseStrategy.trafficAllocation': 'Traffic allocation',
  'releasePipelines.reviewersDialog.addLabel': 'add reviewers',
  'releasePipelines.reviewersDialog.addTooltip': 'Add reviewers to this audience',
  'releasePipelines.reviewersDialog.clearSearchLabel': 'clear search',
  'releasePipelines.reviewersDialog.closeLabel': 'close options',
  'releasePipelines.reviewersDialog.listLabel': 'Select members or teams to approve',
  'releasePipelines.reviewersDialog.membersSection': 'Members',
  'releasePipelines.reviewersDialog.noneFound': 'No {section, select, teams {teams} other {members}} found',
  'releasePipelines.reviewersDialog.searchLabel': 'search members or teams',
  'releasePipelines.reviewersDialog.searchPlaceholder': 'Search members or teams',
  'releasePipelines.reviewersDialog.teamsSection': 'Teams',
  'releasePipelines.reviewersDialog.title': 'Choose reviewer(s)',
  'releasePipelines.searchPicker.clearSearchLabel': 'clear search',
  'releasePipelines.searchPicker.loadingLabel': 'loading options',
  'releasePipelines.segmentsPicker.addLabel': 'add segments',
  'releasePipelines.segmentsPicker.addTooltip': 'Add segments',
  'releasePipelines.segmentsPicker.chooseEnvironmentFirst': 'Select an environment to add segments',
  'releasePipelines.segmentsPicker.clearSearchLabel': 'clear search',
  'releasePipelines.segmentsPicker.closeLabel': 'close options',
  'releasePipelines.segmentsPicker.fieldLabel': 'Segments',
  'releasePipelines.segmentsPicker.listLabel': 'Select segments',
  'releasePipelines.segmentsPicker.loadingSegmentLabel': 'loading segment',
  'releasePipelines.segmentsPicker.searchLabel': 'search segments',
  'releasePipelines.segmentsPicker.searchPlaceholder': 'Search segments',
  'releasePipelines.segmentsPicker.tagGroupLabel': 'audience segment keys',
  'releasePipelines.segmentsPicker.title': 'Choose segment(s)',
  'releasePipelines.status.loading': 'Loading…',
  'releasePipelines.tagsPicker.addTags': 'Add tags',
  'releasePipelines.tagsPicker.noTagsFound': 'No tags found',
  'releasePipelines.tagsPicker.searchLabel': 'Search tags',
  'releasePipelines.tagsPicker.searchPlaceholder': 'Search tags',
  'releasePipelines.tagsPicker.tagsLabel': 'Release pipeline tags',
  'releasePipelines.validation.audienceRequired': 'Audience is required',
  'releasePipelines.validation.automationsRequired': 'One or more automations is required',
  'releasePipelines.validation.descriptionMaxLength': 'Description cannot be more than {max} characters',
  'releasePipelines.validation.durationPositive': 'Duration must be a positive number',
  'releasePipelines.validation.environmentRequired': 'Environment is required',
  'releasePipelines.validation.keyMaxLength': 'Key cannot be more than {max} characters',
  'releasePipelines.validation.keyRequired': 'Key is required',
  'releasePipelines.validation.maxPhases': 'No more than {max} phases are allowed',
  'releasePipelines.validation.minPhases': 'At least two phases are required',
  'releasePipelines.validation.nameMaxLength': 'Name can be no more than {max} characters',
  'releasePipelines.validation.nameRequired': 'Name is required',
  'releasePipelines.validation.phaseNameMaxLength': 'Phase name cannot be more than {max} characters',
  'releasePipelines.validation.phaseNameRequired': 'Phase name is required',
  'releasePipelines.validation.phaseNameUnique': 'Phase name must be unique',
  'releasePipelines.validation.reviewerIdEmpty': 'reviewer id cannot be empty',
  'releasePipelines.validation.reviewersRequired': 'One or more reviewers is required',
  'releasePipelines.validation.segmentKeyEmpty': 'segment key cannot be empty',
  'releasePipelines.validation.segmentsRequired': 'One or more segments is required',
  'releasePipelines.validation.trafficAllocationPositive': 'Traffic allocation must be a positive number',
  'releasePlan.codeCleanupDescription':
    'LaunchDarkly opens a pull request to remove the flag from your code when cleanup is ready.',
  'releasePlan.codeCleanupTitle': 'Code cleanup',
  'releasePlan.flagArchivalDescription': 'LaunchDarkly archives the flag once it meets your archival conditions.',
  'releasePlan.flagArchivalTitle': 'Flag archival',
  'releasePolicies.autoRollbackField.checkboxLabel': 'Enable automatic rollback',
  'releasePolicies.autoRollbackField.checkboxTitle': 'Enable auto-rollback',
  'releasePolicies.autoRollbackField.description':
    'Automatically roll back to the original variation when a regression is detected on any metric',
  'releasePolicies.autoRollbackField.label': 'Auto-rollback',
  'releasePolicies.contextKindField.label': 'Target by',
  'releasePolicies.contextKindField.loadingPlaceholder': 'Loading context kinds…',
  'releasePolicies.contextKindField.pickerLabel': 'Context kind',
  'releasePolicies.contextKindField.placeholder': 'Select a context kind',
  'releasePolicies.createDialog.errorToastBody': 'Failed to create release policy. Try again later.',
  'releasePolicies.createDialog.errorToastTitle': 'Error creating release policy',
  'releasePolicies.createDialog.successToast': 'Release policy created',
  'releasePolicies.createDialog.title': 'Create release policy',
  'releasePolicies.deleteDialog.confirm': 'Delete',
  'releasePolicies.deleteDialog.errorToastBody': 'Failed to delete release policy. Try again later.',
  'releasePolicies.deleteDialog.errorToastTitle': 'Error deleting release policy',
  'releasePolicies.deleteDialog.keyConfirmationLabel': "Enter the release policy's key to confirm",
  'releasePolicies.deleteDialog.successToast': 'Release policy deleted',
  'releasePolicies.deleteDialog.title': 'Delete release policy?',
  'releasePolicies.deprecationBanner.body':
    "LaunchDarkly is replacing release policies with environment release plans, and your existing policies will migrate automatically. Complete any incomplete policies before migration — incomplete policies migrate as incomplete plans and can't be selected on the flag targeting page.",
  'releasePolicies.deprecationBanner.heading': 'Release policies are being deprecated',
  'releasePolicies.deprecationBanner.infoBody':
    'LaunchDarkly is replacing release policies with environment release plans.',
  'releasePolicies.editDialog.errorToastBody': 'Failed to update release policy. Try again later.',
  'releasePolicies.editDialog.errorToastTitle': 'Error updating release policy',
  'releasePolicies.editDialog.successToast': 'Release policy updated',
  'releasePolicies.emptyState.createBody':
    'A release policy recommends a default release method for new flags across environments, so teams start from a consistent rollout strategy.',
  'releasePolicies.emptyState.createTitle': 'Create your first release policy',
  'releasePolicies.emptyState.noAccessBody':
    'A release policy recommends a default release method for new flags across environments. Contact a project admin to create one.',
  'releasePolicies.emptyState.noAccessTitle': 'No release policies for this project',
  'releasePolicies.environmentsField.label': 'Environments',
  'releasePolicies.environmentsField.noResults': 'No environments found for: {query}',
  'releasePolicies.environmentsField.noneAvailable': 'No environments available',
  'releasePolicies.environmentsField.searchPlaceholder': 'Search environments',
  'releasePolicies.incompletePolicy.description':
    "{missing, select, metrics {This policy is missing metrics, so it will migrate as an incomplete plan that can't be selected on the flag targeting page.} environmentAndMetrics {This policy is missing an environment and metrics, so it will migrate as an incomplete plan that can't be selected on the flag targeting page.} other {This policy is missing an environment, so it will migrate as an incomplete plan that can't be selected on the flag targeting page.}}",
  'releasePolicies.incompletePolicy.fallbackDescription':
    "{missing, select, environmentAndMetrics {The fallback policy has no environment or metrics, so it will migrate as an incomplete plan that can't be selected on the flag targeting page.} other {The fallback policy has no environment, so it will migrate as an incomplete plan that can't be selected on the flag targeting page.}}",
  'releasePolicies.incompletePolicy.label': 'Incomplete policy',
  'releasePolicies.metricsField.description': 'Metrics or metric groups to monitor for regressions during the release',
  'releasePolicies.metricsField.label': 'Release guardrail metrics',
  'releasePolicies.metricsField.loadError': 'Failed to load metrics',
  'releasePolicies.metricsField.loadingLabel': 'Loading metrics',
  'releasePolicies.metricsField.placeholder': 'Select metrics or metric groups',
  'releasePolicies.metricsField.selectedLabel': 'Selected metrics',
  'releasePolicies.policyActions.delete': 'Delete',
  'releasePolicies.policyActions.edit': 'Edit',
  'releasePolicies.policyActions.menuLabel': '{name} options menu',
  'releasePolicies.policyActions.menuTriggerLabel': '{name} options',
  'releasePolicies.policyActions.view': 'View',
  'releasePolicies.policyForm.addCondition': 'Add condition',
  'releasePolicies.policyForm.addSetting': 'Add setting',
  'releasePolicies.policyForm.autoRollbackSetting': 'Auto-rollback',
  'releasePolicies.policyForm.cancel': 'Cancel',
  'releasePolicies.policyForm.closeLabel': 'close',
  'releasePolicies.policyForm.conditionsSection': 'Add conditions',
  'releasePolicies.policyForm.configureSection': 'Configure the policy',
  'releasePolicies.policyForm.contextKindSetting': 'Context kind',
  'releasePolicies.policyForm.createPolicy': 'Create policy',
  'releasePolicies.policyForm.environmentsCondition': 'Environments',
  'releasePolicies.policyForm.keyExists': 'Release policy key already exists',
  'releasePolicies.policyForm.keyInvalid':
    'Key must only contain letters, numbers, ., _, or - and must start with an alphanumeric',
  'releasePolicies.policyForm.keyLabel': 'Key',
  'releasePolicies.policyForm.keyPlaceholder': 'key will be auto generated',
  'releasePolicies.policyForm.keyRequired': 'Key is required',
  'releasePolicies.policyForm.keyTooLong': 'Key must be at most 256 characters',
  'releasePolicies.policyForm.metricsContextKindMismatch': 'All selected metrics must share a context kind',
  'releasePolicies.policyForm.metricsSetting': 'Metrics',
  'releasePolicies.policyForm.nameLabel': 'Name',
  'releasePolicies.policyForm.nameRequired': 'Name is required',
  'releasePolicies.policyForm.nameTooLong': 'Name must be at most 256 characters',
  'releasePolicies.policyForm.releaseMethodRequired': 'Select a release method',
  'releasePolicies.policyForm.removeFieldLabel': '{label}-cancel',
  'releasePolicies.policyForm.savePolicy': 'Save policy',
  'releasePolicies.policyForm.tagsCondition': 'Tags',
  'releasePolicies.policyForm.trafficProgressionSetting': 'Traffic progression',
  'releasePolicies.policyList.defaultListLabel': 'default release policies list',
  'releasePolicies.policyList.fallbackPolicy': 'Fallback policy',
  'releasePolicies.policyList.fallbackPolicyDescription': 'Applies when no other policies match',
  'releasePolicies.policyList.flagTagsLabel': 'Flag tags',
  'releasePolicies.policyList.listLabel': 'release policies list',
  'releasePolicies.policyList.reorderErrorBody': 'The order has been reverted.',
  'releasePolicies.policyList.reorderErrorTitle': 'Failed to reorder release policies',
  'releasePolicies.policyList.reorderSuccessBody': 'The order has been updated successfully.',
  'releasePolicies.policyList.reorderSuccessTitle': 'Release policies reordered',
  'releasePolicies.releaseMethodField.guarded': 'Guarded release',
  'releasePolicies.releaseMethodField.guardedDescription':
    'Gradually increase traffic while tracking regressions in real time',
  'releasePolicies.releaseMethodField.immediate': 'Immediate',
  'releasePolicies.releaseMethodField.immediateDescription': 'Release immediately with no additional configuration',
  'releasePolicies.releaseMethodField.label': 'Select preferred release method',
  'releasePolicies.releaseMethodField.progressive': 'Progressive release',
  'releasePolicies.releaseMethodField.progressiveDescription': 'Gradually increase traffic over a period of time',
  'releasePolicies.settingsCard.createReleasePolicy': 'Create release policy',
  'releasePolicies.settingsCard.description': 'Create and edit release methods across environments.',
  'releasePolicies.settingsCard.docsLink': 'Read about releasing',
  'releasePolicies.settingsCard.loadError': 'Failed to load release policies. Try again.',
  'releasePolicies.settingsCard.title': 'Release policies',
  'releasePolicies.settingsCard.tryAgain': 'Try again',
  'releasePolicies.status.loading': 'Loading…',
  'releasePolicies.tagsField.addTags': 'Add tag(s)',
  'releasePolicies.tagsField.empty': 'No tags',
  'releasePolicies.tagsField.label': 'Tags',
  'releasePolicies.tagsField.tagGroupLabel': 'Tags',
  'releasePolicies.trafficProgressionField.label': 'Traffic progression',
  'releases.addFlags.addFlags': 'Add flags',
  'releases.addFlags.clearSearchLabel': 'clear search',
  'releases.addFlags.currentPipeline': 'Currently in this pipeline',
  'releases.addFlags.flagsMenuLabel': 'flags',
  'releases.addFlags.inPipeline': 'Currently in {pipelineName}',
  'releases.addFlags.inPipelineLoading': 'Currently in…',
  'releases.addFlags.loadingOptionsLabel': 'loading options',
  'releases.addFlags.noBooleanFlagsFound': 'No boolean flags found',
  'releases.addFlags.noFlagsFound': 'No flags found',
  'releases.addFlags.searchLabel': 'Search flags by name, key, or description',
  'releases.addFlags.searchPlaceholder': 'Search flags by name, key, or description',
  'releases.addFlags.startingReleaseLabel': 'Starting release…',
  'releases.audience.and': 'And',
  'releases.audience.approvalPending': 'Approval pending',
  'releases.audience.approvalsRequired': 'Approvals required',
  'releases.audience.approved': 'Approved',
  'releases.audience.everyone': 'everyone',
  'releases.audience.monitoring': 'Monitoring',
  'releases.audience.monitoringCompleted': 'Monitoring completed',
  'releases.audience.monitoringRequired': 'Monitoring required',
  'releases.audience.releaseInDeletedEnvironment':
    '{status, select, completed {Released} other {Release}}{strategy, select, immediate { immediately} other {}} in a deleted environment',
  'releases.audience.releaseTo':
    '{status, select, completed {Released} other {Release}}{strategy, select, immediate { immediately} other {}} to <target></target> in',
  'releases.audience.segmentCount': '{count, plural, one {# segment} other {# segments}}',
  'releases.audience.segmentSuffix': '{count, plural, one {segment} other {segments}}',
  'releases.audience.segmentsError': 'ERROR',
  'releases.audience.segmentsErrorTooltip': 'Error fetching segments. Try again.',
  'releases.audience.segmentsLoading': 'loading…',
  'releases.cancelRelease.canceledToast': 'Release canceled',
  'releases.cancelRelease.failedToast': 'Failed to cancel release. Try again later.',
  'releases.cancelReleaseDialog.cancelRelease': 'Cancel release',
  'releases.cancelReleaseDialog.close': 'Close',
  'releases.cancelReleaseDialog.confirmHint':
    'Enter <strong>{pipelineName}</strong> or <code>{pipelineKey}</code> to confirm',
  'releases.cancelReleaseDialog.confirmLabel': 'confirm',
  'releases.cancelReleaseDialog.description': 'This action will cancel the release in <strong>{pipelineName}</strong>',
  'releases.cancelReleaseDialog.title': 'Cancel release for {flagName}?',
  'releases.cancelReleaseModal.abort': 'Close',
  'releases.cancelReleaseModal.blockersIntro': 'Before you can cancel this release, you must:',
  'releases.cancelReleaseModal.cancelGuardedRollout':
    'Cancel guarded rollout on <link>{flagName}</link> in <environment></environment>',
  'releases.cancelReleaseModal.close': 'Close',
  'releases.cancelReleaseModal.confirm': 'Cancel release',
  'releases.cancelReleaseModal.deletePendingApprovals':
    'Delete <link>pending approvals</link> in <environment></environment>',
  'releases.cancelReleaseModal.deletedEnvironment': 'deleted environment',
  'releases.cancelReleaseModal.title': 'Cancel release?',
  'releases.cancelReleaseModal.unableToCancel': 'Unable to cancel release',
  'releases.cancelReleaseModal.validationLabel': "Enter the flag's name or key to confirm",
  'releases.cancelReleaseModal.warning':
    'You will lose the release history for this flag, and no further automated changes will take affect. Canceling will not affect any automated changes already made to the flag.',
  'releases.completedCard.durationTile': 'Duration',
  'releases.completedCard.healthAlertIgnored': '{metricName} alert ignored',
  'releases.completedCard.healthAllClear': 'No regressions detected',
  'releases.completedCard.healthBelowSampleSize': 'Rolled forward before reaching minimum sample size',
  'releases.completedCard.healthPhaseRow': 'Phase {number}: {text}',
  'releases.completedCard.healthRegressed': '{metricName} regressed',
  'releases.completedCard.healthRolledForwardManually': 'Rolled forward manually',
  'releases.completedCard.healthSampleRatioMismatch': 'Sample ratio mismatch detected',
  'releases.completedCard.healthTile': 'Health',
  'releases.completedCard.healthUnavailable': 'Health information unavailable',
  'releases.completedCard.phaseCount': '{count, plural, one {# phase} other {# phases}}',
  'releases.differenceChart.absoluteDifference': 'Absolute difference',
  'releases.differenceChart.notEnoughData': 'Not enough data',
  'releases.differenceChart.regressionDetected': 'Regression detected',
  'releases.differenceChart.relativeDifference': 'Relative difference',
  'releases.editPipeline.legacyDisabled': 'You cannot edit legacy release pipelines',
  'releases.environmentReleasePlans.collection.actionsColumn': 'Actions',
  'releases.environmentReleasePlans.collection.advancementAutomatic': 'Automatic',
  'releases.environmentReleasePlans.collection.advancementColumn': 'Advancement',
  'releases.environmentReleasePlans.collection.advancementManual': 'Manual',
  'releases.environmentReleasePlans.collection.configurationColumn': 'Configuration',
  'releases.environmentReleasePlans.collection.create': 'Create',
  'releases.environmentReleasePlans.collection.defaultBadge': 'Default',
  'releases.environmentReleasePlans.collection.description':
    'Environment release plans define how a flag is released within one environment and are the building blocks for the larger flag release plans.',
  'releases.environmentReleasePlans.collection.emptyStateBody':
    'Create a plan to reuse the same release steps across flags.',
  'releases.environmentReleasePlans.collection.emptyStateCreate': 'Create plan',
  'releases.environmentReleasePlans.collection.emptyStateTitle': 'No environment release plans',
  'releases.environmentReleasePlans.collection.environmentColumn': 'Environment',
  'releases.environmentReleasePlans.collection.incompleteBadge': 'Incomplete',
  'releases.environmentReleasePlans.collection.incompleteHelper': 'Finish setup to save this plan and make it usable',
  'releases.environmentReleasePlans.collection.learnMore': 'Learn more',
  'releases.environmentReleasePlans.collection.loadError': 'LaunchDarkly could not load environment release plans.',
  'releases.environmentReleasePlans.collection.loadingLabel': 'Loading environment release plans…',
  'releases.environmentReleasePlans.collection.metricCount': '{count, plural, one {# metric} other {# metrics}}',
  'releases.environmentReleasePlans.collection.metricGroupCount':
    '{count, plural, one {# metric group} other {# metric groups}}',
  'releases.environmentReleasePlans.collection.monitoringColumn': 'Monitoring',
  'releases.environmentReleasePlans.collection.monitoringNone': 'None',
  'releases.environmentReleasePlans.collection.nameColumn': 'Name',
  'releases.environmentReleasePlans.collection.phasesSummary':
    '{phaseCount, plural, one {# phase} other {# phases}} • {duration}',
  'releases.environmentReleasePlans.collection.tableLabel': 'Environment release plans',
  'releases.environmentReleasePlans.collection.title': 'Environment release plans',
  'releases.environmentReleasePlans.collection.tryAgain': 'Try again',
  'releases.environmentReleasePlans.createView.cancel': 'Cancel',
  'releases.environmentReleasePlans.createView.cloneName': '{name} (copy)',
  'releases.environmentReleasePlans.createView.cloneTitle': 'Clone {name}',
  'releases.environmentReleasePlans.createView.clonedPlanNotFound':
    "We couldn't find the plan to clone. Start from a blank plan instead.",
  'releases.environmentReleasePlans.createView.defaultErrorToastBody':
    'The plan was saved. Try setting the default again later.',
  'releases.environmentReleasePlans.createView.defaultErrorToastBodyWithReason': 'The plan was saved. {reason}',
  'releases.environmentReleasePlans.createView.defaultErrorToastTitle': 'Default release plan not updated',
  'releases.environmentReleasePlans.createView.defaultForEnvironmentLabel': 'Default for environment',
  'releases.environmentReleasePlans.createView.descriptionLabel': 'Description',
  'releases.environmentReleasePlans.createView.descriptionPlaceholder': 'Describe when to use this plan',
  'releases.environmentReleasePlans.createView.detailsHeading': 'Details',
  'releases.environmentReleasePlans.createView.environmentLabel': 'Environment',
  'releases.environmentReleasePlans.createView.environmentRequired': 'Select an environment',
  'releases.environmentReleasePlans.createView.errorToastBody':
    'The plan could not be created. Try again, and if the problem continues, contact support.',
  'releases.environmentReleasePlans.createView.errorToastTitle': 'Environment release plan not created',
  'releases.environmentReleasePlans.createView.keyLabel': 'Key',
  'releases.environmentReleasePlans.createView.keyPlaceholder': 'auto-generated',
  'releases.environmentReleasePlans.createView.missingSegmentsWarning':
    "These segments don't exist in this environment: {segments}. Update the audience before you save.",
  'releases.environmentReleasePlans.createView.nameInvalid': 'Enter a name that includes a letter or number',
  'releases.environmentReleasePlans.createView.nameLabel': 'Name',
  'releases.environmentReleasePlans.createView.nameRequired': 'Enter a name',
  'releases.environmentReleasePlans.createView.rolloutBy': 'Roll out by',
  'releases.environmentReleasePlans.createView.segmentCheckFailed':
    "We couldn't check the segments in this environment. Make sure they exist before you save.",
  'releases.environmentReleasePlans.createView.submit': 'Create plan',
  'releases.environmentReleasePlans.createView.subtitle': 'Define how a flag is released within one environment.',
  'releases.environmentReleasePlans.createView.successToast': 'Environment release plan created',
  'releases.environmentReleasePlans.createView.title': 'Create environment release plan',
  'releases.environmentReleasePlans.deleteDialog.body': 'This permanently deletes <b>{name}</b>.',
  'releases.environmentReleasePlans.deleteDialog.cancel': 'Cancel',
  'releases.environmentReleasePlans.deleteDialog.confirm': 'Delete',
  'releases.environmentReleasePlans.deleteDialog.errorToastBody': 'Try again later.',
  'releases.environmentReleasePlans.deleteDialog.errorToastTitle':
    'LaunchDarkly could not delete the environment release plan',
  'releases.environmentReleasePlans.deleteDialog.keyConfirmationLabel': 'Type <copy></copy> to confirm.',
  'releases.environmentReleasePlans.deleteDialog.successToast': 'Environment release plan deleted',
  'releases.environmentReleasePlans.deleteDialog.title': 'Delete environment release plan?',
  'releases.environmentReleasePlans.editView.errorToastBody':
    'The plan could not be updated. Try again, and if the problem continues, contact support.',
  'releases.environmentReleasePlans.editView.errorToastTitle': 'Environment release plan not updated',
  'releases.environmentReleasePlans.editView.submit': 'Save changes',
  'releases.environmentReleasePlans.editView.successToast': 'Environment release plan updated',
  'releases.environmentReleasePlans.editView.title': 'Edit environment release plan',
  'releases.environmentReleasePlans.editView.titleWithName': 'Edit {name}',
  'releases.environmentReleasePlans.flowHeader.backToReleaseSettings': 'Back to release settings',
  'releases.environmentReleasePlans.rowMenu.alreadyDefault': 'This plan is already the default for its environment',
  'releases.environmentReleasePlans.rowMenu.clone': 'Clone',
  'releases.environmentReleasePlans.rowMenu.delete': 'Delete',
  'releases.environmentReleasePlans.rowMenu.edit': 'Edit',
  'releases.environmentReleasePlans.rowMenu.incompleteDefault':
    'Finish setting up this plan before making it the default',
  'releases.environmentReleasePlans.rowMenu.menuLabel': '{name} options menu',
  'releases.environmentReleasePlans.rowMenu.setAsDefault': 'Set as default',
  'releases.environmentReleasePlans.rowMenu.setAsDefaultErrorToastBody': 'Try again later.',
  'releases.environmentReleasePlans.rowMenu.setAsDefaultErrorToastTitle': 'Default release plan not updated',
  'releases.environmentReleasePlans.rowMenu.setAsDefaultSuccessToast': 'Default release plan updated',
  'releases.environmentReleasePlans.rowMenu.triggerLabel': '{name} options',
  'releases.environmentReleasePlans.rowMenu.view': 'View',
  'releases.errorsList.fetchFailed': 'Failed to fetch errors. Retry.',
  'releases.errorsList.fetching': 'Fetching errors…',
  'releases.errorsList.fetchingLabel': 'Fetching errors',
  'releases.errorsList.itemUnavailable': 'Error item unavailable',
  'releases.errorsList.retryLabel': 'retry loading errors',
  'releases.errorsList.unavailable': 'Error detection unavailable due to metric configuration',
  'releases.errorsList.viewAll': 'View All',
  'releases.estimateTag.absoluteDifferenceLabel': 'Absolute difference:',
  'releases.estimateTag.currentAbsoluteEstimateLabel': 'Current absolute difference estimate',
  'releases.estimateTag.currentEstimateLabel': 'Current estimate',
  'releases.estimateTag.currentRelativeEstimateLabel': 'Current relative difference estimate',
  'releases.estimateTag.regressionExplanation':
    'A regression is called when the entire {interval, select, confidence {confidence} other {credible}} interval goes <strong>{direction, select, above {above} other {below}}</strong> the threshold line (<threshold>---</threshold>) at {value}',
  'releases.estimateTag.relativeDifferenceLabel': 'Relative difference:',
  'releases.flagCard.archived': 'Archived',
  'releases.flagCard.completed': 'Completed',
  'releases.flagCard.deprecated': 'Deprecated',
  'releases.flagCard.lifecycleLabel': 'flag lifecycle state',
  'releases.flagCard.servingVariations': 'Serving {variations}',
  'releases.flagCard.started': 'Started',
  'releases.flagCard.targetingOff': 'Off',
  'releases.flagCard.targetingOn': 'On',
  'releases.flagCard.targetingStatusLabel': 'targeting status',
  'releases.flagCardMenu.cancelNoPermission': 'You do not have permission to cancel this release',
  'releases.flagCardMenu.cancelRelease': 'Cancel release…',
  'releases.flagCardMenu.moveToPipeline': 'Move to pipeline',
  'releases.flagCardMenu.optionsLabel': 'release options for {flagName}',
  'releases.flagCardMenu.optionsLabelFallback': 'release options for flag',
  'releases.ignoreAlertDialog.body':
    'LaunchDarkly stops alerting you about regressions on <strong>{metricName}</strong> in this release. Monitoring continues for other metrics.',
  'releases.ignoreAlertDialog.cancel': 'Cancel',
  'releases.ignoreAlertDialog.confirm': 'Ignore alert',
  'releases.ignoreAlertDialog.errorAlert': 'Something went wrong. Try again.',
  'releases.ignoreAlertDialog.ignoring': 'Ignoring…',
  'releases.ignoreAlertDialog.title': 'Ignore alert',
  'releases.listPage.title': 'Releases',
  'releases.listPage.todo': 'TODO…',
  'releases.metricPicker.groupUnsupportedUnit': 'Metrics in this group do not support the selected randomization unit.',
  'releases.metricPicker.traceMetricsUnsupported':
    'Metrics from OpenTelemetry traces are not supported for guarded releases.',
  'releases.metricPicker.unsupportedUnit': 'This metric does not support the selected randomization unit.',
  'releases.metricPicker.warehouseMetricsUnavailable':
    'Warehouse native metrics are not available for guarded releases.',
  'releases.metricTile.absoluteDifferenceSeries': 'Absolute Difference',
  'releases.metricTile.conversionsTooltip': '<strong>{conversions}</strong> / {exposures} conversions',
  'releases.metricTile.differenceChartLabel': 'Difference chart for {metricName}',
  'releases.metricTile.estimateLabel': '(estimate):',
  'releases.metricTile.of': 'of',
  'releases.metricTile.percentagePoints': 'Percentage points',
  'releases.metricTile.percentagePointsLabel': 'percentage points',
  'releases.metricTile.relativeDifferenceSeries': 'Relative Difference',
  'releases.monitoringEmptyState.body':
    'Guarded rollouts continuously monitor feature flags and AI Configs in production. They detect regressions in real time and can automatically roll back when things go wrong. Ship with confidence and address issues before users notice.',
  'releases.monitoringEmptyState.exploreExample': 'Explore example',
  'releases.monitoringEmptyState.heading': 'Protect every release in production',
  'releases.monitoringEmptyState.readAboutLink': 'Read about guarded rollouts',
  'releases.monitoringTab.flagMeasurements': 'Flag measurements',
  'releases.monitoringTab.noMeasurements': 'No measurements have been run on this flag.',
  'releases.monitoringTab.noMonitoredRollouts': 'No monitored rollouts yet',
  'releases.monitoringTab.pastReleasesLabel': 'View past releases',
  'releases.monitoringTab.recentReleaseWindow': 'Showing releases within the past {days} days',
  'releases.monitoringTab.viewing': 'Viewing: {date}',
  'releases.monitoringTab.viewingLatest': 'Viewing: {date} (latest data)',
  'releases.moveToPipeline.archivedFlag': 'Archived flags cannot be moved',
  'releases.moveToPipeline.completedRelease': 'You cannot move a flag with a completed release',
  'releases.moveToPipeline.noPermission': 'You do not have permission to move this flag',
  'releases.moveToPipelineDialog.close': 'Close',
  'releases.moveToPipelineDialog.confirmHint':
    'Enter <strong>{pipelineName}</strong> or <copyButton>{pipelineKey}</copyButton> to confirm',
  'releases.moveToPipelineDialog.confirmLabel': 'confirm',
  'releases.moveToPipelineDialog.description':
    'This action will cancel the release in <strong>{sourcePipelineName}</strong> and move this flag to <strong>{targetPipelineName}</strong>',
  'releases.moveToPipelineDialog.loadingLabel': 'loading',
  'releases.moveToPipelineDialog.move': 'Move',
  'releases.moveToPipelineDialog.moveFailedToast': 'Failed to start release on {pipelineName}. Try again later.',
  'releases.moveToPipelineDialog.movedToast': 'Started release on {pipelineName}',
  'releases.moveToPipelineDialog.title': 'Move {flagName} to {pipelineName}?',
  'releases.moveToPipelineMenu.currentPipeline': 'Currently in this pipeline',
  'releases.moveToPipelineMenu.menuLabel': 'release pipelines menu',
  'releases.moveToPipelineMenu.noPipelinesFound': 'No release pipelines found',
  'releases.moveToPipelineMenu.searchLabel': 'Search release pipelines',
  'releases.moveToPipelineMenu.searchPlaceholder': 'Search release pipelines',
  'releases.page.breadcrumb': 'Releases',
  'releases.phase.collapseLabel': 'Collapse "{phaseName}" phase',
  'releases.phase.completedToast': 'Completed "{phaseName}" phase',
  'releases.phase.expandLabel': 'Expand "{phaseName}" phase',
  'releases.phase.legacyPipelineDisabled': 'You cannot start a phase in a legacy pipeline',
  'releases.phase.refreshToSeeTargeting': '<link>Refresh the page</link> to see targeting updates.',
  'releases.phase.startLabel': 'Start "{phaseName}" phase',
  'releases.phase.title': '{phaseName}: {status}',
  'releases.phaseBuilder.addPhase': 'Add phase',
  'releases.phaseBuilder.addStep': 'Add Step',
  'releases.phaseBuilder.audience': 'Audience',
  'releases.phaseBuilder.collapsePhaseLabel': 'collapse phase',
  'releases.phaseBuilder.deletePhaseLabel': 'delete phase',
  'releases.phaseBuilder.durationUnitsLabel': 'Duration units',
  'releases.phaseBuilder.expandPhaseLabel': 'expand phase',
  'releases.phaseBuilder.maxPhasesReached': 'Releases are limited to {max, plural, one {# phase} other {# phases}}',
  'releases.phaseBuilder.moveDownDisabledTooltip': 'This is already the last phase',
  'releases.phaseBuilder.moveDownLabel': 'Move phase down',
  'releases.phaseBuilder.moveUpDisabledTooltip': 'This is already the first phase',
  'releases.phaseBuilder.moveUpLabel': 'Move phase up',
  'releases.phaseBuilder.phaseNamePlaceholder': 'Phase {number}',
  'releases.phaseBuilder.removeStep': 'Remove step',
  'releases.phaseBuilder.rollout': 'Rollout',
  'releases.phaseBuilder.stepDurationQuantityLabel': 'Step duration quantity',
  'releases.phaseBuilder.stepDurationUnitLabel': 'Step duration unit',
  'releases.phaseBuilder.stepPercentageLabel': 'Step percentage',
  'releases.phaseColumn.active': 'Active',
  'releases.phaseColumn.environmentsLabel': 'Environments in the {phaseName} phase',
  'releases.phaseColumn.flagCount': '{count, plural, one {# flag} other {# flags}}',
  'releases.phaseColumn.flagStatusError': 'Failed to fetch flag statuses',
  'releases.phaseColumn.readyToAdvance': 'Ready to advance',
  'releases.phaseColumn.readyToStart': 'Ready to start',
  'releases.phaseColumn.released': 'Released',
  'releases.phaseStartModal.autoCompletedDescription':
    'The flag already meets this phase’s requirements, so no changes were necessary to complete the phase',
  'releases.phaseStartModal.autoCompletedToast': '"{phaseName}" phase auto-completed',
  'releases.phaseStartModal.cancel': 'Cancel',
  'releases.phaseStartModal.metricsLabel': 'Metrics to monitor',
  'releases.phaseStartModal.metricsPlaceholder': 'Select metrics',
  'releases.phaseStartModal.metricsRequired': 'Metrics are required in order to start guarded rollouts.',
  'releases.phaseStartModal.monitoringStartedToast': 'Started monitoring "{phaseName}" phase',
  'releases.phaseStartModal.refreshToSeeTargeting': '<link>Refresh the page</link> to see targeting updates.',
  'releases.phaseStartModal.releaseTo': 'Release to <target></target>',
  'releases.phaseStartModal.start': 'Start',
  'releases.phaseStartModal.startError': 'There was a problem starting the phase',
  'releases.phaseStartModal.startFailedToast': 'Failed to start "{phaseName}" phase. Try again later.',
  'releases.phaseStartModal.startedToast': 'Started "{phaseName}" phase',
  'releases.phaseStartModal.title': 'Start {phaseName}',
  'releases.phaseStatus.active': 'Active',
  'releases.phaseStatus.approvalsPending': 'Approvals pending',
  'releases.phaseStatus.completed': 'Completed',
  'releases.phaseStatus.monitoring': 'Monitoring',
  'releases.phaseStatus.notReleased': 'Released',
  'releases.phaseStatus.notStarted': 'Not started',
  'releases.phaseStatus.readyToStart': 'Ready to start',
  'releases.phaseStatus.released': 'Released',
  'releases.phaseStatus.rollingOut': 'Rolling out',
  'releases.phaseStatus.unknown': 'Unknown',
  'releases.phasedReleaseSummary.advancementAutomatic': 'Automatic',
  'releases.phasedReleaseSummary.advancementLabel': 'Phase advancement:',
  'releases.phasedReleaseSummary.advancementManual': 'Manual',
  'releases.phasedReleaseSummary.autoRollbackColumn': 'Auto rollback',
  'releases.phasedReleaseSummary.cleanupLabel': 'Cleanup phase:',
  'releases.phasedReleaseSummary.cleanupRemoveRules': 'Remove rules and update default rule to serve',
  'releases.phasedReleaseSummary.cleanupRulesRemain': 'Rules remain in place',
  'releases.phasedReleaseSummary.durationColumn': 'Duration',
  'releases.phasedReleaseSummary.durationLabel': 'Rollout duration:',
  'releases.phasedReleaseSummary.heading':
    'Add phased release <badge>{count, plural, one {# phase} other {# phases}}</badge>',
  'releases.phasedReleaseSummary.hideDetails': 'Hide details',
  'releases.phasedReleaseSummary.metricColumn': 'Metric',
  'releases.phasedReleaseSummary.metricCount': '{count, plural, one {# metric} other {# metrics}}',
  'releases.phasedReleaseSummary.metricsLabel': 'Metrics:',
  'releases.phasedReleaseSummary.no': 'No',
  'releases.phasedReleaseSummary.originalVariationLabel': 'Original variation:',
  'releases.phasedReleaseSummary.phaseNumber': 'Phase {number}',
  'releases.phasedReleaseSummary.rolloutBy': 'by',
  'releases.phasedReleaseSummary.rolloutLabel': 'Rollout:',
  'releases.phasedReleaseSummary.sharedHeading': 'Shared across all phases',
  'releases.phasedReleaseSummary.showDetails': 'Show details',
  'releases.phasedReleaseSummary.stepColumn': 'Step',
  'releases.phasedReleaseSummary.stepCount': '{count, plural, one {# step} other {# steps}}',
  'releases.phasedReleaseSummary.stepCountLabel': 'Step count',
  'releases.phasedReleaseSummary.weightColumn': 'Weight',
  'releases.phasedReleaseSummary.yes': 'Yes',
  'releases.pipelineNotFound.createReleasePipeline': 'Create release pipeline',
  'releases.pipelineNotFound.lead': "Sorry, we can't find the pipeline you're looking for.",
  'releases.pipelineNotFound.managePipelines': 'or <link>manage your pipelines</link>',
  'releases.pipelineNotFound.title': 'Release pipeline not found',
  'releases.pipelinePreview.and': 'And',
  'releases.pipelinePreview.approvalsRequired': 'Approvals required',
  'releases.pipelinePreview.collapsePhaseLabel': 'Collapse "{phaseName}" phase',
  'releases.pipelinePreview.environmentDeleted': 'Environment deleted',
  'releases.pipelinePreview.expandPhaseLabel': 'Expand "{phaseName}" phase',
  'releases.pipelinePreview.monitoringRequired': 'Monitoring required',
  'releases.pipelinePreview.releaseTo':
    'Release{strategy, select, immediate { immediately} other {}} to <target></target> in',
  'releases.pipelineSwitcher.clearSearchLabel': 'clear search',
  'releases.pipelineSwitcher.editPipeline': 'Edit release pipeline',
  'releases.pipelineSwitcher.listLabel': 'release pipelines',
  'releases.pipelineSwitcher.managePipelines': 'Manage release pipelines',
  'releases.pipelineSwitcher.noPipelinesFound': 'No release pipelines found',
  'releases.pipelineSwitcher.pipelinesHeading': 'Release pipelines',
  'releases.pipelineSwitcher.searchLabel': 'search for release pipelines',
  'releases.pipelineSwitcher.searchPlaceholder': 'Search for release pipelines',
  'releases.pipelineSwitcher.viewReleasesLabel': 'view releases for {pipelineName}',
  'releases.planReferenceSummary.startReleaseFromPlan': 'Start release from release plan {planName}',
  'releases.regressionFeedback.detailsPlaceholder': 'Additional details about the regression?',
  'releases.regressionFeedback.noDescription': 'No, this metric is not regressed',
  'releases.regressionFeedback.noLabel': 'No',
  'releases.regressionFeedback.otherLabel': 'Other',
  'releases.regressionFeedback.prompt': 'Did our system correctly identify the regression?',
  'releases.regressionFeedback.thanksToast': 'Thanks for the feedback',
  'releases.regressionFeedback.yesDescription': 'Yes, this metric is regressed',
  'releases.regressionFeedback.yesLabel': 'Yes',
  'releases.regressionStatus.insufficientSample':
    'Not enough data, analysis pending until minimum sample size is reached',
  'releases.regressionStatus.insufficientSampleSize': 'Insufficient sample size. Retry with a longer duration.',
  'releases.regressionStatus.invalidAnalysisUnit':
    'An analysis unit received more than one variation, so these results are not trustworthy. Choose an analysis unit that is more granular than the randomization unit, then start a new release.',
  'releases.regressionStatus.manuallyRolled':
    '{ruleName} manually <strong>{direction, select, back {rolled back} other {rolled forward}}</strong>',
  'releases.regressionStatus.monitoringExtended': 'Monitoring extended by {duration}',
  'releases.regressionStatus.noRegressionsRolledForward':
    'No regressions detected, <strong>{ruleName}</strong> has been rolled forward.',
  'releases.regressionStatus.noRegressionsSoFar': 'No regressions detected so far',
  'releases.regressionStatus.nowServing': 'Now serving',
  'releases.regressionStatus.nowServingSuffix': ', now serving',
  'releases.regressionStatus.rolledForward': '<strong>{ruleName}</strong> has been rolled forward.',
  'releases.regressionStatus.rolloutInterrupted': 'Rollout interrupted',
  'releases.regressionStatus.sampleRatioMismatch':
    'Sample ratio mismatch detected. These results are not valid. <link>Learn more</link>',
  'releases.regressionTimeline.alertIgnored': '{metricName} alert ignored {date}',
  'releases.regressionTimeline.eventDate': '{weekday} {month} {day} at {time}',
  'releases.regressionTimeline.guardedAgainst':
    'Guarded against a regression on <strong>{metricName}</strong> {date}. Rolled back automatically',
  'releases.regressionTimeline.hideErrors': 'Hide errors',
  'releases.regressionTimeline.ignoreAlert': 'Ignore alert',
  'releases.regressionTimeline.nowServingSuffix': ', now serving',
  'releases.regressionTimeline.regressed': '{metricName} regressed {date}',
  'releases.regressionTimeline.showErrors': 'Show errors',
  'releases.releasePlanMenu.chooseReleasePlan': 'Choose release plan',
  'releases.releasePlanMenu.createPlan': 'Create plan',
  'releases.releasePlanMenu.customRelease': 'Custom release',
  'releases.releasePlanMenu.defaultTag': 'Default',
  'releases.releasePlanMenu.environmentDefault': 'Environment default',
  'releases.releasePlanMenu.triggerLabel': 'Release plan: {name}',
  'releases.releaseProgress.completedEstimate': 'Completed: <strong>{date}</strong> (estimated)',
  'releases.releaseProgress.elapsed': '{duration} passed',
  'releases.releaseProgress.ended': 'Ended {date}',
  'releases.releaseProgress.endedAtTraffic': 'Ended at {percent}% traffic',
  'releases.releaseProgress.extensionSummary': '{elapsed} · {remaining} (extension)',
  'releases.releaseProgress.paused': 'Paused {date}',
  'releases.releaseProgress.pausedAtTraffic': 'Paused at {percent}% traffic',
  'releases.releaseProgress.phaseBanner': 'Release phase {number} of {total}',
  'releases.releaseProgress.remaining': '{duration} remaining',
  'releases.releaseProgress.started': 'Started: <strong>{date}</strong>',
  'releases.releaseProgress.stepCompleted': 'completed',
  'releases.releaseProgress.stepCurrent': 'current step',
  'releases.releaseProgress.stepIn': 'in {duration}',
  'releases.releaseProgress.stepReached': 'reached {duration} ago',
  'releases.releaseProgress.stepUpcoming': 'upcoming',
  'releases.releaseProgress.stepperLabel': 'Release progress: step {current} of {total}',
  'releases.releaseProgress.untilNextStep':
    '{step, select, next {next step} final {final step} other {completes}} in {duration}',
  'releases.releaseProgress.waitingForNextPhase': 'Waiting to start the next phase',
  'releases.releaseStatus.defaultRule': 'Default rule',
  'releases.releaseStatus.insufficientSampleRolledBack':
    '<strong>{ruleLabel}</strong> rolled back due to insufficient sample size',
  'releases.releaseStatus.monitoring': 'Monitoring <strong>{ruleLabel}</strong> for regressions…',
  'releases.releaseStatus.monitoringCompleted': 'Monitoring completed on <strong>{ruleLabel}</strong>',
  'releases.releaseStatus.notStarted':
    '<strong>{ruleLabel}</strong> is waiting for the previous audience phase to complete',
  'releases.releaseStatus.pausedInvalidAnalysisUnit':
    '<strong>{ruleLabel}</strong> paused at <strong>{percent}%</strong>: the analysis unit is invalid for this rollout',
  'releases.releaseStatus.pausedRegressions':
    '<strong>{ruleLabel}</strong> paused at <strong>{percent}%</strong>: regressions detected for <strong>{metrics}</strong>',
  'releases.releaseStatus.pausedSampleRatioMismatch':
    '<strong>{ruleLabel}</strong> paused at <strong>{percent}%</strong>: sample ratio mismatch detected',
  'releases.releaseStatus.phaseComplete': 'Phase {number} complete',
  'releases.releaseStatus.phaseNumber': 'Phase {number}',
  'releases.releaseStatus.regressionsDetected':
    'Regressions detected on <strong>{ruleLabel}</strong> for <strong>{metrics}</strong>',
  'releases.releaseStatus.releaseComplete': 'Release complete. {audience} serving',
  'releases.releaseStatus.releaseCompleted': 'Release completed on <strong>{ruleLabel}</strong>',
  'releases.releaseStatus.releaseRolledBack': 'Release rolled back on <strong>{ruleLabel}</strong>',
  'releases.releaseStatus.releasing': 'Releasing to <strong>{ruleLabel}</strong>',
  'releases.releaseStatus.rolledBackAutomatically': '<strong>{ruleLabel}</strong> rolled back automatically',
  'releases.releaseStatus.rolledBackForRegression':
    '<strong>{ruleLabel}</strong> rolled back automatically after detecting a regression for <strong>{metrics}</strong>',
  'releases.releaseStatus.rolledBackManually': '<strong>{ruleLabel}</strong> rolled back manually',
  'releases.releaseStatus.rolledForwardManually': '<strong>{ruleLabel}</strong> rolled forward manually',
  'releases.releaseStatus.rolledManually':
    '<strong>{ruleLabel}</strong> rolled {direction, select, forward {forward} other {back}} manually',
  'releases.releaseStatus.ruleNumber': 'Rule {number}',
  'releases.releaseStatus.stoppedEarly': 'Monitoring of <strong>{ruleLabel}</strong> stopped early',
  'releases.releaseStatus.targetedAudiences': 'Targeted audiences',
  'releases.releaseStatus.thisPhase': 'this phase',
  'releases.releaseSwitcher.allPhases': 'All phases',
  'releases.releaseSwitcher.allPhasesTrigger': '{dateRange} · All phases',
  'releases.releaseSwitcher.notStarted': 'Not started',
  'releases.releaseSwitcher.phaseCount': '{count, plural, one {# phase} other {# phases}}',
  'releases.releaseSwitcher.phaseNumber': 'Phase {number}',
  'releases.releaseSwitcher.phaseTrigger': '{dateRange} · Phase {number}',
  'releases.releaseSwitcher.releaseTitle': '{dateRange} release',
  'releases.releaseSwitcher.rolledOut': 'Rolled out',
  'releases.releaseSwitcher.rolloutBy': 'by',
  'releases.releaseSwitcher.singlePhaseTrigger': '{date} · Phase 1',
  'releases.releaseSwitcher.triggerLabel': '{label}: {value}',
  'releases.reviewRegression.button': 'Review regression',
  'releases.reviewRegression.buttonLabel': 'Review regression for {metricName}',
  'releases.reviewRegression.errorAlert': 'An unknown error occurred. Try again.',
  'releases.reviewRegression.submit': 'Submit',
  'releases.reviewRegression.submitting': 'Submitting',
  'releases.rolloutsEmptyState.description': 'Try adjusting your filters or selected environments',
  'releases.rolloutsEmptyState.heading': "This project doesn't have any guarded rollouts",
  'releases.rolloutsEmptyState.learnMore': 'Learn more',
  'releases.rolloutsFilter.all': 'All',
  'releases.rolloutsFilter.archived': 'Archived',
  'releases.rolloutsFilter.completed': 'Completed',
  'releases.rolloutsFilter.filters': 'Filters',
  'releases.rolloutsFilter.monitoring': 'Monitoring',
  'releases.rolloutsFilter.regressed': 'Regressed',
  'releases.rolloutsFilter.rolledBack': 'Rolled back',
  'releases.rolloutsUpsell.body':
    'Test the performance impact of your changes with progressive rollouts, and let LaunchDarkly automatically roll back releases that hurt your key metrics, keeping your app healthy without waking anyone up or disrupting the development cycle.',
  'releases.rolloutsUpsell.heading': 'Get access to <strong>Guarded Rollouts</strong>',
  'releases.rolloutsUpsell.readBlogPost': 'Read blog post',
  'releases.rolloutsUpsell.tryItOut': 'Try it out',
  'releases.startRelease.archivedFlag': 'Releases are not available for archived flags',
  'releases.startRelease.cancel': 'Cancel',
  'releases.startRelease.choosePipeline': 'Choose pipeline',
  'releases.startRelease.confirm': 'Start release',
  'releases.startRelease.createReleasePipeline': 'Create release pipeline',
  'releases.startRelease.emptyState': 'No release',
  'releases.startRelease.failedToast': 'Failed to start release for {flagName}. Try again later.',
  'releases.startRelease.failedToastFallback': 'Failed to start release for flag. Try again later.',
  'releases.startRelease.multivariateFlag': 'Releases are not available for multivariate flags',
  'releases.startRelease.noPermission': 'You do not have permission to start a release',
  'releases.startRelease.phaseCount': '{count} phases',
  'releases.startRelease.pipelineSelectLabel': 'Select release pipeline',
  'releases.startRelease.release': 'Release',
  'releases.startRelease.startRelease': 'Start release',
  'releases.startRelease.startedToast': 'Started release for {flagName}',
  'releases.startRelease.startedToastFallback': 'Started release for flag',
  'releases.statistics.active': 'active',
  'releases.statistics.completed': 'completed',
  'releases.statistics.total': 'total',
  'releases.status.loading': 'Loading…',
  'releases.stopReleaseDialog.allPhasesServe': 'All phases serve',
  'releases.stopReleaseDialog.currentPhaseVariationLabel': 'Variation to serve for the in-progress phase',
  'releases.stopReleaseDialog.partial': 'Partial',
  'releases.stopReleaseDialog.partialDescription':
    "Completed phases keep their result and phases that haven't started yet are removed. Select which variation to serve for the in-progress phase.",
  'releases.stopReleaseDialog.phaseName': 'Phase {number}',
  'releases.stopReleaseDialog.phaseStatusLabel': 'Phase status',
  'releases.stopReleaseDialog.removeRule': 'Remove rule',
  'releases.stopReleaseDialog.rollBack': 'Roll back',
  'releases.stopReleaseDialog.rollForward': 'Roll forward',
  'releases.stopReleaseDialog.serve': 'Serve',
  'releases.stopReleaseDialog.startedPhasesServe': 'Phases that have started serve',
  'releases.stopReleaseDialog.statusCompleted': 'Completed',
  'releases.stopReleaseDialog.statusInProgress': 'In progress',
  'releases.stopReleaseDialog.statusPending': 'Pending',
  'releases.stopReleaseDialog.strategyLabel': 'How do you want to stop your release?',
  'releases.stopReleaseDialog.unstartedPhasesRemoved': "; phases that haven't started yet are removed.",
  'releases.stopReleaseDialog.variationNotFound': 'Variation not found',
  'releases.summaryCard.collapseCardLabel': 'Collapse card',
  'releases.summaryCard.expandCardLabel': 'Expand card',
  'releases.summaryCard.sandboxCta': 'Ask us anything',
  'releases.summaryCard.sandboxQuestion': 'Have a question about how this works?',
  'releases.summaryCard.showAllMetrics': 'Show all metrics',
  'releases.summaryCard.showFewerMetrics': 'Show fewer metrics',
  'releases.tracker.archivedFlag': 'Releases are not available for archived flags',
  'releases.tracker.loadError': 'Failed to load',
  'releases.tracker.loadingPhasesLabel': 'loading release phases',
  'releases.tracker.pipelineLoadError': 'Failed to load release',
  'releases.tracker.released': 'Released',
  'releases.tracker.retry': 'Retry',
  'releases.tracker.retryLabel': 'retry',
  'releases.tracker.title': 'Release',
  'releases.tracker.tryAgainLabel': 'try again',
  'releases.trackerMenu.cancelNoPermission': 'You do not have permission to cancel this release',
  'releases.trackerMenu.cancelRelease': 'Cancel release',
  'releases.trackerMenu.editPipeline': 'Edit pipeline',
  'releases.trackerMenu.moveToPipeline': 'Move to pipeline',
  'releases.trackerMenu.optionsLabel': 'Release management options',
  'releases.trackerMenu.viewAllReleases': 'View all releases',
  'releases.variationDetailsTable.conversions': 'Conversions',
  'releases.variationDetailsTable.estimate': 'Estimate',
  'releases.variationDetailsTable.lower': 'Lower',
  'releases.variationDetailsTable.sampleSize': 'Sample size',
  'releases.variationDetailsTable.tableLabel': 'Variation details comparison',
  'releases.variationDetailsTable.upper': 'Upper',
  'releases.variationDetailsTable.variation': 'Variation',
  'releases.variationExposures.atTraffic': 'at <strong>{percent}%</strong> traffic',
  'releases.variationExposures.evaluations': '{count, plural, one {evaluation} other {evaluations}}',
  'releases.variationExposures.noVariationsFound': 'No variations found for this rollout.',
  'releases.variationExposures.originalVariation': 'Original variation',
  'releases.variationExposures.uniqueUnits': '<strong>{units}</strong> unique',
  'roles.additionalFilters.environmentLabel': 'Additional environment filters',
  'roles.additionalFilters.markedCritical': 'marked critical',
  'roles.additionalFilters.projectLabel': 'Additional project filters',
  'roles.additionalFilters.taggedWith': 'tagged with',
  'roles.basePermissions.cannotBeUndone': 'This cannot be undone.',
  'roles.basePermissions.checkboxLabel': 'Base permissions',
  'roles.basePermissions.description':
    'This role currently has base permissions set to <tag>{level}</tag>. Members can view all LaunchDarkly content. To block members from viewing content in LaunchDarkly until given access with a role policy, uncheck this box.',
  'roles.basePermissions.levelLabel': 'Base permissions level',
  'roles.comboBox.clearLabel': 'Clear',
  'roles.createRolePage.body': 'Scope resources and permissions that this role will have access to. To learn more,',
  'roles.createRolePage.docsLink': 'read documentation on custom roles.',
  'roles.createRolePage.errorToast': 'Failed to create role. Try again later.',
  'roles.createRolePage.successToast': 'Role created',
  'roles.criticalFilter.critical': 'Critical',
  'roles.criticalFilter.default': 'Default (neither)',
  'roles.criticalFilter.notCritical': 'Not-critical',
  'roles.editRolePage.body': 'Scope resources and permissions that this role will have access to. To learn more,',
  'roles.editRolePage.cancel': 'Cancel',
  'roles.editRolePage.delete': 'Delete',
  'roles.editRolePage.deleteConfirmBody': 'This role will be deleted permanently.',
  'roles.editRolePage.deleteErrorToast': 'Failed to delete role. Try again later.',
  'roles.editRolePage.deleteRole': 'Delete role',
  'roles.editRolePage.deleteSuccessToast': 'Role deleted',
  'roles.editRolePage.docsLink': 'read documentation on custom roles.',
  'roles.editRolePage.moreActionsLabel': 'More actions',
  'roles.editRolePage.organizationRoleDocsLink': 'Read about organization roles',
  'roles.editRolePage.predefinedOrganizationRoleAlert':
    'This is a predefined LaunchDarkly organization role. While existing statements cannot be edited, new ones can be added and scoped to a limited set of resources.',
  'roles.editRolePage.predefinedProjectRoleAlert':
    'This is a predefined LaunchDarkly project role. While existing statements cannot be edited, new ones can be added.',
  'roles.editRolePage.projectRoleDocsLink': 'Read about project roles',
  'roles.editRolePage.updateErrorToast': 'Failed to update role. Try again later.',
  'roles.editRolePage.updateSuccessToast': 'Role updated',
  'roles.effectPicker.allow': 'Allow',
  'roles.effectPicker.deny': 'Deny',
  'roles.effectPicker.label': 'Actions',
  'roles.effectPicker.placeholder': 'Select an effect',
  'roles.jsonEditor.editorLabel': 'Policy editor',
  'roles.jsonEditor.inlinePolicyHint': 'Add a policy to select and filter the events sent to {manifestName}.',
  'roles.jsonEditor.invalidJson': 'JSON is invalid',
  'roles.jsonEditor.needHelp': 'Need help writing a policy?',
  'roles.jsonEditor.notAnArray': 'Policy must be a JSON array of statements',
  'roles.jsonEditor.readDocs': 'Read <link>our documentation</link> or',
  'roles.organizationRolesList.body':
    'Organization roles are assigned when members are invited to your LaunchDarkly account. These roles can only be assigned directly to members and each member can only have one organization role. To learn more,',
  'roles.organizationRolesList.membersColumn': 'Members',
  'roles.organizationRolesList.nameColumn': 'Name',
  'roles.organizationRolesList.showBaseRoles': 'Show base roles',
  'roles.organizationRolesList.showClassicRoles': 'Show classic roles',
  'roles.organizationRolesList.tableLabel': 'Organization roles table',
  'roles.organizationRolesList.viewProjectAlert':
    'You must assign additional <code>viewProject</code> permissions separately in order for a member to access the LaunchDarkly UI.',
  'roles.pageHeader.description':
    'Scope resources and permissions that this role will have access to. To learn more view documentation on custom roles.',
  'roles.policyEditor.actionsMustBeArray': 'Actions must be an array',
  'roles.policyEditor.addStatement': 'Add statement',
  'roles.policyEditor.builderDescription': 'Create policy statements by selecting resources and associated actions.',
  'roles.policyEditor.createRole': 'Create role',
  'roles.policyEditor.createTitle': 'Create policy',
  'roles.policyEditor.creatingRoleLabel': 'Creating role',
  'roles.policyEditor.denyAllWarning': 'This policy will deny all actions to all projects, including read access.',
  'roles.policyEditor.editTitle': 'Edit policy',
  'roles.policyEditor.editorLabel': 'Editor',
  'roles.policyEditor.effectMustBeString': 'Effect must be a string',
  'roles.policyEditor.errorFallback': 'An error occurred while editing this policy. Our team has been notified.',
  'roles.policyEditor.jsonHelp': 'To learn more about writing a role policy in JSON,',
  'roles.policyEditor.jsonLabel': 'JSON',
  'roles.policyEditor.mustBeStatementArray': 'This must be an array of statements',
  'roles.policyEditor.policyBuilder': 'Policy builder',
  'roles.policyEditor.presetStatementsDescription':
    'Preset statements cannot be edited. New statements can be added to the policy in the Editor section below.',
  'roles.policyEditor.presetStatementsLabel': 'Read-only preset statements',
  'roles.policyEditor.resourcesMustBeArray': 'Resources must be an array',
  'roles.policyEditor.serviceTokenReadOnly': 'You may not change the inline policy of a service token.',
  'roles.policyEditor.updateRole': 'Update role',
  'roles.policyEditor.updatingRoleLabel': 'Updating role',
  'roles.policyEditor.viewJson': 'View JSON',
  'roles.policyErrors.criticalMismatch': 'Environments marked as critical must either all be true or all be false',
  'roles.policyErrors.errorsDescription': 'The following errors indicate that your policy is invalid',
  'roles.policyErrors.errorsHeading': 'Errors',
  'roles.policyErrors.invalidResourceType': 'Invalid resource type: {resourceType}',
  'roles.policyErrors.invalidTags': 'Invalid tags',
  'roles.policyErrors.multipleProjects': 'Multiple projects are not supported in a single statement',
  'roles.policyErrors.multipleResourceTypes': 'Multiple resource types are not supported in a single statement',
  'roles.policyErrors.multipleRoleAttributes':
    'Different role attributes per resource are not supported across statements',
  'roles.policyErrors.warningsDescription':
    'The following warnings indicate that you may not be able to switch to the UI policy editor, however, your policy will be valid',
  'roles.policyErrors.warningsHeading': 'Warnings',
  'roles.projectRolesList.body':
    'Project roles give you precise access control to everything in LaunchDarkly, including feature flags, projects, environments, metrics, and teams, so you can enforce access policies that meet your exact workflow needs.',
  'roles.projectRolesList.docsLink': 'Read about project roles',
  'roles.projectRolesList.membersColumn': 'Members',
  'roles.projectRolesList.nameColumn': 'Name',
  'roles.projectRolesList.tableLabel': 'Project roles table',
  'roles.projectRolesList.teamsColumn': 'Teams',
  'roles.readOnlyRole.basePermissions': 'Base permissions:',
  'roles.readOnlyRole.baseRole': 'Base role:',
  'roles.readOnlyRole.editLabel': 'Edit role',
  'roles.readOnlyRole.jsonHeading': 'JSON',
  'roles.readOnlyRole.manageDescription':
    'Project roles can be managed from the Roles tab in your organization settings. To learn more,',
  'roles.readOnlyRole.planEditTooltip': 'Editing a role is unavailable on your current plan',
  'roles.readOnlyRole.policySummaryHeading': 'Policy summary',
  'roles.readOnlyRole.showJson': 'View JSON',
  'roles.readOnlyRole.showPolicySummary': 'Policy summary',
  'roles.readOnlyRole.trialEditTooltip': 'Editing a role is unavailable during your LaunchDarkly trial',
  'roles.readOnlyRole.upgradeBody':
    'This role can no longer be edited. Upgrade to our paid plans to customize this role and create new project roles.',
  'roles.readOnlyRole.upgradeHeading': 'Customize roles on the paid plans',
  'roles.readOnlyRole.viewPlans': 'View plans',
  'roles.resourcePicker.actionsDialogLabel': 'Actions',
  'roles.resourcePicker.addIntegrations': 'Add integrations',
  'roles.resourcePicker.addValue': 'Add "{value}"',
  'roles.resourcePicker.aiConfigsDialogLabel': 'AI configs',
  'roles.resourcePicker.aiEvaluationsDialogLabel': 'AI evaluations',
  'roles.resourcePicker.alertsDialogLabel': 'Alerts',
  'roles.resourcePicker.allItem': 'All',
  'roles.resourcePicker.allSelected': 'All {itemKind}',
  'roles.resourcePicker.applicationVersionsDialogLabel': 'Application versions',
  'roles.resourcePicker.applicationsDialogLabel': 'Applications',
  'roles.resourcePicker.assignmentDataSourcesDialogLabel': 'Assignment data sources',
  'roles.resourcePicker.attributeDataSourcesDialogLabel': 'Attribute data sources',
  'roles.resourcePicker.contextKindsDialogLabel': 'Context kinds',
  'roles.resourcePicker.dashboardsDialogLabel': 'Dashboards',
  'roles.resourcePicker.destinationsDialogLabel': 'Destinations',
  'roles.resourcePicker.domainVerificationsDialogLabel': 'Domain verifications',
  'roles.resourcePicker.environmentReleasePlansDialogLabel': 'Environment release plans',
  'roles.resourcePicker.environmentsDialogLabel': 'Environments',
  'roles.resourcePicker.errorsDialogLabel': 'Errors',
  'roles.resourcePicker.experimentsDialogLabel': 'Experiments',
  'roles.resourcePicker.filterLabel': 'Filter items',
  'roles.resourcePicker.flagsDialogLabel': 'Flags',
  'roles.resourcePicker.holdoutsDialogLabel': 'Holdouts',
  'roles.resourcePicker.integrationIdPlaceholder': 'Enter an integration ID',
  'roles.resourcePicker.integrationsDialogLabel': 'Integrations',
  'roles.resourcePicker.layersDialogLabel': 'Layers',
  'roles.resourcePicker.loadingLabel': 'Loading…',
  'roles.resourcePicker.logsDialogLabel': 'Logs',
  'roles.resourcePicker.membersDialogLabel': 'Members',
  'roles.resourcePicker.metricDataSourcesDialogLabel': 'Metric data sources',
  'roles.resourcePicker.metricGroupsDialogLabel': 'Metric groups',
  'roles.resourcePicker.metricsDialogLabel': 'Metrics',
  'roles.resourcePicker.noResults': 'No results found',
  'roles.resourcePicker.noneSelected': 'No {itemKind} selected',
  'roles.resourcePicker.observabilityDashboardsDialogLabel': 'Observability Dashboards',
  'roles.resourcePicker.payloadFiltersDialogLabel': 'Payload filters',
  'roles.resourcePicker.pendingRequestsDialogLabel': 'Pending requests',
  'roles.resourcePicker.projectsDialogLabel': 'Projects',
  'roles.resourcePicker.relayProxyConfigsDialogLabel': 'Relay Proxy configs',
  'roles.resourcePicker.releasePipelinesDialogLabel': 'Release pipelines',
  'roles.resourcePicker.releasePoliciesDialogLabel': 'Release policies',
  'roles.resourcePicker.removeLabel': 'Remove {value}',
  'roles.resourcePicker.repositoriesDialogLabel': 'Repositories',
  'roles.resourcePicker.roleAttributeScopedTooltip':
    'This value is scoped with role attributes. To modify it, use the "Advanced" section.',
  'roles.resourcePicker.rolesDialogLabel': 'Roles',
  'roles.resourcePicker.sdkKeysDialogLabel': 'SDK keys',
  'roles.resourcePicker.searchPlaceholder': 'Search',
  'roles.resourcePicker.segmentsDialogLabel': 'Segments',
  'roles.resourcePicker.selectActions': 'Select actions',
  'roles.resourcePicker.selectAiConfigs': 'Select AI configs',
  'roles.resourcePicker.selectAiEvaluations': 'Select AI evaluations',
  'roles.resourcePicker.selectAlerts': 'Select alerts',
  'roles.resourcePicker.selectApplication': 'Select application',
  'roles.resourcePicker.selectApplicationVersions': 'Select application versions',
  'roles.resourcePicker.selectApplications': 'Select applications',
  'roles.resourcePicker.selectAssignmentDataSources': 'Select assignment data sources',
  'roles.resourcePicker.selectAttributeDataSources': 'Select attribute data sources',
  'roles.resourcePicker.selectContextKinds': 'Select context kinds',
  'roles.resourcePicker.selectDashboards': 'Select dashboards',
  'roles.resourcePicker.selectDestinations': 'Select destinations',
  'roles.resourcePicker.selectDomainVerifications': 'Select domain verifications',
  'roles.resourcePicker.selectEnvironmentReleasePlans': 'Select environment release plans',
  'roles.resourcePicker.selectEnvironments': 'Select environments',
  'roles.resourcePicker.selectErrors': 'Select errors',
  'roles.resourcePicker.selectExperiments': 'Select experiments',
  'roles.resourcePicker.selectFlags': 'Select flags',
  'roles.resourcePicker.selectHoldouts': 'Select holdouts',
  'roles.resourcePicker.selectLayers': 'Select layers',
  'roles.resourcePicker.selectLogs': 'Select logs',
  'roles.resourcePicker.selectMembers': 'Select members',
  'roles.resourcePicker.selectMetricDataSources': 'Select metric data sources',
  'roles.resourcePicker.selectMetricGroups': 'Select metric groups',
  'roles.resourcePicker.selectMetrics': 'Select metrics',
  'roles.resourcePicker.selectObservabilityDashboards': 'Select observability dashboards',
  'roles.resourcePicker.selectPayloadFilters': 'Select payload filters',
  'roles.resourcePicker.selectPendingRequests': 'Select pending requests',
  'roles.resourcePicker.selectProject': 'Select project',
  'roles.resourcePicker.selectProjects': 'Select projects',
  'roles.resourcePicker.selectRelayProxyConfigs': 'Select Relay Proxy configs',
  'roles.resourcePicker.selectReleasePipelines': 'Select release pipelines',
  'roles.resourcePicker.selectReleasePolicies': 'Select release policies',
  'roles.resourcePicker.selectRepositories': 'Select repositories',
  'roles.resourcePicker.selectRoles': 'Select roles',
  'roles.resourcePicker.selectSdkKeys': 'Select SDK keys',
  'roles.resourcePicker.selectSegments': 'Select segments',
  'roles.resourcePicker.selectSessions': 'Select sessions',
  'roles.resourcePicker.selectTags': 'Select tags',
  'roles.resourcePicker.selectTeams': 'Select teams',
  'roles.resourcePicker.selectTokens':
    '{tokenType, select, service {Select service tokens} other {Select access tokens}}',
  'roles.resourcePicker.selectTraces': 'Select traces',
  'roles.resourcePicker.selectVega': 'Select vega',
  'roles.resourcePicker.selectViews': 'Select views',
  'roles.resourcePicker.selectWebhooks': 'Select webhooks',
  'roles.resourcePicker.selectWorkflowTemplates': 'Select workflow templates',
  'roles.resourcePicker.selectedLabel': 'Selected {itemKind}',
  'roles.resourcePicker.sessionsDialogLabel': 'Sessions',
  'roles.resourcePicker.tagsDialogLabel': 'Repositories',
  'roles.resourcePicker.teamsDialogLabel': 'Teams',
  'roles.resourcePicker.tokensDialogLabel': '{tokenType, select, service {Service token} other {Access token}}',
  'roles.resourcePicker.tracesDialogLabel': 'Traces',
  'roles.resourcePicker.vegaDialogLabel': 'Vega',
  'roles.resourcePicker.viewsDialogLabel': 'Views',
  'roles.resourcePicker.webhooksDialogLabel': 'Webhooks',
  'roles.resourcePicker.workflowTemplatesDialogLabel': 'Workflow templates',
  'roles.roleAttributes.addAttributeKey': 'Add attribute key',
  'roles.roleAttributes.addResourceScope': 'Add resource scope',
  'roles.roleAttributes.appliedBadgeLabel': 'Role attributes applied',
  'roles.roleAttributes.description':
    'To assign this role to multiple teams or members requiring access to different resources, select which resource types to scope separately.',
  'roles.roleAttributes.readAboutScope': 'Read about role scope',
  'roles.roleAttributes.removeAllTooltip': 'Remove all {resourceType} scoped attributes',
  'roles.roleAttributes.removeLabel': 'Remove',
  'roles.roleAttributes.resourceAttributeKeyLabel': 'Resource attribute key:',
  'roles.roleAttributes.resourceAttributeKeyOption': 'Resource attribute key',
  'roles.roleAttributes.scopeLabel': 'Scope using attribute key',
  'roles.roleAttributes.tagAttributeKeyLabel': 'Tag attribute key:',
  'roles.roleAttributes.tagAttributeKeyOption': 'Tag attribute key',
  'roles.roleAttributes.title': 'Role attributes',
  'roles.roleAttributes.viewAttributeKeyLabel': 'View attribute key:',
  'roles.roleAttributes.viewAttributeKeyOption': 'View attribute key',
  'roles.roleCheckboxList.emptyBody': 'You don’t have any custom roles yet. <link>Manage your roles</link>',
  'roles.roleCheckboxList.listLabel': 'Custom role',
  'roles.roleDetailDrawer.loading': 'Loading…',
  'roles.roleField.customOption': 'Custom',
  'roles.roleField.customRolesLabel': '{count, plural, one {Custom role} other {Custom roles}}',
  'roles.roleField.fieldLabel': 'Role',
  'roles.roleField.inlinePolicyOption': 'Inline policy',
  'roles.roleField.selectRoleLabel': 'Select role',
  'roles.roleField.selectRolePlaceholder': 'Select a role',
  'roles.roleField.selectRolesPlaceholder': 'Select roles',
  'roles.roleForm.descriptionLabel': 'Description (optional)',
  'roles.roleForm.descriptionPlaceholder': 'Custom role provides access to…',
  'roles.roleForm.keyLabel': 'Key',
  'roles.roleForm.nameLabel': 'Name',
  'roles.roleForm.nameRequired': 'Name is a required field',
  'roles.roleForm.toggleSectionLabel': 'Toggle section',
  'roles.roleNotFound.body': "Sorry, we can't find the role you're looking for.",
  'roles.roleNotFound.createRole': 'Create role',
  'roles.roleNotFound.heading': 'Role not found',
  'roles.rolePicker.details': 'Details',
  'roles.rolePicker.loading': 'Loading',
  'roles.rolePicker.noRoles': 'No roles found',
  'roles.rolePicker.selectRole': 'Select a role',
  'roles.rolePicker.selectRoles': 'Select roles',
  'roles.roleTags.inlinePolicy': 'Inline policy',
  'roles.roleTags.organizationRolesLabel': 'organization roles',
  'roles.roleTags.projectRolesLabel': 'project roles',
  'roles.roleTags.roleLabel': 'role',
  'roles.rolesList.actions': 'Actions',
  'roles.rolesList.createRole': 'Create role',
  'roles.rolesList.editRole': 'Edit role',
  'roles.rolesList.editRoleLabel': 'Edit role {name}',
  'roles.rolesList.emptyStateBody': 'Try searching for a custom role by name or key.',
  'roles.rolesList.emptyStateHint': 'Be sure to check for typos.',
  'roles.rolesList.key': 'Key',
  'roles.rolesList.loading': 'Loading…',
  'roles.rolesList.name': 'Name',
  'roles.rolesList.searchPlaceholder': 'Find a role',
  'roles.rolesList.viewRole': 'View role',
  'roles.rolesList.viewRoleLabel': 'Details for {name}',
  'roles.rolesPage.emptyBody': 'No {roleKind} roles found. Create a new role to get started.',
  'roles.rolesPage.emptyHeading': 'No roles found',
  'roles.rolesPage.loadError': 'Something went wrong',
  'roles.rolesPage.noResultsBody': 'No {roleKind} roles match your search query',
  'roles.rolesPage.noResultsHeading': 'No results found',
  'roles.rolesPage.organization': 'Organization',
  'roles.rolesPage.organizationSearchPlaceholder': 'Find an organization role',
  'roles.rolesPage.project': 'Project',
  'roles.rolesPage.projectSearchPlaceholder': 'Find a project role',
  'roles.rolesPage.searchLabel': 'Find a role',
  'roles.scopeOperator.allOption': 'all {itemKind} *',
  'roles.scopeOperator.exceptOption': 'all {itemKind} except…',
  'roles.scopeOperator.onlyOption': 'only these {itemKind}…',
  'roles.scopeOperator.placeholder': 'Select an operation',
  'roles.scopePicker.label': 'Scope',
  'roles.scopePicker.placeholder': 'Select resource type',
  'roles.statement.actionScopeOperatorLabel': 'Action scope operator',
  'roles.statement.actionsMenuLabel': 'overflow',
  'roles.statement.adaptiveTriggersNote':
    'Members with this permission can configure adaptive triggers. Adaptive triggers change resource targeting without approval, even if approvals are required.',
  'roles.statement.collapseLabel': 'Collapse statement',
  'roles.statement.delete': 'Delete',
  'roles.statement.deleteStatement': 'Delete statement',
  'roles.statement.errorFallback':
    'An error occurred while editing this statement. Our team has been notified. To proceed, delete this statement and try again.',
  'roles.statement.expandLabel': 'Expand statement',
  'roles.statement.moveDown': 'Move down',
  'roles.statement.moveUp': 'Move up',
  'roles.statement.scopeOperatorLabel': 'Scope operator',
  'roles.statement.title': 'Statement {number}',
  'roles.statementSummary.actionsItemType': 'actions',
  'roles.statementSummary.allActions': 'All actions',
  'roles.statementSummary.allActionsExcept': 'all actions except',
  'roles.statementSummary.allExcept': 'All {itemType} except',
  'roles.statementSummary.allResources': 'All {itemType}',
  'roles.statementSummary.critical': 'Critical',
  'roles.statementSummary.endingWith': '{itemType} ending with',
  'roles.statementSummary.in': 'in',
  'roles.statementSummary.inViews': 'in <strong>{count, plural, one {View} other {Views}}</strong> {keys}',
  'roles.statementSummary.matchingPattern': '{itemType} matching this pattern',
  'roles.statementSummary.notCritical': 'Not-critical',
  'roles.statementSummary.onlyThese': 'Only these {itemType}',
  'roles.statementSummary.onlyTheseActions': 'only these actions',
  'roles.statementSummary.startingWith': '{itemType} starting with',
  'roles.statementSummary.tagged': 'tagged',
  'roles.validation.actionOperatorRequired': 'Action operator is required',
  'roles.validation.actionsConflict': 'Use actions or notActions, not both',
  'roles.validation.actionsRequired': 'Actions are required',
  'roles.validation.applicationsFilterRequired': 'Applications filter is required',
  'roles.validation.effectRequired': 'Effect is required',
  'roles.validation.environmentsFilterRequired': 'Environments filter is required',
  'roles.validation.keyRequired': 'Key is required',
  'roles.validation.membersFilterRequired': 'Members filter is required',
  'roles.validation.nameRequired': 'Name is required',
  'roles.validation.nameTooLong': 'Name must be {max} characters or less',
  'roles.validation.projectsFilterRequired': 'Projects filter is required',
  'roles.validation.resourceOperatorRequired': 'Resource operator is required',
  'roles.validation.resourceTypeRequired': 'Add a resource type.',
  'roles.validation.resourcesConflict': 'Use resources or notResources, not both',
  'roles.validation.resourcesRequired': 'Resources are required',
  'roles.validation.roleAttributeKeyRequired': 'Role attribute key is required',
  'roles.validation.roleAttributeTagKeyRequired': 'Role attribute tag key is required',
  'roles.validation.roleAttributeViewKeyRequired': 'Role attribute view key is required',
  'roles.validation.statementRequired': 'At least one statement is required',
  'rollback.currentVersion.badge': 'Current',
  'rollback.currentVersion.tooltip': 'This is the current state of the resource',
  'rollback.flagActions.restoreLabel': 'Restore previous version',
  'rollback.previewButton.previewLabel': 'Preview',
  'rollback.previewButton.unavailableLabel': 'Not available',
  'rollback.previewButton.unavailableTooltip': 'Rollback unavailable: {reason}',
  'sandbox.banner.body': 'Safely explore all LaunchDarkly features',
  'sandbox.banner.getDemo': 'Get demo',
  'sandbox.banner.label': 'Sandbox',
  'sandbox.banner.readDocs': 'Read docs',
  'sandbox.banner.startTrial': 'Start free trial',
  'sandbox.signUpDialog.body':
    'Play with feature flags, experiments, and guarded rollouts—all in a risk-free environment, pre-filled with everything you need. Enter your email below to explore the sandbox and stay updated on new features and best practices.',
  'sandbox.signUpDialog.emailLabel': 'Email',
  'sandbox.signUpDialog.heading': 'LaunchDarkly Sandbox',
  'sandbox.signUpDialog.submit': 'Start exploring',
  'sdkEvents.detailsPanel.applicationLabel': 'Application',
  'sdkEvents.detailsPanel.closeLabel': 'Close details',
  'sdkEvents.detailsPanel.contextAttributes': 'Context attributes',
  'sdkEvents.detailsPanel.contextLabel': 'Context',
  'sdkEvents.detailsPanel.notReported': 'Not reported',
  'sdkEvents.detailsPanel.panelLabel': 'Event details',
  'sdkEvents.detailsPanel.rawFields': 'Raw fields',
  'sdkEvents.detailsPanel.rawFieldsLabel': 'Raw event fields',
  'sdkEvents.detailsPanel.sdkLabel': 'SDK',
  'sdkEvents.detailsPanel.summaryContexts':
    "Summary events combine evaluations from all contexts, so individual contexts aren't included. <link>Read more about SDK events</link>",
  'sdkEvents.detailsPanel.timeLabel': 'Time',
  'sdkEvents.detailsPanel.typeLabel': 'Type',
  'sdkEvents.detailsPanel.unknownTime': 'Unknown time',
  'sdkEvents.detailsPanel.untitledEvent': 'Untitled event',
  'sdkEvents.detailsPanel.variationLabel': 'Variation',
  'sdkEvents.diagnosticSummary.findings': 'Findings',
  'sdkEvents.diagnosticSummary.firstRunFound':
    'No events in {environmentName}. The project has {flagCount, plural, one {# flag} other {# flags}} configured.',
  'sdkEvents.diagnosticSummary.firstRunFoundWithOtherEnvironments':
    'No events in {environmentName}, and none in the {otherEnvironmentCount, plural, one {# other environment} other {# other environments}} checked. The project has {flagCount, plural, one {# flag} other {# flags}} configured.',
  'sdkEvents.diagnosticSummary.firstRunMeans':
    'No SDK has reported activity anywhere in this project yet. This looks like first-time setup. Use the steps below to initialize an SDK with the SDK key for <strong>{environmentName}</strong> and start sending events.',
  'sdkEvents.diagnosticSummary.mostlyFrom': 'Mostly from {sdkNames}.',
  'sdkEvents.diagnosticSummary.noFlagsFound':
    'No events anywhere in this project over the last 7 days, and no flags are configured.',
  'sdkEvents.diagnosticSummary.noFlagsMeans':
    'SDKs only emit events when they evaluate flags or track custom events. Create your first flag, then call a variation method (or <code>track()</code>) to start seeing events here.',
  'sdkEvents.diagnosticSummary.otherEnvironmentFound':
    '{count, plural, one {# other environment} other {# other environments}} received events: {environmentNames}.',
  'sdkEvents.diagnosticSummary.otherEnvironmentFoundOfChecked':
    '{found} of {checked} other environments received events: {environmentNames}.',
  'sdkEvents.diagnosticSummary.otherEnvironmentMeans':
    'Your SDK is sending events to another environment instead of <strong>{environmentName}</strong>. The most common cause is initializing the SDK with a different environment’s SDK key. Check that your app uses the SDK key for {environmentName}.',
  'sdkEvents.diagnosticSummary.recentlyActiveFound':
    '{count, plural, one {# event} other {# events}} arrived in {environmentName} over the last 7 days. The most recent was {relativeTime}.',
  'sdkEvents.diagnosticSummary.recentlyActiveMeans':
    'Your SDK is configured correctly, but activity stopped recently. Common causes include a deploy, restart, or quiet window for real user traffic. If you expect continuous activity, check that your app or service is still running and reachable.',
  'sdkEvents.diagnosticSummary.summaryLabel': 'Diagnostic summary',
  'sdkEvents.diagnosticSummary.whatItMeans': 'What it means',
  'sdkEvents.diagnostics.fetchFailed': 'Failed to fetch SDK event diagnostics',
  'sdkEvents.diagnostics.sectionLabel': 'Event diagnostics',
  'sdkEvents.diagnosticsChecklist.activityFound': 'Activity found in {found} of {checked}: {environmentNames}',
  'sdkEvents.diagnosticsChecklist.baselineStep': 'No matching events in the last hour',
  'sdkEvents.diagnosticsChecklist.checkFailed': 'Could not complete this check',
  'sdkEvents.diagnosticsChecklist.checkingLast7Days': 'Checking the last 7 days in {environmentName}',
  'sdkEvents.diagnosticsChecklist.crossEnvironmentStep': 'Looking for events in other environments…',
  'sdkEvents.diagnosticsChecklist.done': 'done',
  'sdkEvents.diagnosticsChecklist.extendedRangeStep': 'Widening the search for events…',
  'sdkEvents.diagnosticsChecklist.flagCountStep': 'Checking for configured flags…',
  'sdkEvents.diagnosticsChecklist.foundEvents':
    'Found {count, plural, one {# event} other {# events}} in the last 7 days',
  'sdkEvents.diagnosticsChecklist.heading': 'Diagnostic results',
  'sdkEvents.diagnosticsChecklist.inProgress': 'in progress',
  'sdkEvents.diagnosticsChecklist.lookingForFlags': 'Looking for any flags in {projectKey}',
  'sdkEvents.diagnosticsChecklist.noEventsInOtherEnvironments':
    'No events in {count, plural, one {# other environment} other {# other environments}}',
  'sdkEvents.diagnosticsChecklist.noEventsLast7Days': 'No events in the last 7 days either',
  'sdkEvents.diagnosticsChecklist.onlyEnvironment': 'Only environment in this project. No other environments to check',
  'sdkEvents.diagnosticsChecklist.pending': 'pending',
  'sdkEvents.diagnosticsChecklist.projectHasFlags': 'Project has {count, plural, one {# flag} other {# flags}}',
  'sdkEvents.diagnosticsChecklist.projectHasNoFlags': 'Project has no flags yet',
  'sdkEvents.diagnosticsChecklist.queryingOtherEnvironments': 'Querying {count} other environments',
  'sdkEvents.diagnosticsChecklist.refreshing': 'Refreshing…',
  'sdkEvents.diagnosticsChecklist.skipped': 'skipped',
  'sdkEvents.diagnosticsChecklist.skippedCauseIdentified': 'Skipped. Cause already identified',
  'sdkEvents.diagnosticsChecklist.skippedStep': 'Skipped',
  'sdkEvents.diagnosticsChecklist.unableToRun': 'Unable to run diagnostics',
  'sdkEvents.diagnosticsChecklist.unavailable': 'Diagnostics unavailable.',
  'sdkEvents.evaluationReason.bigSegmentNotConfigured':
    'Big Segments: The SDK is not configured for Big Segments, or the segment data is missing. The evaluation is likely incorrect.',
  'sdkEvents.evaluationReason.bigSegmentStale':
    "Big Segments: The store is reachable, but data hasn't been refreshed recently. Results may be outdated.",
  'sdkEvents.evaluationReason.bigSegmentStoreError': 'Big Segments: The store is unreachable or returned an error.',
  'sdkEvents.evaluationReason.error': 'Error',
  'sdkEvents.evaluationReason.errorClientNotReady':
    'The SDK client was not yet initialized when the evaluation was requested.',
  'sdkEvents.evaluationReason.errorDescription':
    'An error occurred during evaluation. The SDK returned the application-provided default value.',
  'sdkEvents.evaluationReason.errorException': 'An unexpected exception occurred during evaluation.',
  'sdkEvents.evaluationReason.errorFlagNotFound': 'The flag key does not exist in the environment.',
  'sdkEvents.evaluationReason.errorMalformedFlag': 'The flag configuration is invalid or corrupted.',
  'sdkEvents.evaluationReason.errorUserNotSpecified':
    'No evaluation context (user) was provided to the variation call.',
  'sdkEvents.evaluationReason.errorWrongType': 'The application called the wrong type-specific variation method.',
  'sdkEvents.evaluationReason.fallthrough': 'Default rule',
  'sdkEvents.evaluationReason.fallthroughDescription':
    "Flag targeting is on, but the context didn't match any targets or rules. The SDK served the default rule variation.",
  'sdkEvents.evaluationReason.inExperimentDescription':
    'This evaluation was part of a LaunchDarkly experiment. Full evaluation events are sent so experiment data is captured accurately.',
  'sdkEvents.evaluationReason.off': 'Off',
  'sdkEvents.evaluationReason.offDescription':
    "Flag targeting is turned off. The SDK returned the flag's off variation.",
  'sdkEvents.evaluationReason.prerequisiteFailed': 'Prerequisite failed',
  'sdkEvents.evaluationReason.prerequisiteFailedDescription':
    'A prerequisite flag did not return the expected variation, so this flag could not be evaluated normally.',
  'sdkEvents.evaluationReason.ruleMatch': 'Rule match',
  'sdkEvents.evaluationReason.ruleMatchDescription': "The context matched one of the flag's targeting rules.",
  'sdkEvents.evaluationReason.targetMatch': 'Target match',
  'sdkEvents.evaluationReason.targetMatchDescription':
    'The context matched a specific context target in the flag configuration.',
  'sdkEvents.eventType.click': 'Click',
  'sdkEvents.eventType.clickDescription': 'Click interaction event',
  'sdkEvents.eventType.custom': 'Custom',
  'sdkEvents.eventType.customDescription': 'Custom analytics event',
  'sdkEvents.eventType.feature': 'Feature',
  'sdkEvents.eventType.featureDescription': 'Per-evaluation flag event',
  'sdkEvents.eventType.pageView': 'Page view',
  'sdkEvents.eventType.pageViewDescription': 'Page view event',
  'sdkEvents.eventType.summary': 'Summary',
  'sdkEvents.eventType.summaryDescription': 'Aggregated evaluation summary',
  'sdkEvents.eventVolumeCard.emptyDescription': 'Events appear here as your SDKs evaluate flags or track events.',
  'sdkEvents.eventVolumeCard.emptyFilteredHeading': 'No events matching the filters',
  'sdkEvents.eventVolumeCard.emptyHeading': 'No events in this time range',
  'sdkEvents.eventVolumeCard.events': 'events',
  'sdkEvents.eventVolumeCard.eventsInWindow': 'events in window',
  'sdkEvents.eventVolumeCard.eventsPerMinute': 'events / min',
  'sdkEvents.eventVolumeCard.eventsPerSecond': 'events / sec',
  'sdkEvents.eventVolumeCard.seriesName': 'Event volume',
  'sdkEvents.eventVolumeCard.title': 'Event volume',
  'sdkEvents.eventVolumeCard.total': '{count} total',
  'sdkEvents.eventsTable.bigSegmentSuffix': 'Big segment: {status}',
  'sdkEvents.eventsTable.clearFilters': 'Clear filters',
  'sdkEvents.eventsTable.clicked': 'Clicked',
  'sdkEvents.eventsTable.contextColumn': 'Context',
  'sdkEvents.eventsTable.emptyDescription':
    'Events appear here as your SDKs evaluate flags or track events. Try widening the time range.',
  'sdkEvents.eventsTable.emptyHeading': 'No events in this time range',
  'sdkEvents.eventsTable.evaluatedAs': 'Evaluated as',
  'sdkEvents.eventsTable.eventColumn': 'Event',
  'sdkEvents.eventsTable.fetchFailed': 'Failed to fetch SDK event rows',
  'sdkEvents.eventsTable.inExperimentSuffix': 'In experiment',
  'sdkEvents.eventsTable.keyColumn': 'Key',
  'sdkEvents.eventsTable.loadErrorDescription': 'Something went wrong while loading events. Try again.',
  'sdkEvents.eventsTable.loadErrorHeading': 'LaunchDarkly could not load events',
  'sdkEvents.eventsTable.loadingEvents': 'Loading events…',
  'sdkEvents.eventsTable.newEvents': '{count}+ new events',
  'sdkEvents.eventsTable.noMatchDescription': 'Try adjusting your filters',
  'sdkEvents.eventsTable.noMatchHeading': 'No events match your filter criteria',
  'sdkEvents.eventsTable.sdkColumn': 'SDK',
  'sdkEvents.eventsTable.tableLabel': 'Recent SDK events. {count, plural, one {# row} other {# rows}}.',
  'sdkEvents.eventsTable.timeColumn': 'Time',
  'sdkEvents.eventsTable.tracked': 'Tracked',
  'sdkEvents.eventsTable.typeColumn': 'Type',
  'sdkEvents.filtersMenu.all': 'All',
  'sdkEvents.filtersMenu.allSdks': 'All SDKs',
  'sdkEvents.filtersMenu.clear': 'Clear',
  'sdkEvents.filtersMenu.eventType': 'Event type',
  'sdkEvents.filtersMenu.filters': 'Filters',
  'sdkEvents.filtersMenu.filtersActiveLabel': 'Filters, {count} active',
  'sdkEvents.filtersMenu.noSdkActivity': 'No SDK activity in this time range',
  'sdkEvents.filtersMenu.only': 'Only',
  'sdkEvents.filtersMenu.sdk': 'SDK',
  'sdkEvents.filtersMenu.sdkLabel': 'Filter by SDK',
  'sdkEvents.initialLoading.description':
    'Loading the most recent activity from your SDKs. This usually takes a moment.',
  'sdkEvents.initialLoading.heading': 'Loading SDK events…',
  'sdkEvents.initialLoading.statusLabel': 'Loading SDK events',
  'sdkEvents.noFlags.createFlag': 'Create flag',
  'sdkEvents.noFlags.openSdkSetup': 'Open SDK setup',
  'sdkEvents.otherEnvironment.lead':
    'If you expected events in {environmentName}, check your SDK setup against this environment.',
  'sdkEvents.otherEnvironment.openEventsIn': 'Open events in {environmentName}',
  'sdkEvents.queryBar.clearSearchLabel': 'Clear search',
  'sdkEvents.queryBar.controlsLabel': 'SDK events query controls',
  'sdkEvents.queryBar.liveTooltip': 'New events load every 30 seconds. Select to pause.',
  'sdkEvents.queryBar.pausedTooltip': 'Showing a fixed time window. Select to resume and jump to now.',
  'sdkEvents.queryBar.searchLabel': 'Search by flag key, context key, or event key',
  'sdkEvents.queryBar.searchPlaceholder': 'Search by flag key, context key, or event key…',
  'sdkEvents.recentlyActive.lastEvent': 'Last event {relativeTime} in {environmentName}',
  'sdkEvents.recentlyActive.readTroubleshooting': 'Read event troubleshooting',
  'sdkEvents.recentlyActive.showLast7Days': 'Show last 7 days',
  'sdkEvents.recentlyActive.topSdksLabel': 'Top SDKs seen recently',
  'sdkEvents.sdkDistributionCard.activeCount': '{count} active',
  'sdkEvents.sdkDistributionCard.activeCountTruncated': '{count}+ active',
  'sdkEvents.sdkDistributionCard.clearFilter': 'Clear SDK filter',
  'sdkEvents.sdkDistributionCard.emptyDescription': 'SDK activity appears here after your SDKs start sending events.',
  'sdkEvents.sdkDistributionCard.emptyFilteredHeading': 'No SDK activity matching the filters',
  'sdkEvents.sdkDistributionCard.emptyHeading': 'No SDK activity in this time range',
  'sdkEvents.sdkDistributionCard.filterBySdkLabel': 'Filter events by {sdkName}',
  'sdkEvents.sdkDistributionCard.noActivity': 'no activity',
  'sdkEvents.sdkDistributionCard.other': 'Other',
  'sdkEvents.sdkDistributionCard.otherTooltip': '{count, plural, one {# other SDK} other {# other SDKs}}',
  'sdkEvents.sdkDistributionCard.otherTooltipTruncated': '{count}+ other SDKs',
  'sdkEvents.sdkDistributionCard.title': 'SDK distribution',
  'sdkEvents.setupSteps.checkForEvents': 'Check for events',
  'sdkEvents.setupSteps.checking': 'Checking…',
  'sdkEvents.setupSteps.configureKey': 'Configure with {environmentName}’s SDK key',
  'sdkEvents.setupSteps.configureKeyText':
    'Copy the key from <link>Environment keys</link> and pass it to your SDK during initialization.',
  'sdkEvents.setupSteps.evaluateFlag': 'Evaluate a flag or track an event',
  'sdkEvents.setupSteps.evaluateFlagText':
    'After your SDK starts running, its events appear here. Use the time range and filters to narrow results.',
  'sdkEvents.setupSteps.installSdk': 'Install an SDK',
  'sdkEvents.setupSteps.installSdkText':
    'Choose a server- or client-side SDK in the <link>SDK reference</link> and follow the setup steps for your platform.',
  'sdkEvents.setupSteps.liveRefreshHint': 'This page also refreshes automatically every 30 seconds while it’s Live.',
  'sdkEvents.setupSteps.openSetupGuide': 'Open setup guide',
  'sdkEvents.setupSteps.pausedRefreshHint': 'Automatic refresh is paused while you view a fixed time window.',
  'sdkEvents.unknownFlagsCard.aboutLabel': 'About misconfigured flag keys',
  'sdkEvents.unknownFlagsCard.aboutTooltip':
    'A flag key evaluated by an SDK that does not map to a live flag in LaunchDarkly. This usually means the key in code is misspelled, or the flag was archived or deleted.',
  'sdkEvents.unknownFlagsCard.archivedGroup': 'Archived flags',
  'sdkEvents.unknownFlagsCard.archivedGroupDescription':
    'The flag key still appears in SDK evaluations, but the flag is archived in LaunchDarkly.',
  'sdkEvents.unknownFlagsCard.archivedSelected':
    'These flag keys still appear in SDK evaluations, but the flags are archived in LaunchDarkly.',
  'sdkEvents.unknownFlagsCard.categoryLabel': '{label}: {description}',
  'sdkEvents.unknownFlagsCard.emptyDescription': 'Every flag key your SDKs evaluated maps to a live, active flag.',
  'sdkEvents.unknownFlagsCard.emptyFilteredHeading': 'No misconfigured flag keys matching the filters',
  'sdkEvents.unknownFlagsCard.emptyHeading': 'No misconfigured flag keys in this time range',
  'sdkEvents.unknownFlagsCard.evals': '{count} evals',
  'sdkEvents.unknownFlagsCard.filterAll': 'All',
  'sdkEvents.unknownFlagsCard.filterArchived': 'Archived',
  'sdkEvents.unknownFlagsCard.filterKnown': 'Known',
  'sdkEvents.unknownFlagsCard.filterLabel': 'Filter unknown flags',
  'sdkEvents.unknownFlagsCard.filterSdkConfig': 'SDK config',
  'sdkEvents.unknownFlagsCard.filterUnknown': 'Unknown',
  'sdkEvents.unknownFlagsCard.flagKeyCount':
    '{count, plural, one {{count, number, ::compact-short .#} flag key} other {{count, number, ::compact-short .#} flag keys}}',
  'sdkEvents.unknownFlagsCard.knownGroup': 'Known flags with unknown evals',
  'sdkEvents.unknownFlagsCard.knownGroupDescription':
    'The flag exists and appears available to this SDK type, so these may be stale SDK/store evaluations.',
  'sdkEvents.unknownFlagsCard.knownSelected':
    'These flags exist and are available to their SDK types. They may be stale SDK or store evaluations.',
  'sdkEvents.unknownFlagsCard.none': 'none',
  'sdkEvents.unknownFlagsCard.sdkConfigGroup': 'Active flags not configured for this SDK',
  'sdkEvents.unknownFlagsCard.sdkConfigGroupDescription':
    'The flag exists, but its client-side SDK availability does not match the SDK that evaluated it.',
  'sdkEvents.unknownFlagsCard.sdkConfigSelected':
    'These flags exist, but their client-side SDK availability does not match the SDKs evaluating them.',
  'sdkEvents.unknownFlagsCard.title': 'Misconfigured flag keys',
  'sdkEvents.unknownFlagsCard.unknownGroup': 'Unknown or deleted flags',
  'sdkEvents.unknownFlagsCard.unknownGroupDescription':
    'No current flag was found for this key. The key may be misspelled, deleted, or unavailable.',
  'sdkEvents.unknownFlagsCard.unknownSelected':
    'No current flags were found for these keys. They may be misspelled, deleted, or unavailable.',
  'sdkEvents.zeroState.daysAgo': '{count, plural, one {# day ago} other {# days ago}}',
  'sdkEvents.zeroState.hoursAgo': '{count, plural, one {# hour ago} other {# hours ago}}',
  'sdkEvents.zeroState.justNow': 'just now',
  'sdkEvents.zeroState.minutesAgo': '{count, plural, one {# minute ago} other {# minutes ago}}',
  'sdkEvents.zeroState.secondsAgo': '{count} seconds ago',
  'sdkEvents.zeroStateFooter.hideSetupHelper': 'Hide setup helper',
  'sdkEvents.zeroStateFooter.lastRan': 'Diagnostics last ran {relativeTime}',
  'sdkEvents.zeroStateFooter.runAgain': 'Run diagnostics again',
  'sdkEvents.zeroStateFooter.running': 'Running…',
  'sdkEvents.zeroStateHero.checkingDescription': 'Looking for activity across this environment and others.',
  'sdkEvents.zeroStateHero.checkingHeading': 'Checking for SDK events…',
  'sdkEvents.zeroStateHero.noEventsYetDescription':
    'Events appear here within a few minutes after an SDK initialized with this environment’s SDK key starts evaluating flags or tracking custom events.',
  'sdkEvents.zeroStateHero.noEventsYetHeading': 'No SDK events in {environmentName} yet',
  'sdkEvents.zeroStateHero.noFlagsDescription':
    'SDKs only emit events when they evaluate flags or track custom events.',
  'sdkEvents.zeroStateHero.noFlagsHeading': 'This project has no flags yet',
  'sdkEvents.zeroStateHero.otherEnvironmentDescription':
    'This usually means the SDK is configured with a different environment’s SDK key.',
  'sdkEvents.zeroStateHero.otherEnvironmentHeading':
    'Events are arriving in another environment, not {environmentName}',
  'sdkEvents.zeroStateHero.recentlyActiveDescription':
    'The drop is recent. Your SDK is configured, but traffic has paused.',
  'sdkEvents.zeroStateHero.recentlyActiveHeading': 'Your SDK was active recently, but no events in the last hour',
  'sdkKeys.clientSideIdsSection.description':
    'Use a client-side ID to connect your JavaScript or edge SDK. It does not need to be kept secret, but unlike other keys, it cannot be reset.',
  'sdkKeys.clientSideIdsSection.title': 'Client-side IDs',
  'sdkKeys.createDialog.cancel': 'Cancel',
  'sdkKeys.createDialog.closeLabel': 'Close',
  'sdkKeys.createDialog.create': 'Create',
  'sdkKeys.createDialog.createError': 'Failed to create key',
  'sdkKeys.createDialog.created': '{kind, select, sdk {SDK} other {Mobile}} key created',
  'sdkKeys.createDialog.createdWithCustomPayload':
    '{kind, select, sdk {SDK} other {Mobile}} key created with custom payload',
  'sdkKeys.createDialog.descriptionLabel': 'Description',
  'sdkKeys.createDialog.keyLabel': 'Key',
  'sdkKeys.createDialog.keyRequired': 'Key is required',
  'sdkKeys.createDialog.nameLabel': 'Name',
  'sdkKeys.createDialog.nameRequired': 'Name is required',
  'sdkKeys.createDialog.nameTooLong': 'Name must be no more than 100 characters',
  'sdkKeys.createDialog.title': 'Create {kind, select, sdk {SDK} other {mobile}} key',
  'sdkKeys.customPayloadBadge.customPayload': 'Custom payload',
  'sdkKeys.customPayloadBadge.label': 'Custom payload for {sdkKeyName}',
  'sdkKeys.customPayloadPopover.flags': 'Flags',
  'sdkKeys.customPayloadPopover.heading': 'Custom payload for {sdkKeyName}',
  'sdkKeys.customPayloadPopover.segments': 'Segments',
  'sdkKeys.customPayloadPopover.totalResources': 'Total resources',
  'sdkKeys.customPayloadPopover.views': 'Views',
  'sdkKeys.customPayloadPopover.viewsListLabel': 'Views for {sdkKeyName}',
  'sdkKeys.deleteDialog.body': 'This key will be invalidated immediately. This cannot be undone. Be certain.',
  'sdkKeys.deleteDialog.cancel': 'Cancel',
  'sdkKeys.deleteDialog.confirm': 'Delete key',
  'sdkKeys.deleteDialog.deleteError': 'Failed to delete key',
  'sdkKeys.deleteDialog.deleted': 'Key deleted',
  'sdkKeys.deleteDialog.title': 'Delete key',
  'sdkKeys.editDialog.cancel': 'Cancel',
  'sdkKeys.editDialog.closeLabel': 'Close',
  'sdkKeys.editDialog.continue': 'Continue',
  'sdkKeys.editDialog.copyKeyLabel':
    'Copy the {kind, select, mobile {mobile} other {SDK}} key ({key}) to your clipboard.',
  'sdkKeys.editDialog.defaultKeyPayloadLocked': "The payload for the default SDK key can't be changed",
  'sdkKeys.editDialog.descriptionLabel': 'Description',
  'sdkKeys.editDialog.descriptionPlaceholder': 'Enter a description for your SDK key',
  'sdkKeys.editDialog.expirationLabel': 'Expiration',
  'sdkKeys.editDialog.expirationRequired': 'Expiration is required',
  'sdkKeys.editDialog.keyLabel': 'Key',
  'sdkKeys.editDialog.nameLabel': 'Name',
  'sdkKeys.editDialog.namePlaceholder': "Give this key a name to describe what it's used for",
  'sdkKeys.editDialog.nameRequired': 'Name is required',
  'sdkKeys.editDialog.payloadChangeWarning':
    'Changing the payload will immediately affect all SDKs using this key. This may cause unexpected behavior.',
  'sdkKeys.editDialog.payloadPermissionLocked': "Your permissions don't allow you to change this key's payload",
  'sdkKeys.editDialog.save': 'Save',
  'sdkKeys.editDialog.title': 'Edit {kind, select, mobile {mobile} other {SDK}} key',
  'sdkKeys.editDialog.updateError': 'Failed to update SDK key',
  'sdkKeys.editDialog.updateKey': 'Update key',
  'sdkKeys.editDialog.updated': 'SDK key updated',
  'sdkKeys.emptyState.body': 'Select a project and environment to view your SDK keys.',
  'sdkKeys.emptyState.title': 'No keys to show',
  'sdkKeys.environmentPicker.loadingLabel': 'Loading environments',
  'sdkKeys.environmentPicker.menuLabel': 'Environment selector menu',
  'sdkKeys.environmentPicker.searchLabel': 'Search environments',
  'sdkKeys.environmentPicker.triggerLoadingLabel': 'Loading…',
  'sdkKeys.filterEmptyState.body': 'Clear the filter to see all SDK keys in this environment.',
  'sdkKeys.filterEmptyState.clearFilter': 'Clear filter',
  'sdkKeys.filterEmptyState.title': 'No SDK keys are using this view',
  'sdkKeys.keyActions.cannotActOnDefaultKey':
    'Cannot {action, select, delete {delete} other {expire}} the default key when no non-expiring keys remain',
  'sdkKeys.keyActions.cannotActOnOnlyKey':
    'Cannot {action, select, delete {delete} other {expire}} the only active key',
  'sdkKeys.keyActions.copyPropertiesToNewKey': 'Copy properties to new key',
  'sdkKeys.keyActions.defaultKeyWillChange':
    'If you {action, select, delete {delete} other {expire}} this default key, your oldest non-expiring active key will become the default key',
  'sdkKeys.keyActions.deleteKey': 'Delete key',
  'sdkKeys.keyActions.edit': 'Edit',
  'sdkKeys.keyActions.editName': 'Edit name',
  'sdkKeys.keyActions.menuLabel': 'Key actions',
  'sdkKeys.keyActions.moreActionsLabel': 'More actions',
  'sdkKeys.keyActions.noPermission': 'Your permissions do not allow you to perform this action',
  'sdkKeys.keyActions.setExpiration': 'Set expiration',
  'sdkKeys.keyRow.copied': '{name} copied to clipboard',
  'sdkKeys.keyRow.copyLabel': 'Copy to clipboard',
  'sdkKeys.keyRow.created': 'Created <time>{date}</time>',
  'sdkKeys.keyRow.default': 'Default',
  'sdkKeys.keyRow.defaultStatusLabel': 'Default status',
  'sdkKeys.keyRow.expired': 'Expired {date}',
  'sdkKeys.keyRow.expiresInDays': '{count, plural, one {Expires in # day} other {Expires in # days}}',
  'sdkKeys.keyRow.expiresInHours': '{count, plural, one {Expires in # hour} other {Expires in # hours}}',
  'sdkKeys.keySection.expiredCount': '{count, plural, one {# expired key} other {# expired keys}}',
  'sdkKeys.keySection.loadingLabel': 'Loading {kind, select, sdk {SDK keys} other {mobile keys}}',
  'sdkKeys.keySection.loadingMoreExpiredLabel': 'Loading more expired keys',
  'sdkKeys.keySection.noAccess':
    'You do not have permission to view {kind, select, sdk {SDK keys} other {mobile keys}} for this environment.',
  'sdkKeys.keySection.noKeysMatch': 'No {kind, select, sdk {SDK keys} other {mobile keys}} match your search',
  'sdkKeys.keySection.noKeysYet': 'No {kind, select, sdk {SDK keys} other {mobile keys}} yet',
  'sdkKeys.keySection.toggleExpiredLabel': 'Toggle expired keys',
  'sdkKeys.keysModal.body': 'Use these SDK keys to configure LaunchDarkly.',
  'sdkKeys.keysModal.closeLabel': 'close SDK Keys modal',
  'sdkKeys.keysModal.heading': 'SDK keys',
  'sdkKeys.keysModal.learnMore': 'Learn more.',
  'sdkKeys.keysModal.loadingLabel': 'Loading…',
  'sdkKeys.keysPanel.loadingLabel': 'Loading…',
  'sdkKeys.keysPanel.selectAnEnvironment': 'Select an environment to view this key',
  'sdkKeys.keysTable.environmentColumn': 'Environment',
  'sdkKeys.keysTable.loadError': 'Couldn’t load environment keys',
  'sdkKeys.keysTable.loadingLabel': 'Loading environment keys',
  'sdkKeys.keysTable.tableLabel': 'Environment keys',
  'sdkKeys.mobileAvailabilityAlert.customPayloadBody':
    "Flags won't be included in the payload if that flag doesn't have the <strong>Available on mobile SDKs</strong> option selected.",
  'sdkKeys.mobileAvailabilityAlert.defaultPayloadBody':
    "{count, plural, one {# flag won't be included in the payload because it hasn't been made available on mobile SDKs.} other {# flags won't be included in the payload because they haven't been made available on mobile SDKs.}}",
  'sdkKeys.mobileAvailabilityAlert.learnMore': 'Learn more',
  'sdkKeys.mobileKeyChangeDialog.affectedPayloads': 'Affected payloads',
  'sdkKeys.mobileKeyChangeDialog.alert':
    '{direction, select, enable {Enabling} other {Disabling}} this flag will {direction, select, enable {increase} other {decrease}} payload size for mobile SDKs using <views></views>. Review the affected keys below before confirming.',
  'sdkKeys.mobileKeyChangeDialog.approxFlagCount': '{direction, select, enable {+1 flag} other {-1 flag}}',
  'sdkKeys.mobileKeyChangeDialog.emptyState': 'No mobile SDK keys are affected by this change.',
  'sdkKeys.mobileKeyChangeDialog.flagCount': '{count, plural, one {# flag} other {# flags}}',
  'sdkKeys.mobileKeyChangeDialog.keyListLabel': 'Mobile SDK keys in {envName}',
  'sdkKeys.mobileKeyChangeDialog.loadError': 'Couldn’t load affected payloads. Try again.',
  'sdkKeys.mobileKeyChangeDialog.loadingLabel': 'Loading affected payloads',
  'sdkKeys.mobileKeysSection.createMobileKey': 'Create mobile key',
  'sdkKeys.mobileKeysSection.description':
    'Use a mobile key with client-side SDKs designed for mobile. It does not need to be kept secret, but you can still reset it if needed.',
  'sdkKeys.mobileKeysSection.title': 'Mobile keys',
  'sdkKeys.page.chooseProjectAndEnvironment': 'Choose project and environment',
  'sdkKeys.page.loadingLabel': 'Loading SDK keys',
  'sdkKeys.page.selectADifferentProject': 'Select a different project above to view its SDK keys.',
  'sdkKeys.payloadChangeDialog.cancel': 'Cancel',
  'sdkKeys.payloadChangeDialog.closeLabel': 'Close',
  'sdkKeys.payloadChangeDialog.confirm': 'Update',
  'sdkKeys.payloadChangeDialog.defaultPayload': 'Default payload',
  'sdkKeys.payloadChangeDialog.dialogLabel': 'Payload changes',
  'sdkKeys.payloadChangeDialog.diffRowLabel': '{label} changing from {before} to {after}',
  'sdkKeys.payloadChangeDialog.diffRowValueLabel': '{label}: {value}',
  'sdkKeys.payloadChangeDialog.flagCount': '{count, plural, one {# flag} other {# flags}}',
  'sdkKeys.payloadChangeDialog.heading': 'Review before updating',
  'sdkKeys.payloadChangeDialog.segmentCount': '{count, plural, one {# segment} other {# segments}}',
  'sdkKeys.payloadChangeDialog.totalFlagsRow': 'Total flags',
  'sdkKeys.payloadChangeDialog.totalSegmentsRow': 'Total segments',
  'sdkKeys.payloadChangeDialog.updatingLabel': 'Updating…',
  'sdkKeys.payloadChangeDialog.viewCount': '{count, plural, one {# view} other {# views}}',
  'sdkKeys.payloadChangeDialog.viewsRow': 'Views',
  'sdkKeys.payloadChangeDialog.warningBody':
    'All SDKs using this key will be affected. Review how many are using it before confirming to avoid unexpected behavior.',
  'sdkKeys.payloadChangeDialog.warningHeading': 'This change takes effect immediately',
  'sdkKeys.payloadSection.addViewsLabel': 'Add views',
  'sdkKeys.payloadSection.beta': 'Beta',
  'sdkKeys.payloadSection.customPayload': 'Custom payload',
  'sdkKeys.payloadSection.customPayloadHelper': 'Include only the flags and segments in selected views.',
  'sdkKeys.payloadSection.defaultPayload': 'Default payload',
  'sdkKeys.payloadSection.defaultPayloadHelper': 'Include all flags and segments in the project.',
  'sdkKeys.payloadSection.featureStatusLabel': 'Feature status',
  'sdkKeys.payloadSection.groupLabel': 'Payload',
  'sdkKeys.payloadSection.heading': 'Payload',
  'sdkKeys.payloadSection.helper': 'Choose which flags and segments this key delivers.',
  'sdkKeys.payloadSection.readAbout': 'Read about filtered SDK payloads',
  'sdkKeys.payloadSection.viewLimitError':
    'Custom payloads can include up to {maxViews, plural, one {# view} other {# views}}. Remove {overLimit, plural, one {# view} other {# views}} to continue.',
  'sdkKeys.projectPicker.loadingLabel': 'Loading projects',
  'sdkKeys.projectPicker.menuLabel': 'Projects',
  'sdkKeys.projectPicker.searchLabel': 'Search projects',
  'sdkKeys.projectPicker.triggerLoadingLabel': 'Loading…',
  'sdkKeys.relayProxyWarning.body':
    "The Relay Proxy only supports default SDK keys. This key won't work with the Relay Proxy. <link>Read about the Relay Proxy</link>",
  'sdkKeys.sdkKeysSection.createSdkKey': 'Create SDK key',
  'sdkKeys.sdkKeysSection.description':
    "Use an SDK key to connect your server-side or AI SDK. Keep it secret. If it's ever exposed, you can reset it.",
  'sdkKeys.sdkKeysSection.title': 'SDK keys',
  'sdkKeys.sdkPicker.listLabel': 'Select SDK',
  'sdkKeys.sdkPicker.placeholder': 'Filter by SDK',
  'sdkKeys.sdkPicker.triggerLabel': 'select an SDK',
  'sdkKeys.status.loading': 'Loading…',
  'sdkKeys.toolbar.searchLabel': 'Search SDK and mobile keys',
  'sdkKeys.toolbar.searchPlaceholder': 'Search by name or key',
  'sdkKeys.viewCreator.createAndContinue': 'Create & continue',
  'sdkKeys.viewFilter.allViews': 'All views',
  'sdkKeys.viewFilter.clearLabel': 'Clear view filter',
  'sdkKeys.viewFilter.clearTooltip': 'All views',
  'sdkKeys.viewFilter.viewRemoved': 'Filter cleared — view no longer exists',
  'sdkKeys.viewResourceChangeDialog.addAlert':
    'Adding {noun} to the <views></views> {viewCount, plural, one {view} other {views}} will affect all SDK keys using {viewCount, plural, one {it} other {them}}. This may cause unexpected behavior.',
  'sdkKeys.viewResourceChangeDialog.affectedPayloads': 'Affected payloads',
  'sdkKeys.viewResourceChangeDialog.approxFlagDelta':
    '{direction, select, add {+} other {-}}{count, plural, one {# flag} other {# flags}}',
  'sdkKeys.viewResourceChangeDialog.approxSegmentDelta':
    '{direction, select, add {+} other {-}}{count, plural, one {# segment} other {# segments}}',
  'sdkKeys.viewResourceChangeDialog.emptyState': 'No SDK keys are affected by this change.',
  'sdkKeys.viewResourceChangeDialog.flagCount': '{count, plural, one {# flag} other {# flags}}',
  'sdkKeys.viewResourceChangeDialog.flagsNoun': 'flags',
  'sdkKeys.viewResourceChangeDialog.keyListLabel': 'SDK keys in {envName}',
  'sdkKeys.viewResourceChangeDialog.loadError': 'Couldn’t load affected payloads. Try again.',
  'sdkKeys.viewResourceChangeDialog.loadingLabel': 'Loading affected payloads',
  'sdkKeys.viewResourceChangeDialog.removeAlert':
    'Removing {noun} from the <views></views> {viewCount, plural, one {view} other {views}} will affect all SDK keys using {viewCount, plural, one {it} other {them}}. This may cause unexpected behavior.',
  'sdkKeys.viewResourceChangeDialog.segmentCount': '{count, plural, one {# segment} other {# segments}}',
  'sdkKeys.viewResourceChangeDialog.segmentsNoun': 'segments',
  'sdkKeys.viewResourceChangeDialog.updateAlert':
    'Updating {noun} on the <views></views> {viewCount, plural, one {view} other {views}} will affect all SDK keys using {viewCount, plural, one {it} other {them}}. This may cause unexpected behavior.',
  'sdkSetup.addToCode.needHelp': 'Need help? <link>Read the documentation</link>.',
  'sdkSetup.addToCode.noSnippet': 'To use this SDK, <link>read the documentation</link>.',
  'sdkSetup.addToCode.runBody': 'Confirm that it still works as expected.',
  'sdkSetup.addToCode.runHeading': '2. Run your application',
  'sdkSetup.addToCode.sdkReference': '{sdkName} SDK reference',
  'sdkSetup.addToCode.selectLanguage': 'Select language',
  'sdkSetup.addToCode.turnOnBody': 'Now turn on your flag from the <link>Targeting tab.</link>',
  'sdkSetup.addToCode.turnOnHeading': '3. Turn on your flag',
  'sdkSetup.addToCode.wrapHeading': '1. Wrap your feature with this code',
  'sdkSetup.addToCodeTargetingNote.addFlagToCode': 'Add flag to code',
  'sdkSetup.addToCodeTargetingNote.body':
    'Nothing in your application evaluates this flag yet, so no traffic reaches these rules.',
  'sdkSetup.agentKeys.clientSideId': 'Client-side ID',
  'sdkSetup.agentKeys.copyClientSideId': 'Copy client-side ID',
  'sdkSetup.agentKeys.copyProjectKey': 'Copy project key',
  'sdkSetup.agentKeys.intro': 'Give these to your agent if it asks for them.',
  'sdkSetup.agentKeys.loadError': 'LaunchDarkly could not load your client-side ID.',
  'sdkSetup.agentKeys.nameColumn': 'Name',
  'sdkSetup.agentKeys.otherEnvironmentsIntro':
    "Each environment has its own client-side ID. To connect a different environment, give your agent that environment's ID instead.",
  'sdkSetup.agentKeys.projectKey': 'Project key',
  'sdkSetup.agentKeys.retry': 'Try again',
  'sdkSetup.agentKeys.tableLabel': 'Project key and client-side ID',
  'sdkSetup.agentKeys.valueColumn': 'Value',
  'sdkSetup.agentProgress.allStepsVerified': 'All steps verified',
  'sdkSetup.agentProgress.doneStatus': ', done',
  'sdkSetup.agentProgress.firstDataDetail':
    'Explore your <sessions>Sessions</sessions>, <errors>Errors</errors>, <logs>Logs</logs>, and <traces>Traces</traces>.',
  'sdkSetup.agentProgress.firstDataTitle': 'Sessions, errors, logs, and traces received',
  'sdkSetup.agentProgress.firstEventDetail': 'LaunchDarkly is receiving data from your app.',
  'sdkSetup.agentProgress.firstEventTitle': 'First event received',
  'sdkSetup.agentProgress.firstFlagDetail': 'Your app evaluates hello-world, a boolean flag, when it starts.',
  'sdkSetup.agentProgress.firstFlagTitle': 'First flag created',
  'sdkSetup.agentProgress.heading': "Track your agent's progress",
  'sdkSetup.agentProgress.inProgressHelper': 'Progress appears here as your agent works. Usually 3–5 minutes.',
  'sdkSetup.agentProgress.pendingStatus': ', pending',
  'sdkSetup.agentProgress.stalledNoDataHelper':
    'Your app is connected. Sessions, errors, logs, and traces can take a few minutes to appear.',
  'sdkSetup.agentProgress.stalledNoFlagHelper':
    'Your app connected, but no flag has been created yet. Check your agent finished the setup.',
  'sdkSetup.agentProgress.waitingForPrompt': 'Waiting for you to send the prompt.',
  'sdkSetup.agentPrompt.copy': 'Copy',
  'sdkSetup.agentPrompt.heading': "Paste this prompt into your AI coding agent's chat",
  'sdkSetup.awsDevOpsAgent.agentSetupDescription':
    'The command uses the region from your AWS profile. If you already have an Agent Space, it connects to it. If not, it creates one.',
  'sdkSetup.awsDevOpsAgent.agentSetupHeading': 'Set up the DevOps Agent',
  'sdkSetup.awsDevOpsAgent.back': 'Back',
  'sdkSetup.awsDevOpsAgent.continueToSdk': 'Install the SDK',
  'sdkSetup.awsDevOpsAgent.description':
    'The AWS DevOps Agent is the release and operations agent from AWS, and it runs in your AWS account. Install the LaunchDarkly CLI, then run one command to connect the agent to LaunchDarkly.',
  'sdkSetup.awsDevOpsAgent.heading': 'Connect your AWS DevOps Agent',
  'sdkSetup.awsDevOpsAgent.installCliDescription':
    'Logging in stores the access token the CLI uses to register the LaunchDarkly MCP server with your agent.',
  'sdkSetup.awsDevOpsAgent.installCliHeading': 'Install the LaunchDarkly CLI and log in',
  'sdkSetup.awsDevOpsAgent.installSdkDescription':
    'The agent can manage your flags now, but you need the SDK to use them.',
  'sdkSetup.awsDevOpsAgent.installSdkHeading': 'Install the SDK in your app',
  'sdkSetup.awsDevOpsAgent.next': 'Next',
  'sdkSetup.awsDevOpsAgent.sdkSectionHeading': 'Install the SDK in your app',
  'sdkSetup.awsDevOpsAgent.terminalTab': 'Terminal',
  'sdkSetup.awsDevOpsAgentKiro.addFilesAgentPurpose': 'What Kiro may change',
  'sdkSetup.awsDevOpsAgentKiro.addFilesBody':
    'Commit these to your default branch. Kiro follows the agent file, and the agent starts the two workflows.',
  'sdkSetup.awsDevOpsAgentKiro.addFilesBranchNote':
    'If your default branch isn’t <code>main</code>, change <code>--base main</code> in <code>kiro-implement.yml</code>.',
  'sdkSetup.awsDevOpsAgentKiro.addFilesCopied': 'File copied',
  'sdkSetup.awsDevOpsAgentKiro.addFilesCopy': 'Copy {path}',
  'sdkSetup.awsDevOpsAgentKiro.addFilesHide': 'Hide',
  'sdkSetup.awsDevOpsAgentKiro.addFilesHideLabel': 'Hide {path}',
  'sdkSetup.awsDevOpsAgentKiro.addFilesImplementPurpose': 'Runs Kiro and opens a pull request',
  'sdkSetup.awsDevOpsAgentKiro.addFilesMergePurpose': 'Merges after review',
  'sdkSetup.awsDevOpsAgentKiro.addFilesTitle': 'Add the 3 Kiro files',
  'sdkSetup.awsDevOpsAgentKiro.addFilesView': 'View',
  'sdkSetup.awsDevOpsAgentKiro.addFilesViewLabel': 'View {path}',
  'sdkSetup.awsDevOpsAgentKiro.addRepositoryLink': 'Open the AWS DevOps Agent console',
  'sdkSetup.awsDevOpsAgentKiro.addRepositoryNote': 'The agent then reviews each pull request in this repository.',
  'sdkSetup.awsDevOpsAgentKiro.addRepositoryStepOpen':
    'In your Agent Space, open <b>Capabilities → Pipeline → Add → GitHub</b>.',
  'sdkSetup.awsDevOpsAgentKiro.addRepositoryStepPick':
    'Pick your GitHub registration and your repository, then choose <b>Add</b>.',
  'sdkSetup.awsDevOpsAgentKiro.addRepositoryTitle': 'Add your repository to the Agent Space',
  'sdkSetup.awsDevOpsAgentKiro.allowPullRequestsBody':
    'Under <b>Settings → Actions → General → Workflow permissions</b>, turn on <b>Allow GitHub Actions to create and approve pull requests</b> and save.',
  'sdkSetup.awsDevOpsAgentKiro.allowPullRequestsHint':
    'Is the checkbox locked? Your org turns it off. An org owner can turn it on in the organization’s Actions settings.',
  'sdkSetup.awsDevOpsAgentKiro.allowPullRequestsLink': 'How to change this setting',
  'sdkSetup.awsDevOpsAgentKiro.allowPullRequestsTitle': 'Let GitHub Actions open pull requests',
  'sdkSetup.awsDevOpsAgentKiro.connectGithubBody':
    'Register your GitHub org with AWS. You do this once per AWS account.',
  'sdkSetup.awsDevOpsAgentKiro.connectGithubLink': 'Open GitHub registration',
  'sdkSetup.awsDevOpsAgentKiro.connectGithubStepAuthorize':
    'In GitHub, authorize the app and install it on your repository only.',
  'sdkSetup.awsDevOpsAgentKiro.connectGithubStepOrganization': 'Choose <b>Organization</b> and enter your org name.',
  'sdkSetup.awsDevOpsAgentKiro.connectGithubStepSubmit': 'Choose <b>Submit</b>.',
  'sdkSetup.awsDevOpsAgentKiro.connectGithubTitle': 'Connect GitHub to the agent',
  'sdkSetup.awsDevOpsAgentKiro.copyTooltip': 'Copy',
  'sdkSetup.awsDevOpsAgentKiro.deployAndProtectBranch':
    'Then protect your default branch: require a pull request and passing status checks.',
  'sdkSetup.awsDevOpsAgentKiro.deployAndProtectLink': 'How to protect a branch',
  'sdkSetup.awsDevOpsAgentKiro.deployAndProtectNote':
    'Anyone who can push to that branch can change what the agent is allowed to do.',
  'sdkSetup.awsDevOpsAgentKiro.deployAndProtectTitle': 'Let the agent deploy, and protect your default branch',
  'sdkSetup.awsDevOpsAgentKiro.deployAndProtectTrigger':
    'In your existing deploy workflow, add <code>workflow_dispatch</code> under <code>on:</code>. Keep your other triggers.',
  'sdkSetup.awsDevOpsAgentKiro.heading': 'Set up Kiro',
  'sdkSetup.awsDevOpsAgentKiro.intro':
    'Let the agent ship code, too. Kiro writes each flagged change in GitHub Actions, and the agent reviews, merges, and deploys it.',
  'sdkSetup.awsDevOpsAgentKiro.optionalTag': 'Optional',
  'sdkSetup.awsDevOpsAgentKiro.placeAwsConsole': 'AWS console',
  'sdkSetup.awsDevOpsAgentKiro.placeGithub': 'GitHub',
  'sdkSetup.awsDevOpsAgentKiro.placeKiroAndGithub': 'Kiro, GitHub',
  'sdkSetup.awsDevOpsAgentKiro.placeRepo': 'Your repo',
  'sdkSetup.awsDevOpsAgentKiro.requirementGithubOwner': '<muted>You need:</muted> Owner access to your GitHub org',
  'sdkSetup.awsDevOpsAgentKiro.requirementKiroPlan': '<muted>You need:</muted> Kiro Pro or higher',
  'sdkSetup.awsDevOpsAgentKiro.requirementOncePerRepo': '<muted>You do it:</muted> Once per repository',
  'sdkSetup.awsDevOpsAgentKiro.skipForNow': 'Skip for now',
  'sdkSetup.awsDevOpsAgentKiro.skipToSdk': 'Skip to SDK',
  'sdkSetup.awsDevOpsAgentKiro.skippedTag': 'Skipped',
  'sdkSetup.awsDevOpsAgentKiro.start': 'Set up Kiro',
  'sdkSetup.awsDevOpsAgentKiro.storeApiKeyBody':
    'Kiro runs in GitHub Actions with no one signed in, so it uses an API key.',
  'sdkSetup.awsDevOpsAgentKiro.storeApiKeyCopied': 'Secret name copied',
  'sdkSetup.awsDevOpsAgentKiro.storeApiKeyCopy': 'Copy {name}',
  'sdkSetup.awsDevOpsAgentKiro.storeApiKeyLink': 'How to add a repository secret',
  'sdkSetup.awsDevOpsAgentKiro.storeApiKeyStepCreate': 'Make a key in your <link>Kiro account settings</link>.',
  'sdkSetup.awsDevOpsAgentKiro.storeApiKeyStepSecret': 'Add it as a repository secret named <chip>{name}</chip>.',
  'sdkSetup.awsDevOpsAgentKiro.storeApiKeyTitle': 'Store your Kiro API key in GitHub',
  'sdkSetup.awsDevOpsAgentKiro.withoutKiro':
    'Without Kiro, the agent still manages flags, experiments, and rollouts. You write the code changes.',
  'sdkSetup.cliCommand.heading': 'Run this in your project directory',
  'sdkSetup.connectWithAi.agentDescription':
    'Paste this prompt into your agent. It runs the LaunchDarkly onboarding skill to complete setup.',
  'sdkSetup.connectWithAi.agentTitle': 'Connect via agent',
  'sdkSetup.connectWithAi.cliDescription':
    'Install the LaunchDarkly CLI to set up your app and manage flags from the command line.',
  'sdkSetup.connectWithAi.cliTitle': 'Connect via CLI',
  'sdkSetup.connectWithAi.description':
    'Each option below is a complete way in, so pick the one that fits how you work.',
  'sdkSetup.connectWithAi.heading': 'Choose how to connect',
  'sdkSetup.connectWithAi.mcpClientLabel': 'Select MCP client',
  'sdkSetup.connectWithAi.mcpDescription':
    'Pick your AI client, then use the snippet below to connect it to LaunchDarkly.',
  'sdkSetup.connectWithAi.mcpTitle': 'Connect via MCP',
  'sdkSetup.connectWithAi.tabsLabel': 'Ways to connect with AI',
  'sdkSetup.connectionStatus.connectedBody': 'Your first event arrived.',
  'sdkSetup.connectionStatus.connectedBodyWithCta':
    "Your first event arrived. <link>Create your first flag</link> when you're ready.",
  'sdkSetup.connectionStatus.connectedLegacy': "Connected. You're ready to build with LaunchDarkly.",
  'sdkSetup.connectionStatus.connectedLegacyWithCta':
    "Connected. You're ready to build with LaunchDarkly, <link>create your first flag</link>.",
  'sdkSetup.connectionStatus.connectedSummary': 'SDK connected. Your first event arrived.',
  'sdkSetup.connectionStatus.connectedSummaryWithCta':
    "SDK connected. Your first event arrived. <link>Create your first flag</link> when you're ready.",
  'sdkSetup.connectionStatus.connectedTitle': 'SDK connected',
  'sdkSetup.connectionStatus.errorActions':
    '<tryAgain>Try again</tryAgain> or <contactSupport>contact Support</contactSupport>.',
  'sdkSetup.connectionStatus.errorTitle': 'Error checking your connection',
  'sdkSetup.connectionStatus.sessionReplayConnectedBody':
    'Your app is sending data to LaunchDarkly. Explore your <sessions>Sessions</sessions>, <errors>Errors</errors>, <logs>Logs</logs>, and <traces>Traces</traces>.',
  'sdkSetup.connectionStatus.stalledAgentHelper':
    "Haven't detected your app yet. Check your agent finished installing and your app is running.",
  'sdkSetup.connectionStatus.stalledCliHelper':
    "Haven't detected your app yet. Check the setup command finished and your app is running.",
  'sdkSetup.connectionStatus.stalledFlutterHelper':
    "Haven't detected your app yet. For desktop and mobile, use the Mobile key <code>{mobileKey}</code>. For web, use the Client-side ID <code>{clientSideId}</code>.",
  'sdkSetup.connectionStatus.stalledKeyHelper':
    "Haven't detected your app yet. Did you use the correct {keyLabel} for the <strong>{environmentName}</strong> environment? Your {keyLabel} is <code>{key}</code>.",
  'sdkSetup.connectionStatus.stillWatchingTitle': 'Still watching for your first event…',
  'sdkSetup.connectionStatus.watchingHelper':
    'Install the SDK and run your app. Events can take up to a minute to appear.',
  'sdkSetup.connectionStatus.watchingHelperLegacy':
    'Install the SDK and run your app. Can take up to a minute to detect.',
  'sdkSetup.connectionStatus.watchingTitle': 'Waiting for your first event…',
  'sdkSetup.copyToClipboard.copied': 'Copied',
  'sdkSetup.copyToClipboard.copiedWithText': "''{text}'' copied to clipboard.",
  'sdkSetup.copyToClipboard.copyFailedAnnouncement': 'Failed to copy',
  'sdkSetup.copyToClipboard.copyFailedToast': 'Unable to copy',
  'sdkSetup.copyToClipboard.copyTooltip': 'Copy to clipboard',
  'sdkSetup.credentialName.clientSideId': 'Client-side ID',
  'sdkSetup.credentialName.credentials': 'credentials',
  'sdkSetup.credentialName.generic': 'key',
  'sdkSetup.credentialName.mobileKey': 'Mobile key',
  'sdkSetup.credentialName.sdkKey': 'SDK key',
  'sdkSetup.cursorInstall.copiedToast': 'Prompt copied to clipboard. Paste it into Cursor.',
  'sdkSetup.cursorInstall.installWithCursor': 'Install SDK with Cursor',
  'sdkSetup.cursorInstall.tooltip': 'Click to open Cursor.',
  'sdkSetup.envVarKeys.body':
    'One build, every stage: the snippets, prompts, and PRs below read <code>LAUNCHDARKLY_*</code> env vars. Set each deploy stage’s value from this table.',
  'sdkSetup.envVarKeys.pickSingleEnvironment': 'Prefer to pick a single environment instead?',
  'sdkSetup.envVarKeys.tableCaption': 'Every environment gets its own keys and flag states.',
  'sdkSetup.environmentPicker.envVarHint': 'Shipping one build to every stage?',
  'sdkSetup.environmentPicker.label': 'Environment',
  'sdkSetup.environmentPicker.useEnvVars': 'Use environment variables instead',
  'sdkSetup.experimentationSnippet.additionalSetup':
    'Some architectures need additional setup to track experiments accurately. <link>Read about common cases</link>',
  'sdkSetup.experimentationSnippet.description':
    'LaunchDarkly attributes each event to the flag variations the context saw, which is how experiments and guarded releases are measured. <link>Read about metrics</link>',
  'sdkSetup.experimentationSnippet.heading': 'Send metric events',
  'sdkSetup.experimentationSnippet.warehouseNative':
    'Warehouse native experiments do not require SDKs. <link>Read about warehouse native experimentation</link>',
  'sdkSetup.initInstructions.followTheSteps': 'Follow the steps on <link>{sdkName} docs</link>.',
  'sdkSetup.installNotes.androidX':
    "The SDK uses <androidxLink>AndroidX from Jetpack</androidxLink>. If your project does not use AndroidX, read <migrationLink>Android's migration guide</migrationLink>.",
  'sdkSetup.installNotes.goAlternative': 'Alternatively, you can run the following command:',
  'sdkSetup.installNotes.goModules':
    'If you are using the standard <code>Go</code> modules system, import the SDK packages in your code and <code>go build</code> will automatically download them. The SDK and its dependencies are modules.',
  'sdkSetup.installNotes.reactNativeAsyncStorage':
    "LaunchDarkly's React Native SDK v10 uses <link>@react-native-async-storage/async-storage</link> for bootstrapping. If you are <strong>not using Expo</strong>, you must explicitly add it as a dependency.",
  'sdkSetup.manualSteps.back': 'Back',
  'sdkSetup.manualSteps.chooseSdkDescription':
    'Pick the SDK that matches the language or framework your application uses.',
  'sdkSetup.manualSteps.chooseSdkHeading': 'Choose your SDK',
  'sdkSetup.manualSteps.initializeHeading': 'Initialize the SDK',
  'sdkSetup.manualSteps.installDescription': 'Add the {sdkName} package to your project.',
  'sdkSetup.manualSteps.installHeading': 'Install the package',
  'sdkSetup.manualSteps.metricsHeading': 'Send metric events',
  'sdkSetup.manualSteps.next': 'Next',
  'sdkSetup.manualSteps.noInstallNeeded': "This SDK doesn't require a package install. Continue to initialization.",
  'sdkSetup.manualSteps.optionalTag': 'Optional',
  'sdkSetup.manualSteps.readDocs': 'Need help? Read the <link>{sdkName} SDK docs.</link>',
  'sdkSetup.manualTab.initializeHeading': 'Initialize the SDK',
  'sdkSetup.manualTab.installHeading': 'Install the package',
  'sdkSetup.manualTab.showExperimentationCode':
    'Show code for <strong>Experimentation</strong> and <strong>Guarded releases</strong>',
  'sdkSetup.methodPicker.aiDescription': 'Connect via MCP, a tailored agent prompt, or the CLI.',
  'sdkSetup.methodPicker.aiTitle': 'Install with AI',
  'sdkSetup.methodPicker.label': 'Choose how to install your SDK',
  'sdkSetup.methodPicker.manualDescription': 'Pick your SDK and follow the install steps with copyable snippets.',
  'sdkSetup.methodPicker.manualTitle': 'Install manually',
  'sdkSetup.misconfig.copyCredential': 'Copy the {environmentName} {credentialLabel}',
  'sdkSetup.misconfig.openLiveEvents': 'Open Live events',
  'sdkSetup.misconfig.silentFailureBody':
    'No events have arrived from any environment in this project. Common causes like an invalid SDK key or a stopped app leave nothing to detect. Start by checking your SDK setup.',
  'sdkSetup.misconfig.silentFailureTitle': 'Still waiting for your first event',
  'sdkSetup.misconfig.wrongEnvironmentBody':
    "Your app is sending events to {detectedEnvironmentName}, not {currentEnvironmentName}. This usually means it initialized with a different environment's {credentialName}.",
  'sdkSetup.misconfig.wrongEnvironmentTitle': 'Connected to a different environment',
  'sdkSetup.noProjectAccess.body':
    "You've been added to this organization, but you don't have access to any projects yet. Contact your admin to request project access.",
  'sdkSetup.noProjectAccess.bodyWithOrganization':
    "You've been added to the {organization} organization, but you don't have access to any projects yet. Contact your admin to request project access.",
  'sdkSetup.noProjectAccess.heading': 'No project access yet',
  'sdkSetup.onboardingConnect.activeHeading': 'Make sure the SDK is active',
  'sdkSetup.onboardingConnect.chooseInstallMethod': 'Then, choose whether to install the SDK with AI or manually:',
  'sdkSetup.onboardingConnect.chooseSdk': 'Choose an SDK',
  'sdkSetup.onboardingConnect.description': 'Install an SDK to serve flags, track metrics, and run experiments',
  'sdkSetup.onboardingConnect.followTheSteps': 'Follow the steps on <link>{sdkName} docs</link>.',
  'sdkSetup.onboardingConnect.heading': 'Connect your app',
  'sdkSetup.onboardingConnect.initializeSdk': 'Initialize the SDK',
  'sdkSetup.onboardingConnect.installSdk': 'Install the LaunchDarkly SDK:',
  'sdkSetup.onboardingConnect.loadingSdkStatus': 'Loading SDK status…',
  'sdkSetup.onboardingConnect.moreInformation': 'For more information, see the <link>{sdkName} docs</link>',
  'sdkSetup.onboardingConnect.orInitialize': 'Or initialize the SDK with the instructions below',
  'sdkSetup.onboardingConnect.sdkCredentials':
    'Get your SDK credentials any time in <strong><link>Project settings > Environments</link>.</strong>',
  'sdkSetup.onboardingInstall.cartfile': 'Cartfile',
  'sdkSetup.onboardingInstall.csharp': 'C#',
  'sdkSetup.onboardingInstall.gradle': 'Gradle',
  'sdkSetup.onboardingInstall.gradleGroovy': 'Gradle Groovy',
  'sdkSetup.onboardingInstall.gradleKotlin': 'Gradle Kotlin',
  'sdkSetup.onboardingInstall.podfile': 'Podfile',
  'sdkSetup.onboardingInstall.swift': 'Swift',
  'sdkSetup.onboardingInstall.xml': 'XML',
  'sdkSetup.onboardingSnippet.copyLabel': 'Copy code snippet',
  'sdkSetup.onboardingSteps.collapseStepLabel': 'Collapse step {step}',
  'sdkSetup.onboardingSteps.expandStepLabel': 'Expand step {step}',
  'sdkSetup.onboardingSteps.stepCollapsedLabel': 'Step {step} collapsed',
  'sdkSetup.onboardingSteps.stepExpandedLabel': 'Step {step} expanded',
  'sdkSetup.page.agentControlLink': 'Setting up an AI agent? Install the AI SDK',
  'sdkSetup.page.awsDescription':
    'Connect the AWS DevOps Agent to this LaunchDarkly account, then install an SDK in your app so the agent has flags to work with.',
  'sdkSetup.page.awsHeading': 'Set up LaunchDarkly with AWS',
  'sdkSetup.page.description':
    'Install an SDK in your app to start controlling features in real time. After you connect, you can create flags, target users, and ship changes safely from your own codebase.',
  'sdkSetup.page.heading': 'Connect your app to LaunchDarkly',
  'sdkSetup.page.installDescription':
    'Pick how you want to set it up. LaunchDarkly can set it up for you, or follow step-by-step instructions for your stack.',
  'sdkSetup.page.installHeading': 'Install LaunchDarkly in your app',
  'sdkSetup.page.title': 'Setup SDK',
  'sdkSetup.pageHeader.closeSetup': 'Close setup',
  'sdkSetup.pageHeader.getSupport': 'Get support',
  'sdkSetup.productPicker.configs': 'Configs',
  'sdkSetup.productPicker.experiments': 'Experiments',
  'sdkSetup.productPicker.flags': 'Flags',
  'sdkSetup.productPicker.observability': 'Observability',
  'sdkSetup.sdkFilter.all': 'All SDKs',
  'sdkSetup.sdkFilter.backendAndServer': 'Backend and server',
  'sdkSetup.sdkFilter.categoryLabel': 'Filter SDKs by category',
  'sdkSetup.sdkFilter.desktop': 'Desktop',
  'sdkSetup.sdkFilter.edgeAndCloud': 'Edge and cloud',
  'sdkSetup.sdkFilter.mobile': 'Mobile',
  'sdkSetup.sdkFilter.observabilitySupported': 'Observability supported',
  'sdkSetup.sdkFilter.webAndFrontend': 'Web and frontend',
  'sdkSetup.sdkGrid.label': 'Choose your SDK',
  'sdkSetup.sdkGrid.observabilityLabel': 'Observability supported',
  'sdkSetup.sdkGrid.observabilityLegend': 'Observability supported',
  'sdkSetup.sdkKeyCallout.body':
    "This SDK uses your <strong>{keyText}</strong>. It's already included in the code snippet below for your environment. {secretText}",
  'sdkSetup.sdkKeyCallout.bodyWithEnvironment':
    "This SDK uses your <strong>{keyText}</strong>. It's already included in the code snippet below for the <strong>{environmentName}</strong> environment. {secretText}",
  'sdkSetup.sdkKeyCallout.clientSideKeyText': 'client-side ID',
  'sdkSetup.sdkKeyCallout.clientSideSecretText': 'Client-side IDs do not need to be kept secret and cannot be reset.',
  'sdkSetup.sdkKeyCallout.clientSideTitle': 'Client-side ID',
  'sdkSetup.sdkKeyCallout.mobileKeyText': 'mobile key',
  'sdkSetup.sdkKeyCallout.mobileSecretText':
    'Mobile keys do not need to be kept secret. However if you wish to reset a mobile key, you can.',
  'sdkSetup.sdkKeyCallout.mobileTitle': 'Mobile key',
  'sdkSetup.sdkKeyCallout.serverSideKeyText': 'SDK key (server-side)',
  'sdkSetup.sdkKeyCallout.serverSideSecretText':
    'SDK keys should be kept secret. If an SDK key is exposed, you can reset it.',
  'sdkSetup.sdkKeyCallout.serverSideTitle': 'SDK key',
  'sdkSetup.sdkKeysFooter.keysAnyTime': 'Get your SDK keys any time in <link>Project settings > Environments</link>.',
  'sdkSetup.sdkKeysFooter.viewAllKeys': 'All your keys are in <link>SDK keys</link>.',
  'sdkSetup.sdkPicker.observabilitySupported': 'Observability supported',
  'sdkSetup.sdkSelect.openLabel': 'Select SDK',
  'sdkSetup.sdkSelect.placeholder': 'Choose an SDK',
  'sdkSetup.sessionReplay.agentHelp':
    'Your agent adds 2 packages that capture session replays, errors, logs, and traces. Replays mask text and inputs by default.',
  'sdkSetup.sessionReplay.manualHelp':
    'Adds 2 packages that capture session replays, errors, logs, and traces. Replays mask text and inputs by default.',
  'sdkSetup.sessionReplay.recommendedTag': 'Recommended',
  'sdkSetup.sessionReplay.switchLabel': 'Monitor sessions, errors, logs, and traces',
  'sdkSetup.setupOverview.awsDescription':
    'Install the LaunchDarkly CLI and set up the agent with the LaunchDarkly MCP server. Or <link>set it up manually</link>.',
  'sdkSetup.setupOverview.awsTitle': 'Connect your AWS DevOps Agent',
  'sdkSetup.setupOverview.back': 'Back to overview',
  'sdkSetup.setupOverview.label': 'Choose what to set up',
  'sdkSetup.setupOverview.open': 'Open',
  'sdkSetup.setupOverview.sdkDescription': 'Choose an SDK, add it to your app, and watch for your first event.',
  'sdkSetup.setupOverview.sdkTitle': 'Install the SDK in your app',
  'sdkSetup.setupOverview.yourApp': 'Your app',
  'sdkSetup.snippet.loadingKeysLabel': 'Loading SDK keys',
  'sdkSetup.taskList.markDone': 'Mark done',
  'sdkSetup.taskList.markNotDone': 'Mark not done',
  'sdkSetup.taskList.progress': '{completed} of {total} done',
  'sdkSetup.taskList.progressLabel': 'Tasks done',
  'sdkSetup.taskList.statusDone': 'Done',
  'sdkSetup.taskList.statusNotDone': 'Not done',
  'sdkSetup.taskList.toggleDone': '{title} done',
  'sdkSetup.verifyConnection.heading': 'Verify the connection',
  'security.accountProvisioning.body': 'Manage how new members find and join your organization',
  'security.accountProvisioning.domainMatchingDescription':
    "Members can discover your account and request access if their verified email domain matches your organization's owner's.",
  'security.accountProvisioning.domainMatchingDisabledTooltip':
    'You cannot enable domain matching while using a personal email address.',
  'security.accountProvisioning.enableDomainMatching': 'Enable domain matching',
  'security.accountProvisioning.enableMatchedSeat': 'Enable matched seat provisioning',
  'security.accountProvisioning.heading': 'Organization access settings',
  'security.accountProvisioning.matchedSeatDescription':
    "Members with an email address belonging to your organization's domain name can join your organization automatically if there are available seats. No additional approval is required. Members will have Reader access by default.",
  'security.accountProvisioning.matchedSeatDisabledTooltip':
    'You cannot enable matched seat provisioning without enabling domain matching.',
  'security.accountProvisioning.matchedSeatWarning':
    'Consider who has access and ensure your domain and email services are secure before enabling this feature.',
  'security.accountProvisioning.personalEmailAlert':
    "Looks like your organization owner is using a personal email address. To enable domain matching, your organization owner's email address needs to be updated to include a verified business domain.",
  'security.addDomainDialog.cancel': 'Cancel',
  'security.addDomainDialog.copyNameLabel': 'Copy the name for this domain to your clipboard',
  'security.addDomainDialog.copyValueLabel': 'Copy the value for this domain to your clipboard',
  'security.addDomainDialog.domainInvalidError': 'Invalid domain',
  'security.addDomainDialog.domainNameLabel': 'Domain name',
  'security.addDomainDialog.domainPlaceholder': 'Domain',
  'security.addDomainDialog.domainRequiredError': 'Domain is required',
  'security.addDomainDialog.done': 'Done',
  'security.addDomainDialog.exampleImageAlt': 'Example of TXT record',
  'security.addDomainDialog.instructions': 'Log in to your domain registrar and add the following TXT record',
  'security.addDomainDialog.nameLabel': 'Name',
  'security.addDomainDialog.stepIndicator': 'Step {step} of 2',
  'security.addDomainDialog.submit': 'Add domain',
  'security.addDomainDialog.submitError': 'An unknown error occurred. Try again later.',
  'security.addDomainDialog.title': 'Add a domain',
  'security.addDomainDialog.valueLabel': 'Value',
  'security.addDomainDialog.viewExample': 'Click to view example',
  'security.domainVerification.addDomain': 'Add domain',
  'security.domainVerification.deleteErrorToast': 'Failed to delete domain',
  'security.domainVerification.noPermissionTooltip': 'Your permissions do not allow you to perform this action',
  'security.domainVerification.remove': 'Remove',
  'security.domainVerification.statusLabel': 'verification status',
  'security.domainVerification.verifiedToast': 'Domain verified',
  'security.domainVerification.verify': 'Verify',
  'security.domainVerification.verifyErrorToast': 'Failed to verify TXT record for domain',
  'security.ipAllowlist.addedByColumn': 'Added by',
  'security.ipAllowlist.apiTokenAllowlist': 'API token allowlist',
  'security.ipAllowlist.apiTokenDisabledToast': 'API token allowlist disabled',
  'security.ipAllowlist.apiTokenEnableBlockedToast': 'API token allowlist cannot be enabled',
  'security.ipAllowlist.apiTokenEnabledToast': 'API token allowlist enabled',
  'security.ipAllowlist.apiTokenUpdateError': 'Failed to update API token allowlist. Try again later.',
  'security.ipAllowlist.body':
    'Specify who has access to API tokens and browser sessions. When enabled, only requests from approved IPs are allowed, and all others will be blocked.',
  'security.ipAllowlist.browserSessionAllowlist': 'Browser session allowlist',
  'security.ipAllowlist.browserSessionDisabledToast': 'Browser session allowlist disabled',
  'security.ipAllowlist.browserSessionEnableBlockedToast': 'Browser session allowlist cannot be enabled',
  'security.ipAllowlist.browserSessionEnabledToast': 'Browser session allowlist enabled',
  'security.ipAllowlist.browserSessionUpdateError': 'Failed to update browser session allowlist.',
  'security.ipAllowlist.dateAddedColumn': 'Date added',
  'security.ipAllowlist.descriptionColumn': 'Description',
  'security.ipAllowlist.emptyStateBody': 'Add IP addresses before enabling allowlist.',
  'security.ipAllowlist.emptyStateHeading': 'No entries yet',
  'security.ipAllowlist.enableBlockedToastBody': 'Your current IP must be added before enabling allowlist',
  'security.ipAllowlist.heading': 'IP allowlist',
  'security.ipAllowlist.ipAddressColumn': 'IP address or range',
  'security.ipAllowlist.lastModifiedColumn': 'Last modified',
  'security.ipAllowlist.loadErrorBody': 'Something went wrong. Try again.',
  'security.ipAllowlist.loadErrorHeading': 'Unable to load entries',
  'security.ipAllowlist.loadingHeading': 'Loading entries…',
  'security.ipAllowlist.noResultsBody': 'Try a different IP address or description.',
  'security.ipAllowlist.noResultsHeading': 'No matching entries',
  'security.ipAllowlist.paginationSummary': '<strong>{from}–{to}</strong> of <strong>{total}</strong> entries',
  'security.ipAllowlist.searchLabel': 'Search IP allowlist',
  'security.ipAllowlist.searchPlaceholder': 'Search by IP address or description',
  'security.ipAllowlist.tableLabel': 'IP allowlist entries',
  'security.ipAllowlistAddDialog.cancel': 'Cancel',
  'security.ipAllowlistAddDialog.closeLabel': 'Close',
  'security.ipAllowlistAddDialog.descriptionLabel': 'Description',
  'security.ipAllowlistAddDialog.errorToast': 'Failed to add IP to allowlist',
  'security.ipAllowlistAddDialog.heading': 'Add IP address',
  'security.ipAllowlistAddDialog.invalidIpError': 'Invalid format. Use IPv4, IPv6, or CIDR notation.',
  'security.ipAllowlistAddDialog.ipAddressLabel': 'IP address or range',
  'security.ipAllowlistAddDialog.ipAddressPlaceholder': 'Use IPv4, IPv6, or CIDR notation',
  'security.ipAllowlistAddDialog.submit': 'Add to allowlist',
  'security.ipAllowlistAddDialog.successToast': 'IP added to allowlist',
  'security.ipAllowlistEditDialog.cancel': 'Cancel',
  'security.ipAllowlistEditDialog.closeLabel': 'Close',
  'security.ipAllowlistEditDialog.descriptionLabel': 'Description',
  'security.ipAllowlistEditDialog.descriptionRequiredError': 'Description is required',
  'security.ipAllowlistEditDialog.errorToast': 'Failed to update description',
  'security.ipAllowlistEditDialog.heading': 'Edit description',
  'security.ipAllowlistEditDialog.ipAddressHelpText':
    "This can't be changed. To edit, delete this entry and add a new one.",
  'security.ipAllowlistEditDialog.ipAddressLabel': 'IP address or range',
  'security.ipAllowlistEditDialog.submit': 'Save changes',
  'security.ipAllowlistEditDialog.successToast': 'Description updated',
  'security.ipAllowlistEntryMenu.editDescription': 'Edit description',
  'security.ipAllowlistEntryMenu.menuLabel': 'Entry actions',
  'security.ipAllowlistEntryMenu.removeFromAllowlist': 'Remove from allowlist',
  'security.ipAllowlistEntryMenu.triggerLabel': 'More actions',
  'security.ipAllowlistRemoveDialog.confirmLabel': 'Type <copy></copy> or the IP address range to confirm.',
  'security.ipAllowlistRemoveDialog.description':
    'This IP address or range will be removed from the allowlist immediately. This cannot be undone. Be certain.',
  'security.ipAllowlistRemoveDialog.errorToast': 'Failed to remove IP address',
  'security.ipAllowlistRemoveDialog.successToast': 'IP address removed',
  'security.ipAllowlistRemoveDialog.title': 'Remove from allowlist',
  'security.ipAllowlistRemoveDialog.type': 'Type',
  'security.mfaSettings.enforcedBody': 'LaunchDarkly policy requires MFA for all members of this account.',
  'security.mfaSettings.enforcedHeading': 'Multi-factor authentication is required',
  'security.mfaSettings.formLabel': 'Update account MFA settings',
  'security.mfaSettings.loading': 'Loading…',
  'security.mfaSettings.requireMfa': 'Require multi-factor authentication for new members',
  'security.mfaSettings.save': 'Save',
  'security.mfaSettings.saveLabel': 'Save multi-factor authentication settings',
  'security.mfaSettings.saving': 'Saving',
  'security.mfaSsoAlert.ssoEnforced':
    'This account uses SSO enforcement. Members authenticate through your identity provider. MFA enforcement does not apply.',
  'security.mfaSsoAlert.ssoManagesMfa':
    'Single sign-on manages MFA for your account. Use your identity provider to manage it instead.',
  'security.mfaSsoAlert.ssoTestMode':
    'SSO is in test mode, so members can still log in with a password. MFA is required by LaunchDarkly policy for password-capable members.',
  'security.notifications.samlCreated': 'SAML configuration created',
  'security.notifications.samlDeleted': 'SAML configuration deleted',
  'security.notifications.samlUpdated': 'SAML configuration updated',
  'security.notifications.scimTeamSyncDisabled': 'Team sync with SCIM disabled.',
  'security.notifications.scimTeamSyncEnabled': 'Team sync with SCIM enabled.',
  'security.samlAdvancedSettings.advancedSettings': 'Advanced settings',
  'security.samlAdvancedSettings.certificateAvailableOn': 'This option will be available on {date}',
  'security.samlAdvancedSettings.certificateOption': 'Use encryption certificate expiring on {date}',
  'security.samlAdvancedSettings.certificateOptionLabel': 'Use encryption certificate expiring on {date}',
  'security.samlAdvancedSettings.enableEncryptedSaml': 'Enable encrypted SAML assertions',
  'security.samlAdvancedSettings.enableSignedAuthentication': 'Enable signed authentication requests',
  'security.samlAdvancedSettings.encryptionCertificateHas': 'Encryption certificate has expired',
  'security.samlAdvancedSettings.expired': 'Expired',
  'security.samlAdvancedSettings.expiresSoon': 'Expires soon',
  'security.samlAdvancedSettings.expiringBody': 'Transition to using the newly issued certificate below before {date}',
  'security.samlAdvancedSettings.expiringHeading': 'Your encryption certificate will expire on {date}',
  'security.samlAdvancedSettings.new': 'New',
  'security.samlAdvancedSettings.transitionToUsing': 'Transition to using the newly issued certificate',
  'security.samlConfig.configureSaml': 'Configure SAML',
  'security.samlConfig.editSaml': 'Edit SAML configuration',
  'security.samlConfig.notConfigured': 'SAML not configured',
  'security.samlConfig.statusOff': 'Off',
  'security.samlConfig.statusOn': 'On',
  'security.samlConfig.statusPrefix': 'SSO:',
  'security.samlConfig.statusTestDrive': 'Test-drive mode',
  'security.samlConfig.testDriveBody':
    'Test-drive to verify if SSO is working. If successful, you’ll be redirected to this page by your IdP. This will not require all account members to sign in with SSO.',
  'security.samlConfig.testDriveSso': 'Test-drive SSO',
  'security.samlConfig.turnOffConfirmBody':
    'All team members will be required to sign in via their email and password.',
  'security.samlConfig.turnOffConfirmLabel': 'Turn off SSO',
  'security.samlConfig.turnOffConfirmTitle': 'Turn off single sign-on?',
  'security.samlConfig.turnOffSso': 'Turn off SSO',
  'security.samlConfig.turnOnConfirmBody':
    'All team members will be required to sign in via your identity provider from now on.',
  'security.samlConfig.turnOnConfirmLabel': 'Turn on',
  'security.samlConfig.turnOnConfirmTitle': 'Turn on single sign-on?',
  'security.samlConfig.turnOnSso': 'Turn on SSO',
  'security.samlConfig.verifyEmail':
    'Verify your email to use this feature. To re-send the verification email or change your email address, go to your <link>profile settings</link>.',
  'security.samlConfig.verifyEmailTooltip':
    'Verify your email to use this feature. To re-send the verification email or change your email address, go to your profile settings.',
  'security.samlConfigForm.certificateHint': 'Paste your certificate above or <upload>upload one</upload>',
  'security.samlConfigForm.defaultInitialRole': 'Default initial role',
  'security.samlConfigForm.logoutUrl': 'Logout URL',
  'security.samlConfigForm.save': 'Save',
  'security.samlConfigForm.saveLoading': 'Saving',
  'security.samlConfigForm.signOnUrl': 'Sign-on URL',
  'security.samlConfigForm.thisRoleDetermines':
    'This role determines permissions when a member is initially assigned to LaunchDarkly if no role or custom role is specified by the IdP.',
  'security.samlConfigForm.x509Certificate': 'X.509 certificate',
  'security.samlSetup.applicationHeading': 'SAML application details',
  'security.samlSetup.assertionConsumerService': 'Assertion consumer service URL',
  'security.samlSetup.body':
    'Follow the steps outlined by your identity provider (IdP) to complete setup. To learn more,',
  'security.samlSetup.configureApplicationHeading': 'Configure your SAML application',
  'security.samlSetup.deleteConfiguration': 'Delete configuration',
  'security.samlSetup.deleteDialogBody':
    'Single sign-on will be turned off for your account. All team members will be required to sign in via their email and password.',
  'security.samlSetup.deleteDialogConfirm': 'Delete',
  'security.samlSetup.deleteDialogTitle': 'Delete SAML configuration?',
  'security.samlSetup.deleteDisabledTooltip':
    'You cannot delete the SSO configuration while SSO and SCIM are both enabled.',
  'security.samlSetup.deleteSamlConfiguration': 'Delete SAML configuration',
  'security.samlSetup.editHeading': 'Edit your SAML configuration',
  'security.samlSetup.enterIdpHeading': 'Enter your SAML identity provider details',
  'security.samlSetup.entityId': 'Entity ID',
  'security.samlSetup.fillInThese': 'Fill in these fields with values provided by your IdP.',
  'security.samlSetup.idpHeading': 'SAML identity provider details',
  'security.samlSetup.ifYouDelete':
    'If you delete your configuration, all team members will be required to sign in via their email and password.',
  'security.samlSetup.setupHeading': 'Set up your SAML configuration',
  'security.samlSetup.startUrl': 'Start URL',
  'security.samlSetup.theseValuesAre':
    'These values are required when setting up the LaunchDarkly application in your IdP.',
  'security.scimConfig.configuredIn': 'Configured in {provider}',
  'security.scimConfig.disconnectBlockedTooltip': 'You cannot disconnect SCIM while team sync is on.',
  'security.scimConfig.disconnectConfirmBody': 'This removes {provider}’s ability to manage members.',
  'security.scimConfig.disconnectConfirmLabel': 'Disable',
  'security.scimConfig.disconnectConfirmTitle': 'Disconnect SCIM provisioning?',
  'security.scimConfig.disconnectScim': 'Disconnect SCIM',
  'security.scimConfig.docsLinkText': 'Learn how to configure SCIM',
  'security.scimConfig.managingProvisioning': '{provider} is managing user provisioning for your account.',
  'security.scimConfig.notConfigured': 'Not configured',
  'security.scimConfig.setupDescription':
    'Configure SCIM in your IdP for real-time member and team management. Currently available for Okta and OneLogin.',
  'security.scimConfig.setupDescriptionWithAzure':
    'Configure SCIM in your IdP for real-time member and team management. Currently available for Okta, Azure, and OneLogin.',
  'security.scimConfig.ssoRequired': 'Configure SAML and turn on SSO to configure SCIM',
  'security.scimConfig.statusPrefix': 'SCIM:',
  'security.securitySettings.domainsBody': 'Verify domain ownership. To learn more,',
  'security.securitySettings.domainsHeading': 'Domains',
  'security.securitySettings.domainsLearnMore': 'read the documentation',
  'security.securitySettings.enhancedSupportBody':
    "Allow LaunchDarkly's support team to temporarily view your account to help diagnose and resolve your support request. The support team will only have view access to your account if this feature is enabled and their access will automatically expire after the date/time you've indicated when enabling this feature.",
  'security.securitySettings.enhancedSupportHeading': 'Enable enhanced support',
  'security.securitySettings.generativeAiBenefits':
    "By having this feature enabled, you'll benefit from AI-powered insights and automation that can help streamline your workflows and improve decision-making.",
  'security.securitySettings.generativeAiBody':
    'This selection enables Customer to use generative AI Features across the LaunchDarkly Services to enhance your experience.',
  'security.securitySettings.generativeAiHeading': 'Enable generative AI use cases',
  'security.securitySettings.generativeAiTerms':
    'Your <agreement>existing agreement</agreement> with LaunchDarkly, including the <terms>Product-Specific Terms </terms>, govern your use of your AI Features.',
  'security.securitySettings.mfaBody':
    'Requiring an additional authentication method adds another level of security for your organization.',
  'security.securitySettings.mfaHeading': 'Multi-factor authentication',
  'security.securitySettings.revokeBody':
    'This will log all users out of LaunchDarkly and force them to re-authenticate.',
  'security.securitySettings.revokeHeading': 'Revoke all sessions',
  'security.securitySettings.sessionHeading': 'Session configuration',
  'security.securitySettings.supportAiBody':
    "Enable LaunchDarkly's generative AI tools to automatically analyze, tag, and label your support tickets based on their content. This feature enhances the operation and categorization of incoming tickets, making it easier to manage and prioritize requests. By turning on this generative AI feature, you'll help users in your organization by processing your support tickets more efficiently, allowing for quicker identification of key issues and faster response times.",
  'security.securitySettings.supportAiHeading': 'Enable support generative AI use cases',
  'security.securitySettings.unauthenticatedTargetingBody': 'Internal only. Target unauthenticated contexts.',
  'security.securitySettings.unauthenticatedTargetingHeading': 'Enable unauthenticated targeting',
  'security.ssoManagement.heading': 'SSO management',
  'security.ssoManagement.manageAuthenticationVia': 'Manage authentication via SSO and SCIM. To learn more,',
  'security.status.loading': 'Loading…',
  'security.teamSync.body':
    'When you turn on team sync with SCIM, you will have limited team management permissions in LaunchDarkly for synced teams. Existing teams in LaunchDarkly will not be deleted. To learn more,',
  'security.teamSync.description':
    'Manage teams in your IdP and changes are reflected in LaunchDarkly. Currently available for Okta.',
  'security.teamSync.descriptionWithAzure':
    'Manage teams in your IdP and changes are reflected in LaunchDarkly. Currently available for Okta and Azure.',
  'security.teamSync.providerUnsupported': '{provider} is not supported for team sync',
  'security.teamSync.scimNotConfigured': 'SCIM not configured',
  'security.teamSync.statusOff': 'Off',
  'security.teamSync.statusOn': 'On',
  'security.teamSync.statusPrefix': 'Team sync with SCIM:',
  'security.teamSync.syncedSummary':
    '{syncedTeams} synced and managed in {provider}. {unsyncedTeams} not synced with {provider} and are <link>managed in LaunchDarkly</link>.',
  'security.teamSync.teamCount': '{count, plural, one {# team is} other {# teams are}}',
  'security.teamSync.turnOffConfirmBody':
    'When you turn off team sync with SCIM, you will only be able to manage teams through LaunchDarkly.',
  'security.teamSync.turnOffConfirmLabel': 'Turn off',
  'security.teamSync.turnOffConfirmTitle': 'Turn off team sync with SCIM?',
  'security.teamSync.turnOffHint': 'When off, synced teams will no longer be managed by SCIM in your IdP',
  'security.teamSync.turnOffTeam': 'Turn off team sync',
  'security.teamSync.turnOnConfirmLabel': 'Turn on team sync',
  'security.teamSync.turnOnConfirmTitle': 'Turn on team sync with SCIM?',
  'security.teamSync.turnOnHint': 'Existing teams will not be deleted',
  'security.teamSync.turnOnTeam': 'Turn on team sync',
  'segments.addFlagDialog.body': 'Select a flag to add a rule that targets this segment.',
  'segments.addFlagDialog.cancel': 'Cancel',
  'segments.addFlagDialog.confirm': 'Target this segment',
  'segments.addFlagDialog.flagLabel': 'Select a flag',
  'segments.addFlagDialog.readAbout': 'Read about segments',
  'segments.addFlagDialog.title': 'Target this segment with a flag rule',
  'segments.bigSegmentConfirmDialog.addIncludedTargets': 'Add included {count, plural, one {target} other {targets}}',
  'segments.bigSegmentConfirmDialog.body':
    'This will make changes to the <strong>{segmentName}</strong> segment in the <environment></environment> environment.',
  'segments.bigSegmentConfirmDialog.changesHeading': 'Changes',
  'segments.bigSegmentConfirmDialog.confirm': 'Save changes',
  'segments.bigSegmentConfirmDialog.removeIncludedTargets':
    'Remove included {count, plural, one {target} other {targets}}',
  'segments.bigSegmentConfirmDialog.title': 'Review and save',
  'segments.bigSegmentTargeting.countWarning': 'Count may not be accurate until processing is complete.',
  'segments.bigSegmentTargeting.emptyTitle': 'Targets have not been added to this segment yet',
  'segments.bigSegmentTargeting.exportCsv': 'Export as CSV',
  'segments.bigSegmentTargeting.heading': 'Individual {contextKind} targets',
  'segments.bigSegmentTargeting.includedTargets': 'Included targets',
  'segments.bigSegmentTargeting.optionsLabel': 'Included users target options',
  'segments.bigSegmentTargeting.searchPlaceholder': 'Find target by key…',
  'segments.bigSegmentTargeting.targetCount': '{formattedCount} {count, plural, one {target} other {targets}}',
  'segments.bigSegmentTargeting.updateTargets': 'Update targets',
  'segments.bigSegmentTargeting.uploadCsv': 'Upload CSV',
  'segments.changeSummary.addClause': 'Add clause',
  'segments.changeSummary.addRule': 'Add rule <rule></rule> {rollout}',
  'segments.changeSummary.clauseNotFound': 'Clause {clauseId} not found in rule {ruleId}',
  'segments.changeSummary.deleteScheduledRemoval':
    'Delete scheduled removal of context kind <code>{contextKind}</code> <code>{contextKey}</code>',
  'segments.changeSummary.editRule': 'Edit rule',
  'segments.changeSummary.excludedTargets': 'Excluded targets',
  'segments.changeSummary.forContextKind': 'for context kind <code>{contextKind}</code>',
  'segments.changeSummary.includedTargets': 'Included targets',
  'segments.changeSummary.removeClause': 'Remove clause',
  'segments.changeSummary.removeRule': 'Remove rule',
  'segments.changeSummary.rolloutAllTargets': 'to include all targets',
  'segments.changeSummary.rolloutPercentage': 'to include {percent}% of {contextKind} targets',
  'segments.changeSummary.ruleNotFound': 'Rule {ruleId} not found in segment {segmentName}',
  'segments.changeSummary.ruleNotFoundTitle': 'rule not found',
  'segments.changeSummary.rules': 'Rules',
  'segments.changeSummary.scheduleRemoval':
    'Schedule removal of context kind <code>{contextKind}</code> <code>{contextKey}</code> for <time></time>',
  'segments.changeSummary.updateClause': 'Update clause',
  'segments.changeSummary.updateRuleDescription': 'Update rule description to {description}',
  'segments.changeSummary.updateRuleRollout': 'Update rule rollout {rollout}',
  'segments.changeSummary.updateScheduledRemoval':
    'Update scheduled removal of context kind <code>{contextKind}</code> <code>{contextKey}</code> to <time></time>',
  'segments.changeSummary.uploadTargets': 'Upload targets via CSV',
  'segments.convertToSegmentDialog.addTagsLabel': 'Add tags',
  'segments.convertToSegmentDialog.amplitudeUpsell': 'You can now sync from Amplitude',
  'segments.convertToSegmentDialog.big': 'Big',
  'segments.convertToSegmentDialog.bigHint':
    'Select a very large set of specific users by uploading your own CSV file containing user keys. Does not support dynamic targeting rules. If using a server-side SDK, a Relay Proxy v{version} or higher is required.',
  'segments.convertToSegmentDialog.cancel': 'Cancel',
  'segments.convertToSegmentDialog.contextKindHint':
    'Provide the context kind to be used for this big segment. Big segments can only contain members of a single context kind.',
  'segments.convertToSegmentDialog.contextKindLabel': 'Context kind',
  'segments.convertToSegmentDialog.descriptionLabel': 'Description',
  'segments.convertToSegmentDialog.keyHint':
    'LaunchDarkly uses the key to give you friendly URLs. Keys must only contain letters, numbers, <code>.</code>, <code>_</code> or <code>-</code>. <br></br>You cannot use <code>new</code> as a key.',
  'segments.convertToSegmentDialog.keyLabel': 'Key',
  'segments.convertToSegmentDialog.nameLabel': 'Name',
  'segments.convertToSegmentDialog.namePlaceholder': 'Eg. Beta users',
  'segments.convertToSegmentDialog.newTag': 'New',
  'segments.convertToSegmentDialog.readAboutBigSegments': 'Read about big segments',
  'segments.convertToSegmentDialog.readAboutSyncedSegments': 'Read about synced segments',
  'segments.convertToSegmentDialog.saveSegment': 'Save segment',
  'segments.convertToSegmentDialog.saving': 'Saving segment',
  'segments.convertToSegmentDialog.sizeLabel': 'Size',
  'segments.convertToSegmentDialog.standard': 'Standard',
  'segments.convertToSegmentDialog.standardHint':
    'Best suited for most common use cases. Use dynamic targeting rules to define a set of users of unlimited size. Manually enter specific users to be included or excluded.',
  'segments.convertToSegmentDialog.tagsLabel': 'Tags',
  'segments.convertToSegmentDialog.tagsPlaceholder': 'Add tags',
  'segments.createDetailsStep.back': 'Back',
  'segments.createDetailsStep.bigListBody':
    'Supports large, static lists of a single context kind. Does not support targeting rules.',
  'segments.createDetailsStep.bigListHeading': 'More than 15,000',
  'segments.createDetailsStep.bigListUpsell':
    'Enterprise accounts can upload CSVs with large lists of user or context keys for targeting.',
  'segments.createDetailsStep.buildYourOwn': 'Interested in building your own source?',
  'segments.createDetailsStep.contextKindLabel': 'Context kind',
  'segments.createDetailsStep.descriptionLabel': 'Description',
  'segments.createDetailsStep.done': 'Done',
  'segments.createDetailsStep.joinPartnerProgram': 'Join our partner program',
  'segments.createDetailsStep.keyHint':
    'LaunchDarkly uses the key to give you friendly URLs. Keys must only contain letters, numbers, <code>.</code>, <code>_</code> or <code>-</code>. You cannot use <code>new</code> as a key.',
  'segments.createDetailsStep.keyLabel': 'Key',
  'segments.createDetailsStep.listSizeLabel': 'How many individual targets will be in your list?',
  'segments.createDetailsStep.missingSource': 'Don’t see your preferred source?',
  'segments.createDetailsStep.nameLabel': 'Name',
  'segments.createDetailsStep.namePlaceholder': 'Example: Beta users',
  'segments.createDetailsStep.openSetupDocs': 'Open setup docs',
  'segments.createDetailsStep.readAboutBigSegments': 'Read about big segments',
  'segments.createDetailsStep.saveSegment': 'Save segment',
  'segments.createDetailsStep.saving': 'Saving segment',
  'segments.createDetailsStep.sizeLegend': 'Size',
  'segments.createDetailsStep.smallListBody':
    'Supports individual targets and dynamic targeting rules on multiple context kinds.',
  'segments.createDetailsStep.smallListHeading': '15,000 or fewer',
  'segments.createDetailsStep.suggestIntegration': 'Suggest a new integration',
  'segments.createDetailsStep.syncedIntro':
    'Select a third-party tool below to read the documentation on how to set up a synced segment:',
  'segments.createDetailsStep.tagsLabel': 'Tags',
  'segments.createDetailsStep.tagsPlaceholderPresets': 'Add tags or select from LaunchDarkly preset tags below',
  'segments.createDetailsStep.tagsPlaceholderTopTags': 'Add tags or select from commonly used tags below',
  'segments.createDetailsStep.viewsLabel': 'Views',
  'segments.createDetailsStep.viewsRequired': 'At least one view is required when project requires view association',
  'segments.createDetailsStep.viewsSelectLabel': 'Select views to link this segment to upon creation',
  'segments.createDialog.disabledHeading': 'Segment creation is disabled',
  'segments.createDialog.enterDetailsStep': 'Enter segment details',
  'segments.createDialog.noPermission': 'Your user account does not have permission to create segments.',
  'segments.createDialog.requestPermissions': 'Request the necessary permissions from your administrator.',
  'segments.createDialog.selectKindStep': 'Select a segment kind',
  'segments.createDialog.selectMethodStep': 'Select a method',
  'segments.createDialog.selectSourceStep': 'Select an external source',
  'segments.createDialog.selectSourceStepWithCount': 'Select an external source ({count} available)',
  'segments.createDialog.stepIndicator': 'Step {step} of 2: {description}',
  'segments.createDialog.title': 'Create a segment in <environment></environment>',
  'segments.createSourceStep.andMore': 'and more',
  'segments.createSourceStep.listDescription': 'Target individual contexts or import lists of contexts',
  'segments.createSourceStep.listHeading': 'List-based segments',
  'segments.createSourceStep.listOption': 'Start with a list',
  'segments.createSourceStep.providersLabel': 'Sync with data providers like Amplitude, Heap, Segment, and more.',
  'segments.createSourceStep.readAboutSyncedSegments': 'Read about synced segments',
  'segments.createSourceStep.rulesDescription':
    'Build dynamic conditions and rules to target groups of contexts individually or by attributes',
  'segments.createSourceStep.rulesHeading': 'Rule-based segments',
  'segments.createSourceStep.rulesOption': 'Build dynamic conditions and rules',
  'segments.createSourceStep.syncDescription':
    'Sync audiences defined in supported third-party tools with LaunchDarkly segments',
  'segments.createSourceStep.syncHeading': 'Synced segments',
  'segments.createSourceStep.syncOption': 'Sync data from external sources',
  'segments.createSourceStep.upsellBody': 'Populate your segments with data from external sources.',
  'segments.createSourceStep.upsellHeading': 'Sync customer data from other apps',
  'segments.deleteDialog.andMore': 'And {count} more',
  'segments.deleteDialog.body': 'The <strong>{segmentName}</strong> segment will be deleted permanently.',
  'segments.deleteDialog.confirm': 'Delete',
  'segments.deleteDialog.deleteButton': 'Delete',
  'segments.deleteDialog.hasDependentFlags':
    'You cannot delete this segment because {count} {count, plural, one {flag is} other {flags are}} using it.',
  'segments.deleteDialog.noDependentFlags': 'You may delete this segment. No flags use it.',
  'segments.deleteDialog.title': 'Delete segment?',
  'segments.deleteScheduledChangeDialog.body':
    'This pending change for <segment>{segmentName}</segment> will be permanently removed and the changes will not be applied at the scheduled date and time.',
  'segments.deleteScheduledChangeDialog.failedBody':
    'This failed change for <segment>{segmentName}</segment> will be permanently removed. Its changes were not applied to the segment.',
  'segments.deleteScheduledChangeDialog.cancel': 'Cancel',
  'segments.deleteScheduledChangeDialog.confirmHint': 'Type <code>{token}</code> to confirm.',
  'segments.deleteScheduledChangeDialog.confirmLabel': 'Confirm',
  'segments.deleteScheduledChangeDialog.delete': 'Delete',
  'segments.deleteScheduledChangeDialog.error': 'Unable to delete this scheduled change. Try again.',
  'segments.deleteScheduledChangeDialog.title': 'Delete scheduled change?',
  'segments.description.addDescription': 'Add description',
  'segments.description.confirm': 'Save',
  'segments.description.confirmBody':
    'This will change <strong>{segmentName}</strong> in the {environmentName} environment.',
  'segments.description.confirmTitle': 'Save changes to this segment',
  'segments.description.editLabel': 'Edit description',
  'segments.description.empty': 'None',
  'segments.description.heading': 'Description',
  'segments.description.inputLabel': 'Segment description',
  'segments.description.placeholder': 'Describe what this segment represents',
  'segments.description.reasonDescription': 'Required to save this change.',
  'segments.description.reasonLabel': 'Reason',
  'segments.description.reasonPlaceholder': 'Explain what this segment is for',
  'segments.description.saveError': 'Unable to save description',
  'segments.description.saved': 'Segment saved',
  'segments.description.tryAgain': 'Try again.',
  'segments.detailPage.copyKey': 'Copy key: {segmentKey}',
  'segments.detailPage.created': 'Created',
  'segments.detailPage.description': 'Description',
  'segments.detailPage.failedToLoad6': 'Failed to load pending approvals',
  'segments.detailPage.failedToLoadPendingChanges': 'Failed to load pending changes',
  'segments.detailPage.label': 'entity action',
  'segments.detailPage.lastModified': 'Last modified',
  'segments.detailPage.loading': 'Loading…',
  'segments.detailPage.loadingPendingApprovals': 'Loading pending approvals',
  'segments.detailPage.loadingPendingChanges': 'Loading pending changes',
  'segments.detailPage.manageSegmentSettings': 'Manage segment settings',
  'segments.detailPage.pendingApprovals': 'Pending approvals',
  'segments.detailPage.pendingChanges': 'Pending changes',
  'segments.detailPage.relatedSegmentsBased': 'Related segments based on tags',
  'segments.detailPage.tooltip': 'Copy segment link',
  'segments.detailPage.tryAgain': 'Try again',
  'segments.discardChanges.confirm': 'Discard changes',
  'segments.discardChanges.confirmAllBody':
    'Are you sure you want to discard all unsaved changes? This action cannot be undone.',
  'segments.discardChanges.confirmBody':
    'Are you sure you want to discard changes to <strong>{title}</strong>? This action cannot be undone.',
  'segments.discardChanges.confirmTitle': 'Discard unsaved changes',
  'segments.discardChanges.discardAll': 'Discard all changes',
  'segments.downloadDialog.cancel': 'Cancel',
  'segments.downloadDialog.download': 'Download',
  'segments.downloadDialog.fileSize': 'File size: {fileSize}',
  'segments.downloadDialog.generatedBy': 'Export generated by {generatedBy} on {generatedOn}.',
  'segments.downloadDialog.loadError': 'Something went wrong while retrieving the file export. Try again later.',
  'segments.downloadDialog.subtitle': 'for <strong>{segmentName}</strong>',
  'segments.downloadDialog.title': 'Download CSV',
  'segments.editScheduledChangeModal.cancel': 'Cancel',
  'segments.editScheduledChangeModal.closeLabel': 'Close',
  'segments.editScheduledChangeModal.commentDescription': 'Add a brief description of your changes.',
  'segments.editScheduledChangeModal.commentLabel': 'Comment',
  'segments.editScheduledChangeModal.error': 'Unable to update the scheduled change.',
  'segments.editScheduledChangeModal.save': 'Update scheduled change',
  'segments.editScheduledChangeModal.subtitle':
    'Update scheduled change for <segment>{segmentName}</segment> on <environment></environment>',
  'segments.editScheduledChangeModal.success': 'Scheduled change updated.',
  'segments.editScheduledChangeModal.title': 'Edit scheduled change',
  'segments.emptyState.addSegments': 'Add segments',
  'segments.emptyState.body': 'Segments group contexts into reusable sets you can target in flag rules.',
  'segments.emptyState.clearFilters': 'Clear filters',
  'segments.emptyState.clearView': 'Clear view',
  'segments.emptyState.createSegment': 'Create segment',
  'segments.emptyState.filteredBody': 'Try adjusting your filters',
  'segments.emptyState.filteredHeading': 'No segments match your filter criteria',
  'segments.emptyState.heading': 'Create your first segment',
  'segments.emptyState.viewBody':
    'Add segments to this view, or clear the view to see all segments in this environment.',
  'segments.emptyState.viewFilteredBody': 'Try adjusting your filters, or add more segments to this view.',
  'segments.emptyState.viewFilteredHeading': 'No segments match your filters in this view',
  'segments.emptyState.viewHeading': 'No segments in this view',
  'segments.filterMenu.add': 'Add',
  'segments.filterMenu.kindLabel': 'Kind',
  'segments.filterMenu.selectedTagsLabel': 'Selected tags',
  'segments.filterMenu.tagsDialogLabel': 'Select tags',
  'segments.filterMenu.tagsLabel': 'Tags',
  'segments.filterMenu.title': 'Filter segments by…',
  'segments.filters.activeCountLabel': '{count} {count, plural, one {active filter} other {active filters}}',
  'segments.filters.clear': 'Clear',
  'segments.filters.closeLabel': 'Close dialog',
  'segments.filters.defaultTitle': 'Filter by…',
  'segments.filters.dialogLabel': 'Filters',
  'segments.filters.kind': 'Kind',
  'segments.filters.kindAny': 'Any',
  'segments.filters.kindFieldDescription': 'The segment kind.',
  'segments.filters.kindList': '15k+ list',
  'segments.filters.kindSelected': 'Kind: {kind}',
  'segments.filters.kindStandard': 'Standard',
  'segments.filters.kindSynced': 'Synced',
  'segments.filters.optionsLabel': 'Filter options',
  'segments.filters.searchField': 'Search',
  'segments.filters.searchFieldDescription': 'A fuzzy search across segment key and name.',
  'segments.filters.searchLabel': 'search segments',
  'segments.filters.searchPlaceholder': 'Search segments',
  'segments.filters.selectLabel': 'Filter selection',
  'segments.filters.tags': 'Tags: {selection}',
  'segments.filters.tagsField': 'Tags',
  'segments.filters.tagsFieldDescription': 'The segment tags.',
  'segments.filters.trigger': 'Filters',
  'segments.flagsTargetingSegment.checkForTypos': 'Check for typos.',
  'segments.flagsTargetingSegment.collapseLabel': 'Collapse',
  'segments.flagsTargetingSegment.collapseSectionLabel': 'Collapse section',
  'segments.flagsTargetingSegment.columnHeading': 'Flags targeting this segment',
  'segments.flagsTargetingSegment.emptyTitle': 'This segment has no flags that use it for targeting.',
  'segments.flagsTargetingSegment.expandLabel': 'Expand',
  'segments.flagsTargetingSegment.expandSectionLabel': 'Expand section',
  'segments.flagsTargetingSegment.flagCountHeading':
    '{count} {count, plural, one {flag} other {flags}} referencing this segment',
  'segments.flagsTargetingSegment.flagCountTitle':
    '{count} {count, plural, one {flag targets} other {flags target}} this segment',
  'segments.flagsTargetingSegment.learnMore': 'To learn more, read about <link></link>.',
  'segments.flagsTargetingSegment.modifyFilter': 'Modify your filter selection.',
  'segments.flagsTargetingSegment.noMatch': 'No feature flags match.',
  'segments.flagsTargetingSegment.noMatchTitle': 'No feature flags match. Try this:',
  'segments.flagsTargetingSegment.readAboutLink': 'using segments in your flag targeting',
  'segments.flagsTargetingSegment.searchLabel': 'Search for a feature flag by name, key, or description',
  'segments.flagsTargetingSegment.searchPlaceholder': 'Find a flag by name, key, or description',
  'segments.flagsTargetingSegment.searchTip': 'Search for a flag by name, key, or description.',
  'segments.flagsTargetingSegment.tableSummary': 'A list of flags that target this segment',
  'segments.flagsTargetingSegment.targetThisSegment': 'Target this segment',
  'segments.flagsUsingSegment.body':
    'The following flags target this segment. To learn more, read about <link></link>.',
  'segments.flagsUsingSegment.emptyTitle': 'This segment has no flags that use it for targeting.',
  'segments.flagsUsingSegment.flagColumn': 'Flag',
  'segments.flagsUsingSegment.keyColumn': 'Key',
  'segments.flagsUsingSegment.readAboutLink': 'using segments in your flag targeting',
  'segments.flagsUsingSegment.title': 'Flags using this segment',
  'segments.individualTargeting.discardTitle': 'individual targets',
  'segments.individualTargeting.targetCount':
    '{formattedCount} individual {count, plural, one {target} other {targets}}',
  'segments.individualTargeting.tooManyTargetsAlert':
    'Too many individual targets. Instead, use rules to target based on attributes. <link>Read about individual targeting</link>',
  'segments.individualTargeting.tooManyTargetsWarning':
    'Too many individual targets. Instead, use rules to target based on attributes. <link>Read about individual targeting</link>',
  'segments.jsonEditor.clauseInvalid': 'Clause is invalid',
  'segments.jsonEditor.clauseRequired': 'At least one clause is required',
  'segments.jsonEditor.clausesUniqueIds': 'Clauses must have unique IDs',
  'segments.jsonEditor.closeLabel': 'close',
  'segments.jsonEditor.documentationTitle': 'JSON documentation',
  'segments.jsonEditor.formatJson': 'Format JSON',
  'segments.jsonEditor.help': 'Help',
  'segments.jsonEditor.noChanges': 'No changes detected. Make edits to see instructions.',
  'segments.jsonEditor.pendingChanges': 'Pending changes ({count})',
  'segments.jsonEditor.prettifyLabel': 'Prettify',
  'segments.jsonEditor.resetChangesLabel': 'Reset changes',
  'segments.jsonEditor.rulesUniqueIds': 'Rules must have unique IDs',
  'segments.jsonEditor.snippetCustomClause': 'Add custom clause',
  'segments.jsonEditor.snippetCustomRule': 'Build a custom rule',
  'segments.jsonEditor.snippetIndividualTarget': 'Add individual target',
  'segments.jsonEditor.snippetSegmentClause': 'Add segment clause',
  'segments.jsonEditor.snippetTargetSegments': 'Target segments',
  'segments.jsonEditor.toggleInstructionsLabel': 'Toggle instructions',
  'segments.jsonEditor.undoAllChanges': 'Undo all changes',
  'segments.jsonEditor.valuesEmpty': 'Values cannot be empty',
  'segments.jsonEditor.viewChanges': 'View changes',
  'segments.jsonEditorGuide.basicPropertiesDescription':
    "<code>description</code> is optional and describes the segment's purpose.",
  'segments.jsonEditorGuide.basicPropertiesHeading': 'Basic properties',
  'segments.jsonEditorGuide.basicPropertiesKey': '<code>key</code> is the unique identifier for your segment.',
  'segments.jsonEditorGuide.basicPropertiesName': '<code>name</code> is the human-readable name.',
  'segments.jsonEditorGuide.basicPropertiesTags': '<code>tags</code> are optional labels for organization.',
  'segments.jsonEditorGuide.clauseOperatorContains': '<code>contains</code> - attribute contains the value',
  'segments.jsonEditorGuide.clauseOperatorEndsWith': '<code>endsWith</code> - attribute ends with the value',
  'segments.jsonEditorGuide.clauseOperatorIn': '<code>in</code> - attribute matches any of the values',
  'segments.jsonEditorGuide.clauseOperatorMatches': '<code>matches</code> - attribute matches a regex pattern',
  'segments.jsonEditorGuide.clauseOperatorNegate': 'Set <code>negate</code> to <code>true</code> to invert the result.',
  'segments.jsonEditorGuide.clauseOperatorNumeric':
    '<code>greaterThan</code>, <code>lessThan</code> - numeric comparisons',
  'segments.jsonEditorGuide.clauseOperatorStartsWith': '<code>startsWith</code> - attribute starts with the value',
  'segments.jsonEditorGuide.clauseOperatorsHeading': 'Clause operators',
  'segments.jsonEditorGuide.clauseOperatorsIntro': 'Common operators include:',
  'segments.jsonEditorGuide.gettingStartedBody':
    'Editing your segment JSON follows the same model as the JSON response from <link>GET segment</link> in the API.',
  'segments.jsonEditorGuide.gettingStartedHeading': 'Getting started',
  'segments.jsonEditorGuide.gettingStartedStructure': 'Here is the basic structure:',
  'segments.jsonEditorGuide.individualTargetingContexts':
    'Use <code>includedContexts</code> and <code>excludedContexts</code> for other context kinds.',
  'segments.jsonEditorGuide.individualTargetingHeading': 'Individual targeting',
  'segments.jsonEditorGuide.individualTargetingKeys':
    'Use <code>included</code> and <code>excluded</code> arrays to target specific user keys.',
  'segments.jsonEditorGuide.rulesBody':
    'Rules define conditions for segment membership. Each rule contains clauses that must all be true.',
  'segments.jsonEditorGuide.rulesHeading': 'Rules',
  'segments.jsonEditorGuide.rulesWeight': 'Optional <code>weight</code> (0-100000) controls percentage rollouts.',
  'segments.jsonEditorGuide.unboundedBody':
    'Large segments can be marked as unbounded to handle unlimited contexts efficiently.',
  'segments.jsonEditorGuide.unboundedHeading': 'Unbounded segments',
  'segments.jsonEditorGuide.unboundedSetup':
    'Set <code>unbounded</code> to <code>true</code> and specify the <code>unboundedContextKind</code>.',
  'segments.list.actionsLabel': 'Segment actions',
  'segments.list.contextKindsLabel': 'context kinds',
  'segments.list.copyKey': 'Copy key',
  'segments.list.createDenied': 'You do not have permission to create a segment',
  'segments.list.createSegment': 'Create segment',
  'segments.list.created': 'Created <time>{relative}</time>',
  'segments.list.flagCount': '{count, plural, one {# Flag} other {# Flags}}',
  'segments.list.keyCopied': 'Segment key copied to clipboard',
  'segments.list.label': 'segment list',
  'segments.list.lastModified': 'Last modified <time>{relative}</time>',
  'segments.list.loadError': 'Failed to load',
  'segments.list.tagFilterLabel': 'Filter{segmentName}',
  'segments.list.tagsLabel': '{segmentName} tags',
  'segments.listPage.createDenied': 'You do not have permission to create a segment',
  'segments.listPage.createSegment': 'Create segment',
  'segments.listPage.sdkBanner': 'Connect your SDK to start evaluating flags and see real-time data.',
  'segments.notFound.body':
    'Segments are specific to an environment. However, you can create a new segment in this environment using the same name and key. This makes it easier to create reusable rules across environments.',
  'segments.notFound.createSegment': 'Create segment',
  'segments.notFound.heading': 'Segment does not exist in this environment yet',
  'segments.notifications.clauseConverted': 'Your rule’s clause was converted to segment, <link>{name}</link>.',
  'segments.notifications.deleted': 'Segment deleted',
  'segments.notifications.exportStarted':
    "Exporting data may take time to complete. When your download is ready, you'll get an email.",
  'segments.notifications.importStarted':
    "Importing data may take time to complete. When it's done, you'll get an email.",
  'segments.notifications.saved': 'Segment saved',
  'segments.notifications.settingsSaved': 'Segment settings saved',
  'segments.notifications.targetsConverted': 'Individual targets converted to segment, <link>{name}</link>.',
  'segments.notifications.usersUpdated': 'Users updated',
  'segments.page.createDenied': 'You do not have permission to create a segment',
  'segments.page.createSegment': 'Create segment',
  'segments.page.sdkBanner': 'Connect your SDK to start evaluating flags and see real-time data.',
  'segments.page.title': 'Segments',
  'segments.pendingChangesButton.viewPendingChanges': 'View pending changes',
  'segments.pendingChangesDrawer.closeLabel': 'Close',
  'segments.pendingChangesDrawer.emptyStateBody': 'This drawer will show changes that are scheduled for this segment.',
  'segments.pendingChangesDrawer.heading': 'Pending changes for <segment>{segmentName}</segment>',
  'segments.pendingChangesDrawer.itemRenderError': 'Unable to display this pending change.',
  'segments.pendingChangesDrawer.loadError': 'Unable to load pending changes.',
  'segments.pendingChangesDrawer.loadingLabel': 'Loading pending changes',
  'segments.pendingChangesDrawer.noPendingChanges': 'No pending changes',
  'segments.picker.allGroup': 'Segments',
  'segments.picker.label': 'Select segments',
  'segments.picker.lastModified': 'Last modified:',
  'segments.picker.mostTargetedGroup': 'Most targeted segments',
  'segments.picker.noResults':
    'No segments found — <manage>Manage segments</manage> or <create>Create a new segment</create>',
  'segments.picker.noTargetsLabel': '{segmentName} (0 targets)',
  'segments.picker.placeholder': 'Select segments…',
  'segments.picker.recentlyUpdatedGroup': 'Recently updated segments',
  'segments.picker.selectedGroup': 'Selected segments',
  'segments.picker.targetedBy': 'Targeted by {count} {count, plural, one {flag} other {flags}}',
  'segments.popover.noDescription': 'No description',
  'segments.popover.viewDetails': 'View details',
  'segments.relatedFlags.count': '{count} {count, plural, one {flag is} other {flags are}} using this segment',
  'segments.relatedFlags.none': 'No flags are using this segment',
  'segments.relatedSegments.addTagsHint': 'Add tags to organize and find segments faster.',
  'segments.relatedSegments.none': 'There are no other segments with any of the same tags.',
  'segments.relatedSegments.seeAll': 'See all related segments',
  'segments.relayProxyAlert.configureIntegration': 'Configure a Big Segment integration, such as Redis or DynamoDB, or',
  'segments.relayProxyAlert.forThisEnvironment': 'for this environment.',
  'segments.relayProxyAlert.heading': 'Additional configurations may be required',
  'segments.relayProxyAlert.intro': 'To use this Segment with a server-side SDK, you must either:',
  'segments.relayProxyAlert.readAbout': 'Read about configuring the Relay Proxy for segments',
  'segments.relayProxyAlert.unsupportedBody':
    'Big segments are not supported on server-side SDKs or the LaunchDarkly Relay Proxy.',
  'segments.relayProxyAlert.useRelayProxy': 'Use the Relay Proxy v{version} or above with persistent storage',
  'segments.ruleSummary.allTargets': 'All targets',
  'segments.ruleSummary.include': 'Include',
  'segments.ruleSummary.ofContextKind': 'of <strong>Context kind</strong> <code>{contextKind}</code>',
  'segments.rules.addRule': 'Add rule',
  'segments.rules.allTargets': 'all targets',
  'segments.rules.contextKindLabel': 'Context kind',
  'segments.rules.customRuleOption': 'Custom',
  'segments.rules.deleteRule': 'Delete rule',
  'segments.rules.duplicateDisabled': 'Cannot duplicate segment rule with clause that references another segment.',
  'segments.rules.duplicateRule': 'Duplicate rule',
  'segments.rules.for': 'for',
  'segments.rules.include': 'include',
  'segments.rules.includeModeLabel': 'Include all targets or percentage of targets',
  'segments.rules.optionsLabel': 'Rule {number} segment options',
  'segments.rules.percentageOfTargets': 'a percentage of targets',
  'segments.rules.rolloutWeightLabel': 'Segment rule rollout weight',
  'segments.rules.ruleTitle': 'Rule {number}',
  'segments.rules.segmentRuleOption': 'Segment',
  'segments.rules.then': 'then',
  'segments.saveChangesDialog.body':
    'This will change <strong>{segmentName}</strong> in the <environment></environment> environment.',
  'segments.saveChangesDialog.cancel': 'Cancel',
  'segments.saveChangesDialog.changesHeading': 'Changes',
  'segments.saveChangesDialog.closeLabel': 'close',
  'segments.saveChangesDialog.confirmLabel': 'Confirm',
  'segments.saveChangesDialog.reasonDescription': 'Required to save this change.',
  'segments.saveChangesDialog.reasonLabel': 'Reason',
  'segments.saveChangesDialog.reasonPlaceholder': 'Adding beta testers to this segment',
  'segments.saveChangesDialog.reasonRequired': 'Provide a reason.',
  'segments.saveChangesDialog.save': 'Save',
  'segments.saveChangesDialog.scheduleChanges': 'Schedule changes',
  'segments.saveChangesDialog.title': 'Save changes to this segment',
  'segments.scheduledChangeCard.actionsLabel': 'Scheduled change actions',
  'segments.scheduledChangeCard.applying': 'Applying change scheduled for <date>{executionDate}</date>',
  'segments.scheduledChangeCard.delete': 'Delete',
  'segments.scheduledChangeCard.edit': 'Edit',
  'segments.scheduledChangeCard.failed': 'Failed to apply change scheduled for <date>{executionDate}</date>',
  'segments.scheduledChangeCard.failedBody':
    'This change was not applied to the segment. Delete it, then schedule it again if you still need it.',
  'segments.scheduledChangeCard.scheduledFor': 'Scheduled for <date>{executionDate}</date>',
  'segments.scheduledChangeCard.serviceToken': 'Service token',
  'segments.settings.deleteBody': 'This segment will be deleted permanently.',
  'segments.settings.deleteButton': 'Delete this segment',
  'segments.settings.deleteHeading': 'Delete segment',
  'segments.settings.descriptionLabel': 'Description',
  'segments.settings.descriptionPlaceholder': 'Describe what this segment represents',
  'segments.settings.heading': 'Segment settings',
  'segments.settings.nameLabel': 'Name',
  'segments.settings.saving': 'Saving settings',
  'segments.settingsPage.title': 'Settings',
  'segments.status.loading': 'Loading…',
  'segments.syncedSegment.logoAlt': 'Segment {integrationKey}',
  'segments.syncedTargeting.apiBody': 'You must use the Big Segments API to manage users in this segment.',
  'segments.syncedTargeting.heading': 'Synced contexts:',
  'segments.syncedTargeting.searchPlaceholder': 'Find context by key, using exact match',
  'segments.syncedTargeting.syncedSourceBody':
    'You must use synced source <source></source> to manage contexts in this segment. <link>Read about synced segments</link>',
  'segments.table.actionsColumnLabel': 'Actions',
  'segments.table.created': 'Created',
  'segments.table.label': 'Segments table',
  'segments.table.name': 'Name',
  'segments.table.tags': 'Tags',
  'segments.tags.addTagLabel': 'Add tag {tag}',
  'segments.tags.addTags': 'Add tag(s)',
  'segments.tags.addTagsLabel': 'Add tags',
  'segments.tags.editTagsLabel': 'Edit tags',
  'segments.tags.empty': 'None',
  'segments.tags.groupLabel': 'Tags',
  'segments.tags.heading': 'Tags',
  'segments.tags.saved': 'Segment saved',
  'segments.tags.updateError': 'Failed to update tags',
  'segments.targetStatus.addTarget': 'Add target',
  'segments.targetStatus.excluded': '<strong>{userKey}</strong> is excluded from this segment.',
  'segments.targetStatus.importInProgress': 'Cannot update segment while import is in process',
  'segments.targetStatus.included': '<strong>{userKey}</strong> is included in this segment.',
  'segments.targetStatus.includedAndExcluded':
    '<strong>{userKey}</strong> is included and excluded from this segment, which resolves to included.',
  'segments.targetStatus.notInList': '<strong>{userKey}</strong> is not in the list of targets',
  'segments.targetStatus.notIncluded': '<strong>{userKey}</strong> is not included in this segment.',
  'segments.targetStatus.pendingAdd': 'Target will be added to list of included targets on Update targets',
  'segments.targetStatus.pendingRemove': 'Target will be removed from list of included targets on Update targets',
  'segments.targetStatus.removeTarget': 'Remove target',
  'segments.targeting.allTraffic': 'All traffic',
  'segments.targeting.bulkEditExcluded': 'Bulk edit excluded targets for this segment',
  'segments.targeting.bulkEditIncluded': 'Bulk edit included targets for this segment',
  'segments.targeting.environmentHeading': 'Configuration for <environment></environment>',
  'segments.targeting.evaluateLabel': 'evaluate context',
  'segments.targeting.excludedCount': '{count} {count, plural, one {excluded target} other {excluded targets}}',
  'segments.targeting.excludedLabel': 'excluded targets',
  'segments.targeting.heading': 'Individual targets',
  'segments.targeting.includedCount': '{count} {count, plural, one {included target} other {included targets}}',
  'segments.targeting.includedLabel': 'included targets',
  'segments.targeting.noExcluded': 'There are no excluded users for this segment.',
  'segments.targeting.noIncluded': 'There are no included users for this segment.',
  'segments.targeting.saveChanges': 'Save changes',
  'segments.targeting.scheduleFailed': 'Failed to schedule segment change',
  'segments.targeting.scheduleSucceeded': 'Segment change scheduled',
  'segments.targeting.scheduleUnavailableExpiringTargets': "Changes to expiring targets can't be scheduled.",
  'segments.targeting.scheduleUnavailableNoPermission':
    "You don't have permission to schedule changes to this segment.",
  'segments.targeting.testRunError': 'Something went wrong with test run. Try later.',
  'segments.targeting.testRunHint': "Test run a context to find out if it's included in this segment",
  'segments.targeting.testRunPendingHint': 'Ability to test unsaved changes is coming soon.',
  'segments.targeting.tooManyTargets':
    'Too many individual targets. Instead, use rules to target based on attributes. <link>Read about individual targeting</link>',
  'segments.targeting.updateError': 'Failed to update segment',
  'segments.targeting.updateFailed': 'Failed to update segment',
  'segments.targetingToolbar.evaluateContextLabel': 'evaluate context',
  'segments.targetingToolbar.json': 'JSON',
  'segments.targetingToolbar.jsonEditingLabel': 'JSON editing',
  'segments.targetingToolbar.switchToGui': 'Switch to GUI',
  'segments.targetingToolbar.switchToJson': 'Switch to JSON',
  'segments.targetingToolbar.testRunHint': "Test run a context to find out if it's included in this segment",
  'segments.targetingToolbar.testRunUnsavedHint': 'Ability to test unsaved changes is coming soon.',
  'segments.targetingToolbar.uiEditingLabel': 'UI editing',
  'segments.targetingToolbar.visual': 'Visual',
  'segments.testRun.basedOnCurrent': 'Based on current targeting',
  'segments.testRun.emptyState': 'Search for a context to see whether it belongs to this segment or not.',
  'segments.testRun.error': 'Something went wrong with test run. Try later.',
  'segments.testRun.errorHeading': 'Unable to check targeting',
  'segments.testRun.fetchingResults': 'Fetching results…',
  'segments.testRun.member': 'Context is member of this segment',
  'segments.testRun.notMember': 'Context is not part of this segment',
  'segments.testRun.running': 'Test run is running…',
  'segments.testRun.subtitle': 'See if a context is a member of this segment.',
  'segments.testRun.title': 'Test run a context',
  'segments.testRun.tryAgain': 'Try again.',
  'segments.uploadDialog.cancel': 'Cancel',
  'segments.uploadDialog.clearFileLabel': 'Clear selected file',
  'segments.uploadDialog.continue': 'Continue',
  'segments.uploadDialog.emptyFileBody':
    'The contents of the csv you are trying to upload is empty. This will result in wiping out all the current users for this segment.',
  'segments.uploadDialog.fileAndRowLimit': 'Files may not exceed {maxSize}MB or contain more than {maxRows} rows.',
  'segments.uploadDialog.fileLimit': 'Files may not exceed {maxSize}MB.',
  'segments.uploadDialog.headerRowHint':
    'The selected CSV file must <strong>not</strong> contain a header row. LaunchDarkly reads keys from the first column of the CSV file.',
  'segments.uploadDialog.learnMore': 'Learn more',
  'segments.uploadDialog.merge': 'Merge',
  'segments.uploadDialog.mergeDescription':
    'Add the content of the selected file to the existing segment. Duplicates will be skipped.',
  'segments.uploadDialog.overwrite': 'Overwrite',
  'segments.uploadDialog.overwriteDescription': 'Replace all existing content for this segment.',
  'segments.uploadDialog.requestApproval': 'Request approval',
  'segments.uploadDialog.selectFile': 'Select file',
  'segments.uploadDialog.subtitle': 'for <strong>{segmentName}</strong>',
  'segments.uploadDialog.title': 'Upload CSV',
  'segments.uploadDialog.uploadFile': 'Upload file',
  'segments.uploadDialog.uploadTypeLabel': 'Upload type',
  'segments.uploadDialog.uploading': 'Uploading',
  'sessions.page.title': 'Sessions',
  'sessions.replayCell.excludedBillingQuota': 'This session was recorded after your billing limit was reached.',
  'sessions.replayCell.excludedByFilter':
    'This session was excluded because it matches one of the exclusion filters in your project settings.',
  'sessions.replayCell.excludedByRateLimit': 'This session was excluded by the rate limit settings in your project.',
  'sessions.replayCell.excludedBySampling': 'This session was excluded by the sampling settings in your project.',
  'sessions.replayCell.excludedDefault': 'This session was excluded and is not available for viewing.',
  'sessions.replayCell.excludedIgnoredUser':
    'This session was excluded because the user is set as ignored in your project settings.',
  'sessions.replayCell.excludedInitializing': 'This session is still being processed and will be ready to view soon.',
  'sessions.replayCell.excludedLabel': 'Session excluded',
  'sessions.replayCell.excludedNoErrors':
    'This session was excluded because it has no errors, according to your project settings.',
  'sessions.replayCell.excludedNoInteractions': 'This session was excluded because it has no user interactions.',
  'sessions.replayCell.excludedRetention': 'This session is older than your retention period.',
  'sessions.replayCell.liveTag': 'Live',
  'sessions.replayCell.loadErrorTooltip': 'Failed to load session replay. Click to retry.',
  'sessions.replayCell.lockedLabel': 'Session replay locked',
  'sessions.replayCell.noSessionData': 'No session data',
  'sessions.replayCell.playLabel': 'Session replay',
  'sessions.replayCell.retryLabel': 'Retry loading session',
  'sessions.replayCell.sdkRequiredTooltip': 'Observability SDK installation is required. <setup>View setup</setup>',
  'sessions.replayCell.unavailableLabel': 'Session replay unavailable',
  'sessions.revokeAllSessions.confirm': 'Revoke',
  'sessions.revokeAllSessions.confirmationTitle': 'Revoke all sessions?',
  'sessions.revokeAllSessions.loading': 'Loading…',
  'sessions.revokeAllSessions.revokeAllSessions': 'Revoke all sessions',
  'sessions.revokeAllSessions.validationLabel': 'Type yes to confirm',
  'sessions.revokeAllSessions.warning':
    'You and all members of your organization will be logged out of LaunchDarkly immediately',
  'sessions.sessionSettings.durationDays': '{count, plural, one {Day} other {Days}}',
  'sessions.sessionSettings.durationHours': '{count, plural, one {Hour} other {Hours}}',
  'sessions.sessionSettings.durationMinutes': '{count, plural, one {Minute} other {Minutes}}',
  'sessions.sessionSettings.formLabel': 'Session config',
  'sessions.sessionSettings.loading': 'Loading…',
  'sessions.sessionSettings.refreshCheckbox': 'Refresh sessions automatically',
  'sessions.sessionSettings.refreshCheckboxLabel': 'Refresh session automatically',
  'sessions.sessionSettings.refreshHelp':
    'Automatically refresh the session expiration time when a user visits the website.',
  'sessions.sessionSettings.save': 'Save',
  'sessions.sessionSettings.saveLabel': 'save session configuration settings',
  'sessions.sessionSettings.saving': 'Saving',
  'sessions.sessionSettings.sessionDuration': 'Session duration',
  'sessions.sessionSettings.sessionDurationUnits': 'Session duration units',
  'sessions.unavailableToast.description':
    'Reach out to your account owner or email observability@launchdarkly.com to get access to sessions',
  'sessions.unavailableToast.title': 'Sessions unavailable',
  'settings.applicationsPage.title': 'Applications',
  'settings.authorizationPage.oauthApplications': 'OAuth applications',
  'settings.authorizationPage.title': 'Authorization',
  'settings.authorizationPage.tokens': 'Tokens',
  'settings.billingPage.title': 'Billing',
  'settings.billingPlansConfirmationPage.breadcrumb': 'Plans',
  'settings.billingPlansConfirmationPage.title': 'Confirmation',
  'settings.billingPlansCustomizePage.breadcrumb': 'Plans',
  'settings.billingPlansCustomizePage.title': 'Customize',
  'settings.billingPlansPage.title': 'Plans',
  'settings.billingPlansReviewPage.breadcrumb': 'Plans',
  'settings.billingPlansReviewPage.title': 'Review',
  'settings.codeAutomation.accountDefaultsSignInTooltip': 'Sign in with GitHub to change account defaults',
  'settings.codeAutomation.authorizationUnknownBody':
    'Something went wrong reading your GitHub connection. Try again. If this keeps happening, contact support.',
  'settings.codeAutomation.authorizationUnknownHeading': "We couldn't check your GitHub authorization",
  'settings.codeAutomation.contactSupport': 'Contact support',
  'settings.codeAutomation.description': "A project's automations run only on the repositories mapped to it here.",
  'settings.codeAutomation.docsLinkText': 'Read about the GitHub App integration',
  'settings.codeAutomation.errorHeading': 'Release Autopilot settings',
  'settings.codeAutomation.githubAccessHint':
    "Don't see what you're expecting? <link>Manage your GitHub connections</link>, or check with your project admin that you have access to the repositories you need.",
  'settings.codeAutomation.goToIntegration': 'Go to GitHub App integration',
  'settings.codeAutomation.linkAccount': 'Link account',
  'settings.codeAutomation.githubUnavailableBody':
    "We couldn't check which GitHub installations your account can access. This is usually temporary, and signing in again won't fix it.",
  'settings.codeAutomation.githubUnavailableHeading': "GitHub couldn't be reached",
  'settings.codeAutomation.heading': 'Map projects to repositories',
  'settings.codeAutomation.inaccessibleBody':
    "You are signed in to GitHub, but that account can't access the GitHub organization where the LaunchDarkly GitHub App is installed. Ask an owner of that organization to grant you access, or sign in with a GitHub account that already has it.",
  'settings.codeAutomation.inaccessibleHeading': "Your GitHub account can't access this installation",
  'settings.codeAutomation.loadError':
    'LaunchDarkly could not load Release Autopilot settings. Refresh the page to try again.',
  'settings.codeAutomation.notAuthorizedBody':
    "Release Autopilot is connected for your account. To change account defaults and manage repository mappings, sign in with a GitHub account that has access to your organization's GitHub App installation.",
  'settings.codeAutomation.notAuthorizedHeading': 'Sign in with GitHub to manage Release Autopilot settings',
  'settings.codeAutomation.notConnectedBody':
    'Install the LaunchDarkly GitHub App and authorize your GitHub account before mapping repositories to projects here.',
  'settings.codeAutomation.notConnectedHeading': 'Connect the GitHub App to get started',
  'settings.codeAutomation.retry': 'Try again',
  'settings.codeAutomation.signInWithDifferentGitHub': 'Sign in with a different GitHub account',
  'settings.codeAutomation.signInWithGitHub': 'Sign in with GitHub',
  'settings.codeAutomationPage.title': 'Release Autopilot settings',
  'settings.diagnosticUsagePage.title': 'Diagnostic usage',
  'settings.generalPage.breadcrumb': 'General',
  'settings.generalPage.title': 'General settings',
  'settings.integrationsPage.thereWasAn8': 'There was an issue loading integrations.',
  'settings.integrationsPage.title': 'Integrations',
  'settings.integrationsSentryPage.sentryIntegration': 'Sentry integration',
  'settings.integrationsSentryPage.thereWasAn7': 'There was an issue loading the Sentry integration configuration.',
  'settings.integrationsSentryPage.tryAgain': 'Try again',
  'settings.members_Page.title': 'Members',
  'settings.page.access': 'Access',
  'settings.page.applications': 'Applications',
  'settings.page.authorization': 'Authorization',
  'settings.page.billing': 'Billing',
  'settings.page.configuration': 'Configuration',
  'settings.page.connections': 'Connections',
  'settings.page.diagnosticUsage': 'Diagnostic usage',
  'settings.page.engineeringInsights': 'Engineering insights',
  'settings.page.evaluationTokens': 'Evaluation tokens',
  'settings.page.factorySettings': 'Release Autopilot',
  'settings.page.general': 'General',
  'settings.page.integrations': 'Integrations',
  'settings.page.members': 'Members',
  'settings.page.planUsage': 'Plan usage',
  'settings.page.plansAndUsage': 'Plans and usage',
  'settings.page.projects': 'Projects',
  'settings.page.relayProxy': 'Relay Proxy',
  'settings.page.roles': 'Roles',
  'settings.page.sdkKeys': 'SDK keys',
  'settings.page.sdks': 'SDKs',
  'settings.page.security': 'Security',
  'settings.page.settingsNavigation': 'Settings navigation',
  'settings.page.teams': 'Teams',
  'settings.page.title': 'Organization settings',
  'settings.planUsagePage.title': 'Plan usage',
  'settings.profilePage.backToSetup': 'Back to setup',
  'settings.profilePage.general': 'General',
  'settings.profilePage.settingsNavigation': 'Settings navigation',
  'settings.profilePage.title': 'Personal settings',
  'settings.projectsPage.title': 'Projects',
  'settings.relayPage.title': 'Relay Proxy',
  'settings.repoProjectMapping.accountDefaultLabel': '{capability} account default',
  'settings.repoProjectMapping.accountWideTag': 'Account-wide',
  'settings.repoProjectMapping.actionsColumnLabel': 'Actions',
  'settings.repoProjectMapping.addRepository': 'Add repository',
  'settings.repoProjectMapping.discardChanges': 'Discard changes',
  'settings.repoProjectMapping.incompleteMappingLabel': 'This project needs a repository mapped before it can be saved',
  'settings.repoProjectMapping.incompleteMappingWarning': 'Map at least one repository to this project before saving.',
  'settings.repoProjectMapping.projectColumnLabel': 'Project',
  'settings.repoProjectMapping.releasePolicyRequired': 'At least one release policy must be defined for this project.',
  'settings.repoProjectMapping.removeProjectLabel': 'Remove {projectName} mapping',
  'settings.repoProjectMapping.removeProjectTooltip': 'Remove {projectName} mapping',
  'settings.repoProjectMapping.repoCapabilityLabel': '{capability} for {repo}',
  'settings.repoProjectMapping.saveChanges': 'Save changes',
  'settings.repoProjectMapping.searchPlaceholder': 'Search repositories',
  'settings.repoProjectMapping.tableLabel': 'Map projects to repositories',
  'settings.rolesNewPage.title': 'New role',
  'settings.rolesPage.newRole': 'New role',
  'settings.roles_Page.title': 'Roles',
  'settings.sdkKeysPage.title': 'SDK keys',
  'settings.sdksPage.loading': 'Loading…',
  'settings.sdksPage.title': 'SDK Details',
  'settings.sdksPage.title2': 'SDKs',
  'settings.securityPage.title': 'Security',
  'settings.teamsMembersPage.failedToLoad10': 'Failed to load members',
  'settings.teamsPage.loading': 'Loading…',
  'settings.teamsPage.notFoundLead': 'A team with the key {teamKey} was not found.',
  'settings.teamsPage.title': 'Team not found',
  'settings.teamsPage.viewExistingTeams': 'View existing teams, or create a new team',
  'settings.teamsPermissionsPage.loading': 'Loading…',
  'settings.teams_Page.title': 'Teams',
  'settings.tokenLimitsPage.title': 'Evaluation tokens',
  'shortcuts.saveCommand.hint':
    'Shortcut will include all filters, search queries, and environments that are currently defined.',
  'shortcuts.saveCommand.saveLabel': 'Save',
  'shortcuts.saveCommand.unavailable': 'Shortcuts aren’t available on this page.',
  'shortcuts.saveDialog.cancel': 'Cancel',
  'shortcuts.saveDialog.heading': 'Save shortcut',
  'shortcuts.saveDialog.hint':
    'Shortcut will include all filters, search queries, and environments that are currently defined.',
  'shortcuts.saveDialog.save': 'Save',
  'shortcuts.saveForm.iconLabel': 'Icon',
  'shortcuts.saveForm.nameLabel': 'Name',
  'shortcuts.saveForm.namePlaceholder': 'e.g my favorite environments',
  'signUp.awsRegistration.alreadySetUp': 'Already finished setting up?',
  'signUp.awsRegistration.contactSupport': 'Contact support',
  'signUp.awsRegistration.failedBody':
    'The registration link from AWS Marketplace may have expired or already been used. Start again from AWS Marketplace to get a new one.',
  'signUp.awsRegistration.failedHeading': "We couldn't verify your subscription",
  'signUp.awsRegistration.failedStep1': 'Go to Manage subscriptions in AWS Marketplace.',
  'signUp.awsRegistration.failedStep2': 'Find your LaunchDarkly subscription.',
  'signUp.awsRegistration.failedStep3':
    'Choose the option to set up your account. AWS sends you back here with a new link.',
  'signUp.awsRegistration.failedStepsHeading': 'To try again',
  'signUp.awsRegistration.failedSupport': 'Still having trouble?',
  'signUp.awsRegistration.linkedBody':
    'This AWS Marketplace subscription is already linked to a LaunchDarkly account. Sign in to that account to keep using it.',
  'signUp.awsRegistration.linkedHeading': 'This subscription is already set up',
  'signUp.awsRegistration.linkedSupport': "Not sure which account it's linked to, or need to move it?",
  'signUp.awsRegistration.returnToAws': 'Go to AWS Marketplace',
  'signUp.awsRegistration.signIn': 'Sign in',
  'signUp.awsRegistration.signInToAccount': 'Sign in to your account',
  'signUp.emailVerification.checkYourEmail': 'Check your email',
  'signUp.emailVerification.clickTheLink': 'Click the link in the email to confirm your identity.',
  'signUp.emailVerification.codeBody': "You're almost there. Enter the code we sent to <strong>{email}</strong>.",
  'signUp.emailVerification.codeHeading': 'Check your email for a 6-digit code',
  'signUp.emailVerification.codeInputLabel': 'Verification code',
  'signUp.emailVerification.digitLabel': 'Digit {index} of {total}',
  'signUp.emailVerification.existingAccountBody':
    'Check <strong>{email}</strong> for an email from LaunchDarkly with next steps.',
  'signUp.emailVerification.linkExpiredOr': "Link expired or didn't get an email?",
  'signUp.emailVerification.linkHeading': 'Check your email for a verification link',
  'signUp.emailVerification.resend': 'Resend verification email',
  'signUp.emailVerification.resending': 'Sending…',
  'signUp.emailVerification.signInDifferentEmail': 'Sign in with a different email',
  'signUp.emailVerification.signInWith': 'Sign in with a different account',
  'signUp.emailVerification.verifyError': 'Verification failed. Try again.',
  'signUp.emailVerification.youMustWait': 'You must wait one minute before trying again.',
  'signUp.signUpForm.acceptTerms': 'Accept the',
  'signUp.signUpForm.demoLink': 'Get a live demo',
  'signUp.signUpForm.demoLinkPrefix': 'or',
  'signUp.signUpForm.emailLabel': 'Email',
  'signUp.signUpForm.firstNameLabel': 'First name',
  'signUp.signUpForm.githubButton': 'Sign up with GitHub',
  'signUp.signUpForm.lastNameLabel': 'Last name',
  'signUp.signUpForm.marketingOptIn': '(Optional) Get occasional emails about new features and events',
  'signUp.signUpForm.organizationLabel': 'Organization',
  'signUp.signUpForm.passwordLabel': 'Password',
  'signUp.signUpForm.submit': 'Start my {days}-day trial now',
  'signUp.signUpForm.submitError': 'An error occurred. Try again.',
  'signUp.signUpForm.submitting': 'Submitting…',
  'signUp.signUpForm.termsLink': 'terms and conditions',
  'signUp.signUpForm.termsRequiredError': 'You must accept the terms and conditions',
  'signUp.signUpIntentTabs.enterpriseDemo': 'Enterprise Demo',
  'signUp.signUpIntentTabs.label': 'Sign up options',
  'signUp.signUpIntentTabs.trial': 'Start {days}-Day Trial',
  'signUp.signUpPage.alreadyHaveAn': 'Already have an account?',
  'signUp.signUpPage.awsTrialPrefix': 'Start with {days} days of full access, then continue on the',
  'signUp.signUpPage.awsTrialSuffix': 'billed through AWS Marketplace.',
  'signUp.signUpPage.developerAccountLink': 'Developer account',
  'signUp.signUpPage.enterpriseBody':
    'Partner with our sales team to design a custom evaluation. We’ll help you map out the impact of LaunchDarkly at scale.',
  'signUp.signUpPage.enterpriseHeading': 'Evaluate Enterprise Solutions',
  'signUp.signUpPage.foundationPlanLink': 'Foundation plan',
  'signUp.signUpPage.freeTierSuffix': 'with limits. No credit card required.',
  'signUp.signUpPage.heading': 'Create your free account',
  'signUp.signUpPage.legalPrefix': 'By using LaunchDarkly, you agree to the LaunchDarkly',
  'signUp.signUpPage.privacyLink': 'Privacy Policies',
  'signUp.signUpPage.signIn': 'Sign in',
  'signUp.signUpPage.termsLink': 'Terms',
  'signUp.signUpPage.trialPrefix': 'Start with {days} days of full access, then continue on a free',
  'smartLinks.confluenceEmbed.openInConfluence': 'Open in Confluence',
  'smartLinks.confluenceEmbed.viewPageLabel': 'view Confluence page',
  'support.helpButton.dismissLabel': 'Dismiss help button',
  'support.helpButton.openLabel': 'Get help',
  'support.helpButton.openText': 'Get Help',
  'targeting.actionMenu.bulkEdit': 'Bulk edit',
  'targeting.actionMenu.bulkEditDescription': 'Easily add, remove, or replace individual targets',
  'targeting.actionMenu.convertToSegment': 'Convert to segment',
  'targeting.actionMenu.convertToSegmentApprovalsTooltip':
    'Convert to segment is unavailable because approvals are required for this environment',
  'targeting.actionMenu.exportCsv': 'Export CSV',
  'targeting.actionMenu.exportCsvDescription': 'Download a CSV of individual targets',
  'targeting.actionMenu.menuLabel': 'Open individual targeting menu',
  'targeting.actionMenu.removeAll': 'Remove all',
  'targeting.actionMenu.search': 'Search',
  'targeting.actionMenu.searchLabel': 'Open individual targeting search',
  'targeting.actionMenu.searchTooltip': 'Search for a target',
  'targeting.actionMenu.upgradeDescription': 'Upgrade your plan to use this feature. Click to learn more.',
  'targeting.addTargetForm.add': 'Add',
  'targeting.addTargetForm.contextKeyLabel': 'Context key',
  'targeting.addTargetForm.contextPlaceholder': 'Add or search contexts',
  'targeting.addTargetForm.duplicateTarget': 'This context is already targeted to the selected variation',
  'targeting.addTargetForm.kindLabel': 'Kind',
  'targeting.addTargetForm.variationLabel': 'Variation',
  'targeting.addTargetingMenu.addItem': 'Add {label}',
  'targeting.addTriggerDialog.addAdaptiveTrigger': 'Add adaptive trigger',
  'targeting.addTriggerDialog.advancedThresholdSettings': 'Advanced threshold settings',
  'targeting.addTriggerDialog.alertThreshold': 'Alert threshold',
  'targeting.addTriggerDialog.alertThresholdConfidenceLabel': 'Alert threshold confidence',
  'targeting.addTriggerDialog.alertThresholdInfoLabel': 'Alert threshold info',
  'targeting.addTriggerDialog.alertWindow': 'Alert window',
  'targeting.addTriggerDialog.alertWindowInfoLabel': 'Alert window info',
  'targeting.addTriggerDialog.analysisUnit': 'Analysis unit',
  'targeting.addTriggerDialog.analysisUnitInfoLabel': 'Analysis unit info',
  'targeting.addTriggerDialog.backLabel': 'Back',
  'targeting.addTriggerDialog.cancel': 'Cancel',
  'targeting.addTriggerDialog.closeLabel': 'Close',
  'targeting.addTriggerDialog.condition': 'Condition',
  'targeting.addTriggerDialog.conditionExceeds': 'exceeds',
  'targeting.addTriggerDialog.conditionInfoLabel': 'Condition info',
  'targeting.addTriggerDialog.conditionWhen': 'When',
  'targeting.addTriggerDialog.conditionWithin': 'within',
  'targeting.addTriggerDialog.constantAlertsFire':
    'Constant alerts fire when a value exceeds a fixed number. Anomaly alerts fire when a value deviates from its baseline.',
  'targeting.addTriggerDialog.cooldown': 'Cooldown',
  'targeting.addTriggerDialog.cooldownInfoLabel': 'Cooldown info',
  'targeting.addTriggerDialog.customizeTrigger': 'Customize trigger',
  'targeting.addTriggerDialog.durationUnitLabel': 'Duration unit',
  'targeting.addTriggerDialog.errorLoadingMetrics': 'Error loading metrics',
  'targeting.addTriggerDialog.evaluationDelayInfoLabel': 'Evaluation delay info',
  'targeting.addTriggerDialog.evaluationDelayMinutes': 'Evaluation delay (minutes)',
  'targeting.addTriggerDialog.evaluationDelayMinutesLabel': 'Evaluation delay in minutes',
  'targeting.addTriggerDialog.evaluationWindowAmountLabel': 'Evaluation window amount',
  'targeting.addTriggerDialog.lockedVariationTooltip':
    'All triggers for this {resourceNoun} must serve the same variation',
  'targeting.addTriggerDialog.missingAgentControlMetricsBody':
    'LaunchDarkly generates these metrics from AI SDK events. <link>Learn more</link>',
  'targeting.addTriggerDialog.notificationSettings': 'Notification settings',
  'targeting.addTriggerDialog.sentryMissingPermission':
    "The Sentry integration can't read Monitors. Reinstall it with read access to Alerts. <link>Open the Sentry integration</link>",
  'targeting.addTriggerDialog.sentryMonitorBreachInfo':
    'This trigger fires each time Sentry reports the Monitor breached: when Sentry opens an issue for it, or the issue regresses.',
  'targeting.addTriggerDialog.sentryMonitorDisabled': 'Disabled in Sentry',
  'targeting.addTriggerDialog.sentryMonitorLabel': 'Monitor',
  'targeting.addTriggerDialog.sentryMonitorPlaceholder': 'Select a Sentry Monitor',
  'targeting.addTriggerDialog.sentryMonitorSource': 'Sentry Monitor',
  'targeting.addTriggerDialog.sentryMonitorsError': "Sentry Monitors couldn't be loaded. Try again later.",
  'targeting.addTriggerDialog.sentryMonitorsLoading': 'Loading Sentry Monitors...',
  'targeting.addTriggerDialog.sentryMonitorsLoadMoreError': "More Sentry Monitors couldn't be loaded.",
  'targeting.addTriggerDialog.sentryMonitorsRetry': 'Try again',
  'targeting.addTriggerDialog.sentryNoMonitors':
    'The Sentry projects mapped to this environment have no metric Monitors yet.',
  'targeting.addTriggerDialog.sentryNotConnected':
    'Connect the Sentry integration to fire triggers from Sentry Monitors. <link>Open the Sentry integration</link>',
  'targeting.addTriggerDialog.sentryNotMapped':
    'No Sentry project is mapped to this environment. Map one to use its Monitors. <link>Open the Sentry integration</link>',
  'targeting.addTriggerDialog.serveVariationConditionLabel': 'Variation to serve when the condition is met',
  'targeting.addTriggerDialog.serveVariationLabel': 'Variation to serve when the trigger fires',
  'targeting.addTriggerDialog.source': 'Source',
  'targeting.addTriggerDialog.sourceTypeLabel': 'Source type',
  'targeting.addTriggerDialog.subtitle':
    'An adaptive trigger creates an alert and switches to a selected variation when a metric crosses a threshold you set. <link>Read about adaptive triggers</link>',
  'targeting.addTriggerDialog.switchVariationInfoLabel': 'Switch variation info',
  'targeting.addTriggerDialog.switchVariationTo': 'Switch variation to',
  'targeting.addTriggerDialog.switchVariationTooltip': 'The {resourceNoun} variation to serve when this trigger fires',
  'targeting.addTriggerDialog.theContextKind':
    'The context kind the metric is measured per. This metric supports more than one.',
  'targeting.addTriggerDialog.theEvaluationRule': 'The evaluation rule for the alert to be triggered',
  'targeting.addTriggerDialog.theTimePeriod':
    'The time period after an alert is triggered before it can be triggered again',
  'targeting.addTriggerDialog.theTimePeriod2':
    'The time period to wait after a window ends before the alert is evaluated. Useful when data arrives late.',
  'targeting.addTriggerDialog.theTimePeriod3': 'The time period over which the alert is evaluated',
  'targeting.addTriggerDialog.theValueThat': 'The value that must be met for the alert to be triggered',
  'targeting.addTriggerDialog.thenServe': 'Then serve',
  'targeting.addTriggerDialog.thisAdaptiveTrigger':
    'This adaptive trigger applies changes without approval, even if approvals are required.',
  'targeting.addTriggerDialog.thisProjectDoesn': "This project doesn't have any AgentControl metrics.",
  'targeting.addTriggerDialog.threshold': 'Threshold',
  'targeting.addTriggerDialog.thresholdConditionLabel': 'Threshold condition',
  'targeting.addTriggerDialog.thresholdLabel': 'AI completion error count threshold',
  'targeting.addTriggerDialog.thresholdTypeInfoLabel': 'Threshold type info',
  'targeting.addTriggerDialog.thresholdTypeLabel': 'Threshold type',
  'targeting.addTriggerDialog.type': 'Type',
  'targeting.addTriggerDialog.viewInSentry': 'View Monitor in Sentry',
  'targeting.aiConfigTargeting.addTrigger': 'Add trigger',
  'targeting.aiConfigTargeting.createAVariation': 'Create a variation to enable targeting',
  'targeting.aiConfigTargeting.loading': 'Loading…',
  'targeting.aiConfigTargeting.noVariationsPublished': 'No variations published for this config',
  'targeting.aiConfigTargeting.removeTriggerLabel': 'Remove trigger',
  'targeting.aiPromptDialog.alphaBadge': 'Alpha',
  'targeting.aiPromptDialog.alphaBadgeLabel': 'Alpha software',
  'targeting.aiPromptDialog.alphaHint': 'This is alpha software. You will be able to edit the results of this prompt.',
  'targeting.aiPromptDialog.body': 'Describe how you want to roll out your flag',
  'targeting.aiPromptDialog.cancel': 'Cancel',
  'targeting.aiPromptDialog.error': 'There was an error generating your targeting config. Try tweaking your prompt.',
  'targeting.aiPromptDialog.heading': 'Configure your flag using AI',
  'targeting.aiPromptDialog.promptLabel': 'Prompt for generating targeting config',
  'targeting.aiPromptDialog.promptPlaceholder': 'Roll out to…',
  'targeting.aiPromptTrigger.description': 'Use AI to configure your flag',
  'targeting.aiPromptTrigger.label': 'Ask LaunchDarkly to configure',
  'targeting.applyApprovalDialog.apply': 'Apply',
  'targeting.applyApprovalDialog.applyChanges': 'Apply changes',
  'targeting.applyApprovalDialog.bodyAllEnvironments':
    'This will make changes to the <flag>{resourceName}</flag> flag in all environments.',
  'targeting.applyApprovalDialog.bodyWithEnvironment':
    'This will make changes to the <flag>{resourceName}</flag> flag in the <environment>{environmentName}</environment> environment.',
  'targeting.applyApprovalDialog.cancel': 'Cancel',
  'targeting.applyApprovalDialog.commentLabel': 'Comment',
  'targeting.applyApprovalDialog.commentPlaceholder': 'Leave a comment',
  'targeting.applyApprovalDialog.commentRequiredLabel': 'Comment (required)',
  'targeting.applyApprovalDialog.commentRequiredPlaceholder': 'Comments are required for changes in this environment',
  'targeting.approvalRequestCard.actionsLabel': 'Approval request actions',
  'targeting.approvalRequestCard.approvalRequested': 'Approval requested',
  'targeting.approvalRequestCard.approved': 'Approved',
  'targeting.approvalRequestCard.copyLinkAction': 'Copy link',
  'targeting.approvalRequestCard.declined': 'Declined',
  'targeting.approvalRequestCard.deleteAction': 'Delete',
  'targeting.approvalRequestCard.nestedDeleteScheduledChange': '<action>Delete</action> scheduled change',
  'targeting.approvalRequestCard.nestedScheduledFor': 'Scheduled for {date}',
  'targeting.approvalRequestCard.pendingReview': 'Pending review',
  'targeting.approvalRequestCard.view': 'View',
  'targeting.attributePicker.loadingLabel': 'Loading attributes',
  'targeting.attributePicker.placeholder': 'Select an attribute',
  'targeting.attributePicker.showLabel': 'Show attributes',
  'targeting.bucketBy.attribute': 'Attribute',
  'targeting.bucketBy.attributeTriggerLabel': 'Show attributes',
  'targeting.bucketBy.by': 'By',
  'targeting.bucketBy.contextKind': 'Context kind',
  'targeting.bucketBy.contextKindTriggerLabel': 'Show context kinds',
  'targeting.bucketBy.moreInfoLabel': 'More information',
  'targeting.bucketBy.otherContextKindsTooltip':
    'If this rule is evaluated for context kinds other than the one selected, those contexts will be served the first variation',
  'targeting.bucketBy.placeholder': 'Select an attribute',
  'targeting.bulkEdit.title': 'Bulk edit targets',
  'targeting.bulkEditDialog.addMode': 'Add',
  'targeting.bulkEditDialog.addTargetCount': 'Add {count, plural, one {# target} other {# targets}}',
  'targeting.bulkEditDialog.addTargets': 'Add targets',
  'targeting.bulkEditDialog.allTab': 'All ({count})',
  'targeting.bulkEditDialog.allTabEmpty': 'There are no targets for this operation.',
  'targeting.bulkEditDialog.allTabTooltip': 'All contexts matching entered keys',
  'targeting.bulkEditDialog.alreadyTargeted': 'Already targeted',
  'targeting.bulkEditDialog.batchLimitExceeded':
    'Number of new targets must not exceed batch limit of {available, number}',
  'targeting.bulkEditDialog.batchLimitExceededWithPending':
    'Number of new targets must not exceed {available, number} for this batch ({limit, number} maximum targets, {pending, number} pending targets).',
  'targeting.bulkEditDialog.blankState': 'Enter context keys on the left to search. Add up to 1,500 targets at a time.',
  'targeting.bulkEditDialog.cancel': 'Cancel',
  'targeting.bulkEditDialog.closeLabel': 'close modal',
  'targeting.bulkEditDialog.contextCount': '{count, plural, one {# context} other {# contexts}}',
  'targeting.bulkEditDialog.contextKeysLabel': 'Context keys',
  'targeting.bulkEditDialog.contextKeysPlaceholder':
    'Paste your comma-separated or newline-separated context keys here…',
  'targeting.bulkEditDialog.currentTab': 'Current ({count})',
  'targeting.bulkEditDialog.currentTabEmpty': 'There are no targets.',
  'targeting.bulkEditDialog.currentTabTooltip': 'Currently targeted contexts',
  'targeting.bulkEditDialog.description':
    'Add or edit targets for this {resourceKind}. This {resourceKind} currently has {targetCount}/15,000 targets.',
  'targeting.bulkEditDialog.foundTabEmpty':
    'LaunchDarkly has not seen any contexts in the last 30 days that match the entered keys.',
  'targeting.bulkEditDialog.foundTabTooltip': 'Contexts used in recent evaluations',
  'targeting.bulkEditDialog.loadError': 'An unexpected error occurred.',
  'targeting.bulkEditDialog.lookingUp': 'Looking up individual targets',
  'targeting.bulkEditDialog.modeReplaceTargets': 'with targets',
  'targeting.bulkEditDialog.modeTargets': 'targets',
  'targeting.bulkEditDialog.modeWithVariation': 'with variation',
  'targeting.bulkEditDialog.notFoundTabEmpty':
    'All contexts matching the entered keys have been used in recent evaluations.',
  'targeting.bulkEditDialog.notFoundTabTooltip': 'Contexts not seen by LaunchDarkly in at least 30 days',
  'targeting.bulkEditDialog.notTargeted': 'Not targeted',
  'targeting.bulkEditDialog.removeMode': 'Remove',
  'targeting.bulkEditDialog.removeTargetCount': 'Remove {count, plural, one {# target} other {# targets}}',
  'targeting.bulkEditDialog.removeTargets': 'Remove targets',
  'targeting.bulkEditDialog.replaceMode': 'Replace',
  'targeting.bulkEditDialog.replaceTargetCount': 'Replace with {count, plural, one {# target} other {# targets}}',
  'targeting.bulkEditDialog.replaceTargets': 'Replace with targets',
  'targeting.bulkEditDialog.tabsLabel': 'tabs',
  'targeting.bulkEditTargets.actionLabel': 'Action',
  'targeting.bulkEditTargets.addMode': 'Add',
  'targeting.bulkEditTargets.addTargetCount': 'Add {count, plural, one {# target} other {# targets}}',
  'targeting.bulkEditTargets.addTargets': 'Add targets',
  'targeting.bulkEditTargets.allTab': 'All ({count})',
  'targeting.bulkEditTargets.allTabTooltip': 'All contexts matching entered keys',
  'targeting.bulkEditTargets.blankState':
    'Enter context keys on the left to search. Add up to 1,500 targets at a time.',
  'targeting.bulkEditTargets.blankStateShort': 'Enter context keys on the left to search.',
  'targeting.bulkEditTargets.cancel': 'Cancel',
  'targeting.bulkEditTargets.closeLabel': 'close modal',
  'targeting.bulkEditTargets.contextKeys': 'Context keys',
  'targeting.bulkEditTargets.contextKeysPlaceholder':
    'Paste your comma-separated or newline-separated context keys here…',
  'targeting.bulkEditTargets.contextKindLabel': 'Context kind',
  'targeting.bulkEditTargets.currentTab': 'Current ({count})',
  'targeting.bulkEditTargets.currentTabTooltip': 'Currently targeted contexts',
  'targeting.bulkEditTargets.currentTargetsLabel': 'Current targets',
  'targeting.bulkEditTargets.description':
    'Add or edit targets for this Flag. This Flag currently has {count}/15,000 targets.',
  'targeting.bulkEditTargets.foundTab': 'Found ({count})',
  'targeting.bulkEditTargets.foundTabTooltip': 'Contexts used in recent evaluations',
  'targeting.bulkEditTargets.heading': 'Bulk edit targets',
  'targeting.bulkEditTargets.loadError': 'An unexpected error occurred.',
  'targeting.bulkEditTargets.noMatchingContexts': 'No matching contexts.',
  'targeting.bulkEditTargets.noTargets': 'There are no targets.',
  'targeting.bulkEditTargets.notFoundTab': 'Not found ({count})',
  'targeting.bulkEditTargets.notFoundTabTooltip': 'Contexts not seen by LaunchDarkly in at least 30 days',
  'targeting.bulkEditTargets.removeMode': 'Remove',
  'targeting.bulkEditTargets.removeTargetCount': 'Remove {count, plural, one {# target} other {# targets}}',
  'targeting.bulkEditTargets.removeTargets': 'Remove targets',
  'targeting.bulkEditTargets.replaceMode': 'Replace',
  'targeting.bulkEditTargets.replaceTargetCount': 'Replace with {count, plural, one {# target} other {# targets}}',
  'targeting.bulkEditTargets.replaceTargets': 'Replace with targets',
  'targeting.bulkEditTargets.resultLabel': '{found, select, true {Found} other {Not found}}: {key}',
  'targeting.bulkEditTargets.searchingLabel': 'Searching contexts',
  'targeting.bulkEditTargets.tabsLabel': 'tabs',
  'targeting.bulkEditTargets.targetVariationLabel': 'Target variation',
  'targeting.bulkEditTargets.targetsWithVariation': 'targets with variation',
  'targeting.changeSummary.addIndividualTargets': 'Add individual targets',
  'targeting.changeSummary.addPrerequisiteFlag': 'Add prerequisite flag <bold>{flagName}</bold>',
  'targeting.changeSummary.addRule': 'Add {isDisabled, select, true {disabled } other {}}{ruleLabel}',
  'targeting.changeSummary.addRuleConditions': 'Add rule conditions',
  'targeting.changeSummary.addRuleWithGuardedRollout': 'Add {ruleLabel} with guarded rollout',
  'targeting.changeSummary.addRuleWithProgressiveRollout': 'Add {ruleLabel} with progressive rollout',
  'targeting.changeSummary.addRuleWithProgressiveRolloutLabel': 'Add rule with progressive rollout',
  'targeting.changeSummary.addValuesToClause': 'Add {count, plural, one {value} other {values}}',
  'targeting.changeSummary.addValuesToRuleCondition': 'Add values to rule condition',
  'targeting.changeSummary.advanceAutomatedRelease': 'Advance phased release to the next phase',
  'targeting.changeSummary.allTargets': 'all {contextKind} targets',
  'targeting.changeSummary.categoryDefaultRule': 'Default rule',
  'targeting.changeSummary.categoryIndividualTargets': 'Individual targets',
  'targeting.changeSummary.categoryOffVariation': 'Off variation',
  'targeting.changeSummary.categoryOther': 'Other',
  'targeting.changeSummary.categoryPrerequisites': 'Prerequisites',
  'targeting.changeSummary.categoryRules': 'Rules',
  'targeting.changeSummary.categoryTargeting': 'Targeting',
  'targeting.changeSummary.categoryVariations': 'Variations',
  'targeting.changeSummary.clauseAnd': 'and',
  'targeting.changeSummary.clauseAttribute': 'attribute',
  'targeting.changeSummary.clauseContext': 'context',
  'targeting.changeSummary.clauseIf': 'If',
  'targeting.changeSummary.clauseInSegment': 'is in segment',
  'targeting.changeSummary.clauseMatches': 'matches',
  'targeting.changeSummary.clauseNegatedOperator': 'not {operator}',
  'targeting.changeSummary.clauseNotInSegment': 'is not in segment',
  'targeting.changeSummary.clearAllIndividualTargets': 'Clear all individual targets',
  'targeting.changeSummary.defaultContextKind': 'individual',
  'targeting.changeSummary.deleteScheduledChange': 'Delete scheduled change',
  'targeting.changeSummary.disableRule': 'Disable rule',
  'targeting.changeSummary.enableRule': 'Enable rule',
  'targeting.changeSummary.expiringTargets': 'Expiring targets',
  'targeting.changeSummary.fromClause': 'from clause',
  'targeting.changeSummary.individualTargeting': 'individual targeting',
  'targeting.changeSummary.noChangesDetected': 'No changes detected',
  'targeting.changeSummary.off': 'Off',
  'targeting.changeSummary.on': 'On',
  'targeting.changeSummary.prerequisiteListItem': '<bold>{flagName}</bold> with variation <variation></variation>',
  'targeting.changeSummary.removeIndividualTargets': 'Remove individual targets',
  'targeting.changeSummary.removePrerequisiteFlag': 'Remove prerequisite flag <bold>{flagName}</bold>',
  'targeting.changeSummary.removeRule': 'Remove rule',
  'targeting.changeSummary.removeRuleConditions': 'Remove rule conditions',
  'targeting.changeSummary.removeValuesFromClause': 'Remove {count, plural, one {value} other {values}}',
  'targeting.changeSummary.removeValuesFromRuleCondition': 'Remove values from rule condition',
  'targeting.changeSummary.reorderRules': 'Reorder rules',
  'targeting.changeSummary.reorderRulesHeading': 'Reorder rules:',
  'targeting.changeSummary.reorderServe': 'serve',
  'targeting.changeSummary.reorderVariation': 'variation',
  'targeting.changeSummary.replacePrerequisites': 'Replace prerequisites',
  'targeting.changeSummary.replaceRules': 'Replace rules',
  'targeting.changeSummary.setDefaultRuleToServeVariation': 'Set default rule to serve variation',
  'targeting.changeSummary.setDefaultRuleWithGuardedRollout': 'Set default rule with guarded rollout',
  'targeting.changeSummary.setDefaultRuleWithProgressiveRollout': 'Set default rule with progressive rollout',
  'targeting.changeSummary.startAutomatedRelease': 'Start automated release',
  'targeting.changeSummary.stopAutomatedRelease': 'Stop automated release',
  'targeting.changeSummary.stopGuardedRolloutOnDefaultRule': 'Stop guarded rollout on default rule',
  'targeting.changeSummary.stopGuardedRolloutOnRule': 'Stop guarded rollout on rule',
  'targeting.changeSummary.targetExtraCount': ', and {count} more',
  'targeting.changeSummary.targetForContextKind': 'for {contextKind}',
  'targeting.changeSummary.targetPrepFrom': 'from',
  'targeting.changeSummary.targetPrepTo': 'to',
  'targeting.changeSummary.targetVerbAdd': 'Add',
  'targeting.changeSummary.targetVerbRemove': 'Remove',
  'targeting.changeSummary.toClause': 'to clause',
  'targeting.changeSummary.toTheVariation': 'to the variation',
  'targeting.changeSummary.unavailableVariation': 'unavailable variation',
  'targeting.changeSummary.updateClause': 'Update rule condition',
  'targeting.changeSummary.updateOffVariation': 'Update off variation',
  'targeting.changeSummary.updatePrerequisite': 'Update prerequisite',
  'targeting.changeSummary.updatePrerequisiteFlag': 'Update prerequisite flag <bold>{flagName}</bold>',
  'targeting.changeSummary.updateRuleDescription': 'Update rule description',
  'targeting.changeSummary.updateRuleDescriptionTo': 'Update rule description to <bold>{description}</bold>',
  'targeting.changeSummary.updateRuleTrackEvents': 'Update rule event tracking',
  'targeting.changeSummary.updateRuleWithGuardedRollout': 'Update rule with guarded rollout',
  'targeting.changeSummary.updateRuleWithProgressiveRollout': 'Update rule with progressive rollout',
  'targeting.changeSummary.updateToggleTo': 'Update targeting to',
  'targeting.changeSummary.withVariation': 'with variation',
  'targeting.clauseEditor.addClauseLabel': 'Add a clause',
  'targeting.clauseEditor.attributeFieldLabel': 'Attribute',
  'targeting.clauseEditor.contextKindFieldLabel': 'Context kind',
  'targeting.clauseEditor.operatorFieldLabel': 'Operator',
  'targeting.clauseReadOnly.contextHeading': 'Context',
  'targeting.clauseReadOnly.leadingWhitespace': 'This value contains leading whitespace',
  'targeting.clauseReadOnly.trailingWhitespace': 'This value contains trailing whitespace',
  'targeting.clauseReadOnly.valuesLabel': 'Clause values',
  'targeting.clauseValidation.attributeRequired': 'You must select a user attribute',
  'targeting.clauseValidation.invalidAttribute':
    'Invalid attribute "{attributeName}" specified. Targeting by this attribute is not allowed.',
  'targeting.clauseValidation.invalidRegex': 'You must specify a valid regular expression',
  'targeting.clauseValidation.invalidSemver': 'You must specify a valid semantic version',
  'targeting.clauseValidation.operatorRequired': 'You must select an operator',
  'targeting.clauseValidation.osNameRequired': 'Make sure to select OS name when targeting OS versions.',
  'targeting.clauseValidation.segmentRequired': 'You must select at least one segment',
  'targeting.clauseValues.booleanLabel': 'Select a value',
  'targeting.clauseValues.listLabel': 'Clause values',
  'targeting.clauseValues.nonStringContextKey':
    "This value is a {type}. Context keys are strings, so it won't match any context.",
  'targeting.clauseValues.notAString': 'Not a string',
  'targeting.clauseValues.regexPlaceholder': 'Eg. ^foo$',
  'targeting.clauseValues.selectPlaceholder': 'Select values',
  'targeting.clauseValues.showLess': 'show less',
  'targeting.commentApprovalDialog.addComment': 'Add comment',
  'targeting.commentApprovalDialog.cancel': 'Cancel',
  'targeting.commentApprovalDialog.comment': 'Comment',
  'targeting.commentApprovalDialog.commentPlaceholder': 'Write comment here…',
  'targeting.confirmDiscardDialog.body': 'You have unsaved input in the review form. Discard it?',
  'targeting.confirmDiscardDialog.discard': 'Discard',
  'targeting.confirmDiscardDialog.discardUnsavedInput': 'Discard unsaved input?',
  'targeting.confirmDiscardDialog.keepEditing': 'Keep editing',
  'targeting.conflictAcknowledgment.checkboxLabel':
    'I acknowledge saving this change will introduce conflicts with pending changes',
  'targeting.contextKeyValues.searchPlaceholder': 'Search contexts by key or name',
  'targeting.contextKindPicker.inputLabel': 'Select context kind',
  'targeting.contextKindPicker.kindAttributeDescription': 'Option for matching context kinds',
  'targeting.contextKindPicker.label': 'Select a context kind',
  'targeting.contextKindPicker.listLabel': 'Context Kinds',
  'targeting.contextKindPicker.loadingLabel': 'Loading context kinds',
  'targeting.contextKindPicker.loadingValues': 'Loading values',
  'targeting.contextKindPicker.noValuesFound': 'No values found',
  'targeting.contextKindPicker.openLabel': 'Pick context kind',
  'targeting.contextKindPicker.placeholder': 'Select a context kind',
  'targeting.contextKindPicker.selectPlaceholder': 'Select a context kind',
  'targeting.contextKindsDialog.cancel': 'Cancel',
  'targeting.contextKindsDialog.clearWarning':
    'Removing populated context kinds will also clear the targets for that kind',
  'targeting.contextKindsDialog.confirmClearWarning':
    'Removing populated context kinds will also clear targets for those kinds.',
  'targeting.contextKindsDialog.filterPlaceholder': 'Filter context kinds',
  'targeting.contextKindsDialog.forLabel': 'For {label}',
  'targeting.contextKindsDialog.forVariation': 'For variation',
  'targeting.contextKindsDialog.kindTargetCount':
    'Context kind <strong>{contextKind}</strong> has <strong>{count}</strong> {count, plural, one {target} other {targets}}.',
  'targeting.contextKindsDialog.lastKindTooltip': 'You must have at least one context kind selected',
  'targeting.contextKindsDialog.manageContextKinds': 'Manage context kinds',
  'targeting.contextKindsDialog.noContextsMatch': 'No contexts match your query.',
  'targeting.contextKindsDialog.otherAvailable': 'Other available',
  'targeting.contextKindsDialog.recentlySeen': 'Recently seen',
  'targeting.contextKindsDialog.remove': 'Remove',
  'targeting.contextKindsDialog.removeKindsTitle': 'Remove context {count, plural, one {kind} other {kinds}}',
  'targeting.contextKindsDialog.save': 'Save',
  'targeting.contextKindsDialog.targetCount': '({count} {count, plural, one {target} other {targets}})',
  'targeting.contextKindsDialog.title': 'Select context kinds',
  'targeting.contextKindsDialog.titleForLabel': 'Select context kinds for {label}',
  'targeting.contextKindsDialog.warningIconLabel': 'Warning',
  'targeting.contextPicker.alreadyTargeted': 'Already targeted',
  'targeting.contextPicker.continueTyping': 'Continue typing to search',
  'targeting.contextPicker.createOption': 'Add {contextKind} "{name}"',
  'targeting.contextPicker.loadingTargets': 'Loading targets',
  'targeting.contextPicker.noTargetsFound': 'No targets found',
  'targeting.contextPicker.searchLabel': 'Search to find or add {contextKinds}',
  'targeting.contextPicker.typeToSearch': 'Type to search for more targets',
  'targeting.convertToSegment.addTags': 'Add tags',
  'targeting.convertToSegment.addThisSegment':
    "Add this segment to the flag's targeting as a new rule. Creating a segment does not change this flag's targeting.",
  'targeting.convertToSegment.cancel': 'Cancel',
  'targeting.convertToSegment.closeLabel': 'close modal',
  'targeting.convertToSegment.convertIndividualTargets': 'Convert individual targets to segment',
  'targeting.convertToSegment.convertTargetingRule': 'Convert targeting rule to segment',
  'targeting.convertToSegment.descriptionLabel': 'Description <optional>(optional)</optional>',
  'targeting.convertToSegment.disabledAlreadySegment':
    'Cannot convert a rule that is already a segment. Select a different attribute',
  'targeting.convertToSegment.disabledApprovalsRequired':
    'Convert to segment is unavailable because approvals are required for this environment',
  'targeting.convertToSegment.disabledIncompleteRule': 'Complete the remaining fields to convert to segment',
  'targeting.convertToSegment.disabledNoTargets': 'There are no individual targets to convert',
  'targeting.convertToSegment.disabledRuleApprovalsRequired':
    'Cannot convert a rule to a segment if approvals are required',
  'targeting.convertToSegment.editTags': 'Edit tags',
  'targeting.convertToSegment.environment': 'Environment',
  'targeting.convertToSegment.key': 'Key',
  'targeting.convertToSegment.keyHint':
    'LaunchDarkly uses the key to give you friendly URLs. Keys must only contain letters, numbers, <code>.</code>, <code>_</code> or <code>-</code>. <br></br>You cannot use <code>new</code> as a key.',
  'targeting.convertToSegment.keyRequired': 'Key is required',
  'targeting.convertToSegment.keyReservedNew': 'You cannot use "new" as a key',
  'targeting.convertToSegment.menuItem': 'Convert to segment',
  'targeting.convertToSegment.name': 'Name',
  'targeting.convertToSegment.namePlaceholder': 'Eg. Beta users',
  'targeting.convertToSegment.project': 'Project',
  'targeting.convertToSegment.requiredField': 'Required field',
  'targeting.convertToSegment.ruleConvertedSnackbar':
    "Your rule's clause was converted to segment, <link>{name}</link>.",
  'targeting.convertToSegment.saveSegment': 'Save segment',
  'targeting.convertToSegment.selectTagsLabel': 'Select tags',
  'targeting.convertToSegment.selectedTagsLabel': 'Selected tags',
  'targeting.convertToSegment.summaryCreateRule': 'Create a new rule targeting this segment to <variation></variation>',
  'targeting.convertToSegment.summaryCreateRules':
    'Create {count} new rules targeting these segments to serve the matching variations',
  'targeting.convertToSegment.summaryRemoveTargets':
    'Remove {count} {contextKind} {count, plural, one {target} other {targets}} from <variation></variation>',
  'targeting.convertToSegment.tagsLabel': 'Tags <optional>(optional)</optional>',
  'targeting.convertToSegment.targetsConvertedSnackbar':
    'Individual targets converted to segment, <link>{name}</link>.',
  'targeting.convertToSegment.turnThisRule':
    "Turn this rule into a segment you can use on other flags. Creating a segment does not change this flag's targeting.",
  'targeting.convertToSegment.unknownVariation': 'Unknown variation',
  'targeting.createExperimentDialog.addMaintainer': 'Add maintainer',
  'targeting.createExperimentDialog.addTag': 'Add tag',
  'targeting.createExperimentDialog.cancel': 'Cancel',
  'targeting.createExperimentDialog.closeLabel': 'Close dialog',
  'targeting.createExperimentDialog.createError': 'Failed to create experiment',
  'targeting.createExperimentDialog.createExperiment': 'Create experiment',
  'targeting.createExperimentDialog.createFromRuleError': 'Failed to create experiment from rule',
  'targeting.createExperimentDialog.creatingToast': 'Creating experiment from rule…',
  'targeting.createExperimentDialog.disabledBody':
    'Your user account does not have permission to create experiments. Request the necessary permissions from your administrator.',
  'targeting.createExperimentDialog.disabledHeading': 'Experiment creation is disabled',
  'targeting.createExperimentDialog.heading': 'Create experiment',
  'targeting.createExperimentDialog.headingInEnvironment': 'Create experiment in {environmentName}',
  'targeting.createExperimentDialog.hypothesisLabel': 'Hypothesis',
  'targeting.createExperimentDialog.hypothesisPlaceholder':
    'If [I do something], then [something will happen] because [a reason].',
  'targeting.createExperimentDialog.hypothesisTooLong': 'Hypothesis must be at most 1500 characters',
  'targeting.createExperimentDialog.maintainersLoadError': 'Unable to load maintainers',
  'targeting.createExperimentDialog.nameDescription':
    'Ensure your name describes a key aspect of your experiment. Ex: Homepage CTA color',
  'targeting.createExperimentDialog.nameLabel': 'Experiment name',
  'targeting.createExperimentDialog.namePlaceholder': 'Enter a name for your experiment',
  'targeting.createExperimentDialog.nameRequired': 'Experiment name is required',
  'targeting.createExperimentDialog.nameTooShort': 'Experiment name is required and must be at least 4 characters',
  'targeting.createExperimentDialog.tagPlaceholder': 'Enter tag (e.g., frontend, experimental)',
  'targeting.csvTip.body':
    'Pro tip: to add multiple targets at once, paste a comma separated list of keys above or use <link>bulk edit</link>.',
  'targeting.csvTip.dismissLabel': 'Close',
  'targeting.customStages.errors': 'Errors',
  'targeting.customStages.stepErrorLabel': 'Step {step}:',
  'targeting.declineApprovalDialog.body':
    'Declining this request rejects the proposed changes to <resource>{resourceName}</resource>. The requester can still re-open or update the request.',
  'targeting.declineApprovalDialog.cancel': 'Cancel',
  'targeting.declineApprovalDialog.comment': 'Comment',
  'targeting.declineApprovalDialog.commentPlaceholder': 'Leave a comment',
  'targeting.declineApprovalDialog.decline': 'Decline',
  'targeting.declineApprovalDialog.declineApprovalRequest': 'Decline approval request?',
  'targeting.defaultRule.aGuardedRollout': 'A guarded rollout',
  'targeting.defaultRule.createExperiment': 'Create experiment from rule',
  'targeting.defaultRule.experimentNeedsSave': 'Review and save rule changes before creating an experiment',
  'targeting.defaultRule.experimentNeedsVariations': 'At least 2 variations are required to create an experiment',
  'targeting.defaultRule.optionsLabel': 'default rule options',
  'targeting.defaultRule.resourceLabel': 'rule',
  'targeting.defaultRule.saveToCreateExperiment': 'Save to create an experiment from this rule',
  'targeting.defaultRule.sectionLabel': 'Default rule',
  'targeting.defaultRule.serve': 'Serve',
  'targeting.deleteApprovalDialog.body':
    'Deleting this approval request will permanently remove it from <flag>{flagName}</flag>.',
  'targeting.deleteApprovalDialog.cancel': 'Cancel',
  'targeting.deleteApprovalDialog.delete': 'Delete',
  'targeting.deleteApprovalDialog.deleteApprovalRequest': 'Delete approval request?',
  'targeting.deleteScheduledChangeDialog.body':
    'This pending change for <flag>{flagName}</flag> will be permanently removed and the changes will not be applied at the scheduled date and time',
  'targeting.deleteScheduledChangeDialog.cancel': 'Cancel',
  'targeting.deleteScheduledChangeDialog.selectReviewers': 'Select reviewers',
  'targeting.dependentFlagsAlert.body':
    'Flag is a prerequisite of <strong>{count, plural, one {# other flag} other {# other flags}}</strong> in <environment></environment>.',
  'targeting.dependentFlagsAlert.listHeading': 'Changes to targeting may impact the variations served by these flags',
  'targeting.dependentFlagsAlert.showLess': 'Show less',
  'targeting.dependentFlagsAlert.showMore': 'Show more',
  'targeting.disabledRule.body': 'This rule is disabled and will not be used in evaluations.',
  'targeting.disabledRule.disabled': 'Disabled',
  'targeting.disabledRule.enableRule': 'Enable rule',
  'targeting.disabledRule.statusLabel': 'Rule status',
  'targeting.discardChangesDialog.cancel': 'Cancel',
  'targeting.discardChangesDialog.discardChanges': 'Discard changes',
  'targeting.discardChangesDialog.discardUnsavedChanges': 'Discard unsaved changes',
  'targeting.discardFooter.undoChanges': 'Undo changes',
  'targeting.discardVariationChanges.title': 'Discard unsaved changes',
  'targeting.editApprovalDialog.cancel': 'Cancel',
  'targeting.editApprovalDialog.description': 'Description',
  'targeting.editApprovalDialog.descriptionLabel': 'Approval request description',
  'targeting.editApprovalDialog.descriptionPlaceholder': 'Add a description for this approval request',
  'targeting.editApprovalDialog.editApprovalRequest': 'Edit approval request',
  'targeting.editApprovalDialog.save': 'Save',
  'targeting.editScheduledChangeDialog.cancel': 'Cancel',
  'targeting.editScheduledChangeDialog.closeLabel': 'Close',
  'targeting.editScheduledChangeDialog.editScheduledChange': 'Edit scheduled change',
  'targeting.editScheduledChangeDialog.subtitle':
    'Update scheduled change for <flag>{flagName}</flag> on <environment></environment>',
  'targeting.editTriggerDialog.lockedVariationTooltip':
    'All triggers for this {resourceNoun} must serve the same variation',
  'targeting.editTriggerDialog.notificationSettings': 'Notification settings',
  'targeting.editTriggerDialog.sentryMonitorTitle': 'Sentry Monitor: {name}',
  'targeting.editTriggerDialog.serveVariationLabel': 'Variation to serve when the trigger fires',
  'targeting.editTriggerDialog.switchVariationInfoLabel': 'Switch variation info',
  'targeting.editTriggerDialog.switchVariationTo': 'Switch variation to',
  'targeting.editTriggerDialog.switchVariationTooltip': 'The {resourceNoun} variation to serve when this trigger fires',
  'targeting.editTriggerDialog.title': 'Edit adaptive trigger',
  'targeting.editTriggerDialog.viewAlert': 'View alert',
  'targeting.editTriggerDialog.viewAlertLabel': 'View alert (opens in new tab)',
  'targeting.enableAvailabilityDialog.body':
    'This will make changes to the {flagName} feature flag in all environments.',
  'targeting.enableAvailabilityDialog.cancel': 'Cancel',
  'targeting.enableAvailabilityDialog.changes': 'Changes:',
  'targeting.enableAvailabilityDialog.clientSideIdAvailability':
    'Client-side ID availability: <strong>Enabled</strong>',
  'targeting.enableAvailabilityDialog.commentLabel': 'Comment',
  'targeting.enableAvailabilityDialog.commentOptionalLabel': 'Comment (optional)',
  'targeting.enableAvailabilityDialog.commentOptionalPlaceholder':
    'Add an optional comment about these changes. These changes affect all environments in this project.',
  'targeting.enableAvailabilityDialog.commentRequiredPlaceholder':
    'Comments are required for changes in one or more environments. These changes affect all environments in this project.',
  'targeting.enableAvailabilityDialog.confirm': 'Confirm',
  'targeting.enableAvailabilityDialog.confirmationRequired': 'Confirmation required',
  'targeting.enableAvailabilityDialog.mobileKeyAvailability': 'Mobile key availability: <strong>Enabled</strong>',
  'targeting.enableAvailabilityDialog.typeTheFlag': 'Type the flag name or key to confirm',
  'targeting.entitlementGate.body': '{feature} is available on Enterprise plans.',
  'targeting.experimentRollout.experimentAllocation': 'Experiment allocation',
  'targeting.experimentRollout.inExperiment':
    '<strong>{percent}%</strong> of <kind></kind> contexts will be in the experiment.',
  'targeting.experimentRollout.lockedTooltip':
    'You cannot edit this rule while {isMab, select, true {a multi-arm bandit} other {an experiment}} is running.',
  'targeting.experimentRollout.nonRunningBanner':
    "This rule has been added to an experiment. The experiment's traffic allocation will appear here if it is running.",
  'targeting.experimentRollout.nonRunningBannerWithNames':
    "This rule has been added to {count, plural, one {the experiment} other {the experiments}} <experiments></experiments>. {count, plural, one {The experiment's traffic allocation} other {Each experiment's traffic allocation}} will appear here if it is running.",
  'targeting.experimentRollout.otherContextKinds':
    'If this rule is evaluated for context kinds other than <kind></kind> contexts, those contexts will be served',
  'targeting.experimentRollout.readOnlyLabel':
    'Read only {isMab, select, true {multi-arm bandit} other {experiment}} rule',
  'targeting.experimentRollout.remaining':
    'The remaining <strong>{percent}%</strong> of <kind></kind> contexts will be served',
  'targeting.experimentRollout.targetingRuleHeading': 'Targeting Rule',
  'targeting.experimentRollout.trafficAllocationHeading': 'Traffic Allocation',
  'targeting.experimentRollout.typeLabel': '{isMab, select, true {multi-arm bandit} other {experiment}}',
  'targeting.fallbackSection.learnMore': 'Learn more',
  'targeting.fallbackSection.unreachableDefault':
    'If LaunchDarkly is unreachable, SDKs will serve the default defined in your code.',
  'targeting.fallbackSection.unreachableServesVariation':
    'If LaunchDarkly is unreachable, SDKs will serve <variation></variation> to all contexts. This is defined in your code.',
  'targeting.fallbackSection.unreachableV1':
    'If LaunchDarkly is unreachable, SDKs may fall back to the value defined in your code.',
  'targeting.fallbackSection.unreachableV1WithValue':
    'If LaunchDarkly is unreachable, SDKs may fall back to the value defined in your code{hasVariation, select, true { (<variation></variation>)} other {}}.',
  'targeting.fallbackSection.unreachableV2':
    'SDKs may fall back to the value defined in your code under certain conditions.',
  'targeting.fallbackSection.unreachableV3':
    'Your application remains functional if the connection to LaunchDarkly is interrupted.',
  'targeting.filters.activeCountLabel': '{count} active filters',
  'targeting.filters.clearAll': 'Clear all',
  'targeting.filters.contextKindLabel': 'Context kind',
  'targeting.filters.heading': 'Filter targets by…',
  'targeting.filters.trigger': 'Filter',
  'targeting.filters.triggerLabel': 'Filter individual targets',
  'targeting.filters.variationLabel': 'Variation',
  'targeting.flagToggle.toAllTraffic': 'to all traffic',
  'targeting.flagToggle.variationsBasedOn': 'variations based on rules',
  'targeting.guardedHealthPopover.allMetrics': 'all metrics',
  'targeting.guardedHealthPopover.docsLabel': 'Go to health check documentation',
  'targeting.guardedHealthPopover.evaluating': '<kind></kind> contexts are evaluating this flag',
  'targeting.guardedHealthPopover.heading': 'Health check',
  'targeting.guardedHealthPopover.noEvaluations': 'Flag is not receiving evaluations',
  'targeting.guardedHealthPopover.noRecentEvents': 'No recently seen events for {metrics}',
  'targeting.guardedHealthPopover.notEvaluating': '<kind></kind> contexts are not evaluating this flag',
  'targeting.guardedHealthPopover.notTrackedBy': '<kind></kind> is not tracked by {metrics}',
  'targeting.guardedHealthPopover.selectedContextKind': 'Selected context kind',
  'targeting.guardedHealthPopover.trackedBy': '<kind></kind> is tracked by {metrics}',
  'targeting.guardedHealthPopover.window': 'For the past 90 days:',
  'targeting.guardedRollout.contextKindNeedsMetrics': 'Select metrics to see available context kinds',
  'targeting.guardedRollout.contextKindPlaceholder': 'Select a context kind',
  'targeting.guardedRollout.duration': 'Duration',
  'targeting.guardedRollout.hardLimitBody': 'You have reached your limit of <strong>{limit}</strong> guarded rollouts.',
  'targeting.guardedRollout.heading': 'Guarded rollout',
  'targeting.guardedRollout.limitReachedHeading': 'Limit reached',
  'targeting.guardedRollout.loadError': 'Failed to load guarded rollouts.',
  'targeting.guardedRollout.metricAlertIgnoredLabel': 'Alert ignored',
  'targeting.guardedRollout.metricHealthyLabel': 'Healthy',
  'targeting.guardedRollout.metricPendingLabel': 'Pending',
  'targeting.guardedRollout.metricRegressedLabel': 'Regressed',
  'targeting.guardedRollout.noMonitoringDurationLabel': 'No monitoring duration',
  'targeting.guardedRollout.noValidContextKinds': 'No valid context kinds',
  'targeting.guardedRollout.originalVariation': 'Original variation',
  'targeting.guardedRollout.prefilledFromPolicy':
    'The guarded release settings have been prefilled from the <policyLink></policyLink> release policy',
  'targeting.guardedRollout.progressLabel': 'Guarded rollout step progress',
  'targeting.guardedRollout.retry': 'Try again',
  'targeting.guardedRollout.rollout': 'Rollout',
  'targeting.guardedRollout.rolloutDuration': 'Rollout duration',
  'targeting.guardedRollout.softLimitBody':
    'You have reached your included limit of <strong>{limit}</strong> guarded rollouts.',
  'targeting.guardedRollout.softLimitExtra': "We've added <strong>{count} more</strong> while you consider upgrading.",
  'targeting.guardedRollout.stageLabel': 'Step {current} of {total}',
  'targeting.guardedRollout.statusMonitoring': 'Monitoring',
  'targeting.guardedRollout.statusNotStarted': 'Not started',
  'targeting.guardedRollout.statusRegressed': 'Regression detected',
  'targeting.guardedRollout.statusStopped': 'Stopped',
  'targeting.guardedRollout.statusWaiting': 'Waiting',
  'targeting.guardedRollout.step': 'Step',
  'targeting.guardedRollout.stopRollout': 'Stop rollout',
  'targeting.guardedRollout.targetBy': 'Target by',
  'targeting.guardedRollout.targetVariation': 'Target variation',
  'targeting.guardedRollout.trialLimitBody':
    'You are approaching your limit of <strong>{limit}</strong> guarded rollouts.',
  'targeting.guardedRollout.trialRemainingHeading': '{count} guarded rollouts remaining',
  'targeting.guardedRollout.upgradeCta': 'Upgrade to unlock more',
  'targeting.guardedRollout.upgradeDetails': 'Tell me more about guarded rollouts.',
  'targeting.guardedRolloutStages.activeLabel': 'Active',
  'targeting.guardedRolloutStages.completedLabel': 'Completed',
  'targeting.guardedRolloutStages.duration': 'Duration',
  'targeting.guardedRolloutStages.failedLabel': 'Failed',
  'targeting.guardedRolloutStages.on': 'On',
  'targeting.guardedRolloutStages.rollout': 'Rollout',
  'targeting.guardedRolloutStages.status': 'Status',
  'targeting.guardedRolloutStages.step': 'Step',
  'targeting.guardedRolloutStages.tableLabel': 'Guarded rollout progress',
  'targeting.individualTargeting.add': 'Add',
  'targeting.individualTargeting.closeTipLabel': 'Close',
  'targeting.individualTargeting.contextKey': 'Context key',
  'targeting.individualTargeting.contextPlaceholder': 'Add or search contexts',
  'targeting.individualTargeting.deleteLabel': 'Delete target',
  'targeting.individualTargeting.duplicateTarget': 'This context is already targeted to the selected variation',
  'targeting.individualTargeting.filterLabel': 'Filter targets',
  'targeting.individualTargeting.kind': 'Kind',
  'targeting.individualTargeting.noMatches': 'No targets match your filter criteria.',
  'targeting.individualTargeting.noPermission': "Your permissions don't allow you to perform this action",
  'targeting.individualTargeting.resetFilters': 'Reset filters',
  'targeting.individualTargeting.scheduleLabel': 'Schedule target',
  'targeting.individualTargeting.scheduleRemoval': 'Schedule removal',
  'targeting.individualTargeting.scheduledRemovalLabel': 'Target is scheduled to be removed',
  'targeting.individualTargeting.scheduledRemovalTooltip': 'Scheduled to be removed at {date}',
  'targeting.individualTargeting.searchLabel': 'Search targets',
  'targeting.individualTargeting.searchTip':
    'Pro tip: search matches the start of a context name, key, or email address.',
  'targeting.individualTargeting.searchUnavailable': 'Search is not available. Try again later',
  'targeting.individualTargeting.targetVariationLabel': 'Target variation',
  'targeting.individualTargeting.variation': 'Variation',
  'targeting.individualTargeting.variationFallbackName': 'variation {index}',
  'targeting.individualTargeting.variationFilterLabel':
    'Filter by {name}: {count, plural, one {# target} other {# targets}}',
  'targeting.individualTargeting.variationListLabel': 'Variations',
  'targeting.individualTargeting.variationPlaceholder': 'Select a variation',
  'targeting.individualTargeting.variationTriggerLabel': 'Select variation',
  'targeting.individualTargetingFilters.activeCountLabel':
    '{count, plural, one {# active filter} other {# active filters}}',
  'targeting.individualTargetingFilters.allContextKinds': 'All context kinds',
  'targeting.individualTargetingFilters.allVariations': 'All variations',
  'targeting.individualTargetingFilters.clearAll': 'Clear all',
  'targeting.individualTargetingFilters.contextKind': 'Context kind',
  'targeting.individualTargetingFilters.emptyState': 'Filter targets by…',
  'targeting.individualTargetingFilters.heading': 'Filter individual targets',
  'targeting.individualTargetingFilters.triggerLabel': 'Filter',
  'targeting.individualTargetingFilters.variation': 'Variation',
  'targeting.individualTargetingMenu.bulkEdit': 'Bulk edit',
  'targeting.individualTargetingMenu.exportCsv': 'Export CSV',
  'targeting.individualTargetingMenu.menuLabel': 'Open individual targeting menu',
  'targeting.individualTargetingMenu.removeAll': 'Remove all',
  'targeting.individualTargetingMenu.search': 'Search',
  'targeting.individualTargetingMenu.searchToggleLabel': 'Open individual targeting search',
  'targeting.individualTargetingMenu.upgradeDescription': 'Upgrade your plan to use this feature. Click to learn more.',
  'targeting.individualTargetingMenu.upsellBody': 'Upgrade your plan to unlock bulk user management features:',
  'targeting.individualTargetingMenu.upsellBulkEdit': 'Easily add, remove, or replace individual targets',
  'targeting.individualTargetingMenu.upsellClose': 'Close',
  'targeting.individualTargetingMenu.upsellExportCsv': 'Download a CSV of individual targets',
  'targeting.individualTargetingMenu.upsellHeading': 'Do more with bulk features',
  'targeting.individualTargetingMenu.upsellLearnMore': 'Learn more',
  'targeting.individualTargets.addContext': 'Add context',
  'targeting.individualTargets.copyKeysLabel': 'Copy {contextKind} keys',
  'targeting.individualTargets.copyKeysTooltip': 'Copy {contextKind} keys to clipboard',
  'targeting.individualTargets.editContext': 'Edit context',
  'targeting.individualTargets.gridLabel': 'Individual Targets',
  'targeting.individualTargets.loadError': 'Error loading context: {target}',
  'targeting.individualTargets.removeNamedTargetLabel': 'Remove target {target}',
  'targeting.individualTargets.removeTargetLabel': 'Remove target',
  'targeting.individualTargets.targetCount': '{count, plural, one {# target} other {# targets}}',
  'targeting.individualTargets.viewDetailsLabel': 'View details for target {targetKey}',
  'targeting.individualTargets.whitespaceWarning':
    'This value contains {position, select, leading {leading} other {trailing}} whitespace',
  'targeting.jsonDiffView.renderError': 'Error rendering JSON diff',
  'targeting.jsonDiscardDialog.cancel': 'Cancel',
  'targeting.jsonDiscardDialog.ifYouLeave': 'If you leave the editor, you will lose any unsaved changes.',
  'targeting.jsonDiscardDialog.invalidType': 'Invalid type: {message}',
  'targeting.jsonDiscardDialog.invalidTypeAtPath': 'Invalid type at path <code></code>: {message}',
  'targeting.jsonDiscardDialog.leave': 'Leave',
  'targeting.jsonDiscardDialog.youHaveThe': 'You have the following errors in your JSON:',
  'targeting.jsonEditor.anonymousBooleanValues': 'Anonymous clause values can only be boolean',
  'targeting.jsonEditor.anonymousOperator': 'Anonymous clause can only use in operator',
  'targeting.jsonEditor.anonymousSingleValue': 'Anonymous clause values can only have one boolean value',
  'targeting.jsonEditor.applicationAttribute': 'Application kind can only use {attribute} attribute',
  'targeting.jsonEditor.applicationOperator': 'Application kind can only use applicationVersionSupported operator',
  'targeting.jsonEditor.applicationSingleValue': 'Only one application value is allowed',
  'targeting.jsonEditor.applicationUnsupportedValue': 'Unsupported application value',
  'targeting.jsonEditor.contextKindVariationUnique': 'Context kind + variation combo must be unique',
  'targeting.jsonEditor.duplicateClause': 'You cannot have a clause in the same rule',
  'targeting.jsonEditor.duplicateTargetValues': 'Duplicate values not allowed',
  'targeting.jsonEditor.duplicateValues': 'Remove duplicates from values: {duplicate}',
  'targeting.jsonEditor.emptyValues': 'Targeting values cannot be empty strings',
  'targeting.jsonEditor.formatJson': 'Format JSON',
  'targeting.jsonEditor.notSegmentMatchNegate': 'not-segmentMatch attribute can only be used with negate: true',
  'targeting.jsonEditor.prettifyLabel': 'Prettify',
  'targeting.jsonEditor.resetLabel': 'Reset changes',
  'targeting.jsonEditor.segmentMatchEmpty': 'Segment match clause must reference at least one segment',
  'targeting.jsonEditor.segmentMatchNegate': 'segmentMatch attribute can only be used with negate: false',
  'targeting.jsonEditor.segmentNotFound': 'Segment "{key}" does not exist',
  'targeting.jsonEditor.segmentOperator': 'Segment clause can only use segmentMatch operator',
  'targeting.jsonEditor.somethingWentWrong': 'Something went wrong loading the JSON editor.',
  'targeting.jsonEditor.toggleInstructionsLabel': 'Toggle instructions',
  'targeting.jsonEditor.undoAllChanges': 'Undo all changes',
  'targeting.jsonEditor.unsupportedContextKind': 'Unsupported context kind',
  'targeting.jsonEditor.viewDocs': 'View docs',
  'targeting.jsonEditorInstructions.closeLabel': 'close',
  'targeting.jsonEditorInstructions.contextTargetsBody':
    'For each context kind, list the context keys and the variation those contexts should receive',
  'targeting.jsonEditorInstructions.contextTargetsHeading': 'Individual and context targets',
  'targeting.jsonEditorInstructions.exampleHeading': 'Example',
  'targeting.jsonEditorInstructions.exampleReceivesOne':
    'If the context with user <code>example-user-key</code> receives variation <code>0</code> for <code>prereq-flag-key</code>, it will receive variation <code>1</code> for this flag.',
  'targeting.jsonEditorInstructions.exampleReceivesZero':
    'If the context receives variation <code>1</code> for <code>prereq-flag-key</code>, then this context will receive variation <code>0</code> for this flag.',
  'targeting.jsonEditorInstructions.fallthroughHeading': 'Fallthrough',
  'targeting.jsonEditorInstructions.flagStatusBody':
    'Boolean. Set <code>on</code> to <code>true</code> or <code>false</code> to toggle your flag on or off.',
  'targeting.jsonEditorInstructions.flagStatusHeading': 'Flag status',
  'targeting.jsonEditorInstructions.gettingStartedBody':
    'Editing your rules JSON follows the same model as the JSON response from <link>GET feature flag</link> in the API.',
  'targeting.jsonEditorInstructions.gettingStartedHeading': 'Getting started',
  'targeting.jsonEditorInstructions.hereIsThe': 'Here is the basic structure:',
  'targeting.jsonEditorInstructions.jsonDocumentation': 'JSON documentation',
  'targeting.jsonEditorInstructions.letSAssume': "Let's assume the prerequisite flag defined below is on.",
  'targeting.jsonEditorInstructions.prerequisitesBody':
    "Array. Prerequisites allow you to control dependencies. If the prerequisite flag is <code>on: true</code> and the context encountering this flag is receiving the chosen variation of the prerequisite flag, then the context is subject to this flag's targeting rules. If the prerequisite flag is <code>on: false</code> or the context is receiving a different variation, then the context will receive this flag's offVariation.",
  'targeting.jsonEditorInstructions.prerequisitesHeading': 'Prerequisites',
  'targeting.jsonEditorInstructions.rulesBody':
    'Array. Rules need a <code>variation</code> to serve and a <code>clause</code>. You can optionally give them a <code>description</code>, set them to <code>trackEvents</code>, or disable them with <code>disabled</code>.',
  'targeting.jsonEditorInstructions.rulesHeading': 'Rules',
  'targeting.jsonEditorInstructions.thisIsThe': 'This is the default variation your flag will serve.',
  'targeting.misconfigurationBanner.bannerTooltip':
    "You don't have permission to update client-side SDK availability for this flag",
  'targeting.misconfigurationBanner.enable': 'Enable',
  'targeting.misconfigurationBanner.ignore': 'Ignore',
  'targeting.misconfigurationBanner.updateFailedDescription': 'Try again later.',
  'targeting.misconfigurationBanner.updateFailedTitle': 'Failed to update availability',
  'targeting.mobileClauseEditor.attributeFieldLabel': 'Attribute',
  'targeting.mobileClauseEditor.attributePlaceholder': 'Select an attribute',
  'targeting.mobileClauseEditor.contextKindFieldLabel': 'Context kind',
  'targeting.mobileClauseEditor.operatorFieldLabel': 'Operator',
  'targeting.mobileClauseEditor.operatorPlaceholder': 'Select an operator',
  'targeting.mobileClauseEditor.showSuggestionsLabel': 'Show suggestions',
  'targeting.mobileClauseEditor.valuesFieldLabel': 'Values',
  'targeting.mobileSetupDialog.allSet': "You're all set",
  'targeting.mobileSetupDialog.body': 'To target mobile apps and devices you must:',
  'targeting.mobileSetupDialog.configureEnvironmentAttributes':
    'Configure your SDK to enable collection of environment attributes',
  'targeting.mobileSetupDialog.dismiss': "I'll do this later",
  'targeting.mobileSetupDialog.heading': "Let's get you set up for mobile targeting",
  'targeting.mobileSetupDialog.useSupportedSdk': 'Use a supported mobile SDK',
  'targeting.operatorPicker.placeholder': 'Select an operator',
  'targeting.pendingChangesButton.viewPendingChanges': 'View pending changes',
  'targeting.pendingChangesDrawer.closeLabel': 'Close',
  'targeting.pendingChangesDrawer.delete': 'Delete',
  'targeting.pendingChangesDrawer.edit': 'Edit',
  'targeting.pendingChangesDrawer.heading': 'Pending changes for <flag>{flagName}</flag>',
  'targeting.pendingChangesDrawer.itemRenderError': 'Unable to display this pending change.',
  'targeting.pendingChangesDrawer.loadingLabel': 'Loading pending changes',
  'targeting.pendingChangesDrawer.noPendingChanges': 'No pending changes',
  'targeting.pendingChangesDrawer.scheduledChangeActionsLabel': 'Scheduled change actions',
  'targeting.percentageRollout.decreaseWeightLabel': 'decrease rollout weight',
  'targeting.percentageRollout.increaseWeightLabel': 'increase rollout weight',
  'targeting.percentageRollout.needToBe': 'need to be assigned to a variation.',
  'targeting.percentageRollout.unknownVariation': 'Unknown',
  'targeting.percentageRollout.weightLabel': 'Variation {index} rollout weight',
  'targeting.prerequisites.addLabel': 'Add prerequisite',
  'targeting.prerequisites.andContextIs': 'and context is receiving',
  'targeting.prerequisites.clausePrefix': '{isFirst, select, true {If} other {And}}',
  'targeting.prerequisites.description':
    'List other flags and their expected values as requirements to enable this flag',
  'targeting.prerequisites.failureModePrefix':
    'If any of these flags are off or not serving the right variation, serve',
  'targeting.prerequisites.flagLabel': 'Flag',
  'targeting.prerequisites.flagTriggerLabel': 'Select flag',
  'targeting.prerequisites.holdoutTagsLabel': 'Holdout prerequisite flags',
  'targeting.prerequisites.holdoutTooltip':
    'You cannot edit a prerequisite flag when it is included in an experiment holdout',
  'targeting.prerequisites.infoLabel': 'Prerequisites info',
  'targeting.prerequisites.is': 'is',
  'targeting.prerequisites.loadingVariation': 'Loading variation',
  'targeting.prerequisites.missingVariation': 'The variation referenced by this prerequisite no longer exists',
  'targeting.prerequisites.on': 'On',
  'targeting.prerequisites.opensInNewTab': '(opens in new tab)',
  'targeting.prerequisites.tagsLabel': 'Prerequisite flags',
  'targeting.prerequisites.theOffVariation': 'the off variation',
  'targeting.prerequisites.unavailableVariation': 'unavailable variation',
  'targeting.prerequisites.variationInCode': 'the variation defined in your code',
  'targeting.prerequisites.variationLabel': 'Variation',
  'targeting.prerequisites.variationTriggerLabel': 'Select variation',
  'targeting.prerequisites.viewFlag': 'View flag',
  'targeting.progressiveRollout.contextKindLabel': 'Context kind',
  'targeting.progressiveRollout.heading': 'Progressive rollout',
  'targeting.progressiveRollout.originalVariation': 'Original variation',
  'targeting.progressiveRollout.prefilledFromPolicy':
    'The progressive release settings have been prefilled from the <policyLink></policyLink> release policy',
  'targeting.progressiveRollout.rolloutDuration': 'Rollout duration',
  'targeting.progressiveRollout.selectVariation': 'Select variation',
  'targeting.progressiveRollout.targetBy': 'Target by',
  'targeting.progressiveRollout.targetVariation': 'Target variation',
  'targeting.readOnlyTargets.noTargets': 'Variations with no individual targets',
  'targeting.readOnlyTargets.servedTo': 'served to {count} {count, plural, one {target} other {targets}}',
  'targeting.readOnlyTargets.showAllVariations': 'show all',
  'targeting.readOnlyTargets.showLessTargets': 'show less',
  'targeting.readOnlyTargets.showLessVariations': 'show less',
  'targeting.readOnlyTargets.showMoreTargets': '+{count} more',
  'targeting.releaseActions.bypassRequiredApproval': 'Bypass required approval',
  'targeting.releaseActions.cancel': 'Cancel',
  'targeting.releaseActions.closeLabel': 'Close',
  'targeting.releaseActions.description': 'How do you want to stop your release?',
  'targeting.releaseActions.title': 'Stop release',
  'targeting.releaseBuilder.automatic': 'Automatic',
  'targeting.releaseBuilder.fromVariationLabel': 'from variation',
  'targeting.releaseBuilder.joinerAnd': 'and',
  'targeting.releaseBuilder.joinerWhere': 'where',
  'targeting.releaseBuilder.manual': 'Manual',
  'targeting.releaseBuilder.operatorIs': 'is',
  'targeting.releaseBuilder.operatorIsNot': 'is not',
  'targeting.releaseBuilder.operatorIsNotOneOf': 'is not one of',
  'targeting.releaseBuilder.operatorIsOneOf': 'is one of',
  'targeting.releaseBuilder.phaseAdvancement': 'Phase advancement',
  'targeting.releaseBuilder.phaseSummaryAudience': 'All {units}{segments}',
  'targeting.releaseBuilder.phaseSummaryServes': 'will be served <variation></variation> when this phase ends',
  'targeting.releaseBuilder.progressivelyRollOut': 'progressively roll out from',
  'targeting.releaseBuilder.removeRelease': 'Remove release',
  'targeting.releaseBuilder.removeReleaseLabel': 'remove release',
  'targeting.releaseBuilder.segmentPhraseMany': '{verb} segments ({names})',
  'targeting.releaseBuilder.segmentPhraseOne': '{verb} {name} segment',
  'targeting.releaseBuilder.segmentVerbIn': 'in',
  'targeting.releaseBuilder.segmentVerbNotIn': 'not in',
  'targeting.releaseBuilder.thisAudienceMay':
    "A targeting rule or another phase already targets this audience. Change this phase's audience so it reaches new contexts.",
  'targeting.releaseBuilder.toVariationLabel': 'to variation',
  'targeting.releaseBuilder.unknownVariation': 'Unknown variation',
  'targeting.releaseBuilderCleanup.decideWhatHappens': 'Decide what happens when the last phase completes.',
  'targeting.releaseBuilderCleanup.finalPhase': 'Final phase',
  'targeting.releaseBuilderCleanup.keepTheRelease': 'Keep the release limited to this audience',
  'targeting.releaseBuilderCleanup.keepTheReleaseDescription':
    'Leaves release rules in place. Only the audience you define receives <variation></variation>; everyone else is unchanged.',
  'targeting.releaseBuilderCleanup.keepTheReleaseServeTrue':
    'Leaves release rules in place. Only the audience you define receives true; everyone else is unchanged.',
  'targeting.releaseBuilderCleanup.releaseToEveryone': 'Release to everyone',
  'targeting.releaseBuilderCleanup.releaseToEveryoneDefaultUnchanged':
    'Removes the release rules. The default rule continues to serve <variation></variation>.',
  'targeting.releaseBuilderCleanup.releaseToEveryoneDefaultUpdated':
    'Removes the release rules and sets the default to serve <variation></variation>.',
  'targeting.releaseBuilderCleanup.releaseToEveryoneServeTrue':
    'Removes the release rules and sets the default rule to serve true.',
  'targeting.releaseBuilderMonitoring.analysisUnitLabel': 'Analysis unit',
  'targeting.releaseBuilderMonitoring.autoRollbackLabel': 'Auto rollback for {metricKey}',
  'targeting.releaseBuilderMonitoring.enablementInfo':
    'To add metrics, enable <kind></kind> for guarded rollouts in <link>the settings</link>.',
  'targeting.releaseBuilderMonitoring.incompatibleMetricLabel': 'Incompatible metric',
  'targeting.releaseBuilderMonitoring.loadingMetrics': 'Loading metrics…',
  'targeting.releaseBuilderMonitoring.removeMetric': 'Remove metric',
  'targeting.releaseBuilderMonitoring.removeMetricLabel': 'remove metric',
  'targeting.releaseBuilderMonitoring.removeTheMetric': '. Remove the metric or choose a different randomization unit.',
  'targeting.releaseBuilderMonitoring.sectionLabel': 'Monitor for regressions <optional>(optional)</optional>',
  'targeting.releaseBuilderMonitoring.thisMetricDoes': 'This metric does not support',
  'targeting.releaseHealthBadge.healthy': 'Healthy',
  'targeting.releaseHealthBadge.loadFailed': 'Health check failed to load',
  'targeting.releaseHealthBadge.loadingLabel': 'Loading health check',
  'targeting.releaseHealthBadge.retryLabel': 'Retry health check',
  'targeting.releaseHealthBadge.statusLabel': 'Health check status',
  'targeting.releaseHealthBadge.warnings': 'Health warnings',
  'targeting.releaseHealthCheck.contextKindEvaluating':
    '<code>{kind}</code> contexts are {seen, select, true {} other {not }}evaluating this flag',
  'targeting.releaseHealthCheck.defaultContextKind': 'Selected context kind',
  'targeting.releaseHealthCheck.docsLink': 'Go to health check documentation',
  'targeting.releaseHealthCheck.heading': 'Health check',
  'targeting.releaseHealthCheck.healthy': 'Healthy',
  'targeting.releaseHealthCheck.loadFailed': 'Health check failed to load',
  'targeting.releaseHealthCheck.loadingLabel': 'Loading health check',
  'targeting.releaseHealthCheck.noEvaluations': 'Flag is not receiving evaluations',
  'targeting.releaseHealthCheck.retryLabel': 'Retry health check',
  'targeting.releaseHealthCheck.statusLabel': 'Health check status',
  'targeting.releaseHealthCheck.warnings': 'Health warnings',
  'targeting.releaseHealthCheck.windowLabel': 'For the past 90 days:',
  'targeting.releaseMetrics.addMetricsAbove': 'Add metrics above',
  'targeting.releaseMetrics.analysisUnitLabel': 'Analysis unit for {metricKey}',
  'targeting.releaseMetrics.analysisUnitLockedGroup': 'Metrics in a metric group use the randomization unit.',
  'targeting.releaseMetrics.analysisUnitLockedPercentile': 'Percentile metrics use the randomization unit.',
  'targeting.releaseMetrics.analysisUnitLockedRatio': 'Ratio metrics use the randomization unit.',
  'targeting.releaseMetrics.analysisUnitLockedUnsupported':
    "This metric doesn't support the selected randomization unit.",
  'targeting.releaseMetrics.autoRollbackLabel': 'Automatic rollback',
  'targeting.releaseMetrics.metricColumnLabel': 'Metric',
  'targeting.releaseMetrics.metricsToMonitor': 'Metrics to monitor',
  'targeting.releaseMetrics.partOfGroup': 'part of <icon></icon>{name}',
  'targeting.releaseMetrics.removeLabel': 'Remove {name}',
  'targeting.releaseMetrics.singleAnalysisUnit': 'Only one analysis unit is available for this metric.',
  'targeting.releaseMetrics.unitPlaceholder': 'Select a unit',
  'targeting.releaseMetrics.viewDetailsLabel': 'View details for {name}',
  'targeting.releasePhases.errorLoadingMetrics': 'Error loading metrics',
  'targeting.releasePhases.metricDetailsLabel': 'View details for {displayName}',
  'targeting.releasePolicyRecommendation.defaultPolicyName': 'Release template',
  'targeting.releasePolicyRecommendation.immediateBody':
    'The <policy></policy> policy recommends turning this flag on for <environment></environment>',
  'targeting.releasePolicyRecommendation.releaseBody':
    'Release via <policy></policy> policy – using a release is recommended on <environment></environment>',
  'targeting.releaseSummaryCard.startNextPhase': 'Start next phase',
  'targeting.releaseSummaryCard.stopRelease': 'Stop release',
  'targeting.releaseViewer.completed': 'Completed',
  'targeting.releaseViewer.estimatedEnd': 'est. <time></time>',
  'targeting.releaseViewer.inProgress': 'In progress',
  'targeting.releaseViewer.leaveRulesIn': 'Leave rules in place',
  'targeting.releaseViewer.pending': 'Pending',
  'targeting.releaseViewer.progressivelyRollOut': 'progressively roll out from',
  'targeting.releaseViewer.release': 'Release',
  'targeting.releaseViewer.reload': 'Reload',
  'targeting.releaseViewer.removeReleaseRules': 'Remove release rules and update default rule to serve',
  'targeting.releaseViewer.rollout': 'Rollout',
  'targeting.releaseViewer.start': 'Start',
  'targeting.releaseViewer.stopRelease': 'Stop release',
  'targeting.releaseViewer.targeted': 'Targeted',
  'targeting.releaseViewer.unknownVariation': 'Unknown variation',
  'targeting.removeTriggerDialog.body':
    "This permanently deletes the adaptive trigger{deletesAlert, select, true { and the alert it's based on} other {}}. You cannot undo this action.",
  'targeting.removeTriggerDialog.cancel': 'Cancel',
  'targeting.removeTriggerDialog.delete': 'Delete',
  'targeting.removeTriggerDialog.deleteAdaptiveTrigger': 'Delete adaptive trigger',
  'targeting.requestTriggersAccess.actionsLabel': 'Request access to Triggers actions',
  'targeting.requestTriggersAccess.beta': 'Beta',
  'targeting.requestTriggersAccess.cancel': 'Cancel',
  'targeting.requestTriggersAccess.closeLabel': 'Close',
  'targeting.requestTriggersAccess.defineAlwaysOn':
    'Define always-on rules that automatically adapt your system’s behavior based on real-time signals, shrinking response time from minutes to seconds.',
  'targeting.requestTriggersAccess.errorToast': 'Something went wrong. Try again.',
  'targeting.requestTriggersAccess.featureStatusLabel': 'Feature status',
  'targeting.requestTriggersAccess.messageLabel': 'Message',
  'targeting.requestTriggersAccess.messagePlaceholder': 'I want to use Triggers to…',
  'targeting.requestTriggersAccess.requestAccessTo': 'Request access to Triggers',
  'targeting.requestTriggersAccess.submit': 'Request access',
  'targeting.requestTriggersAccess.successToast': "Thanks. We'll be in touch about Triggers access shortly.",
  'targeting.requestTriggersAccess.tellUsHow': 'Tell us how you plan to use Triggers to get started.',
  'targeting.reviewAlerts.conflictsErrorBody':
    "We couldn't check for conflicts with scheduled changes. You can still proceed with your changes, but be aware that conflicts may exist.",
  'targeting.reviewAlerts.conflictsErrorHeading': 'Error checking for conflicts',
  'targeting.reviewAlerts.deprecatedBody': 'Deprecated flags are still evaluated.',
  'targeting.reviewAlerts.deprecatedHeading': 'You are making changes to a deprecated flag',
  'targeting.reviewAlerts.healthCheckBody':
    'Some health checks related to your guarded rollout configuration are failing. Proceeding may result in insufficient data collection associated with your rollout.',
  'targeting.reviewAlerts.healthCheckWarnings': 'Health check warnings',
  'targeting.reviewAlerts.minimumApprovalsRequired':
    'Minimum {count, plural, one {# approval} other {# approvals}} required to apply these changes',
  'targeting.reviewAlerts.multipleApprovalRequests': 'This will create 2 approval requests.',
  'targeting.reviewAlerts.resourceAiConfig': 'AI config',
  'targeting.reviewAlerts.resourceFlag': 'flag',
  'targeting.reviewAlerts.targetingOffWarning':
    'Your changes will be saved, but the {resourceLabel} will serve the off variation to all traffic until it is turned on.',
  'targeting.reviewDialog.back': 'Back',
  'targeting.reviewDialog.bothSavesFailed': 'Both flag targeting and scheduled removal changes failed to save.',
  'targeting.reviewDialog.cancel': 'Cancel',
  'targeting.reviewDialog.cannotRenderJsonTooltip': 'Cannot render JSON with conflicts',
  'targeting.reviewDialog.cannotRunTest': 'Cannot run test run with conflicts',
  'targeting.reviewDialog.changes': 'Changes',
  'targeting.reviewDialog.closeLabel': 'Close',
  'targeting.reviewDialog.collapseAllLabel': 'Collapse all changes',
  'targeting.reviewDialog.conflictBody':
    'Your changes could not be saved because someone else modified this flag. Reload the page and try again.',
  'targeting.reviewDialog.expandAllLabel': 'Expand all changes',
  'targeting.reviewDialog.expiringSaveFailed': 'Scheduled removal changes failed to save.',
  'targeting.reviewDialog.expiringSavedFlagFailed':
    'Scheduled removal changes saved, but flag targeting changes failed.',
  'targeting.reviewDialog.flagSavedExpiringFailed':
    'Flag targeting changes saved, but scheduled removal changes failed.',
  'targeting.reviewDialog.json': 'JSON',
  'targeting.reviewDialog.jsonEditingLabel': 'JSON editing',
  'targeting.reviewDialog.restoringPreviousVersionComment': 'Restoring previous version',
  'targeting.reviewDialog.restoringVersionComment': 'Restoring version {version}',
  'targeting.reviewDialog.runningReleaseLoadError':
    "Couldn't load this flag's running releases. Reload the page and try again.",
  'targeting.reviewDialog.saveChanges': 'Save changes',
  'targeting.reviewDialog.subtitle': 'Changes to <flag>{flagName}</flag> on <environment></environment>',
  'targeting.reviewDialog.switchToGui': 'Switch to GUI',
  'targeting.reviewDialog.switchToJsonTooltip': 'Switch to JSON',
  'targeting.reviewDialog.testRun': 'Test run',
  'targeting.reviewDialog.uiEditingLabel': 'UI editing',
  'targeting.reviewDialog.visual': 'Visual',
  'targeting.reviewForm.comment': 'Comment',
  'targeting.reviewForm.commentDescription': 'Add a brief description of your changes.',
  'targeting.reviewForm.commentPlaceholder': 'Select reviewers',
  'targeting.reviewForm.commentTooLong': 'Comment must be less than {maxLength} characters',
  'targeting.reviewForm.confirm': 'Confirm',
  'targeting.reviewForm.confirmHint': 'Type <code>{token}</code> to confirm.',
  'targeting.reviewIntegrationFields.fieldPlaceholder': 'Error loading options',
  'targeting.reviewIntegrationFields.loadError': 'There was a problem fetching options.',
  'targeting.reviewIntegrationFields.loadingLabel': 'Loading approval integration fields',
  'targeting.reviewIntegrationFields.loadingOptionsLabel': 'Loading {label} options',
  'targeting.reviewIntegrationFields.selectOptionPlaceholder': 'Select an option',
  'targeting.rollbackFeedback.prompt': 'How was your rollback experience?',
  'targeting.rollbackWarning.archivedContextKinds': 'Archived context kinds',
  'targeting.rollbackWarning.body': 'The rollback configuration references dependencies that may no longer be valid:',
  'targeting.rollbackWarning.heading': 'Rollback Preview',
  'targeting.rollbackWarning.invalidVariationIndices': 'Invalid variation indices',
  'targeting.rollbackWarning.missingPrerequisiteVariations': 'Missing variations referenced by prerequisites',
  'targeting.rollbackWarning.missingPrerequisites': 'Missing prerequisite flags',
  'targeting.rollbackWarning.missingSegments': 'Missing segments',
  'targeting.rolloutBar.distributionLabel': 'Rollout distribution',
  'targeting.rolloutChangeSummary.autoRollback': 'Auto rollback',
  'targeting.rolloutChangeSummary.cleanupPhaseRowLabel': 'Cleanup phase:',
  'targeting.rolloutChangeSummary.duration': 'Duration',
  'targeting.rolloutChangeSummary.experimentRemainder':
    'and remaining {percent}% to <variation></variation> and {canReshuffle, select, true {users can be reshuffled.} other {users cannot be reshuffled.}}',
  'targeting.rolloutChangeSummary.metric': 'Metric',
  'targeting.rolloutChangeSummary.metricsRowLabel': 'Metrics:',
  'targeting.rolloutChangeSummary.noVariationSelected': 'No variation selected',
  'targeting.rolloutChangeSummary.originalVariationRowLabel': 'Original variation:',
  'targeting.rolloutChangeSummary.removeRulesAndUpdateDefault': 'Remove rules and update default rule to serve',
  'targeting.rolloutChangeSummary.rolloutDurationRowLabel': 'Rollout duration:',
  'targeting.rolloutChangeSummary.rolloutRowLabel': 'Rollout:',
  'targeting.rolloutChangeSummary.ruleRow': 'Rule: {ruleName}',
  'targeting.rolloutChangeSummary.serveLabel': 'Serve:',
  'targeting.rolloutChangeSummary.startReleaseOn': 'Start release on {ruleName}',
  'targeting.rolloutChangeSummary.step': 'Step',
  'targeting.rolloutChangeSummary.stepCountLabel': 'Step count',
  'targeting.rolloutChangeSummary.stopAutomatedRelease': 'Stop automated release',
  'targeting.rolloutChangeSummary.unknownVariation': 'unknown variation',
  'targeting.rolloutChangeSummary.updateOffVariation': 'Update off variation to',
  'targeting.rolloutChangeSummary.variationNotFound': 'Variation not found',
  'targeting.rolloutChangeSummary.weight': 'Weight',
  'targeting.rolloutModePicker.experimentApprovalHint':
    'Serve a variation you don’t mind for now, like the control. Once approved, create an experiment from this rule.',
  'targeting.rolloutModePicker.listLabel': 'Variations, rollouts, and optimize',
  'targeting.rolloutModePicker.placeholder': 'Select a variation…',
  'targeting.rolloutModePicker.recommended': 'Recommended',
  'targeting.rolloutModePicker.selectVariationLabel': 'Select variation',
  'targeting.rolloutModePicker.triggerLabel': 'Rollout',
  'targeting.rolloutStages.addStepLabel': 'Add step below',
  'targeting.rolloutStages.deleteStepLabel': 'Delete',
  'targeting.rolloutStages.intervalUnitLabel': 'interval unit',
  'targeting.rolloutStages.totalDuration': 'Total duration',
  'targeting.rolloutSummary.aGuardedRollout': 'A guarded rollout',
  'targeting.rolloutSummary.aPercentageRollout': 'A percentage rollout',
  'targeting.rolloutSummary.aProgressiveRollout': 'A progressive rollout',
  'targeting.rolloutSummary.anExperimentAllocation': 'An experiment allocation',
  'targeting.rolloutSummary.by': 'By',
  'targeting.rolloutSummary.moreInfoLabel': 'More information',
  'targeting.rolloutSummary.otherContextKinds':
    'If this rule is evaluated for context kinds other than the one selected, those contexts will be served the first variation',
  'targeting.rolloutSummary.serve': 'Serve',
  'targeting.rolloutSummary.unknown': 'Unknown',
  'targeting.rolloutSummary.unknownVariation': 'Unknown variation',
  'targeting.rolloutValidation.invalidBucketBy':
    'Invalid attribute "{attributeName}" specified. Bucketing by this attribute is not allowed.',
  'targeting.rolloutValidation.rolloutRequired': 'You must specify a rollout',
  'targeting.rolloutValidation.weightAtLeast': 'The rollout percentage must be at least {value}',
  'targeting.rolloutValidation.weightAtMost': 'The rollout percentage must be at most {value}',
  'targeting.rolloutVariationPicker.selectVariation': 'Select variation',
  'targeting.ruleCard.descriptionLabel': 'Rule description',
  'targeting.ruleDescriptionField.duplicateError': 'Duplicate {label} description',
  'targeting.ruleDescriptionField.heading': 'Description',
  'targeting.ruleDescriptionField.inputLabel': 'Rule name',
  'targeting.ruleDescriptionField.namePlaceholder': '{label} {number}',
  'targeting.ruleDescriptionField.readOnlyPlaceholder': 'Rule {number}',
  'targeting.ruleDescriptionInline.duplicateDescription': 'Duplicate description',
  'targeting.ruleDescriptionInline.inputLabel': 'Rule description',
  'targeting.ruleDescriptionInline.restrictedTooltip': 'Your permissions do not allow you to modify this field',
  'targeting.ruleExclusion.coverageDataExport': 'data export',
  'targeting.ruleExclusion.coverageExperiments': 'experiments',
  'targeting.ruleExclusion.coverageExperimentsAndDataExport': 'experiments and data export',
  'targeting.ruleExclusion.edit': 'Edit',
  'targeting.ruleExclusion.includedIn': 'Included in {coverage}.',
  'targeting.ruleExclusion.saveOrDiscard': "Save or discard changes to the flag's targeting rules before editing",
  'targeting.ruleList.addRule': 'Add rule',
  'targeting.ruleOptionsMenu.copyLink': 'Copy link to {label}',
  'targeting.ruleOptionsMenu.copyLinkToast': 'Link to {label} copied to clipboard',
  'targeting.ruleOptionsMenu.createExperiment': 'Create experiment from {label}',
  'targeting.ruleOptionsMenu.delete': 'Delete {label}',
  'targeting.ruleOptionsMenu.disableBlockedExperiment': 'Discard experiment to disable this {label}',
  'targeting.ruleOptionsMenu.disableBlockedRollout': 'Stop the rollout to disable this {label}',
  'targeting.ruleOptionsMenu.duplicate': 'Duplicate {label}',
  'targeting.ruleOptionsMenu.experimentNeedsSave': 'Review and save rule changes before creating an experiment',
  'targeting.ruleOptionsMenu.experimentNeedsVariations': 'At least 2 variations are required to create an experiment',
  'targeting.ruleOptionsMenu.moveDown': 'Move {label} down',
  'targeting.ruleOptionsMenu.moveUp': 'Move {label} up',
  'targeting.ruleOptionsMenu.reorderBlockedRelease': 'Stop the release to reorder rules',
  'targeting.ruleOptionsMenu.toggleDisabled': '{isDisabled, select, true {Enable} other {Disable}} {label}',
  'targeting.rulePicker.noRules': 'No rules found',
  'targeting.rulePicker.ruleNotFound': 'Rule not found. Rule id <id>{ruleId}</id>',
  'targeting.rulePicker.selectRuleLabel': 'Select rule',
  'targeting.ruleReadOnly.aGuardedRollout': 'A guarded rollout',
  'targeting.ruleReadOnly.aPercentageRollout': 'A percentage rollout',
  'targeting.ruleReadOnly.aProgressiveRollout': 'A progressive rollout',
  'targeting.ruleReadOnly.anExperimentAllocation': 'An experiment allocation',
  'targeting.ruleReadOnly.clauseError': 'There was a problem rendering this clause.',
  'targeting.ruleReadOnly.serve': 'Serve',
  'targeting.ruleReadOnly.tryAgain': 'Try again',
  'targeting.ruleReadOnly.unknownVariation': 'Unknown',
  'targeting.ruleValidation.invalidRolloutVariation':
    'An invalid variation was specified in the rollout.  You must reconfigure the percentage rollout.',
  'targeting.ruleValidation.invalidVariation':
    'An invalid variation was specified.  You must select a valid variation.',
  'targeting.ruleValidation.segmentUnavailable': 'This rule references a segment that no longer exists',
  'targeting.ruleValidation.variationNotFound':
    'An invalid variation was specified. You must select a valid variation.',
  'targeting.ruleValidation.variationOrRolloutRequired':
    'You must either select a variation to serve or set a percentage rollout',
  'targeting.rulesEditor.clauseConnector':
    '{connector, select, editFirst {if} edit {and} readFirst {If} other {And if}}',
  'targeting.rulesEditor.defaultRuleNoPermission': "You don't have permission to update the default rule.",
  'targeting.rulesEditor.errorFallback':
    'This rule encountered an error and cannot be displayed. Try reloading the page.',
  'targeting.rulesEditor.eventsDataExport': 'data export destinations',
  'targeting.rulesEditor.eventsExperiments': 'experiments',
  'targeting.rulesEditor.eventsExperimentsAndExport': 'experiments and data export destinations',
  'targeting.rulesEditor.releaseName': 'Release',
  'targeting.rulesEditor.saveToCreateExperiment': 'Save to create an experiment from this rule',
  'targeting.rulesEditor.sendEvents': 'Send events to {destination}',
  'targeting.rulesEditor.serve': 'Serve',
  'targeting.rulesEditor.variationRequired': 'You must select a variation',
  'targeting.rulesOffBanner.body': '{resourceLabel} is off and no traffic will evaluate the rules below',
  'targeting.rulesOffBanner.prerequisiteCount': '{count, plural, one {# prerequisite} other {# prerequisites}}',
  'targeting.rulesOffBanner.ruleCount': '{count, plural, one {# rule} other {# rules}}',
  'targeting.rulesOffBanner.targetCount': '{count, plural, one {# target} other {# targets}}',
  'targeting.rulesSection.reorderRuleLabel': 'Reorder {ruleNoun}',
  'targeting.saveChangesDialog.accountLevelBody':
    'This will make changes to the <name>{resourceName}</name> {resourceKind} in your organization.',
  'targeting.saveChangesDialog.commentPlaceholder': 'Leave a comment.',
  'targeting.saveChangesDialog.commentRequired': 'Comments are required for changes in this environment',
  'targeting.saveChangesDialog.commentRequiredAllEnvs':
    'Comments are required for changes in one or more environments. These changes affect all environments in this project.',
  'targeting.saveChangesDialog.commentTitle': 'Leave a comment',
  'targeting.saveChangesDialog.confirm': 'Confirm',
  'targeting.saveChangesDialog.confirmationTitle': 'Confirmation required',
  'targeting.saveChangesDialog.environmentLevelBody':
    'This will make changes to the <name>{resourceName}</name> {resourceKind} in the <environment></environment> environment.',
  'targeting.saveChangesDialog.projectLevelBody':
    'This will make changes to the <name>{resourceName}</name> {resourceKind} in all environments.',
  'targeting.saveChangesDialog.save': 'Save changes',
  'targeting.saveChangesDialog.saveWithComment': 'Save with comment',
  'targeting.saveControls.disabledReason.conflict':
    'Someone else saved changes while you were editing. Reload the page and try again.',
  'targeting.saveControls.disabledReason.incomplete': 'Complete the highlighted sections to save.',
  'targeting.saveControls.disabledReason.invalid': 'Fix the errors in the highlighted sections to save.',
  'targeting.saveControls.disabledReason.noChanges': 'Make a change to save.',
  'targeting.saveControls.disabledReason.noPermission': "You don't have permission to save these changes.",
  'targeting.saveControls.moreOptionsLabel': 'More options',
  'targeting.saveControls.reviewAndSave': 'Review and save',
  'targeting.saveControls.saveWithoutApproval': 'Save without approval',
  'targeting.saveMenu.moreOptionsLabel': 'More options',
  'targeting.saveReceipt.trackActivityLink': 'Track evaluation activity on the Monitoring tab',
  'targeting.scheduleBadge.removeScheduleLabel': 'Remove schedule',
  'targeting.scheduleChangesPopover.removeScheduleLabel': 'Remove schedule',
  'targeting.scheduleChangesPopover.scheduleChanges': 'Schedule changes',
  'targeting.scheduleChangesPopover.scheduledLabel': 'Schedule changes, currently scheduled at {date}',
  'targeting.scheduleChangesPopover.update': 'Update',
  'targeting.scheduleRemoval.addAndScheduleRemoval': 'Add and schedule removal',
  'targeting.scheduleRemoval.buttonLabel': 'Schedule removal',
  'targeting.scheduleRemoval.cancel': 'Cancel',
  'targeting.scheduleRemoval.clear': 'Clear',
  'targeting.scheduleRemoval.differentVariationReason':
    'User is already scheduled for removal on a different variation. You must remove them before continuing.',
  'targeting.scheduleRemoval.remove': 'Remove',
  'targeting.scheduleRemoval.save': 'Save',
  'targeting.scheduleRemoval.title': 'Schedule expiring target',
  'targeting.scheduleRemoval.update': 'Update',
  'targeting.scheduleRemovalModal.cancel': 'Cancel',
  'targeting.scheduleRemovalModal.dialogLabel': 'Schedule removal',
  'targeting.scheduleRemovalModal.scheduleExpiringTarget': 'Schedule expiring target',
  'targeting.search.filterTargets': 'Filter targets',
  'targeting.search.matchingCount': '{count, number} matching {count, plural, one {target} other {targets}}',
  'targeting.search.searchLabel': 'Search targets',
  'targeting.search.unavailable': 'Search is not available. Try again later',
  'targeting.sectionCard.closeSectionLabel': 'close section',
  'targeting.sectionCard.discardUnsavedChangesLabel': 'Discard unsaved changes',
  'targeting.sectionCard.edit': 'Edit',
  'targeting.sectionCard.editSectionLabel': 'edit section',
  'targeting.sectionCard.openSectionLabel': 'open section',
  'targeting.sectionConnector.buildCustomRule': 'Build a custom rule',
  'targeting.sectionConnector.release': 'Release',
  'targeting.sectionConnector.setPrerequisites': 'Set prerequisites',
  'targeting.sectionConnector.targetAll': 'Target all…',
  'targeting.sectionConnector.targetIndividuals': 'Target individuals',
  'targeting.sectionConnector.targetMobile': 'Target mobile',
  'targeting.sectionConnector.targetSegments': 'Target segments',
  'targeting.sectionErrorFallback.body': 'The {sectionName} section could not be rendered. Try reloading the page.',
  'targeting.sectionErrorFallback.heading': 'This section encountered an error',
  'targeting.segmentClauseEditor.context': 'Context',
  'targeting.segmentClauseEditor.externalSegment': '{segmentName} is synced from an external source ({source})',
  'targeting.segmentClauseEditor.isIn': 'is in',
  'targeting.segmentClauseEditor.isNotIn': 'is not in',
  'targeting.segmentClauseEditor.noSegments':
    'No segments found — <manage>Manage segments</manage> or <create>Create a new segment</create>',
  'targeting.segmentClauseEditor.operator': 'Operator',
  'targeting.segmentClauseEditor.operatorLabel': 'Select an operator',
  'targeting.segmentClauseEditor.segmentsFieldLabel': 'Segments',
  'targeting.segmentClauseEditor.segmentsPlaceholder': 'Select segments…',
  'targeting.status.loading': 'Loading…',
  'targeting.stopGuardedPhase.investigateWithVega': 'Investigate with Vega',
  'targeting.stopGuardedPhase.viewDetails': 'View details',
  'targeting.targetActions.bulkEdit': 'Bulk edit',
  'targeting.targetActions.clear': 'Clear',
  'targeting.targetActions.clearNeedsTarget': 'Select a minimum of one target to clear targets',
  'targeting.targetActions.convertToSegment': 'Convert to segment',
  'targeting.targetActions.convertToSegmentNeedsTarget': 'Select a minimum of one target to convert to segment',
  'targeting.targetActions.exportCsv': 'Export as CSV',
  'targeting.targetActions.menuLabel': '{variation} target options',
  'targeting.targetActions.upgradeTooltip': 'Upgrade your plan to use this feature. Click to learn more.',
  'targeting.targetAllContextKindMenu.clearLabel': 'Clear',
  'targeting.targetAllContextKindMenu.listLabel': 'Context kinds',
  'targeting.targetAllContextKindMenu.placeholder': 'Search context kinds…',
  'targeting.targetAllContextKindMenu.searchLabel': 'Search context kinds',
  'targeting.targetExpirationPicker.apply': 'Apply',
  'targeting.targetExpirationPicker.cancel': 'Cancel',
  'targeting.targetList.noMatches': 'No targets match your filter criteria.',
  'targeting.targetList.resetFilters': 'Reset filters',
  'targeting.targetPopover.differentVariationTooltip':
    'User is already scheduled for removal on a different variation. You must remove them before continuing.',
  'targeting.targetPopover.privateAttribute': 'Marked as private',
  'targeting.targetPopover.privateAttributeTooltip':
    'This attribute is private so LaunchDarkly does not receive its value.',
  'targeting.targetPopover.removalDateLabel': 'Removal date:',
  'targeting.targetPopover.targetInformation': 'Target information',
  'targeting.targetPopover.viewSettings': 'View settings',
  'targeting.targetRow.cancelLabel': 'Cancel',
  'targeting.targetRow.deleteTarget': 'Delete target',
  'targeting.targetRow.scheduleRemoval': 'Schedule removal',
  'targeting.targetRow.scheduleTargetLabel': 'Schedule target',
  'targeting.targetingActionsMenu.actionsLabel': 'Targeting actions',
  'targeting.targetingActionsMenu.evaluateContextLabel': 'evaluate context',
  'targeting.targetingActionsMenu.json': 'JSON',
  'targeting.targetingActionsMenu.jsonEditingLabel': 'JSON editing',
  'targeting.targetingActionsMenu.switchToGui': 'Switch to GUI',
  'targeting.targetingActionsMenu.switchToJson': 'Switch to JSON',
  'targeting.targetingActionsMenu.testRun': 'Test run a context to find out which variation it will receive',
  'targeting.targetingActionsMenu.uiEditingLabel': 'UI editing',
  'targeting.targetingActionsMenu.visual': 'Visual',
  'targeting.targetingLayout.allTraffic': 'All traffic',
  'targeting.targetingLayout.stageEntryLabel': 'Targeting configuration for',
  'targeting.targetingLoadError.heading': 'Something went wrong.',
  'targeting.targetingSkeleton.loadingLabel': 'Loading flag targeting',
  'targeting.testRun.basedOnCurrent': 'Based on current targeting',
  'targeting.testRun.basedOnUnsaved': 'Based on unsaved changes',
  'targeting.testRun.errorEvaluatingContext': 'Error evaluating context',
  'targeting.testRun.finishConfiguringTargeting': 'Finish configuring targeting',
  'targeting.testRun.subtitle': 'See what variation a context would receive by supplying a context object.',
  'targeting.testRun.title': 'Test run a context',
  'targeting.testRunContext.clearContext': 'Clear context',
  'targeting.testRunContext.editorHeading': 'Context editor',
  'targeting.testRunContext.learnMore': 'Learn more',
  'targeting.testRunContext.pickerLabel': 'Context picker',
  'targeting.testRunContext.searchLabel': 'Search contexts…',
  'targeting.testRunResults.loadingItemDescription': 'Loading description text',
  'targeting.testRunResults.loadingItemLabel': 'Loading',
  'targeting.testRunResults.loadingItemStatus': 'Loading…',
  'targeting.testRunResults.loadingItemTitle': 'Loading placeholder text',
  'targeting.triggerCondition.aggregationOfColumn': '{aggregator} of {column}',
  'targeting.triggerCondition.aggregatorCountDistinct': 'count of distinct',
  'targeting.triggerCondition.anomalyPhrase': 'deviates from baseline (≥{confidence} confidence){over}',
  'targeting.triggerCondition.breachPhrase': 'breaches',
  'targeting.triggerCondition.constantPhrase': '{verb} {value}{over}',
  'targeting.triggerCondition.metricLabel': 'LLM error count',
  'targeting.triggerCondition.overWindow': ' over {window}',
  'targeting.triggerCondition.productCustomEvents': 'Custom events',
  'targeting.triggerCondition.productEvents': 'Events',
  'targeting.triggerCondition.productExternalMonitor': 'External monitor',
  'targeting.triggerCondition.productObservabilityMetrics': 'Observability metrics',
  'targeting.triggerCondition.sentryMonitorLabel': 'your Sentry Monitor',
  'targeting.triggerCondition.sourceInProduct': '{aggregation} in {source}',
  'targeting.triggerCondition.sourceOfProduct': '{aggregation} of {source}',
  'targeting.triggerCondition.summarySentence':
    'When <metric></metric> exceeds <threshold></threshold> over <window></window>',
  'targeting.triggerCondition.unitHours': '{count, plural, one {hour} other {hours}}',
  'targeting.triggerCondition.unitMinutes': '{count, plural, one {minute} other {minutes}}',
  'targeting.triggerCondition.valueWithUnit': '{value} {unit}',
  'targeting.triggerCondition.verbDropsBelow': 'drops below',
  'targeting.triggerCondition.verbExceeds': 'exceeds',
  'targeting.triggerCondition.whenBody': 'When <body></body>',
  'targeting.triggerCondition.windowHours': '{count, plural, one {# hour} other {# hours}}',
  'targeting.triggerCondition.windowMinutes': '{count, plural, one {# minute} other {# minutes}}',
  'targeting.triggerCondition.windowSeconds': '{count, plural, one {# second} other {# seconds}}',
  'targeting.triggerNotifications.addMembers': 'Add members',
  'targeting.triggerNotifications.addNotification': 'Add notification',
  'targeting.triggerNotifications.addSlackChannels': 'Add Slack channels',
  'targeting.triggerNotifications.membersSearchPlaceholder': 'Search members',
  'targeting.triggerNotifications.membersToNotify': 'Members to notify',
  'targeting.triggerNotifications.removeMemberLabel': 'Remove {name}',
  'targeting.triggerNotifications.removeSlackChannelLabel': 'Remove {name}',
  'targeting.triggerNotifications.selectedMembersLabel': 'Selected members',
  'targeting.triggerNotifications.selectedSlackChannelsLabel': 'Selected Slack channels',
  'targeting.triggerNotifications.slackActionPrompt': '{action} to send alerts to channels.',
  'targeting.triggerNotifications.slackAuthenticateAction': 'Authenticate Slack app',
  'targeting.triggerNotifications.slackChannelsTo': 'Slack channels to notify',
  'targeting.triggerNotifications.slackInstallAction': 'Install Slack app',
  'targeting.triggerNotifications.slackSearchPlaceholder': 'Search Slack channels',
  'targeting.triggerNotifications.slackStepAuthorize':
    '2. <link>Authorize the app</link> by running the <code>/launchdarkly account</code> command in your Slack workspace.',
  'targeting.triggerNotifications.slackStepInstall': '1. Install the <link>LaunchDarkly Slack app</link>.',
  'targeting.triggerTemplateStep.addAdaptiveTrigger': 'Add adaptive trigger',
  'targeting.triggerTemplateStep.closeLabel': 'Close',
  'targeting.triggerTemplateStep.selectATemplate': 'Select a template to get started',
  'targeting.triggersList.alertDeleteWarning': 'Trigger removed; the alert could not be deleted from Observability.',
  'targeting.triggersList.editTriggerLabel': 'Edit trigger',
  'targeting.triggersList.removeTriggerLabel': 'Remove trigger',
  'targeting.triggersList.thisTriggerCurrently':
    'This trigger currently has no effect. This rule already serves this variation.',
  'targeting.triggersList.triggerEffectInfoLabel': 'Trigger effect info',
  'targeting.triggersList.triggerRemoved': 'Trigger removed',
  'targeting.triggersList.when': 'When',
  'targeting.unauthenticatedTargetingSettings.disable': 'Disable',
  'targeting.unauthenticatedTargetingSettings.enable': 'Enable',
  'targeting.unauthenticatedTargetingSettings.enabledBody':
    'Unauthenticated targeting enabled. You can target using the user context with email {email}.',
  'targeting.unauthenticatedTargetingSettings.submitLabel': 'Enable unauthenticated targeting',
  'targeting.unsavedChangesPrompt.body': 'You will lose your changes to this flag.',
  'targeting.unsavedChangesPrompt.cancel': 'Keep editing',
  'targeting.unsavedChangesPrompt.confirm': 'Discard',
  'targeting.unsavedChangesPrompt.title': 'Discard your changes',
  'targeting.upgradeLink.viewPlans': 'View plans',
  'targeting.valueTypeIndicator.changeTypeTooltip': 'Click to change from a {type} to a {alternateType}',
  'targeting.valueTypeIndicator.numbersWithMore':
    'Numbers with more than 16 digits are supported as strings to avoid rounding errors',
  'targeting.valueTypeIndicator.typeChipLabel': '{type} value',
  'targeting.valueTypeIndicator.typeTooltip': 'This value is a {type}',
  'targeting.variationBar.filterLabel': 'Filter by {variationLabel}: {count, plural, one {# target} other {# targets}}',
  'targeting.variationBar.variationFallback': 'variation {number}',
  'targeting.variationPicker.clear': 'Clear',
  'targeting.variationSummary.noOffVariation':
    'No off variation is set. Users will get the default defined in your codebase.',
  'targeting.variationSummary.notSet': 'Not set',
  'targeting.variationSummary.offVariation': '– off variation',
  'targeting.variationSummary.onSaveTooltip': 'On save, will serve no off variation',
  'targeting.viewRulesButton.hideTargetingRules': 'Hide targeting rules',
  'targeting.viewRulesButton.viewTargetingRules': 'View targeting rules',
  'teams.access.actionsCellLabel': 'actions',
  'teams.access.actionsColumn': 'Actions',
  'teams.access.actionsMenuLabel': 'Team actions',
  'teams.access.count':
    '{roleCount, plural, one {# role} other {# roles}} and {resourceCount, plural, one {# resource} other {# resources}}',
  'teams.access.countLabel': 'roles and resources count',
  'teams.access.dateAddedCellLabel': 'date added',
  'teams.access.dateAddedColumn': 'Date added',
  'teams.access.editAccess': 'Edit access',
  'teams.access.removeAccess': 'Remove access',
  'teams.access.resourceAttributeKeyCellLabel': 'resource attribute key',
  'teams.access.resourceAttributeKeyColumn': 'Resource attribute key',
  'teams.access.resourcesCellLabel': 'resources',
  'teams.access.resourcesColumn': 'Resources',
  'teams.access.roleNameCellLabel': 'role name',
  'teams.access.roleNameColumn': 'Role name',
  'teams.access.searchLabel': 'Search team roles',
  'teams.access.searchPlaceholder': 'Find an assigned role or resource',
  'teams.access.tableLabel': 'Team access list',
  'teams.accessEmptyState.createCustomRole': 'Create custom role',
  'teams.accessEmptyState.description':
    'Assign roles so members can work in their projects. Use custom roles to grant exactly the permissions they need.',
  'teams.accessEmptyState.heading': 'Give this team access',
  'teams.accessEmptyState.readMore': 'Read more about teams',
  'teams.accessSummary.emptyMessage': 'This team has no project access.',
  'teams.accessSummary.noPermission': "You do not have permission to view this team's access summary.",
  'teams.accessSummary.stem': 'Members of the {teamName} team can:',
  'teams.addMembersDialog.alreadyOnTeam': 'These members are already on this team',
  'teams.addMembersDialog.cancel': 'Cancel',
  'teams.addMembersDialog.invalidEmails': 'Invalid email formatting',
  'teams.addMembersDialog.searchPlaceholder': 'Search members',
  'teams.addMembersDialog.submit': 'Add members',
  'teams.addMembersDialog.title': 'Add members to {teamName}',
  'teams.addMembersDialog.unknownEmails': 'These emails do not belong to members of your LaunchDarkly account',
  'teams.bulkAddMembers.fileTooLarge': 'File exceeds {maxSize} MB',
  'teams.bulkAddMembers.resultSummary':
    '{successCount} lines processed successfully. Errors found in {errorCount} of {totalCount} lines:',
  'teams.bulkAddMembers.unableToProcess': 'Unable to process file',
  'teams.createDialog.cancel': 'Cancel',
  'teams.createDialog.descriptionLabel': 'Description',
  'teams.createDialog.keyLabel': 'Key',
  'teams.createDialog.maintainerLabel': 'Maintainer',
  'teams.createDialog.maintainersLabel': 'Maintainers',
  'teams.createDialog.nameLabel': 'Name',
  'teams.createDialog.selectMaintainer': 'Select maintainer',
  'teams.createDialog.submit': 'Create team',
  'teams.createDialog.title': 'Create team',
  'teams.deleteTeamDialog.body':
    'This team will be deleted permanently and all members will lose access to projects assigned. Team maintainers will be unassigned.',
  'teams.deleteTeamDialog.confirm': 'Delete',
  'teams.deleteTeamDialog.title': 'Delete {teamName}?',
  'teams.emptyState.assignCustomRoles':
    'Assign custom roles to control access to your projects and organize your projects by team',
  'teams.emptyState.heading': 'Manage access to your projects with teams',
  'teams.emptyState.limitAccess': 'Limit access to only those who need it',
  'teams.emptyState.managePermissions': 'Manage permissions for multiple people at once',
  'teams.filteredEmptyState.checkSpelling': 'Make sure you spelled the search terms correctly',
  'teams.filteredEmptyState.heading': 'No teams match. Try this:',
  'teams.filteredEmptyState.searchByNameOrKey': 'Search for a team by name or key.',
  'teams.history.description': "Team history includes updates made to the team's settings in LaunchDarkly.",
  'teams.history.searchPlaceholder': "Search your team's history",
  'teams.list.createdManually': 'Manual',
  'teams.list.membersColumn': 'Members',
  'teams.list.nameColumn': 'Name',
  'teams.list.newTeam': 'New team',
  'teams.list.searchLabel': 'Search teams',
  'teams.list.searchPlaceholder': 'Search teams by name or key',
  'teams.list.syncedStatusColumn': 'Source',
  'teams.list.syncedWith': 'Synced with {provider}',
  'teams.list.tableLabel': 'Teams list',
  'teams.list.teamCount': '{count, plural, one {# team} other {# teams}}',
  'teams.makeMaintainerDialog.body':
    'Team maintainers can add and remove team members only. They cannot manage team permissions, nor do they inherit permissions from the team.',
  'teams.makeMaintainerDialog.cancel': 'Cancel',
  'teams.makeMaintainerDialog.confirm': 'Make maintainer',
  'teams.makeMaintainerDialog.title': 'Make {name} a team maintainer',
  'teams.members.actionsColumn': 'Actions',
  'teams.members.addMembers': 'Add members',
  'teams.members.emailColumn': 'Email',
  'teams.members.maintainerTagsLabel': 'Member tags',
  'teams.members.makeTeamMaintainer': 'Make team maintainer',
  'teams.members.memberActionsLabel': 'Member actions',
  'teams.members.memberCount': '{count, plural, one {# member} other {# members}}',
  'teams.members.nameColumn': 'Name',
  'teams.members.removeFromTeam': 'Remove from team',
  'teams.members.removeTeamMaintainer': 'Remove team maintainer',
  'teams.members.searchPlaceholder': 'Find a member',
  'teams.members.tableLabel': 'Team members',
  'teams.members.teamMaintainerTag': 'Team maintainer',
  'teams.members.uploadCsv': 'Upload CSV',
  'teams.membersEmptyState.description': "This team doesn't have any members yet.",
  'teams.membersEmptyState.heading': 'No team members',
  'teams.membersEmptyState.learnMore': 'Learn how to manage teams',
  'teams.notifications.addCustomRolesPermissionDenied':
    'You do not have the necessary permissions to add team custom roles',
  'teams.notifications.addMaintainersPermissionDenied':
    'You do not have the necessary permissions to add team maintainers',
  'teams.notifications.addMembersPermissionDenied': 'You do not have the necessary permissions to add team members',
  'teams.notifications.bulkMembersAdded': '{count, plural, one {# member} other {# members}} added',
  'teams.notifications.createFailed': 'Team creation unsuccessful. Try again later.',
  'teams.notifications.createPermissionDenied': 'You do not have the necessary permissions to create a team',
  'teams.notifications.created': '{teamName} created',
  'teams.notifications.customRoleRemoved': 'Custom role removed',
  'teams.notifications.customRolesAdded': '{count, plural, one {# custom role} other {# custom roles}} added',
  'teams.notifications.deleteFailed': 'Team deletion unsuccessful. Try again later.',
  'teams.notifications.deleted': '{teamName} deleted',
  'teams.notifications.joinRequestFailed': 'Error sending request. Try again later.',
  'teams.notifications.maintainerRemoved': 'Maintainer removed',
  'teams.notifications.maintainersUpdated': 'Team maintainers updated',
  'teams.notifications.memberRemoved': 'Member removed from team',
  'teams.notifications.membersAdded': '{count, plural, one {# team member} other {# team members}} added',
  'teams.notifications.removeCustomRolesPermissionDenied':
    'You do not have the necessary permissions to remove team custom roles',
  'teams.notifications.removeMaintainersPermissionDenied':
    'You do not have the necessary permissions to remove team maintainers',
  'teams.notifications.removeMembersPermissionDenied':
    'You do not have the necessary permissions to remove team members',
  'teams.notifications.updateFailed': 'Team update unsuccessful. Try again later.',
  'teams.notifications.updatePermissionDenied': 'You do not have the necessary permissions to update that team',
  'teams.notifications.updated': '{teamName} updated',
  'teams.removeMaintainerDialog.body':
    'This action will remove this member as team maintainer of this team. Do you want to proceed?',
  'teams.removeMaintainerDialog.cancel': 'Cancel',
  'teams.removeMaintainerDialog.confirm': 'Remove team maintainer',
  'teams.removeMaintainerDialog.title': 'Remove {name} as team maintainer',
  'teams.removeMemberDialog.body':
    'Removing a member from a team also removes their team-based permissions. This action will remove this member from this team. Do you want to proceed?',
  'teams.removeMemberDialog.cancel': 'Cancel',
  'teams.removeMemberDialog.confirm': 'Remove member',
  'teams.removeMemberDialog.fallbackName': 'member',
  'teams.removeMemberDialog.title': 'Remove {name} from {teamName}',
  'teams.settings.accessDenied': "You don't have access to this team. Contact your administrator.",
  'teams.settings.deleteDescription':
    'This team will be deleted permanently and all members will lose access to assigned projects.',
  'teams.settings.deleteHeading': 'Delete team',
  'teams.settings.deleteTeam': 'Delete team',
  'teams.settings.deleting': 'Deleting …',
  'teams.settings.descriptionLabel': 'Description',
  'teams.settings.descriptionPlaceholder': 'team description',
  'teams.settings.generalHeading': 'General',
  'teams.settings.keyLabel': 'Key',
  'teams.settings.loadError': 'There was a problem fetching the team. Try again later.',
  'teams.settings.nameLabel': 'Name',
  'teams.settings.namePlaceholder': 'team name',
  'teams.settings.restrictedFieldTooltip': "Your permissions don't allow you to modify this field",
  'teams.settings.tableLabel': 'Team settings',
  'teams.settings.update': 'Update',
  'teams.status.loading': 'Loading…',
  'teams.syncAlert.editingLimited':
    'Editing is limited for {count} teams synced with and managed in {provider} because <link>team sync with SCIM is enabled</link>. To learn more,',
  'teams.syncAlert.membershipLocked':
    'Managing team membership is not allowed while <link>team sync with SCIM is enabled</link>.',
  'teams.syncAlert.settingsLocked':
    'You cannot edit or delete the team while <link>team sync with SCIM is enabled</link>.',
  'teams.teamPage.accessSummaryTab': 'Access summary',
  'teams.teamPage.accessTab': 'Access',
  'teams.teamPage.maintainedBy': 'Maintained by',
  'teams.teamPage.maintainers': '{count, plural, =0 {Manage maintainers} one {# maintainer} other {# maintainers}}',
  'teams.teamPage.maintainersUpdated': 'Team maintainers updated',
  'teams.teamPage.membersTab': 'Members',
  'teams.teamPage.menuTitle': 'Team menu',
  'teams.teamPage.notSynced': 'Not synced',
  'teams.teamPage.rolesTab': 'Roles',
  'teams.teamPage.settingsTab': 'Settings',
  'teams.teamPage.syncStatusLabel': 'status',
  'teams.teamPage.synced': 'Synced',
  'teams.teamPage.updateError': 'Team update unsuccessful. Try again later.',
  'teams.teamsPicker.searchPlaceholder': 'Search teams',
  'teams.teamsPicker.triggerLabel': 'Select teams',
  'teams.uploadCsvDialog.addValidEntries': 'Add valid entries',
  'teams.uploadCsvDialog.cancel': 'Cancel',
  'teams.uploadCsvDialog.clearFileLabel': 'Cancel',
  'teams.uploadCsvDialog.emptyLine': 'empty line',
  'teams.uploadCsvDialog.firstColumnNote': 'The file’s first column must be the list of member emails.',
  'teams.uploadCsvDialog.formattingExample': 'Formatting example',
  'teams.uploadCsvDialog.formattingExampleAlt':
    'The format of the file should be a single column CSV containing only identifiers separated by commas',
  'teams.uploadCsvDialog.headersNote': 'Headers are optional.',
  'teams.uploadCsvDialog.learnMore': 'To learn more,',
  'teams.uploadCsvDialog.otherColumnsNote': 'Subsequent columns may contain data, but will be ignored.',
  'teams.uploadCsvDialog.selectFile': 'Select file',
  'teams.uploadCsvDialog.selectedFile': 'Selected File:',
  'teams.uploadCsvDialog.title': 'Upload CSV to {teamName}',
  'teams.uploadCsvDialog.uploadFile': 'Upload file',
  'teams.uploadCsvDialog.uploadNewFile': 'Upload new file',
  'teams.uploadCsvDialog.uploading': 'Uploading',
  'traces.page.title': 'Traces',
  'ui.actionBar.groupLabel': 'Form actions',
  'ui.alertCollapsible.toggle': 'Show {isExpanded, select, true {less} other {more}}',
  'ui.calendar.nextMonthLabel': 'Next month',
  'ui.calendar.previousMonthLabel': 'Previous month',
  'ui.cardWithTitle.toggleLabel': 'Toggle expansion',
  'ui.chart.emptyLabel': 'Empty chart',
  'ui.chart.loadingData': 'Loading data…',
  'ui.clearButton.clearLabel': 'Clear',
  'ui.codeEditor.copyIdLabel': 'Copy id',
  'ui.codeEditor.loadError': 'Failed to load code editor.',
  'ui.codeEditor.readOnlyTooltip': 'This field is read-only',
  'ui.codeEditor.retryLabel': 'retry loading code editor',
  'ui.comboBox.showOptionsLabel': 'Show options',
  'ui.confirmation.configType': 'config',
  'ui.confirmation.confirmLabel': 'Confirm',
  'ui.confirmation.environmentType': 'environment',
  'ui.confirmation.experimentType': 'experiment',
  'ui.confirmation.flagType': 'flag',
  'ui.confirmation.incorrectKey': 'The key entered is incorrect',
  'ui.confirmation.keyType': 'key',
  'ui.confirmation.relayConfigurationType': 'Relay configuration',
  'ui.confirmation.segmentType': 'segment',
  'ui.confirmation.typeToConfirm': 'Type <resource></resource> to confirm changes to this {type}.',
  'ui.confirmation.typeYesToConfirm': 'Type yes to confirm',
  'ui.connectionError.body':
    'There was a problem connecting to LaunchDarkly. Check your connection and try again later.',
  'ui.connectionError.heading': 'Unable to connect',
  'ui.connectionError.learnMore': 'To learn more, read <link>Authentication</link>.',
  'ui.connectionError.originMismatch':
    'Your browser is making requests with an origin header that doesn’t match {baseUri}. Check your browser configuration.',
  'ui.copyLink.copyLabel': 'Copy',
  'ui.dataTable.additionalResults.label': '{count} new {resource}',
  'ui.dataTable.additionalResults.pollingExpired': 'Load new results',
  'ui.dataTable.cellActions.columnRowLabel': 'Actions for {columnName} in row {rowLabel}',
  'ui.dataTable.cellActions.copy': 'Copy',
  'ui.dataTable.cellActions.exclude': 'Exclude this value',
  'ui.dataTable.cellActions.filter': 'Filter for this value',
  'ui.dataTable.cellActions.rowLabel': 'Actions for row {rowLabel}',
  'ui.dataTable.cellButtons.actionLabel': '{label} {rowLabel}',
  'ui.dataTable.cell.empty': 'Empty',
  'ui.dataTable.columnManager.dialogLabel': 'Manage columns',
  'ui.dataTable.columnManager.heading': 'Columns',
  'ui.dataTable.columnManager.loadingLabel': 'Loading attribute columns',
  'ui.dataTable.columnManager.noResults': 'No matching columns',
  'ui.dataTable.columnManager.searchLabel': 'Search columns',
  'ui.dataTable.columnManager.searchPlaceholder': 'Search…',
  'ui.dataTable.columnManager.triggerLabel': 'Manage columns',
  'ui.dataTable.columnMenu.copySearchKey': 'Copy search key',
  'ui.dataTable.columnMenu.hideColumn': 'Hide {columnName}',
  'ui.dataTable.columnMenu.moveLeft': 'Move column left',
  'ui.dataTable.columnMenu.moveRight': 'Move column right',
  'ui.dataTable.columnMenu.removeSort': 'Remove sort',
  'ui.dataTable.columnMenu.resetSize': 'Reset size',
  'ui.dataTable.columnMenu.sortAscending': 'Sort ascending',
  'ui.dataTable.columnMenu.sortDescending': 'Sort descending',
  'ui.dataTable.columnMenu.triggerLabel': 'Actions for {columnName} column',
  'ui.dataTable.columnMenu.updateLabel': 'Update label',
  'ui.dataTable.functions.manageColumnsColumnLabel': 'Manage columns',
  'ui.dataTable.functions.rowActionsColumnLabel': 'Row actions',
  'ui.dataTable.loading.label': 'Loading rows',
  'ui.dataTable.loadMore.loadingLabel': 'Loading more rows',
  'ui.dataTable.selection.columnLabel': 'Row selection',
  'ui.dataTable.tree.collapseLabel': 'Collapse {label}',
  'ui.dataTable.tree.expandLabel': 'Expand {label}',
  'ui.dateField.openCalendarLabel': 'Open date picker',
  'ui.datePicker.applyDateLabel': 'Date when this change should be applied',
  'ui.datePicker.openLabel': 'Open date picker',
  'ui.dateRangeFilter.apply': 'Apply',
  'ui.dateRangeFilter.cancel': 'Cancel',
  'ui.dateRangeFilter.toPlaceholder': 'To',
  'ui.dateRangeFilterMenu.apply': 'Apply',
  'ui.dateRangeFilterMenu.cancel': 'Cancel',
  'ui.dateRangeFilterMenu.toPlaceholder': 'To',
  'ui.dateRangePopover.actionsLabel': 'apply or cancel date range',
  'ui.dateRangePopover.apply': 'Apply',
  'ui.dateRangePopover.cancel': 'Cancel',
  'ui.dateRangePopover.openLabel': 'Select custom date range',
  'ui.dateShortcuts.last12Months': 'Last 12 months',
  'ui.dateShortcuts.last14Days': 'Last 14 days',
  'ui.dateShortcuts.last24Hours': 'Last 24 hours',
  'ui.dateShortcuts.last30Days': 'Last 30 days',
  'ui.dateShortcuts.last3Months': 'Last 3 months',
  'ui.dateShortcuts.last60Days': 'Last 60 days',
  'ui.dateShortcuts.last60Minutes': 'Last 60 minutes',
  'ui.dateShortcuts.last6Months': 'Last 6 months',
  'ui.dateShortcuts.last7Days': 'Last 7 days',
  'ui.dateShortcuts.lastMonth': 'Last month',
  'ui.dateShortcuts.lastWeek': 'Last week',
  'ui.dateShortcuts.mostRecent': 'Most recent',
  'ui.dateShortcuts.since': 'Since {label}',
  'ui.dateShortcuts.today': 'Today',
  'ui.dateShortcuts.yesterday': 'Yesterday',
  'ui.dateTimePicker.mustBeInFutureError': 'Selected time must be in the future',
  'ui.dateTimePicker.nextMonthLabel': 'next',
  'ui.dateTimePicker.previousMonthLabel': 'previous',
  'ui.dateTimePicker.showSuggestionsLabel': 'Show suggestions',
  'ui.dateTimePicker.timezoneLabel': 'Select timezone',
  'ui.dateTimePicker.timezonePlaceholder': 'Select timezone',
  'ui.dialog.closeLabel': 'Close dialog',
  'ui.durationPicker.groupLabel': 'Select time duration',
  'ui.durationPicker.optionLabel': '{duration} duration',
  'ui.editableSection.closeSectionLabel': 'close section',
  'ui.editableSection.edit': 'Edit',
  'ui.editableSection.editLabel': 'edit section',
  'ui.editableSection.openSectionLabel': 'open section',
  'ui.editableSection.undoLabel': 'undo changes',
  'ui.ellipsisButton.menuLabel': '{label} menu',
  'ui.emptyState.learnMorePrefix': 'To learn more,',
  'ui.fieldError.prefix': 'Error:',
  'ui.filterPillGroup.moreCount': '+ {count} more',
  'ui.helpDialog.closeLabel': 'Close dialog',
  'ui.infoLabel.infoButtonLabel': '{label} info',
  'ui.inlineSelect.placeholder': 'Select option',
  'ui.inlineTagGroup.addTags': 'Add tags',
  'ui.inlineTagGroup.selectedLabel': 'Selected tags',
  'ui.jsonSchemaForm.addItem': 'Add Item',
  'ui.jsonSchemaForm.arrayContextError': 'Error: Array renderer requires formContext in options',
  'ui.jsonSchemaForm.arrayItemTypeError': 'Error: Array items must have a type',
  'ui.jsonSchemaForm.arrayItemTypeUnsupported': 'Error: Unsupported array item type: {itemType}',
  'ui.jsonSchemaForm.arraySchemaError': 'Error: Array schema must have items',
  'ui.jsonSchemaForm.emptyArray': 'No items yet. Click "Add Item" to add one.',
  'ui.jsonSchemaForm.objectContextError': 'Error: Object renderer requires formContext in options',
  'ui.jsonSchemaForm.objectSchemaError': 'Error: Object schema must have properties',
  'ui.jsonSchemaForm.removeItemLabel': 'Remove item {position}',
  'ui.jsonSchemaForm.submit': 'Submit',
  'ui.list.clear': 'Clear',
  'ui.list.loadError': 'Error loading data',
  'ui.list.loadingLabel': 'Loading',
  'ui.list.noResults': 'No results',
  'ui.list.retry': 'Retry',
  'ui.list.retryLabel': 'Retry loading data',
  'ui.list.selectedCount': '{count} selected',
  'ui.listBoxDialog.closeLabel': 'Close dialog',
  'ui.listBoxDialog.filterLabel': 'Filter items',
  'ui.listBoxDialog.noItemsFound': 'No items found',
  'ui.listBoxDialog.searching': 'Searching…',
  'ui.logo.label': 'LaunchDarkly',
  'ui.logoGrid.trustedBy': 'Trusted by 5,500+ customers, from startups to enterprise',
  'ui.multiComboBox.clearSelectionLabel': 'Clear selection',
  'ui.multiComboBox.optionsLabel': 'Available options',
  'ui.multiComboBox.searchAndFilterLabel': 'Search and filter',
  'ui.multiComboBox.searchLabel': 'Search items',
  'ui.multiComboBox.selectedItemsLabel': 'Selected items',
  'ui.multiComboBox.showOptionsLabel': 'Show options',
  'ui.multiSelect.clearLabel': 'clear',
  'ui.multiSelect.moreCount': '+{count} more',
  'ui.multiSelect.noOptionsFound': 'No options found',
  'ui.multiSelect.searchLabel': '{placeholder} MultiSelect Search',
  'ui.multiSelectFilter.allOptions': 'All {options}',
  'ui.multiSelectFilter.allSummary': 'All {options}',
  'ui.multiSelectFilter.apply': 'Apply',
  'ui.multiSelectFilter.cancel': 'Cancel',
  'ui.multiSelectFilter.countSummary': '{count} {options}',
  'ui.multiSelectFilter.deselectAllLabel': 'Deselect all',
  'ui.multiSelectFilter.noChangesToApply': 'No changes to apply',
  'ui.multiSelectFilter.optionsLabel': '{label} filter options',
  'ui.multiSelectFilter.searchLabel': 'Search options',
  'ui.multiSelectFilter.selectAllLabel': 'Select all',
  'ui.multiSelectFilter.selectAtLeastOne': 'Select at least one option',
  'ui.newTabLink.opensInNewTab': '(Link opens in new tab)',
  'ui.oauthLogin.connectTo': 'Connect to {name}',
  'ui.oauthLogin.connectedTo': 'Connected to {name}',
  'ui.oauthLogin.connectedToBy': 'Connected to {name} by <strong>{memberName}</strong>',
  'ui.oauthLogin.disconnect': 'Disconnect',
  'ui.oauthLogin.requiresAuth': 'The {name} integration requires an authenticated user',
  'ui.pagination.firstPage': 'first page',
  'ui.pagination.firstPageLabel': 'first {kind} page',
  'ui.pagination.lastPage': 'last page',
  'ui.pagination.lastPageLabel': 'last {kind} page',
  'ui.pagination.loadError': 'Unable to load pagination',
  'ui.pagination.nextPage': 'next page',
  'ui.pagination.nextPageLabel': 'next {kind} page',
  'ui.pagination.noResults': 'No results',
  'ui.pagination.previousPage': 'previous page',
  'ui.pagination.previousPageLabel': 'previous {kind} page',
  'ui.pagination.range': '<range>{from}–{to}</range> of <count>{total}</count>',
  'ui.promoCard.closeLabel': 'Close',
  'ui.promoCard.iconAlt': 'Icon',
  'ui.requiredFields.notice': '{count, plural, one {Required field} other {Required fields}}',
  'ui.resourceSelector.done': 'Done',
  'ui.resourceSelector.selectedCount': '<count>{selectedCount}</count> {resources} selected',
  'ui.resourceSelector.unselectAll': 'Unselect all',
  'ui.saveChangesModal.addReviewer': 'Add a reviewer',
  'ui.saveChangesModal.addReviewerHelp': 'Optionally request someone to review your changes before they are applied.',
  'ui.saveChangesModal.addReviewerHelpLabel': 'Add a reviewer help',
  'ui.saveChangesModal.closeLabel': 'close modal',
  'ui.saveChangesModal.commentHint': 'Add an optional comment',
  'ui.saveChangesModal.commentLabel': 'Comment',
  'ui.saveChangesModal.maxReviewers': 'You may only specify at most {maxLength} reviewers',
  'ui.saveChangesModal.reasonHint': 'Provide a reason for these changes',
  'ui.saveChangesModal.reasonLabel': 'Reason',
  'ui.saveChangesModal.reviewersHint':
    'Select at least {count, plural, one {# reviewer} other {# reviewers}} to be notified.',
  'ui.saveChangesModal.reviewersLabel': 'Reviewers',
  'ui.searchBox.clear': 'Clear',
  'ui.searchBox.clearLabel': 'Clear search',
  'ui.searchBox.loadingLabel': 'Loading search results',
  'ui.searchBox.searchLabel': 'Search',
  'ui.searchCategories.groupLabel': 'Category group {number}',
  'ui.searchField.clearButton': 'clear',
  'ui.searchField.clearLabel': 'Clear',
  'ui.searchField.clearSearch': 'Clear search',
  'ui.searchResults.failedToLoad': 'Failed to load',
  'ui.select.clearLabel': 'clear search',
  'ui.select.label': 'Select',
  'ui.select.noOptions': 'No options available',
  'ui.select.placeholder': 'Select…',
  'ui.select.removeOptionFallbackLabel': 'Remove option',
  'ui.select.removeOptionLabel': 'Remove {option}',
  'ui.select.searchLabel': '{label} search',
  'ui.select.searchPlaceholder': 'Search…',
  'ui.skeleton.loadingLabel': 'Loading…',
  'ui.snippet.copyLabel': 'Copy code snippet',
  'ui.status.loading': 'Loading…',
  'ui.status.loadingDots': 'Loading…',
  'ui.swatchColorPicker.customColorHexLabel': 'Custom color hex value',
  'ui.swatchColorPicker.customColorLabel': 'Custom color',
  'ui.swatchColorPicker.defaultColorsLabel': 'Default colors',
  'ui.swatchColorPicker.heading': 'Environment Color',
  'ui.tabbedSnippet.loadingLabel': 'Loading',
  'ui.tabbedSnippet.showAll': 'Show all',
  'ui.tabbedSnippet.showLess': 'Show less',
  'ui.tag.removeLabel': 'Remove tag',
  'ui.tagAutocomplete.addNew': 'Add: <em>{name}</em>',
  'ui.tagAutocomplete.clearAll': 'Clear all',
  'ui.tagAutocomplete.noMatches': 'No matching tags',
  'ui.tagAutocomplete.searchLabel': 'Search tags…',
  'ui.tagAutocomplete.startTyping': 'Start typing to add tags',
  'ui.tagFilter.addTags': 'Add tag(s)',
  'ui.tagFilter.filterLabel': 'Tags',
  'ui.tagGroup.addLabel': 'Add tag',
  'ui.tagGroup.addNew': 'Add: <em>{name}</em>',
  'ui.tagGroup.clearAll': 'Clear all',
  'ui.tagGroup.clearSearchLabel': 'clear',
  'ui.tagGroup.noMatches': 'No matching {label}',
  'ui.tagGroup.searchLabel': 'Search {label}…',
  'ui.tagGroup.selectedLabel': 'Selected {label}',
  'ui.tagGroup.startTyping': 'Start typing to add {label}',
  'ui.tagPicker.createTag': 'Create new tag "{name}"',
  'ui.tagPicker.noTagsFound': 'No tags found',
  'ui.tagPicker.removeAll': 'Remove all tags',
  'ui.tagPicker.searchLabel': 'Search tags',
  'ui.timePager.backLabel': 'Back',
  'ui.timePager.controlsLabel': 'Time range playback controls',
  'ui.timePager.forwardLabel': 'Forward',
  'ui.timePager.live': 'Live',
  'ui.timePager.paused': 'Paused',
  'ui.timePager.refreshingLabel': 'Refreshing',
  'ui.timePager.stepBackTooltip': 'Step back {presetLabel}',
  'ui.timePager.stepForwardTooltip': 'Step forward {presetLabel}',
  'ui.timePicker.hour': 'Hour',
  'ui.timePicker.minute': 'Minute',
  'ui.timePicker.selectLabel': 'Select time',
  'ui.timeRangeCalendar.nextMonthLabel': 'Next month',
  'ui.timeRangeCalendar.previousMonthLabel': 'Previous month',
  'ui.timeRangeField.durationLabel': 'Selected range duration',
  'ui.timeRangeField.label': 'Date range',
  'ui.timeRangeField.lastPreset': 'Last {preset}',
  'ui.timeRangeField.openPickerLabel': 'Open date range picker',
  'ui.timeRangeFilter.actionsLabel': 'apply or cancel date range',
  'ui.timeRangeFilter.apply': 'Apply',
  'ui.timeRangeFilter.cancel': 'Cancel',
  'ui.timeRangeFilter.customRange': 'Custom range',
  'ui.timeRangeFilter.customRangeTextValue': 'custom date range',
  'ui.timeRangeFilter.openPickerLabel': 'open date range picker',
  'ui.timeRangeFilter.presetsLabel': 'Preset date range options',
  'ui.timeRangeInputs.end': 'End',
  'ui.timeRangeInputs.endDateLabel': 'End date',
  'ui.timeRangeInputs.endTimeLabel': 'End time',
  'ui.timeRangeInputs.start': 'Start',
  'ui.timeRangeInputs.startDateLabel': 'Start date',
  'ui.timeRangeInputs.startTimeLabel': 'Start time',
  'ui.timeRangeMenu.customDateRange': 'Custom date range',
  'ui.timeRangeMenu.lastPreset': 'Last {preset}',
  'ui.timeRangeMenu.menuLabel': 'Time range presets',
  'ui.timeSeriesChart.additionalItems': '{count} additional items not shown.',
  'ui.timeSeriesChart.label': 'Chart for time series data',
  'ui.timeZonePicker.label': 'Timezone',
  'ui.timeZonePicker.selectLabel': 'Select timezone',
  'ui.trendChip.label': 'Trend',
  'ui.urlField.notAbsolute': 'This is not an absolute URL',
  'ui.urlField.portNotAllowed': 'This url contains a port that is not allowed. Use either port 80 or 443',
  'ui.variation.buttonLabel': 'Variation',
  'ui.variation.emptyString': '(empty string)',
  'uxInsights.page.title': 'UX insights',
  'uxInsights.page.runDate': '{day} · {time} PT',
  'uxInsights.page.runBadge': 'Run {date}',
  'uxInsights.page.areaTabsLabel': 'Product areas',
  'uxInsights.page.addProductArea': 'Add product area',
  'uxInsights.page.metricsLabel': 'Headline numbers',
  'uxInsights.page.resultTabsLabel': 'Research results',
  'uxInsights.page.findingsLabel': 'Findings',
  'uxInsights.card.noScreenshot': 'No session replay screenshot yet',
  'uxInsights.page.getCustomReport': 'Get custom report',
  'uxInsights.metrics.wau': 'Weekly active users',
  'uxInsights.metrics.activation': 'Activation rate',
  'uxInsights.metrics.coreAction': 'Core action rate',
  'uxInsights.metrics.taskCompletion': 'Task completion rate',
  'uxInsights.metrics.churn': 'Churn rate',
  'uxInsights.metrics.aboutMetric': 'About {metric}',
  'uxInsights.metrics.wauDefinition': 'People who used this area in the last 7 days, including through the API.',
  'uxInsights.metrics.activationDefinition':
    "Share of members created in the last 7 days who did this area's main action.",
  'uxInsights.metrics.coreActionDefinition.agentcontrol':
    "Share of this week's users who created or edited an AI Config or one of its variations. Sample configs from onboarding don't count.",
  'uxInsights.metrics.coreActionDefinition.featureManagement':
    "Share of this week's users who created or changed a feature flag.",
  'uxInsights.metrics.coreActionDefinition.releaseGuardian':
    "Share of this week's users who started a guarded rollout.",
  'uxInsights.metrics.coreActionDefinition.experimentation':
    "Share of this week's users who created, edited, or started an experiment.",
  'uxInsights.metrics.coreActionDefinition.observability':
    "Share of this week's users who opened a session replay, error, log, or trace.",
  'uxInsights.metrics.coreActionDefinition.platform':
    "Share of this week's users who added or edited a member, created a team, created or edited an environment or custom role, or changed approval settings.",
  'uxInsights.metrics.taskCompletionDefinition':
    'Share of sessions where the user finished what they came to do. Based on a sample of users.',
  'uxInsights.metrics.churnDefinition':
    "Share of users active in the previous {days} days who didn't come back in the last {days} days.",
  'uxInsights.metrics.noValue': '—',
  'uxInsights.metrics.noPrevious': 'No earlier data',
  'uxInsights.metrics.countChange': '{change} vs. last week',
  'uxInsights.metrics.pointsChange': '{change} pts vs. last week',
  'uxInsights.metrics.churnChange': '{days}-day, {change} pts',
  'uxInsights.metrics.churnWindow': '{days}-day',
  'uxInsights.metrics.loadFailed': "Couldn't load the headline numbers.",
  'uxInsights.metrics.noSavedRun': 'No headline numbers are saved for this area yet.',
  'uxInsights.source.loadFailed': "Couldn't load the latest research run.",
  'uxInsights.source.savedRunDate': 'From the research run on {date}.',
  'uxInsights.source.savedRun':
    'From the research run on {date}, based on {count, plural, one {# session replay} other {# session replays}}.',
  'uxInsights.source.savedRunOfTotal':
    'From the research run on {date}, based on {count, plural, one {# session replay} other {# session replays}} out of {total, number} in this area over the past {days, plural, one {day} other {# days}}.',
  'uxInsights.emptyTab.heading': "There isn't any data yet",
  'uxInsights.emptyTab.description': 'Schedule a research run to see results here.',
  'uxInsights.emptyTab.schedule': 'Schedule a research run',
  'uxInsights.emptyTab.started': 'Research run started. Results appear here as each tab finishes.',
  'uxInsights.emptyTab.alreadyRunning': 'A research run is already running for this product area.',
  'uxInsights.emptyTab.startFailed': "The research run didn't start. Try again.",
  'uxInsights.tabs.patterns': 'Patterns',
  'uxInsights.tabs.taskFriction': 'Task friction',
  'uxInsights.tabs.performanceFriction': 'Performance friction',
  'uxInsights.tabs.workarounds': 'Workarounds',
  'uxInsights.tabs.topUsers': 'Top users',
  'uxInsights.tabs.prImpact': 'PR impact',
  'uxInsights.tabs.uiHygiene': 'UI hygiene',
  'uxInsights.tabs.contentHygiene': 'Content hygiene',
  'uxInsights.chips.label': 'Details',
  'uxInsights.finding.goToTicket': 'Go to ticket',
  'uxInsights.panel.closeLabel': 'Close panel',
  'uxInsights.panel.close': 'Close',
  'uxInsights.panel.kicker':
    '{kind, select, pattern {Pattern} friction {Friction} performance {Performance friction} workaround {Workaround} hygiene {UI hygiene} content {Content hygiene} other {Finding}} · rank {rank}',
  'uxInsights.panel.whatWeSaw': 'What we saw',
  'uxInsights.panel.examples': 'Examples',
  'uxInsights.panel.watchSessions': 'Watch the sessions',
  'uxInsights.panel.showMoreSessions': 'Show more',
  'uxInsights.panel.showFewerSessions': 'Show less',
  'uxInsights.panel.likelyMeaning': 'What it likely means',
  'uxInsights.panel.howSure': 'How sure we are',
  'uxInsights.panel.whatWeCouldDo': 'What we could do',
  'uxInsights.panel.confidence': '{confidence, select, high {High} mixed {Mixed} other {Low}}',
  'uxInsights.panel.createTicket': 'Create ticket',
  'uxInsights.panel.createNewTicket': 'Create new ticket',
  'uxInsights.panel.goToTicket': 'Go to ticket',
  'uxInsights.panel.mostObserved': 'Where it happens most',
  'uxInsights.card.recurrence': '{weeks, plural, =1 {New this week} other {Seen # of the last 4 weeks}}',
  'uxInsights.panel.overTime': 'Over time',
  'uxInsights.panel.recurrence':
    'First seen the week of {firstSeen}. Seen in {weeks, plural, =1 {# week} other {# weeks}} of the last 4.',
  'uxInsights.ticket.heading': 'Create Jira ticket',
  'uxInsights.ticket.loadingProjects': 'Loading Jira projects',
  'uxInsights.ticket.unavailable': "Creating Jira tickets isn't turned on for this account.",
  'uxInsights.ticket.noPermission': "You don't have permission to create Jira tickets in this project.",
  'uxInsights.ticket.notConnected': 'Connect Jira to create tickets from UX insights.',
  'uxInsights.ticket.loadFailed': "Couldn't load your Jira projects. Try again later.",
  'uxInsights.ticket.connectJira': 'Connect Jira',
  'uxInsights.ticket.createFailed': "Couldn't create the Jira ticket. Try again.",
  'uxInsights.ticket.project': 'Project',
  'uxInsights.ticket.issueType': 'Issue type',
  'uxInsights.ticket.title': 'Title',
  'uxInsights.ticket.summary': 'Summary',
  'uxInsights.ticket.cancel': 'Cancel',
  'uxInsights.ticket.create': 'Create ticket',
  'uxInsights.ticket.created': 'Jira ticket {key} created',
  'uxInsights.ticket.problem': 'Problem',
  'uxInsights.ticket.whyItMatters': 'Why it matters',
  'uxInsights.ticket.stats': 'Stats',
  'uxInsights.ticket.evidence': 'Evidence',
  'uxInsights.ticket.replay': 'Replay:',
  'uxInsights.ticket.solutions': 'Potential solutions',
  'uxInsights.ticket.acceptanceCriteria': 'Acceptance criteria',
  'uxInsights.ticket.where': 'Where:',
  'uxInsights.ticket.code': 'Code:',
  'uxInsights.ticket.expected': 'Expected:',
  'uxInsights.ticket.measured': 'Measured:',
  'uxInsights.ticket.measuredRow': '{page} at {width}px: {value}',
  'uxInsights.ticket.screenshot': 'Screenshot:',
  'uxInsights.ticket.currentText': 'Current text:',
  'uxInsights.ticket.guideline': 'Guideline:',
  'uxInsights.ticket.rewrite': 'Suggested rewrite:',
  'uxInsights.topUsers.searchLabel': 'Search users',
  'uxInsights.topUsers.searchPlaceholder': 'Search users…',
  'uxInsights.topUsers.signals': 'Signals',
  'uxInsights.topUsers.clearSelection': 'Clear selection',
  'uxInsights.topUsers.timeframe': 'Timeframe',
  'uxInsights.topUsers.timeframeOption':
    '{days, select, 1 {Last 24 hours} 7 {Last 7 days} 14 {Last 14 days} 30 {Last 30 days} other {Last 90 days}}',
  'uxInsights.topUsers.exportCsv': 'Export CSV',
  'uxInsights.topUsers.tableLabel': 'Top users',
  'uxInsights.topUsers.user': 'User',
  'uxInsights.topUsers.account': 'Account',
  'uxInsights.topUsers.sessions': 'Sessions',
  'uxInsights.topUsers.days': 'Days',
  'uxInsights.topUsers.activeMinutes': 'Active min',
  'uxInsights.topUsers.lastSeen': 'Last seen',
  'uxInsights.topUsers.details': 'Details',
  'uxInsights.topUsers.noMatches': 'No users match these filters',
  'uxInsights.topUsers.noMatchesDescription': 'Try a different search, signal or timeframe.',
  'uxInsights.topUsers.today': 'Today',
  'uxInsights.topUsers.yesterday': 'Yesterday',
  'uxInsights.topUsers.daysAgo': '{count, number} days ago',
  'uxInsights.topUsers.weeksAgo': '{count, plural, one {# week ago} other {# weeks ago}}',
  'uxInsights.topUsers.panelKicker': 'User · {account}',
  'uxInsights.topUsers.activity': 'Activity',
  'uxInsights.topUsers.activitySummary':
    '{sessions} sessions over {days, number} days, {minutes, number} active minutes',
  'uxInsights.topUsers.reachOut': 'Reach out',
  'uxInsights.prImpact.group':
    '{signal, select, directional {Small directional signal} inconclusive {Change seen, but not reliable} none {No measurable change} other {Too little data}}',
  'uxInsights.prImpact.number': '#{number}',
  'uxInsights.prImpact.flag': 'Feature flag: {key}',
  'uxInsights.prImpact.cost': 'Estimated cost {cost}',
  'uxInsights.prImpact.roi': 'ROI: {roi, select, high {High} medium {Medium} other {Low}}',
  'uxInsights.prImpact.before': 'Before',
  'uxInsights.prImpact.after': 'After',
  'uxInsights.prImpact.barsLabel': '{metric}. Before {before}, after {after}',
  'uxInsights.uiHygiene.where': 'Where',
  'uxInsights.uiHygiene.comparison': 'Spacing across pages',
  'uxInsights.uiHygiene.page': 'Page',
  'uxInsights.uiHygiene.width': 'Width',
  'uxInsights.uiHygiene.spacing': 'Spacing',
  'uxInsights.uiHygiene.widthValue': '{width}px',
  'uxInsights.uiHygiene.expectedHeading': 'Expected',
  'uxInsights.uiHygiene.screenshots': 'Screenshots',
  'uxInsights.uiHygiene.colorKey':
    'Pink = label or related item, blue = control, purple = helper text, filled band = the measured gap.',
  'uxInsights.uiHygiene.whereInCode': 'Where in code',
  'uxInsights.contentHygiene.currentText': 'Current text',
  'uxInsights.contentHygiene.guideline': 'Guideline',
  'uxInsights.contentHygiene.rewrite': 'Suggested rewrite',
  'uxInsights.contentHygiene.whereInCode': 'Where in code',
  'uxInsights.contentHygiene.weeklyOnly': 'Content hygiene results appear after the weekly Sunday run.',
  'uxInsights.report.heading': 'Custom report',
  'uxInsights.report.closeLabel': 'Close custom report',
  'uxInsights.report.greeting':
    'Hi. What kind of insights are you looking for? Pick a suggestion or describe it in your own words.',
  'uxInsights.report.suggestionOnboarding': 'Where do users drop off in onboarding?',
  'uxInsights.report.suggestionRelease': 'Impact of my last release',
  'uxInsights.report.suggestionMonitoring': 'Friction on the Monitoring page',
  'uxInsights.report.suggestionChurn': 'Which accounts are at churn risk?',
  'uxInsights.report.inputLabel': 'Describe the insights you need',
  'uxInsights.report.placeholder': 'Describe the insights you need…',
  'uxInsights.report.send': 'Send',
  'uxInsights.report.writing': 'Writing your report…',
  'uxInsights.report.error': 'The report could not be written. Try again.',
  'uxInsights.report.download': 'Download as Markdown',
  'uxInsights.report.botName': 'UX insights',
  'uxInsights.report.youName': 'You',
  'uxInsights.report.suggestionsLabel': 'Suggestions',
  'vega.alertRemediation.aiAccessProduct': 'auto-remediation',
  'vega.alertRemediation.automaticallyCreated': 'Automatically created:',
  'vega.alertRemediation.checkBackIn':
    'Check back in a moment or view the full remediation to see the in-progress conversation.',
  'vega.alertRemediation.contactToIncreaseLimit': 'Contact observability@launchdarkly.com to increase your limit.',
  'vega.alertRemediation.disabledBody':
    "With automatic remediations enabled, {botName} can remediate an alert as soon as it's triggered. The remediation results will be shown here.",
  'vega.alertRemediation.enableAutoRemediation': 'Enable auto-remediation',
  'vega.alertRemediation.enabledBody':
    'Auto-remediations are enabled for this alert. {botName} will automatically remediate when this alert is triggered and the remediation results will appear here.',
  'vega.alertRemediation.inProgress':
    'This remediation is still in progress. The AI agent is actively analyzing your alert data.',
  'vega.alertRemediation.limitReached':
    'Auto-remediations are not being run because your workspace has reached its Vega prompt limit ({count} / {limit} prompts used).',
  'vega.alertRemediation.noSummaryAvailable': 'No summary available for this remediation.',
  'vega.alertRemediation.title': 'Remediate with {botName}',
  'vega.alertRemediation.viewFullRemediation': 'View full remediation',
  'vega.alertRemediation.viewLastRemediationHint':
    'You can still view your last auto-remediation using the "View full remediation" button below.',
  'vega.artifactCard.alsoApproved': '· {count} approved',
  'vega.artifactCard.approved': '{count} approved',
  'vega.artifactCard.changesRequested': '{count} changes requested',
  'vega.artifactCard.checks': 'Checks <strong>{success}/{total}</strong>',
  'vega.artifactCard.checksFailing': '· {count} failing',
  'vega.artifactCard.checksPending': '· {count} pending',
  'vega.artifactCard.moreEnvironments': '+{count} more',
  'vega.artifactCard.moreEnvironmentsLabel': '{count} more environments',
  'vega.artifactCard.openExternallyLabel': 'Open {typeLabel} externally (opens in a new tab)',
  'vega.artifactCard.openExternallyTitle': 'Open {typeLabel} externally',
  'vega.artifactCard.openFlagLabel': 'Open flag in LaunchDarkly (opens in a new tab)',
  'vega.artifactCard.openPullRequestLabel': 'Open pull request in GitHub (opens in a new tab)',
  'vega.artifactCard.reviews': 'Reviews <strong>{summary}</strong>',
  'vega.artifactCard.showFewerEnvironments': 'Show fewer environments',
  'vega.artifactCard.showMoreEnvironments':
    '{count, plural, one {Show # more environment} other {Show # more environments}}',
  'vega.artifactsPage.any': 'Any',
  'vega.artifactsPage.clearSearchLabel': 'Clear search',
  'vega.artifactsPage.creatorAny': 'Any',
  'vega.artifactsPage.creatorFilter': 'Creator: {label}',
  'vega.artifactsPage.creatorFilterLabel': 'Filter by creator',
  'vega.artifactsPage.creatorMe': 'Me',
  'vega.artifactsPage.loadingLabel': 'Loading artifacts…',
  'vega.artifactsPage.searchLabel': 'Search artifacts',
  'vega.artifactsPage.searchPlaceholder': 'Search…',
  'vega.artifactsPage.sourceAutomatedCleanup': 'Automated cleanup',
  'vega.artifactsPage.sourceFactory': 'Release Autopilot',
  'vega.artifactsPage.sourceFilter': 'Source: {label}',
  'vega.artifactsPage.sourceFilterLabel': 'Filter by artifact source',
  'vega.artifactsPage.sourceTool': 'Tool',
  'vega.artifactsPage.sourceWebhook': 'Webhook',
  'vega.artifactsPage.title': 'Artifact',
  'vega.artifactsPage.typeFilter': 'Type: {label}',
  'vega.artifactsPage.typeFilterLabel': 'Filter by artifact type',
  'vega.artifactsPane.emptyBody': 'Artifacts that Vega touches in this conversation will appear here.',
  'vega.artifactsPane.emptyTitle': 'No artifacts yet',
  'vega.artifactsPane.loadError': 'Couldn’t load artifacts',
  'vega.artifactsPane.loadErrorBody': 'Something went wrong fetching artifacts. Refresh to try again.',
  'vega.askVegaProgress.checkingAttributes': 'Checking available attributes',
  'vega.askVegaProgress.checkingFlags': 'Checking feature flags',
  'vega.askVegaProgress.checkingRepository': 'Checking the repository',
  'vega.askVegaProgress.editingFiles': 'Editing files',
  'vega.askVegaProgress.fetchingPage': 'Fetching a web page',
  'vega.askVegaProgress.listingFiles': 'Listing files',
  'vega.askVegaProgress.planning': 'Planning next steps',
  'vega.askVegaProgress.queryingData': 'Querying data',
  'vega.askVegaProgress.readingFiles': 'Reading files',
  'vega.askVegaProgress.runningCommand': 'Running a command',
  'vega.askVegaProgress.samplingValues': 'Sampling values',
  'vega.askVegaProgress.searchingCode': 'Searching the code',
  'vega.askVegaProgress.usingSkill': 'Using a skill',
  'vega.cleanupDialog.addContext': 'Add context',
  'vega.cleanupDialog.addContextHint':
    "Optionally provide extra context for the cleanup agent. Note that Vega will automatically respect your repo's agents.md file. Read more using the 'View docs' button below.",
  'vega.cleanupDialog.closeLabel': 'Close',
  'vega.cleanupDialog.contextHint':
    "Optionally provide extra context for the cleanup agent. The default prompt is always included. Vega will also respect your repo's agents.md file. Read more using the 'View docs' button below.",
  'vega.cleanupDialog.contextPlaceholder': 'Additional context or instructions for the cleanup agent…',
  'vega.cleanupDialog.createNew': 'Create new',
  'vega.cleanupDialog.environmentLabel': 'Environment',
  'vega.cleanupDialog.evaluationChartLabel': 'Flag evaluation history for {environment}',
  'vega.cleanupDialog.historyTab': 'Previous cleanups ({count})',
  'vega.cleanupDialog.intro':
    'Start a Vega agent session to open a PR that removes references to "{flagKey}" from the selected repositories.',
  'vega.cleanupDialog.letTeammatesRead': 'Let teammates read this conversation',
  'vega.cleanupDialog.loadingEvaluations': 'Loading evaluations…',
  'vega.cleanupDialog.noDataBody':
    'This flag may not be in use, or your <link>critical environments</link> may not be set up correctly.',
  'vega.cleanupDialog.noDataTitle': 'This flag has no recent evaluations',
  'vega.cleanupDialog.noEvaluationData': 'No evaluation data',
  'vega.cleanupDialog.permissionDenied': 'Permission denied.',
  'vega.cleanupDialog.permissionDeniedBody':
    "You don't have permission to use Vega. A workspace admin must grant you access to the <code>talkToVega</code> action. <link>Read about Vega roles and RBAC</link>.",
  'vega.cleanupDialog.readDocs': 'Read docs',
  'vega.cleanupDialog.safeBody':
    'This flag has evaluated to only one variation in the past week across <link>critical environments</link>. It may be safe to remove code references to the flag.',
  'vega.cleanupDialog.safeTitle': 'Likely safe to clean up',
  'vega.cleanupDialog.shareAgentConversation': 'Share agent conversation',
  'vega.cleanupDialog.startCleanup': 'Start cleanup',
  'vega.cleanupDialog.staticVariation': 'Static variation:',
  'vega.cleanupDialog.staticVariationHint': 'The agent will be instructed to clean up to this variation.',
  'vega.cleanupDialog.tabsLabel': 'Flag cleanup tabs',
  'vega.cleanupDialog.title': 'Flag cleanup',
  'vega.cleanupDialog.unsafeBody':
    'This flag has evaluated to multiple different variations in the past week across <link>critical environments</link>. It may still be actively used.',
  'vega.cleanupDialog.unsafeTitle': 'Potentially unsafe to clean up',
  'vega.cleanupHistory.actionsLabel': 'Actions',
  'vega.cleanupHistory.empty': 'Empty',
  'vega.cleanupHistory.emptyBody':
    'Cleanups you initiate for this flag will appear here so you can track their progress.',
  'vega.cleanupHistory.emptyTitle': 'No previous cleanups',
  'vega.cleanupHistory.openConversationLabel': 'Open conversation',
  'vega.cleanupHistory.owner': 'Owner',
  'vega.cleanupHistory.relatedPr': 'Related PR',
  'vega.cleanupHistory.started': 'Started',
  'vega.cleanupHistory.status': 'Status',
  'vega.cleanupProperty.aiAccessProduct': 'flag cleanup',
  'vega.cleanupProperty.new': 'New',
  'vega.cleanupProperty.none': 'None',
  'vega.cleanupProperty.previousCleanups': '{count, plural, one {# previous cleanup} other {# previous cleanups}}',
  'vega.cleanupProperty.startCleanup': 'Start cleanup',
  'vega.cleanupProperty.statusLabel': 'Feature status',
  'vega.cleanupProperty.title': 'Vega cleanup',
  'vega.canvas.closePickerLabel': 'Close',
  'vega.canvas.emptyBody':
    'Open a trace, session, or other observability view. Vega can open one here during the conversation.',
  'vega.canvas.emptyHeading': 'Nothing is open',
  'vega.canvas.environmentKeyDescription': 'Leave empty to use the selected environment',
  'vega.canvas.environmentKeyLabel': 'Environment key',
  'vega.canvas.environmentLabel': 'Environment',
  'vega.canvas.environmentUnavailable': 'Environment {environmentKey} is not available. Showing {environmentName}.',
  'vega.canvas.errorGroupIdLabel': 'Error group ID',
  'vega.canvas.escapeBack': 'Back to the canvas view',
  'vega.canvas.escapeBody': 'Open it in a new tab to keep this conversation in place.',
  'vega.canvas.escapeHeading': 'This page opens outside the canvas',
  'vega.canvas.flagKeyLabel': 'Flag key',
  'vega.canvas.flagNotFound':
    'LaunchDarkly could not find the flag {flagKey} in this project. Ask Vega to open a different flag.',
  'vega.canvas.flagsQueryLabel': 'Search (optional)',
  'vega.canvas.loading': 'Loading view',
  'vega.canvas.logsQueryLabel': 'Log query',
  'vega.canvas.openInLaunchDarkly': 'Open in LaunchDarkly',
  'vega.canvas.openLabel': 'Open view',
  'vega.canvas.pickerTitle': 'Open a view',
  'vega.canvas.refreshDeferred':
    "Vega changed this flag's targeting while you had unsaved edits. Discard your edits to load its change.",
  'vega.canvas.refreshReload': 'Discard edits and reload',
  'vega.canvas.routeErrorHeading': 'LaunchDarkly could not load this view',
  'vega.canvas.serviceNameLabel': 'Service name',
  'vega.canvas.sessionIdLabel': 'Session ID',
  'vega.canvas.submit': 'Open',
  'vega.canvas.timestampDescription': 'UTC time of the trace',
  'vega.canvas.timestampLabel': 'Timestamp',
  'vega.canvas.traceIdLabel': 'Trace ID',
  'vega.canvas.typeErrorGroup': 'Error group',
  'vega.canvas.typeFlag': 'Flag targeting',
  'vega.canvas.typeFlags': 'Flags list',
  'vega.canvas.typeLogs': 'Logs',
  'vega.canvas.typeService': 'Service',
  'vega.canvas.typeSession': 'Session',
  'vega.canvas.typeTrace': 'Trace',
  'vega.canvas.unsupported': 'This view is not available in the canvas yet.',
  'vega.canvas.viewLabel': 'View',
  'vega.composer.askAboutSourcePlaceholder': 'Ask {botName} about this {label}, @ to use filters…',
  'vega.composer.conversationStopped': 'Conversation stopped — send a message to continue, or start a new chat',
  'vega.composer.loadingRepositories': 'Loading repositories…',
  'vega.composer.placeholder': 'Talk to {botName}, @ to use filters…',
  'vega.composer.releaseType': 'Release type',
  'vega.composer.repoCloneNotice':
    'Code in selected repositories may be cloned in an isolated environment as part of investigations. <link>Learn more</link>',
  'vega.composer.sendPromptLabel': 'Send prompt',
  'vega.composer.settingsLabel': 'Vega settings',
  'vega.composer.sourceAtlas': 'Atlas resource',
  'vega.composer.sourceConfluence': 'Confluence page',
  'vega.composer.sourceGeneric': 'source',
  'vega.composer.sourceGithub': 'GitHub source',
  'vega.composer.sourceIssue': 'issue',
  'vega.composer.sourceJira': 'Jira issue',
  'vega.composer.sourcePullRequest': 'PR',
  'vega.composer.sourceSlack': 'Slack thread',
  'vega.composer.stopGeneratingLabel': 'Stop generating',
  'vega.composer.suggestedPrompts': 'Suggested prompts',
  'vega.composer.usageLimitContactLabel': 'Contact us to increase limit',
  'vega.composer.usageLimitDismissLabel': 'Dismiss usage warning',
  'vega.composer.usageLimitMessage': '{count}/{limit} runs used, contact us to increase limit',
  'vega.conversation.activity.api': 'Called APIs',
  'vega.conversation.activity.calledTool': 'Called {name}',
  'vega.conversation.activity.calledToolTarget': 'Called {name} · {target}',
  'vega.conversation.activity.data': 'Inspected data',
  'vega.conversation.activity.edit': 'Edited files',
  'vega.conversation.activity.editedFile': 'Edited {name}',
  'vega.conversation.activity.explore': 'Explored the codebase',
  'vega.conversation.activity.flags': 'Queried LaunchDarkly',
  'vega.conversation.activity.other': 'Tool calls',
  'vega.conversation.activity.pluralApi': 'Made {count} API calls',
  'vega.conversation.activity.pluralData': 'Ran {count} data queries',
  'vega.conversation.activity.pluralEdit': 'Made {count} edits',
  'vega.conversation.activity.pluralExplore': 'Made {count} codebase lookups',
  'vega.conversation.activity.pluralFlags': 'Made {count} LaunchDarkly queries',
  'vega.conversation.activity.pluralOther': 'Made {count} tool calls',
  'vega.conversation.activity.pluralRepository': 'Ran {count} git commands',
  'vega.conversation.activity.pluralShell': 'Ran {count} bash commands',
  'vega.conversation.activity.repository': 'Repository',
  'vega.conversation.activity.shell': 'Ran commands',
  'vega.conversation.activity.singleApi': 'Called an API',
  'vega.conversation.activity.singleData': 'Inspected data',
  'vega.conversation.activity.singleEdit': 'Edited a file',
  'vega.conversation.activity.singleExplore': 'Explored the codebase',
  'vega.conversation.activity.singleFlags': 'Queried LaunchDarkly',
  'vega.conversation.activity.singleRepository': 'Ran git command',
  'vega.conversation.activity.singleShell': 'Ran bash command',
  'vega.conversation.activity.skill': 'Ran skill',
  'vega.conversation.activity.skillNamed': 'Ran skill · {name}',
  'vega.conversation.activity.toolFallback': 'a tool',
  'vega.conversation.activity.withTarget': '{action} · {name}',
  'vega.conversation.contextLabel': 'Context',
  'vega.conversation.copyInput': 'Copy input',
  'vega.conversation.copyInputSuccess': 'Input copied to clipboard',
  'vega.conversation.copyOutput': 'Copy output',
  'vega.conversation.copyOutputSuccess': 'Output copied to clipboard',
  'vega.conversation.copyReplyLabel': 'Copy message',
  'vega.conversation.copyReplySuccess': 'Message copied to clipboard',
  'vega.conversation.copyUserMessageLabel': 'Copy message',
  'vega.conversation.copyUserMessageSuccess': 'Message copied to clipboard',
  'vega.conversation.errorBedrockConfigurationBody':
    "Check the Amazon Bedrock role and region in your Vega inference key in integration settings, and that the role's trust policy requires the external ID shown in Vega settings. Then send your message again.",
  'vega.conversation.errorBedrockConfigurationTitle': "Vega couldn't use your Amazon Bedrock settings",
  'vega.conversation.errorBudgetExceededBody':
    'The run used its full budget before it could finish. Send a follow-up message to continue, or start a new chat with a narrower request.',
  'vega.conversation.errorBudgetExceededTitle': 'Vega hit its limit for this run',
  'vega.conversation.errorDefaultBody': 'Something went wrong while generating a response. Try again in a moment.',
  'vega.conversation.errorDefaultTitle': 'Vega ran into a problem',
  'vega.conversation.errorForbiddenBody':
    'You do not have permission to use Vega in this project. Ask a project administrator for access.',
  'vega.conversation.errorForbiddenTitle': 'Vega access denied',
  'vega.conversation.errorNoInferenceKeyTitle': 'Custom key unavailable',
  'vega.conversation.errorRuntimeBody':
    'The run stopped because of an unexpected error. Try sending your message again.',
  'vega.conversation.errorRuntimeTitle': 'Vega ran into a problem',
  'vega.conversation.errorUnavailableBody':
    "Vega's inference layer didn't respond. This is usually transient — try the same prompt again in a minute.",
  'vega.conversation.errorUnavailableTitle': 'Vega is temporarily unavailable',
  'vega.conversation.evaluationGrade': 'Evaluation - {grade}',
  'vega.conversation.hideToolInputs': 'Hide tool inputs',
  'vega.conversation.loadingConversation': 'Loading conversation…',
  'vega.conversation.metadataLabel': 'Message metadata',
  'vega.conversation.openResourceLabel': 'Open {resourceType}',
  'vega.conversation.planDeveloping': 'Developing a plan',
  'vega.conversation.planFinalized': 'Plan finalized',
  'vega.conversation.plannedActions': 'Planned actions',
  'vega.conversation.ranCommands': 'Ran {count, plural, one {# command} other {# commands}}',
  'vega.conversation.reasoning': 'Reasoning',
  'vega.conversation.reasoningMessages': '{count, plural, one {# message} other {# messages}}',
  'vega.conversation.replyActions': 'Reply actions',
  'vega.conversation.showDetails': 'Show details',
  'vega.conversation.showLess': 'Show less',
  'vega.conversation.showMore': 'Show more',
  'vega.conversation.showToolInputs': 'Show tool inputs',
  'vega.conversation.startingAgentTask': 'Starting a new agent task…',
  'vega.conversation.thinking': 'Thinking…',
  'vega.conversation.thoughtFor': 'Thought for {duration}',
  'vega.conversation.tokensCache': '{tokens} cache',
  'vega.conversation.tokensIn': '{tokens} in',
  'vega.conversation.tokensOut': '{tokens} out',
  'vega.conversation.toolCalls': '{count, plural, one {# call} other {# calls}}',
  'vega.conversationTabs.artifacts': 'Artifacts',
  'vega.conversationTabs.messages': 'Messages',
  'vega.conversationTabs.noArtifactsYet': 'No artifacts yet',
  'vega.conversationTabs.tabsLabel': 'Conversation',
  'vega.emptyState.aiBadgeLabel': 'AI badge',
  'vega.emptyState.enableVega': 'Enable Vega',
  'vega.emptyState.featureCleanupDescription':
    "Identify stale and unused flags, then safely remove them with Vega's guidance.",
  'vega.emptyState.featureCleanupTitle': 'Flag Cleanup',
  'vega.emptyState.featureDebuggingDescription':
    'Let Vega analyze your feature flags and identify issues before they impact users.',
  'vega.emptyState.featureDebuggingTitle': 'Investigative Debugging',
  'vega.emptyState.featurePrsDescription':
    'Vega can create pull requests to implement flag changes and code updates automatically',
  'vega.emptyState.featurePrsTitle': 'Automated PRs',
  'vega.emptyState.heading': 'Meet Vega',
  'vega.emptyState.orderFormRequired':
    "Vega isn't enabled for your account yet. Reach out to your LaunchDarkly account team to ask about getting access to Vega. Note that enabling Vega requires an additional order form to accept LaunchDarkly's AI feature terms.",
  'vega.emptyState.poweredByAi': 'Powered by AI',
  'vega.emptyState.subtitle':
    'Your intelligent assistant for feature flag management. Vega helps you debug issues, automate workflows and keep your flags clean and organized',
  'vega.flagStatus.description': 'Current delivery state across environments',
  'vega.flagStatus.heading': 'Rollout status',
  'vega.flagStatus.loadError': "Couldn't load rollout status. Refresh to try again.",
  'vega.flagStatus.loading': 'Loading rollout status…',
  'vega.flagStatus.noEnvironments': 'No environment rollout data is available.',
  'vega.flagStatus.unavailable': 'Rollout status is unavailable.',
  'vega.history.anotherUser': 'another user',
  'vega.history.categoriesLabel': 'History categories',
  'vega.history.chatsTab': 'Chats',
  'vega.history.clearSearchLabel': 'Clear search',
  'vega.history.failedToLoad': 'Failed to load conversations',
  'vega.history.filterAll': 'All',
  'vega.history.filterAutoRemediations': 'Auto remediations',
  'vega.history.filterChatsLabel': 'Filter chats',
  'vega.history.filterFactory': 'Release Autopilot',
  'vega.history.filterFlagCleanups': 'Flag cleanups',
  'vega.history.filterMine': 'Mine',
  'vega.history.filterRunsLabel': 'Filter runs',
  'vega.history.filterShared': 'Shared',
  'vega.history.kindAutomatedCleanupTooltip': 'Automated cleanup: {flagKey}',
  'vega.history.kindCleanup': 'Cleanup',
  'vega.history.kindCleanupTooltip': 'Flag cleanup: {flagKey}',
  'vega.history.kindFactory': 'Release Autopilot',
  'vega.history.kindFactoryTooltip': 'Release Autopilot analysis run',
  'vega.history.kindRemediation': 'Remediation',
  'vega.history.kindRemediationTooltip': 'Auto-investigation run',
  'vega.history.loadingConversations': 'Loading conversations…',
  'vega.history.noAutoRemediations': 'No auto remediations yet',
  'vega.history.noChatsMatchSearch': 'No chats match your search',
  'vega.history.noFactoryRuns': 'No Release Autopilot runs yet',
  'vega.history.noFlagCleanups': 'No flag cleanups yet',
  'vega.history.noPersonalChats': 'No personal chats yet',
  'vega.history.noRuns': 'No runs yet',
  'vega.history.noRunsMatchSearch': 'No runs match your search',
  'vega.history.noSharedChats': 'No chats have been shared with you',
  'vega.history.prClosed': 'PR closed',
  'vega.history.prClosedTooltip': 'PR closed without merging',
  'vega.history.prMerged': 'PR merged',
  'vega.history.prMergedTooltip': 'PR merged',
  'vega.history.prOpened': 'PR opened',
  'vega.history.prOpenedTooltip': 'PR opened',
  'vega.history.promptsUsed': '{count} prompts used',
  'vega.history.promptsUsedOfLimit': '{count} / {limit} prompts used',
  'vega.history.runsTab': 'Runs',
  'vega.history.searchChatsLabel': 'Search chats',
  'vega.history.searchPlaceholder': 'Search…',
  'vega.history.searchRunsLabel': 'Search runs',
  'vega.history.shared': 'Shared',
  'vega.history.sharedByLabel': 'Shared by {owner}',
  'vega.history.statusCompleted': 'Completed',
  'vega.history.statusFailed': 'Failed',
  'vega.history.statusInProgress': 'In progress',
  'vega.history.statusStale': 'Active for over 90 minutes — this chat may have stalled',
  'vega.history.statusStopped': 'Stopped',
  'vega.history.tabsLabel': 'Conversation history',
  'vega.history.untitledChat': 'Untitled chat',
  'vega.landingPage.aiBadgeLabel': 'AI badge',
  'vega.landingPage.greeting': 'Get started, {firstName}',
  'vega.landingPage.greetingAnonymous': 'Get started',
  'vega.landingPage.poweredByAi': 'Powered by AI',
  'vega.landingPage.startFailed': 'Failed to start conversation. Try again.',
  'vega.landingPage.subtitle':
    'Your intelligent assistant for feature flag management. Vega helps you debug issues, automate workflows and keep your flags clean and organized',
  'vega.mentions.noCategoriesMatch': 'No categories matching “{query}”',
  'vega.mentions.openResourceLabel': 'Open {resourceType}',
  'vega.mentions.resourcesLabel': 'Resources',
  'vega.modePicker.agent': 'Agent',
  'vega.modePicker.agentDescription':
    'Agent can create dashboards, experiments, and graphs from your data, and (with the GitHub app) author pull requests',
  'vega.modePicker.agentDescriptionNoGithub':
    'Agent can create dashboards, experiments, and graphs from your data, and (with the GitHub app) author pull requests. GitHub app required for PR-writing flows.',
  'vega.modePicker.readOnly': 'Read-only',
  'vega.modePicker.readOnlyDescription': "Read only mode that doesn't affect your code",
  'vega.modePicker.selectAiMode': 'Select AI mode',
  'vega.page.title': 'Vega',
  'vega.pageHeader.conversationCopied': 'Conversation copied to clipboard',
  'vega.pageHeader.copyConversation': 'Copy conversation',
  'vega.pageHeader.copyConversationFailed': 'Failed to copy conversation',
  'vega.pageHeader.copyLinkFailed': 'Failed to copy link',
  'vega.pageHeader.linkCopied': 'Link copied to clipboard',
  'vega.pageHeader.moreOptionsLabel': 'More options',
  'vega.pageHeader.newChat': 'New chat',
  'vega.pageHeader.nothingToCopy': 'Nothing to copy yet',
  'vega.pageHeader.permissionDenied': 'Permission denied',
  'vega.pageHeader.permissionDeniedBody':
    "You don't have permission to use Vega. A workspace admin must grant you access to the <code>talkToVega</code> action on the <code>proj/{projectKey}:vega/*</code> resource. <link>Read more here</link>.",
  'vega.pageHeader.sendFeedback': 'Send feedback',
  'vega.pageHeader.settings': 'Settings',
  'vega.pageHeader.share': 'Share',
  'vega.pageHeader.viewHistory': 'View history',
  'vega.panel.aboutBody':
    '{botName} is a suite of observability AI tools which has been enabled for your organization. {botName} is currently offered to select customers as a Free Trial Service. {botName} can be enabled or disabled in your <link>security settings</link>.',
  'vega.panel.aiAccessProduct': 'Vega chat',
  'vega.panel.closeHistory': 'Close history',
  'vega.panel.closeLabel': 'Close',
  'vega.panel.conversationNotAvailable': 'Conversation not available',
  'vega.panel.copyLinkFailed': 'Failed to copy link',
  'vega.panel.history': 'History',
  'vega.panel.historyTitle': 'Your {botName} history',
  'vega.panel.infoLabel': 'Information about Vega',
  'vega.panel.linkCopied': 'Link copied to clipboard',
  'vega.panel.moreOptionsLabel': 'More options',
  'vega.panel.newChat': 'New chat',
  'vega.panel.notSharedBody':
    'This conversation has not been shared by its owner. The owner can share it using Share in the {botName} panel header.',
  'vega.panel.sendFeedback': 'Send feedback',
  'vega.panel.share': 'Share',
  'vega.panel.testError': 'Test error',
  'vega.permissionDenied.body':
    "You don't have permission to use {botName}. A workspace admin must grant you access to the <code>talkToVega</code> action on the <code>proj/{projectKey}:vega/*</code> resource. <link>Read more here</link>.",
  'vega.permissionDenied.title': 'Permission denied',
  'vega.sidebar.artifacts': 'Artifacts',
  'vega.sidebar.asideLabel': 'Vega navigation',
  'vega.sidebar.history': 'History',
  'vega.sidebar.navLabel': 'Vega',
  'vega.sidebar.newChat': 'New chat',
  'vega.sidebar.recentChats': 'Recent chats',
  'vega.sidebar.settings': 'Settings',
  'vega.sidebar.workflows': 'Workflows',
  'vega.settings.addCustomKeyDescription': 'Add an OpenAI or Anthropic key in integration settings.',
  'vega.settings.addCustomKeyDescriptionWithBedrock':
    'Add an OpenAI, Anthropic, or Amazon Bedrock key in integration settings.',
  'vega.settings.addCustomKeyLabel': 'Add a custom key',
  'vega.settings.automatedCleanupDescription':
    'Vega automatically runs cleanup on flags that are ready for code removal, based on your',
  'vega.settings.automatedCleanupLoadError':
    'Vega could not load automated cleanup settings. Refresh the page and try again.',
  'vega.settings.bedrockDefaultModelDescription':
    'The Amazon Bedrock model ID, inference profile ID, or ARN that runs Vega conversations and investigations.',
  'vega.settings.bedrockDefaultModelLabel': 'Model ID, inference profile ID, or ARN',
  'vega.settings.bedrockExternalIdCopy': 'Copy',
  'vega.settings.bedrockExternalIdDescription':
    "Require this value as the sts:ExternalId condition in your Amazon Bedrock role's trust policy.",
  'vega.settings.bedrockExternalIdLabel': 'External ID',
  'vega.settings.bedrockModelArnInvalid': '{model} is not an Amazon Bedrock ARN in the aws partition with a region.',
  'vega.settings.bedrockModelInvalid': '{model} is not a valid Amazon Bedrock model ID, inference profile ID, or ARN.',
  'vega.settings.bedrockModelRegionUnsupported': 'This ARN names region {region}, which Vega does not support.',
  'vega.settings.bedrockModelRequired': 'Enter a model ID, inference profile ID, or ARN.',
  'vega.settings.bedrockModelTooLong': 'Model IDs can be at most {max} characters.',
  'vega.settings.bedrockSmallModelDescription':
    'Optional. Answers quick requests, such as Ask Vega search queries. Leave blank to use the default model.',
  'vega.settings.bedrockSmallModelLabel': 'Small model ID, inference profile ID, or ARN',
  'vega.settings.byokKeyRemoved':
    'The custom key this project uses is no longer enabled. Choose another key, or turn it back on in your integration settings.',
  'vega.settings.byokNoKey':
    'This account has no custom key. Add an OpenAI or Anthropic key in your integration settings, then switch this project to a custom key.',
  'vega.settings.byokNoKeyWithBedrock':
    'This account has no custom key. Add an OpenAI, Anthropic, or Amazon Bedrock key in your integration settings, then switch this project to a custom key.',
  'vega.settings.byokProviderNoKey':
    'This account has no {provider} key. Choose another key, or add one in your integration settings.',
  'vega.settings.customKeyDetailsDescription':
    'The provider of the custom key this project uses. To change the key, edit it in your integration settings.',
  'vega.settings.customKeyNameLabel': 'Custom key: {name} ({provider})',
  'vega.settings.customKeyProviderLabel': 'Custom key ({provider})',
  'vega.settings.customKeyRemovedLabel': 'Custom key (no longer enabled)',
  'vega.settings.defaultModelDescription': 'Runs Vega conversations and investigations.',
  'vega.settings.defaultModelLabel': 'Default model',
  'vega.settings.description':
    'Configure how Vega manages pull requests, runs inference, and cleans up flags for this project.',
  'vega.settings.editCustomKey': 'Edit',
  'vega.settings.editCustomKeyLabel': 'Edit the custom key in integration settings',
  'vega.settings.flagLifecycleSettingsLink': 'lifecycle settings',
  'vega.settings.heading': 'Vega settings',
  'vega.settings.inferenceConfigurationHeading': 'Inference configuration',
  'vega.settings.inferenceKeyDescription':
    "An inference key is the AI provider account Vega runs its models on. Use LaunchDarkly's managed key, or a custom OpenAI or Anthropic key to choose the models yourself.",
  'vega.settings.inferenceKeyDescriptionWithBedrock':
    "An inference key is the AI provider account Vega runs its models on. Use LaunchDarkly's managed key, or a custom OpenAI, Anthropic, or Amazon Bedrock key to choose the models yourself.",
  'vega.settings.inferenceKeyLabel': 'Inference key',
  'vega.settings.inferenceLoading': 'Loading inference configuration…',
  'vega.settings.inferenceProviderNone': 'No key configured',
  'vega.settings.inferenceSaveFailed': 'Vega could not save the inference configuration. Try again.',
  'vega.settings.keyDetailsLabel': 'Key details',
  'vega.settings.managedKeyDetailsDescription':
    "LaunchDarkly manages this key and its models, so they can't be changed.",
  'vega.settings.managedKeyLabel': 'LaunchDarkly managed key',
  'vega.settings.modelNotSupported': 'Not supported',
  'vega.settings.modelNotSupportedDescription': "This {provider} key doesn't have access to this model.",
  'vega.settings.modelsLoadError': 'Vega could not load models. Refresh the page and try again.',
  'vega.settings.modelsUnavailable':
    'Vega could not list the models this key can use. Check the permissions for this key in {provider}.',
  'vega.settings.navigationLabel': 'Vega settings',
  'vega.settings.pullRequestDetailsHeading': 'Pull request details',
  'vega.settings.pullRequestDetailsLoadError':
    'Vega could not load pull request details. Refresh the page and try again.',
  'vega.settings.pullRequestDetailsLoading': 'Loading pull request details…',
  'vega.settings.pullRequestBotIdentityAutomatedAlertRemediations': 'Automated alert remediation',
  'vega.settings.pullRequestBotIdentityAutomatedFlagCleanups': 'Automated flag cleanup',
  'vega.settings.pullRequestBotIdentityDescription':
    'By default, pull requests are opened by the person who started the conversation or configured the automation. Select which workflows open pull requests as LaunchDarkly instead.',
  'vega.settings.pullRequestBotIdentityLabel': 'Pull request author',
  'vega.settings.pullRequestBotIdentityVegaConversations': 'Vega conversations & manual flag cleanups',
  'vega.settings.pullRequestPrefixDescription': 'Vega adds this prefix to pull request titles.',
  'vega.settings.pullRequestPrefixLabel': 'Pull request title prefix',
  'vega.settings.save': 'Save',
  'vega.settings.saveFailed': 'Vega settings could not be saved. Try again.',
  'vega.settings.saved': 'Vega settings saved',
  'vega.settings.saving': 'Saving…',
  'vega.settings.smallModelDescription': 'Answers quick requests, such as Ask Vega search queries.',
  'vega.settings.smallModelLabel': 'Small model',
  'vega.settings.suggestedModel': '{model} (suggested)',
  'vega.slackSettings.agent': 'Agent',
  'vega.slackSettings.defaultAgentMode': 'Default agent mode',
  'vega.slackSettings.defaultAgentModeHint': 'Select default AI mode for Slack',
  'vega.slackSettings.defaultProject': 'Default project',
  'vega.slackSettings.defaultProjectHint': 'Select which project {botName} should use for ad-hoc Slack conversations.',
  'vega.slackSettings.defaultRepositoryAccess': 'Default repository access',
  'vega.slackSettings.defaultRepositoryAccessHint':
    'Select which repositories {botName} can access during Slack conversations.',
  'vega.slackSettings.manageRepositories': 'Manage repositories',
  'vega.slackSettings.readOnly': 'Read-only',
  'vega.slackSettings.repoCloneNotice':
    'Code in selected repositories may be cloned in an isolated environment as part of investigations. <link>Learn more</link>',
  'vega.slackSettings.saveFailed': 'Failed to save configuration',
  'vega.slackSettings.saved': 'Slack configuration saved',
  'vega.slackSettings.subtitle': 'Manage your {botName} slack defaults and preferences',
  'vega.slackSettings.title': 'Slack Defaults for {botName}',
  'vega.status.loading': 'Loading…',
  'vega.workflows.alertLabel': 'Alert',
  'vega.workflows.alertPlaceholder': 'Choose an alert',
  'vega.workflows.alertRequired': 'Choose the alert that starts this workflow.',
  'vega.workflows.backToWorkflows': 'Workflows',
  'vega.workflows.cancel': 'Cancel',
  'vega.workflows.columnEnabled': 'On',
  'vega.workflows.columnMode': 'Mode',
  'vega.workflows.columnName': 'Name',
  'vega.workflows.columnTrigger': 'Starts',
  'vega.workflows.connectGitHub': 'Connect GitHub',
  'vega.workflows.createWorkflow': 'Create workflow',
  'vega.workflows.dayOfMonthInvalid': 'Choose a day from 1 to 28, or the last day.',
  'vega.workflows.dayOfMonthLabel': 'Day of month',
  'vega.workflows.dayOfMonthLast': 'Last day',
  'vega.workflows.description': 'Work Vega starts on its own for this project. Each run is a Vega conversation.',
  'vega.workflows.editTitle': 'Edit workflow',
  'vega.workflows.emptyBody': 'A workflow tells Vega when to start and what to do.',
  'vega.workflows.emptyHeading': 'No workflows yet',
  'vega.workflows.enabledDescription':
    'Turning a workflow off stops future runs. Past conversations stay where they are.',
  'vega.workflows.enabledLabel': 'On',
  'vega.workflows.instructionDescription': 'Optional. Vega reads this when the workflow starts.',
  'vega.workflows.instructionLabel': 'Instructions for Vega',
  'vega.workflows.joinedIntoAnotherRun': 'Joined into another run',
  'vega.workflows.joinedIntoRun': 'Joined into the {when} run',
  'vega.workflows.loadError': 'Failed to load workflows',
  'vega.workflows.modeAgent': 'Agent',
  'vega.workflows.modeAgentDescription': 'Vega may open a GitHub pull request.',
  'vega.workflows.modeLabel': 'Mode',
  'vega.workflows.modeRead': 'Read-only',
  'vega.workflows.modeReadDescription':
    'Vega investigates and writes up what it finds. It does not open a pull request.',
  'vega.workflows.nameLabel': 'Name',
  'vega.workflows.namePlaceholder': 'For example, Investigate checkout errors',
  'vega.workflows.nameRequired': 'Enter a name.',
  'vega.workflows.newTitle': 'Create workflow',
  'vega.workflows.nextRun': 'Next: {when}',
  'vega.workflows.noAlerts': 'This project has no alerts yet.',
  'vega.workflows.notFound': 'Workflow not found',
  'vega.workflows.openConversation': 'Open conversation',
  'vega.workflows.outcomeCoalesced': 'Joined',
  'vega.workflows.outcomeFailed': 'Failed',
  'vega.workflows.outcomePending': 'Pending',
  'vega.workflows.outcomeSkipped': 'Skipped',
  'vega.workflows.outcomeStarted': 'Started',
  'vega.workflows.outcomeUnknown': 'Unknown',
  'vega.workflows.previewNote': 'Workflows saved here do not start runs yet.',
  'vega.workflows.recentRunsEmpty': 'No runs yet.',
  'vega.workflows.recentRunsHeading': 'Recent runs',
  'vega.workflows.recentRunsLoadError': 'Failed to load recent runs',
  'vega.workflows.recurrenceLabel': 'Recurrence',
  'vega.workflows.repositoriesAgentRequired': 'Agent mode needs at least one repository.',
  'vega.workflows.repositoriesDescription':
    'Repositories Vega may read, and open pull requests in when it runs as Agent.',
  'vega.workflows.repositoriesLabel': 'Repositories',
  'vega.workflows.runNow': 'Run now',
  'vega.workflows.runNowCoalesced': 'Run folded into one already going',
  'vega.workflows.runNowConfirm': 'Run as Agent',
  'vega.workflows.runNowConfirmBody': 'Vega may open a GitHub pull request.',
  'vega.workflows.runNowConfirmTitle': 'Run this workflow as Agent?',
  'vega.workflows.runNowError': 'Could not start the run',
  'vega.workflows.runNowFailed': 'Run failed',
  'vega.workflows.runNowJoined': 'Joined a run already in progress',
  'vega.workflows.runNowSkipped': 'Run skipped',
  'vega.workflows.runNowStarted': 'Run started',
  'vega.workflows.runStatusCompleted': 'Completed',
  'vega.workflows.runStatusFailed': 'Failed',
  'vega.workflows.runStatusRunning': 'Running',
  'vega.workflows.runStatusStopped': 'Stopped',
  'vega.workflows.runStatusUnknown': 'Unknown',
  'vega.workflows.runsColumnDetails': 'Details',
  'vega.workflows.runsColumnOutcome': 'Status',
  'vega.workflows.runsColumnSource': 'Source',
  'vega.workflows.runsColumnTime': 'Time',
  'vega.workflows.save': 'Save',
  'vega.workflows.saveError': 'Failed to save workflow: {error}',
  'vega.workflows.savedRepositories': 'Saved: {repositories}',
  'vega.workflows.scheduleDaily': 'Every day',
  'vega.workflows.scheduleEvery10Minutes': 'Every 10 minutes',
  'vega.workflows.scheduleHourly': 'Every hour',
  'vega.workflows.scheduleMonthly': 'Every month',
  'vega.workflows.scheduleUnknown': 'On a schedule',
  'vega.workflows.scheduleWeekly': 'Every week',
  'vega.workflows.skipReason': '{reason}',
  'vega.workflows.sourceAlert': 'Alert',
  'vega.workflows.sourceManual': 'Manual',
  'vega.workflows.sourceSchedule': 'Schedule',
  'vega.workflows.sourceUnknown': 'Unknown',
  'vega.workflows.startSchedule': 'Schedule',
  'vega.workflows.startTrigger': 'Trigger',
  'vega.workflows.startsWhenLabel': 'Starts when',
  'vega.workflows.timeLabel': 'Time',
  'vega.workflows.timeRequired': 'Enter a time.',
  'vega.workflows.timezoneInvalid': 'Choose a valid timezone.',
  'vega.workflows.timezoneLabel': 'Timezone',
  'vega.workflows.title': 'Workflows',
  'vega.workflows.toggleError': 'Failed to update the workflow',
  'vega.workflows.toggleLabel': 'Turn {name} on or off',
  'vega.workflows.triggerAlert': 'When the {alertName} alert fires',
  'vega.workflows.triggerAlertUnknown': 'When an alert fires',
  'vega.workflows.triggerTypeAlertFired': 'Alert fired',
  'vega.workflows.triggerTypeLabel': 'Trigger',
  'vega.workflows.triggerUnknown': 'When a trigger fires',
  'vega.workflows.untitledAlert': 'Untitled alert',
  'vega.workflows.weekdayFriday': 'Friday',
  'vega.workflows.weekdayMonday': 'Monday',
  'vega.workflows.weekdaySaturday': 'Saturday',
  'vega.workflows.weekdaySunday': 'Sunday',
  'vega.workflows.weekdayThursday': 'Thursday',
  'vega.workflows.weekdayTuesday': 'Tuesday',
  'vega.workflows.weekdayWednesday': 'Wednesday',
  'vega.workflows.weekdaysLabel': 'Days',
  'vega.workflows.weekdaysRequired': 'Choose at least one day.',
  'views.addResources.cancel': 'Cancel',
  'views.addResources.clear': 'Clear',
  'views.addResources.closeLabel': 'close modal',
  'views.addResources.criticalEnvironment': 'Critical environment',
  'views.addResources.environment': 'Environment',
  'views.addResources.environmentId': 'Environment ID: {id}',
  'views.addResources.linkButton': 'Link {resources}',
  'views.addResources.loadingLabel': 'Loading',
  'views.addResources.loadingResourcesLabel': 'Loading {resourceType}',
  'views.addResources.noResults': 'No results',
  'views.addResources.searchLabel': 'Search environments',
  'views.addResources.searchPlaceholder': 'Search…',
  'views.addResources.searchResourcesLabel': 'search {resourceType}',
  'views.addResources.title': 'Link {resourceType} to {viewName}',
  'views.applicationFlagsFilterBar.searchLabel': 'Search flags',
  'views.applicationFlagsFilterBar.searchPlaceholder': 'Search…',
  'views.applicationSettings.applicationLinkedToast': 'Application linked',
  'views.applicationSettings.applicationRemovedToast': 'Application removed',
  'views.applicationSettings.autoLinkDescription':
    'Links flags as they are evaluated by the application. Never unlinks automatically.',
  'views.applicationSettings.autoLinkDisabledToast': 'Automatic linking disabled',
  'views.applicationSettings.autoLinkEnabledToast': 'Automatic linking enabled',
  'views.applicationSettings.autoLinkError': 'Failed to update automatic linking. Try again later.',
  'views.applicationSettings.autoLinkTitle': 'Auto-link evaluated flags',
  'views.applicationSettings.description':
    'Connect the application that this view serves to see and link the flags its SDK evaluates',
  'views.applicationSettings.linkError': 'Failed to link the application. Try again later.',
  'views.applicationSettings.loadingLabel': 'Loading application',
  'views.applicationSettings.removeConfirmationBody':
    'The flags you linked stay linked. Removing the application only stops LaunchDarkly from showing which flags it evaluates.',
  'views.applicationSettings.removeConfirmationConfirm': 'Remove application',
  'views.applicationSettings.removeConfirmationTitle': 'Remove application',
  'views.applicationSettings.removeError': 'Failed to remove the application. Try again later.',
  'views.applicationSettings.statCardAllLinked': 'This view has every flag evaluated by the application.',
  'views.applicationSettings.statCardCountsLoadingLabel': 'Loading flag counts',
  'views.applicationSettings.statCardEvaluatedDescription':
    'Flags evaluated by {applicationKey} in the last {days} days',
  'views.applicationSettings.statCardEvaluatedLabel': 'Evaluated flags',
  'views.applicationSettings.statCardMissingFromView': '{count} missing from view',
  'views.applicationSettings.statCardUnevaluatedDescription':
    'Flags in this view that have not been evaluated by {applicationKey} in the last {days} days',
  'views.applicationSettings.statCardUnevaluatedLabel': 'Unevaluated flags',
  'views.bulkRemove.removeFromView': 'Remove from view',
  'views.connectionStatus.application': 'Connected to <application>{applicationKey}</application>',
  'views.connectionStatus.payloads':
    'Connected to <payloads>{count, plural, one {# SDK key payload} other {# SDK key payloads}}</payloads>',
  'views.connectionStatus.payloadsAndApplication':
    'Connected to <payloads>{count, plural, one {# SDK key payload} other {# SDK key payloads}}</payloads> and <application>{applicationKey}</application>',
  'views.deleteView.body':
    'This will delete the view <strong>{viewName}</strong>. Flags associated with this view will not be deleted.',
  'views.deleteView.cannotBeUndone': 'This action <strong>cannot be undone</strong>.',
  'views.deleteView.deleteThisView': 'Delete this view?',
  'views.evaluatedFlagsDrawer.linkedGroup':
    '{count, plural, one {# flag already linked} other {# flags already linked}}',
  'views.evaluatedFlagsDrawer.loadingLabel': 'Loading evaluated flags',
  'views.evaluatedFlagsDrawer.missingGroup':
    '{count, plural, one {# flag missing from this view} other {# flags missing from this view}}',
  'views.evaluatedFlagsDrawer.shellTitle': 'Evaluated flags',
  'views.evaluatedFlagsDrawer.title': 'Evaluated flags for {applicationKey} application',
  'views.linkEvaluatedFlags.flagCount': '{count, plural, one {# flag} other {# flags}}',
  'views.linkEvaluatedFlags.resolveError': 'Couldn’t load the flags to link. Try again.',
  'views.linkEvaluatedFlags.selectedCount': '{count, plural, one {# flag selected} other {# flags selected}}',
  'views.linkedResourceActions.actionsLabel': 'View actions',
  'views.linkedResourceActions.goToDetail': 'Go to detail page',
  'views.linkedResourceActions.removeResource': 'Remove resource',
  'views.linkedViews.addViews': 'Add views',
  'views.linkedViews.editLabel': 'Edit views',
  'views.linkedViews.failedToLoad': 'Failed to load views',
  'views.linkedViews.somethingWentWrong': 'Something went wrong',
  'views.linkedViews.tryAgain': 'Try again',
  'views.linkedViews.viewsLabel': 'Views',
  'views.missingFlagsAlert.addMissingFlags': '{count, plural, one {Add missing flag} other {Add missing flags}}',
  'views.missingFlagsAlert.message':
    'This view is missing {count, plural, one {# flag} other {# flags}} evaluated by {applicationKey}.',
  'views.payloadActive.couldnTLoad': 'Couldn’t load keys. Try again.',
  'views.payloadActive.listLabel': 'SDK keys using view {viewKey}',
  'views.payloadActive.loadingKeysLabel': 'Loading payload active keys',
  'views.payloadActive.loadingLabel': 'Loading',
  'views.payloadActive.loadingMoreLabel': 'Loading more keys',
  'views.payloadActive.manageKeys': 'Manage keys',
  'views.payloadActive.payloadActive': 'Payload active',
  'views.payloadActive.subtitle':
    'This view is being used to filter payloads for {count, plural, one {# key} other {# keys}}.',
  'views.payloadsSettings.environmentKeysLabel': '{environmentName} SDK keys',
  'views.payloadsSettings.learnMore': 'Learn more about SDK key payloads',
  'views.payloadsSettings.loadingEnvironmentKeysLabel': 'Loading {environmentName} SDK keys',
  'views.payloadsSettings.loadingEnvironmentLabel': 'Loading environment',
  'views.payloadsSettings.loadingMoreEnvironmentKeysLabel': 'Loading more {environmentName} SDK keys',
  'views.payloadsSettings.loadingPayloadsLabel': 'Loading payloads',
  'views.payloadsSettings.manage': 'Manage',
  'views.payloadsSettings.manageSdkKeys': 'Manage SDK keys',
  'views.payloadsSettings.noSdkKeys': 'No SDK keys associated with this view',
  'views.payloadsSettings.payloads': 'Payloads',
  'views.payloadsSettings.reduceSdkKey':
    'Reduce SDK key payload sizes by delivering only the flags and resources in this view.',
  'views.payloadsSettings.sdkKeyPayloads': 'SDK key payloads',
  'views.payloadsSettings.subtitle': 'This view is not currently being used to filter payloads for any SDK keys.',
  'views.resourceList.loadingMore': 'Loading more {resourceType}s',
  'views.resourceList.selectAllMatching': 'Select all {count} matching {resourceType}s',
  'views.status.loading': 'Loading…',
  'views.status.loadingDots': 'Loading…',
  'views.unevaluatedFlagsDrawer.flagCount': '{count, plural, one {# flag} other {# flags}}',
  'views.unevaluatedFlagsDrawer.loadingLabel': 'Loading unevaluated flags',
  'views.unevaluatedFlagsDrawer.title': 'Unevaluated flags for {applicationKey} application',
  'views.unevaluatedFlagsRemove.loadedCount': '{count, plural, one {# loaded flag} other {# loaded flags}}',
  'views.viewActions.actionsLabel': 'View actions',
  'views.viewActions.deleteView': 'Delete view',
  'views.viewActions.edit': 'Edit',
  'views.viewActions.updateLinkedResources': 'Update linked resources',
  'views.viewApplication.cancel': 'Cancel',
  'views.viewApplication.changeApplication': 'Change application',
  'views.viewApplication.evaluatedHeading': 'Flags {applicationKey} evaluates',
  'views.viewApplication.evaluationsAppearHere':
    "Evaluations appear here after the application's SDK starts reporting them.",
  'views.viewApplication.filterLabel': 'Filter flags',
  'views.viewApplication.filterPlaceholder': 'Filter by name or key…',
  'views.viewApplication.flagActionsLabel': 'Flag actions',
  'views.viewApplication.linkApplication': 'Link application',
  'views.viewApplication.linkFlag': 'Link flag',
  'views.viewApplication.linkSelected': 'Link {count, plural, one {# flag} other {# flags}}',
  'views.viewApplication.linkTheApplication':
    'Link the application this view serves. LaunchDarkly then shows which flags that application evaluates, so you can link the ones its SDK needs.',
  'views.viewApplication.loadingFlagsLabel': 'Loading application flags',
  'views.viewApplication.loadingLabel': 'Loading application',
  'views.viewApplication.noEvaluatedMatchDescription': 'No evaluated flag matches this search.',
  'views.viewApplication.noEvaluationsHeading': 'No evaluations recorded yet',
  'views.viewApplication.noEvaluationsInWindow':
    '{applicationKey} has not evaluated any flags in this project in the last {days} days.',
  'views.viewApplication.noMatchHeading': 'No matching flags',
  'views.viewApplication.noUnevaluatedMatchDescription': 'No unevaluated flag matches this search.',
  'views.viewApplication.remove': 'Remove',
  'views.viewApplication.removeApplicationLabel': 'Remove application',
  'views.viewApplication.removeFlag': 'Remove flag',
  'views.viewApplication.staleHeading': 'Flags {applicationKey} has not evaluated',
  'views.viewDetails.configs': 'Configs',
  'views.viewDetails.created': 'Created',
  'views.viewDetails.failedToLoad': 'Failed to load view details',
  'views.viewDetails.flags': 'Flags',
  'views.viewDetails.loadingLabel': 'Loading view details',
  'views.viewDetails.maintainerLabel': 'Maintainer',
  'views.viewDetails.seeViewDetails': 'See view details',
  'views.viewDetails.segments': 'Segments',
  'views.viewDetails.tagsLabel': 'View tags',
  'views.viewForm.addMaintainer': 'Add maintainer',
  'views.viewForm.addTag': 'Add tag',
  'views.viewForm.cancel': 'Cancel',
  'views.viewForm.closeLabel': 'close modal',
  'views.viewForm.createView': 'Create view',
  'views.viewForm.createViewClose': 'Create view & close',
  'views.viewForm.descriptionLabel': 'Description',
  'views.viewForm.keyLabel': 'Key',
  'views.viewForm.nameLabel': 'Name',
  'views.viewForm.noTags': 'No tags',
  'views.viewForm.saveChanges': 'Save changes',
  'views.viewForm.tagPlaceholder': 'Enter tag',
  'views.viewForm.tagsLabel': 'Tags',
  'views.viewForm.theViewWill': 'The view will include all filters that are currently defined.',
  'views.viewSettings.addTag': 'Add tag',
  'views.viewSettings.cancel': 'Cancel',
  'views.viewSettings.copyKey': 'Copy key',
  'views.viewSettings.created': 'Created {date}',
  'views.viewSettings.dangerZone': 'Danger zone',
  'views.viewSettings.dateCreatedLabel': 'Date created',
  'views.viewSettings.delete': 'Delete',
  'views.viewSettings.deleteConfirmationConfirm': 'Delete',
  'views.viewSettings.deleteConfirmationTitle': 'Delete {viewName}',
  'views.viewSettings.deleteView': 'Delete view',
  'views.viewSettings.deleteViewError': 'Failed to delete view. Try again later.',
  'views.viewSettings.descriptionLabel': 'Description',
  'views.viewSettings.descriptionPlaceholder': 'Enter a description',
  'views.viewSettings.edit': 'Edit',
  'views.viewSettings.general': 'General',
  'views.viewSettings.keyCopiedToast': 'Copied {viewKey}',
  'views.viewSettings.keyDescription': 'Used for identifying this view for the LaunchDarkly API',
  'views.viewSettings.keyLabel': 'View key',
  'views.viewSettings.keyTitle': 'Key',
  'views.viewSettings.loadingPayloadsLabel': 'Loading payloads',
  'views.viewSettings.maintainerDescription': 'The member or team responsible for this view.',
  'views.viewSettings.maintainerLabel': 'Maintainer',
  'views.viewSettings.nameAndDescriptionDescription': 'Used to identify and discover this view in the LaunchDarkly UI',
  'views.viewSettings.nameAndDescriptionTitle': 'Name and description',
  'views.viewSettings.nameLabel': 'Name',
  'views.viewSettings.save': 'Save',
  'views.viewSettings.saveChanges': 'Save changes',
  'views.viewSettings.tagsDescription': 'Used to help discover views in searches',
  'views.viewSettings.tagsLabel': 'Tags',
  'views.viewSettings.tagsTitle': 'Tags',
  'views.viewSettings.thisViewWill':
    'This view will be deleted permanently from your LaunchDarkly account. Deleting a view does not delete the resources linked to the view.',
  'views.viewSettings.updateViewError': 'Failed to update view. Try again later.',
  'views.viewSettings.viewDeletedToast': 'View deleted',
  'views.viewSettings.viewUpdatedToast': 'View updated',
  'views.viewsDialog.clear': 'Clear',
  'views.viewsDialog.clearSelection': 'Clear selection',
  'views.viewsDialog.create': 'Create',
  'views.viewsDialog.createView': 'Create view',
  'views.viewsDialog.expandDetails': 'Expand details',
  'views.viewsDialog.noViews': 'No views',
  'views.viewsDialog.noViewsFound': 'No views found',
  'views.viewsDialog.searchLabel': 'Search views',
  'views.viewsDialog.selectLabel': 'Select views',
  'views.viewsDialog.useViewsTo': 'Use views to group flags and segments.',
  'views.viewsDialog.viewsLabel': 'Views',
  'views.viewsList.listLabel': 'views list',
  'views.viewsList.noViewsFound': 'No views found',
  'views.viewsListFilters.all': 'All',
  'views.viewsListFilters.clear': 'Clear',
  'views.viewsListFilters.configs': 'Configs',
  'views.viewsListFilters.filterViewsBy': 'Filter views by…',
  'views.viewsListFilters.filters': 'Filters',
  'views.viewsListFilters.flags': 'Flags',
  'views.viewsListFilters.metrics': 'Metrics',
  'views.viewsListFilters.resourceTypeLabel': 'Resource type',
  'views.viewsListFilters.segments': 'Segments',
  'views.viewsSelect.selectedLabel': 'Selected {count, plural, one {view} other {views}}',
  'vital.accountSearch.help': 'Search by account name, member email, or account, project, environment, or member ID.',
  'vital.accountSearch.legacyHelp': 'Search by account name, member email, or account ID.',
  'vital.accountSearch.loadError': 'Account search failed. Try again.',
  'vital.accountSearch.loading': 'Searching accounts…',
  'vital.accountSearch.minimumInput': 'Enter at least 2 characters to search.',
  'vital.accountSearch.retry': 'Retry',
  'vital.accountSearch.trigger': 'Search accounts…',
  'vital.accountSearch.unauthorized':
    'Your session has expired or you do not have access to account search. Sign in again or request access.',
  'vital.trialHistory.accountId': 'Account ID',
  'vital.trialHistory.activated': 'Activated',
  'vital.trialHistory.allOperators': 'All operators',
  'vital.trialHistory.allStatuses': 'All statuses',
  'vital.trialHistory.clearFilters': 'Clear filters',
  'vital.trialHistory.copied': 'Account ID copied',
  'vital.trialHistory.copyAccountId': 'Copy account ID {accountId}',
  'vital.trialHistory.description':
    'Invitations created in {environment}. Awaiting activation does not confirm email delivery. Dates use UTC.',
  'vital.trialHistory.empty': 'No invitations match this date range and filters. Try a wider range.',
  'vital.trialHistory.expired': 'Expired',
  'vital.trialHistory.expiresAt': 'Expires',
  'vital.trialHistory.firstPage': 'First page',
  'vital.trialHistory.heading': 'Recent trial invitations',
  'vital.trialHistory.invalidFilters': 'Choose valid filters and a request-date range of up to 366 days.',
  'vital.trialHistory.lastDays': 'Last {days} days',
  'vital.trialHistory.loadError': 'Could not load invitations. Your invitation form is still available.',
  'vital.trialHistory.loading': 'Loading invitations',
  'vital.trialHistory.mine': 'My requests',
  'vital.trialHistory.nextMonth': 'Next month',
  'vital.trialHistory.nextPage': 'Next',
  'vital.trialHistory.notApplicable': '—',
  'vital.trialHistory.openAccount': 'Open account {accountId}',
  'vital.trialHistory.pageCount': '{count, plural, one {# invitation on this page} other {# invitations on this page}}',
  'vital.trialHistory.pending': 'Awaiting activation',
  'vital.trialHistory.previousMonth': 'Previous month',
  'vital.trialHistory.previousPage': 'Previous',
  'vital.trialHistory.refresh': 'Refresh',
  'vital.trialHistory.refreshError': 'Could not refresh invitations. These results may be out of date.',
  'vital.trialHistory.requestDates': 'Request dates (UTC)',
  'vital.trialHistory.requestedAt': 'Requested',
  'vital.trialHistory.requestedBy': 'Requested by',
  'vital.trialHistory.retry': 'Retry',
  'vital.trialHistory.search': 'Search by email, organization, or account ID',
  'vital.trialHistory.searchTooLong': 'Search text is too long. Shorten it and try again.',
  'vital.trialHistory.status': 'Status',
  'vital.trialHistory.updated': 'Updated {time}',
  'vital.trialInvitations.accountId': 'Reserved LaunchDarkly account ID:',
  'vital.trialInvitations.accountOwner': 'Account owner',
  'vital.trialInvitations.another': 'Send another invitation',
  'vital.trialInvitations.catamorphic': 'Catamorphic',
  'vital.trialInvitations.commercialProduction': 'Production',
  'vital.trialInvitations.commercialStaging': 'Staging',
  'vital.trialInvitations.copy': 'Copy account ID',
  'vital.trialInvitations.created': 'Invitation created for {email}',
  'vital.trialInvitations.description':
    'Invite a customer to create a trial account in the selected environment. They become the account owner when they accept the invitation.',
  'vital.trialInvitations.duration':
    'The trial uses the selected environment’s default duration. It starts when the customer activates the account.',
  'vital.trialInvitations.email': 'Email',
  'vital.trialInvitations.emailHint': 'The invitation goes to this address.',
  'vital.trialInvitations.emailInvalid': 'Enter an email address in the format name@company.com.',
  'vital.trialInvitations.environment': 'Environment',
  'vital.trialInvitations.error': 'Could not create the invitation. Check the error below before trying again.',
  'vital.trialInvitations.euProduction': 'EU Production',
  'vital.trialInvitations.exampleEmail': 'name@company.com',
  'vital.trialInvitations.exampleFirstName': 'Jordan',
  'vital.trialInvitations.exampleLastName': 'Lee',
  'vital.trialInvitations.exampleOrganization': 'Acme Inc.',
  'vital.trialInvitations.federalProduction': 'Federal Production',
  'vital.trialInvitations.federalStaging': 'Federal Staging',
  'vital.trialInvitations.firstName': 'First name',
  'vital.trialInvitations.firstNameRequired': 'Enter a first name.',
  'vital.trialInvitations.lastName': 'Last name',
  'vital.trialInvitations.lastNameRequired': 'Enter a last name.',
  'vital.trialInvitations.local': 'Local',
  'vital.trialInvitations.organization': 'Organization',
  'vital.trialInvitations.organizationRequired': 'Enter an organization name.',
  'vital.trialInvitations.pending':
    'The customer must receive the invitation email and set a password to activate the account. This page does not confirm email delivery.',
  'vital.trialInvitations.required': 'Enter a value.',
  'vital.trialInvitations.requiredFields': 'All fields are required.',
  'vital.trialInvitations.salesforce':
    'Salesforce customer-account linking is a separate step. This action does not update Salesforce.',
  'vital.trialInvitations.send': 'Send trial invitation',
  'vital.trialInvitations.sending': 'Sending invitation…',
  'vital.trialInvitations.targetEnvironment': 'Target environment',
  'vital.trialInvitations.title': 'Trial invitations',
  'vital.trialInvitations.trialLength': 'Trial length',
  'vital.trialInvitations.uncertain':
    'The request may have created an invitation. Do not resend: this can create another account ID. Ask Billing engineering to check the service logs for this recipient and environment.',
  'workflows.activeWorkflows.completeStage': 'Complete',
  'workflows.activeWorkflows.maintainerLabel': 'Maintainer:',
  'workflows.activeWorkflows.stageFallback': 'Stage {index}',
  'workflows.approvalStep.clearReviewerTooltip': 'Clear reviewer',
  'workflows.approvalStep.descriptionLabel': 'Description:',
  'workflows.approvalStep.descriptionRequired': 'Description cannot be blank.',
  'workflows.approvalStep.integrationFieldRequired': '{fieldName} cannot be blank.',
  'workflows.approvalStep.minimumApprovals':
    'Minimum {count} {count, plural, one {approval} other {approvals}} required to apply these changes',
  'workflows.approvalStep.notifyLabel': 'People to notify:',
  'workflows.approvalStep.requiredHint': 'Required',
  'workflows.approvalStep.reviewersLabel': 'Reviewers:',
  'workflows.approvalStep.reviewersPlaceholder': 'Add reviewers',
  'workflows.approvalStep.reviewersRequired': 'Reviewers cannot be blank.',
  'workflows.approvalStep.roleCannotReview': 'Members with the {role} role cannot review flag approval requests.',
  'workflows.approvalStep.selectReviewersLabel': 'Select reviewers',
  'workflows.approvalStep.viewApprovalRequest': 'View approval request',
  'workflows.builder.addBlockLabel': 'Add block',
  'workflows.builder.deleteStep': 'Delete step',
  'workflows.builder.editLabel': 'Edit',
  'workflows.builder.emptyTemplateFields': 'Fill out the highlighted fields to finish building your workflow',
  'workflows.builder.pendingStepOptionsLabel': 'step in progress options',
  'workflows.builder.stageNeedsAction': 'All stages must include an action.',
  'workflows.builder.stageNeedsCondition': 'All stages must include a condition.',
  'workflows.builder.stepOptionsLabel': 'step {number} options',
  'workflows.builderHeader.cancel': 'Cancel',
  'workflows.builderHeader.createFailed': 'Failed to create workflow. Try again.',
  'workflows.builderHeader.createTemplateRestrictedTooltip': 'You do not have permissions to create templates',
  'workflows.builderHeader.deleteWorkflow': 'Delete workflow',
  'workflows.builderHeader.descriptionPlaceholder': 'Add a description',
  'workflows.builderHeader.incompleteFields':
    'This workflow is incomplete. Some conditions or flag changes are missing required values.',
  'workflows.builderHeader.incompleteStages':
    'This workflow is incomplete. Each stage must have a condition followed by flag changes to start it.',
  'workflows.builderHeader.namePlaceholder': 'Add a workflow name',
  'workflows.builderHeader.nameRequired': 'A name for this workflow is required.',
  'workflows.builderHeader.saveAsTemplate': 'Save as template',
  'workflows.builderHeader.startWorkflow': 'Start workflow',
  'workflows.builderHeader.templatesComingSoonTooltip': 'Workflow templates are coming soon',
  'workflows.completedWorkflows.completedColumn': 'Completed',
  'workflows.completedWorkflows.daysAgo': '{count} days ago',
  'workflows.completedWorkflows.descriptionColumn': 'Description',
  'workflows.completedWorkflows.hoursAgo': '{count} hours ago',
  'workflows.completedWorkflows.maintainerColumn': 'Maintainer',
  'workflows.completedWorkflows.minutesAgo': '{count} minutes ago',
  'workflows.completedWorkflows.nameColumn': 'Name',
  'workflows.defaultVariationStep.label': 'Set default variation to serve',
  'workflows.defaultVariationStep.variationRequired': 'Variation cannot be blank.',
  'workflows.deleteDialog.body':
    'Deleting this workflow will stop remaining changes from completing. Changes that have been completed will be retained.',
  'workflows.deleteDialog.title': 'Delete workflow',
  'workflows.deleteTemplateDialog.body': 'This will permanently delete the workflow template <strong>{name}</strong>',
  'workflows.deleteTemplateDialog.cancel': 'Cancel',
  'workflows.deleteTemplateDialog.closeLabel': 'close',
  'workflows.deleteTemplateDialog.delete': 'Delete',
  'workflows.deleteTemplateDialog.deleteFailedToast': 'Failed to delete workflow template',
  'workflows.deleteTemplateDialog.deletedToast': 'Workflow template "{name}" deleted',
  'workflows.deleteTemplateDialog.title': 'Delete workflow template?',
  'workflows.emptyState.coordinateLaunches': 'Coordinate launches across all teams on a single, manageable timeline',
  'workflows.emptyState.createHeading': 'Create a workflow',
  'workflows.emptyState.heading': 'Automate your release process with workflows',
  'workflows.emptyState.reusableProcesses': 'Build reusable, multi-stage release processes',
  'workflows.emptyState.standardizeReleases':
    'Drive efficiency with less risk by using templates to standardize safe releases',
  'workflows.maintenanceWindowDialog.back': 'Back',
  'workflows.maintenanceWindowDialog.description': 'Turn a flag on or off for a specific period.',
  'workflows.maintenanceWindowDialog.durationLabel': 'Duration:',
  'workflows.maintenanceWindowDialog.learnMore': 'Learn more about maintenance windows',
  'workflows.maintenanceWindowDialog.reviewWorkflow': 'Review workflow',
  'workflows.maintenanceWindowDialog.startDateLabel': 'Start workflow on:',
  'workflows.maintenanceWindowDialog.targetingLabel': 'Set targeting to:',
  'workflows.maintenanceWindowDialog.targetingSwitchLabel': 'flag targeting',
  'workflows.maintenanceWindowDialog.title': 'Configure a maintenance window',
  'workflows.nameDialog.cancel': 'Cancel',
  'workflows.nameDialog.descriptionLabel': 'Description',
  'workflows.nameDialog.descriptionPlaceholder': 'Add a description for your workflow',
  'workflows.nameDialog.nameLabel': 'Name',
  'workflows.nameDialog.namePlaceholder': 'Add a name for your workflow',
  'workflows.nameDialog.next': 'Next',
  'workflows.nameDialog.title': 'Name the workflow',
  'workflows.notFound.body': 'The workflow for <flag>{flagName}</flag> on <environment></environment> was not found.',
  'workflows.notFound.title': 'Custom Workflow not found',
  'workflows.notFound.viewExistingWorkflows': 'View existing workflows',
  'workflows.notifications.created': 'Workflow "{name}" created',
  'workflows.notifications.deleted': 'Workflow deleted',
  'workflows.notifications.templateCreated': 'Workflow template "{name}" created',
  'workflows.options.createTemplate': 'Create template',
  'workflows.options.customBuild': 'Custom build',
  'workflows.options.existingTemplate': 'Existing template',
  'workflows.options.maintenanceWindow': 'Maintenance window',
  'workflows.options.maintenanceWindowExperimentTooltip':
    'A maintenance window may not be performed on a flag with an experiment.',
  'workflows.options.nudgeBody': "Run a progressive rollout directly on a flag's targeting rule.",
  'workflows.options.nudgeHeading': 'New feature',
  'workflows.options.nudgeLearnMore': 'Learn more',
  'workflows.options.progressiveRollout': 'Progressive rollout',
  'workflows.options.progressiveRolloutExperimentTooltip':
    'A progressive rollout may not be performed on a flag with an experiment',
  'workflows.options.progressiveRolloutTooltip': 'Progressive rollouts are now available on the Targeting page',
  'workflows.progressiveRolloutDialog.back': 'Back',
  'workflows.progressiveRolloutDialog.description':
    "Tell us the roll out increments and frequency, and we'll do the rest.",
  'workflows.progressiveRolloutDialog.frequencyLabel': 'Frequency',
  'workflows.progressiveRolloutDialog.frequencyTooltip':
    'How often to increase the percentage of the default variation.',
  'workflows.progressiveRolloutDialog.incrementsLabel': 'Increments',
  'workflows.progressiveRolloutDialog.incrementsTooltip':
    'Each step of the workflow will increase the rollout percentage by this amount, starting from 0%. Note: this will overwrite any previously-defined rollouts for the default variation.',
  'workflows.progressiveRolloutDialog.learnMore': 'Learn more about progressive rollouts',
  'workflows.progressiveRolloutDialog.periodDay': 'day',
  'workflows.progressiveRolloutDialog.periodDays': '{count} days',
  'workflows.progressiveRolloutDialog.reviewWorkflow': 'Review workflow',
  'workflows.progressiveRolloutDialog.startDateLabel': 'Start workflow on',
  'workflows.progressiveRolloutDialog.title': 'Configure progressive rollout',
  'workflows.progressiveRolloutDialog.variationLabel': 'Roll out variation',
  'workflows.rolloutStep.defaultRule': 'Default rule',
  'workflows.rolloutStep.experimentRuleDisabled': '- Cannot modify rules with experiment allocations',
  'workflows.rolloutStep.ruleAlreadySet': '- This rule has already been set for this stage',
  'workflows.rolloutStep.setRolloutOf': 'Set rollout of',
  'workflows.rolloutStep.to': 'to',
  'workflows.rolloutStep.totalError': 'The rollout percentages must add to 100%.',
  'workflows.rolloutStep.weightLabel': 'Boolean flag rollout weight',
  'workflows.ruleStep.incomplete': 'Rule is not complete.',
  'workflows.ruleStep.invalid': 'Rule configuration is invalid',
  'workflows.saveTemplateDialog.cancel': 'Cancel',
  'workflows.saveTemplateDialog.description': 'Save this workflow as a template to use on other flags.',
  'workflows.saveTemplateDialog.descriptionLabel': 'Description',
  'workflows.saveTemplateDialog.keyHint':
    'Keys must only contain letters, numbers, <code>.</code>, <code>_</code> or <code>-</code>.',
  'workflows.saveTemplateDialog.keyHintReserved': 'You cannot use <code>new</code> as a key.',
  'workflows.saveTemplateDialog.keyLabel': 'Key',
  'workflows.saveTemplateDialog.learnMore': 'Learn more.',
  'workflows.saveTemplateDialog.nameLabel': 'Name',
  'workflows.saveTemplateDialog.next': 'Next',
  'workflows.saveTemplateDialog.save': 'Save',
  'workflows.saveTemplateDialog.title': 'Save template',
  'workflows.scheduleStep.dateRequired': 'Date cannot be empty',
  'workflows.scheduleStep.description': 'Select when this workflow stage should begin after the previous stage.',
  'workflows.scheduleStep.waitDurationRequired': 'Wait duration cannot be empty',
  'workflows.stagePopover.approval': 'Approval',
  'workflows.stagePopover.approvalDeclined':
    'The approval request was declined by at least one reviewer. The workflow will not progress until a reviewer approves and applies the request.',
  'workflows.stagePopover.approvalFailed': 'The workflow has failed. The workflow will not progress past this stage.',
  'workflows.stagePopover.daysLater': '{count} {count, plural, one {day} other {days}} later',
  'workflows.stagePopover.flagChanges': 'Flag changes',
  'workflows.stagePopover.hoursLater': '{count} {count, plural, one {hour} other {hours}} later',
  'workflows.stagePopover.integrationApprovalDeclined':
    'The approval request was declined. The workflow will not progress past this stage.',
  'workflows.stagePopover.minutesLater': '{count} {count, plural, one {minute} other {minutes}} later',
  'workflows.stagePopover.schedule': 'Schedule',
  'workflows.stagePopover.scheduledOn': 'On <time>{date}</time>',
  'workflows.stagePopover.weeksLater': '{count} {count, plural, one {week} other {weeks}} later',
  'workflows.status.loading': 'Loading…',
  'workflows.stepHeader.approvalRequest': 'Approval Request',
  'workflows.stepHeader.approvalRequestHint':
    'Approval request steps are based on the approval settings for the selected environment.',
  'workflows.stepHeader.individualTargets': 'Individual Targets',
  'workflows.stepHeader.schedule': 'Schedule',
  'workflows.stepSelector.actionAlreadyIncluded': 'This action is already included in this stage',
  'workflows.stepSelector.addFlagChanges': 'Add flag changes',
  'workflows.stepSelector.buildRule': 'Build my own rule',
  'workflows.stepSelector.noRulesToSet': 'Flag has no further rule rollouts to set',
  'workflows.stepSelector.requestApproval': 'Request approval',
  'workflows.stepSelector.schedule': 'Schedule date and time',
  'workflows.stepSelector.setDefaultVariation': 'Set default variation',
  'workflows.stepSelector.setRollout': 'Set rollout for a rule',
  'workflows.stepSelector.targetIndividuals': 'Target individuals',
  'workflows.stepSelector.targetMobile': 'Target mobile',
  'workflows.stepSelector.targetSegments': 'Target segments',
  'workflows.stepSelector.turnTargeting': 'Turn targeting ON or OFF',
  'workflows.targetingStep.label': 'Set targeting to',
  'workflows.targetsStep.bulkEditing': 'Bulk editing',
  'workflows.targetsStep.kindsLabel': 'kinds',
  'workflows.targetsStep.noTargets': 'No contexts are being targeted',
  'workflows.targetsStep.targetCount': '{count, number} {count, plural, one {target} other {targets}}',
  'workflows.targetsStep.targetRequired': 'At least one target is required.',
  'workflows.targetsStep.targetsLabel': 'targets',
  'workflows.targetsStep.userRequired': 'At least one user is required.',
  'workflows.targetsStep.variationPlaceholder': 'Select variation',
  'workflows.targetsStep.variationRequired': 'Variation cannot be empty.',
  'workflows.templatePickerDialog.clearLabel': 'clear',
  'workflows.templatePickerDialog.clearSearch': 'Clear search',
  'workflows.templatePickerDialog.closeLabel': 'close',
  'workflows.templatePickerDialog.createTemplate': 'Create template',
  'workflows.templatePickerDialog.createTemplateLabel': 'Create template',
  'workflows.templatePickerDialog.created': 'Created {date}',
  'workflows.templatePickerDialog.deleteTemplate': 'Delete template',
  'workflows.templatePickerDialog.emptyDescription': 'Try creating a new template',
  'workflows.templatePickerDialog.emptyHeading': 'No workflow templates found',
  'workflows.templatePickerDialog.listLabel': 'Workflow templates',
  'workflows.templatePickerDialog.moreActionsLabel': 'More actions',
  'workflows.templatePickerDialog.noMatchDescription': 'Try adjusting your filters',
  'workflows.templatePickerDialog.noMatchHeading': 'No workflow templates match your search',
  'workflows.templatePickerDialog.searchLabel': 'Search workflow templates',
  'workflows.templatePickerDialog.searchPlaceholder': 'Search workflow templates',
  'workflows.templatePickerDialog.title': 'Create workflow from template',
  'workflows.templatesDialog.back': 'Back',
  'workflows.templatesDialog.cancel': 'Cancel',
  'workflows.templatesDialog.checkForTypos': 'Check for typos.',
  'workflows.templatesDialog.createRestrictedTooltip': 'You do not have permissions to create templates',
  'workflows.templatesDialog.createTemplate': 'Create template',
  'workflows.templatesDialog.getStartedDescription':
    'Build a custom workflow and save it as a template to reuse later.',
  'workflows.templatesDialog.getStartedTitle': 'Get started with templates',
  'workflows.templatesDialog.noMatchSearchHint': 'Search for a template by name or description.',
  'workflows.templatesDialog.noMatchTitle': 'No templates match. Try this:',
  'workflows.templatesDialog.searchLabel': 'Search for a template',
  'workflows.templatesDialog.searchPlaceholder': 'Search for a template',
  'workflows.templatesDialog.title': 'Templates',
  'workflows.unavailableAlert.body':
    '<link>Review approval settings</link> or <docsLink>learn more about workflows</docsLink>',
  'workflows.unavailableAlert.heading': 'Workflows are not available in this environment',
  'workflows.unavailableAlert.reason':
    'Workflows are not supported in environments that use custom or third-party approval integrations.',
  'workflows.workflowsPage.activeOption': 'Active',
  'workflows.workflowsPage.completedOption': 'Completed',
  'workflows.workflowsPage.createHeading': 'Create a workflow',
  'workflows.workflowsPage.heading': 'Workflows',
  'workflows.workflowsPage.noActiveWorkflows': 'There are no active workflows',
  'workflows.workflowsPage.noCompletedWorkflows': 'There are no completed workflows',
  'workflows.workflowsPage.statusFilterLabel': 'Status',
};
